import * as React from "react";
import { flushSync } from "react-dom";
import { renderToStaticMarkup } from "react-dom/server";
import { useVirtualizer, useWindowVirtualizer } from "@tanstack/react-virtual";
import {
  cloneDocModel,
  type DocModel,
  type DocumentNoteDefinition,
  type FooterSection,
  type FormFieldRunNode,
  type HeaderSection,
  type HeadingLevel,
  type NumberingDefinitionSet,
  type NumberingLevelDefinition,
  type ParagraphStyleDefinition,
  type ParagraphAlignment,
  type ParagraphBorderSet,
  type ParagraphBorderStyle,
  type ParagraphIndent,
  type ImageRunNode,
  type ParagraphNode,
  type TableBorderSet,
  type TableBorderStyle,
  type TableCellStyle,
  type TableCellContentNode,
  type TableNode,
  type TableRowStyle,
  type TextRunNode,
} from "@extend-ai/react-docx-doc-model";
import {
  splitParagraphChildrenAtTextOffsets,
  updateParagraphText,
  updateTableCellParagraphTextRecursive,
  updateTableCellParagraphText,
  updateTableCellText,
} from "@extend-ai/react-docx-editor-ops";
import { type OoxmlPackage } from "@extend-ai/react-docx-ooxml-core";
import { serializeDocx } from "@extend-ai/react-docx-serializer";
import { importDocxBuffer } from "./docx-import";
import {
  collectTableExplicitPageBreakInfo,
  collectTopLevelExplicitPageBreakStartNodeIndexes,
} from "./pagination-breaks";
import {
  reconcilePageCountCandidateToTargetCountByScalingHeight,
  resolveMeasuredBodyFooterOverlapLatchState,
  shouldAllowStoredPageCountReduction,
} from "./page-count-reconciliation";
import {
  DEFAULT_DOCUMENT_LAYOUT,
  DEFAULT_DOC_PAGE_HEIGHT,
  DEFAULT_DOC_PAGE_WIDTH,
  pageMarginPaddingStyle,
  resolveDocumentLayout,
  parseSectionLayout,
  parseSectionPageBorders,
  TWIPS_PER_PIXEL,
  type DocumentLayoutMetrics,
  twipsToPixels,
} from "./section-layout";
import {
  imageUsesPlaceholderFallback,
  resolveRenderableImageSource,
  subscribeRenderableImageSourceUpdates,
  unsupportedImageFallbackLabel,
} from "./image-render";
import {
  layoutItemsWithPretextAroundExclusions,
  layoutTextWithPretextAroundExclusions,
  measurePretextPlainTextLineCount,
  type PretextLayoutItem,
  type PretextExclusionRect,
  type PretextLineFragment,
  type PretextSelectionRect,
  type PretextVariableWidthLayout,
  resolveCaretRectAtOffset,
  resolveOffsetAtPoint,
  resolveSelectionRects,
  sliceLayoutToLineRange,
} from "./pretext-layout";
import {
  docModelThumbnailMetadataSignature,
  docNodeContentSignature,
} from "./content-signature";
import {
  blitDocxThumbnailSurface,
  DOCX_THUMBNAIL_EXCLUDE_ATTRIBUTE,
  type DocxPageThumbnailRenderSnapshot,
  type DocxPageThumbnailSnapshotElement,
  type DocxPageThumbnailTableCellSnapshot,
  type DocxPageThumbnailTextRunSnapshot,
  DocxThumbnailSurfaceCache,
  rasterizeDocxThumbnailSurface,
  renderDocxThumbnailSnapshotSurface,
  SerialIdleTaskQueue,
} from "./thumbnail-raster";

const HIGHLIGHT_TO_CSS: Record<string, string> = {
  yellow: "#fff59d",
  green: "#bbf7d0",
  cyan: "#a5f3fc",
  magenta: "#f5d0fe",
  red: "#fecaca",
  blue: "#bfdbfe",
  black: "#111827",
  white: "#ffffff",
};

export type DocxDocumentTheme = "light" | "dark";
export type DocxHeadingStyleMap = Partial<
  Record<HeadingLevel, React.CSSProperties>
>;

const DEFAULT_WORD_HEADING_STYLES: Record<HeadingLevel, React.CSSProperties> = {
  1: {
    fontFamily: '"Calibri Light", Calibri, sans-serif',
    fontSize: "16pt",
    fontWeight: 600,
    color: "#2f5496",
  },
  2: {
    fontFamily: '"Calibri Light", Calibri, sans-serif',
    fontSize: "13pt",
    fontWeight: 600,
    color: "#2f5496",
  },
  3: {
    fontFamily: '"Calibri", sans-serif',
    fontSize: "12pt",
    fontWeight: 600,
    color: "#1f3763",
  },
  4: {
    fontFamily: '"Calibri", sans-serif',
    fontSize: "11pt",
    fontWeight: 600,
    color: "#1f3763",
  },
  5: {
    fontFamily: '"Calibri", sans-serif',
    fontSize: "11pt",
    fontWeight: 600,
    color: "#1f3763",
  },
  6: {
    fontFamily: '"Calibri", sans-serif',
    fontSize: "11pt",
    fontWeight: 600,
    color: "#1f3763",
  },
};

const DEFAULT_WORD_HEADING_RUN_STYLES: Record<
  HeadingLevel,
  NonNullable<ParagraphStyleDefinition["runStyle"]>
> = {
  1: {
    fontFamily: "Calibri Light",
    fontSizePt: 16,
    bold: true,
    color: "#2f5496",
  },
  2: {
    fontFamily: "Calibri Light",
    fontSizePt: 13,
    bold: true,
    color: "#2f5496",
  },
  3: {
    fontFamily: "Calibri",
    fontSizePt: 12,
    bold: true,
    color: "#1f3763",
  },
  4: {
    fontFamily: "Calibri",
    fontSizePt: 11,
    bold: true,
    color: "#1f3763",
  },
  5: {
    fontFamily: "Calibri",
    fontSizePt: 11,
    bold: true,
    color: "#1f3763",
  },
  6: {
    fontFamily: "Calibri",
    fontSizePt: 11,
    bold: true,
    color: "#1f3763",
  },
};

const DOC_PAGE_BREAK_GAP = 28;
const PAGE_OVERFLOW_TOLERANCE_PX = 2;
const DEFAULT_PARAGRAPH_FONT_SIZE_PT = 11;
const SCRIPT_FONT_SCALE = 0.65;
// Word defaults to single line spacing unless the document/style overrides it.
const DEFAULT_PARAGRAPH_LINE_MULTIPLE = 1;
// Browser line box metrics run taller at single-spacing but converge by ~1.08.
const WORD_SINGLE_LINE_AUTO_SCALE = 0.88;
const WORD_SINGLE_LINE_AUTO_SCALE_SANS = 0.9;
const WORD_SINGLE_LINE_AUTO_SCALE_SERIF = 1.08;
// A text-free paragraph occupies exactly one line at the paragraph mark's
// natural (line-height: normal) font metrics. The wrap-compensation scales
// above deliberately undersize lines to balance wrapped-line overcounting,
// but an empty paragraph has no wrapping to compensate for.
const WORD_EMPTY_PARAGRAPH_LINE_SCALE = 1.21;
const WORD_EMPTY_PARAGRAPH_LINE_SCALE_SERIF = 1.15;
const WORD_EMPTY_PARAGRAPH_LINE_SCALE_SANS = 1.15;
const WORD_AUTO_LINE_SCALE_BLEND_END_MULTIPLE = 1.08;
const MIN_AUTO_LINE_MULTIPLE = 0.1;
const MIN_PARAGRAPH_LINE_HEIGHT_PX = 14;
const FLOATING_HEADER_FOOTER_CLEARANCE_BUFFER_PX = 18;
const MEASURED_PAGE_HEADER_CLEARANCE_BUFFER_PX = 8;
const MEASURED_PAGE_FOOTER_CLEARANCE_BUFFER_PX = 24;
const UNOVERLAPPED_FOOTER_MIN_CLEARANCE_PX = 8;
const FLOATING_FOOTER_BASELINE_CLEARANCE_RESERVE_PX = 8;
const MIN_VISIBLE_FLOW_FOOTER_PAGINATION_RESERVE_PX = 8;
const PAGINATION_MEASUREMENT_INTERACTION_DEBOUNCE_MS = 180;
const MEASURED_BODY_FOOTER_OVERLAP_STABILITY_THRESHOLD = 1;
const WORD_TABLE_CELL_PARAGRAPH_AUTO_LINE_TWIPS = 240;
const WORD_TABLE_CELL_PARAGRAPH_BEFORE_TWIPS = 0;
const WORD_TABLE_CELL_PARAGRAPH_AFTER_TWIPS = 0;
const DOCX_IMPORT_PERFORMANCE_PREFIX = "react-docx.import";

function markDocxImportPerformance(name: string): void {
  if (
    typeof performance === "undefined" ||
    typeof performance.mark !== "function"
  ) {
    return;
  }

  try {
    performance.mark(name);
  } catch {
    // Performance marks are diagnostic-only.
  }
}

function measureDocxImportPerformance(
  name: string,
  startMark: string,
  endMark: string
): void {
  if (
    typeof performance === "undefined" ||
    typeof performance.measure !== "function"
  ) {
    return;
  }

  try {
    performance.measure(name, startMark, endMark);
  } catch {
    // A missing mark should not affect import.
  }
}

function createDocxImportPerformanceTraceName(fileName: string): string {
  const normalizedName = fileName.replace(/[^a-z0-9._-]+/gi, "_").slice(0, 80);
  return `${DOCX_IMPORT_PERFORMANCE_PREFIX}.${Date.now()}.${normalizedName}`;
}
const TABLE_ROW_SLICE_VISUAL_BLEED_PX = 1;
const TABLE_CELL_SLICE_FULLY_VISIBLE_BOTTOM_BUFFER_PX = 4;
const DEFAULT_SPLIT_PARAGRAPH_LINE_TWIPS = 259;
const DEFAULT_SPLIT_PARAGRAPH_AFTER_TWIPS = 160;
const PAGE_CONTENT_MEASUREMENT_IGNORE_ATTRIBUTE = "data-docx-pagination-ignore";
const WORD_TABLE_CELL_FALLBACK_PADDING_PX = {
  top: 4,
  right: 4,
  bottom: 4,
  left: 4,
} as const;
const DEFAULT_UNSPECIFIED_DOCX_FONT_FAMILY = "Times New Roman";
const MAX_TRAILING_SECTION_TAIL_PARAGRAPHS = 18;
const MAX_TRAILING_SECTION_TAIL_OVERFLOW_PX = 700;
const SPLITTABLE_TABLE_ROW_ESTIMATE_EXTRA_LINE_COUNT = 2;
const SPLITTABLE_TABLE_ROW_DEEP_CONTENT_NODE_THRESHOLD = 8;
const PAGE_BREAK_XML_PATTERN = /<w:br\b[^>]*w:type="page"[^>]*\/?>/i;
const COLUMN_BREAK_XML_PATTERN = /<w:br\b[^>]*w:type="column"[^>]*\/?>/i;
const LAST_RENDERED_PAGE_BREAK_XML_PATTERN =
  /<w:lastRenderedPageBreak\b[^>]*\/?>/i;
const PAGE_BREAK_BEFORE_XML_PATTERN = /<w:pageBreakBefore\b[^>]*\/?>/i;
const SECTION_PROPERTIES_XML_PATTERN = /<w:sectPr\b[\s\S]*?<\/w:sectPr>/i;
const SECTION_TYPE_XML_PATTERN = /<w:type\b[^>]*w:val="([^"]+)"/i;
const BOOKMARK_START_XML_PATTERN = /<w:bookmarkStart\b[^>]*w:name="([^"]+)"/gi;
const FOOTNOTE_REFERENCE_XML_PATTERN =
  /<w:footnoteReference\b[^>]*w:id="(-?\d+)"/gi;
const ENDNOTE_REFERENCE_XML_PATTERN =
  /<w:endnoteReference\b[^>]*w:id="(-?\d+)"/gi;
const XML_CACHE_MAX_ENTRIES = 4000;
const TEXT_MEASURE_CACHE_MAX_ENTRIES = 12000;
const DEFAULT_TAB_STOP_PX = 48;
const TAB_LEADER_ZONE_GAP_PX = 20;
const EMPTY_PARAGRAPH_EXTRA_HEIGHT_PX = 0;
const PARAGRAPH_SEGMENT_TOP_BLEED_PX = 22;
const PARAGRAPH_SEGMENT_DESCENDER_BLEED_PX = 6;
const PARAGRAPH_SEGMENT_VISUAL_SAFETY_PX = 24;
const LAST_RENDERED_PAGE_BREAK_HINT_MAX_REMAINING_SPACE_RATIO = 0.18;
const LAST_RENDERED_PAGE_BREAK_HINT_MIN_REMAINING_SPACE_PX =
  MIN_PARAGRAPH_LINE_HEIGHT_PX * 3;
const LAST_RENDERED_PAGE_BREAK_HINT_MAX_REMAINING_SPACE_PX = 120;
const PARAGRAPH_SEGMENT_FALLBACK_TOP_BLEED_MAX_PX = 4;
const PARAGRAPH_SEGMENT_FALLBACK_BOTTOM_BLEED_MAX_PX = 0;
const PARAGRAPH_SEGMENT_FALLBACK_VISUAL_SAFETY_PX = 4;
const INITIAL_PAGINATION_PREMEASURE_PAGE_LIMIT = 8;
const INITIAL_PAGINATION_PAGE_COUNT_OSCILLATION_DISTINCT_THRESHOLD = 2;
const INITIAL_PAGINATION_PAGE_COUNT_OSCILLATION_CHANGE_THRESHOLD = 4;
const INITIAL_PAGINATION_BACKGROUND_REFINEMENT_DELAY_MS = 96;
const DEFAULT_PAGE_VIRTUALIZATION_OVERSCAN = 2;
const LARGE_TABLE_PAGE_VIRTUALIZATION_OVERSCAN = 0;
const LARGE_TABLE_PAGE_ADJACENT_RENDER_COUNT = 1;
const DEFAULT_PAGE_VIRTUALIZATION_SETTLE_DELAY_MS = 350;
const PAGE_SCROLL_MEASUREMENT_SUSPEND_MS = 220;
const ENABLE_TABLE_ROW_SLICING = true;
const TABLE_ROW_HEIGHT_PAGINATION_ESTIMATE_PADDING_RATIO = 1.08;
const TABLE_ROW_HEIGHT_PAGINATION_ESTIMATE_PADDING_MIN_ROWS = 15;
const MIN_TABLE_ROW_SLICE_REMAINING_HEIGHT_PX =
  MIN_PARAGRAPH_LINE_HEIGHT_PX * 2;
const TABLE_ROW_SLICE_BOUNDARY_TOLERANCE_PX = 2;
const TOP_AND_BOTTOM_VERTICAL_DRAG_SNAP_PX = 10;
const HEADER_FOOTER_INACTIVE_OPACITY = 0.5;
const LETTERHEAD_INDENT_MIN_TWIPS = 900;
const LETTERHEAD_INDENT_MAX_TWIPS = 4200;
const LETTERHEAD_MAX_TEXT_LENGTH = 96;
const LETTERHEAD_FRAME_NEARBY_NODE_DISTANCE = 3;
const ACTIVE_RANGE_SYNC_KEYS = new Set([
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
  "PageUp",
  "PageDown",
  "Backspace",
  "Delete",
  "Enter",
  "Tab",
]);
const MODIFIER_ONLY_KEYS = new Set([
  "Shift",
  "Control",
  "Meta",
  "Alt",
  "CapsLock",
]);

const paragraphBreakFlagsBySourceXml = new Map<
  string,
  {
    explicitPageBreak: boolean;
    explicitColumnBreak: boolean;
    lastRenderedPageBreak: boolean;
    pageBreakBefore: boolean;
    sectionBreakStartsNewPage: boolean;
  }
>();
const paragraphEstimatedHeightBySourceXml = new Map<
  string,
  Map<number | string, number>
>();
const tableEstimatedHeightBySourceXml = new Map<string, Map<number, number>>();
const tableEstimatedRowHeightsByNode = new WeakMap<
  TableNode,
  Map<number, number[]>
>();
const paragraphExplicitIndentBySourceXml = new Map<
  string,
  ParagraphIndent | null
>();
const paragraphDropCapBySourceXml = new Map<
  string,
  {
    type: "drop" | "margin";
    lines?: number;
  } | null
>();
interface ParagraphTrackedInlineChange {
  id: string;
  kind: DocxTrackedChangeKind;
  author?: string;
  date?: string;
  text?: string;
}

interface ParagraphTrackedDeletionSegment {
  text: string;
  change: ParagraphTrackedInlineChange;
  style?: TextRunNode["style"] | FormFieldRunNode["style"];
}

interface ParagraphTrackedMarkup {
  inlineChangeByVisibleChildIndex: Array<
    ParagraphTrackedInlineChange | undefined
  >;
  deletedSegmentsByVisibleChildIndex: Map<
    number,
    ParagraphTrackedDeletionSegment[]
  >;
  changes: ParagraphTrackedInlineChange[];
}

interface ParagraphCommentMarkup {
  commentIdsByVisibleChildIndex: Array<number[] | undefined>;
}

const paragraphTrackedMarkupBySourceXml = new Map<
  string,
  ParagraphTrackedMarkup | null
>();
const paragraphCommentMarkupBySourceXml = new Map<
  string,
  ParagraphCommentMarkup | null
>();
let paragraphMeasureCanvasContext: CanvasRenderingContext2D | undefined;
const textWidthByFontAndValue = new Map<string, number>();
const estimatedTextAdvanceWidthByFontAndValue = new Map<string, number>();
const pretextWordBreakModeByText = new Map<string, "normal" | "keep-all">();
const paragraphBaseFontSizePxByParagraph = new WeakMap<ParagraphNode, number>();
const paragraphDominantFontFamilyByParagraph = new WeakMap<
  ParagraphNode,
  string | null
>();

interface TableSpacingTwips {
  topTwips?: number;
  rightTwips?: number;
  bottomTwips?: number;
  leftTwips?: number;
}

function setCacheEntry<K, V>(cache: Map<K, V>, key: K, value: V): void {
  if (!cache.has(key) && cache.size >= XML_CACHE_MAX_ENTRIES) {
    const oldestKey = cache.keys().next().value as K | undefined;
    if (oldestKey !== undefined) {
      cache.delete(oldestKey);
    }
  }
  cache.set(key, value);
}

function widthCacheKeyPx(widthPx?: number): number {
  if (!Number.isFinite(widthPx) || (widthPx as number) <= 0) {
    return -1;
  }

  return Math.max(1, Math.round(widthPx as number));
}

function heightEstimateCacheKeyPx(
  widthPx?: number,
  docGridLinePitchPx?: number,
  disableDocGridSnap = false
): number {
  const widthKey = widthCacheKeyPx(widthPx);
  const docGridKey =
    Number.isFinite(docGridLinePitchPx) && (docGridLinePitchPx as number) > 0
      ? Math.max(0, Math.round(docGridLinePitchPx as number))
      : 0;

  return (
    (widthKey + 2) * 10_000 + docGridKey * 2 + (disableDocGridSnap ? 1 : 0)
  );
}

function xmlAttribute(tagXml: string, attribute: string): string | undefined {
  const escaped = attribute.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = tagXml.match(
    new RegExp(`${escaped}=(?:"([^"]+)"|'([^']+)')`, "i")
  );
  return match?.[1] ?? match?.[2];
}

function headingLevelFromStyleLabel(value?: string): HeadingLevel | undefined {
  if (!value) {
    return undefined;
  }

  const match = value.match(/(?:^|[\s_-])(?:heading|h)\s*([1-6])(?:$|[\s_-])/i);
  if (!match?.[1]) {
    return undefined;
  }

  const parsed = Number(match[1]);
  return parsed >= 1 && parsed <= 6 ? (parsed as HeadingLevel) : undefined;
}

function resolveParagraphStyleHeadingLevel(
  styleDefinition: Pick<
    ParagraphStyleDefinition,
    "headingLevel" | "id" | "name"
  >
): HeadingLevel | undefined {
  if (
    Number.isFinite(styleDefinition.headingLevel) &&
    (styleDefinition.headingLevel as number) >= 1 &&
    (styleDefinition.headingLevel as number) <= 6
  ) {
    return styleDefinition.headingLevel as HeadingLevel;
  }

  return (
    headingLevelFromStyleLabel(styleDefinition.id) ??
    headingLevelFromStyleLabel(styleDefinition.name)
  );
}

function normalizeParagraphStyleDefinitionsForUi(
  styles: ParagraphStyleDefinition[]
): ParagraphStyleDefinition[] {
  return styles.map((styleDefinition) => {
    const headingLevel = resolveParagraphStyleHeadingLevel(styleDefinition);
    const headingRunStyle = headingLevel
      ? DEFAULT_WORD_HEADING_RUN_STYLES[headingLevel]
      : undefined;
    const mergedRunStyle = headingRunStyle
      ? {
          ...headingRunStyle,
          ...(styleDefinition.runStyle ?? {}),
        }
      : styleDefinition.runStyle;
    const runStyleChanged =
      mergedRunStyle !== styleDefinition.runStyle ||
      (headingRunStyle !== undefined && styleDefinition.runStyle === undefined);
    const headingChanged = headingLevel !== styleDefinition.headingLevel;

    if (!runStyleChanged && !headingChanged) {
      return styleDefinition;
    }

    return {
      ...styleDefinition,
      headingLevel,
      runStyle: mergedRunStyle,
    };
  });
}

function paragraphDropCap(
  paragraph: ParagraphNode
): NonNullable<NonNullable<ParagraphNode["style"]>["dropCap"]> | undefined {
  if (paragraph.style?.dropCap) {
    return paragraph.style.dropCap;
  }

  const xml = paragraph.sourceXml ?? "";
  if (!xml) {
    return undefined;
  }

  const cached = paragraphDropCapBySourceXml.get(xml);
  if (cached !== undefined) {
    return cached ?? undefined;
  }

  const framePrTag = xml.match(/<w:framePr\b[^>]*\/?>/i)?.[0];
  if (!framePrTag) {
    setCacheEntry(paragraphDropCapBySourceXml, xml, null);
    return undefined;
  }

  const dropCapRaw = xmlAttribute(framePrTag, "w:dropCap")
    ?.trim()
    .toLowerCase();
  let type: "drop" | "margin" | undefined;
  if (dropCapRaw === "drop" || dropCapRaw === "margin") {
    type = dropCapRaw;
  }
  if (!type) {
    setCacheEntry(paragraphDropCapBySourceXml, xml, null);
    return undefined;
  }

  const linesRaw = xmlAttribute(framePrTag, "w:lines");
  const parsedLines = linesRaw ? Number(linesRaw) : Number.NaN;
  const wrap = xmlAttribute(framePrTag, "w:wrap")?.trim();
  const horizontalAnchor = xmlAttribute(framePrTag, "w:hAnchor")?.trim();
  const verticalAnchor = xmlAttribute(framePrTag, "w:vAnchor")?.trim();
  const xTwipsRaw = xmlAttribute(framePrTag, "w:x");
  const yTwipsRaw = xmlAttribute(framePrTag, "w:y");
  const horizontalSpaceTwipsRaw = xmlAttribute(framePrTag, "w:hSpace");
  const verticalSpaceTwipsRaw = xmlAttribute(framePrTag, "w:vSpace");
  const xTwips = xTwipsRaw ? Number(xTwipsRaw) : Number.NaN;
  const yTwips = yTwipsRaw ? Number(yTwipsRaw) : Number.NaN;
  const horizontalSpaceTwips = horizontalSpaceTwipsRaw
    ? Number(horizontalSpaceTwipsRaw)
    : Number.NaN;
  const verticalSpaceTwips = verticalSpaceTwipsRaw
    ? Number(verticalSpaceTwipsRaw)
    : Number.NaN;
  const resolved = {
    type,
    lines:
      Number.isFinite(parsedLines) && parsedLines > 0
        ? Math.round(parsedLines)
        : undefined,
    ...(wrap ? { wrap } : undefined),
    ...(horizontalAnchor ? { horizontalAnchor } : undefined),
    ...(verticalAnchor ? { verticalAnchor } : undefined),
    ...(Number.isFinite(xTwips) ? { xTwips: Math.round(xTwips) } : undefined),
    ...(Number.isFinite(yTwips) ? { yTwips: Math.round(yTwips) } : undefined),
    ...(Number.isFinite(horizontalSpaceTwips)
      ? { horizontalSpaceTwips: Math.round(horizontalSpaceTwips) }
      : undefined),
    ...(Number.isFinite(verticalSpaceTwips)
      ? { verticalSpaceTwips: Math.round(verticalSpaceTwips) }
      : undefined),
  };
  setCacheEntry(paragraphDropCapBySourceXml, xml, resolved);
  return resolved;
}

type LetterheadFloatSide = "left" | "right";

interface LetterheadColumnGroup {
  startOffset: number;
  endOffset: number;
  leftSegments: DocumentPageNodeSegment[];
  rightSegments: DocumentPageNodeSegment[];
  entries: Array<{
    segment: DocumentPageNodeSegment;
    side: LetterheadFloatSide;
  }>;
}

function paragraphHasLegacyFrame(paragraph: ParagraphNode): boolean {
  const xml = paragraph.sourceXml ?? "";
  if (!xml) {
    return false;
  }

  return /<w:framePr\b[^>]*\/?>/i.test(xml);
}

function paragraphLetterheadSideFromIndent(
  paragraph: ParagraphNode
): LetterheadFloatSide | undefined {
  const leftIndentTwipsRaw = paragraph.style?.indent?.leftTwips;
  const rightIndentTwipsRaw = paragraph.style?.indent?.rightTwips;
  const leftIndentTwips =
    Number.isFinite(leftIndentTwipsRaw) && (leftIndentTwipsRaw as number) > 0
      ? Math.round(leftIndentTwipsRaw as number)
      : 0;
  const rightIndentTwips =
    Number.isFinite(rightIndentTwipsRaw) && (rightIndentTwipsRaw as number) > 0
      ? Math.round(rightIndentTwipsRaw as number)
      : 0;
  const leftCandidate =
    rightIndentTwips >= LETTERHEAD_INDENT_MIN_TWIPS &&
    rightIndentTwips <= LETTERHEAD_INDENT_MAX_TWIPS;
  const rightCandidate =
    leftIndentTwips >= LETTERHEAD_INDENT_MIN_TWIPS &&
    leftIndentTwips <= LETTERHEAD_INDENT_MAX_TWIPS;

  if (leftCandidate && !rightCandidate) {
    return "left";
  }

  if (rightCandidate && !leftCandidate) {
    return "right";
  }

  if (leftCandidate && rightCandidate) {
    return rightIndentTwips >= leftIndentTwips ? "left" : "right";
  }

  return undefined;
}

function paragraphHasNearbyLegacyFrame(
  nodes: DocModel["nodes"],
  nodeIndex: number
): boolean {
  const startIndex = Math.max(
    0,
    nodeIndex - LETTERHEAD_FRAME_NEARBY_NODE_DISTANCE
  );
  const endIndex = Math.min(
    nodes.length - 1,
    nodeIndex + LETTERHEAD_FRAME_NEARBY_NODE_DISTANCE
  );

  for (let index = startIndex; index <= endIndex; index += 1) {
    const node = nodes[index];
    if (!node || node.type !== "paragraph") {
      continue;
    }
    if (paragraphHasLegacyFrame(node)) {
      return true;
    }
  }

  return false;
}

export function paragraphLetterheadFloatSideAtNodeIndex(
  nodes: DocModel["nodes"],
  nodeIndex: number
): LetterheadFloatSide | undefined {
  const node = nodes[nodeIndex];
  if (!node || node.type !== "paragraph") {
    return undefined;
  }

  const side = paragraphLetterheadSideFromIndent(node);
  if (!side) {
    return undefined;
  }

  const normalizedText = paragraphText(node).replace(/\s+/g, " ").trim();
  if (normalizedText.length > LETTERHEAD_MAX_TEXT_LENGTH) {
    return undefined;
  }

  if (
    paragraphHasLegacyFrame(node) ||
    paragraphHasNearbyLegacyFrame(nodes, nodeIndex)
  ) {
    return side;
  }

  return undefined;
}

export function paragraphLetterheadColumnGroupAtSegmentOffset(
  nodes: DocModel["nodes"],
  nodeSegments: DocumentPageNodeSegment[],
  startOffset: number
): LetterheadColumnGroup | undefined {
  if (startOffset < 0 || startOffset >= nodeSegments.length) {
    return undefined;
  }

  const leftSegments: DocumentPageNodeSegment[] = [];
  const rightSegments: DocumentPageNodeSegment[] = [];
  const entries: Array<{
    segment: DocumentPageNodeSegment;
    side: LetterheadFloatSide;
  }> = [];
  let cursor = startOffset;

  while (cursor < nodeSegments.length) {
    const segment = nodeSegments[cursor];
    if (
      !segment ||
      segment.tableRowRange ||
      paragraphSegmentHasPartialLineRange(segment.paragraphLineRange)
    ) {
      break;
    }

    const side = paragraphLetterheadFloatSideAtNodeIndex(
      nodes,
      segment.nodeIndex
    );
    if (!side) {
      break;
    }

    if (side === "left") {
      leftSegments.push(segment);
    } else {
      rightSegments.push(segment);
    }
    entries.push({
      segment,
      side,
    });
    cursor += 1;
  }

  if (
    cursor <= startOffset ||
    leftSegments.length === 0 ||
    rightSegments.length === 0
  ) {
    return undefined;
  }

  return {
    startOffset,
    endOffset: cursor,
    leftSegments,
    rightSegments,
    entries,
  };
}

export function letterheadColumnGroupContainerStyle(): React.CSSProperties {
  return {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    columnGap: 28,
    alignItems: "start",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  };
}

export function letterheadColumnStackStyle(): React.CSSProperties {
  return {
    minWidth: 0,
    maxWidth: "100%",
  };
}

export function letterheadParagraphStyleAdjustments(
  side: LetterheadFloatSide,
  oppositeSide?: LetterheadFloatSide,
  options?: {
    suppressLetterheadColumnLayout?: boolean;
  }
): React.CSSProperties {
  void side;
  void oppositeSide;
  if (options?.suppressLetterheadColumnLayout) {
    return {
      width: "100%",
      boxSizing: "border-box",
      marginLeft: 0,
      marginRight: 0,
    };
  }

  return {};
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function hasVerticalScrollOverflow(value: string): boolean {
  return (
    value === "auto" ||
    value === "scroll" ||
    value === "overlay" ||
    value === "hidden"
  );
}

function nearestScrollableAncestor(
  element: HTMLElement | null
): HTMLElement | null {
  let current: HTMLElement | null = element;
  while (current) {
    const style = window.getComputedStyle(current);
    if (
      hasVerticalScrollOverflow(style.overflowY) ||
      hasVerticalScrollOverflow(style.overflow)
    ) {
      return current;
    }
    current = current.parentElement;
  }
  const scrollingElement =
    typeof document !== "undefined" ? document.scrollingElement : null;
  if (scrollingElement instanceof HTMLElement) {
    return scrollingElement;
  }
  return typeof document !== "undefined" ? document.documentElement : null;
}

function resolveEffectiveZoomScale(element: HTMLElement): number {
  let current: HTMLElement | null = element;
  let scale = 1;
  while (current) {
    const zoomRaw = window.getComputedStyle(current).zoom;
    if (zoomRaw && zoomRaw !== "normal") {
      const zoom = Number.parseFloat(zoomRaw);
      if (Number.isFinite(zoom) && zoom > 0) {
        scale *= zoom;
      }
    }
    current = current.parentElement;
  }

  return Number.isFinite(scale) && scale > 0 ? scale : 1;
}

function normalizePageVirtualizationZoomScale(
  value: unknown
): number | undefined {
  const scale = Number(value);
  return Number.isFinite(scale) && scale > 0 ? scale : undefined;
}

const DOC_SURFACE_STYLE_BY_THEME: Record<
  DocxDocumentTheme,
  React.CSSProperties
> = {
  light: {
    backgroundColor: "#ffffff",
    color: "#111827",
    colorScheme: "light",
    border: "none",
    boxShadow:
      "0 2px 10px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.05)",
  },
  dark: {
    backgroundColor: "#0a0a0a",
    color: "#f3f4f6",
    colorScheme: "dark",
    border: "none",
    boxShadow:
      "0 2px 10px rgba(2, 6, 23, 0.55), 0 1px 2px rgba(2, 6, 23, 0.45)",
  },
};

const NIGHT_READER_INVERSION_FILTER =
  "invert(0.95) hue-rotate(180deg) saturate(0.9) brightness(0.9) contrast(0.94)";

function appendCssFilters(
  ...filters: Array<string | undefined | null>
): string | undefined {
  const resolvedFilters = filters
    .map((filter) => filter?.trim())
    .filter((filter): filter is string => Boolean(filter));
  return resolvedFilters.length > 0 ? resolvedFilters.join(" ") : undefined;
}

const TABLE_RESIZE_HANDLE_SIZE = 8;
const TABLE_RESIZE_HANDLE_HIT_SIZE = 16;
const TABLE_RESIZE_BORDER_SIZE = 1;
const TABLE_MOVE_HANDLE_SIZE = 14;
const TABLE_MOVE_HANDLE_HIT_SIZE = 18;
const TABLE_HANDLE_HOVER_OUTSET_PX = 24;
const TABLE_HANDLE_SAFEZONE_TOP_PX = TABLE_MOVE_HANDLE_HIT_SIZE + 4;
const TABLE_HANDLE_SAFEZONE_LEFT_PX = TABLE_MOVE_HANDLE_HIT_SIZE + 4;
const TABLE_HANDLE_SAFEZONE_RIGHT_PX =
  Math.ceil(TABLE_RESIZE_HANDLE_HIT_SIZE / 2) + 4;
const TABLE_HANDLE_SAFEZONE_BOTTOM_PX = TABLE_RESIZE_HANDLE_HIT_SIZE + 8;
const TABLE_RESIZE_HANDLE_ALIGNMENT_OFFSET_PX = 0.5;
const TABLE_MOVE_DRAG_THRESHOLD_PX = 3;
const TABLE_RESIZE_HANDLE_STYLE: React.CSSProperties = {
  width: TABLE_RESIZE_HANDLE_SIZE,
  height: TABLE_RESIZE_HANDLE_SIZE,
  borderRadius: 2,
  border: "1px solid #d4d4d8",
  backgroundColor: "#ffffff",
  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.14)",
  pointerEvents: "none",
};
const TABLE_MOVE_HANDLE_STYLE: React.CSSProperties = {
  width: TABLE_MOVE_HANDLE_SIZE,
  height: TABLE_MOVE_HANDLE_SIZE,
  borderRadius: 4,
  border: "1px solid #d4d4d8",
  backgroundColor: "#ffffff",
  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.14)",
  color: "#4b5563",
  display: "grid",
  placeItems: "center",
  pointerEvents: "none",
};

const BASE_DOC_STYLE: React.CSSProperties = {
  position: "relative",
  margin: "0 auto",
  width: DEFAULT_DOC_PAGE_WIDTH,
  minHeight: DEFAULT_DOC_PAGE_HEIGHT,
  display: "block",
  gap: 0,
  transition: "box-shadow 0.2s ease",
};

const TRACKED_CHANGE_GUTTER_WIDTH_PX = 300;
const TRACKED_CHANGE_GUTTER_CARD_LEFT_PX = 48;
const TRACKED_CHANGE_GUTTER_CARD_RIGHT_PX = 12;
const TRACKED_CHANGE_GUTTER_CARD_GAP_PX = 4;
const TRACKED_CHANGE_GUTTER_CARD_MIN_HEIGHT_PX = 30;
const TRACKED_CHANGE_GUTTER_BEND_OFFSET_PX = 8;
const TRACKED_CHANGE_GUTTER_CONNECTOR_LANE_GAP_PX = 7;
const TRACKED_CHANGE_GUTTER_CONNECTOR_LANE_COUNT = 5;
const INITIAL_PAGINATION_STABILITY_IDLE_MS = 240;

function scheduleDomWrite(callback: () => void): void {
  if (
    typeof window !== "undefined" &&
    typeof window.requestAnimationFrame === "function"
  ) {
    window.requestAnimationFrame(() => {
      callback();
    });
    return;
  }

  setTimeout(callback, 0);
}

function normalizeMeasuredTableRowHeightPx(heightPx: number): number {
  if (!Number.isFinite(heightPx) || heightPx <= 0) {
    return MIN_PARAGRAPH_LINE_HEIGHT_PX;
  }

  return Math.max(MIN_PARAGRAPH_LINE_HEIGHT_PX, Math.round(heightPx));
}

function reconcileMeasuredTableRowHeightsForImportPagination(
  table: TableNode,
  measuredRowHeights: number[],
  maxAvailableWidthPx?: number,
  pageContentHeightPx?: number,
  numberingDefinitions?: NumberingDefinitionSet,
  docGridLinePitchPx?: number
): number[] | undefined {
  if (measuredRowHeights.length !== table.rows.length) {
    return undefined;
  }

  const estimatedRowHeights = estimateTableRowHeightsPx(
    table,
    maxAvailableWidthPx,
    numberingDefinitions,
    docGridLinePitchPx,
    pageContentHeightPx
  );
  if (estimatedRowHeights.length !== table.rows.length) {
    return undefined;
  }

  return measuredRowHeights.map((heightPx, rowIndex) => {
    const row = table.rows[rowIndex];
    const normalizedMeasuredHeightPx =
      normalizeMeasuredTableRowHeightPx(heightPx);
    const normalizedPageContentHeightPx =
      Number.isFinite(pageContentHeightPx) &&
      (pageContentHeightPx as number) > 0
        ? Math.max(120, Math.round(pageContentHeightPx as number))
        : undefined;
    const estimatedHeightPx = Math.max(
      MIN_PARAGRAPH_LINE_HEIGHT_PX,
      Math.round(estimatedRowHeights[rowIndex] ?? MIN_PARAGRAPH_LINE_HEIGHT_PX)
    );
    const explicitHeightPx = twipsToPixels(row.style?.heightTwips);
    const normalizedExplicitHeightPx =
      Number.isFinite(explicitHeightPx) && (explicitHeightPx as number) > 0
        ? Math.max(
            MIN_PARAGRAPH_LINE_HEIGHT_PX,
            Math.round(explicitHeightPx as number)
          )
        : undefined;
    const allowedGrowthPx = rowHasDeepFlowContent(row)
      ? Math.max(
          MIN_PARAGRAPH_LINE_HEIGHT_PX,
          Math.round(estimatedHeightPx * 0.2)
        )
      : Math.max(4, Math.round(MIN_PARAGRAPH_LINE_HEIGHT_PX * 0.5));
    const explicitMinimumHeightPx =
      normalizedExplicitHeightPx ?? MIN_PARAGRAPH_LINE_HEIGHT_PX;
    if (rowAllowsPageSplit(row) && row.style?.heightRule !== "exact") {
      return Math.max(explicitMinimumHeightPx, normalizedMeasuredHeightPx);
    }

    if (
      Number.isFinite(normalizedPageContentHeightPx) &&
      normalizedMeasuredHeightPx >
        (normalizedPageContentHeightPx as number) + PAGE_OVERFLOW_TOLERANCE_PX
    ) {
      return Math.max(explicitMinimumHeightPx, normalizedMeasuredHeightPx);
    }

    const minimumHeightPx = Math.max(
      estimatedHeightPx,
      explicitMinimumHeightPx
    );
    const maximumHeightPx = Math.max(
      minimumHeightPx,
      (normalizedExplicitHeightPx ?? estimatedHeightPx) + allowedGrowthPx
    );
    return clampNumber(
      normalizedMeasuredHeightPx,
      minimumHeightPx,
      maximumHeightPx
    );
  });
}

export function resolveTableMeasuredRowHeightsForPagination(
  nodes: DocModel["nodes"],
  tableMeasuredRowHeights: Record<number, number[]>,
  options?: {
    allowMeasuredImportPagination?: boolean;
    activeDraftKeys?: string[];
    numberingDefinitions?: NumberingDefinitionSet;
    pageContentWidthPxByNodeIndex?: Map<number, number | undefined>;
    pageContentHeightPxByNodeIndex?: Map<number, number | undefined>;
    docGridLinePitchPxByNodeIndex?: Map<number, number | undefined>;
  }
): Record<number, number[]> | undefined {
  const activeTableIndexes = new Set<number>();

  (options?.activeDraftKeys ?? []).forEach((draftKey) => {
    const [tableIndexText] = draftKey.split(":");
    const tableIndex = Number.parseInt(tableIndexText ?? "", 10);
    if (Number.isFinite(tableIndex) && tableIndex >= 0) {
      activeTableIndexes.add(tableIndex);
    }
  });

  const includeOnlyActiveTables = activeTableIndexes.size > 0;
  if (
    includeOnlyActiveTables === false &&
    options?.allowMeasuredImportPagination !== true
  ) {
    return undefined;
  }

  const next: Record<number, number[]> = {};
  Object.entries(tableMeasuredRowHeights).forEach(
    ([tableIndexText, measuredRowHeights]) => {
      const tableIndex = Number.parseInt(tableIndexText, 10);
      const tableNode = nodes[tableIndex];
      if (
        !Number.isFinite(tableIndex) ||
        !tableNode ||
        tableNode.type !== "table" ||
        !Array.isArray(measuredRowHeights) ||
        measuredRowHeights.length !== tableNode.rows.length
      ) {
        return;
      }

      if (includeOnlyActiveTables && !activeTableIndexes.has(tableIndex)) {
        return;
      }

      const nextHeights = includeOnlyActiveTables
        ? measuredRowHeights.map((heightPx) =>
            normalizeMeasuredTableRowHeightPx(heightPx)
          )
        : reconcileMeasuredTableRowHeightsForImportPagination(
            tableNode,
            measuredRowHeights,
            options?.pageContentWidthPxByNodeIndex?.get(tableIndex),
            options?.pageContentHeightPxByNodeIndex?.get(tableIndex),
            options?.numberingDefinitions,
            options?.docGridLinePitchPxByNodeIndex?.get(tableIndex)
          ) ??
          measuredRowHeights.map((heightPx) =>
            normalizeMeasuredTableRowHeightPx(heightPx)
          );

      next[tableIndex] = nextHeights;
    }
  );

  return Object.keys(next).length > 0 ? next : undefined;
}

function placeCaretInsideElementDom(element: HTMLElement): void {
  const selection = window.getSelection();
  if (!selection) {
    return;
  }

  const range = document.createRange();
  range.selectNodeContents(element);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
}

function selectionOffsetsWithinElementDom(
  element: HTMLElement
): { start: number; end: number } | undefined {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return undefined;
  }

  const selectedRange = selection.getRangeAt(0);
  if (
    !element.contains(selectedRange.startContainer) ||
    !element.contains(selectedRange.endContainer)
  ) {
    return undefined;
  }

  try {
    const textLengthWithoutNumberingLabels = (range: Range): number => {
      const fragment = range.cloneContents();
      fragment
        .querySelectorAll("[data-docx-numbering-label='true']")
        .forEach((label) => {
          label.remove();
        });
      return fragment.textContent?.length ?? 0;
    };

    const startRange = document.createRange();
    startRange.setStart(element, 0);
    startRange.setEnd(selectedRange.startContainer, selectedRange.startOffset);

    const endRange = document.createRange();
    endRange.setStart(element, 0);
    endRange.setEnd(selectedRange.endContainer, selectedRange.endOffset);

    const startOffset = textLengthWithoutNumberingLabels(startRange);
    const endOffset = textLengthWithoutNumberingLabels(endRange);

    return {
      start: Math.min(startOffset, endOffset),
      end: Math.max(startOffset, endOffset),
    };
  } catch {
    return undefined;
  }
}

function setSelectionWithinElementByTextOffsetsDom(
  element: HTMLElement,
  startOffset: number,
  endOffset: number
): void {
  const selection = window.getSelection();
  if (!selection) {
    return;
  }

  const resolveDomPosition = (
    container: HTMLElement,
    targetOffset: number
  ): {
    node: Node;
    offset: number;
  } => {
    const safeOffset = Math.max(0, Math.round(targetOffset));
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    let traversed = 0;
    let currentTextNode: Node | null = walker.nextNode();

    while (currentTextNode) {
      if (
        currentTextNode instanceof Text &&
        currentTextNode.parentElement?.closest(
          "[data-docx-numbering-label='true']"
        )
      ) {
        currentTextNode = walker.nextNode();
        continue;
      }

      const textLength = currentTextNode.textContent?.length ?? 0;
      if (traversed + textLength >= safeOffset) {
        return {
          node: currentTextNode,
          offset: Math.max(0, safeOffset - traversed),
        };
      }

      traversed += textLength;
      currentTextNode = walker.nextNode();
    }

    return {
      node: container,
      offset: container.childNodes.length,
    };
  };

  try {
    const range = document.createRange();
    const normalizedStart = Math.max(0, Math.min(startOffset, endOffset));
    const normalizedEnd = Math.max(
      normalizedStart,
      Math.max(startOffset, endOffset)
    );
    const start = resolveDomPosition(element, normalizedStart);
    const end = resolveDomPosition(element, normalizedEnd);
    range.setStart(start.node, start.offset);
    range.setEnd(end.node, end.offset);
    selection.removeAllRanges();
    selection.addRange(range);
  } catch {
    placeCaretInsideElementDom(element);
  }
}

const DEFAULT_TABLE_CONTEXT_MENU_ACTIONS: DocxTableContextMenuAction[] = [
  { id: "insert-row-above", label: "Insert row above" },
  { id: "insert-row-below", label: "Insert row below" },
  { id: "insert-column-left", label: "Insert column left" },
  { id: "insert-column-right", label: "Insert column right" },
  { id: "delete-row", label: "Delete row", destructive: true },
  { id: "delete-column", label: "Delete column", destructive: true },
  { id: "delete-table", label: "Delete table", destructive: true },
];

function usesCommandKeyShortcutLabel(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }

  const navigatorWithUAData = navigator as Navigator & {
    userAgentData?: {
      platform?: string;
    };
  };
  const userAgentDataPlatform =
    typeof navigatorWithUAData.userAgentData?.platform === "string"
      ? navigatorWithUAData.userAgentData.platform
      : "";
  const platform =
    typeof navigator.platform === "string" ? navigator.platform : "";
  const userAgent =
    typeof navigator.userAgent === "string" ? navigator.userAgent : "";
  const combinedPlatform = `${userAgentDataPlatform} ${platform}`.toLowerCase();
  const normalizedUserAgent = userAgent.toLowerCase();

  return (
    /mac|iphone|ipad|ipod/.test(combinedPlatform) ||
    /macintosh|mac os x|iphone|ipad|ipod/.test(normalizedUserAgent)
  );
}

function clipboardShortcutLabel(key: string): string {
  return usesCommandKeyShortcutLabel() ? `⌘${key}` : `Ctrl+${key}`;
}

const DEFAULT_CONTEXT_MENU_CLIPBOARD_ACTIONS: DocxContextMenuAction[] = [
  { id: "cut", label: "Cut", shortcut: clipboardShortcutLabel("X") },
  { id: "copy", label: "Copy", shortcut: clipboardShortcutLabel("C") },
  { id: "paste", label: "Paste", shortcut: clipboardShortcutLabel("V") },
];

const DEFAULT_CONTEXT_MENU_IMAGE_LAYER_ACTIONS: DocxContextMenuAction[] = [
  {
    id: "image-bring-to-front",
    label: "Bring to Front",
    children: [
      { id: "image-bring-to-front", label: "Bring to Front" },
      { id: "image-bring-forward", label: "Bring Forward" },
    ],
  },
  {
    id: "image-send-to-back",
    label: "Send to Back",
    children: [
      { id: "image-send-to-back", label: "Send to Back" },
      { id: "image-send-backward", label: "Send Backward" },
    ],
  },
];

export type DocxImageWrapMode =
  | "inline"
  | "square"
  | "tight"
  | "through"
  | "topAndBottom"
  | "behindText"
  | "inFrontOfText";

export interface DocxImageWrapState {
  mode: DocxImageWrapMode;
  moveWithText: boolean;
  fixedPositionOnPage: boolean;
}

export interface DocxImageWrapMenuOption {
  actionId: DocxContextMenuActionId | (string & {});
  label: string;
  checked: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

export interface UseDocxImageWrapMenuResult {
  state: DocxImageWrapState;
  wrapOptions: DocxImageWrapMenuOption[];
  positioningOptions: DocxImageWrapMenuOption[];
  editWrapBoundaryOption: DocxImageWrapMenuOption;
  moreLayoutOptionsOption: DocxImageWrapMenuOption;
  setMode: (mode: DocxImageWrapMode) => void;
  setMoveWithText: (moveWithText: boolean) => void;
}

const DOCX_IMAGE_WRAP_MODE_ACTIONS: Array<{
  actionId: DocxContextMenuActionId;
  label: string;
  mode: DocxImageWrapMode;
  separatorBefore?: boolean;
}> = [
  {
    actionId: "image-wrap-inline",
    label: "In Line with Text",
    mode: "inline",
    separatorBefore: true,
  },
  {
    actionId: "image-wrap-square",
    label: "Square",
    mode: "square",
  },
  {
    actionId: "image-wrap-tight",
    label: "Tight",
    mode: "tight",
  },
  {
    actionId: "image-wrap-through",
    label: "Through",
    mode: "through",
  },
  {
    actionId: "image-wrap-top-and-bottom",
    label: "Top and Bottom",
    mode: "topAndBottom",
  },
  {
    actionId: "image-wrap-behind-text",
    label: "Behind Text",
    mode: "behindText",
    separatorBefore: true,
  },
  {
    actionId: "image-wrap-in-front-of-text",
    label: "In Front of Text",
    mode: "inFrontOfText",
  },
];

const WORD_IMAGE_Z_INDEX_MIN = 0;
const WORD_IMAGE_Z_INDEX_STEP = 65536;
const WORD_IMAGE_Z_INDEX_MAX = 251658240;
const WORD_IMAGE_Z_INDEX_DEFAULT = WORD_IMAGE_Z_INDEX_MAX;

export function resolveFloatingZIndex(floating: {
  behindDocument?: boolean;
  zIndex?: number;
}): number {
  const rawZIndex = Number.isFinite(floating.zIndex)
    ? (floating.zIndex as number)
    : floating.behindDocument
    ? WORD_IMAGE_Z_INDEX_MIN
    : 0;
  const normalized = rawZIndex / WORD_IMAGE_Z_INDEX_STEP;
  if (floating.behindDocument) {
    return normalized;
  }
  return WORD_IMAGE_Z_INDEX_STEP + normalized;
}

export function resolveUiActiveTextRange(
  selection: DocxEditorSelection,
  activeTextRange: DocxTextRange | undefined,
  lastInViewerActiveTextRange: DocxTextRange | undefined
): DocxTextRange | undefined {
  if (activeTextRange) {
    return activeTextRange;
  }
  if (
    lastInViewerActiveTextRange &&
    selection.kind === "paragraph" &&
    lastInViewerActiveTextRange.start.location.kind === "paragraph" &&
    selection.nodeIndex === lastInViewerActiveTextRange.start.location.nodeIndex
  ) {
    return lastInViewerActiveTextRange;
  }
  return undefined;
}

export function shouldPreservePendingRunStyleBetweenRanges(
  previousRange: DocxTextRange,
  nextRange: DocxTextRange
): boolean {
  const previous = normalizeTextRange(previousRange);
  const next = normalizeTextRange(nextRange);

  if (compareTextRangeBoundaries(previous.start, previous.end) < 0) {
    return false;
  }
  if (compareTextRangeBoundaries(next.start, next.end) < 0) {
    return false;
  }
  if (!sameParagraphLocation(previous.start.location, next.start.location)) {
    return false;
  }

  return true;
}

interface SectionColumnLayout {
  count: number;
  gapPx: number;
  widthsPx?: number[];
}

const DEFAULT_PAGE_NUMBER_START = 1;

function createDefaultEditorTableBorders(): TableBorderSet {
  return {
    top: { type: "single", sizeEighthPt: 4, color: "#d1d5db" },
    right: { type: "single", sizeEighthPt: 4, color: "#d1d5db" },
    bottom: { type: "single", sizeEighthPt: 4, color: "#d1d5db" },
    left: { type: "single", sizeEighthPt: 4, color: "#d1d5db" },
    insideH: { type: "single", sizeEighthPt: 4, color: "#d1d5db" },
    insideV: { type: "single", sizeEighthPt: 4, color: "#d1d5db" },
  };
}

const DEFAULT_TOOLBAR_BORDER_SIZE_EIGHTH_PT = 8;
const DEFAULT_TOOLBAR_BORDER_COLOR = "#000000";

function twipsToSignedPixels(twips?: number): number | undefined {
  if (!Number.isFinite(twips)) {
    return undefined;
  }

  return Math.round((twips as number) / TWIPS_PER_PIXEL);
}

function pointsToPixels(points?: number): number | undefined {
  if (!Number.isFinite(points)) {
    return undefined;
  }

  return Math.max(0, Number((((points as number) * 96) / 72).toFixed(2)));
}

type EmbeddedFontFaceDescriptor = {
  family: string;
  style: "normal" | "italic";
  weight: string;
  source: ArrayBuffer;
};

function relationshipPartNameForOoxmlPart(partName: string): string {
  const segments = partName.split("/");
  const fileName = segments.pop() ?? partName;
  const directory = segments.join("/");
  return directory.length > 0
    ? `${directory}/_rels/${fileName}.rels`
    : `_rels/${fileName}.rels`;
}

function resolveRelativeOoxmlPartName(
  basePartName: string,
  target: string
): string {
  if (/^[a-z]+:/i.test(target)) {
    return target;
  }

  const normalizedBasePartName = basePartName.replace(/^\/+/, "");
  if (target.startsWith("/")) {
    return target.replace(/^\/+/, "");
  }

  const baseSegments = normalizedBasePartName.split("/");
  baseSegments.pop();
  target.split("/").forEach((segment) => {
    if (!segment || segment === ".") {
      return;
    }

    if (segment === "..") {
      if (baseSegments.length > 0) {
        baseSegments.pop();
      }
      return;
    }

    baseSegments.push(segment);
  });

  return baseSegments.join("/");
}

function parseOoxmlRelationships(
  pkg: OoxmlPackage,
  partName: string
): Map<string, string> {
  const relationships = new Map<string, string>();
  const relationshipsXml = pkg.parts.get(
    relationshipPartNameForOoxmlPart(partName)
  )?.content;
  if (!relationshipsXml) {
    return relationships;
  }

  const relationshipPattern =
    /<Relationship\b[^>]*Id="([^"]+)"[^>]*Target="([^"]+)"[^>]*\/?>/gi;
  let match: RegExpExecArray | null;
  while ((match = relationshipPattern.exec(relationshipsXml))) {
    const relationshipId = match[1]?.trim();
    const target = match[2]?.trim();
    if (!relationshipId || !target) {
      continue;
    }

    relationships.set(
      relationshipId,
      resolveRelativeOoxmlPartName(partName, target)
    );
  }

  return relationships;
}

function deobfuscateEmbeddedFontData(
  fontData: Uint8Array,
  fontKey?: string
): ArrayBuffer {
  const output = Uint8Array.from(fontData);
  const normalizedFontKey = (fontKey ?? "").replace(/[{}-]/g, "");
  if (!/^[0-9a-f]{32}$/i.test(normalizedFontKey)) {
    return output.buffer.slice(
      output.byteOffset,
      output.byteOffset + output.byteLength
    );
  }

  const keyBytes = Uint8Array.from(
    normalizedFontKey.match(/../g)?.map((pair) => Number.parseInt(pair, 16)) ??
      []
  ).reverse();
  const xorLength = Math.min(32, output.length);
  for (let index = 0; index < xorLength; index += 1) {
    output[index] ^= keyBytes[index % keyBytes.length] ?? 0;
  }

  return output.buffer.slice(
    output.byteOffset,
    output.byteOffset + output.byteLength
  );
}

function collectEmbeddedFontFaceDescriptors(
  pkg: OoxmlPackage
): EmbeddedFontFaceDescriptor[] {
  const fontTableXml = pkg.parts.get("word/fontTable.xml")?.content;
  if (!fontTableXml) {
    return [];
  }

  const fontRelationships = parseOoxmlRelationships(pkg, "word/fontTable.xml");
  const descriptors: EmbeddedFontFaceDescriptor[] = [];
  const fontPattern =
    /<w:font\b[^>]*w:name="([^"]+)"[^>]*>([\s\S]*?)<\/w:font>/gi;
  const fontVariants = [
    { tagName: "embedRegular", style: "normal" as const, weight: "400" },
    { tagName: "embedBold", style: "normal" as const, weight: "700" },
    { tagName: "embedItalic", style: "italic" as const, weight: "400" },
    { tagName: "embedBoldItalic", style: "italic" as const, weight: "700" },
  ];

  let fontMatch: RegExpExecArray | null;
  while ((fontMatch = fontPattern.exec(fontTableXml))) {
    const family = fontMatch[1]?.trim();
    const fontXml = fontMatch[2] ?? "";
    if (!family) {
      continue;
    }

    fontVariants.forEach((variant) => {
      const tagXml =
        fontXml.match(
          new RegExp(`<w:${variant.tagName}\\b[^>]*\\/?>`, "i")
        )?.[0] ?? "";
      const relationshipId = xmlAttribute(tagXml, "r:id");
      if (!relationshipId) {
        return;
      }

      const partName = fontRelationships.get(relationshipId);
      const fontData = partName ? pkg.binaryAssets.get(partName) : undefined;
      if (!fontData) {
        return;
      }

      descriptors.push({
        family,
        style: variant.style,
        weight: variant.weight,
        source: deobfuscateEmbeddedFontData(
          fontData,
          xmlAttribute(tagXml, "w:fontKey")
        ),
      });
    });
  }

  return descriptors;
}

function parseSectionColumns(
  sectionPropertiesXml?: string
): SectionColumnLayout | undefined {
  if (!sectionPropertiesXml) {
    return undefined;
  }

  const columnsTag = sectionPropertiesXml.match(/<w:cols\b[^>]*\/?>/i)?.[0];
  if (!columnsTag) {
    return undefined;
  }

  const numberOfColumnsRaw = columnsTag.match(/w:num="([\d.]+)"/i)?.[1];
  const numberOfColumns = numberOfColumnsRaw
    ? Math.round(Number(numberOfColumnsRaw))
    : 1;
  if (!Number.isFinite(numberOfColumns) || numberOfColumns <= 1) {
    return undefined;
  }
  const columnCount = Math.max(2, numberOfColumns);

  const columnTags = [...sectionPropertiesXml.matchAll(/<w:col\b[^>]*\/>/gi)];
  const widthsTwips = columnTags
    .map((match) => Number(match[0].match(/w:w="([\d.]+)"/i)?.[1]))
    .filter((value) => Number.isFinite(value) && value > 0);
  const widthsPx = widthsTwips.map((value) =>
    Math.max(1, Math.round(twipsToPixels(value) ?? 0))
  );

  // Gap resolution: explicit space on the cols tag, then per-col space, then
  // — when every column width is declared — derived from the section body
  // width (Word keeps widths + gaps equal to the body), then Word's default.
  const colsTagSpaceRaw = columnsTag.match(/w:space="([\d.]+)"/i)?.[1];
  const firstColSpaceRaw = columnTags
    .map((match) => match[0].match(/w:space="([\d.]+)"/i)?.[1])
    .find((value) => value !== undefined);
  let columnGapTwips =
    colsTagSpaceRaw !== undefined
      ? Number(colsTagSpaceRaw)
      : firstColSpaceRaw !== undefined
      ? Number(firstColSpaceRaw)
      : undefined;
  if (columnGapTwips === undefined && widthsTwips.length === columnCount) {
    const pageWidthTwips = Number(
      sectionPropertiesXml.match(/<w:pgSz\b[^>]*w:w="([\d.]+)"/i)?.[1]
    );
    const marginTag = sectionPropertiesXml.match(/<w:pgMar\b[^>]*\/?>/i)?.[0];
    const marginLeftTwips = Number(marginTag?.match(/w:left="([\d.-]+)"/i)?.[1]);
    const marginRightTwips = Number(
      marginTag?.match(/w:right="([\d.-]+)"/i)?.[1]
    );
    if (
      Number.isFinite(pageWidthTwips) &&
      Number.isFinite(marginLeftTwips) &&
      Number.isFinite(marginRightTwips)
    ) {
      const bodyTwips = pageWidthTwips - marginLeftTwips - marginRightTwips;
      const totalWidthTwips = widthsTwips.reduce((sum, value) => sum + value, 0);
      columnGapTwips = Math.max(
        0,
        (bodyTwips - totalWidthTwips) / Math.max(1, columnCount - 1)
      );
    }
  }
  if (columnGapTwips === undefined || !Number.isFinite(columnGapTwips)) {
    columnGapTwips = 720;
  }
  const columnGapPx = twipsToPixels(columnGapTwips) ?? 24;

  return {
    count: columnCount,
    gapPx: Math.max(0, columnGapPx),
    ...(widthsPx.length === columnCount ? { widthsPx } : undefined),
  };
}

export function resolveSectionPaginationContentWidthPx(
  layout: Pick<DocumentLayoutMetrics, "pageWidthPx" | "marginsPx">,
  sectionPropertiesXml?: string
): number {
  const bodyWidthPx = Math.max(
    120,
    layout.pageWidthPx - layout.marginsPx.left - layout.marginsPx.right
  );
  const columns = parseSectionColumns(sectionPropertiesXml);
  if (!columns) {
    return bodyWidthPx;
  }

  const totalGapPx =
    Math.max(0, columns.gapPx) * Math.max(0, columns.count - 1);
  return Math.max(120, Math.round((bodyWidthPx - totalGapPx) / columns.count));
}

function parseSectionPageNumberStart(sectionPropertiesXml?: string): number {
  if (!sectionPropertiesXml) {
    return DEFAULT_PAGE_NUMBER_START;
  }

  const pageNumberTag = sectionPropertiesXml.match(
    /<w:pgNumType\b[^>]*\/?>/i
  )?.[0];
  if (!pageNumberTag) {
    return DEFAULT_PAGE_NUMBER_START;
  }

  const startRaw = pageNumberTag.match(/\bw:start="(\d+)"/i)?.[1];
  if (!startRaw) {
    return DEFAULT_PAGE_NUMBER_START;
  }

  const start = Number(startRaw);
  if (!Number.isFinite(start) || start <= 0) {
    return DEFAULT_PAGE_NUMBER_START;
  }

  return Math.max(1, Math.round(start));
}

function parseSectionPageNumberStartOverride(
  sectionPropertiesXml?: string
): number | undefined {
  if (!sectionPropertiesXml) {
    return undefined;
  }

  const pageNumberTag = sectionPropertiesXml.match(
    /<w:pgNumType\b[^>]*\/?>/i
  )?.[0];
  if (!pageNumberTag) {
    return undefined;
  }

  const startRaw = pageNumberTag.match(/\bw:start="(\d+)"/i)?.[1];
  if (!startRaw) {
    return undefined;
  }

  const start = Number(startRaw);
  if (!Number.isFinite(start) || start <= 0) {
    return undefined;
  }

  return Math.max(1, Math.round(start));
}

function parseSectionPageNumberFormat(
  sectionPropertiesXml?: string
): string | undefined {
  if (!sectionPropertiesXml) {
    return undefined;
  }

  const pageNumberTag = sectionPropertiesXml.match(
    /<w:pgNumType\b[^>]*\/?>/i
  )?.[0];
  const format = pageNumberTag?.match(/\bw:fmt="([^"]+)"/i)?.[1]?.trim();
  return format && format.length > 0 ? format : undefined;
}

function parseSectionStartType(
  sectionPropertiesXml?: string
): string | undefined {
  if (!sectionPropertiesXml) {
    return undefined;
  }

  const sectionType = sectionPropertiesXml
    .match(/<w:type\b[^>]*w:val="([^"]+)"/i)?.[1]
    ?.trim();
  return sectionType && sectionType.length > 0
    ? sectionType.toLowerCase()
    : undefined;
}

interface ResolvedDocumentSection {
  startNodeIndex: number;
  sectionPropertiesXml?: string;
  headerSections: HeaderSection[];
  footerSections: FooterSection[];
}

function normalizeSectionReferenceType(referenceType?: string): string {
  const normalized = referenceType?.trim().toLowerCase();
  return normalized && normalized.length > 0 ? normalized : "default";
}

function inheritSectionReferences<T extends HeaderSection | FooterSection>(
  sections: ResolvedDocumentSection[],
  sectionKey: "headerSections" | "footerSections"
): ResolvedDocumentSection[] {
  const inheritedByType = new Map<string, T>();

  return sections.map((section) => {
    const explicitSections = section[sectionKey] as T[];
    if (explicitSections.length > 0) {
      explicitSections.forEach((entry) => {
        inheritedByType.set(
          normalizeSectionReferenceType(entry.referenceType),
          entry
        );
      });
    }

    return {
      ...section,
      [sectionKey]: [...inheritedByType.values()],
    };
  });
}

function resolveInheritedSectionHeaderFooterReferences(
  sections: ResolvedDocumentSection[]
): ResolvedDocumentSection[] {
  if (sections.length === 0) {
    return sections;
  }

  return inheritSectionReferences(
    inheritSectionReferences(sections, "headerSections"),
    "footerSections"
  );
}

function resolveDocumentSectionsFromMetadata(
  metadata: DocModel["metadata"]
): ResolvedDocumentSection[] {
  const normalizedSections = (metadata.sections ?? [])
    .map(
      (section): ResolvedDocumentSection => ({
        startNodeIndex:
          Number.isFinite(section.startNodeIndex) &&
          (section.startNodeIndex as number) >= 0
            ? Math.round(section.startNodeIndex as number)
            : 0,
        sectionPropertiesXml: section.sectionPropertiesXml,
        headerSections: section.headerSections ?? [],
        footerSections: section.footerSections ?? [],
      })
    )
    .sort((left, right) => left.startNodeIndex - right.startNodeIndex);

  if (normalizedSections.length > 0) {
    if (normalizedSections[0].startNodeIndex > 0) {
      normalizedSections.unshift({
        startNodeIndex: 0,
        sectionPropertiesXml: normalizedSections[0].sectionPropertiesXml,
        headerSections: normalizedSections[0].headerSections,
        footerSections: normalizedSections[0].footerSections,
      });
    }
    return resolveInheritedSectionHeaderFooterReferences(normalizedSections);
  }

  return [
    {
      startNodeIndex: 0,
      sectionPropertiesXml: metadata.sectionPropertiesXml,
      headerSections: metadata.headerSections ?? [],
      footerSections: metadata.footerSections ?? [],
    },
  ];
}

interface PaginationSectionMetrics {
  startNodeIndex: number;
  pageContentWidthPx: number;
  pageContentHeightPx: number;
  pageContentHeightMultiplier?: number;
  docGridLinePitchPx?: number;
}

function paragraphHasHeaderFooterReserveRelevantContent(
  paragraph: ParagraphNode
): boolean {
  if (paragraphHasVisibleText(paragraph) || paragraphHasFormField(paragraph)) {
    return true;
  }

  if (!paragraphHasImage(paragraph)) {
    return false;
  }

  return paragraph.children.some((child) => {
    if (child.type !== "image") {
      return false;
    }

    // Inline/wrapped images are part of the header/footer flow and should keep
    // reserve. Absolute behind-text anchors (watermarks/background art) should not.
    if (!child.floating || shouldRenderWrappedFloatingImage(child)) {
      return true;
    }

    if (!shouldRenderAbsoluteFloatingImage(child)) {
      return true;
    }

    return shouldReserveHeaderFooterFloatingImageSpace(child);
  });
}

function sectionHasVisibleHeaderContent(
  section: ResolvedDocumentSection
): boolean {
  const headerSections = section.headerSections ?? [];
  return headerSections.some((headerSection) =>
    (headerSection.nodes ?? []).some(
      (node) =>
        node.type === "table" ||
        paragraphHasHeaderFooterReserveRelevantContent(node)
    )
  );
}

function sectionHasVisibleFooterContent(
  section: ResolvedDocumentSection
): boolean {
  const footerSections = section.footerSections ?? [];
  return footerSections.some((footerSection) =>
    (footerSection.nodes ?? []).some(
      (node) =>
        node.type === "table" ||
        paragraphHasHeaderFooterReserveRelevantContent(node)
    )
  );
}

function resolveHeaderFooterAbsoluteFloatingTopPx(
  image: ImageRunNode,
  layout: Pick<DocumentLayoutMetrics, "marginsPx">
): number | undefined {
  if (!shouldRenderAbsoluteFloatingImage(image) || !image.floating) {
    return undefined;
  }

  const floating = image.floating;
  const verticalRelativeTo = floating.verticalRelativeTo?.trim().toLowerCase();
  if (!Number.isFinite(floating.yPx)) {
    return undefined;
  }

  if (verticalRelativeTo === "page") {
    return Math.round(floating.yPx as number);
  }

  if (verticalRelativeTo === "margin") {
    return Math.round((floating.yPx as number) + layout.marginsPx.top);
  }

  return undefined;
}

function shouldReserveHeaderFooterFloatingImageSpace(
  image: ImageRunNode
): boolean {
  return (
    shouldRenderAbsoluteFloatingImage(image) &&
    Boolean(image.floating) &&
    image.floating?.behindDocument !== true
  );
}

function resolveFooterParagraphFloatingBoundaryTopPx(
  paragraph: ParagraphNode,
  layout: Pick<DocumentLayoutMetrics, "marginsPx">
): number | undefined {
  if (!paragraphHasImage(paragraph)) {
    return undefined;
  }

  let boundaryTopPx: number | undefined;

  paragraph.children.forEach((child) => {
    if (
      child.type !== "image" ||
      !child.floating ||
      !shouldReserveHeaderFooterFloatingImageSpace(child)
    ) {
      return;
    }

    const imageTopPx = resolveHeaderFooterAbsoluteFloatingTopPx(child, layout);
    if (!Number.isFinite(imageTopPx)) {
      return;
    }

    const distTPx = Math.max(0, Math.round(child.floating.distTPx ?? 0));
    const candidateTopPx = Math.max(
      0,
      Math.round((imageTopPx as number) - distTPx)
    );
    boundaryTopPx =
      boundaryTopPx === undefined
        ? candidateTopPx
        : Math.min(boundaryTopPx, candidateTopPx);
  });

  return boundaryTopPx;
}

function resolveFooterNodesFloatingBoundaryTopPx(
  footerNodes: DocModel["nodes"],
  layout: Pick<DocumentLayoutMetrics, "marginsPx">
): number | undefined {
  let boundaryTopPx: number | undefined;

  footerNodes.forEach((node) => {
    if (node.type !== "paragraph") {
      return;
    }

    const paragraphBoundaryTopPx = resolveFooterParagraphFloatingBoundaryTopPx(
      node,
      layout
    );
    if (!Number.isFinite(paragraphBoundaryTopPx)) {
      return;
    }

    boundaryTopPx =
      boundaryTopPx === undefined
        ? Math.round(paragraphBoundaryTopPx as number)
        : Math.min(boundaryTopPx, Math.round(paragraphBoundaryTopPx as number));
  });

  return boundaryTopPx;
}

function estimateHeaderFooterParagraphFloatingReservePx(
  paragraph: ParagraphNode,
  layout: Pick<
    DocumentLayoutMetrics,
    "pageWidthPx" | "pageHeightPx" | "marginsPx"
  > &
    Partial<Pick<DocumentLayoutMetrics, "headerDistancePx">>,
  region: "header" | "footer"
): number {
  if (!paragraphHasImage(paragraph)) {
    return 0;
  }

  const nominalBodyTopPx = layout.marginsPx.top;
  const nominalBodyBottomPx = layout.pageHeightPx - layout.marginsPx.bottom;

  if (region === "footer") {
    const floatingBoundaryTopPx = resolveFooterParagraphFloatingBoundaryTopPx(
      paragraph,
      layout
    );
    return Number.isFinite(floatingBoundaryTopPx)
      ? Math.max(
          0,
          nominalBodyBottomPx -
            Math.round(floatingBoundaryTopPx as number) +
            FLOATING_HEADER_FOOTER_CLEARANCE_BUFFER_PX
        )
      : 0;
  }

  return paragraph.children.reduce((largest, child) => {
    if (child.type !== "image" || !child.floating) {
      return largest;
    }

    const floating = child.floating;
    const imageHeightPx =
      Number.isFinite(child.heightPx) && (child.heightPx as number) > 0
        ? Math.round(child.heightPx as number)
        : Number.isFinite(child.widthPx) && (child.widthPx as number) > 0
        ? Math.round(child.widthPx as number)
        : MIN_PARAGRAPH_LINE_HEIGHT_PX;
    const distTPx = Math.max(0, Math.round(floating.distTPx ?? 0));
    const distBPx = Math.max(0, Math.round(floating.distBPx ?? 0));

    let topPx = resolveHeaderFooterAbsoluteFloatingTopPx(child, layout);
    if (!Number.isFinite(topPx)) {
      const verticalRelativeTo = floating.verticalRelativeTo
        ?.trim()
        .toLowerCase();
      const isParagraphRelativeAnchor =
        verticalRelativeTo === undefined ||
        verticalRelativeTo === "" ||
        verticalRelativeTo === "paragraph" ||
        verticalRelativeTo === "line";
      if (region === "header" && isParagraphRelativeAnchor) {
        const headerAnchorOriginPx = Math.max(
          0,
          Math.round(layout.headerDistancePx ?? layout.marginsPx.top)
        );
        topPx = headerAnchorOriginPx + Math.round(floating.yPx ?? 0);
      }
    }
    if (!Number.isFinite(topPx)) {
      return largest;
    }

    const resolvedTopPx = Math.round(topPx as number);
    const resolvedBottomPx = resolvedTopPx + imageHeightPx + distTPx + distBPx;
    const reserveBehindDocHeaderBand =
      region === "header" &&
      floating.behindDocument === true &&
      Math.max(1, Math.round(child.widthPx ?? 0)) >=
        Math.round(layout.pageWidthPx * 0.5) &&
      resolvedTopPx <= nominalBodyTopPx + 192 &&
      resolvedBottomPx > nominalBodyTopPx;
    if (
      !shouldReserveHeaderFooterFloatingImageSpace(child) &&
      !reserveBehindDocHeaderBand
    ) {
      return largest;
    }

    const bodyOverlapPx = Math.max(0, resolvedBottomPx - nominalBodyTopPx);
    if (bodyOverlapPx <= 0) {
      return largest;
    }

    return Math.max(
      largest,
      bodyOverlapPx + FLOATING_HEADER_FOOTER_CLEARANCE_BUFFER_PX
    );
  }, 0);
}

export function resolveHeaderPaginationReservePx(
  headerSections: HeaderSection[],
  layout: Pick<
    DocumentLayoutMetrics,
    | "pageWidthPx"
    | "pageHeightPx"
    | "marginsPx"
    | "headerDistancePx"
    | "docGridLinePitchPx"
  >
): number {
  const visibleHeaderSections = (headerSections ?? []).filter((headerSection) =>
    (headerSection.nodes ?? []).some(
      (node) =>
        node.type === "table" ||
        paragraphHasHeaderFooterReserveRelevantContent(node)
    )
  );
  if (visibleHeaderSections.length === 0) {
    return 0;
  }

  const availableWidthPx = Math.max(
    24,
    layout.pageWidthPx - layout.marginsPx.left - layout.marginsPx.right
  );
  const estimatedHeaderHeightPx = visibleHeaderSections.reduce(
    (largestHeightPx, headerSection) => {
      const visibleNodes = (headerSection.nodes ?? []).filter(
        (node) =>
          node.type === "table" ||
          paragraphHasHeaderFooterReserveRelevantContent(node)
      );
      if (visibleNodes.length === 0) {
        return largestHeightPx;
      }

      const nodeHeightsPx = visibleNodes.reduce((sum, node) => {
        const floatingReservePx =
          node.type === "paragraph"
            ? estimateHeaderFooterParagraphFloatingReservePx(
                node,
                layout,
                "header"
              )
            : 0;
        return (
          sum +
          Math.max(
            estimateDocNodeHeightPx(
              node,
              availableWidthPx,
              undefined,
              layout.docGridLinePitchPx
            ),
            floatingReservePx
          )
        );
      }, 0);
      const interParagraphGapPx = Math.max(0, visibleNodes.length - 1) * 8;
      return Math.max(
        largestHeightPx,
        Math.round(nodeHeightsPx + interParagraphGapPx)
      );
    },
    0
  );

  if (estimatedHeaderHeightPx <= 0) {
    return 0;
  }

  const headerBodyOverlapPx =
    estimatedHeaderHeightPx + layout.headerDistancePx - layout.marginsPx.top;
  return Math.max(0, Math.round(headerBodyOverlapPx));
}

export function resolveFooterPaginationReservePx(
  footerSections: FooterSection[],
  layout: Pick<
    DocumentLayoutMetrics,
    | "pageWidthPx"
    | "pageHeightPx"
    | "marginsPx"
    | "footerDistancePx"
    | "docGridLinePitchPx"
  >
): number {
  const visibleFooterSections = (footerSections ?? []).filter((footerSection) =>
    (footerSection.nodes ?? []).some(
      (node) =>
        node.type === "table" ||
        paragraphHasHeaderFooterReserveRelevantContent(node)
    )
  );
  if (visibleFooterSections.length === 0) {
    return 0;
  }

  const availableWidthPx = Math.max(
    24,
    layout.pageWidthPx - layout.marginsPx.left - layout.marginsPx.right
  );
  const estimatedFooterHeightPx = visibleFooterSections.reduce(
    (largestHeightPx, footerSection) => {
      const visibleNodes = (footerSection.nodes ?? []).filter(
        (node) =>
          node.type === "table" ||
          paragraphHasHeaderFooterReserveRelevantContent(node)
      );
      if (visibleNodes.length === 0) {
        return largestHeightPx;
      }

      const nodeHeightsPx = visibleNodes.reduce((sum, node) => {
        const floatingReservePx =
          node.type === "paragraph"
            ? estimateHeaderFooterParagraphFloatingReservePx(
                node,
                layout,
                "footer"
              )
            : 0;
        return (
          sum +
          Math.max(
            estimateDocNodeHeightPx(
              node,
              availableWidthPx,
              undefined,
              layout.docGridLinePitchPx
            ),
            floatingReservePx
          )
        );
      }, 0);
      const interParagraphGapPx = Math.max(0, visibleNodes.length - 1) * 8;
      return Math.max(
        largestHeightPx,
        Math.round(nodeHeightsPx + interParagraphGapPx)
      );
    },
    0
  );

  if (estimatedFooterHeightPx <= 0) {
    return 0;
  }

  const nominalBodyBottomPx = layout.pageHeightPx - layout.marginsPx.bottom;
  const floatingFooterBoundaryTopPx = visibleFooterSections.reduce<
    number | undefined
  >((smallestTopPx, footerSection) => {
    const sectionFloatingTopPx = resolveFooterNodesFloatingBoundaryTopPx(
      footerSection.nodes ?? [],
      layout
    );
    if (!Number.isFinite(sectionFloatingTopPx)) {
      return smallestTopPx;
    }

    return smallestTopPx === undefined
      ? Math.round(sectionFloatingTopPx as number)
      : Math.min(smallestTopPx, Math.round(sectionFloatingTopPx as number));
  }, undefined);
  const hasFloatingFooterBodyIntrusionRisk = visibleFooterSections.some(
    (footerSection) =>
      (footerSection.nodes ?? []).some(
        (node) =>
          node.type === "paragraph" &&
          estimateHeaderFooterParagraphFloatingReservePx(
            node,
            layout,
            "footer"
          ) > 0
      )
  );

  const footerBodyOverlapPx =
    estimatedFooterHeightPx + layout.footerDistancePx - layout.marginsPx.bottom;
  const explicitFooterBoundaryReservePx = Math.max(
    0,
    Math.round(footerBodyOverlapPx + UNOVERLAPPED_FOOTER_MIN_CLEARANCE_PX)
  );
  const floatingFooterBoundaryReservePx = Number.isFinite(
    floatingFooterBoundaryTopPx
  )
    ? Math.max(
        0,
        Math.round(
          nominalBodyBottomPx -
            Math.round(floatingFooterBoundaryTopPx as number) +
            UNOVERLAPPED_FOOTER_MIN_CLEARANCE_PX
        )
      )
    : 0;

  const resolvedReservePx = hasFloatingFooterBodyIntrusionRisk
    ? Math.max(
        explicitFooterBoundaryReservePx,
        floatingFooterBoundaryReservePx,
        FLOATING_FOOTER_BASELINE_CLEARANCE_RESERVE_PX
      )
    : Math.max(
        explicitFooterBoundaryReservePx,
        floatingFooterBoundaryReservePx
      );

  return Math.max(
    estimatedFooterHeightPx > 0
      ? MIN_VISIBLE_FLOW_FOOTER_PAGINATION_RESERVE_PX
      : 0,
    resolvedReservePx
  );
}

function resolveMeasuredPageContentHeightDiagnostics(params: {
  pageLayout: Pick<
    DocumentLayoutMetrics,
    "pageHeightPx" | "marginsPx" | "footerDistancePx"
  >;
  fallbackHeightPx: number;
  headerHeightPx: number;
  currentMeasuredHeightPx?: number;
  bodyTopPx?: number;
  bodyRenderedBottomPx?: number;
  footerTopPx?: number;
  skipBodyBottomAdjustment?: boolean;
}): {
  heightPx: number;
  bodyOverrunsFooter: boolean;
} {
  const {
    pageLayout,
    fallbackHeightPx,
    headerHeightPx,
    currentMeasuredHeightPx,
    bodyTopPx,
    bodyRenderedBottomPx,
    footerTopPx,
    skipBodyBottomAdjustment = false,
  } = params;
  const effectiveBodyRenderedBottomPx = skipBodyBottomAdjustment
    ? undefined
    : bodyRenderedBottomPx;
  const effectiveCurrentMeasuredHeightPx = skipBodyBottomAdjustment
    ? undefined
    : currentMeasuredHeightPx;

  const nominalBodyBottomPx =
    pageLayout.pageHeightPx - pageLayout.marginsPx.bottom;
  const footerOverlapPx = Number.isFinite(footerTopPx)
    ? Math.max(0, Math.round(nominalBodyBottomPx - (footerTopPx as number)))
    : 0;
  const allowedBodyBottomPx = nominalBodyBottomPx - footerOverlapPx;
  const hardFooterBottomLimitPx =
    Number.isFinite(footerTopPx) && footerOverlapPx === 0
      ? Math.round(
          (footerTopPx as number) - UNOVERLAPPED_FOOTER_MIN_CLEARANCE_PX
        )
      : undefined;
  const guardedAllowedBodyBottomPx = Number.isFinite(hardFooterBottomLimitPx)
    ? Math.min(allowedBodyBottomPx, hardFooterBottomLimitPx as number)
    : allowedBodyBottomPx;
  const renderedBodyOverrunPx = Number.isFinite(effectiveBodyRenderedBottomPx)
    ? Math.max(
        0,
        Math.round(
          (effectiveBodyRenderedBottomPx as number) - guardedAllowedBodyBottomPx
        )
      )
    : 0;
  const measuredBodyToFooterGapPx = Number.isFinite(
    effectiveBodyRenderedBottomPx
  )
    ? Math.max(
        0,
        Math.round(
          guardedAllowedBodyBottomPx - (effectiveBodyRenderedBottomPx as number)
        )
      )
    : undefined;
  const measuredFooterClearanceBufferPx =
    Number.isFinite(footerTopPx) && Number.isFinite(measuredBodyToFooterGapPx)
      ? Math.max(
          0,
          MEASURED_PAGE_FOOTER_CLEARANCE_BUFFER_PX -
            (measuredBodyToFooterGapPx as number)
        )
      : 0;
  const correctedAllowedBodyBottomPx = Math.max(
    0,
    guardedAllowedBodyBottomPx - measuredFooterClearanceBufferPx
  );
  const iterativeMeasuredHeightPx =
    renderedBodyOverrunPx > 0 &&
    !Number.isFinite(bodyTopPx) &&
    Number.isFinite(effectiveCurrentMeasuredHeightPx) &&
    (effectiveCurrentMeasuredHeightPx as number) > 0
      ? Math.max(
          120,
          Math.round(
            (effectiveCurrentMeasuredHeightPx as number) -
              renderedBodyOverrunPx -
              measuredFooterClearanceBufferPx
          )
        )
      : undefined;
  const bodyOverrunsFooter = renderedBodyOverrunPx > 0;

  if (
    Number.isFinite(bodyTopPx) &&
    correctedAllowedBodyBottomPx > (bodyTopPx as number)
  ) {
    const measuredHeaderClearanceBufferPx =
      headerHeightPx > 0 ? MEASURED_PAGE_HEADER_CLEARANCE_BUFFER_PX : 0;
    const correctedHeightPx = Math.max(
      120,
      Math.round(correctedAllowedBodyBottomPx - (bodyTopPx as number))
    );
    return {
      heightPx: Number.isFinite(iterativeMeasuredHeightPx)
        ? Math.min(
            Math.max(120, correctedHeightPx - measuredHeaderClearanceBufferPx),
            Math.max(
              120,
              (iterativeMeasuredHeightPx as number) -
                measuredHeaderClearanceBufferPx
            )
          )
        : Math.max(120, correctedHeightPx - measuredHeaderClearanceBufferPx),
      bodyOverrunsFooter,
    };
  }

  const measuredHeaderClearanceBufferPx =
    headerHeightPx > 0 ? MEASURED_PAGE_HEADER_CLEARANCE_BUFFER_PX : 0;
  const correctedFallbackHeightPx = Math.max(
    120,
    fallbackHeightPx -
      headerHeightPx -
      measuredHeaderClearanceBufferPx -
      footerOverlapPx -
      renderedBodyOverrunPx -
      measuredFooterClearanceBufferPx
  );
  return {
    heightPx: Number.isFinite(iterativeMeasuredHeightPx)
      ? Math.min(correctedFallbackHeightPx, iterativeMeasuredHeightPx as number)
      : correctedFallbackHeightPx,
    bodyOverrunsFooter,
  };
}

export function resolveMeasuredPageContentHeightPx(params: {
  pageLayout: Pick<
    DocumentLayoutMetrics,
    "pageHeightPx" | "marginsPx" | "footerDistancePx"
  >;
  fallbackHeightPx: number;
  headerHeightPx: number;
  currentMeasuredHeightPx?: number;
  bodyTopPx?: number;
  bodyRenderedBottomPx?: number;
  footerTopPx?: number;
  skipBodyBottomAdjustment?: boolean;
}): number {
  return resolveMeasuredPageContentHeightDiagnostics(params).heightPx;
}

export function resolveMeasuredBodyRenderedBottomPx(
  descendants: Array<{
    bottomPx: number;
    widthPx: number;
    heightPx: number;
    ignore?: boolean;
  }>
): number | undefined {
  let visualBottomPx: number | undefined;

  descendants.forEach((descendant) => {
    if (descendant.ignore) {
      return;
    }
    if (!Number.isFinite(descendant.bottomPx)) {
      return;
    }
    if (descendant.widthPx <= 0 && descendant.heightPx <= 0) {
      return;
    }

    visualBottomPx =
      visualBottomPx === undefined
        ? descendant.bottomPx
        : Math.max(visualBottomPx, descendant.bottomPx);
  });

  return visualBottomPx;
}

export function stabilizeMeasuredPageContentHeights(
  current: number[],
  next: number[],
  options?: {
    currentPageIdentityKeys?: string[];
    nextPageIdentityKeys?: string[];
  }
): number[] {
  return next.map((heightPx, pageIndex) => {
    const roundedNextHeightPx = Math.round(heightPx);
    const currentHeightPx = current[pageIndex];
    const currentPageIdentityKey =
      options?.currentPageIdentityKeys?.[pageIndex];
    const nextPageIdentityKey = options?.nextPageIdentityKeys?.[pageIndex];
    const canPreserveConservativeHeight =
      currentPageIdentityKey === undefined ||
      nextPageIdentityKey === undefined ||
      currentPageIdentityKey === nextPageIdentityKey;
    return Number.isFinite(currentHeightPx)
      ? canPreserveConservativeHeight
        ? Math.min(Math.round(currentHeightPx as number), roundedNextHeightPx)
        : roundedNextHeightPx
      : roundedNextHeightPx;
  });
}

function documentPageNodeSegmentIdentityKey(
  segment: DocumentPageNodeSegment
): string {
  const tableRowRangeKey = segment.tableRowRange
    ? `${segment.tableRowRange.startRowIndex}-${segment.tableRowRange.endRowIndex}`
    : "none";
  const tableRowSliceKey = segment.tableRowSlice
    ? `${segment.tableRowSlice.rowIndex}-${Math.round(
        segment.tableRowSlice.startOffsetPx
      )}-${Math.round(segment.tableRowSlice.sliceHeightPx)}`
    : "none";
  const paragraphLineRangeKey = segment.paragraphLineRange
    ? `${segment.paragraphLineRange.startLineIndex}-${segment.paragraphLineRange.endLineIndex}-${segment.paragraphLineRange.totalLineCount}`
    : "none";

  return `${segment.nodeIndex}|${tableRowRangeKey}|${tableRowSliceKey}|${paragraphLineRangeKey}`;
}

function documentPageNodeSegmentsIdentityKey(
  pageSegments: DocumentPageNodeSegment[]
): string {
  return pageSegments.map(documentPageNodeSegmentIdentityKey).join("::");
}

function buildPaginationSectionMetrics(
  sections: ResolvedDocumentSection[],
  fallbackLayout: DocumentLayoutMetrics
): PaginationSectionMetrics[] {
  const fallbackWidthPx =
    resolveSectionPaginationContentWidthPx(fallbackLayout);
  const fallbackHeightPx = Math.max(
    120,
    fallbackLayout.pageHeightPx -
      fallbackLayout.marginsPx.top -
      fallbackLayout.marginsPx.bottom
  );

  if (sections.length === 0) {
    return [
      {
        startNodeIndex: 0,
        pageContentWidthPx: fallbackWidthPx,
        pageContentHeightPx: fallbackHeightPx,
        pageContentHeightMultiplier: 1,
      },
    ];
  }

  return sections
    .map((section) => {
      const layout = parseSectionLayout(section.sectionPropertiesXml);
      const sectionColumns = parseSectionColumns(section.sectionPropertiesXml);
      const pageContentHeightMultiplier = Math.max(
        1,
        sectionColumns?.count ?? 1
      );
      const hasHeaderContent = sectionHasVisibleHeaderContent(section);
      const hasFooterContent = sectionHasVisibleFooterContent(section);
      const headerPaginationReservePx = hasHeaderContent
        ? resolveHeaderPaginationReservePx(section.headerSections ?? [], layout)
        : 0;
      const footerPaginationReservePx = hasFooterContent
        ? resolveFooterPaginationReservePx(section.footerSections ?? [], layout)
        : 0;
      return {
        startNodeIndex: Math.max(0, Math.round(section.startNodeIndex)),
        pageContentWidthPx: resolveSectionPaginationContentWidthPx(
          layout,
          section.sectionPropertiesXml
        ),
        pageContentHeightPx: Math.max(
          120,
          (layout.pageHeightPx -
            layout.marginsPx.top -
            layout.marginsPx.bottom -
            headerPaginationReservePx -
            footerPaginationReservePx) *
            pageContentHeightMultiplier
        ),
        pageContentHeightMultiplier,
        docGridLinePitchPx: layout.docGridLinePitchPx,
      };
    })
    .sort((left, right) => left.startNodeIndex - right.startNodeIndex);
}

function resolvePaginationSectionMetricsIndexForNodeIndex(
  metricsBySection: PaginationSectionMetrics[],
  nodeIndex: number,
  previousSectionIndex: number
): number {
  if (metricsBySection.length === 0) {
    return 0;
  }

  const safePrevious = Math.max(
    0,
    Math.min(previousSectionIndex, metricsBySection.length - 1)
  );
  let sectionIndex = safePrevious;
  if (nodeIndex < metricsBySection[sectionIndex].startNodeIndex) {
    sectionIndex = 0;
  }

  while (
    sectionIndex + 1 < metricsBySection.length &&
    metricsBySection[sectionIndex + 1].startNodeIndex <= nodeIndex
  ) {
    sectionIndex += 1;
  }

  return sectionIndex;
}

function scalePaginationSectionMetricsHeights(
  metricsBySection: PaginationSectionMetrics[],
  heightScale: number
): PaginationSectionMetrics[] {
  if (!Number.isFinite(heightScale) || Math.abs(heightScale - 1) < 0.001) {
    return metricsBySection;
  }

  return metricsBySection.map((metrics) => ({
    ...metrics,
    pageContentHeightPx: Math.max(
      120,
      Math.round(metrics.pageContentHeightPx * heightScale)
    ),
  }));
}

function scaleMeasuredPageContentHeights(
  measuredPageContentHeightsPxByPageIndex: number[] | undefined,
  heightScale: number
): number[] | undefined {
  if (
    !measuredPageContentHeightsPxByPageIndex ||
    measuredPageContentHeightsPxByPageIndex.length === 0
  ) {
    return measuredPageContentHeightsPxByPageIndex;
  }

  if (!Number.isFinite(heightScale) || Math.abs(heightScale - 1) < 0.001) {
    return measuredPageContentHeightsPxByPageIndex;
  }

  return measuredPageContentHeightsPxByPageIndex.map((heightPx) =>
    Math.max(120, Math.round(heightPx * heightScale))
  );
}

export function resolvePageContentHeightPxForPageSegments(
  pageSegments: DocumentPageNodeSegment[],
  pageIndex: number,
  defaultPageContentHeightPx: number,
  metricsBySection: PaginationSectionMetrics[],
  measuredPageContentHeightsPxByPageIndex?: number[],
  measuredPageContentIdentityKeysByPageIndex?: string[],
  pageIdentityKey?: string
): number {
  const pageContainsOnlySplitParagraphSegments =
    documentPageContainsOnlySplitParagraphSegments(pageSegments);
  const measuredHeightPx = measuredPageContentHeightsPxByPageIndex?.[pageIndex];
  const measuredHeightMatchesCurrentPage =
    pageIdentityKey === undefined ||
    measuredPageContentIdentityKeysByPageIndex?.[pageIndex] === undefined ||
    measuredPageContentIdentityKeysByPageIndex?.[pageIndex] === pageIdentityKey;
  if (
    Number.isFinite(measuredHeightPx) &&
    measuredHeightMatchesCurrentPage &&
    !pageContainsOnlySplitParagraphSegments
  ) {
    return Math.max(120, Math.round(measuredHeightPx as number));
  }

  const firstNodeIndex = pageSegments[0]?.nodeIndex ?? 0;
  const metricsIndex = resolvePaginationSectionMetricsIndexForNodeIndex(
    metricsBySection,
    firstNodeIndex,
    0
  );

  return Math.max(
    120,
    Math.round(
      metricsBySection[metricsIndex]?.pageContentHeightPx ??
        defaultPageContentHeightPx
    )
  );
}

export function resolveRenderPageContentHeightPxForPageSegments(params: {
  pageSegments: DocumentPageNodeSegment[];
  pageIndex: number;
  defaultPageContentHeightPx: number;
  metricsBySection: PaginationSectionMetrics[];
  measuredPageContentHeightsPxByPageIndex?: number[];
  measuredPageContentIdentityKeysByPageIndex?: string[];
  pageIdentityKey?: string;
  useMeasuredPageContentHeights?: boolean;
  pageContentHeightScale?: number;
}): number {
  const firstNodeIndex = params.pageSegments[0]?.nodeIndex ?? 0;
  const metricsIndex = resolvePaginationSectionMetricsIndexForNodeIndex(
    params.metricsBySection,
    firstNodeIndex,
    0
  );
  const sectionHeightMultiplier = Math.max(
    1,
    Math.round(
      params.metricsBySection[metricsIndex]?.pageContentHeightMultiplier ?? 1
    )
  );
  const pageContainsOnlySplitParagraphSegments =
    documentPageContainsOnlySplitParagraphSegments(params.pageSegments);
  const measuredHeightPx =
    params.useMeasuredPageContentHeights === false
      ? undefined
      : params.measuredPageContentHeightsPxByPageIndex?.[params.pageIndex];
  const measuredHeightMatchesCurrentPage =
    params.pageIdentityKey === undefined ||
    params.measuredPageContentIdentityKeysByPageIndex?.[params.pageIndex] ===
      undefined ||
    params.measuredPageContentIdentityKeysByPageIndex?.[params.pageIndex] ===
      params.pageIdentityKey;
  const usesMeasuredVisualHeight =
    Number.isFinite(measuredHeightPx) &&
    measuredHeightMatchesCurrentPage &&
    !pageContainsOnlySplitParagraphSegments;
  let resolvedHeightPx = resolvePageContentHeightPxForPageSegments(
    params.pageSegments,
    params.pageIndex,
    params.defaultPageContentHeightPx,
    params.metricsBySection,
    params.useMeasuredPageContentHeights === false
      ? undefined
      : params.measuredPageContentHeightsPxByPageIndex,
    params.useMeasuredPageContentHeights === false
      ? undefined
      : params.measuredPageContentIdentityKeysByPageIndex,
    params.pageIdentityKey
  );
  if (usesMeasuredVisualHeight || sectionHeightMultiplier <= 1) {
    return resolvedHeightPx;
  }

  return Math.max(120, Math.round(resolvedHeightPx / sectionHeightMultiplier));
}

export function documentPageContainsOnlySplitParagraphSegments(
  pageSegments: DocumentPageNodeSegment[]
): boolean {
  return (
    pageSegments.length > 0 &&
    pageSegments.every(
      (segment) =>
        !segment.tableRowRange &&
        paragraphSegmentHasPartialLineRange(segment.paragraphLineRange)
    )
  );
}

function estimateDocumentNoteSectionHeightPx(
  notes: DocumentNoteDefinition[],
  pageContentWidthPx: number,
  numberingDefinitions?: NumberingDefinitionSet
): number {
  if (notes.length === 0) {
    return 0;
  }

  let totalHeightPx = 19;
  notes.forEach((note, noteIndex) => {
    if (noteIndex > 0) {
      totalHeightPx += 6;
    }

    const noteParagraphs =
      note.nodes?.filter(
        (node): node is ParagraphNode => node.type === "paragraph"
      ) ?? [];
    if (noteParagraphs.length === 0) {
      totalHeightPx += Math.round(12 * 1.35);
      return;
    }

    noteParagraphs.forEach((paragraph, paragraphIndex) => {
      if (paragraphIndex > 0) {
        totalHeightPx += 4;
      }
      totalHeightPx += estimateParagraphHeightPx(
        paragraph,
        pageContentWidthPx,
        numberingDefinitions
      );
    });
  });

  return Math.max(0, totalHeightPx);
}

function collectReferencedFootnotesForPageSegments(
  model: DocModel,
  pageSegments: DocumentPageNodeSegment[],
  footnotesById: Map<number, DocumentNoteDefinition>
): DocumentNoteDefinition[] {
  const referencedIds: number[] = [];
  const seen = new Set<number>();

  pageSegments.forEach((segment) => {
    const node = model.nodes[segment.nodeIndex];
    if (!node) {
      return;
    }

    nodeReferencedNoteIds(
      node,
      "footnote",
      segment.tableRowRange,
      segment.paragraphLineRange
    ).forEach((referenceId) => {
      if (seen.has(referenceId)) {
        return;
      }
      seen.add(referenceId);
      referencedIds.push(referenceId);
    });
  });

  return referencedIds
    .map((referenceId) => footnotesById.get(referenceId))
    .filter((note): note is DocumentNoteDefinition => Boolean(note));
}

function applyEstimatedFootnoteReserveToPages(
  model: DocModel,
  pages: DocumentPageNodeSegment[][],
  defaultPageContentHeightPx: number,
  pageContentWidthPx: number,
  metricsBySection: PaginationSectionMetrics[],
  numberingDefinitions: NumberingDefinitionSet | undefined,
  footnotes: DocumentNoteDefinition[],
  buildPages: (
    pageContentHeightsOverride?: number[]
  ) => DocumentPageNodeSegment[][]
): DocumentPageNodeSegment[][] {
  if (pages.length === 0 || footnotes.length === 0) {
    return pages;
  }

  const footnotesById = new Map(footnotes.map((note) => [note.id, note]));
  let currentPages = pages;

  for (let iteration = 0; iteration < 3; iteration += 1) {
    const reserveOverrides = currentPages.map((pageSegments, pageIndex) => {
      const pageFootnotes = collectReferencedFootnotesForPageSegments(
        model,
        pageSegments,
        footnotesById
      );
      const baseHeightPx = resolvePageContentHeightPxForPageSegments(
        pageSegments,
        pageIndex,
        defaultPageContentHeightPx,
        metricsBySection
      );
      if (pageFootnotes.length === 0) {
        return baseHeightPx;
      }

      const reservePx = estimateDocumentNoteSectionHeightPx(
        pageFootnotes,
        pageContentWidthPx,
        numberingDefinitions
      );
      return Math.max(120, baseHeightPx - reservePx);
    });

    const nextPages = buildPages(reserveOverrides);
    const stable =
      nextPages.length === currentPages.length &&
      nextPages.every((pageSegments, pageIndex) => {
        const previousSegments = currentPages[pageIndex] ?? [];
        if (pageSegments.length !== previousSegments.length) {
          return false;
        }
        return pageSegments.every((segment, segmentIndex) => {
          const previous = previousSegments[segmentIndex];
          return (
            previous?.nodeIndex === segment.nodeIndex &&
            previous?.tableRowRange?.startRowIndex ===
              segment.tableRowRange?.startRowIndex &&
            previous?.tableRowRange?.endRowIndex ===
              segment.tableRowRange?.endRowIndex &&
            previous?.paragraphLineRange?.startLineIndex ===
              segment.paragraphLineRange?.startLineIndex &&
            previous?.paragraphLineRange?.endLineIndex ===
              segment.paragraphLineRange?.endLineIndex
          );
        });
      });

    currentPages = nextPages;
    if (stable) {
      break;
    }
  }

  return currentPages;
}

export type DocxListType = "unordered" | "ordered";

export type DocxEditorSelection =
  | {
      kind: "paragraph";
      nodeIndex: number;
    }
  | {
      kind: "table-cell";
      tableIndex: number;
      rowIndex: number;
      cellIndex: number;
    };

interface DocxTableCellLocation {
  tableIndex: number;
  rowIndex: number;
  cellIndex: number;
}

interface DocxTableCellSelectionRange {
  tableIndex: number;
  anchorRowIndex: number;
  anchorCellIndex: number;
  focusRowIndex: number;
  focusCellIndex: number;
}

interface ParagraphLocationInBody {
  kind: "paragraph";
  nodeIndex: number;
}

interface ParagraphLocationInCell {
  kind: "table-cell";
  tableIndex: number;
  rowIndex: number;
  cellIndex: number;
  paragraphIndex: number;
}

export type DocxTextRangeLocation =
  | ParagraphLocationInBody
  | ParagraphLocationInCell;

export interface DocxTextRangeBoundary {
  location: DocxTextRangeLocation;
  offset: number;
}

export interface DocxTextRange {
  start: DocxTextRangeBoundary;
  end: DocxTextRangeBoundary;
}

function parseDocumentIndexFromAttribute(
  raw: string | null
): number | undefined {
  const parsed = raw ? Number.parseInt(raw, 10) : Number.NaN;
  if (!Number.isFinite(parsed)) {
    return undefined;
  }

  return parsed;
}

function parseParagraphLocationFromElement(
  element: Element | null
): ParagraphLocation | undefined {
  if (!element) {
    return undefined;
  }

  const kind = element.getAttribute("data-docx-paragraph-kind");
  if (kind === "paragraph") {
    const nodeIndex = parseDocumentIndexFromAttribute(
      element.getAttribute("data-docx-paragraph-node-index")
    );
    if (nodeIndex === undefined) {
      return undefined;
    }
    return { kind: "paragraph", nodeIndex };
  }

  if (kind === "table-cell") {
    const tableIndex = parseDocumentIndexFromAttribute(
      element.getAttribute("data-docx-table-index")
    );
    const rowIndex = parseDocumentIndexFromAttribute(
      element.getAttribute("data-docx-row-index")
    );
    const cellIndex = parseDocumentIndexFromAttribute(
      element.getAttribute("data-docx-cell-index")
    );
    const paragraphIndex = parseDocumentIndexFromAttribute(
      element.getAttribute("data-docx-paragraph-index")
    );

    if (
      tableIndex === undefined ||
      rowIndex === undefined ||
      cellIndex === undefined ||
      paragraphIndex === undefined
    ) {
      return undefined;
    }

    return {
      kind: "table-cell",
      tableIndex,
      rowIndex,
      cellIndex,
      paragraphIndex,
    };
  }

  return undefined;
}

function parseTableCellLocationFromElement(
  element: Element | null
): DocxTableCellLocation | undefined {
  if (!element) {
    return undefined;
  }

  const cellElement = element.closest("[data-docx-table-cell='true']");
  if (!cellElement) {
    return undefined;
  }

  const tableIndex = parseDocumentIndexFromAttribute(
    cellElement.getAttribute("data-docx-table-index")
  );
  const rowIndex = parseDocumentIndexFromAttribute(
    cellElement.getAttribute("data-docx-row-index")
  );
  const cellIndex = parseDocumentIndexFromAttribute(
    cellElement.getAttribute("data-docx-cell-index")
  );
  if (
    tableIndex === undefined ||
    rowIndex === undefined ||
    cellIndex === undefined
  ) {
    return undefined;
  }

  return {
    tableIndex,
    rowIndex,
    cellIndex,
  };
}

function tableCellSelectionRangeBounds(range: DocxTableCellSelectionRange): {
  startRowIndex: number;
  endRowIndex: number;
  startCellIndex: number;
  endCellIndex: number;
} {
  return {
    startRowIndex: Math.min(range.anchorRowIndex, range.focusRowIndex),
    endRowIndex: Math.max(range.anchorRowIndex, range.focusRowIndex),
    startCellIndex: Math.min(range.anchorCellIndex, range.focusCellIndex),
    endCellIndex: Math.max(range.anchorCellIndex, range.focusCellIndex),
  };
}

function isSingleTableCellSelectionRange(
  range: DocxTableCellSelectionRange
): boolean {
  return (
    range.anchorRowIndex === range.focusRowIndex &&
    range.anchorCellIndex === range.focusCellIndex
  );
}

function sameTableCellSelectionRange(
  a: DocxTableCellSelectionRange | undefined,
  b: DocxTableCellSelectionRange | undefined
): boolean {
  if (!a || !b) {
    return a === b;
  }

  return (
    a.tableIndex === b.tableIndex &&
    a.anchorRowIndex === b.anchorRowIndex &&
    a.anchorCellIndex === b.anchorCellIndex &&
    a.focusRowIndex === b.focusRowIndex &&
    a.focusCellIndex === b.focusCellIndex
  );
}

function isCellWithinTableSelectionRange(
  range: DocxTableCellSelectionRange | undefined,
  tableIndex: number,
  rowIndex: number,
  cellIndex: number
): boolean {
  if (!range || range.tableIndex !== tableIndex) {
    return false;
  }

  const bounds = tableCellSelectionRangeBounds(range);
  return (
    rowIndex >= bounds.startRowIndex &&
    rowIndex <= bounds.endRowIndex &&
    cellIndex >= bounds.startCellIndex &&
    cellIndex <= bounds.endCellIndex
  );
}

function textRangeTouchesTable(
  range: DocxTextRange | undefined,
  tableIndex: number
): boolean {
  if (!range) {
    return false;
  }

  return [range.start.location, range.end.location].some(
    (location) =>
      location.kind === "table-cell" && location.tableIndex === tableIndex
  );
}

function selectedTableCellLocations(
  table: TableNode,
  range: DocxTableCellSelectionRange
): Array<{ rowIndex: number; cellIndex: number }> {
  const bounds = tableCellSelectionRangeBounds(range);
  const selected: Array<{ rowIndex: number; cellIndex: number }> = [];

  for (let rowIndex = 0; rowIndex < table.rows.length; rowIndex += 1) {
    if (rowIndex < bounds.startRowIndex || rowIndex > bounds.endRowIndex) {
      continue;
    }

    const row = table.rows[rowIndex];
    if (!row) {
      continue;
    }

    for (let cellIndex = 0; cellIndex < row.cells.length; cellIndex += 1) {
      if (
        cellIndex < bounds.startCellIndex ||
        cellIndex > bounds.endCellIndex
      ) {
        continue;
      }

      const cell = row.cells[cellIndex];
      if (!cell || cell.style?.vMergeContinuation) {
        continue;
      }

      selected.push({ rowIndex, cellIndex });
    }
  }

  return selected;
}

function tableSelectableCellExtents(table: TableNode):
  | {
      first: { rowIndex: number; cellIndex: number };
      last: { rowIndex: number; cellIndex: number };
    }
  | undefined {
  let first: { rowIndex: number; cellIndex: number } | undefined;
  let maxSelectableRowIndex = -1;
  let maxSelectableCellIndex = -1;

  for (let rowIndex = 0; rowIndex < table.rows.length; rowIndex += 1) {
    const row = table.rows[rowIndex];
    if (!row) {
      continue;
    }

    for (let cellIndex = 0; cellIndex < row.cells.length; cellIndex += 1) {
      const cell = row.cells[cellIndex];
      if (!cell || cell.style?.vMergeContinuation) {
        continue;
      }

      if (!first) {
        first = { rowIndex, cellIndex };
      }
      if (rowIndex > maxSelectableRowIndex) {
        maxSelectableRowIndex = rowIndex;
      }
      if (cellIndex > maxSelectableCellIndex) {
        maxSelectableCellIndex = cellIndex;
      }
    }
  }

  if (!first || maxSelectableRowIndex < 0 || maxSelectableCellIndex < 0) {
    return undefined;
  }

  return {
    first,
    last: {
      rowIndex: maxSelectableRowIndex,
      cellIndex: maxSelectableCellIndex,
    },
  };
}

function tableSelectionCoversWholeTable(
  table: TableNode,
  range: DocxTableCellSelectionRange
): boolean {
  const selectedKeys = new Set(
    selectedTableCellLocations(table, range).map(
      (location) => `${location.rowIndex}:${location.cellIndex}`
    )
  );
  if (selectedKeys.size === 0) {
    return false;
  }

  let totalSelectableCells = 0;
  for (let rowIndex = 0; rowIndex < table.rows.length; rowIndex += 1) {
    const row = table.rows[rowIndex];
    if (!row) {
      continue;
    }

    for (let cellIndex = 0; cellIndex < row.cells.length; cellIndex += 1) {
      const cell = row.cells[cellIndex];
      if (!cell || cell.style?.vMergeContinuation) {
        continue;
      }

      totalSelectableCells += 1;
      if (!selectedKeys.has(`${rowIndex}:${cellIndex}`)) {
        return false;
      }
    }
  }

  return totalSelectableCells > 0 && selectedKeys.size === totalSelectableCells;
}

function textLengthFromRange(range: Range): number {
  const fragment = range.cloneContents();
  fragment
    .querySelectorAll("[data-docx-numbering-label='true']")
    .forEach((label) => {
      label.remove();
    });
  return fragment.textContent?.length ?? 0;
}

function compareParagraphLocations(
  a: ParagraphLocation,
  b: ParagraphLocation
): number {
  if (a.kind === "paragraph" && b.kind === "paragraph") {
    return Math.sign(a.nodeIndex - b.nodeIndex);
  }

  if (a.kind === "paragraph" && b.kind === "table-cell") {
    if (a.nodeIndex !== b.tableIndex) {
      return Math.sign(a.nodeIndex - b.tableIndex);
    }

    return -1;
  }

  if (a.kind === "table-cell" && b.kind === "paragraph") {
    if (a.tableIndex !== b.nodeIndex) {
      return Math.sign(a.tableIndex - b.nodeIndex);
    }

    return 1;
  }

  if (a.kind === "table-cell" && b.kind === "table-cell") {
    if (a.tableIndex !== b.tableIndex) {
      return Math.sign(a.tableIndex - b.tableIndex);
    }

    if (a.rowIndex !== b.rowIndex) {
      return Math.sign(a.rowIndex - b.rowIndex);
    }

    if (a.cellIndex !== b.cellIndex) {
      return Math.sign(a.cellIndex - b.cellIndex);
    }

    return Math.sign(a.paragraphIndex - b.paragraphIndex);
  }

  return 0;
}

function compareTextRangeBoundaries(
  a: DocxTextRangeBoundary,
  b: DocxTextRangeBoundary
): number {
  const locationCompare = compareParagraphLocations(a.location, b.location);
  if (locationCompare !== 0) {
    return locationCompare;
  }

  return Math.sign(a.offset - b.offset);
}

function normalizeTextRange(range: DocxTextRange): DocxTextRange {
  if (compareTextRangeBoundaries(range.start, range.end) <= 0) {
    return {
      start: range.start,
      end: range.end,
    };
  }

  return {
    start: range.end,
    end: range.start,
  };
}

interface DocxHistorySnapshot {
  model: DocModel;
  selection: DocxEditorSelection;
  activeTextRange?: DocxTextRange;
}

interface DocxHistoryRestoreRequest {
  nonce: number;
  selection: DocxEditorSelection;
  activeTextRange?: DocxTextRange;
}

interface DocxEditorTransactionContext {
  model: DocModel;
  selection: DocxEditorSelection;
  activeTextRange?: DocxTextRange;
  pendingRunStyle?: TextRunNode["style"];
}

interface DocxEditorTransactionPatch {
  model?: DocModel;
  selection?: DocxEditorSelection;
  activeTextRange?: DocxTextRange;
  pendingRunStyle?: TextRunNode["style"];
  status?: string;
  clearSelectedFormField?: boolean;
  pushHistory?: boolean;
}

type ParagraphLocation = DocxTextRangeLocation;

export type DocxImageLocation = ParagraphLocation & {
  childIndex: number;
};

export type DocxSectionRegion = "header" | "footer";

export interface DocxSectionParagraphLocation {
  region: DocxSectionRegion;
  partName: string;
  nodeIndex: number;
  rowIndex?: number;
  cellIndex?: number;
  paragraphIndex?: number;
}

export type DocxSectionImageLocation = DocxSectionParagraphLocation & {
  childIndex: number;
};

export type DocxFormFieldLocation = ParagraphLocation & {
  childIndex: number;
};

export interface DocxSelectedFormField {
  location: DocxFormFieldLocation;
  field: FormFieldRunNode;
}

export type DocxImageDropTarget = ParagraphLocation & {
  childIndex: number;
};

export type DocxTrackedChangeKind =
  | "insertion"
  | "deletion"
  | "move-from"
  | "move-to"
  | "format-change"
  | "paragraph-format-change";

export interface DocxTrackedChange {
  id: string;
  inlineAnchorId?: string;
  kind: DocxTrackedChangeKind;
  author?: string;
  date?: string;
  text?: string;
  nodeIndex: number;
  location: DocxTextRangeLocation;
}

/**
 * A document comment anchored to a text range.
 *
 * Comment definitions come from `word/comments.xml` (threading and resolution
 * state from `word/commentsExtended.xml`); the anchor location is resolved
 * from the paragraph that carries the `commentReference` run.
 */
export interface DocxComment {
  /** Stable identifier (unique per rendered anchor). */
  id: string;
  /** Comment id from `word/comments.xml` (`w:id`). */
  commentId: number;
  author?: string;
  initials?: string;
  date?: string;
  /** Plain-text comment body. */
  text: string;
  /** Comment id this comment replies to, when part of a thread. */
  parentId?: number;
  /** True when the comment thread is marked done. */
  resolved?: boolean;
  /** Plain-text excerpt of the commented document range. */
  anchorText?: string;
  nodeIndex: number;
  location: DocxTextRangeLocation;
}

export type DocxLineSpacingRule = "auto" | "exact" | "atLeast";
export type DocxSelectionSessionKind =
  | "idle"
  | "pointer"
  | "keyboard"
  | "composition"
  | "command"
  | "history-restore";

export interface DocxLineSpacingInfo {
  lineRule: DocxLineSpacingRule;
  lineTwips?: number;
  multiple: number;
}

export type DocxBorderContext = "paragraph" | "table";

export type DocxBorderPreset =
  | "bottom"
  | "top"
  | "left"
  | "right"
  | "none"
  | "all"
  | "outside"
  | "inside"
  | "inside-horizontal"
  | "inside-vertical"
  | "diagonal-down"
  | "diagonal-up"
  | "horizontal-line";

export type DocxBorderPresetState = Record<DocxBorderPreset, boolean>;

/**
 * Initial state for `useDocxEditor`.
 *
 * These values are read when the editor controller is created. Later document
 * imports update the controller through `editor.importDocxFile`.
 */
export interface UseDocxEditorOptions {
  /**
   * Model used for `editor.newDocument()` and for the initial empty editor.
   *
   * @defaultValue `defaultStarterModel`
   */
  starterModel?: DocModel;
  /**
   * Initial display name before a user imports a file.
   *
   * @defaultValue `"(new document)"`
   */
  initialFileName?: string;
  /**
   * Initial status text exposed on `editor.status`.
   *
   * @defaultValue `"Ready"`
   */
  initialStatus?: string;
  /**
   * Initial document surface theme.
   *
   * @defaultValue `"light"`
   */
  initialDocumentTheme?: DocxDocumentTheme;
  /**
   * Whether tracked changes are visible when the editor first mounts.
   *
   * @defaultValue `false`
   */
  initialShowTrackedChanges?: boolean;
  /**
   * Whether document comments are visible when the editor first mounts.
   *
   * @defaultValue `false`
   */
  initialShowComments?: boolean;
}

/**
 * Controller returned by `useDocxEditor`.
 *
 * Pass this object to `DocxEditorViewer`, then wire toolbar buttons and custom
 * controls to the methods exposed here.
 *
 * @example
 * ```tsx
 * const editor = useDocxEditor();
 *
 * return (
 *   <>
 *     <button onClick={() => editor.toggleBold()}>Bold</button>
 *     <DocxEditorViewer editor={editor} />
 *   </>
 * );
 * ```
 */
export interface DocxEditorController {
  model: DocModel;
  documentLoadNonce: number;
  fileName: string;
  status: string;
  importError?: Error;
  isImporting: boolean;
  documentTheme: DocxDocumentTheme;
  selection: DocxEditorSelection;
  activeTextRange?: DocxTextRange;
  historyRestoreRequest?: DocxHistoryRestoreRequest;
  selectedFormField?: DocxSelectedFormField;
  selectedParagraph?: ParagraphNode;
  selectedRunStyle?: TextRunNode["style"];
  selectedLink?: string;
  pendingRunStyle?: TextRunNode["style"];
  selectionSessionKind: DocxSelectionSessionKind;
  suppressNextDomSelectionRestore: () => void;
  beginSelectionSession: (
    kind: Exclude<DocxSelectionSessionKind, "idle">,
    options?: {
      settleAfterMs?: number;
    }
  ) => void;
  clearSelectionSession: (expectedKind?: DocxSelectionSessionKind) => void;
  selectedParagraphStyleId?: string;
  selectedLineSpacing: DocxLineSpacingInfo;
  selectedBorderContext: DocxBorderContext;
  activeBorderPresets: DocxBorderPresetState;
  availableParagraphStyles: ParagraphStyleDefinition[];
  trackedChanges: DocxTrackedChange[];
  showTrackedChanges: boolean;
  comments: DocxComment[];
  showComments: boolean;
  currentPage: number;
  totalPages: number;
  hasUnorderedList: boolean;
  hasOrderedList: boolean;
  canUndo: boolean;
  canRedo: boolean;
  registerPendingExportModelTransformer: (
    transformer?: (model: DocModel) => DocModel
  ) => void;
  setStatus: React.Dispatch<React.SetStateAction<string>>;
  setDocumentTheme: (theme: DocxDocumentTheme) => void;
  setShowTrackedChanges: (showTrackedChanges: boolean) => void;
  setShowComments: (showComments: boolean) => void;
  syncPaginationInfo: (pagination: DocxPaginationInfo) => void;
  toggleShowTrackedChanges: () => void;
  toggleShowComments: () => void;
  importDocxFile: (file: File) => Promise<void>;
  newDocument: () => void;
  exportDocx: () => void;
  undo: () => void;
  redo: () => void;
  setHeading: (heading?: HeadingLevel) => void;
  setParagraphStyle: (styleId?: string) => void;
  setLineSpacing: (lineMultiple: number) => void;
  setFontFamily: (fontFamily: string) => void;
  setFontSize: (fontSizePt: number) => void;
  toggleBold: () => void;
  toggleItalic: () => void;
  toggleUnderline: () => void;
  toggleStrike: () => void;
  toggleSuperscript: () => void;
  toggleSubscript: () => void;
  setHighlight: (highlight?: string) => void;
  setTextColor: (color?: string) => void;
  setLink: (link?: string) => void;
  selectFormField: (location?: DocxFormFieldLocation) => void;
  toggleFormCheckbox: (location: DocxFormFieldLocation) => void;
  setFormFieldValue: (location: DocxFormFieldLocation, value: string) => void;
  updateFormFieldWidget: (
    location: DocxFormFieldLocation,
    patch: Partial<NonNullable<FormFieldRunNode["widget"]>>
  ) => void;
  applyBorderPreset: (preset: DocxBorderPreset) => void;
  setAlignment: (align?: ParagraphAlignment) => void;
  toggleList: (listType: DocxListType) => void;
  adjustSelectedListDepth: (levelDelta: number, draftText?: string) => boolean;
  insertListItemAfterSelection: (
    draftText: string,
    startOffset: number,
    endOffset?: number,
    targetLocation?: ParagraphLocation
  ) =>
    | {
        paragraphIndex: number;
        caretOffset: number;
      }
    | undefined;
  splitParagraphAtSelection: (
    draftText: string,
    startOffset: number,
    endOffset?: number,
    targetLocation?: ParagraphLocation
  ) =>
    | {
        paragraphIndex: number;
        caretOffset: number;
      }
    | undefined;
  insertTable: () => void;
  insertImageFile: (file: File) => Promise<void>;
  appendParagraph: (text?: string) => number;
  resizeImage: (
    location: DocxImageLocation,
    widthPx: number,
    heightPx: number
  ) => void;
  setSyntheticTextBoxText: (location: DocxImageLocation, text: string) => void;
  setImageWrapMode: (
    location: DocxImageLocation,
    mode: DocxImageWrapMode,
    seedFloating?: Partial<NonNullable<ImageRunNode["floating"]>>
  ) => void;
  moveFloatingImage: (
    location: DocxImageLocation,
    patch: Partial<NonNullable<ImageRunNode["floating"]>>
  ) => void;
  moveSectionFloatingImage: (
    location: DocxSectionImageLocation,
    patch: Partial<NonNullable<ImageRunNode["floating"]>>
  ) => void;
  moveParagraphDropCap: (
    nodeIndex: number,
    patch: Partial<NonNullable<NonNullable<ParagraphNode["style"]>["dropCap"]>>
  ) => void;
  setParagraphDropCapFontSizePt: (
    nodeIndex: number,
    fontSizePt: number
  ) => void;
  setParagraphDropCapText: (nodeIndex: number, text: string) => void;
  moveImage: (source: DocxImageLocation, target: DocxImageDropTarget) => void;
  setActiveTextRange: (range?: DocxTextRange) => void;
  selectParagraph: (nodeIndex: number) => void;
  selectTableCell: (
    tableIndex: number,
    rowIndex: number,
    cellIndex: number
  ) => void;
  clearTableCellContents: (
    tableIndex: number,
    cells: Array<{ rowIndex: number; cellIndex: number }>
  ) => void;
  insertTableRow: (
    tableIndex: number,
    rowIndex: number,
    direction: "above" | "below"
  ) => void;
  insertTableColumn: (
    tableIndex: number,
    cellIndex: number,
    direction: "left" | "right",
    rowIndex?: number
  ) => void;
  deleteTableRow: (tableIndex: number, rowIndex: number) => void;
  deleteTableColumn: (
    tableIndex: number,
    cellIndex: number,
    rowIndex?: number
  ) => void;
  deleteTable: (tableIndex: number) => void;
  moveTable: (tableIndex: number, targetNodeIndex: number) => void;
  moveEmbeddedTableToBody: (
    tableRuntimeKey: string,
    targetNodeIndex: number
  ) => void;
  replaceExpandedSelection: (
    text: string,
    range?: DocxTextRange
  ) => DocxTextRange | undefined;
  deleteExpandedSelection: (range?: DocxTextRange) => DocxTextRange | undefined;
  commitParagraphText: (nodeIndex: number, text: string) => void;
  commitTableCellText: (
    tableIndex: number,
    rowIndex: number,
    cellIndex: number,
    text: string
  ) => void;
  commitTableCellParagraphTextRecursive: (
    tableIndex: number,
    rowIndex: number,
    cellIndex: number,
    paragraphIndex: number,
    text: string
  ) => void;
  commitSectionParagraphText: (
    location: DocxSectionParagraphLocation,
    text: string
  ) => void;
}

export type DocxTableContextMenuActionId =
  | "insert-row-above"
  | "insert-row-below"
  | "insert-column-left"
  | "insert-column-right"
  | "delete-row"
  | "delete-column"
  | "delete-table";

export type DocxContextMenuActionId =
  | DocxTableContextMenuActionId
  | "cut"
  | "copy"
  | "paste"
  | "image-wrap-inline"
  | "image-wrap-square"
  | "image-wrap-tight"
  | "image-wrap-through"
  | "image-wrap-top-and-bottom"
  | "image-wrap-behind-text"
  | "image-wrap-in-front-of-text"
  | "image-edit-wrap-boundary"
  | "image-move-with-text"
  | "image-fix-position-on-page"
  | "image-more-layout-options"
  | "image-bring-to-front"
  | "image-bring-forward"
  | "image-in-front-of-text"
  | "image-send-to-back"
  | "image-send-backward"
  | "image-behind-text";

export interface DocxTableContextMenuContext {
  /** Zero-based table index in the document body. */
  tableIndex: number;
  /** Zero-based row index under the pointer. */
  rowIndex: number;
  /** Zero-based cell index under the pointer. */
  cellIndex: number;
}

export interface DocxTableContextMenuAction {
  /** Built-in table command id. */
  id: DocxTableContextMenuActionId;
  /** Human-readable menu label. */
  label: string;
  /** True when the action removes content or structure. */
  destructive?: boolean;
}

export interface DocxContextMenuAction {
  /** Built-in command id, or a custom id from your menu renderer. */
  id: DocxContextMenuActionId | (string & {});
  /** Human-readable menu label. */
  label: string;
  /** Optional keyboard shortcut text to display next to the label. */
  shortcut?: string;
  /** True when the action removes content or structure. */
  destructive?: boolean;
  /** Prevents the action from being selected. */
  disabled?: boolean;
  /** Marks toggle-style menu items as active. */
  checked?: boolean;
  /** Adds a visual separator before this item in default-style menus. */
  separatorBefore?: boolean;
  /** Nested submenu actions. */
  children?: DocxContextMenuAction[];
}

/**
 * Arguments passed to `renderTableContextMenu`.
 */
export interface DocxTableContextMenuRenderProps {
  /** Table cell that opened the menu. */
  context: DocxTableContextMenuContext;
  /** Built-in table actions available for this context. */
  actions: DocxTableContextMenuAction[];
  /** Runs a built-in table action. */
  runAction: (actionId: DocxTableContextMenuActionId) => void;
  /** Closes the menu without running an action. */
  closeMenu: () => void;
  /** Viewport coordinates where the menu should be placed. */
  position: {
    x: number;
    y: number;
  };
  /** Current document theme, useful for custom menu styling. */
  documentTheme: DocxDocumentTheme;
}

export interface DocxContextMenuContext {
  /** Type of target that opened the menu. */
  kind: "text" | "table" | "image";
  /** Active text range when the menu was opened from text. */
  activeTextRange?: DocxTextRange;
  /** Text location under the pointer when available. */
  location?: DocxTextRangeLocation;
  /** Table context when `kind` is `"table"`. */
  tableContext?: DocxTableContextMenuContext;
  /** Image context when `kind` is `"image"`. */
  image?:
    | {
        location: DocxImageLocation;
        floating?: NonNullable<ImageRunNode["floating"]>;
        wrap?: DocxImageWrapState;
      }
    | undefined;
}

/**
 * Arguments passed to `renderContextMenu`.
 */
export interface DocxContextMenuRenderProps {
  /** Target context that opened the menu. */
  context: DocxContextMenuContext;
  /** Built-in actions available for this context. */
  actions: DocxContextMenuAction[];
  /** Runs a built-in or custom action id. */
  runAction: (actionId: DocxContextMenuActionId | (string & {})) => void;
  /** Closes the menu without running an action. */
  closeMenu: () => void;
  /** Viewport coordinates where the menu should be placed. */
  position: {
    x: number;
    y: number;
  };
  /** Current document theme, useful for custom menu styling. */
  documentTheme: DocxDocumentTheme;
}

/**
 * Controls how `DocxEditorViewer` windows page DOM nodes.
 *
 * Virtualization is enabled by default for multi-page documents. It keeps
 * offscreen pages as lightweight placeholders, which is important for large
 * DOCX files with many tables or complex paragraphs.
 */
export interface DocxPageVirtualizationOptions {
  /**
   * Enables or disables internal page virtualization.
   *
   * Disable only when you need every page mounted at once, for example when
   * generating thumbnails for all pages from the live DOM.
   *
   * @defaultValue `true`
   */
  enabled?: boolean;
  /**
   * Number of pages to keep mounted before and after the visible viewport.
   *
   * Higher values reduce mount/unmount churn while scrolling but increase
   * initial DOM work.
   *
   * @defaultValue `2`
   */
  overscan?: number;
  /**
   * Compatibility option from earlier releases.
   *
   * Page virtualization now starts immediately, so this option is accepted but
   * does not delay the initial page window.
   *
   * @defaultValue `0`
   */
  settleDelayMs?: number;
  /**
   * Explicit scroll container for internal page virtualization.
   *
   * Omit this to let the viewer discover the nearest scrollable ancestor.
   * Provide it when the host app owns scrolling, such as when the viewer is
   * mounted inside a custom scroll-area viewport.
   */
  scrollElement?: HTMLElement | null;
  /**
   * Effective visual scale applied around the viewer.
   *
   * Omit this to let the viewer infer CSS `zoom` from its ancestor chain.
   * Provide it when toolbar zoom is controlled outside the viewer so virtual
   * page offsets update synchronously with the selected zoom.
   */
  zoomScale?: number;
}

/**
 * Externally controlled visible page window.
 *
 * Provide this when your app owns scrolling or virtualization and wants
 * `DocxEditorViewer` to mount only a specific page range.
 */
export interface DocxVisiblePageRange {
  /**
   * Zero-based first page index to render.
   *
   * @example
   * ```tsx
   * <DocxEditorViewer
   *   editor={editor}
   *   visiblePageRange={{ startPageIndex: 0, endPageIndex: 2 }}
   * />
   * ```
   */
  startPageIndex: number;
  /** Zero-based last page index to render. */
  endPageIndex: number;
}

/**
 * Props for the full DOCX editor/viewer surface.
 *
 * Use this component with a controller from `useDocxEditor`.
 *
 * @example
 * ```tsx
 * const editor = useDocxEditor();
 *
 * return (
 *   <DocxEditorViewer
 *     editor={editor}
 *     mode="read-only"
 *     pageVirtualization={{ overscan: 3 }}
 *   />
 * );
 * ```
 */
export interface DocxEditorViewerProps {
  /**
   * Editor controller returned by `useDocxEditor`.
   */
  editor: DocxEditorController;
  /**
   * CSS class applied to the outer viewer root.
   */
  className?: string;
  /**
   * Inline styles applied to the outer viewer root.
   */
  style?: React.CSSProperties;
  /**
   * Background color of each rendered page surface.
   *
   * @defaultValue Viewer theme page color.
   */
  pageBackgroundColor?: string;
  /**
   * Background color shown between pages.
   *
   * @defaultValue `"transparent"`
   */
  pageGapBackgroundColor?: string;
  /**
   * Hides the document behind `loadingState` until initial pagination settles.
   *
   * Enable only when you prefer stable first paint over immediate approximate
   * layout.
   *
   * @defaultValue `false`
   */
  deferInitialPaginationPaint?: boolean;
  /**
   * Custom content shown while initial pagination is settling.
   *
   * Used only when `deferInitialPaginationPaint` is true.
   *
   * @defaultValue A compact `"Loading..."` pill.
   */
  loadingState?: React.ReactNode;
  /**
   * Configures internal page virtualization.
   *
   * @defaultValue `{ enabled: true, overscan: 2 }`
   */
  pageVirtualization?: DocxPageVirtualizationOptions;
  /**
   * Controlled page range to mount.
   *
   * When provided, internal page virtualization is bypassed and only this
   * range is rendered.
   */
  visiblePageRange?: DocxVisiblePageRange;
  /**
   * Called whenever the viewer's resolved page count changes.
   */
  onPageCountChange?: (pageCount: number) => void;
  /**
   * Called when the viewer needs a page to become visible, such as after a
   * bookmark or cross-reference navigation.
   *
   * If omitted, the internal virtualizer scrolls the page into view.
   */
  onRequestPageReveal?: (pageIndex: number) => void;
  /**
   * Overrides visual styles for heading levels.
   */
  headingStyles?: DocxHeadingStyleMap;
  /**
   * Interaction mode for the viewer.
   *
   * @defaultValue `"edit"`
   */
  mode?: DocxEditorViewerMode;
  /**
   * Overrides whether tracked changes are shown.
   *
   * If omitted, the value from `useDocxTrackChanges(editor)` or
   * `editor.showTrackedChanges` is used.
   */
  showTrackedChanges?: boolean;
  /**
   * Custom renderer for tracked-change cards in the page gutter.
   */
  renderTrackedChangeCard?: (
    props: DocxTrackedChangeCardRenderProps
  ) => React.ReactNode;
  /**
   * Overrides whether document comments are shown.
   *
   * If omitted, the value from `useDocxComments(editor)` or
   * `editor.showComments` is used.
   */
  showComments?: boolean;
  /**
   * Custom renderer for comment cards in the page gutter.
   */
  renderCommentCard?: (props: DocxCommentCardRenderProps) => React.ReactNode;
  /**
   * Custom renderer for table context menus.
   *
   * Call `props.runAction(action.id)` to execute built-in actions.
   */
  renderTableContextMenu?: (
    props: DocxTableContextMenuRenderProps
  ) => React.ReactNode;
  /**
   * Custom renderer for text, image, and table context menus.
   *
   * Call `props.closeMenu()` after handling a custom action.
   */
  renderContextMenu?: (props: DocxContextMenuRenderProps) => React.ReactNode;
  /**
   * Called when a form field is double-clicked.
   *
   * Use this to open a custom field settings panel.
   */
  onFormFieldDoubleClick?: (location: DocxFormFieldLocation) => void;
}

export interface DocxTrackedChangeCardRenderProps {
  /** Tracked-change data represented by the card. */
  change: DocxTrackedChange;
  /** Short display label for the change kind. */
  kindLabel: string;
  /** Plain-text excerpt for the changed content. */
  snippet: string;
  /** Formatted change date, if the source document provided one. */
  formattedDate?: string;
  /** Accent color chosen for this change kind. */
  accentColor: string;
  /** Current document theme. */
  documentTheme: DocxDocumentTheme;
  /** Zero-based page index that owns the card. */
  pageIndex: number;
  /** Positioning style computed by the viewer. Apply this to the card root. */
  style: React.CSSProperties;
}

export interface DocxCommentCardRenderProps {
  /** Comment data represented by the card. */
  comment: DocxComment;
  /** Plain-text comment body (already normalized for display). */
  snippet: string;
  /** Formatted comment date, if the source document provided one. */
  formattedDate?: string;
  /** Accent color chosen for comments. */
  accentColor: string;
  /** Current document theme. */
  documentTheme: DocxDocumentTheme;
  /** Zero-based page index that owns the card. */
  pageIndex: number;
  /** Positioning style computed by the viewer. Apply this to the card root. */
  style: React.CSSProperties;
}

/**
 * Viewer interaction mode.
 *
 * - `"edit"` allows editing when the document state permits it.
 * - `"read-only"` disables editing affordances while preserving navigation.
 */
export type DocxEditorViewerMode = "edit" | "read-only";

export interface UseDocxDocumentThemeResult {
  documentTheme: DocxDocumentTheme;
  isDarkDocument: boolean;
  setDocumentTheme: (theme: DocxDocumentTheme) => void;
  toggleDocumentTheme: () => void;
}

export interface UseDocxParagraphStylesResult {
  paragraphStyles: ParagraphStyleDefinition[];
  selectedParagraphStyleId?: string;
  setParagraphStyle: (styleId?: string) => void;
}

export interface UseDocxLineSpacingResult {
  lineSpacing: DocxLineSpacingInfo;
  setLineSpacing: (lineMultiple: number) => void;
}

export interface UseDocxBordersResult {
  borderContext: DocxBorderContext;
  activeBorderPresets: DocxBorderPresetState;
  applyBorderPreset: (preset: DocxBorderPreset) => void;
}

export interface UseDocxFormFieldsResult {
  formFields: DocxSelectedFormField[];
  selectedFormField?: DocxSelectedFormField;
  selectFormField: (location?: DocxFormFieldLocation) => void;
  setFormFieldValue: (location: DocxFormFieldLocation, value: string) => void;
  toggleFormCheckbox: (location: DocxFormFieldLocation) => void;
  updateFormFieldWidget: (
    location: DocxFormFieldLocation,
    patch: Partial<NonNullable<FormFieldRunNode["widget"]>>
  ) => void;
  updateSelectedFormFieldWidget: (
    patch: Partial<NonNullable<FormFieldRunNode["widget"]>>
  ) => void;
}

export interface UseDocxTrackChangesResult {
  trackedChanges: DocxTrackedChange[];
  showTrackedChanges: boolean;
  setShowTrackedChanges: (showTrackedChanges: boolean) => void;
  toggleShowTrackedChanges: () => void;
  changesByLocation: Map<string, DocxTrackedChange[]>;
  getChangesForLocation: (
    location: DocxTextRangeLocation
  ) => DocxTrackedChange[];
}

export interface UseDocxCommentsResult {
  comments: DocxComment[];
  showComments: boolean;
  setShowComments: (showComments: boolean) => void;
  toggleShowComments: () => void;
  commentsByLocation: Map<string, DocxComment[]>;
  getCommentsForLocation: (location: DocxTextRangeLocation) => DocxComment[];
}

export interface DocxSectionColumnLayout {
  count: number;
  gapPx: number;
}

export interface DocxPageLayoutInfo {
  pageWidthPx: number;
  pageHeightPx: number;
  contentWidthPx: number;
  contentHeightPx: number;
  marginsPx: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  headerDistancePx: number;
  footerDistancePx: number;
  pageNumberStart: number;
  columns?: DocxSectionColumnLayout;
  viewportDefaults: {
    zoomPercent: number;
    pageGapPx: number;
  };
}

export interface UseDocxPageLayoutResult {
  layout: DocxPageLayoutInfo;
}

export interface DocxPaginationInfo {
  currentPage: number;
  totalPages: number;
}

export interface UseDocxPaginationResult {
  pagination: DocxPaginationInfo;
}

export interface DocxPageThumbnailResolutionOptions {
  /** Source page width in CSS pixels. */
  sourceWidthPx: number;
  /** Source page height in CSS pixels. */
  sourceHeightPx: number;
  /**
   * Desired thumbnail bounds.
   *
   * A number means "fit within this max width and max height". An object lets
   * you constrain width and height independently.
   *
   * @defaultValue `160`
   */
  resolution?: DocxPageThumbnailBounds;
  /**
   * Maximum CSS pixel width for the thumbnail.
   *
   * Overrides `resolution.maxWidth` when provided.
   */
  maxWidthPx?: number;
  /**
   * Maximum CSS pixel height for the thumbnail.
   *
   * Overrides `resolution.maxHeight` when provided.
   */
  maxHeightPx?: number;
  /**
   * Canvas backing-store pixel ratio.
   *
   * Increase for sharper thumbnails on high-density displays.
   *
   * @defaultValue `window.devicePixelRatio`, capped internally.
   */
  pixelRatio?: number;
}

/**
 * Thumbnail size constraint.
 *
 * @example
 * ```tsx
 * useDocxPageThumbnails(editor, { resolution: 180 });
 * useDocxPageThumbnails(editor, { resolution: { maxWidth: 120 } });
 * ```
 */
export type DocxPageThumbnailBounds =
  | number
  | {
      /** Maximum thumbnail CSS height. */
      maxHeight?: number;
      /** Maximum thumbnail CSS width. */
      maxWidth?: number;
    };

export interface DocxPageThumbnailResolution {
  /** Thumbnail CSS width. */
  widthPx: number;
  /** Thumbnail CSS height. */
  heightPx: number;
  /** Canvas backing-store width. */
  pixelWidthPx: number;
  /** Canvas backing-store height. */
  pixelHeightPx: number;
  /** Scale from source page pixels to thumbnail CSS pixels. */
  scale: number;
}

export interface DocxPageThumbnailRenderWindow {
  /**
   * Page indexes whose attached thumbnail canvases should render first.
   *
   * Use this for the thumbnails currently visible in a virtualized sidebar.
   */
  visiblePageIndexes?: readonly number[];
  /**
   * Page indexes to rasterize into the thumbnail surface cache after visible
   * thumbnails. Prefetched pages paint quickly once their canvases mount.
   */
  prefetchPageIndexes?: readonly number[];
}

/**
 * Options for `useDocxPageThumbnails`.
 */
export interface UseDocxPageThumbnailsOptions {
  /**
   * Desired thumbnail bounds.
   *
   * @defaultValue `160`
   */
  resolution?: DocxPageThumbnailBounds;
  /** Maximum thumbnail CSS width. */
  maxWidthPx?: number;
  /** Maximum thumbnail CSS height. */
  maxHeightPx?: number;
  /**
   * Canvas backing-store pixel ratio.
   *
   * @defaultValue `window.devicePixelRatio`, capped internally.
   */
  pixelRatio?: number;
  /**
   * Minimum interval between repeat raster jobs for the same thumbnail canvas.
   *
   * Lower this when the consumer already limits work to a small visible
   * thumbnail window.
   *
   * @defaultValue `200`
   */
  minRasterIntervalMs?: number;
  /**
   * Prioritizes thumbnails for consumer-owned virtualized thumbnail rails.
   */
  renderWindow?: DocxPageThumbnailRenderWindow;
  /**
   * Prevents thumbnail rendering while keeping stable item metadata.
   *
   * @defaultValue `false`
   */
  disabled?: boolean;
}

export type DocxPageThumbnailStatus =
  | "idle"
  | "rendering"
  | "ready"
  | "unavailable"
  | "error";

export interface DocxPageThumbnailItem extends DocxPageThumbnailResolution {
  /** Source page aspect ratio. */
  aspectRatio: number;
  /** Alias for `sourceHeightPx` for compatibility with other preview APIs. */
  contentHeight: number;
  /** Alias for `sourceWidthPx` for compatibility with other preview APIs. */
  contentWidth: number;
  /** Alias for `heightPx`. */
  height: number;
  /** Zero-based page index. */
  pageIndex: number;
  /** One-based page number for display. */
  pageNumber: number;
  /** Source page width in CSS pixels. */
  sourceWidthPx: number;
  /** Source page height in CSS pixels. */
  sourceHeightPx: number;
  /** True when the source page DOM is currently mounted. */
  isMounted: boolean;
  /** Current thumbnail render status. */
  status: DocxPageThumbnailStatus;
  /** Last thumbnail rendering error, if any. */
  error?: Error;
  /** Paints this thumbnail into a canvas. Returns false when unavailable. */
  paint: (canvas: HTMLCanvasElement | null) => boolean;
  /** Ref callback that keeps an attached canvas rendered as the page changes. */
  canvasRef: (canvas: HTMLCanvasElement | null) => void;
  /** Asynchronously renders this thumbnail into a canvas. */
  renderToCanvas: (canvas: HTMLCanvasElement) => Promise<void>;
  /** Alias for `widthPx`. */
  width: number;
}

export interface UseDocxPageThumbnailsResult {
  /** Paints the requested page thumbnail into a canvas. */
  paintThumbnail: (
    pageIndex: number,
    canvas: HTMLCanvasElement | null
  ) => boolean;
  /** Thumbnail metadata and paint helpers for each known page. */
  thumbnails: DocxPageThumbnailItem[];
  /** Re-renders every thumbnail canvas currently attached through `canvasRef`. */
  rerenderAttachedThumbnails: () => Promise<void>;
}

export type UseDocxViewerThumbnailsOptions = UseDocxPageThumbnailsOptions;
export type DocxViewerThumbnails = UseDocxPageThumbnailsResult;

export const defaultStarterModel: DocModel = {
  nodes: [
    {
      type: "paragraph",
      children: [{ type: "text", text: "" }],
    },
  ],
  metadata: {
    sourceParts: 1,
    warnings: [],
    headerSections: [],
    footerSections: [],
    paragraphStyles: [
      {
        id: "Normal",
        name: "Body",
        isDefault: true,
        // Word's blank-document default body typeface. Without this the body
        // default fell through to a heading font / Times New Roman and did not
        // match the "Calibri" shown in the toolbar.
        runStyle: { fontFamily: "Calibri", fontSizePt: 11 },
      },
      {
        id: "Heading1",
        name: "Heading 1",
        headingLevel: 1,
        isPrimary: true,
        runStyle: {
          fontFamily: "Calibri Light",
          fontSizePt: 16,
          bold: true,
          color: "#2f5496",
        },
      },
      {
        id: "Heading2",
        name: "Heading 2",
        headingLevel: 2,
        isPrimary: true,
        runStyle: {
          fontFamily: "Calibri Light",
          fontSizePt: 13,
          bold: true,
          color: "#2f5496",
        },
      },
      {
        id: "Heading3",
        name: "Heading 3",
        headingLevel: 3,
        isPrimary: true,
        runStyle: {
          fontFamily: "Calibri",
          fontSizePt: 12,
          bold: true,
          color: "#1f3763",
        },
      },
      {
        id: "Heading4",
        name: "Heading 4",
        headingLevel: 4,
        runStyle: {
          fontFamily: "Calibri",
          fontSizePt: 11,
          bold: true,
          color: "#1f3763",
        },
      },
      {
        id: "Heading5",
        name: "Heading 5",
        headingLevel: 5,
        runStyle: {
          fontFamily: "Calibri",
          fontSizePt: 11,
          bold: true,
          color: "#1f3763",
        },
      },
      {
        id: "Heading6",
        name: "Heading 6",
        headingLevel: 6,
        runStyle: {
          fontFamily: "Calibri",
          fontSizePt: 11,
          bold: true,
          color: "#1f3763",
        },
      },
    ],
    defaultParagraphStyleId: "Normal",
  },
};

function createBlankDocumentModel(): DocModel {
  return cloneDocModel(defaultStarterModel);
}

function textRuns(paragraph: ParagraphNode): TextRunNode[] {
  return paragraph.children.filter(
    (child): child is TextRunNode => child.type === "text"
  );
}

function paragraphText(paragraph: ParagraphNode): string {
  return textRuns(paragraph)
    .map((run) => run.text)
    .join("");
}

function nodeTreeContainsExplicitFontFamily(
  nodes: DocModel["nodes"] | HeaderSection["nodes"] | FooterSection["nodes"]
): boolean {
  return nodes.some((node) => {
    if (node.type === "paragraph") {
      return node.children.some((child) => {
        if (child.type === "text" || child.type === "form-field") {
          return Boolean(child.style?.fontFamily?.trim());
        }
        return false;
      });
    }

    if (node.type === "table") {
      return node.rows.some((row) =>
        row.cells.some((cell) => nodeTreeContainsExplicitFontFamily(cell.nodes))
      );
    }

    return false;
  });
}

function firstExplicitFontFamilyInNodeTree(
  nodes: DocModel["nodes"] | HeaderSection["nodes"] | FooterSection["nodes"]
): string | undefined {
  for (const node of nodes) {
    if (node.type === "paragraph") {
      for (const child of node.children) {
        if (child.type !== "text" && child.type !== "form-field") {
          continue;
        }

        const fontFamily = child.style?.fontFamily?.trim();
        if (fontFamily) {
          return fontFamily;
        }
      }
      continue;
    }

    if (node.type === "table") {
      for (const row of node.rows) {
        for (const cell of row.cells) {
          const nestedFontFamily = firstExplicitFontFamilyInNodeTree(
            cell.nodes
          );
          if (nestedFontFamily) {
            return nestedFontFamily;
          }
        }
      }
    }
  }

  return undefined;
}

function resolveDocumentInheritedFontFamily(
  model: DocModel
): string | undefined {
  const paragraphStyles = model.metadata.paragraphStyles ?? [];
  const normalizedDefaultStyleId =
    model.metadata.defaultParagraphStyleId?.trim().toLowerCase() ?? "";
  const defaultParagraphStyle =
    paragraphStyles.find(
      (style) => style.id.trim().toLowerCase() === normalizedDefaultStyleId
    ) ??
    paragraphStyles.find((style) => style.isDefault) ??
    paragraphStyles.find((style) => style.id.trim().toLowerCase() === "normal");
  const defaultStyleFontFamily =
    defaultParagraphStyle?.runStyle?.fontFamily?.trim();
  if (defaultStyleFontFamily) {
    return cssFontFamily(defaultStyleFontFamily);
  }

  // Never adopt a heading style's font as the document body default. Heading
  // styles (e.g. "Calibri Light") are not the body typeface, and using one here
  // made freshly typed body text render in the wrong font until it committed.
  const paragraphStyleFontFamily = paragraphStyles.find(
    (style) =>
      style.headingLevel === undefined &&
      Boolean(style.runStyle?.fontFamily?.trim())
  )?.runStyle?.fontFamily;
  if (paragraphStyleFontFamily) {
    return cssFontFamily(paragraphStyleFontFamily);
  }

  const explicitBodyFontFamily = firstExplicitFontFamilyInNodeTree(model.nodes);
  if (explicitBodyFontFamily) {
    return cssFontFamily(explicitBodyFontFamily);
  }

  for (const section of model.metadata.headerSections ?? []) {
    const explicitHeaderFontFamily = firstExplicitFontFamilyInNodeTree(
      section.nodes
    );
    if (explicitHeaderFontFamily) {
      return cssFontFamily(explicitHeaderFontFamily);
    }
  }

  for (const section of model.metadata.footerSections ?? []) {
    const explicitFooterFontFamily = firstExplicitFontFamilyInNodeTree(
      section.nodes
    );
    if (explicitFooterFontFamily) {
      return cssFontFamily(explicitFooterFontFamily);
    }
  }

  return nodeTreeContainsExplicitFontFamily(model.nodes) ||
    (model.metadata.headerSections ?? []).some((section) =>
      nodeTreeContainsExplicitFontFamily(section.nodes)
    ) ||
    (model.metadata.footerSections ?? []).some((section) =>
      nodeTreeContainsExplicitFontFamily(section.nodes)
    )
    ? undefined
    : cssFontFamily(DEFAULT_UNSPECIFIED_DOCX_FONT_FAMILY);
}

function replaceTabLayoutMarkersWithTabText(root: HTMLElement): void {
  const centerLayouts = Array.from(
    root.querySelectorAll<HTMLElement>("[data-docx-tab-layout='center']")
  );
  centerLayouts.forEach((layout) => {
    const left =
      layout.querySelector<HTMLElement>("[data-docx-tab-zone='left']")
        ?.textContent ?? "";
    const center =
      layout.querySelector<HTMLElement>("[data-docx-tab-zone='center']")
        ?.textContent ?? "";
    layout.replaceWith(`${left}\t${center}`);
  });

  const centerRightLayouts = Array.from(
    root.querySelectorAll<HTMLElement>("[data-docx-tab-layout='center-right']")
  );
  centerRightLayouts.forEach((layout) => {
    const first =
      layout.querySelector<HTMLElement>("[data-docx-tab-zone='0']")
        ?.textContent ?? "";
    const second =
      layout.querySelector<HTMLElement>("[data-docx-tab-zone='1']")
        ?.textContent ?? "";
    const third =
      layout.querySelector<HTMLElement>("[data-docx-tab-zone='2']")
        ?.textContent ?? "";
    layout.replaceWith(`${first}\t${second}\t${third}`);
  });

  const leaderLayouts = Array.from(
    root.querySelectorAll<HTMLElement>("[data-docx-tab-layout='leader']")
  );
  leaderLayouts.forEach((layout) => {
    const left =
      layout.querySelector<HTMLElement>("[data-docx-tab-zone='left']")
        ?.textContent ?? "";
    const right =
      layout.querySelector<HTMLElement>("[data-docx-tab-zone='right']")
        ?.textContent ?? "";
    layout.replaceWith(`${left}\t${right}`);
  });

  const rightLayouts = Array.from(
    root.querySelectorAll<HTMLElement>("[data-docx-tab-layout='right']")
  );
  rightLayouts.forEach((layout) => {
    const left =
      layout.querySelector<HTMLElement>("[data-docx-tab-zone='left']")
        ?.textContent ?? "";
    const right =
      layout.querySelector<HTMLElement>("[data-docx-tab-zone='right']")
        ?.textContent ?? "";
    layout.replaceWith(`${left}\t${right}`);
  });

  const explicitTabMarkers = Array.from(
    root.querySelectorAll<HTMLElement>("[data-docx-tab-char='true']")
  );
  explicitTabMarkers.forEach((marker) => {
    marker.replaceWith("\t");
  });
}

function editableTextFromElement(element: HTMLElement): string {
  const clone = element.cloneNode(true) as HTMLElement;
  replaceTabLayoutMarkersWithTabText(clone);
  clone
    .querySelectorAll("[data-docx-numbering-label='true']")
    .forEach((label) => {
      label.remove();
    });
  clone.querySelectorAll("br").forEach((lineBreak) => {
    lineBreak.replaceWith("\n");
  });
  return clone.textContent ?? "";
}

function editableTextFromTableCellElement(element: HTMLElement): string {
  const paragraphLikeChildren = Array.from(element.children).filter(
    (child): child is HTMLElement =>
      child instanceof HTMLElement &&
      child.getAttribute("contenteditable") !== "false"
  );
  if (paragraphLikeChildren.length === 0) {
    return editableTextFromElement(element);
  }

  return paragraphLikeChildren
    .map((paragraphLikeChild) => {
      if (paragraphLikeChild.tagName.toUpperCase() === "BR") {
        return "";
      }
      const text = editableTextFromElement(paragraphLikeChild);
      return text === "\n" ? "" : text;
    })
    .join("\n");
}

function editableTextFromDraftHtml(html: string): string {
  if (typeof document === "undefined") {
    return html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/gi, " ");
  }

  const container = document.createElement("div");
  container.innerHTML = html;
  return editableTextFromElement(container);
}

function editableTextFromTableCellDraftHtml(html: string): string {
  if (typeof document === "undefined") {
    return editableTextFromDraftHtml(html);
  }

  const container = document.createElement("div");
  container.innerHTML = html;
  return editableTextFromTableCellElement(container);
}

function renderStaticHtml(node: React.ReactNode): string {
  return renderToStaticMarkup(<>{node}</>);
}

function formFieldDisplayValue(field: FormFieldRunNode): string {
  switch (field.fieldType) {
    case "checkbox":
      return field.checked ?? field.widget?.checkbox?.defaultChecked
        ? field.checkedSymbol ?? "☒"
        : field.uncheckedSymbol ?? "☐";
    case "dropdown":
      return field.value ?? field.options?.[0]?.displayText ?? "";
    case "date":
      return field.value ?? "";
    case "text":
      return field.value ?? field.widget?.text?.defaultText ?? "";
    default:
      return field.value ?? "";
  }
}

function firstRunStyle(paragraph?: ParagraphNode): TextRunNode["style"] {
  return textRuns(
    paragraph ?? ({ type: "paragraph", children: [] } as ParagraphNode)
  )[0]?.style;
}

function ensureTextRunNode(paragraph: ParagraphNode): TextRunNode {
  const existing = paragraph.children.find(
    (child): child is TextRunNode => child.type === "text"
  );
  if (existing) {
    return existing;
  }

  const created: TextRunNode = {
    type: "text",
    text: "",
    style: {},
  };
  paragraph.children.unshift(created);
  return created;
}

function isParagraphCellContentNode(
  node: TableCellContentNode
): node is ParagraphNode {
  return node.type === "paragraph";
}

function isTableCellTableContentNode(
  node: TableCellContentNode
): node is TableNode {
  return node.type === "table";
}

function tableCellParagraphs(
  nodeContent: TableCellContentNode[]
): ParagraphNode[] {
  return nodeContent.filter(isParagraphCellContentNode);
}

function tableCellParagraphsRecursively(
  nodeContent: TableCellContentNode[]
): ParagraphNode[] {
  const paragraphs: ParagraphNode[] = [];

  const walk = (entries: TableCellContentNode[]): void => {
    for (const entry of entries) {
      if (isParagraphCellContentNode(entry)) {
        paragraphs.push(entry);
        continue;
      }

      for (const row of entry.rows) {
        for (const nestedCell of row.cells) {
          walk(nestedCell.nodes);
        }
      }
    }
  };

  walk(nodeContent);
  return paragraphs;
}

function tableCellHasImage(nodeContent: TableCellContentNode[]): boolean {
  for (const entry of nodeContent) {
    if (isParagraphCellContentNode(entry) && paragraphHasImage(entry)) {
      return true;
    }

    if (isTableCellTableContentNode(entry)) {
      for (const row of entry.rows) {
        for (const nestedCell of row.cells) {
          if (tableCellHasImage(nestedCell.nodes)) {
            return true;
          }
        }
      }
    }
  }

  return false;
}

function paragraphHasImage(paragraph: ParagraphNode): boolean {
  return paragraph.children.some((child) => child.type === "image");
}

function paragraphHasInFlowImage(paragraph: ParagraphNode): boolean {
  return paragraph.children.some((child) => {
    if (child.type !== "image") {
      return false;
    }

    return (
      !shouldRenderAbsoluteFloatingImage(child) &&
      !shouldRenderWrappedFloatingImage(child)
    );
  });
}

function paragraphHasTextBearingAbsoluteFloatingTextBox(
  paragraph: ParagraphNode
): boolean {
  return paragraph.children.some(
    (child) =>
      child.type === "image" &&
      shouldRenderAbsoluteFloatingImage(child) &&
      child.syntheticTextBox === true &&
      !syntheticTextBoxContainsPictureLayer(child) &&
      Boolean(floatingTextBoxVisibleTextFromImage(child))
  );
}

function paragraphIsFloatingImageAnchorOnly(paragraph: ParagraphNode): boolean {
  if (
    paragraphHasVisibleText(paragraph) ||
    paragraphHasFormField(paragraph) ||
    paragraphHasTextBearingAbsoluteFloatingTextBox(paragraph)
  ) {
    return false;
  }

  let hasFloatingImage = false;
  for (const child of paragraph.children) {
    if (child.type === "text") {
      if (child.text.trim().length > 0) {
        return false;
      }
      continue;
    }

    if (child.type !== "image") {
      return false;
    }

    if (
      !shouldRenderWrappedFloatingImage(child) &&
      !shouldRenderAbsoluteFloatingImage(child)
    ) {
      return false;
    }

    hasFloatingImage = true;
  }

  return hasFloatingImage;
}

function paragraphIsAbsoluteFloatingImageAnchorOnly(
  paragraph: ParagraphNode
): boolean {
  const allowSectionBreakTextBearingAnchor =
    paragraphContainsSectionBreakProperties(paragraph) &&
    !paragraphHasVisibleText(paragraph) &&
    !paragraphHasFormField(paragraph);

  if (
    paragraphHasVisibleText(paragraph) ||
    paragraphHasFormField(paragraph) ||
    (paragraphHasTextBearingAbsoluteFloatingTextBox(paragraph) &&
      !allowSectionBreakTextBearingAnchor)
  ) {
    return false;
  }

  let hasAbsoluteFloatingImage = false;
  for (const child of paragraph.children) {
    if (child.type === "text") {
      if (child.text.trim().length > 0) {
        return false;
      }
      continue;
    }

    if (child.type !== "image" || !shouldRenderAbsoluteFloatingImage(child)) {
      return false;
    }

    hasAbsoluteFloatingImage = true;
  }

  return hasAbsoluteFloatingImage;
}

function paragraphContainsOnlyAbsoluteFloatingContent(
  paragraph: ParagraphNode
): boolean {
  if (paragraphHasFormField(paragraph)) {
    return false;
  }

  let hasAbsoluteFloatingImage = false;
  for (const child of paragraph.children) {
    if (child.type === "text") {
      continue;
    }

    if (child.type !== "image" || !shouldRenderAbsoluteFloatingImage(child)) {
      return false;
    }

    hasAbsoluteFloatingImage = true;
  }

  return hasAbsoluteFloatingImage;
}

function paragraphIsBehindTextAbsoluteFloatingImageAnchorOnly(
  paragraph: ParagraphNode
): boolean {
  if (!paragraphIsAbsoluteFloatingImageAnchorOnly(paragraph)) {
    return false;
  }

  return paragraph.children.every(
    (child) =>
      child.type === "text" ||
      (child.type === "image" && child.floating?.behindDocument === true)
  );
}

function imageBehavesAsDecorativeBehindTextBackground(
  image: ImageRunNode,
  paragraph: ParagraphNode
): boolean {
  return (
    image.floating?.behindDocument === true &&
    shouldRenderAbsoluteFloatingImage(image) &&
    paragraphIsBehindTextAbsoluteFloatingImageAnchorOnly(paragraph)
  );
}

function paragraphActsAsDecorativeBehindTextBackgroundOverlay(
  paragraph: ParagraphNode
): boolean {
  return (
    paragraphIsBehindTextAbsoluteFloatingImageAnchorOnly(paragraph) &&
    !paragraphHasVisibleText(paragraph) &&
    !paragraphHasFormField(paragraph) &&
    paragraph.children.every(
      (child) => child.type !== "image" || child.syntheticTextBox !== true
    )
  );
}

function paragraphNeedsPageWidthAnchorHost(paragraph: ParagraphNode): boolean {
  if (!paragraphIsFloatingImageAnchorOnly(paragraph)) {
    return false;
  }

  return paragraph.children.some((child) => {
    if (
      child.type !== "image" ||
      !shouldRenderAbsoluteFloatingImage(child) ||
      !child.floating
    ) {
      return false;
    }

    const horizontalRelativeTo = child.floating.horizontalRelativeTo
      ?.trim()
      .toLowerCase();
    return horizontalRelativeTo === "page" || horizontalRelativeTo === "margin";
  });
}

function paragraphHasAbsoluteFloatingImage(paragraph: ParagraphNode): boolean {
  return paragraph.children.some(
    (child) =>
      child.type === "image" && shouldRenderAbsoluteFloatingImage(child)
  );
}

function collectHeadingTextColorByLevel(
  model: DocModel
): Partial<Record<number, string>> {
  const colorsByLevel: Partial<Record<number, string>> = {};
  const paragraphStyleById = new Map(
    (model.metadata.paragraphStyles ?? []).map((styleDefinition) => [
      styleDefinition.id,
      styleDefinition,
    ])
  );

  const resolveParagraphHeadingLevel = (
    paragraph: ParagraphNode
  ): number | undefined => {
    if (
      Number.isFinite(paragraph.style?.headingLevel) &&
      (paragraph.style?.headingLevel as number) > 0
    ) {
      return Math.round(paragraph.style?.headingLevel as number);
    }

    const styleDefinition = paragraph.style?.styleId
      ? paragraphStyleById.get(paragraph.style.styleId)
      : undefined;
    const fromStyleDefinition = styleDefinition
      ? resolveParagraphStyleHeadingLevel(styleDefinition)
      : undefined;
    if (fromStyleDefinition) {
      return fromStyleDefinition;
    }

    return (
      headingLevelFromStyleLabel(paragraph.style?.styleId) ??
      headingLevelFromStyleLabel(paragraph.style?.styleName)
    );
  };

  const resolveParagraphTextColor = (
    paragraph: ParagraphNode
  ): string | undefined => {
    for (const child of paragraph.children) {
      if (
        (child.type === "text" || child.type === "form-field") &&
        child.style?.color
      ) {
        return child.style.color;
      }
    }

    const styleDefinition = paragraph.style?.styleId
      ? paragraphStyleById.get(paragraph.style.styleId)
      : undefined;
    return styleDefinition?.runStyle?.color;
  };

  const registerParagraph = (paragraph: ParagraphNode): void => {
    const headingLevel = resolveParagraphHeadingLevel(paragraph);
    if (!headingLevel || colorsByLevel[headingLevel]) {
      return;
    }

    const color = resolveParagraphTextColor(paragraph);
    if (!color) {
      return;
    }

    colorsByLevel[headingLevel] = color;
  };

  const walkCellNodes = (nodes: TableCellContentNode[]): void => {
    nodes.forEach((node) => {
      if (node.type === "paragraph") {
        registerParagraph(node);
        return;
      }

      node.rows.forEach((row) => {
        row.cells.forEach((cell) => {
          walkCellNodes(cell.nodes);
        });
      });
    });
  };

  model.nodes.forEach((node) => {
    if (node.type === "paragraph") {
      registerParagraph(node);
      return;
    }

    node.rows.forEach((row) => {
      row.cells.forEach((cell) => {
        walkCellNodes(cell.nodes);
      });
    });
  });

  return colorsByLevel;
}

function sectionNodesNeedPageWideLayout(
  nodes: DocModel["nodes"],
  pageWidthPx: number,
  contentWidthPx: number
): boolean {
  const safePageWidthPx = Math.max(1, Math.round(pageWidthPx));
  const safeContentWidthPx = Math.max(1, Math.round(contentWidthPx));
  const requiresWideImageLayout = (image: ImageRunNode): boolean => {
    if (!image.floating) {
      return false;
    }

    const horizontalRelativeTo =
      image.floating.horizontalRelativeTo?.toLowerCase();
    const usesPageWidthAnchorOrigin =
      horizontalRelativeTo === "page" || horizontalRelativeTo === "margin";
    if (image.floating.behindDocument && usesPageWidthAnchorOrigin) {
      return true;
    }

    if (
      usesPageWidthAnchorOrigin &&
      (shouldRenderAbsoluteFloatingImage(image) ||
        shouldRenderWrappedFloatingImage(image))
    ) {
      return true;
    }

    if (
      Number.isFinite(image.widthPx) &&
      Number.isFinite(image.floating.xPx) &&
      (image.widthPx as number) >= safeContentWidthPx - 12 &&
      (image.floating.xPx as number) <= 12
    ) {
      return true;
    }

    if (
      Number.isFinite(image.widthPx) &&
      Number.isFinite(image.floating.xPx) &&
      (image.widthPx as number) >= safePageWidthPx - 12 &&
      (image.floating.xPx as number) <= 12
    ) {
      return true;
    }

    return false;
  };

  const paragraphNeedsWideLayout = (paragraph: ParagraphNode): boolean =>
    paragraph.children.some(
      (child) => child.type === "image" && requiresWideImageLayout(child)
    );
  const tableNeedsWideLayout = (table: TableNode): boolean =>
    table.rows.some((row) =>
      row.cells.some((cell) =>
        cell.nodes.some((cellNode) =>
          cellNode.type === "paragraph"
            ? paragraphNeedsWideLayout(cellNode)
            : tableNeedsWideLayout(cellNode)
        )
      )
    );

  return nodes.some((node) =>
    node.type === "paragraph"
      ? paragraphNeedsWideLayout(node)
      : tableNeedsWideLayout(node)
  );
}

function sectionNodesNeedFullPageFooterOverlay(
  nodes: DocModel["nodes"]
): boolean {
  const paragraphNeedsFullPageFooterOverlay = (
    paragraph: ParagraphNode
  ): boolean => paragraphHasPageAnchoredAbsoluteFloatingImage(paragraph);
  const tableNeedsFullPageFooterOverlay = (table: TableNode): boolean =>
    table.rows.some((row) =>
      row.cells.some((cell) =>
        cell.nodes.some((cellNode) =>
          cellNode.type === "paragraph"
            ? paragraphNeedsFullPageFooterOverlay(cellNode)
            : tableNeedsFullPageFooterOverlay(cellNode)
        )
      )
    );

  return nodes.some((node) =>
    node.type === "paragraph"
      ? paragraphNeedsFullPageFooterOverlay(node)
      : tableNeedsFullPageFooterOverlay(node)
  );
}

function paragraphHasFormField(paragraph: ParagraphNode): boolean {
  return paragraph.children.some((child) => child.type === "form-field");
}

function paragraphHasCheckboxFormField(paragraph: ParagraphNode): boolean {
  return paragraph.children.some(
    (child) => child.type === "form-field" && child.fieldType === "checkbox"
  );
}

function paragraphHasVisibleText(paragraph: ParagraphNode): boolean {
  return paragraph.children.some(
    (child) =>
      (child.type === "text" && child.text.trim().length > 0) ||
      (child.type === "form-field" &&
        formFieldDisplayValue(child).trim().length > 0)
  );
}

function normalizeFloatingTextBoxComparisonText(text: string): string {
  return text
    .replace(/\r/g, "")
    .split("\n")
    .map((line) => line.replace(/[\s\u00a0]+/g, " ").trim())
    .filter((line) => line.length > 0)
    .join("\n")
    .trim();
}

function floatingTextBoxVisibleTextFromImage(
  image: ImageRunNode
): string | undefined {
  if (
    !shouldRenderAbsoluteFloatingImage(image) ||
    !image.sourceXml ||
    !/<w:txbxContent\b/i.test(image.sourceXml)
  ) {
    return undefined;
  }

  const paragraphs = syntheticTextBoxParagraphsFromRunXml(image.sourceXml);
  if (paragraphs.length === 0) {
    return undefined;
  }

  const text = paragraphs
    .map((paragraph) =>
      paragraph.segments.map((segment) => segment.text).join("")
    )
    .join("\n");
  const normalized = normalizeFloatingTextBoxComparisonText(text);
  return normalized.length > 0 ? normalized : undefined;
}

function paragraphVisibleTextIsOnlyAbsoluteFloatingTextBoxContent(
  paragraph: ParagraphNode
): boolean {
  if (!paragraphHasVisibleText(paragraph) || paragraphHasFormField(paragraph)) {
    return false;
  }

  const paragraphVisibleText = normalizeFloatingTextBoxComparisonText(
    paragraph.children
      .filter((child): child is TextRunNode => child.type === "text")
      .map((child) => child.text)
      .join("")
  );
  if (!paragraphVisibleText) {
    return false;
  }

  const floatingTextBoxTexts = paragraph.children
    .filter(
      (child): child is ImageRunNode =>
        child.type === "image" && shouldRenderAbsoluteFloatingImage(child)
    )
    .map((child) => floatingTextBoxVisibleTextFromImage(child))
    .filter((text): text is string => Boolean(text));

  if (floatingTextBoxTexts.length === 0) {
    return false;
  }

  if (floatingTextBoxTexts.includes(paragraphVisibleText)) {
    return true;
  }

  return (
    normalizeFloatingTextBoxComparisonText(floatingTextBoxTexts.join("\n")) ===
    paragraphVisibleText
  );
}

function paragraphHasOnlyWhitespaceText(paragraph: ParagraphNode): boolean {
  if (paragraphHasImage(paragraph) || paragraphHasFormField(paragraph)) {
    return false;
  }

  return paragraph.children.every((child) => {
    if (child.type !== "text") {
      return false;
    }

    return child.text.replace(/[\s\u00a0]+/g, "").length === 0;
  });
}

function paragraphHasActiveNumbering(paragraph: ParagraphNode): boolean {
  const numbering = paragraph.style?.numbering;
  return Boolean(
    numbering &&
      Number.isFinite(numbering.numId) &&
      Math.round(numbering.numId) > 0
  );
}

function paragraphContainsSectionBreakProperties(
  paragraph: ParagraphNode
): boolean {
  return /<w:sectPr\b/i.test(paragraph.sourceXml ?? "");
}

function paragraphAbsoluteFloatingAnchorsDependOnParagraphFlow(
  paragraph: ParagraphNode
): boolean {
  return paragraph.children.some((child) => {
    if (
      child.type !== "image" ||
      !shouldRenderAbsoluteFloatingImage(child) ||
      child.syntheticTextBox !== true ||
      !floatingTextBoxVisibleTextFromImage(child) ||
      child.floating?.behindDocument !== true
    ) {
      return false;
    }

    const verticalRelativeTo = child.floating?.verticalRelativeTo
      ?.trim()
      .toLowerCase();
    return (
      verticalRelativeTo === undefined ||
      verticalRelativeTo === "" ||
      verticalRelativeTo === "paragraph" ||
      verticalRelativeTo === "line"
    );
  });
}

function likelyFullPageCoverImageRelativeToContentBox(
  image: ImageRunNode,
  pageContentWidthPx: number,
  pageContentHeightPx: number
): boolean {
  if (!shouldRenderAbsoluteFloatingImage(image) || !image.floating) {
    return false;
  }

  const floating = image.floating;
  if (
    floating.behindDocument !== true ||
    (floating.wrapType ?? "none") !== "none"
  ) {
    return false;
  }

  const widthPx = Math.max(0, Math.round(image.widthPx ?? 0));
  const heightPx = Math.max(0, Math.round(image.heightPx ?? 0));
  const safeContentWidthPx = Math.max(1, Math.round(pageContentWidthPx));
  const safeContentHeightPx = Math.max(1, Math.round(pageContentHeightPx));
  const widthLooksCoverSized = widthPx >= safeContentWidthPx * 0.8;
  const heightLooksCoverSized = heightPx >= safeContentHeightPx * 0.7;
  const verticalRelativeTo = floating.verticalRelativeTo?.trim().toLowerCase();

  return (
    widthLooksCoverSized &&
    heightLooksCoverSized &&
    (verticalRelativeTo === undefined ||
      verticalRelativeTo === "" ||
      verticalRelativeTo === "paragraph" ||
      verticalRelativeTo === "line")
  );
}

export function isLikelyFullPageCoverFloatingImage(
  image: ImageRunNode,
  pageWidthPx: number,
  pageHeightPx: number
): boolean {
  return likelyFullPageCoverImageRelativeToContentBox(
    image,
    pageWidthPx,
    pageHeightPx
  );
}

export function resolveLinkedImageWrapperStyle(params: {
  baseStyle?: React.CSSProperties;
  cursor?: React.CSSProperties["cursor"];
}): React.CSSProperties {
  const { baseStyle, cursor } = params;
  const hasPositionedBaseStyle =
    baseStyle !== undefined && Object.keys(baseStyle).length > 0;

  return {
    // Floating images carry their positioning (float/position/offsets) on the
    // wrapper so the hyperlink anchor occupies the same box as the image.
    ...(hasPositionedBaseStyle ? baseStyle : { display: "inline-block" }),
    lineHeight: 0,
    verticalAlign: "middle",
    ...(cursor !== undefined ? { cursor } : undefined),
  };
}

function paragraphIsLikelyFullPageCoverArtAnchor(
  paragraph: ParagraphNode,
  pageContentWidthPx: number,
  pageContentHeightPx: number
): boolean {
  return (
    paragraphContainsOnlyAbsoluteFloatingContent(paragraph) &&
    paragraph.children.some(
      (child) =>
        child.type === "image" &&
        likelyFullPageCoverImageRelativeToContentBox(
          child,
          pageContentWidthPx,
          pageContentHeightPx
        )
    )
  );
}

function pageAnchoredImageLikelyStartsNearPageTop(
  image: ImageRunNode,
  layout: DocumentLayoutMetrics
): boolean {
  if (!shouldRenderAbsoluteFloatingImage(image) || !image.floating) {
    return false;
  }

  const verticalRelativeTo = image.floating.verticalRelativeTo
    ?.trim()
    .toLowerCase();
  if (verticalRelativeTo !== "page" && verticalRelativeTo !== "margin") {
    return false;
  }

  const imageHeightPx = Math.max(0, Math.round(image.heightPx ?? 0));
  const imageWidthPx = Math.max(0, Math.round(image.widthPx ?? 0));
  const coverSized =
    imageWidthPx >= Math.round(layout.pageWidthPx * 0.8) &&
    imageHeightPx >= Math.round(layout.pageHeightPx * 0.7);
  if (!coverSized) {
    return false;
  }

  const topOffsetPx = image.floating.yPx ?? 0;
  return topOffsetPx <= layout.marginsPx.top;
}

function paragraphActsAsPageAnchoredCoverOverlayHost(
  paragraph: ParagraphNode,
  layout: DocumentLayoutMetrics
): boolean {
  if (!paragraphNeedsPageWidthAnchorHost(paragraph)) {
    return false;
  }

  return paragraph.children.some(
    (child) =>
      child.type === "image" &&
      pageAnchoredImageLikelyStartsNearPageTop(child, layout)
  );
}

function paragraphStartsNormalFlowContent(paragraph: ParagraphNode): boolean {
  if (!paragraphHasVisibleText(paragraph)) {
    return false;
  }

  if (paragraphVisibleTextIsOnlyAbsoluteFloatingTextBoxContent(paragraph)) {
    return false;
  }

  return !paragraph.children.some(
    (child) =>
      child.type === "image" && shouldRenderAbsoluteFloatingImage(child)
  );
}

function paragraphParticipatesInLeadingCoverLayout(
  model: DocModel,
  nodeIndex: number,
  pageContentWidthPx: number,
  pageContentHeightPx: number
): boolean {
  let sawLikelyCoverArtAnchor = false;
  for (let probeIndex = 0; probeIndex <= nodeIndex; probeIndex += 1) {
    const probeNode = model.nodes[probeIndex];
    if (!probeNode || probeNode.type !== "paragraph") {
      return false;
    }

    if (paragraphStartsNormalFlowContent(probeNode)) {
      return false;
    }

    if (
      paragraphIsLikelyFullPageCoverArtAnchor(
        probeNode,
        pageContentWidthPx,
        pageContentHeightPx
      )
    ) {
      sawLikelyCoverArtAnchor = true;
    }
  }

  return sawLikelyCoverArtAnchor;
}

function fullPageCoverImageRenderKey(
  nodeIndex: number,
  childIndex: number
): string {
  return `${nodeIndex}:${childIndex}`;
}

function paragraphActsAsLeadingCoverLayoutOverlay(
  model: DocModel,
  nodeIndex: number,
  paragraph: ParagraphNode,
  pageContentWidthPx: number,
  pageContentHeightPx: number
): boolean {
  if (
    !paragraphParticipatesInLeadingCoverLayout(
      model,
      nodeIndex,
      pageContentWidthPx,
      pageContentHeightPx
    ) ||
    paragraphIsLikelyFullPageCoverArtAnchor(
      paragraph,
      pageContentWidthPx,
      pageContentHeightPx
    ) ||
    (paragraphHasVisibleText(paragraph) &&
      !paragraphVisibleTextIsOnlyAbsoluteFloatingTextBoxContent(paragraph)) ||
    paragraphHasFormField(paragraph)
  ) {
    return false;
  }

  return paragraph.children.every(
    (child) =>
      child.type === "text" ||
      (child.type === "image" && shouldRenderAbsoluteFloatingImage(child))
  );
}

function fullPageCoverAbsoluteFloatingImageStyle(
  image: ImageRunNode,
  layout: DocumentLayoutMetrics,
  options?: {
    deltaX?: number;
    deltaY?: number;
    anchorToPageSurface?: boolean;
  }
): React.CSSProperties {
  const floating = image.floating;
  const normalizedZIndex = Number.isFinite(floating?.zIndex)
    ? Math.max(
        1,
        Math.min(
          65535,
          Math.round((floating?.zIndex as number) / WORD_IMAGE_Z_INDEX_STEP)
        )
      )
    : 1;
  return {
    position: "absolute",
    left:
      (options?.anchorToPageSurface ? 0 : -layout.marginsPx.left) +
      Math.round(options?.deltaX ?? 0),
    top:
      (options?.anchorToPageSurface ? 0 : -layout.marginsPx.top) +
      Math.round(options?.deltaY ?? 0),
    width: layout.pageWidthPx,
    height: layout.pageHeightPx,
    zIndex: floating?.behindDocument === true ? 0 : normalizedZIndex,
  };
}

function paragraphLooksLikeCheckboxChoiceRow(
  paragraph: ParagraphNode
): boolean {
  if (paragraph.children.some((child) => child.type === "image")) {
    return false;
  }

  const checkboxCount = paragraph.children.filter(
    (child) => child.type === "form-field" && child.fieldType === "checkbox"
  ).length;
  if (checkboxCount < 2) {
    return false;
  }

  const combinedText = paragraph.children
    .filter((child): child is TextRunNode => child.type === "text")
    .map((child) => child.text)
    .join("");
  if (!combinedText.includes("\t")) {
    return false;
  }

  const normalized = combinedText.replace(/\s+/g, " ").trim().toLowerCase();
  return normalized.includes("yes") && normalized.includes("no");
}

interface ParagraphPretextLayoutRun {
  kind: "text" | "image" | "tab";
  key: string;
  text: string;
  startOffset: number;
  endOffset: number;
  style?: TextRunNode["style"];
  link?: string;
  image?: ImageRunNode;
  tabWidthPx?: number;
}

interface ParagraphPretextLayoutSource {
  text: string;
  runs: ParagraphPretextLayoutRun[];
}

const KEEP_ALL_SCRIPT_RE =
  /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}]/u;

interface WrappedParagraphEditingSession {
  location: ParagraphLocation;
  locationKey: string;
  text: string;
  selectionStart: number;
  selectionEnd: number;
  anchorOffset: number;
  isComposing: boolean;
  preferredCaretX?: number;
}

interface WrappedParagraphSelectionDragState {
  pointerId: number;
  locationKey: string;
  anchorOffset: number;
  anchorBoundary: DocxTextRangeBoundary;
  startX: number;
  startY: number;
}

interface WrappedParagraphSurfaceRegistration {
  location: ParagraphLocation;
  element: HTMLElement;
  layout: PretextVariableWidthLayout;
  textLength: number;
}

interface DualWrappedFloatingImageGeometry {
  image: ImageRunNode;
  imageIndex: number;
  containerWidthPx: number;
  imageLeftPx: number;
  imageTopPx: number;
  imageWidthPx: number;
  imageHeightPx: number;
  exclusion: PretextExclusionRect;
}

const MIN_DUAL_WRAPPED_INTERIOR_BAND_PX = 72;

interface ParagraphDualWrappedTextLayout {
  source: ParagraphPretextLayoutSource;
  geometries: DualWrappedFloatingImageGeometry[];
  lineHeightPx: number;
  layout: PretextVariableWidthLayout;
}

interface PageFlowFloatingWrapObstacle extends PretextExclusionRect {
  sourceNodeIndex: number;
}

function resolveDualWrapParagraphRenderBlockHeightPx(
  layout: PretextVariableWidthLayout,
  geometries: DualWrappedFloatingImageGeometry[]
): number {
  const fullHeightPx = wrappedPretextParagraphBlockHeightPx(layout);
  const textBottomPx = pretextLayoutContentBottomPx(layout);
  const minImageTopPx = geometries.reduce(
    (smallest, geometry) => Math.min(smallest, geometry.exclusion.top),
    0
  );
  const maxImageBottomPx = geometries.reduce(
    (largest, geometry) => Math.max(largest, geometry.exclusion.bottom),
    0
  );
  if (minImageTopPx < -4 || maxImageBottomPx > textBottomPx + 4) {
    return Math.max(MIN_PARAGRAPH_LINE_HEIGHT_PX, textBottomPx);
  }

  return fullHeightPx;
}

export function resolveForeignWrapExclusionsForFlowRange(
  obstacles: PageFlowFloatingWrapObstacle[],
  sourceNodeIndex: number,
  pageFlowTopPx: number,
  pageFlowBottomPx: number
): PretextExclusionRect[] {
  const foreignExclusions: PretextExclusionRect[] = [];

  for (const obstacle of obstacles) {
    if (obstacle.sourceNodeIndex === sourceNodeIndex) {
      continue;
    }
    if (obstacle.bottom <= pageFlowTopPx || obstacle.top >= pageFlowBottomPx) {
      continue;
    }

    foreignExclusions.push({
      left: obstacle.left,
      right: obstacle.right,
      top: Math.max(0, Math.round(obstacle.top - pageFlowTopPx)),
      bottom: Math.max(0, Math.round(obstacle.bottom - pageFlowTopPx)),
    });
  }

  return foreignExclusions;
}

function resolveWrappedImageGeometryForPageFlow(
  image: ImageRunNode,
  childIndex: number,
  containerWidthPx: number,
  options?: {
    paragraphTopPx?: number;
    pageMarginTopPx?: number;
    widthPx?: number;
    heightPx?: number;
    movePreview?: {
      deltaX: number;
      deltaY: number;
      baseLeftPx?: number;
      baseTopPx?: number;
    };
    allowNegativeImageTop?: boolean;
  }
): DualWrappedFloatingImageGeometry | undefined {
  return resolveDualWrappedFloatingImageGeometry(image, containerWidthPx, {
    imageIndex: childIndex,
    widthPx: options?.widthPx,
    heightPx: options?.heightPx,
    paragraphTopPx: options?.paragraphTopPx,
    pageMarginTopPx: options?.pageMarginTopPx,
    deltaX: options?.movePreview?.deltaX,
    deltaY: options?.movePreview?.deltaY,
    baseLeftPx: options?.movePreview?.baseLeftPx,
    baseTopPx: options?.movePreview?.baseTopPx,
    allowNegativeImageTop:
      options?.allowNegativeImageTop ?? Boolean(options?.movePreview),
  });
}

export function collectPageFlowWrapObstaclesForParagraph(
  paragraph: ParagraphNode,
  nodeIndex: number,
  pageFlowTopPx: number,
  containerWidthPx: number,
  lineHeightPx: number,
  options?: {
    paragraphTopPx?: number;
    pageMarginTopPx?: number;
    location?: ParagraphLocation;
    floatingMovePreview?: {
      imageKey: string;
      deltaX: number;
      deltaY: number;
      baseLeftPx?: number;
      baseTopPx?: number;
    };
    resizePreview?: {
      imageKey: string;
      widthPx: number;
      heightPx: number;
    };
  }
): PageFlowFloatingWrapObstacle[] {
  const obstacles: PageFlowFloatingWrapObstacle[] = [];
  const movePreviewByImageIndex = new Map<
    number,
    {
      deltaX: number;
      deltaY: number;
      baseLeftPx?: number;
      baseTopPx?: number;
    }
  >();

  paragraph.children.forEach((child, childIndex) => {
    if (child.type !== "image" || !options?.location) {
      return;
    }

    const imageKey = imageLocationKey({ ...options.location, childIndex });
    const movePreview =
      options.floatingMovePreview?.imageKey === imageKey
        ? options.floatingMovePreview
        : undefined;
    if (!movePreview) {
      return;
    }

    movePreviewByImageIndex.set(childIndex, {
      deltaX: Math.round(movePreview.deltaX),
      deltaY: Math.round(movePreview.deltaY),
      ...(Number.isFinite(movePreview.baseLeftPx)
        ? { baseLeftPx: Math.round(movePreview.baseLeftPx as number) }
        : undefined),
      ...(Number.isFinite(movePreview.baseTopPx)
        ? { baseTopPx: Math.round(movePreview.baseTopPx as number) }
        : undefined),
    });
  });

  const dualWrappedLayout = resolveParagraphDualWrappedTextLayout(
    paragraph,
    containerWidthPx,
    lineHeightPx,
    {
      paragraphTopPx: options?.paragraphTopPx,
      pageMarginTopPx: options?.pageMarginTopPx,
      movePreviewByImageIndex,
      allowNegativeImageTop: movePreviewByImageIndex.size > 0,
    }
  );
  const coveredImageIndexes = new Set<number>();

  if (dualWrappedLayout) {
    for (const geometry of dualWrappedLayout.geometries) {
      coveredImageIndexes.add(geometry.imageIndex);
      obstacles.push({
        sourceNodeIndex: nodeIndex,
        left: geometry.exclusion.left,
        right: geometry.exclusion.right,
        top: pageFlowTopPx + geometry.exclusion.top,
        bottom: pageFlowTopPx + geometry.exclusion.bottom,
      });
    }
  }

  paragraph.children.forEach((child, childIndex) => {
    if (child.type !== "image" || !shouldRenderWrappedFloatingImage(child)) {
      return;
    }
    if (coveredImageIndexes.has(childIndex)) {
      return;
    }

    const imageKey = options?.location
      ? imageLocationKey({ ...options.location, childIndex })
      : undefined;
    const resizePreview =
      imageKey && options?.resizePreview?.imageKey === imageKey
        ? options.resizePreview
        : undefined;
    const movePreview = movePreviewByImageIndex.get(childIndex);
    const geometry = resolveWrappedImageGeometryForPageFlow(
      child,
      childIndex,
      containerWidthPx,
      {
        paragraphTopPx: options?.paragraphTopPx,
        pageMarginTopPx: options?.pageMarginTopPx,
        widthPx: resizePreview?.widthPx ?? child.widthPx,
        heightPx: resizePreview?.heightPx ?? child.heightPx,
        movePreview,
        allowNegativeImageTop: Boolean(movePreview),
      }
    );
    if (!geometry) {
      return;
    }

    obstacles.push({
      sourceNodeIndex: nodeIndex,
      left: geometry.exclusion.left,
      right: geometry.exclusion.right,
      top: pageFlowTopPx + geometry.exclusion.top,
      bottom: pageFlowTopPx + geometry.exclusion.bottom,
    });
  });

  return obstacles;
}

export function resolveParagraphForeignOnlyWrappedTextLayout(
  paragraph: ParagraphNode,
  containerWidthPx: number,
  lineHeightPx: number,
  foreignExclusions: PretextExclusionRect[]
): ParagraphDualWrappedTextLayout | undefined {
  if (foreignExclusions.length === 0) {
    return undefined;
  }

  const source = buildParagraphPretextLayoutSource(paragraph);
  if (!source) {
    return undefined;
  }

  const layout = resolveParagraphPretextExclusionLayout(
    paragraph,
    source,
    containerWidthPx,
    lineHeightPx,
    foreignExclusions
  );
  if (!layout) {
    return undefined;
  }

  return {
    source,
    geometries: [],
    lineHeightPx,
    layout,
  };
}

function precomputePageSegmentForeignWrapExclusions(
  segments: DocumentPageNodeSegment[],
  model: DocModel,
  availableWidthPx: number,
  numberingDefinitions: NumberingDefinitionSet | undefined,
  docGridLinePitchPxByNodeIndex: Map<number, number | undefined>,
  pageContentWidthPxByNodeIndex: Map<number, number>,
  pageLayout: DocumentLayoutMetrics,
  interaction?: {
    floatingMovePreview?: {
      imageKey: string;
      deltaX: number;
      deltaY: number;
      baseLeftPx?: number;
      baseTopPx?: number;
    };
    resizePreview?: {
      imageKey: string;
      widthPx: number;
      heightPx: number;
    };
  }
): PretextExclusionRect[][] {
  const flowTopPxBySegmentIndex: number[] = [];
  const flowHeightPxBySegmentIndex: number[] = [];
  let pageFlowTopPx = 0;

  for (const segment of segments) {
    flowTopPxBySegmentIndex.push(pageFlowTopPx);
    const node = model.nodes[segment.nodeIndex];
    const segmentWidthPx =
      pageContentWidthPxByNodeIndex.get(segment.nodeIndex) ?? availableWidthPx;
    const segmentHeightPx = estimateRenderedPageSegmentHeightPx(
      node,
      segment,
      model,
      segmentWidthPx,
      numberingDefinitions,
      docGridLinePitchPxByNodeIndex.get(segment.nodeIndex),
      // For the wrap-exclusion flow simulation, a square/tight-wrapped float
      // must not inflate its own paragraph's flow height. Following
      // paragraphs flow beside the float (Word behavior), so they need to
      // start right after this paragraph's text — not below the image — for
      // the float's exclusion rect to overlap and indent them.
      { excludeWrappedFloatingImageFootprint: true }
    );
    flowHeightPxBySegmentIndex.push(segmentHeightPx);
    pageFlowTopPx += segmentHeightPx;
  }

  const obstacles: PageFlowFloatingWrapObstacle[] = [];
  for (let segmentIndex = 0; segmentIndex < segments.length; segmentIndex += 1) {
    const segment = segments[segmentIndex];
    const node = model.nodes[segment.nodeIndex];
    if (node?.type !== "paragraph") {
      continue;
    }

    const segmentWidthPx =
      pageContentWidthPxByNodeIndex.get(segment.nodeIndex) ?? availableWidthPx;
    const paragraphRenderTextWidthPx = paragraphAvailableTextWidthPx(
      node,
      segmentWidthPx,
      numberingDefinitions
    );
    obstacles.push(
      ...collectPageFlowWrapObstaclesForParagraph(
        node,
        segment.nodeIndex,
        flowTopPxBySegmentIndex[segmentIndex] ?? 0,
        paragraphRenderTextWidthPx,
        estimateParagraphLineHeightPx(
          node,
          docGridLinePitchPxByNodeIndex.get(segment.nodeIndex)
        ),
        {
          paragraphTopPx: flowTopPxBySegmentIndex[segmentIndex] ?? 0,
          pageMarginTopPx: pageLayout.marginsPx.top,
          location: {
            kind: "paragraph",
            nodeIndex: segment.nodeIndex,
          },
          floatingMovePreview: interaction?.floatingMovePreview,
          resizePreview: interaction?.resizePreview,
        }
      )
    );
  }

  const result = segments.map((segment, segmentIndex) =>
    resolveForeignWrapExclusionsForFlowRange(
      obstacles,
      segment.nodeIndex,
      flowTopPxBySegmentIndex[segmentIndex] ?? 0,
      (flowTopPxBySegmentIndex[segmentIndex] ?? 0) +
        (flowHeightPxBySegmentIndex[segmentIndex] ?? 0)
    )
  );
  return result;
}

function applyWrappedFloatingInteractionPreviewToParagraph(
  paragraph: ParagraphNode,
  location: ParagraphLocation,
  paragraphRenderTextWidthPx: number,
  options?: {
    floatingMovePreview?: {
      imageKey: string;
      deltaX: number;
      deltaY: number;
      baseLeftPx?: number;
      baseTopPx?: number;
    };
    resizePreview?: {
      imageKey: string;
      widthPx: number;
      heightPx: number;
    };
  }
): ParagraphNode {
  const hasInteractionPreview = Boolean(
    options?.floatingMovePreview || options?.resizePreview
  );
  if (!hasInteractionPreview) {
    return paragraph;
  }

  return {
    ...paragraph,
    children: paragraph.children.map((child, childIndex) => {
      if (child.type !== "image") {
        return child;
      }

      const imageLocation: DocxImageLocation = { ...location, childIndex };
      const imageKey = imageLocationKey(imageLocation);
      const resizePreview =
        options?.resizePreview?.imageKey === imageKey
          ? options.resizePreview
          : undefined;
      const movePreview =
        options?.floatingMovePreview?.imageKey === imageKey
          ? options.floatingMovePreview
          : undefined;
      if (!movePreview || !shouldRenderWrappedFloatingImage(child)) {
        return child;
      }

      const widthPx =
        resizePreview?.widthPx ??
        child.widthPx ??
        MIN_PARAGRAPH_LINE_HEIGHT_PX;
      const heightPx =
        resizePreview?.heightPx ??
        child.heightPx ??
        widthPx;
      const baseGeometry = resolveDualWrappedFloatingImageGeometry(
        child,
        paragraphRenderTextWidthPx,
        {
          widthPx,
          heightPx,
          ...(Number.isFinite(movePreview.baseLeftPx)
            ? { baseLeftPx: Math.round(movePreview.baseLeftPx as number) }
            : undefined),
          ...(Number.isFinite(movePreview.baseTopPx)
            ? { baseTopPx: Math.round(movePreview.baseTopPx as number) }
            : undefined),
        }
      );
      const movedLeft = clampNumber(
        Math.round((baseGeometry?.imageLeftPx ?? 0) + movePreview.deltaX),
        0,
        Math.max(0, paragraphRenderTextWidthPx - widthPx)
      );
      const movedTop = Math.round(
        (baseGeometry?.imageTopPx ?? 0) + movePreview.deltaY
      );
      const patch = resolveWrappedFloatingImageDropPatch(
        child,
        paragraphRenderTextWidthPx,
        movedLeft,
        movedTop,
        {
          widthPx,
          heightPx,
        }
      );

      return {
        ...child,
        floating: {
          ...(child.floating ?? {}),
          ...patch,
        },
      };
    }),
  };
}

const paragraphPretextLayoutSourceCache = new WeakMap<
  ParagraphNode,
  Map<number, ParagraphPretextLayoutSource | null>
>();
const paragraphPretextLayoutItemsBySource = new WeakMap<
  ParagraphPretextLayoutSource,
  PretextLayoutItem[]
>();
const paragraphPretextUniformFontBySource = new WeakMap<
  ParagraphPretextLayoutSource,
  string | null
>();

function imageWrapModeFromFloating(
  floating?: ImageRunNode["floating"]
): DocxImageWrapMode {
  if (!floating) {
    return "inline";
  }

  const wrapType = floating.wrapType;
  if (!wrapType || wrapType === "none") {
    return floating.behindDocument ? "behindText" : "inFrontOfText";
  }

  if (
    wrapType === "square" ||
    wrapType === "tight" ||
    wrapType === "through" ||
    wrapType === "topAndBottom"
  ) {
    return wrapType;
  }

  return "square";
}

function floatingImageMovesWithText(
  floating?: ImageRunNode["floating"]
): boolean {
  if (!floating) {
    return true;
  }

  const verticalRelativeTo = floating.verticalRelativeTo?.trim().toLowerCase();
  return verticalRelativeTo !== "page" && verticalRelativeTo !== "margin";
}

export function resolveDocxImageWrapState(
  floating?: ImageRunNode["floating"]
): DocxImageWrapState {
  const moveWithText = floatingImageMovesWithText(floating);
  return {
    mode: imageWrapModeFromFloating(floating),
    moveWithText,
    fixedPositionOnPage: !moveWithText,
  };
}

function imageWrapModeActionId(
  mode: DocxImageWrapMode
): DocxContextMenuActionId {
  switch (mode) {
    case "inline":
      return "image-wrap-inline";
    case "square":
      return "image-wrap-square";
    case "tight":
      return "image-wrap-tight";
    case "through":
      return "image-wrap-through";
    case "topAndBottom":
      return "image-wrap-top-and-bottom";
    case "behindText":
      return "image-wrap-behind-text";
    case "inFrontOfText":
      return "image-wrap-in-front-of-text";
    default:
      return "image-wrap-square";
  }
}

function imageWrapModeFromActionId(
  actionId: DocxContextMenuActionId | (string & {})
): DocxImageWrapMode | undefined {
  switch (actionId) {
    case "image-wrap-inline":
      return "inline";
    case "image-wrap-square":
      return "square";
    case "image-wrap-tight":
      return "tight";
    case "image-wrap-through":
      return "through";
    case "image-wrap-top-and-bottom":
      return "topAndBottom";
    case "image-wrap-behind-text":
    case "image-behind-text":
      return "behindText";
    case "image-wrap-in-front-of-text":
    case "image-in-front-of-text":
      return "inFrontOfText";
    default:
      return undefined;
  }
}

function resolveFloatingForImageWrapMode(
  mode: DocxImageWrapMode,
  currentFloating?: NonNullable<ImageRunNode["floating"]>,
  seedFloating?: Partial<NonNullable<ImageRunNode["floating"]>>
): NonNullable<ImageRunNode["floating"]> | undefined {
  if (mode === "inline") {
    return undefined;
  }

  const currentZIndex = Number.isFinite(currentFloating?.zIndex)
    ? clampNumber(
        Math.round(currentFloating?.zIndex as number),
        WORD_IMAGE_Z_INDEX_MIN,
        WORD_IMAGE_Z_INDEX_MAX
      )
    : WORD_IMAGE_Z_INDEX_DEFAULT;

  const base: NonNullable<ImageRunNode["floating"]> = {
    ...(currentFloating ?? {}),
    ...(seedFloating ?? {}),
    distLPx: Math.max(
      0,
      Math.round(seedFloating?.distLPx ?? currentFloating?.distLPx ?? 8)
    ),
    distRPx: Math.max(
      0,
      Math.round(seedFloating?.distRPx ?? currentFloating?.distRPx ?? 8)
    ),
    distTPx: Math.max(
      0,
      Math.round(seedFloating?.distTPx ?? currentFloating?.distTPx ?? 2)
    ),
    distBPx: Math.max(
      0,
      Math.round(seedFloating?.distBPx ?? currentFloating?.distBPx ?? 4)
    ),
    horizontalRelativeTo:
      seedFloating?.horizontalRelativeTo ??
      currentFloating?.horizontalRelativeTo ??
      "column",
    verticalRelativeTo:
      seedFloating?.verticalRelativeTo ??
      currentFloating?.verticalRelativeTo ??
      "paragraph",
    wrapText:
      seedFloating?.wrapText ?? currentFloating?.wrapText ?? "bothSides",
    behindDocument:
      seedFloating?.behindDocument ?? currentFloating?.behindDocument ?? false,
    zIndex: Number.isFinite(seedFloating?.zIndex)
      ? clampNumber(
          Math.round(seedFloating?.zIndex as number),
          WORD_IMAGE_Z_INDEX_MIN,
          WORD_IMAGE_Z_INDEX_MAX
        )
      : currentZIndex,
  };

  switch (mode) {
    case "behindText":
      return {
        ...base,
        wrapType: "none",
        behindDocument: true,
        zIndex: WORD_IMAGE_Z_INDEX_MIN,
      };
    case "inFrontOfText":
      return {
        ...base,
        wrapType: "none",
        behindDocument: false,
        zIndex: Math.max(WORD_IMAGE_Z_INDEX_STEP, currentZIndex),
      };
    case "topAndBottom":
      return {
        ...base,
        wrapType: "topAndBottom",
        behindDocument: false,
      };
    case "square":
    case "tight":
    case "through":
      return {
        ...base,
        wrapType: mode,
        behindDocument: false,
      };
    default:
      return {
        ...base,
        wrapType: "square",
        behindDocument: false,
      };
  }
}

function buildParagraphPretextTabSpacerText(
  widthPx: number,
  style: TextRunNode["style"] | undefined,
  paragraphBaseFontPx: number
): string {
  const safeWidthPx = Math.max(8, Math.round(widthPx));
  const spacerCharacter = "\u00a0";
  const spacerAdvancePx = Math.max(
    1,
    measureTextWidthPx(spacerCharacter, style, paragraphBaseFontPx)
  );
  let spacerCount = Math.max(1, Math.round(safeWidthPx / spacerAdvancePx));
  let spacerText = spacerCharacter.repeat(spacerCount);
  let measuredWidthPx = measureTextWidthPx(
    spacerText,
    style,
    paragraphBaseFontPx
  );

  while (
    measuredWidthPx + spacerAdvancePx * 0.5 < safeWidthPx &&
    spacerCount < 64
  ) {
    spacerCount += 1;
    spacerText = spacerCharacter.repeat(spacerCount);
    measuredWidthPx = measureTextWidthPx(
      spacerText,
      style,
      paragraphBaseFontPx
    );
  }

  while (spacerCount > 1) {
    const nextText = spacerCharacter.repeat(spacerCount - 1);
    const nextWidthPx = measureTextWidthPx(
      nextText,
      style,
      paragraphBaseFontPx
    );
    if (
      Math.abs(nextWidthPx - safeWidthPx) >=
      Math.abs(measuredWidthPx - safeWidthPx)
    ) {
      break;
    }
    spacerCount -= 1;
    spacerText = nextText;
    measuredWidthPx = nextWidthPx;
  }

  return spacerText;
}

export function buildParagraphPretextLayoutSource(
  paragraph: ParagraphNode,
  options?: {
    allowExplicitLineBreakText?: boolean;
    expandTabsForLayout?: boolean;
  }
): ParagraphPretextLayoutSource | undefined {
  const cacheKey =
    (options?.allowExplicitLineBreakText ? 1 : 0) |
    (options?.expandTabsForLayout ? 2 : 0);
  const cachedVariants = paragraphPretextLayoutSourceCache.get(paragraph);
  const cachedSource = cachedVariants?.get(cacheKey);
  if (cachedSource !== undefined) {
    return cachedSource ?? undefined;
  }
  const storeCachedSource = (
    source: ParagraphPretextLayoutSource | undefined
  ): ParagraphPretextLayoutSource | undefined => {
    if (!cachedVariants) {
      paragraphPretextLayoutSourceCache.set(
        paragraph,
        new Map([[cacheKey, source ?? null]])
      );
    } else {
      cachedVariants.set(cacheKey, source ?? null);
    }
    return source;
  };
  if (paragraphHasFormField(paragraph)) {
    return storeCachedSource(undefined);
  }
  if (
    !options?.allowExplicitLineBreakText &&
    paragraphContainsExplicitLineBreakText(paragraph)
  ) {
    return storeCachedSource(undefined);
  }

  const runs: ParagraphPretextLayoutRun[] = [];
  let combinedText = "";
  const paragraphBaseFontPx = paragraphBaseFontSizePx(paragraph);
  const tabStopPositionsPx = options?.expandTabsForLayout
    ? resolveParagraphTabStopsPx(paragraph)
    : [];
  const usesCheckboxRowTabFallback =
    paragraphLooksLikeCheckboxChoiceRow(paragraph);
  const fallbackTabWidthPx = usesCheckboxRowTabFallback
    ? checkboxChoiceRowTabWidthPx(paragraph)
    : DEFAULT_TAB_STOP_PX;
  let approximateLineWidthPx = 0;
  let lastTextStyle: TextRunNode["style"] | undefined =
    firstRunStyle(paragraph);

  for (
    let childIndex = 0;
    childIndex < paragraph.children.length;
    childIndex += 1
  ) {
    const child = paragraph.children[childIndex];
    if (!child) {
      continue;
    }
    if (child.type === "image") {
      if (child.floating) {
        continue;
      }

      const placeholderText = buildInlineImagePlaceholderText(
        child,
        lastTextStyle,
        paragraphBaseFontPx
      );
      const startOffset = combinedText.length;
      combinedText += placeholderText;
      runs.push({
        kind: "image",
        key: `run-${childIndex}`,
        text: placeholderText,
        startOffset,
        endOffset: combinedText.length,
        style: lastTextStyle,
        image: child,
      });
      continue;
    }
    if (child.type !== "text") {
      return storeCachedSource(undefined);
    }
    if (child.noteReference) {
      return storeCachedSource(undefined);
    }
    if (child.text.includes("\t")) {
      if (!options?.expandTabsForLayout) {
        return storeCachedSource(undefined);
      }

      const tabSegments = child.text.split("\t");
      tabSegments.forEach((segmentText, segmentIndex) => {
        if (segmentText.length > 0) {
          const startOffset = combinedText.length;
          combinedText += segmentText;
          runs.push({
            kind: "text",
            key: `run-${childIndex}-${segmentIndex}`,
            text: segmentText,
            startOffset,
            endOffset: combinedText.length,
            style: child.style,
            link: child.link,
          });
          approximateLineWidthPx = updateEstimatedLineWidthPxForText(
            approximateLineWidthPx,
            segmentText,
            child.style
          );
        }

        if (segmentIndex >= tabSegments.length - 1) {
          return;
        }

        const tabWidthPx = resolveTabSpacerWidthPx(
          tabStopPositionsPx,
          approximateLineWidthPx,
          fallbackTabWidthPx,
          usesCheckboxRowTabFallback
        );
        const spacerText = buildParagraphPretextTabSpacerText(
          tabWidthPx,
          child.style,
          paragraphBaseFontPx
        );
        const startOffset = combinedText.length;
        combinedText += spacerText;
        runs.push({
          kind: "tab",
          key: `run-${childIndex}-${segmentIndex}-tab`,
          text: spacerText,
          startOffset,
          endOffset: combinedText.length,
          style: child.style,
          tabWidthPx,
        });
        approximateLineWidthPx += tabWidthPx;
      });
      lastTextStyle = child.style ?? lastTextStyle;
      continue;
    }

    const startOffset = combinedText.length;
    combinedText += child.text;
    runs.push({
      kind: "text",
      key: `run-${childIndex}`,
      text: child.text,
      startOffset,
      endOffset: combinedText.length,
      style: child.style,
      link: child.link,
    });
    approximateLineWidthPx = updateEstimatedLineWidthPxForText(
      approximateLineWidthPx,
      child.text,
      child.style
    );
    lastTextStyle = child.style ?? lastTextStyle;
  }

  if (combinedText.length === 0) {
    return storeCachedSource(undefined);
  }

  return storeCachedSource({
    text: combinedText,
    runs,
  });
}

function splitParagraphAtExplicitColumnBreaks(
  paragraph: ParagraphNode
): ParagraphNode[] | undefined {
  if (!paragraphHasExplicitColumnBreak(paragraph)) {
    return undefined;
  }

  const paragraphChildren: ParagraphNode["children"][] = [];
  let currentChildren: ParagraphNode["children"] = [];
  let sawExplicitColumnBreak = false;
  const appendCurrentSegment = (): void => {
    paragraphChildren.push(currentChildren);
    currentChildren = [];
  };

  for (
    let childIndex = 0;
    childIndex < paragraph.children.length;
    childIndex += 1
  ) {
    const child = paragraph.children[childIndex];
    if (!child) {
      continue;
    }

    if (child.type === "text") {
      if (/^[\r\n]+$/.test(child.text)) {
        const explicitBreakCount = child.text.replace(/[^\n]/g, "").length;
        if (explicitBreakCount > 0) {
          for (
            let breakIndex = 0;
            breakIndex < explicitBreakCount;
            breakIndex += 1
          ) {
            sawExplicitColumnBreak = true;
            appendCurrentSegment();
          }
          continue;
        }
      }

      currentChildren.push(cloneTextRunWithMetadata(child));
      continue;
    }

    if (child.type === "form-field") {
      currentChildren.push(cloneFormFieldRun(child));
      continue;
    }

    return undefined;
  }

  if (!sawExplicitColumnBreak) {
    return undefined;
  }

  appendCurrentSegment();

  return paragraphChildren.map((children, segmentIndex) => {
    const nextStyle = cloneParagraphStyle(paragraph.style);
    if (segmentIndex > 0 && nextStyle?.numbering) {
      nextStyle.numbering = undefined;
    }

    return {
      ...paragraph,
      style: nextStyle,
      sourceXml: undefined,
      children:
        children.length > 0
          ? children
          : [
              {
                type: "text",
                text: "",
              },
            ],
    };
  });
}

function estimateParagraphContentHeightPx(
  paragraph: ParagraphNode,
  availableWidthPx: number,
  numberingDefinitions?: NumberingDefinitionSet,
  docGridLinePitchPx?: number
): number {
  if (!paragraphHasImage(paragraph) && !paragraphHasFormField(paragraph)) {
    const lineHeightPx = estimateParagraphLineHeightPx(
      paragraph,
      docGridLinePitchPx
    );
    const lineCount = paragraphLineCountWithinWidth(
      paragraph,
      availableWidthPx,
      numberingDefinitions
    );
    return Math.max(1, lineHeightPx * Math.max(1, lineCount));
  }

  return Math.max(
    1,
    estimateParagraphHeightPx(
      paragraph,
      availableWidthPx,
      numberingDefinitions,
      docGridLinePitchPx
    ) -
      paragraphBeforeSpacingPx(paragraph) -
      paragraphAfterSpacingPx(paragraph)
  );
}

function projectParagraphConsumedHeightWithExplicitColumnBreaks(
  paragraphSegments: ParagraphNode[],
  pageConsumedHeightPx: number,
  pageContentHeightPx: number,
  sectionFlowOriginPx: number,
  columnCount: number,
  availableWidthPx: number,
  beforeSpacingPx: number,
  afterSpacingPx: number,
  collapsedMarginPx: number,
  numberingDefinitions?: NumberingDefinitionSet,
  docGridLinePitchPx?: number
): number | undefined {
  if (paragraphSegments.length <= 1 || columnCount <= 1) {
    return undefined;
  }

  const normalizedParagraphStartPx = Math.max(
    0,
    Math.max(pageConsumedHeightPx, sectionFlowOriginPx)
  );
  const firstSegmentTopSpacingPx =
    pageConsumedHeightPx > 0
      ? Math.max(0, beforeSpacingPx - collapsedMarginPx)
      : beforeSpacingPx;
  const tallestSegmentHeightPx = paragraphSegments.reduce(
    (tallest, segment) => {
      return Math.max(
        tallest,
        estimateParagraphContentHeightPx(
          segment,
          availableWidthPx,
          numberingDefinitions,
          docGridLinePitchPx
        )
      );
    },
    0
  );
  const projectedConsumedHeightPx =
    normalizedParagraphStartPx +
    firstSegmentTopSpacingPx +
    tallestSegmentHeightPx +
    afterSpacingPx;
  const sectionFlowCapacityPx = Math.max(
    0,
    pageContentHeightPx - sectionFlowOriginPx
  );
  const sectionConsumedPx = Math.max(
    0,
    pageConsumedHeightPx - sectionFlowOriginPx
  );
  const remainingColumnHeightPx = Math.max(
    0,
    sectionFlowCapacityPx / columnCount - sectionConsumedPx
  );

  if (
    projectedConsumedHeightPx - pageConsumedHeightPx >
    remainingColumnHeightPx + PAGE_OVERFLOW_TOLERANCE_PX
  ) {
    return undefined;
  }

  return projectedConsumedHeightPx;
}

function buildSyntheticPretextLayoutSource(
  text: string,
  style?: TextRunNode["style"]
): ParagraphPretextLayoutSource {
  return {
    text,
    runs: [
      {
        kind: "text",
        key: "synthetic-0",
        text,
        startOffset: 0,
        endOffset: text.length,
        style,
      },
    ],
  };
}

function pretextWordBreakModeForText(text: string): "normal" | "keep-all" {
  const cached = pretextWordBreakModeByText.get(text);
  if (cached) {
    return cached;
  }

  const mode = KEEP_ALL_SCRIPT_RE.test(text) ? "keep-all" : "normal";
  setCacheEntry(pretextWordBreakModeByText, text, mode);
  while (pretextWordBreakModeByText.size > TEXT_MEASURE_CACHE_MAX_ENTRIES) {
    const firstKey = pretextWordBreakModeByText.keys().next().value as
      | string
      | undefined;
    if (!firstKey) {
      break;
    }
    pretextWordBreakModeByText.delete(firstKey);
  }
  return mode;
}

export function sanitizeRenderedPretextFragmentText(text: string): string {
  return text.replace(/\r\n?|\n/g, "");
}

function buildParagraphPretextLayoutItems(
  paragraph: ParagraphNode,
  source: ParagraphPretextLayoutSource
): PretextLayoutItem[] {
  const cachedItems = paragraphPretextLayoutItemsBySource.get(source);
  if (cachedItems) {
    return cachedItems;
  }

  const paragraphBaseFontPx = paragraphBaseFontSizePx(paragraph);
  const wordBreak = pretextWordBreakModeForText(source.text);

  const items: PretextLayoutItem[] = source.runs
    .filter((run) => run.endOffset > run.startOffset)
    .map((run) => ({
      text: run.text,
      font: resolveMeasureFont(run.style, paragraphBaseFontPx),
      startOffset: run.startOffset,
      endOffset: run.endOffset,
      break: run.kind === "image" || run.kind === "tab" ? "never" : "normal",
      wordBreak,
    }));
  paragraphPretextLayoutItemsBySource.set(source, items);
  return items;
}

/**
 * Returns the single measurement font shared by every non-empty run of this
 * pretext source, or `undefined` if there are atomic items (images, tabs)
 * or the runs vary in font. Callers can use this to take a fast
 * `measureLineStats`-based line count path when it returns a value.
 */
function resolveUniformPretextSourceFont(
  paragraph: ParagraphNode,
  source: ParagraphPretextLayoutSource
): string | undefined {
  const cachedUniformFont = paragraphPretextUniformFontBySource.get(source);
  if (cachedUniformFont !== undefined) {
    return cachedUniformFont ?? undefined;
  }

  const paragraphBaseFontPx = paragraphBaseFontSizePx(paragraph);
  let uniformFont: string | undefined;

  for (const run of source.runs) {
    if (run.endOffset <= run.startOffset) {
      continue;
    }
    if (run.kind !== "text") {
      paragraphPretextUniformFontBySource.set(source, null);
      return undefined;
    }

    const runFont = resolveMeasureFont(run.style, paragraphBaseFontPx);
    if (uniformFont === undefined) {
      uniformFont = runFont;
      continue;
    }
    if (runFont !== uniformFont) {
      paragraphPretextUniformFontBySource.set(source, null);
      return undefined;
    }
  }

  paragraphPretextUniformFontBySource.set(source, uniformFont ?? null);
  return uniformFont;
}

function buildMeasureSegmentsPretextLayoutItems(
  segments: ParagraphMeasureSegment[],
  paragraphBaseFontPx: number,
  text: string
): PretextLayoutItem[] {
  const wordBreak = pretextWordBreakModeForText(text);
  let nextOffset = 0;

  return segments
    .map((segment) => {
      const startOffset = nextOffset;
      nextOffset += segment.text.length;
      return {
        text: segment.text,
        font: resolveMeasureFont(segment.style, paragraphBaseFontPx),
        startOffset,
        endOffset: nextOffset,
        break: "normal" as const,
        wordBreak,
      };
    })
    .filter((item) => item.endOffset > item.startOffset);
}

function layoutParagraphPretextSource(
  paragraph: ParagraphNode,
  source: ParagraphPretextLayoutSource,
  containerWidthPx: number,
  lineHeightPx: number,
  exclusions?: PretextExclusionRect[]
): PretextVariableWidthLayout | undefined {
  const fallbackFont = resolveMeasureFont(
    firstRunStyle(paragraph),
    paragraphBaseFontSizePx(paragraph)
  );
  const wordBreak = pretextWordBreakModeForText(source.text);
  const items = buildParagraphPretextLayoutItems(paragraph, source);
  const richLayout =
    items.length > 0
      ? layoutItemsWithPretextAroundExclusions(
          source.text,
          items,
          containerWidthPx,
          lineHeightPx,
          exclusions,
          fallbackFont
        )
      : undefined;
  if (richLayout) {
    return richLayout;
  }

  return layoutTextWithPretextAroundExclusions(
    source.text,
    fallbackFont,
    containerWidthPx,
    lineHeightPx,
    exclusions,
    {
      wordBreak,
    }
  );
}

function wrappedParagraphSessionText(paragraph: ParagraphNode): string {
  return (
    buildParagraphPretextLayoutSource(paragraph)?.text ??
    paragraphText(paragraph)
  );
}

function buildInlineImagePlaceholderText(
  image: ImageRunNode,
  style: TextRunNode["style"] | undefined,
  paragraphBaseFontPx: number
): string {
  const targetWidthPx = Math.max(
    1,
    Math.round(image.widthPx ?? image.heightPx ?? MIN_PARAGRAPH_LINE_HEIGHT_PX)
  );
  let placeholderText = "\u00a0";
  while (
    measureTextWidthPx(placeholderText, style, paragraphBaseFontPx) <
      targetWidthPx &&
    placeholderText.length < 64
  ) {
    placeholderText += "\u00a0";
  }
  return placeholderText;
}

function paragraphChildAnchorOffset(
  paragraph: ParagraphNode,
  childIndex: number
): number {
  const paragraphBaseFontPx = paragraphBaseFontSizePx(paragraph);
  let lastTextStyle: TextRunNode["style"] | undefined =
    firstRunStyle(paragraph);
  let offset = 0;

  for (let currentIndex = 0; currentIndex < childIndex; currentIndex += 1) {
    const child = paragraph.children[currentIndex];
    if (!child) {
      continue;
    }
    if (child.type === "image") {
      if (child.floating) {
        continue;
      }
      offset += buildInlineImagePlaceholderText(
        child,
        lastTextStyle,
        paragraphBaseFontPx
      ).length;
      continue;
    }
    if (child.type !== "text") {
      continue;
    }
    offset += child.text.length;
    lastTextStyle = child.style ?? lastTextStyle;
  }

  return offset;
}

function expandOffsetToWord(
  text: string,
  offset: number
): {
  start: number;
  end: number;
} {
  const safeOffset = Math.max(0, Math.min(Math.round(offset), text.length));
  if (!text) {
    return { start: 0, end: 0 };
  }

  const characterAt = (index: number): string => text.slice(index, index + 1);
  const isWordCharacter = (value: string): boolean =>
    /[0-9A-Za-z_]/.test(value);
  let probeIndex = safeOffset;
  if (probeIndex > 0 && probeIndex === text.length) {
    probeIndex -= 1;
  }
  if (
    !isWordCharacter(characterAt(probeIndex)) &&
    probeIndex > 0 &&
    isWordCharacter(characterAt(probeIndex - 1))
  ) {
    probeIndex -= 1;
  }

  if (!isWordCharacter(characterAt(probeIndex))) {
    return {
      start: safeOffset,
      end: safeOffset,
    };
  }

  let start = probeIndex;
  let end = probeIndex + 1;
  while (start > 0 && isWordCharacter(characterAt(start - 1))) {
    start -= 1;
  }
  while (end < text.length && isWordCharacter(characterAt(end))) {
    end += 1;
  }

  return { start, end };
}

function pixelsToTwips(valuePx: number): number {
  return Math.round(valuePx * TWIPS_PER_PIXEL);
}

export function resolveDualWrappedFloatingImageGeometry(
  image: ImageRunNode,
  containerWidthPx: number,
  options?: {
    imageIndex?: number;
    deltaX?: number;
    deltaY?: number;
    widthPx?: number;
    heightPx?: number;
    baseLeftPx?: number;
    baseTopPx?: number;
    paragraphTopPx?: number;
    pageMarginTopPx?: number;
    allowNegativeImageTop?: boolean;
  }
): DualWrappedFloatingImageGeometry | undefined {
  const floating = image.floating;
  const isWrappedFloatingImage = shouldRenderWrappedFloatingImage(image);
  const hasExplicitBaseLeftPx = Number.isFinite(options?.baseLeftPx);
  const hasExplicitBaseTopPx = Number.isFinite(options?.baseTopPx);
  const isInlinePreview =
    !floating && hasExplicitBaseLeftPx && hasExplicitBaseTopPx;
  if (!isWrappedFloatingImage && !isInlinePreview) {
    return undefined;
  }

  const safeContainerWidthPx = Math.max(1, Math.round(containerWidthPx));
  const imageWidthPx = Math.max(
    1,
    Math.round(
      (options?.widthPx ??
        image.widthPx ??
        image.heightPx ??
        MIN_PARAGRAPH_LINE_HEIGHT_PX) as number
    )
  );
  const imageHeightPx = Math.max(
    1,
    Math.round(
      (options?.heightPx ??
        image.heightPx ??
        image.widthPx ??
        MIN_PARAGRAPH_LINE_HEIGHT_PX) as number
    )
  );
  const deltaX = Number.isFinite(options?.deltaX)
    ? Math.round(options?.deltaX as number)
    : 0;
  const deltaY = Number.isFinite(options?.deltaY)
    ? Math.round(options?.deltaY as number)
    : 0;
  const wrapType = floating?.wrapType;
  const horizontalAlign = floating?.horizontalAlign?.trim().toLowerCase();
  const distLPx = Math.max(0, Math.round(floating?.distLPx ?? 0));
  const distRPx = Math.max(0, Math.round(floating?.distRPx ?? 0));
  const distTPx = Math.max(0, Math.round(floating?.distTPx ?? 0));
  const distBPx = Math.max(0, Math.round(floating?.distBPx ?? 0));
  const baseLeftPx = hasExplicitBaseLeftPx
    ? Math.round(options?.baseLeftPx as number)
    : Number.isFinite(floating?.xPx)
    ? Math.round(floating?.xPx as number)
    : horizontalAlign === "right" || horizontalAlign === "outside"
    ? safeContainerWidthPx - distRPx - imageWidthPx
    : horizontalAlign === "center"
    ? Math.round((safeContainerWidthPx - imageWidthPx) / 2)
    : distLPx;
  const baseTopPx = hasExplicitBaseTopPx
    ? Math.round(options?.baseTopPx as number)
    : Number.isFinite(floating?.yPx)
    ? Math.round(floating?.yPx as number)
    : 0;
  const normalizedVerticalRelativeTo = floating?.verticalRelativeTo
    ?.trim()
    .toLowerCase();
  const paragraphTopPx = Number.isFinite(options?.paragraphTopPx)
    ? Math.max(0, Math.round(options?.paragraphTopPx as number))
    : undefined;
  const pageMarginTopPx = Number.isFinite(options?.pageMarginTopPx)
    ? Math.max(0, Math.round(options?.pageMarginTopPx as number))
    : 0;
  const paragraphLocalBaseTopPx =
    !hasExplicitBaseTopPx &&
    paragraphTopPx !== undefined &&
    (normalizedVerticalRelativeTo === "margin" ||
      normalizedVerticalRelativeTo === "page")
      ? Math.round(
          (normalizedVerticalRelativeTo === "page"
            ? baseTopPx - pageMarginTopPx
            : baseTopPx) - paragraphTopPx
        )
      : baseTopPx;
  const imageLeftPx = clampNumber(
    baseLeftPx + deltaX,
    0,
    Math.max(0, safeContainerWidthPx - imageWidthPx)
  );
  const rawImageTopPx =
    (hasExplicitBaseTopPx
      ? baseTopPx
      : paragraphLocalBaseTopPx + (isInlinePreview ? 0 : distTPx)) + deltaY;
  const modelAllowsNegativeTop =
    Number.isFinite(floating?.yPx) && (floating?.yPx as number) < 0;
  const imageTopPx =
    options?.allowNegativeImageTop || modelAllowsNegativeTop
      ? Math.round(rawImageTopPx)
      : Math.max(0, Math.round(rawImageTopPx));

  let exclusionLeftPx = Math.max(0, imageLeftPx - distLPx);
  let exclusionRightPx = Math.min(
    safeContainerWidthPx,
    imageLeftPx + imageWidthPx + distRPx
  );
  if (wrapType === "topAndBottom") {
    exclusionLeftPx = 0;
    exclusionRightPx = safeContainerWidthPx;
  } else if (horizontalAlign === "left" || horizontalAlign === "inside") {
    exclusionLeftPx = 0;
  } else if (horizontalAlign === "right" || horizontalAlign === "outside") {
    exclusionRightPx = safeContainerWidthPx;
  }

  if (!isInlinePreview && wrapType !== "topAndBottom") {
    const leftBandWidthPx = exclusionLeftPx;
    const rightBandWidthPx = Math.max(
      0,
      safeContainerWidthPx - exclusionRightPx
    );
    const spansInteriorGap =
      exclusionLeftPx > 0 &&
      exclusionRightPx < safeContainerWidthPx &&
      leftBandWidthPx >= MIN_DUAL_WRAPPED_INTERIOR_BAND_PX &&
      rightBandWidthPx >= MIN_DUAL_WRAPPED_INTERIOR_BAND_PX;
    const spansSideFloat =
      exclusionLeftPx === 0 || exclusionRightPx === safeContainerWidthPx;
    if (!spansInteriorGap && !spansSideFloat) {
      return undefined;
    }
  }

  return {
    image,
    imageIndex: options?.imageIndex ?? 0,
    containerWidthPx: safeContainerWidthPx,
    imageLeftPx,
    imageTopPx,
    imageWidthPx,
    imageHeightPx,
    exclusion: {
      left: exclusionLeftPx,
      right: exclusionRightPx,
      top: imageTopPx,
      bottom: imageTopPx + imageHeightPx + distBPx,
    },
  };
}

export function resolveParagraphDualWrappedTextLayout(
  paragraph: ParagraphNode,
  containerWidthPx: number,
  lineHeightPx: number,
  options?: {
    deltaX?: number;
    deltaY?: number;
    widthPxByImageIndex?: Map<number, number>;
    heightPxByImageIndex?: Map<number, number>;
    paragraphTopPx?: number;
    pageMarginTopPx?: number;
    movePreviewByImageIndex?: Map<
      number,
      {
        deltaX: number;
        deltaY: number;
        baseLeftPx?: number;
        baseTopPx?: number;
      }
    >;
    foreignExclusions?: PretextExclusionRect[];
    allowNegativeImageTop?: boolean;
  }
): ParagraphDualWrappedTextLayout | undefined {
  const source = buildParagraphPretextLayoutSource(paragraph);
  if (!source) {
    return undefined;
  }

  const geometries = paragraph.children
    .map((child, childIndex) => {
      if (child.type !== "image" || !child.floating) {
        return undefined;
      }

      const movePreview = options?.movePreviewByImageIndex?.get(childIndex);
      return resolveDualWrappedFloatingImageGeometry(child, containerWidthPx, {
        imageIndex: childIndex,
        deltaX: movePreview?.deltaX ?? options?.deltaX,
        deltaY: movePreview?.deltaY ?? options?.deltaY,
        widthPx: options?.widthPxByImageIndex?.get(childIndex),
        heightPx: options?.heightPxByImageIndex?.get(childIndex),
        paragraphTopPx: options?.paragraphTopPx,
        pageMarginTopPx: options?.pageMarginTopPx,
        baseLeftPx: movePreview?.baseLeftPx,
        baseTopPx: movePreview?.baseTopPx,
        allowNegativeImageTop:
          options?.allowNegativeImageTop ?? Boolean(movePreview),
      });
    })
    .filter((candidate): candidate is DualWrappedFloatingImageGeometry =>
      Boolean(candidate)
    );
  if (geometries.length === 0) {
    return undefined;
  }

  const unexcludedLayout = layoutParagraphPretextSource(
    paragraph,
    source,
    Math.max(...geometries.map((geometry) => geometry.containerWidthPx)),
    lineHeightPx,
    []
  );
  const anchorAdjustedGeometries = geometries.map((geometry) => {
    const wrapType = geometry.image.floating?.wrapType?.trim().toLowerCase();
    const verticalRelativeTo = geometry.image.floating?.verticalRelativeTo
      ?.trim()
      .toLowerCase();
    const anchorOffset = paragraphChildAnchorOffset(
      paragraph,
      geometry.imageIndex
    );
    if (
      wrapType === "topandbottom" &&
      anchorOffset <= 0 &&
      syntheticTextBoxActsAsTopAndBottomMasthead(geometry.image) &&
      (verticalRelativeTo === undefined ||
        verticalRelativeTo === "" ||
        verticalRelativeTo === "paragraph" ||
        verticalRelativeTo === "line")
    ) {
      const clampedTopPx = 0;
      if (geometry.imageTopPx <= clampedTopPx) {
        return geometry;
      }

      const deltaTopPx = clampedTopPx - geometry.imageTopPx;
      return {
        ...geometry,
        imageTopPx: geometry.imageTopPx + deltaTopPx,
        exclusion: {
          ...geometry.exclusion,
          top: geometry.exclusion.top + deltaTopPx,
          bottom: geometry.exclusion.bottom + deltaTopPx,
        },
      };
    }

    if (
      !unexcludedLayout ||
      wrapType === "topandbottom" ||
      (verticalRelativeTo !== "margin" && verticalRelativeTo !== "page")
    ) {
      return geometry;
    }

    if (anchorOffset <= 0) {
      return geometry;
    }

    const anchorCaretRect = resolveCaretRectAtOffset(
      unexcludedLayout,
      anchorOffset
    );
    const anchorTopPx = Math.max(0, Math.round(anchorCaretRect?.top ?? 0));
    if (anchorTopPx <= geometry.imageTopPx) {
      return geometry;
    }

    const deltaTopPx = anchorTopPx - geometry.imageTopPx;
    return {
      ...geometry,
      imageTopPx: geometry.imageTopPx + deltaTopPx,
      exclusion: {
        ...geometry.exclusion,
        top: geometry.exclusion.top + deltaTopPx,
        bottom: geometry.exclusion.bottom + deltaTopPx,
      },
    };
  });

  const mergedExclusions = [
    ...(options?.foreignExclusions ?? []),
    ...anchorAdjustedGeometries.map((geometry) => geometry.exclusion),
  ];
  const layout = resolveParagraphPretextExclusionLayout(
    paragraph,
    source,
    Math.max(
      ...anchorAdjustedGeometries.map((geometry) => geometry.containerWidthPx)
    ),
    lineHeightPx,
    mergedExclusions
  );
  if (!layout) {
    return undefined;
  }

  return {
    source,
    geometries: anchorAdjustedGeometries,
    lineHeightPx,
    layout,
  };
}

function resolveParagraphPretextExclusionLayout(
  paragraph: ParagraphNode,
  source: ParagraphPretextLayoutSource,
  containerWidthPx: number,
  lineHeightPx: number,
  exclusions: PretextExclusionRect[]
): PretextVariableWidthLayout | undefined {
  const layout = layoutParagraphPretextSource(
    paragraph,
    source,
    containerWidthPx,
    lineHeightPx,
    exclusions
  );
  if (!layout || layout.lineCount <= 0) {
    return undefined;
  }

  const normalizedAlignment = (paragraph.style?.align ?? "left")
    .trim()
    .toLowerCase();
  const supportsSingleIntervalAlignment =
    normalizedAlignment === "center" || normalizedAlignment === "right";
  const alignedLines = supportsSingleIntervalAlignment
    ? layout.lines.map((line) => {
        if (line.fragments.length === 0) {
          return line;
        }

        const firstFragment = line.fragments[0];
        const lastFragment = line.fragments[line.fragments.length - 1];
        if (!firstFragment || !lastFragment) {
          return line;
        }
        const usesSingleInterval = line.fragments.every(
          (fragment) =>
            fragment.intervalX === firstFragment.intervalX &&
            fragment.intervalWidth === firstFragment.intervalWidth
        );
        if (!usesSingleInterval) {
          return line;
        }

        const occupiedWidthPx =
          lastFragment.x + lastFragment.width - firstFragment.x;

        const freeSpacePx = Math.max(
          0,
          Math.round(
            (firstFragment.intervalWidth ?? occupiedWidthPx) - occupiedWidthPx
          )
        );
        if (freeSpacePx <= 0) {
          return line;
        }

        const shiftPx =
          normalizedAlignment === "center"
            ? Math.round(freeSpacePx / 2)
            : freeSpacePx;
        if (shiftPx <= 0) {
          return line;
        }

        return {
          ...line,
          fragments: line.fragments.map((fragment) => ({
            ...fragment,
            x: fragment.x + shiftPx,
          })),
        };
      })
    : layout.lines;

  return {
    ...layout,
    lines: alignedLines,
  };
}

function checkboxChoiceRowTabWidthPx(paragraph: ParagraphNode): number {
  const checkboxCount = paragraph.children.filter(
    (child) => child.type === "form-field" && child.fieldType === "checkbox"
  ).length;
  const labelCharCount = paragraph.children
    .filter((child): child is TextRunNode => child.type === "text")
    .map((child) => child.text.replace(/\t/g, ""))
    .join("")
    .replace(/\s+/g, "").length;

  if (checkboxCount >= 3) {
    return 6;
  }

  if (labelCharCount <= 8) {
    return 8;
  }

  return 10;
}

function attachTextToPreviousCheckbox(
  paragraph: ParagraphNode,
  childIndex: number,
  text: string
): string {
  if (!text || text[0] !== " ") {
    return text;
  }

  const previousChild =
    childIndex > 0 ? paragraph.children[childIndex - 1] : undefined;
  if (
    previousChild?.type !== "form-field" ||
    previousChild.fieldType !== "checkbox"
  ) {
    return text;
  }

  return `\u00a0${text.slice(1)}`;
}

function runFontSizePx(
  style?: TextRunNode["style"] | FormFieldRunNode["style"]
): number {
  if (
    style?.fontSizePt &&
    Number.isFinite(style.fontSizePt) &&
    style.fontSizePt > 0
  ) {
    return Math.max(9, (style.fontSizePt * 96) / 72);
  }

  return Math.max(9, (DEFAULT_PARAGRAPH_FONT_SIZE_PT * 96) / 72);
}

function explicitRunFontSizePx(
  style?: TextRunNode["style"] | FormFieldRunNode["style"]
): number | undefined {
  if (
    style?.fontSizePt &&
    Number.isFinite(style.fontSizePt) &&
    style.fontSizePt > 0
  ) {
    return Math.max(9, (style.fontSizePt * 96) / 72);
  }

  return undefined;
}

export function resolveDropCapFontSizePx(
  style: TextRunNode["style"] | undefined,
  lineHeightPx: number,
  lineCount: number,
  previewFontSizePx?: number
): number {
  if (Number.isFinite(previewFontSizePx) && (previewFontSizePx as number) > 0) {
    return Math.max(12, Math.round(previewFontSizePx as number));
  }

  const explicitFontSizePx = explicitRunFontSizePx(style);
  if (
    Number.isFinite(explicitFontSizePx) &&
    (explicitFontSizePx as number) > 0
  ) {
    return Math.max(12, Math.round(explicitFontSizePx as number));
  }

  const baseFontSizePx = runFontSizePx(style);
  const derivedFromLinesPx = Math.max(
    18,
    Math.round(lineHeightPx * Math.max(1.6, lineCount * 0.92))
  );
  return Math.max(derivedFromLinesPx, Math.round(baseFontSizePx * 1.8));
}

export function resolveDropCapVisualHeightPx(
  style: TextRunNode["style"] | undefined,
  lineHeightPx: number,
  lineCount: number,
  currentFontSizePx?: number
): number {
  const baseHeightPx = Math.max(18, Math.round(lineHeightPx * lineCount));
  const baselineFontSizePx = resolveDropCapFontSizePx(
    style,
    lineHeightPx,
    lineCount
  );
  const effectiveFontSizePx =
    Number.isFinite(currentFontSizePx) && (currentFontSizePx as number) > 0
      ? Math.max(12, Math.round(currentFontSizePx as number))
      : baselineFontSizePx;
  const scale =
    baselineFontSizePx > 0 ? effectiveFontSizePx / baselineFontSizePx : 1;

  return Math.max(
    18,
    Math.round(baseHeightPx * scale),
    Math.round(effectiveFontSizePx * 1.05)
  );
}

function estimateTextAdvanceWidthPx(
  text: string,
  style?: TextRunNode["style"] | FormFieldRunNode["style"]
): number {
  if (!text) {
    return 0;
  }

  const fontSizePx = runFontSizePx(style);
  const normalized = text.includes("\u00a0")
    ? text.replace(/\u00a0/g, " ")
    : text;
  const cacheKey = `${fontSizePx}\u0000${normalized}`;
  const cached = estimatedTextAdvanceWidthByFontAndValue.get(cacheKey);
  if (cached !== undefined) {
    return cached;
  }

  let total = 0;

  for (let index = 0; index < normalized.length; index += 1) {
    const code = normalized.charCodeAt(index);
    if (code === 10 || code === 13) {
      continue;
    }
    if (code === 9) {
      total += fontSizePx * 2;
      continue;
    }
    if (code === 32) {
      total += fontSizePx * 0.33;
      continue;
    }
    if (
      code === 33 ||
      code === 39 ||
      code === 44 ||
      code === 46 ||
      code === 49 ||
      code === 58 ||
      code === 59 ||
      code === 73 ||
      code === 96 ||
      code === 105 ||
      code === 108 ||
      code === 124
    ) {
      total += fontSizePx * 0.29;
      continue;
    }
    if ((code >= 65 && code <= 90) || (code >= 48 && code <= 57)) {
      total += fontSizePx * 0.6;
      continue;
    }
    if (code >= 0x3000 && code <= 0x9fff) {
      total += fontSizePx * 0.95;
      continue;
    }
    total += fontSizePx * 0.54;
  }

  const estimatedWidthPx = Math.max(0, Math.round(total));
  setCacheEntry(
    estimatedTextAdvanceWidthByFontAndValue,
    cacheKey,
    estimatedWidthPx
  );
  while (
    estimatedTextAdvanceWidthByFontAndValue.size >
    TEXT_MEASURE_CACHE_MAX_ENTRIES
  ) {
    const firstKey = estimatedTextAdvanceWidthByFontAndValue.keys().next()
      .value as string | undefined;
    if (!firstKey) {
      break;
    }
    estimatedTextAdvanceWidthByFontAndValue.delete(firstKey);
  }

  return estimatedWidthPx;
}

function updateEstimatedLineWidthPxForText(
  currentLineWidthPx: number,
  text: string,
  style?: TextRunNode["style"] | FormFieldRunNode["style"]
): number {
  if (!text) {
    return currentLineWidthPx;
  }

  if (!text.includes("\n")) {
    return currentLineWidthPx + estimateTextAdvanceWidthPx(text, style);
  }

  const segments = text.split("\n");
  const trailingSegment = segments[segments.length - 1] ?? "";
  return estimateTextAdvanceWidthPx(trailingSegment, style);
}

function resolveTabSpacerWidthPx(
  tabStopPositionsPx: number[],
  currentLineWidthPx: number,
  fallbackWidthPx: number,
  fixedFallback = false
): number {
  const safeFallback = Math.max(12, Math.round(fallbackWidthPx));
  if (tabStopPositionsPx.length === 0) {
    if (fixedFallback) {
      return safeFallback;
    }
    // Word's default tab behavior: advance to the next multiple of the
    // default tab stop from the current position (not a fixed-width gap).
    const nextStop =
      (Math.floor((currentLineWidthPx + 0.5) / safeFallback) + 1) *
      safeFallback;
    return Math.max(2, Math.round(nextStop - currentLineWidthPx));
  }

  const nextStop = tabStopPositionsPx.find(
    (stop) => stop > currentLineWidthPx + 0.5
  );
  if (nextStop !== undefined) {
    return Math.max(8, Math.round(nextStop - currentLineWidthPx));
  }

  const lastStop = tabStopPositionsPx[tabStopPositionsPx.length - 1] ?? 0;
  const overflow = Math.max(0, currentLineWidthPx - lastStop);
  const stepCount = Math.floor(overflow / safeFallback) + 1;
  const projectedStop = lastStop + stepCount * safeFallback;
  return Math.max(8, Math.round(projectedStop - currentLineWidthPx));
}

function estimateInteractiveFieldWidthPx(field: FormFieldRunNode): number {
  if (field.fieldType === "checkbox") {
    return resolveCheckboxFieldWidthPx(field);
  }

  if (field.fieldType === "date") {
    const text = field.value?.trim() || "MM/DD/YYYY";
    return Math.max(
      68,
      Math.min(220, estimateTextAdvanceWidthPx(text, field.style) + 12)
    );
  }

  if (field.fieldType === "dropdown") {
    const text =
      field.value?.trim() ||
      field.options?.[0]?.displayText?.trim() ||
      field.title?.trim() ||
      "Select";
    return Math.max(
      58,
      Math.min(240, estimateTextAdvanceWidthPx(text, field.style) + 16)
    );
  }

  const defaultText = field.widget?.text?.defaultText?.trim();
  const textValue =
    field.value?.trim() || defaultText || field.title?.trim() || "Click here.";
  return Math.max(
    42,
    Math.min(280, estimateTextAdvanceWidthPx(textValue, field.style) + 12)
  );
}

export function resolveCheckboxFieldWidthPx(field: FormFieldRunNode): number {
  const exactWidgetWidthPx =
    field.widget?.checkbox?.sizeMode === "exact" &&
    Number.isFinite(field.widget.checkbox.sizePt)
      ? Math.max(
          14,
          Math.round(((field.widget.checkbox.sizePt as number) * 96) / 72 + 4)
        )
      : undefined;
  if (Number.isFinite(exactWidgetWidthPx)) {
    return exactWidgetWidthPx as number;
  }

  const checkedSymbol = field.checkedSymbol ?? "☒";
  const uncheckedSymbol = field.uncheckedSymbol ?? "☐";
  return Math.max(
    14,
    estimateTextAdvanceWidthPx(checkedSymbol, field.style),
    estimateTextAdvanceWidthPx(uncheckedSymbol, field.style)
  );
}

function paragraphIsEffectivelyEmpty(paragraph: ParagraphNode): boolean {
  if (paragraphHasImage(paragraph) || paragraphHasFormField(paragraph)) {
    return false;
  }

  return paragraph.children.every(
    (child) => child.type === "text" && child.text.length === 0
  );
}

function paragraphHasDeletedParagraphMark(paragraph: ParagraphNode): boolean {
  if (paragraph.paragraphMarkDeleted === true) {
    return true;
  }

  const sourceXml = paragraph.sourceXml ?? "";
  return /<w:pPr\b[\s\S]*?<w:rPr\b[\s\S]*?<w:del\b/i.test(sourceXml);
}

function paragraphCollapsesIntoPreviousParagraph(
  paragraph: ParagraphNode,
  previousNode?: DocModel["nodes"][number]
): boolean {
  return (
    paragraphIsEffectivelyEmpty(paragraph) &&
    previousNode?.type === "paragraph" &&
    paragraphHasDeletedParagraphMark(previousNode)
  );
}

function paragraphIsStructuralSectionBreakSpacer(
  paragraph: ParagraphNode
): boolean {
  const sourceXml = paragraph.sourceXml ?? "";
  if (!sourceXml || !SECTION_PROPERTIES_XML_PATTERN.test(sourceXml)) {
    return false;
  }

  if (
    !paragraphIsEffectivelyEmpty(paragraph) ||
    paragraphHasExplicitPageBreak(paragraph) ||
    paragraphHasPageBreakBefore(paragraph) ||
    paragraphStartsWithLastRenderedPageBreak(paragraph)
  ) {
    return false;
  }

  if (paragraphLetterheadSideFromIndent(paragraph)) {
    return false;
  }

  return true;
}

function paragraphActsAsSectionBreakCarryoverSpacer(
  model: DocModel,
  nodeIndex: number,
  paragraph: ParagraphNode
): boolean {
  if (
    !paragraphIsEffectivelyEmpty(paragraph) ||
    paragraphHasExplicitPageBreak(paragraph) ||
    paragraphHasPageBreakBefore(paragraph) ||
    paragraphStartsWithLastRenderedPageBreak(paragraph)
  ) {
    return false;
  }

  const nextNode = model.nodes[nodeIndex + 1];
  if (!nextNode || nextNode.type !== "paragraph") {
    return false;
  }

  return paragraphIsSectionBreakAnchorCarryover(nextNode);
}

function paragraphActsAsTrailingRenderedPageBreakSpacer(
  model: DocModel,
  nodeIndex: number,
  paragraph: ParagraphNode
): boolean {
  if (
    !paragraphIsEffectivelyEmpty(paragraph) ||
    paragraphHasExplicitPageBreak(paragraph) ||
    paragraphHasPageBreakBefore(paragraph) ||
    paragraphStartsWithLastRenderedPageBreak(paragraph) ||
    paragraphHasImage(paragraph) ||
    paragraphHasFormField(paragraph)
  ) {
    return false;
  }

  let lookaheadIndex = nodeIndex + 1;
  while (lookaheadIndex < model.nodes.length) {
    const nextNode = model.nodes[lookaheadIndex];
    if (nextNode?.type !== "paragraph") {
      return false;
    }
    if (!paragraphIsEffectivelyEmpty(nextNode)) {
      return (
        paragraphHasPageBreakBefore(nextNode) ||
        paragraphStartsWithLastRenderedPageBreak(nextNode)
      );
    }
    if (
      paragraphHasExplicitPageBreak(nextNode) ||
      paragraphHasPageBreakBefore(nextNode) ||
      paragraphStartsWithLastRenderedPageBreak(nextNode) ||
      paragraphHasImage(nextNode) ||
      paragraphHasFormField(nextNode)
    ) {
      return false;
    }
    lookaheadIndex += 1;
  }

  return false;
}

function paragraphContextualSpacingStyleKey(
  paragraph: ParagraphNode
): string | undefined {
  const styleId = paragraph.style?.styleId?.trim().toLowerCase();
  if (styleId) {
    return `id:${styleId}`;
  }

  const styleName = paragraph.style?.styleName?.trim().toLowerCase();
  if (styleName) {
    return `name:${styleName}`;
  }

  return undefined;
}

function paragraphsSuppressInterParagraphSpacing(
  previousParagraph: ParagraphNode | undefined,
  currentParagraph: ParagraphNode
): boolean {
  if (!previousParagraph) {
    return false;
  }

  const previousStyleKey =
    paragraphContextualSpacingStyleKey(previousParagraph);
  const currentStyleKey = paragraphContextualSpacingStyleKey(currentParagraph);
  if (!previousStyleKey || previousStyleKey !== currentStyleKey) {
    return false;
  }

  return (
    previousParagraph.style?.contextualSpacing === true ||
    currentParagraph.style?.contextualSpacing === true
  );
}

function effectiveParagraphAfterSpacingPx(
  model: DocModel,
  nodeIndex: number,
  paragraph: ParagraphNode
): number {
  const afterSpacingPx = paragraphAfterSpacingPx(paragraph);
  const nextNode = model.nodes[nodeIndex + 1];
  if (nextNode?.type !== "paragraph") {
    return afterSpacingPx;
  }

  return paragraphsSuppressInterParagraphSpacing(paragraph, nextNode)
    ? 0
    : afterSpacingPx;
}

function effectiveParagraphBeforeSpacingPx(
  model: DocModel,
  nodeIndex: number,
  paragraph: ParagraphNode,
  pageConsumedHeightPx: number,
  suppressSpacingBeforeAfterPageBreak: boolean
): number {
  let beforeSpacingPx = resolveParagraphBeforeSpacingPx(
    model,
    nodeIndex,
    paragraph,
    pageConsumedHeightPx,
    suppressSpacingBeforeAfterPageBreak
  );

  if (pageConsumedHeightPx <= 0) {
    return beforeSpacingPx;
  }

  const previousNode = model.nodes[nodeIndex - 1];
  if (previousNode?.type !== "paragraph") {
    return beforeSpacingPx;
  }

  return paragraphsSuppressInterParagraphSpacing(previousNode, paragraph)
    ? 0
    : beforeSpacingPx;
}

function nodeHasSubstantiveContentForPagination(
  node: DocModel["nodes"][number]
): boolean {
  if (node.type === "table") {
    return true;
  }

  if (
    paragraphHasVisibleText(node) ||
    paragraphHasImage(node) ||
    paragraphHasFormField(node)
  ) {
    return true;
  }

  return (
    paragraphHasPageBreakBefore(node) ||
    paragraphHasExplicitPageBreak(node) ||
    sectionBreakAfterParagraphStartsNewPage(node)
  );
}

function paragraphBookmarkNames(paragraph: ParagraphNode): string[] {
  const sourceXml = paragraph.sourceXml ?? "";
  if (!sourceXml) {
    return [];
  }

  const names = [
    ...sourceXml.matchAll(new RegExp(BOOKMARK_START_XML_PATTERN.source, "gi")),
  ]
    .map((match) => match[1]?.trim())
    .filter((name): name is string =>
      Boolean(name && name.length > 0 && name !== "_GoBack")
    );
  return [...new Set(names)];
}

function paragraphReferencedNoteIds(
  paragraph: ParagraphNode,
  noteType: "footnote" | "endnote"
): number[] {
  const sourceXml = paragraph.sourceXml ?? "";
  if (!sourceXml) {
    return [];
  }

  const pattern =
    noteType === "footnote"
      ? new RegExp(FOOTNOTE_REFERENCE_XML_PATTERN.source, "gi")
      : new RegExp(ENDNOTE_REFERENCE_XML_PATTERN.source, "gi");
  const references: number[] = [];

  for (const match of sourceXml.matchAll(pattern)) {
    const rawId = Number(match[1]);
    if (!Number.isFinite(rawId) || rawId < 0) {
      continue;
    }
    references.push(Math.round(rawId));
  }

  return references;
}

function nodeReferencedNoteIds(
  node: DocModel["nodes"][number],
  noteType: "footnote" | "endnote",
  tableRowRange?: TableRowRange,
  paragraphLineRange?: ParagraphLineRange
): number[] {
  if (node.type === "paragraph") {
    if (paragraphLineRange && paragraphLineRange.startLineIndex > 0) {
      return [];
    }
    return paragraphReferencedNoteIds(node, noteType);
  }

  const references: number[] = [];
  const startRowIndex = Math.max(0, tableRowRange?.startRowIndex ?? 0);
  const endRowIndex = Math.min(
    node.rows.length,
    tableRowRange?.endRowIndex ?? node.rows.length
  );
  for (let rowIndex = startRowIndex; rowIndex < endRowIndex; rowIndex += 1) {
    const row = node.rows[rowIndex];
    row?.cells.forEach((cell) => {
      tableCellParagraphsRecursively(cell.nodes).forEach((paragraph) => {
        references.push(...paragraphReferencedNoteIds(paragraph, noteType));
      });
    });
  }
  return references;
}

function eventTargetIsInteractiveControl(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }

  return Boolean(
    target.closest("a[href],button,input,select,textarea,[role='checkbox']")
  );
}

function eventTargetIsNestedTableParagraphEditor(
  target: EventTarget | null
): boolean {
  if (!(target instanceof Element)) {
    return false;
  }

  return Boolean(
    target.closest("[data-docx-table-cell-paragraph-host='true']")
  );
}

function sectionBreakPropertiesStartNewPage(
  sectionPropertiesXml: string
): boolean {
  const sectionType =
    sectionPropertiesXml
      .match(SECTION_TYPE_XML_PATTERN)?.[1]
      ?.trim()
      .toLowerCase() ?? "nextpage";

  if (sectionType === "continuous") {
    return false;
  }

  if (sectionType === "nextcolumn") {
    const columnsTag = sectionPropertiesXml.match(/<w:cols\b[^>]*\/?>/i)?.[0];
    const columnsCount = Number.parseInt(
      columnsTag?.match(/\bw:num="(\d+)"/i)?.[1] ?? "",
      10
    );
    // In single-column sections, "nextColumn" effectively behaves like "nextPage".
    return !Number.isFinite(columnsCount) || columnsCount <= 1;
  }

  return true;
}

function paragraphHasExplicitPageBreak(paragraph: ParagraphNode): boolean {
  const xml = paragraph.sourceXml ?? "";
  if (!xml) {
    return false;
  }

  const cached = paragraphBreakFlagsBySourceXml.get(xml);
  if (cached) {
    return cached.explicitPageBreak;
  }

  const flags = {
    explicitPageBreak: PAGE_BREAK_XML_PATTERN.test(xml),
    explicitColumnBreak: COLUMN_BREAK_XML_PATTERN.test(xml),
    lastRenderedPageBreak: LAST_RENDERED_PAGE_BREAK_XML_PATTERN.test(xml),
    pageBreakBefore: isOnOffTagEnabled(
      xml.match(PAGE_BREAK_BEFORE_XML_PATTERN)?.[0]
    ),
    sectionBreakStartsNewPage: (() => {
      const sectionProperties = xml.match(SECTION_PROPERTIES_XML_PATTERN)?.[0];
      if (!sectionProperties) {
        return false;
      }
      // ECMA-376 §2.6.22: omitted <w:type> defaults to nextPage.
      return sectionBreakPropertiesStartNewPage(sectionProperties);
    })(),
  };
  setCacheEntry(paragraphBreakFlagsBySourceXml, xml, flags);
  return flags.explicitPageBreak;
}

function paragraphHasExplicitColumnBreak(paragraph: ParagraphNode): boolean {
  const xml = paragraph.sourceXml ?? "";
  if (!xml) {
    return false;
  }

  const cached = paragraphBreakFlagsBySourceXml.get(xml);
  if (cached) {
    return cached.explicitColumnBreak;
  }

  paragraphHasExplicitPageBreak(paragraph);
  return paragraphBreakFlagsBySourceXml.get(xml)?.explicitColumnBreak ?? false;
}

function paragraphHasLastRenderedPageBreak(paragraph: ParagraphNode): boolean {
  const xml = paragraph.sourceXml ?? "";
  if (!xml) {
    return false;
  }

  const cached = paragraphBreakFlagsBySourceXml.get(xml);
  if (cached) {
    return cached.lastRenderedPageBreak;
  }

  paragraphHasExplicitPageBreak(paragraph);
  return (
    paragraphBreakFlagsBySourceXml.get(xml)?.lastRenderedPageBreak ?? false
  );
}

function paragraphStartsWithLastRenderedPageBreak(
  paragraph: ParagraphNode
): boolean {
  const xml = paragraph.sourceXml ?? "";
  if (!xml || !paragraphHasLastRenderedPageBreak(paragraph)) {
    return false;
  }

  const breakMatch = xml.match(LAST_RENDERED_PAGE_BREAK_XML_PATTERN);
  if (!breakMatch || breakMatch.index === undefined) {
    return false;
  }

  const leadingXml = xml
    .slice(0, breakMatch.index)
    .replace(/^<w:p\b[^>]*>/i, "")
    .replace(/<w:pPr\b(?:[^/>]*\/>|[\s\S]*?<\/w:pPr>)/i, "")
    .replace(/<w:rPr\b[\s\S]*?<\/w:rPr>/gi, "")
    .replace(/<\/?w:r\b[^>]*>/gi, "")
    .replace(
      /<w:(?:proofErr|bookmarkStart|bookmarkEnd|permStart|permEnd)\b[^>]*\/?>/gi,
      ""
    )
    .replace(/<\/?w:(?:ins|smartTag)\b[^>]*>/gi, "")
    .replace(/\s+/g, "");

  return leadingXml.length === 0;
}

function shouldHonorParagraphStartLastRenderedPageBreak(params: {
  pageConsumedHeightPx: number;
  pageContentHeightPx: number;
}): boolean {
  const pageConsumedHeightPx = Math.max(
    0,
    Math.round(params.pageConsumedHeightPx)
  );
  const pageContentHeightPx = Math.max(
    0,
    Math.round(params.pageContentHeightPx)
  );
  if (pageConsumedHeightPx <= 0 || pageContentHeightPx <= 0) {
    return false;
  }

  const remainingHeightPx = Math.max(
    0,
    pageContentHeightPx - pageConsumedHeightPx
  );
  const maxAllowedRemainingHeightPx = clampNumber(
    Math.round(
      pageContentHeightPx *
        LAST_RENDERED_PAGE_BREAK_HINT_MAX_REMAINING_SPACE_RATIO
    ),
    LAST_RENDERED_PAGE_BREAK_HINT_MIN_REMAINING_SPACE_PX,
    LAST_RENDERED_PAGE_BREAK_HINT_MAX_REMAINING_SPACE_PX
  );
  return remainingHeightPx <= maxAllowedRemainingHeightPx;
}

function isOnOffTagEnabled(tagXml: string | undefined): boolean {
  if (!tagXml) {
    return false;
  }

  const valueMatch = tagXml
    .match(/\bw:val="([^"]+)"/i)?.[1]
    ?.trim()
    .toLowerCase();
  if (!valueMatch) {
    return true;
  }

  return !["0", "false", "off", "no"].includes(valueMatch);
}

function sectionTitlePageEnabled(sectionPropertiesXml?: string): boolean {
  if (!sectionPropertiesXml) {
    return false;
  }

  const titlePageTag = sectionPropertiesXml.match(
    /<w:titlePg\b[^>]*\/?>/i
  )?.[0];
  return isOnOffTagEnabled(titlePageTag);
}

function selectSectionVariantForPage<T extends HeaderSection | FooterSection>(
  sections: T[],
  sectionPropertiesXml: string | undefined,
  pageIndex: number,
  options?: {
    evenAndOddHeaders?: boolean;
  }
): T | undefined {
  if (sections.length === 0) {
    return undefined;
  }

  const titlePage = sectionTitlePageEnabled(sectionPropertiesXml);
  const normalizeType = (value: string | undefined): string =>
    value?.trim().toLowerCase() ?? "";
  const first = sections.find(
    (section) => normalizeType(section.referenceType) === "first"
  );
  const defaultSection = sections.find((section) => {
    const referenceType = normalizeType(section.referenceType);
    return referenceType === "default" || referenceType === "";
  });
  const even = sections.find(
    (section) => normalizeType(section.referenceType) === "even"
  );
  const evenAndOddHeadersEnabled = options?.evenAndOddHeaders ?? true;

  const safePageIndex = Number.isFinite(pageIndex)
    ? Math.max(0, Math.round(pageIndex))
    : 0;
  const oddPageNumber = safePageIndex % 2 === 0;

  if (safePageIndex === 0 && titlePage) {
    return first;
  }

  if (evenAndOddHeadersEnabled && !oddPageNumber && even) {
    return even;
  }

  if (defaultSection) {
    return defaultSection;
  }

  return first ?? even ?? sections[0];
}

function resolveSectionIndexForNodeIndex(
  sections: ResolvedDocumentSection[],
  nodeIndex: number,
  previousSectionIndex: number
): number {
  if (sections.length === 0) {
    return 0;
  }

  const safePrevious = Math.max(
    0,
    Math.min(previousSectionIndex, sections.length - 1)
  );
  let sectionIndex = safePrevious;

  if (nodeIndex < sections[sectionIndex].startNodeIndex) {
    sectionIndex = 0;
  }

  while (
    sectionIndex + 1 < sections.length &&
    sections[sectionIndex + 1].startNodeIndex <= nodeIndex
  ) {
    sectionIndex += 1;
  }

  return sectionIndex;
}

function paragraphHasVisibleBorder(paragraph: ParagraphNode): boolean {
  return (
    paragraphBorderVisible(paragraph.style?.borders?.top) ||
    paragraphBorderVisible(paragraph.style?.borders?.right) ||
    paragraphBorderVisible(paragraph.style?.borders?.bottom) ||
    paragraphBorderVisible(paragraph.style?.borders?.left) ||
    paragraphBorderVisible(paragraph.style?.borders?.between) ||
    paragraphBorderVisible(paragraph.style?.borders?.bar)
  );
}

function paragraphIsSectionBreakAnchorCarryover(
  paragraph: ParagraphNode
): boolean {
  if (!paragraph.sourceXml?.includes("<w:sectPr")) {
    return false;
  }

  // Section-break anchor paragraphs can legitimately carry a text-bearing
  // synthetic textbox duplicate used by Word's floating-shape anchoring.
  // Treat these as carryover anchors so they don't consume a standalone page.
  if (!paragraphIsAbsoluteFloatingImageAnchorOnly(paragraph)) {
    return false;
  }

  if (paragraphHasVisibleText(paragraph) || paragraphHasFormField(paragraph)) {
    return false;
  }

  return !paragraphHasVisibleBorder(paragraph);
}

function resolveSectionIndexForPageSegments(
  sections: ResolvedDocumentSection[],
  nodes: DocModel["nodes"],
  pageSegments: DocumentPageNodeSegment[],
  previousSectionIndex: number
): number {
  if (sections.length === 0 || pageSegments.length === 0) {
    return 0;
  }

  const firstNodeIndex = pageSegments[0]?.nodeIndex;
  let sectionIndex = Number.isFinite(firstNodeIndex)
    ? resolveSectionIndexForNodeIndex(
        sections,
        firstNodeIndex as number,
        previousSectionIndex
      )
    : Math.max(0, Math.min(previousSectionIndex, sections.length - 1));
  sectionIndex = Math.max(
    sectionIndex,
    Math.max(0, Math.min(previousSectionIndex, sections.length - 1))
  );

  let walkingSectionIndex = sectionIndex;
  for (const segment of pageSegments) {
    walkingSectionIndex = resolveSectionIndexForNodeIndex(
      sections,
      segment.nodeIndex,
      walkingSectionIndex
    );
    if (walkingSectionIndex <= sectionIndex) {
      continue;
    }

    const nextSection = sections[walkingSectionIndex];
    if (!nextSection) {
      continue;
    }

    if (
      parseSectionStartType(nextSection.sectionPropertiesXml) === "continuous"
    ) {
      continue;
    }

    const candidateNode = nodes[segment.nodeIndex];
    if (
      candidateNode?.type === "paragraph" &&
      paragraphIsSectionBreakAnchorCarryover(candidateNode)
    ) {
      continue;
    }

    sectionIndex = walkingSectionIndex;
  }

  return sectionIndex;
}

function paragraphHasPageBreakBefore(paragraph: ParagraphNode): boolean {
  if (paragraph.style?.pageBreakBefore === true) {
    return true;
  }

  const xml = paragraph.sourceXml ?? "";
  if (!xml) {
    return false;
  }

  const cached = paragraphBreakFlagsBySourceXml.get(xml);
  if (cached) {
    return cached.pageBreakBefore;
  }

  paragraphHasExplicitPageBreak(paragraph);
  return paragraphBreakFlagsBySourceXml.get(xml)?.pageBreakBefore ?? false;
}

function sectionBreakAfterParagraphStartsNewPage(
  paragraph: ParagraphNode
): boolean {
  const xml = paragraph.sourceXml ?? "";
  if (!xml) {
    return false;
  }

  const cached = paragraphBreakFlagsBySourceXml.get(xml);
  if (cached) {
    return cached.sectionBreakStartsNewPage;
  }

  // `paragraphHasExplicitPageBreak` populates the shared cache with every
  // break-related flag, including `sectionBreakStartsNewPage`. The cache
  // read below has to happen unconditionally — a section break paragraph
  // is allowed to start a new page (per ECMA-376 §2.6.22) even when it
  // carries no explicit `<w:br w:type="page"/>` run, and an earlier
  // version of this helper returned `false` in exactly that case.
  paragraphHasExplicitPageBreak(paragraph);
  return (
    paragraphBreakFlagsBySourceXml.get(xml)?.sectionBreakStartsNewPage ?? false
  );
}

function nodeAlreadyEndsAtExplicitPageBoundary(
  node: DocModel["nodes"][number] | undefined
): boolean {
  if (!node || node.type !== "paragraph") {
    return false;
  }

  return (
    paragraphHasExplicitPageBreak(node) ||
    paragraphHasPageBreakBefore(node) ||
    sectionBreakAfterParagraphStartsNewPage(node)
  );
}

const docxHardPageBreakStartNodeIndexesByModel = new WeakMap<
  DocModel,
  Set<number>
>();

function collectDocxHardPageBreakStartNodeIndexes(
  model: DocModel
): Set<number> {
  const cached = docxHardPageBreakStartNodeIndexesByModel.get(model);
  if (cached) {
    return cached;
  }
  const result = computeDocxHardPageBreakStartNodeIndexes(model);
  docxHardPageBreakStartNodeIndexesByModel.set(model, result);
  return result;
}

function computeDocxHardPageBreakStartNodeIndexes(
  model: DocModel
): Set<number> {
  const breaks = collectTopLevelExplicitPageBreakStartNodeIndexes(model.nodes);

  const sections = resolveDocumentSectionsFromMetadata(model.metadata);
  for (
    let sectionIndex = 1;
    sectionIndex < sections.length;
    sectionIndex += 1
  ) {
    const section = sections[sectionIndex];
    const startNodeIndex = Math.max(0, Math.round(section.startNodeIndex));
    if (startNodeIndex <= 0 || startNodeIndex >= model.nodes.length) {
      continue;
    }

    const sectionPropertiesXml = section.sectionPropertiesXml;
    if (!sectionPropertiesXml) {
      continue;
    }

    // Section break pagination should follow the section that starts at this
    // node index, which is represented by metadata.sections.
    if (sectionBreakPropertiesStartNewPage(sectionPropertiesXml)) {
      breaks.add(startNodeIndex);
    }
  }

  for (const breakIndex of [...breaks]) {
    if (breakIndex <= 0 || breakIndex >= model.nodes.length) {
      breaks.delete(breakIndex);
    }
  }

  return breaks;
}

const docxSectionStartPageBreakNodeIndexesByModel = new WeakMap<
  DocModel,
  Set<number>
>();

function collectDocxSectionStartPageBreakNodeIndexes(
  model: DocModel
): Set<number> {
  const cached = docxSectionStartPageBreakNodeIndexesByModel.get(model);
  if (cached) {
    return cached;
  }
  const result = computeDocxSectionStartPageBreakNodeIndexes(model);
  docxSectionStartPageBreakNodeIndexesByModel.set(model, result);
  return result;
}

function computeDocxSectionStartPageBreakNodeIndexes(
  model: DocModel
): Set<number> {
  const breaks = new Set<number>();
  const sections = resolveDocumentSectionsFromMetadata(model.metadata);
  for (
    let sectionIndex = 1;
    sectionIndex < sections.length;
    sectionIndex += 1
  ) {
    const section = sections[sectionIndex];
    const startNodeIndex = Math.max(0, Math.round(section.startNodeIndex));
    if (startNodeIndex <= 0 || startNodeIndex >= model.nodes.length) {
      continue;
    }

    const sectionPropertiesXml = section.sectionPropertiesXml;
    if (!sectionPropertiesXml) {
      continue;
    }

    if (sectionBreakPropertiesStartNewPage(sectionPropertiesXml)) {
      breaks.add(startNodeIndex);
    }
  }

  return breaks;
}

function buildNextHardBreakStartNodeIndexLookup(
  nodeCount: number,
  hardBreakStartNodeIndexes: Set<number>
): number[] {
  const nextBreakStartNodeIndexes = new Array<number>(nodeCount).fill(-1);
  let nextBreakStartNodeIndex = -1;

  for (let nodeIndex = nodeCount - 1; nodeIndex >= 0; nodeIndex -= 1) {
    nextBreakStartNodeIndexes[nodeIndex] = nextBreakStartNodeIndex;
    if (hardBreakStartNodeIndexes.has(nodeIndex)) {
      nextBreakStartNodeIndex = nodeIndex;
    }
  }

  return nextBreakStartNodeIndexes;
}

function paragraphIsSimpleTrailingSectionTailCandidate(
  paragraph: ParagraphNode
): boolean {
  if (
    !paragraphHasVisibleText(paragraph) ||
    paragraphHasImage(paragraph) ||
    paragraphHasFormField(paragraph) ||
    paragraphHasExplicitPageBreak(paragraph) ||
    paragraphHasPageBreakBefore(paragraph) ||
    paragraphStartsWithLastRenderedPageBreak(paragraph) ||
    sectionBreakAfterParagraphStartsNewPage(paragraph)
  ) {
    return false;
  }

  return true;
}

function shouldKeepTrailingSectionTailOnCurrentPage(
  model: DocModel,
  startNodeIndex: number,
  pageConsumedHeightPx: number,
  previousParagraphAfterPx: number,
  pageContentWidthPx: number,
  pageContentHeightPx: number,
  hardBreakStartNodeIndexes: Set<number>,
  sectionStartPageBreakNodeIndexes: Set<number>,
  nextHardBreakStartNodeIndexByNodeIndex: number[],
  estimateNodeHeightPx: (
    nodeIndex: number,
    node: DocModel["nodes"][number],
    pageContentWidthPx: number
  ) => number,
  resolveNodeBeforeSpacingPx: (
    nodeIndex: number,
    paragraph: ParagraphNode,
    consumedHeightPx: number
  ) => number,
  resolveNodeAfterSpacingPx: (
    nodeIndex: number,
    paragraph: ParagraphNode
  ) => number
): boolean {
  if (pageConsumedHeightPx <= 0 || startNodeIndex <= 0) {
    return false;
  }

  const nextHardBreakStartNodeIndex =
    nextHardBreakStartNodeIndexByNodeIndex[startNodeIndex] ?? -1;
  if (
    nextHardBreakStartNodeIndex <= startNodeIndex ||
    !hardBreakStartNodeIndexes.has(nextHardBreakStartNodeIndex) ||
    !sectionStartPageBreakNodeIndexes.has(nextHardBreakStartNodeIndex)
  ) {
    return false;
  }

  const remainingHeightPx = pageContentHeightPx - pageConsumedHeightPx;

  let tailConsumedHeightPx = 0;
  let tailPreviousParagraphAfterPx = previousParagraphAfterPx;
  let substantiveParagraphCount = 0;

  for (
    let nodeIndex = startNodeIndex;
    nodeIndex < nextHardBreakStartNodeIndex;
    nodeIndex += 1
  ) {
    const node = model.nodes[nodeIndex];
    if (
      node.type === "paragraph" &&
      paragraphIsStructuralSectionBreakSpacer(node)
    ) {
      tailPreviousParagraphAfterPx = 0;
      continue;
    }
    if (
      node.type === "paragraph" &&
      paragraphActsAsLeadingCoverLayoutOverlay(
        model,
        nodeIndex,
        node,
        pageContentWidthPx,
        pageContentHeightPx
      )
    ) {
      return false;
    }
    if (
      node.type === "paragraph" &&
      paragraphActsAsDecorativeBehindTextBackgroundOverlay(node)
    ) {
      return false;
    }
    if (
      node.type === "paragraph" &&
      paragraphCollapsesIntoPreviousParagraph(node, model.nodes[nodeIndex - 1])
    ) {
      continue;
    }
    if (node.type !== "paragraph") {
      return false;
    }
    if (!paragraphIsSimpleTrailingSectionTailCandidate(node)) {
      return false;
    }

    substantiveParagraphCount += 1;
    if (substantiveParagraphCount > MAX_TRAILING_SECTION_TAIL_PARAGRAPHS) {
      return false;
    }

    const directBeforeSpacingPx = paragraphBeforeSpacingPx(node);
    const directAfterSpacingPx = paragraphAfterSpacingPx(node);
    const nodeBeforeSpacingPx = resolveNodeBeforeSpacingPx(
      nodeIndex,
      node,
      pageConsumedHeightPx + tailConsumedHeightPx
    );
    const nodeAfterSpacingPx = resolveNodeAfterSpacingPx(nodeIndex, node);
    const rawNodeHeightPx = Math.max(
      1,
      estimateNodeHeightPx(nodeIndex, node, pageContentWidthPx) -
        directBeforeSpacingPx -
        directAfterSpacingPx +
        nodeBeforeSpacingPx +
        nodeAfterSpacingPx
    );
    const collapsedMarginPx =
      pageConsumedHeightPx + tailConsumedHeightPx > 0
        ? Math.min(tailPreviousParagraphAfterPx, nodeBeforeSpacingPx)
        : 0;
    const effectiveNodeHeightPx = Math.max(
      1,
      rawNodeHeightPx - collapsedMarginPx
    );
    tailConsumedHeightPx += effectiveNodeHeightPx;
    tailPreviousParagraphAfterPx = nodeAfterSpacingPx;
  }

  if (substantiveParagraphCount === 0) {
    return false;
  }

  const overflowPx = tailConsumedHeightPx - remainingHeightPx;
  return (
    overflowPx > PAGE_OVERFLOW_TOLERANCE_PX &&
    overflowPx <= MAX_TRAILING_SECTION_TAIL_OVERFLOW_PX
  );
}

function paragraphDominantFontSizePt(
  paragraph: ParagraphNode
): number | undefined {
  const weightByFontSizePt = new Map<number, number>();

  const addWeight = (fontSizePt: number | undefined, weight: number): void => {
    if (
      !Number.isFinite(fontSizePt) ||
      (fontSizePt as number) <= 0 ||
      weight <= 0
    ) {
      return;
    }

    const key = Number((fontSizePt as number).toFixed(2));
    weightByFontSizePt.set(key, (weightByFontSizePt.get(key) ?? 0) + weight);
  };

  paragraph.children.forEach((child) => {
    if (child.type !== "text" && child.type !== "form-field") {
      return;
    }

    const text =
      child.type === "text"
        ? child.text.replace(/\u2063/g, "")
        : formFieldDisplayValue(child);
    const textWeight = Math.max(1, text.length);
    addWeight(child.style?.fontSizePt, textWeight);
  });

  if (weightByFontSizePt.size === 0) {
    return undefined;
  }

  let dominantFontSizePt: number | undefined;
  let dominantWeight = -1;

  for (const [fontSizePt, weight] of weightByFontSizePt) {
    if (
      weight > dominantWeight ||
      (weight === dominantWeight &&
        (dominantFontSizePt === undefined || fontSizePt > dominantFontSizePt))
    ) {
      dominantWeight = weight;
      dominantFontSizePt = fontSizePt;
    }
  }

  return dominantFontSizePt;
}

function paragraphBaseFontSizePx(paragraph: ParagraphNode): number {
  const cached = paragraphBaseFontSizePxByParagraph.get(paragraph);
  if (cached !== undefined) {
    return cached;
  }

  const dominantRunFontSizePt = paragraphDominantFontSizePt(paragraph);
  const fontSizePt =
    dominantRunFontSizePt && dominantRunFontSizePt > 0
      ? dominantRunFontSizePt
      : Number.isFinite(paragraph.style?.headingLevel)
      ? DEFAULT_PARAGRAPH_FONT_SIZE_PT +
        Math.max(0, 6 - (paragraph.style?.headingLevel ?? 6))
      : DEFAULT_PARAGRAPH_FONT_SIZE_PT;

  const baseFontSizePx = Math.max(10, Math.round((fontSizePt * 96) / 72));
  paragraphBaseFontSizePxByParagraph.set(paragraph, baseFontSizePx);
  return baseFontSizePx;
}

function paragraphMaxFontSizePx(paragraph: ParagraphNode): number {
  const paragraphBaseFontPx = paragraphBaseFontSizePx(paragraph);
  let maxFontSizePx = paragraphBaseFontPx;

  paragraph.children.forEach((child) => {
    if (child.type !== "text" && child.type !== "form-field") {
      return;
    }
    maxFontSizePx = Math.max(
      maxFontSizePx,
      resolveMeasureFontSizePx(child.style, paragraphBaseFontPx)
    );
  });

  return Math.max(1, Math.round(maxFontSizePx));
}

function normalizeFontFamilyToken(fontFamily?: string): string | undefined {
  if (!fontFamily) {
    return undefined;
  }

  const [firstToken] = fontFamily.split(",");
  const normalized = firstToken
    ?.trim()
    .replace(/^['"]+|['"]+$/g, "")
    .toLowerCase();
  return normalized || undefined;
}

function paragraphDominantFontFamily(
  paragraph: ParagraphNode
): string | undefined {
  const cached = paragraphDominantFontFamilyByParagraph.get(paragraph);
  if (cached !== undefined) {
    return cached ?? undefined;
  }

  const weightByFamily = new Map<string, number>();
  const addWeight = (fontFamily: string | undefined, weight: number): void => {
    const normalizedFamily = normalizeFontFamilyToken(fontFamily);
    if (!normalizedFamily) {
      return;
    }

    const safeWeight = Math.max(1, Math.round(weight));
    weightByFamily.set(
      normalizedFamily,
      (weightByFamily.get(normalizedFamily) ?? 0) + safeWeight
    );
  };

  paragraph.children.forEach((child) => {
    if (child.type !== "text" && child.type !== "form-field") {
      return;
    }

    const text =
      child.type === "text"
        ? child.text.replace(/\u2063/g, "")
        : formFieldDisplayValue(child);
    addWeight(child.style?.fontFamily, text.length);
  });

  if (weightByFamily.size === 0) {
    paragraphDominantFontFamilyByParagraph.set(paragraph, null);
    return undefined;
  }

  let dominantFamily: string | undefined;
  let dominantWeight = -1;
  for (const [family, weight] of weightByFamily) {
    if (weight > dominantWeight) {
      dominantFamily = family;
      dominantWeight = weight;
    }
  }

  paragraphDominantFontFamilyByParagraph.set(paragraph, dominantFamily ?? null);
  return dominantFamily;
}

function singleLineAutoScaleForFontFamily(fontFamily?: string): number {
  const normalized =
    normalizeFontFamilyToken(fontFamily) ?? fontFamily?.toLowerCase();
  if (!normalized) {
    return WORD_SINGLE_LINE_AUTO_SCALE;
  }

  if (
    normalized === "times roman" ||
    normalized === "times new roman" ||
    normalized === "cambria" ||
    normalized === "garamond" ||
    normalized === "georgia" ||
    normalized === "book antiqua" ||
    normalized === "palatino linotype"
  ) {
    return WORD_SINGLE_LINE_AUTO_SCALE_SERIF;
  }

  if (normalized === "arial") {
    return WORD_SINGLE_LINE_AUTO_SCALE_SANS;
  }

  return WORD_SINGLE_LINE_AUTO_SCALE;
}

function emptyParagraphLineScaleForFontFamily(fontFamily?: string): number {
  const normalized =
    normalizeFontFamilyToken(fontFamily) ?? fontFamily?.toLowerCase();
  if (!normalized) {
    return WORD_EMPTY_PARAGRAPH_LINE_SCALE;
  }

  if (
    normalized === "times roman" ||
    normalized === "times new roman" ||
    normalized === "cambria" ||
    normalized === "garamond" ||
    normalized === "georgia" ||
    normalized === "book antiqua" ||
    normalized === "palatino linotype"
  ) {
    return WORD_EMPTY_PARAGRAPH_LINE_SCALE_SERIF;
  }

  if (normalized === "arial") {
    return WORD_EMPTY_PARAGRAPH_LINE_SCALE_SANS;
  }

  return WORD_EMPTY_PARAGRAPH_LINE_SCALE;
}

// A paragraph whose only content is whitespace and/or floating anchors lays
// out as one empty line at the mark font's natural metrics — floating
// objects never contribute to the line box.
function paragraphRendersTextFreeLine(paragraph: ParagraphNode): boolean {
  return (
    paragraphHasOnlyWhitespaceText(paragraph) ||
    paragraphIsFloatingImageAnchorOnly(paragraph)
  );
}

function resolveParagraphSingleLineAutoScale(
  paragraph: ParagraphNode,
  fontFamily?: string
): number {
  if (paragraphRendersTextFreeLine(paragraph)) {
    return emptyParagraphLineScaleForFontFamily(fontFamily);
  }

  const baseScale = singleLineAutoScaleForFontFamily(fontFamily);
  return paragraphHasCheckboxFormField(paragraph)
    ? Math.max(1.08, baseScale)
    : baseScale;
}

function paragraphLineCount(paragraph: ParagraphNode): number {
  return paragraphLineCountWithinWidth(paragraph);
}

function paragraphContainsExplicitLineBreakText(
  paragraph: ParagraphNode
): boolean {
  return paragraph.children.some(
    (child) => child.type === "text" && /[\r\n]/.test(child.text)
  );
}

function estimatedGlyphWidthPx(character: string, fontSizePx: number): number {
  if (/\s/.test(character)) {
    return fontSizePx * 0.34;
  }
  if (/[A-Z]/.test(character)) {
    return fontSizePx * 0.66;
  }
  if (/[a-z]/.test(character)) {
    return fontSizePx * 0.55;
  }
  if (/[0-9]/.test(character)) {
    return fontSizePx * 0.57;
  }
  if (/[\u2e80-\u9fff\u3040-\u30ff\uac00-\ud7af]/.test(character)) {
    return fontSizePx;
  }

  return fontSizePx * 0.48;
}

function fallbackMeasureTextWidthPx(text: string, fontSizePx: number): number {
  let widthPx = 0;
  for (const character of text) {
    widthPx += estimatedGlyphWidthPx(character, fontSizePx);
  }
  return widthPx;
}

function resolveMeasureFontSizePx(
  style: TextRunNode["style"] | FormFieldRunNode["style"] | undefined,
  paragraphBaseFontPx: number
): number {
  const runFontSizePx =
    Number.isFinite(style?.fontSizePt) && (style?.fontSizePt as number) > 0
      ? ((style?.fontSizePt as number) * 96) / 72
      : paragraphBaseFontPx;
  const verticalAlignScale =
    style?.verticalAlign === "superscript" ||
    style?.verticalAlign === "subscript"
      ? SCRIPT_FONT_SCALE
      : 1;
  return Math.max(8, Math.round(runFontSizePx * verticalAlignScale));
}

function resolveMeasureFont(
  style: TextRunNode["style"] | FormFieldRunNode["style"] | undefined,
  paragraphBaseFontPx: number
): string {
  const fontStyle = style?.italic ? "italic" : "normal";
  const fontWeight = style?.bold ? "700" : "400";
  const fontSizePx = resolveMeasureFontSizePx(style, paragraphBaseFontPx);
  const fontFamily =
    cssFontFamily(style?.fontFamily) ??
    cssFontFamily(DEFAULT_UNSPECIFIED_DOCX_FONT_FAMILY) ??
    '"Times New Roman", serif';
  return `${fontStyle} normal ${fontWeight} ${fontSizePx}px ${fontFamily}`;
}

function measureTextWidthPx(
  text: string,
  style: TextRunNode["style"] | FormFieldRunNode["style"] | undefined,
  paragraphBaseFontPx: number
): number {
  if (!text) {
    return 0;
  }

  const font = resolveMeasureFont(style, paragraphBaseFontPx);
  const characterSpacingPx = Number.isFinite(style?.characterSpacingTwips)
    ? (style?.characterSpacingTwips as number) / TWIPS_PER_PIXEL
    : 0;
  const cacheKey = `${font}\u0000${characterSpacingPx}\u0000${text}`;
  const cached = textWidthByFontAndValue.get(cacheKey);
  if (cached !== undefined) {
    return cached;
  }

  const fontSizePx = resolveMeasureFontSizePx(style, paragraphBaseFontPx);
  let measuredWidthPx = fallbackMeasureTextWidthPx(text, fontSizePx);

  if (typeof document !== "undefined") {
    try {
      if (!paragraphMeasureCanvasContext) {
        const canvas = document.createElement("canvas");
        paragraphMeasureCanvasContext = canvas.getContext("2d") ?? undefined;
      }
      const context = paragraphMeasureCanvasContext;
      if (context) {
        context.font = font;
        measuredWidthPx = context.measureText(text).width;
      }
    } catch {
      // Keep deterministic fallback width when canvas measurement is unavailable.
    }
  }

  if (characterSpacingPx !== 0 && text.length > 1) {
    measuredWidthPx += characterSpacingPx * Math.max(0, text.length - 1);
  }

  setCacheEntry(textWidthByFontAndValue, cacheKey, measuredWidthPx);
  while (textWidthByFontAndValue.size > TEXT_MEASURE_CACHE_MAX_ENTRIES) {
    const firstKey = textWidthByFontAndValue.keys().next().value as
      | string
      | undefined;
    if (!firstKey) {
      break;
    }
    textWidthByFontAndValue.delete(firstKey);
  }

  return measuredWidthPx;
}

function resolveParagraphTabStopsPx(paragraph: ParagraphNode): number[] {
  const stopsPx = (paragraph.style?.tabStops ?? [])
    .map((tabStop) => twipsToPixels(tabStop.positionTwips))
    .filter(
      (value): value is number =>
        Number.isFinite(value) && (value as number) > 0
    )
    .map((value) => Math.round(value))
    .sort((left, right) => left - right);

  return stopsPx;
}

function resolveParagraphFirstLineOriginPx(paragraph: ParagraphNode): number {
  const leftIndentPx = twipsToSignedPixels(paragraph.style?.indent?.leftTwips);
  const firstLineIndentPx = twipsToSignedPixels(
    paragraph.style?.indent?.firstLineTwips
  );
  const hangingIndentPx = twipsToSignedPixels(
    paragraph.style?.indent?.hangingTwips
  );
  const textIndentPx =
    firstLineIndentPx ??
    (Number.isFinite(hangingIndentPx) ? -(hangingIndentPx as number) : 0);

  return (
    (Number.isFinite(leftIndentPx) ? (leftIndentPx as number) : 0) +
    (Number.isFinite(textIndentPx) ? (textIndentPx as number) : 0)
  );
}

function resolveParagraphFirstLineLeftTabStopsPx(
  paragraph: ParagraphNode
): number[] {
  const firstLineOriginPx = resolveParagraphFirstLineOriginPx(paragraph);
  return (paragraph.style?.tabStops ?? [])
    .filter((tabStop) => tabStop.alignment !== "right")
    .map((tabStop) => twipsToPixels(tabStop.positionTwips))
    .filter(
      (value): value is number =>
        Number.isFinite(value) && (value as number) > firstLineOriginPx + 0.5
    )
    .map((value) => Math.round(value - firstLineOriginPx))
    .sort((left, right) => left - right);
}

function resolveNextTabStopPx(
  currentLineWidthPx: number,
  tabStopsPx: number[]
): number {
  const nextExplicit = tabStopsPx.find(
    (stopPx) => stopPx > currentLineWidthPx + 0.5
  );
  if (nextExplicit !== undefined) {
    return nextExplicit;
  }

  const tabStepPx = DEFAULT_TAB_STOP_PX;
  const nextMultiple =
    Math.floor(Math.max(0, currentLineWidthPx) / tabStepPx + 1) * tabStepPx;
  return Math.max(tabStepPx, nextMultiple);
}

interface ParagraphMeasureSegment {
  text: string;
  style?: TextRunNode["style"] | FormFieldRunNode["style"];
}

interface ParagraphMeasureToken extends ParagraphMeasureSegment {
  isTab?: boolean;
}

function normalizeTextForTabLeaderMeasurement(text: string): string {
  return text.replace(/\t/g, "        ");
}

function measureParagraphSegmentTextWidthPx(
  segments: ParagraphMeasureSegment[],
  paragraphBaseFontPx: number
): number {
  return segments.reduce((totalWidthPx, segment) => {
    const normalizedText = normalizeTextForTabLeaderMeasurement(segment.text);
    if (!normalizedText) {
      return totalWidthPx;
    }
    return (
      totalWidthPx +
      measureTextWidthPx(normalizedText, segment.style, paragraphBaseFontPx)
    );
  }, 0);
}

function estimateWrappedLineCountForSegments(
  segments: ParagraphMeasureSegment[],
  maxLineWidthPx: number,
  paragraphBaseFontPx: number
): number {
  let lineCount = 1;
  let currentLineWidthPx = 0;
  let hasVisibleContent = false;

  const advanceByTextToken = (
    token: string,
    style: TextRunNode["style"] | FormFieldRunNode["style"] | undefined
  ): void => {
    const tokenWidthPx = measureTextWidthPx(token, style, paragraphBaseFontPx);
    if (
      token.trim().length > 0 &&
      currentLineWidthPx > 0 &&
      currentLineWidthPx + tokenWidthPx > maxLineWidthPx
    ) {
      lineCount += 1;
      currentLineWidthPx = 0;
    }

    if (tokenWidthPx <= maxLineWidthPx) {
      currentLineWidthPx = Math.min(
        maxLineWidthPx,
        currentLineWidthPx + tokenWidthPx
      );
      return;
    }

    for (const character of token) {
      const characterWidthPx = measureTextWidthPx(
        character,
        style,
        paragraphBaseFontPx
      );
      if (
        currentLineWidthPx > 0 &&
        currentLineWidthPx + characterWidthPx > maxLineWidthPx
      ) {
        lineCount += 1;
        currentLineWidthPx = 0;
      }
      currentLineWidthPx = Math.min(
        maxLineWidthPx,
        currentLineWidthPx + characterWidthPx
      );
    }
  };

  for (const segment of segments) {
    const normalizedText = normalizeTextForTabLeaderMeasurement(segment.text);
    if (!normalizedText) {
      continue;
    }

    const tokens =
      normalizedText.match(/(\r\n|\n|[^\S\r\n]+|[^\s\r\n]+)/g) ?? [];
    for (const token of tokens) {
      if (token.length === 0 || token === "\r") {
        continue;
      }

      hasVisibleContent = true;
      if (token === "\n" || token === "\r\n") {
        lineCount += 1;
        currentLineWidthPx = 0;
        continue;
      }

      advanceByTextToken(token, segment.style);
    }
  }

  return hasVisibleContent ? Math.max(1, lineCount) : 1;
}

function collectParagraphMeasureTokens(
  paragraph: ParagraphNode
): ParagraphMeasureToken[] {
  const tokens: ParagraphMeasureToken[] = [];

  const appendText = (
    text: string,
    style?: TextRunNode["style"] | FormFieldRunNode["style"]
  ): void => {
    if (!text || text === "\r") {
      return;
    }

    const parts = text.split("\t");
    parts.forEach((part, partIndex) => {
      if (part.length > 0) {
        tokens.push({
          text: part,
          style,
        });
      }

      if (partIndex < parts.length - 1) {
        tokens.push({
          text: "\t",
          style,
          isTab: true,
        });
      }
    });
  };

  paragraph.children.forEach((child) => {
    if (child.type !== "text" && child.type !== "form-field") {
      return;
    }

    appendText(
      child.type === "text" ? child.text : formFieldDisplayValue(child),
      child.style
    );
  });

  return tokens;
}

function collectParagraphTabLeaderMeasureSegments(paragraph: ParagraphNode): {
  leadingSegments: ParagraphMeasureSegment[];
  contentSegments: ParagraphMeasureSegment[];
  rightSegments: ParagraphMeasureSegment[];
  hasTabSplit: boolean;
} {
  const tokens = collectParagraphMeasureTokens(paragraph);
  const tabIndexes = tokens.reduce<number[]>((indexes, token, tokenIndex) => {
    if (token.isTab) {
      indexes.push(tokenIndex);
    }
    return indexes;
  }, []);

  if (tabIndexes.length === 0) {
    return {
      leadingSegments: [],
      contentSegments: tokens,
      rightSegments: [],
      hasTabSplit: false,
    };
  }

  const firstTabIndex = tabIndexes[0];
  const lastTabIndex = tabIndexes[tabIndexes.length - 1];

  const toSegments = (
    slice: ParagraphMeasureToken[]
  ): ParagraphMeasureSegment[] =>
    slice
      .filter((token) => !token.isTab && token.text.length > 0)
      .map((token) => ({
        text: token.text,
        style: token.style,
      }));

  if (firstTabIndex === lastTabIndex) {
    return {
      leadingSegments: [],
      contentSegments: toSegments(tokens.slice(0, firstTabIndex)),
      rightSegments: toSegments(tokens.slice(lastTabIndex + 1)),
      hasTabSplit: true,
    };
  }

  return {
    leadingSegments: toSegments(tokens.slice(0, firstTabIndex)),
    contentSegments: toSegments(tokens.slice(firstTabIndex + 1, lastTabIndex)),
    rightSegments: toSegments(tokens.slice(lastTabIndex + 1)),
    hasTabSplit: true,
  };
}

function estimateTabLeaderWrappedLineCountForParagraph(
  paragraph: ParagraphNode,
  maxLineWidthPx: number,
  paragraphBaseFontPx: number
): number | undefined {
  const { leadingSegments, contentSegments, rightSegments, hasTabSplit } =
    collectParagraphTabLeaderMeasureSegments(paragraph);
  if (!hasTabSplit) {
    return undefined;
  }

  const reservedRightZoneWidthPx =
    measureParagraphSegmentTextWidthPx(rightSegments, paragraphBaseFontPx) +
    TAB_LEADER_ZONE_GAP_PX;
  const leftZoneWidthPx = Math.max(
    paragraphBaseFontPx * 2,
    Math.round(maxLineWidthPx - reservedRightZoneWidthPx)
  );
  const leadingTextWidthPx = measureParagraphSegmentTextWidthPx(
    leadingSegments,
    paragraphBaseFontPx
  );
  const tabStopPositionsPx = resolveParagraphFirstLineLeftTabStopsPx(paragraph);
  const explicitLeadingTabStopPx =
    tableOfContentsLeadingLeftTabStopPx(paragraph);
  const leadingReservationWidthPx =
    leadingSegments.length === 0
      ? 0
      : Number.isFinite(explicitLeadingTabStopPx) &&
        (explicitLeadingTabStopPx as number) > 0
      ? Math.max(
          leadingTextWidthPx,
          Math.round(explicitLeadingTabStopPx as number)
        )
      : Math.max(
          leadingTextWidthPx,
          Math.round(
            leadingTextWidthPx +
              resolveTabSpacerWidthPx(
                tabStopPositionsPx,
                leadingTextWidthPx,
                DEFAULT_TAB_STOP_PX
              )
          )
        );
  const contentZoneWidthPx = Math.max(
    paragraphBaseFontPx * 2,
    Math.round(leftZoneWidthPx - leadingReservationWidthPx)
  );
  const leftZoneText = contentSegments
    .map((segment) => normalizeTextForTabLeaderMeasurement(segment.text))
    .join("");
  if (leftZoneText.trim().length === 0) {
    return 1;
  }

  const leftZoneFont = resolveMeasureFont(
    contentSegments.find((segment) => segment.text.trim().length > 0)?.style,
    paragraphBaseFontPx
  );
  const lineHeightPx = estimateParagraphLineHeightPx(paragraph);
  const pretextLayout = layoutItemsWithPretextAroundExclusions(
    leftZoneText,
    buildMeasureSegmentsPretextLayoutItems(
      contentSegments,
      paragraphBaseFontPx,
      leftZoneText
    ),
    contentZoneWidthPx,
    lineHeightPx,
    [],
    leftZoneFont
  );
  if (pretextLayout?.lineCount) {
    return pretextLayout.lineCount;
  }

  return estimateWrappedLineCountForSegments(
    contentSegments,
    contentZoneWidthPx,
    paragraphBaseFontPx
  );
}

const wrappedLineCountByParagraph = new WeakMap<
  ParagraphNode,
  Map<number | string, number>
>();

function cachedWrappedLineCountForParagraph(
  paragraph: ParagraphNode,
  widthKey: number | string
): number | undefined {
  return wrappedLineCountByParagraph.get(paragraph)?.get(widthKey);
}

function rememberWrappedLineCountForParagraph(
  paragraph: ParagraphNode,
  widthKey: number | string,
  lineCount: number
): number {
  let countsByWidth = wrappedLineCountByParagraph.get(paragraph);
  if (!countsByWidth) {
    countsByWidth = new Map<number | string, number>();
    wrappedLineCountByParagraph.set(paragraph, countsByWidth);
  }
  countsByWidth.set(widthKey, lineCount);
  return lineCount;
}

function estimateWrappedLineCountForParagraph(
  paragraph: ParagraphNode,
  availableWidthPx: number,
  firstLineAvailableWidthPx?: number
): number {
  const paragraphBaseFontPx = paragraphBaseFontSizePx(paragraph);
  const maxLineWidthPx = Math.max(
    paragraphBaseFontPx * 2,
    Math.round(availableWidthPx)
  );
  const firstLineMaxWidthPx =
    Number.isFinite(firstLineAvailableWidthPx) &&
    (firstLineAvailableWidthPx as number) > 0
      ? Math.max(
          paragraphBaseFontPx * 2,
          Math.round(firstLineAvailableWidthPx as number)
        )
      : maxLineWidthPx;
  const widthCacheKey: number | string =
    firstLineMaxWidthPx === maxLineWidthPx
      ? maxLineWidthPx
      : `${maxLineWidthPx}|${firstLineMaxWidthPx}`;
  const cachedLineCount = cachedWrappedLineCountForParagraph(
    paragraph,
    widthCacheKey
  );
  if (cachedLineCount !== undefined) {
    return cachedLineCount;
  }

  const rememberLineCount = (lineCount: number): number =>
    rememberWrappedLineCountForParagraph(
      paragraph,
      widthCacheKey,
      Math.max(1, Math.round(lineCount))
    );
  const tabStopsPx = resolveParagraphTabStopsPx(paragraph);
  const useTabLeaderLayout = paragraphUsesTabLeaders(paragraph);
  const anchoredTabLayout = paragraphAnchoredTabLayout(paragraph);
  const useCenterRightTabLayout =
    !useTabLeaderLayout && anchoredTabLayout === "center-right";
  const useCenterTabLayout =
    !useTabLeaderLayout && anchoredTabLayout === "center";
  const useRightTabLayout =
    !useTabLeaderLayout && anchoredTabLayout === "right";
  const useAnchoredTabLayout =
    useCenterRightTabLayout || useCenterTabLayout || useRightTabLayout;
  const paragraphEligibleForPlainPretextLineCount =
    !paragraph.style?.indent &&
    firstLineMaxWidthPx === maxLineWidthPx &&
    !paragraphContainsExplicitLineBreakText(paragraph);

  if (
    paragraphEligibleForPlainPretextLineCount &&
    !useTabLeaderLayout &&
    !useAnchoredTabLayout
  ) {
    const pretextPlainLineCount = (() => {
      const pretextSource = buildParagraphPretextLayoutSource(paragraph);
      if (!pretextSource) {
        return undefined;
      }

      // Fast path (pretext 0.0.5+): when the paragraph's measurement runs all
      // share a single font and there are no atomic items (images, tabs),
      // `measureLineStats` wraps the text and returns the line count using
      // pure arithmetic over cached segment widths — no per-line string
      // allocations, no fragment objects. This is the hot path during
      // pagination of large documents.
      const uniformFont = resolveUniformPretextSourceFont(
        paragraph,
        pretextSource
      );
      if (uniformFont !== undefined) {
        const fastLineCount = measurePretextPlainTextLineCount(
          pretextSource.text,
          uniformFont,
          maxLineWidthPx,
          { wordBreak: pretextWordBreakModeForText(pretextSource.text) }
        );
        if (fastLineCount !== undefined) {
          return fastLineCount;
        }
      }

      const lineHeightPx = estimateParagraphLineHeightPx(paragraph);
      const layout = layoutParagraphPretextSource(
        paragraph,
        pretextSource,
        maxLineWidthPx,
        lineHeightPx,
        []
      );
      return layout?.lineCount;
    })();
    if (pretextPlainLineCount) {
      return rememberLineCount(pretextPlainLineCount);
    }
  }

  if (useTabLeaderLayout) {
    const tabLeaderLineCount = estimateTabLeaderWrappedLineCountForParagraph(
      paragraph,
      maxLineWidthPx,
      paragraphBaseFontPx
    );
    if (tabLeaderLineCount !== undefined) {
      return rememberLineCount(tabLeaderLineCount);
    }
  }
  let lineCount = 1;
  let currentLineWidthPx = 0;
  let hasVisibleContent = false;
  let tabLeaderRightZoneActive = false;
  let tabLeaderRightZoneWidthPx = 0;

  // The first line keeps a hanging indent's extra room (minus any list marker
  // box); subsequent lines wrap within the regular indented width.
  const currentMaxLineWidthPx = (): number =>
    lineCount === 1 ? firstLineMaxWidthPx : maxLineWidthPx;

  const advanceByTextToken = (
    token: string,
    style: TextRunNode["style"] | FormFieldRunNode["style"] | undefined
  ): void => {
    const tokenWidthPx = measureTextWidthPx(token, style, paragraphBaseFontPx);
    if (
      token.trim().length > 0 &&
      currentLineWidthPx > 0 &&
      currentLineWidthPx + tokenWidthPx > currentMaxLineWidthPx()
    ) {
      lineCount += 1;
      currentLineWidthPx = 0;
    }

    if (tokenWidthPx <= currentMaxLineWidthPx()) {
      currentLineWidthPx = Math.min(
        currentMaxLineWidthPx(),
        currentLineWidthPx + tokenWidthPx
      );
      return;
    }

    for (const character of token) {
      const characterWidthPx = measureTextWidthPx(
        character,
        style,
        paragraphBaseFontPx
      );
      if (
        currentLineWidthPx > 0 &&
        currentLineWidthPx + characterWidthPx > currentMaxLineWidthPx()
      ) {
        lineCount += 1;
        currentLineWidthPx = 0;
      }
      currentLineWidthPx = Math.min(
        currentMaxLineWidthPx(),
        currentLineWidthPx + characterWidthPx
      );
    }
  };

  const commitToken = (
    token: string,
    style: TextRunNode["style"] | FormFieldRunNode["style"] | undefined
  ): void => {
    if (token.length === 0 || token === "\r") {
      return;
    }

    hasVisibleContent = true;
    if (token === "\n" || token === "\r\n") {
      tabLeaderRightZoneActive = false;
      tabLeaderRightZoneWidthPx = 0;
      lineCount += 1;
      currentLineWidthPx = 0;
      return;
    }

    if (useTabLeaderLayout && tabLeaderRightZoneActive) {
      tabLeaderRightZoneWidthPx += measureTextWidthPx(
        token,
        style,
        paragraphBaseFontPx
      );
      if (
        currentLineWidthPx > 0 &&
        currentLineWidthPx +
          tabLeaderRightZoneWidthPx +
          TAB_LEADER_ZONE_GAP_PX >
          maxLineWidthPx + PAGE_OVERFLOW_TOLERANCE_PX
      ) {
        lineCount += 1;
        currentLineWidthPx = 0;
        tabLeaderRightZoneWidthPx = 0;
      }
      return;
    }

    if (token === "\t") {
      if (useTabLeaderLayout) {
        // TOC leader layout renders the right-side page-number zone independently
        // from left text width while still sharing the same visual line.
        tabLeaderRightZoneActive = true;
        tabLeaderRightZoneWidthPx = 0;
        return;
      }

      if (useAnchoredTabLayout) {
        currentLineWidthPx = Math.min(
          maxLineWidthPx,
          Math.max(currentLineWidthPx, maxLineWidthPx - 1)
        );
        return;
      }

      const nextTabStopPx = resolveNextTabStopPx(
        currentLineWidthPx,
        tabStopsPx
      );
      if (
        nextTabStopPx > maxLineWidthPx + PAGE_OVERFLOW_TOLERANCE_PX &&
        currentLineWidthPx > 0
      ) {
        lineCount += 1;
        currentLineWidthPx = 0;
      } else {
        currentLineWidthPx = Math.min(maxLineWidthPx, nextTabStopPx);
      }
      return;
    }

    advanceByTextToken(token, style);
  };

  for (const child of paragraph.children) {
    if (child.type === "image") {
      if (
        shouldRenderAbsoluteFloatingImage(child) ||
        shouldRenderWrappedFloatingImage(child)
      ) {
        continue;
      }
      const inlineImageWidthPx = Math.max(
        1,
        Math.round(child.widthPx ?? child.heightPx ?? 24)
      );
      hasVisibleContent = true;
      if (
        currentLineWidthPx > 0 &&
        currentLineWidthPx + inlineImageWidthPx > maxLineWidthPx
      ) {
        lineCount += 1;
        currentLineWidthPx = 0;
      }
      currentLineWidthPx = Math.min(
        maxLineWidthPx,
        currentLineWidthPx + inlineImageWidthPx
      );
      continue;
    }

    const text =
      child.type === "text" ? child.text : formFieldDisplayValue(child);
    if (!text) {
      continue;
    }

    const tokens = text.match(/(\r\n|\n|\t|[^\S\r\n\t]+|[^\s\r\n\t]+)/g) ?? [];
    for (const token of tokens) {
      commitToken(token, child.style);
    }
  }

  return rememberLineCount(hasVisibleContent ? lineCount : 1);
}

function paragraphAvailableTextWidthPx(
  paragraph: ParagraphNode,
  availableWidthPx: number,
  numberingDefinitions?: NumberingDefinitionSet
): number {
  const safeAvailableWidthPx = Math.max(24, Math.round(availableWidthPx));
  const resolvedIndent = resolveListParagraphIndent(
    paragraph,
    numberingDefinitions
  );
  const leftIndentPx = Math.max(
    0,
    twipsToSignedPixels(resolvedIndent?.leftTwips) ?? 0
  );
  const rightIndentPx = Math.max(
    0,
    twipsToSignedPixels(paragraph.style?.indent?.rightTwips) ?? 0
  );
  const firstLineIndentPx = twipsToSignedPixels(resolvedIndent?.firstLineTwips);
  const hangingIndentPx = twipsToSignedPixels(resolvedIndent?.hangingTwips);
  const firstLineDeltaPx =
    firstLineIndentPx ?? (hangingIndentPx ? -hangingIndentPx : 0);
  const textIndentReductionPx =
    Number.isFinite(firstLineDeltaPx) && (firstLineDeltaPx as number) > 0
      ? (firstLineDeltaPx as number)
      : 0;
  const leftBorderInsetPx = paragraphBorderInsetPx(
    paragraph.style?.borders?.left
  );
  const rightBorderInsetPx = paragraphBorderInsetPx(
    paragraph.style?.borders?.right
  );

  return Math.max(
    24,
    Math.round(
      safeAvailableWidthPx -
        leftIndentPx -
        rightIndentPx -
        textIndentReductionPx -
        leftBorderInsetPx -
        rightBorderInsetPx
    )
  );
}

export function paragraphLineCountWithinWidth(
  paragraph: ParagraphNode,
  availableWidthPx?: number,
  numberingDefinitions?: NumberingDefinitionSet,
  numberingLabel?: ParagraphNumberingLabel
): number {
  const textContent = paragraph.children
    .map((child) => {
      if (child.type === "text") {
        return child.text;
      }
      if (child.type === "form-field") {
        return formFieldDisplayValue(child);
      }
      return "";
    })
    .join("");
  if (!textContent) {
    return 1;
  }

  if (!Number.isFinite(availableWidthPx) || (availableWidthPx as number) <= 0) {
    return Math.max(1, textContent.split(/\r?\n/).length);
  }

  const effectiveWidthPx = paragraphAvailableTextWidthPx(
    paragraph,
    availableWidthPx as number,
    numberingDefinitions
  );
  const dualWrappedLayout = resolveParagraphDualWrappedTextLayout(
    paragraph,
    effectiveWidthPx,
    estimateParagraphLineHeightPx(paragraph)
  );
  if (dualWrappedLayout) {
    return dualWrappedLayout.layout.lineCount;
  }

  // A hanging indent only narrows lines after the first, so the first line
  // keeps the hanging offset as extra room. Numbered list paragraphs render an
  // inline marker box at the start of that first line, which consumes part (or
  // all) of that extra room before the paragraph text begins.
  const resolvedIndent = resolveListParagraphIndent(
    paragraph,
    numberingDefinitions
  );
  const hangingIndentPx = twipsToSignedPixels(resolvedIndent?.hangingTwips);
  const firstLineHangingBonusPx =
    Number.isFinite(hangingIndentPx) && (hangingIndentPx as number) > 0
      ? (hangingIndentPx as number)
      : 0;
  const numberingMarkerReservePx =
    (paragraph.style?.numbering?.numId ?? 0) > 0
      ? resolveNumberingMarkerBoxWidthPx(
          paragraph,
          numberingDefinitions,
          numberingLabel
        ) ?? 0
      : 0;
  const firstLineWidthPx = Math.max(
    24,
    Math.round(
      effectiveWidthPx + firstLineHangingBonusPx - numberingMarkerReservePx
    )
  );

  return estimateWrappedLineCountForParagraph(
    paragraph,
    effectiveWidthPx,
    firstLineWidthPx
  );
}

function estimateWrappedFloatingImageFootprintPx(
  paragraph: ParagraphNode,
  image: ImageRunNode
): number {
  if (!shouldRenderWrappedFloatingImage(image)) {
    return 0;
  }

  const wrapType = image.floating?.wrapType;
  const isTopAndBottomWrap = wrapType === "topAndBottom";
  const isImageOnlyAnchorParagraph = !paragraphHasVisibleText(paragraph);
  if (!isTopAndBottomWrap && !isImageOnlyAnchorParagraph) {
    return 0;
  }

  // For paragraphs that already have visible text, top-and-bottom wrapped
  // objects should not force the anchor paragraph box to extend all the way to
  // the image bottom. Word allows following text to occupy the gap above a
  // lowered object, so keep that reserve logic only for image-only anchors.
  if (isTopAndBottomWrap && !isImageOnlyAnchorParagraph) {
    return 0;
  }

  const floating = image.floating;
  const imageHeightPx =
    Number.isFinite(image.heightPx) && (image.heightPx as number) > 0
      ? Math.round(image.heightPx as number)
      : Number.isFinite(image.widthPx) && (image.widthPx as number) > 0
      ? Math.round(image.widthPx as number)
      : MIN_PARAGRAPH_LINE_HEIGHT_PX;
  const distTPx = Math.max(0, Math.round(floating?.distTPx ?? 0));
  const distBPx = Math.max(0, Math.round(floating?.distBPx ?? 0));
  const verticalOffsetPx = Math.max(0, Math.round(floating?.yPx ?? 0));

  return Math.max(
    MIN_PARAGRAPH_LINE_HEIGHT_PX,
    imageHeightPx + distTPx + distBPx + verticalOffsetPx
  );
}

function pretextLayoutContentBottomPx(
  layout: PretextVariableWidthLayout
): number {
  if (layout.lines.length === 0) {
    return 0;
  }

  const lineHeightPx = Math.max(1, Math.round(layout.lineHeightPx ?? 1));
  return (layout.lines[layout.lines.length - 1]?.y ?? 0) + lineHeightPx;
}

function topAndBottomExclusionCanOverflowParagraphBox(
  layout: PretextVariableWidthLayout
): boolean {
  const containerWidthPx = Math.max(
    1,
    Math.round(layout.containerWidthPx ?? 0)
  );
  if (containerWidthPx <= 0 || layout.lines.length === 0) {
    return false;
  }

  return (layout.exclusions ?? []).some((exclusion) => {
    const left = Math.round(exclusion.left);
    const right = Math.round(exclusion.right);
    return left <= 0 && right >= containerWidthPx;
  });
}

export function wrappedPretextParagraphBlockHeightPx(
  layout: PretextVariableWidthLayout
): number {
  const contentBottomPx = pretextLayoutContentBottomPx(layout);
  if (topAndBottomExclusionCanOverflowParagraphBox(layout)) {
    return Math.max(1, contentBottomPx);
  }

  return Math.max(1, Math.round(layout.height));
}

export function resolvePretextLineRangeContentHeightPx(
  layout: PretextVariableWidthLayout,
  startLineIndex: number,
  endLineIndex: number
): number {
  if (layout.lines.length === 0) {
    return 0;
  }

  const lineHeightPx = Math.max(1, Math.round(layout.lineHeightPx ?? 1));
  const safeStart = Math.max(
    0,
    Math.min(Math.round(startLineIndex), layout.lines.length)
  );
  const safeEnd = Math.max(
    safeStart,
    Math.min(Math.round(endLineIndex), layout.lines.length)
  );
  if (safeEnd <= safeStart) {
    return 0;
  }

  const firstLineTopPx = layout.lines[safeStart]?.y ?? safeStart * lineHeightPx;
  const lastLineTopPx =
    layout.lines[safeEnd - 1]?.y ?? (safeEnd - 1) * lineHeightPx;
  return Math.max(1, Math.round(lastLineTopPx - firstLineTopPx + lineHeightPx));
}

export function resolveMaxPretextLineRangeEndIndexThatFits(
  layout: PretextVariableWidthLayout,
  startLineIndex: number,
  maxEndLineIndex: number,
  availableHeightPx: number
): number {
  const safeStart = Math.max(
    0,
    Math.min(Math.round(startLineIndex), layout.lines.length)
  );
  const safeMaxEnd = Math.max(
    safeStart,
    Math.min(Math.round(maxEndLineIndex), layout.lines.length)
  );
  const safeAvailableHeightPx = Math.max(0, Math.round(availableHeightPx));
  if (safeAvailableHeightPx <= 0 || safeMaxEnd <= safeStart) {
    return safeStart;
  }

  let low = safeStart;
  let high = safeMaxEnd;
  let bestEnd = safeStart;
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const candidateHeightPx = resolvePretextLineRangeContentHeightPx(
      layout,
      safeStart,
      mid
    );
    if (candidateHeightPx <= safeAvailableHeightPx) {
      bestEnd = mid;
      low = mid + 1;
      continue;
    }
    high = mid - 1;
  }

  return bestEnd;
}

function resolveAutoLineSpacingMultiple(
  lineTwips: number | undefined,
  fallbackMultiple: number
): number {
  if (!Number.isFinite(lineTwips)) {
    return Math.max(MIN_AUTO_LINE_MULTIPLE, fallbackMultiple);
  }

  return Math.max(MIN_AUTO_LINE_MULTIPLE, (lineTwips as number) / 240);
}

function autoLineHeightScaleForMultiple(
  multiple: number,
  singleLineScale: number
): number {
  const safeSingleLineScale = Math.max(
    MIN_AUTO_LINE_MULTIPLE,
    Number.isFinite(singleLineScale)
      ? singleLineScale
      : WORD_SINGLE_LINE_AUTO_SCALE
  );
  if (!Number.isFinite(multiple)) {
    return safeSingleLineScale;
  }

  if (multiple <= 1) {
    return safeSingleLineScale;
  }

  if (multiple >= WORD_AUTO_LINE_SCALE_BLEND_END_MULTIPLE) {
    return 1;
  }

  const blendProgress =
    (multiple - 1) / (WORD_AUTO_LINE_SCALE_BLEND_END_MULTIPLE - 1);
  return Number(
    (safeSingleLineScale + (1 - safeSingleLineScale) * blendProgress).toFixed(4)
  );
}

function calibrateAutoLineSpacingMultiple(
  multiple: number,
  fontFamily?: string,
  singleLineScaleOverride?: number
): number {
  const normalizedMultiple = Math.max(MIN_AUTO_LINE_MULTIPLE, multiple);
  const singleLineScale =
    singleLineScaleOverride ?? singleLineAutoScaleForFontFamily(fontFamily);
  return Math.max(
    MIN_AUTO_LINE_MULTIPLE,
    Number(
      (
        normalizedMultiple *
        autoLineHeightScaleForMultiple(normalizedMultiple, singleLineScale)
      ).toFixed(3)
    )
  );
}

function paragraphDocGridSnapState(
  paragraph: ParagraphNode
): "inherit" | "snap" | "disable" {
  const sourceXml = paragraph.sourceXml ?? "";
  if (!sourceXml) {
    return "inherit";
  }

  const paragraphPropertiesXml =
    sourceXml.match(/<w:pPr\b[\s\S]*?<\/w:pPr>/i)?.[0] ??
    sourceXml.match(/<w:pPr\b[^>]*\/>/i)?.[0] ??
    "";
  if (!paragraphPropertiesXml) {
    return "inherit";
  }

  const snapToGridTag =
    paragraphPropertiesXml.match(/<w:snapToGrid\b[^>]*\/?>/i)?.[0] ?? "";
  if (!snapToGridTag) {
    return "inherit";
  }

  const valueMatch = snapToGridTag
    .match(/\bw:val="([^"]+)"/i)?.[1]
    ?.trim()
    .toLowerCase();
  if (!valueMatch) {
    return "snap";
  }

  return valueMatch === "0" || valueMatch === "false" || valueMatch === "off"
    ? "disable"
    : "snap";
}

function resolveParagraphDocGridLinePitchPx(
  paragraph: ParagraphNode,
  docGridLinePitchPx?: number,
  disableDocGridSnap = false
): number | undefined {
  const docGridSnapState = paragraphDocGridSnapState(paragraph);
  if (
    disableDocGridSnap ||
    docGridSnapState === "disable" ||
    !Number.isFinite(docGridLinePitchPx) ||
    (docGridLinePitchPx as number) <= 0
  ) {
    return undefined;
  }

  return Math.max(1, Math.round(docGridLinePitchPx as number));
}

export function estimateParagraphLineHeightPx(
  paragraph: ParagraphNode,
  docGridLinePitchPx?: number,
  disableDocGridSnap = false
): number {
  const lineTwips = paragraph.style?.spacing?.lineTwips;
  const lineRule = paragraph.style?.spacing?.lineRule ?? "auto";
  const docGridMinimumLineHeightPx = resolveParagraphDocGridLinePitchPx(
    paragraph,
    docGridLinePitchPx,
    disableDocGridSnap
  );
  const baseFontPx = paragraphBaseFontSizePx(paragraph);
  const baseFontFamily = paragraphDominantFontFamily(paragraph);
  const singleLineScale = resolveParagraphSingleLineAutoScale(
    paragraph,
    baseFontFamily
  );
  const defaultLineMultiple = isTableOfContentsParagraph(paragraph)
    ? 1.05
    : DEFAULT_PARAGRAPH_LINE_MULTIPLE;
  const normalLineHeightPx = Math.max(
    1,
    Math.round(
      baseFontPx *
        calibrateAutoLineSpacingMultiple(
          DEFAULT_PARAGRAPH_LINE_MULTIPLE,
          baseFontFamily,
          singleLineScale
        )
    )
  );

  if (lineRule !== "auto" && Number.isFinite(lineTwips)) {
    const explicitLineHeightPx = Math.max(1, twipsToPixels(lineTwips) ?? 1);
    if (lineRule === "exact") {
      return explicitLineHeightPx;
    }

    return Math.max(
      explicitLineHeightPx,
      normalLineHeightPx,
      docGridMinimumLineHeightPx ?? 0
    );
  }

  const resolvedAutoMultiple = resolveAutoLineSpacingMultiple(
    lineTwips,
    defaultLineMultiple
  );
  // A text-free paragraph renders exactly one line at the mark font's natural
  // metrics, and an explicit auto multiple scales that natural line (Word
  // semantics). The wrapped-text blend toward bare font-size lines exists to
  // offset wrapped-line overcounting, which cannot happen here.
  const multiple = paragraphRendersTextFreeLine(paragraph)
    ? Math.max(
        MIN_AUTO_LINE_MULTIPLE,
        Number((resolvedAutoMultiple * singleLineScale).toFixed(3))
      )
    : calibrateAutoLineSpacingMultiple(
        resolvedAutoMultiple,
        baseFontFamily,
        singleLineScale
      );
  const autoLineHeightPx = Math.max(1, Math.round(baseFontPx * multiple));
  const minimumReadableAutoLineHeightPx = paragraph.style?.numbering
    ? Math.ceil(baseFontPx)
    : 0;
  return Math.max(
    autoLineHeightPx,
    minimumReadableAutoLineHeightPx,
    docGridMinimumLineHeightPx ?? 0
  );
}

function estimateParagraphHeightPx(
  paragraph: ParagraphNode,
  availableWidthPx?: number,
  numberingDefinitions?: NumberingDefinitionSet,
  docGridLinePitchPx?: number,
  disableDocGridSnap = false,
  numberingLabel?: ParagraphNumberingLabel,
  options?: {
    // Word-like flow: square/tight wrapped floats do not consume flow height;
    // following paragraphs flow beside them. The foreign-wrap exclusion
    // simulation needs the text-flow height, not the float footprint.
    excludeWrappedFloatingImageFootprint?: boolean;
  }
): number {
  const excludeWrappedFloatingImageFootprint =
    options?.excludeWrappedFloatingImageFootprint === true;
  const sourceXml = paragraph.sourceXml;
  const baseWidthKey = heightEstimateCacheKeyPx(
    availableWidthPx,
    docGridLinePitchPx,
    disableDocGridSnap
  );
  let widthKey: number | string =
    numberingLabel?.text !== undefined
      ? `${baseWidthKey}|${numberingLabel.text}`
      : baseWidthKey;
  if (excludeWrappedFloatingImageFootprint) {
    widthKey = `${widthKey}|no-wrap-footprint`;
  }
  if (sourceXml) {
    const cachedByWidth = paragraphEstimatedHeightBySourceXml.get(sourceXml);
    const cached = cachedByWidth?.get(widthKey);
    if (cached !== undefined) {
      return cached;
    }
  }

  const beforeSpacing =
    twipsToPixels(paragraph.style?.spacing?.beforeTwips) ?? 0;
  const afterSpacing = twipsToPixels(paragraph.style?.spacing?.afterTwips) ?? 0;
  const lineHeightPx = estimateParagraphLineHeightPx(
    paragraph,
    docGridLinePitchPx,
    disableDocGridSnap
  );
  const effectiveWidthPx =
    Number.isFinite(availableWidthPx) && (availableWidthPx as number) > 0
      ? paragraphAvailableTextWidthPx(
          paragraph,
          availableWidthPx as number,
          numberingDefinitions
        )
      : undefined;
  const dualWrappedLayout =
    effectiveWidthPx !== undefined
      ? resolveParagraphDualWrappedTextLayout(
          paragraph,
          effectiveWidthPx,
          lineHeightPx
        )
      : undefined;
  const lineCount = paragraphLineCountWithinWidth(
    paragraph,
    availableWidthPx,
    numberingDefinitions,
    numberingLabel
  );
  const absoluteFloatingAnchorOnlyParagraph =
    paragraphIsAbsoluteFloatingImageAnchorOnly(paragraph);
  // Word still lays out an anchor-only paragraph as one empty line at its
  // mark/run height; only section-break carryover anchors collapse (their
  // paragraph mark belongs to the swallowed section boundary).
  const collapsibleAbsoluteFloatingAnchorOnlyParagraph =
    absoluteFloatingAnchorOnlyParagraph &&
    paragraphIsSectionBreakAnchorCarryover(paragraph);
  const inlineImageHeightPx = paragraph.children.reduce((largest, child) => {
    if (child.type !== "image") {
      return largest;
    }
    if (
      shouldRenderAbsoluteFloatingImage(child) ||
      shouldRenderWrappedFloatingImage(child)
    ) {
      return largest;
    }

    return Math.max(largest, child.heightPx ?? 0);
  }, 0);
  const wrappedFloatingImageHeightPx = excludeWrappedFloatingImageFootprint
    ? 0
    : paragraph.children.reduce((largest, child) => {
        if (child.type !== "image") {
          return largest;
        }

        return Math.max(
          largest,
          estimateWrappedFloatingImageFootprintPx(paragraph, child)
        );
      }, 0);
  const emptyParagraphHeightPx = paragraphIsEffectivelyEmpty(paragraph)
    ? lineHeightPx + EMPTY_PARAGRAPH_EXTRA_HEIGHT_PX
    : 0;
  const topBorderInsetPx = paragraphBorderInsetPx(
    paragraph.style?.borders?.top
  );
  const bottomBorderInsetPx = paragraphBorderInsetPx(
    paragraph.style?.borders?.bottom
  );
  const textFlowHeightPx = collapsibleAbsoluteFloatingAnchorOnlyParagraph
    ? 0
    : // When excluding the wrapped-float footprint, the dual-wrapped block
    // height spans the image; but the rendered paragraph only occupies its
    // text lines while the float overhangs. Use the text-line height so the
    // float's exclusion can overlap following paragraphs.
    dualWrappedLayout && !excludeWrappedFloatingImageFootprint
    ? wrappedPretextParagraphBlockHeightPx(dualWrappedLayout.layout)
    : lineHeightPx * lineCount;

  const contentHeightPx = Math.max(
    collapsibleAbsoluteFloatingAnchorOnlyParagraph ? 0 : lineHeightPx,
    textFlowHeightPx,
    inlineImageHeightPx,
    wrappedFloatingImageHeightPx,
    emptyParagraphHeightPx
  );
  if (excludeWrappedFloatingImageFootprint && paragraph.children.some((c) => c.type === "image")) {
  }
  const estimatedHeightPx = Math.max(
    1,
    beforeSpacing +
      afterSpacing +
      topBorderInsetPx +
      bottomBorderInsetPx +
      contentHeightPx
  );
  if (sourceXml) {
    const cachedByWidth =
      paragraphEstimatedHeightBySourceXml.get(sourceXml) ??
      new Map<number, number>();
    cachedByWidth.set(widthKey, estimatedHeightPx);
    setCacheEntry(
      paragraphEstimatedHeightBySourceXml,
      sourceXml,
      cachedByWidth
    );
  }
  return estimatedHeightPx;
}

function paragraphHasExplicitBeforeSpacing(paragraph: ParagraphNode): boolean {
  const xml = paragraph.sourceXml ?? "";
  if (!xml) {
    return false;
  }

  const spacingTag = xml.match(/<w:spacing\b[^>]*\/?>/i)?.[0];
  if (!spacingTag) {
    return false;
  }

  return /\bw:before(?:\s*=|Lines\s*=|Autospacing\s*=)/i.test(spacingTag);
}

function paragraphHasExplicitSpacing(paragraph: ParagraphNode): boolean {
  const xml = paragraph.sourceXml ?? "";
  if (!xml) {
    return false;
  }

  const paragraphPropertiesXml =
    xml.match(/<w:pPr\b[\s\S]*?<\/w:pPr>/i)?.[0] ??
    xml.match(/<w:pPr\b[^>]*\/>/i)?.[0] ??
    "";
  if (!paragraphPropertiesXml) {
    return false;
  }

  return /<w:spacing\b[^>]*\/?>/i.test(paragraphPropertiesXml);
}

function wordLikeTableCellParagraph(
  paragraph: ParagraphNode,
  applyWordTableDefaults: boolean
): ParagraphNode {
  if (
    !applyWordTableDefaults ||
    !paragraph.sourceXml ||
    paragraphHasExplicitSpacing(paragraph)
  ) {
    return paragraph;
  }

  return {
    ...paragraph,
    sourceXml: undefined,
    style: {
      ...(paragraph.style ?? {}),
      spacing: {
        ...(paragraph.style?.spacing ?? {}),
        beforeTwips: WORD_TABLE_CELL_PARAGRAPH_BEFORE_TWIPS,
        afterTwips: WORD_TABLE_CELL_PARAGRAPH_AFTER_TWIPS,
        lineTwips: WORD_TABLE_CELL_PARAGRAPH_AUTO_LINE_TWIPS,
        lineRule: "auto",
      },
    },
  };
}

function suppressFirstTableCellParagraphTopSpacing(
  paragraph: ParagraphNode
): boolean {
  // Keep explicit paragraph top spacing from DOCX; only suppress implicit style spacing.
  if (!paragraph.sourceXml) {
    const beforeTwips = paragraph.style?.spacing?.beforeTwips;
    return !(Number.isFinite(beforeTwips) && (beforeTwips as number) > 0);
  }

  return !paragraphHasExplicitBeforeSpacing(paragraph);
}

function estimateTableCellContentHeightPx(
  nodeContent: TableCellContentNode[],
  availableWidthPx?: number,
  numberingDefinitions?: NumberingDefinitionSet,
  applyWordTableDefaults = false,
  docGridLinePitchPx?: number
): number {
  let paragraphIndex = 0;
  let expandedWithPretextLayout = false;
  let totalHeightPx = 0;

  for (const contentNode of nodeContent) {
    if (!isParagraphCellContentNode(contentNode)) {
      totalHeightPx += estimateTableHeightPx(
        contentNode,
        availableWidthPx,
        numberingDefinitions,
        docGridLinePitchPx
      );
      continue;
    }

    const disableDocGridSnap =
      paragraphDocGridSnapState(contentNode) !== "snap";
    const paragraphForLayout = wordLikeTableCellParagraph(
      contentNode,
      applyWordTableDefaults
    );
    const baseHeight = estimateParagraphHeightPx(
      paragraphForLayout,
      availableWidthPx,
      numberingDefinitions,
      docGridLinePitchPx,
      disableDocGridSnap
    );
    const lineHeightPx = Math.max(
      MIN_PARAGRAPH_LINE_HEIGHT_PX,
      estimateParagraphLineHeightPx(
        paragraphForLayout,
        docGridLinePitchPx,
        disableDocGridSnap
      )
    );
    const pretextSource = buildParagraphPretextLayoutSource(
      paragraphForLayout,
      {
        allowExplicitLineBreakText: true,
        expandTabsForLayout: true,
      }
    );
    const paragraphTextWidthPx =
      typeof availableWidthPx === "number" && availableWidthPx > 0
        ? paragraphAvailableTextWidthPx(
            paragraphForLayout,
            availableWidthPx,
            numberingDefinitions
          )
        : undefined;
    const pretextLayout =
      pretextSource &&
      typeof paragraphTextWidthPx === "number" &&
      paragraphTextWidthPx > 0
        ? layoutParagraphPretextSource(
            paragraphForLayout,
            pretextSource,
            paragraphTextWidthPx,
            lineHeightPx,
            []
          )
        : undefined;
    const suppressTopSpacing =
      paragraphIndex === 0 &&
      suppressFirstTableCellParagraphTopSpacing(contentNode);
    paragraphIndex += 1;
    const beforeSpacing = suppressTopSpacing
      ? 0
      : twipsToPixels(paragraphForLayout.style?.spacing?.beforeTwips) ?? 0;
    const afterSpacing =
      twipsToPixels(paragraphForLayout.style?.spacing?.afterTwips) ?? 0;
    const topBorderInsetPx = paragraphBorderInsetPx(
      paragraphForLayout.style?.borders?.top
    );
    const bottomBorderInsetPx = paragraphBorderInsetPx(
      paragraphForLayout.style?.borders?.bottom
    );
    const pretextHeightPx = pretextLayout
      ? beforeSpacing +
        afterSpacing +
        topBorderInsetPx +
        bottomBorderInsetPx +
        wrappedPretextParagraphBlockHeightPx(pretextLayout)
      : 0;
    const resolvedBaseHeight =
      pretextHeightPx > 0 ? Math.max(baseHeight, pretextHeightPx) : baseHeight;
    // Only treat the pretext layout as a genuine multi-line expansion when it
    // exceeds the base estimate by at least half a line. A tiny (rounding)
    // difference between the two height calcs must not trigger the extra-line
    // safety pad below, which otherwise added a phantom ~14px to every
    // single-line cell and roughly doubled table heights during pagination.
    if (pretextHeightPx > baseHeight + lineHeightPx / 2) {
      expandedWithPretextLayout = true;
    }

    if (!suppressTopSpacing) {
      totalHeightPx += resolvedBaseHeight;
      continue;
    }

    totalHeightPx += Math.max(1, resolvedBaseHeight - beforeSpacing);
  }

  return (
    totalHeightPx +
    (expandedWithPretextLayout ? Math.max(1, MIN_PARAGRAPH_LINE_HEIGHT_PX) : 0)
  );
}

function rowAllowsPageSplit(row: TableNode["rows"][number]): boolean {
  return row.style?.cantSplit !== true && row.style?.heightRule !== "exact";
}

function rowHasDeepFlowContent(row: TableNode["rows"][number]): boolean {
  let blockNodeCount = 0;
  let nestedTableCount = 0;

  for (const cell of row.cells) {
    blockNodeCount += cell.nodes.length;
    for (const contentNode of cell.nodes) {
      if (contentNode.type === "table") {
        nestedTableCount += 1;
      }
    }
  }

  return (
    nestedTableCount > 0 ||
    blockNodeCount >= SPLITTABLE_TABLE_ROW_DEEP_CONTENT_NODE_THRESHOLD
  );
}

function rowHasNestedTableContent(row: TableNode["rows"][number]): boolean {
  return row.cells.some((cell) =>
    cell.nodes.some((contentNode) => contentNode.type === "table")
  );
}

function capSplitFriendlyTableRowEstimatePx(
  row: TableNode["rows"][number],
  estimatedRowHeightPx: number,
  explicitHeightPx?: number,
  pageContentHeightPx?: number
): number {
  if (!rowAllowsPageSplit(row)) {
    return estimatedRowHeightPx;
  }

  if (!Number.isFinite(explicitHeightPx) || (explicitHeightPx as number) <= 0) {
    return estimatedRowHeightPx;
  }

  if (!rowHasDeepFlowContent(row)) {
    return estimatedRowHeightPx;
  }

  const safeExplicitHeightPx = Math.max(
    MIN_PARAGRAPH_LINE_HEIGHT_PX * 2,
    Math.round(explicitHeightPx as number)
  );
  const safePageContentHeightPx =
    Number.isFinite(pageContentHeightPx) && (pageContentHeightPx as number) > 0
      ? Math.max(
          MIN_PARAGRAPH_LINE_HEIGHT_PX * 4,
          Math.round(pageContentHeightPx as number)
        )
      : undefined;
  if (
    safePageContentHeightPx !== undefined &&
    estimatedRowHeightPx > safePageContentHeightPx + PAGE_OVERFLOW_TOLERANCE_PX
  ) {
    return estimatedRowHeightPx;
  }

  const cappedHeightPx =
    safeExplicitHeightPx +
    MIN_PARAGRAPH_LINE_HEIGHT_PX *
      SPLITTABLE_TABLE_ROW_ESTIMATE_EXTRA_LINE_COUNT;

  return Math.min(estimatedRowHeightPx, cappedHeightPx);
}

function tableStyleIdFromSourceXml(table: TableNode): string | undefined {
  const sourceXml = table.sourceXml ?? "";
  if (!sourceXml) {
    return undefined;
  }

  const styleMatch = sourceXml.match(/<w:tblStyle\b[^>]*w:val="([^"]+)"/i);
  const styleId = styleMatch?.[1]?.trim();
  return styleId ? styleId : undefined;
}

function tableHasVisibleBorders(table: TableNode): boolean {
  const borders = table.style?.borders;
  if (!borders) {
    return false;
  }

  return Object.values(borders).some(
    (border) => border && border.type !== "none" && border.type !== "nil"
  );
}

function tableContainsParagraphsWithoutExplicitSpacing(
  table: TableNode
): boolean {
  return table.rows.some((row) =>
    row.cells.some((cell) =>
      cell.nodes.some(
        (node) =>
          node.type === "paragraph" && !paragraphHasExplicitSpacing(node)
      )
    )
  );
}

function tableUsesWordLikeParagraphDefaults(table: TableNode): boolean {
  const styleId = tableStyleIdFromSourceXml(table)?.toLowerCase();
  if (styleId === "tablegrid") {
    return true;
  }

  return (
    table.style?.layout === "fixed" &&
    tableHasVisibleBorders(table) &&
    tableContainsParagraphsWithoutExplicitSpacing(table)
  );
}

export function estimateTableRowHeightsPx(
  table: TableNode,
  maxAvailableWidthPx?: number,
  numberingDefinitions?: NumberingDefinitionSet,
  docGridLinePitchPx?: number,
  pageContentHeightPx?: number
): number[] {
  const baseCacheKey = heightEstimateCacheKeyPx(
    maxAvailableWidthPx,
    docGridLinePitchPx
  );
  const cachedByKey = tableEstimatedRowHeightsByNode.get(table);
  let baseRowHeights = cachedByKey?.get(baseCacheKey);

  if (!baseRowHeights) {
    baseRowHeights = computeTableCellDerivedRowHeightsPx(
      table,
      maxAvailableWidthPx,
      numberingDefinitions,
      docGridLinePitchPx
    );
    const cacheByKey =
      cachedByKey ?? new Map<number, number[]>();
    cacheByKey.set(baseCacheKey, baseRowHeights);
    tableEstimatedRowHeightsByNode.set(table, cacheByKey);
  }

  return table.rows.map((row, rowIndex) => {
    let rowHeightPx = baseRowHeights[rowIndex] ?? 0;

    const explicitHeightPx = twipsToPixels(row.style?.heightTwips);
    if (explicitHeightPx && explicitHeightPx > 0) {
      rowHeightPx =
        row.style?.heightRule === "exact"
          ? explicitHeightPx
          : Math.max(rowHeightPx, explicitHeightPx);
    }
    rowHeightPx = capSplitFriendlyTableRowEstimatePx(
      row,
      rowHeightPx,
      explicitHeightPx,
      pageContentHeightPx
    );

    const paginationPaddingRatio =
      table.rows.length >= 35
        ? 1.32
        : table.rows.length >= TABLE_ROW_HEIGHT_PAGINATION_ESTIMATE_PADDING_MIN_ROWS
        ? TABLE_ROW_HEIGHT_PAGINATION_ESTIMATE_PADDING_RATIO
        : 1;
    const paddedRowHeightPx =
      paginationPaddingRatio > 1
        ? Math.round(rowHeightPx * paginationPaddingRatio)
        : rowHeightPx;

    return Math.max(MIN_PARAGRAPH_LINE_HEIGHT_PX, paddedRowHeightPx);
  });
}

function computeTableCellDerivedRowHeightsPx(
  table: TableNode,
  maxAvailableWidthPx?: number,
  numberingDefinitions?: NumberingDefinitionSet,
  docGridLinePitchPx?: number
): number[] {
  const defaultCellMargin = table.style?.cellMarginTwips;
  const columnCount = tableColumnCount(table);
  const tableWidthPx = twipsToPixels(table.style?.widthTwips);
  const rawTableColumnWidthsPx = (() => {
    const definedWidthsTwips = columnWidthsFromTableDefinition(
      table,
      columnCount
    );
    if (!definedWidthsTwips || definedWidthsTwips.length === 0) {
      return defaultColumnWidthsPx(columnCount, tableWidthPx);
    }

    const widthsPx = definedWidthsTwips.map(
      (widthTwips) => twipsToPixels(widthTwips) ?? 0
    );
    return normalizeColumnWidthsPx(widthsPx, columnCount, tableWidthPx, 1);
  })();
  const rawResolvedTableWidthPx =
    tableWidthPx ??
    rawTableColumnWidthsPx.reduce((sum, widthPx) => sum + widthPx, 0);
  const collapsedHorizontalBorderBleedPx =
    resolveCollapsedTableHorizontalOuterBleedPx(table, columnCount);
  const maxTableWidthPx =
    Number.isFinite(maxAvailableWidthPx) && (maxAvailableWidthPx as number) > 0
      ? Math.max(
          120,
          (maxAvailableWidthPx as number) - collapsedHorizontalBorderBleedPx
        )
      : undefined;
  const resolvedTableWidthPx = clampTableWidthPx(
    rawResolvedTableWidthPx,
    maxTableWidthPx
  );
  const tableColumnWidthsPx = fitColumnWidthsToWidth(
    rawTableColumnWidthsPx,
    resolvedTableWidthPx
  );
  const applyWordTableDefaults = tableUsesWordLikeParagraphDefaults(table);

  return table.rows.map((row) => {
    let columnCursor = 0;
    const rowHeightPx = row.cells.reduce((largest, cell) => {
      const columnSpan =
        cell.style?.gridSpan && cell.style.gridSpan > 1
          ? cell.style.gridSpan
          : 1;
      const startColumnIndex = columnCursor;
      const endColumnIndex = Math.min(
        columnCount - 1,
        startColumnIndex + columnSpan - 1
      );
      columnCursor += columnSpan;
      const spanWidthPx = tableColumnWidthsPx
        .slice(startColumnIndex, endColumnIndex + 1)
        .reduce((sum, widthPx) => sum + widthPx, 0);
      const fallbackCellWidthPx =
        (resolvedTableWidthPx / Math.max(1, columnCount)) * columnSpan;
      const cellWidthPx = spanWidthPx > 0 ? spanWidthPx : fallbackCellWidthPx;
      const margin = cell.style?.marginTwips ?? defaultCellMargin;
      const resolvedPaddingPx = resolveTableSpacingPaddingPx(margin);
      const verticalPaddingPx =
        resolvedPaddingPx.top + resolvedPaddingPx.bottom;
      const horizontalPaddingPx =
        resolvedPaddingPx.left + resolvedPaddingPx.right;
      const contentWidthPx = Math.max(
        24,
        Math.round(cellWidthPx - horizontalPaddingPx)
      );
      const paragraphHeightPx = estimateTableCellContentHeightPx(
        cell.nodes,
        contentWidthPx,
        numberingDefinitions,
        applyWordTableDefaults,
        docGridLinePitchPx
      );
      return Math.max(largest, paragraphHeightPx + verticalPaddingPx);
    }, 0);

    return rowHeightPx;
  });
}

function resolveTableRowHeightCss(
  row: TableNode["rows"][number],
  rowHeightPx?: number
): React.CSSProperties | undefined {
  if (!Number.isFinite(rowHeightPx) || (rowHeightPx as number) <= 0) {
    return undefined;
  }

  const resolvedHeightPx = Math.max(
    MIN_PARAGRAPH_LINE_HEIGHT_PX,
    Math.round(rowHeightPx as number)
  );
  if (row.style?.heightRule === "exact") {
    return { height: `${resolvedHeightPx}px` };
  }

  // Use `height` rather than `min-height`: browsers ignore `min-height` on
  // table rows/cells, but on a table row `height` is treated as a MINIMUM
  // (the row still grows when content is taller). This is what makes an
  // explicit "at least" row height (e.g. a docx trHeight on a header band)
  // actually apply instead of collapsing to the text line height.
  return { height: `${resolvedHeightPx}px` };
}

interface TableCellSliceBoundaryLayout {
  safeBoundariesPx: number[];
  contentBottomPx: number;
}

function uniqueSortedPixelBoundaries(values: number[]): number[] {
  const sorted = values
    .filter((value) => Number.isFinite(value))
    .map((value) => Math.max(0, Math.round(value)))
    .sort((left, right) => left - right);
  const unique: number[] = [];
  for (const value of sorted) {
    const previous = unique[unique.length - 1];
    if (
      previous === undefined ||
      Math.abs(previous - value) > TABLE_ROW_SLICE_BOUNDARY_TOLERANCE_PX
    ) {
      unique.push(value);
    }
  }
  return unique;
}

function estimateParagraphBoundaryOffsetsPx(
  paragraph: ParagraphNode,
  availableWidthPx: number,
  numberingDefinitions: NumberingDefinitionSet | undefined,
  applyWordTableDefaults: boolean,
  docGridLinePitchPx: number | undefined,
  paragraphIndex: number
): {
  heightPx: number;
  safeBoundariesPx: number[];
} {
  const paragraphForLayout = wordLikeTableCellParagraph(
    paragraph,
    applyWordTableDefaults
  );
  const disableDocGridSnap = paragraphDocGridSnapState(paragraph) === "disable";
  const paragraphHeightPx = estimateParagraphHeightPx(
    paragraphForLayout,
    availableWidthPx,
    numberingDefinitions,
    docGridLinePitchPx,
    disableDocGridSnap
  );
  const suppressTopSpacing =
    paragraphIndex === 0 &&
    suppressFirstTableCellParagraphTopSpacing(paragraph);
  const beforeSpacingPx = suppressTopSpacing
    ? 0
    : twipsToPixels(paragraphForLayout.style?.spacing?.beforeTwips) ?? 0;
  const afterSpacingPx =
    twipsToPixels(paragraphForLayout.style?.spacing?.afterTwips) ?? 0;
  const topBorderInsetPx = paragraphBorderInsetPx(
    paragraphForLayout.style?.borders?.top
  );
  const bottomBorderInsetPx = paragraphBorderInsetPx(
    paragraphForLayout.style?.borders?.bottom
  );
  const lineHeightPx = Math.max(
    MIN_PARAGRAPH_LINE_HEIGHT_PX,
    estimateParagraphLineHeightPx(
      paragraphForLayout,
      docGridLinePitchPx,
      disableDocGridSnap
    )
  );
  const pretextSource = buildParagraphPretextLayoutSource(paragraphForLayout, {
    allowExplicitLineBreakText: true,
    expandTabsForLayout: true,
  });
  const paragraphTextWidthPx = paragraphAvailableTextWidthPx(
    paragraphForLayout,
    availableWidthPx,
    numberingDefinitions
  );
  const pretextLayout = pretextSource
    ? layoutParagraphPretextSource(
        paragraphForLayout,
        pretextSource,
        paragraphTextWidthPx,
        lineHeightPx,
        []
      )
    : undefined;
  const lineTopOffsetsPx = pretextLayout
    ? pretextLayout.lines.map((line) => Math.max(0, Math.round(line.y)))
    : Array.from(
        {
          length: Math.max(
            1,
            paragraphLineCountWithinWidth(
              paragraphForLayout,
              availableWidthPx,
              numberingDefinitions
            )
          ),
        },
        (_, lineIndex) => lineIndex * lineHeightPx
      );
  const textTopPx = beforeSpacingPx + topBorderInsetPx;
  const textHeightPx = pretextLayout
    ? wrappedPretextParagraphBlockHeightPx(pretextLayout)
    : lineTopOffsetsPx.length * lineHeightPx;
  const visualHeightPx = Math.max(
    1,
    beforeSpacingPx +
      topBorderInsetPx +
      textHeightPx +
      bottomBorderInsetPx +
      afterSpacingPx
  );
  const heightPx = Math.max(1, paragraphHeightPx, visualHeightPx);
  const lineBoundariesPx = lineTopOffsetsPx.map(
    (lineTopPx) => textTopPx + lineTopPx + lineHeightPx
  );

  return {
    heightPx,
    safeBoundariesPx: uniqueSortedPixelBoundaries([
      ...lineBoundariesPx,
      heightPx,
    ]),
  };
}

function estimateNestedTableBoundaryOffsetsPx(
  table: TableNode,
  availableWidthPx: number,
  numberingDefinitions: NumberingDefinitionSet | undefined,
  docGridLinePitchPx: number | undefined
): {
  heightPx: number;
  safeBoundariesPx: number[];
} {
  const rowHeightsPx = estimateTableRowHeightsPx(
    table,
    availableWidthPx,
    numberingDefinitions,
    docGridLinePitchPx
  );
  const boundariesPx: number[] = [];
  let cursorPx = 0;
  for (const rowHeightPx of rowHeightsPx) {
    cursorPx += Math.max(MIN_PARAGRAPH_LINE_HEIGHT_PX, rowHeightPx);
    boundariesPx.push(cursorPx);
  }

  return {
    heightPx: Math.max(MIN_PARAGRAPH_LINE_HEIGHT_PX, cursorPx),
    safeBoundariesPx: uniqueSortedPixelBoundaries(boundariesPx),
  };
}

function estimateTableCellSliceBoundaryLayoutPx(params: {
  cell: TableNode["rows"][number]["cells"][number];
  rowHeightPx: number;
  contentWidthPx: number;
  tableCellMarginTwips?: TableSpacingTwips;
  numberingDefinitions?: NumberingDefinitionSet;
  applyWordTableDefaults: boolean;
  docGridLinePitchPx?: number;
}): TableCellSliceBoundaryLayout {
  const {
    cell,
    rowHeightPx,
    contentWidthPx,
    tableCellMarginTwips,
    numberingDefinitions,
    applyWordTableDefaults,
    docGridLinePitchPx,
  } = params;
  const paddingPx = resolveTableSpacingPaddingPx(
    mergeTableSpacing(tableCellMarginTwips, cell.style?.marginTwips)
  );
  const localBoundariesPx = [0, paddingPx.top];
  let contentCursorPx = paddingPx.top;
  let paragraphIndex = 0;

  for (const contentNode of cell.nodes) {
    const layout =
      contentNode.type === "paragraph"
        ? estimateParagraphBoundaryOffsetsPx(
            contentNode,
            contentWidthPx,
            numberingDefinitions,
            applyWordTableDefaults,
            docGridLinePitchPx,
            paragraphIndex++
          )
        : estimateNestedTableBoundaryOffsetsPx(
            contentNode,
            contentWidthPx,
            numberingDefinitions,
            docGridLinePitchPx
          );

    localBoundariesPx.push(
      ...layout.safeBoundariesPx.map(
        (boundaryPx) => contentCursorPx + boundaryPx
      )
    );
    contentCursorPx += layout.heightPx;
  }

  const contentBottomPx = contentCursorPx + paddingPx.bottom;
  const contentFlowHeightPx = Math.max(0, contentCursorPx - paddingPx.top);
  const availableContentHeightPx = Math.max(
    0,
    rowHeightPx - paddingPx.top - paddingPx.bottom
  );
  const extraVerticalSpacePx = Math.max(
    0,
    availableContentHeightPx - contentFlowHeightPx
  );
  const verticalOffsetPx =
    cell.style?.verticalAlign === "center"
      ? Math.round(extraVerticalSpacePx / 2)
      : cell.style?.verticalAlign === "bottom"
      ? extraVerticalSpacePx
      : 0;

  return {
    safeBoundariesPx: uniqueSortedPixelBoundaries(
      localBoundariesPx.map((boundaryPx) =>
        Math.min(rowHeightPx, boundaryPx + verticalOffsetPx)
      )
    ),
    contentBottomPx: Math.min(rowHeightPx, contentBottomPx + verticalOffsetPx),
  };
}

function tableCellSliceBoundaryIsSafe(
  layout: TableCellSliceBoundaryLayout,
  boundaryPx: number
): boolean {
  if (boundaryPx <= TABLE_ROW_SLICE_BOUNDARY_TOLERANCE_PX) {
    return true;
  }

  if (
    boundaryPx >=
    layout.contentBottomPx - TABLE_ROW_SLICE_BOUNDARY_TOLERANCE_PX
  ) {
    return true;
  }

  return layout.safeBoundariesPx.some(
    (safeBoundaryPx) =>
      Math.abs(safeBoundaryPx - boundaryPx) <=
      TABLE_ROW_SLICE_BOUNDARY_TOLERANCE_PX
  );
}

function resolveTableRowSliceHeightOnSafeBoundaryPx(params: {
  table: TableNode;
  rowIndex: number;
  rowHeightPx: number;
  rowSliceOffsetPx: number;
  preferredSliceHeightPx: number;
  maxAvailableWidthPx?: number;
  numberingDefinitions?: NumberingDefinitionSet;
  docGridLinePitchPx?: number;
}): number | undefined {
  const {
    table,
    rowIndex,
    rowHeightPx,
    rowSliceOffsetPx,
    preferredSliceHeightPx,
    maxAvailableWidthPx,
    numberingDefinitions,
    docGridLinePitchPx,
  } = params;
  const row = table.rows[rowIndex];
  if (!row || !rowHasNestedTableContent(row)) {
    return preferredSliceHeightPx;
  }

  const sliceStartPx = Math.max(0, Math.round(rowSliceOffsetPx));
  const preferredSliceEndPx = Math.min(
    rowHeightPx,
    sliceStartPx + Math.max(0, Math.round(preferredSliceHeightPx))
  );
  if (
    preferredSliceEndPx >=
    rowHeightPx - TABLE_ROW_SLICE_BOUNDARY_TOLERANCE_PX
  ) {
    return Math.max(0, rowHeightPx - sliceStartPx);
  }

  const columnCount = tableColumnCount(table);
  const tableWidthPx = twipsToPixels(table.style?.widthTwips);
  const rawTableColumnWidthsPx = (() => {
    const definedWidthsTwips = columnWidthsFromTableDefinition(
      table,
      columnCount
    );
    if (!definedWidthsTwips || definedWidthsTwips.length === 0) {
      return defaultColumnWidthsPx(columnCount, tableWidthPx);
    }

    const widthsPx = definedWidthsTwips.map(
      (widthTwips) => twipsToPixels(widthTwips) ?? 0
    );
    return normalizeColumnWidthsPx(widthsPx, columnCount, tableWidthPx, 1);
  })();
  const rawResolvedTableWidthPx =
    tableWidthPx ??
    rawTableColumnWidthsPx.reduce((sum, widthPx) => sum + widthPx, 0);
  const collapsedHorizontalBorderBleedPx =
    resolveCollapsedTableHorizontalOuterBleedPx(table, columnCount);
  const maxTableWidthPx =
    Number.isFinite(maxAvailableWidthPx) && (maxAvailableWidthPx as number) > 0
      ? Math.max(
          120,
          (maxAvailableWidthPx as number) - collapsedHorizontalBorderBleedPx
        )
      : undefined;
  const resolvedTableWidthPx = clampTableWidthPx(
    rawResolvedTableWidthPx,
    maxTableWidthPx
  );
  const tableColumnWidthsPx = fitColumnWidthsToWidth(
    rawTableColumnWidthsPx,
    resolvedTableWidthPx
  );
  const applyWordTableDefaults = tableUsesWordLikeParagraphDefaults(table);
  const tableCellMarginTwips = table.style?.cellMarginTwips;
  const cellLayouts: TableCellSliceBoundaryLayout[] = [];
  const candidateBoundariesPx = [preferredSliceEndPx];
  let columnCursor = 0;

  for (const cell of row.cells) {
    const colSpanValue =
      cell.style?.gridSpan && cell.style.gridSpan > 1 ? cell.style.gridSpan : 1;
    const startColumnIndex = columnCursor;
    const endColumnIndex = Math.min(
      columnCount - 1,
      startColumnIndex + colSpanValue - 1
    );
    columnCursor += colSpanValue;
    const spannedWidthPx = tableColumnWidthsPx
      .slice(startColumnIndex, endColumnIndex + 1)
      .reduce((sum, widthPx) => sum + widthPx, 0);
    const fallbackCellWidthPx =
      (resolvedTableWidthPx / Math.max(1, columnCount)) * colSpanValue;
    const cellRenderedWidthPx =
      twipsToPixels(cell.style?.widthTwips) ??
      (spannedWidthPx > 0 ? spannedWidthPx : fallbackCellWidthPx);
    const cellPaddingPx = resolveTableSpacingPaddingPx(
      mergeTableSpacing(tableCellMarginTwips, cell.style?.marginTwips)
    );
    const cellContentWidthPx = Math.max(
      1,
      cellRenderedWidthPx - cellPaddingPx.left - cellPaddingPx.right
    );
    const cellLayout = estimateTableCellSliceBoundaryLayoutPx({
      cell,
      rowHeightPx,
      contentWidthPx: cellContentWidthPx,
      tableCellMarginTwips,
      numberingDefinitions,
      applyWordTableDefaults,
      docGridLinePitchPx,
    });
    cellLayouts.push(cellLayout);
    candidateBoundariesPx.push(...cellLayout.safeBoundariesPx);
  }

  const minimumSliceEndPx =
    sliceStartPx + Math.max(1, MIN_TABLE_ROW_SLICE_REMAINING_HEIGHT_PX);
  const candidatesPx = uniqueSortedPixelBoundaries(candidateBoundariesPx)
    .filter(
      (boundaryPx) =>
        boundaryPx >= minimumSliceEndPx &&
        boundaryPx <=
          preferredSliceEndPx + TABLE_ROW_SLICE_BOUNDARY_TOLERANCE_PX
    )
    .sort((left, right) => right - left);

  for (const candidatePx of candidatesPx) {
    if (
      cellLayouts.every((layout) =>
        tableCellSliceBoundaryIsSafe(layout, candidatePx)
      )
    ) {
      return Math.max(0, candidatePx - sliceStartPx);
    }
  }

  return undefined;
}

function estimateTableHeightPx(
  table: TableNode,
  maxAvailableWidthPx?: number,
  numberingDefinitions?: NumberingDefinitionSet,
  docGridLinePitchPx?: number
): number {
  const sourceXml = table.sourceXml;
  const widthKey = heightEstimateCacheKeyPx(
    maxAvailableWidthPx,
    docGridLinePitchPx
  );
  if (sourceXml) {
    const cachedByWidth = tableEstimatedHeightBySourceXml.get(sourceXml);
    const cached = cachedByWidth?.get(widthKey);
    if (cached !== undefined) {
      return cached;
    }
  }

  const estimatedRowsHeightPx = estimateTableRowHeightsPx(
    table,
    maxAvailableWidthPx,
    numberingDefinitions,
    docGridLinePitchPx
  ).reduce(
    (sum, rowHeightPx) =>
      sum + Math.max(MIN_PARAGRAPH_LINE_HEIGHT_PX, rowHeightPx),
    0
  );

  const estimatedHeightPx = Math.max(
    MIN_PARAGRAPH_LINE_HEIGHT_PX * 2,
    estimatedRowsHeightPx
  );
  if (sourceXml) {
    const cachedByWidth =
      tableEstimatedHeightBySourceXml.get(sourceXml) ??
      new Map<number, number>();
    cachedByWidth.set(widthKey, estimatedHeightPx);
    setCacheEntry(tableEstimatedHeightBySourceXml, sourceXml, cachedByWidth);
  }
  return estimatedHeightPx;
}

function estimateDocNodeHeightPx(
  node: DocModel["nodes"][number],
  availableWidthPx?: number,
  numberingDefinitions?: NumberingDefinitionSet,
  docGridLinePitchPx?: number
): number {
  return node.type === "paragraph"
    ? estimateParagraphHeightPx(
        node,
        availableWidthPx,
        numberingDefinitions,
        docGridLinePitchPx
      )
    : estimateTableHeightPx(
        node,
        availableWidthPx,
        numberingDefinitions,
        docGridLinePitchPx
      );
}

function paragraphBeforeSpacingPx(paragraph: ParagraphNode): number {
  return twipsToPixels(paragraph.style?.spacing?.beforeTwips) ?? 0;
}

function paragraphAfterSpacingPx(paragraph: ParagraphNode): number {
  return twipsToPixels(paragraph.style?.spacing?.afterTwips) ?? 0;
}

function paragraphWidowControlEnabled(paragraph: ParagraphNode): boolean {
  return paragraph.style?.widowControl !== false;
}

function paragraphIsOnlyExplicitPageBreak(paragraph: ParagraphNode): boolean {
  if (!paragraphHasExplicitPageBreak(paragraph)) {
    return false;
  }

  return (
    !paragraphHasVisibleText(paragraph) &&
    !paragraphHasImage(paragraph) &&
    !paragraphHasFormField(paragraph)
  );
}

function resolveParagraphBeforeSpacingPx(
  model: DocModel,
  nodeIndex: number,
  paragraph: ParagraphNode,
  pageConsumedHeightPx: number,
  suppressSpacingBeforeAfterPageBreak: boolean
): number {
  const beforeSpacingPx = paragraphBeforeSpacingPx(paragraph);
  if (
    !suppressSpacingBeforeAfterPageBreak ||
    pageConsumedHeightPx > 0 ||
    nodeIndex <= 0
  ) {
    return beforeSpacingPx;
  }

  const previousNode = model.nodes[nodeIndex - 1];
  if (previousNode?.type !== "paragraph") {
    return beforeSpacingPx;
  }

  // ECMA-376 §2.15.3.49: when enabled, suppress before-spacing on first content
  // line following a page-break-only paragraph.
  return paragraphIsOnlyExplicitPageBreak(previousNode) ? 0 : beforeSpacingPx;
}

function paragraphCanSplitAcrossPages(
  paragraph: ParagraphNode,
  lineCount: number,
  options?: {
    allowKeepLinesOverflow?: boolean;
    allowKeepNextOverflow?: boolean;
    allowImageParagraphSplit?: boolean;
  }
): boolean {
  if (lineCount < 2) {
    return false;
  }

  if (paragraph.style?.keepLines === true && !options?.allowKeepLinesOverflow) {
    return false;
  }

  if (paragraph.style?.keepNext === true && !options?.allowKeepNextOverflow) {
    return false;
  }

  if (paragraphHasImage(paragraph) && !options?.allowImageParagraphSplit) {
    return false;
  }

  if (paragraphHasFormField(paragraph)) {
    return false;
  }

  return true;
}

function collectDocxEstimatedOverflowBreakStartNodeIndexes(
  model: DocModel,
  hardBreakStartNodeIndexes: Set<number>,
  pageContentHeightPx: number,
  pageContentWidthPx: number,
  numberingDefinitions?: NumberingDefinitionSet,
  paginationMetricsBySection?: PaginationSectionMetrics[],
  options?: {
    suppressSpacingBeforeAfterPageBreak?: boolean;
  }
): Set<number> {
  const breaks = new Set<number>();
  if (!Number.isFinite(pageContentHeightPx) || pageContentHeightPx <= 0) {
    return breaks;
  }

  const fallbackMetrics: PaginationSectionMetrics = {
    startNodeIndex: 0,
    pageContentWidthPx: Math.max(120, Math.round(pageContentWidthPx)),
    pageContentHeightPx: Math.max(120, Math.round(pageContentHeightPx)),
    pageContentHeightMultiplier: 1,
    docGridLinePitchPx: undefined,
  };
  const metricsBySection = paginationMetricsBySection?.length
    ? paginationMetricsBySection
    : [fallbackMetrics];
  const sectionStartPageBreakNodeIndexes =
    collectDocxSectionStartPageBreakNodeIndexes(model);
  const nextHardBreakStartNodeIndexByNodeIndex =
    buildNextHardBreakStartNodeIndexLookup(
      model.nodes.length,
      hardBreakStartNodeIndexes
    );

  let pageConsumedHeightPx = 0;
  let previousParagraphAfterPx = 0;
  let currentMetricsIndex = 0;
  // Mirrors buildDocumentPageNodeSegments: once a keepNext chain head starts a
  // page, later chain members must not re-trigger a keep-induced break — that
  // would strand the head on a near-empty page.
  let committedKeepNextChainEndNodeIndex = -1;
  const suppressSpacingBeforeAfterPageBreak =
    options?.suppressSpacingBeforeAfterPageBreak ?? false;
  let currentPageContentHeightPx =
    metricsBySection[0]?.pageContentHeightPx ??
    fallbackMetrics.pageContentHeightPx;
  const projectConsumedHeightAcrossSectionMultipliers = (
    consumedHeightPx: number,
    fromMetrics: PaginationSectionMetrics | undefined,
    toMetrics: PaginationSectionMetrics | undefined
  ): number => {
    const safeConsumedHeightPx = Math.max(0, Math.round(consumedHeightPx));
    if (safeConsumedHeightPx <= 0) {
      return 0;
    }

    const fromMultiplier = Math.max(
      1,
      Math.round(fromMetrics?.pageContentHeightMultiplier ?? 1)
    );
    const toMultiplier = Math.max(
      1,
      Math.round(toMetrics?.pageContentHeightMultiplier ?? 1)
    );
    if (fromMultiplier === toMultiplier) {
      return safeConsumedHeightPx;
    }

    const approximateVisualDepthPx = safeConsumedHeightPx / fromMultiplier;
    return Math.max(0, Math.round(approximateVisualDepthPx * toMultiplier));
  };
  for (let nodeIndex = 0; nodeIndex < model.nodes.length; nodeIndex += 1) {
    const previousMetricsIndex = currentMetricsIndex;
    currentMetricsIndex = resolvePaginationSectionMetricsIndexForNodeIndex(
      metricsBySection,
      nodeIndex,
      currentMetricsIndex
    );
    const nodeMetrics =
      metricsBySection[currentMetricsIndex] ?? fallbackMetrics;
    if (nodeIndex > 0 && currentMetricsIndex !== previousMetricsIndex) {
      pageConsumedHeightPx = projectConsumedHeightAcrossSectionMultipliers(
        pageConsumedHeightPx,
        metricsBySection[previousMetricsIndex],
        nodeMetrics
      );
      currentPageContentHeightPx = nodeMetrics.pageContentHeightPx;
    }

    if (hardBreakStartNodeIndexes.has(nodeIndex)) {
      pageConsumedHeightPx = 0;
      previousParagraphAfterPx = 0;
      currentPageContentHeightPx = nodeMetrics.pageContentHeightPx;
    }

    const node = model.nodes[nodeIndex];
    if (
      node.type === "paragraph" &&
      (paragraphIsStructuralSectionBreakSpacer(node) ||
        paragraphActsAsSectionBreakCarryoverSpacer(model, nodeIndex, node))
    ) {
      previousParagraphAfterPx = 0;
      continue;
    }
    if (
      node.type === "paragraph" &&
      paragraphActsAsTrailingRenderedPageBreakSpacer(model, nodeIndex, node)
    ) {
      previousParagraphAfterPx = 0;
      continue;
    }
    if (
      node.type === "paragraph" &&
      paragraphCollapsesIntoPreviousParagraph(node, model.nodes[nodeIndex - 1])
    ) {
      continue;
    }
    const directNodeBeforeSpacingPx =
      node.type === "paragraph" ? paragraphBeforeSpacingPx(node) : 0;
    const directNodeAfterSpacingPx =
      node.type === "paragraph" ? paragraphAfterSpacingPx(node) : 0;
    const nodeBeforeSpacingPx =
      node.type === "paragraph"
        ? effectiveParagraphBeforeSpacingPx(
            model,
            nodeIndex,
            node,
            pageConsumedHeightPx,
            suppressSpacingBeforeAfterPageBreak
          )
        : 0;
    const nodeAfterSpacingPx =
      node.type === "paragraph"
        ? effectiveParagraphAfterSpacingPx(model, nodeIndex, node)
        : 0;
    const rawNodeHeightPx = Math.max(
      1,
      estimateDocNodeHeightPx(
        node,
        nodeMetrics.pageContentWidthPx,
        numberingDefinitions,
        nodeMetrics.docGridLinePitchPx
      ) -
        directNodeBeforeSpacingPx -
        directNodeAfterSpacingPx +
        nodeBeforeSpacingPx +
        nodeAfterSpacingPx
    );
    const collapsedMarginPx =
      node.type === "paragraph" && pageConsumedHeightPx > 0
        ? Math.min(previousParagraphAfterPx, nodeBeforeSpacingPx)
        : 0;
    const collapsedNodeHeightPx = Math.max(
      1,
      rawNodeHeightPx - collapsedMarginPx
    );

    let requiredHeightPx = collapsedNodeHeightPx;
    let keepNextChainEndNodeIndex = -1;

    if (
      node.type === "paragraph" &&
      node.style?.keepNext === true &&
      nodeIndex > committedKeepNextChainEndNodeIndex &&
      paragraphHasVisibleText(node)
    ) {
      let chainCursor = nodeIndex;
      let chainPreviousParagraphAfterPx = nodeAfterSpacingPx;
      while (chainCursor < model.nodes.length - 1) {
        const currentChainNode = model.nodes[chainCursor];
        if (
          currentChainNode.type !== "paragraph" ||
          currentChainNode.style?.keepNext !== true ||
          !paragraphHasVisibleText(currentChainNode)
        ) {
          break;
        }
        if (hardBreakStartNodeIndexes.has(chainCursor + 1)) {
          break;
        }
        const nextChainNode = model.nodes[chainCursor + 1];
        if (nextChainNode.type !== "paragraph") {
          break;
        }

        chainCursor += 1;
        const chainMetricsIndex =
          resolvePaginationSectionMetricsIndexForNodeIndex(
            metricsBySection,
            chainCursor,
            currentMetricsIndex
          );
        const chainMetrics =
          metricsBySection[chainMetricsIndex] ?? fallbackMetrics;
        const nextDirectBeforeSpacingPx =
          nextChainNode.type === "paragraph"
            ? paragraphBeforeSpacingPx(nextChainNode)
            : 0;
        const nextDirectAfterSpacingPx =
          nextChainNode.type === "paragraph"
            ? paragraphAfterSpacingPx(nextChainNode)
            : 0;
        const nextBeforeSpacingPx =
          nextChainNode.type === "paragraph"
            ? effectiveParagraphBeforeSpacingPx(
                model,
                chainCursor,
                nextChainNode,
                1,
                suppressSpacingBeforeAfterPageBreak
              )
            : 0;
        const nextAfterSpacingPx =
          nextChainNode.type === "paragraph"
            ? effectiveParagraphAfterSpacingPx(
                model,
                chainCursor,
                nextChainNode
              )
            : 0;
        const nextRawHeightPx = Math.max(
          1,
          estimateDocNodeHeightPx(
            nextChainNode,
            chainMetrics.pageContentWidthPx,
            numberingDefinitions,
            chainMetrics.docGridLinePitchPx
          ) -
            nextDirectBeforeSpacingPx -
            nextDirectAfterSpacingPx +
            nextBeforeSpacingPx +
            nextAfterSpacingPx
        );
        const collapsedChainMarginPx =
          nextChainNode.type === "paragraph"
            ? Math.min(chainPreviousParagraphAfterPx, nextBeforeSpacingPx)
            : 0;
        requiredHeightPx += Math.max(
          1,
          nextRawHeightPx - collapsedChainMarginPx
        );
        requiredHeightPx += keepNextPaginationReservePx(
          currentChainNode,
          nextChainNode,
          chainMetrics.docGridLinePitchPx
        );
        chainPreviousParagraphAfterPx = nextAfterSpacingPx;
      }
      keepNextChainEndNodeIndex = chainCursor;
    }

    const remainingHeightPx = currentPageContentHeightPx - pageConsumedHeightPx;
    const canKeepTrailingSectionTailOnCurrentPage =
      shouldKeepTrailingSectionTailOnCurrentPage(
        model,
        nodeIndex,
        pageConsumedHeightPx,
        previousParagraphAfterPx,
        nodeMetrics.pageContentWidthPx,
        currentPageContentHeightPx,
        hardBreakStartNodeIndexes,
        sectionStartPageBreakNodeIndexes,
        nextHardBreakStartNodeIndexByNodeIndex,
        (_candidateNodeIndex, candidateNode, candidatePageContentWidthPx) =>
          estimateDocNodeHeightPx(
            candidateNode,
            candidatePageContentWidthPx,
            numberingDefinitions,
            nodeMetrics.docGridLinePitchPx
          ),
        (candidateNodeIndex, candidateParagraph, consumedHeightPx) =>
          effectiveParagraphBeforeSpacingPx(
            model,
            candidateNodeIndex,
            candidateParagraph,
            consumedHeightPx,
            suppressSpacingBeforeAfterPageBreak
          ),
        (candidateNodeIndex, candidateParagraph) =>
          effectiveParagraphAfterSpacingPx(
            model,
            candidateNodeIndex,
            candidateParagraph
          )
      );
    if (
      pageConsumedHeightPx > 0 &&
      requiredHeightPx > remainingHeightPx + PAGE_OVERFLOW_TOLERANCE_PX &&
      !canKeepTrailingSectionTailOnCurrentPage
    ) {
      breaks.add(nodeIndex);
      pageConsumedHeightPx = 0;
      previousParagraphAfterPx = 0;
      currentPageContentHeightPx = nodeMetrics.pageContentHeightPx;
    }

    if (pageConsumedHeightPx === 0 && keepNextChainEndNodeIndex > nodeIndex) {
      committedKeepNextChainEndNodeIndex = keepNextChainEndNodeIndex;
    }
    const effectiveNodeHeightPx =
      pageConsumedHeightPx > 0 ? collapsedNodeHeightPx : rawNodeHeightPx;
    pageConsumedHeightPx += effectiveNodeHeightPx;
    previousParagraphAfterPx =
      node.type === "paragraph" ? nodeAfterSpacingPx : 0;
  }

  for (const breakIndex of [...breaks]) {
    if (
      breakIndex <= 0 ||
      breakIndex >= model.nodes.length ||
      hardBreakStartNodeIndexes.has(breakIndex)
    ) {
      breaks.delete(breakIndex);
    }
  }

  return breaks;
}

interface DocumentPageRange {
  startNodeIndex: number;
  endNodeIndex: number;
}

function buildDocumentPageRanges(
  nodeCount: number,
  pageBreakStartNodeIndexes: Set<number>
): DocumentPageRange[] {
  if (nodeCount <= 0) {
    return [];
  }

  const sortedBreakStartIndexes = [...pageBreakStartNodeIndexes]
    .filter((index) => index > 0 && index < nodeCount)
    .sort((left, right) => left - right);

  const ranges: DocumentPageRange[] = [];
  let startNodeIndex = 0;

  for (const breakStartIndex of sortedBreakStartIndexes) {
    if (breakStartIndex <= startNodeIndex) {
      continue;
    }

    ranges.push({
      startNodeIndex,
      endNodeIndex: breakStartIndex,
    });
    startNodeIndex = breakStartIndex;
  }

  ranges.push({
    startNodeIndex,
    endNodeIndex: nodeCount,
  });

  return ranges;
}

function collectDocxPageBreakStartNodeIndexes(
  model: DocModel,
  pageContentHeightPx: number,
  pageContentWidthPx: number,
  numberingDefinitions?: NumberingDefinitionSet,
  paginationMetricsBySection?: PaginationSectionMetrics[],
  options?: {
    suppressSpacingBeforeAfterPageBreak?: boolean;
  }
): Set<number> {
  const hardBreaks = collectDocxHardPageBreakStartNodeIndexes(model);
  const overflowBreaks = collectDocxEstimatedOverflowBreakStartNodeIndexes(
    model,
    hardBreaks,
    pageContentHeightPx,
    pageContentWidthPx,
    numberingDefinitions,
    paginationMetricsBySection,
    options
  );
  return new Set<number>([...hardBreaks, ...overflowBreaks]);
}

interface TableRowRange {
  startRowIndex: number;
  endRowIndex: number;
}

interface TableRowSlice {
  rowIndex: number;
  startOffsetPx: number;
  sliceHeightPx: number;
  totalRowHeightPx: number;
}

interface ParagraphLineRange {
  startLineIndex: number;
  endLineIndex: number;
  totalLineCount: number;
  lineHeightPx: number;
}

interface ParagraphSegmentIdentity {
  nodeIndex: number;
  startLineIndex: number;
  endLineIndex: number;
}

interface DocumentPageNodeSegment {
  nodeIndex: number;
  tableRowRange?: TableRowRange;
  tableRowSlice?: TableRowSlice;
  paragraphLineRange?: ParagraphLineRange;
}

function paragraphSegmentHasPartialLineRange(
  paragraphLineRange?: ParagraphLineRange
): boolean {
  if (!paragraphLineRange) {
    return false;
  }

  return (
    paragraphLineRange.startLineIndex > 0 ||
    paragraphLineRange.endLineIndex < paragraphLineRange.totalLineCount
  );
}

export function resolveLineRangeWithinVerticalSlice(
  lineTopOffsetsPx: number[],
  lineHeightPx: number,
  sliceTopPx: number,
  sliceBottomPx: number
): ParagraphLineRange | undefined {
  if (
    lineTopOffsetsPx.length === 0 ||
    !Number.isFinite(lineHeightPx) ||
    lineHeightPx <= 0
  ) {
    return undefined;
  }

  const safeSliceTopPx = Math.max(0, sliceTopPx);
  const safeSliceBottomPx = Math.max(safeSliceTopPx, sliceBottomPx);
  const sliceHasHeight = safeSliceBottomPx > safeSliceTopPx;
  let startLineIndex: number | undefined;
  let endLineIndex: number | undefined;

  for (let lineIndex = 0; lineIndex < lineTopOffsetsPx.length; lineIndex += 1) {
    const lineTopPx = lineTopOffsetsPx[lineIndex] ?? lineIndex * lineHeightPx;
    const lineBottomPx = lineTopPx + lineHeightPx;
    const lineBelongsToSlice =
      sliceHasHeight &&
      lineBottomPx > safeSliceTopPx + PAGE_OVERFLOW_TOLERANCE_PX &&
      lineBottomPx <= safeSliceBottomPx + PAGE_OVERFLOW_TOLERANCE_PX;
    if (lineBelongsToSlice) {
      if (startLineIndex === undefined) {
        startLineIndex = lineIndex;
      }
      endLineIndex = lineIndex + 1;
    }
  }

  if (
    startLineIndex === undefined ||
    endLineIndex === undefined ||
    endLineIndex <= startLineIndex
  ) {
    return undefined;
  }

  return {
    startLineIndex,
    endLineIndex,
    totalLineCount: lineTopOffsetsPx.length,
    lineHeightPx,
  };
}

export function resolveTableCellParagraphVisualBottomPx(params: {
  paragraphTopPx: number;
  paragraphHeightPx: number;
  textBottomPx: number;
}): number {
  return Math.max(
    Math.round(params.paragraphTopPx + params.paragraphHeightPx),
    Math.round(params.textBottomPx)
  );
}

export function tableCellParagraphFitsFullyWithinSlice(params: {
  sliceStartPx: number;
  sliceBottomPx: number;
  paragraphTopPx: number;
  paragraphBottomPx: number;
}): boolean {
  return (
    params.sliceStartPx <= params.paragraphTopPx + PAGE_OVERFLOW_TOLERANCE_PX &&
    params.sliceBottomPx >=
      params.paragraphBottomPx +
        TABLE_CELL_SLICE_FULLY_VISIBLE_BOTTOM_BUFFER_PX -
        PAGE_OVERFLOW_TOLERANCE_PX
  );
}

export function resolveParagraphSegmentClipBleedPx(
  paragraphLineRange?: ParagraphLineRange
): {
  topPx: number;
  bottomPx: number;
} {
  if (!paragraphSegmentHasPartialLineRange(paragraphLineRange)) {
    return {
      topPx: 0,
      bottomPx: 0,
    };
  }

  return {
    topPx:
      paragraphLineRange && paragraphLineRange.startLineIndex > 0
        ? Math.max(0, PARAGRAPH_SEGMENT_TOP_BLEED_PX)
        : 0,
    bottomPx:
      paragraphLineRange &&
      paragraphLineRange.endLineIndex < paragraphLineRange.totalLineCount
        ? Math.max(0, PARAGRAPH_SEGMENT_DESCENDER_BLEED_PX)
        : 0,
  };
}

export function resolveFallbackParagraphSegmentClipBleedPx(
  paragraph: ParagraphNode,
  paragraphLineRange?: ParagraphLineRange
): {
  topPx: number;
  bottomPx: number;
} {
  if (!paragraphSegmentHasPartialLineRange(paragraphLineRange)) {
    return {
      topPx: 0,
      bottomPx: 0,
    };
  }

  const lineHeightPx = Math.max(1, paragraphLineRange?.lineHeightPx ?? 0);
  const maxFontSizePx = paragraphMaxFontSizePx(paragraph);
  const glyphOvershootPx = Math.max(
    0,
    Math.ceil((maxFontSizePx - lineHeightPx) / 2)
  );
  const ascenderSafetyPx = Math.max(
    glyphOvershootPx,
    Math.ceil(lineHeightPx * 0.22)
  );

  return {
    topPx:
      paragraphLineRange && paragraphLineRange.startLineIndex > 0
        ? Math.min(
            PARAGRAPH_SEGMENT_FALLBACK_TOP_BLEED_MAX_PX,
            ascenderSafetyPx
          )
        : 0,
    bottomPx:
      paragraphLineRange &&
      paragraphLineRange.endLineIndex < paragraphLineRange.totalLineCount
        ? Math.min(
            PARAGRAPH_SEGMENT_FALLBACK_BOTTOM_BLEED_MAX_PX,
            glyphOvershootPx
          )
        : 0,
  };
}

export function resolveParagraphSegmentNonFlowReservePx(
  paragraphLineRange?: ParagraphLineRange
): number {
  const bleed = resolveParagraphSegmentClipBleedPx(paragraphLineRange);
  if (bleed.topPx <= 0 && bleed.bottomPx <= 0) {
    return 0;
  }

  const lineHeightSafetyPx = Math.max(
    0,
    Math.ceil((paragraphLineRange?.lineHeightPx ?? 0) * 0.9)
  );
  return (
    Math.max(0, bleed.topPx) +
    Math.max(0, bleed.bottomPx) +
    Math.max(0, PARAGRAPH_SEGMENT_VISUAL_SAFETY_PX, lineHeightSafetyPx)
  );
}

function resolveFallbackParagraphSegmentNonFlowReservePx(
  paragraph: ParagraphNode,
  paragraphLineRange?: ParagraphLineRange
): number {
  const bleed = resolveFallbackParagraphSegmentClipBleedPx(
    paragraph,
    paragraphLineRange
  );
  if (bleed.topPx <= 0 && bleed.bottomPx <= 0) {
    return 0;
  }

  const lineHeightSafetyPx = Math.max(
    0,
    Math.ceil((paragraphLineRange?.lineHeightPx ?? 0) * 0.15)
  );
  return (
    Math.max(0, bleed.topPx) +
    Math.max(0, bleed.bottomPx) +
    Math.max(1, PARAGRAPH_SEGMENT_FALLBACK_VISUAL_SAFETY_PX, lineHeightSafetyPx)
  );
}

function paragraphSegmentIdentityMatches(
  segment: ParagraphSegmentIdentity | undefined,
  nodeIndex: number,
  paragraphLineRange?: ParagraphLineRange
): boolean {
  if (!segment || !paragraphLineRange) {
    return false;
  }

  return (
    segment.nodeIndex === nodeIndex &&
    segment.startLineIndex === paragraphLineRange.startLineIndex &&
    segment.endLineIndex === paragraphLineRange.endLineIndex
  );
}

function estimateRenderedPageSegmentHeightPx(
  node: DocModel["nodes"][number],
  segment: DocumentPageNodeSegment,
  model: DocModel,
  availableWidthPx: number,
  numberingDefinitions?: NumberingDefinitionSet,
  docGridLinePitchPx?: number,
  options?: {
    excludeWrappedFloatingImageFootprint?: boolean;
  }
): number {
  if (node.type === "paragraph") {
    const paragraphLineRange = segment.paragraphLineRange;
    if (paragraphLineRange) {
      const beforeSpacingPx =
        paragraphLineRange.startLineIndex === 0
          ? effectiveParagraphBeforeSpacingPx(
              model,
              segment.nodeIndex,
              node,
              segment.nodeIndex > 0 ? 1 : 0,
              false
            )
          : 0;
      const afterSpacingPx =
        paragraphLineRange.endLineIndex >= paragraphLineRange.totalLineCount
          ? effectiveParagraphAfterSpacingPx(model, segment.nodeIndex, node)
          : 0;
      const paragraphPretextSource = buildParagraphPretextLayoutSource(node, {
        allowExplicitLineBreakText: true,
        expandTabsForLayout: true,
      });
      const paragraphPretextLayout = paragraphPretextSource
        ? layoutParagraphPretextSource(
            node,
            paragraphPretextSource,
            paragraphAvailableTextWidthPx(
              node,
              availableWidthPx,
              numberingDefinitions
            ),
            Math.max(1, paragraphLineRange.lineHeightPx),
            []
          )
        : undefined;
      const segmentContentHeightPx =
        paragraphPretextLayout && paragraphPretextLayout.lineCount > 0
          ? resolvePretextLineRangeContentHeightPx(
              paragraphPretextLayout,
              paragraphLineRange.startLineIndex,
              paragraphLineRange.endLineIndex
            )
          : Math.max(
              1,
              paragraphLineRange.endLineIndex -
                paragraphLineRange.startLineIndex
            ) * Math.max(1, paragraphLineRange.lineHeightPx);
      return Math.max(
        1,
        beforeSpacingPx + segmentContentHeightPx + afterSpacingPx
      );
    }

    return Math.max(
      1,
      estimateParagraphHeightPx(
        node,
        availableWidthPx,
        numberingDefinitions,
        docGridLinePitchPx,
        false,
        undefined,
        options?.excludeWrappedFloatingImageFootprint
          ? { excludeWrappedFloatingImageFootprint: true }
          : undefined
      )
    );
  }

  if (segment.tableRowRange) {
    if (segment.tableRowSlice) {
      return Math.max(
        MIN_PARAGRAPH_LINE_HEIGHT_PX,
        Math.round(segment.tableRowSlice.sliceHeightPx)
      );
    }
    const rowHeightsPx = estimateTableRowHeightsPx(
      node,
      availableWidthPx,
      numberingDefinitions,
      docGridLinePitchPx
    );
    return Math.max(
      MIN_PARAGRAPH_LINE_HEIGHT_PX,
      sumEstimatedTableRowHeightsPx(
        rowHeightsPx,
        segment.tableRowRange.startRowIndex,
        segment.tableRowRange.endRowIndex
      )
    );
  }

  return Math.max(
    MIN_PARAGRAPH_LINE_HEIGHT_PX,
    estimateTableHeightPx(
      node,
      availableWidthPx,
      numberingDefinitions,
      docGridLinePitchPx
    )
  );
}

function resolveParagraphColumnRenderLineRange(
  paragraph: ParagraphNode,
  segment: DocumentPageNodeSegment,
  availableWidthPx: number,
  numberingDefinitions?: NumberingDefinitionSet,
  docGridLinePitchPx?: number
): ParagraphLineRange {
  const lineHeightPx = Math.max(
    1,
    segment.paragraphLineRange?.lineHeightPx ??
      estimateParagraphLineHeightPx(paragraph, docGridLinePitchPx)
  );
  if (segment.paragraphLineRange) {
    return {
      ...segment.paragraphLineRange,
      lineHeightPx,
    };
  }

  const paragraphTextWidthPx = paragraphAvailableTextWidthPx(
    paragraph,
    availableWidthPx,
    numberingDefinitions
  );
  const pretextSource = buildParagraphPretextLayoutSource(paragraph, {
    allowExplicitLineBreakText: true,
    expandTabsForLayout: true,
  });
  const pretextLayout = pretextSource
    ? layoutParagraphPretextSource(
        paragraph,
        pretextSource,
        paragraphTextWidthPx,
        lineHeightPx,
        []
      )
    : undefined;
  const totalLineCount =
    pretextLayout && pretextLayout.lineCount > 0
      ? pretextLayout.lineCount
      : paragraphLineCountWithinWidth(
          paragraph,
          availableWidthPx,
          numberingDefinitions
        );

  return {
    startLineIndex: 0,
    endLineIndex: Math.max(1, totalLineCount),
    totalLineCount: Math.max(1, totalLineCount),
    lineHeightPx,
  };
}

function splitParagraphSegmentForColumnRender(params: {
  paragraph: ParagraphNode;
  segment: DocumentPageNodeSegment;
  model: DocModel;
  availableWidthPx: number;
  availableHeightPx: number;
  numberingDefinitions?: NumberingDefinitionSet;
  docGridLinePitchPx?: number;
}):
  | {
      currentSegment: DocumentPageNodeSegment;
      currentHeightPx: number;
      remainderSegment: DocumentPageNodeSegment;
    }
  | undefined {
  const {
    paragraph,
    segment,
    model,
    availableWidthPx,
    availableHeightPx,
    numberingDefinitions,
    docGridLinePitchPx,
  } = params;
  if (
    segment.tableRowRange ||
    segment.tableRowSlice ||
    paragraphHasExplicitColumnBreak(paragraph)
  ) {
    return undefined;
  }

  const fullLineRange = resolveParagraphColumnRenderLineRange(
    paragraph,
    segment,
    availableWidthPx,
    numberingDefinitions,
    docGridLinePitchPx
  );
  const startLineIndex = Math.max(0, fullLineRange.startLineIndex);
  const endLineIndex = Math.max(startLineIndex, fullLineRange.endLineIndex);
  if (
    endLineIndex - startLineIndex < 2 ||
    !paragraphCanSplitAcrossPages(paragraph, fullLineRange.totalLineCount)
  ) {
    return undefined;
  }

  const safeAvailableHeightPx = Math.max(0, Math.round(availableHeightPx));
  let bestSegment: DocumentPageNodeSegment | undefined;
  let bestHeightPx = 0;

  for (
    let candidateEndLineIndex = startLineIndex + 1;
    candidateEndLineIndex < endLineIndex;
    candidateEndLineIndex += 1
  ) {
    const candidateSegment: DocumentPageNodeSegment = {
      ...segment,
      paragraphLineRange: {
        startLineIndex,
        endLineIndex: candidateEndLineIndex,
        totalLineCount: fullLineRange.totalLineCount,
        lineHeightPx: fullLineRange.lineHeightPx,
      },
    };
    const candidateHeightPx = estimateRenderedPageSegmentHeightPx(
      paragraph,
      candidateSegment,
      model,
      availableWidthPx,
      numberingDefinitions,
      docGridLinePitchPx
    );
    if (
      candidateHeightPx >
      safeAvailableHeightPx + PAGE_OVERFLOW_TOLERANCE_PX
    ) {
      break;
    }

    bestSegment = candidateSegment;
    bestHeightPx = candidateHeightPx;
  }

  if (!bestSegment?.paragraphLineRange) {
    return undefined;
  }

  return {
    currentSegment: bestSegment,
    currentHeightPx: bestHeightPx,
    remainderSegment: {
      ...segment,
      paragraphLineRange: {
        startLineIndex: bestSegment.paragraphLineRange.endLineIndex,
        endLineIndex,
        totalLineCount: fullLineRange.totalLineCount,
        lineHeightPx: fullLineRange.lineHeightPx,
      },
    },
  };
}

export function buildRenderColumnSegmentsForPageSection(
  model: DocModel,
  flowSegments: DocumentPageNodeSegment[],
  columnWidthsPx: number[],
  columnHeightPx: number,
  numberingDefinitions?: NumberingDefinitionSet,
  docGridLinePitchPxByNodeIndex?: Map<number, number | undefined>,
  measuredParagraphOuterHeightsPxByNodeIndex?: Map<number, number>,
  balanceColumns = false,
  forceColumnBreakNodeIndexes?: Set<number>
): DocumentPageNodeSegment[][] {
  const columnCount = Math.max(1, columnWidthsPx.length);
  const columns = Array.from(
    { length: columnCount },
    () => [] as DocumentPageNodeSegment[]
  );
  const maxColumnHeightPx = Math.max(120, Math.round(columnHeightPx));
  const resolveSegmentHeightPx = (
    segment: DocumentPageNodeSegment,
    columnWidthPx: number
  ): number => {
    const segmentNode = model.nodes[segment.nodeIndex];
    if (!segmentNode) {
      return MIN_PARAGRAPH_LINE_HEIGHT_PX;
    }

    const docGridLinePitchPx = docGridLinePitchPxByNodeIndex?.get(
      segment.nodeIndex
    );
    const measuredSegmentHeightPx =
      segmentNode.type === "paragraph" &&
      !segment.paragraphLineRange &&
      !segment.tableRowRange &&
      !segment.tableRowSlice
        ? measuredParagraphOuterHeightsPxByNodeIndex?.get(segment.nodeIndex)
        : undefined;
    return Number.isFinite(measuredSegmentHeightPx) &&
      (measuredSegmentHeightPx as number) > 0
      ? Math.max(1, Math.round(measuredSegmentHeightPx as number))
      : estimateRenderedPageSegmentHeightPx(
          segmentNode,
          segment,
          model,
          columnWidthPx,
          numberingDefinitions,
          docGridLinePitchPx
        );
  };
  const safeColumnHeightPx =
    balanceColumns && columnCount > 1
      ? Math.min(
          maxColumnHeightPx,
          Math.max(
            MIN_PARAGRAPH_LINE_HEIGHT_PX * 4,
            Math.ceil(
              flowSegments.reduce((totalHeightPx, segment) => {
                const columnWidthPx = Math.max(
                  120,
                  Math.round(columnWidthsPx[0] ?? 120)
                );
                return (
                  totalHeightPx + resolveSegmentHeightPx(segment, columnWidthPx)
                );
              }, 0) / columnCount
            ) + PAGE_OVERFLOW_TOLERANCE_PX
          )
        )
      : maxColumnHeightPx;
  let columnIndex = 0;
  let consumedHeightPx = 0;

  const moveToNextColumn = (): boolean => {
    if (columnIndex + 1 >= columnCount) {
      return false;
    }

    columnIndex += 1;
    consumedHeightPx = 0;
    return true;
  };

  const pushSegment = (
    segment: DocumentPageNodeSegment,
    heightPx: number
  ): void => {
    columns[columnIndex]?.push(segment);
    consumedHeightPx += Math.max(1, Math.round(heightPx));
  };

  for (const flowSegment of flowSegments) {
    // Explicit "nextColumn" section breaks force a column advance regardless
    // of how much room is left in the current column.
    const isNodeStartSegment =
      (flowSegment.paragraphLineRange?.startLineIndex ?? 0) === 0 &&
      (flowSegment.tableRowRange?.startRowIndex ?? 0) === 0 &&
      !flowSegment.tableRowSlice;
    if (
      isNodeStartSegment &&
      forceColumnBreakNodeIndexes?.has(flowSegment.nodeIndex) &&
      consumedHeightPx > 0
    ) {
      moveToNextColumn();
    }
    let pendingSegment: DocumentPageNodeSegment | undefined = flowSegment;
    let splitGuard = 0;

    while (pendingSegment && splitGuard < 256) {
      splitGuard += 1;
      const currentSegment: DocumentPageNodeSegment = pendingSegment;
      const segmentNode: DocModel["nodes"][number] | undefined =
        model.nodes[currentSegment.nodeIndex];
      if (!segmentNode) {
        columns[columnIndex]?.push(currentSegment);
        break;
      }

      const columnWidthPx = Math.max(
        120,
        Math.round(columnWidthsPx[columnIndex] ?? columnWidthsPx[0] ?? 120)
      );
      const docGridLinePitchPx = docGridLinePitchPxByNodeIndex?.get(
        currentSegment.nodeIndex
      );
      const segmentHeightPx = resolveSegmentHeightPx(
        currentSegment,
        columnWidthPx
      );
      const remainingHeightPx = Math.max(
        0,
        safeColumnHeightPx - consumedHeightPx
      );

      if (
        segmentHeightPx <= remainingHeightPx + PAGE_OVERFLOW_TOLERANCE_PX ||
        columnIndex + 1 >= columnCount
      ) {
        pushSegment(currentSegment, segmentHeightPx);
        pendingSegment = undefined;
        break;
      }

      const splitSegment:
        | {
            currentSegment: DocumentPageNodeSegment;
            currentHeightPx: number;
            remainderSegment: DocumentPageNodeSegment;
          }
        | undefined =
        segmentNode.type === "paragraph"
          ? splitParagraphSegmentForColumnRender({
              paragraph: segmentNode,
              segment: currentSegment,
              model,
              availableWidthPx: columnWidthPx,
              availableHeightPx: remainingHeightPx,
              numberingDefinitions,
              docGridLinePitchPx,
            })
          : undefined;
      if (splitSegment) {
        pushSegment(splitSegment.currentSegment, splitSegment.currentHeightPx);
        pendingSegment = splitSegment.remainderSegment;
        if (!moveToNextColumn()) {
          const remainderSegment = splitSegment.remainderSegment;
          const remainderHeightPx = estimateRenderedPageSegmentHeightPx(
            segmentNode,
            remainderSegment,
            model,
            columnWidthPx,
            numberingDefinitions,
            docGridLinePitchPx
          );
          pushSegment(remainderSegment, remainderHeightPx);
          pendingSegment = undefined;
        }
        continue;
      }

      if (!moveToNextColumn()) {
        pushSegment(currentSegment, segmentHeightPx);
        pendingSegment = undefined;
      }
    }
  }

  return columns;
}

function sumEstimatedTableRowHeightsPx(
  rowHeightsPx: number[],
  startRowIndex: number,
  endRowIndex: number
): number {
  let total = 0;
  const clampedStart = Math.max(0, startRowIndex);
  const clampedEnd = Math.max(
    clampedStart,
    Math.min(endRowIndex, rowHeightsPx.length)
  );
  for (let rowIndex = clampedStart; rowIndex < clampedEnd; rowIndex += 1) {
    total += Math.max(
      1,
      rowHeightsPx[rowIndex] ?? MIN_PARAGRAPH_LINE_HEIGHT_PX
    );
  }
  return total;
}

function fitTableRowsWithinHeightPx(
  rowHeightsPx: number[],
  startRowIndex: number,
  availableHeightPx: number,
  forceAtLeastOneRow: boolean
): number {
  if (startRowIndex >= rowHeightsPx.length) {
    return startRowIndex;
  }

  const safeAvailableHeightPx =
    Number.isFinite(availableHeightPx) && availableHeightPx > 0
      ? availableHeightPx
      : 0;
  let consumedHeightPx = 0;
  let rowCursor = startRowIndex;

  while (rowCursor < rowHeightsPx.length) {
    const rowHeightPx = Math.max(
      1,
      rowHeightsPx[rowCursor] ?? MIN_PARAGRAPH_LINE_HEIGHT_PX
    );
    if (
      consumedHeightPx + rowHeightPx >
      safeAvailableHeightPx + PAGE_OVERFLOW_TOLERANCE_PX
    ) {
      break;
    }

    consumedHeightPx += rowHeightPx;
    rowCursor += 1;
  }

  if (rowCursor === startRowIndex && forceAtLeastOneRow) {
    return Math.min(rowHeightsPx.length, startRowIndex + 1);
  }

  return rowCursor;
}

export function buildDocumentPageNodeSegmentsFromLastRenderedPageBreakHints(
  model: DocModel
): DocumentPageNodeSegment[][] {
  if (model.nodes.length === 0) {
    return [];
  }

  const pageStartNodeIndexes: number[] = [0];
  model.nodes.forEach((node, nodeIndex) => {
    if (nodeIndex === 0 || node.type !== "paragraph") {
      return;
    }

    if (
      paragraphHasLastRenderedPageBreak(node) ||
      paragraphHasPageBreakBefore(node)
    ) {
      pageStartNodeIndexes.push(nodeIndex);
    }
  });

  const uniquePageStartNodeIndexes = [...new Set(pageStartNodeIndexes)].sort(
    (left, right) => left - right
  );
  const pages: DocumentPageNodeSegment[][] = [];

  uniquePageStartNodeIndexes.forEach((startNodeIndex, pageIndex) => {
    const nextStartNodeIndex =
      uniquePageStartNodeIndexes[pageIndex + 1] ?? model.nodes.length;
    const pageSegments: DocumentPageNodeSegment[] = [];
    for (
      let nodeIndex = startNodeIndex;
      nodeIndex < nextStartNodeIndex;
      nodeIndex += 1
    ) {
      pageSegments.push({ nodeIndex });
    }
    if (pageSegments.length > 0) {
      pages.push(pageSegments);
    }
  });

  return pages;
}

export function buildDocumentPageNodeSegments(
  model: DocModel,
  pageContentHeightPx: number,
  pageContentWidthPx: number,
  numberingDefinitions?: NumberingDefinitionSet,
  paginationMetricsBySection?: PaginationSectionMetrics[],
  options?: {
    allowParagraphLineSplitting?: boolean;
    suppressSpacingBeforeAfterPageBreak?: boolean;
    measuredTableRowHeightsByNodeIndex?: Record<number, number[]>;
    measuredPageContentHeightsPxByPageIndex?: number[];
    measuredParagraphOuterHeightsPxByNodeIndex?: Map<number, number>;
    preferLastRenderedParagraphStartBreaks?: boolean;
    strictLastRenderedParagraphStartBreaks?: boolean;
  }
): DocumentPageNodeSegment[][] {
  if (model.nodes.length === 0) {
    return [];
  }

  const fallbackMetrics: PaginationSectionMetrics = {
    startNodeIndex: 0,
    pageContentWidthPx: Math.max(120, Math.round(pageContentWidthPx)),
    pageContentHeightPx: Math.max(120, Math.round(pageContentHeightPx)),
    docGridLinePitchPx: undefined,
  };
  const metricsBySection = paginationMetricsBySection?.length
    ? paginationMetricsBySection
    : [fallbackMetrics];

  const pages: DocumentPageNodeSegment[][] = [];
  let currentPageSegments: DocumentPageNodeSegment[] = [];
  let currentPageIndex = 0;
  const allowParagraphLineSplitting =
    options?.allowParagraphLineSplitting ?? true;
  const shouldSuppressSpacingAtPageBreaks =
    options?.suppressSpacingBeforeAfterPageBreak ?? false;
  const preferLastRenderedParagraphStartBreaks =
    options?.preferLastRenderedParagraphStartBreaks ?? false;
  const strictLastRenderedParagraphStartBreaks =
    options?.strictLastRenderedParagraphStartBreaks ?? false;
  const hardBreakStartNodeIndexes =
    collectDocxHardPageBreakStartNodeIndexes(model);
  // Numbered-list markers consume horizontal text room, so pagination needs the
  // rendered label text (e.g. "10.") to estimate line wrapping accurately.
  const paginationNumberingLabels = buildParagraphNumberingLabels(model);
  const sectionStartPageBreakNodeIndexes =
    collectDocxSectionStartPageBreakNodeIndexes(model);
  const nextHardBreakStartNodeIndexByNodeIndex =
    buildNextHardBreakStartNodeIndexLookup(
      model.nodes.length,
      hardBreakStartNodeIndexes
    );
  const estimatedRowHeightsByTableNodeIndex = new Map<number, number[]>();
  const measuredPageContentHeightsPxByPageIndex =
    options?.measuredPageContentHeightsPxByPageIndex;
  const resolvePageContentHeightPx = (
    pageIndex: number,
    fallbackHeightPx: number,
    heightMultiplier = 1
  ): number => {
    const overrideHeightPx =
      measuredPageContentHeightsPxByPageIndex?.[pageIndex];
    if (Number.isFinite(overrideHeightPx) && (overrideHeightPx as number) > 0) {
      return Math.max(
        24,
        Math.min(
          Math.round(fallbackHeightPx),
          Math.round(
            (overrideHeightPx as number) * Math.max(1, heightMultiplier)
          )
        )
      );
    }
    return Math.max(24, Math.round(fallbackHeightPx));
  };
  const resolveMetricsPageContentHeightPx = (
    pageIndex: number,
    metrics: PaginationSectionMetrics | undefined
  ): number =>
    resolvePageContentHeightPx(
      pageIndex,
      metrics?.pageContentHeightPx ?? fallbackMetrics.pageContentHeightPx,
      metrics?.pageContentHeightMultiplier ??
        fallbackMetrics.pageContentHeightMultiplier ??
        1
    );

  const startNextPage = (): void => {
    if (currentPageSegments.length > 0) {
      pages.push(currentPageSegments);
    }
    currentPageSegments = [];
    currentPageIndex += 1;
  };
  if (!Number.isFinite(pageContentHeightPx) || pageContentHeightPx <= 0) {
    return [model.nodes.map((_, nodeIndex) => ({ nodeIndex }))];
  }

  let pageConsumedHeightPx = 0;
  let previousParagraphAfterPx = 0;
  let currentMetricsIndex = 0;
  let currentSectionPageFlowOriginPx = 0;
  // Once a keepNext chain head starts a page, every page up to the chain end
  // begins with a chain member, so a keep-induced push for a later member can
  // never reunite it with its predecessors — it would only strand the chain
  // head on a near-empty page. Track the chain end to suppress those pushes.
  let committedKeepNextChainEndNodeIndex = -1;
  let currentPageContentHeightPx = resolveMetricsPageContentHeightPx(
    0,
    metricsBySection[0]
  );
  const projectConsumedHeightAcrossSectionMultipliers = (
    consumedHeightPx: number,
    fromMetrics: PaginationSectionMetrics | undefined,
    toMetrics: PaginationSectionMetrics | undefined
  ): number => {
    const safeConsumedHeightPx = Math.max(0, Math.round(consumedHeightPx));
    if (safeConsumedHeightPx <= 0) {
      return 0;
    }

    const fromMultiplier = Math.max(
      1,
      Math.round(fromMetrics?.pageContentHeightMultiplier ?? 1)
    );
    const toMultiplier = Math.max(
      1,
      Math.round(toMetrics?.pageContentHeightMultiplier ?? 1)
    );
    if (fromMultiplier === toMultiplier) {
      return safeConsumedHeightPx;
    }

    const approximateVisualDepthPx = safeConsumedHeightPx / fromMultiplier;
    return Math.max(0, Math.round(approximateVisualDepthPx * toMultiplier));
  };

  for (let nodeIndex = 0; nodeIndex < model.nodes.length; nodeIndex += 1) {
    const previousMetricsIndex = currentMetricsIndex;
    currentMetricsIndex = resolvePaginationSectionMetricsIndexForNodeIndex(
      metricsBySection,
      nodeIndex,
      currentMetricsIndex
    );
    const nodeMetrics =
      metricsBySection[currentMetricsIndex] ?? fallbackMetrics;
    if (nodeIndex > 0 && currentMetricsIndex !== previousMetricsIndex) {
      pageConsumedHeightPx = projectConsumedHeightAcrossSectionMultipliers(
        pageConsumedHeightPx,
        metricsBySection[previousMetricsIndex],
        nodeMetrics
      );
      currentPageContentHeightPx = resolveMetricsPageContentHeightPx(
        currentPageIndex,
        nodeMetrics
      );
      currentSectionPageFlowOriginPx = pageConsumedHeightPx;
    }

    if (
      hardBreakStartNodeIndexes.has(nodeIndex) &&
      currentPageSegments.length > 0
    ) {
      startNextPage();
      pageConsumedHeightPx = 0;
      previousParagraphAfterPx = 0;
      currentSectionPageFlowOriginPx = 0;
      currentPageContentHeightPx = resolveMetricsPageContentHeightPx(
        currentPageIndex,
        nodeMetrics
      );
    }

    const node = model.nodes[nodeIndex];
    if (
      node.type === "paragraph" &&
      (paragraphIsStructuralSectionBreakSpacer(node) ||
        paragraphActsAsSectionBreakCarryoverSpacer(model, nodeIndex, node))
    ) {
      previousParagraphAfterPx = 0;
      continue;
    }
    if (
      node.type === "paragraph" &&
      paragraphActsAsTrailingRenderedPageBreakSpacer(model, nodeIndex, node)
    ) {
      previousParagraphAfterPx = 0;
      continue;
    }
    if (
      node.type === "paragraph" &&
      paragraphCollapsesIntoPreviousParagraph(node, model.nodes[nodeIndex - 1])
    ) {
      continue;
    }
    if (node.type === "paragraph") {
      if (paragraphHasPageBreakBefore(node) && currentPageSegments.length > 0) {
        startNextPage();
        pageConsumedHeightPx = 0;
        previousParagraphAfterPx = 0;
        currentSectionPageFlowOriginPx = 0;
        currentPageContentHeightPx = resolveMetricsPageContentHeightPx(
          currentPageIndex,
          nodeMetrics
        );
      }

      if (
        preferLastRenderedParagraphStartBreaks &&
        paragraphStartsWithLastRenderedPageBreak(node) &&
        (strictLastRenderedParagraphStartBreaks ||
          shouldHonorParagraphStartLastRenderedPageBreak({
            pageConsumedHeightPx,
            pageContentHeightPx: currentPageContentHeightPx,
          })) &&
        !nodeAlreadyEndsAtExplicitPageBoundary(model.nodes[nodeIndex - 1]) &&
        currentPageSegments.length > 0
      ) {
        startNextPage();
        pageConsumedHeightPx = 0;
        previousParagraphAfterPx = 0;
        currentSectionPageFlowOriginPx = 0;
        currentPageContentHeightPx = resolveMetricsPageContentHeightPx(
          currentPageIndex,
          nodeMetrics
        );
      }

      const directBeforeSpacingPx = paragraphBeforeSpacingPx(node);
      const directAfterSpacingPx = paragraphAfterSpacingPx(node);
      const beforeSpacingPx = effectiveParagraphBeforeSpacingPx(
        model,
        nodeIndex,
        node,
        pageConsumedHeightPx,
        shouldSuppressSpacingAtPageBreaks
      );
      const afterSpacingPx = effectiveParagraphAfterSpacingPx(
        model,
        nodeIndex,
        node
      );
      const paragraphLineHeightPx = estimateParagraphLineHeightPx(
        node,
        nodeMetrics.docGridLinePitchPx
      );
      const paragraphTextWidthPx = paragraphAvailableTextWidthPx(
        node,
        nodeMetrics.pageContentWidthPx,
        numberingDefinitions
      );
      const paragraphPretextSourceForSegmentRendering =
        buildParagraphPretextLayoutSource(node, {
          allowExplicitLineBreakText: true,
          expandTabsForLayout: true,
        });
      let paragraphPretextLayoutForSegmentRendering:
        | PretextVariableWidthLayout
        | undefined;
      let paragraphPretextLayoutForSegmentRenderingResolved = false;
      const resolveParagraphPretextLayoutForSegmentRendering = ():
        | PretextVariableWidthLayout
        | undefined => {
        if (paragraphPretextLayoutForSegmentRenderingResolved) {
          return paragraphPretextLayoutForSegmentRendering;
        }

        paragraphPretextLayoutForSegmentRenderingResolved = true;
        paragraphPretextLayoutForSegmentRendering =
          paragraphPretextSourceForSegmentRendering
            ? layoutParagraphPretextSource(
                node,
                paragraphPretextSourceForSegmentRendering,
                paragraphTextWidthPx,
                paragraphLineHeightPx,
                []
              )
            : undefined;
        return paragraphPretextLayoutForSegmentRendering;
      };
      const nodeNumberingLabel = paginationNumberingLabels.get(
        `p:${nodeIndex}`
      );
      // Column-flow sections have no measured-page-height convergence, so
      // estimate drift (custom fonts, dense wrapping) accumulates unchecked;
      // prefer the actually rendered paragraph height there. Single-column
      // pagination keeps its tuned estimate + reconciliation behavior.
      const sectionUsesColumnFlow =
        (nodeMetrics.pageContentHeightMultiplier ?? 1) > 1;
      const measuredOuterHeightPx = sectionUsesColumnFlow
        ? options?.measuredParagraphOuterHeightsPxByNodeIndex?.get(nodeIndex)
        : undefined;
      const estimatedOrMeasuredHeightPx =
        Number.isFinite(measuredOuterHeightPx) &&
        (measuredOuterHeightPx as number) > 0
          ? (measuredOuterHeightPx as number)
          : estimateParagraphHeightPx(
              node,
              nodeMetrics.pageContentWidthPx,
              numberingDefinitions,
              nodeMetrics.docGridLinePitchPx,
              false,
              nodeNumberingLabel
            );
      let rawNodeHeightPx = Math.max(
        1,
        estimatedOrMeasuredHeightPx -
          directBeforeSpacingPx -
          directAfterSpacingPx +
          beforeSpacingPx +
          afterSpacingPx
      );
      const paragraphTooTallForSinglePage =
        rawNodeHeightPx >
        nodeMetrics.pageContentHeightPx + PAGE_OVERFLOW_TOLERANCE_PX;
      const keepLinesOverflowSplit =
        node.style?.keepLines === true && paragraphTooTallForSinglePage;
      const keepNextOverflowSplit =
        node.style?.keepNext === true && paragraphTooTallForSinglePage;
      const forceOverflowSplit =
        keepLinesOverflowSplit || keepNextOverflowSplit;
      const nodeIsWithinCommittedKeepNextChain =
        nodeIndex <= committedKeepNextChainEndNodeIndex;
      if (
        forceOverflowSplit &&
        !nodeIsWithinCommittedKeepNextChain &&
        pageConsumedHeightPx > 0 &&
        currentPageSegments.length > 0
      ) {
        // ECMA-376 §2.3.1.14/§2.3.1.15: if "keep" constraints cannot fit on one
        // page, start at a new page and continue with page breaks as needed.
        startNextPage();
        pageConsumedHeightPx = 0;
        previousParagraphAfterPx = 0;
        currentSectionPageFlowOriginPx = 0;
        currentPageContentHeightPx = resolveMetricsPageContentHeightPx(
          currentPageIndex,
          nodeMetrics
        );
      }

      const collapsedMarginPx =
        pageConsumedHeightPx > 0
          ? Math.min(previousParagraphAfterPx, beforeSpacingPx)
          : 0;
      const collapsedNodeHeightPx = Math.max(
        1,
        rawNodeHeightPx - collapsedMarginPx
      );
      const paragraphSupportsPretextSegmentRendering = Boolean(
        paragraphPretextSourceForSegmentRendering
      );
      const remainingHeightBeforeParagraphPx = Math.max(
        0,
        currentPageContentHeightPx - pageConsumedHeightPx
      );
      if (
        paragraphSupportsPretextSegmentRendering &&
        remainingHeightBeforeParagraphPx <=
          collapsedNodeHeightPx +
            paragraphLineHeightPx +
            PAGE_OVERFLOW_TOLERANCE_PX
      ) {
        const pretextLayout =
          resolveParagraphPretextLayoutForSegmentRendering();
        const pretextContentHeightPx = pretextLayout
          ? wrappedPretextParagraphBlockHeightPx(pretextLayout)
          : undefined;
        if (Number.isFinite(pretextContentHeightPx)) {
          rawNodeHeightPx = Math.max(
            rawNodeHeightPx,
            Math.max(
              1,
              Math.round(
                beforeSpacingPx +
                  (pretextContentHeightPx as number) +
                  afterSpacingPx
              )
            )
          );
        }
      }
      const collapsedNodeHeightPxAdjusted = Math.max(
        1,
        rawNodeHeightPx - collapsedMarginPx
      );
      const paragraphPretextLineCount =
        paragraphContainsExplicitLineBreakText(node) ||
        paragraphContainsTabCharacter(node)
          ? resolveParagraphPretextLayoutForSegmentRendering()?.lineCount
          : undefined;
      const supportsImageParagraphLineSplit =
        paragraphHasImage(node) && paragraphSupportsPretextSegmentRendering;
      const paragraphLineCount = paragraphLineCountWithinWidth(
        node,
        nodeMetrics.pageContentWidthPx,
        numberingDefinitions,
        nodeNumberingLabel
      );
      const resolvedParagraphLineCount =
        Number.isFinite(paragraphPretextLineCount) &&
        (paragraphPretextLineCount as number) > 0
          ? Math.max(1, Math.round(paragraphPretextLineCount as number))
          : paragraphLineCount;
      const explicitColumnBreakParagraphSegments =
        (nodeMetrics.pageContentHeightMultiplier ?? 1) > 1
          ? splitParagraphAtExplicitColumnBreaks(node)
          : undefined;
      const tryConsumeExplicitColumnBreakParagraph = (): boolean => {
        if (
          !explicitColumnBreakParagraphSegments ||
          explicitColumnBreakParagraphSegments.length <= 1
        ) {
          return false;
        }

        const projectedConsumedHeightPx =
          projectParagraphConsumedHeightWithExplicitColumnBreaks(
            explicitColumnBreakParagraphSegments,
            pageConsumedHeightPx,
            currentPageContentHeightPx,
            currentSectionPageFlowOriginPx,
            Math.max(
              1,
              Math.round(nodeMetrics.pageContentHeightMultiplier ?? 1)
            ),
            nodeMetrics.pageContentWidthPx,
            beforeSpacingPx,
            afterSpacingPx,
            collapsedMarginPx,
            numberingDefinitions,
            nodeMetrics.docGridLinePitchPx
          );
        if (!Number.isFinite(projectedConsumedHeightPx)) {
          return false;
        }

        currentPageSegments.push({ nodeIndex });
        pageConsumedHeightPx = Math.max(
          pageConsumedHeightPx,
          Math.round(projectedConsumedHeightPx as number)
        );
        previousParagraphAfterPx = afterSpacingPx;
        return true;
      };
      if (tryConsumeExplicitColumnBreakParagraph()) {
        continue;
      }
      if (
        explicitColumnBreakParagraphSegments &&
        explicitColumnBreakParagraphSegments.length > 1 &&
        pageConsumedHeightPx > 0 &&
        currentPageSegments.length > 0
      ) {
        startNextPage();
        pageConsumedHeightPx = 0;
        previousParagraphAfterPx = 0;
        currentSectionPageFlowOriginPx = 0;
        currentPageContentHeightPx = resolveMetricsPageContentHeightPx(
          currentPageIndex,
          nodeMetrics
        );
        if (tryConsumeExplicitColumnBreakParagraph()) {
          continue;
        }
      }
      const widowControlEnabled = paragraphWidowControlEnabled(node);
      const minLinesPerSegment = widowControlEnabled ? 2 : 1;
      const canSplitParagraphAcrossPages =
        paragraphCanSplitAcrossPages(node, resolvedParagraphLineCount, {
          allowKeepLinesOverflow: keepLinesOverflowSplit,
          allowKeepNextOverflow: keepNextOverflowSplit,
          allowImageParagraphSplit: supportsImageParagraphLineSplit,
        }) &&
        (!widowControlEnabled || resolvedParagraphLineCount > 3);

      if (canSplitParagraphAcrossPages && allowParagraphLineSplitting) {
        const pretextLayoutForSegmentSplitting =
          resolveParagraphPretextLayoutForSegmentRendering();
        const resolveSegmentReservePx = (
          startLineIndex: number,
          endLineIndex: number
        ): number => {
          const paragraphSegmentRange: ParagraphLineRange = {
            startLineIndex,
            endLineIndex,
            totalLineCount: resolvedParagraphLineCount,
            lineHeightPx: paragraphLineHeightPx,
          };
          return paragraphSupportsPretextSegmentRendering
            ? resolveParagraphSegmentNonFlowReservePx(paragraphSegmentRange)
            : resolveFallbackParagraphSegmentNonFlowReservePx(
                node,
                paragraphSegmentRange
              );
        };
        const resolveSegmentContentHeightPx = (
          startLineIndex: number,
          endLineIndex: number
        ): number => {
          if (
            pretextLayoutForSegmentSplitting &&
            pretextLayoutForSegmentSplitting.lineCount > 0
          ) {
            return resolvePretextLineRangeContentHeightPx(
              pretextLayoutForSegmentSplitting,
              startLineIndex,
              endLineIndex
            );
          }

          return (
            Math.max(1, endLineIndex - startLineIndex) * paragraphLineHeightPx
          );
        };
        let lineCursor = 0;
        let isFirstSegment = true;
        while (lineCursor < resolvedParagraphLineCount) {
          const linesRemaining = resolvedParagraphLineCount - lineCursor;
          const topSpacingPx = isFirstSegment
            ? pageConsumedHeightPx > 0
              ? Math.max(0, beforeSpacingPx - collapsedMarginPx)
              : beforeSpacingPx
            : 0;
          const mustKeepBottomSpacing = linesRemaining <= minLinesPerSegment;
          const bottomSpacingPx = mustKeepBottomSpacing ? afterSpacingPx : 0;
          const remainingHeightPx = Math.max(
            0,
            currentPageContentHeightPx - pageConsumedHeightPx
          );
          const allRemainingSegmentReservePx = resolveSegmentReservePx(
            lineCursor,
            resolvedParagraphLineCount
          );
          const allRemainingHeightPx =
            topSpacingPx +
            resolveSegmentContentHeightPx(
              lineCursor,
              resolvedParagraphLineCount
            ) +
            bottomSpacingPx;

          if (
            allRemainingHeightPx + allRemainingSegmentReservePx <=
            remainingHeightPx
          ) {
            currentPageSegments.push({
              nodeIndex,
              paragraphLineRange: {
                startLineIndex: lineCursor,
                endLineIndex: resolvedParagraphLineCount,
                totalLineCount: resolvedParagraphLineCount,
                lineHeightPx: paragraphLineHeightPx,
              },
            });
            pageConsumedHeightPx += allRemainingHeightPx;
            previousParagraphAfterPx = afterSpacingPx;
            lineCursor = resolvedParagraphLineCount;
            break;
          }

          const maxLinesThisPage = Math.max(
            0,
            linesRemaining - minLinesPerSegment
          );
          const continuingSegmentReservePx = resolveSegmentReservePx(
            lineCursor,
            Math.min(resolvedParagraphLineCount, lineCursor + maxLinesThisPage)
          );
          const availableForLinesPx = Math.max(
            0,
            remainingHeightPx - topSpacingPx - continuingSegmentReservePx
          );
          let linesThatFit = Math.floor(
            availableForLinesPx / paragraphLineHeightPx
          );
          linesThatFit = Math.min(linesThatFit, maxLinesThisPage);
          if (
            pretextLayoutForSegmentSplitting &&
            pretextLayoutForSegmentSplitting.lineCount > 0 &&
            linesThatFit > 0
          ) {
            const exactSegmentEndLineIndex =
              resolveMaxPretextLineRangeEndIndexThatFits(
                pretextLayoutForSegmentSplitting,
                lineCursor,
                Math.min(resolvedParagraphLineCount, lineCursor + linesThatFit),
                availableForLinesPx
              );
            linesThatFit = Math.max(0, exactSegmentEndLineIndex - lineCursor);
          }

          if (linesThatFit < minLinesPerSegment) {
            if (currentPageSegments.length > 0) {
              startNextPage();
              pageConsumedHeightPx = 0;
              previousParagraphAfterPx = 0;
              currentSectionPageFlowOriginPx = 0;
              currentPageContentHeightPx = resolveMetricsPageContentHeightPx(
                currentPageIndex,
                nodeMetrics
              );
              continue;
            }

            const fallbackLines = Math.max(
              1,
              Math.floor(
                Math.max(1, availableForLinesPx) / paragraphLineHeightPx
              )
            );
            linesThatFit = Math.max(
              1,
              Math.min(
                maxLinesThisPage > 0 ? maxLinesThisPage : linesRemaining,
                fallbackLines
              )
            );
          }

          let segmentEndLineIndex = Math.min(
            resolvedParagraphLineCount,
            lineCursor + linesThatFit
          );
          while (linesThatFit > minLinesPerSegment) {
            const segmentReservePx = resolveSegmentReservePx(
              lineCursor,
              segmentEndLineIndex
            );
            if (
              topSpacingPx +
                resolveSegmentContentHeightPx(lineCursor, segmentEndLineIndex) +
                segmentReservePx <=
              remainingHeightPx
            ) {
              break;
            }
            linesThatFit -= 1;
            segmentEndLineIndex = Math.min(
              resolvedParagraphLineCount,
              lineCursor + linesThatFit
            );
          }
          const safeSegmentEndLineIndex = Math.min(
            resolvedParagraphLineCount,
            lineCursor + linesThatFit
          );
          currentPageSegments.push({
            nodeIndex,
            paragraphLineRange: {
              startLineIndex: lineCursor,
              endLineIndex: safeSegmentEndLineIndex,
              totalLineCount: resolvedParagraphLineCount,
              lineHeightPx: paragraphLineHeightPx,
            },
          });

          pageConsumedHeightPx +=
            topSpacingPx +
            resolveSegmentContentHeightPx(lineCursor, safeSegmentEndLineIndex);
          previousParagraphAfterPx = 0;
          lineCursor = safeSegmentEndLineIndex;
          isFirstSegment = false;

          if (lineCursor < resolvedParagraphLineCount) {
            startNextPage();
            pageConsumedHeightPx = 0;
            previousParagraphAfterPx = 0;
            currentSectionPageFlowOriginPx = 0;
            currentPageContentHeightPx = resolveMetricsPageContentHeightPx(
              currentPageIndex,
              nodeMetrics
            );
          }
        }
        continue;
      }

      let requiredHeightPx = collapsedNodeHeightPxAdjusted;
      let keepNextChainEndNodeIndex = -1;
      if (
        node.style?.keepNext === true &&
        !nodeIsWithinCommittedKeepNextChain &&
        paragraphHasVisibleText(node)
      ) {
        let chainCursor = nodeIndex;
        let chainPreviousParagraphAfterPx = afterSpacingPx;
        while (chainCursor < model.nodes.length - 1) {
          const currentChainNode = model.nodes[chainCursor];
          if (
            currentChainNode.type !== "paragraph" ||
            currentChainNode.style?.keepNext !== true ||
            !paragraphHasVisibleText(currentChainNode)
          ) {
            break;
          }
          if (hardBreakStartNodeIndexes.has(chainCursor + 1)) {
            break;
          }
          const nextChainNode = model.nodes[chainCursor + 1];
          if (nextChainNode.type !== "paragraph") {
            break;
          }

          chainCursor += 1;
          const chainMetricsIndex =
            resolvePaginationSectionMetricsIndexForNodeIndex(
              metricsBySection,
              chainCursor,
              currentMetricsIndex
            );
          const chainMetrics =
            metricsBySection[chainMetricsIndex] ?? fallbackMetrics;
          const nextDirectBeforeSpacingPx =
            paragraphBeforeSpacingPx(nextChainNode);
          const nextDirectAfterSpacingPx =
            paragraphAfterSpacingPx(nextChainNode);
          const nextBeforeSpacingPx = effectiveParagraphBeforeSpacingPx(
            model,
            chainCursor,
            nextChainNode,
            1,
            shouldSuppressSpacingAtPageBreaks
          );
          const nextAfterSpacingPx = effectiveParagraphAfterSpacingPx(
            model,
            chainCursor,
            nextChainNode
          );
          const nextRawHeightPx = Math.max(
            1,
            estimateParagraphHeightPx(
              nextChainNode,
              chainMetrics.pageContentWidthPx,
              numberingDefinitions,
              chainMetrics.docGridLinePitchPx
            ) -
              nextDirectBeforeSpacingPx -
              nextDirectAfterSpacingPx +
              nextBeforeSpacingPx +
              nextAfterSpacingPx
          );
          const collapsedChainMarginPx = Math.min(
            chainPreviousParagraphAfterPx,
            nextBeforeSpacingPx
          );
          requiredHeightPx += Math.max(
            1,
            nextRawHeightPx - collapsedChainMarginPx
          );
          requiredHeightPx += keepNextPaginationReservePx(
            currentChainNode,
            nextChainNode,
            chainMetrics.docGridLinePitchPx
          );
          chainPreviousParagraphAfterPx = nextAfterSpacingPx;
        }
        keepNextChainEndNodeIndex = chainCursor;
      }

      const remainingHeightPx =
        currentPageContentHeightPx - pageConsumedHeightPx;
      const canKeepTrailingSectionTailOnCurrentPage =
        shouldKeepTrailingSectionTailOnCurrentPage(
          model,
          nodeIndex,
          pageConsumedHeightPx,
          previousParagraphAfterPx,
          nodeMetrics.pageContentWidthPx,
          currentPageContentHeightPx,
          hardBreakStartNodeIndexes,
          sectionStartPageBreakNodeIndexes,
          nextHardBreakStartNodeIndexByNodeIndex,
          (_candidateNodeIndex, candidateNode, candidatePageContentWidthPx) =>
            estimateDocNodeHeightPx(
              candidateNode,
              candidatePageContentWidthPx,
              numberingDefinitions,
              nodeMetrics.docGridLinePitchPx
            ),
          (candidateNodeIndex, candidateParagraph, consumedHeightPx) =>
            effectiveParagraphBeforeSpacingPx(
              model,
              candidateNodeIndex,
              candidateParagraph,
              consumedHeightPx,
              shouldSuppressSpacingAtPageBreaks
            ),
          (candidateNodeIndex, candidateParagraph) =>
            effectiveParagraphAfterSpacingPx(
              model,
              candidateNodeIndex,
              candidateParagraph
            )
        );
      if (
        pageConsumedHeightPx > 0 &&
        requiredHeightPx > remainingHeightPx + PAGE_OVERFLOW_TOLERANCE_PX &&
        !canKeepTrailingSectionTailOnCurrentPage
      ) {
        startNextPage();
        pageConsumedHeightPx = 0;
        previousParagraphAfterPx = 0;
        currentSectionPageFlowOriginPx = 0;
        currentPageContentHeightPx = resolveMetricsPageContentHeightPx(
          currentPageIndex,
          nodeMetrics
        );
      }

      if (pageConsumedHeightPx === 0 && keepNextChainEndNodeIndex > nodeIndex) {
        committedKeepNextChainEndNodeIndex = keepNextChainEndNodeIndex;
      }
      currentPageSegments.push({ nodeIndex });
      const effectiveNodeHeightPx =
        pageConsumedHeightPx > 0 ? collapsedNodeHeightPx : rawNodeHeightPx;
      pageConsumedHeightPx += effectiveNodeHeightPx;
      previousParagraphAfterPx = afterSpacingPx;
      continue;
    }

    const measuredRowHeightsPxRaw =
      options?.measuredTableRowHeightsByNodeIndex?.[nodeIndex];
    const measuredRowHeightsPx =
      measuredRowHeightsPxRaw &&
      measuredRowHeightsPxRaw.length === node.rows.length
        ? measuredRowHeightsPxRaw.map((heightPx) =>
            Math.max(
              MIN_PARAGRAPH_LINE_HEIGHT_PX,
              Number.isFinite(heightPx)
                ? Math.round(heightPx as number)
                : MIN_PARAGRAPH_LINE_HEIGHT_PX
            )
          )
        : undefined;
    const estimatedRowHeightsPx =
      measuredRowHeightsPx ??
      estimatedRowHeightsByTableNodeIndex.get(nodeIndex) ??
      estimateTableRowHeightsPx(
        node,
        nodeMetrics.pageContentWidthPx,
        numberingDefinitions,
        nodeMetrics.docGridLinePitchPx,
        nodeMetrics.pageContentHeightPx
      );
    if (
      !measuredRowHeightsPx &&
      !estimatedRowHeightsByTableNodeIndex.has(nodeIndex)
    ) {
      estimatedRowHeightsByTableNodeIndex.set(nodeIndex, estimatedRowHeightsPx);
    }

    if (estimatedRowHeightsPx.length === 0) {
      currentPageSegments.push({ nodeIndex });
      previousParagraphAfterPx = 0;
      continue;
    }

    const tableExplicitPageBreakInfo = collectTableExplicitPageBreakInfo(node);
    const tableBreakStartRows = tableExplicitPageBreakInfo.startRowIndexes;
    if (tableBreakStartRows.includes(0) && currentPageSegments.length > 0) {
      startNextPage();
      pageConsumedHeightPx = 0;
      previousParagraphAfterPx = 0;
      currentSectionPageFlowOriginPx = 0;
      currentPageContentHeightPx = resolveMetricsPageContentHeightPx(
        currentPageIndex,
        nodeMetrics
      );
    }

    let headerRunLength = 0;
    for (const row of node.rows) {
      if (row.style?.isHeader === true) {
        headerRunLength += 1;
      } else {
        break;
      }
    }
    const headerRunHeightPx =
      headerRunLength > 0
        ? sumEstimatedTableRowHeightsPx(
            estimatedRowHeightsPx,
            0,
            headerRunLength
          )
        : 0;
    const canRepeatHeader =
      headerRunLength > 0 &&
      headerRunHeightPx > 0 &&
      headerRunHeightPx + MIN_PARAGRAPH_LINE_HEIGHT_PX * 2 <
        currentPageContentHeightPx;

    let rowStartIndex = 0;
    let rowSliceOffsetPx = 0;
    let repeatedHeaderHeightPxOnThisPage = 0;
    let tableBreakStartRowCursor = 0;
    while (rowStartIndex < estimatedRowHeightsPx.length) {
      if (currentPageSegments.length === 0) {
        repeatedHeaderHeightPxOnThisPage = 0;
      }

      if (
        canRepeatHeader &&
        rowStartIndex >= headerRunLength &&
        currentPageSegments.length === 0
      ) {
        currentPageSegments.push({
          nodeIndex,
          tableRowRange: {
            startRowIndex: 0,
            endRowIndex: headerRunLength,
          },
        });
        pageConsumedHeightPx += headerRunHeightPx;
        previousParagraphAfterPx = 0;
        repeatedHeaderHeightPxOnThisPage = headerRunHeightPx;
      }

      const remainingHeightPx = Math.max(
        0,
        currentPageContentHeightPx - pageConsumedHeightPx
      );
      const currentRow = node.rows[rowStartIndex];
      const currentRowTotalHeightPx = Math.max(
        MIN_PARAGRAPH_LINE_HEIGHT_PX,
        estimatedRowHeightsPx[rowStartIndex] ?? MIN_PARAGRAPH_LINE_HEIGHT_PX
      );
      const currentRowRemainingHeightPx = Math.max(
        0,
        currentRowTotalHeightPx - rowSliceOffsetPx
      );
      const freshPageAvailableHeightPx = Math.max(
        MIN_PARAGRAPH_LINE_HEIGHT_PX,
        currentPageContentHeightPx - repeatedHeaderHeightPxOnThisPage
      );
      const rowExceedsFreshPageHeightPx =
        currentRowTotalHeightPx >
        freshPageAvailableHeightPx + PAGE_OVERFLOW_TOLERANCE_PX;
      const hasUsableCurrentPageSliceSpace =
        pageConsumedHeightPx > 0 &&
        remainingHeightPx >= MIN_TABLE_ROW_SLICE_REMAINING_HEIGHT_PX;
      const canSplitCurrentRow =
        rowSliceOffsetPx > 0 ||
        rowAllowsPageSplit(currentRow) ||
        rowExceedsFreshPageHeightPx;
      const shouldContinueExistingRowSlice = rowSliceOffsetPx > 0;
      const rowNeedsSliceOnThisPage =
        ENABLE_TABLE_ROW_SLICING &&
        canSplitCurrentRow &&
        currentRowRemainingHeightPx >
          remainingHeightPx + PAGE_OVERFLOW_TOLERANCE_PX &&
        (shouldContinueExistingRowSlice ||
          rowExceedsFreshPageHeightPx ||
          hasUsableCurrentPageSliceSpace);

      if (
        canSplitCurrentRow &&
        currentRowRemainingHeightPx >
          remainingHeightPx + PAGE_OVERFLOW_TOLERANCE_PX &&
        !rowNeedsSliceOnThisPage &&
        currentPageSegments.length > 0
      ) {
        startNextPage();
        pageConsumedHeightPx = 0;
        previousParagraphAfterPx = 0;
        currentSectionPageFlowOriginPx = 0;
        currentPageContentHeightPx = resolveMetricsPageContentHeightPx(
          currentPageIndex,
          nodeMetrics
        );
        continue;
      }

      if (rowNeedsSliceOnThisPage) {
        if (
          currentPageSegments.length > 0 &&
          remainingHeightPx <= MIN_PARAGRAPH_LINE_HEIGHT_PX
        ) {
          startNextPage();
          pageConsumedHeightPx = 0;
          previousParagraphAfterPx = 0;
          currentSectionPageFlowOriginPx = 0;
          currentPageContentHeightPx = resolveMetricsPageContentHeightPx(
            currentPageIndex,
            nodeMetrics
          );
          continue;
        }

        const availableSliceHeightPx =
          pageConsumedHeightPx <= 0
            ? currentPageContentHeightPx
            : Math.max(
                MIN_PARAGRAPH_LINE_HEIGHT_PX,
                Math.round(remainingHeightPx)
              );
        const preferredSliceHeightPx = Math.max(
          MIN_PARAGRAPH_LINE_HEIGHT_PX,
          Math.min(currentRowRemainingHeightPx, availableSliceHeightPx)
        );
        const safeSliceHeightPx = resolveTableRowSliceHeightOnSafeBoundaryPx({
          table: node,
          rowIndex: rowStartIndex,
          rowHeightPx: currentRowTotalHeightPx,
          rowSliceOffsetPx,
          preferredSliceHeightPx,
          maxAvailableWidthPx: nodeMetrics.pageContentWidthPx,
          numberingDefinitions,
          docGridLinePitchPx: nodeMetrics.docGridLinePitchPx,
        });
        if (
          safeSliceHeightPx === undefined &&
          pageConsumedHeightPx > 0 &&
          currentPageSegments.length > 0
        ) {
          startNextPage();
          pageConsumedHeightPx = 0;
          previousParagraphAfterPx = 0;
          currentSectionPageFlowOriginPx = 0;
          currentPageContentHeightPx = resolveMetricsPageContentHeightPx(
            currentPageIndex,
            nodeMetrics
          );
          continue;
        }

        const sliceHeightPx = Math.max(
          MIN_PARAGRAPH_LINE_HEIGHT_PX,
          Math.min(
            currentRowRemainingHeightPx,
            safeSliceHeightPx ?? preferredSliceHeightPx
          )
        );
        currentPageSegments.push({
          nodeIndex,
          tableRowRange: {
            startRowIndex: rowStartIndex,
            endRowIndex: Math.min(
              estimatedRowHeightsPx.length,
              rowStartIndex + 1
            ),
          },
          tableRowSlice: {
            rowIndex: rowStartIndex,
            startOffsetPx: rowSliceOffsetPx,
            sliceHeightPx,
            totalRowHeightPx: currentRowTotalHeightPx,
          },
        });
        pageConsumedHeightPx += sliceHeightPx;
        previousParagraphAfterPx = 0;
        rowSliceOffsetPx += sliceHeightPx;
        if (
          rowSliceOffsetPx >=
          currentRowTotalHeightPx - PAGE_OVERFLOW_TOLERANCE_PX
        ) {
          rowStartIndex += 1;
          rowSliceOffsetPx = 0;
        }

        if (rowStartIndex < estimatedRowHeightsPx.length) {
          startNextPage();
          pageConsumedHeightPx = 0;
          previousParagraphAfterPx = 0;
          currentSectionPageFlowOriginPx = 0;
          currentPageContentHeightPx = resolveMetricsPageContentHeightPx(
            currentPageIndex,
            nodeMetrics
          );
        }
        continue;
      }

      if (rowSliceOffsetPx > 0) {
        currentPageSegments.push({
          nodeIndex,
          tableRowRange: {
            startRowIndex: rowStartIndex,
            endRowIndex: Math.min(
              estimatedRowHeightsPx.length,
              rowStartIndex + 1
            ),
          },
          tableRowSlice: {
            rowIndex: rowStartIndex,
            startOffsetPx: rowSliceOffsetPx,
            sliceHeightPx: currentRowRemainingHeightPx,
            totalRowHeightPx: currentRowTotalHeightPx,
          },
        });
        pageConsumedHeightPx += currentRowRemainingHeightPx;
        previousParagraphAfterPx = 0;
        rowStartIndex += 1;
        rowSliceOffsetPx = 0;
        continue;
      }

      const fittedRowEndIndex = fitTableRowsWithinHeightPx(
        estimatedRowHeightsPx,
        rowStartIndex,
        remainingHeightPx,
        pageConsumedHeightPx <= 0
      );
      let rowEndIndex = fittedRowEndIndex;
      while (
        tableBreakStartRowCursor < tableBreakStartRows.length &&
        tableBreakStartRows[tableBreakStartRowCursor] <= rowStartIndex
      ) {
        tableBreakStartRowCursor += 1;
      }
      const forcedBreakRowIndex = tableBreakStartRows[tableBreakStartRowCursor];
      if (forcedBreakRowIndex !== undefined) {
        rowEndIndex = Math.min(rowEndIndex, forcedBreakRowIndex);
      }

      if (rowEndIndex <= rowStartIndex) {
        if (currentPageSegments.length > 0) {
          startNextPage();
          pageConsumedHeightPx = 0;
          previousParagraphAfterPx = 0;
          currentSectionPageFlowOriginPx = 0;
          currentPageContentHeightPx = resolveMetricsPageContentHeightPx(
            currentPageIndex,
            nodeMetrics
          );
          continue;
        }

        const forcedEndIndex = Math.min(
          estimatedRowHeightsPx.length,
          rowStartIndex + 1
        );
        const forcedHeightPx = sumEstimatedTableRowHeightsPx(
          estimatedRowHeightsPx,
          rowStartIndex,
          forcedEndIndex
        );
        currentPageSegments.push({
          nodeIndex,
          tableRowRange: {
            startRowIndex: rowStartIndex,
            endRowIndex: forcedEndIndex,
          },
        });
        pageConsumedHeightPx += forcedHeightPx;
        previousParagraphAfterPx = 0;
        rowStartIndex = forcedEndIndex;
        rowSliceOffsetPx = 0;

        if (rowStartIndex < estimatedRowHeightsPx.length) {
          startNextPage();
          pageConsumedHeightPx = 0;
          previousParagraphAfterPx = 0;
          currentSectionPageFlowOriginPx = 0;
          currentPageContentHeightPx = resolveMetricsPageContentHeightPx(
            currentPageIndex,
            nodeMetrics
          );
        }
        continue;
      }

      const segmentHeightPx = sumEstimatedTableRowHeightsPx(
        estimatedRowHeightsPx,
        rowStartIndex,
        rowEndIndex
      );
      const coversWholeTable =
        rowStartIndex === 0 && rowEndIndex >= estimatedRowHeightsPx.length;
      currentPageSegments.push({
        nodeIndex,
        tableRowRange: coversWholeTable
          ? undefined
          : {
              startRowIndex: rowStartIndex,
              endRowIndex: rowEndIndex,
            },
      });
      pageConsumedHeightPx += segmentHeightPx;
      previousParagraphAfterPx = 0;
      rowStartIndex = rowEndIndex;
      rowSliceOffsetPx = 0;

      if (rowStartIndex < estimatedRowHeightsPx.length) {
        const nextRow = node.rows[rowStartIndex];
        const nextRowHeightPx = Math.max(
          MIN_PARAGRAPH_LINE_HEIGHT_PX,
          estimatedRowHeightsPx[rowStartIndex] ?? MIN_PARAGRAPH_LINE_HEIGHT_PX
        );
        const remainingHeightAfterSegmentPx = Math.max(
          0,
          currentPageContentHeightPx - pageConsumedHeightPx
        );
        const canSliceNextRowOnCurrentPage =
          ENABLE_TABLE_ROW_SLICING &&
          nextRow !== undefined &&
          rowAllowsPageSplit(nextRow) &&
          !tableBreakStartRows.includes(rowStartIndex) &&
          remainingHeightAfterSegmentPx >=
            MIN_TABLE_ROW_SLICE_REMAINING_HEIGHT_PX &&
          nextRowHeightPx >
            remainingHeightAfterSegmentPx + PAGE_OVERFLOW_TOLERANCE_PX;
        if (canSliceNextRowOnCurrentPage) {
          continue;
        }

        startNextPage();
        pageConsumedHeightPx = 0;
        previousParagraphAfterPx = 0;
        currentSectionPageFlowOriginPx = 0;
        currentPageContentHeightPx = resolveMetricsPageContentHeightPx(
          currentPageIndex,
          nodeMetrics
        );
      }
    }
  }

  if (currentPageSegments.length > 0 || pages.length === 0) {
    pages.push(currentPageSegments);
  }

  return pages;
}

function mergeTrailingPagesToTargetCount(
  pages: DocumentPageNodeSegment[][],
  targetPageCount: number
): DocumentPageNodeSegment[][] {
  const safeTargetPageCount = Math.max(1, Math.round(targetPageCount));
  if (pages.length <= safeTargetPageCount) {
    return pages;
  }

  const merged = pages.map((pageSegments) => [...pageSegments]);
  while (merged.length > safeTargetPageCount) {
    const trailingPage = merged.pop();
    if (!trailingPage || merged.length === 0) {
      break;
    }

    merged[merged.length - 1] = [...merged[merged.length - 1], ...trailingPage];
  }

  return merged;
}

function shouldRenderWrappedFloatingImage(image: ImageRunNode): boolean {
  const floating = image.floating;
  if (!floating) {
    return false;
  }

  if (image.syntheticTextBox && !syntheticTextBoxContainsPictureLayer(image)) {
    return false;
  }

  const wrapType = floating.wrapType;
  if (wrapType === undefined || wrapType === "none") {
    return false;
  }

  if (shouldRenderTopAnchoredMarginFloatAsAbsolute(image)) {
    return false;
  }

  if (!floating.behindDocument) {
    return true;
  }

  const horizontalRelativeTo = floating.horizontalRelativeTo?.toLowerCase();
  const verticalRelativeTo = floating.verticalRelativeTo?.toLowerCase();
  const hasAnchoredAlignment = Boolean(
    floating.horizontalAlign || floating.verticalAlign || floating.wrapText
  );

  // Some DOCX exports mark anchored wrapped images as behindDoc even when Word
  // still lays them out like wrapped content. Keep those in flow unless they
  // are explicitly page-anchored overlays.
  return (
    hasAnchoredAlignment &&
    horizontalRelativeTo !== "page" &&
    verticalRelativeTo !== "page"
  );
}

function isFixedPositionWrappedFloatingImage(image: ImageRunNode): boolean {
  return (
    shouldRenderWrappedFloatingImage(image) &&
    !floatingImageMovesWithText(image.floating)
  );
}

function shouldRenderTopAnchoredMarginFloatAsAbsolute(
  image: ImageRunNode
): boolean {
  const floating = image.floating;
  if (!floating) {
    return false;
  }

  if (
    !floating.wrapType ||
    floating.wrapType === "none" ||
    floating.behindDocument
  ) {
    return false;
  }

  const horizontalRelativeTo = floating.horizontalRelativeTo?.toLowerCase();
  const verticalRelativeTo = floating.verticalRelativeTo?.toLowerCase();
  const horizontalAlign = floating.horizontalAlign?.toLowerCase();
  const verticalAlign = floating.verticalAlign?.toLowerCase();

  const pageAnchoredHorizontally =
    horizontalRelativeTo === "margin" || horizontalRelativeTo === "page";
  const pageAnchoredVertically =
    verticalRelativeTo === "margin" || verticalRelativeTo === "page";
  const sideAligned =
    horizontalAlign === "left" ||
    horizontalAlign === "right" ||
    horizontalAlign === "inside" ||
    horizontalAlign === "outside";
  const topAligned = verticalAlign === "top" || verticalAlign === "inside";

  // Anchor it absolutely only when an explicit side/top ALIGNMENT is present.
  // Such corner-anchored floats stay fixed on the page. Dragging one keeps its
  // alignment and adds a posOffset (xPx/yPx) for the new position — the
  // absolute render honors that offset, so it lands exactly at the drop point.
  // Offset-only floats (no alignment) keep flowing as wrapped content so text
  // still wraps around them; those are handled by the wrapped layout path.
  return (
    pageAnchoredHorizontally &&
    pageAnchoredVertically &&
    sideAligned &&
    topAligned
  );
}

function resolveWrappedFloatingSide(
  image: ImageRunNode,
  options?: {
    containerWidthPx?: number;
    imageWidthPx?: number;
  }
): "left" | "right" {
  const floating = image.floating;
  const wrapText = floating?.wrapText;
  if (wrapText === "left") {
    return "right";
  }
  if (wrapText === "right") {
    return "left";
  }

  const horizontalAlign = floating?.horizontalAlign?.toLowerCase();
  if (horizontalAlign === "right" || horizontalAlign === "outside") {
    return "right";
  }
  if (horizontalAlign === "left" || horizontalAlign === "inside") {
    return "left";
  }

  const containerWidthPx =
    Number.isFinite(options?.containerWidthPx) &&
    (options?.containerWidthPx as number) > 0
      ? Math.max(1, Math.round(options?.containerWidthPx as number))
      : undefined;
  const imageWidthPx =
    Number.isFinite(options?.imageWidthPx) &&
    (options?.imageWidthPx as number) > 0
      ? Math.max(1, Math.round(options?.imageWidthPx as number))
      : undefined;
  if (
    Number.isFinite(containerWidthPx) &&
    Number.isFinite(imageWidthPx) &&
    Number.isFinite(floating?.xPx)
  ) {
    const centerX = (floating?.xPx as number) + (imageWidthPx as number) / 2;
    return centerX <= (containerWidthPx as number) / 2 ? "left" : "right";
  }

  if ((floating?.xPx ?? 0) >= 0) {
    return (floating?.xPx ?? 0) > 96 ? "right" : "left";
  }

  return "left";
}

export function wrappedFloatingImageStyle(
  image: ImageRunNode,
  options?: {
    containerWidthPx?: number;
    deltaX?: number;
    deltaY?: number;
    allowNegativeOffsets?: boolean;
  }
): React.CSSProperties {
  const floating = image.floating;
  const wrapType = floating?.wrapType;
  if (!floating || !wrapType) {
    return {};
  }

  // Wrap distances should come from the DOCX anchor only; avoid synthetic gaps.
  const distL = floating.distLPx ?? 0;
  const distR = floating.distRPx ?? 0;
  const distT = floating.distTPx ?? 0;
  const distB = floating.distBPx ?? 0;
  const deltaX = Number.isFinite(options?.deltaX)
    ? Math.round(options?.deltaX as number)
    : 0;
  const deltaY = Number.isFinite(options?.deltaY)
    ? Math.round(options?.deltaY as number)
    : 0;
  const allowNegativeOffsets = options?.allowNegativeOffsets === true;
  const shiftedXPx = Number.isFinite(floating.xPx)
    ? Math.round((floating.xPx as number) + deltaX)
    : undefined;
  const shiftedYPx = Number.isFinite(floating.yPx)
    ? Math.round((floating.yPx as number) + deltaY)
    : undefined;
  const horizontalOffset = allowNegativeOffsets
    ? Math.round(shiftedXPx ?? 0)
    : Math.max(0, Math.round(shiftedXPx ?? 0));
  const verticalOffset = allowNegativeOffsets
    ? Math.round(shiftedYPx ?? 0)
    : Math.max(0, Math.round(shiftedYPx ?? 0));
  const horizontalAlign = floating.horizontalAlign?.toLowerCase();
  const hasExplicitHorizontalAlign =
    horizontalAlign === "left" ||
    horizontalAlign === "center" ||
    horizontalAlign === "right" ||
    horizontalAlign === "inside" ||
    horizontalAlign === "outside";
  const containerWidthPx = Number.isFinite(options?.containerWidthPx)
    ? Math.max(1, Math.round(options?.containerWidthPx as number))
    : undefined;
  const imageWidthPx = Number.isFinite(image.widthPx)
    ? Math.max(1, Math.round(image.widthPx as number))
    : undefined;
  const intrinsicBlockWidthStyle: Pick<React.CSSProperties, "width"> =
    imageWidthPx ? { width: imageWidthPx } : { width: "fit-content" };
  const rightOffsetPx =
    Number.isFinite(shiftedXPx) &&
    Number.isFinite(shiftedXPx) &&
    Number.isFinite(containerWidthPx) &&
    Number.isFinite(imageWidthPx)
      ? allowNegativeOffsets
        ? Math.round(
            (containerWidthPx as number) -
              (shiftedXPx as number) -
              (imageWidthPx as number)
          )
        : Math.max(
            0,
            Math.round(
              (containerWidthPx as number) -
                (shiftedXPx as number) -
                (imageWidthPx as number)
            )
          )
      : 0;
  const hasExplicitHorizontalOffset = Number.isFinite(shiftedXPx);
  const leftOffsetPx =
    hasExplicitHorizontalAlign && !hasExplicitHorizontalOffset
      ? 0
      : horizontalOffset;
  const topOffsetPx = distT + verticalOffset;

  if (wrapType === "topAndBottom") {
    if (horizontalAlign === "center") {
      return {
        display: "block",
        ...intrinsicBlockWidthStyle,
        marginTop: topOffsetPx,
        marginBottom: distB,
        marginLeft: "auto",
        marginRight: "auto",
        clear: "both",
      };
    }
    if (horizontalAlign === "right" || horizontalAlign === "outside") {
      return {
        display: "block",
        ...intrinsicBlockWidthStyle,
        marginTop: topOffsetPx,
        marginBottom: distB,
        marginLeft: "auto",
        marginRight: hasExplicitHorizontalOffset ? rightOffsetPx : distR,
        clear: "both",
      };
    }
    return {
      display: "block",
      ...intrinsicBlockWidthStyle,
      marginTop: topOffsetPx,
      marginBottom: distB,
      marginLeft: hasExplicitHorizontalOffset
        ? leftOffsetPx
        : distL + leftOffsetPx,
      marginRight: distR,
      clear: "both",
    };
  }

  const side = resolveWrappedFloatingSide(image, {
    containerWidthPx,
    imageWidthPx,
  });
  const horizontalRelativeTo = floating.horizontalRelativeTo?.toLowerCase();
  const explicitHorizontalInsetPx =
    hasExplicitHorizontalOffset &&
    !hasExplicitHorizontalAlign &&
    (horizontalRelativeTo === "margin" ||
      horizontalRelativeTo === "page" ||
      horizontalRelativeTo === "column")
      ? side === "left"
        ? Math.max(0, leftOffsetPx)
        : Math.max(0, rightOffsetPx)
      : 0;
  const columnAnchoredExplicitInset =
    explicitHorizontalInsetPx > 0 && horizontalRelativeTo === "column";
  return {
    display: "block",
    ...intrinsicBlockWidthStyle,
    float: side,
    marginTop: topOffsetPx,
    marginBottom: distB,
    marginLeft:
      side === "left"
        ? explicitHorizontalInsetPx > 0
          ? columnAnchoredExplicitInset
            ? distL + explicitHorizontalInsetPx
            : 0
          : hasExplicitHorizontalOffset
          ? leftOffsetPx
          : distL + leftOffsetPx
        : distL,
    marginRight:
      side === "right"
        ? explicitHorizontalInsetPx > 0
          ? columnAnchoredExplicitInset
            ? distR + explicitHorizontalInsetPx
            : 0
          : hasExplicitHorizontalOffset
          ? rightOffsetPx
          : distR + rightOffsetPx
        : distR,
    paddingLeft:
      side === "left" &&
      explicitHorizontalInsetPx > 0 &&
      !columnAnchoredExplicitInset
        ? explicitHorizontalInsetPx
        : undefined,
    paddingRight:
      side === "right" &&
      explicitHorizontalInsetPx > 0 &&
      !columnAnchoredExplicitInset
        ? explicitHorizontalInsetPx
        : undefined,
    boxSizing:
      explicitHorizontalInsetPx > 0 && !columnAnchoredExplicitInset
        ? "content-box"
        : undefined,
  };
}

interface WrappedFloatingDualExclusionLayout {
  leftSpacerStyle: React.CSSProperties;
  rightSpacerStyle: React.CSSProperties;
  imageStyle: React.CSSProperties;
}

function wrappedFloatingImageDualExclusionLayout(
  image: ImageRunNode,
  options?: {
    containerWidthPx?: number;
    deltaX?: number;
    deltaY?: number;
  }
): WrappedFloatingDualExclusionLayout | undefined {
  void image;
  void options;
  // Center-hole exclusion requires a real line layout engine. The CSS spacer approach
  // created incorrect middle-column wrapping and placement regressions across documents.
  // Keep the stable side-float path until a proper glyph-line layout implementation exists.
  return undefined;
}

export function absoluteFloatingImageStyle(
  image: ImageRunNode,
  options?: {
    pageOriginLeft?: number;
    pageOriginTop?: number;
    marginOriginLeft?: number;
    marginOriginTop?: number;
    columnOriginLeft?: number;
    columnOriginTop?: number;
    paragraphOriginLeft?: number;
    paragraphOriginTop?: number;
    deltaX?: number;
    deltaY?: number;
  }
): React.CSSProperties {
  const floating = image.floating;
  if (!floating) {
    return {};
  }

  const horizontalRelativeTo = floating.horizontalRelativeTo?.toLowerCase();
  const verticalRelativeTo = floating.verticalRelativeTo?.toLowerCase();
  const horizontalAlign = floating.horizontalAlign?.toLowerCase();
  const verticalAlign = floating.verticalAlign?.toLowerCase();
  const usesWrapDistance = Boolean(
    floating.wrapType && floating.wrapType !== "none"
  );
  const distL = usesWrapDistance ? floating.distLPx ?? 0 : 0;
  const distR = usesWrapDistance ? floating.distRPx ?? 0 : 0;
  const distT = usesWrapDistance ? floating.distTPx ?? 0 : 0;
  const distB = usesWrapDistance ? floating.distBPx ?? 0 : 0;
  const deltaX = Number.isFinite(options?.deltaX)
    ? Math.round(options?.deltaX as number)
    : 0;
  const deltaY = Number.isFinite(options?.deltaY)
    ? Math.round(options?.deltaY as number)
    : 0;
  const normalizedZIndex = Number.isFinite(floating.zIndex)
    ? Math.max(
        1,
        Math.min(65535, Math.round((floating.zIndex as number) / 65536))
      )
    : undefined;
  const resolvedZIndex = floating.behindDocument
    ? -100000 + (normalizedZIndex ?? 1)
    : normalizedZIndex ?? 4;

  const resolvedLeft =
    floating.xPx !== undefined
      ? horizontalRelativeTo === "page"
        ? floating.xPx + (options?.pageOriginLeft ?? 0)
        : horizontalRelativeTo === "margin"
        ? floating.xPx +
          (options?.marginOriginLeft ?? options?.pageOriginLeft ?? 0)
        : horizontalRelativeTo === "column"
        ? floating.xPx + (options?.columnOriginLeft ?? 0)
        : horizontalRelativeTo === "paragraph" ||
          horizontalRelativeTo === "line"
        ? floating.xPx + (options?.paragraphOriginLeft ?? 0)
        : floating.xPx
      : undefined;
  const resolvedTop =
    floating.yPx !== undefined
      ? verticalRelativeTo === "page"
        ? floating.yPx + (options?.pageOriginTop ?? 0)
        : verticalRelativeTo === "margin"
        ? floating.yPx +
          (options?.marginOriginTop ?? options?.pageOriginTop ?? 0)
        : verticalRelativeTo === "column"
        ? floating.yPx + (options?.columnOriginTop ?? 0)
        : verticalRelativeTo === "paragraph" || verticalRelativeTo === "line"
        ? floating.yPx + (options?.paragraphOriginTop ?? 0)
        : floating.yPx
      : undefined;

  const style: React.CSSProperties = {
    position: "absolute",
    zIndex: resolvedZIndex,
  };
  const transforms: string[] = [];

  if (resolvedLeft !== undefined) {
    style.left = resolvedLeft + deltaX;
  } else if (horizontalAlign === "right" || horizontalAlign === "outside") {
    style.right = distR - deltaX;
  } else if (horizontalAlign === "center") {
    style.left = "50%";
    transforms.push("translateX(-50%)");
  } else {
    style.left = distL + deltaX;
  }

  if (resolvedTop !== undefined) {
    style.top = resolvedTop + deltaY;
  } else if (verticalAlign === "bottom" || verticalAlign === "outside") {
    style.bottom = distB - deltaY;
  } else if (verticalAlign === "center") {
    style.top = "50%";
    transforms.push("translateY(-50%)");
  } else {
    style.top = distT + deltaY;
  }

  if (transforms.length > 0 || deltaX !== 0 || deltaY !== 0) {
    const applyDeltaTranslationX =
      resolvedLeft === undefined && horizontalAlign === "center";
    const applyDeltaTranslationY =
      resolvedTop === undefined && verticalAlign === "center";
    const translatePart =
      applyDeltaTranslationX || applyDeltaTranslationY
        ? `translate(${applyDeltaTranslationX ? deltaX : 0}px, ${
            applyDeltaTranslationY ? deltaY : 0
          }px)`
        : "";
    style.transform = [...transforms, translatePart].filter(Boolean).join(" ");
  }

  return style;
}

interface AbsoluteFloatingDropRect {
  left: number;
  top: number;
  width?: number;
  height?: number;
}

export function resolveAbsoluteFloatingImageDropPatch(
  floating: NonNullable<ImageRunNode["floating"]> | undefined,
  layout: Pick<
    DocumentLayoutMetrics,
    "marginsPx" | "pageWidthPx" | "pageHeightPx"
  >,
  options: {
    wrapperRect: AbsoluteFloatingDropRect;
    pageSurfaceRect?: AbsoluteFloatingDropRect;
    deltaX: number;
    deltaY: number;
  }
): Partial<NonNullable<ImageRunNode["floating"]>> {
  const nextLeftPx = options.wrapperRect.left + Math.round(options.deltaX);
  const nextTopPx = options.wrapperRect.top + Math.round(options.deltaY);
  const imageWidthPx = Number.isFinite(options.wrapperRect.width)
    ? Math.max(1, Math.round(options.wrapperRect.width as number))
    : undefined;
  const imageHeightPx = Number.isFinite(options.wrapperRect.height)
    ? Math.max(1, Math.round(options.wrapperRect.height as number))
    : undefined;

  // Preserve any existing side/top alignment so corner-anchored margin floats
  // keep rendering via the fixed-position (absolute) path after a drag instead
  // of flipping to in-flow wrapped rendering (which would re-clamp them to
  // their anchor paragraph and jump away from the drop point). The explicit
  // xPx/yPx below take precedence in the absolute renderer.
  const preservedHorizontalAlign = floating?.horizontalAlign;
  const preservedVerticalAlign = floating?.verticalAlign;

  if (!options.pageSurfaceRect) {
    return {
      xPx: Math.round((floating?.xPx ?? 0) + options.deltaX),
      yPx: Math.round((floating?.yPx ?? 0) + options.deltaY),
      horizontalAlign: preservedHorizontalAlign,
      verticalAlign: preservedVerticalAlign,
      horizontalRelativeTo: "margin",
      verticalRelativeTo: "margin",
    };
  }

  const minimumXPx = -Math.round(layout.marginsPx.left);
  const minimumYPx = -Math.round(layout.marginsPx.top);
  const maximumXPx = Number.isFinite(imageWidthPx)
    ? Math.round(
        layout.pageWidthPx - layout.marginsPx.left - (imageWidthPx as number)
      )
    : Math.round(layout.pageWidthPx - layout.marginsPx.left);
  const maximumYPx = Number.isFinite(imageHeightPx)
    ? Math.round(
        layout.pageHeightPx - layout.marginsPx.top - (imageHeightPx as number)
      )
    : Math.round(layout.pageHeightPx - layout.marginsPx.top);

  return {
    xPx: clampNumber(
      Math.round(
        nextLeftPx - options.pageSurfaceRect.left - layout.marginsPx.left
      ),
      minimumXPx,
      maximumXPx
    ),
    yPx: clampNumber(
      Math.round(
        nextTopPx - options.pageSurfaceRect.top - layout.marginsPx.top
      ),
      minimumYPx,
      maximumYPx
    ),
    horizontalAlign: preservedHorizontalAlign,
    verticalAlign: preservedVerticalAlign,
    horizontalRelativeTo: "margin",
    verticalRelativeTo: "margin",
  };
}

export function resolveWrappedFloatingImageDropPatch(
  image: ImageRunNode,
  hostWidth: number,
  movedLeft: number,
  movedTop: number,
  options?: {
    widthPx?: number;
    heightPx?: number;
  }
): Partial<NonNullable<ImageRunNode["floating"]>> {
  const baseFloating = image.floating ?? {};
  const imageWidth =
    options?.widthPx ?? image.widthPx ?? MIN_PARAGRAPH_LINE_HEIGHT_PX;
  const imageHeight = options?.heightPx ?? image.heightPx ?? imageWidth;
  const wrapText = baseFloating.wrapText ?? "bothSides";
  const wrapType = baseFloating.wrapType ?? "square";
  const convertFixedPositionToMoveWithText =
    !floatingImageMovesWithText(baseFloating);
  const preserveAlignedHorizontalPlacement =
    !convertFixedPositionToMoveWithText &&
    wrapType.trim().toLowerCase() === "topandbottom" &&
    Boolean(baseFloating.horizontalAlign);
  const side: "left" | "right" =
    movedLeft + imageWidth / 2 <= hostWidth / 2 ? "left" : "right";
  const previewGeometry = resolveDualWrappedFloatingImageGeometry(
    {
      ...image,
      widthPx: imageWidth,
      heightPx: imageHeight,
    },
    hostWidth,
    {
      widthPx: imageWidth,
      heightPx: imageHeight,
      baseLeftPx: movedLeft,
      baseTopPx: movedTop,
      allowNegativeImageTop: true,
    }
  );
  const previewUsesSideFloat =
    previewGeometry === undefined ||
    previewGeometry.exclusion.left <= 0 ||
    previewGeometry.exclusion.right >= hostWidth;
  const rawExplicitTopPx = Math.round(
    movedTop - Math.round(baseFloating.distTPx ?? 0)
  );
  const currentTopPx = Math.round(baseFloating.yPx ?? 0);
  const explicitTopPx =
    wrapType.trim().toLowerCase() === "topandbottom" &&
    Math.abs(rawExplicitTopPx - currentTopPx) <
      TOP_AND_BOTTOM_VERTICAL_DRAG_SNAP_PX
      ? currentTopPx
      : rawExplicitTopPx;

  return {
    wrapType,
    wrapText,
    horizontalAlign: preserveAlignedHorizontalPlacement
      ? baseFloating.horizontalAlign
      : previewUsesSideFloat
      ? side
      : wrapText === "bothSides" || wrapText === "largest"
      ? undefined
      : side,
    xPx: preserveAlignedHorizontalPlacement ? undefined : Math.round(movedLeft),
    yPx: explicitTopPx,
    distLPx: Math.max(4, Math.round(baseFloating.distLPx ?? 8)),
    distRPx: Math.max(4, Math.round(baseFloating.distRPx ?? 8)),
    distTPx: Math.max(0, Math.round(baseFloating.distTPx ?? 2)),
    distBPx: Math.max(0, Math.round(baseFloating.distBPx ?? 4)),
    horizontalRelativeTo: convertFixedPositionToMoveWithText
      ? "column"
      : baseFloating.horizontalRelativeTo ?? "column",
    verticalRelativeTo: convertFixedPositionToMoveWithText
      ? "paragraph"
      : baseFloating.verticalRelativeTo ?? "paragraph",
    behindDocument: false,
  };
}

function resolvePageSpanningAbsoluteFloatingDimensions(
  image: ImageRunNode,
  widthPx?: number,
  heightPx?: number,
  floatingPageOriginPx?: {
    left: number;
    top: number;
    marginLeft?: number;
    marginTop?: number;
    pageWidth?: number;
  }
): {
  widthPx?: number;
  heightPx?: number;
} {
  const floating = image.floating;
  const pageWidthPx = Number.isFinite(floatingPageOriginPx?.pageWidth)
    ? Math.max(1, Math.round(floatingPageOriginPx?.pageWidth as number))
    : undefined;
  if (
    !floating ||
    !shouldRenderAbsoluteFloatingImage(image) ||
    floating.behindDocument !== true ||
    (floating.wrapType !== undefined && floating.wrapType !== "none") ||
    !Number.isFinite(pageWidthPx)
  ) {
    return { widthPx, heightPx };
  }

  const horizontalRelativeTo = floating.horizontalRelativeTo?.toLowerCase();
  if (horizontalRelativeTo !== "page" && horizontalRelativeTo !== "margin") {
    return { widthPx, heightPx };
  }

  const currentWidthPx = Number.isFinite(widthPx)
    ? Math.max(1, Math.round(widthPx as number))
    : undefined;
  if (
    Number.isFinite(currentWidthPx) &&
    (currentWidthPx as number) < (pageWidthPx as number) * 0.55
  ) {
    return { widthPx, heightPx };
  }

  const resolvedLeftPx = Number.isFinite(floating.xPx)
    ? horizontalRelativeTo === "margin"
      ? Math.round(
          (floating.xPx as number) +
            (floatingPageOriginPx?.marginLeft ??
              floatingPageOriginPx?.left ??
              0)
        )
      : Math.round(floating.xPx as number)
    : horizontalRelativeTo === "margin"
    ? Math.round(
        floatingPageOriginPx?.marginLeft ?? floatingPageOriginPx?.left ?? 0
      )
    : 0;
  const minimumWidthPx = Math.max(
    1,
    Math.round((pageWidthPx as number) - Math.max(0, resolvedLeftPx))
  );
  if (
    Number.isFinite(currentWidthPx) &&
    (currentWidthPx as number) >= minimumWidthPx
  ) {
    return { widthPx, heightPx };
  }

  const nextWidthPx = minimumWidthPx;
  const nextHeightPx =
    Number.isFinite(widthPx) &&
    Number.isFinite(heightPx) &&
    (widthPx as number) > 0
      ? Math.max(
          1,
          Math.round(((heightPx as number) * nextWidthPx) / (widthPx as number))
        )
      : heightPx;

  return {
    widthPx: nextWidthPx,
    heightPx: nextHeightPx,
  };
}

function shouldRenderAbsoluteFloatingImage(image: ImageRunNode): boolean {
  const floating = image.floating;
  if (!floating) {
    return false;
  }

  if (shouldRenderWrappedFloatingImage(image)) {
    return false;
  }

  return (
    floating.xPx !== undefined ||
    floating.yPx !== undefined ||
    floating.horizontalAlign !== undefined ||
    floating.verticalAlign !== undefined ||
    floating.zIndex !== undefined ||
    floating.behindDocument === true
  );
}

function isPageOrMarginAnchoredAbsoluteFloatingImage(
  image: ImageRunNode
): boolean {
  if (!shouldRenderAbsoluteFloatingImage(image)) {
    return false;
  }

  const floating = image.floating;
  if (!floating) {
    return false;
  }

  const horizontalRelativeTo = floating.horizontalRelativeTo?.toLowerCase();
  const verticalRelativeTo = floating.verticalRelativeTo?.toLowerCase();
  const horizontalPageAnchored =
    horizontalRelativeTo === "page" || horizontalRelativeTo === "margin";
  const verticalPageAnchored =
    verticalRelativeTo === "page" || verticalRelativeTo === "margin";

  // Use page-level absolute positioning context only when both axes are page/margin
  // anchored. Mixed anchors (e.g. horizontal=page + vertical=line) must stay
  // paragraph-anchored so the line-relative axis remains stable.
  return horizontalPageAnchored && verticalPageAnchored;
}

function isPageOrMarginAnchoredWrappedFloatingImage(
  image: ImageRunNode
): boolean {
  if (!shouldRenderWrappedFloatingImage(image)) {
    return false;
  }

  const floating = image.floating;
  if (!floating) {
    return false;
  }

  const horizontalRelativeTo = floating.horizontalRelativeTo?.toLowerCase();
  const verticalRelativeTo = floating.verticalRelativeTo?.toLowerCase();
  const horizontalPageAnchored =
    horizontalRelativeTo === "page" || horizontalRelativeTo === "margin";
  const verticalPageAnchored =
    verticalRelativeTo === "page" || verticalRelativeTo === "margin";

  return horizontalPageAnchored && verticalPageAnchored;
}

function paragraphNeedsPageAnchoredAbsolutePositioningContext(
  paragraph: ParagraphNode
): boolean {
  return paragraph.children.some(
    (child) =>
      child.type === "image" &&
      (isPageOrMarginAnchoredAbsoluteFloatingImage(child) ||
        isPageOrMarginAnchoredWrappedFloatingImage(child))
  );
}

function paragraphNeedsLocalAbsolutePositioningContext(
  paragraph: ParagraphNode
): boolean {
  return paragraph.children.some(
    (child) =>
      child.type === "image" &&
      shouldRenderAbsoluteFloatingImage(child) &&
      !isPageOrMarginAnchoredAbsoluteFloatingImage(child)
  );
}

function paragraphHasPageAnchoredAbsoluteFloatingImage(
  paragraph: ParagraphNode
): boolean {
  return paragraph.children.some(
    (child) =>
      child.type === "image" &&
      isPageOrMarginAnchoredAbsoluteFloatingImage(child)
  );
}

function paragraphHasPageAnchoredForegroundAbsoluteFloatingImage(
  paragraph: ParagraphNode
): boolean {
  return paragraph.children.some(
    (child) =>
      child.type === "image" &&
      isPageOrMarginAnchoredAbsoluteFloatingImage(child) &&
      child.floating?.behindDocument !== true
  );
}

function imageCropLayout(
  image: ImageRunNode,
  widthPx?: number,
  heightPx?: number
):
  | {
      frameWidthPx: number;
      frameHeightPx: number;
      imageWidthPx: number;
      imageHeightPx: number;
      offsetXPx: number;
      offsetYPx: number;
    }
  | undefined {
  const crop = image.crop;
  if (
    !crop ||
    !Number.isFinite(widthPx) ||
    !Number.isFinite(heightPx) ||
    (widthPx as number) <= 0 ||
    (heightPx as number) <= 0
  ) {
    return undefined;
  }

  const left = Math.max(0, Math.min(1, crop.leftFraction ?? 0));
  const top = Math.max(0, Math.min(1, crop.topFraction ?? 0));
  const right = Math.max(0, Math.min(1, crop.rightFraction ?? 0));
  const bottom = Math.max(0, Math.min(1, crop.bottomFraction ?? 0));
  const visibleWidthFraction = 1 - left - right;
  const visibleHeightFraction = 1 - top - bottom;
  if (visibleWidthFraction <= 0 || visibleHeightFraction <= 0) {
    return undefined;
  }

  const frameWidthPx = Math.max(1, Math.round(widthPx as number));
  const frameHeightPx = Math.max(1, Math.round(heightPx as number));
  const imageWidthPx = Math.max(
    1,
    Math.round(frameWidthPx / visibleWidthFraction)
  );
  const imageHeightPx = Math.max(
    1,
    Math.round(frameHeightPx / visibleHeightFraction)
  );
  const offsetXPx = Math.round(imageWidthPx * left);
  const offsetYPx = Math.round(imageHeightPx * top);

  return {
    frameWidthPx,
    frameHeightPx,
    imageWidthPx,
    imageHeightPx,
    offsetXPx,
    offsetYPx,
  };
}

function resolveHighlightColor(value?: string): string | undefined {
  if (!value) {
    return undefined;
  }

  const normalized = value.trim().toLowerCase();
  if (!normalized) {
    return undefined;
  }

  if (normalized.startsWith("#")) {
    return normalized;
  }

  return HIGHLIGHT_TO_CSS[normalized] ?? normalized;
}

function paragraphLineHeight(
  paragraph: ParagraphNode,
  docGridLinePitchPx?: number,
  disableDocGridSnap = false
): number | string | undefined {
  const baseFontFamily = paragraphDominantFontFamily(paragraph);
  const singleLineScale = resolveParagraphSingleLineAutoScale(
    paragraph,
    baseFontFamily
  );
  const lineTwips = paragraph.style?.spacing?.lineTwips;
  const docGridMinimumLineHeightPx = resolveParagraphDocGridLinePitchPx(
    paragraph,
    docGridLinePitchPx,
    disableDocGridSnap
  );
  if (!Number.isFinite(lineTwips)) {
    if (docGridMinimumLineHeightPx) {
      return `${estimateParagraphLineHeightPx(
        paragraph,
        docGridLinePitchPx,
        disableDocGridSnap
      )}px`;
    }
    return calibrateAutoLineSpacingMultiple(
      DEFAULT_PARAGRAPH_LINE_MULTIPLE,
      baseFontFamily,
      singleLineScale
    );
  }

  const lineRule = paragraph.style?.spacing?.lineRule ?? "auto";
  if (lineRule === "auto") {
    if (docGridMinimumLineHeightPx) {
      return `${estimateParagraphLineHeightPx(
        paragraph,
        docGridLinePitchPx,
        disableDocGridSnap
      )}px`;
    }
    const resolvedAutoMultiple = resolveAutoLineSpacingMultiple(
      lineTwips as number,
      DEFAULT_PARAGRAPH_LINE_MULTIPLE
    );
    // Keep the rendered strut in lockstep with estimateParagraphLineHeightPx:
    // text-free paragraphs scale the natural single line by the multiple
    // instead of blending toward bare font-size lines.
    const lineMultiple = paragraphRendersTextFreeLine(paragraph)
      ? Math.max(
          MIN_AUTO_LINE_MULTIPLE,
          Number((resolvedAutoMultiple * singleLineScale).toFixed(3))
        )
      : calibrateAutoLineSpacingMultiple(
          resolvedAutoMultiple,
          baseFontFamily,
          singleLineScale
        );
    return Number(lineMultiple.toFixed(3));
  }

  const lineHeightPx = twipsToPixels(lineTwips);
  if (lineRule === "atLeast") {
    const normalLineHeightPx = Math.max(
      1,
      Math.round(
        paragraphBaseFontSizePx(paragraph) *
          calibrateAutoLineSpacingMultiple(
            DEFAULT_PARAGRAPH_LINE_MULTIPLE,
            baseFontFamily,
            singleLineScale
          )
      )
    );
    return `${Math.max(
      normalLineHeightPx,
      lineHeightPx ?? 0,
      docGridMinimumLineHeightPx ?? 0
    )}px`;
  }

  if (lineHeightPx && lineHeightPx > 0) {
    return `${lineHeightPx}px`;
  }

  if (lineRule === "exact") {
    return calibrateAutoLineSpacingMultiple(
      DEFAULT_PARAGRAPH_LINE_MULTIPLE,
      baseFontFamily,
      singleLineScale
    );
  }

  return calibrateAutoLineSpacingMultiple(
    DEFAULT_PARAGRAPH_LINE_MULTIPLE,
    baseFontFamily,
    singleLineScale
  );
}

function keepNextPaginationReservePx(
  paragraph: ParagraphNode,
  nextParagraph: ParagraphNode | undefined,
  docGridLinePitchPx?: number
): number {
  if (
    paragraph.style?.keepNext !== true ||
    !nextParagraph ||
    !Number.isFinite(paragraph.style?.headingLevel)
  ) {
    return 0;
  }

  const nextParagraphText = paragraphText(nextParagraph)
    .replace(/\s+/g, " ")
    .trim();
  if (
    nextParagraph.style?.numbering === undefined &&
    nextParagraphText.length < 80
  ) {
    return 0;
  }

  return Math.max(
    nextParagraph.style?.numbering ? 10 : 6,
    Math.round(
      estimateParagraphLineHeightPx(nextParagraph, docGridLinePitchPx) *
        (nextParagraph.style?.numbering ? 1 : 0.5)
    )
  );
}

function paragraphBorderToCss(
  border: ParagraphBorderStyle | undefined
): string | undefined {
  return tableBorderToCss(border);
}

function paragraphBorderPaddingPx(
  border: ParagraphBorderStyle | undefined
): number | undefined {
  const type = normalizeBorderType(border?.type);
  if (!type || type === "none" || type === "nil") {
    return undefined;
  }

  return pointsToPixels(border?.spacePt);
}

function paragraphBorderStrokeWidthPx(
  border: ParagraphBorderStyle | undefined
): number {
  const type = normalizeBorderType(border?.type);
  if (!type || type === "none" || type === "nil") {
    return 0;
  }

  const sizeEighthPt = border?.sizeEighthPt;
  return Number.isFinite(sizeEighthPt) && (sizeEighthPt as number) > 0
    ? Math.max(0.5, Number(((sizeEighthPt as number) / 6).toFixed(2)))
    : 1;
}

function paragraphBorderInsetPx(
  border: ParagraphBorderStyle | undefined
): number {
  const type = normalizeBorderType(border?.type);
  if (!type || type === "none" || type === "nil") {
    return 0;
  }

  return (
    paragraphBorderStrokeWidthPx(border) +
    (paragraphBorderPaddingPx(border) ?? 0)
  );
}

function paragraphExplicitIndentTwips(
  paragraph: ParagraphNode
): ParagraphIndent | undefined {
  const sourceXml = paragraph.sourceXml;
  if (!sourceXml) {
    return undefined;
  }

  const cached = paragraphExplicitIndentBySourceXml.get(sourceXml);
  if (cached !== undefined) {
    return cached === null ? undefined : cached;
  }

  const paragraphPropertiesXml =
    sourceXml.match(/<w:pPr\b[\s\S]*?<\/w:pPr>/i)?.[0] ??
    sourceXml.match(/<w:pPr\b[^>]*\/>/i)?.[0];
  if (!paragraphPropertiesXml) {
    paragraphExplicitIndentBySourceXml.set(sourceXml, null);
    return undefined;
  }

  const indentTag = paragraphPropertiesXml.match(/<w:ind\b[^>]*\/?>/i)?.[0];
  if (!indentTag) {
    paragraphExplicitIndentBySourceXml.set(sourceXml, null);
    return undefined;
  }

  const parseIndentTwips = (attribute: string): number | undefined => {
    const match = indentTag.match(
      new RegExp(`\\b${attribute}="(-?\\d+)"`, "i")
    );
    if (!match?.[1]) {
      return undefined;
    }

    const parsed = Number.parseInt(match[1], 10);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  let firstLineTwips = parseIndentTwips("w:firstLine");
  const hangingTwips = parseIndentTwips("w:hanging");
  if (Number.isFinite(firstLineTwips) && Number.isFinite(hangingTwips)) {
    // ECMA-376: when both firstLine and hanging are present, firstLine is ignored.
    firstLineTwips = undefined;
  }

  const explicitIndent: ParagraphIndent = {
    leftTwips: parseIndentTwips("w:left"),
    firstLineTwips,
    hangingTwips,
  };
  const hasAnyExplicitIndent =
    Number.isFinite(explicitIndent.leftTwips) ||
    Number.isFinite(explicitIndent.firstLineTwips) ||
    Number.isFinite(explicitIndent.hangingTwips);
  if (!hasAnyExplicitIndent) {
    paragraphExplicitIndentBySourceXml.set(sourceXml, null);
    return undefined;
  }

  paragraphExplicitIndentBySourceXml.set(sourceXml, explicitIndent);
  return explicitIndent;
}

function resolveListParagraphIndent(
  paragraph: ParagraphNode,
  numberingDefinitions?: NumberingDefinitionSet
): ParagraphIndent | undefined {
  const numbering = paragraph.style?.numbering;
  if (!numbering || !Number.isFinite(numbering.numId) || numbering.numId <= 0) {
    return paragraph.style?.indent;
  }

  if (!numberingDefinitions) {
    return paragraph.style?.indent;
  }

  const effectiveNumId =
    effectiveNumberingNumIdForParagraph(paragraph, numberingDefinitions) ??
    numbering.numId;
  const numberingRecoveryActive = effectiveNumId !== numbering.numId;
  const ilvl = Math.max(0, Math.round(numbering.ilvl ?? 0));
  const level = findNumberingLevelDefinition(
    numberingDefinitions,
    effectiveNumId,
    ilvl
  );
  const baseLevel = findNumberingLevelDefinition(
    numberingDefinitions,
    effectiveNumId,
    0
  );
  const levelIndent = level?.indent;
  const styleIndent = paragraph.style?.indent;
  const numberingHasVisibleMarker = Boolean(
    (level?.text && level.text.trim().length > 0) || level?.pictureBullet?.src
  );
  const numberingProvidesUsableIndent = Boolean(
    Number.isFinite(levelIndent?.leftTwips) ||
      Number.isFinite(levelIndent?.firstLineTwips) ||
      Number.isFinite(levelIndent?.hangingTwips)
  );
  const styleLeftTwips = styleIndent?.leftTwips;
  const explicitParagraphIndent = paragraphExplicitIndentTwips(paragraph);
  const explicitParagraphLeftTwips = explicitParagraphIndent?.leftTwips;
  const explicitParagraphFirstLineTwips =
    explicitParagraphIndent?.firstLineTwips;
  const explicitParagraphHangingTwips = explicitParagraphIndent?.hangingTwips;
  const hasExplicitParagraphFirstLineTwips = Number.isFinite(
    explicitParagraphFirstLineTwips
  );
  const hasExplicitParagraphHangingTwips = Number.isFinite(
    explicitParagraphHangingTwips
  );
  const preferRecoveredNumberingTextIndent = numberingRecoveryActive;
  if (!numberingHasVisibleMarker && !numberingProvidesUsableIndent) {
    return styleIndent;
  }
  const baseLevelLeftTwips = Number.isFinite(baseLevel?.indent?.leftTwips)
    ? baseLevel?.indent?.leftTwips ?? 0
    : Number.isFinite(styleLeftTwips)
    ? styleLeftTwips
    : undefined;
  const levelLeftTwips = levelIndent?.leftTwips;
  const hasExplicitLevelLeftTwips = Number.isFinite(levelLeftTwips);
  const hasExplicitStyleLeftTwips = Number.isFinite(styleLeftTwips);
  const hasExplicitParagraphLeftTwips = Number.isFinite(
    explicitParagraphLeftTwips
  );
  let nextLeftTwips = styleLeftTwips;

  if (hasExplicitParagraphLeftTwips) {
    nextLeftTwips = explicitParagraphLeftTwips;
  } else if (
    Number.isFinite(levelLeftTwips) &&
    Number.isFinite(baseLevelLeftTwips) &&
    Number.isFinite(styleLeftTwips)
  ) {
    const levelOffsetTwips = Math.max(
      0,
      (levelLeftTwips ?? 0) - (baseLevelLeftTwips ?? 0)
    );
    // When paragraph style indentation comes from defaults (often 0), it should not
    // suppress the numbering level indentation from DOCX.
    const styleUsesListBaseIndent =
      (styleLeftTwips as number) >= (baseLevelLeftTwips as number) - 120;
    nextLeftTwips = styleUsesListBaseIndent
      ? (styleLeftTwips ?? 0) + levelOffsetTwips
      : levelLeftTwips ?? 0;
  } else if (Number.isFinite(levelLeftTwips)) {
    nextLeftTwips = levelLeftTwips;
  } else if (ilvl > 0) {
    nextLeftTwips = ilvl * LIST_LEVEL_STEP_TWIPS;
  }

  // Some documents provide numbering but omit usable paragraph indents for list
  // paragraphs. Preserve explicit zero indents from OOXML and only synthesize
  // a fallback list indent when both style and numbering level omit left indents.
  if (!Number.isFinite(nextLeftTwips)) {
    if (hasExplicitParagraphLeftTwips) {
      nextLeftTwips = explicitParagraphLeftTwips;
    } else if (hasExplicitLevelLeftTwips) {
      nextLeftTwips = levelLeftTwips;
    } else if (hasExplicitStyleLeftTwips) {
      nextLeftTwips = styleLeftTwips;
    } else {
      nextLeftTwips = Math.max(
        LIST_LEVEL_STEP_TWIPS,
        (ilvl + 1) * LIST_LEVEL_STEP_TWIPS
      );
    }
  } else if ((nextLeftTwips as number) <= 0) {
    if (hasExplicitParagraphLeftTwips) {
      nextLeftTwips = explicitParagraphLeftTwips;
    } else if (hasExplicitLevelLeftTwips) {
      nextLeftTwips = levelLeftTwips;
    } else if (hasExplicitStyleLeftTwips) {
      nextLeftTwips = styleLeftTwips;
    } else if (
      Number.isFinite(levelLeftTwips) &&
      (levelLeftTwips as number) > 0
    ) {
      nextLeftTwips = levelLeftTwips;
    } else if (
      Number.isFinite(styleLeftTwips) &&
      (styleLeftTwips as number) > 0
    ) {
      nextLeftTwips = styleLeftTwips;
    } else {
      nextLeftTwips = Math.max(
        LIST_LEVEL_STEP_TWIPS,
        (ilvl + 1) * LIST_LEVEL_STEP_TWIPS
      );
    }
  }

  if (!Number.isFinite(nextLeftTwips)) {
    return styleIndent;
  }

  const nextLeftTwipsRounded = Math.max(0, Math.round(nextLeftTwips ?? 0));
  let nextFirstLineTwips: number | undefined;
  let nextHangingTwips: number | undefined;
  if (preferRecoveredNumberingTextIndent) {
    nextFirstLineTwips = levelIndent?.firstLineTwips;
    nextHangingTwips = levelIndent?.hangingTwips;
  } else if (
    hasExplicitParagraphFirstLineTwips ||
    hasExplicitParagraphHangingTwips
  ) {
    // Paragraph-level w:ind overrides numbering/style indentation semantics.
    // If firstLine is explicitly present without hanging, do not inherit hanging.
    // If hanging is explicitly present without firstLine, do not inherit firstLine.
    nextFirstLineTwips = hasExplicitParagraphFirstLineTwips
      ? explicitParagraphFirstLineTwips
      : undefined;
    nextHangingTwips = hasExplicitParagraphHangingTwips
      ? explicitParagraphHangingTwips
      : undefined;
  } else {
    nextFirstLineTwips = Number.isFinite(styleIndent?.firstLineTwips)
      ? styleIndent?.firstLineTwips
      : levelIndent?.firstLineTwips;
    nextHangingTwips = Number.isFinite(styleIndent?.hangingTwips)
      ? styleIndent?.hangingTwips
      : levelIndent?.hangingTwips;
  }

  if (
    Number.isFinite(nextFirstLineTwips) &&
    Number.isFinite(nextHangingTwips)
  ) {
    // ECMA-376: firstLine is ignored when both values are present.
    nextFirstLineTwips = undefined;
  }

  return {
    ...styleIndent,
    leftTwips: nextLeftTwipsRounded,
    firstLineTwips: nextFirstLineTwips,
    hangingTwips: nextHangingTwips,
  };
}

function resolveNumberingMarkerBoxWidthPx(
  paragraph: ParagraphNode,
  numberingDefinitions?: NumberingDefinitionSet,
  numberingLabel?: ParagraphNumberingLabel
): number | undefined {
  const resolvedIndent = resolveListParagraphIndent(
    paragraph,
    numberingDefinitions
  );
  const hangingIndentPx = twipsToSignedPixels(resolvedIndent?.hangingTwips);
  const firstLineIndentPx = twipsToSignedPixels(resolvedIndent?.firstLineTwips);
  const candidateWidthPx =
    Number.isFinite(hangingIndentPx) && Math.abs(hangingIndentPx as number) > 0
      ? Math.abs(hangingIndentPx as number)
      : Number.isFinite(firstLineIndentPx) && (firstLineIndentPx as number) < 0
      ? Math.abs(firstLineIndentPx as number)
      : undefined;

  const labelTextForWidth = numberingLabel?.imageSrc
    ? numberingLabel.trailingText ?? ""
    : numberingLabel?.text ?? "";
  const normalizedLabelTextForWidth = labelTextForWidth
    .replace(/\t/g, " ")
    .replace(/\u00a0/g, " ");
  const measuredLabelWidthPx =
    normalizedLabelTextForWidth.length > 0
      ? Math.ceil(
          measureTextWidthPx(
            normalizedLabelTextForWidth,
            numberingLabel?.style,
            paragraphBaseFontSizePx(paragraph)
          )
        ) + 4
      : 0;

  const imageWidthPx = numberingLabel?.imageWidthPx;
  const tocLeadingLeftTabStopPx =
    tableOfContentsLeadingLeftTabStopPx(paragraph);
  const tocLabelGapPx = isTableOfContentsParagraph(paragraph) ? 6 : 0;
  const tocMarkerTargetWidthPx =
    Number.isFinite(tocLeadingLeftTabStopPx) &&
    (tocLeadingLeftTabStopPx as number) > 0
      ? Math.min(
          Math.ceil(tocLeadingLeftTabStopPx as number),
          Math.max(
            measuredLabelWidthPx + tocLabelGapPx,
            measuredLabelWidthPx + 16
          )
        )
      : 0;
  const minimumVisualWidthPx = Math.max(
    measuredLabelWidthPx + tocLabelGapPx,
    Number.isFinite(imageWidthPx) && (imageWidthPx as number) > 0
      ? Math.ceil(imageWidthPx as number) + 2
      : 0,
    tocMarkerTargetWidthPx
  );

  if (!Number.isFinite(candidateWidthPx) || (candidateWidthPx as number) < 8) {
    return minimumVisualWidthPx > 0
      ? clampNumber(Math.round(minimumVisualWidthPx), 8, 220)
      : undefined;
  }

  return Math.max(
    minimumVisualWidthPx,
    clampNumber(Math.round(candidateWidthPx as number), 8, 220)
  );
}

function numberingMarkerStyle(
  paragraph: ParagraphNode,
  numberingDefinitions: NumberingDefinitionSet | undefined,
  numberingLabel: ParagraphNumberingLabel,
  baseStyle: React.CSSProperties | undefined,
  documentTheme: DocxDocumentTheme
): React.CSSProperties {
  const markerBoxWidthPx = resolveNumberingMarkerBoxWidthPx(
    paragraph,
    numberingDefinitions,
    numberingLabel
  );
  const markerGapPx = isTableOfContentsParagraph(paragraph)
    ? 6
    : markerBoxWidthPx
    ? 0
    : 2;

  return {
    ...(baseStyle ?? {}),
    display: "inline-flex",
    alignItems: "baseline",
    justifyContent: "flex-end",
    verticalAlign: "baseline",
    width: markerBoxWidthPx ? `${markerBoxWidthPx}px` : undefined,
    minWidth: markerBoxWidthPx ? `${markerBoxWidthPx}px` : "1.1em",
    marginRight: markerGapPx,
    whiteSpace: "pre",
    fontFamily: cssFontFamily(
      numberingLabel.fontFamily ?? numberingLabel.style?.fontFamily
    ),
    color: themedRunColor(
      numberingLabel.color ?? numberingLabel.style?.color,
      documentTheme
    ),
  };
}

function paragraphBlockStyle(
  paragraph: ParagraphNode,
  numberingDefinitions?: NumberingDefinitionSet,
  headingStyles?: DocxHeadingStyleMap,
  docGridLinePitchPx?: number,
  disableDocGridSnap = false
): React.CSSProperties {
  const beforeSpacing =
    twipsToPixels(paragraph.style?.spacing?.beforeTwips) ?? 0;
  const afterSpacing = twipsToPixels(paragraph.style?.spacing?.afterTwips) ?? 0;
  const resolvedIndent = resolveListParagraphIndent(
    paragraph,
    numberingDefinitions
  );
  const leftIndent = twipsToSignedPixels(resolvedIndent?.leftTwips) ?? 0;
  const rightIndent =
    twipsToSignedPixels(paragraph.style?.indent?.rightTwips) ?? 0;
  const firstLineIndent = twipsToSignedPixels(resolvedIndent?.firstLineTwips);
  const hangingIndent = twipsToSignedPixels(resolvedIndent?.hangingTwips);
  const topBorder = paragraphBorderToCss(paragraph.style?.borders?.top);
  const rightBorder = paragraphBorderToCss(paragraph.style?.borders?.right);
  const bottomBorder = paragraphBorderToCss(paragraph.style?.borders?.bottom);
  const leftBorder = paragraphBorderToCss(paragraph.style?.borders?.left);
  const topPadding = paragraphBorderPaddingPx(paragraph.style?.borders?.top);
  const rightPadding = paragraphBorderPaddingPx(
    paragraph.style?.borders?.right
  );
  const bottomPadding = paragraphBorderPaddingPx(
    paragraph.style?.borders?.bottom
  );
  const leftPadding = paragraphBorderPaddingPx(paragraph.style?.borders?.left);
  const checkboxChoiceRow = paragraphLooksLikeCheckboxChoiceRow(paragraph);
  const suppressTocNumberingTextIndent =
    isTableOfContentsParagraph(paragraph) && paragraphHasNumbering(paragraph);
  const suppressIndentForFloatingAnchorOnlyParagraph =
    paragraphIsFloatingImageAnchorOnly(paragraph);
  const suppressStackingContextForBehindTextAnchorOnlyParagraph =
    paragraphIsBehindTextAbsoluteFloatingImageAnchorOnly(paragraph);
  const reservedMinHeightPx = paragraphIsEffectivelyEmpty(paragraph)
    ? estimateParagraphLineHeightPx(
        paragraph,
        docGridLinePitchPx,
        disableDocGridSnap
      ) + EMPTY_PARAGRAPH_EXTRA_HEIGHT_PX
    : undefined;
  const headingLevel = paragraph.style?.headingLevel;
  const applyWordLikeHeadingFallback = !paragraph.sourceXml;
  const hasSoftLineBreak = paragraphText(paragraph).includes("\n");
  const headingStyle =
    headingLevel && headingLevel >= 1 && headingLevel <= 6
      ? headingStyles?.[headingLevel] ??
        (applyWordLikeHeadingFallback
          ? DEFAULT_WORD_HEADING_STYLES[headingLevel]
          : undefined)
      : undefined;

  return {
    position: "relative",
    zIndex: suppressStackingContextForBehindTextAnchorOnlyParagraph
      ? undefined
      : 1,
    overflow: "visible",
    textAlign: paragraph.style?.align ?? "left",
    ...(paragraph.style?.align === "justify" && hasSoftLineBreak
      ? ({ textAlignLast: "justify" } as React.CSSProperties)
      : undefined),
    // Pin the block font size to the paragraph's dominant run size so the
    // line-box strut tracks the actual content instead of the browser's
    // 16px default, which inflates lines for sub-12pt paragraphs.
    fontSize: `${paragraphBaseFontSizePx(paragraph)}px`,
    lineHeight: paragraphLineHeight(
      paragraph,
      docGridLinePitchPx,
      disableDocGridSnap
    ),
    marginTop: beforeSpacing,
    marginBottom: afterSpacing,
    marginLeft: suppressIndentForFloatingAnchorOnlyParagraph ? 0 : leftIndent,
    marginRight: suppressIndentForFloatingAnchorOnlyParagraph ? 0 : rightIndent,
    backgroundColor: paragraph.style?.backgroundColor,
    textIndent:
      suppressTocNumberingTextIndent ||
      suppressIndentForFloatingAnchorOnlyParagraph
        ? undefined
        : firstLineIndent ?? (hangingIndent ? -hangingIndent : undefined),
    minHeight: Number.isFinite(reservedMinHeightPx)
      ? `${reservedMinHeightPx}px`
      : undefined,
    ...(headingStyle ?? undefined),
    ...(topBorder !== undefined ? { borderTop: topBorder } : undefined),
    ...(rightBorder !== undefined ? { borderRight: rightBorder } : undefined),
    ...(bottomBorder !== undefined
      ? { borderBottom: bottomBorder }
      : undefined),
    ...(leftBorder !== undefined ? { borderLeft: leftBorder } : undefined),
    ...(topPadding !== undefined ? { paddingTop: topPadding } : undefined),
    ...(rightPadding !== undefined
      ? { paddingRight: rightPadding }
      : undefined),
    ...(bottomPadding !== undefined
      ? { paddingBottom: bottomPadding }
      : undefined),
    ...(leftPadding !== undefined ? { paddingLeft: leftPadding } : undefined),
    whiteSpace: checkboxChoiceRow ? "nowrap" : "pre-wrap",
    ...(checkboxChoiceRow
      ? ({ tabSize: 1 } as React.CSSProperties)
      : undefined),
    wordWrap: checkboxChoiceRow ? "normal" : "break-word",
    overflowWrap: checkboxChoiceRow ? "normal" : "break-word",
    wordBreak: checkboxChoiceRow ? "normal" : "break-word",
  };
}

function tableCellParagraphBlockStyle(
  paragraph: ParagraphNode,
  numberingDefinitions: NumberingDefinitionSet | undefined,
  headingStyles: DocxHeadingStyleMap | undefined,
  paragraphIndex: number,
  applyWordTableDefaults: boolean,
  docGridLinePitchPx?: number
): React.CSSProperties {
  const paragraphForLayout = wordLikeTableCellParagraph(
    paragraph,
    applyWordTableDefaults
  );
  const disableDocGridSnap = paragraphDocGridSnapState(paragraph) === "disable";
  const baseStyle = paragraphBlockStyle(
    paragraphForLayout,
    numberingDefinitions,
    headingStyles,
    docGridLinePitchPx,
    disableDocGridSnap
  );
  const suppressTopSpacing =
    paragraphIndex <= 0 && suppressFirstTableCellParagraphTopSpacing(paragraph);
  const normalizedMarginLeft =
    typeof baseStyle.marginLeft === "number"
      ? baseStyle.marginLeft
      : Number.parseFloat(String(baseStyle.marginLeft ?? ""));
  const normalizedMarginRight =
    typeof baseStyle.marginRight === "number"
      ? baseStyle.marginRight
      : Number.parseFloat(String(baseStyle.marginRight ?? ""));
  const normalizedPaddingLeft =
    typeof baseStyle.paddingLeft === "number"
      ? baseStyle.paddingLeft
      : Number.parseFloat(String(baseStyle.paddingLeft ?? ""));
  const normalizedPaddingRight =
    typeof baseStyle.paddingRight === "number"
      ? baseStyle.paddingRight
      : Number.parseFloat(String(baseStyle.paddingRight ?? ""));
  const convertPositiveCellIndentToPadding =
    (Number.isFinite(normalizedMarginLeft) &&
      (normalizedMarginLeft as number) > 0) ||
    (Number.isFinite(normalizedMarginRight) &&
      (normalizedMarginRight as number) > 0);

  return {
    ...baseStyle,
    ...(convertPositiveCellIndentToPadding
      ? {
          marginLeft:
            Number.isFinite(normalizedMarginLeft) &&
            (normalizedMarginLeft as number) > 0
              ? 0
              : baseStyle.marginLeft,
          marginRight:
            Number.isFinite(normalizedMarginRight) &&
            (normalizedMarginRight as number) > 0
              ? 0
              : baseStyle.marginRight,
          paddingLeft:
            Number.isFinite(normalizedMarginLeft) &&
            (normalizedMarginLeft as number) > 0
              ? Math.max(
                  0,
                  Math.round(
                    (Number.isFinite(normalizedPaddingLeft)
                      ? (normalizedPaddingLeft as number)
                      : 0) + (normalizedMarginLeft as number)
                  )
                )
              : baseStyle.paddingLeft,
          paddingRight:
            Number.isFinite(normalizedMarginRight) &&
            (normalizedMarginRight as number) > 0
              ? Math.max(
                  0,
                  Math.round(
                    (Number.isFinite(normalizedPaddingRight)
                      ? (normalizedPaddingRight as number)
                      : 0) + (normalizedMarginRight as number)
                  )
                )
              : baseStyle.paddingRight,
          boxSizing: "border-box",
          width: "100%",
        }
      : undefined),
    ...(suppressTopSpacing ? { marginTop: 0 } : undefined),
  };
}

function themedRunColor(
  color: string | undefined,
  documentTheme: DocxDocumentTheme
): string | undefined {
  if (documentTheme !== "dark") {
    return color;
  }

  if (!color) {
    return "#f3f4f6";
  }

  const normalized = color.trim().toLowerCase();
  if (
    normalized === "#000" ||
    normalized === "#000000" ||
    normalized === "#111111" ||
    normalized === "#111827" ||
    normalized === "black" ||
    normalized === "rgb(0,0,0)" ||
    normalized === "rgb(0, 0, 0)"
  ) {
    return "#f3f4f6";
  }

  return color;
}

function cssFontFamily(fontFamily?: string): string | undefined {
  if (!fontFamily) {
    return undefined;
  }

  const trimmed = fontFamily.trim();
  if (!trimmed) {
    return undefined;
  }

  if (trimmed.includes(",")) {
    return trimmed;
  }

  const normalized = trimmed.replace(/^['"]+|['"]+$/g, "");
  if (!normalized) {
    return undefined;
  }

  const escaped = normalized.replace(/"/g, '\\"');
  const familyToken = /\s/.test(escaped) ? `"${escaped}"` : escaped;
  const lower = normalized.toLowerCase();
  const genericFamily = /(mono|consolas|courier|menlo|code)/.test(lower)
    ? "monospace"
    : /(times|cambria|georgia|garamond|baskerville|serif)/.test(lower)
    ? "serif"
    : "sans-serif";

  return `${familyToken}, ${genericFamily}`;
}

function runStyleToCss(
  style?: TextRunNode["style"],
  documentTheme: DocxDocumentTheme = "light"
): React.CSSProperties {
  const hasScriptVerticalAlign =
    style?.verticalAlign === "superscript" ||
    style?.verticalAlign === "subscript";
  const verticalAlign =
    style?.verticalAlign === "superscript"
      ? "super"
      : style?.verticalAlign === "subscript"
      ? "sub"
      : undefined;
  const textDecorationTokens = [
    style?.underline ? "underline" : "",
    style?.strike ? "line-through" : "",
  ].filter(Boolean);
  const textDecoration =
    textDecorationTokens.length > 0 ? textDecorationTokens.join(" ") : "none";
  const borderType = style?.runBorder?.type?.trim().toLowerCase();
  const borderStyle =
    borderType === "single"
      ? "solid"
      : borderType === "nil" || borderType === "none"
      ? undefined
      : borderType;
  const borderWidthPx =
    Number.isFinite(style?.runBorder?.sizeEighthPt) &&
    (style?.runBorder?.sizeEighthPt as number) > 0
      ? Math.max(
          1,
          Number(
            (
              ((style?.runBorder?.sizeEighthPt as number) / 8) *
              (96 / 72)
            ).toFixed(2)
          )
        )
      : borderStyle
      ? 1
      : undefined;
  const borderPaddingPt =
    Number.isFinite(style?.runBorder?.spacePt) &&
    (style?.runBorder?.spacePt as number) >= 0
      ? Math.max(0, Math.round(style?.runBorder?.spacePt as number))
      : undefined;

  return {
    fontWeight: style?.bold ? 700 : undefined,
    fontStyle: style?.italic ? "italic" : undefined,
    textDecoration,
    color: themedRunColor(style?.color, documentTheme),
    backgroundColor:
      style?.backgroundColor ?? resolveHighlightColor(style?.highlight),
    fontSize: style?.fontSizePt
      ? `${Number(
          (
            style.fontSizePt * (hasScriptVerticalAlign ? SCRIPT_FONT_SCALE : 1)
          ).toFixed(3)
        )}pt`
      : hasScriptVerticalAlign
      ? `${SCRIPT_FONT_SCALE}em`
      : undefined,
    fontFamily: cssFontFamily(style?.fontFamily),
    letterSpacing: Number.isFinite(style?.characterSpacingTwips)
      ? `${Number(
          ((style?.characterSpacingTwips as number) / 20).toFixed(3)
        )}pt`
      : undefined,
    verticalAlign,
    display: borderStyle ? "inline-block" : undefined,
    borderStyle,
    borderWidth: borderWidthPx ? `${borderWidthPx}px` : undefined,
    borderColor: borderStyle
      ? style?.runBorder?.color ?? "currentColor"
      : undefined,
    ...(borderPaddingPt !== undefined
      ? {
          paddingTop: `${borderPaddingPt}pt`,
          paddingRight: `${borderPaddingPt}pt`,
          paddingBottom: `${borderPaddingPt}pt`,
          paddingLeft: `${borderPaddingPt}pt`,
        }
      : undefined),
    boxDecorationBreak: borderStyle ? "clone" : undefined,
    whiteSpace: "pre-wrap",
  };
}

function linkStyleToCss(
  style?: TextRunNode["style"],
  documentTheme: DocxDocumentTheme = "light"
): React.CSSProperties {
  const base = runStyleToCss(style, documentTheme);
  const resolvedTextDecoration =
    typeof base.textDecoration === "string" &&
    base.textDecoration.trim().length > 0
      ? base.textDecoration
      : "none";
  return {
    ...base,
    color: base.color ?? "inherit",
    textDecoration: resolvedTextDecoration,
  };
}

function mergeTextDecorations(
  baseDecoration: React.CSSProperties["textDecoration"],
  decoration: string
): string {
  const tokens = new Set<string>();
  if (typeof baseDecoration === "string") {
    baseDecoration
      .split(/\s+/)
      .map((token) => token.trim())
      .filter(Boolean)
      .forEach((token) => tokens.add(token));
  }

  decoration
    .split(/\s+/)
    .map((token) => token.trim())
    .filter(Boolean)
    .forEach((token) => tokens.add(token));

  return tokens.size > 0 ? Array.from(tokens).join(" ") : "none";
}

function trackedInlineStyle(
  baseStyle: React.CSSProperties,
  change: ParagraphTrackedInlineChange | undefined
): React.CSSProperties {
  if (!change) {
    return baseStyle;
  }

  if (change.kind === "insertion" || change.kind === "move-to") {
    const accentColor = change.kind === "move-to" ? "#70ad47" : "#dc2626";
    return {
      ...baseStyle,
      color: accentColor,
      textDecoration: mergeTextDecorations(
        baseStyle.textDecoration,
        "underline"
      ),
    };
  }

  return baseStyle;
}

function trackedDeletedStyle(
  documentTheme: DocxDocumentTheme,
  baseRunStyle?: TextRunNode["style"] | FormFieldRunNode["style"]
): React.CSSProperties {
  const baseStyle = runStyleToCss(baseRunStyle, documentTheme);
  return {
    ...baseStyle,
    color: documentTheme === "dark" ? "#fca5a5" : "#b91c1c",
    textDecoration: mergeTextDecorations(
      baseStyle.textDecoration,
      "line-through"
    ),
    whiteSpace: baseStyle.whiteSpace ?? "pre-wrap",
    lineHeight: "inherit",
    opacity: 0.95,
  };
}

const TABLE_OF_CONTENTS_STYLE_ID = /^toc(?:[\s_-]*\d+)?$/i;

function isTableOfContentsStyle(styleId?: string): boolean {
  if (!styleId) {
    return false;
  }
  return TABLE_OF_CONTENTS_STYLE_ID.test(styleId.trim());
}

function tableOfContentsLevel(paragraph: ParagraphNode): number | undefined {
  const candidates = [paragraph.style?.styleId, paragraph.style?.styleName];
  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }
    const match = candidate.trim().match(/^toc(?:[\s_-]*(\d+))?$/i);
    if (!match) {
      continue;
    }
    const parsedLevel = match[1] ? Number.parseInt(match[1], 10) : 1;
    if (Number.isFinite(parsedLevel) && parsedLevel > 0) {
      return Math.round(parsedLevel);
    }
    return 1;
  }
  return undefined;
}

function isTableOfContentsParagraph(paragraph: ParagraphNode): boolean {
  return (
    isTableOfContentsStyle(paragraph.style?.styleId) ||
    isTableOfContentsStyle(paragraph.style?.styleName)
  );
}

function paragraphUsesTabLeaders(paragraph: ParagraphNode): boolean {
  if (!isTableOfContentsParagraph(paragraph)) {
    return false;
  }

  const tabStops = paragraph.style?.tabStops ?? [];
  if (
    tabStops.some(
      (tabStop) => tabStop.alignment === "right" || tabStop.leader === "dot"
    )
  ) {
    return true;
  }

  return paragraph.children.some((child) => {
    if (child.type === "text") {
      return child.text.includes("\t");
    }
    if (child.type === "form-field") {
      return formFieldDisplayValue(child).includes("\t");
    }
    return false;
  });
}

function paragraphLeadingTabStop(paragraph: ParagraphNode):
  | {
      alignment?: "left" | "center" | "right" | "decimal" | "bar";
      leader?: "none" | "dot" | "hyphen" | "underscore" | "middleDot";
      positionTwips?: number;
    }
  | undefined {
  const tabStops = paragraph.style?.tabStops ?? [];
  const explicitTabStop = tabStops.find(
    (tabStop) => tabStop.alignment === "right" || tabStop.leader === "dot"
  );
  if (explicitTabStop) {
    return explicitTabStop;
  }
  if (isTableOfContentsParagraph(paragraph)) {
    return {
      alignment: "right",
      leader: "dot",
    };
  }
  return undefined;
}

function tableOfContentsLeadingLeftTabStopPx(
  paragraph: ParagraphNode
): number | undefined {
  if (!isTableOfContentsParagraph(paragraph)) {
    return undefined;
  }

  return resolveParagraphFirstLineLeftTabStopsPx(paragraph)[0];
}

function paragraphContainsTabCharacter(paragraph: ParagraphNode): boolean {
  return paragraph.children.some((child) => {
    if (child.type === "text") {
      return child.text.includes("\t");
    }
    if (child.type === "form-field") {
      return formFieldDisplayValue(child).includes("\t");
    }
    return false;
  });
}

function paragraphTabCharacterCount(paragraph: ParagraphNode): number {
  return paragraph.children.reduce((count, child) => {
    const text =
      child.type === "text"
        ? child.text
        : child.type === "form-field"
        ? formFieldDisplayValue(child)
        : "";
    if (!text) {
      return count;
    }
    const matches = text.match(/\t/g);
    return count + (matches ? matches.length : 0);
  }, 0);
}

type ParagraphAnchoredTabLayout = "none" | "center-right" | "center" | "right";

function paragraphAnchoredTabLayout(
  paragraph: ParagraphNode,
  options?: {
    withinHeaderFooter?: boolean;
  }
): ParagraphAnchoredTabLayout {
  const tabStops = paragraph.style?.tabStops ?? [];
  const hasLeft = tabStops.some((tabStop) => tabStop.alignment === "left");
  const hasCenter = tabStops.some((tabStop) => tabStop.alignment === "center");
  const hasRight = tabStops.some((tabStop) => tabStop.alignment === "right");
  const tabCount = paragraphTabCharacterCount(paragraph);
  const withinHeaderFooter = options?.withinHeaderFooter === true;

  if (hasLeft && (hasCenter || hasRight)) {
    return "none";
  }

  if (hasCenter && hasRight) {
    if (tabCount >= 2) {
      return "center-right";
    }
    if (withinHeaderFooter && tabCount >= 1) {
      return "center-right";
    }
    return "none";
  }
  if (hasCenter && tabCount === 1) {
    return "center";
  }
  if (hasRight && tabCount === 1) {
    return "right";
  }

  return "none";
}

function paragraphFirstTabStopPx(
  paragraph: ParagraphNode,
  alignment: "center" | "right"
): number | undefined {
  return (paragraph.style?.tabStops ?? [])
    .filter((tabStop) => tabStop.alignment === alignment)
    .map((tabStop) => twipsToPixels(tabStop.positionTwips))
    .filter(
      (positionPx): positionPx is number =>
        Number.isFinite(positionPx) && (positionPx as number) > 0
    )
    .sort((left, right) => left - right)[0];
}

function paragraphUsesCenterTabLayout(paragraph: ParagraphNode): boolean {
  return paragraphAnchoredTabLayout(paragraph) === "center";
}

function paragraphUsesRightTabLayout(paragraph: ParagraphNode): boolean {
  return paragraphAnchoredTabLayout(paragraph) === "right";
}

function paragraphUsesCenterRightTabLayout(paragraph: ParagraphNode): boolean {
  return paragraphAnchoredTabLayout(paragraph) === "center-right";
}

type PageFieldKind = "PAGE" | "NUMPAGES";

interface PageFieldValueToken {
  kind: PageFieldKind;
  rawText: string;
}

interface StyleRefFieldValueToken {
  target: string;
  rawText: string;
}

function decodeXmlText(text: string): string {
  if (!text) {
    return text;
  }

  const withNumericEntities = text
    .replace(/&#x([0-9a-f]+);/gi, (_, hex: string) => {
      const codePoint = Number.parseInt(hex, 16);
      return Number.isFinite(codePoint) ? String.fromCodePoint(codePoint) : _;
    })
    .replace(/&#([0-9]+);/g, (_, decimal: string) => {
      const codePoint = Number.parseInt(decimal, 10);
      return Number.isFinite(codePoint) ? String.fromCodePoint(codePoint) : _;
    });

  return withNumericEntities
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'");
}

interface XmlBalancedTagRange {
  start: number;
  end: number;
  tagName: string;
  openTag: string;
}

function extractBalancedTagRanges(
  xml: string,
  tagName: string
): XmlBalancedTagRange[] {
  if (!xml) {
    return [];
  }

  const escapedTagName = tagName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp(`<(/?)${escapedTagName}(?=[\\s>/])[^>]*>`, "gi");
  const stack: Array<{ start: number; openTag: string }> = [];
  const ranges: XmlBalancedTagRange[] = [];

  for (const match of xml.matchAll(pattern)) {
    const fullMatch = match[0] ?? "";
    if (!fullMatch) {
      continue;
    }

    const start = match.index ?? 0;
    const isClosing = match[1] === "/";
    const isSelfClosing = !isClosing && /\/>\s*$/i.test(fullMatch);
    if (isSelfClosing) {
      ranges.push({
        start,
        end: start + fullMatch.length,
        tagName,
        openTag: fullMatch,
      });
      continue;
    }

    if (!isClosing) {
      stack.push({
        start,
        openTag: fullMatch,
      });
      continue;
    }

    const opener = stack.pop();
    if (!opener) {
      continue;
    }

    ranges.push({
      start: opener.start,
      end: start + fullMatch.length,
      tagName,
      openTag: opener.openTag,
    });
  }

  return ranges;
}

interface RevisionTagRange extends XmlBalancedTagRange {
  kind: Exclude<
    DocxTrackedChangeKind,
    "format-change" | "paragraph-format-change"
  >;
  revisionId?: string;
  author?: string;
  date?: string;
}

function trackedChangeKindFromTagName(
  tagName: string
):
  | Exclude<DocxTrackedChangeKind, "format-change" | "paragraph-format-change">
  | undefined {
  const normalized = tagName.trim().toLowerCase();
  switch (normalized) {
    case "w:ins":
      return "insertion";
    case "w:del":
      return "deletion";
    case "w:movefrom":
      return "move-from";
    case "w:moveto":
      return "move-to";
    default:
      return undefined;
  }
}

function normalizeTrackedChangeSnippet(value?: string): string | undefined {
  if (!value) {
    return undefined;
  }
  const normalized = value
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return normalized.length > 0 ? normalized : undefined;
}

function formatTrackedChangeDate(value?: string): string | undefined {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  }).format(parsed);
}

function stripTextBoxContentFromRunXml(runXml: string): string {
  if (!runXml.includes("w:txbxContent")) {
    return runXml;
  }
  return runXml.replace(/<w:txbxContent\b[\s\S]*?<\/w:txbxContent>/gi, "");
}

function parseTrackedRunTokens(
  runXml: string,
  includeDeletedText: boolean
): Array<{ text: string; isNote: boolean }> {
  if (!runXml) {
    return [];
  }

  const tokens: Array<{ text: string; isNote: boolean }> = [];
  const pattern =
    /<w:delText\b[^>]*>([\s\S]*?)<\/w:delText>|<(?:w|a):t\b[^>]*>([\s\S]*?)<\/(?:w|a):t>|<w:tab\b[^>]*\/?>|<w:(?:br|cr)\b[^>]*\/?>|<w:footnoteReference\b[^>]*\/?>|<w:endnoteReference\b[^>]*\/?>/gi;

  for (const match of runXml.matchAll(pattern)) {
    if (match[1] !== undefined) {
      if (!includeDeletedText) {
        continue;
      }
      const decoded = decodeXmlText(match[1] ?? "");
      tokens.push({ text: decoded, isNote: false });
      continue;
    }

    if (match[2] !== undefined) {
      const decoded = decodeXmlText(match[2] ?? "");
      tokens.push({ text: decoded, isNote: false });
      continue;
    }

    const tagXml = match[0] ?? "";
    if (/^<w:tab\b/i.test(tagXml)) {
      tokens.push({ text: "\t", isNote: false });
      continue;
    }
    if (/^<w:(?:br|cr)\b/i.test(tagXml)) {
      tokens.push({ text: "\n", isNote: false });
      continue;
    }
    if (/^<w:(?:footnoteReference|endnoteReference)\b/i.test(tagXml)) {
      tokens.push({ text: "\u2063", isNote: true });
    }
  }

  return tokens;
}

function xmlBooleanFlag(tagXml: string | undefined): boolean {
  if (!tagXml) {
    return false;
  }

  const raw = xmlAttribute(tagXml, "w:val")?.trim().toLowerCase();
  if (!raw) {
    return true;
  }

  return !(raw === "false" || raw === "0" || raw === "off" || raw === "none");
}

function xmlColorValue(tagXml: string | undefined): string | undefined {
  if (!tagXml) {
    return undefined;
  }

  const raw = xmlAttribute(tagXml, "w:val")?.trim();
  if (!raw || /^auto$/i.test(raw)) {
    return undefined;
  }

  if (/^#[0-9a-f]{6}$/i.test(raw)) {
    return raw.toLowerCase();
  }

  if (/^[0-9a-f]{6}$/i.test(raw)) {
    return `#${raw.toLowerCase()}`;
  }

  return raw;
}

function parseRunStyleFromRunXml(
  runXml: string
): TextRunNode["style"] | undefined {
  const rPrRange = extractBalancedTagRanges(runXml, "w:rPr")[0];
  const rPrXml = rPrRange ? runXml.slice(rPrRange.start, rPrRange.end) : "";
  if (!rPrXml) {
    return undefined;
  }

  const rFontsTag = rPrXml.match(/<w:rFonts\b[^>]*\/?>/i)?.[0];
  const rFontsAscii = rFontsTag
    ? xmlAttribute(rFontsTag, "w:ascii")
    : undefined;
  const rFontsHAnsi = rFontsTag
    ? xmlAttribute(rFontsTag, "w:hAnsi")
    : undefined;
  const rFontsEastAsia = rFontsTag
    ? xmlAttribute(rFontsTag, "w:eastAsia")
    : undefined;
  const rFontsCs = rFontsTag ? xmlAttribute(rFontsTag, "w:cs") : undefined;
  const fontFamily = rFontsAscii ?? rFontsHAnsi ?? rFontsEastAsia ?? rFontsCs;

  const sizeTag =
    rPrXml.match(/<w:sz\b[^>]*\/?>/i)?.[0] ??
    rPrXml.match(/<w:szCs\b[^>]*\/?>/i)?.[0];
  const sizeHalfPoints = sizeTag
    ? Number(xmlAttribute(sizeTag, "w:val"))
    : Number.NaN;
  const fontSizePt =
    Number.isFinite(sizeHalfPoints) && sizeHalfPoints > 0
      ? Number((sizeHalfPoints / 2).toFixed(2))
      : undefined;

  const bold = xmlBooleanFlag(rPrXml.match(/<w:b(?:Cs)?\b[^>]*\/?>/i)?.[0]);
  const italic = xmlBooleanFlag(rPrXml.match(/<w:i(?:Cs)?\b[^>]*\/?>/i)?.[0]);
  const underlineTag = rPrXml.match(/<w:u\b[^>]*\/?>/i)?.[0];
  const underline = xmlBooleanFlag(underlineTag);
  const strike = xmlBooleanFlag(rPrXml.match(/<w:strike\b[^>]*\/?>/i)?.[0]);
  const color = xmlColorValue(rPrXml.match(/<w:color\b[^>]*\/?>/i)?.[0]);
  const highlightTag = rPrXml.match(/<w:highlight\b[^>]*\/?>/i)?.[0];
  const highlight = highlightTag
    ? xmlAttribute(highlightTag, "w:val")
    : undefined;
  const verticalAlignTag = rPrXml.match(/<w:vertAlign\b[^>]*\/?>/i)?.[0];
  const verticalAlign = verticalAlignTag
    ? xmlAttribute(verticalAlignTag, "w:val")
    : undefined;

  const style: NonNullable<TextRunNode["style"]> = {
    fontFamily: fontFamily?.trim() || undefined,
    fontSizePt,
    bold: bold || undefined,
    italic: italic || undefined,
    underline: underline || undefined,
    strike: strike || undefined,
    color,
    highlight: highlight?.trim() || undefined,
    verticalAlign:
      verticalAlign === "superscript" || verticalAlign === "subscript"
        ? verticalAlign
        : undefined,
  };

  return Object.values(style).some((value) => value !== undefined)
    ? style
    : undefined;
}

function balancedTagXmlBlocks(xml: string, tagName: string): string[] {
  return extractBalancedTagRanges(xml, tagName).map((range) =>
    xml.slice(range.start, range.end)
  );
}

function mergeTextRunStyles(
  base?: TextRunNode["style"],
  override?: TextRunNode["style"]
): TextRunNode["style"] | undefined {
  if (!base && !override) {
    return undefined;
  }

  const merged: TextRunNode["style"] = {
    fontFamily: override?.fontFamily ?? base?.fontFamily,
    fontSizePt: override?.fontSizePt ?? base?.fontSizePt,
    bold: override?.bold ?? base?.bold,
    italic: override?.italic ?? base?.italic,
    underline: override?.underline ?? base?.underline,
    strike: override?.strike ?? base?.strike,
    color: override?.color ?? base?.color,
    highlight: override?.highlight ?? base?.highlight,
    verticalAlign: override?.verticalAlign ?? base?.verticalAlign,
  };

  return Object.values(merged).some((value) => value !== undefined)
    ? merged
    : undefined;
}

function parseParagraphAlignmentFromXml(
  paragraphPropertiesXml: string
): ParagraphAlignment | undefined {
  const jcTag = paragraphPropertiesXml.match(/<w:jc\b[^>]*\/?>/i)?.[0];
  const raw = jcTag
    ? xmlAttribute(jcTag, "w:val")?.trim().toLowerCase()
    : undefined;
  if (
    raw === "center" ||
    raw === "right" ||
    raw === "justify" ||
    raw === "left"
  ) {
    return raw;
  }
  return undefined;
}

function escapeSvgText(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function svgDataUri(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

interface SyntheticTextBoxSegment {
  text: string;
  style?: TextRunNode["style"];
}

interface SyntheticTextBoxParagraph {
  align?: ParagraphAlignment;
  lineHeightPx: number;
  segments: SyntheticTextBoxSegment[];
}

interface SyntheticTextBoxFrameStyle {
  backgroundColor?: string;
  borderColor?: string;
  borderWidthPx: number;
  paddingLeftPx: number;
  paddingTopPx: number;
  paddingRightPx: number;
  paddingBottomPx: number;
}

function emuToPixels(value: number | undefined): number | undefined {
  if (!Number.isFinite(value)) {
    return undefined;
  }

  return Math.max(0, Number(((value as number) / 9525).toFixed(3)));
}

function resolveSyntheticTextBoxFieldText(
  rawText: string,
  fieldKind: PageFieldKind | undefined,
  styleRefTarget: string | undefined,
  pageNumber: number | undefined,
  totalPages: number | undefined,
  pageNumberFormat: string | undefined,
  resolveStyleRefFieldValue?: (target: string) => string | undefined
): string {
  if (fieldKind === "PAGE") {
    const resolvedFieldValue =
      Number.isFinite(pageNumber) && (pageNumber as number) > 0
        ? Math.max(1, Math.round(pageNumber as number))
        : undefined;
    if (Number.isFinite(resolvedFieldValue)) {
      const leadingWhitespace = rawText.match(/^\s*/)?.[0] ?? "";
      const trailingWhitespace = rawText.match(/\s*$/)?.[0] ?? "";
      return `${leadingWhitespace}${formatPageFieldValue(
        Math.round(resolvedFieldValue as number),
        pageNumberFormat
      )}${trailingWhitespace}`;
    }
  }

  if (fieldKind === "NUMPAGES") {
    const resolvedFieldValue =
      Number.isFinite(totalPages) && (totalPages as number) > 0
        ? Math.max(1, Math.round(totalPages as number))
        : undefined;
    if (Number.isFinite(resolvedFieldValue)) {
      const leadingWhitespace = rawText.match(/^\s*/)?.[0] ?? "";
      const trailingWhitespace = rawText.match(/\s*$/)?.[0] ?? "";
      return `${leadingWhitespace}${Math.round(
        resolvedFieldValue as number
      )}${trailingWhitespace}`;
    }
  }

  if (styleRefTarget) {
    const resolved = resolveStyleRefFieldValue?.(styleRefTarget)?.trim();
    if (resolved) {
      const leadingWhitespace = rawText.match(/^\s*/)?.[0] ?? "";
      const trailingWhitespace = rawText.match(/\s*$/)?.[0] ?? "";
      return `${leadingWhitespace}${resolved}${trailingWhitespace}`;
    }
  }

  return rawText;
}

function syntheticTextBoxParagraphsFromRunXml(
  runXml: string,
  pageNumber?: number,
  totalPages?: number,
  pageNumberFormat?: string,
  resolveStyleRefFieldValue?: (target: string) => string | undefined
): SyntheticTextBoxParagraph[] {
  const textBoxXml = balancedTagXmlBlocks(runXml, "w:txbxContent")[0];
  if (!textBoxXml) {
    return [];
  }

  const resolved: SyntheticTextBoxParagraph[] = [];
  for (const paragraphXml of balancedTagXmlBlocks(textBoxXml, "w:p")) {
    const paragraphPropertiesXml =
      balancedTagXmlBlocks(paragraphXml, "w:pPr")[0] ??
      paragraphXml.match(/<w:pPr\b[^>]*\/?>/i)?.[0] ??
      "";
    const paragraphRunPropertiesXml =
      balancedTagXmlBlocks(paragraphPropertiesXml, "w:rPr")[0] ??
      paragraphPropertiesXml.match(/<w:rPr\b[^>]*\/?>/i)?.[0] ??
      "";
    const paragraphStyle = parseRunStyleFromRunXml(
      `<w:r>${paragraphRunPropertiesXml}</w:r>`
    );
    const spacingTag =
      paragraphPropertiesXml.match(/<w:spacing\b[^>]*\/?>/i)?.[0] ?? "";
    const lineRaw = spacingTag
      ? Number(xmlAttribute(spacingTag, "w:line"))
      : Number.NaN;
    const lineRule = spacingTag
      ? xmlAttribute(spacingTag, "w:lineRule")?.trim().toLowerCase()
      : undefined;
    const lineHeightPx =
      Number.isFinite(lineRaw) &&
      (lineRaw as number) > 0 &&
      lineRule === "exact"
        ? Math.max(1, twipsToPixels(lineRaw as number) ?? 1)
        : undefined;

    const segments: SyntheticTextBoxSegment[] = [];
    const fieldStack: Array<{
      pageFieldKind?: PageFieldKind;
      styleRefTarget?: string;
      inResult: boolean;
      instructionStyle?: TextRunNode["style"];
    }> = [];

    const appendSegment = (
      text: string,
      style?: TextRunNode["style"]
    ): void => {
      if (!text) {
        return;
      }
      const previous = segments[segments.length - 1];
      if (
        previous &&
        JSON.stringify(previous.style ?? {}) === JSON.stringify(style ?? {})
      ) {
        previous.text += text;
        return;
      }
      segments.push({ text, style });
    };

    for (const runBlockXml of balancedTagXmlBlocks(paragraphXml, "w:r")) {
      const runStyle = mergeTextRunStyles(
        paragraphStyle,
        parseRunStyleFromRunXml(runBlockXml)
      );

      const beginCount =
        runBlockXml.match(/<w:fldChar\b[^>]*\bw:fldCharType="begin"[^>]*\/?>/gi)
          ?.length ?? 0;
      for (let index = 0; index < beginCount; index += 1) {
        fieldStack.push({ inResult: false });
      }

      for (const instructionMatch of runBlockXml.matchAll(
        /<w:instrText\b[^>]*>([\s\S]*?)<\/w:instrText>/gi
      )) {
        const pageFieldKind = instructionTextToPageFieldKind(
          instructionMatch[1] ?? ""
        );
        const styleRefTarget = instructionTextToStyleRefTarget(
          instructionMatch[1] ?? ""
        );
        if ((!pageFieldKind && !styleRefTarget) || fieldStack.length === 0) {
          continue;
        }

        for (
          let stackIndex = fieldStack.length - 1;
          stackIndex >= 0;
          stackIndex -= 1
        ) {
          if (
            !fieldStack[stackIndex].pageFieldKind &&
            !fieldStack[stackIndex].styleRefTarget
          ) {
            fieldStack[stackIndex].pageFieldKind = pageFieldKind;
            fieldStack[stackIndex].styleRefTarget = styleRefTarget;
            fieldStack[stackIndex].instructionStyle = mergeTextRunStyles(
              fieldStack[stackIndex].instructionStyle,
              runStyle
            );
            break;
          }
        }
      }

      const separateCount =
        runBlockXml.match(
          /<w:fldChar\b[^>]*\bw:fldCharType="separate"[^>]*\/?>/gi
        )?.length ?? 0;
      for (let index = 0; index < separateCount; index += 1) {
        for (
          let stackIndex = fieldStack.length - 1;
          stackIndex >= 0;
          stackIndex -= 1
        ) {
          if (!fieldStack[stackIndex].inResult) {
            fieldStack[stackIndex].inResult = true;
            break;
          }
        }
      }

      const activeField = (() => {
        for (
          let stackIndex = fieldStack.length - 1;
          stackIndex >= 0;
          stackIndex -= 1
        ) {
          const stackEntry = fieldStack[stackIndex];
          if (
            stackEntry.inResult &&
            (stackEntry.pageFieldKind || stackEntry.styleRefTarget)
          ) {
            return stackEntry;
          }
        }
        return undefined;
      })();

      for (const textMatch of runBlockXml.matchAll(
        /<w:t\b[^>]*>([\s\S]*?)<\/w:t>/gi
      )) {
        const rawText = decodeXmlText(textMatch[1] ?? "");
        const resolvedText = activeField
          ? resolveSyntheticTextBoxFieldText(
              rawText,
              activeField.pageFieldKind,
              activeField.styleRefTarget,
              pageNumber,
              totalPages,
              pageNumberFormat,
              resolveStyleRefFieldValue
            )
          : rawText;
        appendSegment(
          resolvedText,
          activeField?.instructionStyle
            ? mergeTextRunStyles(activeField.instructionStyle, runStyle)
            : runStyle
        );
      }

      const endCount =
        runBlockXml.match(/<w:fldChar\b[^>]*\bw:fldCharType="end"[^>]*\/?>/gi)
          ?.length ?? 0;
      for (let index = 0; index < endCount; index += 1) {
        fieldStack.pop();
      }
    }

    if (segments.length === 0) {
      continue;
    }

    const effectiveFontSizePt =
      segments.find((segment) => segment.style?.fontSizePt)?.style
        ?.fontSizePt ?? 12;
    resolved.push({
      align: parseParagraphAlignmentFromXml(paragraphPropertiesXml),
      lineHeightPx:
        lineHeightPx ??
        Math.max(
          14,
          Math.round((((effectiveFontSizePt ?? 12) * 96) / 72) * 1.24)
        ),
      segments,
    });
  }

  return resolved;
}

function syntheticTextBoxFrameStyleFromRunXml(
  runXml: string
): SyntheticTextBoxFrameStyle {
  const bodyPrTag =
    balancedTagXmlBlocks(runXml, "wps:bodyPr")[0] ??
    runXml.match(/<wps:bodyPr\b[^>]*\/?>/i)?.[0] ??
    "";
  const lineTag =
    balancedTagXmlBlocks(runXml, "a:ln")[0] ??
    runXml.match(/<a:ln\b[\s\S]*?<\/a:ln>/i)?.[0] ??
    runXml.match(/<a:ln\b[^>]*\/?>/i)?.[0] ??
    "";
  const shapePropsXml =
    balancedTagXmlBlocks(runXml, "wps:spPr")[0] ??
    runXml.match(/<wps:spPr\b[\s\S]*?<\/wps:spPr>/i)?.[0] ??
    "";

  const hasNoFill =
    /<a:noFill\b/i.test(shapePropsXml) || /<wps:noFill\b/i.test(shapePropsXml);
  const lineHasNoFill = /<a:noFill\b/i.test(lineTag);
  const fillColor =
    shapePropsXml.match(
      /<a:solidFill>\s*<a:srgbClr\b[^>]*val="([^"]+)"/i
    )?.[1] ??
    shapePropsXml.match(
      /<a:solidFill>\s*<a:schemeClr\b[^>]*val="([^"]+)"/i
    )?.[1];
  const lineColor =
    lineTag.match(/<a:solidFill>\s*<a:srgbClr\b[^>]*val="([^"]+)"/i)?.[1] ??
    lineTag.match(/<a:solidFill>\s*<a:schemeClr\b[^>]*val="([^"]+)"/i)?.[1];
  const lineWidthEmu = Number(xmlAttribute(lineTag, "w"));
  const resolvedBorderWidthPx =
    !lineTag || lineHasNoFill
      ? 0
      : Number.isFinite(lineWidthEmu) && (lineWidthEmu as number) > 0
      ? Math.max(1, Math.round((lineWidthEmu as number) / 9525))
      : 0;

  return {
    backgroundColor: !hasNoFill && fillColor ? `#${fillColor}` : undefined,
    borderColor:
      resolvedBorderWidthPx > 0 && lineColor ? `#${lineColor}` : undefined,
    borderWidthPx: resolvedBorderWidthPx,
    paddingLeftPx: emuToPixels(Number(xmlAttribute(bodyPrTag, "lIns"))) ?? 6,
    paddingTopPx: emuToPixels(Number(xmlAttribute(bodyPrTag, "tIns"))) ?? 3,
    paddingRightPx: emuToPixels(Number(xmlAttribute(bodyPrTag, "rIns"))) ?? 6,
    paddingBottomPx: emuToPixels(Number(xmlAttribute(bodyPrTag, "bIns"))) ?? 3,
  };
}

function syntheticTextBoxTextValue(image: ImageRunNode): string | undefined {
  const explicit = image.textBoxText;
  if (typeof explicit === "string") {
    return explicit;
  }

  const paragraphs = image.sourceXml
    ? syntheticTextBoxParagraphsFromRunXml(image.sourceXml)
    : [];
  if (paragraphs.length === 0) {
    return undefined;
  }

  return paragraphs
    .map((paragraph) =>
      paragraph.segments.map((segment) => segment.text).join("")
    )
    .join("\n");
}

function resolveSyntheticTextBoxParagraphs(
  image: ImageRunNode,
  pageNumber?: number,
  totalPages?: number,
  pageNumberFormat?: string,
  resolveStyleRefFieldValue?: (target: string) => string | undefined
): SyntheticTextBoxParagraph[] {
  const baseParagraphs = image.sourceXml
    ? syntheticTextBoxParagraphsFromRunXml(
        image.sourceXml,
        pageNumber,
        totalPages,
        pageNumberFormat,
        resolveStyleRefFieldValue
      )
    : [];
  const explicitText = image.textBoxText;
  if (typeof explicitText !== "string") {
    return baseParagraphs;
  }

  const lines = explicitText.split(/\r?\n/);
  if (lines.length === 0) {
    return baseParagraphs;
  }

  return lines.map((line, index) => {
    const baseParagraph = baseParagraphs[index] ?? baseParagraphs[0];
    const baseStyle = baseParagraph?.segments[0]?.style;
    return {
      align: baseParagraph?.align,
      lineHeightPx: baseParagraph?.lineHeightPx ?? 18,
      segments: [{ text: line, style: baseStyle }],
    };
  });
}

function syntheticTextBoxForegroundZIndex(image: ImageRunNode): number {
  const normalizedZIndex = Number.isFinite(image.floating?.zIndex)
    ? Math.max(
        1,
        Math.min(
          65535,
          Math.round(
            (image.floating?.zIndex as number) / WORD_IMAGE_Z_INDEX_STEP
          )
        )
      )
    : 4;

  return Math.max(4, normalizedZIndex);
}

function syntheticTextBoxSvg(
  image: ImageRunNode,
  pageNumber?: number,
  totalPages?: number,
  pageNumberFormat?: string,
  resolveStyleRefFieldValue?: (target: string) => string | undefined
): string | undefined {
  if (!image.syntheticTextBox || !image.sourceXml) {
    return undefined;
  }

  // Grouped drawings that already import as a combined SVG image + textbox
  // should keep that original synthetic SVG. Rebuilding them here from only
  // the textbox XML drops the picture layer and regresses letterhead/logo art.
  if (syntheticTextBoxContainsPictureLayer(image)) {
    return undefined;
  }

  const paragraphs = resolveSyntheticTextBoxParagraphs(
    image,
    pageNumber,
    totalPages,
    pageNumberFormat,
    resolveStyleRefFieldValue
  );
  if (paragraphs.length === 0) {
    return undefined;
  }

  const safeWidth = Math.max(8, Math.round(image.widthPx ?? 320));
  const estimatedHeight = paragraphs.reduce(
    (sum, paragraph) => sum + paragraph.lineHeightPx,
    6
  );
  const safeHeight = Math.max(8, Math.round(image.heightPx ?? estimatedHeight));
  let cursorY = 0;
  const textBlocks: string[] = [];

  for (const paragraph of paragraphs) {
    const nextCursorY = cursorY + paragraph.lineHeightPx;
    const clampedBaselineY = Math.min(nextCursorY, Math.max(1, safeHeight - 1));
    if (nextCursorY > safeHeight + 1 && textBlocks.length > 0) {
      break;
    }
    cursorY = nextCursorY;

    const fullText = paragraph.segments.map((segment) => segment.text).join("");
    const baseStyle = paragraph.segments.find(
      (segment) => segment.style
    )?.style;
    const baseFontSizePx = Math.max(
      10,
      Math.round(((baseStyle?.fontSizePt ?? 12) * 96) / 72)
    );
    const estimatedTextWidth = Math.max(
      1,
      estimateTextAdvanceWidthPx(fullText, baseStyle)
    );
    const fitScale =
      estimatedTextWidth > safeWidth && estimatedTextWidth > 0
        ? safeWidth / estimatedTextWidth
        : 1;
    const textAlign = paragraph.align ?? "left";
    const anchor =
      textAlign === "center"
        ? "middle"
        : textAlign === "right"
        ? "end"
        : "start";
    const x =
      textAlign === "center"
        ? Math.round(safeWidth / 2)
        : textAlign === "right"
        ? safeWidth
        : 0;
    let segmentOffsetPx = 0;
    const tspans = paragraph.segments
      .map((segment, segmentIndex) => {
        const segmentStyle = mergeTextRunStyles(baseStyle, segment.style);
        const segmentFontSizePx = Math.max(
          10,
          Math.round((((segmentStyle?.fontSizePt ?? 12) * 96) / 72) * fitScale)
        );
        const textDecoration = [
          segmentStyle?.underline ? "underline" : "",
          segmentStyle?.strike ? "line-through" : "",
        ]
          .filter(Boolean)
          .join(" ");
        const attrs = [
          `font-size="${segmentFontSizePx}"`,
          segmentStyle?.fontFamily
            ? `font-family="${escapeSvgText(segmentStyle.fontFamily)}"`
            : "",
          segmentStyle?.bold ? 'font-weight="700"' : "",
          segmentStyle?.italic ? 'font-style="italic"' : "",
          segmentStyle?.color
            ? `fill="${segmentStyle.color}"`
            : 'fill="#000000"',
          textDecoration ? `text-decoration="${textDecoration}"` : "",
        ]
          .filter(Boolean)
          .join(" ");
        const xAttr =
          textAlign === "left" && segmentIndex > 0
            ? ` dx="${Math.round(segmentOffsetPx)}"`
            : segmentIndex > 0
            ? ' dx="0"'
            : "";
        segmentOffsetPx = 0;
        return `<tspan${xAttr} ${attrs}>${escapeSvgText(segment.text)}</tspan>`;
      })
      .join("");

    if (textAlign === "left") {
      let runningWidth = 0;
      const leftTspans = paragraph.segments
        .map((segment) => {
          const segmentStyle = mergeTextRunStyles(baseStyle, segment.style);
          const segmentFontSizePx = Math.max(
            10,
            Math.round(
              (((segmentStyle?.fontSizePt ?? 12) * 96) / 72) * fitScale
            )
          );
          const textDecoration = [
            segmentStyle?.underline ? "underline" : "",
            segmentStyle?.strike ? "line-through" : "",
          ]
            .filter(Boolean)
            .join(" ");
          const attrs = [
            `x="${Math.round(runningWidth)}"`,
            `font-size="${segmentFontSizePx}"`,
            segmentStyle?.fontFamily
              ? `font-family="${escapeSvgText(segmentStyle.fontFamily)}"`
              : "",
            segmentStyle?.bold ? 'font-weight="700"' : "",
            segmentStyle?.italic ? 'font-style="italic"' : "",
            segmentStyle?.color
              ? `fill="${segmentStyle.color}"`
              : 'fill="#000000"',
            textDecoration ? `text-decoration="${textDecoration}"` : "",
          ]
            .filter(Boolean)
            .join(" ");
          const block = `<tspan ${attrs}>${escapeSvgText(
            segment.text
          )}</tspan>`;
          runningWidth +=
            estimateTextAdvanceWidthPx(segment.text, segmentStyle) * fitScale;
          return block;
        })
        .join("");
      textBlocks.push(
        `<text y="${Math.round(
          clampedBaselineY
        )}" text-anchor="${anchor}">${leftTspans}</text>`
      );
      continue;
    }

    textBlocks.push(
      `<text x="${x}" y="${Math.round(
        clampedBaselineY
      )}" text-anchor="${anchor}">${tspans}</text>`
    );
  }

  return svgDataUri(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${safeWidth}" height="${safeHeight}" viewBox="0 0 ${safeWidth} ${safeHeight}">${textBlocks.join(
      ""
    )}</svg>`
  );
}

function syntheticTextBoxContainsPictureLayer(image: ImageRunNode): boolean {
  if (!image.syntheticTextBox || !image.sourceXml) {
    return false;
  }

  if (/<pic:pic\b|<a:blip\b/i.test(image.sourceXml)) {
    return true;
  }

  if (!/<wpg:wgp\b/i.test(image.sourceXml)) {
    return false;
  }

  const groupedShapes = balancedTagXmlBlocks(image.sourceXml, "wps:wsp");
  return groupedShapes.some((shapeXml) => {
    const shapePropertiesXml =
      balancedTagXmlBlocks(shapeXml, "wps:spPr")[0] ?? "";
    if (!shapePropertiesXml || /<w:txbxContent\b/i.test(shapeXml)) {
      return false;
    }

    const lineXml = balancedTagXmlBlocks(shapePropertiesXml, "a:ln")[0] ?? "";
    return (
      /<a:solidFill\b|<a:gradFill\b|<a:blipFill\b/i.test(shapePropertiesXml) ||
      (Boolean(lineXml) && !/<a:noFill\b/i.test(lineXml))
    );
  });
}

function parseDrawingImageTransformFromSourceXml(sourceXml?: string):
  | {
      rotationDegrees?: number;
      flipH?: boolean;
      flipV?: boolean;
    }
  | undefined {
  if (!sourceXml) {
    return undefined;
  }

  const transformXml =
    balancedTagXmlBlocks(sourceXml, "a:xfrm")[0] ??
    sourceXml.match(/<a:xfrm\b[^>]*\/?>/i)?.[0] ??
    "";
  if (!transformXml) {
    return undefined;
  }

  const rotationRaw = Number(xmlAttribute(transformXml, "rot"));
  const rotationDegrees = Number.isFinite(rotationRaw)
    ? Number(((rotationRaw as number) / 60000).toFixed(3))
    : undefined;
  const flipHRaw = xmlAttribute(transformXml, "flipH")?.trim().toLowerCase();
  const flipVRaw = xmlAttribute(transformXml, "flipV")?.trim().toLowerCase();
  const flipH = flipHRaw === "1" || flipHRaw === "true";
  const flipV = flipVRaw === "1" || flipVRaw === "true";

  if (!Number.isFinite(rotationDegrees) && !flipH && !flipV) {
    return undefined;
  }

  return {
    rotationDegrees,
    flipH: flipH || undefined,
    flipV: flipV || undefined,
  };
}

function joinCssTransforms(
  ...parts: Array<string | undefined>
): string | undefined {
  const resolved = parts.filter((part): part is string =>
    Boolean(part && part.trim().length > 0)
  );
  return resolved.length > 0 ? resolved.join(" ") : undefined;
}

function resolveImageRenderTransformStyle(
  image: ImageRunNode,
  options?: {
    frameWidthPx?: number;
    frameHeightPx?: number;
    fillFrame?: boolean;
    baseTransform?: string;
  }
): React.CSSProperties {
  const transform = parseDrawingImageTransformFromSourceXml(image.sourceXml);
  const rotationDegrees = transform?.rotationDegrees;
  const frameWidthPx = Number.isFinite(options?.frameWidthPx)
    ? Math.max(1, Math.round(options?.frameWidthPx as number))
    : undefined;
  const frameHeightPx = Number.isFinite(options?.frameHeightPx)
    ? Math.max(1, Math.round(options?.frameHeightPx as number))
    : undefined;
  const fillFrame = options?.fillFrame === true;
  const normalizedQuarterTurn =
    Number.isFinite(rotationDegrees) &&
    Math.abs((Math.abs(rotationDegrees as number) % 180) - 90) < 0.5
      ? (((Math.round((rotationDegrees as number) / 90) % 4) + 4) % 4) * 90
      : undefined;
  const flipScaleTransform =
    transform?.flipH || transform?.flipV
      ? `scale(${transform?.flipH ? -1 : 1}, ${transform?.flipV ? -1 : 1})`
      : undefined;

  if (
    fillFrame &&
    Number.isFinite(frameWidthPx) &&
    Number.isFinite(frameHeightPx) &&
    Number.isFinite(normalizedQuarterTurn)
  ) {
    const safeFrameWidthPx = frameWidthPx as number;
    const safeFrameHeightPx = frameHeightPx as number;
    const rotationTransform =
      normalizedQuarterTurn === 90
        ? `translate(${safeFrameWidthPx}px, 0px) rotate(90deg)`
        : normalizedQuarterTurn === 180
        ? `translate(${safeFrameWidthPx}px, ${safeFrameHeightPx}px) rotate(180deg)`
        : normalizedQuarterTurn === 270
        ? `translate(0px, ${safeFrameHeightPx}px) rotate(-90deg)`
        : undefined;

    return {
      width: `${safeFrameHeightPx}px`,
      height: `${safeFrameWidthPx}px`,
      maxWidth: "none",
      transformOrigin: "top left",
      transform: joinCssTransforms(
        options?.baseTransform,
        rotationTransform,
        flipScaleTransform
      ),
    };
  }

  const rotationTransform =
    Number.isFinite(rotationDegrees) &&
    Math.abs(rotationDegrees as number) >= 0.01
      ? `rotate(${rotationDegrees}deg)`
      : undefined;
  const transformValue = joinCssTransforms(
    options?.baseTransform,
    rotationTransform,
    flipScaleTransform
  );
  if (!transformValue) {
    return {};
  }

  return {
    transformOrigin: "center center",
    transform: transformValue,
  };
}

function syntheticTextBoxActsAsTopAndBottomMasthead(
  image: ImageRunNode
): boolean {
  if (!image.syntheticTextBox || !image.sourceXml) {
    return false;
  }

  return (
    /<wpg:wgp\b/i.test(image.sourceXml) && /<wps:wsp\b/i.test(image.sourceXml)
  );
}

function summarizeChangeFeatures(
  prefix: string,
  features: string[],
  fallback: string
): string {
  if (features.length === 0) {
    return fallback;
  }

  const unique = Array.from(new Set(features));
  return `${prefix}: ${unique.join(", ")}`;
}

function summarizeRunFormattingChange(changeXml: string): string {
  const features: string[] = [];
  if (/<w:rFonts\b/i.test(changeXml)) {
    features.push("font");
  }
  if (/<w:sz\b/i.test(changeXml)) {
    features.push("size");
  }
  if (/<w:color\b/i.test(changeXml)) {
    features.push("color");
  }
  if (/<w:highlight\b/i.test(changeXml)) {
    features.push("highlight");
  }
  if (/<w:b(?:Cs)?\b/i.test(changeXml)) {
    features.push("bold");
  }
  if (/<w:i(?:Cs)?\b/i.test(changeXml)) {
    features.push("italic");
  }
  if (/<w:u\b/i.test(changeXml)) {
    features.push("underline");
  }
  if (/<w:strike\b/i.test(changeXml)) {
    features.push("strikethrough");
  }
  if (/<w:vertAlign\b/i.test(changeXml)) {
    features.push("baseline");
  }
  return summarizeChangeFeatures("Run formatting", features, "Run formatting");
}

function summarizeParagraphFormattingChange(changeXml: string): string {
  const features: string[] = [];
  if (/<w:ind\b/i.test(changeXml)) {
    features.push("margins/indent");
  }
  if (/<w:spacing\b/i.test(changeXml)) {
    features.push("line spacing");
  }
  if (/<w:jc\b/i.test(changeXml)) {
    features.push("alignment");
  }
  if (/<w:tabs\b/i.test(changeXml)) {
    features.push("tabs");
  }
  if (/<w:numPr\b/i.test(changeXml)) {
    features.push("numbering");
  }
  if (/<w:pBdr\b/i.test(changeXml)) {
    features.push("borders");
  }
  if (/<w:shd\b/i.test(changeXml)) {
    features.push("shading");
  }
  if (/<w:rPr\b/i.test(changeXml)) {
    features.push("text style");
  }
  return summarizeChangeFeatures(
    "Paragraph formatting",
    features,
    "Paragraph formatting"
  );
}

function summarizeTableFormattingChange(
  scope: "table" | "row" | "cell",
  changeXml: string
): string {
  const features: string[] = [];
  if (/<w:tblW\b|<w:tcW\b|<w:gridSpan\b/i.test(changeXml)) {
    features.push("width");
  }
  if (/<w:tblLayout\b/i.test(changeXml)) {
    features.push("layout");
  }
  if (/<w:tblInd\b|<w:ind\b/i.test(changeXml)) {
    features.push("indent");
  }
  if (/<w:tblCellMar\b|<w:tcMar\b/i.test(changeXml)) {
    features.push("margins");
  }
  if (/<w:(?:tblBorders|tcBorders|trBorders|pBdr)\b/i.test(changeXml)) {
    features.push("borders");
  }
  if (/<w:trHeight\b/i.test(changeXml)) {
    features.push("row height");
  }
  if (/<w:vAlign\b/i.test(changeXml)) {
    features.push("vertical align");
  }
  if (/<w:jc\b/i.test(changeXml)) {
    features.push("alignment");
  }
  if (/<w:shd\b/i.test(changeXml)) {
    features.push("shading");
  }

  const prefix =
    scope === "table"
      ? "Table formatting"
      : scope === "row"
      ? "Row formatting"
      : "Cell formatting";
  return summarizeChangeFeatures(prefix, features, prefix);
}

function resolveParagraphTrackedMarkup(
  paragraph: ParagraphNode
): ParagraphTrackedMarkup | undefined {
  const sourceXml = paragraph.sourceXml ?? "";
  if (!sourceXml) {
    return undefined;
  }

  const cached = paragraphTrackedMarkupBySourceXml.get(sourceXml);
  if (cached !== undefined) {
    return cached ?? undefined;
  }

  const revisionRanges: RevisionTagRange[] = [];
  for (const tagName of ["w:ins", "w:del", "w:moveFrom", "w:moveTo"] as const) {
    const kind = trackedChangeKindFromTagName(tagName);
    if (!kind) {
      continue;
    }

    extractBalancedTagRanges(sourceXml, tagName).forEach((range) => {
      revisionRanges.push({
        ...range,
        kind,
        revisionId: xmlAttribute(range.openTag, "w:id"),
        author: decodeXmlText(xmlAttribute(range.openTag, "w:author") ?? ""),
        date: xmlAttribute(range.openTag, "w:date"),
      });
    });
  }

  const inlineChangeByVisibleChildIndex: Array<
    ParagraphTrackedInlineChange | undefined
  > = [];
  const deletedSegmentsByVisibleChildIndex = new Map<
    number,
    ParagraphTrackedDeletionSegment[]
  >();
  const changes: ParagraphTrackedInlineChange[] = [];
  const changeByKey = new Map<string, ParagraphTrackedInlineChange>();
  let anonymousChangeCounter = 0;
  let visibleChildIndex = 0;

  const getOrCreateChange = (
    kind: DocxTrackedChangeKind,
    revisionId: string | undefined,
    author: string | undefined,
    date: string | undefined,
    text: string | undefined
  ): ParagraphTrackedInlineChange => {
    const normalizedAuthor = author?.trim() || undefined;
    const normalizedDate = date?.trim() || undefined;
    const normalizedText = normalizeTrackedChangeSnippet(text);
    const normalizedRevisionId = revisionId?.trim() || undefined;
    // Word groups tracked items primarily by revision id. Keep a single card per
    // revision id/kind to avoid over-fragmenting changes into many tiny entries.
    const key = normalizedRevisionId
      ? `${kind}:id:${normalizedRevisionId}`
      : `${kind}:anon:${normalizedAuthor ?? ""}:${normalizedDate ?? ""}:${
          normalizedText ?? ""
        }`;
    const existing = changeByKey.get(key);
    if (existing) {
      if (!existing.text && normalizedText) {
        existing.text = normalizedText;
      } else if (
        existing.text &&
        normalizedText &&
        normalizedText.length > existing.text.length
      ) {
        existing.text = normalizedText;
      }
      if (!existing.author && normalizedAuthor) {
        existing.author = normalizedAuthor;
      }
      if (!existing.date && normalizedDate) {
        existing.date = normalizedDate;
      }
      return existing;
    }

    const stableId = normalizedRevisionId
      ? `${kind}-${normalizedRevisionId}`
      : `${kind}-inline-${anonymousChangeCounter}`;
    anonymousChangeCounter += 1;
    const next: ParagraphTrackedInlineChange = {
      id: stableId,
      kind,
      author: normalizedAuthor,
      date: normalizedDate,
      text: normalizedText,
    };
    changeByKey.set(key, next);
    changes.push(next);
    return next;
  };

  const runPattern = /<w:r\b[\s\S]*?<\/w:r>/gi;
  for (const runMatch of sourceXml.matchAll(runPattern)) {
    const runXml = runMatch[0] ?? "";
    if (!runXml) {
      continue;
    }

    const runStart = runMatch.index ?? 0;
    const runEnd = runStart + runXml.length;
    const enclosingRevision = revisionRanges
      .filter(
        (revisionRange) =>
          runStart >= revisionRange.start && runEnd <= revisionRange.end
      )
      .sort(
        (left, right) => left.end - left.start - (right.end - right.start)
      )[0];

    const revisionKind = enclosingRevision?.kind;
    const revisionId = enclosingRevision?.revisionId;
    const revisionAuthor = enclosingRevision?.author;
    const revisionDate = enclosingRevision?.date;
    const isDeletionLike =
      revisionKind === "deletion" || revisionKind === "move-from";
    const contentRunXml = stripTextBoxContentFromRunXml(runXml);
    const trackedRunStyle = parseRunStyleFromRunXml(contentRunXml);
    const visibleTokens = parseTrackedRunTokens(contentRunXml, false);
    const deletedTokens = parseTrackedRunTokens(contentRunXml, true);
    const hasImage = /<w:(?:drawing|pict)\b/i.test(runXml);
    const visibleChildCount =
      visibleTokens.filter((token) => token.text.length > 0 || token.isNote)
        .length + (hasImage ? 1 : 0);
    const visibleText = normalizeTrackedChangeSnippet(
      visibleTokens.map((token) => token.text).join("")
    );
    const deletedText = normalizeTrackedChangeSnippet(
      deletedTokens.map((token) => token.text).join("")
    );

    if (isDeletionLike) {
      const deletionSnippet = deletedText ?? (hasImage ? "[image]" : undefined);
      if (!deletionSnippet) {
        continue;
      }
      const change = getOrCreateChange(
        revisionKind,
        revisionId,
        revisionAuthor,
        revisionDate,
        deletionSnippet
      );
      if (deletedText) {
        const segments =
          deletedSegmentsByVisibleChildIndex.get(visibleChildIndex) ?? [];
        segments.push({
          text: deletedText,
          change,
          style: trackedRunStyle,
        });
        deletedSegmentsByVisibleChildIndex.set(visibleChildIndex, segments);
      }
      continue;
    }

    const rPrChangeRanges = extractBalancedTagRanges(runXml, "w:rPrChange");
    rPrChangeRanges.forEach((rPrChangeRange) => {
      const rPrChangeTag = rPrChangeRange.openTag ?? "";
      const rPrChangeXml = runXml.slice(
        rPrChangeRange.start,
        rPrChangeRange.end
      );
      const formatAuthor =
        decodeXmlText(xmlAttribute(rPrChangeTag, "w:author") ?? "") ||
        revisionAuthor;
      const formatDate = xmlAttribute(rPrChangeTag, "w:date") ?? revisionDate;
      const formatId = xmlAttribute(rPrChangeTag, "w:id") ?? revisionId;
      const formatSnippet = summarizeRunFormattingChange(rPrChangeXml);
      getOrCreateChange(
        "format-change",
        formatId,
        formatAuthor,
        formatDate,
        formatSnippet
      );
    });

    if (revisionKind === "insertion" || revisionKind === "move-to") {
      const insertionSnippet =
        visibleText ?? (hasImage ? "[image]" : undefined);
      if (!insertionSnippet) {
        visibleChildIndex += visibleChildCount;
        continue;
      }
      const inlineChange = getOrCreateChange(
        revisionKind,
        revisionId,
        revisionAuthor,
        revisionDate,
        insertionSnippet
      );
      for (let index = 0; index < visibleChildCount; index += 1) {
        inlineChangeByVisibleChildIndex[visibleChildIndex + index] =
          inlineChange;
      }
    }

    visibleChildIndex += visibleChildCount;
  }

  const pPrChangeRanges = extractBalancedTagRanges(sourceXml, "w:pPrChange");
  pPrChangeRanges.forEach((pPrChangeRange) => {
    const pPrChangeTag = pPrChangeRange.openTag ?? "";
    const pPrChangeXml = sourceXml.slice(
      pPrChangeRange.start,
      pPrChangeRange.end
    );
    getOrCreateChange(
      "paragraph-format-change",
      xmlAttribute(pPrChangeTag, "w:id"),
      decodeXmlText(xmlAttribute(pPrChangeTag, "w:author") ?? ""),
      xmlAttribute(pPrChangeTag, "w:date"),
      summarizeParagraphFormattingChange(pPrChangeXml)
    );
  });

  // Some DOCX revisions wrap non-run content. Ensure every revision range is
  // represented even if the run-based pass above does not emit it.
  revisionRanges.forEach((revisionRange) => {
    const revisionXml = sourceXml.slice(revisionRange.start, revisionRange.end);
    const includeDeletedText =
      revisionRange.kind === "deletion" || revisionRange.kind === "move-from";
    const revisionTokens = parseTrackedRunTokens(
      stripTextBoxContentFromRunXml(revisionXml),
      includeDeletedText
    );
    const revisionText = normalizeTrackedChangeSnippet(
      revisionTokens.map((token) => token.text).join("")
    );
    const hasImage = /<w:(?:drawing|pict)\b/i.test(revisionXml);
    const revisionSnippet = revisionText ?? (hasImage ? "[image]" : undefined);
    if (!revisionSnippet) {
      return;
    }
    getOrCreateChange(
      revisionRange.kind,
      revisionRange.revisionId,
      revisionRange.author,
      revisionRange.date,
      revisionSnippet
    );
  });

  const hasInlineChanges = inlineChangeByVisibleChildIndex.some(Boolean);
  const hasDeletedSegments = deletedSegmentsByVisibleChildIndex.size > 0;
  if (!hasInlineChanges && !hasDeletedSegments && changes.length === 0) {
    setCacheEntry(paragraphTrackedMarkupBySourceXml, sourceXml, null);
    return undefined;
  }

  const resolved: ParagraphTrackedMarkup = {
    inlineChangeByVisibleChildIndex,
    deletedSegmentsByVisibleChildIndex,
    changes,
  };
  setCacheEntry(paragraphTrackedMarkupBySourceXml, sourceXml, resolved);
  return resolved;
}

/**
 * Maps comment ranges (`commentRangeStart`/`commentRangeEnd`) to the
 * paragraph's visible child indexes using the same run accounting as
 * `resolveParagraphTrackedMarkup`, so the run renderer can highlight
 * commented content at the matching child cursor.
 */
function resolveParagraphCommentMarkup(
  paragraph: ParagraphNode
): ParagraphCommentMarkup | undefined {
  const sourceXml = paragraph.sourceXml ?? "";
  if (!sourceXml || !/commentRange|commentReference/i.test(sourceXml)) {
    return undefined;
  }

  const cached = paragraphCommentMarkupBySourceXml.get(sourceXml);
  if (cached !== undefined) {
    return cached ?? undefined;
  }

  const rangeStartById = new Map<number, number>();
  const rangeEndById = new Map<number, number>();
  for (const match of sourceXml.matchAll(
    /<w:commentRangeStart\b[^>]*w:id="(-?\d+)"[^>]*\/?>/gi
  )) {
    const commentId = Number.parseInt(match[1] ?? "", 10);
    if (Number.isFinite(commentId) && match.index !== undefined) {
      rangeStartById.set(commentId, match.index + match[0].length);
    }
  }
  for (const match of sourceXml.matchAll(
    /<w:commentRangeEnd\b[^>]*w:id="(-?\d+)"[^>]*\/?>/gi
  )) {
    const commentId = Number.parseInt(match[1] ?? "", 10);
    if (Number.isFinite(commentId) && match.index !== undefined) {
      rangeEndById.set(commentId, match.index);
    }
  }
  // Ranges may open in an earlier paragraph (start missing) or close in a
  // later one (end missing); treat the missing side as the paragraph edge.
  const ranges: Array<{ commentId: number; start: number; end: number }> = [];
  const rangeIds = new Set<number>([
    ...rangeStartById.keys(),
    ...rangeEndById.keys(),
  ]);
  rangeIds.forEach((commentId) => {
    const start = rangeStartById.get(commentId) ?? 0;
    const end = rangeEndById.get(commentId) ?? sourceXml.length;
    if (end > start) {
      ranges.push({ commentId, start, end });
    }
  });
  if (ranges.length === 0) {
    setCacheEntry(paragraphCommentMarkupBySourceXml, sourceXml, null);
    return undefined;
  }

  const commentIdsByVisibleChildIndex: Array<number[] | undefined> = [];
  let visibleChildIndex = 0;
  const runPattern = /<w:r\b[\s\S]*?<\/w:r>/gi;
  for (const runMatch of sourceXml.matchAll(runPattern)) {
    const runXml = runMatch[0] ?? "";
    if (!runXml) {
      continue;
    }

    const runStart = runMatch.index ?? 0;
    const contentRunXml = stripTextBoxContentFromRunXml(runXml);
    const visibleTokens = parseTrackedRunTokens(contentRunXml, false);
    const hasImage = /<w:(?:drawing|pict)\b/i.test(runXml);
    const visibleChildCount =
      visibleTokens.filter((token) => token.text.length > 0 || token.isNote)
        .length + (hasImage ? 1 : 0);
    if (visibleChildCount === 0) {
      continue;
    }

    const activeCommentIds = ranges
      .filter((range) => runStart >= range.start && runStart < range.end)
      .map((range) => range.commentId);
    if (activeCommentIds.length > 0) {
      for (let index = 0; index < visibleChildCount; index += 1) {
        commentIdsByVisibleChildIndex[visibleChildIndex + index] =
          activeCommentIds;
      }
    }
    visibleChildIndex += visibleChildCount;
  }

  if (commentIdsByVisibleChildIndex.length === 0) {
    setCacheEntry(paragraphCommentMarkupBySourceXml, sourceXml, null);
    return undefined;
  }

  const resolved: ParagraphCommentMarkup = { commentIdsByVisibleChildIndex };
  setCacheEntry(paragraphCommentMarkupBySourceXml, sourceXml, resolved);
  return resolved;
}

function instructionTextToPageFieldKind(
  rawInstruction: string
): PageFieldKind | undefined {
  const normalized = decodeXmlText(rawInstruction)
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
  if (!normalized || normalized.includes("PAGEREF")) {
    return undefined;
  }

  if (/\bNUMPAGES\b/.test(normalized)) {
    return "NUMPAGES";
  }
  if (/\bPAGE\b/.test(normalized)) {
    return "PAGE";
  }

  return undefined;
}

function instructionTextToStyleRefTarget(
  rawInstruction: string
): string | undefined {
  const normalized = decodeXmlText(rawInstruction).replace(/\s+/g, " ").trim();
  if (!normalized) {
    return undefined;
  }

  const match = normalized.match(/\bSTYLEREF\b\s+(?:"([^"]+)"|([^\s\\]+))/i);
  const target = (match?.[1] ?? match?.[2] ?? "").trim();
  return target.length > 0 ? target : undefined;
}

function paragraphPageFieldSequence(paragraph: ParagraphNode): PageFieldKind[] {
  const xml = paragraph.sourceXml ?? "";
  if (!xml) {
    return [];
  }

  const fields: PageFieldKind[] = [];
  for (const instructionMatch of xml.matchAll(
    /<w:instrText\b[^>]*>([\s\S]*?)<\/w:instrText>/gi
  )) {
    const kind = instructionTextToPageFieldKind(instructionMatch[1] ?? "");
    if (kind) {
      fields.push(kind);
    }
  }
  for (const simpleFieldMatch of xml.matchAll(
    /<w:fldSimple\b[^>]*\bw:instr="([^"]+)"[^>]*>/gi
  )) {
    const kind = instructionTextToPageFieldKind(simpleFieldMatch[1] ?? "");
    if (kind) {
      fields.push(kind);
    }
  }

  return fields;
}

const pageFieldValueSequenceBySourceXml = new Map<
  string,
  PageFieldValueToken[]
>();

function paragraphPageFieldValueSequence(
  paragraph: ParagraphNode
): PageFieldValueToken[] {
  const xml = paragraph.sourceXml ?? "";
  if (!xml) {
    return [];
  }

  const cached = pageFieldValueSequenceBySourceXml.get(xml);
  if (cached) {
    return cached;
  }

  const values: PageFieldValueToken[] = [];
  const fieldStack: Array<{ kind?: PageFieldKind; inResult: boolean }> = [];
  const tokenPattern =
    /<w:fldSimple\b[^>]*\bw:instr="([^"]+)"[^>]*>[\s\S]*?<\/w:fldSimple>|<w:r\b[\s\S]*?<\/w:r>/gi;

  for (const tokenMatch of xml.matchAll(tokenPattern)) {
    const tokenXml = tokenMatch[0] ?? "";
    if (!tokenXml) {
      continue;
    }

    if (/^<w:fldSimple\b/i.test(tokenXml)) {
      const kind = instructionTextToPageFieldKind(tokenMatch[1] ?? "");
      if (!kind) {
        continue;
      }

      for (const textMatch of tokenXml.matchAll(
        /<w:t\b[^>]*>([\s\S]*?)<\/w:t>/gi
      )) {
        values.push({
          kind,
          rawText: decodeXmlText(textMatch[1] ?? ""),
        });
      }
      continue;
    }

    const beginCount =
      tokenXml.match(/<w:fldChar\b[^>]*\bw:fldCharType="begin"[^>]*\/?>/gi)
        ?.length ?? 0;
    for (let index = 0; index < beginCount; index += 1) {
      fieldStack.push({ inResult: false });
    }

    for (const instructionMatch of tokenXml.matchAll(
      /<w:instrText\b[^>]*>([\s\S]*?)<\/w:instrText>/gi
    )) {
      const kind = instructionTextToPageFieldKind(instructionMatch[1] ?? "");
      if (!kind || fieldStack.length === 0) {
        continue;
      }

      let assigned = false;
      for (
        let stackIndex = fieldStack.length - 1;
        stackIndex >= 0;
        stackIndex -= 1
      ) {
        if (fieldStack[stackIndex].kind === undefined) {
          fieldStack[stackIndex].kind = kind;
          assigned = true;
          break;
        }
      }
      if (!assigned) {
        fieldStack[fieldStack.length - 1].kind = kind;
      }
    }

    const separateCount =
      tokenXml.match(/<w:fldChar\b[^>]*\bw:fldCharType="separate"[^>]*\/?>/gi)
        ?.length ?? 0;
    for (let index = 0; index < separateCount; index += 1) {
      for (
        let stackIndex = fieldStack.length - 1;
        stackIndex >= 0;
        stackIndex -= 1
      ) {
        if (!fieldStack[stackIndex].inResult) {
          fieldStack[stackIndex].inResult = true;
          break;
        }
      }
    }

    const activeFieldKind = (() => {
      for (
        let stackIndex = fieldStack.length - 1;
        stackIndex >= 0;
        stackIndex -= 1
      ) {
        const stackEntry = fieldStack[stackIndex];
        if (stackEntry.inResult && stackEntry.kind) {
          return stackEntry.kind;
        }
      }
      return undefined;
    })();

    if (activeFieldKind) {
      for (const textMatch of tokenXml.matchAll(
        /<w:t\b[^>]*>([\s\S]*?)<\/w:t>/gi
      )) {
        values.push({
          kind: activeFieldKind,
          rawText: decodeXmlText(textMatch[1] ?? ""),
        });
      }
    }

    const endCount =
      tokenXml.match(/<w:fldChar\b[^>]*\bw:fldCharType="end"[^>]*\/?>/gi)
        ?.length ?? 0;
    for (let index = 0; index < endCount; index += 1) {
      fieldStack.pop();
    }
  }

  setCacheEntry(pageFieldValueSequenceBySourceXml, xml, values);
  return values;
}

const styleRefFieldValueSequenceBySourceXml = new Map<
  string,
  StyleRefFieldValueToken[]
>();

function paragraphStyleRefFieldValueSequence(
  paragraph: ParagraphNode
): StyleRefFieldValueToken[] {
  const xml = paragraph.sourceXml ?? "";
  if (!xml) {
    return [];
  }

  const cached = styleRefFieldValueSequenceBySourceXml.get(xml);
  if (cached) {
    return cached;
  }

  const values: StyleRefFieldValueToken[] = [];
  const fieldStack: Array<{ target?: string; inResult: boolean }> = [];
  const tokenPattern =
    /<w:fldSimple\b[^>]*\bw:instr="([^"]+)"[^>]*>[\s\S]*?<\/w:fldSimple>|<w:r\b[\s\S]*?<\/w:r>/gi;

  for (const tokenMatch of xml.matchAll(tokenPattern)) {
    const tokenXml = tokenMatch[0] ?? "";
    if (!tokenXml) {
      continue;
    }

    if (/^<w:fldSimple\b/i.test(tokenXml)) {
      const target = instructionTextToStyleRefTarget(tokenMatch[1] ?? "");
      if (!target) {
        continue;
      }

      for (const textMatch of tokenXml.matchAll(
        /<w:t\b[^>]*>([\s\S]*?)<\/w:t>/gi
      )) {
        values.push({
          target,
          rawText: decodeXmlText(textMatch[1] ?? ""),
        });
      }
      continue;
    }

    const beginCount =
      tokenXml.match(/<w:fldChar\b[^>]*\bw:fldCharType="begin"[^>]*\/?>/gi)
        ?.length ?? 0;
    for (let index = 0; index < beginCount; index += 1) {
      fieldStack.push({ inResult: false });
    }

    for (const instructionMatch of tokenXml.matchAll(
      /<w:instrText\b[^>]*>([\s\S]*?)<\/w:instrText>/gi
    )) {
      const target = instructionTextToStyleRefTarget(instructionMatch[1] ?? "");
      if (!target || fieldStack.length === 0) {
        continue;
      }

      let assigned = false;
      for (
        let stackIndex = fieldStack.length - 1;
        stackIndex >= 0;
        stackIndex -= 1
      ) {
        if (fieldStack[stackIndex].target === undefined) {
          fieldStack[stackIndex].target = target;
          assigned = true;
          break;
        }
      }
      if (!assigned) {
        fieldStack[fieldStack.length - 1].target = target;
      }
    }

    const separateCount =
      tokenXml.match(/<w:fldChar\b[^>]*\bw:fldCharType="separate"[^>]*\/?>/gi)
        ?.length ?? 0;
    for (let index = 0; index < separateCount; index += 1) {
      for (
        let stackIndex = fieldStack.length - 1;
        stackIndex >= 0;
        stackIndex -= 1
      ) {
        if (!fieldStack[stackIndex].inResult) {
          fieldStack[stackIndex].inResult = true;
          break;
        }
      }
    }

    const activeFieldTarget = (() => {
      for (
        let stackIndex = fieldStack.length - 1;
        stackIndex >= 0;
        stackIndex -= 1
      ) {
        const stackEntry = fieldStack[stackIndex];
        if (stackEntry.inResult && stackEntry.target) {
          return stackEntry.target;
        }
      }
      return undefined;
    })();

    if (activeFieldTarget) {
      for (const textMatch of tokenXml.matchAll(
        /<w:t\b[^>]*>([\s\S]*?)<\/w:t>/gi
      )) {
        values.push({
          target: activeFieldTarget,
          rawText: decodeXmlText(textMatch[1] ?? ""),
        });
      }
    }

    const endCount =
      tokenXml.match(/<w:fldChar\b[^>]*\bw:fldCharType="end"[^>]*\/?>/gi)
        ?.length ?? 0;
    for (let index = 0; index < endCount; index += 1) {
      fieldStack.pop();
    }
  }

  setCacheEntry(styleRefFieldValueSequenceBySourceXml, xml, values);
  return values;
}

function normalizeStyleRefTarget(value: string): string {
  return value.replace(/\s+/g, " ").trim().toLowerCase();
}

function paragraphStyledRunText(
  paragraph: ParagraphNode,
  styleRefTarget: string
): string | undefined {
  const xml = paragraph.sourceXml ?? "";
  if (!xml) {
    return undefined;
  }

  const normalizedTarget = normalizeStyleRefTarget(styleRefTarget);
  if (!normalizedTarget) {
    return undefined;
  }

  const textChunks: string[] = [];
  for (const runMatch of xml.matchAll(/<w:r\b[\s\S]*?<\/w:r>/gi)) {
    const runXml = runMatch[0] ?? "";
    if (!runXml) {
      continue;
    }

    const runStyleTag = runXml.match(/<w:rStyle\b[^>]*>/i)?.[0] ?? "";
    const runStyleValue = xmlAttribute(runStyleTag, "w:val");
    if (normalizeStyleRefTarget(runStyleValue ?? "") !== normalizedTarget) {
      continue;
    }

    for (const textMatch of runXml.matchAll(
      /<w:t\b[^>]*>([\s\S]*?)<\/w:t>/gi
    )) {
      const value = decodeXmlText(textMatch[1] ?? "");
      if (value.length > 0) {
        textChunks.push(value);
      }
    }

    if (/<w:tab\b[^>]*\/?>/i.test(runXml)) {
      textChunks.push("\t");
    }
  }

  const joined = textChunks.join("").trim();
  return joined.length > 0 ? joined : undefined;
}

function tabLeaderStyle(
  leader: string | undefined,
  color: string | undefined
): React.CSSProperties {
  const normalizedLeader = leader === "middleDot" ? "dot" : leader;
  const resolvedColor = color || "currentColor";
  if (normalizedLeader === "hyphen") {
    return {
      backgroundImage: `linear-gradient(to right, transparent 0, transparent 4px, ${resolvedColor} 4px, ${resolvedColor} 5px, transparent 5px, transparent 8px)`,
      backgroundSize: "8px 1px",
      backgroundPosition: "0 80%",
      backgroundRepeat: "repeat-x",
    };
  }
  if (normalizedLeader === "underscore") {
    return {
      backgroundImage: `linear-gradient(to right, ${resolvedColor} 0, ${resolvedColor} 1px, transparent 1px, transparent 4px)`,
      backgroundSize: "4px 1px",
      backgroundPosition: "0 90%",
      backgroundRepeat: "repeat-x",
    };
  }

  return {
    backgroundImage: `radial-gradient(${resolvedColor} 1px, transparent 1px)`,
    backgroundSize: "7px 9px",
    backgroundPosition: "0 72%",
    backgroundRepeat: "repeat-x",
  };
}

function noteMarkerLabel(
  noteReference: TextRunNode["noteReference"],
  footnoteDisplayIndexById: Map<number, number>,
  endnoteDisplayIndexById: Map<number, number>
): string | undefined {
  if (!noteReference) {
    return undefined;
  }

  const index =
    noteReference.kind === "footnote"
      ? footnoteDisplayIndexById.get(noteReference.id)
      : endnoteDisplayIndexById.get(noteReference.id);

  if (noteReference.kind === "footnote") {
    const value = index ?? Math.max(1, Math.round(noteReference.id));
    if (!Number.isFinite(value) || value <= 0) {
      return undefined;
    }
    return String(Math.round(value));
  }

  const romanValue = index ?? Math.max(1, Math.round(noteReference.id));
  if (!Number.isFinite(romanValue) || romanValue <= 0) {
    return undefined;
  }

  return numberToRoman(Math.round(romanValue)).toLowerCase();
}

function normalizeDateInputValue(value?: string): string | undefined {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  const directMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
  if (directMatch?.[1]) {
    return directMatch[1];
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }

  return parsed.toISOString().slice(0, 10);
}

interface ParagraphRunRenderOptions {
  showTrackedChanges?: boolean;
  showCommentHighlights?: boolean;
  numberingDefinitions?: NumberingDefinitionSet;
  tocLinkColorByLevel?: Partial<Record<number, string | undefined>>;
  trackedMarkupMode?: "inline" | "gutter";
  withinHeaderFooter?: boolean;
  headerFooterRegion?: "header" | "footer";
  pageNumberFormat?: string;
  resolveStyleRefFieldValue?: (target: string) => string | undefined;
  floatingAnchorOriginCorrectionXPx?: number;
  sectionImageInteraction?: HeaderFooterImageInteraction;
  paragraphOriginLeftPx?: number;
  paragraphOriginTopPx?: number;
  imageFilterSuffix?: string;
}

function renderParagraphRuns(
  paragraph: ParagraphNode,
  keyPrefix: string,
  documentTheme: DocxDocumentTheme = "light",
  numberingLabel?: ParagraphNumberingLabel,
  onInternalLinkClick?: (bookmarkName: string) => void,
  floatingPageOriginPx?: {
    left: number;
    top: number;
    marginLeft?: number;
    marginTop?: number;
    columnLeft?: number;
    columnTop?: number;
    pageWidth?: number;
  },
  noteMarkerIndexes?: {
    footnote: Map<number, number>;
    endnote: Map<number, number>;
  },
  pageNumber?: number,
  totalPages?: number,
  options?: ParagraphRunRenderOptions
): React.ReactNode {
  const runs: React.ReactNode[] = [];
  const runsLeft: React.ReactNode[] = [];
  const runsRight: React.ReactNode[] = [];
  const safeNoteMarkerIndexes = {
    footnote: noteMarkerIndexes?.footnote ?? new Map<number, number>(),
    endnote: noteMarkerIndexes?.endnote ?? new Map<number, number>(),
  };
  const useTabLeaderLayout = paragraphUsesTabLeaders(paragraph);
  const anchoredTabLayout = paragraphAnchoredTabLayout(paragraph, {
    withinHeaderFooter: options?.withinHeaderFooter,
  });
  const useCenterRightTabLayout =
    !useTabLeaderLayout && anchoredTabLayout === "center-right";
  const useCenterTabLayout =
    !useTabLeaderLayout && anchoredTabLayout === "center";
  const useRightTabLayout =
    !useTabLeaderLayout && anchoredTabLayout === "right";
  const useAnchoredTabLayout =
    useCenterRightTabLayout || useCenterTabLayout || useRightTabLayout;
  const pageFieldSequence = paragraphPageFieldSequence(paragraph);
  const pageFieldValueSequence = paragraphPageFieldValueSequence(paragraph);
  const styleRefFieldValueSequence =
    paragraphStyleRefFieldValueSequence(paragraph);
  const hasPageField =
    pageFieldSequence.length > 0 || pageFieldValueSequence.length > 0;
  const hasStyleRefField = styleRefFieldValueSequence.length > 0;
  let consumedPageFieldValues = 0;
  let consumedStyleRefFieldValues = 0;
  const tabStop = paragraphLeadingTabStop(paragraph);
  const tabStopPositionsPx = (paragraph.style?.tabStops ?? [])
    .map((tabStopEntry) => twipsToPixels(tabStopEntry.positionTwips))
    .filter(
      (value): value is number =>
        Number.isFinite(value) && (value as number) > 0
    )
    .sort((left, right) => left - right);
  let hasTabSplit = false;
  let tabLeaderColor: string | undefined;
  const showTrackedChanges = options?.showTrackedChanges === true;
  const showTrackedInlineMarkup =
    showTrackedChanges && options?.trackedMarkupMode !== "gutter";
  const trackedMarkup = showTrackedInlineMarkup
    ? resolveParagraphTrackedMarkup(paragraph)
    : undefined;
  const commentMarkup =
    options?.showCommentHighlights === true
      ? resolveParagraphCommentMarkup(paragraph)
      : undefined;
  const tocParagraphLevel = tableOfContentsLevel(paragraph);
  const tocLinkColor = tocParagraphLevel
    ? options?.tocLinkColorByLevel?.[tocParagraphLevel]
    : undefined;
  const floatingAnchorOriginCorrectionXPx = Number.isFinite(
    options?.floatingAnchorOriginCorrectionXPx
  )
    ? Math.round(options?.floatingAnchorOriginCorrectionXPx as number)
    : 0;
  const checkboxChoiceRow = paragraphLooksLikeCheckboxChoiceRow(paragraph);
  const fallbackTabWidthPx = checkboxChoiceRow
    ? checkboxChoiceRowTabWidthPx(paragraph)
    : DEFAULT_TAB_STOP_PX;
  const tabLeaderLeftTabStopPositionsPx = useTabLeaderLayout
    ? resolveParagraphFirstLineLeftTabStopsPx(paragraph)
    : [];
  const shouldTrackTabLineWidth = !useTabLeaderLayout && !useAnchoredTabLayout;
  let approximateLineWidthPx = 0;
  let trackedVisibleChildCursor = 0;
  const appendTrackedDeletionSegments = (
    target: React.ReactNode[],
    keySeed: string,
    fallbackStyle?: TextRunNode["style"] | FormFieldRunNode["style"]
  ): void => {
    if (!trackedMarkup) {
      return;
    }

    const segments = trackedMarkup.deletedSegmentsByVisibleChildIndex.get(
      trackedVisibleChildCursor
    );
    if (!segments || segments.length === 0) {
      return;
    }

    segments.forEach((segment, segmentIndex) => {
      target.push(
        <span
          key={`${keySeed}-tracked-del-${trackedVisibleChildCursor}-${segmentIndex}`}
          data-docx-tracked-change="deletion"
          data-docx-tracked-change-id={segment.change.id}
          title={
            segment.change.author
              ? `${segment.change.author} deleted`
              : "Deleted"
          }
          style={trackedDeletedStyle(
            documentTheme,
            segment.style ?? fallbackStyle
          )}
        >
          {segment.text}
        </span>
      );
    });
  };
  const currentTrackedInlineChange = ():
    | ParagraphTrackedInlineChange
    | undefined =>
    trackedMarkup?.inlineChangeByVisibleChildIndex[trackedVisibleChildCursor];
  const currentCommentHighlightStyle = (): React.CSSProperties | undefined => {
    const commentIds =
      commentMarkup?.commentIdsByVisibleChildIndex[trackedVisibleChildCursor];
    if (!commentIds || commentIds.length === 0) {
      return undefined;
    }
    return commentHighlightStyle(documentTheme, commentIds[0]);
  };
  const currentAnnotationAttributes = (
    trackedInlineChange: ParagraphTrackedInlineChange | undefined
  ): Record<string, string> => {
    const attributes: Record<string, string> = {};
    if (trackedInlineChange) {
      attributes["data-docx-tracked-change"] = trackedInlineChange.kind;
      attributes["data-docx-tracked-change-id"] = trackedInlineChange.id;
    }

    const commentIds =
      commentMarkup?.commentIdsByVisibleChildIndex[trackedVisibleChildCursor];
    if (commentIds && commentIds.length > 0) {
      attributes["data-docx-comment-ids"] = commentIds.join(" ");
      if (commentIds.length === 1) {
        attributes["data-docx-comment-id"] = String(commentIds[0]);
      }
    }

    return attributes;
  };
  const consumeTrackedVisibleChild = (
    child: ParagraphNode["children"][number]
  ): void => {
    if (!trackedMarkup && !commentMarkup) {
      return;
    }
    if (child.type === "form-field") {
      return;
    }
    trackedVisibleChildCursor += 1;
  };
  const normalizeFieldComparableText = (input: string): string =>
    input
      .replace(/\u00a0/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  const trackTextAdvance = (
    text: string,
    style?: TextRunNode["style"] | FormFieldRunNode["style"]
  ): void => {
    if (!shouldTrackTabLineWidth) {
      return;
    }
    approximateLineWidthPx = updateEstimatedLineWidthPxForText(
      approximateLineWidthPx,
      text,
      style
    );
  };
  const trackInlineAdvance = (widthPx: number): void => {
    if (!shouldTrackTabLineWidth) {
      return;
    }
    approximateLineWidthPx += Math.max(0, Math.round(widthPx));
  };
  const resolveNextTabWidthPx = (): number =>
    resolveTabSpacerWidthPx(
      tabStopPositionsPx,
      approximateLineWidthPx,
      fallbackTabWidthPx,
      checkboxChoiceRow
    );
  const appendPlainTextWithSoftBreakControl = (
    target: React.ReactNode[],
    keySeed: string,
    text: string,
    style: React.CSSProperties,
    measureStyle?: TextRunNode["style"] | FormFieldRunNode["style"],
    spanAttributes?: Record<string, string>
  ): void => {
    const shouldControlSoftBreakStretch =
      paragraph.style?.align === "justify" && text.includes("\n");
    if (!shouldControlSoftBreakStretch) {
      target.push(
        <span key={keySeed} {...spanAttributes} style={style}>
          {text}
        </span>
      );
      trackTextAdvance(text, measureStyle);
      return;
    }

    const segments = text.split("\n");
    segments.forEach((segment, segmentIndex) => {
      const isLastSegment = segmentIndex === segments.length - 1;
      if (segmentIndex > 0) {
        target.push(<br key={`${keySeed}-soft-break-${segmentIndex}`} />);
      }

      if (segment.length > 0) {
        target.push(
          <span
            key={`${keySeed}-segment-${segmentIndex}`}
            {...spanAttributes}
            style={
              isLastSegment
                ? {
                    ...style,
                    display: "inline-block",
                    maxWidth: "100%",
                    whiteSpace: "pre-wrap",
                    verticalAlign: "baseline",
                  }
                : style
            }
          >
            {segment}
          </span>
        );
      }

      trackTextAdvance(segment, measureStyle);
      if (!isLastSegment) {
        approximateLineWidthPx = 0;
      }
    });
  };
  const tabTextStyle = (
    style: TextRunNode["style"] | FormFieldRunNode["style"],
    textStyle: React.CSSProperties
  ): React.CSSProperties => {
    const tabWidthPx = resolveNextTabWidthPx();
    trackInlineAdvance(tabWidthPx);
    const hasUnderline = Boolean(style?.underline);
    return {
      ...textStyle,
      display: "inline-block",
      width: tabWidthPx,
      minWidth: tabWidthPx,
      whiteSpace: "pre",
      textDecoration: hasUnderline ? "none" : textStyle.textDecoration,
      borderBottom: hasUnderline ? "1px solid currentColor" : undefined,
      lineHeight: "1em",
    };
  };
  const renderTabLeaderLeftSpacer = (
    target: React.ReactNode[],
    key: string,
    style: TextRunNode["style"] | undefined,
    trackedInlineChange: ParagraphTrackedInlineChange | undefined,
    widthPx: number
  ): void => {
    const textStyle = trackedInlineStyle(
      runStyleToCss(style, documentTheme),
      trackedInlineChange
    );
    const hasUnderline = Boolean(style?.underline);
    target.push(
      <span
        key={key}
        style={{
          ...textStyle,
          display: "inline-block",
          width: widthPx,
          minWidth: widthPx,
          whiteSpace: "pre",
          textDecoration: hasUnderline ? "none" : textStyle.textDecoration,
          borderBottom: hasUnderline ? "1px solid currentColor" : undefined,
          lineHeight: "1em",
        }}
      >
        {"\u00a0"}
      </span>
    );
  };
  const resolvePageFieldText = (
    value: string,
    preferredZone?: number
  ): string => {
    if (!hasPageField || value.trim().length === 0) {
      return value;
    }

    let fieldKind = pageFieldSequence[consumedPageFieldValues];
    const valueToken = pageFieldValueSequence[consumedPageFieldValues];
    if (valueToken) {
      if (
        normalizeFieldComparableText(value) !==
        normalizeFieldComparableText(valueToken.rawText)
      ) {
        return value;
      }
      fieldKind = valueToken.kind;
    } else {
      if (!fieldKind) {
        return value;
      }

      const normalized = value.trim();
      const likelyFieldResult =
        /^\d+$/.test(normalized) || /^[ivxlcdm]+$/i.test(normalized);
      if (!likelyFieldResult && preferredZone !== 1) {
        return value;
      }
    }

    const resolvedFieldValue =
      fieldKind === "NUMPAGES"
        ? Number.isFinite(totalPages) && (totalPages as number) > 0
          ? Math.max(1, Math.round(totalPages as number))
          : undefined
        : Number.isFinite(pageNumber) && (pageNumber as number) > 0
        ? Math.max(1, Math.round(pageNumber as number))
        : undefined;
    if (!Number.isFinite(resolvedFieldValue)) {
      return value;
    }

    consumedPageFieldValues += 1;
    const leadingWhitespace = value.match(/^\s*/)?.[0] ?? "";
    const trailingWhitespace = value.match(/\s*$/)?.[0] ?? "";
    const normalizedValue = Math.max(
      1,
      Math.round(resolvedFieldValue as number)
    );
    const formattedFieldValue =
      fieldKind === "PAGE"
        ? formatPageFieldValue(normalizedValue, options?.pageNumberFormat)
        : String(normalizedValue);
    return `${leadingWhitespace}${formattedFieldValue}${trailingWhitespace}`;
  };
  const resolveStyleRefFieldText = (value: string): string => {
    if (!hasStyleRefField || value.trim().length === 0) {
      return value;
    }

    const valueToken = styleRefFieldValueSequence[consumedStyleRefFieldValues];
    if (!valueToken) {
      return value;
    }

    if (
      normalizeFieldComparableText(value) !==
      normalizeFieldComparableText(valueToken.rawText)
    ) {
      return value;
    }

    const resolvedValue = options
      ?.resolveStyleRefFieldValue?.(valueToken.target)
      ?.trim();
    if (!resolvedValue) {
      return value;
    }

    consumedStyleRefFieldValues += 1;
    const leadingWhitespace = value.match(/^\s*/)?.[0] ?? "";
    const trailingWhitespace = value.match(/\s*$/)?.[0] ?? "";
    return `${leadingWhitespace}${resolvedValue}${trailingWhitespace}`;
  };
  const resolveFieldText = (value: string, preferredZone?: number): string =>
    resolvePageFieldText(resolveStyleRefFieldText(value), preferredZone);

  const trackedLinkStyle = (
    style: TextRunNode["style"] | FormFieldRunNode["style"] | undefined,
    trackedInlineChange: ParagraphTrackedInlineChange | undefined
  ): React.CSSProperties => {
    if (!isTableOfContentsParagraph(paragraph)) {
      return {
        ...trackedInlineStyle(
          linkStyleToCss(style, documentTheme),
          trackedInlineChange
        ),
        ...currentCommentHighlightStyle(),
      };
    }

    const base = runStyleToCss(style, documentTheme);
    return {
      ...trackedInlineStyle(
        {
          ...base,
          color: tocLinkColor
            ? themedRunColor(tocLinkColor, documentTheme)
            : "inherit",
          textDecoration: "none",
        },
        trackedInlineChange
      ),
      ...currentCommentHighlightStyle(),
    };
  };
  const usesExternalHorizontalAnchorOrigin = (image: ImageRunNode): boolean => {
    const horizontalRelativeTo = image.floating?.horizontalRelativeTo
      ?.trim()
      .toLowerCase();
    return (
      horizontalRelativeTo === "page" ||
      horizontalRelativeTo === "margin" ||
      horizontalRelativeTo === "column"
    );
  };

  const renderRun = (
    target: React.ReactNode[],
    child: ParagraphNode["children"][number],
    key: string,
    textOverride?: string,
    trackedInlineChange?: ParagraphTrackedInlineChange,
    childIndex = -1
  ): void => {
    if (child.type === "form-field") {
      const textValue = (formFieldDisplayValue(child) || "\u00a0").replace(
        /\t/g,
        " "
      );
      const text = textOverride ?? textValue;
      const annotationAttributes =
        currentAnnotationAttributes(trackedInlineChange);
      if (child.link) {
        const linkHref = child.link;
        const isInternalLink = linkHref.startsWith("#");
        target.push(
          <a
            key={key}
            {...annotationAttributes}
            href={linkHref}
            target={isInternalLink ? undefined : "_blank"}
            rel={isInternalLink ? undefined : "noreferrer noopener"}
            onMouseDown={(event) => {
              if (!isInternalLink || !onInternalLinkClick) {
                return;
              }

              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={(event) => {
              if (!isInternalLink || !onInternalLinkClick) {
                return;
              }

              event.preventDefault();
              event.stopPropagation();
              onInternalLinkClick(linkHref.slice(1));
            }}
            style={trackedLinkStyle(child.style, trackedInlineChange)}
          >
            {text}
          </a>
        );
        trackTextAdvance(text, child.style);
        return;
      }

      const trackedStyle = {
        ...trackedInlineStyle(
          runStyleToCss(child.style, documentTheme),
          trackedInlineChange
        ),
        ...currentCommentHighlightStyle(),
      };
      if (text === "\t" && !useTabLeaderLayout && !useAnchoredTabLayout) {
        target.push(
          <span
            key={key}
            {...annotationAttributes}
            style={tabTextStyle(child.style, trackedStyle)}
          >
            {"\u00a0"}
          </span>
        );
        return;
      }

      target.push(
        <span key={key} {...annotationAttributes} style={trackedStyle}>
          {text}
        </span>
      );
      trackTextAdvance(text, child.style);
      return;
    }

    if (child.type === "image") {
      const behavesAsDecorativeBehindTextBackground =
        imageBehavesAsDecorativeBehindTextBackground(child, paragraph);
      const sectionImageInteraction = options?.sectionImageInteraction;
      const sectionImageLocation =
        sectionImageInteraction?.imageLocationForChild?.(childIndex);
      const sectionImageKey = sectionImageLocation
        ? sectionImageLocationKey(sectionImageLocation)
        : undefined;
      const movePreview =
        sectionImageKey &&
        sectionImageInteraction?.floatingMovePreview?.imageKey ===
          sectionImageKey
          ? sectionImageInteraction.floatingMovePreview
          : undefined;
      const forceAbsoluteFixedPositionSectionFloat =
        options?.withinHeaderFooter === true &&
        isPageOrMarginAnchoredWrappedFloatingImage(child);
      const forceWrappedTopAnchoredSectionFloat =
        options?.withinHeaderFooter === true &&
        Boolean(
          child.floating?.wrapType && child.floating.wrapType !== "none"
        ) &&
        shouldRenderTopAnchoredMarginFloatAsAbsolute(child);
      const isWrappedFloatingImage =
        !forceAbsoluteFixedPositionSectionFloat &&
        (forceWrappedTopAnchoredSectionFloat
          ? true
          : shouldRenderWrappedFloatingImage(child));
      const isAbsoluteFloatingImage = forceAbsoluteFixedPositionSectionFloat
        ? true
        : forceWrappedTopAnchoredSectionFloat
        ? false
        : shouldRenderAbsoluteFloatingImage(child);
      const horizontalRelativeTo =
        child.floating?.horizontalRelativeTo?.toLowerCase();
      const verticalRelativeTo =
        child.floating?.verticalRelativeTo?.toLowerCase();
      let widthPx = child.widthPx;
      let heightPx = child.heightPx;
      ({ widthPx, heightPx } = resolvePageSpanningAbsoluteFloatingDimensions(
        child,
        widthPx,
        heightPx,
        floatingPageOriginPx
      ));
      const horizontalAnchorCorrectionPx = usesExternalHorizontalAnchorOrigin(
        child
      )
        ? floatingAnchorOriginCorrectionXPx
        : 0;
      const absoluteColumnOriginLeftPx =
        options?.withinHeaderFooter === true &&
        options?.headerFooterRegion === "header" &&
        child.floating?.behindDocument === true &&
        horizontalRelativeTo === "column"
          ? floatingPageOriginPx?.marginLeft
          : floatingPageOriginPx?.columnLeft;
      const floatingStyle: React.CSSProperties = isWrappedFloatingImage
        ? wrappedFloatingImageStyle(child, {
            containerWidthPx: floatingPageOriginPx?.pageWidth,
            deltaX: (movePreview?.deltaX ?? 0) + horizontalAnchorCorrectionPx,
            deltaY: movePreview?.deltaY ?? 0,
            allowNegativeOffsets: true,
          })
        : isAbsoluteFloatingImage
        ? absoluteFloatingImageStyle(child, {
            pageOriginLeft: floatingPageOriginPx?.left,
            pageOriginTop: floatingPageOriginPx?.top,
            marginOriginLeft: floatingPageOriginPx?.marginLeft,
            marginOriginTop: floatingPageOriginPx?.marginTop,
            columnOriginLeft: absoluteColumnOriginLeftPx,
            columnOriginTop: floatingPageOriginPx?.columnTop,
            deltaX: (movePreview?.deltaX ?? 0) + horizontalAnchorCorrectionPx,
            deltaY: movePreview?.deltaY ?? 0,
          })
        : movePreview
        ? {
            transform: `translate(${movePreview.deltaX}px, ${movePreview.deltaY}px)`,
            position: "relative",
            zIndex: 4,
          }
        : {};
      const floatingStyleWithMovePreview = isWrappedFloatingImage
        ? {
            ...floatingStyle,
          }
        : floatingStyle;
      const onSectionImagePointerDown =
        sectionImageLocation &&
        sectionImageInteraction?.onImagePointerDown &&
        !sectionImageInteraction.isReadOnly &&
        !behavesAsDecorativeBehindTextBackground
          ? (event: React.PointerEvent<HTMLElement>) => {
              sectionImageInteraction.onImagePointerDown?.(
                event,
                sectionImageLocation,
                child,
                isWrappedFloatingImage,
                isAbsoluteFloatingImage
              );
            }
          : undefined;
      const onSectionImageClick =
        sectionImageLocation &&
        sectionImageInteraction?.onImageClick &&
        !sectionImageInteraction.isReadOnly &&
        !behavesAsDecorativeBehindTextBackground
          ? (event: React.MouseEvent<HTMLElement>) => {
              event.stopPropagation();
              sectionImageInteraction.onImageClick?.(sectionImageLocation);
            }
          : undefined;
      const sectionImageCursor =
        sectionImageLocation &&
        !sectionImageInteraction?.isReadOnly &&
        !behavesAsDecorativeBehindTextBackground &&
        (isWrappedFloatingImage || isAbsoluteFloatingImage)
          ? "move"
          : undefined;
      const isCenteredStandaloneInlineImage =
        !isWrappedFloatingImage &&
        !isAbsoluteFloatingImage &&
        paragraph.style?.align === "center" &&
        paragraph.children.length === 1 &&
        paragraph.children[0]?.type === "image";
      const trackedImageStyle =
        trackedInlineChange?.kind === "insertion" ||
        trackedInlineChange?.kind === "move-to"
          ? {
              outline:
                trackedInlineChange.kind === "move-to"
                  ? "2px solid rgba(112, 173, 71, 0.35)"
                  : "2px solid rgba(220, 38, 38, 0.3)",
              outlineOffset: 1,
            }
          : undefined;

      const renderableImageSrc =
        syntheticTextBoxSvg(
          child,
          pageNumber,
          totalPages,
          options?.pageNumberFormat,
          options?.resolveStyleRefFieldValue
        ) ?? resolveRenderableImageSource(child);

      if (!child.src) {
        target.push(
          <span
            key={key}
            style={{
              display: "inline-flex",
              minWidth: 112,
              minHeight: 80,
              alignItems: "center",
              justifyContent: "center",
              border: "1px dashed #d1d5db",
              borderRadius: 4,
              color: "#6b7280",
              fontSize: 12,
              marginInline:
                isWrappedFloatingImage || isAbsoluteFloatingImage
                  ? undefined
                  : 0,
              paddingInline: 8,
              ...trackedImageStyle,
              ...floatingStyleWithMovePreview,
              cursor: sectionImageCursor,
              ...(behavesAsDecorativeBehindTextBackground
                ? { pointerEvents: "none", userSelect: "none" }
                : undefined),
            }}
            onPointerDown={onSectionImagePointerDown}
            onClick={onSectionImageClick}
          >
            Missing image
          </span>
        );
        return;
      }

      if (
        imageUsesPlaceholderFallback(child) ||
        (child.src && !renderableImageSrc)
      ) {
        const isSmallIcon = (widthPx ?? 0) <= 56 && (heightPx ?? 0) <= 56;
        const fallbackText = unsupportedImageFallbackLabel(
          child,
          widthPx,
          heightPx
        );
        target.push(
          <span
            key={key}
            role="img"
            aria-label={child.alt ?? "DOCX image"}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: widthPx ? `${widthPx}px` : "1.8em",
              height: heightPx ? `${heightPx}px` : "1.8em",
              minWidth: 16,
              minHeight: 16,
              border: "1px solid #d1d5db",
              borderRadius: 3,
              backgroundColor: "#ffffff",
              color: "#0f172a",
              fontSize: isSmallIcon ? 12 : 10,
              fontWeight: 700,
              textTransform: "lowercase",
              fontFamily: "Arial, sans-serif",
              lineHeight: 1,
              verticalAlign: "middle",
              marginInline:
                isWrappedFloatingImage || isAbsoluteFloatingImage
                  ? undefined
                  : 0,
              ...trackedImageStyle,
              ...floatingStyleWithMovePreview,
              cursor: sectionImageCursor,
              ...(behavesAsDecorativeBehindTextBackground
                ? { pointerEvents: "none", userSelect: "none" }
                : undefined),
            }}
            onPointerDown={onSectionImagePointerDown}
            onClick={onSectionImageClick}
          >
            {fallbackText}
          </span>
        );
        return;
      }

      target.push(
        (() => {
          const cropLayout = imageCropLayout(child, widthPx, heightPx);
          const imageVisualStyle: React.CSSProperties = {
            filter: appendCssFilters(
              child.cssFilter,
              options?.imageFilterSuffix
            ),
            opacity: child.cssOpacity,
          };
          const imageTransformStyle = resolveImageRenderTransformStyle(child, {
            frameWidthPx: cropLayout?.frameWidthPx ?? widthPx,
            frameHeightPx: cropLayout?.frameHeightPx ?? heightPx,
            baseTransform: cropLayout
              ? `translate(${-cropLayout.offsetXPx}px, ${-cropLayout.offsetYPx}px)`
              : undefined,
          });

          if (cropLayout) {
            return (
              <span
                key={key}
                style={{
                  width: `${cropLayout.frameWidthPx}px`,
                  height: `${cropLayout.frameHeightPx}px`,
                  overflow: "clip",
                  display: isCenteredStandaloneInlineImage
                    ? "block"
                    : "inline-block",
                  marginLeft: isCenteredStandaloneInlineImage
                    ? "auto"
                    : undefined,
                  marginRight: isCenteredStandaloneInlineImage
                    ? "auto"
                    : undefined,
                  verticalAlign: "middle",
                  marginInline:
                    isWrappedFloatingImage || isAbsoluteFloatingImage
                      ? undefined
                      : 0,
                  ...trackedImageStyle,
                  ...floatingStyleWithMovePreview,
                  cursor: sectionImageCursor,
                  ...(behavesAsDecorativeBehindTextBackground
                    ? { pointerEvents: "none", userSelect: "none" }
                    : undefined),
                }}
                onPointerDown={onSectionImagePointerDown}
                onClick={onSectionImageClick}
              >
                <img
                  src={renderableImageSrc}
                  alt={child.alt ?? "DOCX image"}
                  draggable={false}
                  style={{
                    width: `${cropLayout.imageWidthPx}px`,
                    height: `${cropLayout.imageHeightPx}px`,
                    maxWidth: "none",
                    display: "block",
                    ...imageVisualStyle,
                    ...imageTransformStyle,
                  }}
                />
              </span>
            );
          }

          return (
            <img
              key={key}
              src={renderableImageSrc}
              alt={child.alt ?? "DOCX image"}
              draggable={false}
              onPointerDown={onSectionImagePointerDown}
              onClick={onSectionImageClick}
              style={{
                width: widthPx ? `${widthPx}px` : undefined,
                height: heightPx ? `${heightPx}px` : undefined,
                maxWidth:
                  isWrappedFloatingImage || isAbsoluteFloatingImage
                    ? undefined
                    : "100%",
                display: isCenteredStandaloneInlineImage ? "block" : undefined,
                marginLeft: isCenteredStandaloneInlineImage
                  ? "auto"
                  : undefined,
                marginRight: isCenteredStandaloneInlineImage
                  ? "auto"
                  : undefined,
                verticalAlign: "middle",
                marginInline:
                  isWrappedFloatingImage || isAbsoluteFloatingImage
                    ? undefined
                    : 0,
                ...imageVisualStyle,
                ...resolveImageRenderTransformStyle(child, {
                  frameWidthPx: widthPx,
                  frameHeightPx: heightPx,
                }),
                ...trackedImageStyle,
                ...floatingStyleWithMovePreview,
                cursor: sectionImageCursor,
                ...(behavesAsDecorativeBehindTextBackground
                  ? { pointerEvents: "none", userSelect: "none" }
                  : undefined),
              }}
            />
          );
        })()
      );
      if (!isWrappedFloatingImage && !isAbsoluteFloatingImage) {
        trackInlineAdvance(widthPx ?? 0);
      }
      return;
    }

    const textStyle = {
      ...trackedInlineStyle(
        runStyleToCss(child.style, documentTheme),
        trackedInlineChange
      ),
      ...currentCommentHighlightStyle(),
    };
    const annotationAttributes =
      currentAnnotationAttributes(trackedInlineChange);
    const noteLabel = noteMarkerLabel(
      child.noteReference,
      safeNoteMarkerIndexes.footnote,
      safeNoteMarkerIndexes.endnote
    );
    if (noteLabel) {
      target.push(
        <span
          key={key}
          {...annotationAttributes}
          style={{
            ...textStyle,
            verticalAlign: "super",
            fontSize: "0.75em",
          }}
        >
          {noteLabel}
        </span>
      );
      trackTextAdvance(noteLabel, child.style);
      return;
    }

    if (
      (textOverride ?? child.text) === "\t" &&
      !useTabLeaderLayout &&
      !useAnchoredTabLayout
    ) {
      target.push(
        <span
          key={key}
          {...annotationAttributes}
          style={tabTextStyle(child.style, textStyle)}
        >
          {"\u00a0"}
        </span>
      );
      return;
    }

    if (child.link) {
      const linkHref = child.link;
      const isInternalLink = linkHref.startsWith("#");
      target.push(
        <a
          key={key}
          {...annotationAttributes}
          href={linkHref}
          target={isInternalLink ? undefined : "_blank"}
          rel={isInternalLink ? undefined : "noreferrer noopener"}
          onMouseDown={(event) => {
            if (!isInternalLink || !onInternalLinkClick) {
              return;
            }

            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={(event) => {
            if (!isInternalLink || !onInternalLinkClick) {
              return;
            }

            event.preventDefault();
            event.stopPropagation();
            onInternalLinkClick(linkHref.slice(1));
          }}
          style={trackedLinkStyle(child.style, trackedInlineChange)}
        >
          {textOverride ?? child.text}
        </a>
      );
      trackTextAdvance(textOverride ?? child.text, child.style);
      return;
    }

    appendPlainTextWithSoftBreakControl(
      target,
      key,
      textOverride ?? child.text,
      textStyle,
      child.style,
      annotationAttributes
    );
  };

  const anchoredTabZoneStyle: React.CSSProperties = {
    display: "inline-flex",
    minWidth: 0,
    whiteSpace: "pre-wrap",
    alignItems: "baseline",
    wordBreak: "normal",
    overflowWrap: "normal",
    flexWrap: "nowrap",
  };

  const renderNumberingIntoTarget = (
    target: React.ReactNode[],
    numberingKey: string
  ): void => {
    if (!numberingLabel) {
      return;
    }

    const numberingTextStyle = numberingLabel.style
      ? runStyleToCss(numberingLabel.style, documentTheme)
      : undefined;
    target.push(
      <span
        key={numberingKey}
        style={numberingMarkerStyle(
          paragraph,
          options?.numberingDefinitions,
          numberingLabel,
          numberingTextStyle,
          documentTheme
        )}
      >
        {numberingLabel.imageSrc ? (
          <>
            <img
              src={numberingLabel.imageSrc}
              alt=""
              aria-hidden="true"
              style={{
                display: "inline-block",
                verticalAlign: "text-bottom",
                width: numberingLabel.imageWidthPx ?? 12,
                height: numberingLabel.imageHeightPx ?? 12,
                marginRight: 2,
              }}
            />
            {numberingLabel.trailingText ?? ""}
          </>
        ) : (
          numberingLabel.text ?? ""
        )}
      </span>
    );
  };

  const buildAnchoredTabZones = (zoneCount: 2 | 3): React.ReactNode[][] => {
    const zones = Array.from(
      { length: zoneCount },
      () => [] as React.ReactNode[]
    );
    let activeZone = 0;

    renderNumberingIntoTarget(zones[0], `${keyPrefix}-numbering-anchored`);

    paragraph.children.forEach((child, childIndex) => {
      const zoneIndex = Math.max(0, Math.min(zoneCount - 1, activeZone));
      const zoneTarget = zones[zoneIndex];
      const key = `${keyPrefix}-anchored-run-${childIndex}`;
      appendTrackedDeletionSegments(
        zoneTarget,
        `${key}-before`,
        child.type === "text" || child.type === "form-field"
          ? child.style
          : undefined
      );
      const trackedInlineChange = currentTrackedInlineChange();

      const rawText =
        child.type === "text"
          ? child.text
          : child.type === "form-field"
          ? formFieldDisplayValue(child)
          : undefined;
      if (typeof rawText !== "string" || !rawText.includes("\t")) {
        if (child.type === "text") {
          const resolvedText = resolveFieldText(rawText ?? "", zoneIndex);
          renderRun(
            zoneTarget,
            child,
            key,
            attachTextToPreviousCheckbox(paragraph, childIndex, resolvedText),
            trackedInlineChange,
            childIndex
          );
        } else {
          renderRun(
            zoneTarget,
            child,
            key,
            undefined,
            trackedInlineChange,
            childIndex
          );
        }
        consumeTrackedVisibleChild(child);
        return;
      }

      const parts = rawText.split("\t");
      parts.forEach((part, partIndex) => {
        const currentZone = Math.max(0, Math.min(zoneCount - 1, activeZone));
        if (part.length > 0) {
          renderRun(
            zones[currentZone],
            child,
            `${key}-part-${partIndex}`,
            resolveFieldText(part, currentZone),
            trackedInlineChange,
            childIndex
          );
        }
        if (partIndex < parts.length - 1 && activeZone < zoneCount - 1) {
          activeZone += 1;
        }
      });
      consumeTrackedVisibleChild(child);
    });

    appendTrackedDeletionSegments(
      zones[Math.max(0, Math.min(zoneCount - 1, activeZone))],
      `${keyPrefix}-tail`
    );
    return zones;
  };

  if (numberingLabel && !useAnchoredTabLayout) {
    renderNumberingIntoTarget(
      useTabLeaderLayout ? runsLeft : runs,
      `${keyPrefix}-numbering`
    );
    if (numberingLabel.imageSrc) {
      trackInlineAdvance(numberingLabel.imageWidthPx ?? 12);
      trackTextAdvance(numberingLabel.trailingText ?? "", numberingLabel.style);
    } else {
      trackTextAdvance(numberingLabel.text ?? "", numberingLabel.style);
    }
  }

  if (!useAnchoredTabLayout) {
    paragraph.children.forEach((child, childIndex) => {
      const key = `${keyPrefix}-run-${childIndex}`;
      const target = useTabLeaderLayout
        ? hasTabSplit
          ? runsRight
          : runsLeft
        : runs;
      appendTrackedDeletionSegments(
        target,
        `${key}-before`,
        child.type === "text" || child.type === "form-field"
          ? child.style
          : undefined
      );
      const trackedInlineChange = currentTrackedInlineChange();
      if (
        useTabLeaderLayout &&
        child.type === "text" &&
        child.text.includes("\t") &&
        !hasTabSplit
      ) {
        const text = child.text;
        const tabIndex = text.lastIndexOf("\t");
        const leftText = text.slice(0, tabIndex);
        const rightText = text.slice(tabIndex + 1);
        if (!tabLeaderColor && child.style?.color) {
          tabLeaderColor = child.style.color;
        }

        const hasLaterTab = paragraph.children
          .slice(childIndex + 1)
          .some(
            (nextChild) =>
              (nextChild.type === "text" && nextChild.text.includes("\t")) ||
              (nextChild.type === "form-field" &&
                formFieldDisplayValue(nextChild).includes("\t"))
          );
        if (leftText.length === 0 && rightText.length === 0 && hasLaterTab) {
          renderRun(
            runsLeft,
            child,
            `${keyPrefix}-run-${childIndex}-spacer`,
            " ",
            trackedInlineChange,
            childIndex
          );
          consumeTrackedVisibleChild(child);
          return;
        }

        if (leftText.length > 0) {
          let tabLeaderLeftLineWidthPx = 0;
          const leftTextParts = leftText.split("\t");
          leftTextParts.forEach((part, partIndex) => {
            if (part.length > 0) {
              const resolvedPart = resolveFieldText(part, 0);
              renderRun(
                runsLeft,
                child,
                `${keyPrefix}-run-${childIndex}-left-${partIndex}`,
                resolvedPart,
                trackedInlineChange,
                childIndex
              );
              tabLeaderLeftLineWidthPx = updateEstimatedLineWidthPxForText(
                tabLeaderLeftLineWidthPx,
                resolvedPart,
                child.style
              );
            }

            if (partIndex >= leftTextParts.length - 1) {
              return;
            }

            const tabWidthPx = resolveTabSpacerWidthPx(
              tabLeaderLeftTabStopPositionsPx,
              tabLeaderLeftLineWidthPx,
              fallbackTabWidthPx
            );
            renderTabLeaderLeftSpacer(
              runsLeft,
              `${keyPrefix}-run-${childIndex}-left-tab-${partIndex}`,
              child.style,
              trackedInlineChange,
              tabWidthPx
            );
            tabLeaderLeftLineWidthPx += tabWidthPx;
          });
        }
        renderRun(
          runsRight,
          child,
          `${keyPrefix}-run-${childIndex}-right`,
          resolveFieldText(rightText, 1),
          trackedInlineChange,
          childIndex
        );
        hasTabSplit = true;
        consumeTrackedVisibleChild(child);
        return;
      }

      if (child.type === "text") {
        const resolvedText = resolveFieldText(child.text);
        renderRun(
          target,
          child,
          key,
          attachTextToPreviousCheckbox(paragraph, childIndex, resolvedText),
          trackedInlineChange,
          childIndex
        );
        consumeTrackedVisibleChild(child);
        return;
      }
      renderRun(target, child, key, undefined, trackedInlineChange, childIndex);
      consumeTrackedVisibleChild(child);
    });

    const trailingTarget = useTabLeaderLayout
      ? hasTabSplit
        ? runsRight
        : runsLeft
      : runs;
    appendTrackedDeletionSegments(trailingTarget, `${keyPrefix}-tail`);
  }

  if (useCenterRightTabLayout) {
    const zones = buildAnchoredTabZones(3);
    const centerStopPx = Math.max(
      0,
      Math.round(paragraphFirstTabStopPx(paragraph, "center") ?? 0)
    );
    const rightStopPx = Math.max(
      centerStopPx,
      Math.round(paragraphFirstTabStopPx(paragraph, "right") ?? centerStopPx)
    );

    return (
      <div
        key={`${keyPrefix}-center-right-tabs`}
        data-docx-tab-layout="center-right"
        style={{
          display: "grid",
          gridTemplateColumns: `${centerStopPx}px 0px ${Math.max(
            0,
            rightStopPx - centerStopPx
          )}px 0px minmax(0, 1fr)`,
          alignItems: "start",
          width: "100%",
        }}
      >
        <span
          data-docx-tab-zone="0"
          style={{
            ...anchoredTabZoneStyle,
            gridColumn: "1 / 2",
            gridRow: "1 / 2",
          }}
        >
          {zones[0]}
        </span>
        <span
          data-docx-tab-zone="1"
          style={{
            ...anchoredTabZoneStyle,
            gridColumn: "1 / -1",
            gridRow: "1 / 2",
            justifySelf: "start",
            marginLeft: centerStopPx,
            textAlign: "center",
            transform: "translateX(-50%)",
            width: "max-content",
            maxWidth: "100%",
          }}
        >
          {zones[1]}
        </span>
        <span
          data-docx-tab-zone="2"
          style={{
            ...anchoredTabZoneStyle,
            gridColumn: "3 / 4",
            gridRow: "1 / 2",
            justifySelf: "end",
            justifyContent: "flex-end",
            textAlign: "right",
          }}
        >
          {zones[2]}
        </span>
      </div>
    );
  }

  if (useCenterTabLayout) {
    const zones = buildAnchoredTabZones(2);
    const centerStopPx = Math.max(
      0,
      Math.round(paragraphFirstTabStopPx(paragraph, "center") ?? 0)
    );
    return (
      <div
        key={`${keyPrefix}-center-tabs`}
        data-docx-tab-layout="center"
        style={{
          display: "grid",
          gridTemplateColumns: `${centerStopPx}px 0px minmax(0, 1fr)`,
          alignItems: "start",
          width: "100%",
        }}
      >
        {zones[0].length > 0 ? (
          <span
            data-docx-tab-zone="left"
            style={{
              ...anchoredTabZoneStyle,
              gridColumn: "1 / 2",
              gridRow: "1 / 2",
            }}
          >
            {zones[0]}
          </span>
        ) : null}
        <span
          data-docx-tab-zone="center"
          style={{
            ...anchoredTabZoneStyle,
            gridColumn: "1 / -1",
            gridRow: "1 / 2",
            justifySelf: "start",
            marginLeft: centerStopPx,
            textAlign: "center",
            transform: "translateX(-50%)",
            width: "max-content",
            maxWidth: "100%",
          }}
        >
          {zones[1]}
        </span>
      </div>
    );
  }

  if (useRightTabLayout) {
    const zones = buildAnchoredTabZones(2);
    const rightStopPx = Math.max(
      0,
      Math.round(paragraphFirstTabStopPx(paragraph, "right") ?? 0)
    );
    return (
      <div
        key={`${keyPrefix}-right-tabs`}
        data-docx-tab-layout="right"
        style={{
          display: "grid",
          gridTemplateColumns: `${rightStopPx}px 0px minmax(0, 1fr)`,
          alignItems: "start",
          width: "100%",
        }}
      >
        <span
          data-docx-tab-zone="left"
          style={{
            ...anchoredTabZoneStyle,
            gridColumn: "1 / 2",
            gridRow: "1 / 2",
          }}
        >
          {zones[0]}
        </span>
        <span
          data-docx-tab-zone="right"
          style={{
            ...anchoredTabZoneStyle,
            gridColumn: "1 / 2",
            gridRow: "1 / 2",
            justifySelf: "end",
            justifyContent: "flex-end",
            textAlign: "right",
          }}
        >
          {zones[1]}
        </span>
      </div>
    );
  }

  if (!useTabLeaderLayout || !hasTabSplit) {
    return runsLeft.length > 0 ? runsLeft : runs;
  }

  return (
    <div
      key={`${keyPrefix}-toc`}
      data-docx-tab-layout="leader"
      style={{
        display: "flex",
        alignItems: "baseline",
        width: "100%",
      }}
    >
      <span
        data-docx-tab-zone="left"
        style={{
          display: "block",
          flex: "0 1 auto",
          minWidth: 0,
          whiteSpace: "pre-wrap",
        }}
      >
        {runsLeft}
      </span>
      <span
        aria-hidden="true"
        style={{
          ...tabLeaderStyle(
            tabStop?.leader,
            themedRunColor(tabLeaderColor, documentTheme)
          ),
          flex: "1 1 auto",
          minWidth: 8,
          marginLeft: 6,
          marginRight: 6,
          height: "1em",
          alignSelf: "center",
        }}
      />
      <span
        data-docx-tab-zone="right"
        style={{
          display: "block",
          flex: "0 0 max-content",
          minWidth: "max-content",
          width: "max-content",
          textAlign: "right",
          whiteSpace: "nowrap",
          textIndent: 0,
        }}
      >
        {runsRight}
      </span>
    </div>
  );
}

function numberToRoman(value: number): string {
  if (!Number.isFinite(value) || value <= 0) {
    return "";
  }

  const numerals: Array<[number, string]> = [
    [1000, "M"],
    [900, "CM"],
    [500, "D"],
    [400, "CD"],
    [100, "C"],
    [90, "XC"],
    [50, "L"],
    [40, "XL"],
    [10, "X"],
    [9, "IX"],
    [5, "V"],
    [4, "IV"],
    [1, "I"],
  ];

  let remaining = Math.floor(value);
  let output = "";

  for (const [base, numeral] of numerals) {
    while (remaining >= base) {
      output += numeral;
      remaining -= base;
    }
  }

  return output;
}

function numberToLetters(value: number): string {
  if (!Number.isFinite(value) || value <= 0) {
    return "";
  }

  let remaining = Math.floor(value);
  let output = "";
  while (remaining > 0) {
    const offset = (remaining - 1) % 26;
    output = String.fromCharCode(65 + offset) + output;
    remaining = Math.floor((remaining - 1) / 26);
  }

  return output;
}

interface ParagraphNumberingLabel {
  text?: string;
  fontFamily?: string;
  color?: string;
  style?: TextRunNode["style"];
  imageSrc?: string;
  imageWidthPx?: number;
  imageHeightPx?: number;
  trailingText?: string;
}

function formatNumberingCounter(
  format: string | undefined,
  value: number
): string {
  if (!Number.isFinite(value) || value <= 0) {
    return "";
  }

  switch ((format ?? "decimal").toLowerCase()) {
    case "decimal":
      return String(value);
    case "upperroman":
      return numberToRoman(value);
    case "lowerroman":
      return numberToRoman(value).toLowerCase();
    case "upperletter":
      return numberToLetters(value);
    case "lowerletter":
      return numberToLetters(value).toLowerCase();
    case "ordinal":
    case "cardinaltext":
    case "ordinaltext":
      return String(value);
    case "bullet":
      return "\u2022";
    case "none":
      return "";
    default:
      return String(value);
  }
}

function formatPageFieldValue(
  value: number,
  format: string | undefined
): string {
  const formatted = formatNumberingCounter(format, value);
  return formatted.length > 0 ? formatted : String(value);
}

function findNumberingLevelDefinition(
  numbering: NumberingDefinitionSet,
  numId: number,
  ilvl: number
): NumberingLevelDefinition | undefined {
  const instance = numbering.instances.find((item) => item.numId === numId);
  if (!instance) {
    return undefined;
  }

  const overrideLevel = instance.levelOverrides?.find(
    (item) => item.ilvl === ilvl
  );
  if (overrideLevel) {
    return overrideLevel;
  }

  const abstract = numbering.abstracts.find(
    (item) => item.abstractNumId === instance.abstractNumId
  );
  return abstract?.levels.find((item) => item.ilvl === ilvl);
}

function numberingLevelHasVisibleMarker(
  level: NumberingLevelDefinition | undefined
): boolean {
  return Boolean(
    level?.pictureBullet?.src || (level?.text && level.text.trim().length > 0)
  );
}

function numberingLevelIsBulletLike(
  level: NumberingLevelDefinition | undefined
): boolean {
  const format = level?.format?.trim().toLowerCase();
  return (
    format === "bullet" ||
    Boolean(level?.pictureBullet?.src) ||
    isBulletLikeNumberingText(level?.text ?? "", level?.bulletFontFamily)
  );
}

function numberingAbstractLevelsForNumId(
  numberingDefinitions: NumberingDefinitionSet,
  numId: number
): NumberingLevelDefinition[] {
  const instance = numberingDefinitions.instances.find(
    (item) => item.numId === numId
  );
  if (!instance) {
    return [];
  }

  const abstract = numberingDefinitions.abstracts.find(
    (item) => item.abstractNumId === instance.abstractNumId
  );
  return [...(abstract?.levels ?? []), ...(instance.levelOverrides ?? [])];
}

function effectiveNumberingNumIdForParagraph(
  paragraph: ParagraphNode,
  numberingDefinitions: NumberingDefinitionSet | undefined
): number | undefined {
  const numbering = paragraph.style?.numbering;
  if (
    !numberingDefinitions ||
    !numbering ||
    !Number.isFinite(numbering.numId) ||
    numbering.numId <= 0
  ) {
    return numbering?.numId;
  }

  const numId = numbering.numId;
  const ilvl = Math.max(0, Math.round(numbering.ilvl ?? 0));
  const currentLevel = findNumberingLevelDefinition(
    numberingDefinitions,
    numId,
    ilvl
  );
  if (
    !numberingLevelIsBulletLike(currentLevel) ||
    numberingLevelHasVisibleMarker(currentLevel)
  ) {
    return numId;
  }

  const currentAbstractLevels = numberingAbstractLevelsForNumId(
    numberingDefinitions,
    numId
  );
  if (
    currentAbstractLevels.length === 0 ||
    currentAbstractLevels.some(
      (level) =>
        !numberingLevelIsBulletLike(level) ||
        numberingLevelHasVisibleMarker(level)
    )
  ) {
    return numId;
  }

  const paragraphTextValue = paragraphText(paragraph).trim();
  if (
    paragraphTextValue.length === 0 ||
    isBulletLikeNumberingText(paragraphTextValue) ||
    /^[\u2022\u25cf\u25cb\u25a0\u25a1\u25aa\u25ab\u25c6\u25c7\u25e6\u2043]/.test(
      paragraphTextValue
    )
  ) {
    return numId;
  }

  const candidate = numberingDefinitions.instances.find((instance) => {
    if (instance.numId === numId) {
      return false;
    }
    const candidateLevel = findNumberingLevelDefinition(
      numberingDefinitions,
      instance.numId,
      ilvl
    );
    if (!candidateLevel) {
      return false;
    }
    const format = candidateLevel.format?.trim().toLowerCase();
    if (
      !format ||
      format === "none" ||
      numberingLevelIsBulletLike(candidateLevel)
    ) {
      return false;
    }
    if (!candidateLevel.text || !/%\d+/.test(candidateLevel.text)) {
      return false;
    }
    if (ilvl > 0) {
      const parentLevel = findNumberingLevelDefinition(
        numberingDefinitions,
        instance.numId,
        ilvl - 1
      );
      if (!parentLevel) {
        return false;
      }
      const parentFormat = parentLevel.format?.trim().toLowerCase();
      if (
        !parentFormat ||
        parentFormat === "none" ||
        numberingLevelIsBulletLike(parentLevel)
      ) {
        return false;
      }
    }
    return true;
  });

  return candidate?.numId ?? numId;
}

function numberingStartValue(
  numbering: NumberingDefinitionSet,
  numId: number,
  ilvl: number
): number {
  const instance = numbering.instances.find((item) => item.numId === numId);
  const override = instance?.levelStartOverrides?.[String(ilvl)];
  if (Number.isFinite(override)) {
    return Math.max(1, Math.round(override as number));
  }

  const level = findNumberingLevelDefinition(numbering, numId, ilvl);
  if (Number.isFinite(level?.start) && (level?.start as number) > 0) {
    return Math.round(level?.start as number);
  }

  return 1;
}

function numberingSuffix(level: NumberingLevelDefinition | undefined): string {
  if (level?.suffix === "tab") {
    return "\t";
  }
  if (level?.suffix === "space") {
    return " ";
  }
  return "";
}

const LEGACY_BULLET_GLYPH_FALLBACKS: Record<string, string> = {
  "\uf0a7": "□",
  "\uf0a8": "▪",
  "\uf0b7": "•",
  "\uf0d8": "➢",
};

function normalizeLegacyBulletGlyphs(
  text: string,
  bulletFontFamily?: string
): string {
  if (!text) {
    return text;
  }

  const normalizedFont = bulletFontFamily?.trim().toLowerCase() ?? "";
  const fontUsesLegacyGlyphs =
    normalizedFont.includes("wingdings") ||
    normalizedFont.includes("symbol") ||
    normalizedFont.includes("courier");
  if (!fontUsesLegacyGlyphs && !/[\uf0a7\uf0a8\uf0b7\uf0d8]/.test(text)) {
    return text;
  }

  return Array.from(text)
    .map((glyph) => {
      const mapped = LEGACY_BULLET_GLYPH_FALLBACKS[glyph];
      if (mapped) {
        return mapped;
      }
      if (glyph === "o" && normalizedFont.includes("courier")) {
        return "◦";
      }
      return glyph;
    })
    .join("");
}

export function buildParagraphNumberingLabels(
  model: DocModel
): Map<string, ParagraphNumberingLabel> {
  const labels = new Map<string, ParagraphNumberingLabel>();
  const numbering = model.metadata.numberingDefinitions;
  if (!numbering) {
    return labels;
  }

  const numberingInstanceByNumId = new Map(
    numbering.instances.map((instance) => [instance.numId, instance])
  );
  const paragraphStyleById = new Map(
    (model.metadata.paragraphStyles ?? []).map((styleDefinition) => [
      styleDefinition.id,
      styleDefinition,
    ])
  );
  const countersByNumId = new Map<number, Array<number | undefined>>();
  const abstractCountersByAbstractNumId = new Map<
    number,
    Array<number | undefined>
  >();

  const registerParagraph = (paragraph: ParagraphNode, key: string): void => {
    const paragraphNumbering = paragraph.style?.numbering;
    if (!paragraphNumbering || paragraphNumbering.numId <= 0) {
      return;
    }

    const numId =
      effectiveNumberingNumIdForParagraph(paragraph, numbering) ??
      paragraphNumbering.numId;
    const ilvl = Math.max(0, paragraphNumbering.ilvl ?? 0);
    const level = findNumberingLevelDefinition(numbering, numId, ilvl);
    const numberingInstance = numberingInstanceByNumId.get(numId);
    const abstractNumId = numberingInstance?.abstractNumId;
    const template = level?.text ?? `%${ilvl + 1}.`;
    if (!template || template.trim().length === 0) {
      return;
    }

    const counters = countersByNumId.get(numId) ?? [];
    const sharedAbstractCounters = Number.isFinite(abstractNumId)
      ? abstractCountersByAbstractNumId.get(abstractNumId as number) ?? []
      : undefined;
    let parentCountersChanged = false;
    for (let index = 0; index < ilvl; index += 1) {
      const abstractCounterValue =
        sharedAbstractCounters && Number.isFinite(sharedAbstractCounters[index])
          ? Math.max(1, Math.round(sharedAbstractCounters[index] as number))
          : undefined;
      if (abstractCounterValue !== undefined) {
        const previousCounter = counters[index];
        if (
          !Number.isFinite(previousCounter) ||
          Math.max(1, Math.round(previousCounter as number)) !==
            abstractCounterValue
        ) {
          parentCountersChanged = true;
        }
        counters[index] = abstractCounterValue;
        continue;
      }
      if (!Number.isFinite(counters[index])) {
        counters[index] = numberingStartValue(numbering, numId, index);
      }
    }

    const currentValue = parentCountersChanged ? undefined : counters[ilvl];
    counters[ilvl] = Number.isFinite(currentValue)
      ? (currentValue as number) + 1
      : numberingStartValue(numbering, numId, ilvl);
    for (let index = ilvl + 1; index < counters.length; index += 1) {
      counters[index] = undefined;
    }
    countersByNumId.set(numId, counters);
    if (sharedAbstractCounters && Number.isFinite(abstractNumId)) {
      for (let index = 0; index <= ilvl; index += 1) {
        sharedAbstractCounters[index] = counters[index];
      }
      for (
        let index = ilvl + 1;
        index < sharedAbstractCounters.length;
        index += 1
      ) {
        sharedAbstractCounters[index] = undefined;
      }
      abstractCountersByAbstractNumId.set(
        abstractNumId as number,
        sharedAbstractCounters
      );
    }

    const label = template.replaceAll(/%(\d+)/g, (_, rawLevel) => {
      const levelIndex = Math.max(0, Number(rawLevel) - 1);
      const levelValue = counters[levelIndex];
      const safeValue = Number.isFinite(levelValue)
        ? Math.max(1, Math.round(levelValue as number))
        : numberingStartValue(numbering, numId, levelIndex);
      const levelFormat = findNumberingLevelDefinition(
        numbering,
        numId,
        levelIndex
      )?.format;
      return formatNumberingCounter(levelFormat, safeValue);
    });

    if (!label || label.trim().length === 0) {
      return;
    }

    const isBullet = (level?.format ?? "").trim().toLowerCase() === "bullet";
    const trailingText = numberingSuffix(level);
    if (isBullet && level?.pictureBullet?.src) {
      labels.set(key, {
        imageSrc: level.pictureBullet.src,
        imageWidthPx: level.pictureBullet.widthPx,
        imageHeightPx: level.pictureBullet.heightPx,
        trailingText,
      });
      return;
    }

    const firstStyledRun = paragraph.children.find(
      (child): child is TextRunNode | FormFieldRunNode =>
        (child.type === "text" || child.type === "form-field") &&
        Boolean(child.style)
    );
    const paragraphStyleRunStyle = paragraph.style?.styleId
      ? paragraphStyleById.get(paragraph.style.styleId)?.runStyle
      : undefined;
    const baseNumberingTextStyle =
      firstStyledRun?.style ?? paragraphStyleRunStyle;
    const numberingTextStyle = level?.runStyle
      ? { ...(baseNumberingTextStyle ?? {}), ...level.runStyle }
      : baseNumberingTextStyle;
    const normalizedLabel = isBullet
      ? normalizeLegacyBulletGlyphs(label, level?.bulletFontFamily)
      : label;
    labels.set(key, {
      text: `${normalizedLabel}${trailingText}`,
      fontFamily: isBullet ? level?.bulletFontFamily : undefined,
      color: isBullet ? level?.bulletColor : undefined,
      style: numberingTextStyle ? { ...numberingTextStyle } : undefined,
    });
  };

  model.nodes.forEach((node, nodeIndex) => {
    if (node.type === "paragraph") {
      registerParagraph(node, `p:${nodeIndex}`);
      return;
    }

    node.rows.forEach((row, rowIndex) => {
      row.cells.forEach((cell, cellIndex) => {
        tableCellParagraphs(cell.nodes).forEach((paragraph, paragraphIndex) => {
          registerParagraph(
            paragraph,
            `t:${nodeIndex}:${rowIndex}:${cellIndex}:${paragraphIndex}`
          );
        });
      });
    });
  });

  return labels;
}

const UNORDERED_LIST_PREFIX_PATTERN = /^\s*•\s+/;
const ORDERED_LIST_PREFIX_PATTERN = /^\s*\d+\.\s+/;
const LIST_PREFIX_PATTERN = /^\s*(?:•\s+|\d+\.\s+)/;
const ORDERED_LIST_PREFIX_CAPTURE_PATTERN = /^(\s*)(\d+)\.\s+/;
const LIST_LEVEL_STEP_TWIPS = 720;
const DEFAULT_LIST_HANGING_TWIPS = 360;
const MAX_FALLBACK_LIST_LEVEL = 8;

function isUnorderedListText(text: string): boolean {
  return UNORDERED_LIST_PREFIX_PATTERN.test(text);
}

function isOrderedListText(text: string): boolean {
  return ORDERED_LIST_PREFIX_PATTERN.test(text);
}

function isBulletLikeNumberingText(
  text: string,
  bulletFontFamily?: string
): boolean {
  const normalized = normalizeLegacyBulletGlyphs(text, bulletFontFamily).trim();
  if (!normalized || /%[\d]+/.test(normalized)) {
    return false;
  }

  return /^[\u2022\u25cf\u25cb\u25a0\u25a1\u25aa\u25ab\u25c6\u25c7\u25e6\u2043\-–—]+$/.test(
    normalized
  );
}

function paragraphHasNumbering(paragraph: ParagraphNode): boolean {
  return Boolean(
    paragraph.style?.numbering && paragraph.style.numbering.numId > 0
  );
}

function paragraphListType(
  paragraph: ParagraphNode,
  numberingDefinitions?: NumberingDefinitionSet
): DocxListType | undefined {
  const numbering = paragraph.style?.numbering;
  if (numbering && numbering.numId > 0) {
    if (numberingDefinitions) {
      const effectiveNumId =
        effectiveNumberingNumIdForParagraph(paragraph, numberingDefinitions) ??
        numbering.numId;
      const level = findNumberingLevelDefinition(
        numberingDefinitions,
        effectiveNumId,
        Math.max(0, numbering.ilvl ?? 0)
      );
      const format = level?.format?.trim().toLowerCase();
      if (
        format === "bullet" ||
        level?.pictureBullet ||
        isBulletLikeNumberingText(level?.text ?? "", level?.bulletFontFamily)
      ) {
        return "unordered";
      }
      if (format && format !== "none") {
        return "ordered";
      }
    }
    // If numbering exists but we cannot resolve a specific list format, treat it as ordered.
    return "ordered";
  }

  const text = paragraphText(paragraph);
  if (isUnorderedListText(text)) {
    return "unordered";
  }
  if (isOrderedListText(text)) {
    return "ordered";
  }
  return undefined;
}

function paragraphIsList(
  paragraph: ParagraphNode,
  textOverride?: string
): boolean {
  const text = textOverride ?? paragraphText(paragraph);
  return (
    paragraphHasNumbering(paragraph) ||
    isUnorderedListText(text) ||
    isOrderedListText(text)
  );
}

function stripListPrefix(text: string): string {
  return text.replace(/^\s*(?:•\s+|\d+\.\s+)/, "");
}

function listPrefixLength(text: string): number {
  return text.match(LIST_PREFIX_PATTERN)?.[0]?.length ?? 0;
}

function nextOrderedListItemText(
  currentText: string,
  nextItemText: string
): string {
  const match = currentText.match(ORDERED_LIST_PREFIX_CAPTURE_PATTERN);
  const leadingWhitespace = match?.[1] ?? "";
  const currentNumber = Number.parseInt(match?.[2] ?? "", 10);
  const nextNumber = Number.isFinite(currentNumber)
    ? Math.max(1, currentNumber + 1)
    : 1;
  return `${leadingWhitespace}${nextNumber}. ${stripListPrefix(nextItemText)}`;
}

function textWithListType(text: string, listType: DocxListType): string {
  const normalized = stripListPrefix(text);
  return listType === "unordered" ? `• ${normalized}` : `1. ${normalized}`;
}

function cloneParagraphBorderStyle(
  border?: ParagraphBorderStyle
): ParagraphBorderStyle | undefined {
  return border ? { ...border } : undefined;
}

function cloneParagraphBorderSet(
  borders?: ParagraphBorderSet
): ParagraphBorderSet | undefined {
  if (!borders) {
    return undefined;
  }

  return {
    top: cloneParagraphBorderStyle(borders.top),
    right: cloneParagraphBorderStyle(borders.right),
    bottom: cloneParagraphBorderStyle(borders.bottom),
    left: cloneParagraphBorderStyle(borders.left),
    between: cloneParagraphBorderStyle(borders.between),
    bar: cloneParagraphBorderStyle(borders.bar),
  };
}

function cloneParagraphStyle(
  style?: ParagraphNode["style"]
): ParagraphNode["style"] | undefined {
  if (!style) {
    return undefined;
  }

  return {
    ...style,
    numbering: style.numbering ? { ...style.numbering } : undefined,
    spacing: style.spacing ? { ...style.spacing } : undefined,
    indent: style.indent ? { ...style.indent } : undefined,
    tabStops: style.tabStops
      ? style.tabStops.map((tabStop) => ({ ...tabStop }))
      : undefined,
    borders: cloneParagraphBorderSet(style.borders),
    dropCap: style.dropCap ? { ...style.dropCap } : undefined,
  };
}

function splitParagraphStyleWithDefaultSpacing(
  style?: ParagraphNode["style"],
  sourceXml?: string
): ParagraphNode["style"] | undefined {
  const clonedStyle = cloneParagraphStyle(style);
  // Keep imported/explicit paragraph styling untouched.
  if (sourceXml || clonedStyle?.styleId) {
    return clonedStyle;
  }

  const spacing = clonedStyle?.spacing;
  const hasExplicitSpacing =
    spacing?.beforeTwips !== undefined ||
    spacing?.afterTwips !== undefined ||
    spacing?.lineTwips !== undefined ||
    spacing?.lineRule !== undefined;
  if (hasExplicitSpacing) {
    return clonedStyle;
  }

  return {
    ...(clonedStyle ?? {}),
    spacing: {
      ...(spacing ?? {}),
      lineRule: "auto",
      lineTwips: DEFAULT_SPLIT_PARAGRAPH_LINE_TWIPS,
      afterTwips: DEFAULT_SPLIT_PARAGRAPH_AFTER_TWIPS,
    },
  };
}

function cloneTableBoxSpacing(
  spacing?: TableCellStyle["marginTwips"]
): TableCellStyle["marginTwips"] | undefined {
  if (!spacing) {
    return undefined;
  }

  return {
    topTwips: spacing.topTwips,
    rightTwips: spacing.rightTwips,
    bottomTwips: spacing.bottomTwips,
    leftTwips: spacing.leftTwips,
  };
}

function cloneTableBorderStyle(
  border?: TableBorderStyle
): TableBorderStyle | undefined {
  if (!border) {
    return undefined;
  }

  return {
    type: border.type,
    color: border.color,
    sizeEighthPt: border.sizeEighthPt,
  };
}

function cloneTableBorderSet(
  borders?: TableBorderSet
): TableBorderSet | undefined {
  if (!borders) {
    return undefined;
  }

  return {
    top: cloneTableBorderStyle(borders.top),
    right: cloneTableBorderStyle(borders.right),
    bottom: cloneTableBorderStyle(borders.bottom),
    left: cloneTableBorderStyle(borders.left),
    insideH: cloneTableBorderStyle(borders.insideH),
    insideV: cloneTableBorderStyle(borders.insideV),
    tl2br: cloneTableBorderStyle(borders.tl2br),
    tr2bl: cloneTableBorderStyle(borders.tr2bl),
  };
}

function cloneTableCellStyle(
  style?: TableCellStyle
): TableCellStyle | undefined {
  if (!style) {
    return undefined;
  }

  return {
    ...style,
    marginTwips: cloneTableBoxSpacing(style.marginTwips),
    borders: cloneTableBorderSet(style.borders),
  };
}

function cloneTableRowStyle(style?: TableRowStyle): TableRowStyle | undefined {
  if (!style) {
    return undefined;
  }

  return {
    ...style,
  };
}

function createEmptyParagraphFromTemplate(
  template?: ParagraphNode
): ParagraphNode {
  const nextTextStyle = cloneTextStyle(firstRunStyle(template));

  return {
    type: "paragraph",
    style: cloneParagraphStyle(template?.style),
    children: [
      {
        type: "text",
        text: "",
        style: nextTextStyle,
      },
    ],
  };
}

function createEmptyTableCellFromTemplate(
  templateCell?: TableNode["rows"][number]["cells"][number]
): TableNode["rows"][number]["cells"][number] {
  const templateParagraph = templateCell
    ? tableCellParagraphs(templateCell.nodes)[0]
    : undefined;
  const clonedStyle = cloneTableCellStyle(templateCell?.style);
  const normalizedStyle = clonedStyle
    ? {
        ...clonedStyle,
        rowSpan: undefined,
        vMergeContinuation: undefined,
      }
    : undefined;

  return {
    type: "table-cell",
    style: normalizedStyle,
    nodes: [createEmptyParagraphFromTemplate(templateParagraph)],
  };
}

function resolveMaxNumberingLevel(
  numberingDefinitions: NumberingDefinitionSet | undefined,
  numId: number
): number {
  if (!numberingDefinitions) {
    return MAX_FALLBACK_LIST_LEVEL;
  }

  const instance = numberingDefinitions.instances.find(
    (item) => item.numId === numId
  );
  if (!instance) {
    return MAX_FALLBACK_LIST_LEVEL;
  }

  const abstract = numberingDefinitions.abstracts.find(
    (item) => item.abstractNumId === instance.abstractNumId
  );
  const levels = [
    ...(abstract?.levels ?? []),
    ...(instance.levelOverrides ?? []),
  ];
  if (levels.length === 0) {
    return MAX_FALLBACK_LIST_LEVEL;
  }

  return clampNumber(
    Math.max(
      0,
      ...levels.map((level) => Math.max(0, Math.round(level.ilvl ?? 0)))
    ),
    0,
    MAX_FALLBACK_LIST_LEVEL
  );
}

function shiftListIndent(
  indent: ParagraphIndent | undefined,
  levelDelta: number
): ParagraphIndent | undefined {
  if (!levelDelta) {
    return indent;
  }

  const nextLeftTwips = Math.max(
    0,
    (indent?.leftTwips ?? 0) + levelDelta * LIST_LEVEL_STEP_TWIPS
  );
  const nextHangingTwips =
    indent?.hangingTwips ??
    (nextLeftTwips > 0 ? DEFAULT_LIST_HANGING_TWIPS : undefined);

  return {
    ...(indent ?? {}),
    leftTwips: nextLeftTwips,
    hangingTwips: nextHangingTwips,
  };
}

function ensurePrefixListIndent(paragraph: ParagraphNode): boolean {
  const clonedStyle = cloneParagraphStyle(paragraph.style) ?? {};
  const existingIndent = clonedStyle.indent;
  const hasMeaningfulLeftIndent =
    Number.isFinite(existingIndent?.leftTwips) &&
    Math.abs(existingIndent?.leftTwips ?? 0) > 0;
  const hasMeaningfulFirstLine =
    Number.isFinite(existingIndent?.firstLineTwips) &&
    Math.abs(existingIndent?.firstLineTwips ?? 0) > 0;
  const hasMeaningfulHanging =
    Number.isFinite(existingIndent?.hangingTwips) &&
    Math.abs(existingIndent?.hangingTwips ?? 0) > 0;

  if (
    hasMeaningfulLeftIndent ||
    hasMeaningfulFirstLine ||
    hasMeaningfulHanging
  ) {
    return false;
  }

  paragraph.style = {
    ...clonedStyle,
    indent: {
      ...(existingIndent ?? {}),
      leftTwips: LIST_LEVEL_STEP_TWIPS,
      hangingTwips: DEFAULT_LIST_HANGING_TWIPS,
    },
  };
  paragraph.sourceXml = undefined;
  return true;
}

function clearAutoPrefixListIndent(paragraph: ParagraphNode): boolean {
  const clonedStyle = cloneParagraphStyle(paragraph.style);
  const existingIndent = clonedStyle?.indent;
  if (!clonedStyle || !existingIndent) {
    return false;
  }

  const leftTwips = existingIndent.leftTwips;
  const hangingTwips = existingIndent.hangingTwips;
  const firstLineTwips = existingIndent.firstLineTwips;
  const matchesAutoIndent =
    Number.isFinite(leftTwips) &&
    Math.round(leftTwips ?? 0) === LIST_LEVEL_STEP_TWIPS &&
    Number.isFinite(hangingTwips) &&
    Math.round(hangingTwips ?? 0) === DEFAULT_LIST_HANGING_TWIPS &&
    !Number.isFinite(firstLineTwips);
  if (!matchesAutoIndent) {
    return false;
  }

  const nextIndent: ParagraphIndent = {
    ...existingIndent,
    leftTwips: undefined,
    hangingTwips: undefined,
  };
  const hasRemainingIndent = [
    nextIndent.leftTwips,
    nextIndent.rightTwips,
    nextIndent.firstLineTwips,
    nextIndent.hangingTwips,
  ].some((value) => Number.isFinite(value));

  paragraph.style = {
    ...clonedStyle,
    indent: hasRemainingIndent ? nextIndent : undefined,
  };
  paragraph.sourceXml = undefined;
  return true;
}

function tableCellText(paragraphs: ParagraphNode[]): string {
  return paragraphs.map(paragraphText).join("\n");
}

function tableColumnCount(table: TableNode): number {
  return Math.max(
    1,
    ...table.rows.map((row) =>
      row.cells.reduce(
        (total, cell) =>
          total +
          (cell.style?.gridSpan && cell.style.gridSpan > 1
            ? cell.style.gridSpan
            : 1),
        0
      )
    )
  );
}

function resolveFloatingTableSide(
  table: TableNode
): "left" | "right" | undefined {
  const floating = table.style?.floating;
  if (!floating) {
    return undefined;
  }

  const horizontalAlign = floating.horizontalAlign?.toLowerCase();
  if (horizontalAlign === "right" || horizontalAlign === "outside") {
    return "right";
  }
  if (horizontalAlign === "left" || horizontalAlign === "inside") {
    return "left";
  }

  if (Number.isFinite(floating.xTwips) && (floating.xTwips as number) > 1440) {
    return "right";
  }

  return "left";
}

function tableWrapperStyle(
  table: TableNode,
  indentPx: number
): React.CSSProperties {
  const floating = table.style?.floating;
  if (!floating) {
    return {
      marginLeft: indentPx,
      position: "relative",
    };
  }

  const side = resolveFloatingTableSide(table) ?? "left";
  const marginTop = twipsToPixels(floating.topFromTextTwips) ?? 0;
  const marginBottom = twipsToPixels(floating.bottomFromTextTwips) ?? 8;
  const marginLeftFromText = twipsToPixels(floating.leftFromTextTwips) ?? 8;
  const marginRightFromText = twipsToPixels(floating.rightFromTextTwips) ?? 8;

  return {
    float: side,
    clear: "none",
    marginTop,
    marginBottom,
    marginLeft:
      side === "left" ? marginLeftFromText + indentPx : marginLeftFromText,
    marginRight:
      side === "right" ? marginRightFromText + indentPx : marginRightFromText,
    position: "relative",
    zIndex: 1,
  };
}

interface EmbeddedTableRuntimeKeySegment {
  rowIndex: number;
  cellIndex: number;
  contentIndex: number;
}

interface EmbeddedTableRuntimeKeyLocation {
  hostTableIndex: number;
  hostRowIndex: number;
  hostCellIndex: number;
  rootContentIndex: number;
  descendants: EmbeddedTableRuntimeKeySegment[];
}

function parseEmbeddedTableRuntimeKey(
  tableRuntimeKey: string
): EmbeddedTableRuntimeKeyLocation | undefined {
  const tokens = tableRuntimeKey.split("-");
  const parseIndex = (token: string | undefined): number | undefined => {
    const value = Number(token);
    if (!Number.isInteger(value) || value < 0) {
      return undefined;
    }
    return value;
  };

  if (
    tokens.length < 8 ||
    (tokens[0] !== "body" && tokens[0] !== "active") ||
    tokens[1] !== "cell" ||
    tokens[2] !== "nested" ||
    tokens[3] !== "table"
  ) {
    return undefined;
  }

  const hostTableIndex = parseIndex(tokens[4]);
  const hostRowIndex = parseIndex(tokens[5]);
  const hostCellIndex = parseIndex(tokens[6]);
  const rootContentIndex = parseIndex(tokens[7]);
  if (
    hostTableIndex === undefined ||
    hostRowIndex === undefined ||
    hostCellIndex === undefined ||
    rootContentIndex === undefined
  ) {
    return undefined;
  }

  const descendants: EmbeddedTableRuntimeKeySegment[] = [];
  let cursor = 8;
  while (cursor < tokens.length) {
    if (tokens[cursor] !== "nested" || tokens[cursor + 1] !== "table") {
      return undefined;
    }

    const rowIndex = parseIndex(tokens[cursor + 2]);
    const cellIndex = parseIndex(tokens[cursor + 3]);
    const contentIndex = parseIndex(tokens[cursor + 4]);
    if (
      rowIndex === undefined ||
      cellIndex === undefined ||
      contentIndex === undefined
    ) {
      return undefined;
    }

    descendants.push({ rowIndex, cellIndex, contentIndex });
    cursor += 5;
  }

  return {
    hostTableIndex,
    hostRowIndex,
    hostCellIndex,
    rootContentIndex,
    descendants,
  };
}

const columnWidthsByTable = new WeakMap<
  TableNode,
  Map<number, number[] | undefined>
>();

function columnWidthsFromTableDefinition(
  table: TableNode,
  columnCount: number
): number[] | undefined {
  const cachedByCount = columnWidthsByTable.get(table);
  if (cachedByCount?.has(columnCount)) {
    return cachedByCount.get(columnCount);
  }

  const resolved = computeColumnWidthsFromTableDefinition(table, columnCount);
  const cache = cachedByCount ?? new Map<number, number[] | undefined>();
  cache.set(columnCount, resolved);
  columnWidthsByTable.set(table, cache);
  return resolved;
}

function computeColumnWidthsFromTableDefinition(
  table: TableNode,
  columnCount: number
): number[] | undefined {
  const gridWidths = table.style?.columnWidthsTwips;
  const rowDerivedWidths = deriveColumnWidthsFromTableRows(table, columnCount);

  if (gridWidths && gridWidths.length === columnCount) {
    // Some generators emit a placeholder uniform grid while the real column
    // geometry lives in per-cell tcW. Word's fixed-layout algorithm trusts
    // cell widths over the grid, so when the two disagree on most measured
    // cells, prefer the row-derived widths.
    if (
      rowDerivedWidths &&
      rowDerivedWidths.length > 0 &&
      gridConflictsWithRowWidths(table, gridWidths)
    ) {
      return rowDerivedWidths;
    }
    return normalizeColumnWidthsTwips(gridWidths, columnCount);
  }

  if (rowDerivedWidths && rowDerivedWidths.length > 0) {
    return rowDerivedWidths;
  }

  if (gridWidths && gridWidths.length > 0) {
    return normalizeColumnWidthsTwips(gridWidths, columnCount);
  }

  return undefined;
}

function gridConflictsWithRowWidths(
  table: TableNode,
  gridWidths: number[]
): boolean {
  let conflictRows = 0;
  let measuredRows = 0;

  for (const row of table.rows) {
    let columnCursor = 0;
    let measuredCells = 0;
    let conflictCells = 0;

    for (const cell of row.cells) {
      const span =
        cell.style?.gridSpan && cell.style.gridSpan > 1
          ? cell.style.gridSpan
          : 1;
      const expected = gridWidths
        .slice(columnCursor, columnCursor + span)
        .reduce((sum, value) => sum + Math.max(0, value), 0);
      columnCursor += span;

      const actual = cell.style?.widthTwips;
      if (!actual || actual <= 0 || expected <= 0) {
        continue;
      }
      measuredCells += 1;
      if (Math.abs(actual - expected) / expected > 0.2) {
        conflictCells += 1;
      }
    }

    if (measuredCells > 0) {
      measuredRows += 1;
      if (conflictCells * 2 > measuredCells) {
        conflictRows += 1;
      }
    }
  }

  return measuredRows > 0 && conflictRows * 2 > measuredRows;
}

function deriveColumnWidthsFromTableRows(
  table: TableNode,
  columnCount: number
): number[] | undefined {
  let bestCandidate: number[] | undefined;
  let bestPositiveCount = -1;
  let bestTotalWidth = -1;

  for (const row of table.rows) {
    const candidate: number[] = [];
    for (const cell of row.cells) {
      const span =
        cell.style?.gridSpan && cell.style.gridSpan > 1
          ? cell.style.gridSpan
          : 1;
      const cellWidth = cell.style?.widthTwips;

      if (cellWidth && cellWidth > 0) {
        const perColumn = cellWidth / span;
        for (let index = 0; index < span; index += 1) {
          candidate.push(perColumn);
        }
        continue;
      }

      for (let index = 0; index < span; index += 1) {
        candidate.push(0);
      }
    }

    if (candidate.length !== columnCount || candidate.length === 0) {
      continue;
    }

    const positiveCount = candidate.filter((value) => value > 0).length;
    if (positiveCount <= 0) {
      continue;
    }

    const totalWidth = candidate.reduce(
      (sum, value) => sum + (value > 0 ? value : 0),
      0
    );
    if (
      positiveCount > bestPositiveCount ||
      (positiveCount === bestPositiveCount && totalWidth > bestTotalWidth)
    ) {
      bestCandidate = candidate;
      bestPositiveCount = positiveCount;
      bestTotalWidth = totalWidth;
    }
  }

  if (!bestCandidate) {
    return undefined;
  }

  return normalizeColumnWidthsTwips(bestCandidate, columnCount);
}

function normalizeColumnWidthsTwips(
  widths: number[],
  columnCount: number
): number[] {
  const fallback = 1440 / Math.max(1, columnCount);
  const sanitized = Array.from({ length: columnCount }, (_, index) => {
    const raw = widths[index];
    if (!Number.isFinite(raw)) {
      return fallback;
    }
    return Math.max(1, raw);
  });

  if (sanitized.every((value) => value <= 0)) {
    return Array.from({ length: columnCount }, () => fallback);
  }

  return sanitized;
}

function normalizeColumnWidthsPx(
  widths: number[],
  columnCount: number,
  fallbackTableWidthPx?: number,
  minimumWidthPx = 24
): number[] {
  const fallbackWidth =
    Number.isFinite(fallbackTableWidthPx) &&
    (fallbackTableWidthPx as number) > 0
      ? (fallbackTableWidthPx as number) / Math.max(1, columnCount)
      : 140;

  return Array.from({ length: columnCount }, (_, index) => {
    const raw = widths[index];
    if (!Number.isFinite(raw) || (raw as number) <= 0) {
      return Math.max(minimumWidthPx, Math.round(fallbackWidth));
    }
    return Math.max(minimumWidthPx, Math.round(raw as number));
  });
}

function defaultColumnWidthsPx(
  columnCount: number,
  tableWidthPx?: number
): number[] {
  const fallbackWidth =
    Number.isFinite(tableWidthPx) && (tableWidthPx as number) > 0
      ? (tableWidthPx as number) / Math.max(1, columnCount)
      : 140;
  return Array.from({ length: columnCount }, () =>
    Math.max(24, Math.round(fallbackWidth))
  );
}

function clampTableWidthPx(widthPx: number, maxWidthPx?: number): number {
  if (!Number.isFinite(widthPx) || widthPx <= 0) {
    return 1;
  }

  if (!Number.isFinite(maxWidthPx) || (maxWidthPx as number) <= 0) {
    return Math.max(1, Math.round(widthPx));
  }

  return Math.max(
    1,
    Math.min(Math.round(widthPx), Math.round(maxWidthPx as number))
  );
}

function fitColumnWidthsToWidth(
  columnWidths: number[],
  targetWidthPx: number
): number[] {
  if (columnWidths.length === 0) {
    return [];
  }

  if (!Number.isFinite(targetWidthPx) || targetWidthPx <= 0) {
    return [...columnWidths];
  }

  const sanitized = columnWidths.map((value) =>
    Number.isFinite(value) && (value as number) > 0 ? (value as number) : 1
  );
  const currentTotal = sanitized.reduce((sum, value) => sum + value, 0);
  if (!Number.isFinite(currentTotal) || currentTotal <= 0) {
    const even = Math.max(1, targetWidthPx / sanitized.length);
    return Array.from({ length: sanitized.length }, () => even);
  }

  if (Math.abs(currentTotal - targetWidthPx) <= 0.5) {
    return sanitized;
  }

  if (currentTotal < targetWidthPx) {
    const scale = targetWidthPx / currentTotal;
    return sanitized.map((value) => Math.max(1, value * scale));
  }

  const minimumWidthPx = 8;
  if (sanitized.length * minimumWidthPx >= targetWidthPx) {
    const even = Math.max(1, targetWidthPx / sanitized.length);
    return Array.from({ length: sanitized.length }, () => even);
  }

  const scaled = sanitized.map((value) =>
    Math.max(minimumWidthPx, (value / currentTotal) * targetWidthPx)
  );
  let overflow = scaled.reduce((sum, value) => sum + value, 0) - targetWidthPx;
  let guard = 0;

  while (overflow > 0.25 && guard < 64) {
    const adjustableIndexes = scaled
      .map((value, index) => ({ value, index }))
      .filter((entry) => entry.value > minimumWidthPx + 0.01);
    if (adjustableIndexes.length === 0) {
      break;
    }

    const adjustableTotal = adjustableIndexes.reduce(
      (sum, entry) => sum + (entry.value - minimumWidthPx),
      0
    );
    if (adjustableTotal <= 0) {
      break;
    }

    for (const entry of adjustableIndexes) {
      const share =
        ((entry.value - minimumWidthPx) / adjustableTotal) * overflow;
      scaled[entry.index] = Math.max(minimumWidthPx, entry.value - share);
    }

    overflow = scaled.reduce((sum, value) => sum + value, 0) - targetWidthPx;
    guard += 1;
  }

  return scaled;
}

function rowGridSpanCount(
  row: TableNode["rows"][number],
  maxColumnCount: number
): number {
  const span = row.cells.reduce((total, cell) => {
    const cellSpan =
      cell.style?.gridSpan && cell.style.gridSpan > 1 ? cell.style.gridSpan : 1;
    return total + cellSpan;
  }, 0);

  return Math.max(0, Math.min(maxColumnCount, span));
}

function resolveFittedTableColumnWidths(
  table: TableNode,
  rawColumnWidthsPx: number[],
  targetWidthPx: number
): {
  columnWidthsPx: number[];
  effectiveColumnCount: number;
} {
  const columnCount = rawColumnWidthsPx.length;
  if (columnCount === 0) {
    return {
      columnWidthsPx: [],
      effectiveColumnCount: 0,
    };
  }

  const fallback = fitColumnWidthsToWidth(rawColumnWidthsPx, targetWidthPx);
  const rawTotalWidthPx = rawColumnWidthsPx.reduce(
    (sum, widthPx) => sum + widthPx,
    0
  );
  if (
    table.style?.layout !== "fixed" ||
    table.rows.length < 2 ||
    !Number.isFinite(rawTotalWidthPx) ||
    rawTotalWidthPx <= 0 ||
    !Number.isFinite(targetWidthPx) ||
    targetWidthPx <= 0 ||
    rawTotalWidthPx <= targetWidthPx + 0.5
  ) {
    return {
      columnWidthsPx: fallback,
      effectiveColumnCount: columnCount,
    };
  }

  const rowSpanCounts = table.rows.map((row) =>
    rowGridSpanCount(row, columnCount)
  );
  const spanFrequency = new Map<number, number>();
  rowSpanCounts.forEach((spanCount) => {
    if (spanCount <= 0 || spanCount >= columnCount) {
      return;
    }
    spanFrequency.set(spanCount, (spanFrequency.get(spanCount) ?? 0) + 1);
  });

  if (spanFrequency.size === 0) {
    return {
      columnWidthsPx: fallback,
      effectiveColumnCount: columnCount,
    };
  }

  let dominantSpanCount = columnCount;
  let dominantSpanFrequency = 0;
  for (const [spanCount, frequency] of spanFrequency.entries()) {
    if (
      frequency > dominantSpanFrequency ||
      (frequency === dominantSpanFrequency && spanCount > dominantSpanCount)
    ) {
      dominantSpanCount = spanCount;
      dominantSpanFrequency = frequency;
    }
  }

  const dominantCoverage =
    dominantSpanFrequency / Math.max(1, rowSpanCounts.length);
  if (
    dominantCoverage < 0.6 ||
    dominantSpanCount <= 0 ||
    dominantSpanCount >= columnCount
  ) {
    return {
      columnWidthsPx: fallback,
      effectiveColumnCount: columnCount,
    };
  }

  const trailingColumnWidthsPx = rawColumnWidthsPx.slice(dominantSpanCount);
  const trailingWidthPx = trailingColumnWidthsPx.reduce(
    (sum, widthPx) => sum + widthPx,
    0
  );
  const trailingRatio = trailingWidthPx / rawTotalWidthPx;
  if (
    !Number.isFinite(trailingWidthPx) ||
    trailingWidthPx < 24 ||
    trailingRatio < 0.12
  ) {
    return {
      columnWidthsPx: fallback,
      effectiveColumnCount: columnCount,
    };
  }

  const leadingWidthsPx = rawColumnWidthsPx.slice(0, dominantSpanCount);
  const fittedLeadingWidthsPx = fitColumnWidthsToWidth(
    leadingWidthsPx,
    targetWidthPx
  );
  return {
    columnWidthsPx: [
      ...fittedLeadingWidthsPx,
      ...Array.from({ length: columnCount - dominantSpanCount }, () => 0),
    ],
    effectiveColumnCount: dominantSpanCount,
  };
}

function resolveTableSpacingPaddingPx(spacing?: TableSpacingTwips): {
  top: number;
  right: number;
  bottom: number;
  left: number;
} {
  return {
    top:
      twipsToPixels(spacing?.topTwips) ??
      WORD_TABLE_CELL_FALLBACK_PADDING_PX.top,
    right:
      twipsToPixels(spacing?.rightTwips) ??
      WORD_TABLE_CELL_FALLBACK_PADDING_PX.right,
    bottom:
      twipsToPixels(spacing?.bottomTwips) ??
      WORD_TABLE_CELL_FALLBACK_PADDING_PX.bottom,
    left:
      twipsToPixels(spacing?.leftTwips) ??
      WORD_TABLE_CELL_FALLBACK_PADDING_PX.left,
  };
}

function tableSpacingPaddingStyle(
  spacing?: TableSpacingTwips
): React.CSSProperties {
  const { top, right, bottom, left } = resolveTableSpacingPaddingPx(spacing);

  return {
    paddingTop: top,
    paddingRight: right,
    paddingBottom: bottom,
    paddingLeft: left,
  };
}

function mergeTableSpacing(
  baseSpacing?: TableSpacingTwips,
  overrideSpacing?: TableSpacingTwips
): TableSpacingTwips | undefined {
  if (!baseSpacing && !overrideSpacing) {
    return undefined;
  }

  return {
    topTwips: overrideSpacing?.topTwips ?? baseSpacing?.topTwips,
    rightTwips: overrideSpacing?.rightTwips ?? baseSpacing?.rightTwips,
    bottomTwips: overrideSpacing?.bottomTwips ?? baseSpacing?.bottomTwips,
    leftTwips: overrideSpacing?.leftTwips ?? baseSpacing?.leftTwips,
  };
}

type TableBorderSide = "top" | "right" | "bottom" | "left";

function normalizeBorderType(type: string | undefined): string | undefined {
  if (!type) {
    return undefined;
  }

  const normalized = type.trim().toLowerCase();
  return normalized.length > 0 ? normalized : undefined;
}

function tableBorderToCss(
  border: TableBorderStyle | undefined
): string | undefined {
  const type = normalizeBorderType(border?.type);
  if (!type) {
    return undefined;
  }

  if (type === "none" || type === "nil") {
    return "none";
  }

  const cssStyle =
    type === "double"
      ? "double"
      : type === "dashed" ||
        type === "dashsmallgap" ||
        type === "dotdash" ||
        type === "dotdotdash"
      ? "dashed"
      : type === "dotted"
      ? "dotted"
      : "solid";
  const sizeEighthPt = border?.sizeEighthPt;
  const widthPx =
    Number.isFinite(sizeEighthPt) && (sizeEighthPt as number) > 0
      ? Math.max(0.5, Number(((sizeEighthPt as number) / 6).toFixed(2)))
      : 1;
  const color = border?.color ?? "#000000";

  return `${widthPx}px ${cssStyle} ${color}`;
}

function tableBorderStrokeWidthPx(
  border: TableBorderStyle | undefined
): number {
  const type = normalizeBorderType(border?.type);
  if (!type || type === "none" || type === "nil") {
    return 0;
  }

  const sizeEighthPt = border?.sizeEighthPt;
  return Number.isFinite(sizeEighthPt) && (sizeEighthPt as number) > 0
    ? Math.max(0.5, Number(((sizeEighthPt as number) / 6).toFixed(2)))
    : 1;
}

function borderTypeVisible(type: string | undefined): boolean {
  const normalizedType = normalizeBorderType(type);
  return Boolean(
    normalizedType && normalizedType !== "none" && normalizedType !== "nil"
  );
}

function paragraphBorderVisible(
  border: ParagraphBorderStyle | undefined
): boolean {
  return borderTypeVisible(border?.type);
}

function tableBorderVisible(border: TableBorderStyle | undefined): boolean {
  return borderTypeVisible(border?.type);
}

function tableBorderSetHasVisibleEdges(
  borders: TableBorderSet | undefined
): boolean {
  return Boolean(
    tableBorderVisible(borders?.top) ||
      tableBorderVisible(borders?.right) ||
      tableBorderVisible(borders?.bottom) ||
      tableBorderVisible(borders?.left)
  );
}

function tableUsesSeparateBorderModel(table: TableNode): boolean {
  const explicitCellSpacingPx = twipsToPixels(table.style?.cellSpacingTwips);
  if (
    Number.isFinite(explicitCellSpacingPx) &&
    (explicitCellSpacingPx as number) > 0
  ) {
    return true;
  }

  const tableBorders = table.style?.borders;
  if (
    tableBorderVisible(tableBorders?.insideH) ||
    tableBorderVisible(tableBorders?.insideV)
  ) {
    return false;
  }

  return table.rows.some((row) =>
    row.cells.some((cell) => tableBorderSetHasVisibleEdges(cell.style?.borders))
  );
}

function resolveTableSeparateBorderSpacingPx(table: TableNode): number {
  const explicitCellSpacingPx = twipsToPixels(table.style?.cellSpacingTwips);
  if (
    Number.isFinite(explicitCellSpacingPx) &&
    (explicitCellSpacingPx as number) > 0
  ) {
    return Math.max(0, Math.round(explicitCellSpacingPx as number));
  }

  if (
    tableUsesSeparateBorderModel(table) &&
    tableBorderSetHasVisibleEdges(table.style?.borders)
  ) {
    return 1;
  }

  return 0;
}

function tableElementBorderStyle(
  table: TableNode,
  borderSpacingPx = resolveTableSeparateBorderSpacingPx(table)
): React.CSSProperties {
  if (!tableUsesSeparateBorderModel(table)) {
    return {
      borderCollapse: "collapse",
    };
  }

  return {
    borderCollapse: "separate",
    borderSpacing: `${Math.max(0, Math.round(borderSpacingPx))}px`,
  };
}

function resolvePreferredParagraphBorder(
  borders: ParagraphBorderSet | undefined
): ParagraphBorderStyle | undefined {
  if (!borders) {
    return undefined;
  }

  return (
    borders.top ??
    borders.right ??
    borders.bottom ??
    borders.left ??
    borders.between ??
    borders.bar
  );
}

function resolvePreferredTableBorder(
  borders: TableBorderSet | undefined
): TableBorderStyle | undefined {
  if (!borders) {
    return undefined;
  }

  return (
    borders.top ??
    borders.right ??
    borders.bottom ??
    borders.left ??
    borders.insideH ??
    borders.insideV ??
    borders.tl2br ??
    borders.tr2bl
  );
}

function toolbarParagraphBorderStyle(
  seed: ParagraphBorderStyle | undefined
): ParagraphBorderStyle {
  return {
    type: borderTypeVisible(seed?.type) ? (seed?.type as string) : "single",
    sizeEighthPt:
      Number.isFinite(seed?.sizeEighthPt) && (seed?.sizeEighthPt as number) > 0
        ? Math.round(seed?.sizeEighthPt as number)
        : DEFAULT_TOOLBAR_BORDER_SIZE_EIGHTH_PT,
    color: seed?.color ?? DEFAULT_TOOLBAR_BORDER_COLOR,
    ...(Number.isFinite(seed?.spacePt)
      ? { spacePt: Math.max(0, Math.round(seed?.spacePt as number)) }
      : undefined),
  };
}

function toolbarTableBorderStyle(
  seed: TableBorderStyle | undefined
): TableBorderStyle {
  return {
    type: borderTypeVisible(seed?.type) ? (seed?.type as string) : "single",
    sizeEighthPt:
      Number.isFinite(seed?.sizeEighthPt) && (seed?.sizeEighthPt as number) > 0
        ? Math.round(seed?.sizeEighthPt as number)
        : DEFAULT_TOOLBAR_BORDER_SIZE_EIGHTH_PT,
    color: seed?.color ?? DEFAULT_TOOLBAR_BORDER_COLOR,
  };
}

function nilParagraphBorderStyle(): ParagraphBorderStyle {
  return { type: "nil" };
}

function nilTableBorderStyle(): TableBorderStyle {
  return { type: "nil" };
}

function paragraphBorderPresetState(
  borders: ParagraphBorderSet | undefined
): DocxBorderPresetState {
  const top = paragraphBorderVisible(borders?.top);
  const right = paragraphBorderVisible(borders?.right);
  const bottom = paragraphBorderVisible(borders?.bottom);
  const left = paragraphBorderVisible(borders?.left);
  const between = paragraphBorderVisible(borders?.between);
  const bar = paragraphBorderVisible(borders?.bar);
  const hasAny = top || right || bottom || left || between || bar;

  return {
    bottom,
    top,
    left,
    right,
    none: !hasAny,
    all: top && right && bottom && left,
    outside: top && right && bottom && left,
    inside: between || bar,
    "inside-horizontal": between,
    "inside-vertical": bar,
    "diagonal-down": false,
    "diagonal-up": false,
    "horizontal-line": bottom,
  };
}

function paragraphRangePresetActive(
  preset: DocxBorderPreset,
  bordersByParagraph: Array<ParagraphBorderSet | undefined>
): boolean {
  if (bordersByParagraph.length === 0) {
    return false;
  }

  const firstBorders = bordersByParagraph[0];
  const lastBorders = bordersByParagraph[bordersByParagraph.length - 1];
  const allLeft = bordersByParagraph.every((borders) =>
    paragraphBorderVisible(borders?.left)
  );
  const allRight = bordersByParagraph.every((borders) =>
    paragraphBorderVisible(borders?.right)
  );
  const allTop = bordersByParagraph.every((borders) =>
    paragraphBorderVisible(borders?.top)
  );
  const allBottom = bordersByParagraph.every((borders) =>
    paragraphBorderVisible(borders?.bottom)
  );
  const allBetween = bordersByParagraph.every((borders) =>
    paragraphBorderVisible(borders?.between)
  );
  const allBar = bordersByParagraph.every((borders) =>
    paragraphBorderVisible(borders?.bar)
  );

  switch (preset) {
    case "top":
      return paragraphBorderVisible(firstBorders?.top);
    case "bottom":
      return paragraphBorderVisible(lastBorders?.bottom);
    case "left":
      return allLeft;
    case "right":
      return allRight;
    case "all":
    case "outside":
      return (
        paragraphBorderVisible(firstBorders?.top) &&
        paragraphBorderVisible(lastBorders?.bottom) &&
        allLeft &&
        allRight
      );
    case "none":
      return bordersByParagraph.every(
        (borders) => paragraphBorderPresetState(borders).none
      );
    case "inside":
      return allBetween && allBar;
    case "inside-horizontal":
      return allBetween;
    case "inside-vertical":
      return allBar;
    case "horizontal-line":
      return paragraphBorderVisible(lastBorders?.bottom);
    case "diagonal-down":
    case "diagonal-up":
      return false;
    default:
      return allTop && allBottom;
  }
}

function applyParagraphBorderPresetForRangeEntry(
  borders: ParagraphBorderSet | undefined,
  preset: DocxBorderPreset,
  remove: boolean,
  index: number,
  total: number
): ParagraphBorderSet | undefined {
  if (total <= 1) {
    return applyParagraphBorderPreset(borders, preset, remove);
  }

  const nextBorders = cloneParagraphBorderSet(borders) ?? {};
  const visibleBorder = toolbarParagraphBorderStyle(
    resolvePreferredParagraphBorder(nextBorders)
  );
  const nilBorder = nilParagraphBorderStyle();
  const borderToApply = remove ? nilBorder : visibleBorder;
  const isFirst = index === 0;
  const isLast = index === total - 1;

  switch (preset) {
    case "top":
      nextBorders.top = { ...(isFirst ? borderToApply : nilBorder) };
      return nextBorders;
    case "bottom":
      nextBorders.bottom = { ...(isLast ? borderToApply : nilBorder) };
      return nextBorders;
    case "left":
      nextBorders.left = { ...borderToApply };
      return nextBorders;
    case "right":
      nextBorders.right = { ...borderToApply };
      return nextBorders;
    case "all":
    case "outside":
      if (remove) {
        nextBorders.top = { ...nilBorder };
        nextBorders.right = { ...nilBorder };
        nextBorders.bottom = { ...nilBorder };
        nextBorders.left = { ...nilBorder };
      } else {
        nextBorders.top = { ...(isFirst ? borderToApply : nilBorder) };
        nextBorders.right = { ...borderToApply };
        nextBorders.bottom = { ...(isLast ? borderToApply : nilBorder) };
        nextBorders.left = { ...borderToApply };
      }
      return nextBorders;
    case "horizontal-line":
      nextBorders.bottom = { ...(isLast ? borderToApply : nilBorder) };
      return nextBorders;
    case "none":
      nextBorders.top = { ...nilBorder };
      nextBorders.right = { ...nilBorder };
      nextBorders.bottom = { ...nilBorder };
      nextBorders.left = { ...nilBorder };
      nextBorders.between = { ...nilBorder };
      nextBorders.bar = { ...nilBorder };
      return nextBorders;
    default:
      return applyParagraphBorderPreset(borders, preset, remove);
  }
}

function tableBorderPresetState(
  borders: TableBorderSet | undefined,
  selectedCellBorders?: TableBorderSet
): DocxBorderPresetState {
  const top = tableBorderVisible(borders?.top);
  const right = tableBorderVisible(borders?.right);
  const bottom = tableBorderVisible(borders?.bottom);
  const left = tableBorderVisible(borders?.left);
  const insideH = tableBorderVisible(borders?.insideH);
  const insideV = tableBorderVisible(borders?.insideV);
  const diagonalDown = tableBorderVisible(
    selectedCellBorders?.tl2br ?? borders?.tl2br
  );
  const diagonalUp = tableBorderVisible(
    selectedCellBorders?.tr2bl ?? borders?.tr2bl
  );
  const hasAny =
    top ||
    right ||
    bottom ||
    left ||
    insideH ||
    insideV ||
    diagonalDown ||
    diagonalUp;

  return {
    bottom,
    top,
    left,
    right,
    none: !hasAny,
    all: top && right && bottom && left && insideH && insideV,
    outside: top && right && bottom && left,
    inside: insideH && insideV,
    "inside-horizontal": insideH,
    "inside-vertical": insideV,
    "diagonal-down": diagonalDown,
    "diagonal-up": diagonalUp,
    "horizontal-line": insideH,
  };
}

function applyParagraphBorderPreset(
  borders: ParagraphBorderSet | undefined,
  preset: DocxBorderPreset,
  remove = false
): ParagraphBorderSet | undefined {
  const nextBorders = cloneParagraphBorderSet(borders) ?? {};
  const visibleBorder = toolbarParagraphBorderStyle(
    resolvePreferredParagraphBorder(nextBorders)
  );
  const nilBorder = nilParagraphBorderStyle();
  const borderToApply = remove ? nilBorder : visibleBorder;

  switch (preset) {
    case "top":
      nextBorders.top = { ...borderToApply };
      break;
    case "right":
      nextBorders.right = { ...borderToApply };
      break;
    case "bottom":
      nextBorders.bottom = { ...borderToApply };
      break;
    case "left":
      nextBorders.left = { ...borderToApply };
      break;
    case "all":
    case "outside":
      nextBorders.top = { ...borderToApply };
      nextBorders.right = { ...borderToApply };
      nextBorders.bottom = { ...borderToApply };
      nextBorders.left = { ...borderToApply };
      break;
    case "inside":
      nextBorders.between = { ...borderToApply };
      nextBorders.bar = { ...borderToApply };
      break;
    case "inside-horizontal":
      nextBorders.between = { ...borderToApply };
      break;
    case "inside-vertical":
      nextBorders.bar = { ...borderToApply };
      break;
    case "horizontal-line":
      nextBorders.bottom = { ...borderToApply };
      break;
    case "none":
      nextBorders.top = { ...nilBorder };
      nextBorders.right = { ...nilBorder };
      nextBorders.bottom = { ...nilBorder };
      nextBorders.left = { ...nilBorder };
      nextBorders.between = { ...nilBorder };
      nextBorders.bar = { ...nilBorder };
      break;
    case "diagonal-down":
    case "diagonal-up":
      return undefined;
    default:
      return nextBorders;
  }

  return nextBorders;
}

function applyTableBorderPreset(
  borders: TableBorderSet | undefined,
  preset: DocxBorderPreset,
  remove = false
): TableBorderSet | undefined {
  const nextBorders = cloneTableBorderSet(borders) ?? {};
  const visibleBorder = toolbarTableBorderStyle(
    resolvePreferredTableBorder(nextBorders)
  );
  const nilBorder = nilTableBorderStyle();
  const borderToApply = remove ? nilBorder : visibleBorder;

  switch (preset) {
    case "top":
      nextBorders.top = { ...borderToApply };
      break;
    case "right":
      nextBorders.right = { ...borderToApply };
      break;
    case "bottom":
      nextBorders.bottom = { ...borderToApply };
      break;
    case "left":
      nextBorders.left = { ...borderToApply };
      break;
    case "all":
      nextBorders.top = { ...borderToApply };
      nextBorders.right = { ...borderToApply };
      nextBorders.bottom = { ...borderToApply };
      nextBorders.left = { ...borderToApply };
      nextBorders.insideH = { ...borderToApply };
      nextBorders.insideV = { ...borderToApply };
      break;
    case "outside":
      nextBorders.top = { ...borderToApply };
      nextBorders.right = { ...borderToApply };
      nextBorders.bottom = { ...borderToApply };
      nextBorders.left = { ...borderToApply };
      if (!remove) {
        nextBorders.insideH = { ...nilBorder };
        nextBorders.insideV = { ...nilBorder };
      }
      break;
    case "inside":
      nextBorders.insideH = { ...borderToApply };
      nextBorders.insideV = { ...borderToApply };
      break;
    case "inside-horizontal":
      nextBorders.insideH = { ...borderToApply };
      break;
    case "inside-vertical":
      nextBorders.insideV = { ...borderToApply };
      break;
    case "horizontal-line":
      nextBorders.insideH = { ...borderToApply };
      break;
    case "none":
      nextBorders.top = { ...nilBorder };
      nextBorders.right = { ...nilBorder };
      nextBorders.bottom = { ...nilBorder };
      nextBorders.left = { ...nilBorder };
      nextBorders.insideH = { ...nilBorder };
      nextBorders.insideV = { ...nilBorder };
      nextBorders.tl2br = { ...nilBorder };
      nextBorders.tr2bl = { ...nilBorder };
      break;
    case "diagonal-down":
      nextBorders.tl2br = { ...borderToApply };
      break;
    case "diagonal-up":
      nextBorders.tr2bl = { ...borderToApply };
      break;
    default:
      return nextBorders;
  }

  return nextBorders;
}

function resolveTableBorder(
  tableBorders: TableBorderSet | undefined,
  cellBorders: TableBorderSet | undefined,
  side: TableBorderSide,
  rowIndex: number,
  rowCount: number,
  startColumnIndex: number,
  endColumnIndex: number,
  columnCount: number
): TableBorderStyle | undefined {
  const directCellBorder = cellBorders?.[side];
  if (directCellBorder) {
    return directCellBorder;
  }

  const isTopRow = rowIndex === 0;
  const isBottomRow = rowIndex >= rowCount - 1;
  const isFirstColumn = startColumnIndex === 0;
  const isLastColumn = endColumnIndex >= columnCount - 1;

  if (side === "top") {
    return isTopRow ? tableBorders?.top : tableBorders?.insideH;
  }
  if (side === "bottom") {
    return isBottomRow ? tableBorders?.bottom : tableBorders?.insideH;
  }
  if (side === "left") {
    return isFirstColumn ? tableBorders?.left : tableBorders?.insideV;
  }

  return isLastColumn ? tableBorders?.right : tableBorders?.insideV;
}

function resolveTableCellBorderCss(
  tableBorders: TableBorderSet | undefined,
  cellBorders: TableBorderSet | undefined,
  rowIndex: number,
  rowCount: number,
  startColumnIndex: number,
  endColumnIndex: number,
  columnCount: number
): React.CSSProperties {
  const top = tableBorderToCss(
    resolveTableBorder(
      tableBorders,
      cellBorders,
      "top",
      rowIndex,
      rowCount,
      startColumnIndex,
      endColumnIndex,
      columnCount
    )
  );
  const right = tableBorderToCss(
    resolveTableBorder(
      tableBorders,
      cellBorders,
      "right",
      rowIndex,
      rowCount,
      startColumnIndex,
      endColumnIndex,
      columnCount
    )
  );
  const bottom = tableBorderToCss(
    resolveTableBorder(
      tableBorders,
      cellBorders,
      "bottom",
      rowIndex,
      rowCount,
      startColumnIndex,
      endColumnIndex,
      columnCount
    )
  );
  const left = tableBorderToCss(
    resolveTableBorder(
      tableBorders,
      cellBorders,
      "left",
      rowIndex,
      rowCount,
      startColumnIndex,
      endColumnIndex,
      columnCount
    )
  );

  return {
    ...(top !== undefined ? { borderTop: top } : undefined),
    ...(right !== undefined ? { borderRight: right } : undefined),
    ...(bottom !== undefined ? { borderBottom: bottom } : undefined),
    ...(left !== undefined ? { borderLeft: left } : undefined),
  };
}

function resolveCollapsedTableHorizontalOuterBleedPx(
  table: TableNode,
  columnCount = tableColumnCount(table)
): number {
  if (columnCount <= 0 || tableUsesSeparateBorderModel(table)) {
    return 0;
  }

  const rowCount = table.rows.length;
  const tableBorders = table.style?.borders;
  let maxLeftBorderWidthPx = tableBorderStrokeWidthPx(tableBorders?.left);
  let maxRightBorderWidthPx = tableBorderStrokeWidthPx(tableBorders?.right);

  table.rows.forEach((row, rowIndex) => {
    let columnCursor = 0;

    row.cells.forEach((cell) => {
      const columnSpan =
        cell.style?.gridSpan && cell.style.gridSpan > 1
          ? cell.style.gridSpan
          : 1;
      const startColumnIndex = columnCursor;
      const endColumnIndex = Math.min(
        columnCount - 1,
        startColumnIndex + columnSpan - 1
      );
      columnCursor += columnSpan;

      if (cell.style?.vMergeContinuation) {
        return;
      }

      if (startColumnIndex === 0) {
        maxLeftBorderWidthPx = Math.max(
          maxLeftBorderWidthPx,
          tableBorderStrokeWidthPx(
            resolveTableBorder(
              tableBorders,
              cell.style?.borders,
              "left",
              rowIndex,
              rowCount,
              startColumnIndex,
              endColumnIndex,
              columnCount
            )
          )
        );
      }

      if (endColumnIndex >= columnCount - 1) {
        maxRightBorderWidthPx = Math.max(
          maxRightBorderWidthPx,
          tableBorderStrokeWidthPx(
            resolveTableBorder(
              tableBorders,
              cell.style?.borders,
              "right",
              rowIndex,
              rowCount,
              startColumnIndex,
              endColumnIndex,
              columnCount
            )
          )
        );
      }
    });
  });

  return Math.max(
    0,
    Math.ceil((maxLeftBorderWidthPx + maxRightBorderWidthPx) / 2)
  );
}

function resolveTableCellDiagonalOverlayCss(
  tableBorders: TableBorderSet | undefined,
  cellBorders: TableBorderSet | undefined
): React.CSSProperties {
  const diagonalDownBorder = cellBorders?.tl2br ?? tableBorders?.tl2br;
  const diagonalUpBorder = cellBorders?.tr2bl ?? tableBorders?.tr2bl;
  const layers: string[] = [];

  const addLayer = (
    border: TableBorderStyle | undefined,
    direction: "to bottom right" | "to bottom left"
  ): void => {
    if (!tableBorderVisible(border)) {
      return;
    }

    const color = border?.color ?? "#000000";
    const widthPx =
      Number.isFinite(border?.sizeEighthPt) &&
      (border?.sizeEighthPt as number) > 0
        ? Math.max(
            0.75,
            Number(((border?.sizeEighthPt as number) / 6).toFixed(2))
          )
        : 1;
    const halfWidthPx = Number((widthPx / 2).toFixed(2));
    layers.push(
      `linear-gradient(${direction}, transparent calc(50% - ${halfWidthPx}px), ${color} calc(50% - ${halfWidthPx}px), ${color} calc(50% + ${halfWidthPx}px), transparent calc(50% + ${halfWidthPx}px))`
    );
  };

  addLayer(diagonalDownBorder, "to bottom right");
  addLayer(diagonalUpBorder, "to bottom left");

  if (layers.length === 0) {
    return {};
  }

  return {
    backgroundImage: layers.join(", "),
    backgroundRepeat: "no-repeat",
    backgroundSize: "100% 100%",
  };
}

function cloneTextStyle(
  style?: TextRunNode["style"]
): TextRunNode["style"] | undefined {
  return style ? { ...style } : undefined;
}

function cloneFormFieldWidget(
  widget?: FormFieldRunNode["widget"]
): FormFieldRunNode["widget"] | undefined {
  if (!widget) {
    return undefined;
  }

  return {
    name: widget.name,
    enabled: widget.enabled,
    calcOnExit: widget.calcOnExit,
    text: widget.text
      ? {
          inputType: widget.text.inputType,
          defaultText: widget.text.defaultText,
          maxLength: widget.text.maxLength,
          textFormat: widget.text.textFormat,
        }
      : undefined,
    checkbox: widget.checkbox
      ? {
          defaultChecked: widget.checkbox.defaultChecked,
          sizeMode: widget.checkbox.sizeMode,
          sizePt: widget.checkbox.sizePt,
        }
      : undefined,
    dropdown: widget.dropdown
      ? {
          defaultValue: widget.dropdown.defaultValue,
        }
      : undefined,
  };
}

function mergeFormFieldWidgetPatch(
  current: FormFieldRunNode["widget"] | undefined,
  patch: Partial<NonNullable<FormFieldRunNode["widget"]>>
): FormFieldRunNode["widget"] | undefined {
  const hasPatch =
    patch.name !== undefined ||
    patch.enabled !== undefined ||
    patch.calcOnExit !== undefined ||
    patch.text !== undefined ||
    patch.checkbox !== undefined ||
    patch.dropdown !== undefined;
  if (!hasPatch) {
    return cloneFormFieldWidget(current);
  }

  const mergedText =
    patch.text === undefined
      ? cloneFormFieldWidget(current)?.text
      : {
          ...(current?.text ?? {}),
          ...patch.text,
        };
  const mergedCheckbox =
    patch.checkbox === undefined
      ? cloneFormFieldWidget(current)?.checkbox
      : {
          ...(current?.checkbox ?? {}),
          ...patch.checkbox,
        };
  const mergedDropdown =
    patch.dropdown === undefined
      ? cloneFormFieldWidget(current)?.dropdown
      : {
          ...(current?.dropdown ?? {}),
          ...patch.dropdown,
        };

  return {
    ...(current ?? {}),
    ...(patch.name !== undefined ? { name: patch.name } : undefined),
    ...(patch.enabled !== undefined ? { enabled: patch.enabled } : undefined),
    ...(patch.calcOnExit !== undefined
      ? { calcOnExit: patch.calcOnExit }
      : undefined),
    ...(mergedText ? { text: mergedText } : undefined),
    ...(mergedCheckbox ? { checkbox: mergedCheckbox } : undefined),
    ...(mergedDropdown ? { dropdown: mergedDropdown } : undefined),
  };
}

function cloneFormFieldRun(field: FormFieldRunNode): FormFieldRunNode {
  return {
    type: "form-field",
    fieldType: field.fieldType,
    sourceKind: field.sourceKind,
    id: field.id,
    tag: field.tag,
    title: field.title,
    placeholder: field.placeholder,
    checked: field.checked,
    value: field.value,
    options: field.options?.map((option) => ({
      displayText: option.displayText,
      value: option.value,
    })),
    widget: cloneFormFieldWidget(field.widget),
    checkedSymbol: field.checkedSymbol,
    uncheckedSymbol: field.uncheckedSymbol,
    style: cloneTextStyle(field.style),
    link: field.link,
    sourceXml: field.sourceXml,
  };
}

function textStylesEqual(
  a?: TextRunNode["style"],
  b?: TextRunNode["style"]
): boolean {
  return JSON.stringify(a ?? {}) === JSON.stringify(b ?? {});
}

function mergeAdjacentTextRuns(
  children: ParagraphNode["children"]
): ParagraphNode["children"] {
  const merged: ParagraphNode["children"] = [];

  for (const child of children) {
    const previous = merged[merged.length - 1];
    if (
      previous &&
      previous.type === "text" &&
      child.type === "text" &&
      textStylesEqual(previous.style, child.style) &&
      previous.link === child.link
    ) {
      previous.text += child.text;
      continue;
    }

    merged.push(
      child.type === "text"
        ? {
            type: "text",
            text: child.text,
            style: cloneTextStyle(child.style),
            link: child.link,
          }
        : child.type === "form-field"
        ? cloneFormFieldRun(child)
        : {
            type: "image",
            src: child.src,
            alt: child.alt,
            widthPx: child.widthPx,
            heightPx: child.heightPx,
            partName: child.partName,
            contentType: child.contentType,
            data: child.data ? new Uint8Array(child.data) : undefined,
            floating: child.floating ? { ...child.floating } : undefined,
            syntheticTextBox: child.syntheticTextBox,
            textBoxText: child.textBoxText,
          }
    );
  }

  return merged;
}

function paragraphHasOnlyTextRuns(paragraph: ParagraphNode): boolean {
  return paragraph.children.every(
    (child): child is TextRunNode => child.type === "text"
  );
}

function cloneTextRunWithMetadata(run: TextRunNode): TextRunNode {
  return {
    type: "text",
    text: run.text,
    style: cloneTextStyle(run.style),
    link: run.link,
    noteReference: run.noteReference ? { ...run.noteReference } : undefined,
  };
}

function splitTextRunsAtOffset(
  runs: TextRunNode[],
  offset: number
): {
  left: TextRunNode[];
  right: TextRunNode[];
} {
  const safeOffset = Math.max(0, Math.round(offset));
  const left: TextRunNode[] = [];
  const right: TextRunNode[] = [];
  let cursor = 0;

  for (const run of runs) {
    const runLength = run.text.length;
    const runStart = cursor;
    const runEnd = runStart + runLength;
    cursor = runEnd;

    if (runEnd <= safeOffset) {
      left.push(cloneTextRunWithMetadata(run));
      continue;
    }

    if (runStart >= safeOffset) {
      right.push(cloneTextRunWithMetadata(run));
      continue;
    }

    const localSplit = Math.max(0, Math.min(runLength, safeOffset - runStart));
    const before = run.text.slice(0, localSplit);
    const after = run.text.slice(localSplit);
    if (before.length > 0) {
      left.push({
        ...cloneTextRunWithMetadata(run),
        text: before,
      });
    }
    if (after.length > 0) {
      right.push({
        ...cloneTextRunWithMetadata(run),
        text: after,
      });
    }
  }

  return { left, right };
}

function firstTextStyleAtOffset(
  paragraph: ParagraphNode,
  offset: number,
  preferPreviousAtBoundary: boolean
): TextRunNode["style"] | undefined {
  const textChildren = paragraph.children.filter(
    (child): child is TextRunNode => child.type === "text"
  );
  if (textChildren.length === 0) {
    return undefined;
  }

  const safeOffset = Math.max(0, offset);
  let cursor = 0;

  for (let index = 0; index < textChildren.length; index += 1) {
    const run = textChildren[index];
    const runLength = run.text.length;
    const runStart = cursor;
    const runEnd = runStart + runLength;
    cursor = runEnd;

    if (safeOffset < runEnd) {
      return cloneTextStyle(run.style);
    }

    if (safeOffset === runEnd) {
      if (preferPreviousAtBoundary || index === textChildren.length - 1) {
        return cloneTextStyle(run.style);
      }

      return cloneTextStyle(textChildren[index + 1]?.style);
    }
  }

  return cloneTextStyle(textChildren[textChildren.length - 1]?.style);
}

function linkAtOffset(
  paragraph: ParagraphNode,
  offset: number,
  preferPreviousAtBoundary: boolean
): string | undefined {
  const textChildren = paragraph.children.filter(
    (child): child is TextRunNode => child.type === "text"
  );
  if (textChildren.length === 0) {
    return undefined;
  }

  const safeOffset = Math.max(0, offset);
  let cursor = 0;

  for (let index = 0; index < textChildren.length; index += 1) {
    const run = textChildren[index];
    const runLength = run.text.length;
    const runStart = cursor;
    const runEnd = runStart + runLength;
    cursor = runEnd;

    if (safeOffset < runEnd) {
      return run.link;
    }

    if (safeOffset === runEnd) {
      if (preferPreviousAtBoundary || index === textChildren.length - 1) {
        return run.link;
      }

      return textChildren[index + 1]?.link;
    }
  }

  return textChildren[textChildren.length - 1]?.link;
}

function uniformLinkInRange(
  paragraph: ParagraphNode,
  startOffset: number,
  endOffset: number
): string | undefined {
  const safeStart = Math.max(0, Math.min(startOffset, endOffset));
  const safeEnd = Math.max(safeStart, Math.max(startOffset, endOffset));
  if (safeStart === safeEnd) {
    return linkAtOffset(paragraph, safeStart, true);
  }

  let cursor = 0;
  let candidateLink: string | undefined;

  for (const child of paragraph.children) {
    if (child.type !== "text") {
      continue;
    }

    const runStart = cursor;
    const runEnd = runStart + child.text.length;
    cursor = runEnd;

    if (runEnd <= safeStart || runStart >= safeEnd) {
      continue;
    }

    if (!candidateLink) {
      candidateLink = child.link;
      continue;
    }

    if (candidateLink !== child.link) {
      return undefined;
    }
  }

  return candidateLink;
}

function linkRangeAtOffset(
  paragraph: ParagraphNode,
  offset: number
):
  | {
      start: number;
      end: number;
      link: string;
    }
  | undefined {
  const textChildren = paragraph.children.filter(
    (child): child is TextRunNode => child.type === "text"
  );
  if (textChildren.length === 0) {
    return undefined;
  }

  const safeOffset = Math.max(0, offset);
  let cursor = 0;
  let runIndex = -1;

  for (let index = 0; index < textChildren.length; index += 1) {
    const run = textChildren[index];
    const runStart = cursor;
    const runEnd = runStart + run.text.length;
    cursor = runEnd;

    if (
      safeOffset < runEnd ||
      safeOffset === runEnd ||
      index === textChildren.length - 1
    ) {
      runIndex = index;
      break;
    }
  }

  if (runIndex < 0) {
    return undefined;
  }

  const currentRun = textChildren[runIndex];
  const currentLink = currentRun.link;
  if (!currentLink) {
    return undefined;
  }

  const runStarts: number[] = [];
  cursor = 0;
  for (const run of textChildren) {
    runStarts.push(cursor);
    cursor += run.text.length;
  }

  let startIndex = runIndex;
  while (startIndex > 0 && textChildren[startIndex - 1]?.link === currentLink) {
    startIndex -= 1;
  }

  let endIndex = runIndex;
  while (
    endIndex + 1 < textChildren.length &&
    textChildren[endIndex + 1]?.link === currentLink
  ) {
    endIndex += 1;
  }

  const start = runStarts[startIndex] ?? 0;
  const end =
    (runStarts[endIndex] ?? 0) + (textChildren[endIndex]?.text.length ?? 0);
  if (end <= start) {
    return undefined;
  }

  return {
    start,
    end,
    link: currentLink,
  };
}

function mutateParagraphTextStyleInRange(
  paragraph: ParagraphNode,
  startOffset: number,
  endOffset: number,
  mutator: (
    currentStyle: TextRunNode["style"] | undefined
  ) => TextRunNode["style"] | undefined
): boolean {
  const safeStart = Math.max(0, Math.min(startOffset, endOffset));
  const safeEnd = Math.max(safeStart, Math.max(startOffset, endOffset));
  if (safeStart === safeEnd) {
    return false;
  }

  const nextChildren: ParagraphNode["children"] = [];
  let cursor = 0;
  let touched = false;

  for (const child of paragraph.children) {
    if (child.type !== "text") {
      if (child.type === "form-field") {
        nextChildren.push(cloneFormFieldRun(child));
      } else {
        nextChildren.push({
          type: "image",
          src: child.src,
          alt: child.alt,
          widthPx: child.widthPx,
          heightPx: child.heightPx,
          partName: child.partName,
          contentType: child.contentType,
          data: child.data ? new Uint8Array(child.data) : undefined,
          floating: child.floating ? { ...child.floating } : undefined,
          syntheticTextBox: child.syntheticTextBox,
          textBoxText: child.textBoxText,
        });
      }
      continue;
    }

    const text = child.text;
    const runStart = cursor;
    const runEnd = runStart + text.length;
    cursor = runEnd;

    if (runEnd <= safeStart || runStart >= safeEnd || text.length === 0) {
      nextChildren.push({
        type: "text",
        text,
        style: cloneTextStyle(child.style),
        link: child.link,
      });
      continue;
    }

    touched = true;
    const localStart = Math.max(0, safeStart - runStart);
    const localEnd = Math.min(text.length, safeEnd - runStart);

    if (localStart > 0) {
      nextChildren.push({
        type: "text",
        text: text.slice(0, localStart),
        style: cloneTextStyle(child.style),
        link: child.link,
      });
    }

    const selectedText = text.slice(localStart, localEnd);
    if (selectedText.length > 0) {
      nextChildren.push({
        type: "text",
        text: selectedText,
        style: mutator(cloneTextStyle(child.style)),
        link: child.link,
      });
    }

    if (localEnd < text.length) {
      nextChildren.push({
        type: "text",
        text: text.slice(localEnd),
        style: cloneTextStyle(child.style),
        link: child.link,
      });
    }
  }

  if (!touched) {
    return false;
  }

  paragraph.children = mergeAdjacentTextRuns(nextChildren);
  return true;
}

function mutateParagraphLinkInRange(
  paragraph: ParagraphNode,
  startOffset: number,
  endOffset: number,
  link?: string
): boolean {
  const safeStart = Math.max(0, Math.min(startOffset, endOffset));
  const safeEnd = Math.max(safeStart, Math.max(startOffset, endOffset));
  if (safeStart === safeEnd) {
    return false;
  }

  const nextChildren: ParagraphNode["children"] = [];
  let cursor = 0;
  let touched = false;

  for (const child of paragraph.children) {
    if (child.type !== "text") {
      if (child.type === "form-field") {
        nextChildren.push(cloneFormFieldRun(child));
      } else {
        nextChildren.push({
          type: "image",
          src: child.src,
          alt: child.alt,
          widthPx: child.widthPx,
          heightPx: child.heightPx,
          partName: child.partName,
          contentType: child.contentType,
          data: child.data ? new Uint8Array(child.data) : undefined,
          floating: child.floating ? { ...child.floating } : undefined,
          syntheticTextBox: child.syntheticTextBox,
          textBoxText: child.textBoxText,
        });
      }
      continue;
    }

    const text = child.text;
    const runStart = cursor;
    const runEnd = runStart + text.length;
    cursor = runEnd;

    if (runEnd <= safeStart || runStart >= safeEnd || text.length === 0) {
      nextChildren.push({
        type: "text",
        text,
        style: cloneTextStyle(child.style),
        link: child.link,
      });
      continue;
    }

    touched = true;
    const localStart = Math.max(0, safeStart - runStart);
    const localEnd = Math.min(text.length, safeEnd - runStart);

    if (localStart > 0) {
      nextChildren.push({
        type: "text",
        text: text.slice(0, localStart),
        style: cloneTextStyle(child.style),
        link: child.link,
      });
    }

    const selectedText = text.slice(localStart, localEnd);
    if (selectedText.length > 0) {
      nextChildren.push({
        type: "text",
        text: selectedText,
        style: cloneTextStyle(child.style),
        link,
      });
    }

    if (localEnd < text.length) {
      nextChildren.push({
        type: "text",
        text: text.slice(localEnd),
        style: cloneTextStyle(child.style),
        link: child.link,
      });
    }
  }

  if (!touched) {
    return false;
  }

  paragraph.children = mergeAdjacentTextRuns(nextChildren);
  return true;
}

function toBase64(bytes: Uint8Array): string {
  if (typeof Buffer !== "undefined") {
    return Buffer.from(bytes).toString("base64");
  }

  let binary = "";
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.byteLength; index += chunkSize) {
    const chunk = bytes.subarray(index, index + chunkSize);
    binary += String.fromCharCode(...chunk);
  }

  if (typeof btoa === "function") {
    return btoa(binary);
  }

  return "";
}

function isParagraphSelected(
  selection: DocxEditorSelection,
  nodeIndex: number
): boolean {
  return selection.kind === "paragraph" && selection.nodeIndex === nodeIndex;
}

function isCellSelected(
  selection: DocxEditorSelection,
  tableIndex: number,
  rowIndex: number,
  cellIndex: number
): boolean {
  return (
    selection.kind === "table-cell" &&
    selection.tableIndex === tableIndex &&
    selection.rowIndex === rowIndex &&
    selection.cellIndex === cellIndex
  );
}

function paragraphLocationKey(location: ParagraphLocation): string {
  if (location.kind === "paragraph") {
    return `p:${location.nodeIndex}`;
  }

  return `t:${location.tableIndex}:${location.rowIndex}:${location.cellIndex}:${location.paragraphIndex}`;
}

function imageLocationKey(location: DocxImageLocation): string {
  return `${paragraphLocationKey(location)}:${location.childIndex}`;
}

function dropTargetKey(target: DocxImageDropTarget): string {
  return `${paragraphLocationKey(target)}:${target.childIndex}`;
}

function parseImageDropTargetFromDataset(
  dataset: DOMStringMap
): DocxImageDropTarget | undefined {
  const kind = dataset.docxTargetKind;
  const childIndex = Number.parseInt(dataset.docxChildIndex ?? "", 10);
  if (!Number.isFinite(childIndex) || childIndex < 0) {
    return undefined;
  }

  if (kind === "paragraph") {
    const nodeIndex = Number.parseInt(dataset.docxNodeIndex ?? "", 10);
    if (!Number.isFinite(nodeIndex) || nodeIndex < 0) {
      return undefined;
    }

    return {
      kind: "paragraph",
      nodeIndex,
      childIndex,
    };
  }

  if (kind === "table-cell") {
    const tableIndex = Number.parseInt(dataset.docxTableIndex ?? "", 10);
    const rowIndex = Number.parseInt(dataset.docxRowIndex ?? "", 10);
    const cellIndex = Number.parseInt(dataset.docxCellIndex ?? "", 10);
    const paragraphIndex = Number.parseInt(
      dataset.docxParagraphIndex ?? "",
      10
    );
    if (
      !Number.isFinite(tableIndex) ||
      !Number.isFinite(rowIndex) ||
      !Number.isFinite(cellIndex) ||
      !Number.isFinite(paragraphIndex) ||
      tableIndex < 0 ||
      rowIndex < 0 ||
      cellIndex < 0 ||
      paragraphIndex < 0
    ) {
      return undefined;
    }

    return {
      kind: "table-cell",
      tableIndex,
      rowIndex,
      cellIndex,
      paragraphIndex,
      childIndex,
    };
  }

  return undefined;
}

function firstTableCellAnchorLocation(
  table: TableNode,
  tableIndex: number
): Extract<DocxTextRangeLocation, { kind: "table-cell" }> | undefined {
  for (let rowIndex = 0; rowIndex < table.rows.length; rowIndex += 1) {
    const row = table.rows[rowIndex];
    if (!row) {
      continue;
    }

    for (let cellIndex = 0; cellIndex < row.cells.length; cellIndex += 1) {
      const cell = row.cells[cellIndex];
      if (!cell) {
        continue;
      }

      return {
        kind: "table-cell",
        tableIndex,
        rowIndex,
        cellIndex,
        paragraphIndex: 0,
      };
    }
  }

  return undefined;
}

function collectTablePropertyTrackedChanges(
  table: TableNode,
  tableIndex: number
): Array<{
  stableId: string;
  kind: DocxTrackedChangeKind;
  author?: string;
  date?: string;
  text?: string;
  location: DocxTextRangeLocation;
}> {
  const sourceXml = table.sourceXml ?? "";
  if (!sourceXml) {
    return [];
  }

  const anchorLocation = firstTableCellAnchorLocation(table, tableIndex);
  if (!anchorLocation) {
    return [];
  }

  const entries: Array<{
    stableId: string;
    kind: DocxTrackedChangeKind;
    author?: string;
    date?: string;
    text?: string;
    location: DocxTextRangeLocation;
  }> = [];
  const entryByKey = new Map<string, (typeof entries)[number]>();

  const append = (
    scope: "table" | "row" | "cell",
    changeTag: XmlBalancedTagRange
  ): void => {
    const kind: DocxTrackedChangeKind = "paragraph-format-change";
    const author =
      decodeXmlText(xmlAttribute(changeTag.openTag, "w:author") ?? "") ||
      undefined;
    const date = xmlAttribute(changeTag.openTag, "w:date")?.trim() || undefined;
    const revisionId =
      xmlAttribute(changeTag.openTag, "w:id")?.trim() || undefined;
    const changeXml = sourceXml.slice(changeTag.start, changeTag.end);
    const text = summarizeTableFormattingChange(scope, changeXml);
    const key = revisionId
      ? `${scope}:${kind}:id:${revisionId}`
      : `${scope}:${kind}:${author ?? ""}:${date ?? ""}:${text}`;
    const existing = entryByKey.get(key);
    if (existing) {
      if (!existing.text && text) {
        existing.text = text;
      }
      if (!existing.author && author) {
        existing.author = author;
      }
      if (!existing.date && date) {
        existing.date = date;
      }
      return;
    }

    const stableId = revisionId
      ? `${scope}-${kind}-${revisionId}`
      : `${scope}-${kind}-${entryByKey.size}`;
    const next = {
      stableId,
      kind,
      author,
      date,
      text,
      location: {
        kind: "table-cell" as const,
        tableIndex,
        rowIndex: anchorLocation.rowIndex,
        cellIndex: anchorLocation.cellIndex,
        paragraphIndex: anchorLocation.paragraphIndex,
      },
    };
    entries.push(next);
    entryByKey.set(key, next);
  };

  extractBalancedTagRanges(sourceXml, "w:tblPrChange").forEach((range) =>
    append("table", range)
  );
  extractBalancedTagRanges(sourceXml, "w:trPrChange").forEach((range) =>
    append("row", range)
  );
  extractBalancedTagRanges(sourceXml, "w:tcPrChange").forEach((range) =>
    append("cell", range)
  );

  return entries;
}

function collectTrackedChangesFromModel(model: DocModel): DocxTrackedChange[] {
  const trackedChanges: DocxTrackedChange[] = [];

  const appendParagraphChanges = (
    paragraph: ParagraphNode,
    nodeIndex: number,
    location: ParagraphLocation
  ): void => {
    const trackedMarkup = resolveParagraphTrackedMarkup(paragraph);
    if (!trackedMarkup) {
      return;
    }

    trackedMarkup.changes.forEach((change, changeIndex) => {
      trackedChanges.push({
        id: `${paragraphLocationKey(location)}:${change.id}:${changeIndex}`,
        inlineAnchorId: change.id,
        kind: change.kind,
        author: change.author,
        date: change.date,
        text: change.text,
        nodeIndex,
        location:
          location.kind === "paragraph"
            ? { kind: "paragraph", nodeIndex: location.nodeIndex }
            : {
                kind: "table-cell",
                tableIndex: location.tableIndex,
                rowIndex: location.rowIndex,
                cellIndex: location.cellIndex,
                paragraphIndex: location.paragraphIndex,
              },
      });
    });
  };

  model.nodes.forEach((node, nodeIndex) => {
    if (node.type === "paragraph") {
      appendParagraphChanges(node, nodeIndex, {
        kind: "paragraph",
        nodeIndex,
      });
      return;
    }

    node.rows.forEach((row, rowIndex) => {
      row.cells.forEach((cell, cellIndex) => {
        const directParagraphs = tableCellParagraphs(cell.nodes);
        directParagraphs.forEach((paragraph, paragraphIndex) => {
          appendParagraphChanges(paragraph, nodeIndex, {
            kind: "table-cell",
            tableIndex: nodeIndex,
            rowIndex,
            cellIndex,
            paragraphIndex,
          });
        });

        // Nested tables inside a cell are rendered without per-paragraph location
        // attributes, so anchor these changes to the owning cell via a negative
        // paragraph index to avoid colliding with direct paragraph indexes.
        const nestedParagraphs = tableCellParagraphsRecursively(
          cell.nodes
        ).filter((paragraph) => !directParagraphs.includes(paragraph));
        nestedParagraphs.forEach((paragraph, nestedParagraphIndex) => {
          appendParagraphChanges(paragraph, nodeIndex, {
            kind: "table-cell",
            tableIndex: nodeIndex,
            rowIndex,
            cellIndex,
            paragraphIndex: -(nestedParagraphIndex + 1),
          });
        });
      });
    });

    collectTablePropertyTrackedChanges(node, nodeIndex).forEach(
      (change, changeIndex) => {
        trackedChanges.push({
          id: `${paragraphLocationKey(change.location)}:${
            change.stableId
          }:${changeIndex}`,
          kind: change.kind,
          author: change.author,
          date: change.date,
          text: change.text,
          nodeIndex,
          location: change.location,
        });
      }
    );
  });

  return trackedChanges;
}

function decodeCommentRangeText(rangeXml: string): string | undefined {
  const texts: string[] = [];
  for (const match of rangeXml.matchAll(
    /<w:t\b[^>]*>([\s\S]*?)<\/w:t>/gi
  )) {
    texts.push(decodeXmlText(match[1] ?? ""));
  }
  const combined = texts.join("").replace(/\s+/g, " ").trim();
  if (!combined) {
    return undefined;
  }
  return combined.length > 120 ? `${combined.slice(0, 119)}…` : combined;
}

function resolveCommentAnchorText(
  sourceXml: string,
  commentId: number
): string | undefined {
  const startMatch = sourceXml.match(
    new RegExp(`<w:commentRangeStart\\b[^>]*w:id="${commentId}"[^>]*/?>`, "i")
  );
  const endMatch = sourceXml.match(
    new RegExp(`<w:commentRangeEnd\\b[^>]*w:id="${commentId}"[^>]*/?>`, "i")
  );
  const startIndex =
    startMatch?.index !== undefined
      ? startMatch.index + startMatch[0].length
      : // Range opened in an earlier paragraph: take from the paragraph start.
        endMatch?.index !== undefined
      ? 0
      : undefined;
  if (startIndex === undefined) {
    return undefined;
  }
  const endIndex =
    endMatch?.index !== undefined ? endMatch.index : sourceXml.length;
  if (endIndex <= startIndex) {
    return undefined;
  }
  return decodeCommentRangeText(sourceXml.slice(startIndex, endIndex));
}

function collectCommentsFromModel(model: DocModel): DocxComment[] {
  const definitions = model.metadata.comments ?? [];
  if (definitions.length === 0) {
    return [];
  }
  const definitionById = new Map(
    definitions.map((definition) => [definition.id, definition])
  );

  const comments: DocxComment[] = [];
  const appendParagraphComments = (
    paragraph: ParagraphNode,
    nodeIndex: number,
    location: ParagraphLocation
  ): void => {
    const sourceXml = paragraph.sourceXml ?? "";
    if (!sourceXml || !/commentReference/i.test(sourceXml)) {
      return;
    }

    for (const match of sourceXml.matchAll(
      /<w:commentReference\b[^>]*w:id="(-?\d+)"/gi
    )) {
      const commentId = Number.parseInt(match[1] ?? "", 10);
      const definition = Number.isFinite(commentId)
        ? definitionById.get(commentId)
        : undefined;
      if (!definition) {
        continue;
      }

      comments.push({
        id: `${paragraphLocationKey(location)}:comment:${commentId}`,
        commentId,
        author: definition.author,
        initials: definition.initials,
        date: definition.date,
        text: definition.text,
        parentId: definition.parentId,
        resolved: definition.resolved,
        anchorText: resolveCommentAnchorText(sourceXml, commentId),
        nodeIndex,
        location:
          location.kind === "paragraph"
            ? { kind: "paragraph", nodeIndex: location.nodeIndex }
            : {
                kind: "table-cell",
                tableIndex: location.tableIndex,
                rowIndex: location.rowIndex,
                cellIndex: location.cellIndex,
                paragraphIndex: location.paragraphIndex,
              },
      });
    }
  };

  model.nodes.forEach((node, nodeIndex) => {
    if (node.type === "paragraph") {
      appendParagraphComments(node, nodeIndex, {
        kind: "paragraph",
        nodeIndex,
      });
      return;
    }

    node.rows.forEach((row, rowIndex) => {
      row.cells.forEach((cell, cellIndex) => {
        const directParagraphs = tableCellParagraphs(cell.nodes);
        directParagraphs.forEach((paragraph, paragraphIndex) => {
          appendParagraphComments(paragraph, nodeIndex, {
            kind: "table-cell",
            tableIndex: nodeIndex,
            rowIndex,
            cellIndex,
            paragraphIndex,
          });
        });

        const nestedParagraphs = tableCellParagraphsRecursively(
          cell.nodes
        ).filter((paragraph) => !directParagraphs.includes(paragraph));
        nestedParagraphs.forEach((paragraph, nestedParagraphIndex) => {
          appendParagraphComments(paragraph, nodeIndex, {
            kind: "table-cell",
            tableIndex: nodeIndex,
            rowIndex,
            cellIndex,
            paragraphIndex: -(nestedParagraphIndex + 1),
          });
        });
      });
    });
  });

  return comments;
}

function hexColorWithAlpha(color: string, alpha: number): string {
  const normalized = color.trim().replace(/^#/, "");
  if (!/^[\da-f]{6}$/i.test(normalized)) {
    return color;
  }

  const red = Number.parseInt(normalized.slice(0, 2), 16);
  const green = Number.parseInt(normalized.slice(2, 4), 16);
  const blue = Number.parseInt(normalized.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function commentAccentColor(
  documentTheme: DocxDocumentTheme,
  commentId?: number
): string {
  const lightPalette = ["#5b9bd5", "#d65f5f", "#8f6ac8", "#70ad47", "#d9872b"];
  const darkPalette = ["#7dd3fc", "#fca5a5", "#c4b5fd", "#86efac", "#fdba74"];
  if (Number.isFinite(commentId)) {
    const palette = documentTheme === "dark" ? darkPalette : lightPalette;
    return palette[Math.abs(Math.round(commentId as number)) % palette.length];
  }

  return documentTheme === "dark" ? darkPalette[0] : lightPalette[0];
}

function commentHighlightStyle(
  documentTheme: DocxDocumentTheme,
  commentId?: number
): React.CSSProperties {
  const accent = commentAccentColor(documentTheme, commentId);
  return {
    backgroundColor: hexColorWithAlpha(
      accent,
      documentTheme === "dark" ? 0.3 : 0.22
    ),
    boxShadow: `inset 0 -1px 0 ${hexColorWithAlpha(
      accent,
      documentTheme === "dark" ? 0.7 : 0.48
    )}`,
  };
}

function estimateCommentCardHeight(comment: DocxComment): number {
  const snippet = comment.text || "Comment";
  const lines = Math.min(2, Math.max(1, Math.ceil(snippet.length / 42)));
  return Math.max(TRACKED_CHANGE_GUTTER_CARD_MIN_HEIGHT_PX, 24 + lines * 11);
}

function trackedChangeKindLabel(kind: DocxTrackedChangeKind): string {
  switch (kind) {
    case "insertion":
      return "Inserted";
    case "deletion":
      return "Deleted";
    case "move-from":
      return "Moved from";
    case "move-to":
      return "Moved to";
    case "format-change":
      return "Formatted";
    case "paragraph-format-change":
      return "Paragraph formatting";
    default:
      return kind;
  }
}

function trackedChangeAccentColor(
  kind: DocxTrackedChangeKind,
  documentTheme: DocxDocumentTheme
): string {
  const palette =
    documentTheme === "dark"
      ? {
          insertion: "#f87171",
          deletion: "#f87171",
          moveFrom: "#86efac",
          moveTo: "#86efac",
          format: "#c084fc",
        }
      : {
          insertion: "#dc2626",
          deletion: "#dc2626",
          moveFrom: "#70ad47",
          moveTo: "#70ad47",
          format: "#7c3aed",
        };

  switch (kind) {
    case "insertion":
      return palette.insertion;
    case "deletion":
      return palette.deletion;
    case "move-from":
      return palette.moveFrom;
    case "move-to":
      return palette.moveTo;
    case "format-change":
    case "paragraph-format-change":
      return palette.format;
    default:
      return palette.format;
  }
}

function trackedChangeUsesGutterBalloon(
  change: DocxTrackedChange
): boolean {
  return change.kind !== "insertion" && change.kind !== "move-to";
}

function gutterAnnotationSortTuple(
  location: DocxTextRangeLocation
): [number, number, number, number] {
  if (location.kind === "paragraph") {
    return [location.nodeIndex, 0, 0, 0];
  }

  return [
    location.tableIndex,
    location.rowIndex,
    location.cellIndex,
    location.paragraphIndex,
  ];
}

function trackedChangeBelongsToPageSegments(
  location: DocxTextRangeLocation,
  pageSegments: DocumentPageNodeSegment[]
): boolean {
  if (location.kind === "paragraph") {
    return pageSegments.some(
      (segment) => segment.nodeIndex === location.nodeIndex
    );
  }

  return pageSegments.some((segment) => {
    if (segment.nodeIndex !== location.tableIndex) {
      return false;
    }

    if (!segment.tableRowRange) {
      return true;
    }

    return (
      location.rowIndex >= segment.tableRowRange.startRowIndex &&
      location.rowIndex < segment.tableRowRange.endRowIndex
    );
  });
}

function resolveGutterAnnotationPageIndex(
  location: DocxTextRangeLocation,
  pageNodeSegmentsByPage: DocumentPageNodeSegment[][]
): number {
  for (
    let pageIndex = 0;
    pageIndex < pageNodeSegmentsByPage.length;
    pageIndex += 1
  ) {
    if (
      trackedChangeBelongsToPageSegments(
        location,
        pageNodeSegmentsByPage[pageIndex] ?? []
      )
    ) {
      return pageIndex;
    }
  }

  return -1;
}

function findTrackedChangeAnchorElementInPage(
  pageElement: HTMLElement,
  location: DocxTextRangeLocation
): HTMLElement | undefined {
  if (location.kind === "paragraph") {
    const paragraphElement = pageElement.querySelector(
      `[data-docx-paragraph-kind="paragraph"][data-docx-paragraph-node-index="${location.nodeIndex}"]`
    ) as HTMLElement | null;
    return paragraphElement ?? undefined;
  }

  const paragraphElement = pageElement.querySelector(
    `[data-docx-paragraph-kind="table-cell"][data-docx-table-index="${location.tableIndex}"][data-docx-row-index="${location.rowIndex}"][data-docx-cell-index="${location.cellIndex}"][data-docx-paragraph-index="${location.paragraphIndex}"]`
  ) as HTMLElement | null;
  if (paragraphElement) {
    return paragraphElement;
  }

  const cellElement = pageElement.querySelector(
    `[data-docx-table-cell="true"][data-docx-table-index="${location.tableIndex}"][data-docx-row-index="${location.rowIndex}"][data-docx-cell-index="${location.cellIndex}"]`
  ) as HTMLElement | null;
  return cellElement ?? undefined;
}

function elementRectWithinContainer(
  element: HTMLElement,
  container: HTMLElement
):
  | {
      left: number;
      width: number;
      right: number;
      top: number;
      height: number;
    }
  | undefined {
  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  if (containerRect.height <= 0 || containerRect.width <= 0) {
    return undefined;
  }

  const scaleY = containerRect.height / Math.max(1, container.offsetHeight);
  if (!Number.isFinite(scaleY) || scaleY <= 0) {
    return undefined;
  }

  const scaleX = containerRect.width / Math.max(1, container.offsetWidth);
  if (!Number.isFinite(scaleX) || scaleX <= 0) {
    return undefined;
  }

  const left = (elementRect.left - containerRect.left) / scaleX;
  const width = elementRect.width / scaleX;

  return {
    left,
    width,
    right: left + width,
    top: (elementRect.top - containerRect.top) / scaleY,
    height: elementRect.height / scaleY,
  };
}

interface TrackedChangeAnchorPoint {
  x: number;
  y: number;
}

/**
 * One entry in the page gutter: either a tracked change or a comment. Both
 * share the anchor/stacking pipeline so they interleave in document order.
 */
interface DocxGutterAnnotation {
  id: string;
  location: DocxTextRangeLocation;
  trackedChange?: DocxTrackedChange;
  comment?: DocxComment;
}

function findFirstElementWithSpaceSeparatedDataValue(
  rootElement: HTMLElement,
  attributeName: string,
  value: string
): HTMLElement | undefined {
  const candidateElements: HTMLElement[] = [];
  if (rootElement.hasAttribute(attributeName)) {
    candidateElements.push(rootElement);
  }
  candidateElements.push(
    ...rootElement.querySelectorAll<HTMLElement>(
      `[${attributeName}]`
    )
  );

  for (const candidate of candidateElements) {
    const rawValue = candidate.getAttribute(attributeName);
    if (!rawValue) {
      continue;
    }

    if (rawValue.split(/\s+/).includes(value)) {
      return candidate;
    }
  }

  return undefined;
}

function findGutterAnnotationScopeElementInPage(
  pageElement: HTMLElement,
  annotation: DocxGutterAnnotation
): HTMLElement | undefined {
  return findTrackedChangeAnchorElementInPage(pageElement, annotation.location);
}

function findGutterAnnotationDataAnchorInPage(
  pageElement: HTMLElement,
  annotation: DocxGutterAnnotation,
  attributeName: string,
  value: string
): HTMLElement | undefined {
  const scopedRoot = findGutterAnnotationScopeElementInPage(
    pageElement,
    annotation
  );
  if (scopedRoot) {
    const scopedAnchor = findFirstElementWithSpaceSeparatedDataValue(
      scopedRoot,
      attributeName,
      value
    );
    if (scopedAnchor) {
      return scopedAnchor;
    }
  }

  return findFirstElementWithSpaceSeparatedDataValue(
    pageElement,
    attributeName,
    value
  );
}

function findGutterAnnotationAnchorElementInPage(
  pageElement: HTMLElement,
  annotation: DocxGutterAnnotation
): HTMLElement | undefined {
  if (annotation.trackedChange) {
    const trackedAnchor = findGutterAnnotationDataAnchorInPage(
      pageElement,
      annotation,
      "data-docx-tracked-change-id",
      annotation.trackedChange.id
    );
    if (trackedAnchor) {
      return trackedAnchor;
    }

    const inlineAnchorId = annotation.trackedChange.inlineAnchorId;
    if (inlineAnchorId && inlineAnchorId !== annotation.trackedChange.id) {
      const inlineTrackedAnchor = findGutterAnnotationDataAnchorInPage(
        pageElement,
        annotation,
        "data-docx-tracked-change-id",
        inlineAnchorId
      );
      if (inlineTrackedAnchor) {
        return inlineTrackedAnchor;
      }
    }
  }

  if (annotation.comment) {
    const commentAnchor = findGutterAnnotationDataAnchorInPage(
      pageElement,
      annotation,
      "data-docx-comment-ids",
      String(annotation.comment.commentId)
    );
    if (commentAnchor) {
      return commentAnchor;
    }
  }

  return findTrackedChangeAnchorElementInPage(pageElement, annotation.location);
}

interface PositionedGutterAnnotation {
  annotation: DocxGutterAnnotation;
  anchorX: number;
  anchorY: number;
  top: number;
  heightPx: number;
  connectorLane: number;
}

function estimateTrackedChangeCardHeight(change: DocxTrackedChange): number {
  const snippet =
    normalizeTrackedChangeSnippet(change.text) ??
    trackedChangeKindLabel(change.kind);
  const lines = Math.min(2, Math.max(1, Math.ceil(snippet.length / 42)));
  return Math.max(TRACKED_CHANGE_GUTTER_CARD_MIN_HEIGHT_PX, 22 + lines * 11);
}

function assignGutterConnectorLanes(
  entries: PositionedGutterAnnotation[]
): void {
  if (entries.length <= 1) {
    entries.forEach((entry) => {
      entry.connectorLane = 0;
    });
    return;
  }

  const intervalPaddingPx = 3;
  const laneEndY = Array.from(
    { length: TRACKED_CHANGE_GUTTER_CONNECTOR_LANE_COUNT },
    () => Number.NEGATIVE_INFINITY
  );
  const routedEntries = entries
    .map((entry, index) => {
      const cardCenterY = entry.top + entry.heightPx / 2;
      return {
        entry,
        index,
        startY: Math.min(entry.anchorY, cardCenterY) - intervalPaddingPx,
        endY: Math.max(entry.anchorY, cardCenterY) + intervalPaddingPx,
      };
    })
    .sort((left, right) => {
      const startDelta = left.startY - right.startY;
      if (startDelta !== 0) {
        return startDelta;
      }

      const endDelta = left.endY - right.endY;
      if (endDelta !== 0) {
        return endDelta;
      }

      return left.index - right.index;
    });

  routedEntries.forEach((route) => {
    let laneIndex = laneEndY.findIndex(
      (laneEnd) => route.startY > laneEnd + intervalPaddingPx
    );

    if (laneIndex < 0) {
      laneIndex = laneEndY.reduce(
        (bestLane, laneEnd, index) =>
          laneEnd < laneEndY[bestLane] ? index : bestLane,
        0
      );
    }

    route.entry.connectorLane = laneIndex;
    laneEndY[laneIndex] = Math.max(laneEndY[laneIndex], route.endY);
  });
}

function layoutTrackedChangesForPage(
  annotations: DocxGutterAnnotation[],
  anchorByChangeId: Map<string, TrackedChangeAnchorPoint>,
  cardHeightsByChangeId: Map<string, number> | undefined,
  pageWidthPx: number,
  pageHeightPx: number
): PositionedGutterAnnotation[] {
  if (annotations.length === 0) {
    return [];
  }

  const fallbackStride = Math.max(
    18,
    pageHeightPx / Math.max(1, annotations.length + 1)
  );
  const withAnchors = annotations.map((annotation, index) => {
    const defaultAnchor = Math.min(
      Math.max(10, Math.round((index + 1) * fallbackStride)),
      Math.max(10, pageHeightPx - 10)
    );
    const anchorPoint = anchorByChangeId.get(annotation.id);
    const anchorY = anchorPoint?.y ?? defaultAnchor;
    const anchorX = anchorPoint?.x ?? Math.max(10, pageWidthPx - 10);
    const measuredHeightPx = cardHeightsByChangeId?.get(annotation.id);
    const estimatedHeightPx = annotation.trackedChange
      ? estimateTrackedChangeCardHeight(annotation.trackedChange)
      : annotation.comment
      ? estimateCommentCardHeight(annotation.comment)
      : TRACKED_CHANGE_GUTTER_CARD_MIN_HEIGHT_PX;
    const heightPx =
      Number.isFinite(measuredHeightPx) && (measuredHeightPx as number) > 0
        ? Math.max(
            TRACKED_CHANGE_GUTTER_CARD_MIN_HEIGHT_PX,
            Math.round(measuredHeightPx as number)
          )
        : estimatedHeightPx;
    return {
      annotation,
      anchorX: clampNumber(
        Math.round(anchorX),
        10,
        Math.max(10, pageWidthPx - 10)
      ),
      anchorY: clampNumber(
        Math.round(anchorY),
        10,
        Math.max(10, pageHeightPx - 10)
      ),
      top: 0,
      heightPx,
      connectorLane: 0,
    };
  });

  withAnchors.sort((left, right) => {
    const yDelta = left.anchorY - right.anchorY;
    if (Math.abs(yDelta) > 2) {
      return yDelta;
    }

    const xDelta = left.anchorX - right.anchorX;
    if (xDelta !== 0) {
      return xDelta;
    }

    return left.annotation.id.localeCompare(right.annotation.id);
  });

  const minTopPx = 8;
  let cursorTopPx = minTopPx;
  withAnchors.forEach((entry) => {
    const desiredTop = entry.anchorY - 8;
    entry.top = Math.max(desiredTop, cursorTopPx);
    cursorTopPx =
      entry.top + entry.heightPx + TRACKED_CHANGE_GUTTER_CARD_GAP_PX;
  });

  const maxBottom = Math.max(0, pageHeightPx - minTopPx);
  const bottomAfterInitialPlacement =
    cursorTopPx - TRACKED_CHANGE_GUTTER_CARD_GAP_PX;
  const overflowPx = bottomAfterInitialPlacement - maxBottom;
  if (overflowPx > 0) {
    let shiftedCursorTopPx = minTopPx;
    withAnchors.forEach((entry) => {
      const desiredTop = entry.top - overflowPx;
      entry.top = Math.max(desiredTop, shiftedCursorTopPx);
      shiftedCursorTopPx =
        entry.top + entry.heightPx + TRACKED_CHANGE_GUTTER_CARD_GAP_PX;
    });
  }

  assignGutterConnectorLanes(withAnchors);

  return withAnchors;
}

function sameParagraphLocation(
  a: ParagraphLocation,
  b: ParagraphLocation
): boolean {
  if (a.kind === "paragraph") {
    return b.kind === "paragraph" && a.nodeIndex === b.nodeIndex;
  }

  if (b.kind === "paragraph") {
    return false;
  }

  return (
    a.tableIndex === b.tableIndex &&
    a.rowIndex === b.rowIndex &&
    a.cellIndex === b.cellIndex &&
    a.paragraphIndex === b.paragraphIndex
  );
}

function firstParagraphLocationInTable(
  model: DocModel,
  tableIndex: number
): ParagraphLocation | undefined {
  const tableNode = model.nodes[tableIndex];
  if (!tableNode || tableNode.type !== "table") {
    return undefined;
  }

  for (let rowIndex = 0; rowIndex < tableNode.rows.length; rowIndex += 1) {
    const row = tableNode.rows[rowIndex];
    if (!row) {
      continue;
    }

    for (let cellIndex = 0; cellIndex < row.cells.length; cellIndex += 1) {
      const cell = row.cells[cellIndex];
      if (!cell || cell.style?.vMergeContinuation) {
        continue;
      }

      const cellParagraphs = tableCellParagraphs(cell.nodes);
      if (cellParagraphs.length === 0) {
        continue;
      }

      return {
        kind: "table-cell",
        tableIndex,
        rowIndex,
        cellIndex,
        paragraphIndex: 0,
      };
    }
  }

  return undefined;
}

function lastParagraphLocationInTable(
  model: DocModel,
  tableIndex: number
): ParagraphLocation | undefined {
  const tableNode = model.nodes[tableIndex];
  if (!tableNode || tableNode.type !== "table") {
    return undefined;
  }

  for (let rowIndex = tableNode.rows.length - 1; rowIndex >= 0; rowIndex -= 1) {
    const row = tableNode.rows[rowIndex];
    if (!row) {
      continue;
    }

    for (let cellIndex = row.cells.length - 1; cellIndex >= 0; cellIndex -= 1) {
      const cell = row.cells[cellIndex];
      if (!cell || cell.style?.vMergeContinuation) {
        continue;
      }

      const cellParagraphs = tableCellParagraphs(cell.nodes);
      if (cellParagraphs.length === 0) {
        continue;
      }

      return {
        kind: "table-cell",
        tableIndex,
        rowIndex,
        cellIndex,
        paragraphIndex: cellParagraphs.length - 1,
      };
    }
  }

  return undefined;
}

function nodeIndexFromParagraphLocation(location: ParagraphLocation): number {
  return location.kind === "paragraph"
    ? location.nodeIndex
    : location.tableIndex;
}

function adjustLocationAfterRemovedNodeIndexes(
  location: DocxTextRangeLocation,
  removedNodeIndexes: number[]
): DocxTextRangeLocation | undefined {
  if (removedNodeIndexes.length === 0) {
    return cloneTextRangeLocation(location);
  }

  const normalizedRemoved = [...removedNodeIndexes]
    .filter((value) => Number.isFinite(value) && value >= 0)
    .map((value) => Math.round(value))
    .sort((left, right) => left - right);

  const sourceNodeIndex =
    location.kind === "paragraph" ? location.nodeIndex : location.tableIndex;
  let adjustedNodeIndex = sourceNodeIndex;
  for (const removedIndex of normalizedRemoved) {
    if (removedIndex === adjustedNodeIndex) {
      return undefined;
    }
    if (removedIndex < adjustedNodeIndex) {
      adjustedNodeIndex -= 1;
    }
  }

  if (location.kind === "paragraph") {
    return {
      kind: "paragraph",
      nodeIndex: Math.max(0, adjustedNodeIndex),
    };
  }

  return {
    kind: "table-cell",
    tableIndex: Math.max(0, adjustedNodeIndex),
    rowIndex: location.rowIndex,
    cellIndex: location.cellIndex,
    paragraphIndex: location.paragraphIndex,
  };
}

function tableCoverageBoundaries(
  model: DocModel,
  tableIndex: number
):
  | {
      start: DocxTextRangeBoundary;
      end: DocxTextRangeBoundary;
    }
  | undefined {
  const firstLocation = firstParagraphLocationInTable(model, tableIndex);
  const lastLocation = lastParagraphLocationInTable(model, tableIndex);
  if (!firstLocation || !lastLocation) {
    return undefined;
  }

  const firstParagraph = getParagraphAtLocation(model, firstLocation).paragraph;
  const lastParagraph = getParagraphAtLocation(model, lastLocation).paragraph;
  if (!firstParagraph || !lastParagraph) {
    return undefined;
  }

  return {
    start: {
      location: cloneTextRangeLocation(firstLocation),
      offset: 0,
    },
    end: {
      location: cloneTextRangeLocation(lastLocation),
      offset: paragraphText(lastParagraph).length,
    },
  };
}

function fullyCoveredTableNodeIndexesForRange(
  model: DocModel,
  normalizedRange: DocxTextRange
): number[] {
  const startNodeIndex = nodeIndexFromParagraphLocation(
    normalizedRange.start.location
  );
  const endNodeIndex = nodeIndexFromParagraphLocation(
    normalizedRange.end.location
  );
  const firstIndex = Math.min(startNodeIndex, endNodeIndex);
  const lastIndex = Math.max(startNodeIndex, endNodeIndex);
  const coveredTableIndexes: number[] = [];

  for (let nodeIndex = firstIndex; nodeIndex <= lastIndex; nodeIndex += 1) {
    const node = model.nodes[nodeIndex];
    if (!node || node.type !== "table") {
      continue;
    }

    const boundaries = tableCoverageBoundaries(model, nodeIndex);
    if (!boundaries) {
      continue;
    }

    const coversFromStart =
      compareTextRangeBoundaries(normalizedRange.start, boundaries.start) <= 0;
    const coversToEnd =
      compareTextRangeBoundaries(normalizedRange.end, boundaries.end) >= 0;
    if (!coversFromStart || !coversToEnd) {
      continue;
    }

    coveredTableIndexes.push(nodeIndex);
  }

  return coveredTableIndexes;
}

function firstParagraphLocationFromNode(
  model: DocModel,
  nodeIndex: number
): ParagraphLocation | undefined {
  const node = model.nodes[nodeIndex];
  if (!node) {
    return undefined;
  }

  if (node.type === "paragraph") {
    return {
      kind: "paragraph",
      nodeIndex,
    };
  }

  return firstParagraphLocationInTable(model, nodeIndex);
}

function lastParagraphLocationInNode(
  model: DocModel,
  nodeIndex: number
): ParagraphLocation | undefined {
  const node = model.nodes[nodeIndex];
  if (!node) {
    return undefined;
  }

  if (node.type === "paragraph") {
    return {
      kind: "paragraph",
      nodeIndex,
    };
  }

  return lastParagraphLocationInTable(model, nodeIndex);
}

function nextParagraphLocation(
  model: DocModel,
  location: ParagraphLocation
): ParagraphLocation | undefined {
  if (location.kind === "paragraph") {
    for (
      let nodeIndex = location.nodeIndex + 1;
      nodeIndex < model.nodes.length;
      nodeIndex += 1
    ) {
      const next = firstParagraphLocationFromNode(model, nodeIndex);
      if (next) {
        return next;
      }
    }
    return undefined;
  }

  const tableNode = model.nodes[location.tableIndex];
  if (!tableNode || tableNode.type !== "table") {
    return undefined;
  }

  const currentCell =
    tableNode.rows[location.rowIndex]?.cells[location.cellIndex];
  if (!currentCell) {
    return undefined;
  }

  const cellParagraphs = tableCellParagraphs(currentCell.nodes);
  if (location.paragraphIndex < cellParagraphs.length - 1) {
    return {
      kind: "table-cell",
      tableIndex: location.tableIndex,
      rowIndex: location.rowIndex,
      cellIndex: location.cellIndex,
      paragraphIndex: location.paragraphIndex + 1,
    };
  }

  for (
    let rowIndex = location.rowIndex;
    rowIndex < tableNode.rows.length;
    rowIndex += 1
  ) {
    const row = tableNode.rows[rowIndex];
    if (!row) {
      continue;
    }

    for (
      let cellIndex =
        rowIndex === location.rowIndex ? location.cellIndex + 1 : 0;
      cellIndex < row.cells.length;
      cellIndex += 1
    ) {
      const cell = row.cells[cellIndex];
      if (!cell || cell.style?.vMergeContinuation) {
        continue;
      }

      if (tableCellParagraphs(cell.nodes).length === 0) {
        continue;
      }

      return {
        kind: "table-cell",
        tableIndex: location.tableIndex,
        rowIndex,
        cellIndex,
        paragraphIndex: 0,
      };
    }
  }

  for (
    let nodeIndex = location.tableIndex + 1;
    nodeIndex < model.nodes.length;
    nodeIndex += 1
  ) {
    const next = firstParagraphLocationFromNode(model, nodeIndex);
    if (next) {
      return next;
    }
  }

  return undefined;
}

function firstParagraphLocationInDocument(
  model: DocModel
): ParagraphLocation | undefined {
  for (let nodeIndex = 0; nodeIndex < model.nodes.length; nodeIndex += 1) {
    const first = firstParagraphLocationFromNode(model, nodeIndex);
    if (first) {
      return first;
    }
  }

  return undefined;
}

function lastParagraphLocationInDocument(
  model: DocModel
): ParagraphLocation | undefined {
  for (let nodeIndex = model.nodes.length - 1; nodeIndex >= 0; nodeIndex -= 1) {
    const node = model.nodes[nodeIndex];
    if (!node) {
      continue;
    }

    if (node.type === "paragraph") {
      return {
        kind: "paragraph",
        nodeIndex,
      };
    }

    for (let rowIndex = node.rows.length - 1; rowIndex >= 0; rowIndex -= 1) {
      const row = node.rows[rowIndex];
      if (!row) {
        continue;
      }

      for (
        let cellIndex = row.cells.length - 1;
        cellIndex >= 0;
        cellIndex -= 1
      ) {
        const cell = row.cells[cellIndex];
        if (!cell || cell.style?.vMergeContinuation) {
          continue;
        }

        const paragraphs = tableCellParagraphs(cell.nodes);
        if (paragraphs.length === 0) {
          continue;
        }

        return {
          kind: "table-cell",
          tableIndex: nodeIndex,
          rowIndex,
          cellIndex,
          paragraphIndex: paragraphs.length - 1,
        };
      }
    }
  }

  return undefined;
}

function paragraphRangeForMutate(
  model: DocModel,
  start: ParagraphLocation,
  end: ParagraphLocation
): {
  location: ParagraphLocation;
}[] {
  const items: { location: ParagraphLocation }[] = [];

  const ordered =
    compareParagraphLocations(start, end) <= 0 ? [start, end] : [end, start];
  let current: ParagraphLocation | undefined = ordered[0];
  const limit = ordered[1];
  while (current) {
    items.push({ location: current });
    if (compareParagraphLocations(current, limit) >= 0) {
      break;
    }
    current = nextParagraphLocation(model, current);
  }

  return items;
}

function normalizeRangeBoundaryParagraphOffset(
  paragraph: ParagraphNode,
  offset: number
): number {
  const length = paragraphText(paragraph).length;
  return Math.max(0, Math.min(Math.max(0, length), Math.round(offset)));
}

function rangeCoversEntireDocument(
  model: DocModel,
  range: DocxTextRange
): boolean {
  const normalizedRange = normalizeTextRange(range);
  const firstLocation = firstParagraphLocationInDocument(model);
  const lastLocation = lastParagraphLocationInDocument(model);
  if (!firstLocation || !lastLocation) {
    return false;
  }

  if (!sameParagraphLocation(normalizedRange.start.location, firstLocation)) {
    return false;
  }

  if (!sameParagraphLocation(normalizedRange.end.location, lastLocation)) {
    return false;
  }

  const firstParagraph = getParagraphAtLocation(model, firstLocation).paragraph;
  const lastParagraph = getParagraphAtLocation(model, lastLocation).paragraph;
  if (!firstParagraph || !lastParagraph) {
    return false;
  }

  const safeStart = normalizeRangeBoundaryParagraphOffset(
    firstParagraph,
    normalizedRange.start.offset
  );
  const safeEnd = normalizeRangeBoundaryParagraphOffset(
    lastParagraph,
    normalizedRange.end.offset
  );
  return safeStart <= 0 && safeEnd >= paragraphText(lastParagraph).length;
}

function resolveRangeBoundaryOffsetsForParagraph(
  currentLocation: ParagraphLocation,
  rangeStart: DocxTextRangeBoundary,
  rangeEnd: DocxTextRangeBoundary,
  paragraph: ParagraphNode
): [number, number] {
  const ordered =
    compareTextRangeBoundaries(rangeStart, rangeEnd) <= 0
      ? [rangeStart, rangeEnd]
      : [rangeEnd, rangeStart];
  const startBoundary = ordered[0];
  const endBoundary = ordered[1];
  const isStartBoundaryHere = sameParagraphLocation(
    currentLocation,
    startBoundary.location
  );
  const isEndBoundaryHere = sameParagraphLocation(
    currentLocation,
    endBoundary.location
  );

  const safeStart = isStartBoundaryHere
    ? normalizeRangeBoundaryParagraphOffset(paragraph, startBoundary.offset)
    : 0;
  const safeEnd = isEndBoundaryHere
    ? normalizeRangeBoundaryParagraphOffset(paragraph, endBoundary.offset)
    : paragraphText(paragraph).length;

  return [safeStart, safeEnd];
}

function cloneTextRangeLocation(
  location: DocxTextRangeLocation
): DocxTextRangeLocation {
  if (location.kind === "paragraph") {
    return {
      kind: "paragraph",
      nodeIndex: location.nodeIndex,
    };
  }

  return {
    kind: "table-cell",
    tableIndex: location.tableIndex,
    rowIndex: location.rowIndex,
    cellIndex: location.cellIndex,
    paragraphIndex: location.paragraphIndex,
  };
}

function cloneTextRange(range?: DocxTextRange): DocxTextRange | undefined {
  if (!range) {
    return undefined;
  }

  return {
    start: {
      location: cloneTextRangeLocation(range.start.location),
      offset: range.start.offset,
    },
    end: {
      location: cloneTextRangeLocation(range.end.location),
      offset: range.end.offset,
    },
  };
}

function cloneEditorSelection(
  selection: DocxEditorSelection
): DocxEditorSelection {
  if (selection.kind === "paragraph") {
    return {
      kind: "paragraph",
      nodeIndex: selection.nodeIndex,
    };
  }

  return {
    kind: "table-cell",
    tableIndex: selection.tableIndex,
    rowIndex: selection.rowIndex,
    cellIndex: selection.cellIndex,
  };
}

function sameEditorSelection(
  a: DocxEditorSelection,
  b: DocxEditorSelection
): boolean {
  if (a.kind !== b.kind) {
    return false;
  }

  if (a.kind === "paragraph") {
    return (
      a.nodeIndex ===
      (b as Extract<DocxEditorSelection, { kind: "paragraph" }>).nodeIndex
    );
  }

  const tableSelection = b as Extract<
    DocxEditorSelection,
    { kind: "table-cell" }
  >;
  return (
    a.tableIndex === tableSelection.tableIndex &&
    a.rowIndex === tableSelection.rowIndex &&
    a.cellIndex === tableSelection.cellIndex
  );
}

function sameTextRangeBoundary(
  a: DocxTextRangeBoundary,
  b: DocxTextRangeBoundary
): boolean {
  return compareTextRangeBoundaries(a, b) === 0;
}

function sameTextRange(a?: DocxTextRange, b?: DocxTextRange): boolean {
  if (!a && !b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }

  const normalizedA = normalizeTextRange(a);
  const normalizedB = normalizeTextRange(b);
  return (
    sameTextRangeBoundary(normalizedA.start, normalizedB.start) &&
    sameTextRangeBoundary(normalizedA.end, normalizedB.end)
  );
}

export function shouldReissueDomSelectionRestore(options: {
  modelChanged: boolean;
  selectionChanged: boolean;
  rangeChanged: boolean;
  activeTextRange?: DocxTextRange;
  suppressNext: boolean;
  selectionSessionKind: DocxSelectionSessionKind;
}): boolean {
  if (options.suppressNext) {
    return false;
  }

  if (
    options.selectionSessionKind === "pointer" ||
    options.selectionSessionKind === "keyboard" ||
    options.selectionSessionKind === "composition"
  ) {
    return false;
  }

  if (!options.activeTextRange) {
    return false;
  }

  if (options.selectionChanged || options.rangeChanged) {
    return false;
  }

  return options.modelChanged;
}

function shouldSyncActiveRangeOnKeyUp(
  event: React.KeyboardEvent<HTMLElement>
): boolean {
  const nativeKeyboardEvent = event.nativeEvent as KeyboardEvent | undefined;
  if (nativeKeyboardEvent?.isComposing) {
    return false;
  }

  if (MODIFIER_ONLY_KEYS.has(event.key)) {
    return false;
  }

  if (ACTIVE_RANGE_SYNC_KEYS.has(event.key)) {
    return true;
  }

  if (event.ctrlKey || event.metaKey) {
    const normalizedKey = event.key.toLowerCase();
    return (
      normalizedKey === "a" ||
      normalizedKey === "x" ||
      normalizedKey === "z" ||
      normalizedKey === "y"
    );
  }

  return false;
}

function isCollapsedSelectionAtElementStart(
  element: HTMLElement,
  range: Range,
  pointX?: number
): boolean {
  if (!range.collapsed) {
    return false;
  }

  if (range.startContainer === element && range.startOffset === 0) {
    return true;
  }

  if (!element.contains(range.startContainer)) {
    return false;
  }

  try {
    const offsetProbe = document.createRange();
    offsetProbe.selectNodeContents(element);
    offsetProbe.setEnd(range.startContainer, range.startOffset);
    const textOffset = offsetProbe.toString().length;
    if (textOffset !== 0) {
      return false;
    }

    if (Number.isFinite(pointX)) {
      const elementRect = element.getBoundingClientRect();
      if ((pointX as number) <= elementRect.left + 12) {
        return false;
      }
    }

    return true;
  } catch {
    return false;
  }
}

function isSuspiciousCollapsedSelectionAtElementStart(
  element: HTMLElement,
  range: Range,
  pointX?: number
): boolean {
  if (!isCollapsedSelectionAtElementStart(element, range, pointX)) {
    return false;
  }

  return (
    range.startContainer === element || !(range.startContainer instanceof Text)
  );
}

function imageLocationToParagraphLocation(
  location: DocxImageLocation
): ParagraphLocation {
  if (location.kind === "paragraph") {
    return {
      kind: "paragraph",
      nodeIndex: location.nodeIndex,
    };
  }

  return {
    kind: "table-cell",
    tableIndex: location.tableIndex,
    rowIndex: location.rowIndex,
    cellIndex: location.cellIndex,
    paragraphIndex: location.paragraphIndex,
  };
}

function getParagraphAtLocation(
  model: DocModel,
  location: ParagraphLocation
): {
  paragraph?: ParagraphNode;
  tableNode?: TableNode;
} {
  if (location.kind === "paragraph") {
    const node = model.nodes[location.nodeIndex];
    if (!node || node.type !== "paragraph") {
      return {};
    }

    return { paragraph: node };
  }

  const tableNode = model.nodes[location.tableIndex];
  if (!tableNode || tableNode.type !== "table") {
    return {};
  }

  const cell = tableNode.rows[location.rowIndex]?.cells[location.cellIndex];
  if (!cell) {
    return {};
  }

  const paragraph = tableCellParagraphs(cell.nodes)[location.paragraphIndex];
  if (!paragraph) {
    return {};
  }

  return { paragraph, tableNode };
}

function collectFormFieldsFromModel(model: DocModel): DocxSelectedFormField[] {
  const collected: DocxSelectedFormField[] = [];

  model.nodes.forEach((node, nodeIndex) => {
    if (node.type === "paragraph") {
      node.children.forEach((child, childIndex) => {
        if (child.type !== "form-field") {
          return;
        }

        collected.push({
          location: {
            kind: "paragraph",
            nodeIndex,
            childIndex,
          },
          field: child,
        });
      });
      return;
    }

    node.rows.forEach((row, rowIndex) => {
      row.cells.forEach((cell, cellIndex) => {
        tableCellParagraphs(cell.nodes).forEach((paragraph, paragraphIndex) => {
          paragraph.children.forEach((child, childIndex) => {
            if (child.type !== "form-field") {
              return;
            }

            collected.push({
              location: {
                kind: "table-cell",
                tableIndex: nodeIndex,
                rowIndex,
                cellIndex,
                paragraphIndex,
                childIndex,
              },
              field: child,
            });
          });
        });
      });
    });
  });

  return collected;
}

type ParagraphTextUpdateOptions = Parameters<typeof updateParagraphText>[3];

function selectionFallbackParagraphLocation(
  selection: DocxEditorSelection
): ParagraphLocation {
  if (selection.kind === "paragraph") {
    return {
      kind: "paragraph",
      nodeIndex: selection.nodeIndex,
    };
  }

  return {
    kind: "table-cell",
    tableIndex: selection.tableIndex,
    rowIndex: selection.rowIndex,
    cellIndex: selection.cellIndex,
    paragraphIndex: 0,
  };
}

function selectionFromTextRangeLocation(
  location: DocxTextRangeLocation
): DocxEditorSelection {
  if (location.kind === "paragraph") {
    return {
      kind: "paragraph",
      nodeIndex: location.nodeIndex,
    };
  }

  return {
    kind: "table-cell",
    tableIndex: location.tableIndex,
    rowIndex: location.rowIndex,
    cellIndex: location.cellIndex,
  };
}

function paragraphLocationFromTextRangeLocation(
  location: DocxTextRangeLocation
): ParagraphLocation {
  if (location.kind === "paragraph") {
    return {
      kind: "paragraph",
      nodeIndex: location.nodeIndex,
    };
  }

  return {
    kind: "table-cell",
    tableIndex: location.tableIndex,
    rowIndex: location.rowIndex,
    cellIndex: location.cellIndex,
    paragraphIndex: Math.max(0, Math.round(location.paragraphIndex)),
  };
}

function resolveSelectedParagraphLocation(
  selection: DocxEditorSelection,
  activeTextRange?: DocxTextRange
): ParagraphLocation {
  const activeLocation = activeTextRange?.start.location;
  if (activeLocation) {
    return paragraphLocationFromTextRangeLocation(activeLocation);
  }

  return selectionFallbackParagraphLocation(selection);
}

function normalizeParagraphLocationForModel(
  model: DocModel,
  location: ParagraphLocation
): ParagraphLocation | undefined {
  const exact = getParagraphAtLocation(model, location).paragraph;
  if (exact) {
    return cloneTextRangeLocation(location);
  }

  if (location.kind === "table-cell") {
    const tableNode = model.nodes[location.tableIndex];
    if (tableNode && tableNode.type === "table") {
      const safeRowIndex = clampNumber(
        location.rowIndex,
        0,
        Math.max(0, tableNode.rows.length - 1)
      );
      const row = tableNode.rows[safeRowIndex];
      if (row) {
        const safeCellIndex = clampNumber(
          location.cellIndex,
          0,
          Math.max(0, row.cells.length - 1)
        );
        const candidateCell = row.cells[safeCellIndex];
        if (candidateCell && !candidateCell.style?.vMergeContinuation) {
          const paragraphs = tableCellParagraphs(candidateCell.nodes);
          if (paragraphs.length > 0) {
            return {
              kind: "table-cell",
              tableIndex: location.tableIndex,
              rowIndex: safeRowIndex,
              cellIndex: safeCellIndex,
              paragraphIndex: clampNumber(
                location.paragraphIndex,
                0,
                Math.max(0, paragraphs.length - 1)
              ),
            };
          }
        }
      }

      const firstInTable = firstParagraphLocationInTable(
        model,
        location.tableIndex
      );
      if (firstInTable) {
        return firstInTable;
      }
    }
  }

  if (model.nodes.length === 0) {
    return undefined;
  }

  const anchorNodeIndex =
    location.kind === "paragraph" ? location.nodeIndex : location.tableIndex;
  const clampedAnchorNodeIndex = clampNumber(
    anchorNodeIndex,
    0,
    Math.max(0, model.nodes.length - 1)
  );

  const sameNodeFallback = firstParagraphLocationFromNode(
    model,
    clampedAnchorNodeIndex
  );
  if (sameNodeFallback) {
    return sameNodeFallback;
  }

  for (
    let nodeIndex = clampedAnchorNodeIndex + 1;
    nodeIndex < model.nodes.length;
    nodeIndex += 1
  ) {
    const forward = firstParagraphLocationFromNode(model, nodeIndex);
    if (forward) {
      return forward;
    }
  }

  for (
    let nodeIndex = clampedAnchorNodeIndex - 1;
    nodeIndex >= 0;
    nodeIndex -= 1
  ) {
    const backward = firstParagraphLocationFromNode(model, nodeIndex);
    if (backward) {
      return backward;
    }
  }

  return firstParagraphLocationInDocument(model);
}

function normalizeTextRangeForModel(
  model: DocModel,
  range?: DocxTextRange
): DocxTextRange | undefined {
  if (!range) {
    return undefined;
  }

  const normalized = normalizeTextRange(range);
  const startLocation = normalizeParagraphLocationForModel(
    model,
    paragraphLocationFromTextRangeLocation(normalized.start.location)
  );
  const endLocation = normalizeParagraphLocationForModel(
    model,
    paragraphLocationFromTextRangeLocation(normalized.end.location)
  );
  if (!startLocation || !endLocation) {
    return undefined;
  }

  const startParagraph = getParagraphAtLocation(model, startLocation).paragraph;
  const endParagraph = getParagraphAtLocation(model, endLocation).paragraph;
  if (!startParagraph || !endParagraph) {
    return undefined;
  }

  return normalizeTextRange({
    start: {
      location: cloneTextRangeLocation(startLocation),
      offset: normalizeRangeBoundaryParagraphOffset(
        startParagraph,
        normalized.start.offset
      ),
    },
    end: {
      location: cloneTextRangeLocation(endLocation),
      offset: normalizeRangeBoundaryParagraphOffset(
        endParagraph,
        normalized.end.offset
      ),
    },
  });
}

function normalizeSelectionForModel(
  model: DocModel,
  selection: DocxEditorSelection
): DocxEditorSelection {
  const normalizedParagraphLocation = normalizeParagraphLocationForModel(
    model,
    selectionFallbackParagraphLocation(selection)
  );
  if (!normalizedParagraphLocation) {
    return {
      kind: "paragraph",
      nodeIndex: 0,
    };
  }

  return selectionFromTextRangeLocation(normalizedParagraphLocation);
}

function normalizeEditorCursorStateForModel(
  model: DocModel,
  selection: DocxEditorSelection,
  activeTextRange?: DocxTextRange
): {
  selection: DocxEditorSelection;
  activeTextRange?: DocxTextRange;
} {
  const normalizedRange = normalizeTextRangeForModel(model, activeTextRange);
  if (normalizedRange) {
    return {
      selection: normalizeSelectionForModel(
        model,
        selectionFromTextRangeLocation(normalizedRange.start.location)
      ),
      activeTextRange: normalizedRange,
    };
  }

  return {
    selection: normalizeSelectionForModel(model, selection),
    activeTextRange: undefined,
  };
}

function updateParagraphTextAtLocation(
  model: DocModel,
  location: ParagraphLocation,
  text: string,
  options?: ParagraphTextUpdateOptions
): DocModel {
  if (location.kind === "paragraph") {
    return updateParagraphText(model, location.nodeIndex, text, options);
  }

  return updateTableCellParagraphText(
    model,
    location.tableIndex,
    location.rowIndex,
    location.cellIndex,
    location.paragraphIndex,
    text,
    options
  );
}

function sectionParagraphLocationKey(
  location: DocxSectionParagraphLocation
): string {
  return JSON.stringify({
    region: location.region,
    partName: location.partName,
    nodeIndex: location.nodeIndex,
    rowIndex: location.rowIndex ?? -1,
    cellIndex: location.cellIndex ?? -1,
    paragraphIndex: location.paragraphIndex ?? -1,
  });
}

function sectionImageLocationKey(location: DocxSectionImageLocation): string {
  return JSON.stringify({
    region: location.region,
    partName: location.partName,
    nodeIndex: location.nodeIndex,
    rowIndex: location.rowIndex ?? -1,
    cellIndex: location.cellIndex ?? -1,
    paragraphIndex: location.paragraphIndex ?? -1,
    childIndex: location.childIndex,
  });
}

function tableCellParagraphDraftKey(
  tableIndex: number,
  rowIndex: number,
  cellIndex: number,
  paragraphIndex: number
): string {
  return `${tableIndex}:${rowIndex}:${cellIndex}:${paragraphIndex}`;
}

function parseSectionParagraphLocationKey(
  raw: string
): DocxSectionParagraphLocation | undefined {
  try {
    const parsed = JSON.parse(raw) as {
      region?: string;
      partName?: string;
      nodeIndex?: number;
      rowIndex?: number;
      cellIndex?: number;
      paragraphIndex?: number;
    };

    if (
      !parsed ||
      (parsed.region !== "header" && parsed.region !== "footer") ||
      typeof parsed.partName !== "string" ||
      !Number.isFinite(parsed.nodeIndex)
    ) {
      return undefined;
    }

    const rowIndex =
      Number.isFinite(parsed.rowIndex) && (parsed.rowIndex as number) >= 0
        ? Math.round(parsed.rowIndex as number)
        : undefined;
    const cellIndex =
      Number.isFinite(parsed.cellIndex) && (parsed.cellIndex as number) >= 0
        ? Math.round(parsed.cellIndex as number)
        : undefined;
    const paragraphIndex =
      Number.isFinite(parsed.paragraphIndex) &&
      (parsed.paragraphIndex as number) >= 0
        ? Math.round(parsed.paragraphIndex as number)
        : undefined;

    return {
      region: parsed.region,
      partName: parsed.partName,
      nodeIndex: Math.max(0, Math.round(parsed.nodeIndex as number)),
      rowIndex,
      cellIndex,
      paragraphIndex,
    };
  } catch {
    return undefined;
  }
}

function paragraphTextFromSectionLocation(
  sectionNodes: DocModel["nodes"],
  location: Omit<DocxSectionParagraphLocation, "region" | "partName">
): string | undefined {
  const rootNode = sectionNodes[location.nodeIndex];
  if (!rootNode) {
    return undefined;
  }

  if (location.rowIndex === undefined || location.cellIndex === undefined) {
    if (rootNode.type !== "paragraph") {
      return undefined;
    }
    return paragraphText(rootNode);
  }

  if (rootNode.type !== "table") {
    return undefined;
  }

  const paragraphIndex = Math.max(0, Math.round(location.paragraphIndex ?? 0));
  const cell = rootNode.rows[location.rowIndex]?.cells[location.cellIndex];
  if (!cell) {
    return undefined;
  }

  const paragraph = tableCellParagraphs(cell.nodes)[paragraphIndex];
  return paragraph ? paragraphText(paragraph) : undefined;
}

function sectionParagraphFromLocation(
  sectionNodes: DocModel["nodes"],
  location: Omit<DocxSectionParagraphLocation, "region" | "partName">
): {
  paragraph?: ParagraphNode;
  tableNode?: TableNode;
} {
  const rootNode = sectionNodes[location.nodeIndex];
  if (!rootNode) {
    return {};
  }

  if (location.rowIndex === undefined || location.cellIndex === undefined) {
    if (rootNode.type !== "paragraph") {
      return {};
    }
    return { paragraph: rootNode };
  }

  if (rootNode.type !== "table") {
    return {};
  }

  const paragraphIndex = Math.max(0, Math.round(location.paragraphIndex ?? 0));
  const cell = rootNode.rows[location.rowIndex]?.cells[location.cellIndex];
  if (!cell) {
    return {};
  }

  const paragraph = tableCellParagraphs(cell.nodes)[paragraphIndex];
  if (!paragraph) {
    return {};
  }

  return {
    paragraph,
    tableNode: rootNode,
  };
}

function updateSectionParagraphTextAtLocation(
  model: DocModel,
  location: DocxSectionParagraphLocation,
  text: string,
  options?: ParagraphTextUpdateOptions
): DocModel {
  const next = cloneDocModel(model);
  const candidateSectionLists =
    location.region === "header"
      ? [
          next.metadata.headerSections ?? [],
          ...(next.metadata.sections?.map(
            (section) => section.headerSections ?? []
          ) ?? []),
        ]
      : [
          next.metadata.footerSections ?? [],
          ...(next.metadata.sections?.map(
            (section) => section.footerSections ?? []
          ) ?? []),
        ];

  const normalizedLocation = {
    nodeIndex: Math.max(0, Math.round(location.nodeIndex)),
    rowIndex:
      Number.isFinite(location.rowIndex) && (location.rowIndex as number) >= 0
        ? Math.round(location.rowIndex as number)
        : undefined,
    cellIndex:
      Number.isFinite(location.cellIndex) && (location.cellIndex as number) >= 0
        ? Math.round(location.cellIndex as number)
        : undefined,
    paragraphIndex:
      Number.isFinite(location.paragraphIndex) &&
      (location.paragraphIndex as number) >= 0
        ? Math.round(location.paragraphIndex as number)
        : undefined,
  };

  let changed = false;
  candidateSectionLists.forEach((sections) => {
    sections.forEach((section) => {
      if (section.partName !== location.partName) {
        return;
      }

      const currentText = paragraphTextFromSectionLocation(
        section.nodes,
        normalizedLocation
      );
      if (currentText === undefined || currentText === text) {
        return;
      }

      const scopedModel: DocModel = {
        ...next,
        nodes: section.nodes,
      };
      const updatedScopedModel =
        normalizedLocation.rowIndex === undefined ||
        normalizedLocation.cellIndex === undefined
          ? updateParagraphText(
              scopedModel,
              normalizedLocation.nodeIndex,
              text,
              options
            )
          : updateTableCellParagraphText(
              scopedModel,
              normalizedLocation.nodeIndex,
              normalizedLocation.rowIndex,
              normalizedLocation.cellIndex,
              normalizedLocation.paragraphIndex ?? 0,
              text,
              options
            );

      section.nodes = updatedScopedModel.nodes;
      changed = true;
    });
  });

  return changed ? next : model;
}

function updateSectionImageFloatingAtLocation(
  model: DocModel,
  location: DocxSectionImageLocation,
  patch: Partial<NonNullable<ImageRunNode["floating"]>>
): DocModel {
  const next = cloneDocModel(model);
  const candidateSectionLists =
    location.region === "header"
      ? [
          next.metadata.headerSections ?? [],
          ...(next.metadata.sections?.map(
            (section) => section.headerSections ?? []
          ) ?? []),
        ]
      : [
          next.metadata.footerSections ?? [],
          ...(next.metadata.sections?.map(
            (section) => section.footerSections ?? []
          ) ?? []),
        ];

  const normalizedLocation = {
    nodeIndex: Math.max(0, Math.round(location.nodeIndex)),
    rowIndex:
      Number.isFinite(location.rowIndex) && (location.rowIndex as number) >= 0
        ? Math.round(location.rowIndex as number)
        : undefined,
    cellIndex:
      Number.isFinite(location.cellIndex) && (location.cellIndex as number) >= 0
        ? Math.round(location.cellIndex as number)
        : undefined,
    paragraphIndex:
      Number.isFinite(location.paragraphIndex) &&
      (location.paragraphIndex as number) >= 0
        ? Math.round(location.paragraphIndex as number)
        : undefined,
    childIndex: Math.max(0, Math.round(location.childIndex)),
  };

  let changed = false;
  candidateSectionLists.forEach((sections) => {
    sections.forEach((section) => {
      if (section.partName !== location.partName) {
        return;
      }

      const lookup = sectionParagraphFromLocation(
        section.nodes,
        normalizedLocation
      );
      const paragraph = lookup.paragraph;
      if (!paragraph) {
        return;
      }

      const child = paragraph.children[normalizedLocation.childIndex];
      if (!child || child.type !== "image") {
        return;
      }

      child.floating = {
        ...(child.floating ?? {}),
        ...patch,
      };
      paragraph.sourceXml = undefined;
      if (lookup.tableNode) {
        lookup.tableNode.sourceXml = undefined;
      }
      changed = true;
    });
  });

  return changed ? next : model;
}

export function useDocxEditor(
  options: UseDocxEditorOptions = {}
): DocxEditorController {
  const starterTemplateRef = React.useRef<DocModel>(
    cloneDocModel(options.starterModel ?? defaultStarterModel)
  );

  const [model, setModel] = React.useState<DocModel>(() =>
    cloneDocModel(starterTemplateRef.current)
  );
  const [basePackage, setBasePackage] = React.useState<
    OoxmlPackage | undefined
  >();
  const [documentLoadNonce, setDocumentLoadNonce] = React.useState<number>(0);
  const [fileName, setFileName] = React.useState<string>(
    options.initialFileName ?? "(new document)"
  );
  const [selection, setSelection] = React.useState<DocxEditorSelection>({
    kind: "paragraph",
    nodeIndex: 0,
  });
  const [selectedFormFieldLocation, setSelectedFormFieldLocation] =
    React.useState<DocxFormFieldLocation | undefined>();
  const [status, setStatus] = React.useState<string>(
    options.initialStatus ?? "Ready"
  );
  const [importError, setImportError] = React.useState<Error | undefined>();
  const [isImporting, setIsImporting] = React.useState(false);
  const [documentTheme, setDocumentThemeState] =
    React.useState<DocxDocumentTheme>(options.initialDocumentTheme ?? "light");
  const [showTrackedChanges, setShowTrackedChangesState] =
    React.useState<boolean>(options.initialShowTrackedChanges ?? false);
  const [showComments, setShowCommentsState] = React.useState<boolean>(
    options.initialShowComments ?? false
  );
  const [paginationInfo, setPaginationInfo] =
    React.useState<DocxPaginationInfo>({
      currentPage: 1,
      totalPages: 1,
    });
  const activeImportAbortControllerRef =
    React.useRef<AbortController | undefined>(undefined);
  const [history, setHistory] = React.useState<{
    past: DocxHistorySnapshot[];
    future: DocxHistorySnapshot[];
  }>({
    past: [],
    future: [],
  });
  const [activeTextRange, setActiveTextRangeState] = React.useState<
    DocxTextRange | undefined
  >();
  const [pendingRunStyle, setPendingRunStyle] = React.useState<
    TextRunNode["style"] | undefined
  >();
  const [historyRestoreRequest, setHistoryRestoreRequest] = React.useState<
    DocxHistoryRestoreRequest | undefined
  >();
  const [selectionSessionKind, setSelectionSessionKind] =
    React.useState<DocxSelectionSessionKind>("idle");

  const modelRef = React.useRef<DocModel>(model);
  const selectionRef = React.useRef<DocxEditorSelection>(selection);
  const activeTextRangeRef = React.useRef<DocxTextRange | undefined>(
    activeTextRange
  );
  const pendingRunStyleRef = React.useRef<TextRunNode["style"] | undefined>(
    pendingRunStyle
  );
  const suppressNextDomSelectionRestoreRef = React.useRef(false);
  const domSelectionRestoreModelRef = React.useRef(model);
  const selectionSessionRef = React.useRef<DocxSelectionSessionKind>("idle");
  const selectionSessionTimeoutRef = React.useRef<number | null>(null);
  const lastInViewerActiveTextRangeRef = React.useRef<
    DocxTextRange | undefined
  >(undefined);
  const suppressSelectionResetRef = React.useRef(false);
  const historyRestoreNonceRef = React.useRef(0);
  const embeddedFontLoadNonceRef = React.useRef(0);
  const loadedEmbeddedFontFacesRef = React.useRef<FontFace[]>([]);

  React.useEffect(() => {
    modelRef.current = model;
  }, [model]);

  React.useEffect(() => {
    selectionRef.current = selection;
  }, [selection]);

  const selectedFormField = React.useMemo<
    DocxSelectedFormField | undefined
  >(() => {
    if (!selectedFormFieldLocation) {
      return undefined;
    }

    const { paragraph } = getParagraphAtLocation(
      model,
      selectedFormFieldLocation
    );
    if (!paragraph) {
      return undefined;
    }

    const child = paragraph.children[selectedFormFieldLocation.childIndex];
    if (!child || child.type !== "form-field") {
      return undefined;
    }

    return {
      location: {
        ...selectedFormFieldLocation,
      },
      field: child,
    };
  }, [model, selectedFormFieldLocation]);

  React.useEffect(() => {
    activeTextRangeRef.current = activeTextRange;
    if (activeTextRange) {
      lastInViewerActiveTextRangeRef.current = cloneTextRange(activeTextRange);
    }
  }, [activeTextRange]);

  React.useEffect(() => {
    pendingRunStyleRef.current = pendingRunStyle;
  }, [pendingRunStyle]);

  React.useEffect(() => {
    setPaginationInfo({
      currentPage: 1,
      totalPages: 1,
    });
  }, [documentLoadNonce]);

  const clearSelectionSession = React.useCallback(
    (expectedKind?: DocxSelectionSessionKind): void => {
      if (expectedKind && selectionSessionRef.current !== expectedKind) {
        return;
      }

      if (
        selectionSessionTimeoutRef.current !== null &&
        typeof window !== "undefined"
      ) {
        window.clearTimeout(selectionSessionTimeoutRef.current);
      }
      selectionSessionTimeoutRef.current = null;
      selectionSessionRef.current = "idle";
      setSelectionSessionKind("idle");
    },
    []
  );

  const beginSelectionSession = React.useCallback(
    (
      kind: Exclude<DocxSelectionSessionKind, "idle">,
      options?: {
        settleAfterMs?: number;
      }
    ): void => {
      if (
        selectionSessionTimeoutRef.current !== null &&
        typeof window !== "undefined"
      ) {
        window.clearTimeout(selectionSessionTimeoutRef.current);
      }
      selectionSessionTimeoutRef.current = null;
      selectionSessionRef.current = kind;
      setSelectionSessionKind(kind);

      if (
        Number.isFinite(options?.settleAfterMs) &&
        (options?.settleAfterMs as number) > 0 &&
        typeof window !== "undefined"
      ) {
        const expectedKind = kind;
        selectionSessionTimeoutRef.current = window.setTimeout(() => {
          selectionSessionTimeoutRef.current = null;
          if (selectionSessionRef.current === expectedKind) {
            selectionSessionRef.current = "idle";
            setSelectionSessionKind("idle");
          }
        }, Math.max(16, Math.round(options?.settleAfterMs as number)));
      }
    },
    []
  );

  React.useEffect(() => {
    const normalizedCursorState = normalizeEditorCursorStateForModel(
      model,
      selection,
      activeTextRange
    );
    const modelChangedSinceLastSelectionRestoreCheck =
      domSelectionRestoreModelRef.current !== model;
    domSelectionRestoreModelRef.current = model;
    const nextSelection = normalizedCursorState.selection;
    const nextRange = normalizedCursorState.activeTextRange;
    const selectionChanged = !sameEditorSelection(selection, nextSelection);
    const rangeChanged = !sameTextRange(activeTextRange, nextRange);

    if (!selectionChanged && !rangeChanged) {
      if (suppressNextDomSelectionRestoreRef.current) {
        suppressNextDomSelectionRestoreRef.current = false;
        return;
      }

      if (
        !shouldReissueDomSelectionRestore({
          modelChanged: modelChangedSinceLastSelectionRestoreCheck,
          selectionChanged,
          rangeChanged,
          activeTextRange,
          suppressNext: false,
          selectionSessionKind: selectionSessionRef.current,
        })
      ) {
        return;
      }

      // Model updates can tear down native DOM ranges even when the logical
      // editor range is unchanged (for example, formatting an expanded
      // selection). Re-issue a restore request so the visual selection remains.
      const nextNonce = historyRestoreNonceRef.current + 1;
      historyRestoreNonceRef.current = nextNonce;
      setHistoryRestoreRequest({
        nonce: nextNonce,
        selection: cloneEditorSelection(selection),
        activeTextRange: cloneTextRange(activeTextRange),
      });
      return;
    }

    suppressSelectionResetRef.current = true;
    if (selectionChanged) {
      setSelection(cloneEditorSelection(nextSelection));
    }
    if (rangeChanged) {
      setActiveTextRangeState(cloneTextRange(nextRange));
    }

    const nextNonce = historyRestoreNonceRef.current + 1;
    historyRestoreNonceRef.current = nextNonce;
    setHistoryRestoreRequest({
      nonce: nextNonce,
      selection: cloneEditorSelection(nextSelection),
      activeTextRange: cloneTextRange(nextRange),
    });
  }, [model]);

  React.useEffect(() => {
    if (!selectedFormFieldLocation) {
      return;
    }

    const { paragraph } = getParagraphAtLocation(
      model,
      selectedFormFieldLocation
    );
    const formField = paragraph?.children[selectedFormFieldLocation.childIndex];
    if (!formField || formField.type !== "form-field") {
      setSelectedFormFieldLocation(undefined);
    }
  }, [model, selectedFormFieldLocation]);

  const selectedParagraphLocation = React.useMemo(
    () => resolveSelectedParagraphLocation(selection, activeTextRange),
    [activeTextRange, selection]
  );

  const selectedParagraph = React.useMemo(() => {
    return getParagraphAtLocation(model, selectedParagraphLocation).paragraph;
  }, [model, selectedParagraphLocation]);

  const availableParagraphStyles = React.useMemo(() => {
    const embeddedStyles = model.metadata.paragraphStyles ?? [];
    if (embeddedStyles.length > 0) {
      return normalizeParagraphStyleDefinitionsForUi(embeddedStyles);
    }

    return normalizeParagraphStyleDefinitionsForUi(
      defaultStarterModel.metadata.paragraphStyles
    );
  }, [model.metadata.paragraphStyles]);
  const paragraphStyleById = React.useMemo(
    () => new Map(availableParagraphStyles.map((style) => [style.id, style])),
    [availableParagraphStyles]
  );

  const selectedParagraphStyleId = React.useMemo(() => {
    if (!selectedParagraph) {
      return (
        model.metadata.defaultParagraphStyleId ??
        availableParagraphStyles.find((style) => style.isDefault)?.id
      );
    }

    if (selectedParagraph.style?.styleId) {
      return selectedParagraph.style.styleId;
    }

    if (selectedParagraph.style?.headingLevel) {
      const headingStyleId = `Heading${selectedParagraph.style.headingLevel}`;
      if (paragraphStyleById.has(headingStyleId)) {
        return headingStyleId;
      }
    }

    return (
      model.metadata.defaultParagraphStyleId ??
      availableParagraphStyles.find((style) => style.isDefault)?.id
    );
  }, [
    selectedParagraph,
    model.metadata.defaultParagraphStyleId,
    availableParagraphStyles,
    paragraphStyleById,
  ]);

  const selectedLineSpacing = React.useMemo<DocxLineSpacingInfo>(() => {
    const lineTwipsRaw = selectedParagraph?.style?.spacing?.lineTwips;
    const lineTwips = Number.isFinite(lineTwipsRaw)
      ? Math.max(1, Math.round(lineTwipsRaw as number))
      : undefined;
    const lineRule = (selectedParagraph?.style?.spacing?.lineRule ??
      "auto") as DocxLineSpacingRule;
    const multiple =
      lineTwips !== undefined
        ? resolveAutoLineSpacingMultiple(
            lineTwips,
            DEFAULT_PARAGRAPH_LINE_MULTIPLE
          )
        : DEFAULT_PARAGRAPH_LINE_MULTIPLE;

    return {
      lineRule,
      lineTwips,
      multiple: Number(multiple.toFixed(3)),
    };
  }, [selectedParagraph]);

  const selectedBorderContext: DocxBorderContext =
    selectedParagraphLocation.kind === "table-cell" ? "table" : "paragraph";

  const activeBorderPresets = React.useMemo<DocxBorderPresetState>(() => {
    if (selectedParagraphLocation.kind === "table-cell") {
      const node = model.nodes[selectedParagraphLocation.tableIndex];
      if (!node || node.type !== "table") {
        return tableBorderPresetState(undefined);
      }

      const cell =
        node.rows[selectedParagraphLocation.rowIndex]?.cells[
          selectedParagraphLocation.cellIndex
        ];
      return tableBorderPresetState(node.style?.borders, cell?.style?.borders);
    }

    return paragraphBorderPresetState(selectedParagraph?.style?.borders);
  }, [model.nodes, selectedParagraph, selectedParagraphLocation]);

  const selectedRunStyleFromSelection = React.useMemo(() => {
    if (!selectedParagraph) {
      return undefined;
    }

    if (!activeTextRange) {
      return firstRunStyle(selectedParagraph);
    }

    const normalizedActiveRange = normalizeTextRange(activeTextRange);
    const isExpandedRange =
      compareTextRangeBoundaries(
        normalizedActiveRange.start,
        normalizedActiveRange.end
      ) < 0;
    const offset =
      isExpandedRange &&
      !sameParagraphLocation(
        normalizedActiveRange.start.location,
        normalizedActiveRange.end.location
      )
        ? 0
        : !sameParagraphLocation(
            normalizedActiveRange.start.location,
            selectedParagraphLocation
          )
        ? 0
        : normalizedActiveRange.start.offset;

    return (
      // Toolbar state should match the style that newly typed text inherits at
      // a caret boundary, which comes from the following run when available.
      firstTextStyleAtOffset(selectedParagraph, offset, false) ??
      firstRunStyle(selectedParagraph)
    );
  }, [activeTextRange, selectedParagraph, selectedParagraphLocation]);

  const selectedRunStyle = React.useMemo(() => {
    const hasExpandedRange = Boolean(
      activeTextRange &&
        compareTextRangeBoundaries(activeTextRange.start, activeTextRange.end) <
          0
    );
    if (hasExpandedRange || !pendingRunStyle) {
      return selectedRunStyleFromSelection;
    }

    return {
      ...(selectedRunStyleFromSelection ?? {}),
      ...pendingRunStyle,
    };
  }, [activeTextRange, pendingRunStyle, selectedRunStyleFromSelection]);

  const selectedLink = React.useMemo(() => {
    if (!selectedParagraph || !activeTextRange) {
      return undefined;
    }

    const normalizedActiveRange = normalizeTextRange(activeTextRange);

    if (
      !sameParagraphLocation(
        normalizedActiveRange.start.location,
        normalizedActiveRange.end.location
      )
    ) {
      if (
        !sameParagraphLocation(
          normalizedActiveRange.start.location,
          selectedParagraphLocation
        )
      ) {
        return undefined;
      }

      return linkAtOffset(
        selectedParagraph,
        normalizedActiveRange.start.offset,
        false
      );
    }

    if (
      compareTextRangeBoundaries(
        normalizedActiveRange.start,
        normalizedActiveRange.end
      ) < 0
    ) {
      return uniformLinkInRange(
        selectedParagraph,
        normalizedActiveRange.start.offset,
        normalizedActiveRange.end.offset
      );
    }

    return linkAtOffset(
      selectedParagraph,
      normalizedActiveRange.start.offset,
      true
    );
  }, [activeTextRange, selectedParagraph, selectedParagraphLocation]);

  const selectedListType = selectedParagraph
    ? paragraphListType(selectedParagraph, model.metadata.numberingDefinitions)
    : undefined;
  const trackedChanges = React.useMemo(
    () => collectTrackedChangesFromModel(model),
    [model]
  );
  const comments = React.useMemo(() => collectCommentsFromModel(model), [
    model,
  ]);
  const hasUnorderedList = selectedListType === "unordered";
  const hasOrderedList = selectedListType === "ordered";
  const canUndo = history.past.length > 0;
  const canRedo = history.future.length > 0;
  const pendingExportModelTransformerRef = React.useRef<
    ((model: DocModel) => DocModel) | undefined
  >(undefined);
  const setDocumentTheme = React.useCallback(
    (theme: DocxDocumentTheme): void => {
      setDocumentThemeState(theme);
    },
    []
  );
  const setShowTrackedChanges = React.useCallback(
    (nextShowTrackedChanges: boolean): void => {
      setShowTrackedChangesState(nextShowTrackedChanges);
    },
    []
  );
  const toggleShowTrackedChanges = React.useCallback((): void => {
    setShowTrackedChangesState((current) => !current);
  }, []);
  const setShowComments = React.useCallback(
    (nextShowComments: boolean): void => {
      setShowCommentsState(nextShowComments);
    },
    []
  );
  const toggleShowComments = React.useCallback((): void => {
    setShowCommentsState((current) => !current);
  }, []);
  const registerPendingExportModelTransformer = React.useCallback(
    (transformer?: (model: DocModel) => DocModel): void => {
      pendingExportModelTransformerRef.current = transformer;
    },
    []
  );
  const unloadEmbeddedFonts = React.useCallback((): void => {
    if (typeof document !== "undefined" && "fonts" in document) {
      loadedEmbeddedFontFacesRef.current.forEach((fontFace) => {
        try {
          document.fonts.delete(fontFace);
        } catch {
          // Ignore font cleanup failures and continue removing the rest.
        }
      });
    }

    loadedEmbeddedFontFacesRef.current = [];
  }, []);
  const loadEmbeddedFontsFromPackage = React.useCallback(
    async (pkg?: OoxmlPackage): Promise<void> => {
      embeddedFontLoadNonceRef.current += 1;
      const requestNonce = embeddedFontLoadNonceRef.current;
      unloadEmbeddedFonts();

      if (
        !pkg ||
        typeof document === "undefined" ||
        !("fonts" in document) ||
        typeof FontFace === "undefined"
      ) {
        return;
      }

      const fontDescriptors = collectEmbeddedFontFaceDescriptors(pkg);
      if (fontDescriptors.length === 0) {
        return;
      }

      const loadedFontFaces = (
        await Promise.all(
          fontDescriptors.map(async (fontDescriptor) => {
            try {
              const fontFace = new FontFace(
                fontDescriptor.family,
                fontDescriptor.source,
                {
                  style: fontDescriptor.style,
                  weight: fontDescriptor.weight,
                }
              );
              await fontFace.load();
              return fontFace;
            } catch {
              return undefined;
            }
          })
        )
      ).filter((fontFace): fontFace is FontFace => Boolean(fontFace));

      if (embeddedFontLoadNonceRef.current !== requestNonce) {
        return;
      }

      loadedFontFaces.forEach((fontFace) => {
        document.fonts.add(fontFace);
      });
      loadedEmbeddedFontFacesRef.current = loadedFontFaces;

      try {
        await document.fonts.ready;
      } catch {
        // Font readiness is best-effort; continue once attempted.
      }
    },
    [unloadEmbeddedFonts]
  );

  React.useEffect(() => {
    return () => {
      activeImportAbortControllerRef.current?.abort();
      unloadEmbeddedFonts();
    };
  }, [unloadEmbeddedFonts]);

  const dispatchEditorTransaction = React.useCallback(
    (
      resolver: (
        current: DocxEditorTransactionContext
      ) => DocxEditorTransactionPatch | undefined
    ): boolean => {
      const currentModel = modelRef.current;
      const currentSelection = cloneEditorSelection(selectionRef.current);
      const currentRange = cloneTextRange(activeTextRangeRef.current);
      const currentPendingRunStyle = cloneTextStyle(pendingRunStyleRef.current);

      const patch = resolver({
        model: currentModel,
        selection: currentSelection,
        activeTextRange: currentRange,
        pendingRunStyle: currentPendingRunStyle,
      });
      if (!patch) {
        return false;
      }

      const nextModel = patch.model ?? currentModel;
      const hasExplicitRangePatch = Object.prototype.hasOwnProperty.call(
        patch,
        "activeTextRange"
      );
      const hasExplicitSelectionPatch = Object.prototype.hasOwnProperty.call(
        patch,
        "selection"
      );
      const hasPendingRunStylePatch = Object.prototype.hasOwnProperty.call(
        patch,
        "pendingRunStyle"
      );
      const requestedSelection = patch.selection ?? currentSelection;
      const requestedRange = hasExplicitRangePatch
        ? patch.activeTextRange
        : currentRange;
      const normalizedCursorState = normalizeEditorCursorStateForModel(
        nextModel,
        requestedSelection,
        requestedRange
      );
      const nextSelection = normalizedCursorState.selection;
      const nextRange = normalizedCursorState.activeTextRange;
      const nextPendingRunStyle = hasPendingRunStylePatch
        ? cloneTextStyle(patch.pendingRunStyle)
        : currentPendingRunStyle;

      const modelChanged = nextModel !== currentModel;
      const selectionChanged = !sameEditorSelection(
        currentSelection,
        nextSelection
      );
      const rangeChanged = !sameTextRange(currentRange, nextRange);
      const pendingRunStyleChanged =
        hasPendingRunStylePatch &&
        JSON.stringify(nextPendingRunStyle ?? null) !==
          JSON.stringify(currentPendingRunStyle ?? null);

      if (
        !modelChanged &&
        !selectionChanged &&
        !rangeChanged &&
        !pendingRunStyleChanged &&
        !patch.status &&
        !patch.clearSelectedFormField
      ) {
        return false;
      }

      if (modelChanged && patch.pushHistory !== false) {
        setHistory((currentHistory) => ({
          past: [
            ...currentHistory.past.slice(-99),
            {
              model: currentModel,
              selection: cloneEditorSelection(currentSelection),
              activeTextRange: cloneTextRange(currentRange),
            },
          ],
          future: [],
        }));
      }

      if (modelChanged) {
        setModel(nextModel);
      }

      if (selectionChanged) {
        suppressSelectionResetRef.current = true;
        setSelection(cloneEditorSelection(nextSelection));
      }

      if (rangeChanged) {
        suppressSelectionResetRef.current = true;
        setActiveTextRangeState(cloneTextRange(nextRange));
      }

      if (pendingRunStyleChanged) {
        setPendingRunStyle(nextPendingRunStyle);
      }

      const localSelectionSessionActive =
        selectionSessionRef.current === "pointer" ||
        selectionSessionRef.current === "keyboard" ||
        selectionSessionRef.current === "composition";
      const shouldRequestDomSelectionRestore =
        selectionChanged || rangeChanged
          ? !localSelectionSessionActive ||
            hasExplicitSelectionPatch ||
            hasExplicitRangePatch
          : false;

      if (shouldRequestDomSelectionRestore) {
        const nextNonce = historyRestoreNonceRef.current + 1;
        historyRestoreNonceRef.current = nextNonce;
        setHistoryRestoreRequest({
          nonce: nextNonce,
          selection: cloneEditorSelection(nextSelection),
          activeTextRange: cloneTextRange(nextRange),
        });
      }

      if (patch.clearSelectedFormField) {
        setSelectedFormFieldLocation(undefined);
      }

      if (patch.status) {
        setStatus(patch.status);
      }

      return true;
    },
    []
  );

  const applyModelChange = React.useCallback(
    (updater: (current: DocModel) => DocModel, successStatus?: string) => {
      dispatchEditorTransaction((current) => {
        const nextModel = updater(current.model);
        if (nextModel === current.model) {
          return undefined;
        }

        return {
          model: nextModel,
          status: successStatus,
          clearSelectedFormField: true,
        };
      });
    },
    [dispatchEditorTransaction]
  );

  const replaceDocumentWithImportError = React.useCallback(
    (fileName: string, error: Error): void => {
      unloadEmbeddedFonts();
      setModel(createBlankDocumentModel());
      setDocumentLoadNonce((current) => current + 1);
      setHistory({ past: [], future: [] });
      setHistoryRestoreRequest(undefined);
      setBasePackage(undefined);
      setFileName(fileName);
      setSelection({ kind: "paragraph", nodeIndex: 0 });
      setActiveTextRangeState(undefined);
      setPendingRunStyle(undefined);
      setSelectedFormFieldLocation(undefined);
      setImportError(error);
      setStatus(`Failed to load file: ${error.message}`);
    },
    [unloadEmbeddedFonts]
  );

  const importDocxFile = React.useCallback(
    async (file: File): Promise<void> => {
      activeImportAbortControllerRef.current?.abort();
      activeImportAbortControllerRef.current = undefined;

      if (!/\.docx?$/i.test(file.name)) {
        replaceDocumentWithImportError(
          file.name,
          new Error("Only .docx and .doc files are supported")
        );
        return;
      }

      setIsImporting(true);
      setImportError(undefined);
      setStatus(`Loading ${file.name}...`);
      const importAbortController = new AbortController();
      activeImportAbortControllerRef.current = importAbortController;
      const traceName = createDocxImportPerformanceTraceName(file.name);
      const startMark = `${traceName}:start`;
      const bufferStartMark = `${traceName}:arrayBuffer:start`;
      const bufferEndMark = `${traceName}:arrayBuffer:end`;
      const workerStartMark = `${traceName}:worker:start`;
      const workerEndMark = `${traceName}:worker:end`;
      const fontsStartMark = `${traceName}:fonts:start`;
      const fontsEndMark = `${traceName}:fonts:end`;
      const stateStartMark = `${traceName}:state:start`;
      const stateEndMark = `${traceName}:state:end`;
      markDocxImportPerformance(startMark);
      try {
        markDocxImportPerformance(bufferStartMark);
        const buffer = await file.arrayBuffer();
        markDocxImportPerformance(bufferEndMark);
        measureDocxImportPerformance(
          `${traceName}:arrayBuffer`,
          bufferStartMark,
          bufferEndMark
        );

        markDocxImportPerformance(workerStartMark);
        const importResult = await importDocxBuffer(buffer, {
          signal: importAbortController.signal,
          transferBuffer: true,
        });
        markDocxImportPerformance(workerEndMark);
        measureDocxImportPerformance(
          `${traceName}:${importResult.source}`,
          workerStartMark,
          workerEndMark
        );
        const pkg = importResult.package;
        const nextModel = importResult.model;

        markDocxImportPerformance(fontsStartMark);
        await loadEmbeddedFontsFromPackage(pkg);
        markDocxImportPerformance(fontsEndMark);
        measureDocxImportPerformance(
          `${traceName}:fonts`,
          fontsStartMark,
          fontsEndMark
        );

        markDocxImportPerformance(stateStartMark);
        setModel(nextModel);
        setDocumentLoadNonce((current) => current + 1);
        setHistory({ past: [], future: [] });
        setHistoryRestoreRequest(undefined);
        setBasePackage(pkg);
        setFileName(file.name);
        setSelection({ kind: "paragraph", nodeIndex: 0 });
        setActiveTextRangeState(undefined);
        setPendingRunStyle(undefined);
        setSelectedFormFieldLocation(undefined);
        setImportError(undefined);
        setStatus(`Loaded ${file.name}`);
        markDocxImportPerformance(stateEndMark);
        measureDocxImportPerformance(
          `${traceName}:state-dispatch`,
          stateStartMark,
          stateEndMark
        );
        measureDocxImportPerformance(`${traceName}:total`, startMark, stateEndMark);
      } catch (error) {
        if (
          error instanceof Error &&
          error.name === "AbortError" &&
          activeImportAbortControllerRef.current !== importAbortController
        ) {
          return;
        }
        const nextError =
          error instanceof Error ? error : new Error("Unknown error");
        replaceDocumentWithImportError(file.name, nextError);
      } finally {
        if (activeImportAbortControllerRef.current === importAbortController) {
          activeImportAbortControllerRef.current = undefined;
          setIsImporting(false);
        }
      }
    },
    [loadEmbeddedFontsFromPackage, replaceDocumentWithImportError]
  );

  const newDocument = React.useCallback((): void => {
    activeImportAbortControllerRef.current?.abort();
    activeImportAbortControllerRef.current = undefined;
    unloadEmbeddedFonts();
    setModel(cloneDocModel(starterTemplateRef.current));
    setDocumentLoadNonce((current) => current + 1);
    setBasePackage(undefined);
    setFileName("(new document)");
    setSelection({ kind: "paragraph", nodeIndex: 0 });
    setActiveTextRangeState(undefined);
    setPendingRunStyle(undefined);
    setSelectedFormFieldLocation(undefined);
    setHistory({ past: [], future: [] });
    setHistoryRestoreRequest(undefined);
    setImportError(undefined);
    setStatus("Created new document");
  }, [unloadEmbeddedFonts]);

  const exportDocx = React.useCallback(async (): Promise<void> => {
    const sourceModel = modelRef.current;
    const transformedModel = pendingExportModelTransformerRef.current
      ? pendingExportModelTransformerRef.current(sourceModel)
      : sourceModel;
    if (transformedModel !== sourceModel) {
      setModel(transformedModel);
    }

    const output = await serializeDocx(transformedModel, basePackage);
    const blob = new Blob([output], {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });

    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = /\.docx?$/i.test(fileName)
      ? fileName.replace(/\.docx?$/i, "") + "-edited.docx"
      : "edited.docx";
    anchor.style.display = "none";
    document.body.append(anchor);
    anchor.click();
    window.setTimeout(() => {
      anchor.remove();
      URL.revokeObjectURL(url);
    }, 1000);

    setStatus("Exported DOCX");
  }, [basePackage, fileName]);

  const undo = React.useCallback((): void => {
    setHistory((currentHistory) => {
      const previousSnapshot =
        currentHistory.past[currentHistory.past.length - 1];
      const previous = previousSnapshot?.model;
      if (!previous) {
        setStatus("Nothing to undo");
        return currentHistory;
      }

      const currentSnapshot: DocxHistorySnapshot = {
        model: modelRef.current,
        selection: cloneEditorSelection(selectionRef.current),
        activeTextRange: cloneTextRange(activeTextRangeRef.current),
      };
      const restoredSelection = cloneEditorSelection(
        previousSnapshot.selection
      );
      const restoredTextRange = cloneTextRange(
        previousSnapshot.activeTextRange
      );
      suppressSelectionResetRef.current = true;
      setModel(previous);
      setSelection(restoredSelection);
      setActiveTextRangeState(restoredTextRange);
      setSelectedFormFieldLocation(undefined);
      setPendingRunStyle(undefined);
      const nextNonce = historyRestoreNonceRef.current + 1;
      historyRestoreNonceRef.current = nextNonce;
      setHistoryRestoreRequest({
        nonce: nextNonce,
        selection: restoredSelection,
        activeTextRange: restoredTextRange,
      });
      setStatus("Undo");

      return {
        past: currentHistory.past.slice(0, -1),
        future: [currentSnapshot, ...currentHistory.future].slice(0, 100),
      };
    });
  }, []);

  const redo = React.useCallback((): void => {
    setHistory((currentHistory) => {
      const nextSnapshot = currentHistory.future[0];
      const next = nextSnapshot?.model;
      if (!next) {
        setStatus("Nothing to redo");
        return currentHistory;
      }

      const currentSnapshot: DocxHistorySnapshot = {
        model: modelRef.current,
        selection: cloneEditorSelection(selectionRef.current),
        activeTextRange: cloneTextRange(activeTextRangeRef.current),
      };
      const restoredSelection = cloneEditorSelection(nextSnapshot.selection);
      const restoredTextRange = cloneTextRange(nextSnapshot.activeTextRange);
      suppressSelectionResetRef.current = true;
      setModel(next);
      setSelection(restoredSelection);
      setActiveTextRangeState(restoredTextRange);
      setSelectedFormFieldLocation(undefined);
      setPendingRunStyle(undefined);
      const nextNonce = historyRestoreNonceRef.current + 1;
      historyRestoreNonceRef.current = nextNonce;
      setHistoryRestoreRequest({
        nonce: nextNonce,
        selection: restoredSelection,
        activeTextRange: restoredTextRange,
      });
      setStatus("Redo");

      return {
        past: [...currentHistory.past, currentSnapshot].slice(-100),
        future: currentHistory.future.slice(1),
      };
    });
  }, []);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented) {
        return;
      }

      if (!(event.metaKey || event.ctrlKey) || event.altKey) {
        return;
      }

      const target = event.target;
      if (target instanceof HTMLElement) {
        const isContentEditableTarget =
          target.isContentEditable ||
          target.closest("[contenteditable='true']") !== null;
        const isFormFieldTarget =
          target instanceof HTMLTextAreaElement ||
          target instanceof HTMLSelectElement ||
          (target instanceof HTMLInputElement &&
            ![
              "button",
              "checkbox",
              "radio",
              "submit",
              "reset",
              "file",
              "range",
              "color",
            ].includes(target.type));

        // Let native undo/redo handle actively edited content and form fields.
        if (isContentEditableTarget || isFormFieldTarget) {
          return;
        }
      }

      const key = event.key.toLowerCase();
      if (key === "z") {
        event.preventDefault();
        if (event.shiftKey) {
          redo();
        } else {
          undo();
        }
        return;
      }

      if (key === "y") {
        event.preventDefault();
        redo();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [redo, undo]);

  const applyToSelectedParagraphNode = React.useCallback(
    (mutate: (paragraph: ParagraphNode) => void): void => {
      applyModelChange((current) => {
        const next = cloneDocModel(current);
        const { paragraph, tableNode } = getParagraphAtLocation(
          next,
          selectedParagraphLocation
        );
        if (!paragraph) {
          return current;
        }

        mutate(paragraph);
        paragraph.sourceXml = undefined;
        if (tableNode) {
          tableNode.sourceXml = undefined;
        }
        return next;
      });
    },
    [applyModelChange, selectedParagraphLocation]
  );

  const restoreSelectionAfterToolbarCommand = React.useCallback(
    (
      selectionSnapshot: DocxEditorSelection,
      textRangeSnapshot?: DocxTextRange
    ): void => {
      dispatchEditorTransaction(() => ({
        selection: cloneEditorSelection(selectionSnapshot),
        activeTextRange: cloneTextRange(textRangeSnapshot),
        pushHistory: false,
      }));
    },
    [dispatchEditorTransaction]
  );

  const setActiveTextRange = React.useCallback(
    (range?: DocxTextRange): void => {
      const currentRange = cloneTextRange(activeTextRangeRef.current);
      if (!range) {
        if (!currentRange) {
          return;
        }

        activeTextRangeRef.current = undefined;
        React.startTransition(() => {
          setActiveTextRangeState(undefined);
        });
        setPendingRunStyle(undefined);
        return;
      }

      const normalized = normalizeTextRange({
        start: {
          location: cloneTextRangeLocation(range.start.location),
          offset: range.start.offset,
        },
        end: {
          location: cloneTextRangeLocation(range.end.location),
          offset: range.end.offset,
        },
      });

      if (sameTextRange(currentRange, normalized)) {
        return;
      }

      const nextRange = cloneTextRange(normalized);
      activeTextRangeRef.current = nextRange;
      lastInViewerActiveTextRangeRef.current = cloneTextRange(nextRange);
      React.startTransition(() => {
        setActiveTextRangeState(cloneTextRange(nextRange));
      });
      // Pending typing style should only survive while the caret stays in the
      // same spot immediately after a toolbar style toggle.
      setPendingRunStyle(undefined);
    },
    []
  );

  const suppressNextDomSelectionRestore = React.useCallback((): void => {
    suppressNextDomSelectionRestoreRef.current = true;
  }, []);

  const applySelectedStyleChange = React.useCallback(
    (
      mutator: (
        currentStyle: TextRunNode["style"] | undefined
      ) => TextRunNode["style"] | undefined
    ): void => {
      const textRangeSnapshot = cloneTextRange(
        activeTextRangeRef.current ?? lastInViewerActiveTextRangeRef.current
      );
      const selectionSnapshot = textRangeSnapshot
        ? selectionFromTextRangeLocation(
            normalizeTextRange(textRangeSnapshot).start.location
          )
        : cloneEditorSelection(selectionRef.current);
      const hasExpandedRange = Boolean(
        textRangeSnapshot &&
          compareTextRangeBoundaries(
            textRangeSnapshot.start,
            textRangeSnapshot.end
          ) < 0
      );

      if (hasExpandedRange && textRangeSnapshot) {
        applyModelChange((current) => {
          const next = cloneDocModel(current);
          const normalizedRange = normalizeTextRange(textRangeSnapshot);
          const rangeParagraphs = paragraphRangeForMutate(
            next,
            normalizedRange.start.location,
            normalizedRange.end.location
          );

          let changed = false;
          for (const { location } of rangeParagraphs) {
            const { paragraph, tableNode } = getParagraphAtLocation(
              next,
              location
            );
            if (!paragraph) {
              continue;
            }

            const [safeStart, safeEnd] =
              resolveRangeBoundaryOffsetsForParagraph(
                location,
                normalizedRange.start,
                normalizedRange.end,
                paragraph
              );
            const paragraphChanged = mutateParagraphTextStyleInRange(
              paragraph,
              safeStart,
              safeEnd,
              mutator
            );
            if (!paragraphChanged) {
              continue;
            }

            paragraph.sourceXml = undefined;
            if (tableNode) {
              tableNode.sourceXml = undefined;
            }
            changed = true;
          }

          if (!changed) {
            return current;
          }

          return next;
        });
        restoreSelectionAfterToolbarCommand(
          selectionSnapshot,
          textRangeSnapshot
        );
        return;
      }

      setPendingRunStyle((currentPendingStyle) => {
        const baselineStyle =
          currentPendingStyle ??
          selectedRunStyleFromSelection ??
          selectedRunStyle ??
          undefined;
        return mutator(cloneTextStyle(baselineStyle));
      });
      restoreSelectionAfterToolbarCommand(selectionSnapshot, textRangeSnapshot);
    },
    [
      applyModelChange,
      selectedRunStyle,
      selectedRunStyleFromSelection,
      restoreSelectionAfterToolbarCommand,
      lastInViewerActiveTextRangeRef,
    ]
  );

  const setParagraphStyle = React.useCallback(
    (styleId?: string): void => {
      const fallbackStyleId = modelRef.current.metadata.defaultParagraphStyleId;
      const nextStyleId = styleId ?? fallbackStyleId;
      const styleDefinition = nextStyleId
        ? paragraphStyleById.get(nextStyleId)
        : undefined;
      const textRangeSnapshot = cloneTextRange(
        activeTextRangeRef.current ?? lastInViewerActiveTextRangeRef.current
      );
      const selectionSnapshot = textRangeSnapshot
        ? selectionFromTextRangeLocation(
            normalizeTextRange(textRangeSnapshot).start.location
          )
        : cloneEditorSelection(selectionRef.current);

      applyToSelectedParagraphNode((paragraph) => {
        paragraph.style = {
          ...(paragraph.style ?? {}),
          align: styleDefinition?.align ?? paragraph.style?.align,
          headingLevel: styleDefinition?.headingLevel,
          styleId: nextStyleId,
          styleName: styleDefinition?.name ?? nextStyleId,
        };
      });
      restoreSelectionAfterToolbarCommand(selectionSnapshot, textRangeSnapshot);
    },
    [
      applyToSelectedParagraphNode,
      paragraphStyleById,
      restoreSelectionAfterToolbarCommand,
      lastInViewerActiveTextRangeRef,
    ]
  );

  const setHeading = React.useCallback(
    (heading?: HeadingLevel): void => {
      const styleId = heading
        ? `Heading${heading}`
        : modelRef.current.metadata.defaultParagraphStyleId;
      const styleDefinition = styleId
        ? paragraphStyleById.get(styleId)
        : undefined;
      const textRangeSnapshot = cloneTextRange(
        activeTextRangeRef.current ?? lastInViewerActiveTextRangeRef.current
      );
      const selectionSnapshot = textRangeSnapshot
        ? selectionFromTextRangeLocation(
            normalizeTextRange(textRangeSnapshot).start.location
          )
        : cloneEditorSelection(selectionRef.current);

      applyToSelectedParagraphNode((paragraph) => {
        paragraph.style = {
          ...(paragraph.style ?? {}),
          headingLevel: heading,
          styleId,
          styleName:
            styleDefinition?.name ??
            (heading ? `Heading ${heading}` : paragraph.style?.styleName),
        };
      });
      restoreSelectionAfterToolbarCommand(selectionSnapshot, textRangeSnapshot);
    },
    [
      applyToSelectedParagraphNode,
      paragraphStyleById,
      restoreSelectionAfterToolbarCommand,
      lastInViewerActiveTextRangeRef,
    ]
  );

  const setLineSpacing = React.useCallback(
    (lineMultiple: number): void => {
      if (!Number.isFinite(lineMultiple)) {
        return;
      }

      const normalizedMultiple = Math.max(0.5, Math.min(6, lineMultiple));
      const textRangeSnapshot = cloneTextRange(
        activeTextRangeRef.current ?? lastInViewerActiveTextRangeRef.current
      );
      const selectionSnapshot = textRangeSnapshot
        ? selectionFromTextRangeLocation(
            normalizeTextRange(textRangeSnapshot).start.location
          )
        : cloneEditorSelection(selectionRef.current);
      const targetLocation = resolveSelectedParagraphLocation(
        selectionSnapshot,
        textRangeSnapshot
      );

      applyModelChange((current) => {
        const next = cloneDocModel(current);
        const { paragraph, tableNode } = getParagraphAtLocation(
          next,
          targetLocation
        );
        if (!paragraph) {
          return current;
        }

        const spacing = paragraph.style?.spacing;
        paragraph.style = {
          ...(paragraph.style ?? {}),
          spacing: {
            ...(spacing ?? {}),
            lineTwips: Math.round(normalizedMultiple * 240),
            lineRule: "auto",
          },
        };
        paragraph.sourceXml = undefined;
        if (tableNode) {
          tableNode.sourceXml = undefined;
        }
        return next;
      });
      restoreSelectionAfterToolbarCommand(selectionSnapshot, textRangeSnapshot);
    },
    [
      applyModelChange,
      restoreSelectionAfterToolbarCommand,
      lastInViewerActiveTextRangeRef,
    ]
  );

  const setFontFamily = React.useCallback(
    (fontFamily: string): void => {
      applySelectedStyleChange((style) => ({
        ...(style ?? {}),
        fontFamily,
      }));
    },
    [applySelectedStyleChange]
  );

  const setFontSize = React.useCallback(
    (fontSizePt: number): void => {
      applySelectedStyleChange((style) => ({
        ...(style ?? {}),
        fontSizePt,
      }));
    },
    [applySelectedStyleChange]
  );

  const toggleBold = React.useCallback((): void => {
    const nextBold = !Boolean(selectedRunStyle?.bold);
    applySelectedStyleChange((style) => ({
      ...(style ?? {}),
      bold: nextBold,
    }));
  }, [applySelectedStyleChange, selectedRunStyle?.bold]);

  const toggleItalic = React.useCallback((): void => {
    const nextItalic = !Boolean(selectedRunStyle?.italic);
    applySelectedStyleChange((style) => ({
      ...(style ?? {}),
      italic: nextItalic,
    }));
  }, [applySelectedStyleChange, selectedRunStyle?.italic]);

  const toggleUnderline = React.useCallback((): void => {
    const nextUnderline = !Boolean(selectedRunStyle?.underline);
    applySelectedStyleChange((style) => ({
      ...(style ?? {}),
      underline: nextUnderline,
    }));
  }, [applySelectedStyleChange, selectedRunStyle?.underline]);

  const toggleStrike = React.useCallback((): void => {
    const nextStrike = !Boolean(selectedRunStyle?.strike);
    applySelectedStyleChange((style) => ({
      ...(style ?? {}),
      strike: nextStrike,
    }));
  }, [applySelectedStyleChange, selectedRunStyle?.strike]);

  const toggleSuperscript = React.useCallback((): void => {
    const nextVerticalAlign =
      selectedRunStyle?.verticalAlign === "superscript"
        ? undefined
        : "superscript";
    applySelectedStyleChange((style) => ({
      ...(style ?? {}),
      verticalAlign: nextVerticalAlign,
    }));
  }, [applySelectedStyleChange, selectedRunStyle?.verticalAlign]);

  const toggleSubscript = React.useCallback((): void => {
    const nextVerticalAlign =
      selectedRunStyle?.verticalAlign === "subscript" ? undefined : "subscript";
    applySelectedStyleChange((style) => ({
      ...(style ?? {}),
      verticalAlign: nextVerticalAlign,
    }));
  }, [applySelectedStyleChange, selectedRunStyle?.verticalAlign]);

  const setHighlight = React.useCallback(
    (highlight?: string): void => {
      applySelectedStyleChange((style) => ({
        ...(style ?? {}),
        highlight,
      }));
    },
    [applySelectedStyleChange]
  );

  const setTextColor = React.useCallback(
    (color?: string): void => {
      applySelectedStyleChange((style) => ({
        ...(style ?? {}),
        color,
      }));
    },
    [applySelectedStyleChange]
  );

  const setLink = React.useCallback(
    (link?: string): void => {
      const normalized = link?.trim();
      const nextLink =
        normalized && normalized.length > 0 ? normalized : undefined;

      if (!activeTextRange) {
        setStatus("Select text to apply a link");
        return;
      }

      applyModelChange(
        (current) => {
          const next = cloneDocModel(current);
          const normalizedRange = normalizeTextRange(activeTextRange);
          let changed = false;
          const rangeParagraphs = paragraphRangeForMutate(
            next,
            normalizedRange.start.location,
            normalizedRange.end.location
          );

          for (const { location } of rangeParagraphs) {
            const { paragraph, tableNode } = getParagraphAtLocation(
              next,
              location
            );
            if (!paragraph) {
              continue;
            }

            let rangeStart = normalizedRange.start.offset;
            let rangeEnd = normalizedRange.end.offset;
            if (
              !sameParagraphLocation(location, normalizedRange.start.location)
            ) {
              rangeStart = 0;
            }
            if (
              !sameParagraphLocation(location, normalizedRange.end.location)
            ) {
              rangeEnd = paragraphText(paragraph).length;
            }
            if (
              compareParagraphLocations(
                location,
                normalizedRange.start.location
              ) > 0
            ) {
              rangeStart = 0;
            }
            if (
              compareParagraphLocations(
                location,
                normalizedRange.end.location
              ) < 0
            ) {
              rangeEnd = paragraphText(paragraph).length;
            }

            if (
              compareTextRangeBoundaries(
                normalizedRange.start,
                normalizedRange.end
              ) >= 0
            ) {
              const linkRange = linkRangeAtOffset(paragraph, rangeStart);
              if (!linkRange) {
                continue;
              }

              rangeStart = linkRange.start;
              rangeEnd = linkRange.end;
            }

            const paragraphChanged = mutateParagraphLinkInRange(
              paragraph,
              rangeStart,
              rangeEnd,
              nextLink
            );
            if (!paragraphChanged) {
              continue;
            }

            paragraph.sourceXml = undefined;
            if (tableNode) {
              tableNode.sourceXml = undefined;
            }
            changed = true;
          }

          if (!changed) {
            return current;
          }

          return next;
        },
        nextLink ? "Updated link" : "Removed link"
      );
    },
    [activeTextRange, applyModelChange]
  );

  const replaceExpandedSelection = React.useCallback(
    (text: string, range?: DocxTextRange): DocxTextRange | undefined => {
      const sourceRange = range ?? activeTextRangeRef.current;
      if (!sourceRange) {
        return undefined;
      }

      const normalizedRange = normalizeTextRange({
        start: {
          location: cloneTextRangeLocation(sourceRange.start.location),
          offset: sourceRange.start.offset,
        },
        end: {
          location: cloneTextRangeLocation(sourceRange.end.location),
          offset: sourceRange.end.offset,
        },
      });

      if (
        compareTextRangeBoundaries(
          normalizedRange.start,
          normalizedRange.end
        ) >= 0
      ) {
        return undefined;
      }

      const replacementText = text.replace(/\r\n?/g, "\n");
      let collapsedLocation = cloneTextRangeLocation(
        normalizedRange.start.location
      );
      let collapsedOffset = Math.max(
        0,
        Math.round(normalizedRange.start.offset)
      );
      let replaced = false;

      applyModelChange(
        (current) => {
          let next = cloneDocModel(current);
          const clearNumberingIfParagraphEmpty = (
            location: ParagraphLocation
          ): void => {
            const { paragraph, tableNode } = getParagraphAtLocation(
              next,
              location
            );
            if (
              !paragraph ||
              !paragraphIsEffectivelyEmpty(paragraph) ||
              !paragraph.style?.numbering
            ) {
              return;
            }

            paragraph.style = {
              ...(cloneParagraphStyle(paragraph.style) ?? {}),
              numbering: undefined,
            };
            paragraph.sourceXml = undefined;
            if (tableNode) {
              tableNode.sourceXml = undefined;
            }
          };
          if (rangeCoversEntireDocument(current, normalizedRange)) {
            const firstParagraphLocation =
              firstParagraphLocationInDocument(next);
            const firstParagraph = firstParagraphLocation
              ? getParagraphAtLocation(next, firstParagraphLocation).paragraph
              : undefined;
            const replacementRunStyle = cloneTextStyle(
              pendingRunStyle ??
                (firstParagraph
                  ? firstTextStyleAtOffset(firstParagraph, 0, true) ??
                    firstRunStyle(firstParagraph)
                  : undefined)
            );

            next.nodes = [
              {
                type: "paragraph",
                children: [
                  {
                    type: "text",
                    text: replacementText,
                    ...(replacementRunStyle
                      ? { style: replacementRunStyle }
                      : undefined),
                  },
                ],
              },
            ];
            collapsedLocation = {
              kind: "paragraph",
              nodeIndex: 0,
            };
            collapsedOffset = replacementText.length;
            replaced = true;
            return next;
          }

          const startLookup = getParagraphAtLocation(
            next,
            normalizedRange.start.location
          );
          const endLookup = getParagraphAtLocation(
            next,
            normalizedRange.end.location
          );
          const startParagraph = startLookup.paragraph;
          const endParagraph = endLookup.paragraph;
          if (!startParagraph || !endParagraph) {
            return current;
          }

          const safeStart = normalizeRangeBoundaryParagraphOffset(
            startParagraph,
            normalizedRange.start.offset
          );
          const safeEnd = normalizeRangeBoundaryParagraphOffset(
            endParagraph,
            normalizedRange.end.offset
          );
          collapsedOffset = safeStart + replacementText.length;
          const endParagraphStyleForMerge = cloneParagraphStyle(
            endParagraph.style
          );

          const insertedStyle = cloneTextStyle(
            pendingRunStyle ??
              firstTextStyleAtOffset(startParagraph, safeStart, true) ??
              firstRunStyle(startParagraph) ??
              firstTextStyleAtOffset(endParagraph, safeEnd, false) ??
              firstRunStyle(endParagraph)
          );

          if (
            sameParagraphLocation(
              normalizedRange.start.location,
              normalizedRange.end.location
            )
          ) {
            const text = paragraphText(startParagraph);
            const nextText = `${text.slice(
              0,
              safeStart
            )}${replacementText}${text.slice(safeEnd)}`;
            if (nextText === text) {
              return current;
            }

            next = updateParagraphTextAtLocation(
              next,
              normalizedRange.start.location,
              nextText,
              {
                insertedStyle,
              }
            );
            clearNumberingIfParagraphEmpty(normalizedRange.start.location);
            replaced = true;
            return next;
          }

          const startText = paragraphText(startParagraph);
          const endText = paragraphText(endParagraph);
          const carriesStartPrefix = safeStart > 0;
          const carriesEndSuffix = safeEnd < endText.length;
          const shouldAdoptEndParagraphStyleOnDelete =
            replacementText.length === 0 &&
            !carriesStartPrefix &&
            carriesEndSuffix;
          const canPreserveCrossParagraphRunStyles =
            paragraphHasOnlyTextRuns(startParagraph) &&
            paragraphHasOnlyTextRuns(endParagraph);
          if (canPreserveCrossParagraphRunStyles) {
            const startRuns = startParagraph.children
              .filter((child): child is TextRunNode => child.type === "text")
              .map(cloneTextRunWithMetadata);
            const endRuns = endParagraph.children
              .filter((child): child is TextRunNode => child.type === "text")
              .map(cloneTextRunWithMetadata);
            const splitStartRuns = splitTextRunsAtOffset(startRuns, safeStart);
            const splitEndRuns = splitTextRunsAtOffset(endRuns, safeEnd);
            const mergedRuns: TextRunNode[] = [...splitStartRuns.left];
            if (replacementText.length > 0) {
              const previousRun = mergedRuns[mergedRuns.length - 1];
              const nextRun = splitEndRuns.right[0];
              const inferredLink =
                previousRun?.link &&
                nextRun?.link &&
                previousRun.link === nextRun.link
                  ? previousRun.link
                  : previousRun?.link ?? nextRun?.link;
              mergedRuns.push({
                type: "text",
                text: replacementText,
                style: cloneTextStyle(
                  insertedStyle ?? previousRun?.style ?? nextRun?.style
                ),
                link: inferredLink,
              });
            }
            mergedRuns.push(...splitEndRuns.right);

            startParagraph.children =
              mergedRuns.length > 0
                ? mergedRuns
                : [
                    {
                      type: "text",
                      text: "",
                      style: cloneTextStyle(insertedStyle),
                    },
                  ];
            startParagraph.sourceXml = undefined;
            if (startLookup.tableNode) {
              startLookup.tableNode.sourceXml = undefined;
            }
          } else {
            const mergedText = `${startText.slice(
              0,
              safeStart
            )}${replacementText}${endText.slice(safeEnd)}`;
            next = updateParagraphTextAtLocation(
              next,
              normalizedRange.start.location,
              mergedText,
              {
                insertedStyle,
              }
            );
          }
          if (shouldAdoptEndParagraphStyleOnDelete) {
            const mergedLookup = getParagraphAtLocation(
              next,
              normalizedRange.start.location
            );
            if (mergedLookup.paragraph) {
              mergedLookup.paragraph.style = endParagraphStyleForMerge
                ? cloneParagraphStyle(endParagraphStyleForMerge)
                : undefined;
              mergedLookup.paragraph.sourceXml = undefined;
              if (mergedLookup.tableNode) {
                mergedLookup.tableNode.sourceXml = undefined;
              }
            }
          }
          clearNumberingIfParagraphEmpty(normalizedRange.start.location);

          let compactedRemovedParagraphs = false;
          if (
            normalizedRange.start.location.kind === "paragraph" &&
            normalizedRange.end.location.kind === "paragraph" &&
            normalizedRange.end.location.nodeIndex >
              normalizedRange.start.location.nodeIndex
          ) {
            const removeStartIndex =
              normalizedRange.start.location.nodeIndex + 1;
            const removeEndIndex = normalizedRange.end.location.nodeIndex;
            const removeCount = removeEndIndex - removeStartIndex + 1;
            if (removeCount > 0) {
              next.nodes.splice(removeStartIndex, removeCount);
              compactedRemovedParagraphs = true;
            }
          } else if (
            normalizedRange.start.location.kind === "table-cell" &&
            normalizedRange.end.location.kind === "table-cell" &&
            normalizedRange.start.location.tableIndex ===
              normalizedRange.end.location.tableIndex &&
            normalizedRange.start.location.rowIndex ===
              normalizedRange.end.location.rowIndex &&
            normalizedRange.start.location.cellIndex ===
              normalizedRange.end.location.cellIndex &&
            normalizedRange.end.location.paragraphIndex >
              normalizedRange.start.location.paragraphIndex
          ) {
            const tableNode =
              next.nodes[normalizedRange.start.location.tableIndex];
            if (tableNode && tableNode.type === "table") {
              const cell =
                tableNode.rows[normalizedRange.start.location.rowIndex]?.cells[
                  normalizedRange.start.location.cellIndex
                ];
              if (cell) {
                const paragraphNodeIndexes = cell.nodes.reduce<number[]>(
                  (indexes, node, nodeIndex) => {
                    if (node.type === "paragraph") {
                      indexes.push(nodeIndex);
                    }
                    return indexes;
                  },
                  []
                );
                for (
                  let paragraphIndex =
                    normalizedRange.end.location.paragraphIndex;
                  paragraphIndex >
                  normalizedRange.start.location.paragraphIndex;
                  paragraphIndex -= 1
                ) {
                  const nodeIndex = paragraphNodeIndexes[paragraphIndex];
                  if (nodeIndex !== undefined) {
                    cell.nodes.splice(nodeIndex, 1);
                    compactedRemovedParagraphs = true;
                  }
                }

                if (compactedRemovedParagraphs) {
                  tableNode.sourceXml = undefined;
                }
              }
            }
          }

          const fullyCoveredTableIndexes =
            replacementText.length === 0
              ? fullyCoveredTableNodeIndexesForRange(next, normalizedRange)
              : [];
          if (fullyCoveredTableIndexes.length > 0) {
            const uniqueDescendingIndexes = Array.from(
              new Set(fullyCoveredTableIndexes)
            ).sort((left, right) => right - left);
            let removedAnyTable = false;
            uniqueDescendingIndexes.forEach((tableIndex) => {
              if (next.nodes[tableIndex]?.type !== "table") {
                return;
              }
              next.nodes.splice(tableIndex, 1);
              removedAnyTable = true;
            });

            if (removedAnyTable) {
              compactedRemovedParagraphs = true;
              const adjustedLocation = adjustLocationAfterRemovedNodeIndexes(
                collapsedLocation,
                [...uniqueDescendingIndexes].sort((left, right) => left - right)
              );
              if (adjustedLocation) {
                collapsedLocation = adjustedLocation;
              } else {
                const firstLocation = firstParagraphLocationInDocument(next);
                if (firstLocation) {
                  collapsedLocation = cloneTextRangeLocation(firstLocation);
                } else {
                  next.nodes = [
                    {
                      type: "paragraph",
                      children: [{ type: "text", text: "" }],
                    },
                  ];
                  collapsedLocation = {
                    kind: "paragraph",
                    nodeIndex: 0,
                  };
                }
                collapsedOffset = 0;
              }
            }
          }

          if (!compactedRemovedParagraphs) {
            const rangeParagraphs = paragraphRangeForMutate(
              next,
              normalizedRange.start.location,
              normalizedRange.end.location
            );
            for (const { location } of rangeParagraphs) {
              if (
                sameParagraphLocation(location, normalizedRange.start.location)
              ) {
                continue;
              }
              next = updateParagraphTextAtLocation(next, location, "");
              clearNumberingIfParagraphEmpty(location);
            }
          }

          const collapsedParagraph = getParagraphAtLocation(
            next,
            collapsedLocation
          ).paragraph;
          if (!collapsedParagraph) {
            const firstLocation = firstParagraphLocationInDocument(next);
            if (firstLocation) {
              collapsedLocation = cloneTextRangeLocation(firstLocation);
              collapsedOffset = 0;
            } else {
              next.nodes = [
                {
                  type: "paragraph",
                  children: [{ type: "text", text: "" }],
                },
              ];
              collapsedLocation = {
                kind: "paragraph",
                nodeIndex: 0,
              };
              collapsedOffset = 0;
            }
          } else {
            collapsedOffset = normalizeRangeBoundaryParagraphOffset(
              collapsedParagraph,
              collapsedOffset
            );
          }

          replaced = true;
          return next;
        },
        replacementText.length > 0 ? "Replaced selection" : "Deleted selection"
      );

      if (!replaced) {
        return undefined;
      }

      const collapsedRange: DocxTextRange = {
        start: {
          location: cloneTextRangeLocation(collapsedLocation),
          offset: collapsedOffset,
        },
        end: {
          location: cloneTextRangeLocation(collapsedLocation),
          offset: collapsedOffset,
        },
      };

      suppressSelectionResetRef.current = true;
      if (collapsedLocation.kind === "paragraph") {
        setSelection({
          kind: "paragraph",
          nodeIndex: collapsedLocation.nodeIndex,
        });
      } else {
        setSelection({
          kind: "table-cell",
          tableIndex: collapsedLocation.tableIndex,
          rowIndex: collapsedLocation.rowIndex,
          cellIndex: collapsedLocation.cellIndex,
        });
      }
      setActiveTextRangeState(collapsedRange);
      setPendingRunStyle(undefined);

      return collapsedRange;
    },
    [applyModelChange, pendingRunStyle]
  );

  const deleteExpandedSelection = React.useCallback(
    (range?: DocxTextRange): DocxTextRange | undefined => {
      return replaceExpandedSelection("", range);
    },
    [replaceExpandedSelection]
  );

  const updateFormField = React.useCallback(
    (
      location: DocxFormFieldLocation,
      updater: (field: FormFieldRunNode) => FormFieldRunNode,
      successStatus?: string
    ): void => {
      dispatchEditorTransaction((current) => {
        const next = cloneDocModel(current.model);
        const { paragraph, tableNode } = getParagraphAtLocation(next, location);
        if (!paragraph) {
          return undefined;
        }

        const child = paragraph.children[location.childIndex];
        if (!child || child.type !== "form-field") {
          return undefined;
        }

        const updated = updater(cloneFormFieldRun(child));
        paragraph.children[location.childIndex] = {
          ...updated,
          type: "form-field",
          sourceXml: undefined,
        };
        paragraph.sourceXml = undefined;
        if (tableNode) {
          tableNode.sourceXml = undefined;
        }

        return {
          model: next,
          status: successStatus,
        };
      });
    },
    [dispatchEditorTransaction]
  );

  const selectFormField = React.useCallback(
    (location?: DocxFormFieldLocation): void => {
      if (!location) {
        setSelectedFormFieldLocation(undefined);
        return;
      }

      if (location.kind === "paragraph") {
        setSelection({
          kind: "paragraph",
          nodeIndex: location.nodeIndex,
        });
      } else {
        setSelection({
          kind: "table-cell",
          tableIndex: location.tableIndex,
          rowIndex: location.rowIndex,
          cellIndex: location.cellIndex,
        });
      }

      setSelectedFormFieldLocation({ ...location });
      setActiveTextRangeState(undefined);
      setPendingRunStyle(undefined);
    },
    []
  );

  const toggleFormCheckbox = React.useCallback(
    (location: DocxFormFieldLocation): void => {
      updateFormField(
        location,
        (field) => {
          if (field.fieldType !== "checkbox") {
            return field;
          }
          return {
            ...field,
            checked: !Boolean(field.checked),
          };
        },
        "Updated checkbox"
      );
    },
    [updateFormField]
  );

  const setFormFieldValue = React.useCallback(
    (location: DocxFormFieldLocation, value: string): void => {
      updateFormField(
        location,
        (field) => {
          if (field.fieldType === "checkbox") {
            const normalized = value.trim().toLowerCase();
            return {
              ...field,
              checked: ["1", "true", "yes", "on"].includes(normalized),
            };
          }

          return {
            ...field,
            value,
          };
        },
        "Updated form field"
      );
    },
    [updateFormField]
  );

  const updateFormFieldWidget = React.useCallback(
    (
      location: DocxFormFieldLocation,
      patch: Partial<NonNullable<FormFieldRunNode["widget"]>>
    ): void => {
      updateFormField(
        location,
        (field) => ({
          ...field,
          widget: mergeFormFieldWidgetPatch(field.widget, patch),
        }),
        "Updated form field settings"
      );
    },
    [updateFormField]
  );

  const applyBorderPreset = React.useCallback(
    (preset: DocxBorderPreset): void => {
      const textRangeSnapshot = cloneTextRange(
        activeTextRangeRef.current ?? lastInViewerActiveTextRangeRef.current
      );
      const normalizedRange = textRangeSnapshot
        ? normalizeTextRange(textRangeSnapshot)
        : undefined;
      const hasExpandedRange = Boolean(
        normalizedRange &&
          compareTextRangeBoundaries(
            normalizedRange.start,
            normalizedRange.end
          ) < 0
      );
      const selectionSnapshot = textRangeSnapshot
        ? selectionFromTextRangeLocation(
            (normalizedRange ?? textRangeSnapshot).start.location
          )
        : cloneEditorSelection(selectionRef.current);
      const targetLocation = resolveSelectedParagraphLocation(
        selectionSnapshot,
        normalizedRange ?? textRangeSnapshot
      );
      let isUnsupportedPreset = false;

      applyModelChange((current) => {
        if (targetLocation.kind === "table-cell") {
          const next = cloneDocModel(current);
          const tableNode = next.nodes[targetLocation.tableIndex];
          if (!tableNode || tableNode.type !== "table") {
            return current;
          }

          const targetCell =
            tableNode.rows[targetLocation.rowIndex]?.cells[
              targetLocation.cellIndex
            ];
          if (!targetCell) {
            return current;
          }

          const presetState = tableBorderPresetState(
            tableNode.style?.borders,
            targetCell.style?.borders
          );
          const removePreset = preset !== "none" && presetState[preset];

          if (preset === "diagonal-down" || preset === "diagonal-up") {
            const clonedCellStyle = cloneTableCellStyle(targetCell.style) ?? {};
            const nextCellBorders =
              cloneTableBorderSet(clonedCellStyle.borders) ?? {};
            const visibleBorder = removePreset
              ? nilTableBorderStyle()
              : toolbarTableBorderStyle(
                  resolvePreferredTableBorder(nextCellBorders) ??
                    resolvePreferredTableBorder(tableNode.style?.borders)
                );
            if (preset === "diagonal-down") {
              nextCellBorders.tl2br = { ...visibleBorder };
            } else {
              nextCellBorders.tr2bl = { ...visibleBorder };
            }
            targetCell.style = {
              ...clonedCellStyle,
              borders: nextCellBorders,
            };
            tableNode.sourceXml = undefined;
            return next;
          }

          const nextBorders = applyTableBorderPreset(
            tableNode.style?.borders,
            preset,
            removePreset
          );
          if (!nextBorders) {
            isUnsupportedPreset = true;
            return current;
          }

          tableNode.style = {
            ...(tableNode.style ?? {}),
            borders: nextBorders,
          };
          if (preset === "none" && targetCell.style?.borders) {
            const nextCellBorders =
              cloneTableBorderSet(targetCell.style.borders) ?? {};
            const nilBorder = nilTableBorderStyle();
            nextCellBorders.tl2br = { ...nilBorder };
            nextCellBorders.tr2bl = { ...nilBorder };
            targetCell.style = {
              ...(cloneTableCellStyle(targetCell.style) ?? {}),
              borders: nextCellBorders,
            };
          }
          tableNode.sourceXml = undefined;
          return next;
        }

        if (hasExpandedRange && normalizedRange) {
          const rangeLocations = paragraphRangeForMutate(
            current,
            normalizedRange.start.location,
            normalizedRange.end.location
          ).map((entry) => entry.location);
          if (rangeLocations.length === 0) {
            return current;
          }

          const paragraphsWithLocation = rangeLocations
            .map((location) => {
              const lookup = getParagraphAtLocation(current, location);
              if (!lookup.paragraph) {
                return undefined;
              }
              return {
                location,
                borders: lookup.paragraph.style?.borders,
              };
            })
            .filter(
              (
                entry
              ): entry is {
                location: ParagraphLocation;
                borders: ParagraphBorderSet | undefined;
              } => Boolean(entry)
            );
          if (paragraphsWithLocation.length === 0) {
            return current;
          }

          const removePreset =
            preset !== "none" &&
            paragraphRangePresetActive(
              preset,
              paragraphsWithLocation.map((entry) => entry.borders)
            );
          const next = cloneDocModel(current);
          let changed = false;

          for (
            let index = 0;
            index < paragraphsWithLocation.length;
            index += 1
          ) {
            const target = paragraphsWithLocation[index];
            const { paragraph, tableNode } = getParagraphAtLocation(
              next,
              target.location
            );
            if (!paragraph) {
              continue;
            }

            const clonedStyle = cloneParagraphStyle(paragraph.style) ?? {};
            const nextBorders = applyParagraphBorderPresetForRangeEntry(
              clonedStyle.borders,
              preset,
              removePreset,
              index,
              paragraphsWithLocation.length
            );
            if (!nextBorders) {
              isUnsupportedPreset = true;
              return current;
            }

            paragraph.style = {
              ...clonedStyle,
              borders: nextBorders,
            };
            paragraph.sourceXml = undefined;
            if (tableNode) {
              tableNode.sourceXml = undefined;
            }
            changed = true;
          }

          return changed ? next : current;
        }

        const next = cloneDocModel(current);
        const { paragraph, tableNode } = getParagraphAtLocation(
          next,
          targetLocation
        );
        if (!paragraph) {
          return current;
        }
        const clonedStyle = cloneParagraphStyle(paragraph.style) ?? {};
        const presetState = paragraphBorderPresetState(clonedStyle.borders);
        const removePreset = preset !== "none" && presetState[preset];
        const nextBorders = applyParagraphBorderPreset(
          clonedStyle.borders,
          preset,
          removePreset
        );
        if (!nextBorders) {
          isUnsupportedPreset = true;
          return current;
        }

        paragraph.style = {
          ...clonedStyle,
          borders: nextBorders,
        };
        paragraph.sourceXml = undefined;
        if (tableNode) {
          tableNode.sourceXml = undefined;
        }
        return next;
      }, "Updated borders");

      if (isUnsupportedPreset) {
        setStatus(
          "This border option is not supported for the current selection."
        );
      }

      restoreSelectionAfterToolbarCommand(selectionSnapshot, textRangeSnapshot);
    },
    [
      applyModelChange,
      restoreSelectionAfterToolbarCommand,
      setStatus,
      lastInViewerActiveTextRangeRef,
    ]
  );

  const setAlignment = React.useCallback(
    (align?: ParagraphAlignment): void => {
      const textRangeSnapshot = cloneTextRange(
        activeTextRangeRef.current ?? lastInViewerActiveTextRangeRef.current
      );
      const selectionSnapshot = textRangeSnapshot
        ? selectionFromTextRangeLocation(
            normalizeTextRange(textRangeSnapshot).start.location
          )
        : cloneEditorSelection(selectionRef.current);
      const normalizedRange = textRangeSnapshot
        ? normalizeTextRange(textRangeSnapshot)
        : undefined;
      const hasExpandedRange = Boolean(
        normalizedRange &&
          compareTextRangeBoundaries(
            normalizedRange.start,
            normalizedRange.end
          ) < 0
      );

      applyModelChange((current) => {
        const next = cloneDocModel(current);
        const targetLocations =
          hasExpandedRange && normalizedRange
            ? paragraphRangeForMutate(
                next,
                normalizedRange.start.location,
                normalizedRange.end.location
              ).map((entry) => entry.location)
            : [
                resolveSelectedParagraphLocation(
                  selectionSnapshot,
                  normalizedRange
                ),
              ];

        if (targetLocations.length === 0) {
          return current;
        }

        let changed = false;
        for (const location of targetLocations) {
          const { paragraph, tableNode } = getParagraphAtLocation(
            next,
            location
          );
          if (!paragraph || paragraph.style?.align === align) {
            continue;
          }

          paragraph.style = {
            ...(paragraph.style ?? {}),
            align,
          };
          paragraph.sourceXml = undefined;
          if (tableNode) {
            tableNode.sourceXml = undefined;
          }
          changed = true;
        }

        return changed ? next : current;
      });
      restoreSelectionAfterToolbarCommand(selectionSnapshot, textRangeSnapshot);
    },
    [
      applyModelChange,
      restoreSelectionAfterToolbarCommand,
      lastInViewerActiveTextRangeRef,
    ]
  );

  const toggleList = React.useCallback(
    (listType: DocxListType): void => {
      const textRangeSnapshot = cloneTextRange(
        activeTextRangeRef.current ?? lastInViewerActiveTextRangeRef.current
      );
      const normalizedRange = textRangeSnapshot
        ? normalizeTextRange(textRangeSnapshot)
        : undefined;
      const selectionSnapshot = normalizedRange
        ? selectionFromTextRangeLocation(normalizedRange.start.location)
        : cloneEditorSelection(selectionRef.current);
      const hasExpandedRange = Boolean(
        normalizedRange &&
          compareTextRangeBoundaries(
            normalizedRange.start,
            normalizedRange.end
          ) < 0
      );
      const collapsedTargetLocation = !hasExpandedRange
        ? resolveSelectedParagraphLocation(selectionSnapshot, normalizedRange)
        : undefined;
      const collapsedTargetOffset = normalizedRange?.start.offset;
      let postToggleRange: DocxTextRange | undefined;

      applyModelChange((current) => {
        const targetLocations =
          hasExpandedRange && normalizedRange
            ? paragraphRangeForMutate(
                current,
                normalizedRange.start.location,
                normalizedRange.end.location
              ).map((entry) => entry.location)
            : [
                resolveSelectedParagraphLocation(
                  selectionSnapshot,
                  normalizedRange
                ),
              ];

        if (targetLocations.length === 0) {
          return current;
        }

        const paragraphsWithLocation = targetLocations
          .map((location) => {
            const lookup = getParagraphAtLocation(current, location);
            if (!lookup.paragraph) {
              return undefined;
            }
            return {
              location,
              paragraph: lookup.paragraph,
            };
          })
          .filter(
            (
              entry
            ): entry is {
              location: ParagraphLocation;
              paragraph: ParagraphNode;
            } => Boolean(entry)
          );

        if (paragraphsWithLocation.length === 0) {
          return current;
        }

        const shouldRemove = paragraphsWithLocation.every(
          ({ paragraph }) =>
            paragraphListType(
              paragraph,
              current.metadata.numberingDefinitions
            ) === listType
        );

        let next = cloneDocModel(current);
        let changed = false;
        for (const { location } of paragraphsWithLocation) {
          const { paragraph, tableNode } = getParagraphAtLocation(
            next,
            location
          );
          if (!paragraph) {
            continue;
          }

          const currentText = paragraphText(paragraph);
          if (shouldRemove) {
            if (paragraphHasNumbering(paragraph)) {
              const clonedStyle = cloneParagraphStyle(paragraph.style);
              if (clonedStyle?.numbering) {
                clonedStyle.numbering = undefined;
                paragraph.style = clonedStyle;
                paragraph.sourceXml = undefined;
                if (tableNode) {
                  tableNode.sourceXml = undefined;
                }
                changed = true;
              }
              continue;
            }

            const stripped = stripListPrefix(currentText);
            if (stripped !== currentText) {
              next = updateParagraphTextAtLocation(next, location, stripped);
              const updatedParagraph = getParagraphAtLocation(
                next,
                location
              ).paragraph;
              if (updatedParagraph) {
                clearAutoPrefixListIndent(updatedParagraph);
              }
              changed = true;
            }
            continue;
          }

          const nextText = textWithListType(currentText, listType);
          if (nextText === currentText) {
            continue;
          }
          next = updateParagraphTextAtLocation(next, location, nextText);
          const updatedParagraph = getParagraphAtLocation(
            next,
            location
          ).paragraph;
          if (updatedParagraph && !paragraphHasNumbering(updatedParagraph)) {
            ensurePrefixListIndent(updatedParagraph);
          }
          if (
            collapsedTargetLocation &&
            sameParagraphLocation(location, collapsedTargetLocation)
          ) {
            const updatedText = updatedParagraph
              ? paragraphText(updatedParagraph)
              : nextText;
            const previousPrefixLength = listPrefixLength(currentText);
            const nextPrefixLength = listPrefixLength(updatedText);
            const nextOffset = Number.isFinite(collapsedTargetOffset)
              ? Math.max(
                  nextPrefixLength,
                  Math.max(0, Math.round(collapsedTargetOffset as number)) +
                    (nextPrefixLength - previousPrefixLength)
                )
              : nextPrefixLength;
            postToggleRange = {
              start: {
                location: cloneTextRangeLocation(location),
                offset: nextOffset,
              },
              end: {
                location: cloneTextRangeLocation(location),
                offset: nextOffset,
              },
            };
          }
          changed = true;
        }

        if (!changed) {
          return current;
        }

        return next;
      });
      if (postToggleRange) {
        restoreSelectionAfterToolbarCommand(
          selectionFromTextRangeLocation(postToggleRange.start.location),
          postToggleRange
        );
        return;
      }

      restoreSelectionAfterToolbarCommand(selectionSnapshot, textRangeSnapshot);
    },
    [
      applyModelChange,
      restoreSelectionAfterToolbarCommand,
      lastInViewerActiveTextRangeRef,
    ]
  );

  const adjustSelectedListDepth = React.useCallback(
    (levelDelta: number, draftText?: string): boolean => {
      const normalizedLevelDelta = Math.trunc(levelDelta);
      if (!normalizedLevelDelta) {
        return false;
      }

      const targetLocation = resolveSelectedParagraphLocation(
        selection,
        activeTextRangeRef.current
      );
      let changed = false;
      applyModelChange((current) => {
        let next = cloneDocModel(current);
        let { paragraph: paragraphNode, tableNode } = getParagraphAtLocation(
          next,
          targetLocation
        );
        if (!paragraphNode) {
          return current;
        }

        const textFromDraft = draftText ?? paragraphText(paragraphNode);
        if (!paragraphIsList(paragraphNode, textFromDraft)) {
          return current;
        }

        let didMutateText = false;
        if (textFromDraft !== paragraphText(paragraphNode)) {
          next = updateParagraphTextAtLocation(
            next,
            targetLocation,
            textFromDraft,
            {
              insertedStyle: cloneTextStyle(pendingRunStyle),
            }
          );
          ({ paragraph: paragraphNode, tableNode } = getParagraphAtLocation(
            next,
            targetLocation
          ));
          if (!paragraphNode) {
            return current;
          }
          paragraphNode.sourceXml = undefined;
          if (tableNode) {
            tableNode.sourceXml = undefined;
          }
          didMutateText = true;
        }

        const numbering = paragraphNode.style?.numbering;
        if (
          numbering &&
          Number.isFinite(numbering.numId) &&
          numbering.numId > 0
        ) {
          const currentLevel = Math.max(0, Math.round(numbering.ilvl ?? 0));
          const maxLevel = resolveMaxNumberingLevel(
            current.metadata.numberingDefinitions,
            numbering.numId
          );
          const nextLevel = clampNumber(
            currentLevel + normalizedLevelDelta,
            0,
            maxLevel
          );
          if (nextLevel === currentLevel) {
            if (didMutateText) {
              changed = true;
              return next;
            }
            return current;
          }

          paragraphNode.style = {
            ...(cloneParagraphStyle(paragraphNode.style) ?? {}),
            numbering: {
              ...numbering,
              ilvl: nextLevel,
            },
            indent: shiftListIndent(
              paragraphNode.style?.indent,
              nextLevel - currentLevel
            ),
          };
          paragraphNode.sourceXml = undefined;
          if (tableNode) {
            tableNode.sourceXml = undefined;
          }
          changed = true;
          return next;
        }

        const indentation = textFromDraft.match(/^\s*/)?.[0] ?? "";
        const paragraphTextWithoutIndent = textFromDraft.slice(
          indentation.length
        );
        const nextIndentation =
          normalizedLevelDelta > 0
            ? `${indentation}${"  ".repeat(normalizedLevelDelta)}`
            : indentation.slice(
                0,
                Math.max(0, indentation.length + normalizedLevelDelta * 2)
              );
        const nextText = `${nextIndentation}${paragraphTextWithoutIndent}`;
        if (nextText === textFromDraft) {
          return didMutateText ? next : current;
        }

        next = updateParagraphTextAtLocation(next, targetLocation, nextText, {
          insertedStyle: cloneTextStyle(pendingRunStyle),
        });
        ({ paragraph: paragraphNode, tableNode } = getParagraphAtLocation(
          next,
          targetLocation
        ));
        if (!paragraphNode) {
          return current;
        }
        paragraphNode.sourceXml = undefined;
        if (tableNode) {
          tableNode.sourceXml = undefined;
        }
        changed = true;
        return next;
      }, "Updated list level");

      return changed;
    },
    [applyModelChange, pendingRunStyle, selection]
  );

  const insertListItemAfterSelection = React.useCallback(
    (
      draftText: string,
      startOffset: number,
      endOffset?: number,
      targetLocationOverride?: ParagraphLocation
    ):
      | {
          paragraphIndex: number;
          caretOffset: number;
        }
      | undefined => {
      const targetLocation =
        targetLocationOverride ??
        resolveSelectedParagraphLocation(selection, activeTextRangeRef.current);
      if (targetLocation.kind !== "paragraph") {
        return undefined;
      }

      const insertionIndex = targetLocation.nodeIndex + 1;
      let insertedParagraphResult:
        | {
            paragraphIndex: number;
            caretOffset: number;
          }
        | undefined;
      const normalizedDraftText = draftText ?? "";
      const normalizedStartOffset = Math.max(0, Math.round(startOffset));
      const normalizedEndOffset = Math.max(
        normalizedStartOffset,
        Math.round(endOffset ?? normalizedStartOffset)
      );

      applyModelChange((current) => {
        let next = cloneDocModel(current);
        let paragraphNode = next.nodes[targetLocation.nodeIndex];
        if (!paragraphNode || paragraphNode.type !== "paragraph") {
          return current;
        }

        if (!paragraphIsList(paragraphNode, normalizedDraftText)) {
          return current;
        }

        const safeStart = Math.max(
          0,
          Math.min(normalizedStartOffset, normalizedDraftText.length)
        );
        const safeEnd = Math.max(
          safeStart,
          Math.min(normalizedEndOffset, normalizedDraftText.length)
        );
        const beforeText = normalizedDraftText.slice(0, safeStart);
        const afterText = normalizedDraftText.slice(safeEnd);
        const insertRunStyle =
          cloneTextStyle(
            pendingRunStyle ??
              firstTextStyleAtOffset(paragraphNode, safeStart, true) ??
              firstRunStyle(paragraphNode)
          ) ?? {};

        const paragraphListType: DocxListType | undefined = isUnorderedListText(
          normalizedDraftText
        )
          ? "unordered"
          : isOrderedListText(normalizedDraftText)
          ? "ordered"
          : undefined;
        const hasDocxNumbering = paragraphHasNumbering(paragraphNode);
        const textForInsertedParagraph =
          hasDocxNumbering || !paragraphListType
            ? afterText
            : paragraphListType === "ordered"
            ? nextOrderedListItemText(normalizedDraftText, afterText)
            : textWithListType(afterText, paragraphListType);

        const insertedCaretOffset = hasDocxNumbering
          ? 0
          : listPrefixLength(textForInsertedParagraph);

        const textForCurrentParagraph =
          hasDocxNumbering || !paragraphListType
            ? beforeText
            : paragraphListType === "ordered"
            ? (() => {
                const currentMatch = normalizedDraftText.match(
                  ORDERED_LIST_PREFIX_CAPTURE_PATTERN
                );
                if (!currentMatch) {
                  return beforeText;
                }
                return `${currentMatch[1] ?? ""}${
                  currentMatch[2] ?? "1"
                }. ${stripListPrefix(beforeText)}`;
              })()
            : textWithListType(beforeText, paragraphListType);

        next = updateParagraphText(
          next,
          targetLocation.nodeIndex,
          textForCurrentParagraph,
          {
            insertedStyle: cloneTextStyle(pendingRunStyle),
          }
        );
        paragraphNode = next.nodes[targetLocation.nodeIndex];
        if (!paragraphNode || paragraphNode.type !== "paragraph") {
          return current;
        }
        paragraphNode.sourceXml = undefined;

        next.nodes.splice(insertionIndex, 0, {
          type: "paragraph",
          style: cloneParagraphStyle(paragraphNode.style),
          children: [
            {
              type: "text",
              text: textForInsertedParagraph,
              style: insertRunStyle,
            },
          ],
        });
        insertedParagraphResult = {
          paragraphIndex: insertionIndex,
          caretOffset: insertedCaretOffset,
        };
        return next;
      }, "Inserted list item");

      if (insertedParagraphResult === undefined) {
        return undefined;
      }

      suppressSelectionResetRef.current = true;
      setSelection({
        kind: "paragraph",
        nodeIndex: insertedParagraphResult.paragraphIndex,
      });
      setActiveTextRangeState({
        start: {
          location: {
            kind: "paragraph",
            nodeIndex: insertedParagraphResult.paragraphIndex,
          },
          offset: insertedParagraphResult.caretOffset,
        },
        end: {
          location: {
            kind: "paragraph",
            nodeIndex: insertedParagraphResult.paragraphIndex,
          },
          offset: insertedParagraphResult.caretOffset,
        },
      });
      return insertedParagraphResult;
    },
    [applyModelChange, pendingRunStyle, selection]
  );

  const splitParagraphAtSelection = React.useCallback(
    (
      draftText: string,
      startOffset: number,
      endOffset?: number,
      targetLocationOverride?: ParagraphLocation
    ):
      | {
          paragraphIndex: number;
          caretOffset: number;
        }
      | undefined => {
      const targetLocation =
        targetLocationOverride ??
        resolveSelectedParagraphLocation(selection, activeTextRangeRef.current);
      if (targetLocation.kind !== "paragraph") {
        return undefined;
      }

      const normalizedDraftText = draftText ?? "";
      const normalizedStartOffset = Math.max(0, Math.round(startOffset));
      const normalizedEndOffset = Math.max(
        normalizedStartOffset,
        Math.round(endOffset ?? normalizedStartOffset)
      );
      const insertionIndex = targetLocation.nodeIndex + 1;

      let splitResult:
        | {
            paragraphIndex: number;
            caretOffset: number;
          }
        | undefined;

      applyModelChange((current) => {
        const next = cloneDocModel(current);
        const paragraphNode = next.nodes[targetLocation.nodeIndex];
        if (!paragraphNode || paragraphNode.type !== "paragraph") {
          return current;
        }

        const safeStart = Math.max(
          0,
          Math.min(normalizedStartOffset, normalizedDraftText.length)
        );
        const safeEnd = Math.max(
          safeStart,
          Math.min(normalizedEndOffset, normalizedDraftText.length)
        );
        const splitParagraphStyle = splitParagraphStyleWithDefaultSpacing(
          paragraphNode.style,
          paragraphNode.sourceXml
        );
        const beforeInsertedStyle = cloneTextStyle(
          pendingRunStyle ??
            firstTextStyleAtOffset(paragraphNode, safeStart, true) ??
            firstRunStyle(paragraphNode)
        );
        const inheritedRunStyle =
          cloneTextStyle(
            pendingRunStyle ??
              firstTextStyleAtOffset(paragraphNode, safeEnd, false) ??
              firstRunStyle(paragraphNode)
          ) ?? {};
        const splitChildren = splitParagraphChildrenAtTextOffsets(
          paragraphNode,
          normalizedDraftText,
          safeStart,
          safeEnd,
          {
            beforeInsertedStyle,
            afterInsertedStyle: inheritedRunStyle,
          }
        );

        paragraphNode.style = cloneParagraphStyle(splitParagraphStyle);
        paragraphNode.children = splitChildren.beforeChildren;
        paragraphNode.sourceXml = undefined;

        next.nodes.splice(insertionIndex, 0, {
          type: "paragraph",
          style: cloneParagraphStyle(splitParagraphStyle),
          children: splitChildren.afterChildren,
        });

        splitResult = {
          paragraphIndex: insertionIndex,
          caretOffset: 0,
        };

        return next;
      }, "Split paragraph");

      if (!splitResult) {
        return undefined;
      }

      suppressSelectionResetRef.current = true;
      setSelection({
        kind: "paragraph",
        nodeIndex: splitResult.paragraphIndex,
      });
      setActiveTextRangeState({
        start: {
          location: {
            kind: "paragraph",
            nodeIndex: splitResult.paragraphIndex,
          },
          offset: splitResult.caretOffset,
        },
        end: {
          location: {
            kind: "paragraph",
            nodeIndex: splitResult.paragraphIndex,
          },
          offset: splitResult.caretOffset,
        },
      });

      return splitResult;
    },
    [applyModelChange, pendingRunStyle, selection]
  );

  const insertTable = React.useCallback((): void => {
    applyModelChange((current) => {
      const next = cloneDocModel(current);
      const insertionIndex =
        selection.kind === "paragraph"
          ? selection.nodeIndex + 1
          : selection.tableIndex + 1;

      next.nodes.splice(
        Math.max(0, Math.min(insertionIndex, next.nodes.length)),
        0,
        {
          type: "table",
          style: {
            borders: createDefaultEditorTableBorders(),
          },
          rows: [
            {
              type: "table-row",
              style: { backgroundColor: "#e5e7eb" },
              cells: [
                {
                  type: "table-cell",
                  nodes: [
                    {
                      type: "paragraph",
                      children: [
                        {
                          type: "text",
                          text: "Header 1",
                          style: { bold: true },
                        },
                      ],
                    },
                  ],
                },
                {
                  type: "table-cell",
                  nodes: [
                    {
                      type: "paragraph",
                      children: [
                        {
                          type: "text",
                          text: "Header 2",
                          style: { bold: true },
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              type: "table-row",
              cells: [
                {
                  type: "table-cell",
                  nodes: [
                    {
                      type: "paragraph",
                      children: [{ type: "text", text: "Value 1" }],
                    },
                  ],
                },
                {
                  type: "table-cell",
                  nodes: [
                    {
                      type: "paragraph",
                      children: [{ type: "text", text: "Value 2" }],
                    },
                  ],
                },
              ],
            },
          ],
        }
      );

      return next;
    }, "Inserted table");
  }, [applyModelChange, selection]);

  const insertImageFile = React.useCallback(
    async (file: File): Promise<void> => {
      if (!file.type.startsWith("image/")) {
        setStatus("Select an image file");
        return;
      }

      const bytes = new Uint8Array(await file.arrayBuffer());
      const src = `data:${file.type};base64,${toBase64(bytes)}`;

      applyModelChange((current) => {
        const next = cloneDocModel(current);

        if (selection.kind === "paragraph") {
          const paragraph = next.nodes[selection.nodeIndex];
          if (!paragraph || paragraph.type !== "paragraph") {
            return current;
          }

          paragraph.children.push({
            type: "image",
            src,
            alt: file.name,
            contentType: file.type,
            data: new Uint8Array(bytes),
            widthPx: 240,
          });
          paragraph.sourceXml = undefined;
          return next;
        }

        const table = next.nodes[selection.tableIndex];
        if (!table || table.type !== "table") {
          return current;
        }

        const cell = table.rows[selection.rowIndex]?.cells[selection.cellIndex];
        const paragraph = tableCellParagraphs(cell?.nodes ?? [])[0];
        if (!paragraph) {
          return current;
        }

        paragraph.children.push({
          type: "image",
          src,
          alt: file.name,
          contentType: file.type,
          data: new Uint8Array(bytes),
          widthPx: 240,
        });
        paragraph.sourceXml = undefined;
        table.sourceXml = undefined;
        return next;
      }, `Inserted image: ${file.name}`);
    },
    [applyModelChange, selection]
  );

  const appendParagraph = React.useCallback(
    (text = ""): number => {
      const insertedIndex = modelRef.current.nodes.length;

      applyModelChange((current) => {
        const next = cloneDocModel(current);
        next.nodes.push({
          type: "paragraph",
          children: [{ type: "text", text }],
        });
        return next;
      }, "Inserted paragraph");

      setSelection({ kind: "paragraph", nodeIndex: insertedIndex });
      return insertedIndex;
    },
    [applyModelChange]
  );

  const resizeImage = React.useCallback(
    (location: DocxImageLocation, widthPx: number, heightPx: number): void => {
      const safeWidth = Math.max(24, Math.round(widthPx));
      const safeHeight = Math.max(24, Math.round(heightPx));

      applyModelChange((current) => {
        const next = cloneDocModel(current);
        const { paragraph, tableNode } = getParagraphAtLocation(
          next,
          imageLocationToParagraphLocation(location)
        );
        if (!paragraph) {
          return current;
        }

        const child = paragraph.children[location.childIndex];
        if (!child || child.type !== "image") {
          return current;
        }

        if (child.widthPx === safeWidth && child.heightPx === safeHeight) {
          return current;
        }

        child.widthPx = safeWidth;
        child.heightPx = safeHeight;
        paragraph.sourceXml = undefined;
        if (tableNode) {
          tableNode.sourceXml = undefined;
        }

        return next;
      }, "Resized image");
    },
    [applyModelChange]
  );

  const setSyntheticTextBoxText = React.useCallback(
    (location: DocxImageLocation, text: string): void => {
      applyModelChange((current) => {
        const next = cloneDocModel(current);
        const { paragraph, tableNode } = getParagraphAtLocation(
          next,
          imageLocationToParagraphLocation(location)
        );
        if (!paragraph) {
          return current;
        }

        const child = paragraph.children[location.childIndex];
        if (!child || child.type !== "image" || !child.syntheticTextBox) {
          return current;
        }

        const normalizedText = text.replace(/\r\n?/g, "\n");
        if (child.textBoxText === normalizedText) {
          return current;
        }

        child.textBoxText = normalizedText;
        child.src =
          syntheticTextBoxSvg(
            {
              ...child,
              textBoxText: normalizedText,
            },
            undefined,
            undefined,
            undefined,
            undefined
          ) ?? child.src;
        paragraph.sourceXml = undefined;
        if (tableNode) {
          tableNode.sourceXml = undefined;
        }

        return next;
      }, "Edited text box");
    },
    [applyModelChange]
  );

  const setImageWrapMode = React.useCallback(
    (
      location: DocxImageLocation,
      mode: DocxImageWrapMode,
      seedFloating?: Partial<NonNullable<ImageRunNode["floating"]>>
    ): void => {
      applyModelChange((current) => {
        const next = cloneDocModel(current);
        const { paragraph, tableNode } = getParagraphAtLocation(
          next,
          imageLocationToParagraphLocation(location)
        );
        if (!paragraph) {
          return current;
        }

        const child = paragraph.children[location.childIndex];
        if (!child || child.type !== "image") {
          return current;
        }

        const nextFloating = resolveFloatingForImageWrapMode(
          mode,
          child.floating,
          seedFloating
        );
        const currentFloatingJson = JSON.stringify(child.floating ?? null);
        const nextFloatingJson = JSON.stringify(nextFloating ?? null);
        if (
          resolveDocxImageWrapState(child.floating).mode === mode &&
          currentFloatingJson === nextFloatingJson
        ) {
          return current;
        }

        child.floating = nextFloating;
        paragraph.sourceXml = undefined;
        if (tableNode) {
          tableNode.sourceXml = undefined;
        }

        return next;
      }, "Updated image wrap");
    },
    [applyModelChange]
  );

  const moveFloatingImage = React.useCallback(
    (
      location: DocxImageLocation,
      patch: Partial<NonNullable<ImageRunNode["floating"]>>
    ): void => {
      applyModelChange((current) => {
        const next = cloneDocModel(current);
        const { paragraph, tableNode } = getParagraphAtLocation(
          next,
          imageLocationToParagraphLocation(location)
        );
        if (!paragraph) {
          return current;
        }

        const child = paragraph.children[location.childIndex];
        if (!child || child.type !== "image") {
          return current;
        }

        child.floating = {
          ...(child.floating ?? {}),
          ...patch,
        };
        paragraph.sourceXml = undefined;
        if (tableNode) {
          tableNode.sourceXml = undefined;
        }

        return next;
      }, "Moved floating image");
    },
    [applyModelChange]
  );

  const moveSectionFloatingImage = React.useCallback(
    (
      location: DocxSectionImageLocation,
      patch: Partial<NonNullable<ImageRunNode["floating"]>>
    ): void => {
      applyModelChange((current) => {
        return updateSectionImageFloatingAtLocation(current, location, patch);
      }, "Moved floating image");
    },
    [applyModelChange]
  );

  const moveParagraphDropCap = React.useCallback(
    (
      nodeIndex: number,
      patch: Partial<
        NonNullable<NonNullable<ParagraphNode["style"]>["dropCap"]>
      >
    ): void => {
      applyModelChange((current) => {
        const next = cloneDocModel(current);
        const paragraph = next.nodes[nodeIndex];
        if (!paragraph || paragraph.type !== "paragraph") {
          return current;
        }

        const currentDropCap = paragraphDropCap(paragraph);
        if (!currentDropCap) {
          return current;
        }

        paragraph.style = cloneParagraphStyle(paragraph.style) ?? {};
        paragraph.style.dropCap = {
          ...currentDropCap,
          ...patch,
        };
        paragraph.sourceXml = undefined;
        return next;
      }, "Moved drop cap");
    },
    [applyModelChange]
  );

  const setParagraphDropCapFontSizePt = React.useCallback(
    (nodeIndex: number, fontSizePt: number): void => {
      applyModelChange((current) => {
        const next = cloneDocModel(current);
        const paragraph = next.nodes[nodeIndex];
        if (!paragraph || paragraph.type !== "paragraph") {
          return current;
        }

        const dropCap = paragraphDropCap(paragraph);
        if (!dropCap) {
          return current;
        }

        paragraph.style = {
          ...(paragraph.style ?? {}),
          dropCap: {
            ...dropCap,
          },
        };

        const nextFontSizePt = Math.max(1, fontSizePt);
        let updated = false;
        paragraph.children = paragraph.children.map((child) => {
          if (updated || child.type !== "text") {
            return child;
          }
          updated = true;
          return {
            ...child,
            style: {
              ...(child.style ?? {}),
              fontSizePt: nextFontSizePt,
            },
          };
        });

        paragraph.sourceXml = undefined;
        return next;
      }, "Resized drop cap");
    },
    [applyModelChange]
  );

  const setParagraphDropCapText = React.useCallback(
    (nodeIndex: number, text: string): void => {
      applyModelChange((current) => {
        const next = cloneDocModel(current);
        const paragraph = next.nodes[nodeIndex];
        if (!paragraph || paragraph.type !== "paragraph") {
          return current;
        }

        const currentDropCap = paragraphDropCap(paragraph);
        if (!currentDropCap) {
          return current;
        }

        const normalizedText = text
          .replace(/\r?\n/g, "")
          .replace(/\s+/g, " ")
          .trim();
        const existingTextRuns = paragraph.children.filter(
          (child): child is TextRunNode => child.type === "text"
        );
        const preservedStyle =
          cloneTextStyle(existingTextRuns[0]?.style) ??
          cloneTextStyle(firstRunStyle(paragraph));
        const preservedLink = existingTextRuns[0]?.link;

        paragraph.children = [
          {
            type: "text",
            text: normalizedText,
            style: preservedStyle,
            link: preservedLink,
          },
        ];
        paragraph.style = cloneParagraphStyle(paragraph.style) ?? {};
        paragraph.style.dropCap = {
          ...currentDropCap,
        };
        paragraph.sourceXml = undefined;
        return next;
      }, "Updated drop cap text");
    },
    [applyModelChange]
  );

  const moveImage = React.useCallback(
    (source: DocxImageLocation, target: DocxImageDropTarget): void => {
      const sourceParagraphLocation = imageLocationToParagraphLocation(source);
      const targetParagraphLocation: ParagraphLocation =
        target.kind === "paragraph"
          ? {
              kind: "paragraph",
              nodeIndex: target.nodeIndex,
            }
          : {
              kind: "table-cell",
              tableIndex: target.tableIndex,
              rowIndex: target.rowIndex,
              cellIndex: target.cellIndex,
              paragraphIndex: target.paragraphIndex,
            };

      if (
        sameParagraphLocation(
          sourceParagraphLocation,
          targetParagraphLocation
        ) &&
        (target.childIndex === source.childIndex ||
          target.childIndex === source.childIndex + 1)
      ) {
        return;
      }

      applyModelChange((current) => {
        const next = cloneDocModel(current);
        const sourceLookup = getParagraphAtLocation(
          next,
          sourceParagraphLocation
        );
        const targetLookup = getParagraphAtLocation(
          next,
          targetParagraphLocation
        );
        const sourceParagraph = sourceLookup.paragraph;
        const targetParagraph = targetLookup.paragraph;

        if (!sourceParagraph || !targetParagraph) {
          return current;
        }

        const sourceChild = sourceParagraph.children[source.childIndex];
        if (!sourceChild || sourceChild.type !== "image") {
          return current;
        }

        const movedImage = {
          ...sourceChild,
          data: sourceChild.data ? new Uint8Array(sourceChild.data) : undefined,
          floating: sourceChild.floating
            ? { ...sourceChild.floating }
            : undefined,
        };
        sourceParagraph.children.splice(source.childIndex, 1);

        let insertionIndex = Math.max(
          0,
          Math.min(target.childIndex, targetParagraph.children.length)
        );
        if (
          sameParagraphLocation(
            sourceParagraphLocation,
            targetParagraphLocation
          ) &&
          source.childIndex < insertionIndex
        ) {
          insertionIndex -= 1;
        }

        targetParagraph.children.splice(insertionIndex, 0, movedImage);

        if (sourceParagraph.children.length === 0) {
          sourceParagraph.children.push({ type: "text", text: "" });
        }

        sourceParagraph.sourceXml = undefined;
        targetParagraph.sourceXml = undefined;

        if (sourceLookup.tableNode) {
          sourceLookup.tableNode.sourceXml = undefined;
        }
        if (targetLookup.tableNode) {
          targetLookup.tableNode.sourceXml = undefined;
        }

        return next;
      }, "Moved image");
    },
    [applyModelChange]
  );

  const selectParagraph = React.useCallback(
    (nodeIndex: number): void => {
      dispatchEditorTransaction((current) => {
        const nextSelection: DocxEditorSelection = {
          kind: "paragraph",
          nodeIndex,
        };
        if (
          sameEditorSelection(current.selection, nextSelection) &&
          !current.activeTextRange
        ) {
          return undefined;
        }

        return {
          selection: nextSelection,
          activeTextRange: undefined,
          clearSelectedFormField: true,
          pushHistory: false,
        };
      });
    },
    [dispatchEditorTransaction]
  );

  const selectTableCell = React.useCallback(
    (tableIndex: number, rowIndex: number, cellIndex: number): void => {
      dispatchEditorTransaction((current) => {
        const nextSelection: DocxEditorSelection = {
          kind: "table-cell",
          tableIndex,
          rowIndex,
          cellIndex,
        };
        if (
          sameEditorSelection(current.selection, nextSelection) &&
          !current.activeTextRange
        ) {
          return undefined;
        }

        return {
          selection: nextSelection,
          activeTextRange: undefined,
          clearSelectedFormField: true,
          pushHistory: false,
        };
      });
    },
    [dispatchEditorTransaction]
  );

  const clearTableCellContents = React.useCallback(
    (
      tableIndex: number,
      cells: Array<{ rowIndex: number; cellIndex: number }>
    ): void => {
      const dedupedCells = Array.from(
        new Map(
          cells.map((cell) => [`${cell.rowIndex}:${cell.cellIndex}`, cell])
        ).values()
      );
      if (dedupedCells.length === 0) {
        return;
      }

      applyModelChange((current) => {
        const tableNode = current.nodes[tableIndex];
        if (!tableNode || tableNode.type !== "table") {
          return current;
        }

        const next = cloneDocModel(current);
        const table = next.nodes[tableIndex];
        if (!table || table.type !== "table") {
          return current;
        }

        let changed = false;
        const insertedStyle = cloneTextStyle(pendingRunStyle);
        for (const cellLocation of dedupedCells) {
          const cell =
            table.rows[cellLocation.rowIndex]?.cells[cellLocation.cellIndex];
          if (!cell || cell.style?.vMergeContinuation) {
            continue;
          }

          const paragraphs = tableCellParagraphs(cell.nodes);
          const firstParagraph = paragraphs[0];
          const nextParagraph: ParagraphNode = {
            type: "paragraph",
            style: cloneParagraphStyle(firstParagraph?.style),
            children: [
              {
                type: "text",
                text: "",
                style: cloneTextStyle(
                  insertedStyle ?? firstRunStyle(firstParagraph)
                ),
              },
            ],
          };
          const hasContent =
            cell.nodes.length !== 1 ||
            paragraphs.length !== 1 ||
            tableCellText(paragraphs).length > 0 ||
            firstParagraph?.children.some((child) => child.type !== "text");
          if (!hasContent) {
            continue;
          }

          cell.nodes = [nextParagraph];
          changed = true;
        }

        if (!changed) {
          return current;
        }

        table.sourceXml = undefined;
        return next;
      }, "Cleared table cells");
    },
    [applyModelChange, pendingRunStyle]
  );

  const deleteTable = React.useCallback(
    (tableIndex: number): void => {
      const currentModel = modelRef.current;
      const target = currentModel.nodes[tableIndex];
      if (!target || target.type !== "table") {
        return;
      }

      let nextSelection: DocxEditorSelection = {
        kind: "paragraph",
        nodeIndex: 0,
      };
      if (tableIndex + 1 < currentModel.nodes.length) {
        const nextNode = currentModel.nodes[tableIndex + 1];
        if (nextNode?.type === "paragraph") {
          nextSelection = {
            kind: "paragraph",
            nodeIndex: tableIndex,
          };
        } else if (nextNode?.type === "table") {
          const firstLocation = firstParagraphLocationInTable(
            currentModel,
            tableIndex + 1
          );
          if (firstLocation?.kind === "table-cell") {
            nextSelection = {
              kind: "table-cell",
              tableIndex,
              rowIndex: firstLocation.rowIndex,
              cellIndex: firstLocation.cellIndex,
            };
          }
        }
      } else if (tableIndex > 0) {
        const previousNode = currentModel.nodes[tableIndex - 1];
        if (previousNode?.type === "paragraph") {
          nextSelection = {
            kind: "paragraph",
            nodeIndex: tableIndex - 1,
          };
        } else if (previousNode?.type === "table") {
          const firstLocation = firstParagraphLocationInTable(
            currentModel,
            tableIndex - 1
          );
          if (firstLocation?.kind === "table-cell") {
            nextSelection = {
              kind: "table-cell",
              tableIndex: tableIndex - 1,
              rowIndex: firstLocation.rowIndex,
              cellIndex: firstLocation.cellIndex,
            };
          }
        }
      }

      applyModelChange((current) => {
        const tableNode = current.nodes[tableIndex];
        if (!tableNode || tableNode.type !== "table") {
          return current;
        }

        const next = cloneDocModel(current);
        next.nodes.splice(tableIndex, 1);
        if (next.nodes.length === 0) {
          next.nodes.push({
            type: "paragraph",
            children: [{ type: "text", text: "" }],
          });
          nextSelection = { kind: "paragraph", nodeIndex: 0 };
        }

        return next;
      }, "Deleted table");

      suppressSelectionResetRef.current = true;
      setSelection(nextSelection);
      setActiveTextRangeState(undefined);
      setPendingRunStyle(undefined);
    },
    [applyModelChange]
  );

  const moveTable = React.useCallback(
    (tableIndex: number, targetNodeIndex: number): void => {
      let nextSelection: DocxEditorSelection | undefined;

      applyModelChange((current) => {
        const tableNode = current.nodes[tableIndex];
        if (!tableNode || tableNode.type !== "table") {
          return current;
        }

        const clampedTargetNodeIndex = clampNumber(
          targetNodeIndex,
          0,
          current.nodes.length
        );
        if (
          clampedTargetNodeIndex === tableIndex ||
          clampedTargetNodeIndex === tableIndex + 1
        ) {
          return current;
        }

        const next = cloneDocModel(current);
        const extracted = next.nodes.splice(tableIndex, 1)[0];
        if (!extracted || extracted.type !== "table") {
          return current;
        }

        const insertionIndex = clampNumber(
          clampedTargetNodeIndex > tableIndex
            ? clampedTargetNodeIndex - 1
            : clampedTargetNodeIndex,
          0,
          next.nodes.length
        );
        next.nodes.splice(insertionIndex, 0, extracted);

        const firstLocation = firstParagraphLocationInTable(
          next,
          insertionIndex
        );
        if (firstLocation?.kind === "table-cell") {
          nextSelection = {
            kind: "table-cell",
            tableIndex: insertionIndex,
            rowIndex: firstLocation.rowIndex,
            cellIndex: firstLocation.cellIndex,
          };
        } else {
          nextSelection = {
            kind: "paragraph",
            nodeIndex: Math.max(
              0,
              Math.min(insertionIndex, Math.max(0, next.nodes.length - 1))
            ),
          };
        }

        return next;
      }, "Moved table");

      if (nextSelection) {
        suppressSelectionResetRef.current = true;
        setSelection(nextSelection);
        setActiveTextRangeState(undefined);
        setPendingRunStyle(undefined);
      }
    },
    [applyModelChange]
  );

  const moveEmbeddedTableToBody = React.useCallback(
    (tableRuntimeKey: string, targetNodeIndex: number): void => {
      const runtimeLocation = parseEmbeddedTableRuntimeKey(tableRuntimeKey);
      if (!runtimeLocation) {
        return;
      }

      let nextSelection: DocxEditorSelection | undefined;

      applyModelChange((current) => {
        const next = cloneDocModel(current);
        const hostTable = next.nodes[runtimeLocation.hostTableIndex];
        if (!hostTable || hostTable.type !== "table") {
          return current;
        }

        const hostCell =
          hostTable.rows[runtimeLocation.hostRowIndex]?.cells[
            runtimeLocation.hostCellIndex
          ];
        if (!hostCell) {
          return current;
        }

        let parentCellNodes = hostCell.nodes;
        let targetContentIndex = runtimeLocation.rootContentIndex;
        let targetNode = parentCellNodes[targetContentIndex];
        if (!targetNode || targetNode.type !== "table") {
          return current;
        }

        const touchedTables: TableNode[] = [hostTable];
        for (const segment of runtimeLocation.descendants) {
          touchedTables.push(targetNode);
          const nextCell =
            targetNode.rows[segment.rowIndex]?.cells[segment.cellIndex];
          if (!nextCell) {
            return current;
          }

          parentCellNodes = nextCell.nodes;
          targetContentIndex = segment.contentIndex;
          targetNode = parentCellNodes[targetContentIndex];
          if (!targetNode || targetNode.type !== "table") {
            return current;
          }
        }

        const extractedNode = parentCellNodes.splice(targetContentIndex, 1)[0];
        if (!extractedNode || extractedNode.type !== "table") {
          return current;
        }
        const extractedTable = extractedNode;

        if (parentCellNodes.length === 0) {
          parentCellNodes.push(createEmptyParagraphFromTemplate(undefined));
        }

        touchedTables.forEach((table) => {
          table.sourceXml = undefined;
        });
        extractedTable.sourceXml = undefined;

        const insertionIndex = clampNumber(
          targetNodeIndex,
          0,
          next.nodes.length
        );
        next.nodes.splice(insertionIndex, 0, extractedTable);

        const firstLocation = firstParagraphLocationInTable(
          next,
          insertionIndex
        );
        if (firstLocation?.kind === "table-cell") {
          nextSelection = {
            kind: "table-cell",
            tableIndex: insertionIndex,
            rowIndex: firstLocation.rowIndex,
            cellIndex: firstLocation.cellIndex,
          };
        } else {
          nextSelection = {
            kind: "paragraph",
            nodeIndex: Math.max(
              0,
              Math.min(insertionIndex, Math.max(0, next.nodes.length - 1))
            ),
          };
        }

        return next;
      }, "Moved nested table");

      if (nextSelection) {
        suppressSelectionResetRef.current = true;
        setSelection(nextSelection);
        setActiveTextRangeState(undefined);
        setPendingRunStyle(undefined);
      }
    },
    [applyModelChange]
  );

  const insertTableRow = React.useCallback(
    (
      tableIndex: number,
      rowIndex: number,
      direction: "above" | "below"
    ): void => {
      let nextSelection: DocxEditorSelection | undefined;
      const currentSelection = selectionRef.current;

      applyModelChange(
        (current) => {
          const tableNode = current.nodes[tableIndex];
          if (!tableNode || tableNode.type !== "table") {
            return current;
          }

          const next = cloneDocModel(current);
          const table = next.nodes[tableIndex];
          if (!table || table.type !== "table") {
            return current;
          }

          const clampedRowIndex = clampNumber(
            rowIndex,
            0,
            Math.max(0, table.rows.length - 1)
          );
          const templateRow = table.rows[clampedRowIndex] ?? table.rows[0];
          const nextRowStyle = cloneTableRowStyle(templateRow?.style);
          const nextCells = templateRow?.cells.map((templateCell) =>
            createEmptyTableCellFromTemplate(templateCell)
          ) ?? [createEmptyTableCellFromTemplate(undefined)];
          const insertionIndex =
            direction === "above"
              ? clampedRowIndex
              : Math.min(table.rows.length, clampedRowIndex + 1);
          table.rows.splice(insertionIndex, 0, {
            type: "table-row",
            style: nextRowStyle,
            cells: nextCells,
          });
          table.sourceXml = undefined;

          const preferredCellIndex =
            currentSelection.kind === "table-cell" &&
            currentSelection.tableIndex === tableIndex
              ? currentSelection.cellIndex
              : 0;
          const fallbackCellIndex = Math.max(
            0,
            Math.min(nextCells.length - 1, preferredCellIndex)
          );
          nextSelection = {
            kind: "table-cell",
            tableIndex,
            rowIndex: insertionIndex,
            cellIndex: fallbackCellIndex,
          };

          return next;
        },
        direction === "above" ? "Inserted row above" : "Inserted row below"
      );

      if (nextSelection) {
        suppressSelectionResetRef.current = true;
        setSelection(nextSelection);
        setActiveTextRangeState(undefined);
      }
    },
    [applyModelChange]
  );

  const insertTableColumn = React.useCallback(
    (
      tableIndex: number,
      cellIndex: number,
      direction: "left" | "right",
      rowIndex?: number
    ): void => {
      let nextSelection: DocxEditorSelection | undefined;

      applyModelChange(
        (current) => {
          const tableNode = current.nodes[tableIndex];
          if (!tableNode || tableNode.type !== "table") {
            return current;
          }

          const next = cloneDocModel(current);
          const table = next.nodes[tableIndex];
          if (!table || table.type !== "table") {
            return current;
          }

          const anchorRowIndex = clampNumber(
            rowIndex ?? 0,
            0,
            Math.max(0, table.rows.length - 1)
          );
          const anchorRow = table.rows[anchorRowIndex];
          if (!anchorRow) {
            return current;
          }
          const clampedCellIndex = clampNumber(
            cellIndex,
            0,
            Math.max(0, anchorRow.cells.length - 1)
          );
          const insertionIndex =
            direction === "left"
              ? clampedCellIndex
              : Math.min(anchorRow.cells.length, clampedCellIndex + 1);

          table.rows.forEach((row, currentRowIndex) => {
            const referenceIndex = clampNumber(
              clampedCellIndex,
              0,
              Math.max(0, row.cells.length - 1)
            );
            const referenceCell = row.cells[referenceIndex];
            const targetInsertionIndex = Math.min(
              insertionIndex,
              row.cells.length
            );
            row.cells.splice(
              targetInsertionIndex,
              0,
              createEmptyTableCellFromTemplate(referenceCell)
            );

            if (currentRowIndex === anchorRowIndex) {
              nextSelection = {
                kind: "table-cell",
                tableIndex,
                rowIndex: anchorRowIndex,
                cellIndex: targetInsertionIndex,
              };
            }
          });

          if (
            table.style?.columnWidthsTwips &&
            table.style.columnWidthsTwips.length > 0
          ) {
            const widths = [...table.style.columnWidthsTwips];
            const fallbackWidthTwips =
              widths[
                Math.max(0, Math.min(clampedCellIndex, widths.length - 1))
              ] ??
              Math.round(
                (table.style.widthTwips ?? 3600) /
                  Math.max(1, widths.length + 1)
              );
            widths.splice(
              Math.min(insertionIndex, widths.length),
              0,
              fallbackWidthTwips
            );
            table.style = {
              ...(table.style ?? {}),
              columnWidthsTwips: widths,
            };
          }

          table.sourceXml = undefined;
          return next;
        },
        direction === "left" ? "Inserted column left" : "Inserted column right"
      );

      if (nextSelection) {
        suppressSelectionResetRef.current = true;
        setSelection(nextSelection);
        setActiveTextRangeState(undefined);
      }
    },
    [applyModelChange]
  );

  const deleteTableRow = React.useCallback(
    (tableIndex: number, rowIndex: number): void => {
      const tableNode = modelRef.current.nodes[tableIndex];
      if (!tableNode || tableNode.type !== "table") {
        return;
      }
      const currentSelection = selectionRef.current;

      if (tableNode.rows.length <= 1) {
        deleteTable(tableIndex);
        return;
      }

      let nextSelection: DocxEditorSelection | undefined;
      applyModelChange((current) => {
        const currentTableNode = current.nodes[tableIndex];
        if (!currentTableNode || currentTableNode.type !== "table") {
          return current;
        }

        const next = cloneDocModel(current);
        const table = next.nodes[tableIndex];
        if (!table || table.type !== "table") {
          return current;
        }

        const clampedRowIndex = clampNumber(
          rowIndex,
          0,
          Math.max(0, table.rows.length - 1)
        );
        table.rows.splice(clampedRowIndex, 1);
        if (table.rows.length === 0) {
          return current;
        }

        table.sourceXml = undefined;
        const targetRowIndex = Math.max(
          0,
          Math.min(clampedRowIndex, table.rows.length - 1)
        );
        const targetRow = table.rows[targetRowIndex];
        const targetCellIndex = Math.max(
          0,
          Math.min(
            currentSelection.kind === "table-cell" &&
              currentSelection.tableIndex === tableIndex
              ? currentSelection.cellIndex
              : 0,
            Math.max(
              0,
              targetRow?.cells.length ? targetRow.cells.length - 1 : 0
            )
          )
        );
        nextSelection = {
          kind: "table-cell",
          tableIndex,
          rowIndex: targetRowIndex,
          cellIndex: targetCellIndex,
        };

        return next;
      }, "Deleted table row");

      if (nextSelection) {
        suppressSelectionResetRef.current = true;
        setSelection(nextSelection);
        setActiveTextRangeState(undefined);
      }
    },
    [applyModelChange, deleteTable]
  );

  const deleteTableColumn = React.useCallback(
    (tableIndex: number, cellIndex: number, rowIndex?: number): void => {
      const tableNode = modelRef.current.nodes[tableIndex];
      if (!tableNode || tableNode.type !== "table") {
        return;
      }

      const maxColumnCount = Math.max(
        0,
        ...tableNode.rows.map((row) => row.cells.length)
      );
      if (maxColumnCount <= 1) {
        deleteTable(tableIndex);
        return;
      }

      let nextSelection: DocxEditorSelection | undefined;
      applyModelChange((current) => {
        const currentTableNode = current.nodes[tableIndex];
        if (!currentTableNode || currentTableNode.type !== "table") {
          return current;
        }

        const next = cloneDocModel(current);
        const table = next.nodes[tableIndex];
        if (!table || table.type !== "table") {
          return current;
        }

        table.rows.forEach((row) => {
          if (row.cells.length === 0) {
            row.cells.push(createEmptyTableCellFromTemplate(undefined));
            return;
          }

          const clampedCellIndex = clampNumber(
            cellIndex,
            0,
            Math.max(0, row.cells.length - 1)
          );
          row.cells.splice(clampedCellIndex, 1);
          if (row.cells.length === 0) {
            row.cells.push(createEmptyTableCellFromTemplate(undefined));
          }
        });

        if (
          table.style?.columnWidthsTwips &&
          table.style.columnWidthsTwips.length > 0
        ) {
          const widths = [...table.style.columnWidthsTwips];
          const clampedCellIndex = clampNumber(
            cellIndex,
            0,
            Math.max(0, widths.length - 1)
          );
          widths.splice(clampedCellIndex, 1);
          table.style = {
            ...(table.style ?? {}),
            columnWidthsTwips: widths,
          };
        }

        table.sourceXml = undefined;

        const targetRowIndex = clampNumber(
          rowIndex ?? 0,
          0,
          Math.max(0, table.rows.length - 1)
        );
        const targetRow = table.rows[targetRowIndex];
        const targetCellIndex = Math.max(
          0,
          Math.min(
            cellIndex,
            Math.max(
              0,
              targetRow?.cells.length ? targetRow.cells.length - 1 : 0
            )
          )
        );
        nextSelection = {
          kind: "table-cell",
          tableIndex,
          rowIndex: targetRowIndex,
          cellIndex: targetCellIndex,
        };

        return next;
      }, "Deleted table column");

      if (nextSelection) {
        suppressSelectionResetRef.current = true;
        setSelection(nextSelection);
        setActiveTextRangeState(undefined);
      }
    },
    [applyModelChange, deleteTable]
  );

  const commitParagraphText = React.useCallback(
    (nodeIndex: number, text: string): void => {
      applyModelChange((current) => {
        const activeRange = activeTextRangeRef.current
          ? normalizeTextRange(activeTextRangeRef.current)
          : undefined;
        const caretAtParagraph =
          activeRange &&
          compareTextRangeBoundaries(activeRange.start, activeRange.end) ===
            0 &&
          activeRange.start.location.kind === "paragraph" &&
          activeRange.start.location.nodeIndex === nodeIndex
            ? activeRange
            : undefined;
        const paragraph = current.nodes[nodeIndex];
        const insertedStyle =
          cloneTextStyle(pendingRunStyleRef.current) ??
          (paragraph && paragraph.type === "paragraph" && caretAtParagraph
            ? firstTextStyleAtOffset(
                paragraph,
                caretAtParagraph.start.offset,
                false
              )
            : undefined);

        return updateParagraphText(current, nodeIndex, text, {
          insertedStyle,
        });
      });
    },
    [applyModelChange]
  );

  const commitTableCellText = React.useCallback(
    (
      tableIndex: number,
      rowIndex: number,
      cellIndex: number,
      text: string
    ): void => {
      applyModelChange((current) => {
        const activeRange = activeTextRangeRef.current
          ? normalizeTextRange(activeTextRangeRef.current)
          : undefined;
        const caretInCell =
          activeRange &&
          compareTextRangeBoundaries(activeRange.start, activeRange.end) ===
            0 &&
          activeRange.start.location.kind === "table-cell" &&
          activeRange.start.location.tableIndex === tableIndex &&
          activeRange.start.location.rowIndex === rowIndex &&
          activeRange.start.location.cellIndex === cellIndex
            ? activeRange
            : undefined;
        const caretParagraph =
          caretInCell && caretInCell.start.location.kind === "table-cell"
            ? getParagraphAtLocation(current, {
                kind: "table-cell",
                tableIndex,
                rowIndex,
                cellIndex,
                paragraphIndex: caretInCell.start.location.paragraphIndex,
              }).paragraph
            : undefined;
        const insertedStyle =
          cloneTextStyle(pendingRunStyleRef.current) ??
          (caretParagraph
            ? firstTextStyleAtOffset(
                caretParagraph,
                caretInCell?.start.offset ?? 0,
                false
              )
            : undefined);

        return updateTableCellText(
          current,
          tableIndex,
          rowIndex,
          cellIndex,
          text,
          {
            insertedStyle,
          }
        );
      });
    },
    [applyModelChange]
  );

  const commitTableCellParagraphTextRecursive = React.useCallback(
    (
      tableIndex: number,
      rowIndex: number,
      cellIndex: number,
      paragraphIndex: number,
      text: string
    ): void => {
      applyModelChange((current) => {
        const activeRange = activeTextRangeRef.current
          ? normalizeTextRange(activeTextRangeRef.current)
          : undefined;
        const caretAtParagraph =
          activeRange &&
          compareTextRangeBoundaries(activeRange.start, activeRange.end) ===
            0 &&
          activeRange.start.location.kind === "table-cell" &&
          activeRange.start.location.tableIndex === tableIndex &&
          activeRange.start.location.rowIndex === rowIndex &&
          activeRange.start.location.cellIndex === cellIndex &&
          activeRange.start.location.paragraphIndex === paragraphIndex
            ? activeRange
            : undefined;
        const paragraph = getParagraphAtLocation(current, {
          kind: "table-cell",
          tableIndex,
          rowIndex,
          cellIndex,
          paragraphIndex,
        }).paragraph;
        const insertedStyle =
          cloneTextStyle(pendingRunStyleRef.current) ??
          (paragraph && caretAtParagraph
            ? firstTextStyleAtOffset(
                paragraph,
                caretAtParagraph.start.offset,
                false
              )
            : undefined);

        return updateTableCellParagraphTextRecursive(
          current,
          tableIndex,
          rowIndex,
          cellIndex,
          paragraphIndex,
          text,
          {
            insertedStyle,
          }
        );
      });
    },
    [applyModelChange]
  );

  const commitSectionParagraphText = React.useCallback(
    (location: DocxSectionParagraphLocation, text: string): void => {
      applyModelChange(
        (current) =>
          updateSectionParagraphTextAtLocation(current, location, text, {
            insertedStyle: cloneTextStyle(pendingRunStyleRef.current),
          }),
        location.region === "header" ? "Updated header" : "Updated footer"
      );
    },
    [applyModelChange]
  );

  const syncPaginationInfo = React.useCallback(
    (nextPaginationInfo: DocxPaginationInfo): void => {
      setPaginationInfo((current) => {
        const nextTotalPages = Math.max(
          1,
          Math.round(nextPaginationInfo.totalPages || 1)
        );
        const nextCurrentPage = clampNumber(
          Math.round(nextPaginationInfo.currentPage || 1),
          1,
          nextTotalPages
        );

        if (
          current.currentPage === nextCurrentPage &&
          current.totalPages === nextTotalPages
        ) {
          return current;
        }

        return {
          currentPage: nextCurrentPage,
          totalPages: nextTotalPages,
        };
      });
    },
    []
  );

  return {
    model,
    documentLoadNonce,
    fileName,
    status,
    importError,
    isImporting,
    documentTheme,
    trackedChanges,
    showTrackedChanges,
    comments,
    showComments,
    currentPage: paginationInfo.currentPage,
    totalPages: paginationInfo.totalPages,
    selection,
    activeTextRange,
    historyRestoreRequest,
    selectedFormField,
    selectedParagraph,
    selectedRunStyle,
    selectedLink,
    pendingRunStyle,
    selectionSessionKind,
    suppressNextDomSelectionRestore,
    beginSelectionSession,
    clearSelectionSession,
    selectedParagraphStyleId,
    selectedLineSpacing,
    selectedBorderContext,
    activeBorderPresets,
    availableParagraphStyles,
    hasUnorderedList,
    hasOrderedList,
    canUndo,
    canRedo,
    registerPendingExportModelTransformer,
    setStatus,
    setDocumentTheme,
    setShowTrackedChanges,
    setShowComments,
    syncPaginationInfo,
    toggleShowTrackedChanges,
    toggleShowComments,
    importDocxFile,
    newDocument,
    exportDocx,
    undo,
    redo,
    setHeading,
    setParagraphStyle,
    setLineSpacing,
    setFontFamily,
    setFontSize,
    toggleBold,
    toggleItalic,
    toggleUnderline,
    toggleStrike,
    toggleSuperscript,
    toggleSubscript,
    setHighlight,
    setTextColor,
    setLink,
    selectFormField,
    toggleFormCheckbox,
    setFormFieldValue,
    updateFormFieldWidget,
    applyBorderPreset,
    setAlignment,
    toggleList,
    adjustSelectedListDepth,
    insertListItemAfterSelection,
    splitParagraphAtSelection,
    insertTable,
    insertImageFile,
    appendParagraph,
    resizeImage,
    setSyntheticTextBoxText,
    setImageWrapMode,
    moveFloatingImage,
    moveSectionFloatingImage,
    moveParagraphDropCap,
    setParagraphDropCapFontSizePt,
    setParagraphDropCapText,
    moveImage,
    setActiveTextRange,
    selectParagraph,
    selectTableCell,
    clearTableCellContents,
    insertTableRow,
    insertTableColumn,
    deleteTableRow,
    deleteTableColumn,
    deleteTable,
    moveTable,
    moveEmbeddedTableToBody,
    replaceExpandedSelection,
    deleteExpandedSelection,
    commitParagraphText,
    commitTableCellText,
    commitTableCellParagraphTextRecursive,
    commitSectionParagraphText,
  };
}

interface DocxViewerPageSurfaceSize {
  widthPx: number;
  heightPx: number;
}

interface DocxViewerPageThumbnailSnapshotEntry {
  key: string;
  getSnapshot: () => DocxPageThumbnailRenderSnapshot;
}

interface DocxViewerPageSurfaceRegistry {
  pageElements: Map<number, HTMLDivElement>;
  /**
   * Content signature per page, synced by the viewer whenever pagination or
   * page content changes. Thumbnail consumers use it to skip re-rasterizing
   * pages whose rendered content is unchanged.
   */
  pageContentKeys: Map<number, string>;
  /**
   * Layout-derived page sizes are available even when a page is not currently
   * mounted in the main viewport.
   */
  pageSizes: Map<number, DocxViewerPageSurfaceSize>;
  /**
   * Compact layout/model draw snapshots used by the fast thumbnail renderer.
   * These are available for virtualized/offscreen pages without mounting a page
   * surface into a hidden React root.
   */
  pageThumbnailSnapshots: Map<number, DocxViewerPageThumbnailSnapshotEntry>;
  listeners: Set<() => void>;
}

const docxViewerPageSurfaceRegistryByEditor = new WeakMap<
  object,
  DocxViewerPageSurfaceRegistry
>();

function docxViewerPageSurfaceRegistryOwner(
  editor: Pick<DocxEditorController, "syncPaginationInfo">
): object {
  return editor.syncPaginationInfo as object;
}

function ensureDocxViewerPageSurfaceRegistry(
  editor: Pick<DocxEditorController, "syncPaginationInfo">
): DocxViewerPageSurfaceRegistry {
  const owner = docxViewerPageSurfaceRegistryOwner(editor);
  let registry = docxViewerPageSurfaceRegistryByEditor.get(owner);
  if (!registry) {
    registry = {
      pageElements: new Map<number, HTMLDivElement>(),
      pageContentKeys: new Map<number, string>(),
      pageSizes: new Map<number, DocxViewerPageSurfaceSize>(),
      pageThumbnailSnapshots: new Map<
        number,
        DocxViewerPageThumbnailSnapshotEntry
      >(),
      listeners: new Set<() => void>(),
    };
    docxViewerPageSurfaceRegistryByEditor.set(owner, registry);
  }
  return registry;
}

function syncDocxViewerPageSurfaceContentKeys(
  editor: Pick<DocxEditorController, "syncPaginationInfo">,
  contentKeysByPage: ReadonlyArray<string>,
  pageSizesByPage: ReadonlyArray<DocxViewerPageSurfaceSize> = [],
  thumbnailSnapshotsByPage: ReadonlyArray<
    DocxViewerPageThumbnailSnapshotEntry | undefined
  > = []
): void {
  const registry = ensureDocxViewerPageSurfaceRegistry(editor);
  let changed = false;
  contentKeysByPage.forEach((contentKey, pageIndex) => {
    if (registry.pageContentKeys.get(pageIndex) !== contentKey) {
      registry.pageContentKeys.set(pageIndex, contentKey);
      changed = true;
    }
  });
  pageSizesByPage.forEach((pageSize, pageIndex) => {
    const widthPx = Math.max(1, Math.round(pageSize.widthPx));
    const heightPx = Math.max(1, Math.round(pageSize.heightPx));
    const previous = registry.pageSizes.get(pageIndex);
    if (previous?.widthPx !== widthPx || previous?.heightPx !== heightPx) {
      registry.pageSizes.set(pageIndex, { widthPx, heightPx });
      changed = true;
    }
  });
  thumbnailSnapshotsByPage.forEach((snapshot, pageIndex) => {
    const previous = registry.pageThumbnailSnapshots.get(pageIndex);
    if (!snapshot) {
      if (previous) {
        registry.pageThumbnailSnapshots.delete(pageIndex);
        changed = true;
      }
      return;
    }
    if (previous?.key !== snapshot.key) {
      registry.pageThumbnailSnapshots.set(pageIndex, snapshot);
      changed = true;
    }
  });
  registry.pageContentKeys.forEach((_, pageIndex) => {
    if (pageIndex >= contentKeysByPage.length) {
      registry.pageContentKeys.delete(pageIndex);
      changed = true;
    }
  });
  registry.pageSizes.forEach((_, pageIndex) => {
    if (pageIndex >= pageSizesByPage.length) {
      registry.pageSizes.delete(pageIndex);
      changed = true;
    }
  });
  registry.pageThumbnailSnapshots.forEach((_, pageIndex) => {
    if (pageIndex >= thumbnailSnapshotsByPage.length) {
      registry.pageThumbnailSnapshots.delete(pageIndex);
      changed = true;
    }
  });

  if (changed) {
    notifyDocxViewerPageSurfaceSubscribers(registry);
  }
}

function subscribeDocxViewerPageSurfaces(
  editor: Pick<DocxEditorController, "syncPaginationInfo">,
  listener: () => void
): () => void {
  const registry = ensureDocxViewerPageSurfaceRegistry(editor);
  registry.listeners.add(listener);
  return () => {
    registry.listeners.delete(listener);
  };
}

function notifyDocxViewerPageSurfaceSubscribers(
  registry: DocxViewerPageSurfaceRegistry
): void {
  registry.listeners.forEach((listener) => {
    listener();
  });
}

function registerDocxViewerPageSurface(
  editor: Pick<DocxEditorController, "syncPaginationInfo">,
  pageIndex: number,
  element?: HTMLDivElement | null,
  previousElement?: HTMLDivElement | null
): void {
  const registry = ensureDocxViewerPageSurfaceRegistry(editor);
  const normalizedPageIndex = Math.max(0, Math.round(pageIndex));
  const currentElement = registry.pageElements.get(normalizedPageIndex);

  if (element) {
    if (currentElement === element) {
      return;
    }
    registry.pageElements.set(normalizedPageIndex, element);
    notifyDocxViewerPageSurfaceSubscribers(registry);
    return;
  }

  if (!currentElement) {
    return;
  }
  if (previousElement && currentElement !== previousElement) {
    return;
  }

  registry.pageElements.delete(normalizedPageIndex);
  notifyDocxViewerPageSurfaceSubscribers(registry);
}

function resolveDocxViewerRegisteredPageSurfaceSize(
  registry: DocxViewerPageSurfaceRegistry,
  pageIndex: number,
  fallbackWidthPx: number,
  fallbackHeightPx: number
): DocxViewerPageSurfaceSize {
  const registeredSize = registry.pageSizes.get(pageIndex);
  if (registeredSize) {
    return registeredSize;
  }

  return {
    widthPx: Math.max(1, Math.round(fallbackWidthPx)),
    heightPx: Math.max(1, Math.round(fallbackHeightPx)),
  };
}

function resolveDocxViewerPageSurfaceSize(
  element: HTMLElement | undefined,
  fallbackWidthPx: number,
  fallbackHeightPx: number
): {
  widthPx: number;
  heightPx: number;
} {
  if (element) {
    const rect = element.getBoundingClientRect();
    const rectWidthPx = Number.isFinite(rect.width) ? rect.width : 0;
    const rectHeightPx = Number.isFinite(rect.height) ? rect.height : 0;
    const offsetWidthPx = Number.isFinite(element.offsetWidth)
      ? element.offsetWidth
      : 0;
    const offsetHeightPx = Number.isFinite(element.offsetHeight)
      ? element.offsetHeight
      : 0;
    const clientWidthPx = Number.isFinite(element.clientWidth)
      ? element.clientWidth
      : 0;
    const clientHeightPx = Number.isFinite(element.clientHeight)
      ? element.clientHeight
      : 0;
    const resolvedWidthPx = Math.max(
      0,
      rectWidthPx,
      offsetWidthPx,
      clientWidthPx
    );
    const resolvedHeightPx = Math.max(
      0,
      rectHeightPx,
      offsetHeightPx,
      clientHeightPx
    );

    if (resolvedWidthPx > 0 && resolvedHeightPx > 0) {
      return {
        widthPx: Math.round(resolvedWidthPx),
        heightPx: Math.round(resolvedHeightPx),
      };
    }
  }

  return {
    widthPx: Math.max(1, Math.round(fallbackWidthPx)),
    heightPx: Math.max(1, Math.round(fallbackHeightPx)),
  };
}

const DOCX_DIRECT_THUMBNAIL_MAX_ELEMENTS_PER_PAGE = 260;
const DOCX_DIRECT_THUMBNAIL_MAX_TEXT_RUNS = 28;
const DOCX_DIRECT_THUMBNAIL_MAX_TEXT_CHARS = 900;
const DOCX_DIRECT_THUMBNAIL_MAX_TABLE_CELLS = 220;
const DOCX_DIRECT_THUMBNAIL_MAX_CELL_TEXT_CHARS = 120;

function docxThumbnailCssNumber(value: React.CSSProperties[keyof React.CSSProperties]): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function normalizeDocxThumbnailColor(value?: string): string | undefined {
  const normalized = value?.trim();
  if (!normalized || normalized.toLowerCase() === "auto") {
    return undefined;
  }
  if (/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return `#${normalized}`;
  }
  return normalized;
}

function docxThumbnailTextRunStylesMatch(
  left: DocxPageThumbnailTextRunSnapshot,
  right: DocxPageThumbnailTextRunSnapshot
): boolean {
  return (
    left.bold === right.bold &&
    left.italic === right.italic &&
    left.color === right.color &&
    left.backgroundColor === right.backgroundColor &&
    left.fontSizePx === right.fontSizePx &&
    left.fontFamily === right.fontFamily
  );
}

function appendDocxThumbnailTextRun(
  runs: DocxPageThumbnailTextRunSnapshot[],
  run: DocxPageThumbnailTextRunSnapshot,
  remaining: { chars: number }
): void {
  if (remaining.chars <= 0 || runs.length >= DOCX_DIRECT_THUMBNAIL_MAX_TEXT_RUNS) {
    return;
  }

  const text = run.text.slice(0, remaining.chars);
  if (!text) {
    return;
  }
  remaining.chars -= text.length;

  const nextRun = { ...run, text };
  const previous = runs[runs.length - 1];
  if (previous && docxThumbnailTextRunStylesMatch(previous, nextRun)) {
    previous.text += nextRun.text;
    return;
  }

  runs.push(nextRun);
}

function docxThumbnailFallbackHeadingRunStyle(
  paragraph: ParagraphNode
): TextRunNode["style"] | undefined {
  const headingLevel = paragraph.style?.headingLevel;
  return headingLevel && headingLevel >= 1 && headingLevel <= 6
    ? DEFAULT_WORD_HEADING_RUN_STYLES[headingLevel]
    : undefined;
}

function docxThumbnailTextRunsFromParagraph(
  paragraph: ParagraphNode,
  documentTheme: DocxDocumentTheme,
  maxChars = DOCX_DIRECT_THUMBNAIL_MAX_TEXT_CHARS
): DocxPageThumbnailTextRunSnapshot[] {
  const runs: DocxPageThumbnailTextRunSnapshot[] = [];
  const remaining = { chars: maxChars };
  const fallbackHeadingStyle = docxThumbnailFallbackHeadingRunStyle(paragraph);
  const fallbackFontFamily =
    cssFontFamily(fallbackHeadingStyle?.fontFamily) ??
    cssFontFamily(paragraphDominantFontFamily(paragraph));

  paragraph.children.forEach((child) => {
    if (remaining.chars <= 0) {
      return;
    }

    const style =
      child.type === "text" || child.type === "form-field"
        ? child.style
        : undefined;
    // Images contribute no text: their alt/name would otherwise be painted as
    // a caption and (worse) count as "visible text", suppressing the image
    // element itself. They are rendered as image elements instead.
    const text =
      child.type === "text"
        ? child.text
        : child.type === "form-field"
        ? formFieldDisplayValue(child)
        : "";
    appendDocxThumbnailTextRun(
      runs,
      {
        text,
        bold: style?.bold ?? fallbackHeadingStyle?.bold,
        italic: style?.italic ?? fallbackHeadingStyle?.italic,
        color: normalizeDocxThumbnailColor(
          themedRunColor(style?.color ?? fallbackHeadingStyle?.color, documentTheme)
        ),
        backgroundColor: normalizeDocxThumbnailColor(
          style?.backgroundColor ?? resolveHighlightColor(style?.highlight)
        ),
        fontSizePx:
          style?.fontSizePt && style.fontSizePt > 0
            ? Math.max(6, (style.fontSizePt * 96) / 72)
            : fallbackHeadingStyle?.fontSizePt
            ? Math.max(6, (fallbackHeadingStyle.fontSizePt * 96) / 72)
            : paragraphBaseFontSizePx(paragraph),
        fontFamily: cssFontFamily(style?.fontFamily) ?? fallbackFontFamily,
      },
      remaining
    );
  });

  return runs;
}

function docxThumbnailCellTextRuns(
  cell: TableNode["rows"][number]["cells"][number],
  documentTheme: DocxDocumentTheme
): DocxPageThumbnailTextRunSnapshot[] | undefined {
  const paragraph = tableCellParagraphsRecursively(cell.nodes).find(
    (candidate) => paragraphText(candidate).trim().length > 0
  );
  if (!paragraph) {
    return undefined;
  }
  const runs = docxThumbnailTextRunsFromParagraph(
    paragraph,
    documentTheme,
    DOCX_DIRECT_THUMBNAIL_MAX_CELL_TEXT_CHARS
  );
  return runs.length > 0 ? runs : undefined;
}

function buildDocxThumbnailParagraphElements(params: {
  paragraph: ParagraphNode;
  segment: DocumentPageNodeSegment;
  contentLeftPx: number;
  contentTopPx: number;
  contentWidthPx: number;
  yPx: number;
  heightPx: number;
  numberingDefinitions?: NumberingDefinitionSet;
  docGridLinePitchPx?: number;
  documentTheme: DocxDocumentTheme;
}): DocxPageThumbnailSnapshotElement[] {
  const {
    paragraph,
    segment,
    contentLeftPx,
    contentWidthPx,
    yPx,
    heightPx,
    numberingDefinitions,
    docGridLinePitchPx,
    documentTheme,
  } = params;
  const blockStyle = paragraphBlockStyle(
    paragraph,
    numberingDefinitions,
    undefined,
    docGridLinePitchPx
  );
  const paragraphLineRange = segment.paragraphLineRange;
  const marginTopPx =
    paragraphLineRange && paragraphLineRange.startLineIndex > 0
      ? 0
      : Math.max(0, docxThumbnailCssNumber(blockStyle.marginTop));
  const marginBottomPx =
    paragraphLineRange &&
    paragraphLineRange.endLineIndex < paragraphLineRange.totalLineCount
      ? 0
      : Math.max(0, docxThumbnailCssNumber(blockStyle.marginBottom));
  const marginLeftPx = docxThumbnailCssNumber(blockStyle.marginLeft);
  const marginRightPx = Math.max(0, docxThumbnailCssNumber(blockStyle.marginRight));
  const xPx = contentLeftPx + marginLeftPx;
  const widthPx = Math.max(8, contentWidthPx - marginLeftPx - marginRightPx);
  const bodyYPx = yPx + marginTopPx;
  const bodyHeightPx = Math.max(1, heightPx - marginTopPx - marginBottomPx);
  const runs = docxThumbnailTextRunsFromParagraph(paragraph, documentTheme);
  const elements: DocxPageThumbnailSnapshotElement[] = [];

  elements.push({
    kind: "paragraph",
    xPx,
    yPx: bodyYPx,
    widthPx,
    heightPx: bodyHeightPx,
    align: paragraph.style?.align,
    backgroundColor: normalizeDocxThumbnailColor(
      paragraph.style?.backgroundColor
    ),
    lineHeightPx:
      paragraphLineRange?.lineHeightPx ??
      estimateParagraphLineHeightPx(paragraph, docGridLinePitchPx),
    startLineIndex: paragraphLineRange?.startLineIndex,
    runs,
  });

  const hasVisibleText = runs.some((run) => run.text.trim().length > 0);
  if (!hasVisibleText) {
    const imageRuns = paragraph.children.filter(
      (child): child is ImageRunNode => child.type === "image"
    );
    imageRuns.slice(0, 4).forEach((imageRun) => {
      const imageWidthPx = Math.max(18, imageRun.widthPx ?? widthPx * 0.5);
      const imageHeightPx = Math.max(18, imageRun.heightPx ?? bodyHeightPx * 0.5);
      const scale = Math.min(
        1,
        (widthPx - 4) / imageWidthPx,
        (bodyHeightPx - 4) / imageHeightPx
      );
      const elementXPx = xPx + 2;
      const elementYPx = bodyYPx + 2;
      const elementWidthPx = Math.max(12, imageWidthPx * scale);
      const elementHeightPx = Math.max(12, imageHeightPx * scale);
      // Paint the real bitmap when one is renderable (the live page does the
      // same via resolveRenderableImageSource); metafiles with no raster
      // fall back to the placeholder box the page shows a badge for.
      const renderableSrc = resolveRenderableImageSource(imageRun);
      if (renderableSrc) {
        elements.push({
          kind: "image",
          xPx: elementXPx,
          yPx: elementYPx,
          widthPx: elementWidthPx,
          heightPx: elementHeightPx,
          src: renderableSrc,
        });
      } else {
        elements.push({
          kind: "image-placeholder",
          xPx: elementXPx,
          yPx: elementYPx,
          widthPx: elementWidthPx,
          heightPx: elementHeightPx,
        });
      }
    });
  }

  return elements;
}

function buildDocxThumbnailTableElement(params: {
  table: TableNode;
  segment: DocumentPageNodeSegment;
  contentLeftPx: number;
  contentWidthPx: number;
  contentHeightPx: number;
  yPx: number;
  heightPx: number;
  numberingDefinitions?: NumberingDefinitionSet;
  docGridLinePitchPx?: number;
  documentTheme: DocxDocumentTheme;
}): DocxPageThumbnailSnapshotElement | undefined {
  const {
    table,
    segment,
    contentLeftPx,
    contentWidthPx,
    contentHeightPx,
    yPx,
    heightPx,
    numberingDefinitions,
    docGridLinePitchPx,
    documentTheme,
  } = params;
  const columnCount = tableColumnCount(table);
  const tableIndentPx = twipsToSignedPixels(table.style?.indentTwips) ?? 0;
  const tableWidthPx = twipsToPixels(table.style?.widthTwips);
  const definedWidthsTwips = columnWidthsFromTableDefinition(table, columnCount);
  const rawTableColumnWidthsPx =
    definedWidthsTwips && definedWidthsTwips.length > 0
      ? normalizeColumnWidthsPx(
          definedWidthsTwips.map((widthTwips) => twipsToPixels(widthTwips) ?? 0),
          columnCount,
          tableWidthPx,
          1
        )
      : defaultColumnWidthsPx(columnCount, tableWidthPx);
  const rawResolvedTableWidthPx =
    tableWidthPx ??
    rawTableColumnWidthsPx.reduce((sum, widthPx) => sum + widthPx, 0);
  const maxTableWidthPx = Math.max(
    24,
    contentWidthPx -
      tableIndentPx -
      resolveCollapsedTableHorizontalOuterBleedPx(table, columnCount)
  );
  const resolvedTableWidthPx = clampTableWidthPx(
    rawResolvedTableWidthPx,
    maxTableWidthPx
  );
  const { columnWidthsPx } = resolveFittedTableColumnWidths(
    table,
    rawTableColumnWidthsPx,
    resolvedTableWidthPx
  );
  const rowHeightsPx = estimateTableRowHeightsPx(
    table,
    contentWidthPx,
    numberingDefinitions,
    docGridLinePitchPx,
    contentHeightPx
  );
  const startRowIndex = Math.max(
    0,
    segment.tableRowSlice?.rowIndex ?? segment.tableRowRange?.startRowIndex ?? 0
  );
  const endRowIndex = Math.min(
    table.rows.length,
    segment.tableRowSlice
      ? startRowIndex + 1
      : segment.tableRowRange?.endRowIndex ?? table.rows.length
  );
  const cells: DocxPageThumbnailTableCellSnapshot[] = [];
  let rowYPx = segment.tableRowSlice
    ? -Math.max(0, segment.tableRowSlice.startOffsetPx)
    : 0;

  for (
    let rowIndex = startRowIndex;
    rowIndex < endRowIndex && cells.length < DOCX_DIRECT_THUMBNAIL_MAX_TABLE_CELLS;
    rowIndex += 1
  ) {
    const row = table.rows[rowIndex];
    if (!row) {
      continue;
    }
    const rowHeightPx =
      segment.tableRowSlice && rowIndex === segment.tableRowSlice.rowIndex
        ? Math.max(1, segment.tableRowSlice.totalRowHeightPx)
        : Math.max(1, rowHeightsPx[rowIndex] ?? MIN_PARAGRAPH_LINE_HEIGHT_PX);
    let columnCursor = 0;
    row.cells.forEach((cell) => {
      if (cells.length >= DOCX_DIRECT_THUMBNAIL_MAX_TABLE_CELLS) {
        return;
      }
      const span =
        cell.style?.gridSpan && cell.style.gridSpan > 1 ? cell.style.gridSpan : 1;
      const xPx = columnWidthsPx
        .slice(0, columnCursor)
        .reduce((sum, widthPx) => sum + widthPx, 0);
      const widthPx = columnWidthsPx
        .slice(columnCursor, columnCursor + span)
        .reduce((sum, widthPx) => sum + widthPx, 0);
      columnCursor += span;
      cells.push({
        xPx,
        yPx: rowYPx,
        widthPx: Math.max(1, widthPx),
        heightPx: rowHeightPx,
        backgroundColor: normalizeDocxThumbnailColor(
          cell.style?.backgroundColor ?? row.style?.backgroundColor
        ),
        runs: docxThumbnailCellTextRuns(cell, documentTheme),
      });
    });
    rowYPx += rowHeightPx;
  }

  if (cells.length === 0) {
    return undefined;
  }

  return {
    kind: "table",
    xPx: contentLeftPx + tableIndentPx,
    yPx,
    widthPx: Math.max(1, resolvedTableWidthPx),
    heightPx: Math.max(1, heightPx),
    cells,
  };
}

function docxThumbnailNodeRequiresDomRaster(
  node: DocModel["nodes"][number] | TableCellContentNode | undefined
): boolean {
  if (!node) {
    return false;
  }

  if (node.type === "paragraph") {
    return node.children.some((child) => child.type === "image");
  }

  return node.rows.some((row) =>
    row.cells.some((cell) =>
      cell.nodes.some((cellNode) => docxThumbnailNodeRequiresDomRaster(cellNode))
    )
  );
}

function docxThumbnailPageRequiresDomRaster(
  model: DocModel,
  pageSegments: ReadonlyArray<DocumentPageNodeSegment>
): boolean {
  return pageSegments.some((segment) =>
    docxThumbnailNodeRequiresDomRaster(model.nodes[segment.nodeIndex])
  );
}

function buildDocxPageThumbnailRenderSnapshotEntries(params: {
  model: DocModel;
  pageNodeSegmentsByPage: ReadonlyArray<ReadonlyArray<DocumentPageNodeSegment>>;
  pageSectionInfoByIndex: ReadonlyArray<{ layout: DocumentLayoutMetrics }>;
  contentKeysByPage: ReadonlyArray<string>;
  fallbackLayout: DocumentLayoutMetrics;
  documentTheme: DocxDocumentTheme;
  docGridLinePitchPxByNodeIndex: Map<number, number | undefined>;
  numberingDefinitions?: NumberingDefinitionSet;
}): Array<DocxViewerPageThumbnailSnapshotEntry | undefined> {
  const {
    model,
    pageNodeSegmentsByPage,
    pageSectionInfoByIndex,
    contentKeysByPage,
    fallbackLayout,
    documentTheme,
    docGridLinePitchPxByNodeIndex,
    numberingDefinitions,
  } = params;

  return pageNodeSegmentsByPage.map((pageSegments, pageIndex) => {
    if (docxThumbnailPageRequiresDomRaster(model, pageSegments)) {
      return undefined;
    }

    const key = `${contentKeysByPage[pageIndex] ?? ""}|theme:${documentTheme}`;
    let cachedSnapshot: DocxPageThumbnailRenderSnapshot | undefined;

    return {
      key,
      getSnapshot: () => {
        if (cachedSnapshot) {
          return cachedSnapshot;
        }

        const pageLayout =
          pageSectionInfoByIndex[pageIndex]?.layout ?? fallbackLayout;
        const contentLeftPx = pageLayout.marginsPx.left;
        const contentTopPx = pageLayout.marginsPx.top;
        const contentWidthPx = Math.max(
          1,
          pageLayout.pageWidthPx -
            pageLayout.marginsPx.left -
            pageLayout.marginsPx.right
        );
        const contentHeightPx = Math.max(
          1,
          pageLayout.pageHeightPx -
            pageLayout.marginsPx.top -
            pageLayout.marginsPx.bottom
        );
        const elements: DocxPageThumbnailSnapshotElement[] = [];
        let yPx = contentTopPx;

        for (const segment of pageSegments) {
          if (elements.length >= DOCX_DIRECT_THUMBNAIL_MAX_ELEMENTS_PER_PAGE) {
            break;
          }

          const node = model.nodes[segment.nodeIndex];
          if (!node) {
            continue;
          }
          const docGridLinePitchPx = docGridLinePitchPxByNodeIndex.get(
            segment.nodeIndex
          );
          const segmentHeightPx = estimateRenderedPageSegmentHeightPx(
            node,
            segment,
            model,
            contentWidthPx,
            numberingDefinitions,
            docGridLinePitchPx
          );

          if (node.type === "paragraph" && !segment.tableRowRange) {
            elements.push(
              ...buildDocxThumbnailParagraphElements({
                paragraph: node,
                segment,
                contentLeftPx,
                contentTopPx,
                contentWidthPx,
                yPx,
                heightPx: segmentHeightPx,
                numberingDefinitions,
                docGridLinePitchPx,
                documentTheme,
              })
            );
          } else if (node.type === "table") {
            const tableElement = buildDocxThumbnailTableElement({
              table: node,
              segment,
              contentLeftPx,
              contentWidthPx,
              contentHeightPx,
              yPx,
              heightPx: segmentHeightPx,
              numberingDefinitions,
              docGridLinePitchPx,
              documentTheme,
            });
            if (tableElement) {
              elements.push(tableElement);
            }
          }

          yPx += Math.max(1, segmentHeightPx);
        }

        cachedSnapshot = {
          key,
          sourceWidthPx: pageLayout.pageWidthPx,
          sourceHeightPx: pageLayout.pageHeightPx,
          pageBackgroundColor: documentTheme === "dark" ? "#111827" : "#ffffff",
          elements,
        };
        return cachedSnapshot;
      },
    };
  });
}

interface DocxDetachedThumbnailSurfaceRendererRoot {
  render(children: React.ReactNode): void;
  unmount(): void;
}

function DocxDetachedThumbnailPageSurface({
  editor,
  pageIndex,
}: {
  editor: DocxEditorController;
  pageIndex: number;
}): React.JSX.Element {
  return (
    <DocxEditorViewer
      editor={editor}
      mode="read-only"
      visiblePageRange={{ startPageIndex: pageIndex, endPageIndex: pageIndex }}
      pageVirtualization={{ enabled: false }}
      showTrackedChanges={editor.showTrackedChanges}
      showComments={editor.showComments}
      style={{
        background: "transparent",
        padding: 0,
        margin: 0,
      }}
    />
  );
}

class DocxDetachedThumbnailSurfaceRenderer {
  private host: HTMLDivElement | undefined;
  private root: DocxDetachedThumbnailSurfaceRendererRoot | undefined;
  private activePageIndex: number | undefined;
  private activeRenderKey: string | undefined;

  async renderPageSurface(params: {
    editor: DocxEditorController;
    registry: DocxViewerPageSurfaceRegistry;
    pageIndex: number;
    renderKey: string;
  }): Promise<HTMLDivElement | undefined> {
    if (typeof document === "undefined" || typeof window === "undefined") {
      return undefined;
    }

    const { editor, registry, pageIndex, renderKey } = params;
    await this.ensureRoot();
    if (!this.root) {
      return undefined;
    }

    if (
      this.activePageIndex !== pageIndex ||
      this.activeRenderKey !== renderKey
    ) {
      this.activePageIndex = pageIndex;
      this.activeRenderKey = renderKey;
      this.root.render(
        <DocxDetachedThumbnailPageSurface
          editor={editor}
          pageIndex={pageIndex}
        />
      );
    }

    return this.waitForPageSurface(registry, pageIndex);
  }

  clear(): void {
    if (this.root) {
      this.root.unmount();
      this.root = undefined;
    }
    if (this.host?.parentNode) {
      this.host.parentNode.removeChild(this.host);
    }
    this.host = undefined;
    this.activePageIndex = undefined;
    this.activeRenderKey = undefined;
  }

  private async ensureRoot(): Promise<void> {
    if (this.root) {
      return;
    }
    if (typeof document === "undefined") {
      return;
    }

    const host = document.createElement("div");
    host.setAttribute("data-docx-thumbnail-detached-renderer", "true");
    Object.assign(host.style, {
      position: "fixed",
      left: "-100000px",
      top: "0",
      width: "1px",
      height: "1px",
      overflow: "visible",
      opacity: "0",
      pointerEvents: "none",
      zIndex: "-1",
    });
    document.body.appendChild(host);
    this.host = host;

    const { createRoot } = await import("react-dom/client");
    this.root = createRoot(host);
  }

  private async waitForPageSurface(
    registry: DocxViewerPageSurfaceRegistry,
    pageIndex: number
  ): Promise<HTMLDivElement | undefined> {
    for (let attempt = 0; attempt < 8; attempt += 1) {
      const pageElement = registry.pageElements.get(pageIndex);
      if (pageElement?.isConnected) {
        return pageElement;
      }
      await new Promise<void>((resolve) => {
        window.requestAnimationFrame(() => resolve());
      });
    }

    const pageElement = registry.pageElements.get(pageIndex);
    return pageElement?.isConnected ? pageElement : undefined;
  }
}

// Sized for a virtualized rail: comfortably larger than the live
// visible+overscan+prefetch window so scrolling back over recently-seen pages
// hits the cache (an instant blit) instead of re-rasterizing. Each entry is a
// thumbnail-scale canvas (~0.1-0.2 MB), so 64 stays well under a few MB.
const DOCX_THUMBNAIL_SURFACE_CACHE_MAX_ENTRIES = 64;
const DOCX_THUMBNAIL_MIN_RASTER_INTERVAL_MS = 200;
const DOCX_THUMBNAIL_RENDER_PRIORITY_VISIBLE = 0;
const DOCX_THUMBNAIL_RENDER_PRIORITY_ATTACHED = 1;
const DOCX_THUMBNAIL_RENDER_PRIORITY_PREFETCH = 2;

function normalizeDocxThumbnailMinRasterIntervalMs(value: unknown): number {
  return Number.isFinite(value) && Number(value) >= 0
    ? Number(value)
    : DOCX_THUMBNAIL_MIN_RASTER_INTERVAL_MS;
}

function normalizeDocxThumbnailPageIndexes(
  indexes: readonly number[] | undefined,
  totalPages: number
): number[] {
  if (!indexes?.length || totalPages <= 0) {
    return [];
  }

  const seen = new Set<number>();
  const normalized: number[] = [];
  indexes.forEach((pageIndex) => {
    if (!Number.isFinite(pageIndex)) {
      return;
    }
    const roundedPageIndex = Math.trunc(pageIndex);
    if (
      roundedPageIndex < 0 ||
      roundedPageIndex >= totalPages ||
      seen.has(roundedPageIndex)
    ) {
      return;
    }
    seen.add(roundedPageIndex);
    normalized.push(roundedPageIndex);
  });
  return normalized;
}

function docxThumbnailPageIndexesKey(pageIndexes: readonly number[]): string {
  return pageIndexes.join(",");
}

function docxThumbnailCanvasQueueKey(canvasId: number): string {
  return `canvas:${canvasId}`;
}

function docxThumbnailPrefetchQueueKey(pageIndex: number): string {
  return `prefetch:${pageIndex}`;
}

export function resolveDocxPageThumbnailResolution(
  options: DocxPageThumbnailResolutionOptions
): DocxPageThumbnailResolution {
  const safeSourceWidthPx = Math.max(1, Math.round(options.sourceWidthPx));
  const safeSourceHeightPx = Math.max(1, Math.round(options.sourceHeightPx));
  const resolutionBounds =
    typeof options.resolution === "number" &&
    Number.isFinite(options.resolution) &&
    options.resolution > 0
      ? {
          maxWidthPx: Number(options.resolution),
          maxHeightPx: Number(options.resolution),
        }
      : typeof options.resolution === "object" && options.resolution
      ? {
          maxWidthPx:
            Number.isFinite(options.resolution.maxWidth) &&
            Number(options.resolution.maxWidth) > 0
              ? Number(options.resolution.maxWidth)
              : undefined,
          maxHeightPx:
            Number.isFinite(options.resolution.maxHeight) &&
            Number(options.resolution.maxHeight) > 0
              ? Number(options.resolution.maxHeight)
              : undefined,
        }
      : undefined;
  const widthBoundPx = Number.isFinite(options.maxWidthPx)
    ? Math.max(1, Math.round(options.maxWidthPx as number))
    : Number.isFinite(resolutionBounds?.maxWidthPx)
    ? Math.max(1, Math.round(resolutionBounds?.maxWidthPx as number))
    : 180;
  const heightBoundPx = Number.isFinite(options.maxHeightPx)
    ? Math.max(1, Math.round(options.maxHeightPx as number))
    : Number.isFinite(resolutionBounds?.maxHeightPx)
    ? Math.max(1, Math.round(resolutionBounds?.maxHeightPx as number))
    : Number.POSITIVE_INFINITY;
  const scale = Math.min(
    1,
    widthBoundPx / safeSourceWidthPx,
    heightBoundPx / safeSourceHeightPx
  );
  const pixelRatio = Number.isFinite(options.pixelRatio)
    ? Math.max(1, Number(options.pixelRatio))
    : 1;
  const widthPx = Math.max(1, Math.round(safeSourceWidthPx * scale));
  const heightPx = Math.max(1, Math.round(safeSourceHeightPx * scale));

  return {
    widthPx,
    heightPx,
    pixelWidthPx: Math.max(1, Math.round(widthPx * pixelRatio)),
    pixelHeightPx: Math.max(1, Math.round(heightPx * pixelRatio)),
    scale,
  };
}

export function useDocxPageThumbnails(
  editor: DocxEditorController,
  options: UseDocxPageThumbnailsOptions = {}
): UseDocxPageThumbnailsResult {
  const pageSurfaceRegistryOwner = docxViewerPageSurfaceRegistryOwner(editor);
  const pageSurfaceRegistryEditor = React.useMemo(
    () => ({ syncPaginationInfo: editor.syncPaginationInfo }),
    [pageSurfaceRegistryOwner]
  );
  const [pageSurfaceEpoch, setPageSurfaceEpoch] = React.useState(0);
  const [pageThumbnailStates, setPageThumbnailStates] = React.useState<
    Map<number, { status: DocxPageThumbnailStatus; error?: Error }>
  >(() => new Map());
  const attachedCanvasByPageRef = React.useRef<Map<number, HTMLCanvasElement>>(
    new Map()
  );
  const canvasRefCallbacksRef = React.useRef<
    Map<number, React.RefCallback<HTMLCanvasElement>>
  >(new Map());
  const renderToCanvasCallbacksRef = React.useRef<
    Map<number, (canvas: HTMLCanvasElement) => Promise<void>>
  >(new Map());
  const fallbackLayout = React.useMemo(
    () => resolveDocumentLayout(editor.model),
    [editor.model]
  );
  const pageSurfaceRegistry = React.useMemo(
    () => ensureDocxViewerPageSurfaceRegistry(pageSurfaceRegistryEditor),
    [pageSurfaceRegistryEditor]
  );

  React.useEffect(
    () =>
      subscribeDocxViewerPageSurfaces(pageSurfaceRegistryEditor, () => {
        setPageSurfaceEpoch((current) => current + 1);
      }),
    [pageSurfaceRegistryEditor]
  );

  const mountedPageElements = React.useMemo(
    () => new Map(pageSurfaceRegistry.pageElements),
    [pageSurfaceEpoch, pageSurfaceRegistry.pageElements]
  );

  const updatePageThumbnailState = React.useCallback(
    (
      pageIndex: number,
      status: DocxPageThumbnailStatus,
      error?: Error
    ): void => {
      setPageThumbnailStates((current) => {
        const next = new Map(current);
        const previous = next.get(pageIndex);
        if (
          previous?.status === status &&
          previous?.error?.message === error?.message
        ) {
          return current;
        }
        next.set(pageIndex, { status, error });
        return next;
      });
    },
    []
  );

  const thumbnailSurfaceCacheRef = React.useRef<
    DocxThumbnailSurfaceCache<HTMLCanvasElement> | undefined
  >(undefined);
  const thumbnailRasterQueueRef = React.useRef<
    SerialIdleTaskQueue<string> | undefined
  >(undefined);
  const detachedThumbnailSurfaceRendererRef = React.useRef<
    DocxDetachedThumbnailSurfaceRenderer | undefined
  >(undefined);
  const lastPaintedThumbnailKeyByCanvasRef = React.useRef(
    new WeakMap<HTMLCanvasElement, string>()
  );
  const thumbnailQueueKeyByCanvasRef = React.useRef(
    new WeakMap<HTMLCanvasElement, string>()
  );
  const nextThumbnailCanvasQueueIdRef = React.useRef(0);
  const queuedPrefetchThumbnailKeysRef = React.useRef<Set<string>>(new Set());
  const thumbnailMinRasterIntervalMs =
    normalizeDocxThumbnailMinRasterIntervalMs(options.minRasterIntervalMs);
  const ensureThumbnailSurfaceCache = React.useCallback(() => {
    if (!thumbnailSurfaceCacheRef.current) {
      thumbnailSurfaceCacheRef.current = new DocxThumbnailSurfaceCache(
        DOCX_THUMBNAIL_SURFACE_CACHE_MAX_ENTRIES
      );
    }
    return thumbnailSurfaceCacheRef.current;
  }, []);
  const ensureThumbnailRasterQueue = React.useCallback(() => {
    if (!thumbnailRasterQueueRef.current) {
      thumbnailRasterQueueRef.current = new SerialIdleTaskQueue({
        minTaskIntervalMs: thumbnailMinRasterIntervalMs,
      });
    }
    return thumbnailRasterQueueRef.current;
  }, [thumbnailMinRasterIntervalMs]);
  const thumbnailQueueKeyForCanvas = React.useCallback(
    (canvas: HTMLCanvasElement): string => {
      const existing = thumbnailQueueKeyByCanvasRef.current.get(canvas);
      if (existing) {
        return existing;
      }

      const nextKey = docxThumbnailCanvasQueueKey(
        nextThumbnailCanvasQueueIdRef.current
      );
      nextThumbnailCanvasQueueIdRef.current += 1;
      thumbnailQueueKeyByCanvasRef.current.set(canvas, nextKey);
      return nextKey;
    },
    []
  );
  React.useEffect(() => {
    thumbnailRasterQueueRef.current?.clear();
    thumbnailRasterQueueRef.current = undefined;
    queuedPrefetchThumbnailKeysRef.current.clear();
  }, [thumbnailMinRasterIntervalMs]);
  React.useEffect(() => {
    thumbnailSurfaceCacheRef.current?.clear();
    thumbnailRasterQueueRef.current?.clear();
    detachedThumbnailSurfaceRendererRef.current?.clear();
    detachedThumbnailSurfaceRendererRef.current = undefined;
    lastPaintedThumbnailKeyByCanvasRef.current = new WeakMap();
    thumbnailQueueKeyByCanvasRef.current = new WeakMap();
    nextThumbnailCanvasQueueIdRef.current = 0;
    queuedPrefetchThumbnailKeysRef.current.clear();
  }, [editor.documentLoadNonce, pageSurfaceRegistryOwner]);
  React.useEffect(() => {
    detachedThumbnailSurfaceRendererRef.current?.clear();
    detachedThumbnailSurfaceRendererRef.current = undefined;
  }, [
    editor.documentTheme,
    editor.model,
    editor.showComments,
    editor.showTrackedChanges,
  ]);
  React.useEffect(
    () => () => {
      detachedThumbnailSurfaceRendererRef.current?.clear();
      detachedThumbnailSurfaceRendererRef.current = undefined;
    },
    []
  );

  const thumbnailResolutionOptionsKey = React.useMemo(() => {
    const bounds = options.resolution;
    const boundsKey =
      typeof bounds === "number"
        ? `n${bounds}`
        : bounds
        ? `b${bounds.maxWidth ?? ""}x${bounds.maxHeight ?? ""}`
        : "";
    return `${boundsKey}|${options.maxWidthPx ?? ""}|${
      options.maxHeightPx ?? ""
    }|${options.pixelRatio ?? ""}`;
  }, [
    options.maxHeightPx,
    options.maxWidthPx,
    options.pixelRatio,
    options.resolution,
  ]);
  // Identifies what an up-to-date thumbnail of a page must reflect: page
  // content (via the viewer-synced content key), theme, and requested
  // resolution. Undefined when the viewer has not synced content keys, which
  // disables skip/cache behavior and falls back to always rasterizing.
  const thumbnailSkipKeyForPage = React.useCallback(
    (pageIndex: number): string | undefined => {
      const contentKey = pageSurfaceRegistry.pageContentKeys.get(pageIndex);
      return contentKey === undefined
        ? undefined
        : `${contentKey}|${editor.documentTheme}|${thumbnailResolutionOptionsKey}`;
    },
    [editor.documentTheme, pageSurfaceRegistry, thumbnailResolutionOptionsKey]
  );
  const totalThumbnailPages = Math.max(1, editor.totalPages);
  const visibleThumbnailPageIndexes = React.useMemo(
    () =>
      normalizeDocxThumbnailPageIndexes(
        options.renderWindow?.visiblePageIndexes,
        totalThumbnailPages
      ),
    [options.renderWindow?.visiblePageIndexes, totalThumbnailPages]
  );
  const prefetchThumbnailPageIndexes = React.useMemo(
    () =>
      normalizeDocxThumbnailPageIndexes(
        options.renderWindow?.prefetchPageIndexes,
        totalThumbnailPages
      ),
    [options.renderWindow?.prefetchPageIndexes, totalThumbnailPages]
  );
  const visibleThumbnailPageIndexesKey = docxThumbnailPageIndexesKey(
    visibleThumbnailPageIndexes
  );
  const prefetchThumbnailPageIndexesKey = docxThumbnailPageIndexesKey(
    prefetchThumbnailPageIndexes
  );
  const visibleThumbnailPageIndexSet = React.useMemo(
    () => new Set(visibleThumbnailPageIndexes),
    [visibleThumbnailPageIndexesKey]
  );
  const thumbnailRenderPriorityForPage = React.useCallback(
    (
      pageIndex: number,
      fallbackPriority = DOCX_THUMBNAIL_RENDER_PRIORITY_ATTACHED
    ): number =>
      visibleThumbnailPageIndexSet.has(pageIndex)
        ? DOCX_THUMBNAIL_RENDER_PRIORITY_VISIBLE
        : fallbackPriority,
    [visibleThumbnailPageIndexSet]
  );

  const renderPageThumbnailSurface = React.useCallback(
    async (
      pageIndex: number,
      renderOptions?: { force?: boolean }
    ): Promise<
      | {
          surface: HTMLCanvasElement;
          resolution: DocxPageThumbnailResolution;
          runSkipKey: string | undefined;
        }
      | undefined
    > => {
      const force = renderOptions?.force === true;
      const runSkipKey = thumbnailSkipKeyForPage(pageIndex);
      const detachedRenderKey = [
        runSkipKey ?? `load:${editor.documentLoadNonce}`,
        editor.showComments ? "comments:1" : "comments:0",
        editor.showTrackedChanges ? "tracked:1" : "tracked:0",
      ].join("|");
      const thumbnailSnapshotEntry =
        pageSurfaceRegistry.pageThumbnailSnapshots.get(pageIndex);
      const fallbackSourceSize = resolveDocxViewerRegisteredPageSurfaceSize(
        pageSurfaceRegistry,
        pageIndex,
        fallbackLayout.pageWidthPx,
        fallbackLayout.pageHeightPx
      );
      const resolution = resolveDocxPageThumbnailResolution({
        sourceWidthPx: fallbackSourceSize.widthPx,
        sourceHeightPx: fallbackSourceSize.heightPx,
        resolution: options.resolution,
        maxWidthPx: options.maxWidthPx,
        maxHeightPx: options.maxHeightPx,
        pixelRatio: options.pixelRatio,
      });
      const surfaceKey =
        runSkipKey === undefined
          ? undefined
          : `${runSkipKey}|${fallbackSourceSize.widthPx}x${fallbackSourceSize.heightPx}|${resolution.pixelWidthPx}x${resolution.pixelHeightPx}`;
      const surfaceCache = ensureThumbnailSurfaceCache();
      let surface =
        !force && surfaceKey !== undefined
          ? surfaceCache.get(surfaceKey)
          : undefined;

      if (!surface) {
        const thumbnailSnapshot = thumbnailSnapshotEntry?.getSnapshot();
        if (thumbnailSnapshot) {
          surface = await renderDocxThumbnailSnapshotSurface({
            snapshot: thumbnailSnapshot,
            widthPx: resolution.widthPx,
            heightPx: resolution.heightPx,
            pixelWidthPx: resolution.pixelWidthPx,
            pixelHeightPx: resolution.pixelHeightPx,
          });
          if (surfaceKey !== undefined) {
            surfaceCache.set(surfaceKey, surface);
          }
          return { surface, resolution, runSkipKey };
        }

        let livePageElement = pageSurfaceRegistry.pageElements.get(pageIndex);
        if (!livePageElement || !livePageElement.isConnected) {
          if (!detachedThumbnailSurfaceRendererRef.current) {
            detachedThumbnailSurfaceRendererRef.current =
              new DocxDetachedThumbnailSurfaceRenderer();
          }
          livePageElement =
            await detachedThumbnailSurfaceRendererRef.current.renderPageSurface({
              editor,
              registry: pageSurfaceRegistry,
              pageIndex,
              renderKey: detachedRenderKey,
            });
        }
        if (!livePageElement || !livePageElement.isConnected) {
          return undefined;
        }

        const sourceSize = resolveDocxViewerPageSurfaceSize(
          livePageElement,
          fallbackSourceSize.widthPx,
          fallbackSourceSize.heightPx
        );
        surface = await rasterizeDocxThumbnailSurface({
          pageElement: livePageElement,
          sourceWidthPx: sourceSize.widthPx,
          sourceHeightPx: sourceSize.heightPx,
          widthPx: resolution.widthPx,
          heightPx: resolution.heightPx,
          pixelWidthPx: resolution.pixelWidthPx,
          pixelHeightPx: resolution.pixelHeightPx,
        });
        if (surfaceKey !== undefined) {
          surfaceCache.set(surfaceKey, surface);
        }
      }

      return { surface, resolution, runSkipKey };
    },
    [
      ensureThumbnailSurfaceCache,
      fallbackLayout.pageHeightPx,
      fallbackLayout.pageWidthPx,
      options.resolution,
      options.maxHeightPx,
      options.maxWidthPx,
      options.pixelRatio,
      pageSurfaceRegistry,
      thumbnailSkipKeyForPage,
      editor,
    ]
  );

  const renderPageThumbnailToCanvas = React.useCallback(
    async (
      pageIndex: number,
      canvas?: HTMLCanvasElement,
      renderOptions?: { force?: boolean; priority?: number }
    ): Promise<void> => {
      if (options.disabled) {
        return;
      }

      const targetCanvas =
        canvas ?? attachedCanvasByPageRef.current.get(pageIndex);
      if (!targetCanvas) {
        return;
      }
      const requiresAttachedTarget = canvas === undefined;

      const force = renderOptions?.force === true;
      const lastPaintedKey =
        lastPaintedThumbnailKeyByCanvasRef.current.get(targetCanvas);
      if (
        !force &&
        lastPaintedKey !== undefined &&
        lastPaintedKey === thumbnailSkipKeyForPage(pageIndex)
      ) {
        updatePageThumbnailState(pageIndex, "ready");
        return;
      }

      const paintIntoTarget = async (): Promise<void> => {
        if (
          requiresAttachedTarget &&
          attachedCanvasByPageRef.current.get(pageIndex) !== targetCanvas
        ) {
          return;
        }

        try {
          const rendered = await renderPageThumbnailSurface(pageIndex, {
            force,
          });
          if (!rendered) {
            updatePageThumbnailState(pageIndex, "unavailable");
            return;
          }

          const { surface, resolution, runSkipKey } = rendered;
          blitDocxThumbnailSurface(surface, targetCanvas, resolution);
          if (runSkipKey !== undefined) {
            lastPaintedThumbnailKeyByCanvasRef.current.set(
              targetCanvas,
              runSkipKey
            );
          }
          updatePageThumbnailState(pageIndex, "ready");
        } catch (error) {
          updatePageThumbnailState(
            pageIndex,
            "error",
            error instanceof Error
              ? error
              : new Error("Failed to render DOCX page thumbnail.")
          );
        }
      };

      updatePageThumbnailState(pageIndex, "rendering");

      // Fast path: when a draw snapshot exists, renderPageThumbnailSurface paints
      // synchronously (no DOM clone, no SVG image decode), so paint it on the
      // current microtask instead of deferring to the idle queue + per-canvas
      // throttle. Those exist to pace the expensive async DOM/detached-render
      // path; routing instant snapshot paints through them only adds latency.
      // Attached canvases are bounded by the rail's visible+overscan window, so
      // the synchronous burst stays small; off-screen prefetch stays queued.
      if (pageSurfaceRegistry.pageThumbnailSnapshots.has(pageIndex)) {
        await paintIntoTarget();
        return;
      }

      await ensureThumbnailRasterQueue().enqueue(
        thumbnailQueueKeyForCanvas(targetCanvas),
        paintIntoTarget,
        {
          priority:
            renderOptions?.priority ??
            thumbnailRenderPriorityForPage(
              pageIndex,
              DOCX_THUMBNAIL_RENDER_PRIORITY_ATTACHED
            ),
        }
      );
    },
    [
      ensureThumbnailRasterQueue,
      options.disabled,
      pageSurfaceRegistry,
      renderPageThumbnailSurface,
      thumbnailSkipKeyForPage,
      thumbnailQueueKeyForCanvas,
      thumbnailRenderPriorityForPage,
      updatePageThumbnailState,
    ]
  );

  const prefetchPageThumbnailSurface = React.useCallback(
    async (pageIndex: number): Promise<void> => {
      if (options.disabled) {
        return;
      }

      const queueKey = docxThumbnailPrefetchQueueKey(pageIndex);
      queuedPrefetchThumbnailKeysRef.current.add(queueKey);
      await ensureThumbnailRasterQueue().enqueue(
        queueKey,
        async () => {
          try {
            await renderPageThumbnailSurface(pageIndex);
          } catch {
            // Prefetch is opportunistic; attached thumbnail renders report errors.
          }
        },
        { priority: DOCX_THUMBNAIL_RENDER_PRIORITY_PREFETCH }
      );
      queuedPrefetchThumbnailKeysRef.current.delete(queueKey);
    },
    [ensureThumbnailRasterQueue, options.disabled, renderPageThumbnailSurface]
  );

  const requestAttachedThumbnailRenders = React.useCallback(
    async (renderOptions?: { force?: boolean }): Promise<void> => {
      const attachedPageIndexes = [
        ...attachedCanvasByPageRef.current.keys(),
      ].sort((leftPageIndex, rightPageIndex) => {
        const leftPriority = thumbnailRenderPriorityForPage(leftPageIndex);
        const rightPriority = thumbnailRenderPriorityForPage(rightPageIndex);
        return leftPriority - rightPriority || leftPageIndex - rightPageIndex;
      });
      const tasks = attachedPageIndexes.map((pageIndex) =>
        renderPageThumbnailToCanvas(pageIndex, undefined, {
          ...renderOptions,
          priority: thumbnailRenderPriorityForPage(pageIndex),
        })
      );
      await Promise.all(tasks);
    },
    [renderPageThumbnailToCanvas, thumbnailRenderPriorityForPage]
  );
  const requestPrefetchThumbnailRenders = React.useCallback(async (): Promise<
    void
  > => {
    if (options.disabled) {
      queuedPrefetchThumbnailKeysRef.current.forEach((queueKey) => {
        thumbnailRasterQueueRef.current?.cancel(queueKey);
      });
      queuedPrefetchThumbnailKeysRef.current.clear();
      return;
    }

    const requestedPrefetchKeys = new Set(
      prefetchThumbnailPageIndexes.map((pageIndex) =>
        docxThumbnailPrefetchQueueKey(pageIndex)
      )
    );
    queuedPrefetchThumbnailKeysRef.current.forEach((queueKey) => {
      if (!requestedPrefetchKeys.has(queueKey)) {
        thumbnailRasterQueueRef.current?.cancel(queueKey);
        queuedPrefetchThumbnailKeysRef.current.delete(queueKey);
      }
    });
    const tasks = prefetchThumbnailPageIndexes.map((pageIndex) =>
      prefetchPageThumbnailSurface(pageIndex)
    );
    await Promise.all(tasks);
  }, [
    options.disabled,
    prefetchPageThumbnailSurface,
    prefetchThumbnailPageIndexes,
  ]);

  React.useEffect(() => {
    void requestPrefetchThumbnailRenders();
  }, [
    editor.documentLoadNonce,
    editor.documentTheme,
    editor.model,
    mountedPageElements,
    options.maxHeightPx,
    options.maxWidthPx,
    options.pixelRatio,
    options.resolution,
    prefetchThumbnailPageIndexesKey,
    requestPrefetchThumbnailRenders,
  ]);

  // Public API: a forced refresh, bypassing the unchanged-content skip and the
  // surface cache (callers reach for this after out-of-band changes such as
  // late webfont loads).
  const rerenderAttachedThumbnails = React.useCallback(
    async (): Promise<void> => requestAttachedThumbnailRenders({ force: true }),
    [requestAttachedThumbnailRenders]
  );
  const renderPageThumbnailToCanvasRef = React.useRef(
    renderPageThumbnailToCanvas
  );
  React.useEffect(() => {
    renderPageThumbnailToCanvasRef.current = renderPageThumbnailToCanvas;
  }, [renderPageThumbnailToCanvas]);
  React.useEffect(() => {
    canvasRefCallbacksRef.current.clear();
    renderToCanvasCallbacksRef.current.clear();
  }, [pageSurfaceRegistryOwner]);

  // Re-request attached thumbnails whenever something that can affect them
  // changes. Requests are cheap: pages whose synced content key still matches
  // the last painted key skip without touching the raster pipeline.
  React.useEffect(() => {
    void requestAttachedThumbnailRenders();
  }, [
    editor.documentLoadNonce,
    editor.documentTheme,
    editor.model,
    mountedPageElements,
    options.disabled,
    options.resolution,
    options.maxHeightPx,
    options.maxWidthPx,
    options.pixelRatio,
    requestAttachedThumbnailRenders,
  ]);

  // Geometry/paint metadata for every page, independent of render status. This
  // is the expensive part — each mounted page calls resolveDocxViewerPageSurfaceSize
  // (a getBoundingClientRect, i.e. a forced layout). Keeping it out of the
  // status-dependent memo means the ~2-3 status transitions per page during load
  // no longer re-measure every page (which was O(pages^2) of forced layout).
  const thumbnailGeometryItems = React.useMemo(() => {
    const totalPages = Math.max(1, editor.totalPages);
    return Array.from({ length: totalPages }, (_, pageIndex) => {
      const pageElement = mountedPageElements.get(pageIndex);
      const sourceSize =
        pageElement && pageElement.isConnected
          ? resolveDocxViewerPageSurfaceSize(
              pageElement,
              fallbackLayout.pageWidthPx,
              fallbackLayout.pageHeightPx
            )
          : resolveDocxViewerRegisteredPageSurfaceSize(
              pageSurfaceRegistry,
              pageIndex,
              fallbackLayout.pageWidthPx,
              fallbackLayout.pageHeightPx
            );
      const resolution = resolveDocxPageThumbnailResolution({
        sourceWidthPx: sourceSize.widthPx,
        sourceHeightPx: sourceSize.heightPx,
        resolution: options.resolution,
        maxWidthPx: options.maxWidthPx,
        maxHeightPx: options.maxHeightPx,
        pixelRatio: options.pixelRatio,
      });

      return {
        // Internal: drives the default status below; stripped before exposure.
        hasElement: Boolean(pageElement),
        pageIndex,
        pageNumber: pageIndex + 1,
        sourceWidthPx: sourceSize.widthPx,
        sourceHeightPx: sourceSize.heightPx,
        isMounted: Boolean(pageElement && pageElement.isConnected),
        paint: (canvas: HTMLCanvasElement | null): boolean => {
          if (!canvas || options.disabled) {
            return false;
          }
          void renderPageThumbnailToCanvasRef.current(pageIndex, canvas);
          return true;
        },
        canvasRef:
          canvasRefCallbacksRef.current.get(pageIndex) ??
          (() => {
            const nextCanvasRef: React.RefCallback<HTMLCanvasElement> = (
              canvas
            ) => {
              if (canvas) {
                attachedCanvasByPageRef.current.set(pageIndex, canvas);
                void renderPageThumbnailToCanvasRef.current(pageIndex, canvas);
                return;
              }
              const previousCanvas =
                attachedCanvasByPageRef.current.get(pageIndex);
              if (previousCanvas) {
                thumbnailRasterQueueRef.current?.cancel(
                  thumbnailQueueKeyForCanvas(previousCanvas)
                );
              }
              attachedCanvasByPageRef.current.delete(pageIndex);
            };
            canvasRefCallbacksRef.current.set(pageIndex, nextCanvasRef);
            return nextCanvasRef;
          })(),
        renderToCanvas:
          renderToCanvasCallbacksRef.current.get(pageIndex) ??
          (() => {
            const nextRenderToCanvas = async (
              canvas: HTMLCanvasElement
            ): Promise<void> => {
              await renderPageThumbnailToCanvasRef.current(pageIndex, canvas);
            };
            renderToCanvasCallbacksRef.current.set(
              pageIndex,
              nextRenderToCanvas
            );
            return nextRenderToCanvas;
          })(),
        aspectRatio: sourceSize.widthPx / Math.max(1, sourceSize.heightPx),
        contentWidth: sourceSize.widthPx,
        contentHeight: sourceSize.heightPx,
        width: resolution.widthPx,
        height: resolution.heightPx,
        ...resolution,
      };
    });
  }, [
    editor.totalPages,
    fallbackLayout.pageHeightPx,
    fallbackLayout.pageWidthPx,
    mountedPageElements,
    options.disabled,
    options.resolution,
    options.maxHeightPx,
    options.maxWidthPx,
    options.pixelRatio,
    pageSurfaceRegistry,
    thumbnailQueueKeyForCanvas,
  ]);

  // Cheap status merge: a status change only re-maps the (already-measured)
  // geometry items, never re-touching the DOM.
  const thumbnails = React.useMemo<DocxPageThumbnailItem[]>(
    () =>
      thumbnailGeometryItems.map((geometryItem) => {
        const { hasElement, ...item } = geometryItem;
        const state = pageThumbnailStates.get(item.pageIndex);
        return {
          ...item,
          status: state?.status ?? (hasElement ? "idle" : "unavailable"),
          error: state?.error,
        };
      }),
    [thumbnailGeometryItems, pageThumbnailStates]
  );

  const paintThumbnail = React.useCallback(
    (pageIndex: number, canvas: HTMLCanvasElement | null): boolean => {
      if (!canvas || options.disabled) {
        return false;
      }
      const thumbnail = thumbnails[pageIndex];
      if (!thumbnail) {
        return false;
      }
      return thumbnail.paint(canvas);
    },
    [options.disabled, thumbnails]
  );

  return React.useMemo(
    () => ({
      paintThumbnail,
      thumbnails,
      rerenderAttachedThumbnails,
    }),
    [paintThumbnail, rerenderAttachedThumbnails, thumbnails]
  );
}

export const useDocxViewerThumbnails = useDocxPageThumbnails;

export function useDocxDocumentTheme(
  editor: Pick<DocxEditorController, "documentTheme" | "setDocumentTheme">
): UseDocxDocumentThemeResult {
  const setDocumentTheme = React.useCallback(
    (theme: DocxDocumentTheme): void => {
      editor.setDocumentTheme(theme);
    },
    [editor.setDocumentTheme]
  );

  const toggleDocumentTheme = React.useCallback((): void => {
    editor.setDocumentTheme(editor.documentTheme === "dark" ? "light" : "dark");
  }, [editor.documentTheme, editor.setDocumentTheme]);

  return React.useMemo(
    () => ({
      documentTheme: editor.documentTheme,
      isDarkDocument: editor.documentTheme === "dark",
      setDocumentTheme,
      toggleDocumentTheme,
    }),
    [editor.documentTheme, setDocumentTheme, toggleDocumentTheme]
  );
}

export function useDocxParagraphStyles(
  editor: Pick<
    DocxEditorController,
    | "availableParagraphStyles"
    | "selectedParagraphStyleId"
    | "setParagraphStyle"
  >
): UseDocxParagraphStylesResult {
  const setParagraphStyle = React.useCallback(
    (styleId?: string): void => {
      editor.setParagraphStyle(styleId);
    },
    [editor.setParagraphStyle]
  );

  return React.useMemo(
    () => ({
      paragraphStyles: editor.availableParagraphStyles,
      selectedParagraphStyleId: editor.selectedParagraphStyleId,
      setParagraphStyle,
    }),
    [
      editor.availableParagraphStyles,
      editor.selectedParagraphStyleId,
      setParagraphStyle,
    ]
  );
}

export function useDocxImageWrapMenu(
  menu: Pick<DocxContextMenuRenderProps, "context" | "runAction">
): UseDocxImageWrapMenuResult | undefined {
  const wrapState =
    menu.context.kind === "image"
      ? menu.context.image?.wrap ??
        resolveDocxImageWrapState(menu.context.image?.floating)
      : undefined;

  const setMode = React.useCallback(
    (mode: DocxImageWrapMode): void => {
      menu.runAction(imageWrapModeActionId(mode));
    },
    [menu]
  );
  const setMoveWithText = React.useCallback(
    (moveWithText: boolean): void => {
      menu.runAction(
        moveWithText ? "image-move-with-text" : "image-fix-position-on-page"
      );
    },
    [menu]
  );

  return React.useMemo(() => {
    if (!wrapState) {
      return undefined;
    }

    return {
      state: wrapState,
      wrapOptions: DOCX_IMAGE_WRAP_MODE_ACTIONS.map((action) => ({
        actionId: action.actionId,
        label: action.label,
        checked: wrapState.mode === action.mode,
        onSelect: () => menu.runAction(action.actionId),
      })),
      positioningOptions: [
        {
          actionId: "image-move-with-text",
          label: "Move with Text",
          checked: wrapState.moveWithText,
          disabled: wrapState.mode === "inline",
          onSelect: () => menu.runAction("image-move-with-text"),
        },
        {
          actionId: "image-fix-position-on-page",
          label: "Fix Position on Page",
          checked: wrapState.fixedPositionOnPage,
          disabled: wrapState.mode === "inline",
          onSelect: () => menu.runAction("image-fix-position-on-page"),
        },
      ],
      editWrapBoundaryOption: {
        actionId: "image-edit-wrap-boundary",
        label: "Edit Wrap Boundary",
        checked: false,
        disabled: true,
        onSelect: () => menu.runAction("image-edit-wrap-boundary"),
      },
      moreLayoutOptionsOption: {
        actionId: "image-more-layout-options",
        label: "More Layout Options...",
        checked: false,
        disabled: true,
        onSelect: () => menu.runAction("image-more-layout-options"),
      },
      setMode,
      setMoveWithText,
    };
  }, [menu, setMode, setMoveWithText, wrapState]);
}

export function useDocxLineSpacing(
  editor: Pick<DocxEditorController, "selectedLineSpacing" | "setLineSpacing">
): UseDocxLineSpacingResult {
  const setLineSpacing = React.useCallback(
    (lineMultiple: number): void => {
      editor.setLineSpacing(lineMultiple);
    },
    [editor.setLineSpacing]
  );

  return React.useMemo(
    () => ({
      lineSpacing: editor.selectedLineSpacing,
      setLineSpacing,
    }),
    [editor.selectedLineSpacing, setLineSpacing]
  );
}

export function useDocxBorders(
  editor: Pick<
    DocxEditorController,
    "selectedBorderContext" | "activeBorderPresets" | "applyBorderPreset"
  >
): UseDocxBordersResult {
  const applyBorderPreset = React.useCallback(
    (preset: DocxBorderPreset): void => {
      editor.applyBorderPreset(preset);
    },
    [editor.applyBorderPreset]
  );

  return React.useMemo(
    () => ({
      borderContext: editor.selectedBorderContext,
      activeBorderPresets: editor.activeBorderPresets,
      applyBorderPreset,
    }),
    [
      editor.activeBorderPresets,
      editor.selectedBorderContext,
      applyBorderPreset,
    ]
  );
}

export function useDocxFormFields(
  editor: Pick<
    DocxEditorController,
    | "model"
    | "selectedFormField"
    | "selectFormField"
    | "setFormFieldValue"
    | "toggleFormCheckbox"
    | "updateFormFieldWidget"
  >
): UseDocxFormFieldsResult {
  const formFields = React.useMemo(
    () => collectFormFieldsFromModel(editor.model),
    [editor.model]
  );

  const updateSelectedFormFieldWidget = React.useCallback(
    (patch: Partial<NonNullable<FormFieldRunNode["widget"]>>): void => {
      if (!editor.selectedFormField) {
        return;
      }
      editor.updateFormFieldWidget(editor.selectedFormField.location, patch);
    },
    [editor.selectedFormField, editor.updateFormFieldWidget]
  );

  return React.useMemo(
    () => ({
      formFields,
      selectedFormField: editor.selectedFormField,
      selectFormField: editor.selectFormField,
      setFormFieldValue: editor.setFormFieldValue,
      toggleFormCheckbox: editor.toggleFormCheckbox,
      updateFormFieldWidget: editor.updateFormFieldWidget,
      updateSelectedFormFieldWidget,
    }),
    [
      formFields,
      editor.selectedFormField,
      editor.selectFormField,
      editor.setFormFieldValue,
      editor.toggleFormCheckbox,
      editor.updateFormFieldWidget,
      updateSelectedFormFieldWidget,
    ]
  );
}

export function useDocxTrackChanges(
  editor: Pick<
    DocxEditorController,
    | "trackedChanges"
    | "showTrackedChanges"
    | "setShowTrackedChanges"
    | "toggleShowTrackedChanges"
  >
): UseDocxTrackChangesResult {
  const changesByLocation = React.useMemo(() => {
    const grouped = new Map<string, DocxTrackedChange[]>();
    editor.trackedChanges.forEach((change) => {
      const key = paragraphLocationKey(change.location);
      const bucket = grouped.get(key) ?? [];
      bucket.push(change);
      grouped.set(key, bucket);
    });
    return grouped;
  }, [editor.trackedChanges]);

  const getChangesForLocation = React.useCallback(
    (location: DocxTextRangeLocation): DocxTrackedChange[] => {
      return changesByLocation.get(paragraphLocationKey(location)) ?? [];
    },
    [changesByLocation]
  );

  return React.useMemo(
    () => ({
      trackedChanges: editor.trackedChanges,
      showTrackedChanges: editor.showTrackedChanges,
      setShowTrackedChanges: editor.setShowTrackedChanges,
      toggleShowTrackedChanges: editor.toggleShowTrackedChanges,
      changesByLocation,
      getChangesForLocation,
    }),
    [
      editor.trackedChanges,
      editor.showTrackedChanges,
      editor.setShowTrackedChanges,
      editor.toggleShowTrackedChanges,
      changesByLocation,
      getChangesForLocation,
    ]
  );
}

/**
 * Exposes document comments and the show/hide state, mirroring
 * `useDocxTrackChanges`. Pair with `DocxEditorViewer`'s `renderCommentCard`
 * to fully customize how comments render.
 */
export function useDocxComments(
  editor: Pick<
    DocxEditorController,
    "comments" | "showComments" | "setShowComments" | "toggleShowComments"
  >
): UseDocxCommentsResult {
  const commentsByLocation = React.useMemo(() => {
    const grouped = new Map<string, DocxComment[]>();
    editor.comments.forEach((comment) => {
      const key = paragraphLocationKey(comment.location);
      const bucket = grouped.get(key) ?? [];
      bucket.push(comment);
      grouped.set(key, bucket);
    });
    return grouped;
  }, [editor.comments]);

  const getCommentsForLocation = React.useCallback(
    (location: DocxTextRangeLocation): DocxComment[] => {
      return commentsByLocation.get(paragraphLocationKey(location)) ?? [];
    },
    [commentsByLocation]
  );

  return React.useMemo(
    () => ({
      comments: editor.comments,
      showComments: editor.showComments,
      setShowComments: editor.setShowComments,
      toggleShowComments: editor.toggleShowComments,
      commentsByLocation,
      getCommentsForLocation,
    }),
    [
      editor.comments,
      editor.showComments,
      editor.setShowComments,
      editor.toggleShowComments,
      commentsByLocation,
      getCommentsForLocation,
    ]
  );
}

export function useDocxPageLayout(
  editor: Pick<DocxEditorController, "model">
): UseDocxPageLayoutResult {
  const primarySectionPropertiesXml =
    editor.model.metadata.sections?.[0]?.sectionPropertiesXml ??
    editor.model.metadata.sectionPropertiesXml;
  const layout = React.useMemo<DocxPageLayoutInfo>(() => {
    const sectionLayout = parseSectionLayout(primarySectionPropertiesXml);
    const sectionColumns = parseSectionColumns(primarySectionPropertiesXml);
    const pageNumberStart = parseSectionPageNumberStart(
      primarySectionPropertiesXml
    );
    const contentWidthPx = Math.max(
      120,
      sectionLayout.pageWidthPx -
        sectionLayout.marginsPx.left -
        sectionLayout.marginsPx.right
    );
    const contentHeightPx = Math.max(
      120,
      sectionLayout.pageHeightPx -
        sectionLayout.marginsPx.top -
        sectionLayout.marginsPx.bottom
    );

    return {
      pageWidthPx: sectionLayout.pageWidthPx,
      pageHeightPx: sectionLayout.pageHeightPx,
      contentWidthPx,
      contentHeightPx,
      marginsPx: { ...sectionLayout.marginsPx },
      headerDistancePx: sectionLayout.headerDistancePx,
      footerDistancePx: sectionLayout.footerDistancePx,
      pageNumberStart,
      columns: sectionColumns
        ? {
            count: sectionColumns.count,
            gapPx: sectionColumns.gapPx,
          }
        : undefined,
      viewportDefaults: {
        zoomPercent: 100,
        pageGapPx: DOC_PAGE_BREAK_GAP,
      },
    };
  }, [primarySectionPropertiesXml]);

  return React.useMemo(
    () => ({
      layout,
    }),
    [layout]
  );
}

export function useDocxPagination(
  editor: Pick<DocxEditorController, "currentPage" | "totalPages">
): UseDocxPaginationResult {
  return React.useMemo(
    () => ({
      pagination: {
        currentPage: editor.currentPage,
        totalPages: editor.totalPages,
      },
    }),
    [editor.currentPage, editor.totalPages]
  );
}

interface HeaderFooterParagraphEditHandlers {
  registerElement: (draftKey: string, element: HTMLDivElement | null) => void;
  readDraftHtml: (draftKey: string) => string | undefined;
  writeDraftHtml: (draftKey: string, html: string) => void;
  clearDraftHtml: (draftKey: string) => void;
  commitText: (location: DocxSectionParagraphLocation, text: string) => void;
}

interface HeaderFooterRenderEditScope {
  region: DocxSectionRegion;
  partName: string;
  handlers?: HeaderFooterParagraphEditHandlers;
}

interface TableCellParagraphEditHandlers {
  registerElement: (draftKey: string, element: HTMLDivElement | null) => void;
  readDraftHtml: (draftKey: string) => string | undefined;
  writeDraftHtml: (draftKey: string, html: string) => void;
  clearDraftHtml: (draftKey: string) => void;
  commitText: (paragraphIndex: number, text: string) => void;
}

interface TableCellParagraphEditScope {
  isEditable: boolean;
  draftKeyPrefix: string;
  paragraphIndexesByNode: Map<ParagraphNode, number>;
  handlers?: TableCellParagraphEditHandlers;
}

interface HeaderFooterImageInteraction {
  isReadOnly: boolean;
  selectedImageKey?: string;
  floatingMovePreview?: {
    imageKey: string;
    deltaX: number;
    deltaY: number;
    baseLeftPx?: number;
    baseTopPx?: number;
  };
  imageLocationForChild?: (
    childIndex: number
  ) => DocxSectionImageLocation | undefined;
  onImagePointerDown?: (
    event: React.PointerEvent<HTMLElement>,
    location: DocxSectionImageLocation,
    image: ImageRunNode,
    isWrappedFloatingImage: boolean,
    isAbsoluteFloatingImage: boolean
  ) => void;
  onImageClick?: (location: DocxSectionImageLocation) => void;
}

interface EmbeddedTableResizeController {
  isReadOnly: boolean;
  hoveredTableKey?: string;
  focusedTableKey?: string;
  hoveredHandle?: {
    tableKey: string;
    kind: "column" | "row";
    boundaryIndex: number;
  };
  resolveColumnWidths: (
    tableKey: string,
    table: TableNode,
    tableWidthPx?: number
  ) => number[];
  resolveRowHeights: (
    tableKey: string,
    table: TableNode
  ) => Array<number | undefined>;
  isTableResizing: (tableKey: string) => boolean;
  isColumnResizing: (tableKey: string, boundaryColumnIndex: number) => boolean;
  isRowResizing: (tableKey: string, boundaryRowIndex: number) => boolean;
  isCornerResizing: (tableKey: string) => boolean;
  setHoveredTableKey: (tableKey: string | undefined) => void;
  setFocusedTableKey: (tableKey: string | undefined) => void;
  setHoveredHandle: (
    handle:
      | {
          tableKey: string;
          kind: "column" | "row";
          boundaryIndex: number;
        }
      | undefined
  ) => void;
  beginColumnResize: (
    event: React.PointerEvent<HTMLSpanElement>,
    tableKey: string,
    boundaryColumnIndex: number,
    tableWidthPx: number,
    columnWidths: number[]
  ) => void;
  beginRowResize: (
    event: React.PointerEvent<HTMLSpanElement>,
    tableKey: string,
    boundaryRowIndex: number,
    rowHeights: number[]
  ) => void;
  beginCornerResize: (
    event: React.PointerEvent<HTMLSpanElement>,
    tableKey: string,
    tableWidthPx: number,
    columnWidths: number[],
    rowHeights: number[],
    maxTableWidthPx?: number
  ) => void;
  beginTableMove: (
    event: React.PointerEvent<HTMLSpanElement>,
    tableKey: string
  ) => void;
}

function renderHeaderNode(
  node: DocModel["nodes"][number],
  keyPrefix: string,
  documentTheme: DocxDocumentTheme,
  numberingDefinitions: NumberingDefinitionSet | undefined,
  headingStyles?: DocxHeadingStyleMap,
  maxContentWidthPx?: number,
  onInternalLinkClick?: (bookmarkName: string) => void,
  floatingPageOriginPx?: {
    left: number;
    top: number;
    marginLeft?: number;
    marginTop?: number;
    columnLeft?: number;
    columnTop?: number;
    pageWidth?: number;
  },
  noteMarkerIndexes: {
    footnote: Map<number, number>;
    endnote: Map<number, number>;
  } = {
    footnote: new Map<number, number>(),
    endnote: new Map<number, number>(),
  },
  pageNumber?: number,
  totalPages?: number,
  runRenderOptions?: ParagraphRunRenderOptions,
  editScope?: HeaderFooterRenderEditScope,
  imageInteraction?: HeaderFooterImageInteraction,
  tableCellEditScope?: TableCellParagraphEditScope,
  sectionNodeIndex?: number,
  embeddedTableResize?: EmbeddedTableResizeController
): React.JSX.Element {
  const renderHeaderParagraph = (
    paragraphNode: ParagraphNode,
    paragraphKeyPrefix: string,
    paragraphStyle: React.CSSProperties,
    location?: DocxSectionParagraphLocation,
    paragraphRunOptionOverrides?: Partial<ParagraphRunRenderOptions>
  ): React.JSX.Element => {
    const paragraphRunOptions: ParagraphRunRenderOptions = {
      ...(runRenderOptions ?? {}),
      ...(paragraphRunOptionOverrides ?? {}),
      withinHeaderFooter: true,
      headerFooterRegion:
        paragraphRunOptionOverrides?.headerFooterRegion ??
        editScope?.region ??
        runRenderOptions?.headerFooterRegion,
      sectionImageInteraction:
        location && imageInteraction
          ? {
              ...imageInteraction,
              imageLocationForChild: (childIndex) => ({
                ...location,
                childIndex,
              }),
            }
          : undefined,
    };
    const sectionDraftKey = location
      ? sectionParagraphLocationKey(location)
      : undefined;
    const isSectionEditable =
      Boolean(location) &&
      Boolean(editScope?.handlers) &&
      Number.isFinite(sectionNodeIndex) &&
      Number.isFinite(location?.nodeIndex);
    const tableCellParagraphIndex =
      tableCellEditScope?.paragraphIndexesByNode.get(paragraphNode);
    const tableCellDraftKey =
      Number.isFinite(tableCellParagraphIndex) && tableCellEditScope
        ? `${tableCellEditScope.draftKeyPrefix}:${
            tableCellParagraphIndex as number
          }`
        : undefined;
    const isTableCellParagraphEditable =
      Boolean(tableCellEditScope?.handlers) &&
      Boolean(tableCellEditScope?.isEditable) &&
      Number.isFinite(tableCellParagraphIndex);
    const renderedParagraphRuns = renderParagraphRuns(
      paragraphNode,
      paragraphKeyPrefix,
      documentTheme,
      undefined,
      onInternalLinkClick,
      floatingPageOriginPx,
      noteMarkerIndexes,
      pageNumber,
      totalPages,
      paragraphRunOptions
    );

    if (!isSectionEditable && !isTableCellParagraphEditable) {
      return (
        <div
          key={`${paragraphKeyPrefix}-paragraph`}
          data-docx-section-paragraph-key={sectionDraftKey}
          style={paragraphStyle}
        >
          {renderedParagraphRuns}
        </div>
      );
    }

    if (isSectionEditable && location && editScope?.handlers) {
      const sectionEditHandlers = editScope.handlers;
      const resolvedDraftKey =
        sectionDraftKey ?? sectionParagraphLocationKey(location);
      const sectionDraftHtml =
        sectionEditHandlers.readDraftHtml(resolvedDraftKey);
      const renderedSectionParagraphHtml =
        typeof sectionDraftHtml === "string"
          ? sectionDraftHtml
          : renderStaticHtml(renderedParagraphRuns);

      return (
        <div
          key={`${paragraphKeyPrefix}-paragraph`}
          data-docx-section-paragraph-host="true"
          data-docx-section-paragraph-key={resolvedDraftKey}
          data-docx-section-region={location.region}
          data-docx-section-part-name={location.partName}
          data-docx-section-node-index={location.nodeIndex}
          data-docx-section-row-index={location.rowIndex}
          data-docx-section-cell-index={location.cellIndex}
          data-docx-section-paragraph-index={location.paragraphIndex}
          style={{
            ...paragraphStyle,
            outline: "none",
          }}
          dangerouslySetInnerHTML={{
            __html: renderedSectionParagraphHtml,
          }}
          ref={(element) => {
            sectionEditHandlers.registerElement(resolvedDraftKey, element);
            if (!element) {
              return;
            }

            const draftHtml =
              sectionEditHandlers.readDraftHtml(resolvedDraftKey);
            if (typeof draftHtml !== "string") {
              return;
            }

            scheduleDomWrite(() => {
              if (!element.isConnected) {
                return;
              }
              const latestDraftHtml =
                sectionEditHandlers.readDraftHtml(resolvedDraftKey);
              if (latestDraftHtml !== draftHtml) {
                return;
              }
              if (element.innerHTML !== draftHtml) {
                const activeElement = document.activeElement;
                const shouldRestoreSelection =
                  activeElement === element ||
                  (activeElement instanceof Element &&
                    element.contains(activeElement));
                const selectionOffsets = shouldRestoreSelection
                  ? selectionOffsetsWithinElementDom(element)
                  : undefined;
                element.innerHTML = draftHtml;
                if (shouldRestoreSelection) {
                  if (selectionOffsets) {
                    const textLength = editableTextFromElement(element).length;
                    const safeStart = Math.max(
                      0,
                      Math.min(selectionOffsets.start, textLength)
                    );
                    const safeEnd = Math.max(
                      safeStart,
                      Math.min(selectionOffsets.end, textLength)
                    );
                    setSelectionWithinElementByTextOffsetsDom(
                      element,
                      safeStart,
                      safeEnd
                    );
                  } else {
                    placeCaretInsideElementDom(element);
                  }
                }
              }
            });
          }}
        />
      );
    }

    const tableCellDraftHtml =
      tableCellDraftKey && tableCellEditScope?.handlers
        ? tableCellEditScope.handlers.readDraftHtml(tableCellDraftKey)
        : undefined;
    const renderedTableCellParagraphHtml =
      typeof tableCellDraftHtml === "string"
        ? tableCellDraftHtml
        : renderStaticHtml(renderedParagraphRuns);

    return (
      <div
        key={`${paragraphKeyPrefix}-paragraph`}
        contentEditable
        suppressContentEditableWarning
        data-docx-table-cell-paragraph-host="true"
        data-docx-table-cell-paragraph-key={tableCellDraftKey}
        style={{
          ...paragraphStyle,
          outline: "none",
        }}
        dangerouslySetInnerHTML={{
          __html: renderedTableCellParagraphHtml,
        }}
        ref={(element) => {
          const tableCellEditHandlers = tableCellEditScope?.handlers;
          if (!tableCellDraftKey || !tableCellEditHandlers) {
            return;
          }

          tableCellEditHandlers.registerElement(tableCellDraftKey, element);
          if (!element) {
            return;
          }

          const draftHtml =
            tableCellEditHandlers.readDraftHtml(tableCellDraftKey);
          if (typeof draftHtml !== "string") {
            return;
          }

          scheduleDomWrite(() => {
            if (!element.isConnected) {
              return;
            }
            const latestDraftHtml =
              tableCellEditHandlers.readDraftHtml(tableCellDraftKey);
            if (latestDraftHtml !== draftHtml) {
              return;
            }
            if (element.innerHTML !== draftHtml) {
              const activeElement = document.activeElement;
              const shouldRestoreSelection =
                activeElement === element ||
                (activeElement instanceof Element &&
                  element.contains(activeElement));
              const selectionOffsets = shouldRestoreSelection
                ? selectionOffsetsWithinElementDom(element)
                : undefined;
              element.innerHTML = draftHtml;
              if (shouldRestoreSelection) {
                if (selectionOffsets) {
                  const textLength = editableTextFromElement(element).length;
                  const safeStart = Math.max(
                    0,
                    Math.min(selectionOffsets.start, textLength)
                  );
                  const safeEnd = Math.max(
                    safeStart,
                    Math.min(selectionOffsets.end, textLength)
                  );
                  setSelectionWithinElementByTextOffsetsDom(
                    element,
                    safeStart,
                    safeEnd
                  );
                } else {
                  placeCaretInsideElementDom(element);
                }
              }
            }
          });
        }}
        onInput={(event) => {
          if (!tableCellDraftKey || !tableCellEditScope?.handlers) {
            return;
          }
          tableCellEditScope.handlers.writeDraftHtml(
            tableCellDraftKey,
            event.currentTarget.innerHTML
          );
        }}
        onBlur={(event) => {
          if (
            !tableCellEditScope?.handlers ||
            !tableCellDraftKey ||
            !Number.isFinite(tableCellParagraphIndex)
          ) {
            return;
          }

          const nextText = editableTextFromElement(event.currentTarget);
          const currentText = paragraphText(paragraphNode);
          if (nextText === currentText) {
            tableCellEditScope.handlers.clearDraftHtml(tableCellDraftKey);
            return;
          }
          tableCellEditScope.handlers.commitText(
            tableCellParagraphIndex as number,
            nextText
          );
        }}
      />
    );
  };

  if (node.type === "paragraph") {
    const requiresPageAbsoluteContext =
      paragraphNeedsPageAnchoredAbsolutePositioningContext(node);
    const requiresLocalAbsoluteContext =
      paragraphNeedsLocalAbsolutePositioningContext(node);
    return renderHeaderParagraph(
      node,
      keyPrefix,
      {
        ...paragraphBlockStyle(node, numberingDefinitions, headingStyles),
        ...(requiresPageAbsoluteContext
          ? { position: "static" }
          : requiresLocalAbsoluteContext
          ? { position: "relative" }
          : undefined),
      },
      editScope && Number.isFinite(sectionNodeIndex)
        ? {
            region: editScope.region,
            partName: editScope.partName,
            nodeIndex: sectionNodeIndex as number,
          }
        : undefined
    );
  }

  const columnCount = tableColumnCount(node);
  const tableIndentPx = twipsToSignedPixels(node.style?.indentTwips) ?? 0;
  const tableWidthPx = twipsToPixels(node.style?.widthTwips);
  const tableRuntimeKey = keyPrefix;
  const rawTableColumnWidthsPx = (() => {
    if (embeddedTableResize) {
      return embeddedTableResize.resolveColumnWidths(
        tableRuntimeKey,
        node,
        tableWidthPx
      );
    }

    const definedWidthsTwips = columnWidthsFromTableDefinition(
      node,
      columnCount
    );
    if (!definedWidthsTwips || definedWidthsTwips.length === 0) {
      return defaultColumnWidthsPx(columnCount, tableWidthPx);
    }

    const widthsPx = definedWidthsTwips.map(
      (widthTwips) => twipsToPixels(widthTwips) ?? 0
    );
    return normalizeColumnWidthsPx(widthsPx, columnCount, tableWidthPx, 1);
  })();
  const rawResolvedTableWidthPx =
    tableWidthPx ??
    rawTableColumnWidthsPx.reduce((sum, widthPx) => sum + widthPx, 0);
  const collapsedHorizontalBorderBleedPx =
    resolveCollapsedTableHorizontalOuterBleedPx(node, columnCount);
  const maxTableWidthPx =
    Number.isFinite(maxContentWidthPx) && (maxContentWidthPx as number) > 0
      ? Math.max(
          120,
          (maxContentWidthPx as number) -
            tableIndentPx -
            collapsedHorizontalBorderBleedPx
        )
      : undefined;
  const resolvedTableWidthPx = clampTableWidthPx(
    rawResolvedTableWidthPx,
    maxTableWidthPx
  );
  const tableBorderSpacingPx = resolveTableSeparateBorderSpacingPx(node);
  const tableColumnFitWidthPx = tableUsesSeparateBorderModel(node)
    ? Math.max(
        1,
        resolvedTableWidthPx -
          tableBorderSpacingPx * Math.max(0, columnCount + 1)
      )
    : resolvedTableWidthPx;
  const { columnWidthsPx: tableColumnWidthsPx } =
    resolveFittedTableColumnWidths(
      node,
      rawTableColumnWidthsPx,
      tableColumnFitWidthPx
    );
  const tableRowHeightsPx = embeddedTableResize
    ? embeddedTableResize.resolveRowHeights(tableRuntimeKey, node)
    : node.rows.map((row) => twipsToPixels(row.style?.heightTwips));
  const normalizedTableRowHeightsPx = tableRowHeightsPx.map((value) =>
    Number.isFinite(value) && (value as number) > 0
      ? Math.max(24, Math.round(value as number))
      : 24
  );
  const tableColumnBoundaryOffsetsPx = tableColumnWidthsPx.reduce(
    (offsets: number[], widthPx: number) => {
      const previous = offsets[offsets.length - 1] ?? 0;
      offsets.push(previous + widthPx);
      return offsets;
    },
    [] as number[]
  );
  const tableRowBoundaryOffsetsPx = normalizedTableRowHeightsPx.reduce(
    (offsets: number[], heightPx: number) => {
      const previous = offsets[offsets.length - 1] ?? 0;
      offsets.push(previous + heightPx);
      return offsets;
    },
    [] as number[]
  );
  const maxBoundaryColumnIndex = Math.max(0, columnCount - 2);
  const maxBoundaryRowIndex = Math.max(0, node.rows.length - 2);
  const nestedColumnResizing = embeddedTableResize
    ? (boundaryColumnIndex: number) =>
        embeddedTableResize.isColumnResizing(
          tableRuntimeKey,
          boundaryColumnIndex
        )
    : () => false;
  const nestedRowResizing = embeddedTableResize
    ? (boundaryRowIndex: number) =>
        embeddedTableResize.isRowResizing(tableRuntimeKey, boundaryRowIndex)
    : () => false;
  const baseCellMarginTwips = node.style?.cellMarginTwips;
  const tableBorders = node.style?.borders;
  const applyWordTableDefaults = tableUsesWordLikeParagraphDefaults(node);
  const nestedCornerResizing = embeddedTableResize
    ? embeddedTableResize.isCornerResizing(tableRuntimeKey)
    : false;
  const nestedHandlesEnabled = Boolean(
    embeddedTableResize && !embeddedTableResize.isReadOnly
  );
  const showNestedTableHandles =
    nestedHandlesEnabled &&
    Boolean(
      embeddedTableResize?.hoveredTableKey === tableRuntimeKey ||
        embeddedTableResize?.focusedTableKey === tableRuntimeKey ||
        embeddedTableResize?.hoveredHandle?.tableKey === tableRuntimeKey ||
        embeddedTableResize?.isTableResizing(tableRuntimeKey)
    );
  const showNestedTableMoveHandle = showNestedTableHandles;
  const nestedTableMoveHandleToneStyle: React.CSSProperties =
    documentTheme === "dark"
      ? {
          backgroundColor: "#111827",
          border: "1px solid #374151",
          color: "#cbd5e1",
          boxShadow: "0 1px 2px rgba(2, 6, 23, 0.6)",
        }
      : {
          backgroundColor: "#ffffff",
          border: "1px solid #d4d4d8",
          color: "#4b5563",
          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.14)",
        };

  return (
    <div
      key={`${keyPrefix}-table`}
      style={tableWrapperStyle(node, tableIndentPx)}
      onPointerEnter={() => {
        if (!embeddedTableResize || embeddedTableResize.isReadOnly) {
          return;
        }
        embeddedTableResize.setHoveredTableKey(tableRuntimeKey);
      }}
      onPointerLeave={(event) => {
        if (!embeddedTableResize || embeddedTableResize.isReadOnly) {
          return;
        }
        const nextTarget = event.relatedTarget;
        if (
          nextTarget instanceof Node &&
          event.currentTarget.contains(nextTarget)
        ) {
          return;
        }
        if (embeddedTableResize.focusedTableKey === tableRuntimeKey) {
          return;
        }
        embeddedTableResize.setHoveredTableKey(undefined);
      }}
      onFocusCapture={() => {
        if (!embeddedTableResize || embeddedTableResize.isReadOnly) {
          return;
        }
        embeddedTableResize.setFocusedTableKey(tableRuntimeKey);
        embeddedTableResize.setHoveredTableKey(tableRuntimeKey);
      }}
      onBlurCapture={(event) => {
        if (!embeddedTableResize || embeddedTableResize.isReadOnly) {
          return;
        }
        const nextTarget = event.relatedTarget;
        if (
          nextTarget instanceof Node &&
          event.currentTarget.contains(nextTarget)
        ) {
          return;
        }
        if (embeddedTableResize.focusedTableKey === tableRuntimeKey) {
          embeddedTableResize.setFocusedTableKey(undefined);
        }
        embeddedTableResize.setHoveredTableKey(undefined);
      }}
    >
      {showNestedTableHandles ? (
        <>
          <span
            contentEditable={false}
            data-docx-pagination-ignore="true"
            style={{
              position: "absolute",
              top: -TABLE_HANDLE_SAFEZONE_TOP_PX,
              left: -TABLE_HANDLE_SAFEZONE_LEFT_PX,
              width: TABLE_HANDLE_SAFEZONE_LEFT_PX,
              height: TABLE_HANDLE_SAFEZONE_TOP_PX,
              backgroundColor: "transparent",
              zIndex: 4,
            }}
            onPointerEnter={() =>
              embeddedTableResize?.setHoveredTableKey(tableRuntimeKey)
            }
            onMouseEnter={() =>
              embeddedTableResize?.setHoveredTableKey(tableRuntimeKey)
            }
          />
          <span
            contentEditable={false}
            data-docx-pagination-ignore="true"
            style={{
              position: "absolute",
              right: -TABLE_HANDLE_SAFEZONE_RIGHT_PX,
              bottom: -TABLE_HANDLE_SAFEZONE_BOTTOM_PX,
              width:
                TABLE_RESIZE_HANDLE_HIT_SIZE + TABLE_HANDLE_SAFEZONE_RIGHT_PX,
              height:
                TABLE_RESIZE_HANDLE_HIT_SIZE + TABLE_HANDLE_SAFEZONE_BOTTOM_PX,
              backgroundColor: "transparent",
              zIndex: 4,
            }}
            onPointerEnter={() =>
              embeddedTableResize?.setHoveredTableKey(tableRuntimeKey)
            }
            onMouseEnter={() =>
              embeddedTableResize?.setHoveredTableKey(tableRuntimeKey)
            }
          />
        </>
      ) : null}
      {showNestedTableMoveHandle ? (
        <span
          contentEditable={false}
          data-docx-table-move-handle="true"
          title="Nested table"
          style={{
            position: "absolute",
            top: -TABLE_MOVE_HANDLE_HIT_SIZE - 4,
            left: -TABLE_MOVE_HANDLE_HIT_SIZE - 4,
            width: TABLE_MOVE_HANDLE_HIT_SIZE,
            height: TABLE_MOVE_HANDLE_HIT_SIZE,
            display: "grid",
            placeItems: "center",
            cursor: "move",
            zIndex: 7,
            touchAction: "none",
            userSelect: "none",
          }}
          onPointerDown={(event) => {
            embeddedTableResize?.beginTableMove(event, tableRuntimeKey);
          }}
        >
          <span
            style={{
              ...TABLE_MOVE_HANDLE_STYLE,
              ...nestedTableMoveHandleToneStyle,
            }}
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 2v20" />
              <path d="m7 7 5-5 5 5" />
              <path d="m7 17 5 5 5-5" />
              <path d="M2 12h20" />
              <path d="m7 7-5 5 5 5" />
              <path d="m17 7 5 5-5 5" />
            </svg>
          </span>
        </span>
      ) : null}
      <table
        style={{
          width:
            resolvedTableWidthPx > 0 ? `${resolvedTableWidthPx}px` : "100%",
          ...tableElementBorderStyle(node, tableBorderSpacingPx),
          tableLayout: node.style?.layout === "autofit" ? "auto" : "fixed",
        }}
      >
        <colgroup>
          {tableColumnWidthsPx.map((widthPx, columnIndex) => (
            <col
              key={`${keyPrefix}-col-${columnIndex}`}
              style={{ width: `${widthPx}px` }}
            />
          ))}
        </colgroup>
        <tbody>
          {node.rows.map((row, rowIndex) => {
            const rowHeightPx = twipsToPixels(row.style?.heightTwips);
            const resolvedRowHeightStyle = resolveTableRowHeightCss(
              row,
              rowHeightPx
            );
            let columnCursor = 0;

            return (
              <tr
                key={`${keyPrefix}-row-${rowIndex}`}
                style={resolvedRowHeightStyle}
              >
                {row.cells.map((cell, cellIndex) => {
                  const columnSpan =
                    cell.style?.gridSpan && cell.style.gridSpan > 1
                      ? cell.style.gridSpan
                      : 1;
                  const rowSpanValue =
                    cell.style?.rowSpan && cell.style.rowSpan > 1
                      ? cell.style.rowSpan
                      : 1;
                  const startColumnIndex = columnCursor;
                  const endColumnIndex = startColumnIndex + columnSpan - 1;
                  columnCursor += columnSpan;
                  if (cell.style?.vMergeContinuation) {
                    return null;
                  }
                  const cellWidthPx = twipsToPixels(cell.style?.widthTwips);
                  const cellStartOffsetPx =
                    startColumnIndex > 0
                      ? tableColumnBoundaryOffsetsPx[startColumnIndex - 1] ?? 0
                      : 0;
                  const floatingAnchorOriginCorrectionXPx = -(
                    tableIndentPx + cellStartOffsetPx
                  );

                  return (
                    <td
                      key={`${keyPrefix}-cell-${rowIndex}-${cellIndex}`}
                      colSpan={columnSpan > 1 ? columnSpan : undefined}
                      rowSpan={rowSpanValue > 1 ? rowSpanValue : undefined}
                      style={{
                        ...resolveTableCellBorderCss(
                          tableBorders,
                          cell.style?.borders,
                          rowIndex,
                          node.rows.length,
                          startColumnIndex,
                          endColumnIndex,
                          columnCount
                        ),
                        ...resolveTableCellDiagonalOverlayCss(
                          tableBorders,
                          cell.style?.borders
                        ),
                        ...tableSpacingPaddingStyle(
                          mergeTableSpacing(
                            baseCellMarginTwips,
                            cell.style?.marginTwips
                          )
                        ),
                        verticalAlign: cell.style?.verticalAlign ?? "top",
                        backgroundColor:
                          cell.style?.backgroundColor ??
                          row.style?.backgroundColor,
                        minWidth: cellWidthPx ? `${cellWidthPx}px` : 0,
                        width:
                          cellWidthPx && columnSpan === 1
                            ? `${cellWidthPx}px`
                            : undefined,
                        ...resolvedRowHeightStyle,
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                        wordBreak: "break-word",
                      }}
                    >
                      <div style={{ display: "grid", gap: 0 }}>
                        {(() => {
                          let paragraphIndexInCell = 0;
                          return cell.nodes.map((cellContent, contentIndex) => {
                            if (cellContent.type === "paragraph") {
                              const paragraphIndex = paragraphIndexInCell;
                              paragraphIndexInCell += 1;
                              return renderHeaderParagraph(
                                cellContent,
                                `${keyPrefix}-run-${rowIndex}-${cellIndex}-${contentIndex}`,
                                tableCellParagraphBlockStyle(
                                  cellContent,
                                  numberingDefinitions,
                                  headingStyles,
                                  paragraphIndex,
                                  applyWordTableDefaults
                                ),
                                editScope && Number.isFinite(sectionNodeIndex)
                                  ? {
                                      region: editScope.region,
                                      partName: editScope.partName,
                                      nodeIndex: sectionNodeIndex as number,
                                      rowIndex,
                                      cellIndex,
                                      paragraphIndex,
                                    }
                                  : undefined,
                                floatingAnchorOriginCorrectionXPx !== 0
                                  ? {
                                      floatingAnchorOriginCorrectionXPx,
                                    }
                                  : undefined
                              );
                            }

                            return renderHeaderNode(
                              cellContent,
                              `${keyPrefix}-nested-table-${rowIndex}-${cellIndex}-${contentIndex}`,
                              documentTheme,
                              numberingDefinitions,
                              headingStyles,
                              maxContentWidthPx,
                              onInternalLinkClick,
                              floatingPageOriginPx,
                              noteMarkerIndexes,
                              pageNumber,
                              totalPages,
                              runRenderOptions,
                              undefined,
                              imageInteraction,
                              tableCellEditScope,
                              undefined,
                              embeddedTableResize
                            );
                          });
                        })()}
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {showNestedTableHandles && embeddedTableResize && columnCount > 1
        ? tableColumnWidthsPx
            .slice(0, maxBoundaryColumnIndex + 1)
            .map((_, boundaryColumnIndex) => {
              if (boundaryColumnIndex > maxBoundaryColumnIndex) {
                return null;
              }

              const leftPx =
                tableColumnBoundaryOffsetsPx[boundaryColumnIndex] ?? 0;
              const isResizing = nestedColumnResizing(boundaryColumnIndex);
              const isHovered =
                embeddedTableResize.hoveredHandle?.tableKey ===
                  tableRuntimeKey &&
                embeddedTableResize.hoveredHandle.kind === "column" &&
                embeddedTableResize.hoveredHandle.boundaryIndex ===
                  boundaryColumnIndex;
              const isHot = isResizing || isHovered;

              return (
                <span
                  key={`${keyPrefix}-column-border-${boundaryColumnIndex}`}
                  contentEditable={false}
                  style={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: `${
                      leftPx -
                      TABLE_RESIZE_HANDLE_HIT_SIZE / 2 +
                      TABLE_RESIZE_HANDLE_ALIGNMENT_OFFSET_PX
                    }px`,
                    width: TABLE_RESIZE_HANDLE_HIT_SIZE,
                    cursor: "col-resize",
                    zIndex: 5,
                    touchAction: "none",
                    userSelect: "none",
                    backgroundColor: "transparent",
                  }}
                  onMouseEnter={() =>
                    embeddedTableResize.setHoveredHandle({
                      tableKey: tableRuntimeKey,
                      kind: "column",
                      boundaryIndex: boundaryColumnIndex,
                    })
                  }
                  onPointerEnter={() =>
                    embeddedTableResize.setHoveredHandle({
                      tableKey: tableRuntimeKey,
                      kind: "column",
                      boundaryIndex: boundaryColumnIndex,
                    })
                  }
                  onMouseLeave={() => {
                    if (
                      embeddedTableResize.hoveredHandle?.tableKey ===
                        tableRuntimeKey &&
                      embeddedTableResize.hoveredHandle.kind === "column" &&
                      embeddedTableResize.hoveredHandle.boundaryIndex ===
                        boundaryColumnIndex
                    ) {
                      embeddedTableResize.setHoveredHandle(undefined);
                    }
                  }}
                  onPointerLeave={() => {
                    if (
                      embeddedTableResize.hoveredHandle?.tableKey ===
                        tableRuntimeKey &&
                      embeddedTableResize.hoveredHandle.kind === "column" &&
                      embeddedTableResize.hoveredHandle.boundaryIndex ===
                        boundaryColumnIndex
                    ) {
                      embeddedTableResize.setHoveredHandle(undefined);
                    }
                  }}
                  onPointerDown={(event) => {
                    const tableElement =
                      event.currentTarget.parentElement?.querySelector("table");
                    const tableElementWidthPx = tableElement
                      ? tableElement.getBoundingClientRect().width
                      : 0;
                    embeddedTableResize.beginColumnResize(
                      event,
                      tableRuntimeKey,
                      boundaryColumnIndex,
                      tableElementWidthPx,
                      tableColumnWidthsPx
                    );
                    embeddedTableResize.setHoveredHandle(undefined);
                  }}
                >
                  <span
                    style={{
                      position: "absolute",
                      left: "50%",
                      top: 0,
                      transform: "translateX(-50%)",
                      width: TABLE_RESIZE_BORDER_SIZE,
                      height: "100%",
                      backgroundColor: isHot ? "#3b82f6" : "transparent",
                    }}
                  />
                </span>
              );
            })
        : null}
      {showNestedTableHandles && embeddedTableResize && node.rows.length > 1
        ? tableRowHeightsPx
            .slice(0, maxBoundaryRowIndex + 1)
            .map((_, boundaryRowIndex) => {
              if (boundaryRowIndex > maxBoundaryRowIndex) {
                return null;
              }

              const topPx = tableRowBoundaryOffsetsPx[boundaryRowIndex] ?? 0;
              const isResizing = nestedRowResizing(boundaryRowIndex);
              const isHovered =
                embeddedTableResize.hoveredHandle?.tableKey ===
                  tableRuntimeKey &&
                embeddedTableResize.hoveredHandle.kind === "row" &&
                embeddedTableResize.hoveredHandle.boundaryIndex ===
                  boundaryRowIndex;
              const isHot = isResizing || isHovered;

              return (
                <span
                  key={`${keyPrefix}-row-border-${boundaryRowIndex}`}
                  contentEditable={false}
                  style={{
                    position: "absolute",
                    left: 0,
                    width: `${resolvedTableWidthPx}px`,
                    top: `${
                      topPx -
                      TABLE_RESIZE_HANDLE_HIT_SIZE / 2 +
                      TABLE_RESIZE_HANDLE_ALIGNMENT_OFFSET_PX
                    }px`,
                    height: TABLE_RESIZE_HANDLE_HIT_SIZE,
                    cursor: "row-resize",
                    zIndex: 5,
                    touchAction: "none",
                    userSelect: "none",
                    backgroundColor: "transparent",
                  }}
                  onMouseEnter={() =>
                    embeddedTableResize.setHoveredHandle({
                      tableKey: tableRuntimeKey,
                      kind: "row",
                      boundaryIndex: boundaryRowIndex,
                    })
                  }
                  onPointerEnter={() =>
                    embeddedTableResize.setHoveredHandle({
                      tableKey: tableRuntimeKey,
                      kind: "row",
                      boundaryIndex: boundaryRowIndex,
                    })
                  }
                  onMouseLeave={() => {
                    if (
                      embeddedTableResize.hoveredHandle?.tableKey ===
                        tableRuntimeKey &&
                      embeddedTableResize.hoveredHandle.kind === "row" &&
                      embeddedTableResize.hoveredHandle.boundaryIndex ===
                        boundaryRowIndex
                    ) {
                      embeddedTableResize.setHoveredHandle(undefined);
                    }
                  }}
                  onPointerLeave={() => {
                    if (
                      embeddedTableResize.hoveredHandle?.tableKey ===
                        tableRuntimeKey &&
                      embeddedTableResize.hoveredHandle.kind === "row" &&
                      embeddedTableResize.hoveredHandle.boundaryIndex ===
                        boundaryRowIndex
                    ) {
                      embeddedTableResize.setHoveredHandle(undefined);
                    }
                  }}
                  onPointerDown={(event) => {
                    const tableElement =
                      event.currentTarget.parentElement?.querySelector("table");
                    const measuredRows = tableElement
                      ? Array.from(
                          tableElement.querySelectorAll("tbody > tr")
                        ).map((rowElement) =>
                          normalizeMeasuredTableRowHeightPx(
                            (
                              rowElement as HTMLTableRowElement
                            ).getBoundingClientRect().height
                          )
                        )
                      : [];
                    const startingHeights =
                      measuredRows.length > 0
                        ? measuredRows
                        : tableRowHeightsPx.map((heightPx) => heightPx ?? 24);
                    embeddedTableResize.beginRowResize(
                      event,
                      tableRuntimeKey,
                      boundaryRowIndex,
                      startingHeights
                    );
                    embeddedTableResize.setHoveredHandle(undefined);
                  }}
                >
                  <span
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: 0,
                      right: 0,
                      transform: "translateY(-50%)",
                      width: "100%",
                      height: TABLE_RESIZE_BORDER_SIZE,
                      backgroundColor: isHot ? "#3b82f6" : "transparent",
                    }}
                  />
                </span>
              );
            })
        : null}
      {showNestedTableHandles && embeddedTableResize ? (
        <span
          contentEditable={false}
          style={{
            position: "absolute",
            left: `${Math.max(
              0,
              resolvedTableWidthPx -
                TABLE_RESIZE_HANDLE_HIT_SIZE / 2 +
                TABLE_RESIZE_HANDLE_ALIGNMENT_OFFSET_PX
            )}px`,
            bottom: -8,
            width: TABLE_RESIZE_HANDLE_HIT_SIZE,
            height: TABLE_RESIZE_HANDLE_HIT_SIZE,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            cursor: "nwse-resize",
            opacity: nestedCornerResizing ? 1 : 1,
            zIndex: 6,
            touchAction: "none",
          }}
          onPointerDown={(event) => {
            const tableElement =
              event.currentTarget.parentElement?.querySelector("table");
            const handleTableWidthPx = tableElement
              ? tableElement.getBoundingClientRect().width
              : 0;
            const measuredRows = tableElement
              ? Array.from(tableElement.querySelectorAll("tbody > tr")).map(
                  (rowElement) =>
                    normalizeMeasuredTableRowHeightPx(
                      (
                        rowElement as HTMLTableRowElement
                      ).getBoundingClientRect().height
                    )
                )
              : [];
            const currentHeights =
              measuredRows.length > 0
                ? measuredRows
                : tableRowHeightsPx.map((rowHeightPx) => rowHeightPx ?? 24);

            embeddedTableResize.beginCornerResize(
              event,
              tableRuntimeKey,
              handleTableWidthPx,
              tableColumnWidthsPx,
              currentHeights,
              maxTableWidthPx
            );
          }}
        >
          <span
            style={{
              ...TABLE_RESIZE_HANDLE_STYLE,
              position: "absolute",
              right: 0,
              bottom: 0,
            }}
          />
        </span>
      ) : null}
    </div>
  );
}

export function DocxEditorViewer({
  editor,
  className,
  style,
  pageBackgroundColor,
  pageGapBackgroundColor,
  deferInitialPaginationPaint = false,
  loadingState,
  pageVirtualization,
  visiblePageRange,
  onPageCountChange,
  onRequestPageReveal,
  headingStyles,
  showTrackedChanges,
  renderTrackedChangeCard,
  showComments,
  renderCommentCard,
  renderTableContextMenu,
  renderContextMenu,
  onFormFieldDoubleClick,
  mode = "edit",
}: DocxEditorViewerProps): React.JSX.Element {
  const pageSurfaceRegistryOwner = docxViewerPageSurfaceRegistryOwner(editor);
  const trackedChangesEnabled = showTrackedChanges ?? editor.showTrackedChanges;
  const hasTrackedChanges = editor.trackedChanges.length > 0;
  const commentsEnabled = showComments ?? editor.showComments;
  const hasComments = editor.comments.length > 0;
  // Comments share the tracked-change gutter: cards for both annotation kinds
  // stack in the same margin column.
  const showTrackedChangeGutter = trackedChangesEnabled || commentsEnabled;
  const isReadOnly = mode === "read-only" || trackedChangesEnabled;
  const isNightReaderMode = isReadOnly && editor.documentTheme === "dark";
  const documentContentTheme: DocxDocumentTheme = isNightReaderMode
    ? "light"
    : editor.documentTheme;
  const documentContentFilter = isNightReaderMode
    ? NIGHT_READER_INVERSION_FILTER
    : undefined;
  const canReplaceDocumentByDrop = mode !== "read-only";
  const formFieldLocationKey = React.useCallback(
    (location: DocxFormFieldLocation): string => {
      return location.kind === "paragraph"
        ? `p:${location.nodeIndex}:${location.childIndex}`
        : `t:${location.tableIndex}:${location.rowIndex}:${location.cellIndex}:${location.paragraphIndex}:${location.childIndex}`;
    },
    []
  );
  const tocLinkColorByLevel = React.useMemo(
    () => collectHeadingTextColorByLevel(editor.model),
    [editor.model]
  );
  const paragraphRunRenderOptions = React.useMemo<ParagraphRunRenderOptions>(
    () => ({
      showTrackedChanges: trackedChangesEnabled,
      showCommentHighlights: commentsEnabled,
      numberingDefinitions: editor.model.metadata.numberingDefinitions,
      tocLinkColorByLevel,
      imageFilterSuffix: documentContentFilter,
    }),
    [
      commentsEnabled,
      documentContentFilter,
      editor.model.metadata.numberingDefinitions,
      tocLinkColorByLevel,
      trackedChangesEnabled,
    ]
  );
  const viewerRootRef = React.useRef<HTMLDivElement>(null);
  const paragraphDraftsRef = React.useRef<Map<number, string>>(new Map());
  const tableCellDraftsRef = React.useRef<Map<string, string>>(new Map());
  const tableCellParagraphDraftsRef = React.useRef<Map<string, string>>(
    new Map()
  );
  const contextMenuClipboardTextRef = React.useRef("");
  const sectionParagraphDraftsRef = React.useRef<Map<string, string>>(
    new Map()
  );
  const pendingParagraphSegmentFocusRef = React.useRef<
    | {
        nodeIndex: number;
        startLineIndex: number;
        endLineIndex: number;
        point?: {
          x: number;
          y: number;
        };
      }
    | undefined
  >(undefined);
  const paragraphElementsRef = React.useRef<Map<number, HTMLDivElement>>(
    new Map()
  );
  const [
    measuredParagraphOuterHeightsPxByNodeIndex,
    setMeasuredParagraphOuterHeightsPxByNodeIndex,
  ] = React.useState<Map<number, number>>(() => new Map());
  const tableCellEditorElementsRef = React.useRef<Map<string, HTMLDivElement>>(
    new Map()
  );
  const tableCellParagraphElementsRef = React.useRef<
    Map<string, HTMLDivElement>
  >(new Map());
  const sectionParagraphElementsRef = React.useRef<Map<string, HTMLDivElement>>(
    new Map()
  );
  const tableElementsRef = React.useRef<Map<number, HTMLDivElement>>(new Map());
  const pageHeaderElementsRef = React.useRef<Map<number, HTMLDivElement>>(
    new Map()
  );
  const pageBodyElementsRef = React.useRef<Map<number, HTMLDivElement>>(
    new Map()
  );
  const pageFooterElementsRef = React.useRef<Map<number, HTMLDivElement>>(
    new Map()
  );
  const pageElementsRef = React.useRef<Map<number, HTMLDivElement>>(new Map());
  const pageSurfaceRefCallbacksRef = React.useRef<
    Map<number, React.RefCallback<HTMLDivElement>>
  >(new Map());
  const trackedChangeCardElementsRef = React.useRef<
    Map<string, HTMLDivElement>
  >(new Map());
  const initialPaginationStableSignatureRef = React.useRef<string | undefined>(
    undefined
  );
  const initialPaginationRevealFrameRef = React.useRef<number | null>(null);
  const pendingHeaderFooterDeactivateFrameRef = React.useRef<number | null>(
    null
  );
  const appliedHistoryRestoreNonceRef = React.useRef(0);
  const pendingTableCellFocusRef = React.useRef<
    | {
        cellElementKey: string;
        point?: {
          x: number;
          y: number;
        };
        boundary?: DocxTextRangeBoundary;
        paragraphIndex?: number;
      }
    | undefined
  >(undefined);
  const pendingSectionParagraphFocusRef = React.useRef<
    | {
        draftKey: string;
        point?: {
          x: number;
          y: number;
        };
      }
    | undefined
  >(undefined);
  const draggedImageRef = React.useRef<DocxImageLocation | null>(null);
  const previousBodyNodeCountRef = React.useRef<number>(
    editor.model.nodes.length
  );
  const fileDragDepthRef = React.useRef(0);
  const tableDraftLayoutRefreshRafRef = React.useRef<number | null>(null);
  const activeRangeFlushFrameRef = React.useRef<number | null>(null);
  // The last known-good DOM selection (collapsed caret or expanded range) within
  // a single editable paragraph host, captured synchronously before React
  // re-applies `dangerouslySetInnerHTML` and destroys it. The selection
  // invariant restores from this (not the debounced model range, which lags),
  // so freshly typed text isn't reversed and a drag selection isn't erased when
  // a re-render clobbers the contentEditable.
  const pendingEditableCaretRef = React.useRef<{
    nodeIndex: number;
    start: number;
    end: number;
  } | null>(null);
  // Whether a pointer-driven selection drag is currently in progress (button
  // down and moved). While true, the DOM selection is authoritative and the
  // invariant must not touch it; once the pointer is released we may restore.
  const pointerSelectionDragRef = React.useRef<{
    down: boolean;
    moved: boolean;
  }>({ down: false, moved: false });
  const deferredCollapsedSelectionSyncTimeoutRef = React.useRef<number | null>(
    null
  );
  const pendingSelectionIntentFrameRef = React.useRef<number | null>(null);
  const selectionIntentNonceRef = React.useRef(0);
  const lastFormFieldClickRef = React.useRef<
    | {
        key: string;
        time: number;
      }
    | undefined
  >(undefined);

  const [isDragOverCanvas, setIsDragOverCanvas] = React.useState(false);
  const [isDraggingImage, setIsDraggingImage] = React.useState(false);
  const [activeDropTarget, setActiveDropTarget] = React.useState<
    DocxImageDropTarget | undefined
  >();
  const [selectedImage, setSelectedImage] = React.useState<
    DocxImageLocation | undefined
  >();
  const [selectedSectionImageKey, setSelectedSectionImageKey] = React.useState<
    string | undefined
  >();
  const [selectedDropCapNodeIndex, setSelectedDropCapNodeIndex] =
    React.useState<number | undefined>();
  const [hoveredDropCapNodeIndex, setHoveredDropCapNodeIndex] = React.useState<
    number | undefined
  >();
  const [activeWrappedParagraphSession, setActiveWrappedParagraphSession] =
    React.useState<WrappedParagraphEditingSession | undefined>();
  const activeWrappedParagraphSessionRef = React.useRef<
    WrappedParagraphEditingSession | undefined
  >(undefined);
  const [resizePreview, setResizePreview] = React.useState<
    | {
        imageKey: string;
        widthPx: number;
        heightPx: number;
      }
    | undefined
  >();
  const [floatingMovePreview, setFloatingMovePreview] = React.useState<
    | {
        imageKey: string;
        deltaX: number;
        deltaY: number;
        baseLeftPx?: number;
        baseTopPx?: number;
      }
    | undefined
  >();
  const [dropCapMovePreview, setDropCapMovePreview] = React.useState<
    | {
        nodeIndex: number;
        deltaX: number;
        deltaY: number;
      }
    | undefined
  >();
  const [dropCapResizePreview, setDropCapResizePreview] = React.useState<
    | {
        nodeIndex: number;
        fontSizePx: number;
      }
    | undefined
  >();
  const [tableColumnWidths, setTableColumnWidths] = React.useState<
    Record<number, number[]>
  >({});
  const [embeddedTableColumnWidths, setEmbeddedTableColumnWidths] =
    React.useState<Record<string, number[]>>({});
  const handleRepeatedFormFieldClick = React.useCallback(
    (location: DocxFormFieldLocation): boolean => {
      const now =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      const key = formFieldLocationKey(location);
      const previousClick = lastFormFieldClickRef.current;
      if (
        previousClick &&
        previousClick.key === key &&
        now - previousClick.time <= 350
      ) {
        lastFormFieldClickRef.current = undefined;
        editor.selectFormField(location);
        onFormFieldDoubleClick?.(location);
        return true;
      }

      lastFormFieldClickRef.current = { key, time: now };
      return false;
    },
    [editor.selectFormField, formFieldLocationKey, onFormFieldDoubleClick]
  );
  const [activeColumnResize, setActiveColumnResize] = React.useState<
    { tableIndex: number; boundaryColumnIndex: number } | undefined
  >();
  const [activeEmbeddedColumnResize, setActiveEmbeddedColumnResize] =
    React.useState<
      { tableKey: string; boundaryColumnIndex: number } | undefined
    >();
  const [tableRowHeights, setTableRowHeights] = React.useState<
    Record<number, number[]>
  >({});
  const [embeddedTableRowHeights, setEmbeddedTableRowHeights] = React.useState<
    Record<string, number[]>
  >({});
  const [tableMeasuredRowHeights, setTableMeasuredRowHeights] = React.useState<
    Record<number, number[]>
  >({});
  const [
    measuredPageContentHeightByIndex,
    setMeasuredPageContentHeightByIndex,
  ] = React.useState<number[]>([]);
  const pageSurfaceRefForIndex = React.useCallback(
    (pageIndex: number): React.RefCallback<HTMLDivElement> => {
      const normalizedPageIndex = Math.max(0, Math.round(pageIndex));
      const cached =
        pageSurfaceRefCallbacksRef.current.get(normalizedPageIndex);
      if (cached) {
        return cached;
      }

      let registeredElement: HTMLDivElement | null = null;
      const nextRef: React.RefCallback<HTMLDivElement> = (element) => {
        if (element) {
          registeredElement = element;
          pageElementsRef.current.set(normalizedPageIndex, element);
        } else {
          pageElementsRef.current.delete(normalizedPageIndex);
        }
        registerDocxViewerPageSurface(
          editor,
          normalizedPageIndex,
          element,
          registeredElement
        );
        if (!element) {
          registeredElement = null;
        }
      };
      pageSurfaceRefCallbacksRef.current.set(normalizedPageIndex, nextRef);
      return nextRef;
    },
    [pageSurfaceRegistryOwner]
  );
  React.useEffect(() => {
    pageSurfaceRefCallbacksRef.current.clear();
  }, [pageSurfaceRegistryOwner]);
  const [
    measuredPageContentIdentityKeysByIndex,
    setMeasuredPageContentIdentityKeysByIndex,
  ] = React.useState<string[]>([]);
  const [hasMeasuredBodyFooterOverlap, setHasMeasuredBodyFooterOverlap] =
    React.useState(false);
  const measuredBodyFooterOverlapCandidateRef = React.useRef<{
    signature?: string;
    consecutivePasses: number;
  }>({
    signature: undefined,
    consecutivePasses: 0,
  });
  const [, setRenderableImageSourceRevision] = React.useState(0);
  const [activeEditableParagraphSegment, setActiveEditableParagraphSegment] =
    React.useState<ParagraphSegmentIdentity | undefined>(undefined);
  const wrappedParagraphTextareaRef = React.useRef<HTMLTextAreaElement | null>(
    null
  );
  const wrappedParagraphPendingSelectionRef = React.useRef<
    | {
        locationKey: string;
        start: number;
        end: number;
      }
    | undefined
  >(undefined);
  const wrappedParagraphSelectionDragRef = React.useRef<
    WrappedParagraphSelectionDragState | undefined
  >(undefined);
  const wrappedParagraphSurfaceRegistryRef = React.useRef<
    Map<string, WrappedParagraphSurfaceRegistration>
  >(new Map());
  const [isInitialPaginationSettled, setIsInitialPaginationSettled] =
    React.useState(!deferInitialPaginationPaint);
  const [
    initialPaginationBackgroundRefinementUnlocked,
    setInitialPaginationBackgroundRefinementUnlocked,
  ] = React.useState(!deferInitialPaginationPaint);
  const [postImportPaginationUnlocked, setPostImportPaginationUnlocked] =
    React.useState(false);
  const [
    initialPaginationReservedLayoutState,
    setInitialPaginationReservedLayoutState,
  ] = React.useState<{
    pageCount: number;
    totalHeightPx: number;
  }>({
    pageCount: 0,
    totalHeightPx: 0,
  });
  const [disableMeasuredImportPagination, setDisableMeasuredImportPagination] =
    React.useState(false);
  const initialPaginationPageCountOscillationRef = React.useRef<{
    lastPageCount?: number;
    changeCount: number;
    distinctPageCounts: Set<number>;
  }>({
    changeCount: 0,
    distinctPageCounts: new Set<number>(),
  });
  React.useEffect(() => {
    return subscribeRenderableImageSourceUpdates(() => {
      setRenderableImageSourceRevision((value) => value + 1);
    });
  }, []);
  React.useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    const styleId = "docx-wrapped-caret-style";
    let styleElement = document.getElementById(
      styleId
    ) as HTMLStyleElement | null;
    if (!styleElement) {
      styleElement = document.createElement("style");
      styleElement.id = styleId;
      styleElement.textContent = `
        @keyframes docxWrappedCaretBlink {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
      `;
      document.head.appendChild(styleElement);
    }
  }, []);

  React.useEffect(() => {
    // Import/new-document should start from a clean viewer layout state.
    // Stale per-page/per-table measurements from a previous document can
    // otherwise leak into pagination and footer placement.
    setMeasuredPageContentHeightByIndex([]);
    setMeasuredPageContentIdentityKeysByIndex([]);
    setHasMeasuredBodyFooterOverlap(false);
    measuredBodyFooterOverlapCandidateRef.current = {
      signature: undefined,
      consecutivePasses: 0,
    };
    setTableMeasuredRowHeights({});
    setTableRowHeights({});
    setTableColumnWidths({});
    setEmbeddedTableColumnWidths({});
    setEmbeddedTableRowHeights({});
    setSelectedImage(undefined);
    setSelectedSectionImageKey(undefined);
    setSelectedDropCapNodeIndex(undefined);
    setHoveredDropCapNodeIndex(undefined);
    setActiveWrappedParagraphSession(undefined);
    setResizePreview(undefined);
    setFloatingMovePreview(undefined);
    setDropCapMovePreview(undefined);
    setDropCapResizePreview(undefined);
    setActiveDropTarget(undefined);
    setIsDraggingImage(false);
    setActiveHeaderFooterEdit(undefined);
    paragraphDraftsRef.current.clear();
    tableCellDraftsRef.current.clear();
    tableCellParagraphDraftsRef.current.clear();
    sectionParagraphDraftsRef.current.clear();
    pendingParagraphSegmentFocusRef.current = undefined;
    pendingTableCellFocusRef.current = undefined;
    pendingSectionParagraphFocusRef.current = undefined;
    setActiveEditableParagraphSegment(undefined);
    setPostImportPaginationUnlocked(false);
    setInitialPaginationReservedLayoutState({
      pageCount: 0,
      totalHeightPx: 0,
    });
    setDisableMeasuredImportPagination(false);
    initialPaginationPageCountOscillationRef.current = {
      changeCount: 0,
      distinctPageCounts: new Set<number>(),
    };
  }, [editor.documentLoadNonce]);
  React.useEffect(() => {
    initialPaginationStableSignatureRef.current = undefined;
    if (
      initialPaginationRevealFrameRef.current !== null &&
      typeof window !== "undefined" &&
      typeof window.cancelAnimationFrame === "function"
    ) {
      window.cancelAnimationFrame(initialPaginationRevealFrameRef.current);
    }
    initialPaginationRevealFrameRef.current = null;
    setIsInitialPaginationSettled(!deferInitialPaginationPaint);
    setInitialPaginationBackgroundRefinementUnlocked(
      !deferInitialPaginationPaint
    );
  }, [deferInitialPaginationPaint, editor.documentLoadNonce]);
  const [activeRowResize, setActiveRowResize] = React.useState<
    { tableIndex: number; boundaryRowIndex: number } | undefined
  >();
  const [activeEmbeddedRowResize, setActiveEmbeddedRowResize] = React.useState<
    { tableKey: string; boundaryRowIndex: number } | undefined
  >();
  const [activeTableResize, setActiveTableResize] = React.useState<
    { tableIndex: number } | undefined
  >();
  const [activeEmbeddedTableResize, setActiveEmbeddedTableResize] =
    React.useState<{ tableKey: string } | undefined>();
  const [hoveredTableResizeHandle, setHoveredTableResizeHandle] =
    React.useState<
      | {
          tableIndex: number;
          kind: "column" | "row";
          boundaryIndex: number;
        }
      | undefined
    >();
  const [hoveredTableHandleTableIndex, setHoveredTableHandleTableIndex] =
    React.useState<number | undefined>();
  const [
    hoveredEmbeddedTableResizeHandle,
    setHoveredEmbeddedTableResizeHandle,
  ] = React.useState<
    | {
        tableKey: string;
        kind: "column" | "row";
        boundaryIndex: number;
      }
    | undefined
  >();
  const [hoveredEmbeddedTableKey, setHoveredEmbeddedTableKey] = React.useState<
    string | undefined
  >();
  const [focusedEmbeddedTableKey, setFocusedEmbeddedTableKey] = React.useState<
    string | undefined
  >();
  const tableSelectionDragRef = React.useRef<
    | {
        pointerId: number;
        startBoundary: DocxTextRangeBoundary;
        startX: number;
        startY: number;
        hasMoved: boolean;
        explicitSelectionActive: boolean;
      }
    | undefined
  >(undefined);
  const tableCellSelectionDragRef = React.useRef<
    | {
        pointerId: number;
        anchorCell: DocxTableCellLocation;
        startBoundary?: DocxTextRangeBoundary;
        startX: number;
        startY: number;
        isSelectingCells: boolean;
        isSelectingAcrossNodes: boolean;
      }
    | undefined
  >(undefined);
  const tableMoveDragRef = React.useRef<
    | {
        pointerId: number;
        tableIndex: number;
        embeddedTableKey?: string;
        startX: number;
        startY: number;
        hasMoved: boolean;
      }
    | undefined
  >(undefined);
  const suppressNextTableCellClickRef = React.useRef(false);
  const [tableCellSelectionRange, setTableCellSelectionRange] = React.useState<
    DocxTableCellSelectionRange | undefined
  >();
  const [tableMoveDropPreview, setTableMoveDropPreview] = React.useState<
    | {
        top: number;
        left: number;
        width: number;
      }
    | undefined
  >();
  const [tableContextMenuState, setTableContextMenuState] = React.useState<
    | (DocxTableContextMenuContext & {
        clientX: number;
        clientY: number;
      })
    | undefined
  >();
  const [contextMenuState, setContextMenuState] = React.useState<
    | (DocxContextMenuContext & {
        clientX: number;
        clientY: number;
      })
    | undefined
  >();
  const [activeHeaderFooterEdit, setActiveHeaderFooterEdit] = React.useState<
    | {
        region: DocxSectionRegion;
        partName: string;
        pageIndex: number;
      }
    | undefined
  >();
  const [tableDraftLayoutEpoch, bumpTableDraftLayoutEpoch] = React.useReducer(
    (value: number) => value + 1,
    0
  );
  const [paragraphStructureEpoch, bumpParagraphStructureEpoch] =
    React.useReducer((value: number) => value + 1, 0);
  const [paginationMeasurementEpoch, bumpPaginationMeasurementEpoch] =
    React.useReducer((value: number) => value + 1, 0);
  const paginationMeasurementSuspendUntilRef = React.useRef(0);
  const paginationMeasurementResumeTimeoutRef = React.useRef<number | null>(
    null
  );
  React.useLayoutEffect(() => {
    setMeasuredParagraphOuterHeightsPxByNodeIndex(new Map());
  }, [editor.documentLoadNonce, paragraphStructureEpoch]);

  const schedulePaginationMeasurementResume = React.useCallback(
    (delayMs?: number): void => {
      if (typeof window === "undefined") {
        return;
      }

      setPostImportPaginationUnlocked(true);
      const safeDelayMs = Math.max(
        48,
        Math.round(delayMs ?? PAGINATION_MEASUREMENT_INTERACTION_DEBOUNCE_MS)
      );
      paginationMeasurementSuspendUntilRef.current = Date.now() + safeDelayMs;

      if (paginationMeasurementResumeTimeoutRef.current !== null) {
        window.clearTimeout(paginationMeasurementResumeTimeoutRef.current);
      }

      paginationMeasurementResumeTimeoutRef.current = window.setTimeout(() => {
        paginationMeasurementResumeTimeoutRef.current = null;
        bumpPaginationMeasurementEpoch();
      }, safeDelayMs + 16);
    },
    []
  );

  const requestTableDraftLayoutRefresh = React.useCallback((): void => {
    if (
      typeof window === "undefined" ||
      typeof window.requestAnimationFrame !== "function"
    ) {
      bumpTableDraftLayoutEpoch();
      return;
    }

    if (tableDraftLayoutRefreshRafRef.current !== null) {
      return;
    }

    tableDraftLayoutRefreshRafRef.current = window.requestAnimationFrame(() => {
      tableDraftLayoutRefreshRafRef.current = null;
      bumpTableDraftLayoutEpoch();
    });
  }, []);

  React.useEffect(() => {
    return () => {
      if (
        tableDraftLayoutRefreshRafRef.current !== null &&
        typeof window !== "undefined" &&
        typeof window.cancelAnimationFrame === "function"
      ) {
        window.cancelAnimationFrame(tableDraftLayoutRefreshRafRef.current);
      }
      tableDraftLayoutRefreshRafRef.current = null;
      if (
        initialPaginationRevealFrameRef.current !== null &&
        typeof window !== "undefined" &&
        typeof window.cancelAnimationFrame === "function"
      ) {
        window.cancelAnimationFrame(initialPaginationRevealFrameRef.current);
      }
      initialPaginationRevealFrameRef.current = null;
      if (
        pendingHeaderFooterDeactivateFrameRef.current !== null &&
        typeof window !== "undefined" &&
        typeof window.cancelAnimationFrame === "function"
      ) {
        window.cancelAnimationFrame(
          pendingHeaderFooterDeactivateFrameRef.current
        );
      }
      pendingHeaderFooterDeactivateFrameRef.current = null;
      if (
        paginationMeasurementResumeTimeoutRef.current !== null &&
        typeof window !== "undefined"
      ) {
        window.clearTimeout(paginationMeasurementResumeTimeoutRef.current);
      }
      paginationMeasurementResumeTimeoutRef.current = null;
      if (
        activeRangeFlushFrameRef.current !== null &&
        typeof window !== "undefined" &&
        typeof window.cancelAnimationFrame === "function"
      ) {
        window.cancelAnimationFrame(activeRangeFlushFrameRef.current);
      }
      activeRangeFlushFrameRef.current = null;
      if (
        deferredCollapsedSelectionSyncTimeoutRef.current !== null &&
        typeof window !== "undefined" &&
        typeof window.clearTimeout === "function"
      ) {
        window.clearTimeout(deferredCollapsedSelectionSyncTimeoutRef.current);
      }
      deferredCollapsedSelectionSyncTimeoutRef.current = null;
      if (
        pendingSelectionIntentFrameRef.current !== null &&
        typeof window !== "undefined" &&
        typeof window.cancelAnimationFrame === "function"
      ) {
        window.cancelAnimationFrame(pendingSelectionIntentFrameRef.current);
      }
      pendingSelectionIntentFrameRef.current = null;
    };
  }, []);

  React.useEffect(() => {
    if (!isReadOnly) {
      return;
    }

    setIsDragOverCanvas(false);
    setIsDraggingImage(false);
    setActiveDropTarget(undefined);
    setSelectedImage(undefined);
    setSelectedSectionImageKey(undefined);
    setSelectedDropCapNodeIndex(undefined);
    setHoveredDropCapNodeIndex(undefined);
    setActiveWrappedParagraphSession(undefined);
    setResizePreview(undefined);
    setFloatingMovePreview(undefined);
    setDropCapMovePreview(undefined);
    setActiveColumnResize(undefined);
    setActiveEmbeddedColumnResize(undefined);
    setActiveRowResize(undefined);
    setActiveEmbeddedRowResize(undefined);
    setActiveTableResize(undefined);
    setActiveEmbeddedTableResize(undefined);
    setHoveredTableResizeHandle(undefined);
    setHoveredTableHandleTableIndex(undefined);
    setHoveredEmbeddedTableResizeHandle(undefined);
    setHoveredEmbeddedTableKey(undefined);
    setFocusedEmbeddedTableKey(undefined);
    setTableMoveDropPreview(undefined);
    tableMoveDragRef.current = undefined;
    setTableContextMenuState(undefined);
    setContextMenuState(undefined);
    setActiveHeaderFooterEdit(undefined);
    sectionParagraphDraftsRef.current.clear();
    pendingSectionParagraphFocusRef.current = undefined;
  }, [isReadOnly]);

  const beginSelectionIntent = React.useCallback((): number => {
    if (
      pendingSelectionIntentFrameRef.current !== null &&
      typeof window !== "undefined" &&
      typeof window.cancelAnimationFrame === "function"
    ) {
      window.cancelAnimationFrame(pendingSelectionIntentFrameRef.current);
    }
    pendingSelectionIntentFrameRef.current = null;
    const nextNonce = selectionIntentNonceRef.current + 1;
    selectionIntentNonceRef.current = nextNonce;
    return nextNonce;
  }, []);

  const isSelectionIntentCurrent = React.useCallback(
    (nonce: number): boolean => {
      return selectionIntentNonceRef.current === nonce;
    },
    []
  );

  const scheduleSelectionIntentFrame = React.useCallback(
    (nonce: number, callback: () => void): void => {
      if (
        pendingSelectionIntentFrameRef.current !== null &&
        typeof window !== "undefined" &&
        typeof window.cancelAnimationFrame === "function"
      ) {
        window.cancelAnimationFrame(pendingSelectionIntentFrameRef.current);
      }
      pendingSelectionIntentFrameRef.current = null;

      if (
        typeof window === "undefined" ||
        typeof window.requestAnimationFrame !== "function"
      ) {
        if (selectionIntentNonceRef.current === nonce) {
          callback();
        }
        return;
      }

      pendingSelectionIntentFrameRef.current = window.requestAnimationFrame(
        () => {
          pendingSelectionIntentFrameRef.current = null;
          if (selectionIntentNonceRef.current !== nonce) {
            return;
          }
          callback();
        }
      );
    },
    []
  );

  const cancelPendingSelectionIntent = React.useCallback((): void => {
    beginSelectionIntent();
  }, [beginSelectionIntent]);

  React.useEffect(() => {
    const previousNodeCount = previousBodyNodeCountRef.current;
    const nextNodeCount = editor.model.nodes.length;
    const structureChanged = previousNodeCount !== nextNodeCount;

    if (structureChanged) {
      paragraphDraftsRef.current.clear();
      tableCellDraftsRef.current.clear();
      tableCellParagraphDraftsRef.current.clear();
      pendingTableCellFocusRef.current = undefined;
      bumpParagraphStructureEpoch();
    }

    paragraphDraftsRef.current.forEach((_, nodeIndex) => {
      const node = editor.model.nodes[nodeIndex];
      if (!node || node.type !== "paragraph") {
        paragraphDraftsRef.current.delete(nodeIndex);
      }
    });

    tableCellDraftsRef.current.forEach((_, draftKey) => {
      const [tableIndexRaw, rowIndexRaw, cellIndexRaw] = draftKey.split(":");
      const tableIndex = Number.parseInt(tableIndexRaw ?? "", 10);
      const rowIndex = Number.parseInt(rowIndexRaw ?? "", 10);
      const cellIndex = Number.parseInt(cellIndexRaw ?? "", 10);
      if (
        !Number.isFinite(tableIndex) ||
        !Number.isFinite(rowIndex) ||
        !Number.isFinite(cellIndex)
      ) {
        tableCellDraftsRef.current.delete(draftKey);
        return;
      }

      const tableNode = editor.model.nodes[tableIndex];
      const cell =
        tableNode && tableNode.type === "table"
          ? tableNode.rows[rowIndex]?.cells[cellIndex]
          : undefined;
      if (!cell) {
        tableCellDraftsRef.current.delete(draftKey);
      }
    });

    tableCellParagraphDraftsRef.current.forEach((_, draftKey) => {
      const [tableIndexRaw, rowIndexRaw, cellIndexRaw, paragraphIndexRaw] =
        draftKey.split(":");
      const tableIndex = Number.parseInt(tableIndexRaw ?? "", 10);
      const rowIndex = Number.parseInt(rowIndexRaw ?? "", 10);
      const cellIndex = Number.parseInt(cellIndexRaw ?? "", 10);
      const paragraphIndex = Number.parseInt(paragraphIndexRaw ?? "", 10);
      if (
        !Number.isFinite(tableIndex) ||
        !Number.isFinite(rowIndex) ||
        !Number.isFinite(cellIndex) ||
        !Number.isFinite(paragraphIndex)
      ) {
        tableCellParagraphDraftsRef.current.delete(draftKey);
        return;
      }

      const tableNode = editor.model.nodes[tableIndex];
      const cell =
        tableNode && tableNode.type === "table"
          ? tableNode.rows[rowIndex]?.cells[cellIndex]
          : undefined;
      if (!cell) {
        tableCellParagraphDraftsRef.current.delete(draftKey);
        return;
      }

      const paragraphs = tableCellParagraphsRecursively(cell.nodes);
      if (!paragraphs[paragraphIndex]) {
        tableCellParagraphDraftsRef.current.delete(draftKey);
      }
    });

    previousBodyNodeCountRef.current = nextNodeCount;
  }, [editor.model.nodes]);

  const documentSections = React.useMemo(
    () => resolveDocumentSectionsFromMetadata(editor.model.metadata),
    [editor.model.metadata]
  );
  const primarySectionPropertiesXml =
    documentSections[0]?.sectionPropertiesXml ??
    editor.model.metadata.sectionPropertiesXml;
  const documentLayout = React.useMemo(
    () => parseSectionLayout(primarySectionPropertiesXml),
    [primarySectionPropertiesXml]
  );
  const paginationSectionMetrics = React.useMemo(
    () => buildPaginationSectionMetrics(documentSections, documentLayout),
    [documentLayout, documentSections]
  );
  const {
    docGridLinePitchPxByNodeIndex,
    pageContentWidthPxByNodeIndex,
    pageContentHeightPxByNodeIndex,
  } = React.useMemo(() => {
    const pitchByNodeIndex = new Map<number, number>();
    const widthByNodeIndex = new Map<number, number>();
    const heightByNodeIndex = new Map<number, number>();
    if (
      editor.model.nodes.length === 0 ||
      paginationSectionMetrics.length === 0
    ) {
      return {
        docGridLinePitchPxByNodeIndex: pitchByNodeIndex,
        pageContentWidthPxByNodeIndex: widthByNodeIndex,
        pageContentHeightPxByNodeIndex: heightByNodeIndex,
      };
    }

    let currentMetricsIndex = 0;
    for (
      let nodeIndex = 0;
      nodeIndex < editor.model.nodes.length;
      nodeIndex += 1
    ) {
      currentMetricsIndex = resolvePaginationSectionMetricsIndexForNodeIndex(
        paginationSectionMetrics,
        nodeIndex,
        currentMetricsIndex
      );
      const nodeMetrics = paginationSectionMetrics[currentMetricsIndex];
      const docGridLinePitchPx = nodeMetrics?.docGridLinePitchPx;
      if (
        Number.isFinite(docGridLinePitchPx) &&
        (docGridLinePitchPx as number) > 0
      ) {
        pitchByNodeIndex.set(
          nodeIndex,
          Math.round(docGridLinePitchPx as number)
        );
      }
      const pageContentWidthPx = nodeMetrics?.pageContentWidthPx;
      if (
        Number.isFinite(pageContentWidthPx) &&
        (pageContentWidthPx as number) > 0
      ) {
        widthByNodeIndex.set(
          nodeIndex,
          Math.round(pageContentWidthPx as number)
        );
      }
      const pageContentHeightPx = nodeMetrics?.pageContentHeightPx;
      if (
        Number.isFinite(pageContentHeightPx) &&
        (pageContentHeightPx as number) > 0
      ) {
        heightByNodeIndex.set(
          nodeIndex,
          Math.round(pageContentHeightPx as number)
        );
      }
    }

    return {
      docGridLinePitchPxByNodeIndex: pitchByNodeIndex,
      pageContentWidthPxByNodeIndex: widthByNodeIndex,
      pageContentHeightPxByNodeIndex: heightByNodeIndex,
    };
  }, [editor.model.nodes, paginationSectionMetrics]);
  const sectionColumnsBySectionIndex = React.useMemo(
    () =>
      documentSections.map((section) =>
        parseSectionColumns(section.sectionPropertiesXml)
      ),
    [documentSections]
  );
  const paragraphNumberingLabels = React.useMemo(
    () => buildParagraphNumberingLabels(editor.model),
    [editor.model]
  );
  const bookmarkTargetNodeIndexByName = React.useMemo(() => {
    const targets = new Map<string, number>();
    editor.model.nodes.forEach((node, nodeIndex) => {
      if (node.type === "paragraph") {
        for (const bookmarkName of paragraphBookmarkNames(node)) {
          if (!targets.has(bookmarkName)) {
            targets.set(bookmarkName, nodeIndex);
          }
        }
        return;
      }

      node.rows.forEach((row) => {
        row.cells.forEach((cell) => {
          tableCellParagraphsRecursively(cell.nodes).forEach((paragraph) => {
            for (const bookmarkName of paragraphBookmarkNames(paragraph)) {
              if (!targets.has(bookmarkName)) {
                targets.set(bookmarkName, nodeIndex);
              }
            }
          });
        });
      });
    });
    return targets;
  }, [editor.model.nodes]);
  const tableMeasuredRowHeightsForPagination = React.useMemo(() => {
    const allowMeasuredImportPagination =
      !editor.canUndo && !editor.canRedo && !disableMeasuredImportPagination;
    const activeDraftKeys = [
      ...tableCellDraftsRef.current.keys(),
      ...tableCellParagraphDraftsRef.current.keys(),
    ];

    return resolveTableMeasuredRowHeightsForPagination(
      editor.model.nodes,
      tableMeasuredRowHeights,
      {
        allowMeasuredImportPagination,
        activeDraftKeys,
        numberingDefinitions: editor.model.metadata.numberingDefinitions,
        pageContentWidthPxByNodeIndex,
        pageContentHeightPxByNodeIndex,
        docGridLinePitchPxByNodeIndex,
      }
    );
  }, [
    docGridLinePitchPxByNodeIndex,
    editor.canRedo,
    editor.canUndo,
    editor.model.metadata.numberingDefinitions,
    editor.model.nodes,
    pageContentHeightPxByNodeIndex,
    pageContentWidthPxByNodeIndex,
    disableMeasuredImportPagination,
    tableDraftLayoutEpoch,
    tableMeasuredRowHeights,
  ]);
  const pageSegmentationPlan = React.useMemo<{
    pages: DocumentPageNodeSegment[][];
    useMeasuredPageContentHeightsForRender: boolean;
    renderMeasuredPageContentHeightsPxByPageIndex?: number[];
    renderPageContentHeightScale?: number;
  }>(() => {
    const pageContentHeightPx = Math.max(
      120,
      documentLayout.pageHeightPx -
        documentLayout.marginsPx.top -
        documentLayout.marginsPx.bottom
    );
    const pageContentWidthPx = Math.max(
      120,
      documentLayout.pageWidthPx -
        documentLayout.marginsPx.left -
        documentLayout.marginsPx.right
    );
    const suppressSpacingBeforeAfterPageBreak =
      editor.model.metadata.compatibility
        ?.suppressSpacingBeforeAfterPageBreak === true;
    const preferLastRenderedParagraphStartBreaks =
      !editor.canUndo && !editor.canRedo;
    const canUseMeasuredPageContentHeights =
      !editor.canUndo &&
      !editor.canRedo &&
      !disableMeasuredImportPagination &&
      !floatingMovePreview &&
      !dropCapMovePreview &&
      (measuredPageContentHeightByIndex?.length ?? 0) > 0;
    const buildEstimatedPages = (
      measuredTableRowHeightsByNodeIndex?: Record<number, number[]>,
      measuredPageContentHeightsOverride?: number[] | null,
      paginationOptions?: {
        strictLastRenderedParagraphStartBreaks?: boolean;
      }
    ): DocumentPageNodeSegment[][] => {
      const allowMeasuredPageContentHeights =
        !editor.canUndo &&
        !editor.canRedo &&
        !disableMeasuredImportPagination &&
        !floatingMovePreview &&
        !dropCapMovePreview;
      const baseMeasuredHeights =
        !allowMeasuredPageContentHeights ||
        measuredPageContentHeightsOverride === null
          ? undefined
          : measuredPageContentHeightsOverride ??
            measuredPageContentHeightByIndex;
      const buildPagesWithHeights = (
        resolvedPageContentHeights?: number[]
      ): DocumentPageNodeSegment[][] =>
        buildDocumentPageNodeSegments(
          editor.model,
          pageContentHeightPx,
          pageContentWidthPx,
          editor.model.metadata.numberingDefinitions,
          paginationSectionMetrics,
          {
            allowParagraphLineSplitting: true,
            suppressSpacingBeforeAfterPageBreak,
            preferLastRenderedParagraphStartBreaks,
            strictLastRenderedParagraphStartBreaks:
              paginationOptions?.strictLastRenderedParagraphStartBreaks ?? false,
            measuredTableRowHeightsByNodeIndex,
            measuredPageContentHeightsPxByPageIndex: resolvedPageContentHeights,
            measuredParagraphOuterHeightsPxByNodeIndex,
          }
        );
      const basePages = buildPagesWithHeights(baseMeasuredHeights);
      return applyEstimatedFootnoteReserveToPages(
        editor.model,
        basePages,
        pageContentHeightPx,
        pageContentWidthPx,
        paginationSectionMetrics,
        editor.model.metadata.numberingDefinitions,
        editor.model.metadata.footnotes ?? [],
        (pageContentHeightsOverride) =>
          buildPagesWithHeights(pageContentHeightsOverride)
      );
    };
    const storedDocumentPageCount = editor.model.metadata.documentPageCount;
    const hardBreakCount = collectDocxHardPageBreakStartNodeIndexes(
      editor.model
    ).size;
    const hasLastRenderedPageBreakHints = editor.model.nodes.some(
      (node) =>
        node.type === "paragraph" && paragraphHasLastRenderedPageBreak(node)
    );
    const renderedBreakHintPageCount = hasLastRenderedPageBreakHints
      ? 1 +
        editor.model.nodes.reduce(
          (count, node) =>
            count +
            (node.type === "paragraph" &&
            paragraphHasLastRenderedPageBreak(node)
              ? 1
              : 0),
          0
        )
      : undefined;
    const hasMultiColumnRenderedPageBreakHints = editor.model.nodes.some(
      (node, nodeIndex) => {
        if (
          node.type !== "paragraph" ||
          !paragraphStartsWithLastRenderedPageBreak(node)
        ) {
          return false;
        }

        const metricsIndex = resolvePaginationSectionMetricsIndexForNodeIndex(
          paginationSectionMetrics,
          nodeIndex,
          0
        );
        return (
          (paginationSectionMetrics[metricsIndex]
            ?.pageContentHeightMultiplier ?? 1) > 1
        );
      }
    );
    const minimumPageCount = Math.max(1, hardBreakCount + 1);
    // Stored page counts steer reconciliation only for ordinary page flows.
    // Documents with multi-column sections pair our approximate virtual-column
    // pagination with often-fabricated generator page counts; chasing the
    // stored number there compresses content off the page bottom.
    const hasColumnFlowSections = paginationSectionMetrics.some(
      (metrics) => (metrics.pageContentHeightMultiplier ?? 1) > 1
    );
    const targetPageCount =
      !hasColumnFlowSections &&
      Number.isFinite(storedDocumentPageCount) &&
      (storedDocumentPageCount as number) > 0
        ? Math.max(
            minimumPageCount,
            Math.round(storedDocumentPageCount as number)
          )
        : undefined;
    let estimatedPages = buildEstimatedPages(
      tableMeasuredRowHeightsForPagination
    );
    let estimatedPagesUseMeasuredPageContentHeightsForRender =
      canUseMeasuredPageContentHeights;
    let estimatedRenderMeasuredPageContentHeightsPxByPageIndex =
      canUseMeasuredPageContentHeights
        ? measuredPageContentHeightByIndex
        : undefined;
    let estimatedRenderPageContentHeightScale: number | undefined = undefined;
    const deferExpensiveInitialPaginationReconciliation =
      deferInitialPaginationPaint &&
      !initialPaginationBackgroundRefinementUnlocked;
    if (deferExpensiveInitialPaginationReconciliation) {
      return {
        pages: estimatedPages,
        useMeasuredPageContentHeightsForRender:
          estimatedPagesUseMeasuredPageContentHeightsForRender,
        renderMeasuredPageContentHeightsPxByPageIndex:
          estimatedRenderMeasuredPageContentHeightsPxByPageIndex,
        renderPageContentHeightScale: estimatedRenderPageContentHeightScale,
      };
    }
    if (
      measuredPageContentHeightByIndex &&
      measuredPageContentHeightByIndex.length > 0
    ) {
      const pureEstimatedPages = buildEstimatedPages(
        tableMeasuredRowHeightsForPagination,
        null
      );
      const measuredPagesAreOnlySplitParagraphs =
        estimatedPages.length > 0 &&
        estimatedPages.every((pageSegments) =>
          documentPageContainsOnlySplitParagraphSegments(pageSegments)
        );
      const purePagesAreOnlySplitParagraphs =
        pureEstimatedPages.length > 0 &&
        pureEstimatedPages.every((pageSegments) =>
          documentPageContainsOnlySplitParagraphSegments(pageSegments)
        );
      if (
        measuredPagesAreOnlySplitParagraphs &&
        purePagesAreOnlySplitParagraphs &&
        pureEstimatedPages.length < estimatedPages.length
      ) {
        estimatedPages = pureEstimatedPages;
        estimatedPagesUseMeasuredPageContentHeightsForRender = false;
        estimatedRenderMeasuredPageContentHeightsPxByPageIndex = undefined;
        estimatedRenderPageContentHeightScale = undefined;
      }
    }
    if (
      !editor.canUndo &&
      !editor.canRedo &&
      targetPageCount !== undefined &&
      tableMeasuredRowHeightsForPagination &&
      !hasMultiColumnRenderedPageBreakHints
    ) {
      const candidatePages = [
        {
          pages: estimatedPages,
          useMeasuredPageContentHeightsForRender:
            estimatedPagesUseMeasuredPageContentHeightsForRender,
          renderMeasuredPageContentHeightsPxByPageIndex:
            estimatedRenderMeasuredPageContentHeightsPxByPageIndex,
          renderPageContentHeightScale: estimatedRenderPageContentHeightScale,
        },
        {
          pages: buildEstimatedPages(undefined),
          useMeasuredPageContentHeightsForRender:
            canUseMeasuredPageContentHeights,
          renderMeasuredPageContentHeightsPxByPageIndex:
            canUseMeasuredPageContentHeights
              ? measuredPageContentHeightByIndex
              : undefined,
          renderPageContentHeightScale: undefined,
        },
        {
          pages: buildEstimatedPages(undefined, null),
          useMeasuredPageContentHeightsForRender: false,
          renderMeasuredPageContentHeightsPxByPageIndex: undefined,
          renderPageContentHeightScale: undefined,
        },
      ];
      const bestCandidate = candidatePages.reduce((best, candidate) => {
        const bestDifference = Math.abs(best.pages.length - targetPageCount);
        const candidateDifference = Math.abs(
          candidate.pages.length - targetPageCount
        );
        return candidateDifference < bestDifference ? candidate : best;
      });
      estimatedPages = bestCandidate.pages;
      estimatedPagesUseMeasuredPageContentHeightsForRender =
        bestCandidate.useMeasuredPageContentHeightsForRender;
      estimatedRenderMeasuredPageContentHeightsPxByPageIndex =
        bestCandidate.renderMeasuredPageContentHeightsPxByPageIndex;
      estimatedRenderPageContentHeightScale =
        bestCandidate.renderPageContentHeightScale;
    } else if (
      !editor.canUndo &&
      !editor.canRedo &&
      targetPageCount !== undefined &&
      !hasMultiColumnRenderedPageBreakHints
    ) {
      const pureEstimatedPages = buildEstimatedPages(undefined, null);
      if (
        Math.abs(pureEstimatedPages.length - targetPageCount) <
        Math.abs(estimatedPages.length - targetPageCount)
      ) {
        estimatedPages = pureEstimatedPages;
        estimatedPagesUseMeasuredPageContentHeightsForRender = false;
        estimatedRenderMeasuredPageContentHeightsPxByPageIndex = undefined;
        estimatedRenderPageContentHeightScale = undefined;
      }
    }
    const measuredPageHeightOverridesReduceContent =
      !editor.canUndo &&
      !editor.canRedo &&
      !floatingMovePreview &&
      !dropCapMovePreview &&
      estimatedPages.some((segments, pageIndex) => {
        const measuredHeightPx = measuredPageContentHeightByIndex?.[pageIndex];
        if (!Number.isFinite(measuredHeightPx)) {
          return false;
        }

        const firstNodeIndex = segments[0]?.nodeIndex ?? 0;
        const metricsIndex = resolvePaginationSectionMetricsIndexForNodeIndex(
          paginationSectionMetrics,
          firstNodeIndex,
          0
        );
        const sectionHeightPx =
          paginationSectionMetrics[metricsIndex]?.pageContentHeightPx ??
          pageContentHeightPx;
        return (measuredHeightPx as number) < sectionHeightPx - 4;
      });
    const hasPageAnchoredFloatingFooterContent = documentSections.some(
      (section) =>
        (section.footerSections ?? []).some((footerSection) =>
          (footerSection.nodes ?? []).some(
            (node) =>
              node.type === "paragraph" &&
              paragraphHasPageAnchoredForegroundAbsoluteFloatingImage(node)
          )
        )
    );
    if (
      !Number.isFinite(storedDocumentPageCount) ||
      (storedDocumentPageCount as number) <= 0
    ) {
      return {
        pages: estimatedPages,
        useMeasuredPageContentHeightsForRender:
          estimatedPagesUseMeasuredPageContentHeightsForRender,
        renderMeasuredPageContentHeightsPxByPageIndex:
          estimatedRenderMeasuredPageContentHeightsPxByPageIndex,
        renderPageContentHeightScale: estimatedRenderPageContentHeightScale,
      };
    }
    const resolvedTargetPageCount = targetPageCount ?? minimumPageCount;

    const hasTableInternalPageBreaks = editor.model.nodes.some(
      (node) =>
        node.type === "table" &&
        collectTableExplicitPageBreakInfo(node).startRowIndexes.some(
          (rowIndex) => rowIndex > 0
        )
    );
    const allowMeasuredReductionOverPaginationReconciliation =
      measuredPageHeightOverridesReduceContent &&
      estimatedPages.length > resolvedTargetPageCount;
    const tableDominatedDocument =
      editor.model.nodes.length > 0 &&
      editor.model.nodes.filter((node) => node.type === "table").length /
        editor.model.nodes.length >=
        0.4;
    const estimatedPagesIncludeTableRowSplits = estimatedPages.some(
      (segments) =>
        segments.some((segment) => {
          const tableNode = editor.model.nodes[segment.nodeIndex];
          if (!segment.tableRowRange || tableNode?.type !== "table") {
            return false;
          }

          return (
            segment.tableRowRange.endRowIndex -
              segment.tableRowRange.startRowIndex <
            tableNode.rows.length
          );
        })
    );
    const deferTableStoredPageCountScaleReconciliation =
      tableDominatedDocument &&
      estimatedPagesIncludeTableRowSplits &&
      !tableMeasuredRowHeightsForPagination &&
      estimatedPages.length > resolvedTargetPageCount;
    const deferSinglePageScaleUpReconciliation =
      resolvedTargetPageCount === 1 &&
      estimatedPages.length === 2 &&
      !tableMeasuredRowHeightsForPagination &&
      !hasLastRenderedPageBreakHints &&
      !(
        Number.isFinite(storedDocumentPageCount) &&
        (storedDocumentPageCount as number) === 1
      );
    const suppressMeasuredBodyFooterOverlapForStoredSinglePage =
      resolvedTargetPageCount === 1 &&
      Number.isFinite(storedDocumentPageCount) &&
      (storedDocumentPageCount as number) === 1;
    const applyStoredSinglePageImportEstimate = (
      pages: DocumentPageNodeSegment[][],
      renderOptions: {
        useMeasuredPageContentHeightsForRender: boolean;
        renderMeasuredPageContentHeightsPxByPageIndex?: number[];
        renderPageContentHeightScale?: number;
      }
    ) => {
      if (!suppressMeasuredBodyFooterOverlapForStoredSinglePage) {
        return {
          pages,
          ...renderOptions,
        };
      }

      const singlePageEstimate = buildEstimatedPages(undefined, null);
      if (singlePageEstimate.length !== 1 || pages.length <= 1) {
        return {
          pages,
          ...renderOptions,
        };
      }

      return {
        pages: singlePageEstimate,
        useMeasuredPageContentHeightsForRender: false,
        renderMeasuredPageContentHeightsPxByPageIndex: undefined,
        renderPageContentHeightScale: undefined,
      };
    };
    const allowStoredPageCountReconciliation =
      !editor.canUndo &&
      !editor.canRedo &&
      !hasPageAnchoredFloatingFooterContent &&
      !hasMultiColumnRenderedPageBreakHints &&
      !deferTableStoredPageCountScaleReconciliation &&
      !deferSinglePageScaleUpReconciliation &&
      shouldAllowStoredPageCountReduction({
        estimatedPageCount: estimatedPages.length,
        targetPageCount: resolvedTargetPageCount,
        hasLastRenderedPageBreakHints,
        renderedBreakHintPageCount,
        hasMeasuredBodyFooterOverlap: suppressMeasuredBodyFooterOverlapForStoredSinglePage
          ? false
          : hasMeasuredBodyFooterOverlap,
      }) &&
      (!measuredPageHeightOverridesReduceContent ||
        allowMeasuredReductionOverPaginationReconciliation);
    if (!allowStoredPageCountReconciliation) {
      return applyStoredSinglePageImportEstimate(estimatedPages, {
        useMeasuredPageContentHeightsForRender:
          estimatedPagesUseMeasuredPageContentHeightsForRender,
        renderMeasuredPageContentHeightsPxByPageIndex:
          estimatedRenderMeasuredPageContentHeightsPxByPageIndex,
        renderPageContentHeightScale: estimatedRenderPageContentHeightScale,
      });
    }

    if (estimatedPages.length === resolvedTargetPageCount) {
      return {
        pages: estimatedPages,
        useMeasuredPageContentHeightsForRender:
          estimatedPagesUseMeasuredPageContentHeightsForRender,
        renderMeasuredPageContentHeightsPxByPageIndex:
          estimatedRenderMeasuredPageContentHeightsPxByPageIndex,
      };
    }

    if (
      hasLastRenderedPageBreakHints &&
      renderedBreakHintPageCount === resolvedTargetPageCount
    ) {
      const hintAlignedPages = applyEstimatedFootnoteReserveToPages(
        editor.model,
        buildDocumentPageNodeSegmentsFromLastRenderedPageBreakHints(
          editor.model
        ),
        pageContentHeightPx,
        pageContentWidthPx,
        paginationSectionMetrics,
        editor.model.metadata.numberingDefinitions,
        editor.model.metadata.footnotes ?? [],
        (pageContentHeightsOverride) =>
          buildEstimatedPages(
            tableMeasuredRowHeightsForPagination,
            pageContentHeightsOverride
          )
      );
      if (hintAlignedPages.length === resolvedTargetPageCount) {
        return {
          pages: hintAlignedPages,
          useMeasuredPageContentHeightsForRender:
            canUseMeasuredPageContentHeights,
          renderMeasuredPageContentHeightsPxByPageIndex:
            canUseMeasuredPageContentHeights
              ? measuredPageContentHeightByIndex
              : undefined,
          renderPageContentHeightScale: undefined,
        };
      }
    }

    const reconciliationScales =
      allowMeasuredReductionOverPaginationReconciliation
        ? [
            1.02, 1.04, 1.06, 1.08, 1.1, 1.12, 1.14, 1.16, 1.18, 1.2, 1.22,
            1.24, 1.26, 1.28, 1.3,
          ]
        : undefined;
    const reconcileCandidatePages = (
      initialPages: DocumentPageNodeSegment[][],
      measuredTableRowHeightsByNodeIndex?: Record<number, number[]>,
      measuredPageContentHeightsOverride?: number[] | null
    ) =>
      reconcilePageCountCandidateToTargetCountByScalingHeight({
        initialPages,
        targetPageCount: resolvedTargetPageCount,
        maxDifference: hasTableInternalPageBreaks ? 2 : 3,
        scales: reconciliationScales,
        buildPagesAtScale: (heightScale) =>
          buildDocumentPageNodeSegments(
            editor.model,
            Math.max(120, Math.round(pageContentHeightPx * heightScale)),
            pageContentWidthPx,
            editor.model.metadata.numberingDefinitions,
            scalePaginationSectionMetricsHeights(
              paginationSectionMetrics,
              heightScale
            ),
            {
              allowParagraphLineSplitting: true,
              suppressSpacingBeforeAfterPageBreak,
              preferLastRenderedParagraphStartBreaks,
              measuredTableRowHeightsByNodeIndex,
              measuredPageContentHeightsPxByPageIndex:
                measuredPageContentHeightsOverride === null
                  ? undefined
                  : scaleMeasuredPageContentHeights(
                      measuredPageContentHeightsOverride ??
                        measuredPageContentHeightByIndex,
                      heightScale
                    ),
              measuredParagraphOuterHeightsPxByNodeIndex,
            }
          ),
      });

    let reconciledCandidate = reconcileCandidatePages(
      estimatedPages,
      tableMeasuredRowHeightsForPagination,
      estimatedPagesUseMeasuredPageContentHeightsForRender
        ? measuredPageContentHeightByIndex
        : null
    );
    let reconciledPages = reconciledCandidate.pages;
    let reconciledPagesUseMeasuredPageContentHeightsForRender =
      estimatedPagesUseMeasuredPageContentHeightsForRender;
    let reconciledRenderMeasuredPageContentHeightsPxByPageIndex =
      reconciledPagesUseMeasuredPageContentHeightsForRender
        ? scaleMeasuredPageContentHeights(
            measuredPageContentHeightByIndex,
            reconciledCandidate.scale
          )
        : undefined;
    let reconciledRenderPageContentHeightScale: number | undefined = undefined;
    if (!editor.canUndo && !editor.canRedo) {
      const pureEstimatedPages = buildEstimatedPages(undefined, null);
      const pureReconciledCandidate = reconcileCandidatePages(
        pureEstimatedPages,
        undefined,
        null
      );
      const pureReconciledPages = pureReconciledCandidate.pages;
      if (
        Math.abs(pureReconciledPages.length - resolvedTargetPageCount) <
        Math.abs(reconciledPages.length - resolvedTargetPageCount)
      ) {
        reconciledPages = pureReconciledPages;
        reconciledPagesUseMeasuredPageContentHeightsForRender = false;
        reconciledRenderMeasuredPageContentHeightsPxByPageIndex = undefined;
        reconciledRenderPageContentHeightScale = undefined;
      }
    }

    if (
      resolvedTargetPageCount === 1 &&
      Number.isFinite(storedDocumentPageCount) &&
      (storedDocumentPageCount as number) === 1
    ) {
      const correctedSinglePagePlan = applyStoredSinglePageImportEstimate(
        reconciledPages,
        {
          useMeasuredPageContentHeightsForRender:
            reconciledPagesUseMeasuredPageContentHeightsForRender,
          renderMeasuredPageContentHeightsPxByPageIndex:
            reconciledRenderMeasuredPageContentHeightsPxByPageIndex,
          renderPageContentHeightScale: reconciledRenderPageContentHeightScale,
        }
      );
      reconciledPages = correctedSinglePagePlan.pages;
      reconciledPagesUseMeasuredPageContentHeightsForRender =
        correctedSinglePagePlan.useMeasuredPageContentHeightsForRender;
      reconciledRenderMeasuredPageContentHeightsPxByPageIndex =
        correctedSinglePagePlan.renderMeasuredPageContentHeightsPxByPageIndex;
      reconciledRenderPageContentHeightScale =
        correctedSinglePagePlan.renderPageContentHeightScale;
    }

    return {
      pages: reconciledPages,
      useMeasuredPageContentHeightsForRender:
        reconciledPagesUseMeasuredPageContentHeightsForRender,
      renderMeasuredPageContentHeightsPxByPageIndex:
        reconciledRenderMeasuredPageContentHeightsPxByPageIndex,
      renderPageContentHeightScale: reconciledRenderPageContentHeightScale,
    };
  }, [
    editor.canRedo,
    editor.canUndo,
    editor.model,
    editor.model.metadata.documentPageCount,
    editor.model.metadata.endnotes,
    documentLayout.pageHeightPx,
    documentLayout.pageWidthPx,
    documentLayout.marginsPx.bottom,
    documentLayout.marginsPx.left,
    documentLayout.marginsPx.right,
    documentLayout.marginsPx.top,
    editor.model.metadata.numberingDefinitions,
    paginationSectionMetrics,
    isReadOnly,
    floatingMovePreview,
    dropCapMovePreview,
    deferInitialPaginationPaint,
    disableMeasuredImportPagination,
    hasMeasuredBodyFooterOverlap,
    initialPaginationBackgroundRefinementUnlocked,
    measuredPageContentHeightByIndex,
    measuredParagraphOuterHeightsPxByNodeIndex,
    tableMeasuredRowHeightsForPagination,
  ]);
  const pageNodeSegmentsByPage = pageSegmentationPlan.pages;
  const useMeasuredPageContentHeightsForRender =
    pageSegmentationPlan.useMeasuredPageContentHeightsForRender;
  const renderMeasuredPageContentHeightsPxByPageIndex =
    pageSegmentationPlan.renderMeasuredPageContentHeightsPxByPageIndex;
  const renderPageContentHeightScale =
    pageSegmentationPlan.renderPageContentHeightScale;
  const pageNodeSegmentIdentityKeysByPage = React.useMemo(
    () =>
      pageNodeSegmentsByPage.map((pageSegments) =>
        documentPageNodeSegmentsIdentityKey(pageSegments)
      ),
    [pageNodeSegmentsByPage]
  );
  const pageIndexByNodeIndex = React.useMemo(() => {
    const indexByNode = new Map<number, number>();
    pageNodeSegmentsByPage.forEach((segments, pageIndex) => {
      segments.forEach((segment) => {
        if (!indexByNode.has(segment.nodeIndex)) {
          indexByNode.set(segment.nodeIndex, pageIndex);
        }
      });
    });
    return indexByNode;
  }, [pageNodeSegmentsByPage]);
  const storedDocumentPageCount = React.useMemo(() => {
    const value = editor.model.metadata.documentPageCount;
    if (!Number.isFinite(value) || (value as number) <= 0) {
      return undefined;
    }

    return Math.max(1, Math.round(value as number));
  }, [editor.model.metadata.documentPageCount]);
  const pageSectionInfoByIndex = React.useMemo(() => {
    if (pageNodeSegmentsByPage.length === 0) {
      return [] as Array<{
        section: ResolvedDocumentSection;
        sectionIndex: number;
        pageIndexWithinSection: number;
        layout: DocumentLayoutMetrics;
        pageNumber: number;
      }>;
    }

    const sections =
      documentSections.length > 0
        ? documentSections
        : [
            {
              startNodeIndex: 0,
              sectionPropertiesXml: editor.model.metadata.sectionPropertiesXml,
              headerSections: editor.model.metadata.headerSections,
              footerSections: editor.model.metadata.footerSections,
            },
          ];
    const firstRenderedPageIndexBySectionIndex = new Map<number, number>();
    sections.forEach((section, sectionIndex) => {
      const startNodeIndex = Math.max(0, Math.round(section.startNodeIndex));
      const startPageIndex = pageIndexByNodeIndex.get(startNodeIndex);
      if (!Number.isFinite(startPageIndex)) {
        return;
      }

      let firstOwnedPageIndex = Math.max(
        0,
        Math.round(startPageIndex as number)
      );
      // A continuous section that begins partway down a physical page shares
      // that page with the previous section: Word keeps the previous section's
      // footer and page numbering for the shared page and only applies the new
      // section (and any page-number restart) from the next physical page.
      if (
        sectionIndex > 0 &&
        parseSectionStartType(section.sectionPropertiesXml) === "continuous"
      ) {
        const startPageFirstNodeIndex =
          pageNodeSegmentsByPage[firstOwnedPageIndex]?.[0]?.nodeIndex;
        if (
          Number.isFinite(startPageFirstNodeIndex) &&
          (startPageFirstNodeIndex as number) < startNodeIndex
        ) {
          firstOwnedPageIndex += 1;
        }
      }

      firstRenderedPageIndexBySectionIndex.set(
        sectionIndex,
        firstOwnedPageIndex
      );
    });
    if (!firstRenderedPageIndexBySectionIndex.has(0)) {
      firstRenderedPageIndexBySectionIndex.set(0, 0);
    }
    let previewSectionIndex = 0;
    for (
      let pageIndex = 0;
      pageIndex < pageNodeSegmentsByPage.length;
      pageIndex += 1
    ) {
      previewSectionIndex = resolveSectionIndexForPageSegments(
        sections,
        editor.model.nodes,
        pageNodeSegmentsByPage[pageIndex] ?? [],
        previewSectionIndex
      );
      if (!firstRenderedPageIndexBySectionIndex.has(previewSectionIndex)) {
        firstRenderedPageIndexBySectionIndex.set(
          previewSectionIndex,
          pageIndex
        );
      }
    }

    const initialPageOffsetBySectionIndex = new Map<number, number>();
    sections.forEach((section, sectionIndex) => {
      if (sectionIndex === 0 || section.startNodeIndex < 0) {
        return;
      }

      if (
        parseSectionStartType(section.sectionPropertiesXml) !== "continuous"
      ) {
        return;
      }

      const firstNodePageIndex = pageIndexByNodeIndex.get(
        section.startNodeIndex
      );
      const firstRenderedPageIndex =
        firstRenderedPageIndexBySectionIndex.get(sectionIndex);
      if (
        Number.isFinite(firstNodePageIndex) &&
        Number.isFinite(firstRenderedPageIndex) &&
        (firstRenderedPageIndex as number) > (firstNodePageIndex as number)
      ) {
        initialPageOffsetBySectionIndex.set(
          sectionIndex,
          Math.round(
            (firstRenderedPageIndex as number) - (firstNodePageIndex as number)
          )
        );
      }
    });

    const sectionPageCounts = new Map<number, number>();
    const pageInfo: Array<{
      section: ResolvedDocumentSection;
      sectionIndex: number;
      pageIndexWithinSection: number;
      layout: DocumentLayoutMetrics;
      pageNumber: number;
    }> = [];
    let currentSectionIndex = 0;
    let currentPageNumber = parseSectionPageNumberStart(
      sections[0]?.sectionPropertiesXml
    );

    for (
      let pageIndex = 0;
      pageIndex < pageNodeSegmentsByPage.length;
      pageIndex += 1
    ) {
      const pageSegments = pageNodeSegmentsByPage[pageIndex] ?? [];
      while (currentSectionIndex + 1 < sections.length) {
        const nextSectionStartPageIndex =
          firstRenderedPageIndexBySectionIndex.get(currentSectionIndex + 1);
        if (
          !Number.isFinite(nextSectionStartPageIndex) ||
          (nextSectionStartPageIndex as number) > pageIndex
        ) {
          break;
        }
        currentSectionIndex += 1;
      }
      currentSectionIndex = resolveSectionIndexForPageSegments(
        sections,
        editor.model.nodes,
        pageSegments,
        currentSectionIndex
      );

      const section =
        sections[currentSectionIndex] ?? sections[sections.length - 1];
      const renderedPageIndexWithinSection =
        sectionPageCounts.get(currentSectionIndex) ?? 0;
      sectionPageCounts.set(
        currentSectionIndex,
        renderedPageIndexWithinSection + 1
      );

      const initialPageOffset =
        initialPageOffsetBySectionIndex.get(currentSectionIndex) ?? 0;
      const pageIndexWithinSection =
        renderedPageIndexWithinSection + initialPageOffset;

      if (pageIndex === 0) {
        const firstOverride = parseSectionPageNumberStartOverride(
          section.sectionPropertiesXml
        );
        currentPageNumber =
          firstOverride ??
          parseSectionPageNumberStart(section.sectionPropertiesXml);
      } else if (renderedPageIndexWithinSection === 0) {
        const sectionOverride = parseSectionPageNumberStartOverride(
          section.sectionPropertiesXml
        );
        const sectionStart = sectionOverride ?? currentPageNumber + 1;
        currentPageNumber = sectionStart + initialPageOffset;
      } else {
        currentPageNumber += 1;
      }

      pageInfo.push({
        section,
        sectionIndex: currentSectionIndex,
        pageIndexWithinSection,
        layout: parseSectionLayout(
          section.sectionPropertiesXml ?? primarySectionPropertiesXml
        ),
        pageNumber: currentPageNumber,
      });
    }

    return pageInfo;
  }, [
    documentSections,
    editor.model.nodes,
    editor.model.metadata.footerSections,
    editor.model.metadata.headerSections,
    editor.model.metadata.sectionPropertiesXml,
    pageIndexByNodeIndex,
    pageNodeSegmentsByPage,
    primarySectionPropertiesXml,
  ]);
  // Content signature per page for thumbnail invalidation. Structure keys
  // alone cannot detect edits that keep pagination identical (same node
  // index, same line ranges), and editor ops deep-clone every node, so this
  // combines the segment structure with memoized per-node content signatures.
  const pageThumbnailContentKeysByPage = React.useMemo(() => {
    const metadataSignature = docModelThumbnailMetadataSignature(
      editor.model.metadata
    );
    return pageNodeSegmentsByPage.map((pageSegments, pageIndex) => {
      const sectionInfo = pageSectionInfoByIndex[pageIndex];
      let nodeSignatures = "";
      for (const segment of pageSegments) {
        nodeSignatures += docNodeContentSignature(
          editor.model.nodes[segment.nodeIndex]
        );
        nodeSignatures += ",";
      }
      return [
        editor.documentLoadNonce,
        trackedChangesEnabled ? "tc1" : "tc0",
        metadataSignature,
        sectionInfo
          ? `${sectionInfo.sectionIndex}.${sectionInfo.pageNumber}`
          : "s?",
        pageNodeSegmentIdentityKeysByPage[pageIndex] ?? "",
        nodeSignatures,
      ].join("|");
    });
  }, [
    editor.documentLoadNonce,
    editor.model,
    pageNodeSegmentIdentityKeysByPage,
    pageNodeSegmentsByPage,
    pageSectionInfoByIndex,
    trackedChangesEnabled,
  ]);
  const pageThumbnailSurfaceSizesByPage = React.useMemo(
    () =>
      pageNodeSegmentsByPage.map((_, pageIndex) => {
        const pageLayout =
          pageSectionInfoByIndex[pageIndex]?.layout ?? documentLayout;
        return {
          widthPx: pageLayout.pageWidthPx,
          heightPx: pageLayout.pageHeightPx,
        };
      }),
    [documentLayout, pageNodeSegmentsByPage, pageSectionInfoByIndex]
  );
  const pageThumbnailSnapshotEntriesByPage = React.useMemo(
    () =>
      buildDocxPageThumbnailRenderSnapshotEntries({
        model: editor.model,
        pageNodeSegmentsByPage,
        pageSectionInfoByIndex,
        contentKeysByPage: pageThumbnailContentKeysByPage,
        fallbackLayout: documentLayout,
        documentTheme: editor.documentTheme,
        docGridLinePitchPxByNodeIndex,
        numberingDefinitions: editor.model.metadata.numberingDefinitions,
      }),
    [
      docGridLinePitchPxByNodeIndex,
      documentLayout,
      editor.documentTheme,
      editor.model,
      editor.model.metadata.numberingDefinitions,
      pageNodeSegmentsByPage,
      pageSectionInfoByIndex,
      pageThumbnailContentKeysByPage,
    ]
  );
  React.useEffect(() => {
    syncDocxViewerPageSurfaceContentKeys(
      editor,
      pageThumbnailContentKeysByPage,
      pageThumbnailSurfaceSizesByPage,
      pageThumbnailSnapshotEntriesByPage
    );
  }, [
    pageSurfaceRegistryOwner,
    pageThumbnailContentKeysByPage,
    pageThumbnailSnapshotEntriesByPage,
    pageThumbnailSurfaceSizesByPage,
  ]);
  const resolveStyleRefFieldValueForPage = React.useMemo(() => {
    const valueCache = new Map<string, string | undefined>();
    const nodes = editor.model.nodes;

    const resolveFromParagraph = (
      paragraph: ParagraphNode,
      target: string,
      normalizedTarget: string
    ): string | undefined => {
      const paragraphStyleId = normalizeStyleRefTarget(
        paragraph.style?.styleId ?? ""
      );
      const paragraphStyleName = normalizeStyleRefTarget(
        paragraph.style?.styleName ?? ""
      );
      if (
        paragraphStyleId === normalizedTarget ||
        paragraphStyleName === normalizedTarget
      ) {
        const resolvedParagraphText = paragraphText(paragraph)
          .replace(/\s+/g, " ")
          .trim();
        if (resolvedParagraphText.length > 0) {
          return resolvedParagraphText;
        }
      }

      return paragraphStyledRunText(paragraph, target);
    };

    return (pageIndex: number, target: string): string | undefined => {
      const normalizedTarget = normalizeStyleRefTarget(target);
      if (!normalizedTarget) {
        return undefined;
      }

      const cacheKey = `${pageIndex}:${normalizedTarget}`;
      if (valueCache.has(cacheKey)) {
        return valueCache.get(cacheKey);
      }

      const pageSegments = pageNodeSegmentsByPage[pageIndex] ?? [];
      const visitedNodeIndexes = new Set<number>();
      for (const segment of pageSegments) {
        if (
          !Number.isFinite(segment.nodeIndex) ||
          visitedNodeIndexes.has(segment.nodeIndex)
        ) {
          continue;
        }
        visitedNodeIndexes.add(segment.nodeIndex);
        const node = nodes[segment.nodeIndex];
        if (!node || node.type !== "paragraph") {
          continue;
        }
        const resolved = resolveFromParagraph(node, target, normalizedTarget);
        if (resolved) {
          valueCache.set(cacheKey, resolved);
          return resolved;
        }
      }

      const fallbackStartNodeIndex = pageSegments[0]?.nodeIndex;
      const fallbackStart =
        Number.isFinite(fallbackStartNodeIndex) &&
        (fallbackStartNodeIndex as number) > 0
          ? Math.min(
              nodes.length - 1,
              Math.round(fallbackStartNodeIndex as number) - 1
            )
          : nodes.length - 1;
      for (let nodeIndex = fallbackStart; nodeIndex >= 0; nodeIndex -= 1) {
        const node = nodes[nodeIndex];
        if (!node || node.type !== "paragraph") {
          continue;
        }
        const resolved = resolveFromParagraph(node, target, normalizedTarget);
        if (resolved) {
          valueCache.set(cacheKey, resolved);
          return resolved;
        }
      }

      valueCache.set(cacheKey, undefined);
      return undefined;
    };
  }, [editor.model.nodes, pageNodeSegmentsByPage]);
  const pageCount = pageNodeSegmentsByPage.length;
  const hasLargeTableLayoutSurface = React.useMemo(() => {
    let tableParagraphCount = 0;
    let tableCellCount = 0;
    let largestRowParagraphCount = 0;

    for (const node of editor.model.nodes) {
      if (node.type !== "table") {
        continue;
      }

      for (const row of node.rows) {
        let rowParagraphCount = 0;
        tableCellCount += row.cells.length;
        for (const cell of row.cells) {
          const paragraphCount = tableCellParagraphsRecursively(
            cell.nodes
          ).length;
          tableParagraphCount += paragraphCount;
          rowParagraphCount += paragraphCount;
        }
        largestRowParagraphCount = Math.max(
          largestRowParagraphCount,
          rowParagraphCount
        );
      }
    }

    return (
      tableParagraphCount >= 1000 ||
      tableCellCount >= 240 ||
      largestRowParagraphCount >= 100
    );
  }, [editor.model.nodes]);
  const hideDocumentUntilPaginationSettled =
    deferInitialPaginationPaint && !isInitialPaginationSettled;
  const estimateDocumentStackHeightPx = React.useCallback(
    (count: number): number => {
      const safeCount = Math.max(0, Math.round(count));
      if (safeCount <= 0) {
        return 0;
      }

      const fallbackLayout =
        pageSectionInfoByIndex[pageSectionInfoByIndex.length - 1]?.layout ??
        documentLayout;
      let totalHeightPx = Math.max(0, (safeCount - 1) * DOC_PAGE_BREAK_GAP);
      for (let pageIndex = 0; pageIndex < safeCount; pageIndex += 1) {
        const pageLayout =
          pageSectionInfoByIndex[pageIndex]?.layout ?? fallbackLayout;
        totalHeightPx += Math.max(1, Math.round(pageLayout.pageHeightPx));
      }
      return totalHeightPx;
    },
    [documentLayout, pageSectionInfoByIndex]
  );
  React.useEffect(() => {
    if (!hideDocumentUntilPaginationSettled) {
      setInitialPaginationReservedLayoutState((current) => {
        if (current.pageCount === 0 && current.totalHeightPx === 0) {
          return current;
        }
        return {
          pageCount: 0,
          totalHeightPx: 0,
        };
      });
      return;
    }

    const baselinePageCount = Math.max(
      1,
      pageCount,
      storedDocumentPageCount ?? 0
    );
    setInitialPaginationReservedLayoutState((current) => {
      const nextPageCount = Math.max(current.pageCount, baselinePageCount);
      const nextTotalHeightPx = Math.max(
        current.totalHeightPx,
        estimateDocumentStackHeightPx(nextPageCount)
      );
      if (
        current.pageCount === nextPageCount &&
        current.totalHeightPx === nextTotalHeightPx
      ) {
        return current;
      }
      return {
        pageCount: nextPageCount,
        totalHeightPx: nextTotalHeightPx,
      };
    });
  }, [
    estimateDocumentStackHeightPx,
    hideDocumentUntilPaginationSettled,
    pageCount,
    storedDocumentPageCount,
  ]);
  const preRevealStablePageCount =
    storedDocumentPageCount ??
    Math.max(1, initialPaginationReservedLayoutState.pageCount, pageCount);
  const stableReportedPageCount = hideDocumentUntilPaginationSettled
    ? preRevealStablePageCount
    : pageCount;
  const stableHiddenDocumentHeightPx = hideDocumentUntilPaginationSettled
    ? Math.max(
        initialPaginationReservedLayoutState.totalHeightPx,
        estimateDocumentStackHeightPx(
          Math.max(
            1,
            pageCount,
            storedDocumentPageCount ?? 0,
            initialPaginationReservedLayoutState.pageCount
          )
        )
      )
    : 0;
  const hasExternalVisiblePageRange =
    Number.isFinite(Number(visiblePageRange?.startPageIndex)) ||
    Number.isFinite(Number(visiblePageRange?.endPageIndex));
  const [deferInternalPageVirtualization, setDeferInternalPageVirtualization] =
    React.useState(false);
  const [internalVirtualScrollElement, setInternalVirtualScrollElement] =
    React.useState<HTMLElement | null>(null);
  const [virtualizerMeasurementScale, setVirtualizerMeasurementScale] =
    React.useState(1);
  const [observedVisiblePageRange, setObservedVisiblePageRange] =
    React.useState<
      | {
          startPageIndex: number;
          endPageIndex: number;
        }
      | undefined
    >(undefined);
  const pageVirtualizationSettleDelayMs = Math.max(
    0,
    Math.round(
      Number.isFinite(pageVirtualization?.settleDelayMs)
        ? (pageVirtualization?.settleDelayMs as number)
        : DEFAULT_PAGE_VIRTUALIZATION_SETTLE_DELAY_MS
    )
  );
  const rawPageVirtualizationOverscan = Math.max(
    0,
    Math.round(
      Number.isFinite(pageVirtualization?.overscan)
        ? (pageVirtualization?.overscan as number)
        : DEFAULT_PAGE_VIRTUALIZATION_OVERSCAN
    )
  );
  const pageVirtualizationOverscan =
    hasLargeTableLayoutSurface && !Number.isFinite(pageVirtualization?.overscan)
      ? LARGE_TABLE_PAGE_VIRTUALIZATION_OVERSCAN
      : rawPageVirtualizationOverscan;
  const webdriverActive =
    typeof navigator !== "undefined" && navigator.webdriver === true;
  const internalPageVirtualizationRequested =
    pageVirtualization?.enabled !== false &&
    !hasExternalVisiblePageRange &&
    !hideDocumentUntilPaginationSettled &&
    isInitialPaginationSettled &&
    !deferInternalPageVirtualization &&
    !webdriverActive &&
    pageCount > 1;
  const explicitPageVirtualizationScrollElement =
    pageVirtualization?.scrollElement ?? null;
  const explicitPageVirtualizationZoomScale =
    normalizePageVirtualizationZoomScale(pageVirtualization?.zoomScale);
  const resolveViewerMeasurementZoomScale = React.useCallback(
    (rootElement: HTMLElement | null, fallback = 1): number => {
      if (explicitPageVirtualizationZoomScale !== undefined) {
        return explicitPageVirtualizationZoomScale;
      }

      return rootElement ? resolveEffectiveZoomScale(rootElement) : fallback;
    },
    [explicitPageVirtualizationZoomScale]
  );
  React.useEffect(() => {
    setDeferInternalPageVirtualization(!hasLargeTableLayoutSurface);
  }, [editor.documentLoadNonce, hasLargeTableLayoutSurface]);
  React.useEffect(() => {
    if (typeof window === "undefined" || webdriverActive || pageCount <= 1) {
      setDeferInternalPageVirtualization(true);
      return;
    }

    if (hasLargeTableLayoutSurface) {
      setDeferInternalPageVirtualization(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setDeferInternalPageVirtualization(false);
    }, pageVirtualizationSettleDelayMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    editor.documentLoadNonce,
    hasLargeTableLayoutSurface,
    pageCount,
    pageVirtualizationSettleDelayMs,
    webdriverActive,
  ]);
  React.useLayoutEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    setInternalVirtualScrollElement(
      explicitPageVirtualizationScrollElement instanceof HTMLElement
        ? explicitPageVirtualizationScrollElement
        : nearestScrollableAncestor(viewerRootRef.current)
    );
  }, [
    editor.documentLoadNonce,
    explicitPageVirtualizationScrollElement,
    pageCount,
    trackedChangesEnabled,
  ]);
  // Ancestor zoom changes are rare (toolbar zoom, window resize); probing the
  // ancestor chain with getComputedStyle on every render forces style work in
  // the scroll hot path, so re-probe only on resize/zoom-shaped events.
  const [zoomProbeNonce, setZoomProbeNonce] = React.useState(0);
  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const rootElement = viewerRootRef.current;
    if (!rootElement) {
      return;
    }
    const bumpZoomProbe = (): void => {
      setZoomProbeNonce((nonce) => nonce + 1);
    };
    const resizeObserver =
      typeof ResizeObserver === "function"
        ? new ResizeObserver(bumpZoomProbe)
        : undefined;
    resizeObserver?.observe(rootElement);
    window.addEventListener("resize", bumpZoomProbe);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", bumpZoomProbe);
    };
  }, [editor.documentLoadNonce]);
  React.useLayoutEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const rootElement = viewerRootRef.current;
    if (!rootElement) {
      return;
    }

    const nextScale =
      Math.round(
        resolveViewerMeasurementZoomScale(
          rootElement,
          virtualizerMeasurementScale
        ) * 100
      ) / 100;
    setVirtualizerMeasurementScale((current) =>
      Math.abs(current - nextScale) < 0.005 ? current : nextScale
    );
  }, [
    editor.documentLoadNonce,
    pageCount,
    resolveViewerMeasurementZoomScale,
    virtualizerMeasurementScale,
    zoomProbeNonce,
  ]);
  const internalPageVirtualizationEnabled =
    internalPageVirtualizationRequested &&
    internalVirtualScrollElement !== null;
  const internalPageVirtualizationPending =
    typeof window !== "undefined" &&
    internalPageVirtualizationRequested &&
    internalVirtualScrollElement === null;
  const internalVirtualScrollUsesWindow =
    typeof document !== "undefined" &&
    internalVirtualScrollElement !== null &&
    (internalVirtualScrollElement === document.scrollingElement ||
      internalVirtualScrollElement === document.documentElement ||
      internalVirtualScrollElement === document.body);
  const internalElementPageVirtualizationEnabled =
    internalPageVirtualizationEnabled && !internalVirtualScrollUsesWindow;
  const internalWindowPageVirtualizationEnabled =
    internalPageVirtualizationEnabled && internalVirtualScrollUsesWindow;
  const estimateVirtualPageSize = React.useCallback(
    (pageIndex: number): number => {
      const pageLayout =
        pageSectionInfoByIndex[pageIndex]?.layout ?? documentLayout;
      return Math.max(
        1,
        Math.round(
          (pageLayout.pageHeightPx + DOC_PAGE_BREAK_GAP) *
            virtualizerMeasurementScale
        )
      );
    },
    [documentLayout, pageSectionInfoByIndex, virtualizerMeasurementScale]
  );
  const suspendPaginationMeasurementForScroll = React.useCallback((): void => {
    paginationMeasurementSuspendUntilRef.current =
      Date.now() + PAGE_SCROLL_MEASUREMENT_SUSPEND_MS;
  }, []);
  const internalElementPageVirtualizer = useVirtualizer({
    count: Math.max(1, pageCount),
    getScrollElement: () =>
      internalElementPageVirtualizationEnabled
        ? internalVirtualScrollElement
        : null,
    estimateSize: estimateVirtualPageSize,
    overscan: pageVirtualizationOverscan,
  });
  const internalWindowPageVirtualizer = useWindowVirtualizer({
    count: Math.max(1, pageCount),
    getScrollElement: () =>
      internalWindowPageVirtualizationEnabled && typeof window !== "undefined"
        ? window
        : null,
    estimateSize: estimateVirtualPageSize,
    overscan: pageVirtualizationOverscan,
  });
  const internalPageVirtualizer = internalVirtualScrollUsesWindow
    ? internalWindowPageVirtualizer
    : internalElementPageVirtualizer;
  // The virtualizer consults estimateSize only while (re)building its internal
  // measurement cache, which it does only when `count` changes. Page heights
  // also change with the effective CSS zoom and with per-section layouts, so
  // without an explicit re-measure the cached offsets stay denominated in the
  // old scale — at zoom < 1 that overestimates total height and makes the
  // trailing pages unreachable at any scroll position.
  React.useLayoutEffect(() => {
    internalElementPageVirtualizer.measure();
    internalWindowPageVirtualizer.measure();
  }, [
    estimateVirtualPageSize,
    internalElementPageVirtualizer,
    internalWindowPageVirtualizer,
  ]);
  const internalVirtualItems = internalPageVirtualizer.getVirtualItems();
  const internalMostVisiblePageIndex = React.useMemo(() => {
    if (
      !internalPageVirtualizationEnabled ||
      internalVirtualItems.length === 0
    ) {
      return undefined;
    }

    const virtualizerMetrics = internalPageVirtualizer as {
      scrollOffset?: number | null;
      scrollRect?: { height?: number; width?: number };
    };
    const viewportStart = Number.isFinite(virtualizerMetrics.scrollOffset)
      ? (virtualizerMetrics.scrollOffset as number)
      : internalVirtualItems[0]?.start ?? 0;
    const viewportSize = virtualizerMetrics.scrollRect?.height;
    const viewportEnd =
      viewportStart +
      (Number.isFinite(viewportSize) && (viewportSize as number) > 0
        ? (viewportSize as number)
        : internalVirtualItems[0]?.size ?? 0);

    let bestPageIndex: number | undefined;
    let bestVisibleSize = -1;
    internalVirtualItems.forEach((item) => {
      const visibleSize =
        Math.min(item.end, viewportEnd) - Math.max(item.start, viewportStart);
      if (visibleSize > bestVisibleSize) {
        bestVisibleSize = visibleSize;
        bestPageIndex = item.index;
      }
    });

    return bestPageIndex === undefined
      ? undefined
      : clampNumber(bestPageIndex, 0, pageCount - 1);
  }, [
    internalPageVirtualizationEnabled,
    internalPageVirtualizer,
    internalVirtualItems,
    pageCount,
  ]);
  const internalVisiblePageRange = React.useMemo(() => {
    if (!internalPageVirtualizationEnabled) {
      return undefined;
    }

    if (pageCount <= 0) {
      return {
        startPageIndex: 0,
        endPageIndex: -1,
      };
    }

    if (internalVirtualItems.length === 0) {
      return {
        startPageIndex: 0,
        endPageIndex: Math.min(pageCount - 1, 2),
      };
    }

    const first = internalVirtualItems[0];
    const last = internalVirtualItems[internalVirtualItems.length - 1];
    const startPageIndex = clampNumber(
      Math.round(first?.index ?? 0),
      0,
      pageCount - 1
    );
    const endPageIndex = clampNumber(
      Math.round(last?.index ?? startPageIndex),
      startPageIndex,
      pageCount - 1
    );
    return {
      startPageIndex,
      endPageIndex,
    };
  }, [internalPageVirtualizationEnabled, internalVirtualItems, pageCount]);
  const internalRenderVisiblePageRange = React.useMemo(() => {
    if (!internalVisiblePageRange) {
      return undefined;
    }

    if (
      !hasLargeTableLayoutSurface ||
      Number.isFinite(pageVirtualization?.overscan)
    ) {
      return internalVisiblePageRange;
    }

    // Pre-render in the direction of travel; when idle, cover both neighbors.
    const scrollDirection = internalPageVirtualizer.scrollDirection;
    const renderPreviousPage = scrollDirection !== "forward";
    const renderNextPage = scrollDirection !== "backward";
    const startPageIndex = clampNumber(
      internalVisiblePageRange.startPageIndex -
        (renderPreviousPage ? LARGE_TABLE_PAGE_ADJACENT_RENDER_COUNT : 0),
      0,
      pageCount - 1
    );
    const endPageIndex = clampNumber(
      internalVisiblePageRange.endPageIndex +
        (renderNextPage ? LARGE_TABLE_PAGE_ADJACENT_RENDER_COUNT : 0),
      startPageIndex,
      pageCount - 1
    );

    return {
      startPageIndex,
      endPageIndex,
    };
  }, [
    hasLargeTableLayoutSurface,
    internalPageVirtualizer.scrollDirection,
    internalVisiblePageRange,
    pageCount,
    pageVirtualization?.overscan,
  ]);
  const shouldObserveVisiblePageRange =
    hasLargeTableLayoutSurface || internalVirtualItems.length === 0;
  React.useLayoutEffect(() => {
    if (
      !internalPageVirtualizationEnabled ||
      !internalVirtualScrollElement ||
      !shouldObserveVisiblePageRange ||
      typeof window === "undefined"
    ) {
      setObservedVisiblePageRange((current) =>
        current === undefined ? current : undefined
      );
      return;
    }

    const rootElement = viewerRootRef.current;
    if (!rootElement) {
      setObservedVisiblePageRange((current) =>
        current === undefined ? current : undefined
      );
      return;
    }

    let frameId: number | null = null;
    const commitObservedVisiblePageRange = (
      updater: React.SetStateAction<
        | {
            startPageIndex: number;
            endPageIndex: number;
          }
        | undefined
      >
    ): void => {
      React.startTransition(() => {
        setObservedVisiblePageRange(updater);
      });
    };
    const syncVisiblePageRange = (): void => {
      frameId = null;
      suspendPaginationMeasurementForScroll();

      const viewportRect = internalVirtualScrollUsesWindow
        ? {
            top: 0,
            bottom: window.innerHeight,
          }
        : internalVirtualScrollElement.getBoundingClientRect();
      const pageWrappers = rootElement.querySelectorAll<HTMLElement>(
        '[data-docx-page-wrapper="true"][data-index]'
      );

      let firstVisiblePageIndex: number | undefined;
      let lastVisiblePageIndex: number | undefined;
      for (const pageWrapper of pageWrappers) {
        const rawPageIndex = pageWrapper.getAttribute("data-index");
        const pageIndex = Number.parseInt(rawPageIndex ?? "", 10);
        if (!Number.isFinite(pageIndex)) {
          continue;
        }

        const pageRect = pageWrapper.getBoundingClientRect();
        if (pageRect.bottom <= viewportRect.top) {
          continue;
        }
        if (pageRect.top >= viewportRect.bottom) {
          if (firstVisiblePageIndex !== undefined) {
            break;
          }
          continue;
        }

        if (firstVisiblePageIndex === undefined) {
          firstVisiblePageIndex = pageIndex;
        }
        lastVisiblePageIndex = pageIndex;
      }

      if (
        firstVisiblePageIndex === undefined ||
        lastVisiblePageIndex === undefined
      ) {
        commitObservedVisiblePageRange((current) =>
          current === undefined ? current : undefined
        );
        return;
      }

      const startPageIndex = clampNumber(
        firstVisiblePageIndex - pageVirtualizationOverscan,
        0,
        pageCount - 1
      );
      const endPageIndex = clampNumber(
        lastVisiblePageIndex + pageVirtualizationOverscan,
        startPageIndex,
        pageCount - 1
      );

      commitObservedVisiblePageRange((current) => {
        if (
          current?.startPageIndex === startPageIndex &&
          current?.endPageIndex === endPageIndex
        ) {
          return current;
        }
        return {
          startPageIndex,
          endPageIndex,
        };
      });
    };

    const scheduleVisiblePageRangeSync = (): void => {
      suspendPaginationMeasurementForScroll();
      if (frameId !== null) {
        return;
      }
      frameId = window.requestAnimationFrame(syncVisiblePageRange);
    };

    const resizeObserver =
      typeof ResizeObserver === "function"
        ? new ResizeObserver(() => {
            scheduleVisiblePageRangeSync();
          })
        : undefined;

    if (!internalVirtualScrollUsesWindow) {
      resizeObserver?.observe(internalVirtualScrollElement);
    }
    resizeObserver?.observe(rootElement);
    if (internalVirtualScrollUsesWindow) {
      window.addEventListener("scroll", scheduleVisiblePageRangeSync, {
        passive: true,
      });
    } else {
      internalVirtualScrollElement.addEventListener(
        "scroll",
        scheduleVisiblePageRangeSync,
        { passive: true }
      );
    }
    window.addEventListener("resize", scheduleVisiblePageRangeSync);
    scheduleVisiblePageRangeSync();

    return () => {
      if (internalVirtualScrollUsesWindow) {
        window.removeEventListener("scroll", scheduleVisiblePageRangeSync);
      } else {
        internalVirtualScrollElement.removeEventListener(
          "scroll",
          scheduleVisiblePageRangeSync
        );
      }
      window.removeEventListener("resize", scheduleVisiblePageRangeSync);
      resizeObserver?.disconnect();
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [
    hasLargeTableLayoutSurface,
    internalPageVirtualizationEnabled,
    internalVirtualItems.length,
    internalVirtualScrollElement,
    internalVirtualScrollUsesWindow,
    pageCount,
    pageVirtualizationOverscan,
    shouldObserveVisiblePageRange,
    suspendPaginationMeasurementForScroll,
  ]);
  React.useEffect(() => {
    if (
      !internalPageVirtualizationEnabled ||
      !internalVirtualScrollElement ||
      typeof window === "undefined"
    ) {
      return;
    }

    const onScroll = (): void => {
      suspendPaginationMeasurementForScroll();
    };
    const scrollTarget = internalVirtualScrollUsesWindow
      ? window
      : internalVirtualScrollElement;
    scrollTarget.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      scrollTarget.removeEventListener("scroll", onScroll);
    };
  }, [
    internalPageVirtualizationEnabled,
    internalVirtualScrollElement,
    internalVirtualScrollUsesWindow,
    suspendPaginationMeasurementForScroll,
  ]);
  const internalEffectiveRenderVisiblePageRange = React.useMemo(() => {
    if (!internalRenderVisiblePageRange) {
      return observedVisiblePageRange;
    }
    if (!observedVisiblePageRange || !hasLargeTableLayoutSurface) {
      return internalRenderVisiblePageRange;
    }

    const rangesAreAdjacentOrOverlapping =
      observedVisiblePageRange.startPageIndex <=
        internalRenderVisiblePageRange.endPageIndex + 2 &&
      internalRenderVisiblePageRange.startPageIndex <=
        observedVisiblePageRange.endPageIndex + 2;
    if (!rangesAreAdjacentOrOverlapping) {
      return internalRenderVisiblePageRange;
    }

    return {
      startPageIndex: Math.min(
        internalRenderVisiblePageRange.startPageIndex,
        observedVisiblePageRange.startPageIndex
      ),
      endPageIndex: Math.max(
        internalRenderVisiblePageRange.endPageIndex,
        observedVisiblePageRange.endPageIndex
      ),
    };
  }, [
    hasLargeTableLayoutSurface,
    internalRenderVisiblePageRange,
    observedVisiblePageRange,
  ]);
  const effectiveVisiblePageRange =
    visiblePageRange ??
    internalEffectiveRenderVisiblePageRange ??
    observedVisiblePageRange ??
    (internalPageVirtualizationPending
      ? {
          startPageIndex: 0,
          endPageIndex: Math.min(
            pageCount - 1,
            Math.max(0, pageVirtualizationOverscan)
          ),
        }
      : undefined);
  const normalizedVisiblePageRange = React.useMemo(() => {
    if (pageCount <= 0) {
      return {
        startPageIndex: 0,
        endPageIndex: -1,
      };
    }

    const rawStart = Number(effectiveVisiblePageRange?.startPageIndex);
    const rawEnd = Number(effectiveVisiblePageRange?.endPageIndex);
    const startCandidate = Number.isFinite(rawStart) ? Math.round(rawStart) : 0;
    const endCandidate = Number.isFinite(rawEnd)
      ? Math.round(rawEnd)
      : pageCount - 1;
    const startPageIndex = clampNumber(startCandidate, 0, pageCount - 1);
    const endPageIndex = clampNumber(
      endCandidate,
      startPageIndex,
      pageCount - 1
    );

    return {
      startPageIndex,
      endPageIndex,
    };
  }, [
    effectiveVisiblePageRange?.endPageIndex,
    effectiveVisiblePageRange?.startPageIndex,
    pageCount,
  ]);
  const initialPremeasureStartPageIndex = hasExternalVisiblePageRange
    ? normalizedVisiblePageRange.startPageIndex
    : 0;
  const initialPremeasurePageLimit = hasLargeTableLayoutSurface
    ? Math.min(2, INITIAL_PAGINATION_PREMEASURE_PAGE_LIMIT)
    : INITIAL_PAGINATION_PREMEASURE_PAGE_LIMIT;
  const initialPremeasureEndPageIndex = hasExternalVisiblePageRange
    ? normalizedVisiblePageRange.endPageIndex
    : Math.min(pageCount - 1, initialPremeasurePageLimit - 1);
  const measureAllPagesBeforeInitialReveal =
    hideDocumentUntilPaginationSettled &&
    pageCount > 0 &&
    initialPremeasureStartPageIndex === 0 &&
    initialPremeasureEndPageIndex >= pageCount - 1;
  const visiblePageStartIndex = measureAllPagesBeforeInitialReveal
    ? 0
    : hideDocumentUntilPaginationSettled
    ? initialPremeasureStartPageIndex
    : normalizedVisiblePageRange.startPageIndex;
  const visiblePageEndIndex = measureAllPagesBeforeInitialReveal
    ? pageCount - 1
    : hideDocumentUntilPaginationSettled
    ? initialPremeasureEndPageIndex
    : normalizedVisiblePageRange.endPageIndex;
  const visiblePageIndexes = React.useMemo(() => {
    if (pageCount <= 0 || visiblePageEndIndex < visiblePageStartIndex) {
      return [] as number[];
    }

    const indexes: number[] = [];
    for (
      let pageIndex = visiblePageStartIndex;
      pageIndex <= visiblePageEndIndex;
      pageIndex += 1
    ) {
      indexes.push(pageIndex);
    }
    return indexes;
  }, [pageCount, visiblePageEndIndex, visiblePageStartIndex]);
  const pageStackVirtualSpacers = React.useMemo(() => {
    const pageWrapperWidthPxForIndex = (pageIndex: number): number => {
      const pageLayout =
        pageSectionInfoByIndex[pageIndex]?.layout ?? documentLayout;
      return showTrackedChangeGutter
        ? pageLayout.pageWidthPx + TRACKED_CHANGE_GUTTER_WIDTH_PX
        : pageLayout.pageWidthPx;
    };
    const summarizeSkippedPages = (
      startPageIndex: number,
      endPageIndex: number
    ): { heightPx: number; widthPx: number } => {
      if (
        pageCount <= 0 ||
        endPageIndex < startPageIndex ||
        startPageIndex >= pageCount ||
        endPageIndex < 0
      ) {
        return { heightPx: 0, widthPx: 0 };
      }

      const start = clampNumber(startPageIndex, 0, pageCount - 1);
      const end = clampNumber(endPageIndex, start, pageCount - 1);
      let heightPx = 0;
      let widthPx = 0;
      for (let pageIndex = start; pageIndex <= end; pageIndex += 1) {
        const pageLayout =
          pageSectionInfoByIndex[pageIndex]?.layout ?? documentLayout;
        heightPx += Math.max(1, Math.round(pageLayout.pageHeightPx));
        widthPx = Math.max(widthPx, pageWrapperWidthPxForIndex(pageIndex));
      }

      const skippedPageCount = end - start + 1;
      heightPx += Math.max(0, skippedPageCount - 1) * DOC_PAGE_BREAK_GAP;
      return { heightPx, widthPx };
    };

    return {
      before: summarizeSkippedPages(0, visiblePageStartIndex - 1),
      after: summarizeSkippedPages(visiblePageEndIndex + 1, pageCount - 1),
    };
  }, [
    documentLayout,
    pageCount,
    pageSectionInfoByIndex,
    showTrackedChangeGutter,
    visiblePageEndIndex,
    visiblePageStartIndex,
  ]);
  React.useLayoutEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (Date.now() < paginationMeasurementSuspendUntilRef.current) {
      return;
    }

    setMeasuredParagraphOuterHeightsPxByNodeIndex((current) => {
      const next = new Map(current);
      let changed = false;

      // Client rects scale with the CSS zoom applied above the viewer, but
      // pagination operates in unzoomed page space — normalize, or every zoom
      // change rewrites measurements and re-paginates (laggy zoom, blank
      // virtualized pages).
      const rootElement = viewerRootRef.current;
      const zoomScale = resolveViewerMeasurementZoomScale(rootElement, 1);

      paragraphElementsRef.current.forEach((element, nodeIndex) => {
        if (
          !element.isConnected ||
          element.dataset.docxParagraphPartialLineRange === "true" ||
          element.closest('[data-docx-header-footer-region="footer"]') ||
          element.closest('[data-docx-header-footer-region="header"]')
        ) {
          return;
        }

        const rect = element.getBoundingClientRect();
        if (rect.width <= 0 && rect.height <= 0) {
          return;
        }

        const style = window.getComputedStyle(element);
        const marginTop = Number.parseFloat(style.marginTop || "0");
        const marginBottom = Number.parseFloat(style.marginBottom || "0");
        const outerHeightPx = Math.max(
          1,
          Math.round(
            rect.height / zoomScale +
              (Number.isFinite(marginTop) ? marginTop : 0) +
              (Number.isFinite(marginBottom) ? marginBottom : 0)
          )
        );
        const previousHeightPx = next.get(nodeIndex);
        // 1px hysteresis: zoom-division rounding must not oscillate
        // measurements and churn pagination.
        if (
          previousHeightPx === undefined ||
          Math.abs(previousHeightPx - outerHeightPx) > 1
        ) {
          next.set(nodeIndex, outerHeightPx);
          changed = true;
        }
      });

      return changed ? next : current;
    });
  }, [
    editor.documentLoadNonce,
    pageNodeSegmentIdentityKeysByPage,
    resolveViewerMeasurementZoomScale,
    visiblePageEndIndex,
    visiblePageStartIndex,
  ]);
  const visibleTableRowIndexesByNodeIndex = React.useMemo(() => {
    const rowsByNodeIndex = new Map<number, Set<number>>();

    const addRowIndex = (nodeIndex: number, rowIndex: number): void => {
      const tableNode = editor.model.nodes[nodeIndex];
      if (
        !tableNode ||
        tableNode.type !== "table" ||
        rowIndex < 0 ||
        rowIndex >= tableNode.rows.length
      ) {
        return;
      }

      const existing = rowsByNodeIndex.get(nodeIndex);
      if (existing) {
        existing.add(rowIndex);
      } else {
        rowsByNodeIndex.set(nodeIndex, new Set([rowIndex]));
      }
    };

    for (const pageIndex of visiblePageIndexes) {
      for (const segment of pageNodeSegmentsByPage[pageIndex] ?? []) {
        const tableNode = editor.model.nodes[segment.nodeIndex];
        if (!tableNode || tableNode.type !== "table") {
          continue;
        }

        if (segment.tableRowSlice) {
          addRowIndex(segment.nodeIndex, segment.tableRowSlice.rowIndex);
          continue;
        }

        if (segment.tableRowRange) {
          for (
            let rowIndex = segment.tableRowRange.startRowIndex;
            rowIndex < segment.tableRowRange.endRowIndex;
            rowIndex += 1
          ) {
            addRowIndex(segment.nodeIndex, rowIndex);
          }
          continue;
        }

        for (
          let rowIndex = 0;
          rowIndex < tableNode.rows.length;
          rowIndex += 1
        ) {
          addRowIndex(segment.nodeIndex, rowIndex);
        }
      }
    }

    return rowsByNodeIndex;
  }, [editor.model.nodes, pageNodeSegmentsByPage, visiblePageIndexes]);
  const paginationMeasurementEnabled =
    hideDocumentUntilPaginationSettled || postImportPaginationUnlocked;
  const visiblePagesHavePendingPaginationMeasurements = React.useMemo(() => {
    if (pageCount === 0) {
      return false;
    }

    for (const pageIndex of visiblePageIndexes) {
      const pageSegments = pageNodeSegmentsByPage[pageIndex] ?? [];
      const pageContainsTableRowSlice = pageSegments.some((segment) =>
        Boolean(segment.tableRowSlice)
      );
      if (
        !Number.isFinite(measuredPageContentHeightByIndex[pageIndex]) &&
        !(hasLargeTableLayoutSurface && pageContainsTableRowSlice)
      ) {
        return true;
      }

      for (const segment of pageSegments) {
        const tableNode = editor.model.nodes[segment.nodeIndex];
        if (!tableNode || tableNode.type !== "table") {
          continue;
        }

        if (segment.tableRowSlice) {
          continue;
        }

        const measuredHeights = tableMeasuredRowHeights[segment.nodeIndex];
        if (
          !measuredHeights ||
          measuredHeights.length !== tableNode.rows.length
        ) {
          return true;
        }
      }
    }

    return false;
  }, [
    editor.model.nodes,
    hasLargeTableLayoutSurface,
    measuredPageContentHeightByIndex,
    pageCount,
    pageNodeSegmentsByPage,
    tableMeasuredRowHeights,
    visiblePageIndexes,
  ]);
  const allowPostImportPaginationMeasurement =
    !hasLargeTableLayoutSurface || !internalPageVirtualizationEnabled;
  const visiblePagesNeedMeasurementUnlock =
    !hideDocumentUntilPaginationSettled &&
    allowPostImportPaginationMeasurement &&
    visiblePagesHavePendingPaginationMeasurements;
  React.useEffect(() => {
    if (!visiblePagesNeedMeasurementUnlock) {
      return;
    }

    setPostImportPaginationUnlocked(true);
  }, [visiblePagesNeedMeasurementUnlock]);
  React.useEffect(() => {
    if (
      hideDocumentUntilPaginationSettled ||
      !postImportPaginationUnlocked ||
      visiblePagesNeedMeasurementUnlock
    ) {
      return;
    }

    let cancelled = false;
    let secondFrameId: number | null = null;
    const firstFrameId = window.requestAnimationFrame(() => {
      secondFrameId = window.requestAnimationFrame(() => {
        if (cancelled) {
          return;
        }
        setPostImportPaginationUnlocked(false);
      });
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(firstFrameId);
      if (secondFrameId !== null) {
        window.cancelAnimationFrame(secondFrameId);
      }
    };
  }, [
    hideDocumentUntilPaginationSettled,
    postImportPaginationUnlocked,
    visiblePagesNeedMeasurementUnlock,
  ]);
  React.useEffect(() => {
    if (!onPageCountChange) {
      return;
    }

    onPageCountChange(stableReportedPageCount);
  }, [onPageCountChange, stableReportedPageCount]);
  React.useEffect(() => {
    const nextCurrentPage = clampNumber(
      (internalMostVisiblePageIndex ?? visiblePageStartIndex) + 1,
      1,
      Math.max(1, stableReportedPageCount)
    );
    editor.syncPaginationInfo({
      currentPage: nextCurrentPage,
      totalPages: Math.max(1, stableReportedPageCount),
    });
  }, [
    editor.syncPaginationInfo,
    internalMostVisiblePageIndex,
    stableReportedPageCount,
    visiblePageStartIndex,
  ]);
  const trackedChangesByPage = React.useMemo(() => {
    const pageBuckets = pageNodeSegmentsByPage.map(
      () => [] as DocxGutterAnnotation[]
    );

    const placeAnnotation = (annotation: DocxGutterAnnotation): void => {
      const pageIndex = resolveGutterAnnotationPageIndex(
        annotation.location,
        pageNodeSegmentsByPage
      );
      if (pageIndex < 0 || pageIndex >= pageBuckets.length) {
        return;
      }
      pageBuckets[pageIndex].push(annotation);
    };

    if (trackedChangesEnabled) {
      editor.trackedChanges.forEach((change) => {
        if (!trackedChangeUsesGutterBalloon(change)) {
          return;
        }

        placeAnnotation({
          id: change.id,
          location: change.location,
          trackedChange: change,
        });
      });
    }
    if (commentsEnabled) {
      editor.comments.forEach((comment) => {
        placeAnnotation({
          id: comment.id,
          location: comment.location,
          comment,
        });
      });
    }

    pageBuckets.forEach((pageAnnotations) => {
      pageAnnotations.sort((left, right) => {
        const leftKey = gutterAnnotationSortTuple(left.location);
        const rightKey = gutterAnnotationSortTuple(right.location);
        for (let index = 0; index < leftKey.length; index += 1) {
          if (leftKey[index] === rightKey[index]) {
            continue;
          }
          return leftKey[index] - rightKey[index];
        }
        return left.id.localeCompare(right.id);
      });
    });

    return pageBuckets;
  }, [
    commentsEnabled,
    editor.comments,
    editor.trackedChanges,
    pageNodeSegmentsByPage,
    trackedChangesEnabled,
  ]);
  const [trackedChangeAnchorByPage, setTrackedChangeAnchorByPage] =
    React.useState<Array<Map<string, TrackedChangeAnchorPoint>>>([]);
  const [headerBodyClearanceByPage, setHeaderBodyClearanceByPage] =
    React.useState<Record<number, number>>({});
  React.useEffect(() => {
    setHeaderBodyClearanceByPage({});
  }, [editor.documentLoadNonce]);
  const [trackedChangeCardHeightsByPage, setTrackedChangeCardHeightsByPage] =
    React.useState<Array<Map<string, number>>>([]);
  React.useLayoutEffect(() => {
    if (!showTrackedChangeGutter || trackedChangesByPage.length === 0) {
      setTrackedChangeAnchorByPage((current) =>
        current.length === 0 ? current : []
      );
      return;
    }

    const nextAnchorMaps = trackedChangesByPage.map(
      (annotations, pageIndex) => {
        const pageElement = pageElementsRef.current.get(pageIndex);
        const anchorsByChangeId = new Map<string, TrackedChangeAnchorPoint>();
        if (!pageElement || annotations.length === 0) {
          return anchorsByChangeId;
        }

        const pageHeightPx = Math.max(1, pageElement.offsetHeight);
        const pageWidthPx = Math.max(1, pageElement.offsetWidth);
        annotations.forEach((annotation) => {
          const anchorElement = findGutterAnnotationAnchorElementInPage(
            pageElement,
            annotation
          );
          if (!anchorElement) {
            return;
          }

          const anchorRect = elementRectWithinContainer(
            anchorElement,
            pageElement
          );
          if (!anchorRect) {
            return;
          }

          const anchorY = clampNumber(
            Math.round(anchorRect.top + anchorRect.height / 2),
            10,
            Math.max(10, pageHeightPx - 10)
          );
          const anchorX = clampNumber(
            Math.round(anchorRect.right),
            10,
            Math.max(10, pageWidthPx - 10)
          );
          anchorsByChangeId.set(annotation.id, { x: anchorX, y: anchorY });
        });

        return anchorsByChangeId;
      }
    );

    setTrackedChangeAnchorByPage(nextAnchorMaps);
  }, [
    trackedChangesByPage,
    showTrackedChangeGutter,
    visiblePageEndIndex,
    visiblePageStartIndex,
  ]);
  React.useLayoutEffect(() => {
    if (!showTrackedChangeGutter || trackedChangesByPage.length === 0) {
      setTrackedChangeCardHeightsByPage((current) =>
        current.length === 0 ? current : []
      );
      return;
    }

    const rootElement = viewerRootRef.current;
    const zoomScale = resolveViewerMeasurementZoomScale(rootElement, 1);
    const nextHeightsByPage = trackedChangesByPage.map(
      (annotations, pageIndex) => {
        const pageHeights = new Map<string, number>();
        annotations.forEach((annotation) => {
          const cardElement = trackedChangeCardElementsRef.current.get(
            `${pageIndex}:${annotation.id}`
          );
          if (!cardElement) {
            return;
          }

          const measuredHeight = Math.round(
            cardElement.getBoundingClientRect().height / zoomScale
          );
          if (!Number.isFinite(measuredHeight) || measuredHeight <= 0) {
            return;
          }

          pageHeights.set(
            annotation.id,
            Math.max(TRACKED_CHANGE_GUTTER_CARD_MIN_HEIGHT_PX, measuredHeight)
          );
        });
        return pageHeights;
      }
    );

    setTrackedChangeCardHeightsByPage((current) => {
      if (current.length === nextHeightsByPage.length) {
        let equal = true;
        for (
          let pageIndex = 0;
          pageIndex < nextHeightsByPage.length && equal;
          pageIndex += 1
        ) {
          const currentPage = current[pageIndex] ?? new Map<string, number>();
          const nextPage =
            nextHeightsByPage[pageIndex] ?? new Map<string, number>();
          if (currentPage.size !== nextPage.size) {
            equal = false;
            break;
          }
          for (const [changeId, nextHeight] of nextPage) {
            const currentHeight = currentPage.get(changeId);
            if (currentHeight !== nextHeight) {
              equal = false;
              break;
            }
          }
        }
        if (equal) {
          return current;
        }
      }
      return nextHeightsByPage;
    });
  }, [
    showTrackedChangeGutter,
    trackedChangesByPage,
    visiblePageEndIndex,
    visiblePageStartIndex,
  ]);
  const positionedTrackedChangesByPage = React.useMemo(() => {
    return trackedChangesByPage.map((changes, pageIndex) => {
      const pageLayout =
        pageSectionInfoByIndex[pageIndex]?.layout ?? documentLayout;
      const anchorsByChangeId =
        trackedChangeAnchorByPage[pageIndex] ??
        new Map<string, TrackedChangeAnchorPoint>();
      const cardHeightsByChangeId =
        trackedChangeCardHeightsByPage[pageIndex] ?? new Map<string, number>();
      return layoutTrackedChangesForPage(
        changes,
        anchorsByChangeId,
        cardHeightsByChangeId,
        Math.max(1, pageLayout.pageWidthPx),
        Math.max(1, pageLayout.pageHeightPx)
      );
    });
  }, [
    documentLayout,
    pageSectionInfoByIndex,
    trackedChangeAnchorByPage,
    trackedChangeCardHeightsByPage,
    trackedChangesByPage,
  ]);
  const totalPagesForFieldResolution = React.useMemo(() => {
    if (editor.canUndo || editor.canRedo) {
      return pageCount;
    }
    const storedDocumentPageCount = editor.model.metadata.documentPageCount;
    if (
      Number.isFinite(storedDocumentPageCount) &&
      (storedDocumentPageCount as number) > 0
    ) {
      return Math.max(1, Math.round(storedDocumentPageCount as number));
    }
    return pageCount;
  }, [
    editor.canRedo,
    editor.canUndo,
    editor.model.metadata.documentPageCount,
    pageCount,
  ]);
  const isPageVisible = React.useCallback(
    (pageIndex: number): boolean => {
      return (
        pageIndex >= visiblePageStartIndex && pageIndex <= visiblePageEndIndex
      );
    },
    [visiblePageEndIndex, visiblePageStartIndex]
  );
  const requestPageReveal = React.useCallback(
    (targetPageIndex: number): void => {
      if (onRequestPageReveal) {
        onRequestPageReveal(targetPageIndex);
        return;
      }

      if (!internalPageVirtualizationEnabled) {
        return;
      }

      const safePageCount = Math.max(1, pageCount);
      const clampedPageIndex = clampNumber(
        Math.round(targetPageIndex),
        0,
        safePageCount - 1
      );
      internalPageVirtualizer.scrollToIndex(clampedPageIndex, {
        align: "center",
      });
    },
    [
      internalPageVirtualizationEnabled,
      internalPageVirtualizer,
      onRequestPageReveal,
      pageCount,
    ]
  );
  const scrollToBookmark = React.useCallback(
    (bookmarkName: string): boolean => {
      const normalized = bookmarkName.trim();
      if (!normalized) {
        return false;
      }

      const targetNodeIndex = bookmarkTargetNodeIndexByName.get(normalized);
      if (targetNodeIndex === undefined) {
        return false;
      }

      const targetElement =
        paragraphElementsRef.current.get(targetNodeIndex) ??
        tableElementsRef.current.get(targetNodeIndex);
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
        if (editor.model.nodes[targetNodeIndex]?.type === "paragraph") {
          editor.selectParagraph(targetNodeIndex);
        }
        return true;
      }

      const targetPageIndex = pageIndexByNodeIndex.get(targetNodeIndex);
      if (targetPageIndex === undefined) {
        return false;
      }

      const pageIsVisible =
        targetPageIndex >= visiblePageStartIndex &&
        targetPageIndex <= visiblePageEndIndex;
      if (!pageIsVisible) {
        requestPageReveal(targetPageIndex);
      }

      const scrollToResolvedTarget = (attempt = 0): void => {
        const fallbackElement =
          paragraphElementsRef.current.get(targetNodeIndex) ??
          tableElementsRef.current.get(targetNodeIndex) ??
          pageElementsRef.current.get(targetPageIndex);
        if (fallbackElement) {
          fallbackElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
          return;
        }

        if (attempt < 6) {
          window.requestAnimationFrame(() => {
            scrollToResolvedTarget(attempt + 1);
          });
        }
      };

      window.requestAnimationFrame(() => {
        scrollToResolvedTarget();
      });

      if (editor.model.nodes[targetNodeIndex]?.type === "paragraph") {
        editor.selectParagraph(targetNodeIndex);
      }
      return true;
    },
    [
      bookmarkTargetNodeIndexByName,
      editor,
      editor.model.nodes,
      pageIndexByNodeIndex,
      requestPageReveal,
      visiblePageEndIndex,
      visiblePageStartIndex,
    ]
  );
  const pageHeaderAndFooterNodes = React.useMemo(
    () =>
      pageNodeSegmentsByPage.map((_, pageIndex) => {
        const sectionInfo = pageSectionInfoByIndex[pageIndex];
        const sectionHeaderSections =
          sectionInfo?.section.headerSections ??
          editor.model.metadata.headerSections;
        const sectionFooterSections =
          sectionInfo?.section.footerSections ??
          editor.model.metadata.footerSections;
        const sectionPropertiesXml =
          sectionInfo?.section.sectionPropertiesXml ??
          editor.model.metadata.sectionPropertiesXml;
        const sectionPageIndex =
          sectionInfo?.pageIndexWithinSection ?? pageIndex;
        const evenAndOddHeadersEnabled =
          editor.model.metadata.compatibility?.evenAndOddHeaders === true;
        const headerSection = selectSectionVariantForPage(
          sectionHeaderSections,
          sectionPropertiesXml,
          sectionPageIndex,
          {
            evenAndOddHeaders: evenAndOddHeadersEnabled,
          }
        );
        const footerSection = selectSectionVariantForPage(
          sectionFooterSections,
          sectionPropertiesXml,
          sectionPageIndex,
          {
            evenAndOddHeaders: evenAndOddHeadersEnabled,
          }
        );

        const paragraphHasVisibleBorder = (paragraph: ParagraphNode): boolean =>
          paragraphBorderVisible(paragraph.style?.borders?.top) ||
          paragraphBorderVisible(paragraph.style?.borders?.right) ||
          paragraphBorderVisible(paragraph.style?.borders?.bottom) ||
          paragraphBorderVisible(paragraph.style?.borders?.left) ||
          paragraphBorderVisible(paragraph.style?.borders?.between) ||
          paragraphBorderVisible(paragraph.style?.borders?.bar);

        const filterVisibleNodes = (
          nodes: DocModel["nodes"]
        ): DocModel["nodes"] =>
          nodes.filter((node) =>
            node.type === "paragraph"
              ? paragraphHasImage(node) ||
                paragraphHasVisibleText(node) ||
                paragraphHasFormField(node) ||
                paragraphHasVisibleBorder(node)
              : true
          );

        return {
          headerPartName: headerSection?.partName,
          footerPartName: footerSection?.partName,
          headerNodes: filterVisibleNodes(headerSection?.nodes ?? []),
          footerNodes: filterVisibleNodes(footerSection?.nodes ?? []),
        };
      }),
    [
      pageSectionInfoByIndex,
      pageNodeSegmentsByPage,
      editor.model.metadata.headerSections,
      editor.model.metadata.footerSections,
      editor.model.metadata.sectionPropertiesXml,
    ]
  );
  const headerBodyClearanceFloorByPage = React.useMemo(() => {
    const next: Record<number, number> = {};
    for (
      let pageIndex = 0;
      pageIndex < pageNodeSegmentsByPage.length;
      pageIndex += 1
    ) {
      const headerNodes =
        pageHeaderAndFooterNodes[pageIndex]?.headerNodes ?? [];
      if (headerNodes.length === 0) {
        continue;
      }

      const pageLayout =
        pageSectionInfoByIndex[pageIndex]?.layout ?? documentLayout;
      const headerPartName =
        pageHeaderAndFooterNodes[pageIndex]?.headerPartName ??
        `page-${pageIndex}-header`;
      const reservePx = resolveHeaderPaginationReservePx(
        [
          {
            partName: headerPartName,
            referenceType: "default",
            nodes: headerNodes,
          },
        ],
        pageLayout
      );
      if (reservePx > 0) {
        next[pageIndex] = Math.max(0, Math.round(reservePx));
      }
    }

    return next;
  }, [
    documentLayout,
    pageHeaderAndFooterNodes,
    pageNodeSegmentsByPage.length,
    pageSectionInfoByIndex,
  ]);
  React.useLayoutEffect(() => {
    if (!paginationMeasurementEnabled) {
      return;
    }

    const rootElement = viewerRootRef.current;
    if (!rootElement || pageCount === 0) {
      setHeaderBodyClearanceByPage((current) =>
        Object.keys(current).length === 0 ? current : {}
      );
      return;
    }

    const zoomScale = resolveViewerMeasurementZoomScale(rootElement, 1);
    const next: Record<number, number> = {};
    for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
      if (!isPageVisible(pageIndex)) {
        continue;
      }

      const headerNodes = pageHeaderAndFooterNodes[pageIndex]?.headerNodes;
      if (!headerNodes || headerNodes.length === 0) {
        continue;
      }

      const headerElement = pageHeaderElementsRef.current.get(pageIndex);
      const pageElement = pageElementsRef.current.get(pageIndex);
      if (!headerElement || !pageElement) {
        continue;
      }

      const pageLayout =
        pageSectionInfoByIndex[pageIndex]?.layout ?? documentLayout;
      const pageRect = pageElement.getBoundingClientRect();
      const headerRect = headerElement.getBoundingClientRect();
      let visualBottom = headerRect.bottom;
      const maxOverflowCapPx = Math.max(
        16,
        Math.min(
          Math.round(pageLayout.pageHeightPx * 0.12),
          Math.round(
            pageLayout.marginsPx.top + pageLayout.headerDistancePx + 24
          )
        )
      );
      headerElement.querySelectorAll<HTMLElement>("*").forEach((element) => {
        if (!element.isConnected) {
          return;
        }

        const rect = element.getBoundingClientRect();
        if (rect.width <= 0 && rect.height <= 0) {
          return;
        }

        if (rect.bottom > headerRect.bottom + maxOverflowCapPx + 1) {
          return;
        }

        visualBottom = Math.max(visualBottom, rect.bottom);
      });

      const headerBottomPx = Math.max(
        0,
        (headerRect.bottom - pageRect.top) / zoomScale
      );
      const visualBottomPx = Math.max(
        0,
        (visualBottom - pageRect.top) / zoomScale
      );
      const requiredBodyTopPx = Math.max(
        pageLayout.marginsPx.top,
        visualBottomPx
      );
      const clearancePx = Math.max(0, requiredBodyTopPx - headerBottomPx);
      const boundedClearancePx = clampNumber(
        Math.round(clearancePx),
        0,
        maxOverflowCapPx
      );
      // Record every measured header page (including a 0 clearance) so the
      // renderer can tell "measured to need no extra clearance" apart from
      // "not measured yet". An in-flow header already provides its own body
      // offset, so once measured the estimated floor must not be re-added on
      // top of it (that double-counts and pushes the body far down the page).
      next[pageIndex] = boundedClearancePx;
    }

    setHeaderBodyClearanceByPage((current) => {
      const currentKeys = Object.keys(current).map((key) =>
        Number.parseInt(key, 10)
      );
      const nextKeys = Object.keys(next).map((key) => Number.parseInt(key, 10));
      if (currentKeys.length === nextKeys.length) {
        let equal = true;
        for (const key of nextKeys) {
          if ((current[key] ?? 0) !== (next[key] ?? 0)) {
            equal = false;
            break;
          }
        }
        if (equal) {
          return current;
        }
      }
      return next;
    });
  }, [
    documentLayout,
    isPageVisible,
    paginationMeasurementEnabled,
    pageCount,
    pageHeaderAndFooterNodes,
    pageSectionInfoByIndex,
    resolveViewerMeasurementZoomScale,
    visiblePageEndIndex,
    visiblePageStartIndex,
  ]);
  React.useEffect(() => {
    if (disableMeasuredImportPagination) {
      setMeasuredPageContentHeightByIndex((current) =>
        current.length === 0 ? current : []
      );
      setMeasuredPageContentIdentityKeysByIndex((current) =>
        current.length === 0 ? current : []
      );
      return;
    }

    if (!paginationMeasurementEnabled) {
      return;
    }

    const rootElement = viewerRootRef.current;
    if (!rootElement || pageCount === 0) {
      setMeasuredPageContentHeightByIndex((current) =>
        current.length === 0 ? current : []
      );
      setMeasuredPageContentIdentityKeysByIndex((current) =>
        current.length === 0 ? current : []
      );
      return;
    }

    if (Date.now() < paginationMeasurementSuspendUntilRef.current) {
      return;
    }

    const zoomScale = resolveViewerMeasurementZoomScale(rootElement, 1);
    const nextMeasuredPageIdentityKeys = pageNodeSegmentIdentityKeysByPage;
    const nextMeasuredPageDiagnostics = pageNodeSegmentsByPage.map(
      (_, pageIndex) => {
        const pageLayout =
          pageSectionInfoByIndex[pageIndex]?.layout ?? documentLayout;
        const fallbackHeightPx = Math.max(
          120,
          pageLayout.pageHeightPx -
            pageLayout.marginsPx.top -
            pageLayout.marginsPx.bottom
        );
        const pageIsVisible =
          pageIndex >= visiblePageStartIndex &&
          pageIndex <= visiblePageEndIndex;
        if (!pageIsVisible) {
          return {
            heightPx:
              measuredPageContentHeightByIndex?.[pageIndex] ?? fallbackHeightPx,
            bodyOverrunsFooter: false,
          };
        }

        const pageElement = pageElementsRef.current.get(pageIndex);
        if (!pageElement) {
          return {
            heightPx: fallbackHeightPx,
            bodyOverrunsFooter: false,
          };
        }

        const headerElement = pageHeaderElementsRef.current.get(pageIndex);
        const bodyElement = pageBodyElementsRef.current.get(pageIndex);
        const footerElement = pageFooterElementsRef.current.get(pageIndex);
        const headerHeightPx = headerElement
          ? Math.max(
              0,
              Math.round(
                headerElement.getBoundingClientRect().height / zoomScale
              )
            )
          : 0;

        const docxFooterTopPx = resolveFooterNodesFloatingBoundaryTopPx(
          pageHeaderAndFooterNodes[pageIndex]?.footerNodes ?? [],
          pageLayout
        );
        let footerTopPx = Number.isFinite(docxFooterTopPx)
          ? Math.round(docxFooterTopPx as number)
          : undefined;
        if (footerElement) {
          const pageRect = pageElement.getBoundingClientRect();
          const maxOverflowCapPx = Math.max(
            16,
            Math.min(
              Math.round(pageLayout.pageHeightPx * 0.12),
              Math.round(
                pageLayout.marginsPx.bottom + pageLayout.footerDistancePx + 24
              )
            )
          );
          const footerFlowBoundaryTopPx = Math.max(
            0,
            pageLayout.pageHeightPx - Math.max(0, pageLayout.footerDistancePx)
          );
          const minimumRelevantFooterTopPx = Number.isFinite(docxFooterTopPx)
            ? Math.max(
                0,
                Math.min(
                  Math.max(0, footerFlowBoundaryTopPx - maxOverflowCapPx),
                  Math.round((docxFooterTopPx as number) - maxOverflowCapPx)
                )
              )
            : Math.max(0, footerFlowBoundaryTopPx - maxOverflowCapPx);
          let visualTop = Number.POSITIVE_INFINITY;
          footerElement
            .querySelectorAll<HTMLElement>("*")
            .forEach((element) => {
              if (!element.isConnected) {
                return;
              }

              const rect = element.getBoundingClientRect();
              if (rect.width <= 0 && rect.height <= 0) {
                return;
              }

              const relativeTopPx = (rect.top - pageRect.top) / zoomScale;
              if (relativeTopPx < minimumRelevantFooterTopPx - 1) {
                return;
              }

              visualTop = Math.min(visualTop, rect.top);
            });

          if (Number.isFinite(visualTop)) {
            const measuredFooterTopPx = (visualTop - pageRect.top) / zoomScale;
            footerTopPx = Number.isFinite(footerTopPx)
              ? Math.min(
                  Math.round(footerTopPx as number),
                  Math.round(measuredFooterTopPx)
                )
              : Math.round(measuredFooterTopPx);
          }
        }

        const pageRect = pageElement.getBoundingClientRect();
        const bodyRect = bodyElement?.getBoundingClientRect();
        const bodyTopPx = bodyRect
          ? Math.max(0, Math.round((bodyRect.top - pageRect.top) / zoomScale))
          : undefined;
        const bodyImageCount =
          bodyElement?.querySelectorAll("[data-docx-image-location]").length ??
          0;
        const bodyTextTrimmedLength =
          bodyElement?.textContent?.replace(/\s+/g, "").length ?? 0;
        const skipBodyBottomAdjustment =
          bodyImageCount > 0 && bodyTextTrimmedLength === 0;
        const visualBodyBottom =
          bodyElement && bodyRect
            ? resolveMeasuredBodyRenderedBottomPx(
                Array.from(bodyElement.querySelectorAll<HTMLElement>("*")).map(
                  (element) => {
                    const rect = element.getBoundingClientRect();
                    return {
                      bottomPx: rect.bottom,
                      widthPx: rect.width,
                      heightPx: rect.height,
                      ignore:
                        !element.isConnected ||
                        Boolean(
                          element.closest("[data-docx-image-location]")
                        ) ||
                        Boolean(
                          element.closest(
                            `[${PAGE_CONTENT_MEASUREMENT_IGNORE_ATTRIBUTE}="true"]`
                          )
                        ),
                    };
                  }
                )
              )
            : undefined;
        const bodyRenderedBottomPx =
          bodyRect && Number.isFinite(visualBodyBottom)
            ? Math.max(
                0,
                Math.round(
                  ((visualBodyBottom as number) - pageRect.top) / zoomScale
                )
              )
            : undefined;

        return resolveMeasuredPageContentHeightDiagnostics({
          pageLayout,
          fallbackHeightPx,
          headerHeightPx,
          currentMeasuredHeightPx:
            measuredPageContentHeightByIndex?.[pageIndex],
          bodyTopPx,
          bodyRenderedBottomPx,
          footerTopPx,
          skipBodyBottomAdjustment,
        });
      }
    );
    const nextMeasuredHeights = nextMeasuredPageDiagnostics.map(
      (diagnostics) => diagnostics.heightPx
    );
    const overlappingPageIndexes = nextMeasuredPageDiagnostics.reduce<number[]>(
      (indexes, diagnostics, pageIndex) => {
        if (diagnostics.bodyOverrunsFooter) {
          indexes.push(pageIndex);
        }
        return indexes;
      },
      []
    );
    // The latch must compare against the same clamped target the pagination
    // plan chases (stored count raised to the hard-break minimum); the raw
    // stored count can sit below the achievable page count, which would keep
    // the latch from ever releasing an over-compressed plan.
    const storedDocumentPageCountForLatch =
      editor.model.metadata.documentPageCount;
    const latchTargetPageCount = Number.isFinite(storedDocumentPageCountForLatch)
      ? Math.max(
          collectDocxHardPageBreakStartNodeIndexes(editor.model).size + 1,
          Math.round(storedDocumentPageCountForLatch as number)
        )
      : undefined;
    const nextMeasuredBodyFooterOverlapLatchState =
      resolveMeasuredBodyFooterOverlapLatchState({
        pageCount,
        targetPageCount: latchTargetPageCount,
        overlappingPageIndexes,
        previousSignature:
          measuredBodyFooterOverlapCandidateRef.current.signature,
        previousConsecutivePasses:
          measuredBodyFooterOverlapCandidateRef.current.consecutivePasses,
        stabilityThreshold: MEASURED_BODY_FOOTER_OVERLAP_STABILITY_THRESHOLD,
      });
    measuredBodyFooterOverlapCandidateRef.current = {
      signature: nextMeasuredBodyFooterOverlapLatchState.signature,
      consecutivePasses:
        nextMeasuredBodyFooterOverlapLatchState.consecutivePasses,
    };
    const frameId = window.requestAnimationFrame(() => {
      if (nextMeasuredBodyFooterOverlapLatchState.shouldLatch) {
        setHasMeasuredBodyFooterOverlap(true);
      }
      setMeasuredPageContentHeightByIndex((current) => {
        const stabilizedHeights = stabilizeMeasuredPageContentHeights(
          current,
          nextMeasuredHeights,
          {
            currentPageIdentityKeys: measuredPageContentIdentityKeysByIndex,
            nextPageIdentityKeys: nextMeasuredPageIdentityKeys,
          }
        );
        if (current.length === stabilizedHeights.length) {
          let equal = true;
          for (
            let pageIndex = 0;
            pageIndex < stabilizedHeights.length;
            pageIndex += 1
          ) {
            if (
              Math.abs(
                (current[pageIndex] ?? 0) - stabilizedHeights[pageIndex]
              ) > 1
            ) {
              equal = false;
              break;
            }
          }
          if (equal) {
            return current;
          }
        }
        return stabilizedHeights;
      });
      setMeasuredPageContentIdentityKeysByIndex((current) => {
        if (current.length === nextMeasuredPageIdentityKeys.length) {
          let equal = true;
          for (
            let pageIndex = 0;
            pageIndex < nextMeasuredPageIdentityKeys.length;
            pageIndex += 1
          ) {
            if (
              current[pageIndex] !== nextMeasuredPageIdentityKeys[pageIndex]
            ) {
              equal = false;
              break;
            }
          }
          if (equal) {
            return current;
          }
        }
        return nextMeasuredPageIdentityKeys;
      });
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [
    disableMeasuredImportPagination,
    documentLayout,
    measuredPageContentHeightByIndex,
    measuredPageContentIdentityKeysByIndex,
    paginationMeasurementEnabled,
    paginationMeasurementEpoch,
    pageCount,
    editor.model.metadata.documentPageCount,
    pageNodeSegmentsByPage,
    pageNodeSegmentIdentityKeysByPage,
    pageSectionInfoByIndex,
    pageHeaderAndFooterNodes,
    resolveViewerMeasurementZoomScale,
    visiblePageEndIndex,
    visiblePageStartIndex,
  ]);
  React.useEffect(() => {
    if (
      !deferInitialPaginationPaint ||
      isInitialPaginationSettled ||
      disableMeasuredImportPagination
    ) {
      return;
    }

    if (pageCount <= 0) {
      return;
    }

    const nextPageCount = Math.max(1, Math.round(pageCount));
    const oscillationState = initialPaginationPageCountOscillationRef.current;
    oscillationState.distinctPageCounts.add(nextPageCount);
    if (
      oscillationState.lastPageCount !== undefined &&
      oscillationState.lastPageCount !== nextPageCount
    ) {
      oscillationState.changeCount += 1;
    }
    oscillationState.lastPageCount = nextPageCount;

    if (
      oscillationState.distinctPageCounts.size <
        INITIAL_PAGINATION_PAGE_COUNT_OSCILLATION_DISTINCT_THRESHOLD ||
      oscillationState.changeCount <
        INITIAL_PAGINATION_PAGE_COUNT_OSCILLATION_CHANGE_THRESHOLD
    ) {
      return;
    }

    initialPaginationStableSignatureRef.current = undefined;
    setDisableMeasuredImportPagination(true);
    setMeasuredPageContentHeightByIndex([]);
    setTableMeasuredRowHeights({});
  }, [
    deferInitialPaginationPaint,
    disableMeasuredImportPagination,
    isInitialPaginationSettled,
    pageCount,
  ]);
  React.useEffect(() => {
    if (
      !deferInitialPaginationPaint ||
      !isInitialPaginationSettled ||
      initialPaginationBackgroundRefinementUnlocked
    ) {
      return;
    }

    if (typeof window === "undefined") {
      setInitialPaginationBackgroundRefinementUnlocked(true);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      React.startTransition(() => {
        setInitialPaginationBackgroundRefinementUnlocked(true);
      });
    }, INITIAL_PAGINATION_BACKGROUND_REFINEMENT_DELAY_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    deferInitialPaginationPaint,
    initialPaginationBackgroundRefinementUnlocked,
    isInitialPaginationSettled,
  ]);
  React.useEffect(() => {
    if (!deferInitialPaginationPaint || isInitialPaginationSettled) {
      return;
    }

    if (pageCount === 0) {
      setIsInitialPaginationSettled(true);
      return;
    }

    if (Date.now() < paginationMeasurementSuspendUntilRef.current) {
      return;
    }

    const visiblePageIndexes: number[] = [];
    for (
      let pageIndex = visiblePageStartIndex;
      pageIndex <= visiblePageEndIndex;
      pageIndex += 1
    ) {
      visiblePageIndexes.push(pageIndex);
    }

    const pageElementsReady = visiblePageIndexes.every((pageIndex) => {
      if (
        !pageElementsRef.current.get(pageIndex) ||
        !pageBodyElementsRef.current.get(pageIndex)
      ) {
        return false;
      }

      const pageSections = pageHeaderAndFooterNodes[pageIndex];
      if (
        (pageSections?.headerNodes.length ?? 0) > 0 &&
        !pageHeaderElementsRef.current.get(pageIndex)
      ) {
        return false;
      }
      if (
        (pageSections?.footerNodes.length ?? 0) > 0 &&
        !pageFooterElementsRef.current.get(pageIndex)
      ) {
        return false;
      }
      return true;
    });
    if (!pageElementsReady) {
      return;
    }

    const segmentationSignature = visiblePageIndexes
      .map((pageIndex) =>
        (pageNodeSegmentsByPage[pageIndex] ?? [])
          .map((segment) => {
            const tableRowRange = segment.tableRowRange
              ? `${segment.tableRowRange.startRowIndex}-${segment.tableRowRange.endRowIndex}`
              : "none";
            const tableRowSlice = segment.tableRowSlice
              ? `${Math.max(
                  0,
                  Math.round(segment.tableRowSlice.rowIndex)
                )}:${Math.max(
                  0,
                  Math.round(segment.tableRowSlice.startOffsetPx)
                )}:${Math.max(
                  MIN_PARAGRAPH_LINE_HEIGHT_PX,
                  Math.round(segment.tableRowSlice.sliceHeightPx)
                )}`
              : "none";
            const paragraphLineRange = segment.paragraphLineRange
              ? `${segment.paragraphLineRange.startLineIndex}-${segment.paragraphLineRange.endLineIndex}`
              : "none";
            return `${segment.nodeIndex}:${tableRowRange}:${tableRowSlice}:${paragraphLineRange}`;
          })
          .join(",")
      )
      .join("|");
    const measurementSignature = visiblePageIndexes
      .map(
        (pageIndex) =>
          `${Math.round(measuredPageContentHeightByIndex[pageIndex] ?? 0)}:${
            headerBodyClearanceByPage[pageIndex] ?? 0
          }`
      )
      .join("|");
    const visibleTableMeasurementSignature = visiblePageIndexes
      .map((pageIndex) =>
        (pageNodeSegmentsByPage[pageIndex] ?? [])
          .filter(
            (segment) => editor.model.nodes[segment.nodeIndex]?.type === "table"
          )
          .map((segment) => {
            const measuredHeights =
              tableMeasuredRowHeights[segment.nodeIndex] ?? [];
            return `${segment.nodeIndex}:${measuredHeights.join(",")}`;
          })
          .join(";")
      )
      .join("|");
    const nextSignature =
      `${pageCount}::${segmentationSignature}::${measurementSignature}` +
      `::${visibleTableMeasurementSignature}`;

    initialPaginationStableSignatureRef.current = nextSignature;
    if (
      initialPaginationRevealFrameRef.current !== null &&
      typeof window !== "undefined" &&
      typeof window.cancelAnimationFrame === "function"
    ) {
      window.cancelAnimationFrame(initialPaginationRevealFrameRef.current);
    }

    let revealCancelled = false;
    let revealTimeoutId: number | null = null;
    const settleAndReveal = (): void => {
      if (
        typeof window === "undefined" ||
        typeof window.requestAnimationFrame !== "function"
      ) {
        setIsInitialPaginationSettled(true);
        return;
      }

      revealTimeoutId = window.setTimeout(() => {
        revealTimeoutId = null;
        initialPaginationRevealFrameRef.current = window.requestAnimationFrame(
          () => {
            if (revealCancelled) {
              return;
            }
            if (Date.now() < paginationMeasurementSuspendUntilRef.current) {
              return;
            }
            if (initialPaginationStableSignatureRef.current !== nextSignature) {
              return;
            }
            initialPaginationRevealFrameRef.current = null;
            setIsInitialPaginationSettled(true);
          }
        );
      }, INITIAL_PAGINATION_STABILITY_IDLE_MS);
    };

    settleAndReveal();

    return () => {
      revealCancelled = true;
      if (revealTimeoutId !== null && typeof window !== "undefined") {
        window.clearTimeout(revealTimeoutId);
      }
      if (
        initialPaginationRevealFrameRef.current !== null &&
        typeof window !== "undefined" &&
        typeof window.cancelAnimationFrame === "function"
      ) {
        window.cancelAnimationFrame(initialPaginationRevealFrameRef.current);
      }
      initialPaginationRevealFrameRef.current = null;
    };
  }, [
    deferInitialPaginationPaint,
    editor.model.nodes,
    headerBodyClearanceByPage,
    isInitialPaginationSettled,
    measuredPageContentHeightByIndex,
    pageCount,
    pageHeaderAndFooterNodes,
    pageNodeSegmentsByPage,
    paginationMeasurementEpoch,
    tableMeasuredRowHeights,
    visiblePageEndIndex,
    visiblePageStartIndex,
  ]);
  const setSingleTableCellSelection = React.useCallback(
    (tableIndex: number, rowIndex: number, cellIndex: number): void => {
      const nextRange: DocxTableCellSelectionRange = {
        tableIndex,
        anchorRowIndex: rowIndex,
        anchorCellIndex: cellIndex,
        focusRowIndex: rowIndex,
        focusCellIndex: cellIndex,
      };
      setTableCellSelectionRange((current) => {
        if (sameTableCellSelectionRange(current, nextRange)) {
          return current;
        }
        return nextRange;
      });
    },
    []
  );
  const clearTableCellSelection = React.useCallback((): void => {
    setTableCellSelectionRange((current) => (current ? undefined : current));
  }, []);
  const isPointWithinTableHandleHoverZone = React.useCallback(
    (tableIndex: number, x: number, y: number): boolean => {
      const tableElement = tableElementsRef.current.get(tableIndex);
      if (!tableElement || !tableElement.isConnected) {
        return false;
      }

      const rect = tableElement.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) {
        return false;
      }

      return (
        x >= rect.left - TABLE_HANDLE_HOVER_OUTSET_PX &&
        x <= rect.right + TABLE_HANDLE_HOVER_OUTSET_PX &&
        y >= rect.top - TABLE_HANDLE_HOVER_OUTSET_PX &&
        y <= rect.bottom + TABLE_HANDLE_HOVER_OUTSET_PX
      );
    },
    []
  );
  const resolveHoveredTableHandleIndexFromPoint = React.useCallback(
    (x: number, y: number): number | undefined => {
      let nextHoveredTableIndex: number | undefined;
      let bestDistance = Number.POSITIVE_INFINITY;

      tableElementsRef.current.forEach((tableElement, tableIndex) => {
        if (!tableElement || !tableElement.isConnected) {
          return;
        }

        const rect = tableElement.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) {
          return;
        }

        if (
          x < rect.left - TABLE_HANDLE_HOVER_OUTSET_PX ||
          x > rect.right + TABLE_HANDLE_HOVER_OUTSET_PX ||
          y < rect.top - TABLE_HANDLE_HOVER_OUTSET_PX ||
          y > rect.bottom + TABLE_HANDLE_HOVER_OUTSET_PX
        ) {
          return;
        }

        const dx =
          x < rect.left ? rect.left - x : x > rect.right ? x - rect.right : 0;
        const dy =
          y < rect.top ? rect.top - y : y > rect.bottom ? y - rect.bottom : 0;
        const distance = Math.hypot(dx, dy);

        if (distance < bestDistance) {
          bestDistance = distance;
          nextHoveredTableIndex = tableIndex;
        }
      });

      return nextHoveredTableIndex;
    },
    []
  );
  const onViewerPointerMove = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>): void => {
      if (isReadOnly) {
        return;
      }
      if (tableMoveDragRef.current) {
        return;
      }

      const nextHoveredTableIndex = resolveHoveredTableHandleIndexFromPoint(
        event.clientX,
        event.clientY
      );
      setHoveredTableHandleTableIndex((current) =>
        current === nextHoveredTableIndex ? current : nextHoveredTableIndex
      );
    },
    [isReadOnly, resolveHoveredTableHandleIndexFromPoint]
  );
  const onViewerPointerLeave = React.useCallback((): void => {
    if (isReadOnly) {
      return;
    }
    if (tableMoveDragRef.current) {
      return;
    }
    setHoveredTableHandleTableIndex(undefined);
  }, [isReadOnly]);
  const selectWholeTable = React.useCallback(
    (tableIndex: number): void => {
      const tableNode = editor.model.nodes[tableIndex];
      if (!tableNode || tableNode.type !== "table") {
        return;
      }

      const extents = tableSelectableCellExtents(tableNode);
      if (!extents) {
        return;
      }

      setTableCellSelectionRange({
        tableIndex,
        anchorRowIndex: extents.first.rowIndex,
        anchorCellIndex: extents.first.cellIndex,
        focusRowIndex: extents.last.rowIndex,
        focusCellIndex: extents.last.cellIndex,
      });
      editor.selectTableCell(
        tableIndex,
        extents.first.rowIndex,
        extents.first.cellIndex
      );
      editor.setActiveTextRange(undefined);
    },
    [editor]
  );
  const resolveTableMoveDropTarget = React.useCallback(
    (
      tableIndex: number,
      point: {
        x: number;
        y: number;
      }
    ):
      | {
          targetNodeIndex: number;
          preview: {
            top: number;
            left: number;
            width: number;
          };
        }
      | undefined => {
      const rootElement = viewerRootRef.current;
      if (!rootElement) {
        return undefined;
      }

      const nodeElements = new Map<number, HTMLElement>();
      paragraphElementsRef.current.forEach((element, nodeIndex) => {
        if (element.isConnected) {
          nodeElements.set(nodeIndex, element);
        }
      });
      tableElementsRef.current.forEach((element, nodeIndex) => {
        if (element.isConnected) {
          nodeElements.set(nodeIndex, element);
        }
      });

      const orderedNodes = Array.from(nodeElements.entries())
        .map(([nodeIndex, element]) => {
          const rect = element.getBoundingClientRect();
          return {
            nodeIndex,
            rect,
          };
        })
        .filter((entry) => entry.rect.height > 0 && entry.rect.width > 0)
        .sort((left, right) => left.nodeIndex - right.nodeIndex);
      if (orderedNodes.length === 0) {
        return undefined;
      }

      const rootRect = rootElement.getBoundingClientRect();
      let targetNodeIndex = orderedNodes[orderedNodes.length - 1].nodeIndex + 1;
      let previewTop =
        orderedNodes[orderedNodes.length - 1].rect.bottom - rootRect.top;
      let previewLeft =
        orderedNodes[orderedNodes.length - 1].rect.left - rootRect.left;
      let previewWidth = orderedNodes[orderedNodes.length - 1].rect.width;

      for (const entry of orderedNodes) {
        const midpointY = entry.rect.top + entry.rect.height / 2;
        if (point.y < midpointY) {
          targetNodeIndex = entry.nodeIndex;
          previewTop = entry.rect.top - rootRect.top;
          previewLeft = entry.rect.left - rootRect.left;
          previewWidth = entry.rect.width;
          break;
        }
      }

      if (!Number.isFinite(targetNodeIndex)) {
        return undefined;
      }

      const clampedTargetNodeIndex = clampNumber(
        targetNodeIndex,
        0,
        Math.max(0, editor.model.nodes.length)
      );
      return {
        targetNodeIndex: clampedTargetNodeIndex,
        preview: {
          top: Math.max(0, Math.round(previewTop)),
          left: Math.max(0, Math.round(previewLeft)),
          width: Math.max(0, Math.round(previewWidth)),
        },
      };
    },
    [editor.model.nodes.length]
  );
  const beginTableMoveDrag = React.useCallback(
    (event: React.PointerEvent<HTMLElement>, tableIndex: number): void => {
      if (isReadOnly) {
        return;
      }
      if (event.button !== 0 && event.button !== undefined) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      selectWholeTable(tableIndex);
      setHoveredTableHandleTableIndex(tableIndex);

      tableMoveDragRef.current = {
        pointerId: event.pointerId,
        tableIndex,
        startX: event.clientX,
        startY: event.clientY,
        hasMoved: false,
      };

      const onPointerMove = (pointerMoveEvent: PointerEvent): void => {
        const dragState = tableMoveDragRef.current;
        if (!dragState || pointerMoveEvent.pointerId !== dragState.pointerId) {
          return;
        }

        const deltaX = Math.abs(pointerMoveEvent.clientX - dragState.startX);
        const deltaY = Math.abs(pointerMoveEvent.clientY - dragState.startY);
        if (deltaX + deltaY < TABLE_MOVE_DRAG_THRESHOLD_PX) {
          return;
        }

        dragState.hasMoved = true;
        const target = resolveTableMoveDropTarget(dragState.tableIndex, {
          x: pointerMoveEvent.clientX,
          y: pointerMoveEvent.clientY,
        });
        setTableMoveDropPreview(target?.preview);
        pointerMoveEvent.preventDefault();
      };

      const onPointerUp = (pointerUpEvent: PointerEvent): void => {
        const dragState = tableMoveDragRef.current;
        if (!dragState || pointerUpEvent.pointerId !== dragState.pointerId) {
          return;
        }

        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        window.removeEventListener("pointercancel", onPointerUp);
        tableMoveDragRef.current = undefined;
        const pointerTarget = document.elementFromPoint(
          pointerUpEvent.clientX,
          pointerUpEvent.clientY
        );
        const tableElement = tableElementsRef.current.get(dragState.tableIndex);
        const pointerStillOverTable = Boolean(
          pointerTarget && tableElement?.contains(pointerTarget)
        );
        setHoveredTableHandleTableIndex(
          pointerStillOverTable ? dragState.tableIndex : undefined
        );

        const target = resolveTableMoveDropTarget(dragState.tableIndex, {
          x: pointerUpEvent.clientX,
          y: pointerUpEvent.clientY,
        });
        setTableMoveDropPreview(undefined);
        if (!dragState.hasMoved) {
          return;
        }

        if (!target) {
          return;
        }

        editor.moveTable(dragState.tableIndex, target.targetNodeIndex);
      };

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp, { once: true });
      window.addEventListener("pointercancel", onPointerUp, { once: true });
    },
    [editor, isReadOnly, resolveTableMoveDropTarget, selectWholeTable]
  );
  const closeTableContextMenu = React.useCallback((): void => {
    setTableContextMenuState(undefined);
  }, []);
  const closeContextMenu = React.useCallback((): void => {
    setContextMenuState(undefined);
  }, []);
  const openContextMenu = React.useCallback(
    (
      context: DocxContextMenuContext,
      point: {
        x: number;
        y: number;
      }
    ): void => {
      setTableContextMenuState(undefined);
      setContextMenuState({
        ...context,
        clientX: point.x,
        clientY: point.y,
      });
    },
    []
  );
  const openTableContextMenu = React.useCallback(
    (
      context: DocxTableContextMenuContext,
      point: {
        x: number;
        y: number;
      }
    ): void => {
      setContextMenuState(undefined);
      setTableContextMenuState({
        ...context,
        clientX: point.x,
        clientY: point.y,
      });
    },
    []
  );
  const runTableContextMenuAction = React.useCallback(
    (
      actionId: DocxTableContextMenuActionId,
      providedContext?: DocxTableContextMenuContext
    ): void => {
      const context = providedContext ?? tableContextMenuState;
      if (!context) {
        return;
      }

      switch (actionId) {
        case "insert-row-above":
          editor.insertTableRow(context.tableIndex, context.rowIndex, "above");
          break;
        case "insert-row-below":
          editor.insertTableRow(context.tableIndex, context.rowIndex, "below");
          break;
        case "insert-column-left":
          editor.insertTableColumn(
            context.tableIndex,
            context.cellIndex,
            "left",
            context.rowIndex
          );
          break;
        case "insert-column-right":
          editor.insertTableColumn(
            context.tableIndex,
            context.cellIndex,
            "right",
            context.rowIndex
          );
          break;
        case "delete-row":
          editor.deleteTableRow(context.tableIndex, context.rowIndex);
          break;
        case "delete-column":
          editor.deleteTableColumn(
            context.tableIndex,
            context.cellIndex,
            context.rowIndex
          );
          break;
        case "delete-table":
          editor.deleteTable(context.tableIndex);
          break;
        default:
          break;
      }

      closeTableContextMenu();
    },
    [closeTableContextMenu, editor, tableContextMenuState]
  );
  const tableContextMenuActions = React.useMemo(
    () => [...DEFAULT_TABLE_CONTEXT_MENU_ACTIONS],
    []
  );
  const resolveTableContextMenuLocation = React.useCallback(
    (
      tableIndex: number,
      fallbackRowIndex = 0,
      fallbackCellIndex = 0
    ): DocxTableContextMenuContext => {
      const tableNode = editor.model.nodes[tableIndex];
      if (!tableNode || tableNode.type !== "table") {
        return {
          tableIndex,
          rowIndex: Math.max(0, fallbackRowIndex),
          cellIndex: Math.max(0, fallbackCellIndex),
        };
      }

      const selection = editor.selection;
      const rowIndex = clampNumber(
        selection.kind === "table-cell" && selection.tableIndex === tableIndex
          ? selection.rowIndex
          : fallbackRowIndex,
        0,
        Math.max(0, tableNode.rows.length - 1)
      );
      const row = tableNode.rows[rowIndex];
      const cellIndex = clampNumber(
        selection.kind === "table-cell" && selection.tableIndex === tableIndex
          ? selection.cellIndex
          : fallbackCellIndex,
        0,
        Math.max(0, (row?.cells.length ?? 1) - 1)
      );

      return {
        tableIndex,
        rowIndex,
        cellIndex,
      };
    },
    [editor.model.nodes, editor.selection]
  );

  React.useEffect(() => {
    if (!tableContextMenuState && !contextMenuState) {
      return;
    }

    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest(
          "[data-docx-table-context-menu='true'],[data-docx-context-menu='true']"
        )
      ) {
        return;
      }
      setTableContextMenuState(undefined);
      setContextMenuState(undefined);
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") {
        return;
      }
      event.preventDefault();
      setTableContextMenuState(undefined);
      setContextMenuState(undefined);
    };

    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("keydown", onKeyDown, true);
    };
  }, [contextMenuState, tableContextMenuState]);

  React.useEffect(() => {
    if (!tableCellSelectionRange) {
      return;
    }

    const tableNode = editor.model.nodes[tableCellSelectionRange.tableIndex];
    if (!tableNode || tableNode.type !== "table") {
      setTableCellSelectionRange(undefined);
    }
  }, [editor.model.nodes, tableCellSelectionRange]);
  React.useEffect(() => {
    if (!tableContextMenuState) {
      return;
    }

    const tableNode = editor.model.nodes[tableContextMenuState.tableIndex];
    if (!tableNode || tableNode.type !== "table") {
      setTableContextMenuState(undefined);
    }
  }, [editor.model.nodes, tableContextMenuState]);
  React.useEffect(() => {
    if (!contextMenuState || contextMenuState.kind !== "image") {
      return;
    }

    const imageLocation = contextMenuState.image?.location;
    if (!imageLocation) {
      setContextMenuState(undefined);
      return;
    }

    const { paragraph } = getParagraphAtLocation(
      editor.model,
      imageLocationToParagraphLocation(imageLocation)
    );
    const child = paragraph?.children[imageLocation.childIndex];
    if (!child || child.type !== "image") {
      setContextMenuState(undefined);
    }
  }, [contextMenuState, editor.model]);

  const placeCaretInsideElement = React.useCallback(
    (
      element: HTMLElement,
      point?: {
        x: number;
        y: number;
      }
    ): void => {
      const selection = window.getSelection();
      if (!selection) {
        return;
      }

      const collapseAtStart = (): void => {
        const range = document.createRange();
        range.selectNodeContents(element);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      };

      const collapseAtEnd = (): void => {
        const range = document.createRange();
        range.selectNodeContents(element);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      };

      if (!point) {
        collapseAtEnd();
        return;
      }

      const documentWithCaret = document as Document & {
        caretPositionFromPoint?: (
          x: number,
          y: number
        ) => {
          offsetNode: Node;
          offset: number;
        } | null;
        caretRangeFromPoint?: (x: number, y: number) => Range | null;
      };

      const resolveCollapsedRangeAtPoint = (
        x: number,
        y: number
      ): Range | undefined => {
        let range: Range | undefined;
        if (typeof documentWithCaret.caretPositionFromPoint === "function") {
          const position = documentWithCaret.caretPositionFromPoint(x, y);
          if (position) {
            range = document.createRange();
            range.setStart(position.offsetNode, position.offset);
            range.collapse(true);
          }
        } else if (
          typeof documentWithCaret.caretRangeFromPoint === "function"
        ) {
          const pointRange = documentWithCaret.caretRangeFromPoint(x, y);
          if (pointRange) {
            range = pointRange;
            range.collapse(true);
          }
        }
        return range;
      };

      const resolveRangeByNearestTextPosition = (
        x: number,
        y: number
      ): Range | undefined => {
        const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
        const caretProbe = document.createRange();
        const glyphProbe = document.createRange();
        let bestRange: Range | undefined;
        let bestDistance = Number.POSITIVE_INFINITY;
        let node: Node | null = walker.nextNode();

        while (node) {
          if (
            node instanceof Text &&
            node.parentElement?.closest("[data-docx-numbering-label='true']")
          ) {
            node = walker.nextNode();
            continue;
          }

          if (!(node instanceof Text)) {
            node = walker.nextNode();
            continue;
          }

          const textLength = node.textContent?.length ?? 0;
          for (let offset = 0; offset <= textLength; offset += 1) {
            try {
              caretProbe.setStart(node, offset);
              caretProbe.collapse(true);
            } catch {
              continue;
            }

            let caretX = 0;
            let rectTop = 0;
            let rectBottom = 0;
            if (textLength > 0) {
              const glyphStart = Math.max(
                0,
                Math.min(
                  textLength - 1,
                  offset === textLength ? offset - 1 : offset
                )
              );
              const glyphEnd = Math.min(textLength, glyphStart + 1);
              if (glyphEnd <= glyphStart) {
                continue;
              }

              let glyphRect: DOMRect;
              try {
                glyphProbe.setStart(node, glyphStart);
                glyphProbe.setEnd(node, glyphEnd);
                glyphRect = glyphProbe.getBoundingClientRect();
              } catch {
                continue;
              }

              if (glyphRect.width === 0 && glyphRect.height === 0) {
                continue;
              }

              caretX = offset === textLength ? glyphRect.right : glyphRect.left;
              rectTop = glyphRect.top;
              rectBottom = glyphRect.bottom;
            } else {
              const caretRect = caretProbe.getBoundingClientRect();
              if (caretRect.width === 0 && caretRect.height === 0) {
                continue;
              }
              caretX = caretRect.left;
              rectTop = caretRect.top;
              rectBottom = caretRect.bottom;
            }

            const dx = Math.abs(x - caretX);
            const dy =
              y < rectTop ? rectTop - y : y > rectBottom ? y - rectBottom : 0;
            const distance = dx * dx + dy * dy;
            if (distance < bestDistance) {
              bestDistance = distance;
              bestRange = caretProbe.cloneRange();
            }
          }

          node = walker.nextNode();
        }

        return bestRange;
      };

      let range = resolveCollapsedRangeAtPoint(point.x, point.y);
      const elementRect = element.getBoundingClientRect();
      // WebKit/Chromium can return the paragraph container itself (offset 0)
      // when clicking around styled runs. Probe nearby x-positions to recover
      // the nearest text-node caret instead of forcing insertion at line start.
      if (range && range.startContainer === element) {
        const MAX_DELTA = 36;
        for (let delta = 1; delta <= MAX_DELTA; delta += 1) {
          const candidates = [
            resolveCollapsedRangeAtPoint(point.x + delta, point.y),
            resolveCollapsedRangeAtPoint(point.x - delta, point.y),
            resolveCollapsedRangeAtPoint(point.x, point.y + delta),
            resolveCollapsedRangeAtPoint(point.x, point.y - delta),
            resolveCollapsedRangeAtPoint(point.x + delta, point.y + delta),
            resolveCollapsedRangeAtPoint(point.x - delta, point.y - delta),
            resolveCollapsedRangeAtPoint(point.x + delta, point.y - delta),
            resolveCollapsedRangeAtPoint(point.x - delta, point.y + delta),
          ];
          const resolvedCandidate = candidates.find(
            (candidateRange) =>
              candidateRange &&
              element.contains(candidateRange.startContainer) &&
              candidateRange.startContainer !== element
          );
          if (resolvedCandidate) {
            range = resolvedCandidate;
            break;
          }
        }
      }

      const suspiciousStartResolvedRange = Boolean(
        range &&
          range.collapsed &&
          point.x > elementRect.left + 12 &&
          ((range.startContainer === element && range.startOffset === 0) ||
            (range.startContainer instanceof Text && range.startOffset === 0))
      );
      if (
        !range ||
        !element.contains(range.startContainer) ||
        range.startContainer === element ||
        suspiciousStartResolvedRange
      ) {
        const resolvedByGeometry = resolveRangeByNearestTextPosition(
          point.x,
          point.y
        );
        if (
          resolvedByGeometry &&
          element.contains(resolvedByGeometry.startContainer)
        ) {
          range = resolvedByGeometry;
        }
      }

      if (!range || !element.contains(range.startContainer)) {
        const shouldCollapseAtStart = point.x <= elementRect.left + 12;
        if (shouldCollapseAtStart) {
          collapseAtStart();
        } else {
          collapseAtEnd();
        }
        return;
      }

      if (range.startContainer === element) {
        const shouldCollapseAtStart = point.x <= elementRect.left + 12;
        if (shouldCollapseAtStart) {
          collapseAtStart();
        } else {
          collapseAtEnd();
        }
        return;
      }

      selection.removeAllRanges();
      selection.addRange(range);
    },
    []
  );

  React.useEffect(() => {
    const pendingFocus = pendingSectionParagraphFocusRef.current;
    if (!pendingFocus) {
      return;
    }

    window.requestAnimationFrame(() => {
      const latestPendingFocus = pendingSectionParagraphFocusRef.current;
      if (!latestPendingFocus) {
        return;
      }

      const element = sectionParagraphElementsRef.current.get(
        latestPendingFocus.draftKey
      );
      if (!element) {
        return;
      }

      element.focus();
      placeCaretInsideElement(element, latestPendingFocus.point);
      pendingSectionParagraphFocusRef.current = undefined;
    });
  }, [activeHeaderFooterEdit, placeCaretInsideElement]);

  const setSelectionWithinElementByTextOffsets = React.useCallback(
    (element: HTMLElement, startOffset: number, endOffset: number): void => {
      const selection = window.getSelection();
      if (!selection) {
        return;
      }

      const resolveDomPosition = (
        container: HTMLElement,
        targetOffset: number
      ): {
        node: Node;
        offset: number;
      } => {
        const safeOffset = Math.max(0, Math.round(targetOffset));
        const walker = document.createTreeWalker(
          container,
          NodeFilter.SHOW_TEXT
        );
        let traversed = 0;
        let currentTextNode: Node | null = walker.nextNode();

        while (currentTextNode) {
          if (
            currentTextNode instanceof Text &&
            currentTextNode.parentElement?.closest(
              "[data-docx-numbering-label='true']"
            )
          ) {
            currentTextNode = walker.nextNode();
            continue;
          }

          const textLength = currentTextNode.textContent?.length ?? 0;
          if (traversed + textLength >= safeOffset) {
            return {
              node: currentTextNode,
              offset: Math.max(0, safeOffset - traversed),
            };
          }

          traversed += textLength;
          currentTextNode = walker.nextNode();
        }

        return {
          node: container,
          offset: container.childNodes.length,
        };
      };

      try {
        const range = document.createRange();
        const normalizedStart = Math.max(0, Math.min(startOffset, endOffset));
        const normalizedEnd = Math.max(
          normalizedStart,
          Math.max(startOffset, endOffset)
        );
        const start = resolveDomPosition(element, normalizedStart);
        const end = resolveDomPosition(element, normalizedEnd);
        range.setStart(start.node, start.offset);
        range.setEnd(end.node, end.offset);
        selection.removeAllRanges();
        selection.addRange(range);
      } catch {
        placeCaretInsideElement(element);
      }
    },
    [placeCaretInsideElement]
  );

  const paragraphTextLengthFromElement = React.useCallback(
    (element: HTMLElement): number => {
      const range = document.createRange();
      range.selectNodeContents(element);
      return textLengthFromRange(range);
    },
    []
  );

  const activateEditableParagraphSegment = React.useCallback(
    (
      nodeIndex: number,
      paragraphLineRange?: ParagraphLineRange,
      point?: {
        x: number;
        y: number;
      }
    ): void => {
      if (!paragraphSegmentHasPartialLineRange(paragraphLineRange)) {
        pendingParagraphSegmentFocusRef.current = undefined;
        setActiveEditableParagraphSegment((current) =>
          current?.nodeIndex === nodeIndex ? undefined : current
        );
        return;
      }

      const segmentRange = paragraphLineRange!;

      const nextSegment: ParagraphSegmentIdentity = {
        nodeIndex,
        startLineIndex: segmentRange.startLineIndex,
        endLineIndex: segmentRange.endLineIndex,
      };
      pendingParagraphSegmentFocusRef.current = {
        ...nextSegment,
        ...(point ? { point } : undefined),
      };
      setActiveEditableParagraphSegment((current) =>
        paragraphSegmentIdentityMatches(current, nodeIndex, segmentRange)
          ? current
          : nextSegment
      );
    },
    []
  );

  const resolveParagraphHostElement = React.useCallback(
    (location: DocxTextRangeLocation): HTMLElement | undefined => {
      if (location.kind === "paragraph") {
        const wrappedSurface = wrappedParagraphSurfaceRegistryRef.current.get(
          paragraphLocationKey({
            kind: "paragraph",
            nodeIndex: location.nodeIndex,
          })
        );
        if (wrappedSurface?.element.isConnected) {
          return wrappedSurface.element;
        }

        const trackedElement = paragraphElementsRef.current.get(
          location.nodeIndex
        );
        if (trackedElement?.isConnected) {
          return trackedElement;
        }

        const paragraphHosts = Array.from(
          viewerRootRef.current?.querySelectorAll<HTMLElement>(
            `[data-docx-paragraph-kind="paragraph"][data-docx-paragraph-node-index="${location.nodeIndex}"]`
          ) ?? []
        );
        const editableHost = paragraphHosts.find(
          (host) => host.isContentEditable
        );
        return editableHost ?? paragraphHosts[0] ?? undefined;
      }

      if (!viewerRootRef.current) {
        return undefined;
      }

      return (
        viewerRootRef.current.querySelector<HTMLElement>(
          `[data-docx-paragraph-kind="table-cell"]` +
            `[data-docx-table-index="${location.tableIndex}"]` +
            `[data-docx-row-index="${location.rowIndex}"]` +
            `[data-docx-cell-index="${location.cellIndex}"]` +
            `[data-docx-paragraph-index="${location.paragraphIndex}"]`
        ) ?? undefined
      );
    },
    [paragraphElementsRef]
  );

  const resolveDomPositionFromTextOffset = React.useCallback(
    (
      element: HTMLElement,
      targetOffset: number
    ): { node: Node; offset: number } => {
      const safeOffset = Math.max(0, Math.round(targetOffset));
      const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
      let traversed = 0;
      let currentTextNode: Node | null = walker.nextNode();

      while (currentTextNode) {
        if (
          currentTextNode instanceof Text &&
          currentTextNode.parentElement?.closest(
            "[data-docx-numbering-label='true']"
          )
        ) {
          currentTextNode = walker.nextNode();
          continue;
        }

        const textLength = currentTextNode.textContent?.length ?? 0;
        if (traversed + textLength >= safeOffset) {
          return {
            node: currentTextNode,
            offset: Math.max(0, safeOffset - traversed),
          };
        }
        traversed += textLength;
        currentTextNode = walker.nextNode();
      }

      return {
        node: element,
        offset: element.childNodes.length,
      };
    },
    []
  );

  const resolveParagraphHostElementForOffset = React.useCallback(
    (paragraphIndex: number, offset: number): HTMLElement | undefined => {
      const rootElement = viewerRootRef.current;
      const trackedElement = paragraphElementsRef.current.get(paragraphIndex);
      const paragraphHosts = Array.from(
        rootElement?.querySelectorAll<HTMLElement>(
          `[data-docx-paragraph-kind="paragraph"][data-docx-paragraph-node-index="${paragraphIndex}"]`
        ) ?? []
      );
      const candidates = [
        ...(trackedElement ? [trackedElement] : []),
        ...paragraphHosts,
      ].filter(
        (candidate, index, all) =>
          candidate.isConnected && all.indexOf(candidate) === index
      );
      const safeOffset = Math.max(0, Math.round(offset));

      for (const candidate of candidates) {
        try {
          const position = resolveDomPositionFromTextOffset(
            candidate,
            safeOffset
          );
          const range = document.createRange();
          range.setStart(position.node, position.offset);
          range.setEnd(position.node, position.offset);
          const caretRect =
            range.getClientRects().item(0) ?? range.getBoundingClientRect();
          const hostRect = candidate.getBoundingClientRect();
          if (
            Number.isFinite(caretRect.top) &&
            Number.isFinite(caretRect.bottom) &&
            Number.isFinite(hostRect.top) &&
            Number.isFinite(hostRect.bottom) &&
            caretRect.bottom >= hostRect.top - 1 &&
            caretRect.top <= hostRect.bottom + 1
          ) {
            return candidate;
          }
        } catch {
          continue;
        }
      }

      return (
        candidates.find((candidate) => candidate.isContentEditable) ??
        candidates[0] ??
        undefined
      );
    },
    [resolveDomPositionFromTextOffset]
  );

  const resolveDomPositionFromBoundary = React.useCallback(
    (
      boundary: DocxTextRangeBoundary
    ): { node: Node; offset: number } | undefined => {
      const location = boundary.location;
      const hostElement = resolveParagraphHostElement(location);
      if (!hostElement) {
        return undefined;
      }

      const textLength = paragraphTextLengthFromElement(hostElement);
      return resolveDomPositionFromTextOffset(
        hostElement,
        Math.max(0, Math.min(boundary.offset, textLength))
      );
    },
    [
      resolveParagraphHostElement,
      resolveDomPositionFromTextOffset,
      paragraphTextLengthFromElement,
    ]
  );

  const setSelectionFromDocxBoundaries = React.useCallback(
    (
      rawStartBoundary: DocxTextRangeBoundary,
      rawEndBoundary: DocxTextRangeBoundary
    ): void => {
      const selection = window.getSelection();
      if (!selection) {
        return;
      }

      const normalizedRange = normalizeTextRange({
        start: {
          location: cloneTextRangeLocation(rawStartBoundary.location),
          offset: rawStartBoundary.offset,
        },
        end: {
          location: cloneTextRangeLocation(rawEndBoundary.location),
          offset: rawEndBoundary.offset,
        },
      });

      const rawStartPosition = resolveDomPositionFromBoundary(rawStartBoundary);
      const rawEndPosition = resolveDomPositionFromBoundary(rawEndBoundary);
      if (!rawStartPosition || !rawEndPosition) {
        const fallbackElement =
          resolveParagraphHostElement(normalizedRange.start.location) ??
          resolveParagraphHostElement(normalizedRange.end.location);
        if (!fallbackElement) {
          editor.setActiveTextRange(undefined);
          return;
        }

        const fallbackPosition = resolveDomPositionFromTextOffset(
          fallbackElement,
          normalizedRange.start.offset
        );
        const fallbackRange = document.createRange();
        fallbackRange.setStart(fallbackPosition.node, fallbackPosition.offset);
        fallbackRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(fallbackRange);
        editor.setActiveTextRange({
          start: {
            location: cloneTextRangeLocation(normalizedRange.start.location),
            offset: normalizedRange.start.offset,
          },
          end: {
            location: cloneTextRangeLocation(normalizedRange.start.location),
            offset: normalizedRange.start.offset,
          },
        });
        return;
      }

      try {
        const isForwardSelection =
          compareTextRangeBoundaries(rawStartBoundary, rawEndBoundary) <= 0;
        const startPosition = isForwardSelection
          ? rawStartPosition
          : rawEndPosition;
        const endPosition = isForwardSelection
          ? rawEndPosition
          : rawStartPosition;
        const alreadyMatchesAnchorFocus =
          selection.anchorNode === rawStartPosition.node &&
          selection.anchorOffset === rawStartPosition.offset &&
          selection.focusNode === rawEndPosition.node &&
          selection.focusOffset === rawEndPosition.offset;

        if (!alreadyMatchesAnchorFocus) {
          if (typeof selection.setBaseAndExtent === "function") {
            selection.setBaseAndExtent(
              rawStartPosition.node,
              rawStartPosition.offset,
              rawEndPosition.node,
              rawEndPosition.offset
            );
          } else {
            const range = document.createRange();
            range.setStart(startPosition.node, startPosition.offset);
            range.setEnd(endPosition.node, endPosition.offset);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }

        editor.setActiveTextRange(normalizedRange);
      } catch {
        const fallbackElement = resolveParagraphHostElement(
          normalizedRange.start.location
        );
        if (!fallbackElement) {
          editor.setActiveTextRange(undefined);
          return;
        }

        placeCaretInsideElement(fallbackElement);
        editor.setActiveTextRange({
          start: {
            location: cloneTextRangeLocation(normalizedRange.start.location),
            offset: normalizedRange.start.offset,
          },
          end: {
            location: cloneTextRangeLocation(normalizedRange.start.location),
            offset: normalizedRange.start.offset,
          },
        });
      }
    },
    [
      editor,
      resolveDomPositionFromBoundary,
      resolveDomPositionFromTextOffset,
      resolveParagraphHostElement,
      placeCaretInsideElement,
    ]
  );

  const syncSelectionFromDocxRange = React.useCallback(
    (range: DocxTextRange): void => {
      const selectionIntentNonce = beginSelectionIntent();
      editor.beginSelectionSession("command", { settleAfterMs: 140 });
      const normalizedRange = normalizeTextRange({
        start: {
          location: cloneTextRangeLocation(range.start.location),
          offset: range.start.offset,
        },
        end: {
          location: cloneTextRangeLocation(range.end.location),
          offset: range.end.offset,
        },
      });
      const isCollapsed =
        compareTextRangeBoundaries(
          normalizedRange.start,
          normalizedRange.end
        ) === 0;

      const applySelection = (attempt: number): void => {
        if (!isSelectionIntentCurrent(selectionIntentNonce)) {
          return;
        }

        if (isCollapsed) {
          if (attempt === 0) {
            window.getSelection()?.removeAllRanges();
          }

          setSelectionFromDocxBoundaries(
            normalizedRange.start,
            normalizedRange.start
          );

          const selection = window.getSelection();
          const collapsedSelectionReady = Boolean(
            selection && selection.rangeCount > 0 && selection.isCollapsed
          );
          if (!collapsedSelectionReady && attempt < 4) {
            selection?.removeAllRanges();
            scheduleSelectionIntentFrame(selectionIntentNonce, () => {
              applySelection(attempt + 1);
            });
          }
          return;
        }

        setSelectionFromDocxBoundaries(
          normalizedRange.start,
          normalizedRange.end
        );
        if (attempt === 0) {
          scheduleSelectionIntentFrame(selectionIntentNonce, () => {
            setSelectionFromDocxBoundaries(
              normalizedRange.start,
              normalizedRange.end
            );
          });
        }
      };

      applySelection(0);
    },
    [
      beginSelectionIntent,
      editor,
      isSelectionIntentCurrent,
      scheduleSelectionIntentFrame,
      setSelectionFromDocxBoundaries,
    ]
  );

  const resolveWrappedParagraphBoundaryFromPoint = React.useCallback(
    (point: { x: number; y: number }): DocxTextRangeBoundary | undefined => {
      const elementAtPoint = document.elementFromPoint(point.x, point.y);
      if (!(elementAtPoint instanceof Element)) {
        return undefined;
      }

      const wrappedRoot = elementAtPoint.closest<HTMLElement>(
        "[data-docx-wrapped-paragraph-root='true'][data-docx-wrapped-paragraph-location-key]"
      );
      if (!wrappedRoot) {
        return undefined;
      }

      const locationKey = wrappedRoot.getAttribute(
        "data-docx-wrapped-paragraph-location-key"
      );
      if (!locationKey) {
        return undefined;
      }

      const registeredSurface =
        wrappedParagraphSurfaceRegistryRef.current.get(locationKey);
      if (!registeredSurface || !registeredSurface.element.isConnected) {
        return undefined;
      }

      const surfaceRect = registeredSurface.element.getBoundingClientRect();
      const offset = clampNumber(
        resolveOffsetAtPoint(
          registeredSurface.layout,
          point.x - surfaceRect.left,
          point.y - surfaceRect.top
        ),
        0,
        registeredSurface.textLength
      );

      return {
        location: cloneTextRangeLocation(registeredSurface.location),
        offset,
      };
    },
    []
  );

  const selectAllDocumentText = React.useCallback((): void => {
    const rootElement = viewerRootRef.current;
    if (!rootElement) {
      return;
    }

    clearTableCellSelection();

    const paragraphHosts = Array.from(
      rootElement.querySelectorAll<HTMLElement>(
        "[data-docx-paragraph-host='true'],[data-docx-wrapped-paragraph-root='true'][data-docx-wrapped-paragraph-location-key]"
      )
    );
    if (paragraphHosts.length === 0) {
      return;
    }

    const firstHost = paragraphHosts[0];
    const lastHost = paragraphHosts[paragraphHosts.length - 1];
    const firstLocation = parseParagraphLocationFromElement(firstHost);
    const lastLocation = parseParagraphLocationFromElement(lastHost);
    if (!firstLocation || !lastLocation) {
      return;
    }

    const startBoundary: DocxTextRangeBoundary = {
      location: firstLocation,
      offset: 0,
    };
    const endBoundary: DocxTextRangeBoundary = {
      location: lastLocation,
      offset: paragraphTextLengthFromElement(lastHost),
    };

    setSelectionFromDocxBoundaries(startBoundary, endBoundary);
  }, [
    clearTableCellSelection,
    paragraphTextLengthFromElement,
    setSelectionFromDocxBoundaries,
  ]);

  const resolveParagraphBoundaryFromSelectionPoint = React.useCallback(
    (
      container: Node | null,
      offset: number
    ): DocxTextRangeBoundary | undefined => {
      if (!container) {
        return undefined;
      }

      const referenceElement =
        container instanceof Element ? container : container.parentElement;
      const paragraphElement = referenceElement?.closest<HTMLElement>(
        "[data-docx-paragraph-kind]"
      );
      if (!paragraphElement) {
        const wrappedRoot = referenceElement?.closest<HTMLElement>(
          "[data-docx-wrapped-paragraph-root='true'][data-docx-wrapped-paragraph-location-key]"
        );
        if (!wrappedRoot) {
          return undefined;
        }

        const locationKey = wrappedRoot.getAttribute(
          "data-docx-wrapped-paragraph-location-key"
        );
        if (!locationKey) {
          return undefined;
        }

        const wrappedSurface =
          wrappedParagraphSurfaceRegistryRef.current.get(locationKey);
        if (!wrappedSurface || !wrappedSurface.element.contains(container)) {
          return undefined;
        }

        try {
          const boundaryRange = document.createRange();
          boundaryRange.setStart(wrappedSurface.element, 0);
          const normalizedOffset = Math.max(
            0,
            Number.isFinite(offset) ? Math.round(offset) : 0
          );
          boundaryRange.setEnd(container, normalizedOffset);
          const boundaryTextLength = textLengthFromRange(boundaryRange);
          return {
            location: cloneTextRangeLocation(wrappedSurface.location),
            offset: clampNumber(
              Math.round(boundaryTextLength),
              0,
              wrappedSurface.textLength
            ),
          };
        } catch {
          return undefined;
        }
      }

      const location = parseParagraphLocationFromElement(paragraphElement);
      if (!location) {
        return undefined;
      }

      try {
        const boundaryRange = document.createRange();
        boundaryRange.setStart(paragraphElement, 0);
        const normalizedOffset = Math.max(
          0,
          Number.isFinite(offset) ? Math.round(offset) : 0
        );
        boundaryRange.setEnd(container, normalizedOffset);
        const boundaryTextLength = textLengthFromRange(boundaryRange);
        return {
          location,
          offset: Math.max(0, Math.round(boundaryTextLength)),
        };
      } catch {
        return undefined;
      }
    },
    []
  );

  const resolveBoundaryFromPoint = React.useCallback(
    (
      point: { x: number; y: number },
      fallbackBias?: "start" | "end"
    ): DocxTextRangeBoundary | undefined => {
      const wrappedParagraphBoundary =
        resolveWrappedParagraphBoundaryFromPoint(point);
      if (wrappedParagraphBoundary) {
        return wrappedParagraphBoundary;
      }

      const documentWithCaret = document as Document & {
        caretPositionFromPoint?: (
          x: number,
          y: number
        ) => {
          offsetNode: Node;
          offset: number;
        } | null;
        caretRangeFromPoint?: (x: number, y: number) => Range | null;
      };

      const resolveCaretBoundaryAtPoint = (candidatePoint: {
        x: number;
        y: number;
      }): DocxTextRangeBoundary | undefined => {
        let offsetNode: Node | null = null;
        let offset = 0;

        if (typeof documentWithCaret.caretPositionFromPoint === "function") {
          const caretPosition = documentWithCaret.caretPositionFromPoint(
            candidatePoint.x,
            candidatePoint.y
          );
          if (caretPosition) {
            offsetNode = caretPosition.offsetNode;
            offset = caretPosition.offset;
          }
        } else if (
          typeof documentWithCaret.caretRangeFromPoint === "function"
        ) {
          const caretRange = documentWithCaret.caretRangeFromPoint(
            candidatePoint.x,
            candidatePoint.y
          );
          if (caretRange) {
            offsetNode = caretRange.startContainer;
            offset = caretRange.startOffset;
          }
        }

        if (!offsetNode) {
          return undefined;
        }

        return resolveParagraphBoundaryFromSelectionPoint(offsetNode, offset);
      };

      const boundaryMatchesParagraphElement = (
        boundary: DocxTextRangeBoundary,
        paragraphElement: HTMLElement
      ): boolean => {
        const paragraphLocation =
          parseParagraphLocationFromElement(paragraphElement);
        if (!paragraphLocation) {
          return false;
        }

        return sameParagraphLocation(boundary.location, paragraphLocation);
      };

      const clampedPointInsideElement = (
        element: HTMLElement
      ): {
        x: number;
        y: number;
      } => {
        const rect = element.getBoundingClientRect();
        const safeX =
          rect.width > 2
            ? clampNumber(point.x, rect.left + 1, rect.right - 1)
            : rect.left + rect.width / 2;
        const safeY =
          rect.height > 2
            ? clampNumber(point.y, rect.top + 1, rect.bottom - 1)
            : rect.top + rect.height / 2;
        return {
          x: safeX,
          y: safeY,
        };
      };

      const caretBoundary = resolveCaretBoundaryAtPoint(point);
      if (caretBoundary) {
        if (!fallbackBias) {
          return caretBoundary;
        }

        const caretParagraphElement = resolveParagraphHostElement(
          caretBoundary.location
        );
        if (!caretParagraphElement) {
          return caretBoundary;
        }

        const caretRect = caretParagraphElement.getBoundingClientRect();
        const verticalTolerancePx = 1;
        const pointIsVerticallyOutsideCaretParagraph =
          point.y < caretRect.top - verticalTolerancePx ||
          point.y > caretRect.bottom + verticalTolerancePx;
        if (!pointIsVerticallyOutsideCaretParagraph) {
          return caretBoundary;
        }
      }

      const pickBoundaryFromParagraphElement = (
        paragraphElement: HTMLElement | undefined
      ): DocxTextRangeBoundary | undefined => {
        if (!paragraphElement) {
          return undefined;
        }

        const location =
          parseParagraphLocationFromElement(paragraphElement) ??
          (() => {
            const wrappedLocationKey = paragraphElement.getAttribute(
              "data-docx-wrapped-paragraph-location-key"
            );
            const wrappedSurface = wrappedLocationKey
              ? wrappedParagraphSurfaceRegistryRef.current.get(
                  wrappedLocationKey
                )
              : undefined;
            return wrappedSurface?.location;
          })();
        if (!location) {
          return undefined;
        }

        const clampedCaretBoundary = resolveCaretBoundaryAtPoint(
          clampedPointInsideElement(paragraphElement)
        );
        if (
          clampedCaretBoundary &&
          boundaryMatchesParagraphElement(
            clampedCaretBoundary,
            paragraphElement
          )
        ) {
          return clampedCaretBoundary;
        }

        if (fallbackBias === "start") {
          return {
            location,
            offset: 0,
          };
        }

        if (fallbackBias === "end") {
          return {
            location,
            offset: paragraphTextLengthFromElement(paragraphElement),
          };
        }

        const paragraphRect = paragraphElement.getBoundingClientRect();
        const shouldUseTrailingEdge =
          point.y >= paragraphRect.top + paragraphRect.height / 2;
        return {
          location,
          offset: shouldUseTrailingEdge
            ? paragraphTextLengthFromElement(paragraphElement)
            : 0,
        };
      };

      const elementAtPoint = document.elementFromPoint(point.x, point.y);
      if (elementAtPoint instanceof Element) {
        const directParagraphElement =
          elementAtPoint.closest<HTMLElement>("[data-docx-paragraph-kind]") ??
          undefined;
        const directBoundary = pickBoundaryFromParagraphElement(
          directParagraphElement
        );
        if (directBoundary) {
          return directBoundary;
        }

        const tableCellElement =
          elementAtPoint.closest<HTMLElement>(
            "[data-docx-table-cell='true']"
          ) ?? undefined;
        if (tableCellElement) {
          const paragraphElements = Array.from(
            tableCellElement.querySelectorAll<HTMLElement>(
              "[data-docx-paragraph-kind='table-cell'][data-docx-table-paragraph-index]"
            )
          ).sort((left, right) => {
            const leftIndex = Number.parseInt(
              left.getAttribute("data-docx-table-paragraph-index") ?? "",
              10
            );
            const rightIndex = Number.parseInt(
              right.getAttribute("data-docx-table-paragraph-index") ?? "",
              10
            );
            const safeLeft = Number.isFinite(leftIndex) ? leftIndex : 0;
            const safeRight = Number.isFinite(rightIndex) ? rightIndex : 0;
            return safeLeft - safeRight;
          });
          if (paragraphElements.length > 0) {
            if (fallbackBias === "start") {
              return pickBoundaryFromParagraphElement(paragraphElements[0]);
            }
            if (fallbackBias === "end") {
              return pickBoundaryFromParagraphElement(
                paragraphElements[paragraphElements.length - 1]
              );
            }
            const nearestParagraph = paragraphElements.reduce<HTMLElement>(
              (closest, candidate) => {
                const closestRect = closest.getBoundingClientRect();
                const candidateRect = candidate.getBoundingClientRect();
                const closestDistance = Math.abs(
                  point.y - (closestRect.top + closestRect.height / 2)
                );
                const candidateDistance = Math.abs(
                  point.y - (candidateRect.top + candidateRect.height / 2)
                );
                return candidateDistance < closestDistance
                  ? candidate
                  : closest;
              },
              paragraphElements[0]
            );
            return pickBoundaryFromParagraphElement(nearestParagraph);
          }
        }
      }

      const rootElement = viewerRootRef.current;
      if (!rootElement) {
        return undefined;
      }
      const paragraphHosts = Array.from(
        rootElement.querySelectorAll<HTMLElement>(
          "[data-docx-paragraph-host='true'],[data-docx-wrapped-paragraph-root='true'][data-docx-wrapped-paragraph-location-key]"
        )
      );
      if (paragraphHosts.length === 0) {
        return undefined;
      }

      let previousHost: HTMLElement | undefined;
      let nextHost: HTMLElement | undefined;

      for (const host of paragraphHosts) {
        const hostRect = host.getBoundingClientRect();
        if (point.y < hostRect.top) {
          nextHost = host;
          break;
        }
        previousHost = host;
      }

      if (previousHost && nextHost) {
        return fallbackBias === "start"
          ? pickBoundaryFromParagraphElement(previousHost)
          : pickBoundaryFromParagraphElement(nextHost);
      }

      if (previousHost) {
        return pickBoundaryFromParagraphElement(previousHost);
      }

      if (nextHost) {
        return pickBoundaryFromParagraphElement(nextHost);
      }

      return undefined;
    },
    [
      paragraphTextLengthFromElement,
      resolveParagraphBoundaryFromSelectionPoint,
      resolveParagraphHostElement,
      resolveWrappedParagraphBoundaryFromPoint,
    ]
  );

  const resolveActiveRangeFromDomSelection = React.useCallback(():
    | DocxTextRange
    | undefined => {
    const rootElement = viewerRootRef.current;
    const selection = window.getSelection();
    if (!rootElement || !selection || selection.rangeCount === 0) {
      return undefined;
    }

    const selectionRange = selection.getRangeAt(0);
    if (
      !rootElement.contains(selectionRange.startContainer) ||
      !rootElement.contains(selectionRange.endContainer)
    ) {
      return undefined;
    }

    const startBoundary = resolveParagraphBoundaryFromSelectionPoint(
      selectionRange.startContainer,
      selectionRange.startOffset
    );
    const endBoundary = resolveParagraphBoundaryFromSelectionPoint(
      selectionRange.endContainer,
      selectionRange.endOffset
    );
    if (!startBoundary || !endBoundary) {
      return undefined;
    }

    return normalizeTextRange({
      start: startBoundary,
      end: endBoundary,
    });
  }, [resolveParagraphBoundaryFromSelectionPoint]);

  const setActiveRangeFromSelection = React.useCallback((): void => {
    // Selection-authority model (mirrors ProseMirror): while the user is
    // actively producing input (typing / IME composition) the *DOM* selection
    // is authoritative and the model lags by one debounced flush. While idle or
    // after a pointer interaction the *model* is authoritative. We only ever
    // write the model back into the DOM when the DOM selection has been
    // destroyed by a re-render (innerHTML rewrite / remount during pagination
    // churn) — never over a live, valid selection.
    const session = editor.selectionSessionKind;
    const rootElement = viewerRootRef.current;
    const activeElement = document.activeElement;
    const editorHasFocus = Boolean(
      activeElement instanceof HTMLElement &&
        activeElement.isContentEditable &&
        rootElement &&
        rootElement.contains(activeElement)
    );

    const liveSelection = window.getSelection();
    // A genuine caret is anchored to a Text node (or an empty host). An element
    // anchor inside a non-empty host, or a dropped range while the editor still
    // holds focus, means the selection was destroyed by a DOM rewrite.
    const anchorNode = liveSelection?.anchorNode ?? null;
    const collapsedElementAnchor = Boolean(
      liveSelection &&
        liveSelection.isCollapsed &&
        liveSelection.rangeCount > 0 &&
        anchorNode &&
        anchorNode.nodeType !== Node.TEXT_NODE
    );
    const selectionDropped = Boolean(
      editorHasFocus && (!liveSelection || liveSelection.rangeCount === 0)
    );

    if (collapsedElementAnchor || selectionDropped) {
      // During active text input the model is intentionally behind the DOM;
      // forcing the (stale) model caret here is what makes a freshly typed
      // character appear without the cursor advancing. Leave the DOM alone and
      // let the draft / selection-preservation path own the caret.
      if (session === "keyboard" || session === "composition") {
        return;
      }
      const anchorElement =
        anchorNode instanceof Element
          ? anchorNode
          : anchorNode?.parentElement ?? null;
      const destroyedHost =
        anchorElement?.closest<HTMLElement>(
          "[data-docx-paragraph-host='true']"
        ) ??
        (editorHasFocus
          ? (activeElement as HTMLElement).closest<HTMLElement>(
              "[data-docx-paragraph-host='true']"
            )
          : null);
      const lastGoodRange = cloneTextRange(editor.activeTextRange);
      if (lastGoodRange && destroyedHost) {
        const normalizedLastGood = normalizeTextRange(lastGoodRange);
        const lastGoodHost = resolveParagraphHostElement(
          normalizedLastGood.start.location
        );
        if (lastGoodHost === destroyedHost) {
          setSelectionFromDocxBoundaries(
            normalizedLastGood.start,
            normalizedLastGood.end
          );
        }
      }
      // Never overwrite the model with a destroyed selection.
      return;
    }

    const range = resolveActiveRangeFromDomSelection();
    if (!range) {
      // Don't wipe the model selection during transient churn while the editor
      // still holds focus; only clear once focus has actually left the editor.
      if (editorHasFocus && editor.activeTextRange) {
        return;
      }
      editor.setActiveTextRange(undefined);
      return;
    }

    clearTableCellSelection();
    editor.setActiveTextRange(range);
  }, [
    clearTableCellSelection,
    editor,
    resolveActiveRangeFromDomSelection,
    resolveParagraphHostElement,
    setSelectionFromDocxBoundaries,
  ]);

  const flushActiveRangeFromSelection = React.useCallback((): void => {
    if (deferredCollapsedSelectionSyncTimeoutRef.current !== null) {
      window.clearTimeout(deferredCollapsedSelectionSyncTimeoutRef.current);
      deferredCollapsedSelectionSyncTimeoutRef.current = null;
    }

    if (activeRangeFlushFrameRef.current !== null) {
      return;
    }

    activeRangeFlushFrameRef.current = window.requestAnimationFrame(() => {
      activeRangeFlushFrameRef.current = null;
      setActiveRangeFromSelection();
    });
  }, [setActiveRangeFromSelection]);

  // ProseMirror-style invariant: the DOM selection must follow the model. When
  // a re-render rewrites an editable paragraph's `innerHTML` (pagination /
  // layout churn), the browser destroys the live selection — collapsing it onto
  // the host element or dropping it entirely. This layout effect runs
  // synchronously after every commit (before paint and before the async
  // `selectionchange` handler can observe and persist the destroyed selection),
  // and re-asserts the selection from the authoritative model range whenever the
  // DOM selection has been destroyed. It is deliberately conservative: it never
  // touches a valid in-host selection (so it cannot fight a real caret), and it
  // bows out entirely while the user is actively producing input — dragging a
  // selection (pointer), composing (IME), or typing (keyboard) — where the DOM
  // is authoritative and the model intentionally lags by one debounced flush.
  // Re-asserting during those windows would snap the caret back to the
  // pre-input offset (a freshly typed character would appear without the cursor
  // advancing), so we only restore once the user is idle / after a programmatic
  // change.
  React.useLayoutEffect(() => {
    if (isReadOnly) {
      return;
    }
    const session = editor.selectionSessionKind;
    // An in-progress pointer drag and IME composition own the DOM selection
    // directly; never interfere with them. We gate on the live drag (button
    // down + moved) rather than the whole "pointer" session, because that
    // session lingers ~320ms after release — long enough for the mouseup
    // re-render to clobber and "erase" the just-made selection before we could
    // restore it.
    if (pointerSelectionDragRef.current.moved || session === "composition") {
      return;
    }
    const rootElement = viewerRootRef.current;
    if (!rootElement) {
      return;
    }
    const activeElement = document.activeElement;
    if (
      !(activeElement instanceof HTMLElement) ||
      !activeElement.isContentEditable ||
      !rootElement.contains(activeElement)
    ) {
      return;
    }
    const focusedHost = activeElement.closest<HTMLElement>(
      "[data-docx-paragraph-host='true']"
    );
    if (!focusedHost) {
      return;
    }
    const selection = window.getSelection();
    if (!selection) {
      return;
    }
    const liveRange =
      selection.rangeCount > 0 ? selection.getRangeAt(0) : undefined;
    const anchorNode = selection.anchorNode;
    const selectionInsideHost = Boolean(
      liveRange &&
        focusedHost.contains(liveRange.startContainer) &&
        focusedHost.contains(liveRange.endContainer)
    );
    const anchorIsElement = Boolean(
      anchorNode && anchorNode.nodeType !== Node.TEXT_NODE
    );
    const selectionWasDestroyed =
      !liveRange ||
      !selectionInsideHost ||
      (selection.isCollapsed && anchorIsElement);
    if (!selectionWasDestroyed) {
      // The DOM selection is live and valid — leave it alone (this is the
      // normal typing path once React did not need to rewrite innerHTML).
      return;
    }

    // The selection was destroyed by a re-render that rewrote the editable
    // host's innerHTML (the per-keystroke draft is fed through
    // `dangerouslySetInnerHTML`, and React rebuilds the child nodes). Restore
    // the caret. During active typing the authoritative position is the offset
    // captured in `onInput` (the model range lags by a debounced flush, so
    // using it would snap the caret backwards and reverse the typed text).
    const focusedNodeIndexAttr = focusedHost.getAttribute(
      "data-docx-paragraph-node-index"
    );
    const focusedNodeIndex =
      focusedNodeIndexAttr != null
        ? Number.parseInt(focusedNodeIndexAttr, 10)
        : Number.NaN;
    const pendingCaret = pendingEditableCaretRef.current;
    if (
      pendingCaret &&
      Number.isFinite(focusedNodeIndex) &&
      pendingCaret.nodeIndex === focusedNodeIndex
    ) {
      const textLength = editableTextFromElement(focusedHost).length;
      const safeStart = Math.max(0, Math.min(pendingCaret.start, textLength));
      const safeEnd = Math.max(
        safeStart,
        Math.min(pendingCaret.end, textLength)
      );
      setSelectionWithinElementByTextOffsets(focusedHost, safeStart, safeEnd);
      return;
    }

    // Not actively typing this paragraph: fall back to the committed model
    // range. Only do this when idle/programmatic — during keyboard input
    // without a captured caret we must not force the stale model range.
    if (session === "keyboard") {
      return;
    }
    const range = editor.activeTextRange;
    if (!range) {
      return;
    }
    const normalized = normalizeTextRange(range);
    const targetHost = resolveParagraphHostElement(normalized.start.location);
    if (!targetHost || targetHost !== focusedHost) {
      return;
    }
    setSelectionFromDocxBoundaries(normalized.start, normalized.end);
  });

  const selectionIsExpandedWithinElement = React.useCallback(
    (element: HTMLElement): boolean => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
        return false;
      }

      const selectedRange = selection.getRangeAt(0);
      return (
        element.contains(selectedRange.startContainer) &&
        element.contains(selectedRange.endContainer)
      );
    },
    []
  );

  const ensureCaretPlacementInsideElement = React.useCallback(
    (
      element: HTMLElement,
      point: {
        x: number;
        y: number;
      },
      selectionIntentNonce?: number
    ): void => {
      const selection = window.getSelection();
      const selectedRange =
        selection && selection.rangeCount > 0
          ? selection.getRangeAt(0)
          : undefined;
      const rangeInsideElement = Boolean(
        selectedRange &&
          element.contains(selectedRange.startContainer) &&
          element.contains(selectedRange.endContainer)
      );
      if (!rangeInsideElement) {
        placeCaretInsideElement(element, point);
        return;
      }

      const shouldVerifySuspiciousStart = Boolean(
        selectedRange &&
          isSuspiciousCollapsedSelectionAtElementStart(
            element,
            selectedRange,
            point.x
          )
      );
      if (!shouldVerifySuspiciousStart) {
        return;
      }

      const verifyPlacement = (): void => {
        if (
          Number.isFinite(selectionIntentNonce) &&
          !isSelectionIntentCurrent(selectionIntentNonce as number)
        ) {
          return;
        }
        const latestSelection = window.getSelection();
        const latestRange =
          latestSelection && latestSelection.rangeCount > 0
            ? latestSelection.getRangeAt(0)
            : undefined;
        const latestRangeInsideElement = Boolean(
          latestRange &&
            element.contains(latestRange.startContainer) &&
            element.contains(latestRange.endContainer)
        );
        if (
          latestRangeInsideElement &&
          latestRange &&
          isSuspiciousCollapsedSelectionAtElementStart(
            element,
            latestRange,
            point.x
          )
        ) {
          placeCaretInsideElement(element, point);
        }
      };

      if (Number.isFinite(selectionIntentNonce)) {
        scheduleSelectionIntentFrame(
          selectionIntentNonce as number,
          verifyPlacement
        );
        return;
      }

      window.requestAnimationFrame(verifyPlacement);
    },
    [
      isSelectionIntentCurrent,
      placeCaretInsideElement,
      scheduleSelectionIntentFrame,
    ]
  );

  const schedulePointerSelectionReconcile = React.useCallback(
    (
      element: HTMLElement,
      point: {
        x: number;
        y: number;
      },
      syncSelection: () => void
    ): void => {
      const selectionIntentNonce = beginSelectionIntent();
      editor.beginSelectionSession("pointer", { settleAfterMs: 320 });

      scheduleSelectionIntentFrame(selectionIntentNonce, () => {
        if (!isSelectionIntentCurrent(selectionIntentNonce)) {
          return;
        }
        const selection = window.getSelection();
        const selectedRange =
          selection && selection.rangeCount > 0
            ? selection.getRangeAt(0)
            : undefined;
        const expandedSelection = Boolean(
          selection && !selection.isCollapsed && selectedRange
        );
        if (expandedSelection) {
          syncSelection();
          editor.clearSelectionSession("pointer");
          return;
        }
        const rangeInsideElement = Boolean(
          selectedRange &&
            element.contains(selectedRange.startContainer) &&
            element.contains(selectedRange.endContainer)
        );

        if (!rangeInsideElement) {
          ensureCaretPlacementInsideElement(
            element,
            point,
            selectionIntentNonce
          );
          scheduleSelectionIntentFrame(selectionIntentNonce, () => {
            if (!isSelectionIntentCurrent(selectionIntentNonce)) {
              return;
            }
            syncSelection();
            editor.clearSelectionSession("pointer");
          });
          return;
        }

        if (
          selectedRange &&
          isSuspiciousCollapsedSelectionAtElementStart(
            element,
            selectedRange,
            point.x
          )
        ) {
          ensureCaretPlacementInsideElement(
            element,
            point,
            selectionIntentNonce
          );
          scheduleSelectionIntentFrame(selectionIntentNonce, () => {
            if (!isSelectionIntentCurrent(selectionIntentNonce)) {
              return;
            }
            syncSelection();
            editor.clearSelectionSession("pointer");
          });
          return;
        }

        syncSelection();
        editor.clearSelectionSession("pointer");
      });
    },
    [
      beginSelectionIntent,
      editor,
      ensureCaretPlacementInsideElement,
      isSelectionIntentCurrent,
      scheduleSelectionIntentFrame,
    ]
  );

  const cancelPendingPointerSelectionReconcile = React.useCallback((): void => {
    cancelPendingSelectionIntent();
    editor.clearSelectionSession("pointer");
  }, [cancelPendingSelectionIntent, editor]);

  const clearObjectSelectionForParagraphEntry = React.useCallback(
    (nodeIndex: number): void => {
      clearTableCellSelection();
      setSelectedImage(undefined);
      setSelectedSectionImageKey(undefined);
      setSelectedDropCapNodeIndex(undefined);
      setHoveredDropCapNodeIndex(undefined);
      setActiveWrappedParagraphSession(undefined);

      if (editor.selection.kind === "table-cell") {
        editor.selectParagraph(nodeIndex);
      }
    },
    [clearTableCellSelection, editor]
  );

  const syncWrappedParagraphRange = React.useCallback(
    (
      location: ParagraphLocation,
      startOffset: number,
      endOffset: number,
      options?: {
        textLength?: number;
        preferredCaretX?: number;
        textOverride?: string;
        isComposing?: boolean;
        anchorOffset?: number;
      }
    ): void => {
      const paragraph = getParagraphAtLocation(
        editor.model,
        location
      ).paragraph;
      const textLength =
        options?.textLength ??
        options?.textOverride?.length ??
        (paragraph ? wrappedParagraphSessionText(paragraph).length : 0);
      const clampedStart = Math.max(
        0,
        Math.min(Math.round(startOffset), textLength)
      );
      const clampedEnd = Math.max(
        0,
        Math.min(Math.round(endOffset), textLength)
      );
      const safeStart = Math.min(clampedStart, clampedEnd);
      const safeEnd = Math.max(clampedStart, clampedEnd);
      const safeAnchor = Math.max(
        0,
        Math.min(Math.round(options?.anchorOffset ?? startOffset), textLength)
      );
      const locationKey = paragraphLocationKey(location);
      const sessionText =
        options?.textOverride ??
        (paragraph ? wrappedParagraphSessionText(paragraph) : "");
      wrappedParagraphPendingSelectionRef.current = {
        locationKey,
        start: safeStart,
        end: safeEnd,
      };

      setActiveWrappedParagraphSession((current) => ({
        location,
        locationKey,
        text: sessionText,
        selectionStart: safeStart,
        selectionEnd: safeEnd,
        anchorOffset: safeAnchor,
        isComposing: options?.isComposing ?? current?.isComposing ?? false,
        preferredCaretX:
          options?.preferredCaretX ??
          (safeStart === safeEnd ? current?.preferredCaretX : undefined),
      }));

      if (location.kind === "paragraph") {
        editor.selectParagraph(location.nodeIndex);
      } else {
        editor.selectTableCell(
          location.tableIndex,
          location.rowIndex,
          location.cellIndex
        );
      }
      editor.setActiveTextRange({
        start: {
          location: cloneTextRangeLocation(location),
          offset: safeStart,
        },
        end: {
          location: cloneTextRangeLocation(location),
          offset: safeEnd,
        },
      });
    },
    [editor]
  );

  const focusWrappedParagraphTextarea = React.useCallback((): void => {
    scheduleDomWrite(() => {
      const textarea = wrappedParagraphTextareaRef.current;
      if (!textarea || !textarea.isConnected) {
        return;
      }
      textarea.focus();
      const pendingSelection = wrappedParagraphPendingSelectionRef.current;
      const session = activeWrappedParagraphSession;
      const selectionSource =
        pendingSelection &&
        (!session || pendingSelection.locationKey === session.locationKey)
          ? pendingSelection
          : session
          ? {
              locationKey: session.locationKey,
              start: session.selectionStart,
              end: session.selectionEnd,
            }
          : undefined;
      if (!selectionSource) {
        return;
      }

      const safeStart = Math.max(
        0,
        Math.min(selectionSource.start, textarea.value.length)
      );
      const safeEnd = Math.max(
        safeStart,
        Math.min(selectionSource.end, textarea.value.length)
      );
      try {
        textarea.setSelectionRange(safeStart, safeEnd);
        if (
          pendingSelection &&
          pendingSelection.locationKey === selectionSource.locationKey &&
          pendingSelection.start === selectionSource.start &&
          pendingSelection.end === selectionSource.end
        ) {
          wrappedParagraphPendingSelectionRef.current = undefined;
        }
      } catch {
        // Ignore selection sync failures; focus alone is still better than dropping the session.
      }
    });
  }, [activeWrappedParagraphSession]);

  const commitWrappedParagraphTextAtLocation = React.useCallback(
    (location: ParagraphLocation, text: string): void => {
      editor.suppressNextDomSelectionRestore();
      if (location.kind === "paragraph") {
        editor.commitParagraphText(location.nodeIndex, text);
        return;
      }

      editor.commitTableCellParagraphTextRecursive(
        location.tableIndex,
        location.rowIndex,
        location.cellIndex,
        location.paragraphIndex,
        text
      );
    },
    [editor]
  );

  const focusDropCapWrappedParagraph = React.useCallback(
    (
      nodeIndex: number,
      point?: {
        x: number;
        y: number;
      }
    ): void => {
      clearObjectSelectionForParagraphEntry(nodeIndex);
      editor.selectParagraph(nodeIndex);
      scheduleDomWrite(() => {
        const element = paragraphElementsRef.current.get(nodeIndex);
        if (!element || !element.isConnected) {
          return;
        }

        if (point) {
          placeCaretInsideElement(element, point);
        } else {
          placeCaretInsideElement(element);
        }
        flushActiveRangeFromSelection();
      });
    },
    [
      clearObjectSelectionForParagraphEntry,
      editor,
      flushActiveRangeFromSelection,
      placeCaretInsideElement,
    ]
  );

  const activateDropCapEditor = React.useCallback(
    (nodeIndex: number): void => {
      if (isReadOnly) {
        return;
      }

      clearTableCellSelection();
      setSelectedImage(undefined);
      setSelectedSectionImageKey(undefined);
      setActiveWrappedParagraphSession(undefined);
      setSelectedDropCapNodeIndex(nodeIndex);
    },
    [clearTableCellSelection, isReadOnly]
  );

  const scheduleDeferredCollapsedSelectionSync = React.useCallback((): void => {
    if (deferredCollapsedSelectionSyncTimeoutRef.current !== null) {
      window.clearTimeout(deferredCollapsedSelectionSyncTimeoutRef.current);
    }

    deferredCollapsedSelectionSyncTimeoutRef.current = window.setTimeout(() => {
      deferredCollapsedSelectionSyncTimeoutRef.current = null;
      flushActiveRangeFromSelection();
    }, 80);
  }, [flushActiveRangeFromSelection]);

  React.useEffect(() => {
    const restoreRequest = editor.historyRestoreRequest;
    if (!restoreRequest) {
      return;
    }
    if (restoreRequest.nonce === appliedHistoryRestoreNonceRef.current) {
      return;
    }

    appliedHistoryRestoreNonceRef.current = restoreRequest.nonce;
    const selectionIntentNonce = beginSelectionIntent();
    editor.beginSelectionSession("history-restore", { settleAfterMs: 180 });
    // History restore must reflect canonical model state, not transient in-DOM drafts.
    paragraphDraftsRef.current.clear();
    tableCellDraftsRef.current.clear();
    tableCellParagraphDraftsRef.current.clear();
    sectionParagraphDraftsRef.current.clear();
    // Keep pendingTableCellFocusRef intact: it carries the precise boundary
    // captured on pointer-down for the cell the user just clicked, and the
    // table-cell branch of applyRestore below consumes it to place the caret.
    const normalizedTargetRange = restoreRequest.activeTextRange
      ? normalizeTextRange(restoreRequest.activeTextRange)
      : undefined;
    const resolveSelectionElement = (): HTMLElement | undefined => {
      if (normalizedTargetRange) {
        const paragraphHostElement = resolveParagraphHostElement(
          normalizedTargetRange.start.location
        );
        if (paragraphHostElement) {
          return paragraphHostElement;
        }
      }

      if (restoreRequest.selection.kind === "paragraph") {
        return paragraphElementsRef.current.get(
          restoreRequest.selection.nodeIndex
        );
      }

      const cellEditorKey = `${restoreRequest.selection.tableIndex}:${restoreRequest.selection.rowIndex}:${restoreRequest.selection.cellIndex}`;
      return tableCellEditorElementsRef.current.get(cellEditorKey);
    };
    const applyRestore = (attempt = 0): void => {
      if (!isSelectionIntentCurrent(selectionIntentNonce)) {
        return;
      }

      const element = resolveSelectionElement();
      if (!element) {
        if (attempt < 6) {
          scheduleSelectionIntentFrame(selectionIntentNonce, () => {
            applyRestore(attempt + 1);
          });
        }
        return;
      }

      // Check for an existing in-cell selection BEFORE focusing: focusing a
      // contentEditable cell can collapse a caret that an earlier pending
      // click-focus restore already placed precisely.
      if (
        !normalizedTargetRange &&
        restoreRequest.selection.kind === "table-cell"
      ) {
        const selection = window.getSelection();
        const selectionAlreadyInCell = Boolean(
          selection &&
            selection.rangeCount > 0 &&
            element.contains(selection.getRangeAt(0).startContainer) &&
            element.contains(selection.getRangeAt(0).endContainer)
        );
        if (selectionAlreadyInCell) {
          editor.clearSelectionSession("history-restore");
          return;
        }
      }

      try {
        element.focus({ preventScroll: true });
      } catch {
        element.focus();
      }
      if (normalizedTargetRange) {
        setSelectionFromDocxBoundaries(
          normalizedTargetRange.start,
          normalizedTargetRange.end
        );
        editor.clearSelectionSession("history-restore");
        return;
      }

      if (restoreRequest.selection.kind === "table-cell") {

        const pendingCellElementKey =
          `cell-${restoreRequest.selection.tableIndex}` +
          `-${restoreRequest.selection.rowIndex}` +
          `-${restoreRequest.selection.cellIndex}`;
        const pendingFocus = pendingTableCellFocusRef.current;
        const pendingFocusMatchesCell =
          pendingFocus?.cellElementKey === pendingCellElementKey;
        const pendingFocusBoundary =
          pendingFocusMatchesCell &&
          pendingFocus?.boundary?.location.kind === "table-cell" &&
          pendingFocus.boundary.location.tableIndex ===
            restoreRequest.selection.tableIndex &&
          pendingFocus.boundary.location.rowIndex ===
            restoreRequest.selection.rowIndex &&
          pendingFocus.boundary.location.cellIndex ===
            restoreRequest.selection.cellIndex
            ? pendingFocus.boundary
            : undefined;
        if (pendingFocusBoundary) {
          setSelectionFromDocxBoundaries(
            pendingFocusBoundary,
            pendingFocusBoundary
          );
          pendingTableCellFocusRef.current = undefined;
          editor.clearSelectionSession("history-restore");
          return;
        }
        const pendingFocusPoint = pendingFocusMatchesCell
          ? pendingFocus.point
          : undefined;
        const paragraphSelector =
          `[data-docx-paragraph-kind='table-cell']` +
          `[data-docx-table-index='${restoreRequest.selection.tableIndex}']` +
          `[data-docx-row-index='${restoreRequest.selection.rowIndex}']` +
          `[data-docx-cell-index='${restoreRequest.selection.cellIndex}']` +
          `[data-docx-table-paragraph-index]`;
        const paragraphElements = Array.from(
          element.querySelectorAll<HTMLElement>(paragraphSelector)
        );
        const paragraphElementByIndex =
          pendingFocusMatchesCell &&
          Number.isFinite(pendingFocus?.paragraphIndex)
            ? element.querySelector<HTMLElement>(
                `[data-docx-paragraph-kind='table-cell']` +
                  `[data-docx-table-index='${restoreRequest.selection.tableIndex}']` +
                  `[data-docx-row-index='${restoreRequest.selection.rowIndex}']` +
                  `[data-docx-cell-index='${restoreRequest.selection.cellIndex}']` +
                  `[data-docx-table-paragraph-index='${pendingFocus?.paragraphIndex}']`
              ) ?? undefined
            : undefined;
        const paragraphElementByPoint =
          pendingFocusPoint && paragraphElements.length > 0
            ? paragraphElements.reduce<HTMLElement>((closest, candidate) => {
                const closestRect = closest.getBoundingClientRect();
                const candidateRect = candidate.getBoundingClientRect();
                const closestDistance = Math.abs(
                  pendingFocusPoint.y -
                    (closestRect.top + closestRect.height / 2)
                );
                const candidateDistance = Math.abs(
                  pendingFocusPoint.y -
                    (candidateRect.top + candidateRect.height / 2)
                );
                return candidateDistance < closestDistance
                  ? candidate
                  : closest;
              }, paragraphElements[0])
            : undefined;
        const paragraphElementAtPoint = pendingFocusPoint
          ? document
              .elementFromPoint(pendingFocusPoint.x, pendingFocusPoint.y)
              ?.closest<HTMLElement>(paragraphSelector) ?? undefined
          : undefined;
        const fallbackParagraphElement = paragraphElements[0];
        const paragraphElement =
          paragraphElementByIndex ??
          paragraphElementAtPoint ??
          paragraphElementByPoint ??
          fallbackParagraphElement;
        if (paragraphElement) {
          placeCaretInsideElement(paragraphElement, pendingFocusPoint);
        } else if (pendingFocusPoint) {
          placeCaretInsideElement(element, pendingFocusPoint);
        }

        if (pendingFocusMatchesCell) {
          pendingTableCellFocusRef.current = undefined;
        }
        editor.clearSelectionSession("history-restore");
        return;
      }

      placeCaretInsideElement(element);
      editor.clearSelectionSession("history-restore");
    };

    scheduleSelectionIntentFrame(selectionIntentNonce, () => {
      applyRestore();
    });
  }, [
    beginSelectionIntent,
    editor,
    editor.historyRestoreRequest,
    isSelectionIntentCurrent,
    placeCaretInsideElement,
    resolveParagraphHostElement,
    scheduleSelectionIntentFrame,
    setSelectionFromDocxBoundaries,
  ]);

  React.useEffect(() => {
    const handleSelectionChange = (): void => {
      const selection = window.getSelection();
      if (!selection) {
        return;
      }

      if (selection.rangeCount === 0) {
        return;
      }

      const selectionRange = selection.getRangeAt(0);
      const rootElement = viewerRootRef.current;
      if (
        !rootElement ||
        !rootElement.contains(selectionRange.startContainer) ||
        !rootElement.contains(selectionRange.endContainer)
      ) {
        return;
      }

      // Cross-node and table-cell drag selection paths manage DOM + model
      // selection explicitly. Syncing from native selectionchange while dragging
      // can race those updates and cause transient flicker.
      const crossNodeDrag = tableSelectionDragRef.current;
      if (crossNodeDrag?.hasMoved) {
        return;
      }

      const tableCellDrag = tableCellSelectionDragRef.current;
      if (
        tableCellDrag?.isSelectingAcrossNodes ||
        tableCellDrag?.isSelectingCells
      ) {
        return;
      }

      const activeElement = document.activeElement;
      const collapsedCaretInsideEditableHost = Boolean(
        selectionRange.collapsed &&
          activeElement instanceof HTMLElement &&
          activeElement.isContentEditable &&
          rootElement.contains(activeElement) &&
          activeElement.contains(selectionRange.startContainer)
      );
      // Track the last valid DOM selection (collapsed caret or expanded range)
      // within a single focused editable paragraph host, so the selection
      // invariant can restore it after a re-render rewrites the host's
      // innerHTML. Only capture genuine text-node anchors at both ends — an
      // element anchor means the selection was already destroyed by a clobber,
      // and capturing it would defeat the restore.
      const selectionWithinSingleEditableHost = Boolean(
        activeElement instanceof HTMLElement &&
          activeElement.isContentEditable &&
          rootElement.contains(activeElement) &&
          activeElement.contains(selectionRange.startContainer) &&
          activeElement.contains(selectionRange.endContainer) &&
          selectionRange.startContainer.nodeType === Node.TEXT_NODE &&
          selectionRange.endContainer.nodeType === Node.TEXT_NODE
      );
      if (selectionWithinSingleEditableHost && activeElement instanceof HTMLElement) {
        const host = activeElement.closest<HTMLElement>(
          "[data-docx-paragraph-host='true']"
        );
        const nodeIndexAttr = host?.getAttribute(
          "data-docx-paragraph-node-index"
        );
        const offsets = host ? selectionOffsetsWithinElement(host) : undefined;
        if (host && nodeIndexAttr != null && offsets) {
          const nodeIndex = Number.parseInt(nodeIndexAttr, 10);
          if (Number.isFinite(nodeIndex)) {
            pendingEditableCaretRef.current = {
              nodeIndex,
              start: offsets.start,
              end: offsets.end,
            };
          }
        }
      }

      if (collapsedCaretInsideEditableHost) {
        scheduleDeferredCollapsedSelectionSync();
        return;
      }

      flushActiveRangeFromSelection();
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, [flushActiveRangeFromSelection, scheduleDeferredCollapsedSelectionSync]);

  React.useEffect(() => {
    const onPointerDown = (): void => {
      pointerSelectionDragRef.current = { down: true, moved: false };
    };
    const onPointerMove = (event: PointerEvent): void => {
      if (pointerSelectionDragRef.current.down && event.buttons !== 0) {
        pointerSelectionDragRef.current.moved = true;
      }
    };
    const onPointerUp = (): void => {
      pointerSelectionDragRef.current = { down: false, moved: false };
    };
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("pointermove", onPointerMove, true);
    window.addEventListener("pointerup", onPointerUp, true);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("pointermove", onPointerMove, true);
      window.removeEventListener("pointerup", onPointerUp, true);
    };
  }, []);

  const beginCrossNodeSelectionDrag = React.useCallback(
    (
      startBoundary: DocxTextRangeBoundary,
      pointerId: number,
      startX: number,
      startY: number
    ): void => {
      tableSelectionDragRef.current = {
        pointerId,
        startBoundary,
        startX,
        startY,
        hasMoved: false,
        explicitSelectionActive: false,
      };

      const onPointerMove = (pointerMoveEvent: PointerEvent): void => {
        const dragState = tableSelectionDragRef.current;
        if (!dragState || pointerMoveEvent.pointerId !== dragState.pointerId) {
          return;
        }

        const deltaX = Math.abs(pointerMoveEvent.clientX - dragState.startX);
        const deltaY = Math.abs(pointerMoveEvent.clientY - dragState.startY);
        if (deltaX + deltaY >= 3) {
          dragState.hasMoved = true;
        }

        const fallbackBias: "start" | "end" =
          pointerMoveEvent.clientY < dragState.startY ||
          (pointerMoveEvent.clientY === dragState.startY &&
            pointerMoveEvent.clientX < dragState.startX)
            ? "start"
            : "end";

        if (!dragState.explicitSelectionActive) {
          if (!dragState.hasMoved) {
            return;
          }

          const endBoundary = resolveBoundaryFromPoint(
            {
              x: pointerMoveEvent.clientX,
              y: pointerMoveEvent.clientY,
            },
            fallbackBias
          );
          if (!endBoundary) {
            return;
          }

          if (
            sameParagraphLocation(
              dragState.startBoundary.location,
              endBoundary.location
            )
          ) {
            return;
          }

          dragState.explicitSelectionActive = true;
        }

        pointerMoveEvent.preventDefault();
        const endBoundary = resolveBoundaryFromPoint(
          {
            x: pointerMoveEvent.clientX,
            y: pointerMoveEvent.clientY,
          },
          fallbackBias
        );
        if (!endBoundary) {
          return;
        }

        setSelectionFromDocxBoundaries(dragState.startBoundary, endBoundary);
      };

      const onPointerUp = (pointerUpEvent: PointerEvent): void => {
        const dragState = tableSelectionDragRef.current;
        if (!dragState || pointerUpEvent.pointerId !== dragState.pointerId) {
          return;
        }

        if (dragState.explicitSelectionActive && dragState.hasMoved) {
          const fallbackBias: "start" | "end" =
            pointerUpEvent.clientY < dragState.startY ||
            (pointerUpEvent.clientY === dragState.startY &&
              pointerUpEvent.clientX < dragState.startX)
              ? "start"
              : "end";
          const endBoundary = resolveBoundaryFromPoint(
            {
              x: pointerUpEvent.clientX,
              y: pointerUpEvent.clientY,
            },
            fallbackBias
          );
          if (endBoundary) {
            setSelectionFromDocxBoundaries(
              dragState.startBoundary,
              endBoundary
            );
          }
        }

        tableSelectionDragRef.current = undefined;
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        window.removeEventListener("pointercancel", onPointerUp);
      };

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp, { once: true });
      window.addEventListener("pointercancel", onPointerUp, { once: true });
    },
    [resolveBoundaryFromPoint, setSelectionFromDocxBoundaries]
  );

  const resolveTableCellLocationFromPoint = React.useCallback(
    (point: { x: number; y: number }): DocxTableCellLocation | undefined => {
      const element = document.elementFromPoint(point.x, point.y);
      return parseTableCellLocationFromElement(element);
    },
    []
  );

  const beginTableCellSelectionDrag = React.useCallback(
    (
      pointerId: number,
      anchorCell: DocxTableCellLocation,
      startX: number,
      startY: number,
      startBoundary?: DocxTextRangeBoundary
    ): void => {
      tableCellSelectionDragRef.current = {
        pointerId,
        anchorCell,
        startBoundary,
        startX,
        startY,
        isSelectingCells: false,
        isSelectingAcrossNodes: false,
      };

      const onPointerMove = (pointerMoveEvent: PointerEvent): void => {
        const dragState = tableCellSelectionDragRef.current;
        if (!dragState || pointerMoveEvent.pointerId !== dragState.pointerId) {
          return;
        }

        if (!dragState.isSelectingCells && !dragState.isSelectingAcrossNodes) {
          const deltaX = Math.abs(pointerMoveEvent.clientX - dragState.startX);
          const deltaY = Math.abs(pointerMoveEvent.clientY - dragState.startY);
          if (deltaX + deltaY < 3) {
            return;
          }
        }

        const fallbackBias: "start" | "end" =
          pointerMoveEvent.clientY < dragState.startY ||
          (pointerMoveEvent.clientY === dragState.startY &&
            pointerMoveEvent.clientX < dragState.startX)
            ? "start"
            : "end";

        if (dragState.isSelectingAcrossNodes) {
          const endBoundary = resolveBoundaryFromPoint(
            {
              x: pointerMoveEvent.clientX,
              y: pointerMoveEvent.clientY,
            },
            fallbackBias
          );
          if (!dragState.startBoundary || !endBoundary) {
            return;
          }

          clearTableCellSelection();
          setSelectionFromDocxBoundaries(dragState.startBoundary, endBoundary);
          pointerMoveEvent.preventDefault();
          return;
        }

        const hoveredCell = resolveTableCellLocationFromPoint({
          x: pointerMoveEvent.clientX,
          y: pointerMoveEvent.clientY,
        });
        if (
          hoveredCell &&
          hoveredCell.tableIndex === dragState.anchorCell.tableIndex
        ) {
          const movedToDifferentCell =
            hoveredCell.rowIndex !== dragState.anchorCell.rowIndex ||
            hoveredCell.cellIndex !== dragState.anchorCell.cellIndex;
          if (!movedToDifferentCell) {
            return;
          }

          if (!dragState.isSelectingCells) {
            dragState.isSelectingCells = true;
            pendingTableCellFocusRef.current = undefined;
            const selection = window.getSelection();
            selection?.removeAllRanges();
          }

          setTableCellSelectionRange({
            tableIndex: dragState.anchorCell.tableIndex,
            anchorRowIndex: dragState.anchorCell.rowIndex,
            anchorCellIndex: dragState.anchorCell.cellIndex,
            focusRowIndex: hoveredCell.rowIndex,
            focusCellIndex: hoveredCell.cellIndex,
          });
          editor.selectTableCell(
            hoveredCell.tableIndex,
            hoveredCell.rowIndex,
            hoveredCell.cellIndex
          );
          editor.setActiveTextRange(undefined);
          pointerMoveEvent.preventDefault();
          return;
        }

        if (!dragState.startBoundary) {
          return;
        }

        const endBoundary = resolveBoundaryFromPoint(
          {
            x: pointerMoveEvent.clientX,
            y: pointerMoveEvent.clientY,
          },
          fallbackBias
        );
        if (!endBoundary) {
          return;
        }

        dragState.isSelectingAcrossNodes = true;
        dragState.isSelectingCells = false;
        pendingTableCellFocusRef.current = undefined;
        clearTableCellSelection();
        setSelectionFromDocxBoundaries(dragState.startBoundary, endBoundary);
        pointerMoveEvent.preventDefault();
      };

      const onPointerUp = (pointerUpEvent: PointerEvent): void => {
        const dragState = tableCellSelectionDragRef.current;
        if (!dragState || pointerUpEvent.pointerId !== dragState.pointerId) {
          return;
        }

        if (dragState.isSelectingCells || dragState.isSelectingAcrossNodes) {
          suppressNextTableCellClickRef.current = true;
        }

        tableCellSelectionDragRef.current = undefined;
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        window.removeEventListener("pointercancel", onPointerUp);
      };

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp, { once: true });
      window.addEventListener("pointercancel", onPointerUp, { once: true });
    },
    [
      clearTableCellSelection,
      editor,
      resolveBoundaryFromPoint,
      resolveTableCellLocationFromPoint,
      setSelectionFromDocxBoundaries,
    ]
  );

  const onViewerPointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>): void => {
      if (tableContextMenuState || contextMenuState) {
        const target = event.target;
        if (
          target instanceof Element &&
          !target.closest(
            "[data-docx-table-context-menu='true'],[data-docx-context-menu='true']"
          )
        ) {
          closeTableContextMenu();
          closeContextMenu();
        }
      }

      if (event.button !== 0 && event.button !== undefined) {
        return;
      }

      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      if (
        activeHeaderFooterEdit &&
        !target.closest("[data-docx-header-footer-region='header']") &&
        !target.closest("[data-docx-header-footer-region='footer']")
      ) {
        scheduleCloseActiveHeaderFooterEdit(activeHeaderFooterEdit);
      }

      if (eventTargetIsInteractiveControl(target)) {
        return;
      }

      if (target.closest("[data-docx-table-cell='true']")) {
        return;
      }

      const paragraphHost = target.closest<HTMLElement>(
        "[data-docx-paragraph-host='true']"
      );
      if (!paragraphHost || !paragraphHost.isContentEditable) {
        return;
      }

      const startBoundary = resolveBoundaryFromPoint({
        x: event.clientX,
        y: event.clientY,
      });
      if (!startBoundary) {
        return;
      }

      beginCrossNodeSelectionDrag(
        startBoundary,
        event.pointerId,
        event.clientX,
        event.clientY
      );
    },
    [
      activeHeaderFooterEdit,
      beginCrossNodeSelectionDrag,
      closeContextMenu,
      closeTableContextMenu,
      contextMenuState,
      resolveBoundaryFromPoint,
      scheduleCloseActiveHeaderFooterEdit,
      tableContextMenuState,
    ]
  );

  const onViewerContextMenu = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>): void => {
      if (isReadOnly || event.defaultPrevented) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      if (
        target.closest("[data-docx-table-context-menu='true']") ||
        target.closest("[data-docx-context-menu='true']")
      ) {
        return;
      }

      if (eventTargetIsInteractiveControl(target)) {
        return;
      }

      const selection = window.getSelection();
      const domRange =
        selection && selection.rangeCount > 0
          ? selection.getRangeAt(0)
          : undefined;
      const isExpandedSelection = Boolean(
        selection && !selection.isCollapsed && domRange
      );
      const pointIsWithinSelection = isExpandedSelection
        ? Array.from(domRange?.getClientRects() ?? []).some(
            (rect) =>
              event.clientX >= rect.left &&
              event.clientX <= rect.right &&
              event.clientY >= rect.top &&
              event.clientY <= rect.bottom
          )
        : false;

      let resolvedRange = resolveActiveRangeFromDomSelection();
      if (!pointIsWithinSelection) {
        const boundary = resolveBoundaryFromPoint({
          x: event.clientX,
          y: event.clientY,
        });
        if (!boundary) {
          return;
        }

        clearTableCellSelection();
        setSelectionFromDocxBoundaries(boundary, boundary);
        resolvedRange = {
          start: {
            location: cloneTextRangeLocation(boundary.location),
            offset: boundary.offset,
          },
          end: {
            location: cloneTextRangeLocation(boundary.location),
            offset: boundary.offset,
          },
        };
      } else if (resolvedRange) {
        clearTableCellSelection();
        editor.setActiveTextRange(resolvedRange);
      }

      const location = resolvedRange
        ? cloneTextRangeLocation(
            normalizeTextRange(resolvedRange).start.location
          )
        : undefined;

      event.preventDefault();
      event.stopPropagation();
      openContextMenu(
        {
          kind: "text",
          activeTextRange: resolvedRange
            ? normalizeTextRange(resolvedRange)
            : undefined,
          location,
        },
        {
          x: event.clientX,
          y: event.clientY,
        }
      );
    },
    [
      clearTableCellSelection,
      editor,
      isReadOnly,
      openContextMenu,
      resolveActiveRangeFromDomSelection,
      resolveBoundaryFromPoint,
      setSelectionFromDocxBoundaries,
    ]
  );

  const handleDeleteAcrossTableCellSelection = React.useCallback(
    (event: React.KeyboardEvent<HTMLElement>): boolean => {
      if (isReadOnly) {
        return false;
      }

      if (event.defaultPrevented) {
        return false;
      }

      if (event.key !== "Backspace" && event.key !== "Delete") {
        return false;
      }

      if (!tableCellSelectionRange) {
        return false;
      }
      if (isSingleTableCellSelectionRange(tableCellSelectionRange)) {
        return false;
      }

      const tableNode = editor.model.nodes[tableCellSelectionRange.tableIndex];
      if (!tableNode || tableNode.type !== "table") {
        return false;
      }

      const selectedCells = selectedTableCellLocations(
        tableNode,
        tableCellSelectionRange
      );
      if (selectedCells.length === 0) {
        return false;
      }

      event.preventDefault();
      event.stopPropagation();

      pendingTableCellFocusRef.current = undefined;
      editor.setActiveTextRange(undefined);

      if (tableSelectionCoversWholeTable(tableNode, tableCellSelectionRange)) {
        tableCellDraftsRef.current.forEach((_, draftKey) => {
          if (draftKey.startsWith(`${tableCellSelectionRange.tableIndex}:`)) {
            tableCellDraftsRef.current.delete(draftKey);
          }
        });
        clearTableCellSelection();
        editor.deleteTable(tableCellSelectionRange.tableIndex);
        return true;
      }

      selectedCells.forEach((cellLocation) => {
        tableCellDraftsRef.current.delete(
          `${tableCellSelectionRange.tableIndex}:${cellLocation.rowIndex}:${cellLocation.cellIndex}`
        );
      });
      editor.clearTableCellContents(
        tableCellSelectionRange.tableIndex,
        selectedCells
      );
      const focusCell = selectedCells[0];
      if (focusCell) {
        editor.selectTableCell(
          tableCellSelectionRange.tableIndex,
          focusCell.rowIndex,
          focusCell.cellIndex
        );
        setSingleTableCellSelection(
          tableCellSelectionRange.tableIndex,
          focusCell.rowIndex,
          focusCell.cellIndex
        );
      } else {
        clearTableCellSelection();
      }

      return true;
    },
    [
      clearTableCellSelection,
      editor,
      isReadOnly,
      setSingleTableCellSelection,
      tableCellSelectionRange,
    ]
  );

  const onViewerKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>): void => {
      if (!isReadOnly && handleDeleteAcrossTableCellSelection(event)) {
        return;
      }

      const isPlainBackspaceOrDelete =
        (event.key === "Backspace" || event.key === "Delete") &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey &&
        !event.shiftKey;
      if (!isReadOnly && isPlainBackspaceOrDelete && !event.defaultPrevented) {
        const target = event.target;
        if (
          !(target instanceof Element) ||
          !eventTargetIsInteractiveControl(target)
        ) {
          let activeRange = editor.activeTextRange;
          if (
            !activeRange ||
            compareTextRangeBoundaries(activeRange.start, activeRange.end) >= 0
          ) {
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
              const domRange = selection.getRangeAt(0);
              const startBoundary = resolveParagraphBoundaryFromSelectionPoint(
                domRange.startContainer,
                domRange.startOffset
              );
              const endBoundary = resolveParagraphBoundaryFromSelectionPoint(
                domRange.endContainer,
                domRange.endOffset
              );
              if (startBoundary && endBoundary) {
                activeRange = normalizeTextRange({
                  start: startBoundary,
                  end: endBoundary,
                });
              }
            }
          }

          if (
            activeRange &&
            compareTextRangeBoundaries(activeRange.start, activeRange.end) < 0
          ) {
            const normalizedActiveRange = normalizeTextRange(activeRange);
            event.preventDefault();
            event.stopPropagation();

            syncSelectionFromDocxRange({
              start: {
                location: cloneTextRangeLocation(
                  normalizedActiveRange.start.location
                ),
                offset: normalizedActiveRange.start.offset,
              },
              end: {
                location: cloneTextRangeLocation(
                  normalizedActiveRange.start.location
                ),
                offset: normalizedActiveRange.start.offset,
              },
            });

            const collapsedRange = editor.deleteExpandedSelection(
              normalizedActiveRange
            );
            if (collapsedRange) {
              paragraphDraftsRef.current.clear();
              tableCellDraftsRef.current.clear();
              tableCellParagraphDraftsRef.current.clear();
              syncSelectionFromDocxRange(collapsedRange);
            }
            return;
          }
        }
      }

      const isSelectAll =
        (event.key === "a" || event.key === "A") &&
        (event.ctrlKey || event.metaKey);
      if (!isSelectAll || event.shiftKey || event.altKey) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      if (eventTargetIsInteractiveControl(target)) {
        return;
      }

      if (!viewerRootRef.current?.contains(target)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      selectAllDocumentText();
    },
    [
      editor,
      handleDeleteAcrossTableCellSelection,
      isReadOnly,
      resolveParagraphBoundaryFromSelectionPoint,
      selectAllDocumentText,
      syncSelectionFromDocxRange,
    ]
  );

  const onViewerKeyUp = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>): void => {
      if (!shouldSyncActiveRangeOnKeyUp(event)) {
        return;
      }

      flushActiveRangeFromSelection();
    },
    [flushActiveRangeFromSelection]
  );

  const handleDeleteAcrossSelection = React.useCallback(
    (event: React.KeyboardEvent<HTMLElement>): boolean => {
      if (isReadOnly) {
        return false;
      }

      if (event.defaultPrevented) {
        return false;
      }

      if (event.key !== "Backspace" && event.key !== "Delete") {
        return false;
      }

      let activeRange = editor.activeTextRange;
      if (
        !activeRange ||
        compareTextRangeBoundaries(activeRange.start, activeRange.end) >= 0
      ) {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const domRange = selection.getRangeAt(0);
          const startBoundary = resolveParagraphBoundaryFromSelectionPoint(
            domRange.startContainer,
            domRange.startOffset
          );
          const endBoundary = resolveParagraphBoundaryFromSelectionPoint(
            domRange.endContainer,
            domRange.endOffset
          );
          if (startBoundary && endBoundary) {
            activeRange = normalizeTextRange({
              start: startBoundary,
              end: endBoundary,
            });
          }
        }
      }

      if (
        !activeRange ||
        compareTextRangeBoundaries(activeRange.start, activeRange.end) >= 0
      ) {
        return false;
      }
      const normalizedActiveRange = normalizeTextRange(activeRange);

      event.preventDefault();
      event.stopPropagation();

      // Collapse the native DOM selection immediately so deleted ranges do not
      // remain visibly selected while model updates reconcile.
      syncSelectionFromDocxRange({
        start: {
          location: cloneTextRangeLocation(
            normalizedActiveRange.start.location
          ),
          offset: normalizedActiveRange.start.offset,
        },
        end: {
          location: cloneTextRangeLocation(
            normalizedActiveRange.start.location
          ),
          offset: normalizedActiveRange.start.offset,
        },
      });

      const collapsedRange = editor.deleteExpandedSelection(
        normalizedActiveRange
      );
      if (collapsedRange) {
        paragraphDraftsRef.current.clear();
        tableCellDraftsRef.current.clear();
        tableCellParagraphDraftsRef.current.clear();
        syncSelectionFromDocxRange(collapsedRange);
      }

      return true;
    },
    [
      editor,
      isReadOnly,
      resolveParagraphBoundaryFromSelectionPoint,
      syncSelectionFromDocxRange,
    ]
  );

  const replaceExpandedSelectionWithText = React.useCallback(
    (rawText: string): boolean => {
      if (isReadOnly) {
        return false;
      }

      let activeRange = editor.activeTextRange;
      if (!activeRange) {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const domRange = selection.getRangeAt(0);
          const startBoundary = resolveParagraphBoundaryFromSelectionPoint(
            domRange.startContainer,
            domRange.startOffset
          );
          const endBoundary = resolveParagraphBoundaryFromSelectionPoint(
            domRange.endContainer,
            domRange.endOffset
          );
          if (startBoundary && endBoundary) {
            activeRange = normalizeTextRange({
              start: startBoundary,
              end: endBoundary,
            });
          }
        }
      }
      if (!activeRange) {
        return false;
      }

      const normalizedRange = normalizeTextRange(activeRange);
      if (
        compareTextRangeBoundaries(
          normalizedRange.start,
          normalizedRange.end
        ) >= 0
      ) {
        return false;
      }

      const replacementText = rawText.replace(/\r\n?/g, "\n");
      const collapsedRange = editor.replaceExpandedSelection(
        replacementText,
        normalizedRange
      );
      if (!collapsedRange) {
        return false;
      }

      paragraphDraftsRef.current.clear();
      tableCellDraftsRef.current.clear();
      tableCellParagraphDraftsRef.current.clear();
      syncSelectionFromDocxRange(collapsedRange);
      return true;
    },
    [
      editor,
      isReadOnly,
      resolveParagraphBoundaryFromSelectionPoint,
      syncSelectionFromDocxRange,
    ]
  );

  const resolveContextMenuActions = React.useCallback(
    (context: DocxContextMenuContext): DocxContextMenuAction[] => {
      if (context.kind === "table") {
        return tableContextMenuActions.map((action) => ({
          id: action.id,
          label: action.label,
          destructive: action.destructive,
        }));
      }

      const normalizedRange = context.activeTextRange
        ? normalizeTextRange(context.activeTextRange)
        : undefined;
      const hasExpandedRange = Boolean(
        normalizedRange &&
          compareTextRangeBoundaries(
            normalizedRange.start,
            normalizedRange.end
          ) < 0
      );
      const clipboardActions = DEFAULT_CONTEXT_MENU_CLIPBOARD_ACTIONS.map(
        (action) => ({
          ...action,
          disabled:
            (action.id === "cut" || action.id === "copy") && !hasExpandedRange,
        })
      );

      if (context.kind !== "image") {
        return clipboardActions;
      }

      const floating = context.image?.floating;
      const wrapState =
        context.image?.wrap ?? resolveDocxImageWrapState(floating);
      const hasFloatingImage = Boolean(floating);
      const normalizedZIndex = Number.isFinite(floating?.zIndex)
        ? clampNumber(
            Math.round(floating?.zIndex as number),
            WORD_IMAGE_Z_INDEX_MIN,
            WORD_IMAGE_Z_INDEX_MAX
          )
        : WORD_IMAGE_Z_INDEX_DEFAULT;
      const bringForwardDisabled =
        !hasFloatingImage || normalizedZIndex >= WORD_IMAGE_Z_INDEX_MAX;
      const sendBackwardDisabled =
        !hasFloatingImage || normalizedZIndex <= WORD_IMAGE_Z_INDEX_MIN;

      const layerActionDisabledById = (
        actionId: DocxContextMenuActionId | (string & {})
      ): boolean => {
        switch (actionId) {
          case "image-bring-to-front":
            return (
              !hasFloatingImage || normalizedZIndex >= WORD_IMAGE_Z_INDEX_MAX
            );
          case "image-bring-forward":
            return bringForwardDisabled;
          case "image-send-to-back":
            return (
              !hasFloatingImage || normalizedZIndex <= WORD_IMAGE_Z_INDEX_MIN
            );
          case "image-send-backward":
            return sendBackwardDisabled;
          default:
            return false;
        }
      };

      const wrapActions = DOCX_IMAGE_WRAP_MODE_ACTIONS.map((action) => ({
        id: action.actionId,
        label: action.label,
        checked: wrapState.mode === action.mode,
        separatorBefore: action.separatorBefore,
      }));
      const positioningActions: DocxContextMenuAction[] = [
        {
          id: "image-edit-wrap-boundary",
          label: "Edit Wrap Boundary",
          disabled: true,
          separatorBefore: true,
        },
        {
          id: "image-move-with-text",
          label: "Move with Text",
          checked: wrapState.moveWithText,
          disabled: wrapState.mode === "inline",
          separatorBefore: true,
        },
        {
          id: "image-fix-position-on-page",
          label: "Fix Position on Page",
          checked: wrapState.fixedPositionOnPage,
          disabled: wrapState.mode === "inline",
        },
        {
          id: "image-more-layout-options",
          label: "More Layout Options...",
          disabled: true,
          separatorBefore: true,
        },
      ];
      const layerActions = DEFAULT_CONTEXT_MENU_IMAGE_LAYER_ACTIONS.map(
        (action) => {
          const children = action.children?.map((child) => ({
            ...child,
            disabled: layerActionDisabledById(child.id),
          }));
          return {
            ...action,
            separatorBefore: true,
            disabled: children
              ? children.every((child) => Boolean(child.disabled))
              : layerActionDisabledById(action.id),
            children,
          };
        }
      );

      return [
        ...clipboardActions,
        ...wrapActions,
        ...positioningActions,
        ...layerActions,
      ];
    },
    [tableContextMenuActions]
  );

  const runContextMenuAction = React.useCallback(
    (
      actionId: DocxContextMenuActionId | (string & {}),
      providedContext?: DocxContextMenuContext
    ): void => {
      const isTableAction = (
        value: DocxContextMenuActionId | (string & {})
      ): value is DocxTableContextMenuActionId =>
        value === "insert-row-above" ||
        value === "insert-row-below" ||
        value === "insert-column-left" ||
        value === "insert-column-right" ||
        value === "delete-row" ||
        value === "delete-column" ||
        value === "delete-table";
      if (isTableAction(actionId)) {
        const providedTableContext =
          providedContext?.kind === "table"
            ? providedContext.tableContext
            : undefined;
        runTableContextMenuAction(actionId, providedTableContext);
        return;
      }

      const context = providedContext ?? contextMenuState;
      if (!context) {
        return;
      }

      const currentRange = context.activeTextRange
        ? normalizeTextRange(context.activeTextRange)
        : resolveActiveRangeFromDomSelection();
      const hasExpandedRange = Boolean(
        currentRange &&
          compareTextRangeBoundaries(currentRange.start, currentRange.end) < 0
      );

      const restoreDomSelectionFromRange = (range: DocxTextRange): boolean => {
        const normalizedRange = normalizeTextRange(range);
        const paragraphHost = resolveParagraphHostElement(
          normalizedRange.start.location
        );
        if (!paragraphHost) {
          return false;
        }

        const focusTarget =
          normalizedRange.start.location.kind === "table-cell"
            ? paragraphHost.closest<HTMLElement>("[data-docx-cell-key]") ??
              paragraphHost
            : paragraphHost;
        try {
          focusTarget.focus({ preventScroll: true });
        } catch {
          focusTarget.focus();
        }
        setSelectionFromDocxBoundaries(
          normalizedRange.start,
          normalizedRange.end
        );
        return true;
      };

      const clearDraftsAndSetSelection = (range: DocxTextRange): void => {
        paragraphDraftsRef.current.clear();
        tableCellDraftsRef.current.clear();
        tableCellParagraphDraftsRef.current.clear();
        syncSelectionFromDocxRange(range);
      };

      const resolveSelectedTextForRange = (
        range: DocxTextRange | undefined
      ): string => {
        const domSelectedText = window.getSelection()?.toString() ?? "";
        if (domSelectedText.length > 0) {
          return domSelectedText;
        }
        if (!range) {
          return "";
        }

        const normalizedRange = normalizeTextRange(range);
        if (
          compareTextRangeBoundaries(
            normalizedRange.start,
            normalizedRange.end
          ) >= 0
        ) {
          return "";
        }

        const textSegments = paragraphRangeForMutate(
          editor.model,
          normalizedRange.start.location,
          normalizedRange.end.location
        ).map(({ location }) => {
          const { paragraph } = getParagraphAtLocation(editor.model, location);
          if (!paragraph) {
            return "";
          }
          const [safeStart, safeEnd] = resolveRangeBoundaryOffsetsForParagraph(
            location,
            normalizedRange.start,
            normalizedRange.end,
            paragraph
          );
          return paragraphText(paragraph).slice(safeStart, safeEnd);
        });
        return textSegments.join("\n");
      };

      const deleteExpandedRange = (): boolean => {
        if (!currentRange || !hasExpandedRange) {
          return false;
        }
        syncSelectionFromDocxRange({
          start: {
            location: cloneTextRangeLocation(currentRange.start.location),
            offset: currentRange.start.offset,
          },
          end: {
            location: cloneTextRangeLocation(currentRange.start.location),
            offset: currentRange.start.offset,
          },
        });
        const collapsedRange = editor.deleteExpandedSelection(currentRange);
        if (!collapsedRange) {
          return false;
        }
        clearDraftsAndSetSelection(collapsedRange);
        return true;
      };

      const insertTextAtCollapsedRange = (rawText: string): boolean => {
        if (!currentRange) {
          return false;
        }

        const normalizedRange = normalizeTextRange(currentRange);
        if (
          compareTextRangeBoundaries(
            normalizedRange.start,
            normalizedRange.end
          ) !== 0
        ) {
          return false;
        }

        const { paragraph } = getParagraphAtLocation(
          editor.model,
          normalizedRange.start.location
        );
        if (!paragraph) {
          return false;
        }

        const replacementText = rawText.replace(/\r\n?/g, "\n");
        const safeOffset = normalizeRangeBoundaryParagraphOffset(
          paragraph,
          normalizedRange.start.offset
        );
        const existingText = paragraphText(paragraph);
        const nextText = `${existingText.slice(
          0,
          safeOffset
        )}${replacementText}${existingText.slice(safeOffset)}`;
        if (normalizedRange.start.location.kind === "paragraph") {
          editor.commitParagraphText(
            normalizedRange.start.location.nodeIndex,
            nextText
          );
        } else {
          editor.commitTableCellParagraphTextRecursive(
            normalizedRange.start.location.tableIndex,
            normalizedRange.start.location.rowIndex,
            normalizedRange.start.location.cellIndex,
            normalizedRange.start.location.paragraphIndex,
            nextText
          );
        }

        const insertedRange: DocxTextRange = {
          start: {
            location: cloneTextRangeLocation(normalizedRange.start.location),
            offset: safeOffset + replacementText.length,
          },
          end: {
            location: cloneTextRangeLocation(normalizedRange.start.location),
            offset: safeOffset + replacementText.length,
          },
        };
        clearDraftsAndSetSelection(insertedRange);
        return true;
      };

      const resolveContextImage = ():
        | { location: DocxImageLocation; image: ImageRunNode }
        | undefined => {
        const imageLocation = context.image?.location;
        if (!imageLocation) {
          return undefined;
        }

        const { paragraph } = getParagraphAtLocation(
          editor.model,
          imageLocationToParagraphLocation(imageLocation)
        );
        const child = paragraph?.children[imageLocation.childIndex];
        if (!child || child.type !== "image") {
          return undefined;
        }

        return {
          location: imageLocation,
          image: child,
        };
      };
      const resolveContextImagePlacement = (
        imageLocation: DocxImageLocation
      ): {
        paragraphLeftPx?: number;
        paragraphTopPx?: number;
        pageLeftPx?: number;
        pageTopPx?: number;
      } => {
        if (typeof document === "undefined") {
          return {};
        }

        const imageElement = document.querySelector<HTMLElement>(
          `[data-docx-image-location="${imageLocationKey(imageLocation)}"]`
        );
        if (!imageElement) {
          return {};
        }

        const imageRect = imageElement.getBoundingClientRect();
        const paragraphHost = imageElement.closest<HTMLElement>(
          "[data-docx-paragraph-host='true']"
        );
        const paragraphRect = paragraphHost?.getBoundingClientRect();
        const pageSurface = imageElement.closest<HTMLElement>(
          "[data-docx-page-surface='true']"
        );
        const pageSurfaceRect = pageSurface?.getBoundingClientRect();

        return {
          paragraphLeftPx: paragraphRect
            ? Math.round(imageRect.left - paragraphRect.left)
            : undefined,
          paragraphTopPx: paragraphRect
            ? Math.round(imageRect.top - paragraphRect.top)
            : undefined,
          pageLeftPx: pageSurfaceRect
            ? Math.round(
                imageRect.left -
                  pageSurfaceRect.left -
                  documentLayout.marginsPx.left
              )
            : undefined,
          pageTopPx: pageSurfaceRect
            ? Math.round(
                imageRect.top -
                  pageSurfaceRect.top -
                  documentLayout.marginsPx.top
              )
            : undefined,
        };
      };
      const resolveContextImageWrapSeed = (
        resolvedImage: { location: DocxImageLocation; image: ImageRunNode },
        mode: DocxImageWrapMode
      ): Partial<NonNullable<ImageRunNode["floating"]>> | undefined => {
        if (mode === "inline") {
          return undefined;
        }

        const currentWrapState = resolveDocxImageWrapState(
          resolvedImage.image.floating
        );
        const placement = resolveContextImagePlacement(resolvedImage.location);
        const fixedPositionOnPage =
          currentWrapState.fixedPositionOnPage ||
          mode === "behindText" ||
          mode === "inFrontOfText";
        return {
          horizontalRelativeTo: fixedPositionOnPage ? "margin" : "column",
          verticalRelativeTo: fixedPositionOnPage ? "margin" : "paragraph",
          ...(Number.isFinite(
            fixedPositionOnPage
              ? placement.pageLeftPx
              : placement.paragraphLeftPx
          )
            ? {
                xPx: Math.round(
                  (fixedPositionOnPage
                    ? placement.pageLeftPx
                    : placement.paragraphLeftPx) as number
                ),
              }
            : undefined),
          ...(Number.isFinite(
            fixedPositionOnPage ? placement.pageTopPx : placement.paragraphTopPx
          )
            ? {
                yPx: Math.round(
                  (fixedPositionOnPage
                    ? placement.pageTopPx
                    : placement.paragraphTopPx) as number
                ),
              }
            : undefined),
          ...(fixedPositionOnPage
            ? {
                horizontalAlign: undefined,
                verticalAlign: undefined,
              }
            : undefined),
        };
      };
      const resolveContextImageAnchorPatch = (
        resolvedImage: { location: DocxImageLocation; image: ImageRunNode },
        moveWithText: boolean
      ): Partial<NonNullable<ImageRunNode["floating"]>> | undefined => {
        const floating = resolvedImage.image.floating;
        if (!floating) {
          return undefined;
        }

        const placement = resolveContextImagePlacement(resolvedImage.location);
        const wrapMode = resolveDocxImageWrapState(floating).mode;
        const isAbsoluteOverlay =
          wrapMode === "behindText" || wrapMode === "inFrontOfText";
        return moveWithText
          ? isAbsoluteOverlay
            ? {
                xPx: placement.paragraphLeftPx ?? floating.xPx ?? 0,
                yPx: placement.paragraphTopPx ?? floating.yPx ?? 0,
                horizontalAlign: undefined,
                verticalAlign: undefined,
                horizontalRelativeTo: "column",
                verticalRelativeTo: "paragraph",
              }
            : {
                yPx: placement.paragraphTopPx ?? floating.yPx ?? 0,
                verticalRelativeTo: "paragraph",
              }
          : isAbsoluteOverlay
          ? {
              xPx: placement.pageLeftPx ?? floating.xPx ?? 0,
              yPx: placement.pageTopPx ?? floating.yPx ?? 0,
              horizontalAlign: undefined,
              verticalAlign: undefined,
              horizontalRelativeTo: "margin",
              verticalRelativeTo: "margin",
            }
          : {
              yPx: placement.pageTopPx ?? floating.yPx ?? 0,
              verticalRelativeTo: "margin",
            };
      };

      const tryCopySelectionWithLegacyCommand = (): boolean => {
        if (typeof document.execCommand !== "function") {
          return false;
        }
        try {
          return document.execCommand("copy");
        } catch {
          return false;
        }
      };

      const copyTextToClipboard = async (text: string): Promise<boolean> => {
        if (!text) {
          return false;
        }
        contextMenuClipboardTextRef.current = text;
        if (
          typeof navigator === "undefined" ||
          !navigator.clipboard?.writeText
        ) {
          return false;
        }
        try {
          await navigator.clipboard.writeText(text);
          return true;
        } catch {
          return false;
        }
      };

      const readTextFromClipboard = async (): Promise<string> => {
        if (typeof navigator !== "undefined" && navigator.clipboard?.readText) {
          try {
            const text = await navigator.clipboard.readText();
            if (text.length > 0) {
              contextMenuClipboardTextRef.current = text;
              return text;
            }
          } catch {
            // Fall through to the in-app clipboard fallback.
          }
        }
        return contextMenuClipboardTextRef.current;
      };

      void (async (): Promise<void> => {
        switch (actionId) {
          case "copy": {
            if (!hasExpandedRange) {
              break;
            }
            if (currentRange) {
              restoreDomSelectionFromRange(currentRange);
            }
            const selectedText = resolveSelectedTextForRange(currentRange);
            if (!selectedText) {
              break;
            }
            tryCopySelectionWithLegacyCommand();
            await copyTextToClipboard(selectedText);
            break;
          }
          case "cut": {
            if (!hasExpandedRange) {
              break;
            }
            if (currentRange) {
              restoreDomSelectionFromRange(currentRange);
            }
            const selectedText = resolveSelectedTextForRange(currentRange);
            if (selectedText) {
              tryCopySelectionWithLegacyCommand();
              await copyTextToClipboard(selectedText);
            }
            deleteExpandedRange();
            break;
          }
          case "paste": {
            const pastedText = await readTextFromClipboard();
            if (!pastedText) {
              break;
            }

            if (currentRange && hasExpandedRange) {
              const collapsedRange = editor.replaceExpandedSelection(
                pastedText,
                currentRange
              );
              if (collapsedRange) {
                clearDraftsAndSetSelection(collapsedRange);
                break;
              }
            }

            if (currentRange) {
              restoreDomSelectionFromRange(currentRange);
            }
            if (typeof document.execCommand === "function") {
              try {
                if (document.execCommand("insertText", false, pastedText)) {
                  setActiveRangeFromSelection();
                  break;
                }
              } catch {
                // Ignore insertText failures in unsupported browsers.
              }
            }
            insertTextAtCollapsedRange(pastedText);
            break;
          }
          case "image-bring-to-front":
          case "image-bring-forward":
          case "image-in-front-of-text":
          case "image-send-to-back":
          case "image-send-backward":
          case "image-behind-text":
          case "image-wrap-inline":
          case "image-wrap-square":
          case "image-wrap-tight":
          case "image-wrap-through":
          case "image-wrap-top-and-bottom":
          case "image-wrap-behind-text":
          case "image-wrap-in-front-of-text":
          case "image-move-with-text":
          case "image-fix-position-on-page":
          case "image-edit-wrap-boundary":
          case "image-more-layout-options": {
            const resolvedImage = resolveContextImage();
            if (!resolvedImage) {
              break;
            }

            const wrapMode = imageWrapModeFromActionId(actionId);
            if (wrapMode) {
              editor.setImageWrapMode(
                resolvedImage.location,
                wrapMode,
                resolveContextImageWrapSeed(resolvedImage, wrapMode)
              );
              break;
            }

            if (
              actionId === "image-move-with-text" ||
              actionId === "image-fix-position-on-page"
            ) {
              const anchorPatch = resolveContextImageAnchorPatch(
                resolvedImage,
                actionId === "image-move-with-text"
              );
              if (anchorPatch) {
                editor.moveFloatingImage(resolvedImage.location, anchorPatch);
              }
              break;
            }

            if (!resolvedImage.image.floating) {
              break;
            }

            const floating = resolvedImage.image.floating;
            const currentZIndex = Number.isFinite(floating.zIndex)
              ? clampNumber(
                  Math.round(floating.zIndex as number),
                  WORD_IMAGE_Z_INDEX_MIN,
                  WORD_IMAGE_Z_INDEX_MAX
                )
              : WORD_IMAGE_Z_INDEX_DEFAULT;
            let patch:
              | Partial<NonNullable<ImageRunNode["floating"]>>
              | undefined;

            switch (actionId) {
              case "image-bring-to-front":
                patch = {
                  behindDocument: false,
                  zIndex: WORD_IMAGE_Z_INDEX_MAX,
                };
                break;
              case "image-bring-forward":
                patch = {
                  behindDocument: false,
                  zIndex: clampNumber(
                    currentZIndex + WORD_IMAGE_Z_INDEX_STEP,
                    WORD_IMAGE_Z_INDEX_MIN,
                    WORD_IMAGE_Z_INDEX_MAX
                  ),
                };
                break;
              case "image-send-to-back":
                patch = {
                  behindDocument: false,
                  zIndex: WORD_IMAGE_Z_INDEX_MIN,
                };
                break;
              case "image-send-backward":
                patch = {
                  behindDocument: false,
                  zIndex: clampNumber(
                    currentZIndex - WORD_IMAGE_Z_INDEX_STEP,
                    WORD_IMAGE_Z_INDEX_MIN,
                    WORD_IMAGE_Z_INDEX_MAX
                  ),
                };
                break;
              default:
                patch = undefined;
                break;
            }

            if (patch) {
              editor.moveFloatingImage(resolvedImage.location, patch);
            }
            break;
          }
          default:
            break;
        }

        closeContextMenu();
      })();
    },
    [
      closeContextMenu,
      contextMenuState,
      editor,
      replaceExpandedSelectionWithText,
      resolveActiveRangeFromDomSelection,
      runTableContextMenuAction,
      setActiveRangeFromSelection,
      syncSelectionFromDocxRange,
    ]
  );

  React.useEffect(() => {
    const onWindowKeyDown = (event: KeyboardEvent): void => {
      if (isReadOnly || event.defaultPrevented) {
        return;
      }

      const isPlainBackspaceOrDelete =
        (event.key === "Backspace" || event.key === "Delete") &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey &&
        !event.shiftKey;
      if (!isPlainBackspaceOrDelete) {
        return;
      }

      const target = event.target;
      if (
        target instanceof Element &&
        eventTargetIsInteractiveControl(target)
      ) {
        return;
      }

      const rootElement = viewerRootRef.current;
      const selection = window.getSelection();
      if (!rootElement || !selection || selection.rangeCount === 0) {
        return;
      }

      const domRange = selection.getRangeAt(0);
      if (
        !rootElement.contains(domRange.startContainer) ||
        !rootElement.contains(domRange.endContainer)
      ) {
        return;
      }

      const startBoundary = resolveParagraphBoundaryFromSelectionPoint(
        domRange.startContainer,
        domRange.startOffset
      );
      const endBoundary = resolveParagraphBoundaryFromSelectionPoint(
        domRange.endContainer,
        domRange.endOffset
      );
      if (!startBoundary || !endBoundary) {
        return;
      }

      const normalizedRange = normalizeTextRange({
        start: startBoundary,
        end: endBoundary,
      });
      if (
        compareTextRangeBoundaries(
          normalizedRange.start,
          normalizedRange.end
        ) >= 0
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      syncSelectionFromDocxRange({
        start: {
          location: cloneTextRangeLocation(normalizedRange.start.location),
          offset: normalizedRange.start.offset,
        },
        end: {
          location: cloneTextRangeLocation(normalizedRange.start.location),
          offset: normalizedRange.start.offset,
        },
      });
      const collapsedRange = editor.deleteExpandedSelection(normalizedRange);
      if (!collapsedRange) {
        return;
      }

      paragraphDraftsRef.current.clear();
      tableCellDraftsRef.current.clear();
      tableCellParagraphDraftsRef.current.clear();
      syncSelectionFromDocxRange(collapsedRange);
    };

    window.addEventListener("keydown", onWindowKeyDown, true);
    return () => {
      window.removeEventListener("keydown", onWindowKeyDown, true);
    };
  }, [
    editor,
    isReadOnly,
    resolveParagraphBoundaryFromSelectionPoint,
    syncSelectionFromDocxRange,
  ]);

  React.useEffect(() => {
    if (!selectedImage) {
      return;
    }

    const { paragraph } = getParagraphAtLocation(
      editor.model,
      imageLocationToParagraphLocation(selectedImage)
    );
    const child = paragraph?.children[selectedImage.childIndex];
    if (!child || child.type !== "image") {
      setSelectedImage(undefined);
      setResizePreview(undefined);
    }
  }, [editor.model, selectedImage]);

  React.useEffect(() => {
    activeWrappedParagraphSessionRef.current = activeWrappedParagraphSession;
  }, [activeWrappedParagraphSession]);

  React.useEffect(() => {
    if (!selectedImage && !selectedSectionImageKey) {
      return;
    }

    setSelectedDropCapNodeIndex(undefined);
    setHoveredDropCapNodeIndex(undefined);
    setDropCapMovePreview(undefined);
    setActiveWrappedParagraphSession(undefined);
  }, [selectedImage, selectedSectionImageKey]);

  React.useEffect(() => {
    if (selectedDropCapNodeIndex === undefined) {
      return;
    }

    const paragraph = editor.model.nodes[selectedDropCapNodeIndex];
    if (
      !paragraph ||
      paragraph.type !== "paragraph" ||
      !paragraphDropCap(paragraph)
    ) {
      setSelectedDropCapNodeIndex(undefined);
      setHoveredDropCapNodeIndex(undefined);
      setDropCapMovePreview(undefined);
    }
  }, [editor.model, selectedDropCapNodeIndex]);

  React.useEffect(() => {
    if (!activeWrappedParagraphSession) {
      return;
    }

    const paragraph = getParagraphAtLocation(
      editor.model,
      activeWrappedParagraphSession.location
    ).paragraph;
    if (!paragraph) {
      setActiveWrappedParagraphSession(undefined);
      return;
    }

    const modelText = wrappedParagraphSessionText(paragraph);
    if (
      modelText !== activeWrappedParagraphSession.text &&
      !activeWrappedParagraphSession.isComposing
    ) {
      setActiveWrappedParagraphSession((current) =>
        current &&
        current.locationKey === activeWrappedParagraphSession.locationKey
          ? {
              ...current,
              text: modelText,
              selectionStart: Math.max(
                0,
                Math.min(current.selectionStart, modelText.length)
              ),
              selectionEnd: Math.max(
                0,
                Math.min(current.selectionEnd, modelText.length)
              ),
            }
          : current
      );
    }
  }, [activeWrappedParagraphSession, editor.model]);

  React.useEffect(() => {
    if (!activeWrappedParagraphSession) {
      return;
    }

    focusWrappedParagraphTextarea();
  }, [activeWrappedParagraphSession, focusWrappedParagraphTextarea]);

  React.useEffect(() => {
    const textarea = wrappedParagraphTextareaRef.current;
    const session = activeWrappedParagraphSession;
    if (!textarea || !session) {
      return;
    }

    const safeStart = Math.max(
      0,
      Math.min(session.selectionStart, textarea.value.length)
    );
    const safeEnd = Math.max(
      safeStart,
      Math.min(session.selectionEnd, textarea.value.length)
    );
    try {
      if (
        textarea.selectionStart !== safeStart ||
        textarea.selectionEnd !== safeEnd
      ) {
        textarea.setSelectionRange(safeStart, safeEnd);
      }
    } catch {
      // Ignore selection sync failures; the wrapped session remains authoritative.
    }
  }, [activeWrappedParagraphSession]);

  const focusParagraphAtOffset = React.useCallback(
    (paragraphIndex: number, offset: number): void => {
      const focusAttempt = (attempt: number): void => {
        const element = resolveParagraphHostElementForOffset(
          paragraphIndex,
          offset
        );
        if (!element || !element.isConnected) {
          if (attempt < 6) {
            window.requestAnimationFrame(() => {
              focusAttempt(attempt + 1);
            });
          }
          return;
        }

        try {
          element.focus({ preventScroll: true });
        } catch {
          element.focus();
        }
        const safeOffset = Math.max(
          0,
          Math.min(Math.round(offset), element.textContent?.length ?? 0)
        );
        setSelectionWithinElementByTextOffsets(element, safeOffset, safeOffset);
        const selection = window.getSelection();
        const range =
          selection && selection.rangeCount > 0
            ? selection.getRangeAt(0)
            : undefined;
        const selectionInsideElement = Boolean(
          range &&
            element.contains(range.startContainer) &&
            element.contains(range.endContainer)
        );
        if (!selectionInsideElement && attempt < 6) {
          window.requestAnimationFrame(() => {
            focusAttempt(attempt + 1);
          });
        }
      };

      window.requestAnimationFrame(() => {
        focusAttempt(0);
      });
    },
    [
      resolveParagraphHostElementForOffset,
      setSelectionWithinElementByTextOffsets,
    ]
  );

  const focusTableCellParagraphAtOffset = React.useCallback(
    (cellDraftKey: string, paragraphIndex: number, offset: number): void => {
      window.requestAnimationFrame(() => {
        const cellElement =
          tableCellEditorElementsRef.current.get(cellDraftKey);
        if (!cellElement) {
          return;
        }

        const paragraphElement = cellElement.querySelector(
          `[data-docx-table-paragraph-index="${paragraphIndex}"]`
        ) as HTMLElement | null;
        if (!paragraphElement) {
          cellElement.focus();
          return;
        }

        cellElement.focus();
        const safeOffset = Math.max(
          0,
          Math.min(
            Math.round(offset),
            paragraphElement.textContent?.length ?? 0
          )
        );
        setSelectionWithinElementByTextOffsets(
          paragraphElement,
          safeOffset,
          safeOffset
        );
      });
    },
    [setSelectionWithinElementByTextOffsets]
  );

  const focusParagraphLocationAtOffset = React.useCallback(
    (location: ParagraphLocation, offset: number): void => {
      if (location.kind === "paragraph") {
        focusParagraphAtOffset(location.nodeIndex, offset);
        return;
      }

      const cellDraftKey = `${location.tableIndex}:${location.rowIndex}:${location.cellIndex}`;
      focusTableCellParagraphAtOffset(
        cellDraftKey,
        location.paragraphIndex,
        offset
      );
    },
    [focusParagraphAtOffset, focusTableCellParagraphAtOffset]
  );

  const focusParagraphLocationAtClientPoint = React.useCallback(
    (
      location: ParagraphLocation,
      preferredPoint: { x: number; y: number },
      fallbackOffset: number
    ): boolean => {
      const targetElement = resolveParagraphHostElement(location);
      if (!targetElement) {
        return false;
      }

      const rect = targetElement.getBoundingClientRect();
      if (
        !Number.isFinite(rect.left) ||
        !Number.isFinite(rect.right) ||
        !Number.isFinite(rect.top) ||
        !Number.isFinite(rect.bottom) ||
        rect.width <= 0 ||
        rect.height <= 0
      ) {
        return false;
      }

      const clampedPoint = {
        x: Math.max(rect.left + 1, Math.min(preferredPoint.x, rect.right - 1)),
        y: Math.max(rect.top + 1, Math.min(preferredPoint.y, rect.bottom - 1)),
      };
      const boundary = resolveBoundaryFromPoint(clampedPoint);
      if (
        boundary &&
        sameParagraphLocation(
          location,
          paragraphLocationFromTextRangeLocation(boundary.location)
        )
      ) {
        setSelectionFromDocxBoundaries(boundary, boundary);
        return true;
      }

      const targetParagraph = getParagraphAtLocation(
        editor.model,
        location
      ).paragraph;
      const maxOffset = targetParagraph
        ? paragraphText(targetParagraph).length
        : paragraphTextLengthFromElement(targetElement);
      const safeOffset = Math.max(
        0,
        Math.min(Math.round(fallbackOffset), maxOffset)
      );
      const fallbackBoundary: DocxTextRangeBoundary = {
        location: cloneTextRangeLocation(location),
        offset: safeOffset,
      };
      setSelectionFromDocxBoundaries(fallbackBoundary, fallbackBoundary);
      return true;
    },
    [
      editor.model,
      paragraphTextLengthFromElement,
      resolveBoundaryFromPoint,
      resolveParagraphHostElement,
      setSelectionFromDocxBoundaries,
    ]
  );

  const focusParagraphAtEnd = React.useCallback(
    (paragraphIndex: number): void => {
      window.requestAnimationFrame(() => {
        const element = paragraphElementsRef.current.get(paragraphIndex);
        if (!element) {
          return;
        }

        element.focus();
        const selection = window.getSelection();
        if (!selection) {
          return;
        }

        const range = document.createRange();
        range.selectNodeContents(element);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      });
    },
    []
  );

  const selectionOffsetsWithinElement = React.useCallback(
    (element: HTMLElement): { start: number; end: number } | undefined => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        return undefined;
      }

      const selectedRange = selection.getRangeAt(0);
      if (
        !element.contains(selectedRange.startContainer) ||
        !element.contains(selectedRange.endContainer)
      ) {
        return undefined;
      }

      try {
        const textLengthWithoutNumberingLabels = (range: Range): number => {
          const fragment = range.cloneContents();
          fragment
            .querySelectorAll("[data-docx-numbering-label='true']")
            .forEach((label) => {
              label.remove();
            });
          return fragment.textContent?.length ?? 0;
        };

        const startRange = document.createRange();
        startRange.setStart(element, 0);
        startRange.setEnd(
          selectedRange.startContainer,
          selectedRange.startOffset
        );

        const endRange = document.createRange();
        endRange.setStart(element, 0);
        endRange.setEnd(selectedRange.endContainer, selectedRange.endOffset);

        const startOffset = textLengthWithoutNumberingLabels(startRange);
        const endOffset = textLengthWithoutNumberingLabels(endRange);

        return {
          start: Math.min(startOffset, endOffset),
          end: Math.max(startOffset, endOffset),
        };
      } catch {
        return undefined;
      }
    },
    []
  );

  const collapsedCaretClientPointWithinElement = React.useCallback(
    (element: HTMLElement): { x: number; y: number } | undefined => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) {
        return undefined;
      }

      const range = selection.getRangeAt(0).cloneRange();
      range.collapse(true);
      const rectFromClientRects = range.getClientRects().item(0);
      const caretRect = rectFromClientRects ?? range.getBoundingClientRect();
      if (
        Number.isFinite(caretRect.left) &&
        Number.isFinite(caretRect.top) &&
        Number.isFinite(caretRect.width) &&
        Number.isFinite(caretRect.height) &&
        ((caretRect.width ?? 0) > 0 || (caretRect.height ?? 0) > 0)
      ) {
        return {
          x: caretRect.left + Math.max(1, Math.min(2, caretRect.width || 1)),
          y: caretRect.top + Math.max(1, (caretRect.height || 0) / 2),
        };
      }

      const elementRect = element.getBoundingClientRect();
      if (
        !Number.isFinite(elementRect.left) ||
        !Number.isFinite(elementRect.top) ||
        elementRect.width <= 0 ||
        elementRect.height <= 0
      ) {
        return undefined;
      }

      return {
        x: elementRect.left + 1,
        y:
          elementRect.top +
          Math.max(1, Math.min(elementRect.height - 1, elementRect.height / 2)),
      };
    },
    []
  );

  const activeTableCellParagraphContext = React.useCallback(
    (
      cellElement: HTMLElement
    ):
      | {
          paragraphElement: HTMLElement;
          paragraphIndex: number;
        }
      | undefined => {
      const selection = window.getSelection();
      if (!selection) {
        return undefined;
      }

      const candidateNodes: Array<Node | null> = [
        selection.anchorNode,
        selection.focusNode,
      ];
      for (const candidateNode of candidateNodes) {
        if (!candidateNode) {
          continue;
        }

        const element =
          candidateNode instanceof Element
            ? candidateNode
            : candidateNode.parentElement;
        const paragraphElement = element?.closest(
          "[data-docx-table-paragraph-index]"
        ) as HTMLElement | null;
        if (!paragraphElement || !cellElement.contains(paragraphElement)) {
          continue;
        }

        const rawIndex = paragraphElement.getAttribute(
          "data-docx-table-paragraph-index"
        );
        const parsedIndex = rawIndex
          ? Number.parseInt(rawIndex, 10)
          : Number.NaN;
        if (!Number.isFinite(parsedIndex) || parsedIndex < 0) {
          continue;
        }

        return {
          paragraphElement,
          paragraphIndex: Math.max(0, Math.round(parsedIndex)),
        };
      }

      return undefined;
    },
    []
  );

  const updateActiveTextRangeFromTableCell = React.useCallback(
    (
      cellElement: HTMLElement,
      tableIndex: number,
      rowIndex: number,
      cellIndex: number
    ): void => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        editor.setActiveTextRange(undefined);
        return;
      }

      const selectedRange = selection.getRangeAt(0);
      if (
        !cellElement.contains(selectedRange.startContainer) &&
        !cellElement.contains(selectedRange.endContainer)
      ) {
        editor.setActiveTextRange(undefined);
        return;
      }

      const startBoundary = resolveParagraphBoundaryFromSelectionPoint(
        selectedRange.startContainer,
        selectedRange.startOffset
      );
      const endBoundary = resolveParagraphBoundaryFromSelectionPoint(
        selectedRange.endContainer,
        selectedRange.endOffset
      );
      if (!startBoundary || !endBoundary) {
        return;
      }

      const isSameCell =
        startBoundary.location.kind === "table-cell" &&
        endBoundary.location.kind === "table-cell" &&
        startBoundary.location.tableIndex === tableIndex &&
        startBoundary.location.rowIndex === rowIndex &&
        startBoundary.location.cellIndex === cellIndex &&
        endBoundary.location.tableIndex === tableIndex &&
        endBoundary.location.rowIndex === rowIndex &&
        endBoundary.location.cellIndex === cellIndex;
      if (!isSameCell) {
        return;
      }

      clearTableCellSelection();
      editor.setActiveTextRange({
        start: startBoundary,
        end: endBoundary,
      });
    },
    [
      clearTableCellSelection,
      editor,
      resolveParagraphBoundaryFromSelectionPoint,
    ]
  );

  const updateActiveTextRangeFromElement = React.useCallback(
    (element: HTMLElement, location: ParagraphLocation): void => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        editor.setActiveTextRange(undefined);
        return;
      }

      const selectedRange = selection.getRangeAt(0);
      if (
        !element.contains(selectedRange.startContainer) ||
        !element.contains(selectedRange.endContainer)
      ) {
        if (
          !element.contains(selectedRange.startContainer) &&
          !element.contains(selectedRange.endContainer)
        ) {
          editor.setActiveTextRange(undefined);
        }
        return;
      }

      const offsets = selectionOffsetsWithinElement(element);
      if (!offsets) {
        return;
      }

      clearTableCellSelection();
      editor.setActiveTextRange({
        start: {
          location,
          offset: offsets.start,
        },
        end: {
          location,
          offset: offsets.end,
        },
      });
    },
    [clearTableCellSelection, editor, selectionOffsetsWithinElement]
  );

  const commitParagraphDraftFromElement = React.useCallback(
    (
      nodeIndex: number,
      paragraph: ParagraphNode,
      element: HTMLElement
    ): void => {
      const nextText = editableTextFromElement(element);
      const nextHtml = element.innerHTML;

      if (nextText === paragraphText(paragraph)) {
        paragraphDraftsRef.current.delete(nodeIndex);
        return;
      }

      // Keep the draft until model state catches up so immediate export after blur
      // cannot serialize a stale model snapshot.
      paragraphDraftsRef.current.set(nodeIndex, nextHtml);
      editor.suppressNextDomSelectionRestore();
      editor.commitParagraphText(nodeIndex, nextText);
    },
    [editor]
  );

  const commitTableCellDraftFromElement = React.useCallback(
    (
      tableIndex: number,
      rowIndex: number,
      cellIndex: number,
      cellParagraphs: ParagraphNode[],
      draftKey: string,
      element: HTMLElement
    ): void => {
      const nextText = editableTextFromTableCellElement(element);
      const nextHtml = element.innerHTML;

      if (nextText === tableCellText(cellParagraphs)) {
        tableCellDraftsRef.current.delete(draftKey);
        return;
      }

      // Keep the draft until model state catches up so immediate export after blur
      // cannot serialize a stale model snapshot.
      tableCellDraftsRef.current.set(draftKey, nextHtml);
      editor.suppressNextDomSelectionRestore();
      editor.commitTableCellText(tableIndex, rowIndex, cellIndex, nextText);
    },
    [editor]
  );

  const registerTableCellParagraphElement = React.useCallback(
    (draftKey: string, element: HTMLDivElement | null): void => {
      if (element) {
        tableCellParagraphElementsRef.current.set(draftKey, element);
      } else {
        tableCellParagraphElementsRef.current.delete(draftKey);
      }
    },
    []
  );

  const readTableCellParagraphDraft = React.useCallback(
    (draftKey: string): string | undefined => {
      return tableCellParagraphDraftsRef.current.get(draftKey);
    },
    []
  );

  const writeTableCellParagraphDraft = React.useCallback(
    (draftKey: string, html: string): void => {
      tableCellParagraphDraftsRef.current.set(draftKey, html);
      requestTableDraftLayoutRefresh();
    },
    [requestTableDraftLayoutRefresh]
  );

  const clearTableCellParagraphDraft = React.useCallback(
    (draftKey: string): void => {
      tableCellParagraphDraftsRef.current.delete(draftKey);
      requestTableDraftLayoutRefresh();
    },
    [requestTableDraftLayoutRefresh]
  );

  const commitTableCellParagraphDraft = React.useCallback(
    (
      tableIndex: number,
      rowIndex: number,
      cellIndex: number,
      paragraphIndex: number,
      text: string
    ): void => {
      const draftKey = tableCellParagraphDraftKey(
        tableIndex,
        rowIndex,
        cellIndex,
        paragraphIndex
      );
      const tableNode = editor.model.nodes[tableIndex];
      if (!tableNode || tableNode.type !== "table") {
        return;
      }

      const cell = tableNode.rows[rowIndex]?.cells[cellIndex];
      if (!cell) {
        return;
      }

      const currentParagraph = tableCellParagraphsRecursively(cell.nodes)[
        paragraphIndex
      ];
      if (currentParagraph && paragraphText(currentParagraph) === text) {
        tableCellParagraphDraftsRef.current.delete(draftKey);
        return;
      }

      editor.suppressNextDomSelectionRestore();
      editor.commitTableCellParagraphTextRecursive(
        tableIndex,
        rowIndex,
        cellIndex,
        paragraphIndex,
        text
      );
    },
    [editor]
  );

  const registerSectionParagraphElement = React.useCallback(
    (draftKey: string, element: HTMLDivElement | null): void => {
      if (element) {
        sectionParagraphElementsRef.current.set(draftKey, element);
      } else {
        sectionParagraphElementsRef.current.delete(draftKey);
      }
    },
    []
  );

  const readSectionParagraphDraft = React.useCallback(
    (draftKey: string): string | undefined => {
      return sectionParagraphDraftsRef.current.get(draftKey);
    },
    []
  );

  const writeSectionParagraphDraft = React.useCallback(
    (draftKey: string, html: string): void => {
      sectionParagraphDraftsRef.current.set(draftKey, html);
    },
    []
  );

  const clearSectionParagraphDraft = React.useCallback(
    (draftKey: string): void => {
      sectionParagraphDraftsRef.current.delete(draftKey);
    },
    []
  );

  const commitSectionParagraphDraft = React.useCallback(
    (location: DocxSectionParagraphLocation, text: string): void => {
      const draftKey = sectionParagraphLocationKey(location);
      const currentSectionLists =
        location.region === "header"
          ? [
              editor.model.metadata.headerSections ?? [],
              ...(editor.model.metadata.sections?.map(
                (section) => section.headerSections ?? []
              ) ?? []),
            ]
          : [
              editor.model.metadata.footerSections ?? [],
              ...(editor.model.metadata.sections?.map(
                (section) => section.footerSections ?? []
              ) ?? []),
            ];

      const currentText = currentSectionLists
        .flat()
        .filter((section) => section.partName === location.partName)
        .map((section) =>
          paragraphTextFromSectionLocation(section.nodes, {
            nodeIndex: location.nodeIndex,
            rowIndex: location.rowIndex,
            cellIndex: location.cellIndex,
            paragraphIndex: location.paragraphIndex,
          })
        )
        .find((value): value is string => typeof value === "string");

      if (currentText === text) {
        sectionParagraphDraftsRef.current.delete(draftKey);
        return;
      }

      editor.commitSectionParagraphText(location, text);
    },
    [editor]
  );

  const updateSectionParagraphDraftFromTarget = React.useCallback(
    (target: EventTarget | null): void => {
      if (!(target instanceof Element)) {
        return;
      }

      const paragraphHost = target.closest<HTMLElement>(
        "[data-docx-section-paragraph-key]"
      );
      if (!paragraphHost) {
        return;
      }

      const draftKey = paragraphHost.getAttribute(
        "data-docx-section-paragraph-key"
      );
      if (!draftKey) {
        return;
      }

      sectionParagraphDraftsRef.current.set(draftKey, paragraphHost.innerHTML);
    },
    []
  );

  const commitSectionRegionDraftsFromContainer = React.useCallback(
    (
      container: HTMLElement,
      region: DocxSectionRegion,
      partName: string
    ): void => {
      const paragraphHosts = Array.from(
        container.querySelectorAll<HTMLElement>(
          "[data-docx-section-paragraph-key]"
        )
      );
      paragraphHosts.forEach((paragraphHost) => {
        const draftKey = paragraphHost.getAttribute(
          "data-docx-section-paragraph-key"
        );
        if (!draftKey) {
          return;
        }

        const location = parseSectionParagraphLocationKey(draftKey);
        if (
          !location ||
          location.region !== region ||
          location.partName !== partName
        ) {
          return;
        }

        const nextText = editableTextFromElement(paragraphHost);
        sectionParagraphDraftsRef.current.set(
          draftKey,
          paragraphHost.innerHTML
        );
        commitSectionParagraphDraft(location, nextText);
      });
    },
    [commitSectionParagraphDraft]
  );

  function closeActiveHeaderFooterEdit(activeEdit: {
    region: DocxSectionRegion;
    partName: string;
    pageIndex: number;
  }): void {
    const container =
      activeEdit.region === "header"
        ? pageHeaderElementsRef.current.get(activeEdit.pageIndex)
        : pageFooterElementsRef.current.get(activeEdit.pageIndex);
    if (container) {
      commitSectionRegionDraftsFromContainer(
        container,
        activeEdit.region,
        activeEdit.partName
      );
    }

    setActiveHeaderFooterEdit((current) =>
      current &&
      current.region === activeEdit.region &&
      current.partName === activeEdit.partName &&
      current.pageIndex === activeEdit.pageIndex
        ? undefined
        : current
    );
    setSelectedSectionImageKey(undefined);
    setFloatingMovePreview(undefined);
    pendingSectionParagraphFocusRef.current = undefined;
  }

  function scheduleCloseActiveHeaderFooterEdit(activeEdit: {
    region: DocxSectionRegion;
    partName: string;
    pageIndex: number;
  }): void {
    if (
      typeof window === "undefined" ||
      typeof window.requestAnimationFrame !== "function"
    ) {
      closeActiveHeaderFooterEdit(activeEdit);
      return;
    }

    if (
      pendingHeaderFooterDeactivateFrameRef.current !== null &&
      typeof window.cancelAnimationFrame === "function"
    ) {
      window.cancelAnimationFrame(
        pendingHeaderFooterDeactivateFrameRef.current
      );
    }

    pendingHeaderFooterDeactivateFrameRef.current =
      window.requestAnimationFrame(() => {
        pendingHeaderFooterDeactivateFrameRef.current = null;
        closeActiveHeaderFooterEdit(activeEdit);
      });
  }

  React.useEffect(() => {
    const flushPendingDraftsForExport = (currentModel: DocModel): DocModel => {
      if (
        paragraphDraftsRef.current.size === 0 &&
        tableCellDraftsRef.current.size === 0 &&
        tableCellParagraphDraftsRef.current.size === 0 &&
        sectionParagraphDraftsRef.current.size === 0
      ) {
        return currentModel;
      }

      let nextModel = currentModel;
      const insertedStyle = cloneTextStyle(editor.pendingRunStyle);
      const consumedParagraphDraftIndexes: number[] = [];
      const consumedTableDraftKeys: string[] = [];
      const consumedTableParagraphDraftKeys: string[] = [];
      const consumedSectionDraftKeys: string[] = [];

      paragraphDraftsRef.current.forEach((draftHtml, nodeIndex) => {
        const element = paragraphElementsRef.current.get(nodeIndex);
        if (!element && typeof draftHtml !== "string") {
          return;
        }

        consumedParagraphDraftIndexes.push(nodeIndex);
        const paragraph = nextModel.nodes[nodeIndex];
        if (!paragraph || paragraph.type !== "paragraph") {
          return;
        }

        const nextText = element
          ? editableTextFromElement(element)
          : editableTextFromDraftHtml(draftHtml);
        if (nextText === paragraphText(paragraph)) {
          return;
        }

        nextModel = updateParagraphText(nextModel, nodeIndex, nextText, {
          insertedStyle,
        });
      });

      tableCellParagraphDraftsRef.current.forEach((draftHtml, draftKey) => {
        const element = tableCellParagraphElementsRef.current.get(draftKey);
        if (!element && typeof draftHtml !== "string") {
          return;
        }

        consumedTableParagraphDraftKeys.push(draftKey);
        const [tableIndexRaw, rowIndexRaw, cellIndexRaw, paragraphIndexRaw] =
          draftKey.split(":");
        const tableIndex = Number.parseInt(tableIndexRaw ?? "", 10);
        const rowIndex = Number.parseInt(rowIndexRaw ?? "", 10);
        const cellIndex = Number.parseInt(cellIndexRaw ?? "", 10);
        const paragraphIndex = Number.parseInt(paragraphIndexRaw ?? "", 10);
        if (
          !Number.isFinite(tableIndex) ||
          !Number.isFinite(rowIndex) ||
          !Number.isFinite(cellIndex) ||
          !Number.isFinite(paragraphIndex)
        ) {
          return;
        }

        const table = nextModel.nodes[tableIndex];
        if (!table || table.type !== "table") {
          return;
        }

        const cell = table.rows[rowIndex]?.cells[cellIndex];
        if (!cell) {
          return;
        }

        const paragraph = tableCellParagraphsRecursively(cell.nodes)[
          paragraphIndex
        ];
        if (!paragraph) {
          return;
        }

        const nextText = element
          ? editableTextFromElement(element)
          : editableTextFromDraftHtml(draftHtml);
        if (nextText === paragraphText(paragraph)) {
          return;
        }

        nextModel = updateTableCellParagraphTextRecursive(
          nextModel,
          tableIndex,
          rowIndex,
          cellIndex,
          paragraphIndex,
          nextText,
          {
            insertedStyle,
          }
        );
      });

      // Apply whole-cell drafts after recursive paragraph drafts to keep recursive
      // paragraph indexes stable during this flush pass.
      tableCellDraftsRef.current.forEach((draftHtml, draftKey) => {
        const element = tableCellEditorElementsRef.current.get(draftKey);
        if (!element && typeof draftHtml !== "string") {
          return;
        }

        consumedTableDraftKeys.push(draftKey);
        const [tableIndexRaw, rowIndexRaw, cellIndexRaw] = draftKey.split(":");
        const tableIndex = Number.parseInt(tableIndexRaw ?? "", 10);
        const rowIndex = Number.parseInt(rowIndexRaw ?? "", 10);
        const cellIndex = Number.parseInt(cellIndexRaw ?? "", 10);
        if (
          !Number.isFinite(tableIndex) ||
          !Number.isFinite(rowIndex) ||
          !Number.isFinite(cellIndex)
        ) {
          return;
        }

        const table = nextModel.nodes[tableIndex];
        if (!table || table.type !== "table") {
          return;
        }

        const cell = table.rows[rowIndex]?.cells[cellIndex];
        if (!cell) {
          return;
        }

        const nextText = element
          ? editableTextFromTableCellElement(element)
          : editableTextFromTableCellDraftHtml(draftHtml);
        const currentText = tableCellText(tableCellParagraphs(cell.nodes));
        if (nextText === currentText) {
          return;
        }

        nextModel = updateTableCellText(
          nextModel,
          tableIndex,
          rowIndex,
          cellIndex,
          nextText,
          {
            insertedStyle,
          }
        );
      });

      sectionParagraphDraftsRef.current.forEach((draftHtml, draftKey) => {
        const element = sectionParagraphElementsRef.current.get(draftKey);
        const location = parseSectionParagraphLocationKey(draftKey);
        if (!location || (!element && typeof draftHtml !== "string")) {
          return;
        }

        consumedSectionDraftKeys.push(draftKey);
        const nextText = element
          ? editableTextFromElement(element)
          : editableTextFromDraftHtml(draftHtml);
        nextModel = updateSectionParagraphTextAtLocation(
          nextModel,
          location,
          nextText,
          {
            insertedStyle,
          }
        );
      });

      consumedParagraphDraftIndexes.forEach((nodeIndex) => {
        paragraphDraftsRef.current.delete(nodeIndex);
      });
      consumedTableDraftKeys.forEach((draftKey) => {
        tableCellDraftsRef.current.delete(draftKey);
      });
      consumedTableParagraphDraftKeys.forEach((draftKey) => {
        tableCellParagraphDraftsRef.current.delete(draftKey);
      });
      consumedSectionDraftKeys.forEach((draftKey) => {
        sectionParagraphDraftsRef.current.delete(draftKey);
      });

      return nextModel;
    };

    editor.registerPendingExportModelTransformer(flushPendingDraftsForExport);
    return () => {
      editor.registerPendingExportModelTransformer(undefined);
    };
  }, [editor, editor.pendingRunStyle]);

  const handleEditorHistoryShortcut = React.useCallback(
    (
      event: React.KeyboardEvent<HTMLElement>,
      commitDraft: () => void
    ): boolean => {
      if (
        event.defaultPrevented ||
        !(event.metaKey || event.ctrlKey) ||
        event.altKey
      ) {
        return false;
      }

      const key = event.key.toLowerCase();
      const isUndo = key === "z" && !event.shiftKey;
      const isRedo = key === "y" || (key === "z" && event.shiftKey);
      if (!isUndo && !isRedo) {
        return false;
      }

      event.preventDefault();
      event.stopPropagation();

      flushSync(() => {
        commitDraft();
      });

      if (isRedo) {
        editor.redo();
      } else {
        editor.undo();
      }

      return true;
    },
    [editor]
  );

  const resolveTableColumnWidths = React.useCallback(
    (tableIndex: number, table: TableNode, tableWidthPx?: number): number[] => {
      const columnCount = tableColumnCount(table);
      const storedWidths = tableColumnWidths[tableIndex];
      if (storedWidths && storedWidths.length === columnCount) {
        return normalizeColumnWidthsPx(
          storedWidths,
          columnCount,
          tableWidthPx,
          1
        );
      }

      const definedWidthsTwips = columnWidthsFromTableDefinition(
        table,
        columnCount
      );
      if (definedWidthsTwips && definedWidthsTwips.length > 0) {
        const definedWidthsPx = definedWidthsTwips.map(
          (widthTwips) => twipsToPixels(widthTwips) ?? 0
        );
        return normalizeColumnWidthsPx(
          definedWidthsPx,
          columnCount,
          tableWidthPx,
          1
        );
      }

      return defaultColumnWidthsPx(columnCount, tableWidthPx);
    },
    [tableColumnWidths]
  );

  const resolveTableRowHeights = React.useCallback(
    (tableIndex: number, table: TableNode): Array<number | undefined> => {
      const storedHeights = tableRowHeights[tableIndex];
      return table.rows.map((row, rowIndex) => {
        const stored = storedHeights?.[rowIndex];
        if (Number.isFinite(stored) && (stored as number) > 0) {
          return Math.max(
            MIN_PARAGRAPH_LINE_HEIGHT_PX,
            Math.round(stored as number)
          );
        }

        const fromDoc = twipsToPixels(row.style?.heightTwips);
        if (Number.isFinite(fromDoc) && (fromDoc as number) > 0) {
          return Math.max(MIN_PARAGRAPH_LINE_HEIGHT_PX, fromDoc as number);
        }

        return undefined;
      });
    },
    [tableRowHeights]
  );

  const resolveEmbeddedTableColumnWidths = React.useCallback(
    (tableKey: string, table: TableNode, tableWidthPx?: number): number[] => {
      const columnCount = tableColumnCount(table);
      const storedWidths = embeddedTableColumnWidths[tableKey];
      if (storedWidths && storedWidths.length === columnCount) {
        return normalizeColumnWidthsPx(
          storedWidths,
          columnCount,
          tableWidthPx,
          1
        );
      }

      const definedWidthsTwips = columnWidthsFromTableDefinition(
        table,
        columnCount
      );
      if (definedWidthsTwips && definedWidthsTwips.length > 0) {
        const definedWidthsPx = definedWidthsTwips.map(
          (widthTwips) => twipsToPixels(widthTwips) ?? 0
        );
        return normalizeColumnWidthsPx(
          definedWidthsPx,
          columnCount,
          tableWidthPx,
          1
        );
      }

      return defaultColumnWidthsPx(columnCount, tableWidthPx);
    },
    [embeddedTableColumnWidths]
  );

  const resolveEmbeddedTableRowHeights = React.useCallback(
    (tableKey: string, table: TableNode): Array<number | undefined> => {
      const storedHeights = embeddedTableRowHeights[tableKey];
      return table.rows.map((row, rowIndex) => {
        const stored = storedHeights?.[rowIndex];
        if (Number.isFinite(stored) && (stored as number) > 0) {
          return Math.max(
            MIN_PARAGRAPH_LINE_HEIGHT_PX,
            Math.round(stored as number)
          );
        }

        const fromDoc = twipsToPixels(row.style?.heightTwips);
        if (Number.isFinite(fromDoc) && (fromDoc as number) > 0) {
          return Math.max(MIN_PARAGRAPH_LINE_HEIGHT_PX, fromDoc as number);
        }

        return undefined;
      });
    },
    [embeddedTableRowHeights]
  );

  React.useLayoutEffect(() => {
    if (disableMeasuredImportPagination) {
      setTableMeasuredRowHeights((current) =>
        Object.keys(current).length === 0 ? current : {}
      );
      return;
    }

    if (!paginationMeasurementEnabled) {
      return;
    }

    if (Date.now() < paginationMeasurementSuspendUntilRef.current) {
      return;
    }

    const rootElement = viewerRootRef.current;
    const zoomScale = resolveViewerMeasurementZoomScale(
      rootElement,
      virtualizerMeasurementScale
    );
    const nextMeasuredHeights: Record<number, number[]> = {};
    editor.model.nodes.forEach((node, nodeIndex) => {
      if (node.type !== "table") {
        return;
      }

      const visibleRowIndexes =
        visibleTableRowIndexesByNodeIndex.get(nodeIndex);
      if (!visibleRowIndexes || visibleRowIndexes.size === 0) {
        return;
      }

      const measuredByRowIndex = new Map<number, number>();
      const recordMeasuredRow = (rowIndex: number, rowHeightPx: number) => {
        const normalizedRowIndex = Math.max(0, Math.round(rowIndex));
        if (
          normalizedRowIndex >= node.rows.length ||
          !visibleRowIndexes.has(normalizedRowIndex)
        ) {
          return;
        }
        const documentRowHeightPx =
          Number.isFinite(zoomScale) && zoomScale > 0
            ? rowHeightPx / zoomScale
            : rowHeightPx;
        const normalizedHeight =
          normalizeMeasuredTableRowHeightPx(documentRowHeightPx);
        const previousHeight = measuredByRowIndex.get(normalizedRowIndex);
        measuredByRowIndex.set(
          normalizedRowIndex,
          previousHeight === undefined
            ? normalizedHeight
            : Math.max(previousHeight, normalizedHeight)
        );
      };

      const tableContainer = tableElementsRef.current.get(nodeIndex);
      if (tableContainer) {
        Array.from(
          tableContainer.querySelectorAll<HTMLElement>(
            `tr[data-docx-table-index='${nodeIndex}'][data-docx-row-index]`
          )
        ).forEach((rowElement) => {
          if (rowElement.getAttribute("data-docx-row-sliced") === "true") {
            return;
          }

          const rowIndex = Number.parseInt(
            rowElement.getAttribute("data-docx-row-index") ?? "",
            10
          );
          if (!Number.isFinite(rowIndex)) {
            return;
          }

          recordMeasuredRow(
            rowIndex,
            (rowElement as HTMLTableRowElement).getBoundingClientRect().height
          );
        });
      }

      const measurementRoot = viewerRootRef.current ?? document;
      const rowCells = measurementRoot.querySelectorAll<HTMLElement>(
        `[data-docx-table-cell='true'][data-docx-table-index='${nodeIndex}'][data-docx-row-index]`
      );
      rowCells.forEach((cellElement) => {
        if (cellElement.getAttribute("data-docx-row-sliced") === "true") {
          return;
        }

        const containingRow = cellElement.closest("tr");
        if (containingRow?.getAttribute("data-docx-row-sliced") === "true") {
          return;
        }

        const rowIndex = Number.parseInt(
          cellElement.getAttribute("data-docx-row-index") ?? "",
          10
        );
        if (!Number.isFinite(rowIndex)) {
          return;
        }

        const rowElement = cellElement.closest("tr");
        if (!rowElement) {
          return;
        }

        recordMeasuredRow(rowIndex, rowElement.getBoundingClientRect().height);
      });

      if (measuredByRowIndex.size === 0) {
        return;
      }

      const estimatedRowHeights = estimateTableRowHeightsPx(
        node,
        pageContentWidthPxByNodeIndex.get(nodeIndex),
        editor.model.metadata.numberingDefinitions,
        docGridLinePitchPxByNodeIndex.get(nodeIndex),
        pageContentHeightPxByNodeIndex.get(nodeIndex)
      );
      const previousMeasuredRowHeights = tableMeasuredRowHeights[nodeIndex];
      const measuredHeights = node.rows.map((_, rowIndex) => {
        const measuredHeight = measuredByRowIndex.get(rowIndex);
        if (measuredHeight !== undefined) {
          return measuredHeight;
        }

        const previousHeight = previousMeasuredRowHeights?.[rowIndex];
        if (Number.isFinite(previousHeight) && (previousHeight as number) > 0) {
          return normalizeMeasuredTableRowHeightPx(previousHeight as number);
        }

        return normalizeMeasuredTableRowHeightPx(
          estimatedRowHeights[rowIndex] ?? MIN_PARAGRAPH_LINE_HEIGHT_PX
        );
      });
      nextMeasuredHeights[nodeIndex] = measuredHeights;
    });

    setTableMeasuredRowHeights((current) => {
      let changed = false;
      const next: Record<number, number[]> = {};

      Object.entries(current).forEach(([tableIndexText, heights]) => {
        const tableIndex = Number.parseInt(tableIndexText, 10);
        const tableNode = editor.model.nodes[tableIndex];
        if (!tableNode || tableNode.type !== "table") {
          changed = true;
          return;
        }

        if (
          !Array.isArray(heights) ||
          heights.length !== tableNode.rows.length
        ) {
          changed = true;
          return;
        }

        next[tableIndex] = heights;
      });

      Object.entries(nextMeasuredHeights).forEach(
        ([tableIndexText, measuredHeights]) => {
          const tableIndex = Number.parseInt(tableIndexText, 10);
          const currentHeights = next[tableIndex] ?? [];
          if (currentHeights.length !== measuredHeights.length) {
            next[tableIndex] = measuredHeights;
            changed = true;
            return;
          }

          for (let index = 0; index < measuredHeights.length; index += 1) {
            if (currentHeights[index] !== measuredHeights[index]) {
              next[tableIndex] = measuredHeights;
              changed = true;
              return;
            }
          }
        }
      );

      return changed ? next : current;
    });
  }, [
    disableMeasuredImportPagination,
    docGridLinePitchPxByNodeIndex,
    editor.model.metadata.numberingDefinitions,
    editor.model.nodes,
    pageContentWidthPxByNodeIndex,
    paginationMeasurementEnabled,
    paginationMeasurementEpoch,
    resolveViewerMeasurementZoomScale,
    tableMeasuredRowHeights,
    tableColumnWidths,
    tableRowHeights,
    tableDraftLayoutEpoch,
    virtualizerMeasurementScale,
    visibleTableRowIndexesByNodeIndex,
  ]);

  const normalizeResizableHeights = React.useCallback(
    (rowHeights: Array<number | undefined>): number[] => {
      return rowHeights.map((height) => {
        if (
          typeof height !== "number" ||
          !Number.isFinite(height) ||
          height <= 0
        ) {
          return 24;
        }

        return Math.max(24, Math.round(height));
      });
    },
    []
  );

  const distributeHeightByTotal = React.useCallback(
    (rowHeights: number[], totalHeightPx: number): number[] => {
      const safeCount = rowHeights.length;
      if (safeCount === 0) {
        return [];
      }

      const minimumHeightPx = 24;
      const minimumTotalHeight = minimumHeightPx * safeCount;
      const targetHeight = Math.max(
        minimumTotalHeight,
        Math.round(totalHeightPx)
      );
      const sourceTotalHeight = rowHeights.reduce(
        (sum, value) => sum + Math.max(minimumHeightPx, value),
        0
      );
      if (!Number.isFinite(sourceTotalHeight) || sourceTotalHeight <= 0) {
        const fallback = Math.floor(targetHeight / safeCount);
        return Array.from({ length: safeCount }, () => fallback);
      }

      const scaledHeights = rowHeights.map((height) =>
        Math.max(
          minimumHeightPx,
          Math.round((height / sourceTotalHeight) * targetHeight)
        )
      );

      let remaining =
        targetHeight - scaledHeights.reduce((sum, value) => sum + value, 0);
      if (remaining !== 0) {
        let safetyGuard = 0;
        while (remaining !== 0 && safetyGuard < safeCount * 4) {
          for (
            let rowIndex = 0;
            rowIndex < scaledHeights.length && remaining !== 0;
            rowIndex += 1
          ) {
            if (remaining > 0) {
              scaledHeights[rowIndex] += 1;
              remaining -= 1;
              continue;
            }

            const candidate = scaledHeights[rowIndex];
            if (candidate > minimumHeightPx) {
              scaledHeights[rowIndex] -= 1;
              remaining += 1;
            }
          }

          safetyGuard += 1;
        }
      }

      return scaledHeights;
    },
    []
  );

  const beginTableColumnResize = React.useCallback(
    (
      event: React.PointerEvent<HTMLSpanElement>,
      tableIndex: number,
      boundaryColumnIndex: number,
      tableWidthPx: number,
      columnWidths: number[]
    ): void => {
      if (isReadOnly) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (!Number.isFinite(tableWidthPx) || tableWidthPx <= 0) {
        return;
      }

      const resizeHandle = event.currentTarget;
      const pointerId = event.pointerId;

      if (resizeHandle.setPointerCapture) {
        try {
          resizeHandle.setPointerCapture(pointerId);
        } catch {
          // ignore pointer capture failures from detached handles
        }
      }

      const safeBoundary = Math.max(
        0,
        Math.min(boundaryColumnIndex, columnWidths.length - 2)
      );
      const startingWidths = normalizeColumnWidthsPx(
        columnWidths,
        columnWidths.length,
        tableWidthPx,
        1
      );
      const leftStart = startingWidths[safeBoundary] ?? 0;
      const rightStart = startingWidths[safeBoundary + 1] ?? 0;
      const combined = leftStart + rightStart;
      if (combined <= 0) {
        return;
      }

      const minimumWidthPx = 8;
      const startX = event.clientX;
      let latestWidths = startingWidths;
      let frameId: number | undefined;

      setActiveColumnResize({ tableIndex, boundaryColumnIndex: safeBoundary });

      const commitWidths = (): void => {
        setTableColumnWidths((current) => ({
          ...current,
          [tableIndex]: latestWidths,
        }));
      };

      const onPointerMove = (pointerEvent: PointerEvent): void => {
        const deltaPx = pointerEvent.clientX - startX;
        const nextLeft = Math.max(
          minimumWidthPx,
          Math.min(leftStart + deltaPx, combined - minimumWidthPx)
        );
        const nextRight = combined - nextLeft;

        latestWidths = startingWidths.map((value: number, index: number) => {
          if (index === safeBoundary) {
            return nextLeft;
          }
          if (index === safeBoundary + 1) {
            return nextRight;
          }
          return value;
        });

        if (frameId !== undefined) {
          window.cancelAnimationFrame(frameId);
        }

        frameId = window.requestAnimationFrame(commitWidths);
      };

      const onPointerUp = (pointerUpEvent: PointerEvent): void => {
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        window.removeEventListener("pointercancel", onPointerUp);

        if (frameId !== undefined) {
          window.cancelAnimationFrame(frameId);
        }

        commitWidths();
        if (resizeHandle.releasePointerCapture) {
          try {
            resizeHandle.releasePointerCapture(
              pointerUpEvent.pointerId ?? pointerId
            );
          } catch {
            // ignore pointer capture failures from detached handles
          }
        }
        setActiveColumnResize(undefined);
      };

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp, { once: true });
      window.addEventListener("pointercancel", onPointerUp, { once: true });
    },
    [isReadOnly]
  );

  const beginTableRowResize = React.useCallback(
    (
      event: React.PointerEvent<HTMLSpanElement>,
      tableIndex: number,
      boundaryRowIndex: number,
      rowHeights: number[]
    ): void => {
      if (isReadOnly) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (rowHeights.length < 2) {
        return;
      }

      const resizeHandle = event.currentTarget;
      const pointerId = event.pointerId;

      if (resizeHandle.setPointerCapture) {
        try {
          resizeHandle.setPointerCapture(pointerId);
        } catch {
          // ignore pointer capture failures from detached handles
        }
      }

      const safeBoundary = Math.max(
        0,
        Math.min(boundaryRowIndex, rowHeights.length - 2)
      );
      const startingHeights = rowHeights.map((height) =>
        Number.isFinite(height) && height > 0
          ? Math.max(24, Math.round(height))
          : 24
      );
      const topStart = startingHeights[safeBoundary] ?? 0;
      if (topStart <= 0) {
        return;
      }

      const minimumHeightPx = 24;
      const startY = event.clientY;
      let latestHeights = startingHeights;
      let frameId: number | undefined;

      setActiveRowResize({ tableIndex, boundaryRowIndex: safeBoundary });

      const commitHeights = (): void => {
        setTableRowHeights((current) => ({
          ...current,
          [tableIndex]: latestHeights,
        }));
      };

      const onPointerMove = (pointerEvent: PointerEvent): void => {
        const deltaPx = pointerEvent.clientY - startY;
        const nextTop = Math.max(
          minimumHeightPx,
          Math.round(topStart + deltaPx)
        );

        latestHeights = startingHeights.map((value, index) => {
          if (index === safeBoundary) {
            return nextTop;
          }
          return value;
        });

        if (frameId !== undefined) {
          window.cancelAnimationFrame(frameId);
        }

        frameId = window.requestAnimationFrame(commitHeights);
      };

      const onPointerUp = (pointerUpEvent: PointerEvent): void => {
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        window.removeEventListener("pointercancel", onPointerUp);

        if (frameId !== undefined) {
          window.cancelAnimationFrame(frameId);
        }

        commitHeights();
        if (resizeHandle.releasePointerCapture) {
          try {
            resizeHandle.releasePointerCapture(
              pointerUpEvent.pointerId ?? pointerId
            );
          } catch {
            // ignore pointer capture failures from detached handles
          }
        }
        setActiveRowResize(undefined);
      };

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp, { once: true });
      window.addEventListener("pointercancel", onPointerUp, { once: true });
    },
    [isReadOnly]
  );

  const beginEmbeddedTableColumnResize = React.useCallback(
    (
      event: React.PointerEvent<HTMLSpanElement>,
      tableKey: string,
      boundaryColumnIndex: number,
      tableWidthPx: number,
      columnWidths: number[]
    ): void => {
      if (isReadOnly) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (!Number.isFinite(tableWidthPx) || tableWidthPx <= 0) {
        return;
      }

      const resizeHandle = event.currentTarget;
      const pointerId = event.pointerId;

      if (resizeHandle.setPointerCapture) {
        try {
          resizeHandle.setPointerCapture(pointerId);
        } catch {
          // ignore pointer capture failures from detached handles
        }
      }

      const safeBoundary = Math.max(
        0,
        Math.min(boundaryColumnIndex, columnWidths.length - 2)
      );
      const startingWidths = normalizeColumnWidthsPx(
        columnWidths,
        columnWidths.length,
        tableWidthPx,
        1
      );
      const leftStart = startingWidths[safeBoundary] ?? 0;
      const rightStart = startingWidths[safeBoundary + 1] ?? 0;
      const combined = leftStart + rightStart;
      if (combined <= 0) {
        return;
      }

      const minimumWidthPx = 8;
      const startX = event.clientX;
      let latestWidths = startingWidths;
      let frameId: number | undefined;

      setActiveEmbeddedColumnResize({
        tableKey,
        boundaryColumnIndex: safeBoundary,
      });

      const commitWidths = (): void => {
        setEmbeddedTableColumnWidths((current) => ({
          ...current,
          [tableKey]: latestWidths,
        }));
      };

      const onPointerMove = (pointerEvent: PointerEvent): void => {
        const deltaPx = pointerEvent.clientX - startX;
        const nextLeft = Math.max(
          minimumWidthPx,
          Math.min(leftStart + deltaPx, combined - minimumWidthPx)
        );
        const nextRight = combined - nextLeft;

        latestWidths = startingWidths.map((value: number, index: number) => {
          if (index === safeBoundary) {
            return nextLeft;
          }
          if (index === safeBoundary + 1) {
            return nextRight;
          }
          return value;
        });

        if (frameId !== undefined) {
          window.cancelAnimationFrame(frameId);
        }

        frameId = window.requestAnimationFrame(commitWidths);
      };

      const onPointerUp = (pointerUpEvent: PointerEvent): void => {
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        window.removeEventListener("pointercancel", onPointerUp);

        if (frameId !== undefined) {
          window.cancelAnimationFrame(frameId);
        }

        commitWidths();
        if (resizeHandle.releasePointerCapture) {
          try {
            resizeHandle.releasePointerCapture(
              pointerUpEvent.pointerId ?? pointerId
            );
          } catch {
            // ignore pointer capture failures from detached handles
          }
        }
        setActiveEmbeddedColumnResize(undefined);
      };

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp, { once: true });
      window.addEventListener("pointercancel", onPointerUp, { once: true });
    },
    [isReadOnly]
  );

  const beginEmbeddedTableRowResize = React.useCallback(
    (
      event: React.PointerEvent<HTMLSpanElement>,
      tableKey: string,
      boundaryRowIndex: number,
      rowHeights: number[]
    ): void => {
      if (isReadOnly) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (rowHeights.length < 2) {
        return;
      }

      const resizeHandle = event.currentTarget;
      const pointerId = event.pointerId;

      if (resizeHandle.setPointerCapture) {
        try {
          resizeHandle.setPointerCapture(pointerId);
        } catch {
          // ignore pointer capture failures from detached handles
        }
      }

      const safeBoundary = Math.max(
        0,
        Math.min(boundaryRowIndex, rowHeights.length - 2)
      );
      const startingHeights = rowHeights.map((height) =>
        Number.isFinite(height) && height > 0
          ? Math.max(24, Math.round(height))
          : 24
      );
      const topStart = startingHeights[safeBoundary] ?? 0;
      if (topStart <= 0) {
        return;
      }

      const minimumHeightPx = 24;
      const startY = event.clientY;
      let latestHeights = startingHeights;
      let frameId: number | undefined;

      setActiveEmbeddedRowResize({ tableKey, boundaryRowIndex: safeBoundary });

      const commitHeights = (): void => {
        setEmbeddedTableRowHeights((current) => ({
          ...current,
          [tableKey]: latestHeights,
        }));
      };

      const onPointerMove = (pointerEvent: PointerEvent): void => {
        const deltaPx = pointerEvent.clientY - startY;
        const nextTop = Math.max(
          minimumHeightPx,
          Math.round(topStart + deltaPx)
        );

        latestHeights = startingHeights.map((value, index) => {
          if (index === safeBoundary) {
            return nextTop;
          }
          return value;
        });

        if (frameId !== undefined) {
          window.cancelAnimationFrame(frameId);
        }

        frameId = window.requestAnimationFrame(commitHeights);
      };

      const onPointerUp = (pointerUpEvent: PointerEvent): void => {
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        window.removeEventListener("pointercancel", onPointerUp);

        if (frameId !== undefined) {
          window.cancelAnimationFrame(frameId);
        }

        commitHeights();
        if (resizeHandle.releasePointerCapture) {
          try {
            resizeHandle.releasePointerCapture(
              pointerUpEvent.pointerId ?? pointerId
            );
          } catch {
            // ignore pointer capture failures from detached handles
          }
        }
        setActiveEmbeddedRowResize(undefined);
      };

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp, { once: true });
      window.addEventListener("pointercancel", onPointerUp, { once: true });
    },
    [isReadOnly]
  );

  const beginImageResize = React.useCallback(
    (
      event: React.PointerEvent<HTMLSpanElement>,
      location: DocxImageLocation,
      startWidthPx: number,
      startHeightPx: number,
      xDirection: -1 | 1,
      yDirection: -1 | 1
    ): void => {
      if (isReadOnly) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      setSelectedImage(location);

      const minimumSize = 24;
      const safeStartWidth = Math.max(minimumSize, Math.round(startWidthPx));
      const safeStartHeight = Math.max(minimumSize, Math.round(startHeightPx));
      const aspectRatio = safeStartWidth / Math.max(1, safeStartHeight);
      const startX = event.clientX;
      const startY = event.clientY;
      const currentImageKey = imageLocationKey(location);

      let latestWidth = safeStartWidth;
      let latestHeight = safeStartHeight;
      let frameId: number | undefined;

      const onPointerMove = (pointerMoveEvent: PointerEvent): void => {
        const deltaX = (pointerMoveEvent.clientX - startX) * xDirection;
        const deltaY = (pointerMoveEvent.clientY - startY) * yDirection;
        const dominantDelta =
          Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY;

        latestWidth = Math.max(
          minimumSize,
          Math.round(safeStartWidth + dominantDelta)
        );
        latestHeight = Math.max(
          minimumSize,
          Math.round(latestWidth / aspectRatio)
        );

        if (frameId !== undefined) {
          window.cancelAnimationFrame(frameId);
        }

        frameId = window.requestAnimationFrame(() => {
          setResizePreview({
            imageKey: currentImageKey,
            widthPx: latestWidth,
            heightPx: latestHeight,
          });
        });
      };

      const onPointerUp = (): void => {
        window.removeEventListener("pointermove", onPointerMove);

        if (frameId !== undefined) {
          window.cancelAnimationFrame(frameId);
        }

        setResizePreview(undefined);
        editor.resizeImage(location, latestWidth, latestHeight);
      };

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp, { once: true });
    },
    [editor, isReadOnly]
  );

  const beginFloatingImageMove = React.useCallback(
    (
      event: React.PointerEvent<HTMLElement>,
      location: DocxImageLocation,
      image: ImageRunNode,
      isWrappedFloatingImage: boolean,
      isAbsoluteFloatingImage: boolean
    ): void => {
      if (isReadOnly) {
        return;
      }

      if (event.button !== 0) {
        return;
      }
      if (
        (event.target as HTMLElement).closest(
          "[data-image-resize-handle='true']"
        )
      ) {
        return;
      }

      const isInlineImage = !isWrappedFloatingImage && !isAbsoluteFloatingImage;
      event.preventDefault();
      event.stopPropagation();
      setSelectedImage(location);

      const imageKey = imageLocationKey(location);
      const startX = event.clientX;
      const startY = event.clientY;
      const inlineDragActivationThresholdPx = 4;
      const wrapperElement = event.currentTarget;
      const wrapperRect = wrapperElement.getBoundingClientRect();
      const pageSurface = wrapperElement.closest(
        "[data-docx-page-surface='true']"
      ) as HTMLElement | null;
      const pageSurfaceRect = pageSurface?.getBoundingClientRect();
      const paragraphHost = wrapperElement.closest(
        "[data-docx-paragraph-host='true']"
      ) as HTMLElement | null;
      const paragraphRect = paragraphHost?.getBoundingClientRect();
      const baseFloating = image.floating ? { ...image.floating } : {};

      let latestDeltaX = 0;
      let latestDeltaY = 0;
      let frameId: number | undefined;
      let inlineDragActivated = false;
      let latestInlineDropTarget: DocxImageDropTarget | undefined;
      let latestInlineDropTargetKey: string | undefined;

      const updatePreview = (): void => {
        setFloatingMovePreview({
          imageKey,
          deltaX: latestDeltaX,
          deltaY: latestDeltaY,
          ...(paragraphRect
            ? {
                baseLeftPx: Math.round(wrapperRect.left - paragraphRect.left),
                baseTopPx: Math.round(wrapperRect.top - paragraphRect.top),
              }
            : undefined),
        });
      };

      const onPointerMove = (pointerEvent: PointerEvent): void => {
        latestDeltaX = pointerEvent.clientX - startX;
        latestDeltaY = pointerEvent.clientY - startY;

        if (isInlineImage) {
          if (!inlineDragActivated) {
            const movementDistancePx =
              Math.abs(latestDeltaX) + Math.abs(latestDeltaY);
            if (movementDistancePx < inlineDragActivationThresholdPx) {
              return;
            }

            inlineDragActivated = true;
            draggedImageRef.current = location;
            setIsDraggingImage(true);
            setActiveDropTarget(undefined);
          }

          const candidate = document
            .elementFromPoint(pointerEvent.clientX, pointerEvent.clientY)
            ?.closest(
              "[data-docx-image-drop-zone='true']"
            ) as HTMLElement | null;
          const parsedTarget = candidate
            ? parseImageDropTargetFromDataset(candidate.dataset)
            : undefined;
          const parsedTargetKey = parsedTarget
            ? dropTargetKey(parsedTarget)
            : undefined;

          if (parsedTargetKey !== latestInlineDropTargetKey) {
            latestInlineDropTarget = parsedTarget;
            latestInlineDropTargetKey = parsedTargetKey;
            setActiveDropTarget(parsedTarget);
          }
        }

        if (frameId !== undefined) {
          window.cancelAnimationFrame(frameId);
        }
        frameId = window.requestAnimationFrame(updatePreview);
      };

      const onPointerUp = (): void => {
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        window.removeEventListener("pointercancel", onPointerUp);

        if (frameId !== undefined) {
          window.cancelAnimationFrame(frameId);
        }

        setFloatingMovePreview(undefined);

        if (isInlineImage) {
          if (inlineDragActivated) {
            setIsDraggingImage(false);
            draggedImageRef.current = null;
            setActiveDropTarget(undefined);
          }

          if (Math.abs(latestDeltaX) < 1 && Math.abs(latestDeltaY) < 1) {
            return;
          }

          if (!inlineDragActivated) {
            return;
          }

          if (!latestInlineDropTarget) {
            return;
          }

          editor.moveImage(location, latestInlineDropTarget);
          const sourceParagraphLocation =
            imageLocationToParagraphLocation(location);
          const targetParagraphLocation: ParagraphLocation =
            latestInlineDropTarget.kind === "paragraph"
              ? {
                  kind: "paragraph",
                  nodeIndex: latestInlineDropTarget.nodeIndex,
                }
              : {
                  kind: "table-cell",
                  tableIndex: latestInlineDropTarget.tableIndex,
                  rowIndex: latestInlineDropTarget.rowIndex,
                  cellIndex: latestInlineDropTarget.cellIndex,
                  paragraphIndex: latestInlineDropTarget.paragraphIndex,
                };
          const nextChildIndex =
            sameParagraphLocation(
              sourceParagraphLocation,
              targetParagraphLocation
            ) && location.childIndex < latestInlineDropTarget.childIndex
              ? latestInlineDropTarget.childIndex - 1
              : latestInlineDropTarget.childIndex;
          setSelectedImage({
            ...latestInlineDropTarget,
            childIndex: Math.max(0, nextChildIndex),
          });
          return;
        }

        if (Math.abs(latestDeltaX) < 1 && Math.abs(latestDeltaY) < 1) {
          return;
        }

        if (isWrappedFloatingImage) {
          const hostWidth = paragraphRect?.width ?? DEFAULT_DOC_PAGE_WIDTH;
          const imageWidth =
            image.widthPx ?? Math.max(24, Math.round(wrapperRect.width));
          const imageHeight =
            image.heightPx ?? Math.max(24, Math.round(wrapperRect.height));
          const baseGeometry = resolveDualWrappedFloatingImageGeometry(
            image,
            hostWidth,
            {
              widthPx: imageWidth,
              heightPx: imageHeight,
              ...(paragraphRect
                ? {
                    baseLeftPx: Math.round(
                      wrapperRect.left - paragraphRect.left
                    ),
                    baseTopPx: Math.round(wrapperRect.top - paragraphRect.top),
                  }
                : undefined),
            }
          );
          const movedLeft = clampNumber(
            (baseGeometry?.imageLeftPx ?? 0) + latestDeltaX,
            0,
            Math.max(0, hostWidth - imageWidth)
          );
          const movedTop = Math.round(
            (baseGeometry?.imageTopPx ?? 0) + latestDeltaY
          );
          editor.moveFloatingImage(
            location,
            resolveWrappedFloatingImageDropPatch(
              image,
              hostWidth,
              movedLeft,
              movedTop,
              {
                widthPx: imageWidth,
                heightPx: imageHeight,
              }
            )
          );
          return;
        }

        if (isAbsoluteFloatingImage) {
          editor.moveFloatingImage(
            location,
            resolveAbsoluteFloatingImageDropPatch(
              baseFloating,
              documentLayout,
              {
                wrapperRect,
                pageSurfaceRect,
                deltaX: latestDeltaX,
                deltaY: latestDeltaY,
              }
            )
          );
          return;
        }
      };

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp, { once: true });
      window.addEventListener("pointercancel", onPointerUp, { once: true });
    },
    [documentLayout, editor, isReadOnly]
  );

  const beginDropCapMove = React.useCallback(
    (
      event: React.PointerEvent<HTMLElement>,
      nodeIndex: number,
      dropCap: NonNullable<NonNullable<ParagraphNode["style"]>["dropCap"]>
    ): void => {
      if (isReadOnly) {
        return;
      }

      if (event.button !== 0) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      setSelectedImage(undefined);
      setSelectedSectionImageKey(undefined);
      setSelectedDropCapNodeIndex(nodeIndex);

      const startX = event.clientX;
      const startY = event.clientY;
      let latestDeltaX = 0;
      let latestDeltaY = 0;

      const onPointerMove = (pointerEvent: PointerEvent): void => {
        latestDeltaX = pointerEvent.clientX - startX;
        latestDeltaY = pointerEvent.clientY - startY;
        setDropCapMovePreview({
          nodeIndex,
          deltaX: latestDeltaX,
          deltaY: latestDeltaY,
        });
      };

      const onPointerUp = (): void => {
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        window.removeEventListener("pointercancel", onPointerUp);
        setDropCapMovePreview(undefined);

        if (Math.abs(latestDeltaX) < 1 && Math.abs(latestDeltaY) < 1) {
          return;
        }

        const baseXPx = Math.max(0, twipsToPixels(dropCap.xTwips) ?? 0);
        const baseYPx = Math.max(0, twipsToPixels(dropCap.yTwips) ?? 0);
        editor.moveParagraphDropCap(nodeIndex, {
          xTwips: pixelsToTwips(Math.max(0, baseXPx + latestDeltaX)),
          yTwips: pixelsToTwips(Math.max(0, baseYPx + latestDeltaY)),
        });
      };

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp, { once: true });
      window.addEventListener("pointercancel", onPointerUp, { once: true });
    },
    [editor, isReadOnly]
  );

  const beginDropCapResize = React.useCallback(
    (
      event: React.PointerEvent<HTMLElement>,
      nodeIndex: number,
      currentFontSizePx: number,
      currentWidthPx: number,
      currentHeightPx: number
    ): void => {
      if (isReadOnly || event.button !== 0) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      setSelectedImage(undefined);
      setSelectedSectionImageKey(undefined);
      setSelectedDropCapNodeIndex(nodeIndex);

      const startX = event.clientX;
      const startY = event.clientY;
      const safeWidthPx = Math.max(1, currentWidthPx);
      const safeHeightPx = Math.max(1, currentHeightPx);
      const baseFontSizePx = Math.max(12, currentFontSizePx);
      let latestFontSizePx = baseFontSizePx;

      const onPointerMove = (pointerEvent: PointerEvent): void => {
        const deltaX = pointerEvent.clientX - startX;
        const deltaY = pointerEvent.clientY - startY;
        const widthScale = (safeWidthPx + deltaX) / safeWidthPx;
        const heightScale = (safeHeightPx + deltaY) / safeHeightPx;
        const scale = Math.max(0.35, Math.max(widthScale, heightScale));
        latestFontSizePx = clampNumber(
          Math.round(baseFontSizePx * scale),
          12,
          320
        );
        setDropCapResizePreview({
          nodeIndex,
          fontSizePx: latestFontSizePx,
        });
      };

      const onPointerUp = (): void => {
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        window.removeEventListener("pointercancel", onPointerUp);
        setDropCapResizePreview(undefined);

        if (Math.abs(latestFontSizePx - baseFontSizePx) < 1) {
          return;
        }

        editor.setParagraphDropCapFontSizePt(
          nodeIndex,
          (latestFontSizePx * 72) / 96
        );
      };

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp, { once: true });
      window.addEventListener("pointercancel", onPointerUp, { once: true });
    },
    [editor, isReadOnly]
  );

  const beginSectionFloatingImageMove = React.useCallback(
    (
      event: React.PointerEvent<HTMLElement>,
      location: DocxSectionImageLocation,
      image: ImageRunNode,
      isWrappedFloatingImage: boolean,
      isAbsoluteFloatingImage: boolean
    ): void => {
      if (isReadOnly) {
        return;
      }

      if (
        !activeHeaderFooterEdit ||
        activeHeaderFooterEdit.region !== location.region ||
        activeHeaderFooterEdit.partName !== location.partName
      ) {
        return;
      }

      if (event.button !== 0) {
        return;
      }

      if (
        (event.target as HTMLElement).closest(
          "[data-image-resize-handle='true']"
        )
      ) {
        return;
      }

      const isInlineImage = !isWrappedFloatingImage && !isAbsoluteFloatingImage;
      if (isInlineImage) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const imageKey = sectionImageLocationKey(location);
      setSelectedImage(undefined);
      setSelectedSectionImageKey(imageKey);

      const startX = event.clientX;
      const startY = event.clientY;
      const wrapperElement = event.currentTarget;
      const wrapperRect = wrapperElement.getBoundingClientRect();
      const pageSurface = wrapperElement.closest(
        "[data-docx-page-surface='true']"
      ) as HTMLElement | null;
      const pageSurfaceRect = pageSurface?.getBoundingClientRect();
      const paragraphHost = wrapperElement.closest(
        "[data-docx-section-paragraph-host='true']"
      ) as HTMLElement | null;
      const paragraphRect = paragraphHost?.getBoundingClientRect();
      const baseFloating = image.floating ? { ...image.floating } : {};

      let latestDeltaX = 0;
      let latestDeltaY = 0;
      let frameId: number | undefined;

      const updatePreview = (): void => {
        setFloatingMovePreview({
          imageKey,
          deltaX: latestDeltaX,
          deltaY: latestDeltaY,
          ...(paragraphRect
            ? {
                baseLeftPx: Math.round(wrapperRect.left - paragraphRect.left),
                baseTopPx: Math.round(wrapperRect.top - paragraphRect.top),
              }
            : undefined),
        });
      };

      const onPointerMove = (pointerEvent: PointerEvent): void => {
        latestDeltaX = pointerEvent.clientX - startX;
        latestDeltaY = pointerEvent.clientY - startY;

        if (frameId !== undefined) {
          window.cancelAnimationFrame(frameId);
        }
        frameId = window.requestAnimationFrame(updatePreview);
      };

      const onPointerUp = (): void => {
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        window.removeEventListener("pointercancel", onPointerUp);

        if (frameId !== undefined) {
          window.cancelAnimationFrame(frameId);
        }

        setFloatingMovePreview(undefined);

        if (Math.abs(latestDeltaX) < 1 && Math.abs(latestDeltaY) < 1) {
          return;
        }

        if (isWrappedFloatingImage) {
          const hostWidth = paragraphRect?.width ?? documentLayout.pageWidthPx;
          const imageWidth =
            image.widthPx ?? Math.max(24, Math.round(wrapperRect.width));
          const imageHeight =
            image.heightPx ?? Math.max(24, Math.round(wrapperRect.height));
          const baseGeometry = resolveDualWrappedFloatingImageGeometry(
            image,
            hostWidth,
            {
              widthPx: imageWidth,
              heightPx: imageHeight,
              ...(paragraphRect
                ? {
                    baseLeftPx: Math.round(
                      wrapperRect.left - paragraphRect.left
                    ),
                    baseTopPx: Math.round(wrapperRect.top - paragraphRect.top),
                  }
                : undefined),
            }
          );
          const movedLeft = Math.round(
            clampNumber(
              (baseGeometry?.imageLeftPx ?? 0) + latestDeltaX,
              0,
              Math.max(0, hostWidth - imageWidth)
            )
          );
          const movedTop = Math.round(
            (baseGeometry?.imageTopPx ?? 0) + latestDeltaY
          );
          editor.moveSectionFloatingImage(
            location,
            resolveWrappedFloatingImageDropPatch(
              image,
              hostWidth,
              movedLeft,
              movedTop,
              {
                widthPx: imageWidth,
                heightPx: imageHeight,
              }
            )
          );
          return;
        }

        if (isAbsoluteFloatingImage) {
          editor.moveSectionFloatingImage(
            location,
            resolveAbsoluteFloatingImageDropPatch(
              baseFloating,
              documentLayout,
              {
                wrapperRect,
                pageSurfaceRect,
                deltaX: latestDeltaX,
                deltaY: latestDeltaY,
              }
            )
          );
        }
      };

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp, { once: true });
      window.addEventListener("pointercancel", onPointerUp, { once: true });
    },
    [activeHeaderFooterEdit, documentLayout, editor, isReadOnly]
  );

  const onSectionImageClick = React.useCallback(
    (location: DocxSectionImageLocation): void => {
      if (isReadOnly) {
        return;
      }

      if (
        !activeHeaderFooterEdit ||
        activeHeaderFooterEdit.region !== location.region ||
        activeHeaderFooterEdit.partName !== location.partName
      ) {
        return;
      }

      setSelectedImage(undefined);
      setSelectedSectionImageKey(sectionImageLocationKey(location));
    },
    [activeHeaderFooterEdit, isReadOnly]
  );

  const onImageDragStart = React.useCallback(
    (
      event: React.DragEvent<HTMLImageElement>,
      location: DocxImageLocation
    ): void => {
      if (isReadOnly) {
        event.preventDefault();
        return;
      }

      draggedImageRef.current = location;
      setIsDraggingImage(true);
      setSelectedImage(location);
      setActiveDropTarget(undefined);
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", imageLocationKey(location));
    },
    [isReadOnly]
  );

  const onImageDragEnd = React.useCallback((): void => {
    draggedImageRef.current = null;
    setIsDraggingImage(false);
    setActiveDropTarget(undefined);
  }, []);

  const beginTableCornerResize = React.useCallback(
    (
      event: React.PointerEvent<HTMLSpanElement>,
      tableIndex: number,
      tableWidthPx: number,
      columnWidths: number[],
      rowHeights: number[],
      maxTableWidthPx?: number
    ): void => {
      if (isReadOnly) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (!Number.isFinite(tableWidthPx) || tableWidthPx <= 0) {
        return;
      }

      const resizeHandle = event.currentTarget;
      const pointerId = event.pointerId;

      if (resizeHandle.setPointerCapture) {
        try {
          resizeHandle.setPointerCapture(pointerId);
        } catch {
          // ignore pointer capture failures from detached handles
        }
      }

      const safeStartingColumnWidths = normalizeColumnWidthsPx(
        columnWidths,
        columnWidths.length,
        tableWidthPx,
        1
      );
      const startingHeights = normalizeResizableHeights(rowHeights);
      const minimumTableHeight = Math.max(
        24 * Math.max(1, startingHeights.length),
        1
      );
      const startHeight = startingHeights.reduce(
        (sum, value) => sum + value,
        0
      );
      if (!Number.isFinite(startHeight) || startHeight <= 0) {
        return;
      }

      const startingWidth = fitColumnWidthsToWidth(
        safeStartingColumnWidths,
        tableWidthPx
      ).reduce((sum, widthPx) => sum + widthPx, 0);
      const startY = event.clientY;
      const startX = event.clientX;

      let latestWidths = safeStartingColumnWidths;
      let latestHeights = startingHeights;
      const minimumTableWidth = 120;
      let frameId: number | undefined;

      setActiveTableResize({ tableIndex });

      const commitWidths = (): void => {
        setTableColumnWidths((current) => ({
          ...current,
          [tableIndex]: latestWidths,
        }));
        setTableRowHeights((current) => ({
          ...current,
          [tableIndex]: latestHeights,
        }));
      };

      const onPointerMove = (pointerEvent: PointerEvent): void => {
        const deltaX = pointerEvent.clientX - startX;
        const deltaY = pointerEvent.clientY - startY;
        const nextWidth = Math.max(
          minimumTableWidth,
          clampNumber(
            startingWidth + deltaX,
            0,
            Number.isFinite(maxTableWidthPx)
              ? (maxTableWidthPx as number)
              : Number.POSITIVE_INFINITY
          )
        );
        const nextHeight = Math.max(minimumTableHeight, startHeight + deltaY);

        latestWidths = fitColumnWidthsToWidth(
          safeStartingColumnWidths,
          nextWidth
        );
        latestHeights = distributeHeightByTotal(startingHeights, nextHeight);

        if (frameId !== undefined) {
          window.cancelAnimationFrame(frameId);
        }

        frameId = window.requestAnimationFrame(commitWidths);
      };

      const onPointerUp = (pointerUpEvent: PointerEvent): void => {
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        window.removeEventListener("pointercancel", onPointerUp);

        if (frameId !== undefined) {
          window.cancelAnimationFrame(frameId);
        }

        commitWidths();
        if (resizeHandle.releasePointerCapture) {
          try {
            resizeHandle.releasePointerCapture(
              pointerUpEvent.pointerId ?? pointerId
            );
          } catch {
            // ignore pointer capture failures
          }
        }
        setActiveTableResize(undefined);
      };

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp, { once: true });
      window.addEventListener("pointercancel", onPointerUp, { once: true });
    },
    [isReadOnly]
  );

  const beginEmbeddedTableCornerResize = React.useCallback(
    (
      event: React.PointerEvent<HTMLSpanElement>,
      tableKey: string,
      tableWidthPx: number,
      columnWidths: number[],
      rowHeights: number[],
      maxTableWidthPx?: number
    ): void => {
      if (isReadOnly) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (!Number.isFinite(tableWidthPx) || tableWidthPx <= 0) {
        return;
      }

      const resizeHandle = event.currentTarget;
      const pointerId = event.pointerId;

      if (resizeHandle.setPointerCapture) {
        try {
          resizeHandle.setPointerCapture(pointerId);
        } catch {
          // ignore pointer capture failures from detached handles
        }
      }

      const safeStartingColumnWidths = normalizeColumnWidthsPx(
        columnWidths,
        columnWidths.length,
        tableWidthPx,
        1
      );
      const startingHeights = normalizeResizableHeights(rowHeights);
      const minimumTableHeight = Math.max(
        24 * Math.max(1, startingHeights.length),
        1
      );
      const startHeight = startingHeights.reduce(
        (sum, value) => sum + value,
        0
      );
      if (!Number.isFinite(startHeight) || startHeight <= 0) {
        return;
      }

      const startingWidth = fitColumnWidthsToWidth(
        safeStartingColumnWidths,
        tableWidthPx
      ).reduce((sum, widthPx) => sum + widthPx, 0);
      const startY = event.clientY;
      const startX = event.clientX;

      let latestWidths = safeStartingColumnWidths;
      let latestHeights = startingHeights;
      const minimumTableWidth = 120;
      let frameId: number | undefined;

      setActiveEmbeddedTableResize({ tableKey });

      const commitWidths = (): void => {
        setEmbeddedTableColumnWidths((current) => ({
          ...current,
          [tableKey]: latestWidths,
        }));
        setEmbeddedTableRowHeights((current) => ({
          ...current,
          [tableKey]: latestHeights,
        }));
      };

      const onPointerMove = (pointerEvent: PointerEvent): void => {
        const deltaX = pointerEvent.clientX - startX;
        const deltaY = pointerEvent.clientY - startY;
        const nextWidth = Math.max(
          minimumTableWidth,
          clampNumber(
            startingWidth + deltaX,
            0,
            Number.isFinite(maxTableWidthPx)
              ? (maxTableWidthPx as number)
              : Number.POSITIVE_INFINITY
          )
        );
        const nextHeight = Math.max(minimumTableHeight, startHeight + deltaY);

        latestWidths = fitColumnWidthsToWidth(
          safeStartingColumnWidths,
          nextWidth
        );
        latestHeights = distributeHeightByTotal(startingHeights, nextHeight);

        if (frameId !== undefined) {
          window.cancelAnimationFrame(frameId);
        }

        frameId = window.requestAnimationFrame(commitWidths);
      };

      const onPointerUp = (pointerUpEvent: PointerEvent): void => {
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        window.removeEventListener("pointercancel", onPointerUp);

        if (frameId !== undefined) {
          window.cancelAnimationFrame(frameId);
        }

        commitWidths();
        if (resizeHandle.releasePointerCapture) {
          try {
            resizeHandle.releasePointerCapture(
              pointerUpEvent.pointerId ?? pointerId
            );
          } catch {
            // ignore pointer capture failures
          }
        }
        setActiveEmbeddedTableResize(undefined);
      };

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp, { once: true });
      window.addEventListener("pointercancel", onPointerUp, { once: true });
    },
    [distributeHeightByTotal, isReadOnly, normalizeResizableHeights]
  );

  const beginEmbeddedTableMove = React.useCallback(
    (event: React.PointerEvent<HTMLSpanElement>, tableKey: string): void => {
      if (isReadOnly) {
        return;
      }
      if (event.button !== 0 && event.button !== undefined) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      setFocusedEmbeddedTableKey(tableKey);
      setHoveredEmbeddedTableKey(tableKey);

      const runtimeLocation = parseEmbeddedTableRuntimeKey(tableKey);
      if (!runtimeLocation) {
        return;
      }

      setHoveredTableHandleTableIndex(runtimeLocation.hostTableIndex);
      tableMoveDragRef.current = {
        pointerId: event.pointerId,
        tableIndex: runtimeLocation.hostTableIndex,
        embeddedTableKey: tableKey,
        startX: event.clientX,
        startY: event.clientY,
        hasMoved: false,
      };

      const onPointerMove = (pointerMoveEvent: PointerEvent): void => {
        const dragState = tableMoveDragRef.current;
        if (
          !dragState ||
          dragState.embeddedTableKey !== tableKey ||
          pointerMoveEvent.pointerId !== dragState.pointerId
        ) {
          return;
        }

        const deltaX = Math.abs(pointerMoveEvent.clientX - dragState.startX);
        const deltaY = Math.abs(pointerMoveEvent.clientY - dragState.startY);
        if (deltaX + deltaY < TABLE_MOVE_DRAG_THRESHOLD_PX) {
          return;
        }

        dragState.hasMoved = true;
        const target = resolveTableMoveDropTarget(dragState.tableIndex, {
          x: pointerMoveEvent.clientX,
          y: pointerMoveEvent.clientY,
        });
        setTableMoveDropPreview(target?.preview);
        pointerMoveEvent.preventDefault();
      };

      const onPointerUp = (pointerUpEvent: PointerEvent): void => {
        const dragState = tableMoveDragRef.current;
        if (
          !dragState ||
          dragState.embeddedTableKey !== tableKey ||
          pointerUpEvent.pointerId !== dragState.pointerId
        ) {
          return;
        }

        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        window.removeEventListener("pointercancel", onPointerUp);
        tableMoveDragRef.current = undefined;
        setTableMoveDropPreview(undefined);

        const pointerTarget = document.elementFromPoint(
          pointerUpEvent.clientX,
          pointerUpEvent.clientY
        );
        const hostTableElement = tableElementsRef.current.get(
          dragState.tableIndex
        );
        const pointerStillOverHostTable = Boolean(
          pointerTarget && hostTableElement?.contains(pointerTarget)
        );
        setHoveredTableHandleTableIndex(
          pointerStillOverHostTable ? dragState.tableIndex : undefined
        );

        if (!dragState.hasMoved) {
          return;
        }

        const target = resolveTableMoveDropTarget(dragState.tableIndex, {
          x: pointerUpEvent.clientX,
          y: pointerUpEvent.clientY,
        });
        if (!target) {
          return;
        }

        setHoveredEmbeddedTableKey(undefined);
        setFocusedEmbeddedTableKey(undefined);
        editor.moveEmbeddedTableToBody(tableKey, target.targetNodeIndex);
      };

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp, { once: true });
      window.addEventListener("pointercancel", onPointerUp, { once: true });
    },
    [editor, isReadOnly, resolveTableMoveDropTarget]
  );

  const embeddedTableResizeController =
    React.useMemo<EmbeddedTableResizeController>(
      () => ({
        isReadOnly,
        hoveredTableKey: hoveredEmbeddedTableKey,
        focusedTableKey: focusedEmbeddedTableKey,
        hoveredHandle: hoveredEmbeddedTableResizeHandle,
        resolveColumnWidths: (tableKey, table, tableWidthPx) =>
          resolveEmbeddedTableColumnWidths(tableKey, table, tableWidthPx),
        resolveRowHeights: (tableKey, table) =>
          resolveEmbeddedTableRowHeights(tableKey, table),
        isTableResizing: (tableKey) =>
          activeEmbeddedColumnResize?.tableKey === tableKey ||
          activeEmbeddedRowResize?.tableKey === tableKey ||
          activeEmbeddedTableResize?.tableKey === tableKey,
        isColumnResizing: (tableKey, boundaryColumnIndex) =>
          activeEmbeddedColumnResize?.tableKey === tableKey &&
          activeEmbeddedColumnResize.boundaryColumnIndex ===
            boundaryColumnIndex,
        isRowResizing: (tableKey, boundaryRowIndex) =>
          activeEmbeddedRowResize?.tableKey === tableKey &&
          activeEmbeddedRowResize.boundaryRowIndex === boundaryRowIndex,
        isCornerResizing: (tableKey) =>
          activeEmbeddedTableResize?.tableKey === tableKey,
        setHoveredTableKey: setHoveredEmbeddedTableKey,
        setFocusedTableKey: setFocusedEmbeddedTableKey,
        setHoveredHandle: setHoveredEmbeddedTableResizeHandle,
        beginColumnResize: (
          event,
          tableKey,
          boundaryColumnIndex,
          tableWidthPx,
          columnWidths
        ) => {
          beginEmbeddedTableColumnResize(
            event,
            tableKey,
            boundaryColumnIndex,
            tableWidthPx,
            columnWidths
          );
        },
        beginRowResize: (event, tableKey, boundaryRowIndex, rowHeights) => {
          beginEmbeddedTableRowResize(
            event,
            tableKey,
            boundaryRowIndex,
            rowHeights
          );
        },
        beginCornerResize: (
          event,
          tableKey,
          tableWidthPx,
          columnWidths,
          rowHeights,
          maxTableWidthPx
        ) => {
          beginEmbeddedTableCornerResize(
            event,
            tableKey,
            tableWidthPx,
            columnWidths,
            rowHeights,
            maxTableWidthPx
          );
        },
        beginTableMove: (event, tableKey) => {
          beginEmbeddedTableMove(event, tableKey);
        },
      }),
      [
        activeEmbeddedColumnResize,
        activeEmbeddedRowResize,
        activeEmbeddedTableResize,
        beginEmbeddedTableColumnResize,
        beginEmbeddedTableCornerResize,
        beginEmbeddedTableMove,
        beginEmbeddedTableRowResize,
        focusedEmbeddedTableKey,
        hoveredEmbeddedTableKey,
        hoveredEmbeddedTableResizeHandle,
        isReadOnly,
        resolveEmbeddedTableColumnWidths,
        resolveEmbeddedTableRowHeights,
      ]
    );

  const onDropImageAtTarget = React.useCallback(
    (target: DocxImageDropTarget): void => {
      if (isReadOnly) {
        return;
      }

      const source = draggedImageRef.current;
      if (!source) {
        return;
      }

      editor.moveImage(source, target);

      const sourceParagraphLocation = imageLocationToParagraphLocation(source);
      const targetParagraphLocation: ParagraphLocation =
        target.kind === "paragraph"
          ? {
              kind: "paragraph",
              nodeIndex: target.nodeIndex,
            }
          : {
              kind: "table-cell",
              tableIndex: target.tableIndex,
              rowIndex: target.rowIndex,
              cellIndex: target.cellIndex,
              paragraphIndex: target.paragraphIndex,
            };

      const nextChildIndex =
        sameParagraphLocation(
          sourceParagraphLocation,
          targetParagraphLocation
        ) && source.childIndex < target.childIndex
          ? target.childIndex - 1
          : target.childIndex;

      setSelectedImage({ ...target, childIndex: Math.max(0, nextChildIndex) });
      setActiveDropTarget(undefined);
      setIsDraggingImage(false);
      draggedImageRef.current = null;
    },
    [editor, isReadOnly]
  );

  const renderImageDropZone = React.useCallback(
    (target: DocxImageDropTarget, key: string): React.JSX.Element | null => {
      if (isReadOnly || !isDraggingImage) {
        return null;
      }

      const isActive = activeDropTarget
        ? dropTargetKey(activeDropTarget) === dropTargetKey(target)
        : false;

      return (
        <span
          key={key}
          contentEditable={false}
          style={{
            display: "inline-block",
            width: 0,
            height: 0,
            minWidth: 0,
            minHeight: 0,
            margin: 0,
            padding: 0,
            overflow: "visible",
            verticalAlign: "baseline",
            position: "relative",
          }}
        >
          <span
            contentEditable={false}
            data-docx-image-drop-zone="true"
            data-docx-target-kind={target.kind}
            data-docx-child-index={target.childIndex}
            data-docx-node-index={
              target.kind === "paragraph" ? target.nodeIndex : undefined
            }
            data-docx-table-index={
              target.kind === "table-cell" ? target.tableIndex : undefined
            }
            data-docx-row-index={
              target.kind === "table-cell" ? target.rowIndex : undefined
            }
            data-docx-cell-index={
              target.kind === "table-cell" ? target.cellIndex : undefined
            }
            data-docx-paragraph-index={
              target.kind === "table-cell" ? target.paragraphIndex : undefined
            }
            style={{
              position: "absolute",
              left: -4,
              top: -11,
              width: 8,
              height: 22,
              borderLeft: isActive
                ? "2px solid #0f172a"
                : "2px solid transparent",
              borderRadius: 999,
            }}
            onDragOver={(event) => {
              if (!draggedImageRef.current) {
                return;
              }

              event.preventDefault();
              event.dataTransfer.dropEffect = "move";
              setActiveDropTarget(target);
            }}
            onDrop={(event) => {
              event.preventDefault();
              onDropImageAtTarget(target);
            }}
          />
        </span>
      );
    },
    [activeDropTarget, isDraggingImage, isReadOnly, onDropImageAtTarget]
  );

  const renderWrappedPretextParagraph = (params: {
    location: ParagraphLocation;
    keyPrefix: string;
    source: ParagraphPretextLayoutSource;
    layout: PretextVariableWidthLayout;
    lineHeightPx: number;
    baseTextStyle?: TextRunNode["style"];
    blockHeightPx?: number;
    obstacleNodes?: React.ReactNode;
    pageAbsoluteObstacleNodes?: React.ReactNode;
  }): React.ReactNode => {
    const {
      location,
      keyPrefix,
      source,
      layout,
      lineHeightPx,
      baseTextStyle,
      blockHeightPx,
      obstacleNodes,
      pageAbsoluteObstacleNodes,
    } = params;
    const locationKey = paragraphLocationKey(location);
    const activeSession =
      activeWrappedParagraphSession?.locationKey === locationKey
        ? activeWrappedParagraphSession
        : undefined;
    const paragraphAtLocation = getParagraphAtLocation(
      editor.model,
      location
    ).paragraph;
    const normalizedActiveRange = editor.activeTextRange
      ? normalizeTextRange(editor.activeTextRange)
      : undefined;
    const rangeTouchesParagraph = Boolean(
      paragraphAtLocation &&
        normalizedActiveRange &&
        compareParagraphLocations(
          paragraphLocationFromTextRangeLocation(
            normalizedActiveRange.start.location
          ),
          location
        ) <= 0 &&
        compareParagraphLocations(
          location,
          paragraphLocationFromTextRangeLocation(
            normalizedActiveRange.end.location
          )
        ) <= 0
    );
    const activeRangeOffsets =
      paragraphAtLocation && normalizedActiveRange && rangeTouchesParagraph
        ? resolveRangeBoundaryOffsetsForParagraph(
            location,
            normalizedActiveRange.start,
            normalizedActiveRange.end,
            paragraphAtLocation
          )
        : undefined;
    const selectionStart =
      activeSession?.selectionStart ?? activeRangeOffsets?.[0];
    const selectionEnd = activeSession?.selectionEnd ?? activeRangeOffsets?.[1];
    const selectionRects =
      selectionStart !== undefined && selectionEnd !== undefined
        ? resolveSelectionRects(layout, selectionStart, selectionEnd)
        : [];
    const inputAnchorRect = activeSession
      ? resolveCaretRectAtOffset(layout, activeSession.selectionEnd) ??
        resolveCaretRectAtOffset(layout, activeSession.selectionStart)
      : normalizedActiveRange &&
        activeRangeOffsets &&
        compareTextRangeBoundaries(
          normalizedActiveRange.start,
          normalizedActiveRange.end
        ) === 0
      ? resolveCaretRectAtOffset(layout, activeRangeOffsets[1])
      : undefined;
    const caretRect =
      activeSession &&
      activeSession.selectionStart === activeSession.selectionEnd
        ? inputAnchorRect
        : !activeSession &&
          normalizedActiveRange &&
          activeRangeOffsets &&
          compareTextRangeBoundaries(
            normalizedActiveRange.start,
            normalizedActiveRange.end
          ) === 0
        ? inputAnchorRect
        : undefined;

    const renderFragment = (
      fragment: PretextLineFragment,
      lineIndex: number
    ): React.ReactNode => {
      const pieces = source.runs
        .map((run) => {
          const overlapStart = Math.max(fragment.startOffset, run.startOffset);
          const overlapEnd = Math.min(fragment.endOffset, run.endOffset);
          if (overlapStart >= overlapEnd) {
            return undefined;
          }

          if (run.kind === "image" && run.image) {
            if (overlapStart !== run.startOffset) {
              return undefined;
            }

            const renderableImageSrc =
              syntheticTextBoxSvg(
                run.image,
                undefined,
                undefined,
                undefined,
                undefined
              ) ?? resolveRenderableImageSource(run.image);
            if (!renderableImageSrc) {
              return undefined;
            }

            return (
              <img
                key={`${keyPrefix}-fragment-image-${lineIndex}-${run.key}-${overlapStart}`}
                src={renderableImageSrc}
                alt={run.image.alt ?? "DOCX image"}
                draggable={false}
                style={{
                  width: run.image.widthPx
                    ? `${run.image.widthPx}px`
                    : undefined,
                  height: run.image.heightPx
                    ? `${run.image.heightPx}px`
                    : undefined,
                  verticalAlign: "middle",
                  display: "inline-block",
                  filter: appendCssFilters(
                    run.image.cssFilter,
                    documentContentFilter
                  ),
                  opacity: run.image.cssOpacity,
                }}
              />
            );
          }

          if (run.kind === "tab" && Number.isFinite(run.tabWidthPx)) {
            const overlapRatio =
              run.endOffset > run.startOffset
                ? (overlapEnd - overlapStart) /
                  (run.endOffset - run.startOffset)
                : 1;
            const tabWidthPx = Math.max(
              1,
              Math.round((run.tabWidthPx as number) * overlapRatio)
            );
            return (
              <span
                key={`${keyPrefix}-fragment-tab-${lineIndex}-${run.key}-${overlapStart}`}
                data-docx-tab-char="true"
                style={{
                  ...runStyleToCss(run.style, documentContentTheme),
                  display: "inline-block",
                  width: tabWidthPx,
                  minWidth: tabWidthPx,
                  whiteSpace: "pre",
                  lineHeight: "1em",
                }}
              >
                {"\u00a0"}
              </span>
            );
          }

          const slice = run.text.slice(
            overlapStart - run.startOffset,
            overlapEnd - run.startOffset
          );
          const renderedSlice = sanitizeRenderedPretextFragmentText(slice);
          if (!renderedSlice) {
            return undefined;
          }

          const textStyle = run.link
            ? {
                ...linkStyleToCss(run.style, documentContentTheme),
                whiteSpace: "pre",
              }
            : {
                ...runStyleToCss(run.style, documentContentTheme),
                whiteSpace: "pre",
              };

          if (activeSession) {
            return (
              <span
                key={`${keyPrefix}-active-${lineIndex}-${run.key}-${overlapStart}`}
                style={textStyle}
              >
                {renderedSlice}
              </span>
            );
          }

          return run.link ? (
            <a
              key={`${keyPrefix}-fragment-${lineIndex}-${run.key}-${overlapStart}`}
              href={run.link}
              target={run.link.startsWith("#") ? undefined : "_blank"}
              rel={run.link.startsWith("#") ? undefined : "noreferrer noopener"}
              onMouseDown={(event) => {
                if (!run.link?.startsWith("#")) {
                  return;
                }
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={(event) => {
                if (!run.link?.startsWith("#")) {
                  return;
                }
                event.preventDefault();
                event.stopPropagation();
                scrollToBookmark(run.link.slice(1));
              }}
              style={textStyle}
            >
              {renderedSlice}
            </a>
          ) : (
            <span
              key={`${keyPrefix}-fragment-${lineIndex}-${run.key}-${overlapStart}`}
              style={textStyle}
            >
              {renderedSlice}
            </span>
          );
        })
        .filter(Boolean);

      return (
        <span
          key={`${keyPrefix}-frag-${lineIndex}-${fragment.startOffset}-${fragment.endOffset}`}
          style={{
            position: "absolute",
            left: fragment.x,
            top: layout.lines[lineIndex]?.y ?? 0,
            display: "inline-block",
            whiteSpace: "pre",
            lineHeight: `${lineHeightPx}px`,
            pointerEvents: activeSession ? "none" : undefined,
          }}
        >
          {pieces}
        </span>
      );
    };

    const handlePointerSelection = (
      event: React.PointerEvent<HTMLSpanElement>
    ): void => {
      if (isReadOnly) {
        return;
      }

      const rect = event.currentTarget.getBoundingClientRect();
      const textValue = activeSession?.text ?? source.text;
      const offset = resolveOffsetAtPoint(
        layout,
        event.clientX - rect.left,
        event.clientY - rect.top
      );
      const anchorOffset =
        event.shiftKey && activeSession != null
          ? activeSession.anchorOffset
          : offset;
      const anchorBoundary: DocxTextRangeBoundary = {
        location: cloneTextRangeLocation(location),
        offset: anchorOffset,
      };
      wrappedParagraphSelectionDragRef.current = {
        pointerId: event.pointerId,
        locationKey,
        anchorOffset,
        anchorBoundary,
        startX: event.clientX,
        startY: event.clientY,
      };
      cancelPendingPointerSelectionReconcile();
      editor.beginSelectionSession("pointer", { settleAfterMs: 320 });
      event.currentTarget.setPointerCapture(event.pointerId);
      syncWrappedParagraphRange(location, anchorOffset, offset, {
        textOverride: textValue,
        textLength: textValue.length,
        anchorOffset,
      });
      focusWrappedParagraphTextarea();
    };

    return (
      <span
        data-docx-wrapped-paragraph-root="true"
        data-docx-wrapped-paragraph-location-key={locationKey}
        contentEditable={false}
        ref={(element) => {
          if (element) {
            wrappedParagraphSurfaceRegistryRef.current.set(locationKey, {
              location,
              element,
              layout,
              textLength: (activeSession?.text ?? source.text).length,
            });
          } else {
            wrappedParagraphSurfaceRegistryRef.current.delete(locationKey);
          }
        }}
        style={{
          display: "block",
          position: pageAbsoluteObstacleNodes ? "static" : "relative",
          minHeight: blockHeightPx ?? layout.height,
          overflow: "visible",
          userSelect: "none",
          WebkitUserSelect: "none",
        }}
        onPointerDown={(event) => {
          const target = event.target;
          if (
            eventTargetIsInteractiveControl(target) ||
            (target instanceof Element &&
              target.closest(
                "[data-docx-image-location],[data-docx-drop-cap='true'],[data-docx-table-move-handle='true'],[data-image-resize-handle='true'],[data-docx-form-field='true']"
              ))
          ) {
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          handlePointerSelection(event);
        }}
        onPointerMove={(event) => {
          const dragState = wrappedParagraphSelectionDragRef.current;
          if (
            !dragState ||
            dragState.pointerId !== event.pointerId ||
            dragState.locationKey !== locationKey
          ) {
            return;
          }
          const fallbackBias: "start" | "end" =
            event.clientY < dragState.startY ||
            (event.clientY === dragState.startY &&
              event.clientX < dragState.startX)
              ? "start"
              : "end";
          const endBoundary =
            resolveBoundaryFromPoint(
              {
                x: event.clientX,
                y: event.clientY,
              },
              fallbackBias
            ) ?? dragState.anchorBoundary;

          if (
            sameParagraphLocation(
              paragraphLocationFromTextRangeLocation(
                dragState.anchorBoundary.location
              ),
              location
            ) &&
            sameParagraphLocation(
              paragraphLocationFromTextRangeLocation(endBoundary.location),
              location
            )
          ) {
            const textValue = activeSession?.text ?? source.text;
            syncWrappedParagraphRange(
              location,
              dragState.anchorOffset,
              endBoundary.offset,
              {
                textOverride: textValue,
                textLength: textValue.length,
                anchorOffset: dragState.anchorOffset,
              }
            );
            return;
          }

          wrappedParagraphPendingSelectionRef.current = undefined;
          setActiveWrappedParagraphSession(undefined);
          setSelectionFromDocxBoundaries(dragState.anchorBoundary, endBoundary);
        }}
        onPointerUp={(event) => {
          if (
            wrappedParagraphSelectionDragRef.current?.pointerId ===
            event.pointerId
          ) {
            wrappedParagraphSelectionDragRef.current = undefined;
            event.currentTarget.releasePointerCapture(event.pointerId);
          }
          if (
            activeWrappedParagraphSessionRef.current &&
            activeWrappedParagraphSessionRef.current.locationKey === locationKey
          ) {
            focusWrappedParagraphTextarea();
          } else {
            editor.clearSelectionSession("pointer");
          }
        }}
        onDoubleClick={(event) => {
          if (isReadOnly) {
            return;
          }
          const rect = event.currentTarget.getBoundingClientRect();
          const textValue = activeSession?.text ?? source.text;
          const offset = resolveOffsetAtPoint(
            layout,
            event.clientX - rect.left,
            event.clientY - rect.top
          );
          const wordRange = expandOffsetToWord(textValue, offset);
          syncWrappedParagraphRange(location, wordRange.start, wordRange.end, {
            textOverride: textValue,
            textLength: textValue.length,
          });
          focusWrappedParagraphTextarea();
        }}
        onClick={(event) => {
          if (isReadOnly || event.detail < 3) {
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          const textValue = activeSession?.text ?? source.text;
          syncWrappedParagraphRange(location, 0, textValue.length, {
            textOverride: textValue,
            textLength: textValue.length,
          });
          focusWrappedParagraphTextarea();
        }}
      >
        {pageAbsoluteObstacleNodes}
        <span
          style={{
            display: "block",
            position: "relative",
            minHeight: blockHeightPx ?? layout.height,
          }}
        >
          {obstacleNodes}
          {selectionRects.map((rect, index) => (
            <span
              key={`${keyPrefix}-sel-${index}`}
              contentEditable={false}
              {...{ [DOCX_THUMBNAIL_EXCLUDE_ATTRIBUTE]: "true" }}
              style={{
                position: "absolute",
                left: rect.left,
                top: rect.top,
                width: rect.width,
                height: rect.height,
                backgroundColor: "rgba(59, 130, 246, 0.25)",
                pointerEvents: "none",
                zIndex: 2,
              }}
            />
          ))}
          {layout.lines.map((line, lineIndex) =>
            line.fragments.map((fragment) =>
              renderFragment(fragment, lineIndex)
            )
          )}
          {activeSession && caretRect ? (
            <span
              contentEditable={false}
              {...{ [DOCX_THUMBNAIL_EXCLUDE_ATTRIBUTE]: "true" }}
              style={{
                position: "absolute",
                left: caretRect.left,
                top: caretRect.top,
                width: Math.max(1, caretRect.width),
                height: caretRect.height,
                backgroundColor: "#2563eb",
                pointerEvents: "none",
                zIndex: 4,
                animation: "docxWrappedCaretBlink 1s steps(1, end) infinite",
              }}
            />
          ) : null}
          {activeSession && inputAnchorRect ? (
            <textarea
              ref={(element) => {
                if (activeSession.locationKey === locationKey) {
                  wrappedParagraphTextareaRef.current = element;
                  if (element) {
                    const pendingSelection =
                      wrappedParagraphPendingSelectionRef.current;
                    const safeStart = Math.max(
                      0,
                      Math.min(
                        pendingSelection?.locationKey === locationKey
                          ? pendingSelection.start
                          : activeSession.selectionStart,
                        element.value.length
                      )
                    );
                    const safeEnd = Math.max(
                      safeStart,
                      Math.min(
                        pendingSelection?.locationKey === locationKey
                          ? pendingSelection.end
                          : activeSession.selectionEnd,
                        element.value.length
                      )
                    );
                    try {
                      element.setSelectionRange(safeStart, safeEnd);
                    } catch {
                      // Ignore mount-time selection sync failures.
                    }
                  }
                }
              }}
              value={activeSession.text}
              spellCheck={false}
              aria-label="Wrapped paragraph text"
              onChange={(event) => {
                cancelPendingPointerSelectionReconcile();
                editor.beginSelectionSession("keyboard", {
                  settleAfterMs: 220,
                });
                const nextText = event.currentTarget.value.replace(
                  /\r\n?/g,
                  "\n"
                );
                const nextStart =
                  event.currentTarget.selectionStart ?? nextText.length;
                const nextEnd = event.currentTarget.selectionEnd ?? nextStart;
                syncWrappedParagraphRange(location, nextStart, nextEnd, {
                  textOverride: nextText,
                  textLength: nextText.length,
                });
                commitWrappedParagraphTextAtLocation(location, nextText);
                schedulePaginationMeasurementResume();
              }}
              onSelect={(event) => {
                cancelPendingPointerSelectionReconcile();
                const nextStart = event.currentTarget.selectionStart ?? 0;
                const nextEnd = event.currentTarget.selectionEnd ?? nextStart;
                syncWrappedParagraphRange(location, nextStart, nextEnd, {
                  textOverride: event.currentTarget.value,
                  textLength: event.currentTarget.value.length,
                });
              }}
              onCompositionStart={() => {
                editor.beginSelectionSession("composition");
                setActiveWrappedParagraphSession((current) =>
                  current && current.locationKey === locationKey
                    ? { ...current, isComposing: true }
                    : current
                );
              }}
              onCompositionEnd={(event) => {
                editor.beginSelectionSession("keyboard", {
                  settleAfterMs: 260,
                });
                const nextText = event.currentTarget.value.replace(
                  /\r\n?/g,
                  "\n"
                );
                const nextStart =
                  event.currentTarget.selectionStart ?? nextText.length;
                const nextEnd = event.currentTarget.selectionEnd ?? nextStart;
                setActiveWrappedParagraphSession((current) =>
                  current && current.locationKey === locationKey
                    ? {
                        ...current,
                        isComposing: false,
                        text: nextText,
                        selectionStart: nextStart,
                        selectionEnd: nextEnd,
                      }
                    : current
                );
              }}
              onKeyDown={(event) => {
                cancelPendingPointerSelectionReconcile();
                editor.beginSelectionSession("keyboard", {
                  settleAfterMs: 220,
                });
                const textarea = event.currentTarget;
                const currentText = textarea.value.replace(/\r\n?/g, "\n");
                const nextParagraph = getParagraphAtLocation(
                  editor.model,
                  location
                ).paragraph;
                const selectionStart = textarea.selectionStart ?? 0;
                const selectionEnd = textarea.selectionEnd ?? selectionStart;
                const isCollapsedSelection = selectionStart === selectionEnd;
                const font =
                  layout.font ??
                  resolveMeasureFont(
                    baseTextStyle,
                    nextParagraph ? paragraphBaseFontSizePx(nextParagraph) : 16
                  );
                const currentLayout =
                  layout.text === currentText &&
                  layout.font === font &&
                  layout.containerWidthPx !== undefined &&
                  layout.lineHeightPx !== undefined
                    ? layout
                    : layoutTextWithPretextAroundExclusions(
                        currentText,
                        font,
                        layout.containerWidthPx ?? 1,
                        layout.lineHeightPx ?? lineHeightPx,
                        layout.exclusions,
                        {
                          wordBreak: pretextWordBreakModeForText(currentText),
                        }
                      ) ?? layout;
                const currentCaretRect = resolveCaretRectAtOffset(
                  currentLayout,
                  selectionEnd
                );
                const isPlainEnterKey =
                  event.key === "Enter" &&
                  !event.metaKey &&
                  !event.ctrlKey &&
                  !event.altKey;
                const isListParagraph =
                  location.kind === "paragraph" && nextParagraph
                    ? paragraphIsList(nextParagraph, currentText)
                    : false;
                const updateSelection = (
                  startOffset: number,
                  endOffset: number
                ): void => {
                  syncWrappedParagraphRange(location, startOffset, endOffset, {
                    textOverride: currentText,
                    textLength: currentText.length,
                    preferredCaretX: currentCaretRect?.left,
                  });
                  scheduleDomWrite(() => {
                    const latestTextarea = wrappedParagraphTextareaRef.current;
                    if (!latestTextarea) {
                      return;
                    }
                    latestTextarea.setSelectionRange(startOffset, endOffset);
                  });
                };

                if (
                  (event.key === "Home" || event.key === "End") &&
                  !event.metaKey &&
                  !event.ctrlKey &&
                  !event.altKey
                ) {
                  event.preventDefault();
                  const currentLine =
                    currentLayout.lines.find((line) =>
                      line.fragments.some(
                        (fragment) =>
                          selectionEnd >= fragment.startOffset &&
                          selectionEnd <= fragment.endOffset
                      )
                    ) ?? currentLayout.lines[0];
                  const targetOffset =
                    event.key === "Home"
                      ? currentLine?.fragments[0]?.startOffset ?? 0
                      : currentLine?.fragments[currentLine.fragments.length - 1]
                          ?.endOffset ?? currentText.length;
                  const anchorOffset = event.shiftKey
                    ? activeSession?.anchorOffset ?? selectionStart
                    : targetOffset;
                  updateSelection(anchorOffset, targetOffset);
                  return;
                }

                if (
                  (event.key === "ArrowUp" || event.key === "ArrowDown") &&
                  !event.metaKey &&
                  !event.ctrlKey &&
                  !event.altKey
                ) {
                  event.preventDefault();
                  // For expanded selections collapse to the appropriate end first (Word behavior):
                  // ArrowUp → collapse to min (top) end; ArrowDown → collapse to max (bottom) end
                  const sourceOffset = isCollapsedSelection
                    ? selectionEnd
                    : event.key === "ArrowUp"
                    ? selectionStart
                    : selectionEnd;
                  const sourceCaretRect = isCollapsedSelection
                    ? currentCaretRect
                    : resolveCaretRectAtOffset(currentLayout, sourceOffset);
                  const preferredCaretX =
                    (isCollapsedSelection
                      ? activeSession?.preferredCaretX
                      : undefined) ??
                    sourceCaretRect?.left ??
                    0;
                  const probeY =
                    (sourceCaretRect?.top ?? 0) +
                    (event.key === "ArrowUp"
                      ? -(currentLayout.lineHeightPx ?? lineHeightPx)
                      : currentLayout.lineHeightPx ?? lineHeightPx) +
                    Math.max(
                      1,
                      (currentLayout.lineHeightPx ?? lineHeightPx) / 2
                    );
                  const targetOffset = resolveOffsetAtPoint(
                    currentLayout,
                    preferredCaretX,
                    probeY
                  );
                  updateSelection(targetOffset, targetOffset);
                  return;
                }

                if (
                  event.key === "Tab" &&
                  !event.metaKey &&
                  !event.ctrlKey &&
                  !event.altKey &&
                  isListParagraph &&
                  location.kind === "paragraph"
                ) {
                  event.preventDefault();
                  const changed = editor.adjustSelectedListDepth(
                    event.shiftKey ? -1 : 1,
                    currentText
                  );
                  if (changed) {
                    updateSelection(selectionEnd, selectionEnd);
                  }
                  return;
                }

                if (
                  isPlainEnterKey &&
                  !event.shiftKey &&
                  location.kind === "paragraph" &&
                  isListParagraph
                ) {
                  event.preventDefault();
                  const inserted = editor.insertListItemAfterSelection(
                    currentText,
                    selectionStart,
                    selectionEnd,
                    location
                  );
                  if (inserted) {
                    setActiveWrappedParagraphSession(undefined);
                    focusParagraphAtOffset(
                      inserted.paragraphIndex,
                      inserted.caretOffset
                    );
                  }
                  return;
                }

                if (
                  isPlainEnterKey &&
                  !event.shiftKey &&
                  location.kind === "paragraph" &&
                  !isListParagraph
                ) {
                  event.preventDefault();
                  const split = editor.splitParagraphAtSelection(
                    currentText,
                    selectionStart,
                    selectionEnd,
                    location
                  );
                  if (split) {
                    setActiveWrappedParagraphSession(undefined);
                    focusParagraphAtOffset(
                      split.paragraphIndex,
                      split.caretOffset
                    );
                  }
                  return;
                }
              }}
              onBlur={(event) => {
                const currentTarget = event.currentTarget;
                const wrappedParagraphRoot = currentTarget.closest<HTMLElement>(
                  "[data-docx-wrapped-paragraph-root='true']"
                );
                window.requestAnimationFrame(() => {
                  const activeElement = document.activeElement;
                  if (
                    activeElement &&
                    (activeElement === currentTarget ||
                      wrappedParagraphRoot?.contains(activeElement))
                  ) {
                    return;
                  }
                  setActiveWrappedParagraphSession((current) =>
                    current && current.locationKey === locationKey
                      ? undefined
                      : current
                  );
                });
              }}
              style={{
                position: "absolute",
                left: inputAnchorRect.left,
                top: inputAnchorRect.top,
                width: 1,
                minWidth: 1,
                height: Math.max(lineHeightPx, inputAnchorRect.height),
                opacity: 0,
                color: "transparent",
                caretColor: "transparent",
                background: "transparent",
                border: "none",
                padding: 0,
                margin: 0,
                resize: "none",
                overflow: "clip",
                outline: "none",
                zIndex: 5,
              }}
            />
          ) : null}
        </span>
      </span>
    );
  };

  const renderInteractiveParagraphRuns = React.useCallback(
    (
      paragraph: ParagraphNode,
      keyPrefix: string,
      location: ParagraphLocation,
      options?: {
    pageFlowTopPx?: number;
    pageFlowForeignExclusions?: PretextExclusionRect[];
    pageLayout?: DocumentLayoutMetrics;
    suppressLikelyFullPageCoverImageKeys?: Set<string>;
  }
): React.ReactNode => {
      const nodes: React.ReactNode[] = [];
      const checkboxChoiceRow = paragraphLooksLikeCheckboxChoiceRow(paragraph);
      const fallbackTabWidthPx = checkboxChoiceRow
        ? checkboxChoiceRowTabWidthPx(paragraph)
        : DEFAULT_TAB_STOP_PX;
      const tabStopPositionsPx = (paragraph.style?.tabStops ?? [])
        .map((tabStopEntry) => twipsToPixels(tabStopEntry.positionTwips))
        .filter(
          (value): value is number =>
            Number.isFinite(value) && (value as number) > 0
        )
        .sort((left, right) => left - right);
      const compactTabStopFieldLayout = tabStopPositionsPx.length > 0;
      let approximateLineWidthPx = 0;
      const trackTextAdvance = (
        text: string,
        style?: TextRunNode["style"] | FormFieldRunNode["style"]
      ): void => {
        approximateLineWidthPx = updateEstimatedLineWidthPxForText(
          approximateLineWidthPx,
          text,
          style
        );
      };
      const trackInlineAdvance = (widthPx: number): void => {
        approximateLineWidthPx += Math.max(0, Math.round(widthPx));
      };
      const resolveNextTabWidthPx = (): number =>
        resolveTabSpacerWidthPx(
          tabStopPositionsPx,
          approximateLineWidthPx,
          fallbackTabWidthPx
        );
      const appendInteractiveTextWithSoftBreakControl = (
        keySeed: string,
        text: string,
        style: React.CSSProperties,
        measureStyle?: TextRunNode["style"] | FormFieldRunNode["style"]
      ): void => {
        const shouldControlSoftBreakStretch =
          paragraph.style?.align === "justify" && text.includes("\n");
        if (!shouldControlSoftBreakStretch) {
          nodes.push(
            <span key={keySeed} style={style}>
              {text}
            </span>
          );
          trackTextAdvance(text, measureStyle);
          return;
        }

        const segments = text.split("\n");
        segments.forEach((segment, segmentIndex) => {
          const isLastSegment = segmentIndex === segments.length - 1;
          if (segmentIndex > 0) {
            nodes.push(<br key={`${keySeed}-soft-break-${segmentIndex}`} />);
          }

          if (segment.length > 0) {
            nodes.push(
              <span
                key={`${keySeed}-segment-${segmentIndex}`}
                style={
                  isLastSegment
                    ? {
                        ...style,
                        display: "inline-block",
                        maxWidth: "100%",
                        whiteSpace: "pre-wrap",
                        verticalAlign: "baseline",
                      }
                    : style
                }
              >
                {segment}
              </span>
            );
          }

          trackTextAdvance(segment, measureStyle);
          if (!isLastSegment) {
            approximateLineWidthPx = 0;
          }
        });
      };
      const noteMarkerIndexes = {
        footnote: new Map<number, number>(),
        endnote: new Map<number, number>(),
      };
      editor.model.metadata.footnotes?.forEach((note, index) => {
        noteMarkerIndexes.footnote.set(note.id, index + 1);
      });
      editor.model.metadata.endnotes?.forEach((note, index) => {
        noteMarkerIndexes.endnote.set(note.id, index + 1);
      });

      const numberingLabel = paragraphNumberingLabels.get(
        paragraphLocationKey(location)
      );
      const paragraphPageLayout = options?.pageLayout ?? documentLayout;
      const paragraphContentWidthPx =
        location.kind === "paragraph"
          ? pageContentWidthPxByNodeIndex.get(location.nodeIndex) ??
            documentContentWidthPx
          : documentContentWidthPx;
      const paragraphContainsLikelyFullPageCoverImage = paragraph.children.some(
        (child) =>
          child.type === "image" &&
          shouldRenderAbsoluteFloatingImage(child) &&
          likelyFullPageCoverImageRelativeToContentBox(
            child,
            paragraphContentWidthPx,
            paragraphPageLayout.pageHeightPx -
              paragraphPageLayout.marginsPx.top -
              paragraphPageLayout.marginsPx.bottom
          )
      );
      const interactiveBodyFloatingPageOriginPx =
        paragraphNeedsPageAnchoredAbsolutePositioningContext(paragraph) ||
        paragraphNeedsPageWidthAnchorHost(paragraph) ||
        paragraphContainsLikelyFullPageCoverImage
          ? {
              left: 0,
              top: 0,
              marginLeft: paragraphPageLayout.marginsPx.left,
              marginTop: paragraphPageLayout.marginsPx.top,
              columnLeft: paragraphPageLayout.marginsPx.left,
              columnTop: paragraphPageLayout.marginsPx.top,
              pageWidth: paragraphPageLayout.pageWidthPx,
            }
          : undefined;
      const paragraphRenderTextWidthPx = paragraphAvailableTextWidthPx(
        paragraph,
        paragraphContentWidthPx,
        editor.model.metadata.numberingDefinitions
      );
      const paragraphPageFlowTopPx = Math.max(
        0,
        Math.round(options?.pageFlowTopPx ?? 0)
      );
      const bodyParagraphOriginLeftPx = interactiveBodyFloatingPageOriginPx
        ? paragraphPageLayout.marginsPx.left
        : undefined;
      // The host box is only remapped to the page-surface top for cover
      // overlay hosts; ordinary anchor hosts keep their flow position, so a
      // paragraph-relative offset is already in the host's own frame.
      const bodyParagraphOriginTopPx = interactiveBodyFloatingPageOriginPx
        ? paragraphActsAsPageAnchoredCoverOverlayHost(
            paragraph,
            paragraphPageLayout
          )
          ? paragraphPageLayout.marginsPx.top + paragraphPageFlowTopPx
          : 0
        : undefined;
      const resizedWidthPxByImageIndex = new Map<number, number>();
      const resizedHeightPxByImageIndex = new Map<number, number>();
      const movePreviewByImageIndex = new Map<
        number,
        {
          deltaX: number;
          deltaY: number;
          baseLeftPx?: number;
          baseTopPx?: number;
        }
      >();
      paragraph.children.forEach((child, childIndex) => {
        if (child.type !== "image") {
          return;
        }

        const imageLocation: DocxImageLocation = { ...location, childIndex };
        const imageKey = imageLocationKey(imageLocation);
        const preview =
          resizePreview?.imageKey === imageKey ? resizePreview : undefined;
        const movePreview =
          floatingMovePreview?.imageKey === imageKey
            ? floatingMovePreview
            : undefined;
        let widthPx = preview?.widthPx ?? child.widthPx;
        let heightPx = preview?.heightPx ?? child.heightPx;
        ({ widthPx, heightPx } = resolvePageSpanningAbsoluteFloatingDimensions(
          child,
          widthPx,
          heightPx,
          interactiveBodyFloatingPageOriginPx
        ));
        if (Number.isFinite(widthPx)) {
          resizedWidthPxByImageIndex.set(
            childIndex,
            Math.round(widthPx as number)
          );
        }
        if (Number.isFinite(heightPx)) {
          resizedHeightPxByImageIndex.set(
            childIndex,
            Math.round(heightPx as number)
          );
        }
        if (movePreview) {
          movePreviewByImageIndex.set(childIndex, {
            deltaX: Math.round(movePreview.deltaX),
            deltaY: Math.round(movePreview.deltaY),
            ...(Number.isFinite(movePreview.baseLeftPx)
              ? { baseLeftPx: Math.round(movePreview.baseLeftPx as number) }
              : undefined),
            ...(Number.isFinite(movePreview.baseTopPx)
              ? { baseTopPx: Math.round(movePreview.baseTopPx as number) }
              : undefined),
          });
        }
      });
      const previewParagraph = applyWrappedFloatingInteractionPreviewToParagraph(
        paragraph,
        location,
        paragraphRenderTextWidthPx,
        {
          floatingMovePreview,
          resizePreview,
        }
      );
      const anchoredTabLayout = paragraphAnchoredTabLayout(paragraph);
      const useSpecialTabLayout =
        paragraphUsesTabLeaders(paragraph) ||
        anchoredTabLayout === "center-right" ||
        anchoredTabLayout === "center" ||
        anchoredTabLayout === "right";
      const hasFixedPositionWrappedImage = paragraph.children.some(
        (child) =>
          child.type === "image" && isFixedPositionWrappedFloatingImage(child)
      );
      // Commented paragraphs render through the markup-aware run path so the
      // comment range highlight applies (same static-run path special tab
      // layouts already use in edit mode).
      const paragraphHasCommentAnchors =
        commentsEnabled &&
        /commentRange|commentReference/i.test(paragraph.sourceXml ?? "");
      if (
        (trackedChangesEnabled ||
          useSpecialTabLayout ||
          paragraphHasCommentAnchors) &&
        !hasFixedPositionWrappedImage
      ) {
        return renderParagraphRuns(
          paragraph,
          keyPrefix,
          documentContentTheme,
          numberingLabel,
          scrollToBookmark,
          interactiveBodyFloatingPageOriginPx,
          noteMarkerIndexes,
          undefined,
          undefined,
          {
            showTrackedChanges: trackedChangesEnabled,
            showCommentHighlights: commentsEnabled,
            numberingDefinitions: editor.model.metadata.numberingDefinitions,
            tocLinkColorByLevel,
            paragraphOriginLeftPx: bodyParagraphOriginLeftPx,
            paragraphOriginTopPx: bodyParagraphOriginTopPx,
          }
        );
      }

      const paragraphLineHeightPx = estimateParagraphLineHeightPx(paragraph);
      const manualDualWrappedLayout = !numberingLabel
        ? resolveParagraphDualWrappedTextLayout(
            paragraph,
            paragraphRenderTextWidthPx,
            paragraphLineHeightPx,
            {
              widthPxByImageIndex: resizedWidthPxByImageIndex,
              heightPxByImageIndex: resizedHeightPxByImageIndex,
              paragraphTopPx: paragraphPageFlowTopPx,
              pageMarginTopPx: paragraphPageLayout.marginsPx.top,
              foreignExclusions: options?.pageFlowForeignExclusions,
              movePreviewByImageIndex,
              allowNegativeImageTop: movePreviewByImageIndex.size > 0,
            }
          )
        : undefined;
      const foreignOnlyWrappedLayout =
        !numberingLabel && !manualDualWrappedLayout
          ? resolveParagraphForeignOnlyWrappedTextLayout(
              paragraph,
              paragraphRenderTextWidthPx,
              paragraphLineHeightPx,
              options?.pageFlowForeignExclusions ?? []
            )
          : undefined;
      const activeDualWrappedLayout =
        manualDualWrappedLayout ?? foreignOnlyWrappedLayout;
      if (activeDualWrappedLayout) {
        const manualGeometryImageIndexes = new Set(
          activeDualWrappedLayout.geometries.map(
            (geometry) => geometry.imageIndex
          )
        );
        const renderManualFragment = (
          fragment: PretextLineFragment,
          lineIndex: number
        ): React.ReactNode => {
          const pieces = activeDualWrappedLayout.source.runs
            .map((run) => {
              const overlapStart = Math.max(
                fragment.startOffset,
                run.startOffset
              );
              const overlapEnd = Math.min(fragment.endOffset, run.endOffset);
              if (overlapStart >= overlapEnd) {
                return undefined;
              }

              if (run.kind === "image" && run.image) {
                if (overlapStart !== run.startOffset) {
                  return undefined;
                }

                const renderableImageSrc =
                  syntheticTextBoxSvg(
                    run.image,
                    undefined,
                    undefined,
                    undefined,
                    undefined
                  ) ?? resolveRenderableImageSource(run.image);
                if (!renderableImageSrc) {
                  return undefined;
                }

                return (
                  <img
                    key={`${keyPrefix}-dual-line-image-${lineIndex}-${run.key}-${overlapStart}`}
                    src={renderableImageSrc}
                    alt={run.image.alt ?? "DOCX image"}
                    draggable={false}
                    style={{
                      width: run.image.widthPx
                        ? `${run.image.widthPx}px`
                        : undefined,
                      height: run.image.heightPx
                        ? `${run.image.heightPx}px`
                        : undefined,
                      verticalAlign: "middle",
                      display: "inline-block",
                      filter: appendCssFilters(
                        run.image.cssFilter,
                        documentContentFilter
                      ),
                      opacity: run.image.cssOpacity,
                    }}
                  />
                );
              }

              const slice = run.text.slice(
                overlapStart - run.startOffset,
                overlapEnd - run.startOffset
              );
              if (!slice) {
                return undefined;
              }

              const textStyle = run.link
                ? {
                    ...linkStyleToCss(run.style, documentContentTheme),
                    whiteSpace: "pre",
                  }
                : {
                    ...runStyleToCss(run.style, documentContentTheme),
                    whiteSpace: "pre",
                  };

              return run.link ? (
                <a
                  key={`${keyPrefix}-dual-line-${lineIndex}-${run.key}-${overlapStart}`}
                  href={run.link}
                  target={run.link.startsWith("#") ? undefined : "_blank"}
                  rel={
                    run.link.startsWith("#") ? undefined : "noreferrer noopener"
                  }
                  onMouseDown={(event) => {
                    if (!run.link?.startsWith("#")) {
                      return;
                    }

                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onClick={(event) => {
                    if (!run.link?.startsWith("#")) {
                      return;
                    }

                    event.preventDefault();
                    event.stopPropagation();
                    scrollToBookmark(run.link.slice(1));
                  }}
                  style={textStyle}
                >
                  {slice}
                </a>
              ) : (
                <span
                  key={`${keyPrefix}-dual-line-${lineIndex}-${run.key}-${overlapStart}`}
                  style={textStyle}
                >
                  {slice}
                </span>
              );
            })
            .filter(Boolean);

          return (
            <span
              key={`${keyPrefix}-dual-fragment-${lineIndex}-${fragment.startOffset}-${fragment.endOffset}`}
              style={{
                position: "absolute",
                left: fragment.x,
                top: activeDualWrappedLayout.layout.lines[lineIndex]?.y ?? 0,
                display: "inline-block",
                whiteSpace: "pre",
                lineHeight: `${activeDualWrappedLayout.lineHeightPx}px`,
              }}
            >
              {pieces}
            </span>
          );
        };

        const renderManualImage = (
          geometry: DualWrappedFloatingImageGeometry
        ): React.ReactNode => {
          const manualImage = previewParagraph.children[geometry.imageIndex];
          if (manualImage?.type !== "image") {
            return null;
          }

          const manualImageLocation: DocxImageLocation = {
            ...location,
            childIndex: geometry.imageIndex,
          };
          const manualImageKey = imageLocationKey(manualImageLocation);
          const isSelectedImage = selectedImage
            ? imageLocationKey(selectedImage) === manualImageKey
            : false;
          const widthPx = geometry.imageWidthPx;
          const heightPx = geometry.imageHeightPx;
          const renderableImageSrc =
            syntheticTextBoxSvg(
              manualImage,
              undefined,
              undefined,
              undefined,
              undefined
            ) ?? resolveRenderableImageSource(manualImage);
          const showsUnsupportedFallback =
            imageUsesPlaceholderFallback(manualImage) ||
            (manualImage.src && !renderableImageSrc);
          const isWrappedFloatingImage =
            shouldRenderWrappedFloatingImage(manualImage) ||
            !manualImage.floating;

          return (
            <span
              key={`${keyPrefix}-manual-image-${geometry.imageIndex}`}
              contentEditable={false}
              data-docx-image-location={manualImageKey}
              style={{
                display: "inline-block",
                position: "absolute",
                left: geometry.imageLeftPx,
                top: geometry.imageTopPx,
                width: widthPx ? `${widthPx}px` : undefined,
                height: heightPx ? `${heightPx}px` : undefined,
                zIndex: 3,
              }}
              onPointerDown={(event) =>
                isReadOnly
                  ? undefined
                  : beginFloatingImageMove(
                      event,
                      manualImageLocation,
                      manualImage,
                      isWrappedFloatingImage,
                      false
                    )
              }
              onClick={(event) => {
                event.stopPropagation();
                if (!isReadOnly) {
                  setSelectedImage(manualImageLocation);
                }
              }}
              onContextMenu={(event) => {
                if (isReadOnly) {
                  return;
                }

                event.preventDefault();
                event.stopPropagation();
                clearTableCellSelection();
                setSelectedSectionImageKey(undefined);
                setSelectedImage(manualImageLocation);
                openContextMenu(
                  {
                    kind: "image",
                    activeTextRange: resolveActiveRangeFromDomSelection(),
                    location: cloneTextRangeLocation(
                      imageLocationToParagraphLocation(manualImageLocation)
                    ),
                    image: {
                      location: manualImageLocation,
                      floating: manualImage.floating
                        ? { ...manualImage.floating }
                        : undefined,
                    },
                  },
                  {
                    x: event.clientX,
                    y: event.clientY,
                  }
                );
              }}
            >
              {renderableImageSrc && !showsUnsupportedFallback ? (
                (() => {
                  const cropLayout = imageCropLayout(
                    manualImage,
                    widthPx,
                    heightPx
                  );
                  const imageVisualStyle: React.CSSProperties = {
                    filter: appendCssFilters(
                      manualImage.cssFilter,
                      documentContentFilter
                    ),
                    opacity: manualImage.cssOpacity,
                  };
                  const imageTransformStyle = resolveImageRenderTransformStyle(
                    manualImage,
                    {
                      frameWidthPx: cropLayout?.frameWidthPx ?? widthPx,
                      frameHeightPx: cropLayout?.frameHeightPx ?? heightPx,
                      baseTransform: cropLayout
                        ? `translate(${-cropLayout.offsetXPx}px, ${-cropLayout.offsetYPx}px)`
                        : undefined,
                    }
                  );

                  if (cropLayout) {
                    return (
                      <span
                        style={{
                          width: `${cropLayout.frameWidthPx}px`,
                          height: `${cropLayout.frameHeightPx}px`,
                          overflow: "clip",
                          display: "block",
                        }}
                      >
                        <img
                          src={renderableImageSrc}
                          alt={manualImage.alt ?? "DOCX image"}
                          draggable={false}
                          style={{
                            width: `${cropLayout.imageWidthPx}px`,
                            height: `${cropLayout.imageHeightPx}px`,
                            maxWidth: "none",
                            verticalAlign: "middle",
                            display: "block",
                            cursor: isReadOnly ? "default" : "move",
                            ...imageVisualStyle,
                            ...imageTransformStyle,
                          }}
                        />
                      </span>
                    );
                  }

                  return (
                    <img
                      src={renderableImageSrc}
                      alt={manualImage.alt ?? "DOCX image"}
                      draggable={false}
                      style={{
                        width: widthPx ? `${widthPx}px` : undefined,
                        height: heightPx ? `${heightPx}px` : undefined,
                        verticalAlign: "middle",
                        display: "block",
                        cursor: isReadOnly ? "default" : "move",
                        ...imageVisualStyle,
                        ...resolveImageRenderTransformStyle(manualImage, {
                          frameWidthPx: widthPx,
                          frameHeightPx: heightPx,
                        }),
                      }}
                    />
                  );
                })()
              ) : showsUnsupportedFallback ? (
                <span
                  role="img"
                  aria-label={manualImage.alt ?? "DOCX image"}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: widthPx ? `${widthPx}px` : "1.8em",
                    height: heightPx ? `${heightPx}px` : "1.8em",
                    minWidth: 16,
                    minHeight: 16,
                    border: "1px solid #d1d5db",
                    borderRadius: 3,
                    backgroundColor: "#ffffff",
                    color: "#0f172a",
                    fontSize:
                      (widthPx ?? 0) <= 56 && (heightPx ?? 0) <= 56 ? 12 : 10,
                    fontWeight: 700,
                    textTransform: "lowercase",
                    fontFamily: "Arial, sans-serif",
                    lineHeight: 1,
                  }}
                >
                  {unsupportedImageFallbackLabel(
                    manualImage,
                    widthPx,
                    heightPx
                  )}
                </span>
              ) : null}

              {!isReadOnly && isSelectedImage ? (
                <>
                  {[
                    {
                      key: "top-left",
                      left: -5,
                      top: -5,
                      cursor: "nwse-resize",
                      xDirection: -1,
                      yDirection: -1,
                    },
                    {
                      key: "top-right",
                      right: -5,
                      top: -5,
                      cursor: "nesw-resize",
                      xDirection: 1,
                      yDirection: -1,
                    },
                    {
                      key: "bottom-left",
                      left: -5,
                      bottom: -5,
                      cursor: "nesw-resize",
                      xDirection: -1,
                      yDirection: 1,
                    },
                    {
                      key: "bottom-right",
                      right: -5,
                      bottom: -5,
                      cursor: "nwse-resize",
                      xDirection: 1,
                      yDirection: 1,
                    },
                  ].map((handle) => (
                    <span
                      key={`${keyPrefix}-dual-${geometry.imageIndex}-${handle.key}`}
                      contentEditable={false}
                      data-image-resize-handle="true"
                      style={{
                        position: "absolute",
                        width: 10,
                        height: 10,
                        borderRadius: 3,
                        border: "1px solid #d4d4d8",
                        backgroundColor: "#ffffff",
                        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.14)",
                        cursor: handle.cursor,
                        ...(handle.left !== undefined
                          ? { left: handle.left }
                          : undefined),
                        ...(handle.right !== undefined
                          ? { right: handle.right }
                          : undefined),
                        ...(handle.top !== undefined
                          ? { top: handle.top }
                          : undefined),
                        ...(handle.bottom !== undefined
                          ? { bottom: handle.bottom }
                          : undefined),
                      }}
                      onPointerDown={(event) => {
                        beginImageResize(
                          event,
                          manualImageLocation,
                          widthPx,
                          heightPx,
                          handle.xDirection as -1 | 1,
                          handle.yDirection as -1 | 1
                        );
                      }}
                    />
                  ))}
                </>
              ) : null}
            </span>
          );
        };

        const renderManualPageFixedWrappedImage = (
          geometry: DualWrappedFloatingImageGeometry
        ): React.ReactNode => {
          const manualImage = previewParagraph.children[geometry.imageIndex];
          if (manualImage?.type !== "image") {
            return null;
          }

          const manualImageLocation: DocxImageLocation = {
            ...location,
            childIndex: geometry.imageIndex,
          };
          const manualImageKey = imageLocationKey(manualImageLocation);
          const isSelectedImage = selectedImage
            ? imageLocationKey(selectedImage) === manualImageKey
            : false;
          const movePreview = movePreviewByImageIndex.get(geometry.imageIndex);
          const widthPx = geometry.imageWidthPx;
          const heightPx = geometry.imageHeightPx;
          const floatingStyle = likelyFullPageCoverImageRelativeToContentBox(
            manualImage,
            documentContentWidthPx,
            paragraphPageLayout.pageHeightPx -
              paragraphPageLayout.marginsPx.top -
              paragraphPageLayout.marginsPx.bottom
          )
            ? fullPageCoverAbsoluteFloatingImageStyle(
                manualImage,
                paragraphPageLayout,
                {
                  deltaX: movePreview?.deltaX ?? 0,
                  deltaY: movePreview?.deltaY ?? 0,
                  anchorToPageSurface: true,
                }
              )
            : absoluteFloatingImageStyle(manualImage, {
                pageOriginLeft: interactiveBodyFloatingPageOriginPx?.left ?? 0,
                pageOriginTop: interactiveBodyFloatingPageOriginPx?.top ?? 0,
                marginOriginLeft:
                  interactiveBodyFloatingPageOriginPx?.marginLeft ?? 0,
                marginOriginTop:
                  interactiveBodyFloatingPageOriginPx?.marginTop ?? 0,
                columnOriginLeft:
                  interactiveBodyFloatingPageOriginPx?.columnLeft ?? 0,
                columnOriginTop:
                  interactiveBodyFloatingPageOriginPx?.columnTop ?? 0,
                paragraphOriginLeft: bodyParagraphOriginLeftPx,
                paragraphOriginTop: bodyParagraphOriginTopPx,
                deltaX: movePreview?.deltaX ?? 0,
                deltaY: movePreview?.deltaY ?? 0,
              });
          const renderableImageSrc =
            syntheticTextBoxSvg(
              manualImage,
              undefined,
              undefined,
              undefined,
              undefined
            ) ?? resolveRenderableImageSource(manualImage);
          const showsUnsupportedFallback =
            imageUsesPlaceholderFallback(manualImage) ||
            (manualImage.src && !renderableImageSrc);

          return (
            <span
              key={`${keyPrefix}-manual-fixed-wrap-image-${geometry.imageIndex}`}
              contentEditable={false}
              data-docx-image-location={manualImageKey}
              style={{
                display: "inline-block",
                position: "absolute",
                width: widthPx ? `${widthPx}px` : undefined,
                height: heightPx ? `${heightPx}px` : undefined,
                ...floatingStyle,
              }}
              onPointerDown={(event) =>
                isReadOnly
                  ? undefined
                  : beginFloatingImageMove(
                      event,
                      manualImageLocation,
                      manualImage,
                      true,
                      false
                    )
              }
              onClick={(event) => {
                event.stopPropagation();
                if (!isReadOnly) {
                  setSelectedImage(manualImageLocation);
                }
              }}
              onContextMenu={(event) => {
                if (isReadOnly) {
                  return;
                }

                event.preventDefault();
                event.stopPropagation();
                clearTableCellSelection();
                setSelectedSectionImageKey(undefined);
                setSelectedImage(manualImageLocation);
                openContextMenu(
                  {
                    kind: "image",
                    activeTextRange: resolveActiveRangeFromDomSelection(),
                    location: cloneTextRangeLocation(
                      imageLocationToParagraphLocation(manualImageLocation)
                    ),
                    image: {
                      location: manualImageLocation,
                      floating: manualImage.floating
                        ? { ...manualImage.floating }
                        : undefined,
                    },
                  },
                  {
                    x: event.clientX,
                    y: event.clientY,
                  }
                );
              }}
            >
              {renderableImageSrc && !showsUnsupportedFallback ? (
                <img
                  src={renderableImageSrc}
                  alt={manualImage.alt ?? "DOCX image"}
                  draggable={false}
                  style={{
                    width: widthPx ? `${widthPx}px` : undefined,
                    height: heightPx ? `${heightPx}px` : undefined,
                    maxWidth: "none",
                    verticalAlign: "middle",
                    display: "block",
                    cursor: isReadOnly ? "default" : "move",
                    filter: appendCssFilters(
                      manualImage.cssFilter,
                      documentContentFilter
                    ),
                    opacity: manualImage.cssOpacity,
                  }}
                />
              ) : showsUnsupportedFallback ? (
                <span
                  role="img"
                  aria-label={manualImage.alt ?? "DOCX image"}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: widthPx ? `${widthPx}px` : "1.8em",
                    height: heightPx ? `${heightPx}px` : "1.8em",
                    minWidth: 16,
                    minHeight: 16,
                    border: "1px solid #d1d5db",
                    borderRadius: 3,
                    backgroundColor: "#ffffff",
                    color: "#0f172a",
                    fontSize:
                      (widthPx ?? 0) <= 56 && (heightPx ?? 0) <= 56 ? 12 : 10,
                    fontWeight: 700,
                    textTransform: "lowercase",
                    fontFamily: "Arial, sans-serif",
                    lineHeight: 1,
                  }}
                >
                  {unsupportedImageFallbackLabel(
                    manualImage,
                    widthPx,
                    heightPx
                  )}
                </span>
              ) : null}

              {!isReadOnly && isSelectedImage ? (
                <>
                  {[
                    {
                      key: "top-left",
                      left: -5,
                      top: -5,
                      cursor: "nwse-resize",
                      xDirection: -1,
                      yDirection: -1,
                    },
                    {
                      key: "top-right",
                      right: -5,
                      top: -5,
                      cursor: "nesw-resize",
                      xDirection: 1,
                      yDirection: -1,
                    },
                    {
                      key: "bottom-left",
                      left: -5,
                      bottom: -5,
                      cursor: "nesw-resize",
                      xDirection: -1,
                      yDirection: 1,
                    },
                    {
                      key: "bottom-right",
                      right: -5,
                      bottom: -5,
                      cursor: "nwse-resize",
                      xDirection: 1,
                      yDirection: 1,
                    },
                  ].map((handle) => (
                    <span
                      key={`${keyPrefix}-manual-fixed-wrap-${geometry.imageIndex}-${handle.key}`}
                      contentEditable={false}
                      data-image-resize-handle="true"
                      style={{
                        position: "absolute",
                        width: 10,
                        height: 10,
                        borderRadius: 3,
                        border: "1px solid #d4d4d8",
                        backgroundColor: "#ffffff",
                        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.14)",
                        cursor: handle.cursor,
                        ...(handle.left !== undefined
                          ? { left: handle.left }
                          : undefined),
                        ...(handle.right !== undefined
                          ? { right: handle.right }
                          : undefined),
                        ...(handle.top !== undefined
                          ? { top: handle.top }
                          : undefined),
                        ...(handle.bottom !== undefined
                          ? { bottom: handle.bottom }
                          : undefined),
                      }}
                      onPointerDown={(event) => {
                        beginImageResize(
                          event,
                          manualImageLocation,
                          widthPx,
                          heightPx,
                          handle.xDirection as -1 | 1,
                          handle.yDirection as -1 | 1
                        );
                      }}
                    />
                  ))}
                </>
              ) : null}
            </span>
          );
        };

        const renderManualAbsoluteImage = (
          manualImage: ImageRunNode,
          childIndex: number
        ): React.ReactNode => {
          const manualImageLocation: DocxImageLocation = {
            ...location,
            childIndex,
          };
          const manualImageKey = imageLocationKey(manualImageLocation);
          const isSelectedImage = selectedImage
            ? imageLocationKey(selectedImage) === manualImageKey
            : false;
          const movePreview = movePreviewByImageIndex.get(childIndex);
          const widthPx =
            resizedWidthPxByImageIndex.get(childIndex) ?? manualImage.widthPx;
          const heightPx =
            resizedHeightPxByImageIndex.get(childIndex) ?? manualImage.heightPx;
          const floatingStyle = likelyFullPageCoverImageRelativeToContentBox(
            manualImage,
            documentContentWidthPx,
            paragraphPageLayout.pageHeightPx -
              paragraphPageLayout.marginsPx.top -
              paragraphPageLayout.marginsPx.bottom
          )
            ? fullPageCoverAbsoluteFloatingImageStyle(
                manualImage,
                paragraphPageLayout,
                {
                  deltaX: movePreview?.deltaX ?? 0,
                  deltaY: movePreview?.deltaY ?? 0,
                  anchorToPageSurface: true,
                }
              )
            : absoluteFloatingImageStyle(manualImage, {
                pageOriginLeft: interactiveBodyFloatingPageOriginPx?.left ?? 0,
                pageOriginTop: interactiveBodyFloatingPageOriginPx?.top ?? 0,
                marginOriginLeft:
                  interactiveBodyFloatingPageOriginPx?.marginLeft ?? 0,
                marginOriginTop:
                  interactiveBodyFloatingPageOriginPx?.marginTop ?? 0,
                columnOriginLeft:
                  interactiveBodyFloatingPageOriginPx?.columnLeft ?? 0,
                columnOriginTop:
                  interactiveBodyFloatingPageOriginPx?.columnTop ?? 0,
                paragraphOriginLeft: bodyParagraphOriginLeftPx,
                paragraphOriginTop: bodyParagraphOriginTopPx,
                deltaX: movePreview?.deltaX ?? 0,
                deltaY: movePreview?.deltaY ?? 0,
              });
          const renderableImageSrc =
            syntheticTextBoxSvg(
              manualImage,
              undefined,
              undefined,
              undefined,
              undefined
            ) ?? resolveRenderableImageSource(manualImage);
          const showsUnsupportedFallback =
            imageUsesPlaceholderFallback(manualImage) ||
            (manualImage.src && !renderableImageSrc);

          return (
            <span
              key={`${keyPrefix}-manual-absolute-${childIndex}`}
              contentEditable={false}
              data-docx-image-location={manualImageKey}
              style={{
                display: "inline-block",
                position: "absolute",
                width: widthPx ? `${widthPx}px` : undefined,
                height: heightPx ? `${heightPx}px` : undefined,
                ...floatingStyle,
              }}
              onPointerDown={(event) =>
                isReadOnly
                  ? undefined
                  : beginFloatingImageMove(
                      event,
                      manualImageLocation,
                      manualImage,
                      false,
                      true
                    )
              }
              onClick={(event) => {
                event.stopPropagation();
                if (!isReadOnly) {
                  setSelectedImage(manualImageLocation);
                }
              }}
              onContextMenu={(event) => {
                if (isReadOnly) {
                  return;
                }

                event.preventDefault();
                event.stopPropagation();
                clearTableCellSelection();
                setSelectedSectionImageKey(undefined);
                setSelectedImage(manualImageLocation);
                openContextMenu(
                  {
                    kind: "image",
                    activeTextRange: resolveActiveRangeFromDomSelection(),
                    location: cloneTextRangeLocation(
                      imageLocationToParagraphLocation(manualImageLocation)
                    ),
                    image: {
                      location: manualImageLocation,
                      floating: manualImage.floating
                        ? { ...manualImage.floating }
                        : undefined,
                    },
                  },
                  {
                    x: event.clientX,
                    y: event.clientY,
                  }
                );
              }}
            >
              {renderableImageSrc && !showsUnsupportedFallback ? (
                <img
                  src={renderableImageSrc}
                  alt={manualImage.alt ?? "DOCX image"}
                  draggable={false}
                  style={{
                    width: widthPx ? `${widthPx}px` : undefined,
                    height: heightPx ? `${heightPx}px` : undefined,
                    maxWidth: "none",
                    verticalAlign: "middle",
                    display: "block",
                    cursor: isReadOnly ? "default" : "move",
                    filter: appendCssFilters(
                      manualImage.cssFilter,
                      documentContentFilter
                    ),
                    opacity: manualImage.cssOpacity,
                  }}
                />
              ) : showsUnsupportedFallback ? (
                <span
                  role="img"
                  aria-label={manualImage.alt ?? "DOCX image"}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: widthPx ? `${widthPx}px` : "1.8em",
                    height: heightPx ? `${heightPx}px` : "1.8em",
                    minWidth: 16,
                    minHeight: 16,
                    border: "1px solid #d1d5db",
                    borderRadius: 3,
                    backgroundColor: "#ffffff",
                    color: "#0f172a",
                    fontSize:
                      (widthPx ?? 0) <= 56 && (heightPx ?? 0) <= 56 ? 12 : 10,
                    fontWeight: 700,
                    textTransform: "lowercase",
                    fontFamily: "Arial, sans-serif",
                    lineHeight: 1,
                  }}
                >
                  {unsupportedImageFallbackLabel(
                    manualImage,
                    widthPx,
                    heightPx
                  )}
                </span>
              ) : null}

              {!isReadOnly && isSelectedImage ? (
                <>
                  {[
                    {
                      key: "top-left",
                      left: -5,
                      top: -5,
                      cursor: "nwse-resize",
                      xDirection: -1,
                      yDirection: -1,
                    },
                    {
                      key: "top-right",
                      right: -5,
                      top: -5,
                      cursor: "nesw-resize",
                      xDirection: 1,
                      yDirection: -1,
                    },
                    {
                      key: "bottom-left",
                      left: -5,
                      bottom: -5,
                      cursor: "nesw-resize",
                      xDirection: -1,
                      yDirection: 1,
                    },
                    {
                      key: "bottom-right",
                      right: -5,
                      bottom: -5,
                      cursor: "nwse-resize",
                      xDirection: 1,
                      yDirection: 1,
                    },
                  ].map((handle) => (
                    <span
                      key={`${keyPrefix}-manual-absolute-${childIndex}-${handle.key}`}
                      contentEditable={false}
                      data-image-resize-handle="true"
                      style={{
                        position: "absolute",
                        width: 10,
                        height: 10,
                        borderRadius: 3,
                        border: "1px solid #d4d4d8",
                        backgroundColor: "#ffffff",
                        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.14)",
                        cursor: handle.cursor,
                        ...(handle.left !== undefined
                          ? { left: handle.left }
                          : undefined),
                        ...(handle.right !== undefined
                          ? { right: handle.right }
                          : undefined),
                        ...(handle.top !== undefined
                          ? { top: handle.top }
                          : undefined),
                        ...(handle.bottom !== undefined
                          ? { bottom: handle.bottom }
                          : undefined),
                      }}
                      onPointerDown={(event) => {
                        const resizeWidth =
                          widthPx ?? manualImage.widthPx ?? 240;
                        const resizeHeight =
                          heightPx ??
                          manualImage.heightPx ??
                          Math.round(resizeWidth * 0.62);
                        beginImageResize(
                          event,
                          manualImageLocation,
                          resizeWidth,
                          resizeHeight,
                          handle.xDirection as -1 | 1,
                          handle.yDirection as -1 | 1
                        );
                      }}
                    />
                  ))}
                </>
              ) : null}
            </span>
          );
        };

        const activeSession =
          activeWrappedParagraphSession?.locationKey ===
          paragraphLocationKey(location)
            ? activeWrappedParagraphSession
            : undefined;
        const manualPageFixedWrappedImageIndexes = new Set(
          activeDualWrappedLayout.geometries
            .filter((geometry) => {
              const child = previewParagraph.children[geometry.imageIndex];
              return (
                child?.type === "image" &&
                isPageOrMarginAnchoredWrappedFloatingImage(child)
              );
            })
            .map((geometry) => geometry.imageIndex)
        );
        const activeWrappedSource = activeSession
          ? buildSyntheticPretextLayoutSource(
              activeSession.text,
              firstRunStyle(previewParagraph)
            )
          : activeDualWrappedLayout.source;
        const activeWrappedExclusions = [
          ...(options?.pageFlowForeignExclusions ?? []),
          ...activeDualWrappedLayout.geometries.map(
            (geometry) => geometry.exclusion
          ),
        ];
        const activeWrappedLayout = activeSession
          ? layoutParagraphPretextSource(
              previewParagraph,
              activeWrappedSource,
              paragraphRenderTextWidthPx,
              activeDualWrappedLayout.lineHeightPx,
              activeWrappedExclusions
            ) ?? activeDualWrappedLayout.layout
          : activeDualWrappedLayout.layout;

        return renderWrappedPretextParagraph({
          location,
          keyPrefix: `${keyPrefix}-dual-wrap-manual`,
          source: activeWrappedSource,
          layout: activeWrappedLayout,
          lineHeightPx: activeDualWrappedLayout.lineHeightPx,
          blockHeightPx: resolveDualWrapParagraphRenderBlockHeightPx(
            activeWrappedLayout,
            activeDualWrappedLayout.geometries
          ),
          obstacleNodes: (
            <>
              {activeDualWrappedLayout.geometries.map((geometry) =>
                manualPageFixedWrappedImageIndexes.has(geometry.imageIndex)
                  ? null
                  : renderManualImage(geometry)
              )}
              {previewParagraph.children.map((child, childIndex) =>
                child.type === "image" &&
                !manualGeometryImageIndexes.has(childIndex) &&
                shouldRenderAbsoluteFloatingImage(child) &&
                !isPageOrMarginAnchoredAbsoluteFloatingImage(child)
                  ? renderManualAbsoluteImage(child, childIndex)
                  : null
              )}
            </>
          ),
          pageAbsoluteObstacleNodes: (
            <>
              {activeDualWrappedLayout.geometries.map((geometry) =>
                manualPageFixedWrappedImageIndexes.has(geometry.imageIndex)
                  ? renderManualPageFixedWrappedImage(geometry)
                  : null
              )}
              {previewParagraph.children.map((child, childIndex) =>
                child.type === "image" &&
                !manualGeometryImageIndexes.has(childIndex) &&
                shouldRenderAbsoluteFloatingImage(child) &&
                isPageOrMarginAnchoredAbsoluteFloatingImage(child)
                  ? renderManualAbsoluteImage(child, childIndex)
                  : null
              )}
            </>
          ),
        });
      }

      if (numberingLabel) {
        const numberingTextStyle = numberingLabel.style
          ? runStyleToCss(numberingLabel.style, documentContentTheme)
          : undefined;
        nodes.push(
          <span
            key={`${keyPrefix}-numbering`}
            contentEditable={false}
            data-docx-numbering-label="true"
            style={numberingMarkerStyle(
              paragraph,
              editor.model.metadata.numberingDefinitions,
              numberingLabel,
              numberingTextStyle,
              documentContentTheme
            )}
          >
            {numberingLabel.imageSrc ? (
              <>
                <img
                  src={numberingLabel.imageSrc}
                  alt=""
                  aria-hidden="true"
                  draggable={false}
                  style={{
                    display: "inline-block",
                    verticalAlign: "text-bottom",
                    width: numberingLabel.imageWidthPx ?? 12,
                    height: numberingLabel.imageHeightPx ?? 12,
                    marginRight: 2,
                  }}
                />
                {numberingLabel.trailingText ?? ""}
              </>
            ) : (
              numberingLabel.text ?? ""
            )}
          </span>
        );
        if (numberingLabel.imageSrc) {
          trackInlineAdvance(numberingLabel.imageWidthPx ?? 12);
          trackTextAdvance(
            numberingLabel.trailingText ?? "",
            numberingLabel.style
          );
        } else {
          trackTextAdvance(numberingLabel.text ?? "", numberingLabel.style);
        }
      }
      nodes.push(
        renderImageDropZone(
          { ...location, childIndex: 0 },
          `${keyPrefix}-drop-0`
        )
      );
      let hasDualWrapSpacer = false;

      previewParagraph.children.forEach((child, childIndex) => {
        const runKey = `${keyPrefix}-run-${childIndex}`;

        if (child.type === "image") {
          const coverImageKey = fullPageCoverImageRenderKey(
            nodeIndexFromParagraphLocation(location),
            childIndex
          );
          const imageLocation: DocxImageLocation = { ...location, childIndex };
          const imageKey = imageLocationKey(imageLocation);
          const isSelectedImage = selectedImage
            ? imageLocationKey(selectedImage) === imageKey
            : false;
          const behavesAsDecorativeBehindTextBackground =
            imageBehavesAsDecorativeBehindTextBackground(
              child,
              previewParagraph
            );
          const preview =
            resizePreview?.imageKey === imageKey ? resizePreview : undefined;
          const movePreview =
            floatingMovePreview?.imageKey === imageKey
              ? floatingMovePreview
              : undefined;
          const isWrappedFloatingImage =
            shouldRenderWrappedFloatingImage(child);
          const isAbsoluteFloatingImage =
            shouldRenderAbsoluteFloatingImage(child);
          const isLikelyFullPageCoverImage =
            isAbsoluteFloatingImage &&
            likelyFullPageCoverImageRelativeToContentBox(
              child,
              documentContentWidthPx,
              paragraphPageLayout.pageHeightPx -
                paragraphPageLayout.marginsPx.top -
                paragraphPageLayout.marginsPx.bottom
            );
          if (
            isLikelyFullPageCoverImage &&
            options?.suppressLikelyFullPageCoverImageKeys?.has(coverImageKey)
          ) {
            return;
          }
          const dualWrapExclusionLayout = isWrappedFloatingImage
            ? wrappedFloatingImageDualExclusionLayout(child, {
                containerWidthPx: paragraphRenderTextWidthPx,
                deltaX: movePreview?.deltaX ?? 0,
                deltaY: movePreview?.deltaY ?? 0,
              })
            : undefined;
          let widthPx = preview?.widthPx ?? child.widthPx;
          let heightPx = preview?.heightPx ?? child.heightPx;
          ({ widthPx, heightPx } =
            resolvePageSpanningAbsoluteFloatingDimensions(
              child,
              widthPx,
              heightPx,
              interactiveBodyFloatingPageOriginPx
            ));
          const floatingStyle: React.CSSProperties = isWrappedFloatingImage
            ? dualWrapExclusionLayout?.imageStyle ??
              wrappedFloatingImageStyle(child, {
                containerWidthPx: paragraphRenderTextWidthPx,
                deltaX: movePreview?.deltaX ?? 0,
                deltaY: movePreview?.deltaY ?? 0,
              })
            : isAbsoluteFloatingImage
            ? isLikelyFullPageCoverImage
              ? fullPageCoverAbsoluteFloatingImageStyle(
                  child,
                  paragraphPageLayout,
                  {
                    deltaX: movePreview?.deltaX ?? 0,
                    deltaY: movePreview?.deltaY ?? 0,
                    anchorToPageSurface: true,
                  }
                )
              : absoluteFloatingImageStyle(child, {
                  pageOriginLeft:
                    interactiveBodyFloatingPageOriginPx?.left ?? 0,
                  pageOriginTop: interactiveBodyFloatingPageOriginPx?.top ?? 0,
                  marginOriginLeft:
                    interactiveBodyFloatingPageOriginPx?.marginLeft ?? 0,
                  marginOriginTop:
                    interactiveBodyFloatingPageOriginPx?.marginTop ?? 0,
                  columnOriginLeft:
                    interactiveBodyFloatingPageOriginPx?.columnLeft ?? 0,
                  columnOriginTop:
                    interactiveBodyFloatingPageOriginPx?.columnTop ?? 0,
                  paragraphOriginLeft: bodyParagraphOriginLeftPx,
                  paragraphOriginTop: bodyParagraphOriginTopPx,
                  deltaX: movePreview?.deltaX ?? 0,
                  deltaY: movePreview?.deltaY ?? 0,
                })
            : movePreview
            ? {
                transform: `translate(${movePreview.deltaX}px, ${movePreview.deltaY}px)`,
                position: "relative",
                zIndex: 4,
              }
            : {};

          const renderableImageSrc =
            syntheticTextBoxSvg(
              child,
              undefined,
              undefined,
              undefined,
              undefined
            ) ?? resolveRenderableImageSource(child);
          const isLiveSyntheticTextBox =
            child.syntheticTextBox &&
            !syntheticTextBoxContainsPictureLayer(child);
          const syntheticTextBoxParagraphs = isLiveSyntheticTextBox
            ? resolveSyntheticTextBoxParagraphs(child)
            : undefined;
          const syntheticTextBoxText = isLiveSyntheticTextBox
            ? syntheticTextBoxTextValue(child) ?? ""
            : "";
          const syntheticTextBoxFrame = isLiveSyntheticTextBox
            ? syntheticTextBoxFrameStyleFromRunXml(child.sourceXml ?? "")
            : undefined;
          const showsUnsupportedFallback =
            imageUsesPlaceholderFallback(child) ||
            (child.src && !renderableImageSrc);
          const liftLiveSyntheticTextBoxAboveArtwork =
            isLiveSyntheticTextBox && child.floating?.behindDocument === true;
          const isBehindTextAbsoluteImage =
            isAbsoluteFloatingImage &&
            child.floating?.behindDocument === true &&
            !liftLiveSyntheticTextBoxAboveArtwork;
          const imageShouldFillFrame = isLikelyFullPageCoverImage;
          const imageFrameStyle: React.CSSProperties = {
            display: "inline-block",
            position:
              isAbsoluteFloatingImage || Boolean(dualWrapExclusionLayout)
                ? "absolute"
                : "relative",
            verticalAlign: "middle",
            marginInline:
              isWrappedFloatingImage || isAbsoluteFloatingImage ? undefined : 0,
            width:
              isWrappedFloatingImage || isAbsoluteFloatingImage
                ? widthPx
                  ? `${widthPx}px`
                  : undefined
                : undefined,
            height:
              isWrappedFloatingImage || isAbsoluteFloatingImage
                ? heightPx
                  ? `${heightPx}px`
                  : undefined
                : undefined,
          };
          const resizeHandles: Array<{
            key: string;
            left?: number;
            right?: number;
            top?: number;
            bottom?: number;
            cursor: string;
            xDirection: -1 | 1;
            yDirection: -1 | 1;
          }> = [
            {
              key: "top-left",
              left: -5,
              top: -5,
              cursor: "nwse-resize",
              xDirection: -1,
              yDirection: -1,
            },
            {
              key: "top-right",
              right: -5,
              top: -5,
              cursor: "nesw-resize",
              xDirection: 1,
              yDirection: -1,
            },
            {
              key: "bottom-left",
              left: -5,
              bottom: -5,
              cursor: "nesw-resize",
              xDirection: -1,
              yDirection: 1,
            },
            {
              key: "bottom-right",
              right: -5,
              bottom: -5,
              cursor: "nwse-resize",
              xDirection: 1,
              yDirection: 1,
            },
          ];

          if (dualWrapExclusionLayout) {
            hasDualWrapSpacer = true;
            nodes.push(
              <span
                key={`${runKey}-dual-left-wrap`}
                contentEditable={false}
                aria-hidden="true"
                style={dualWrapExclusionLayout.leftSpacerStyle}
              />
            );
            nodes.push(
              <span
                key={`${runKey}-dual-right-wrap`}
                contentEditable={false}
                aria-hidden="true"
                style={dualWrapExclusionLayout.rightSpacerStyle}
              />
            );
          }

          nodes.push(
            <span
              key={runKey}
              contentEditable={false}
              data-docx-image-location={imageKey}
              style={{
                ...imageFrameStyle,
                ...floatingStyle,
                ...(liftLiveSyntheticTextBoxAboveArtwork
                  ? { zIndex: syntheticTextBoxForegroundZIndex(child) }
                  : undefined),
                ...(isBehindTextAbsoluteImage ||
                behavesAsDecorativeBehindTextBackground
                  ? { pointerEvents: "none", userSelect: "none" }
                  : undefined),
              }}
              onPointerDown={(event) =>
                isReadOnly ||
                behavesAsDecorativeBehindTextBackground ||
                (event.target instanceof Element &&
                  event.target.closest("[data-docx-textbox-editor='true']"))
                  ? undefined
                  : beginFloatingImageMove(
                      event,
                      imageLocation,
                      child,
                      isWrappedFloatingImage,
                      isAbsoluteFloatingImage
                    )
              }
              onClick={(event) => {
                event.stopPropagation();
                if (!isReadOnly && !behavesAsDecorativeBehindTextBackground) {
                  setSelectedImage(imageLocation);
                }
              }}
              onContextMenu={(event) => {
                if (isReadOnly || behavesAsDecorativeBehindTextBackground) {
                  return;
                }

                event.preventDefault();
                event.stopPropagation();
                clearTableCellSelection();
                setSelectedSectionImageKey(undefined);
                setSelectedImage(imageLocation);
                openContextMenu(
                  {
                    kind: "image",
                    activeTextRange: resolveActiveRangeFromDomSelection(),
                    location: cloneTextRangeLocation(
                      imageLocationToParagraphLocation(imageLocation)
                    ),
                    image: {
                      location: imageLocation,
                      floating: child.floating
                        ? { ...child.floating }
                        : undefined,
                    },
                  },
                  {
                    x: event.clientX,
                    y: event.clientY,
                  }
                );
              }}
            >
              {isLiveSyntheticTextBox ? (
                <div
                  data-docx-textbox-editor="true"
                  contentEditable={!isReadOnly}
                  suppressContentEditableWarning
                  spellCheck={false}
                  onFocus={(event) => {
                    event.stopPropagation();
                    if (!isReadOnly) {
                      setSelectedImage(imageLocation);
                    }
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!isReadOnly) {
                      setSelectedImage(imageLocation);
                    }
                  }}
                  onPointerDown={(event) => {
                    event.stopPropagation();
                    if (!isReadOnly) {
                      setSelectedImage(imageLocation);
                    }
                  }}
                  onBlur={(event) => {
                    if (isReadOnly) {
                      return;
                    }
                    editor.setSyntheticTextBoxText(
                      imageLocation,
                      event.currentTarget.innerText
                    );
                  }}
                  style={{
                    width: widthPx ? `${widthPx}px` : undefined,
                    height: heightPx ? `${heightPx}px` : undefined,
                    boxSizing: "border-box",
                    overflow: "visible",
                    backgroundColor: syntheticTextBoxFrame?.backgroundColor,
                    ...(syntheticTextBoxFrame &&
                    syntheticTextBoxFrame.borderWidthPx > 0 &&
                    syntheticTextBoxFrame.borderColor
                      ? {
                          border: `${syntheticTextBoxFrame.borderWidthPx}px solid ${syntheticTextBoxFrame.borderColor}`,
                        }
                      : { border: "none" }),
                    paddingTop: syntheticTextBoxFrame?.paddingTopPx ?? 3,
                    paddingRight: syntheticTextBoxFrame?.paddingRightPx ?? 6,
                    paddingBottom: syntheticTextBoxFrame?.paddingBottomPx ?? 3,
                    paddingLeft: syntheticTextBoxFrame?.paddingLeftPx ?? 6,
                    whiteSpace: "pre-wrap",
                    cursor: isReadOnly ? "default" : "text",
                    outline: "none",
                    display: "block",
                  }}
                >
                  {syntheticTextBoxParagraphs &&
                  syntheticTextBoxParagraphs.length > 0
                    ? syntheticTextBoxParagraphs.map(
                        (paragraph, paragraphIndex) => (
                          <div
                            key={`${runKey}-textbox-paragraph-${paragraphIndex}`}
                            style={{
                              minHeight: `${Math.max(
                                1,
                                paragraph.lineHeightPx
                              )}px`,
                              lineHeight: `${Math.max(
                                1,
                                paragraph.lineHeightPx
                              )}px`,
                              textAlign: paragraph.align ?? "left",
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {paragraph.segments.map((segment, segmentIndex) => (
                              <span
                                key={`${runKey}-textbox-paragraph-${paragraphIndex}-segment-${segmentIndex}`}
                                style={
                                  segment.style
                                    ? runStyleToCss(
                                        segment.style,
                                        documentContentTheme
                                      )
                                    : undefined
                                }
                              >
                                {segment.text}
                              </span>
                            ))}
                          </div>
                        )
                      )
                    : syntheticTextBoxText}
                </div>
              ) : renderableImageSrc && !showsUnsupportedFallback ? (
                (() => {
                  const cropLayout = imageCropLayout(child, widthPx, heightPx);
                  const imageVisualStyle: React.CSSProperties = {
                    filter: appendCssFilters(
                      child.cssFilter,
                      documentContentFilter
                    ),
                    opacity: child.cssOpacity,
                  };
                  const imageTransformStyle = resolveImageRenderTransformStyle(
                    child,
                    {
                      frameWidthPx:
                        imageShouldFillFrame && widthPx
                          ? widthPx
                          : cropLayout?.frameWidthPx ?? widthPx,
                      frameHeightPx:
                        imageShouldFillFrame && heightPx
                          ? heightPx
                          : cropLayout?.frameHeightPx ?? heightPx,
                      fillFrame: imageShouldFillFrame,
                      baseTransform:
                        cropLayout && !imageShouldFillFrame
                          ? `translate(${-cropLayout.offsetXPx}px, ${-cropLayout.offsetYPx}px)`
                          : undefined,
                    }
                  );
                  const imageCursor =
                    isReadOnly || behavesAsDecorativeBehindTextBackground
                      ? "default"
                      : isWrappedFloatingImage || isAbsoluteFloatingImage
                      ? "move"
                      : isDraggingImage
                      ? "grabbing"
                      : "grab";

                  if (cropLayout) {
                    return (
                      <span
                        style={{
                          width: imageShouldFillFrame
                            ? "100%"
                            : `${cropLayout.frameWidthPx}px`,
                          height: imageShouldFillFrame
                            ? "100%"
                            : `${cropLayout.frameHeightPx}px`,
                          overflow: "clip",
                          display: "block",
                        }}
                      >
                        <img
                          src={renderableImageSrc}
                          alt={child.alt ?? "DOCX image"}
                          draggable={
                            !isReadOnly &&
                            !isWrappedFloatingImage &&
                            !isAbsoluteFloatingImage
                          }
                          onDragStart={
                            !isReadOnly &&
                            !isWrappedFloatingImage &&
                            !isAbsoluteFloatingImage
                              ? (event) =>
                                  onImageDragStart(event, imageLocation)
                              : undefined
                          }
                          onDragEnd={
                            !isReadOnly &&
                            !isWrappedFloatingImage &&
                            !isAbsoluteFloatingImage
                              ? onImageDragEnd
                              : undefined
                          }
                          style={{
                            width: imageShouldFillFrame
                              ? "100%"
                              : `${cropLayout.imageWidthPx}px`,
                            height: imageShouldFillFrame
                              ? "100%"
                              : `${cropLayout.imageHeightPx}px`,
                            maxWidth: "none",
                            verticalAlign: "middle",
                            display: "block",
                            objectFit: imageShouldFillFrame
                              ? "cover"
                              : undefined,
                            cursor: imageCursor,
                            ...(behavesAsDecorativeBehindTextBackground
                              ? { pointerEvents: "none", userSelect: "none" }
                              : undefined),
                            ...imageVisualStyle,
                            ...imageTransformStyle,
                          }}
                        />
                      </span>
                    );
                  }

                  return (
                    <img
                      src={renderableImageSrc}
                      alt={child.alt ?? "DOCX image"}
                      draggable={
                        !isReadOnly &&
                        !isWrappedFloatingImage &&
                        !isAbsoluteFloatingImage
                      }
                      onDragStart={
                        !isReadOnly &&
                        !isWrappedFloatingImage &&
                        !isAbsoluteFloatingImage
                          ? (event) => onImageDragStart(event, imageLocation)
                          : undefined
                      }
                      onDragEnd={
                        !isReadOnly &&
                        !isWrappedFloatingImage &&
                        !isAbsoluteFloatingImage
                          ? onImageDragEnd
                          : undefined
                      }
                      style={{
                        width: imageShouldFillFrame
                          ? "100%"
                          : widthPx
                          ? `${widthPx}px`
                          : undefined,
                        height: imageShouldFillFrame
                          ? "100%"
                          : heightPx
                          ? `${heightPx}px`
                          : undefined,
                        maxWidth: imageShouldFillFrame
                          ? "none"
                          : isWrappedFloatingImage || isAbsoluteFloatingImage
                          ? undefined
                          : "100%",
                        verticalAlign: "middle",
                        display: "block",
                        objectFit: imageShouldFillFrame ? "cover" : undefined,
                        cursor: imageCursor,
                        ...(behavesAsDecorativeBehindTextBackground
                          ? { pointerEvents: "none", userSelect: "none" }
                          : undefined),
                        ...imageVisualStyle,
                        ...resolveImageRenderTransformStyle(child, {
                          frameWidthPx: widthPx,
                          frameHeightPx: heightPx,
                          fillFrame: imageShouldFillFrame,
                        }),
                      }}
                    />
                  );
                })()
              ) : showsUnsupportedFallback ? (
                <span
                  role="img"
                  aria-label={child.alt ?? "DOCX image"}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: widthPx ? `${widthPx}px` : "1.8em",
                    height: heightPx ? `${heightPx}px` : "1.8em",
                    minWidth: 16,
                    minHeight: 16,
                    border: "1px solid #d1d5db",
                    borderRadius: 3,
                    backgroundColor: "#ffffff",
                    color: "#0f172a",
                    fontSize:
                      (widthPx ?? 0) <= 56 && (heightPx ?? 0) <= 56 ? 12 : 10,
                    fontWeight: 700,
                    textTransform: "lowercase",
                    fontFamily: "Arial, sans-serif",
                    lineHeight: 1,
                    verticalAlign: "middle",
                  }}
                >
                  {unsupportedImageFallbackLabel(child, widthPx, heightPx)}
                </span>
              ) : (
                <span
                  style={{
                    display: "inline-flex",
                    minWidth: 112,
                    minHeight: 80,
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px dashed #d1d5db",
                    borderRadius: 4,
                    color: "#6b7280",
                    fontSize: 12,
                    paddingInline: 8,
                  }}
                >
                  Missing image
                </span>
              )}

              {!isReadOnly &&
              isSelectedImage &&
              !isBehindTextAbsoluteImage &&
              !behavesAsDecorativeBehindTextBackground ? (
                <>
                  {resizeHandles.map((handle) => (
                    <span
                      key={`${runKey}-${handle.key}`}
                      contentEditable={false}
                      data-image-resize-handle="true"
                      style={{
                        position: "absolute",
                        width: 10,
                        height: 10,
                        borderRadius: 3,
                        border: "1px solid #d4d4d8",
                        backgroundColor: "#ffffff",
                        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.14)",
                        cursor: handle.cursor,
                        ...(handle.left !== undefined
                          ? { left: handle.left }
                          : undefined),
                        ...(handle.right !== undefined
                          ? { right: handle.right }
                          : undefined),
                        ...(handle.top !== undefined
                          ? { top: handle.top }
                          : undefined),
                        ...(handle.bottom !== undefined
                          ? { bottom: handle.bottom }
                          : undefined),
                      }}
                      onPointerDown={(event) => {
                        const wrapper = event.currentTarget.parentElement;
                        const imageElement = wrapper?.querySelector("img");
                        const width = imageElement
                          ? Math.round(
                              imageElement.getBoundingClientRect().width
                            )
                          : child.widthPx ?? 240;
                        const height = imageElement
                          ? Math.round(
                              imageElement.getBoundingClientRect().height
                            )
                          : child.heightPx ??
                            Math.round((child.widthPx ?? 240) * 0.62);

                        beginImageResize(
                          event,
                          imageLocation,
                          width,
                          height,
                          handle.xDirection as -1 | 1,
                          handle.yDirection as -1 | 1
                        );
                      }}
                    />
                  ))}
                </>
              ) : null}
            </span>
          );
          if (
            isBehindTextAbsoluteImage &&
            !isReadOnly &&
            !behavesAsDecorativeBehindTextBackground
          ) {
            nodes.push(
              <span
                key={`${runKey}-hit-target`}
                contentEditable={false}
                aria-hidden="true"
                style={{
                  ...imageFrameStyle,
                  ...floatingStyle,
                  zIndex: 8,
                  backgroundColor: "transparent",
                  cursor: "move",
                  ...(isSelectedImage
                    ? {
                        boxShadow: "inset 0 0 0 1px rgba(37, 99, 235, 0.8)",
                      }
                    : undefined),
                }}
                onPointerDown={(event) =>
                  beginFloatingImageMove(
                    event,
                    imageLocation,
                    child,
                    isWrappedFloatingImage,
                    isAbsoluteFloatingImage
                  )
                }
                onClick={(event) => {
                  event.stopPropagation();
                  setSelectedImage(imageLocation);
                }}
                onContextMenu={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  clearTableCellSelection();
                  setSelectedSectionImageKey(undefined);
                  setSelectedImage(imageLocation);
                  openContextMenu(
                    {
                      kind: "image",
                      activeTextRange: resolveActiveRangeFromDomSelection(),
                      location: cloneTextRangeLocation(
                        imageLocationToParagraphLocation(imageLocation)
                      ),
                      image: {
                        location: imageLocation,
                        floating: child.floating
                          ? { ...child.floating }
                          : undefined,
                      },
                    },
                    {
                      x: event.clientX,
                      y: event.clientY,
                    }
                  );
                }}
              >
                {isSelectedImage
                  ? resizeHandles.map((handle) => (
                      <span
                        key={`${runKey}-hit-${handle.key}`}
                        contentEditable={false}
                        data-image-resize-handle="true"
                        style={{
                          position: "absolute",
                          width: 10,
                          height: 10,
                          borderRadius: 3,
                          border: "1px solid #d4d4d8",
                          backgroundColor: "#ffffff",
                          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.14)",
                          cursor: handle.cursor,
                          ...(handle.left !== undefined
                            ? { left: handle.left }
                            : undefined),
                          ...(handle.right !== undefined
                            ? { right: handle.right }
                            : undefined),
                          ...(handle.top !== undefined
                            ? { top: handle.top }
                            : undefined),
                          ...(handle.bottom !== undefined
                            ? { bottom: handle.bottom }
                            : undefined),
                        }}
                        onPointerDown={(event) => {
                          const overlayWidth = widthPx ?? child.widthPx ?? 240;
                          const overlayHeight =
                            heightPx ??
                            child.heightPx ??
                            Math.round((overlayWidth ?? 240) * 0.62);
                          beginImageResize(
                            event,
                            imageLocation,
                            overlayWidth,
                            overlayHeight,
                            handle.xDirection as -1 | 1,
                            handle.yDirection as -1 | 1
                          );
                        }}
                      />
                    ))
                  : null}
              </span>
            );
          }
          if (!isWrappedFloatingImage && !isAbsoluteFloatingImage) {
            trackInlineAdvance(widthPx ?? 0);
          }
        } else if (child.type === "form-field") {
          const formFieldLocation: DocxFormFieldLocation = {
            ...location,
            childIndex,
          };
          const compactFieldWidthPx = estimateInteractiveFieldWidthPx(child);
          const compactFieldLayout =
            compactTabStopFieldLayout || child.sourceKind === "legacy";
          const isTextLikeFormField =
            child.fieldType === "text" || child.fieldType === "date";
          const inputStyle: React.CSSProperties = {
            ...runStyleToCss(child.style, documentContentTheme),
            fontFamily: cssFontFamily(child.style?.fontFamily) ?? "inherit",
            fontSize: child.style?.fontSizePt
              ? `${child.style.fontSizePt}pt`
              : "inherit",
            color:
              themedRunColor(child.style?.color, documentContentTheme) ??
              "inherit",
            backgroundColor: isTextLikeFormField
              ? "rgba(148, 163, 184, 0.16)"
              : compactFieldLayout
              ? "transparent"
              : "#ffffff",
            border: isTextLikeFormField
              ? "1px solid rgba(148, 163, 184, 0.6)"
              : compactFieldLayout
              ? "none"
              : "1px solid #d4d4d8",
            borderRadius: isTextLikeFormField ? 4 : compactFieldLayout ? 0 : 6,
            paddingInline: compactFieldLayout
              ? isTextLikeFormField
                ? 2
                : 0
              : 6,
            paddingBlock: 0,
            lineHeight: "inherit",
            minHeight: compactFieldLayout ? undefined : 24,
            height: compactFieldLayout ? "1.1em" : undefined,
            margin: 0,
            verticalAlign: "baseline",
            minWidth: compactFieldLayout
              ? `${compactFieldWidthPx}px`
              : child.fieldType === "text"
              ? 120
              : undefined,
            width: compactFieldLayout ? `${compactFieldWidthPx}px` : undefined,
            boxSizing: "border-box",
          };

          if (child.fieldType === "checkbox") {
            const checkboxSymbol =
              child.checked ?? child.widget?.checkbox?.defaultChecked
                ? child.checkedSymbol ?? "☒"
                : child.uncheckedSymbol ?? "☐";
            const checkboxWidthPx = resolveCheckboxFieldWidthPx(child);
            nodes.push(
              <span
                key={runKey}
                contentEditable={false}
                data-docx-form-field="true"
                data-docx-form-field-type="checkbox"
                role="checkbox"
                aria-checked={Boolean(child.checked)}
                tabIndex={isReadOnly ? -1 : 0}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  if (isReadOnly) {
                    return;
                  }

                  event.preventDefault();
                  event.stopPropagation();
                  if (handleRepeatedFormFieldClick(formFieldLocation)) {
                    return;
                  }
                  editor.selectFormField(formFieldLocation);
                  editor.toggleFormCheckbox(formFieldLocation);
                }}
                onKeyDown={(event) => {
                  if (isReadOnly) {
                    return;
                  }

                  if (event.key !== "Enter" && event.key !== " ") {
                    return;
                  }
                  event.preventDefault();
                  event.stopPropagation();
                  editor.selectFormField(formFieldLocation);
                  editor.toggleFormCheckbox(formFieldLocation);
                }}
                onFocus={() => {
                  if (isReadOnly) {
                    return;
                  }
                  editor.selectFormField(formFieldLocation);
                }}
                onDoubleClick={(event) => {
                  if (isReadOnly) {
                    return;
                  }

                  event.preventDefault();
                  event.stopPropagation();
                  editor.selectFormField(formFieldLocation);
                  onFormFieldDoubleClick?.(formFieldLocation);
                }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: `${checkboxWidthPx}px`,
                  marginInline: 0,
                  lineHeight: "inherit",
                  fontFamily:
                    cssFontFamily(child.style?.fontFamily) ?? "inherit",
                  cursor: isReadOnly ? "default" : "pointer",
                  userSelect: "none",
                  outline: "none",
                  ...runStyleToCss(child.style, documentContentTheme),
                }}
              >
                {checkboxSymbol}
              </span>
            );
            trackTextAdvance(checkboxSymbol, child.style);
          } else if (child.fieldType === "dropdown") {
            const options = child.options ?? [];
            const selectedValue =
              child.value ?? options[0]?.value ?? options[0]?.displayText ?? "";

            nodes.push(
              <select
                key={runKey}
                contentEditable={false}
                data-docx-form-field="true"
                data-docx-form-field-type="dropdown"
                value={selectedValue}
                onClick={(event) => {
                  event.stopPropagation();
                }}
                onChange={(event) => {
                  if (isReadOnly) {
                    return;
                  }
                  editor.setFormFieldValue(
                    formFieldLocation,
                    event.currentTarget.value
                  );
                }}
                onFocus={() => {
                  if (isReadOnly) {
                    return;
                  }
                  editor.selectFormField(formFieldLocation);
                }}
                onDoubleClick={(event) => {
                  if (isReadOnly) {
                    return;
                  }

                  event.preventDefault();
                  event.stopPropagation();
                  editor.selectFormField(formFieldLocation);
                  onFormFieldDoubleClick?.(formFieldLocation);
                }}
                disabled={isReadOnly}
                style={inputStyle}
              >
                {options.length > 0 ? (
                  options.map((option, optionIndex) => {
                    const optionValue = option.value ?? option.displayText;
                    return (
                      <option
                        key={`${runKey}-option-${optionIndex}`}
                        value={optionValue}
                      >
                        {option.displayText}
                      </option>
                    );
                  })
                ) : (
                  <option value={selectedValue}>{selectedValue}</option>
                )}
              </select>
            );
            trackInlineAdvance(compactFieldWidthPx);
          } else if (child.fieldType === "date") {
            const normalizedDate = normalizeDateInputValue(child.value);
            if (normalizedDate) {
              nodes.push(
                <input
                  key={runKey}
                  contentEditable={false}
                  data-docx-form-field="true"
                  data-docx-form-field-type="date"
                  type={compactFieldLayout ? "text" : "date"}
                  value={normalizedDate}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                  onChange={(event) => {
                    if (isReadOnly) {
                      return;
                    }
                    editor.setFormFieldValue(
                      formFieldLocation,
                      event.currentTarget.value
                    );
                  }}
                  onFocus={() => {
                    if (isReadOnly) {
                      return;
                    }
                    editor.selectFormField(formFieldLocation);
                  }}
                  onDoubleClick={(event) => {
                    if (isReadOnly) {
                      return;
                    }

                    event.preventDefault();
                    event.stopPropagation();
                    editor.selectFormField(formFieldLocation);
                    onFormFieldDoubleClick?.(formFieldLocation);
                  }}
                  readOnly={isReadOnly}
                  style={inputStyle}
                />
              );
              trackInlineAdvance(compactFieldWidthPx);
            } else {
              nodes.push(
                <input
                  key={runKey}
                  contentEditable={false}
                  data-docx-form-field="true"
                  data-docx-form-field-type="date"
                  type="text"
                  value={child.value ?? ""}
                  placeholder={child.placeholder ?? child.title ?? "Enter date"}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                  onChange={(event) => {
                    if (isReadOnly) {
                      return;
                    }
                    editor.setFormFieldValue(
                      formFieldLocation,
                      event.currentTarget.value
                    );
                  }}
                  onFocus={() => {
                    if (isReadOnly) {
                      return;
                    }
                    editor.selectFormField(formFieldLocation);
                  }}
                  onDoubleClick={(event) => {
                    if (isReadOnly) {
                      return;
                    }

                    event.preventDefault();
                    event.stopPropagation();
                    editor.selectFormField(formFieldLocation);
                    onFormFieldDoubleClick?.(formFieldLocation);
                  }}
                  readOnly={isReadOnly}
                  style={inputStyle}
                />
              );
              trackInlineAdvance(compactFieldWidthPx);
            }
          } else {
            nodes.push(
              <input
                key={runKey}
                contentEditable={false}
                data-docx-form-field="true"
                data-docx-form-field-type="text"
                type="text"
                value={child.value ?? ""}
                placeholder={child.placeholder ?? child.title ?? ""}
                onClick={(event) => {
                  event.stopPropagation();
                }}
                onChange={(event) => {
                  if (isReadOnly) {
                    return;
                  }
                  editor.setFormFieldValue(
                    formFieldLocation,
                    event.currentTarget.value
                  );
                }}
                onFocus={() => {
                  if (isReadOnly) {
                    return;
                  }
                  editor.selectFormField(formFieldLocation);
                }}
                onDoubleClick={(event) => {
                  if (isReadOnly) {
                    return;
                  }

                  event.preventDefault();
                  event.stopPropagation();
                  editor.selectFormField(formFieldLocation);
                  onFormFieldDoubleClick?.(formFieldLocation);
                }}
                readOnly={isReadOnly}
                style={inputStyle}
              />
            );
            trackInlineAdvance(compactFieldWidthPx);
          }
        } else {
          const noteLabel = noteMarkerLabel(
            child.noteReference,
            noteMarkerIndexes.footnote,
            noteMarkerIndexes.endnote
          );
          if (noteLabel) {
            nodes.push(
              <span
                key={runKey}
                style={{
                  ...runStyleToCss(child.style, documentContentTheme),
                  verticalAlign: "super",
                  fontSize: "0.75em",
                }}
              >
                {noteLabel}
              </span>
            );
            trackTextAdvance(noteLabel, child.style);
            nodes.push(
              renderImageDropZone(
                { ...location, childIndex: childIndex + 1 },
                `${keyPrefix}-drop-${childIndex + 1}`
              )
            );
            return;
          }

          if (child.link) {
            const linkHref = child.link;
            const isInternalLink = linkHref.startsWith("#");
            nodes.push(
              <a
                key={runKey}
                href={linkHref}
                target={isInternalLink ? undefined : "_blank"}
                rel={isInternalLink ? undefined : "noreferrer noopener"}
                contentEditable={isInternalLink ? false : undefined}
                onMouseDown={(event) => {
                  if (!isInternalLink) {
                    return;
                  }

                  event.preventDefault();
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  if (!isInternalLink) {
                    return;
                  }

                  event.preventDefault();
                  event.stopPropagation();
                  const bookmarkName = linkHref.slice(1);
                  scrollToBookmark(bookmarkName);
                }}
                style={linkStyleToCss(child.style, documentContentTheme)}
              >
                {child.text}
              </a>
            );
            trackTextAdvance(child.text, child.style);
            nodes.push(
              renderImageDropZone(
                { ...location, childIndex: childIndex + 1 },
                `${keyPrefix}-drop-${childIndex + 1}`
              )
            );
            return;
          }

          const runStyle = runStyleToCss(child.style, documentContentTheme);
          const renderedText = attachTextToPreviousCheckbox(
            paragraph,
            childIndex,
            child.text
          );
          if (renderedText === "\t") {
            const tabWidthPx = resolveNextTabWidthPx();
            nodes.push(
              <span
                key={runKey}
                data-docx-tab-char="true"
                style={{
                  ...runStyle,
                  display: "inline-block",
                  width: tabWidthPx,
                  minWidth: tabWidthPx,
                  whiteSpace: "pre",
                  textDecoration: child.style?.underline
                    ? "none"
                    : runStyle.textDecoration,
                  borderBottom: child.style?.underline
                    ? "1px solid currentColor"
                    : undefined,
                  lineHeight: "1em",
                }}
              >
                {"\u00a0"}
              </span>
            );
            trackInlineAdvance(tabWidthPx);
          } else {
            appendInteractiveTextWithSoftBreakControl(
              runKey,
              renderedText,
              runStyle,
              child.style
            );
          }
        }

        nodes.push(
          renderImageDropZone(
            { ...location, childIndex: childIndex + 1 },
            `${keyPrefix}-drop-${childIndex + 1}`
          )
        );
      });

      if (hasDualWrapSpacer) {
        nodes.push(
          <span
            key={`${keyPrefix}-dual-wrap-clear`}
            contentEditable={false}
            aria-hidden="true"
            style={{
              display: "block",
              clear: "both",
              height: 0,
              pointerEvents: "none",
              userSelect: "none",
            }}
          />
        );
      }

      return nodes;
    },
    [
      beginFloatingImageMove,
      beginImageResize,
      clearTableCellSelection,
      floatingMovePreview,
      isDraggingImage,
      openContextMenu,
      onImageDragEnd,
      onImageDragStart,
      renderImageDropZone,
      resolveActiveRangeFromDomSelection,
      resizePreview,
      selectedImage,
      setSelectedSectionImageKey,
      documentContentFilter,
      documentContentTheme,
      editor.selectFormField,
      editor.setFormFieldValue,
      editor.toggleFormCheckbox,
      handleRepeatedFormFieldClick,
      onFormFieldDoubleClick,
      isReadOnly,
      paragraphNumberingLabels,
      scrollToBookmark,
      documentLayout,
      tocLinkColorByLevel,
      trackedChangesEnabled,
    ]
  );

  const hasFilePayload = React.useCallback(
    (dataTransfer: DataTransfer | null): boolean => {
      return Array.from(dataTransfer?.types ?? []).includes("Files");
    },
    []
  );

  const extractDroppedDocxFile = React.useCallback(
    (dataTransfer: DataTransfer | null): File | undefined => {
      return Array.from(dataTransfer?.files ?? []).find((candidate) =>
        /\.docx?$/i.test(candidate.name)
      );
    },
    []
  );

  const onCanvasDragOver = React.useCallback(
    (event: React.DragEvent<HTMLDivElement>): void => {
      if (isDraggingImage) {
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
        setIsDragOverCanvas(false);
        return;
      }

      if (!canReplaceDocumentByDrop) {
        setIsDragOverCanvas(false);
        return;
      }

      if (!hasFilePayload(event.dataTransfer)) {
        setIsDragOverCanvas(false);
        return;
      }

      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
      setIsDragOverCanvas(true);
    },
    [canReplaceDocumentByDrop, hasFilePayload, isDraggingImage]
  );

  const onCanvasDragLeave = React.useCallback(
    (event: React.DragEvent<HTMLDivElement>): void => {
      if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
        return;
      }

      setIsDragOverCanvas(false);
    },
    []
  );

  const onCanvasDrop = React.useCallback(
    async (event: React.DragEvent<HTMLDivElement>): Promise<void> => {
      setIsDragOverCanvas(false);

      if (isDraggingImage) {
        event.preventDefault();
        return;
      }

      if (!canReplaceDocumentByDrop) {
        return;
      }

      const file = extractDroppedDocxFile(event.dataTransfer);
      if (!file) {
        return;
      }

      event.preventDefault();
      setSelectedImage(undefined);
      await editor.importDocxFile(file);
    },
    [canReplaceDocumentByDrop, editor, extractDroppedDocxFile, isDraggingImage]
  );

  React.useEffect(() => {
    if (!canReplaceDocumentByDrop) {
      fileDragDepthRef.current = 0;
      setIsDragOverCanvas(false);
      return;
    }

    const onWindowDragEnter = (event: DragEvent): void => {
      if (isDraggingImage || !hasFilePayload(event.dataTransfer)) {
        return;
      }

      fileDragDepthRef.current += 1;
      setIsDragOverCanvas(true);
    };

    const onWindowDragOver = (event: DragEvent): void => {
      if (isDraggingImage || !hasFilePayload(event.dataTransfer)) {
        return;
      }

      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = "copy";
      }
      setIsDragOverCanvas(true);
    };

    const onWindowDragLeave = (event: DragEvent): void => {
      if (event.relatedTarget === null) {
        fileDragDepthRef.current = 0;
        setIsDragOverCanvas(false);
        return;
      }

      if (!hasFilePayload(event.dataTransfer)) {
        return;
      }

      fileDragDepthRef.current = Math.max(0, fileDragDepthRef.current - 1);
      if (fileDragDepthRef.current === 0) {
        setIsDragOverCanvas(false);
      }
    };

    const onWindowDrop = (event: DragEvent): void => {
      fileDragDepthRef.current = 0;
      setIsDragOverCanvas(false);

      if (
        event.defaultPrevented ||
        isDraggingImage ||
        !hasFilePayload(event.dataTransfer)
      ) {
        return;
      }

      const file = extractDroppedDocxFile(event.dataTransfer);
      if (!file) {
        return;
      }

      event.preventDefault();
      setSelectedImage(undefined);
      void editor.importDocxFile(file);
    };

    window.addEventListener("dragenter", onWindowDragEnter);
    window.addEventListener("dragover", onWindowDragOver);
    window.addEventListener("dragleave", onWindowDragLeave);
    window.addEventListener("drop", onWindowDrop);

    return () => {
      window.removeEventListener("dragenter", onWindowDragEnter);
      window.removeEventListener("dragover", onWindowDragOver);
      window.removeEventListener("dragleave", onWindowDragLeave);
      window.removeEventListener("drop", onWindowDrop);
      fileDragDepthRef.current = 0;
    };
  }, [
    canReplaceDocumentByDrop,
    editor,
    extractDroppedDocxFile,
    hasFilePayload,
    isDraggingImage,
  ]);

  const documentContentWidthPx = Math.max(
    120,
    documentLayout.pageWidthPx -
      documentLayout.marginsPx.left -
      documentLayout.marginsPx.right
  );
  const documentInheritedFontFamily = React.useMemo(
    () => resolveDocumentInheritedFontFamily(editor.model),
    [editor.model]
  );
  const pageSurfaceBaseStyle: React.CSSProperties = {
    ...BASE_DOC_STYLE,
    ...DOC_SURFACE_STYLE_BY_THEME[editor.documentTheme],
    ...(documentInheritedFontFamily
      ? { fontFamily: documentInheritedFontFamily }
      : undefined),
    ...(isDragOverCanvas
      ? { boxShadow: "0 0 0 2px rgba(59,130,246,0.4)" }
      : undefined),
    boxSizing: "border-box",
    overflow: floatingMovePreview || dropCapMovePreview ? "visible" : "clip",
  };

  interface TableCellParagraphSliceLayoutEntry {
    paragraphForLayout: ParagraphNode;
    paragraphHeightPx: number;
    textTopOffsetPx: number;
    lineHeightPx: number;
    lineTopOffsetsPx: number[];
    pretextSource?: ParagraphPretextLayoutSource;
    pretextLayout?: PretextVariableWidthLayout;
  }

  interface TableCellParagraphSlicePlan {
    paragraph: ParagraphNode;
    paragraphIndex: number;
    topPx: number;
    layoutEntry: TableCellParagraphSliceLayoutEntry;
    paragraphLineRange?: ParagraphLineRange;
  }

  interface TableCellParagraphFlowEntry {
    paragraph: ParagraphNode;
    paragraphIndex: number;
    paragraphTopPx: number;
    textTopPx: number;
    paragraphBottomPx: number;
    layoutEntry: TableCellParagraphSliceLayoutEntry;
  }

  interface TableCellParagraphFlowLayout {
    contentHeightPx: number;
    paddingPx: ReturnType<typeof resolveTableSpacingPaddingPx>;
    entries: TableCellParagraphFlowEntry[];
  }

  const tableCellParagraphSliceLayoutCacheRef = React.useRef(
    new Map<string, TableCellParagraphSliceLayoutEntry>()
  );
  const tableCellParagraphFlowLayoutCacheRef = React.useRef(
    new Map<string, TableCellParagraphFlowLayout>()
  );
  const tableCellParagraphSlicePlanCacheRef = React.useRef(
    new Map<string, TableCellParagraphSlicePlan[]>()
  );
  const tableCellParagraphSliceSignatureCacheRef = React.useRef(
    new WeakMap<object, string>()
  );

  React.useEffect(() => {
    tableCellParagraphSliceLayoutCacheRef.current.clear();
    tableCellParagraphFlowLayoutCacheRef.current.clear();
    tableCellParagraphSlicePlanCacheRef.current.clear();
    tableCellParagraphSliceSignatureCacheRef.current = new WeakMap<
      object,
      string
    >();
  }, [paragraphStructureEpoch]);

  const resolveTableCellParagraphSliceSignature = (
    cell: TableNode["rows"][number]["cells"][number]
  ): string => {
    const cellKey = cell as object;
    const cached =
      tableCellParagraphSliceSignatureCacheRef.current.get(cellKey);
    if (cached) {
      return cached;
    }

    const signature = [
      JSON.stringify(cell.style ?? null),
      ...cell.nodes.map((paragraph, paragraphIndex) =>
        isParagraphCellContentNode(paragraph)
          ? `${paragraphIndex}:${
              paragraph.sourceXml ?? paragraphText(paragraph)
            }`
          : `${paragraphIndex}:${paragraph.type}`
      ),
    ].join("\u001f");
    tableCellParagraphSliceSignatureCacheRef.current.set(cellKey, signature);
    return signature;
  };

  const resolveTableCellParagraphSlicePlans = (params: {
    cell: TableNode["rows"][number]["cells"][number];
    rowHeightPx: number;
    sliceStartPx: number;
    sliceHeightPx: number;
    cellContentWidthPx: number;
    tableCellMarginTwips?: TableSpacingTwips;
    applyWordTableDefaults: boolean;
    docGridLinePitchPx?: number;
  }): TableCellParagraphSlicePlan[] | undefined => {
    const {
      cell,
      rowHeightPx,
      sliceStartPx,
      sliceHeightPx,
      cellContentWidthPx,
      tableCellMarginTwips,
      applyWordTableDefaults,
      docGridLinePitchPx,
    } = params;
    if (!cell.nodes.every(isParagraphCellContentNode)) {
      return undefined;
    }

    const numberingDefinitions = editor.model.metadata.numberingDefinitions;
    const cellSignature = resolveTableCellParagraphSliceSignature(cell);
    const flowLayoutCacheKey = [
      cellContentWidthPx,
      JSON.stringify(tableCellMarginTwips ?? null),
      applyWordTableDefaults ? 1 : 0,
      docGridLinePitchPx ?? 0,
      cellSignature,
    ].join(":");
    let flowLayout =
      tableCellParagraphFlowLayoutCacheRef.current.get(flowLayoutCacheKey);
    if (!flowLayout) {
      const contentHeightPx = estimateTableCellContentHeightPx(
        cell.nodes,
        cellContentWidthPx,
        numberingDefinitions,
        applyWordTableDefaults,
        docGridLinePitchPx
      );
      const paddingPx = resolveTableSpacingPaddingPx(
        mergeTableSpacing(tableCellMarginTwips, cell.style?.marginTwips)
      );
      let paragraphTopPx = paddingPx.top;
      const entries: TableCellParagraphFlowEntry[] = [];

      for (
        let paragraphIndex = 0;
        paragraphIndex < cell.nodes.length;
        paragraphIndex += 1
      ) {
        const paragraph = cell.nodes[paragraphIndex];
        if (!paragraph) {
          continue;
        }

        const disableDocGridSnap =
          paragraphDocGridSnapState(paragraph) === "disable";
        const layoutCacheKey = `${cellContentWidthPx}:${
          applyWordTableDefaults ? 1 : 0
        }:${docGridLinePitchPx ?? 0}:${paragraphIndex}:${
          paragraph.sourceXml ?? paragraphText(paragraph)
        }`;
        let layoutEntry =
          tableCellParagraphSliceLayoutCacheRef.current.get(layoutCacheKey);
        if (!layoutEntry) {
          const paragraphForLayout = wordLikeTableCellParagraph(
            paragraph,
            applyWordTableDefaults
          );
          const paragraphHeightPx = estimateParagraphHeightPx(
            paragraphForLayout,
            cellContentWidthPx,
            numberingDefinitions,
            docGridLinePitchPx,
            disableDocGridSnap
          );
          const suppressTopSpacing =
            paragraphIndex === 0 &&
            suppressFirstTableCellParagraphTopSpacing(paragraph);
          const beforeSpacingPx = suppressTopSpacing
            ? 0
            : twipsToPixels(paragraphForLayout.style?.spacing?.beforeTwips) ??
              0;
          const topBorderInsetPx = paragraphBorderInsetPx(
            paragraphForLayout.style?.borders?.top
          );
          const lineHeightPx = Math.max(
            MIN_PARAGRAPH_LINE_HEIGHT_PX,
            estimateParagraphLineHeightPx(
              paragraphForLayout,
              docGridLinePitchPx,
              disableDocGridSnap
            )
          );
          const pretextSource = buildParagraphPretextLayoutSource(
            paragraphForLayout,
            {
              allowExplicitLineBreakText: true,
              expandTabsForLayout: true,
            }
          );
          const paragraphTextWidthPx = paragraphAvailableTextWidthPx(
            paragraphForLayout,
            cellContentWidthPx,
            numberingDefinitions
          );
          const pretextLayout = pretextSource
            ? layoutParagraphPretextSource(
                paragraphForLayout,
                pretextSource,
                paragraphTextWidthPx,
                lineHeightPx,
                []
              )
            : undefined;
          const totalLineCount = Math.max(
            1,
            pretextLayout?.lineCount ??
              paragraphLineCountWithinWidth(
                paragraphForLayout,
                cellContentWidthPx,
                numberingDefinitions
              )
          );
          layoutEntry = {
            paragraphForLayout,
            paragraphHeightPx,
            textTopOffsetPx: beforeSpacingPx + topBorderInsetPx,
            lineHeightPx,
            lineTopOffsetsPx: pretextLayout
              ? pretextLayout.lines.map((line) =>
                  Math.max(0, Math.round(line.y))
                )
              : Array.from(
                  { length: totalLineCount },
                  (_, lineIndex) => lineIndex * lineHeightPx
                ),
            pretextSource: pretextSource ?? undefined,
            pretextLayout: pretextLayout ?? undefined,
          };
          tableCellParagraphSliceLayoutCacheRef.current.set(
            layoutCacheKey,
            layoutEntry
          );
        }

        const paragraphHeightPx = layoutEntry.paragraphHeightPx;
        const textTopPx = paragraphTopPx + layoutEntry.textTopOffsetPx;
        const textBottomPx =
          textTopPx +
          (layoutEntry.pretextLayout
            ? wrappedPretextParagraphBlockHeightPx(layoutEntry.pretextLayout)
            : layoutEntry.lineHeightPx * layoutEntry.lineTopOffsetsPx.length);
        const paragraphBottomPx = resolveTableCellParagraphVisualBottomPx({
          paragraphTopPx,
          paragraphHeightPx,
          textBottomPx,
        });
        entries.push({
          paragraph,
          paragraphIndex,
          paragraphTopPx,
          textTopPx,
          paragraphBottomPx,
          layoutEntry,
        });
        paragraphTopPx = paragraphBottomPx;
      }

      flowLayout = {
        contentHeightPx,
        paddingPx,
        entries,
      };
      tableCellParagraphFlowLayoutCacheRef.current.set(
        flowLayoutCacheKey,
        flowLayout
      );
    }

    const availableContentHeightPx = Math.max(
      0,
      rowHeightPx - flowLayout.paddingPx.top - flowLayout.paddingPx.bottom
    );
    const extraVerticalSpacePx = Math.max(
      0,
      availableContentHeightPx - flowLayout.contentHeightPx
    );
    const verticalOffsetPx =
      cell.style?.verticalAlign === "center"
        ? Math.round(extraVerticalSpacePx / 2)
        : cell.style?.verticalAlign === "bottom"
        ? extraVerticalSpacePx
        : 0;
    const sliceBottomPx = sliceStartPx + sliceHeightPx;
    const slicePlanCacheKey = [
      flowLayoutCacheKey,
      rowHeightPx,
      sliceStartPx,
      sliceHeightPx,
    ].join(":");
    const cachedPlans =
      tableCellParagraphSlicePlanCacheRef.current.get(slicePlanCacheKey);
    if (cachedPlans) {
      return cachedPlans;
    }

    const plans: TableCellParagraphSlicePlan[] = [];
    let firstCandidateIndex = 0;
    let low = 0;
    let high = flowLayout.entries.length;
    while (low < high) {
      const mid = Math.floor((low + high) / 2);
      const entryBottomPx =
        (flowLayout.entries[mid]?.paragraphBottomPx ?? 0) + verticalOffsetPx;
      if (entryBottomPx <= sliceStartPx + PAGE_OVERFLOW_TOLERANCE_PX) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }
    firstCandidateIndex = low;

    for (
      let entryIndex = firstCandidateIndex;
      entryIndex < flowLayout.entries.length;
      entryIndex += 1
    ) {
      const entry = flowLayout.entries[entryIndex];
      if (!entry) {
        continue;
      }
      const paragraphTopPx = entry.paragraphTopPx + verticalOffsetPx;
      const textTopPx = entry.textTopPx + verticalOffsetPx;
      const paragraphBottomPx = entry.paragraphBottomPx + verticalOffsetPx;
      if (paragraphTopPx >= sliceBottomPx - PAGE_OVERFLOW_TOLERANCE_PX) {
        break;
      }
      const intersectsParagraph =
        sliceBottomPx > paragraphTopPx + PAGE_OVERFLOW_TOLERANCE_PX &&
        sliceStartPx < paragraphBottomPx - PAGE_OVERFLOW_TOLERANCE_PX;
      if (!intersectsParagraph) {
        continue;
      }

      const fullyVisible = tableCellParagraphFitsFullyWithinSlice({
        sliceStartPx,
        sliceBottomPx,
        paragraphTopPx,
        paragraphBottomPx,
      });
      if (fullyVisible) {
        plans.push({
          paragraph: entry.paragraph,
          paragraphIndex: entry.paragraphIndex,
          topPx: Math.max(0, Math.round(paragraphTopPx - sliceStartPx)),
          layoutEntry: entry.layoutEntry,
        });
        continue;
      }

      const paragraphLineRange = resolveLineRangeWithinVerticalSlice(
        entry.layoutEntry.lineTopOffsetsPx,
        entry.layoutEntry.lineHeightPx,
        sliceStartPx - textTopPx,
        sliceBottomPx - textTopPx
      );
      if (!paragraphLineRange) {
        continue;
      }

      const lineRangeTopPx =
        paragraphLineRange.startLineIndex > 0
          ? textTopPx +
            entry.layoutEntry.lineTopOffsetsPx[
              paragraphLineRange.startLineIndex
            ]
          : paragraphTopPx;
      plans.push({
        paragraph: entry.paragraph,
        paragraphIndex: entry.paragraphIndex,
        topPx: Math.max(0, Math.round(lineRangeTopPx - sliceStartPx)),
        layoutEntry: entry.layoutEntry,
        paragraphLineRange,
      });
    }

    tableCellParagraphSlicePlanCacheRef.current.set(slicePlanCacheKey, plans);
    return plans;
  };

  const renderSlicedTableCellParagraph = (params: {
    paragraph: ParagraphNode;
    paragraphIndex: number;
    tableIndex: number;
    rowIndex: number;
    cellIndex: number;
    contentIndex: number;
    cellContentWidthPx: number;
    applyWordTableDefaults: boolean;
    docGridLinePitchPx?: number;
    layoutEntry: TableCellParagraphSliceLayoutEntry;
    paragraphLineRange?: ParagraphLineRange;
  }): React.ReactNode => {
    const {
      paragraph,
      paragraphIndex,
      tableIndex,
      rowIndex,
      cellIndex,
      contentIndex,
      cellContentWidthPx,
      applyWordTableDefaults,
      docGridLinePitchPx,
      layoutEntry,
      paragraphLineRange,
    } = params;
    const numberingDefinitions = editor.model.metadata.numberingDefinitions;
    const paragraphForLayout = layoutEntry.paragraphForLayout;
    const hasPartialLineRange =
      paragraphSegmentHasPartialLineRange(paragraphLineRange);
    const paragraphSegmentLineHeightPx = Math.max(
      1,
      paragraphLineRange?.lineHeightPx ?? layoutEntry.lineHeightPx
    );
    const paragraphSegmentStartLine = paragraphLineRange?.startLineIndex ?? 0;
    const paragraphSegmentEndLine =
      paragraphLineRange?.endLineIndex ??
      paragraphLineRange?.totalLineCount ??
      1;
    const paragraphSegmentTotalLines =
      paragraphLineRange?.totalLineCount ??
      Math.max(1, paragraphSegmentEndLine);
    const paragraphSegmentVisibleLineCount = Math.max(
      1,
      paragraphSegmentEndLine - paragraphSegmentStartLine
    );
    const paragraphSegmentVisibleHeightPx =
      paragraphSegmentVisibleLineCount * paragraphSegmentLineHeightPx;
    const paragraphSegmentTranslateYPx =
      paragraphSegmentStartLine * paragraphSegmentLineHeightPx;
    const location = {
      kind: "table-cell" as const,
      tableIndex,
      rowIndex,
      cellIndex,
      paragraphIndex,
    };
    const numberingLabel = paragraphNumberingLabels.get(
      paragraphLocationKey(location)
    );
    const paragraphRenderTextWidthPx = paragraphAvailableTextWidthPx(
      paragraphForLayout,
      cellContentWidthPx,
      numberingDefinitions
    );
    const pretextParagraphLayout =
      hasPartialLineRange && layoutEntry.pretextSource
        ? layoutEntry.pretextLayout &&
          Math.abs(paragraphSegmentLineHeightPx - layoutEntry.lineHeightPx) <= 1
          ? layoutEntry.pretextLayout
          : layoutParagraphPretextSource(
              paragraphForLayout,
              layoutEntry.pretextSource,
              paragraphRenderTextWidthPx,
              paragraphSegmentLineHeightPx,
              []
            )
        : undefined;
    const pretextParagraphTotalLines = Math.max(
      0,
      pretextParagraphLayout?.lineCount ?? 0
    );
    const pretextParagraphSliceStartLine =
      hasPartialLineRange &&
      paragraphLineRange &&
      pretextParagraphTotalLines > 0
        ? Math.max(
            0,
            Math.min(
              paragraphLineRange.startLineIndex,
              pretextParagraphTotalLines
            )
          )
        : undefined;
    const pretextParagraphSliceEndLine =
      hasPartialLineRange &&
      paragraphLineRange &&
      pretextParagraphTotalLines > 0 &&
      pretextParagraphSliceStartLine !== undefined
        ? Math.max(
            pretextParagraphSliceStartLine,
            Math.min(
              paragraphLineRange.endLineIndex,
              pretextParagraphTotalLines
            )
          )
        : undefined;
    const pretextParagraphSliceLayout =
      hasPartialLineRange &&
      pretextParagraphLayout &&
      pretextParagraphSliceStartLine !== undefined &&
      pretextParagraphSliceEndLine !== undefined &&
      pretextParagraphLayout.lineCount > 0
        ? sliceLayoutToLineRange(
            pretextParagraphLayout,
            pretextParagraphSliceStartLine,
            pretextParagraphSliceEndLine
          )
        : undefined;
    const shouldRenderParagraphSegmentWithPretext =
      hasPartialLineRange &&
      Boolean(layoutEntry.pretextSource) &&
      Boolean(pretextParagraphLayout) &&
      pretextParagraphTotalLines > 0;
    const paragraphSegmentClipBleed = shouldRenderParagraphSegmentWithPretext
      ? resolveParagraphSegmentClipBleedPx(paragraphLineRange)
      : resolveFallbackParagraphSegmentClipBleedPx(
          paragraphForLayout,
          paragraphLineRange
        );
    const paragraphSegmentClipBleedTopPx = paragraphSegmentClipBleed.topPx;
    const paragraphSegmentClipBleedBottomPx =
      paragraphSegmentClipBleed.bottomPx;
    const pretextSegmentNumberingMarkerBoxWidthPx =
      numberingLabel && paragraphSegmentStartLine === 0
        ? resolveNumberingMarkerBoxWidthPx(
            paragraph,
            numberingDefinitions,
            numberingLabel
          ) ??
          Math.max(
            16,
            Math.ceil(
              measureTextWidthPx(
                numberingLabel.imageSrc
                  ? numberingLabel.trailingText ?? ""
                  : numberingLabel.text ?? "",
                numberingLabel.style,
                paragraphBaseFontSizePx(paragraph)
              )
            ) +
              (numberingLabel.imageSrc
                ? Math.ceil(numberingLabel.imageWidthPx ?? 12) + 2
                : 0) +
              4
          )
        : undefined;
    const pretextSegmentNumberingMarkerNode =
      shouldRenderParagraphSegmentWithPretext &&
      numberingLabel &&
      paragraphSegmentStartLine === 0 &&
      Number.isFinite(pretextSegmentNumberingMarkerBoxWidthPx) ? (
        <span
          key={`table-cell-${tableIndex}-${rowIndex}-${cellIndex}-${paragraphIndex}-numbering-marker-${paragraphSegmentStartLine}`}
          contentEditable={false}
          data-docx-numbering-label="true"
          style={{
            ...numberingMarkerStyle(
              paragraph,
              numberingDefinitions,
              numberingLabel,
              numberingLabel.style
                ? runStyleToCss(numberingLabel.style, documentContentTheme)
                : undefined,
              documentContentTheme
            ),
            position: "absolute",
            left: -Math.round(
              pretextSegmentNumberingMarkerBoxWidthPx as number
            ),
            top: 0,
            marginRight: 0,
            zIndex: 1,
            pointerEvents: "none",
          }}
        >
          {numberingLabel.imageSrc ? (
            <>
              <img
                src={numberingLabel.imageSrc}
                alt=""
                aria-hidden="true"
                draggable={false}
                style={{
                  display: "inline-block",
                  verticalAlign: "text-bottom",
                  width: numberingLabel.imageWidthPx ?? 12,
                  height: numberingLabel.imageHeightPx ?? 12,
                  marginRight: 2,
                  filter: documentContentFilter,
                }}
              />
              {numberingLabel.trailingText ?? ""}
            </>
          ) : (
            numberingLabel.text ?? ""
          )}
        </span>
      ) : undefined;
    const baseParagraphStyle = tableCellParagraphBlockStyle(
      paragraph,
      numberingDefinitions,
      headingStyles,
      paragraphIndex,
      applyWordTableDefaults,
      docGridLinePitchPx
    );
    const paragraphStyle: React.CSSProperties = {
      ...baseParagraphStyle,
      ...(hasPartialLineRange
        ? {
            marginTop:
              paragraphSegmentStartLine === 0
                ? baseParagraphStyle.marginTop
                : 0,
            marginBottom:
              paragraphSegmentEndLine >= paragraphSegmentTotalLines
                ? baseParagraphStyle.marginBottom
                : 0,
          }
        : undefined),
      outline: "none",
    };
    let fallbackParagraphRuns: React.ReactNode | undefined;
    const getFallbackParagraphRuns = (): React.ReactNode => {
      if (fallbackParagraphRuns === undefined) {
        fallbackParagraphRuns = renderInteractiveParagraphRuns(
          paragraph,
          `body-cell-${tableIndex}-${rowIndex}-${cellIndex}-${contentIndex}`,
          location
        );
      }
      return fallbackParagraphRuns;
    };
    const renderedInteractiveParagraphContent =
      shouldRenderParagraphSegmentWithPretext ? (
        <div
          style={{
            minHeight:
              paragraphSegmentVisibleHeightPx +
              paragraphSegmentClipBleedTopPx +
              paragraphSegmentClipBleedBottomPx,
            marginTop: -paragraphSegmentClipBleedTopPx,
            marginBottom: -paragraphSegmentClipBleedBottomPx,
            paddingTop: paragraphSegmentClipBleedTopPx,
            paddingBottom: paragraphSegmentClipBleedBottomPx,
            overflow: "visible",
          }}
        >
          {layoutEntry.pretextSource &&
          pretextParagraphSliceLayout &&
          pretextParagraphSliceLayout.lineCount > 0
            ? renderWrappedPretextParagraph({
                location,
                keyPrefix: `body-cell-${tableIndex}-${rowIndex}-${cellIndex}-${contentIndex}-lines-${paragraphSegmentStartLine}-${paragraphSegmentEndLine}`,
                source: layoutEntry.pretextSource,
                layout: pretextParagraphSliceLayout,
                lineHeightPx: paragraphSegmentLineHeightPx,
                baseTextStyle: firstRunStyle(paragraph),
                blockHeightPx: pretextParagraphSliceLayout.height,
                obstacleNodes: pretextSegmentNumberingMarkerNode,
              })
            : null}
        </div>
      ) : hasPartialLineRange ? (
        <div
          style={{
            height:
              paragraphSegmentVisibleHeightPx +
              paragraphSegmentClipBleedTopPx +
              paragraphSegmentClipBleedBottomPx,
            marginTop: -paragraphSegmentClipBleedTopPx,
            marginBottom: -paragraphSegmentClipBleedBottomPx,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              transform: `translateY(-${
                paragraphSegmentTranslateYPx - paragraphSegmentClipBleedTopPx
              }px)`,
            }}
          >
            {getFallbackParagraphRuns()}
          </div>
        </div>
      ) : (
        getFallbackParagraphRuns()
      );

    return (
      <div
        key={`body-cell-slice-${tableIndex}-${rowIndex}-${cellIndex}-${contentIndex}`}
        data-docx-paragraph-host="true"
        data-docx-paragraph-kind="table-cell"
        data-docx-table-index={tableIndex}
        data-docx-row-index={rowIndex}
        data-docx-cell-index={cellIndex}
        data-docx-paragraph-index={paragraphIndex}
        data-docx-table-paragraph-index={paragraphIndex}
        style={paragraphStyle}
      >
        {renderedInteractiveParagraphContent}
      </div>
    );
  };

  interface RenderDocumentNodeOptions {
    letterheadFloatSide?: LetterheadFloatSide;
    oppositeLetterheadFloatSide?: LetterheadFloatSide;
    suppressLetterheadColumnLayout?: boolean;
    isFirstInLetterheadColumn?: boolean;
    contentWidthPxOverride?: number;
    pageLayout?: DocumentLayoutMetrics;
    pageFlowTopPx?: number;
    pageFlowForeignExclusions?: PretextExclusionRect[];
    suppressLikelyFullPageCoverImageKeys?: Set<string>;
    forceReadOnly?: boolean;
    suppressParagraphElementTracking?: boolean;
    syntheticKeySuffix?: string;
    overrideBeforeSpacingPx?: number;
    overrideAfterSpacingPx?: number;
    normalizeParagraphHostFontSize?: boolean;
  }

  const renderDocumentNode = (
    node: DocModel["nodes"][number],
    nodeIndex: number,
    tableRowRange?: TableRowRange,
    tableRowSlice?: TableRowSlice,
    paragraphLineRange?: ParagraphLineRange,
    options?: RenderDocumentNodeOptions
  ): React.ReactNode => {
    const nodeDocGridLinePitchPx = docGridLinePitchPxByNodeIndex.get(nodeIndex);
    const nodeContentWidthPx = Math.max(
      120,
      Math.round(
        options?.contentWidthPxOverride ??
          pageContentWidthPxByNodeIndex.get(nodeIndex) ??
          documentContentWidthPx
      )
    );
    if (node.type === "paragraph") {
      if (
        paragraphCollapsesIntoPreviousParagraph(
          node,
          editor.model.nodes[nodeIndex - 1]
        )
      ) {
        return null;
      }
      const hasImage = paragraphHasImage(node);
      const hasPartialLineRange =
        paragraphSegmentHasPartialLineRange(paragraphLineRange);
      const numberingLabel = paragraphNumberingLabels.get(`p:${nodeIndex}`);
      const paragraphSegmentStartLine = paragraphLineRange?.startLineIndex ?? 0;
      const paragraphSegmentEndLine =
        paragraphLineRange?.endLineIndex ??
        paragraphLineRange?.totalLineCount ??
        1;
      const paragraphSegmentTotalLines =
        paragraphLineRange?.totalLineCount ??
        Math.max(1, paragraphSegmentEndLine);
      const paragraphSegmentLineHeightPx = Math.max(
        1,
        paragraphLineRange?.lineHeightPx ??
          estimateParagraphLineHeightPx(node, nodeDocGridLinePitchPx)
      );
      const paragraphSegmentVisibleLineCount = Math.max(
        1,
        paragraphSegmentEndLine - paragraphSegmentStartLine
      );
      const paragraphSegmentVisibleHeightPx =
        paragraphSegmentVisibleLineCount * paragraphSegmentLineHeightPx;
      const paragraphSegmentTranslateYPx =
        paragraphSegmentStartLine * paragraphSegmentLineHeightPx;
      const paragraphSegmentIsActiveEditable = paragraphSegmentIdentityMatches(
        activeEditableParagraphSegment,
        nodeIndex,
        paragraphLineRange
      );
      const paragraphHasActiveEditableSegment =
        activeEditableParagraphSegment?.nodeIndex === nodeIndex;
      const shouldTrackParagraphElement =
        options?.suppressParagraphElementTracking === true
          ? false
          : !hasPartialLineRange ||
            paragraphSegmentIsActiveEditable ||
            (!paragraphHasActiveEditableSegment &&
              paragraphSegmentStartLine === 0);
      const isManualPageBreakParagraph = paragraphIsOnlyExplicitPageBreak(node);
      const paragraphContentWidthPx = nodeContentWidthPx;
      const paragraphRenderTextWidthPx = paragraphAvailableTextWidthPx(
        node,
        paragraphContentWidthPx,
        editor.model.metadata.numberingDefinitions
      );
      const pretextParagraphSource = hasPartialLineRange
        ? buildParagraphPretextLayoutSource(node, {
            allowExplicitLineBreakText: true,
            expandTabsForLayout: true,
          })
        : undefined;
      const pretextParagraphLayout =
        hasPartialLineRange && pretextParagraphSource
          ? layoutParagraphPretextSource(
              node,
              pretextParagraphSource,
              paragraphRenderTextWidthPx,
              paragraphSegmentLineHeightPx,
              []
            )
          : undefined;
      const pretextParagraphTotalLines = Math.max(
        0,
        pretextParagraphLayout?.lineCount ?? 0
      );
      const pretextParagraphSliceStartLine =
        hasPartialLineRange &&
        paragraphLineRange &&
        pretextParagraphTotalLines > 0
          ? Math.max(
              0,
              Math.min(
                paragraphLineRange.startLineIndex,
                pretextParagraphTotalLines
              )
            )
          : undefined;
      const pretextParagraphSliceEndLine =
        hasPartialLineRange &&
        paragraphLineRange &&
        pretextParagraphTotalLines > 0 &&
        pretextParagraphSliceStartLine !== undefined
          ? Math.max(
              pretextParagraphSliceStartLine,
              Math.min(
                paragraphLineRange.endLineIndex,
                pretextParagraphTotalLines
              )
            )
          : undefined;
      const pretextParagraphSliceLayout =
        hasPartialLineRange &&
        pretextParagraphLayout &&
        pretextParagraphSliceStartLine !== undefined &&
        pretextParagraphSliceEndLine !== undefined &&
        pretextParagraphLayout.lineCount > 0
          ? sliceLayoutToLineRange(
              pretextParagraphLayout,
              pretextParagraphSliceStartLine,
              pretextParagraphSliceEndLine
            )
          : undefined;
      const shouldRenderParagraphSegmentWithPretext =
        hasPartialLineRange &&
        Boolean(pretextParagraphSource) &&
        Boolean(pretextParagraphLayout) &&
        pretextParagraphTotalLines > 0;
      const paragraphSegmentClipBleed = shouldRenderParagraphSegmentWithPretext
        ? resolveParagraphSegmentClipBleedPx(paragraphLineRange)
        : resolveFallbackParagraphSegmentClipBleedPx(node, paragraphLineRange);
      const paragraphSegmentClipBleedTopPx = paragraphSegmentClipBleed.topPx;
      const paragraphSegmentClipBleedBottomPx =
        paragraphSegmentClipBleed.bottomPx;
      const pretextSegmentNumberingMarkerBoxWidthPx =
        numberingLabel && paragraphSegmentStartLine === 0
          ? resolveNumberingMarkerBoxWidthPx(
              node,
              editor.model.metadata.numberingDefinitions,
              numberingLabel
            ) ??
            Math.max(
              16,
              Math.ceil(
                measureTextWidthPx(
                  numberingLabel.imageSrc
                    ? numberingLabel.trailingText ?? ""
                    : numberingLabel.text ?? "",
                  numberingLabel.style,
                  paragraphBaseFontSizePx(node)
                )
              ) +
                (numberingLabel.imageSrc
                  ? Math.ceil(numberingLabel.imageWidthPx ?? 12) + 2
                  : 0) +
                4
            )
          : undefined;
      const pretextSegmentNumberingMarkerNode =
        shouldRenderParagraphSegmentWithPretext &&
        numberingLabel &&
        paragraphSegmentStartLine === 0 &&
        Number.isFinite(pretextSegmentNumberingMarkerBoxWidthPx) ? (
          <span
            key={`node-${nodeIndex}-numbering-marker-${paragraphSegmentStartLine}`}
            contentEditable={false}
            data-docx-numbering-label="true"
            style={{
              ...numberingMarkerStyle(
                node,
                editor.model.metadata.numberingDefinitions,
                numberingLabel,
                numberingLabel.style
                  ? runStyleToCss(numberingLabel.style, documentContentTheme)
                  : undefined,
                documentContentTheme
              ),
              position: "absolute",
              left: -Math.round(
                pretextSegmentNumberingMarkerBoxWidthPx as number
              ),
              top: 0,
              marginRight: 0,
              zIndex: 1,
              pointerEvents: "none",
            }}
          >
            {numberingLabel.imageSrc ? (
              <>
                <img
                  src={numberingLabel.imageSrc}
                  alt=""
                  aria-hidden="true"
                  draggable={false}
                  style={{
                    display: "inline-block",
                    verticalAlign: "text-bottom",
                    width: numberingLabel.imageWidthPx ?? 12,
                    height: numberingLabel.imageHeightPx ?? 12,
                    marginRight: 2,
                    filter: documentContentFilter,
                  }}
                />
                {numberingLabel.trailingText ?? ""}
              </>
            ) : (
              numberingLabel.text ?? ""
            )}
          </span>
        ) : undefined;
      const resolvedBeforeSpacingPx = Number.isFinite(
        options?.overrideBeforeSpacingPx
      )
        ? Math.max(0, Math.round(options?.overrideBeforeSpacingPx as number))
        : undefined;
      const resolvedAfterSpacingPx = Number.isFinite(
        options?.overrideAfterSpacingPx
      )
        ? Math.max(0, Math.round(options?.overrideAfterSpacingPx as number))
        : undefined;
      const editable =
        !hasImage &&
        !isReadOnly &&
        options?.forceReadOnly !== true &&
        (!hasPartialLineRange || paragraphSegmentIsActiveEditable) &&
        !isManualPageBreakParagraph;
      const resolvedPageLayout = options?.pageLayout ?? documentLayout;
      const paragraphContainsLikelyFullPageCoverImage = node.children.some(
        (child) =>
          child.type === "image" &&
          shouldRenderAbsoluteFloatingImage(child) &&
          likelyFullPageCoverImageRelativeToContentBox(
            child,
            paragraphContentWidthPx,
            resolvedPageLayout.pageHeightPx -
              resolvedPageLayout.marginsPx.top -
              resolvedPageLayout.marginsPx.bottom
          )
      );
      const requiresPageAbsoluteContext =
        paragraphNeedsPageAnchoredAbsolutePositioningContext(node) ||
        paragraphContainsLikelyFullPageCoverImage;
      const requiresLocalAbsoluteContext =
        paragraphNeedsLocalAbsolutePositioningContext(node);
      const paragraphPageFlowTopPx = Math.max(
        0,
        Math.round(options?.pageFlowTopPx ?? 0)
      );
      const hasDualWrappedFloatingImage = Boolean(
        resolveParagraphDualWrappedTextLayout(
          node,
          paragraphRenderTextWidthPx,
          paragraphSegmentLineHeightPx,
          {
            paragraphTopPx: paragraphPageFlowTopPx,
            pageMarginTopPx: resolvedPageLayout.marginsPx.top,
            foreignExclusions: options?.pageFlowForeignExclusions,
          }
        ) ??
          resolveParagraphForeignOnlyWrappedTextLayout(
            node,
            paragraphRenderTextWidthPx,
            paragraphSegmentLineHeightPx,
            options?.pageFlowForeignExclusions ?? []
          )
      );
      const beforeSpacingPx = effectiveParagraphBeforeSpacingPx(
        editor.model,
        nodeIndex,
        node,
        nodeIndex > 0 ? 1 : 0,
        false
      );
      const afterSpacingPx = effectiveParagraphAfterSpacingPx(
        editor.model,
        nodeIndex,
        node
      );
      const pageAbsoluteAnchorOnlyParagraph =
        paragraphNeedsPageWidthAnchorHost(node);
      const pageAnchoredCoverOverlayParagraph =
        paragraphActsAsPageAnchoredCoverOverlayHost(node, resolvedPageLayout);
      const bodyFloatingPageOriginPx =
        requiresPageAbsoluteContext || pageAbsoluteAnchorOnlyParagraph
          ? {
              left: 0,
              top: 0,
              marginLeft: resolvedPageLayout.marginsPx.left,
              marginTop: resolvedPageLayout.marginsPx.top,
              pageWidth: resolvedPageLayout.pageWidthPx,
            }
          : undefined;
      const baseParagraphStyle = paragraphBlockStyle(
        node,
        editor.model.metadata.numberingDefinitions,
        headingStyles,
        nodeDocGridLinePitchPx
      );
      const letterheadStyleAdjustments = options?.letterheadFloatSide
        ? letterheadParagraphStyleAdjustments(
            options.letterheadFloatSide,
            options.oppositeLetterheadFloatSide,
            {
              suppressLetterheadColumnLayout:
                options.suppressLetterheadColumnLayout === true,
            }
          )
        : undefined;
      const paragraphStyle: React.CSSProperties = {
        ...baseParagraphStyle,
        ...(hasPartialLineRange
          ? {
              marginTop:
                paragraphSegmentStartLine === 0
                  ? resolvedBeforeSpacingPx ?? beforeSpacingPx
                  : 0,
              marginBottom:
                paragraphSegmentEndLine >= paragraphSegmentTotalLines
                  ? resolvedAfterSpacingPx ?? afterSpacingPx
                  : 0,
            }
          : resolvedBeforeSpacingPx !== undefined ||
            resolvedAfterSpacingPx !== undefined
          ? {
              ...(resolvedBeforeSpacingPx !== undefined
                ? { marginTop: resolvedBeforeSpacingPx }
                : undefined),
              ...(resolvedAfterSpacingPx !== undefined
                ? { marginBottom: resolvedAfterSpacingPx }
                : undefined),
            }
          : undefined),
        ...(letterheadStyleAdjustments ?? undefined),
        ...(options?.normalizeParagraphHostFontSize
          ? { fontSize: `${paragraphBaseFontSizePx(node)}px` }
          : undefined),
        ...(options?.suppressLetterheadColumnLayout &&
        options?.isFirstInLetterheadColumn
          ? { marginTop: 0 }
          : undefined),
        ...(pageAbsoluteAnchorOnlyParagraph
          ? {
              position: "relative",
              width: resolvedPageLayout.pageWidthPx,
              maxWidth: resolvedPageLayout.pageWidthPx,
              marginTop:
                (typeof baseParagraphStyle.marginTop === "number"
                  ? baseParagraphStyle.marginTop
                  : 0) -
                (pageAnchoredCoverOverlayParagraph
                  ? resolvedPageLayout.marginsPx.top
                  : 0),
              marginLeft: -resolvedPageLayout.marginsPx.left,
              marginRight: -resolvedPageLayout.marginsPx.right,
              // A cover-overlay host is remapped to the page surface and must
              // not displace flow; an ordinary anchor host still occupies its
              // one-line paragraph box (Word semantics).
              ...(pageAnchoredCoverOverlayParagraph
                ? { minHeight: 0, height: 0, lineHeight: 0 }
                : undefined),
              overflow: "visible",
            }
          : requiresPageAbsoluteContext
          ? { position: "static" }
          : requiresLocalAbsoluteContext
          ? { position: "relative" }
          : hasDualWrappedFloatingImage
          ? { position: "relative" }
          : undefined),
        // Carry the paragraph's run font on the editable host so text typed into
        // it (which is a bare, not-yet-committed text node, not a styled run
        // span) renders in the same font the committed run will use — i.e. the
        // font shown in the toolbar — instead of inheriting a generic default.
        // Rendered runs set their own font on their span, so this only affects
        // freshly typed/empty content. When the paragraph has no explicit run
        // font we leave it unset so the document default still applies.
        ...((): React.CSSProperties => {
          const runStyle = firstRunStyle(node);
          const hostFontFamily = cssFontFamily(runStyle?.fontFamily);
          return hostFontFamily ? { fontFamily: hostFontFamily } : {};
        })(),
        outline: "none",
      };
      let fallbackParagraphRuns: React.ReactNode | undefined;
      const getFallbackParagraphRuns = (): React.ReactNode => {
        if (fallbackParagraphRuns === undefined) {
          fallbackParagraphRuns = renderInteractiveParagraphRuns(
            node,
            `node-${nodeIndex}`,
            {
              kind: "paragraph",
              nodeIndex,
            },
            {
              pageFlowTopPx: paragraphPageFlowTopPx,
              pageFlowForeignExclusions: options?.pageFlowForeignExclusions,
              pageLayout: resolvedPageLayout,
              suppressLikelyFullPageCoverImageKeys:
                options?.suppressLikelyFullPageCoverImageKeys,
            }
          );
        }
        return fallbackParagraphRuns;
      };
      const renderedInteractiveParagraphContent =
        shouldRenderParagraphSegmentWithPretext ? (
          <div
            style={{
              minHeight:
                paragraphSegmentVisibleHeightPx +
                paragraphSegmentClipBleedTopPx +
                paragraphSegmentClipBleedBottomPx,
              marginTop: -paragraphSegmentClipBleedTopPx,
              marginBottom: -paragraphSegmentClipBleedBottomPx,
              paddingTop: paragraphSegmentClipBleedTopPx,
              paddingBottom: paragraphSegmentClipBleedBottomPx,
              overflow: "visible",
            }}
          >
            {pretextParagraphSource &&
            pretextParagraphSliceLayout &&
            pretextParagraphSliceLayout.lineCount > 0
              ? renderWrappedPretextParagraph({
                  location: {
                    kind: "paragraph",
                    nodeIndex,
                  },
                  keyPrefix: `node-${nodeIndex}-lines-${paragraphSegmentStartLine}-${paragraphSegmentEndLine}`,
                  source: pretextParagraphSource,
                  layout: pretextParagraphSliceLayout,
                  lineHeightPx: paragraphSegmentLineHeightPx,
                  baseTextStyle: firstRunStyle(node),
                  blockHeightPx: pretextParagraphSliceLayout.height,
                  obstacleNodes: pretextSegmentNumberingMarkerNode,
                })
              : null}
          </div>
        ) : hasPartialLineRange ? (
          <div
            style={{
              height:
                paragraphSegmentVisibleHeightPx +
                paragraphSegmentClipBleedTopPx +
                paragraphSegmentClipBleedBottomPx,
              marginTop: -paragraphSegmentClipBleedTopPx,
              marginBottom: -paragraphSegmentClipBleedBottomPx,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                transform: `translateY(-${
                  paragraphSegmentTranslateYPx - paragraphSegmentClipBleedTopPx
                }px)`,
              }}
            >
              {getFallbackParagraphRuns()}
            </div>
          </div>
        ) : (
          getFallbackParagraphRuns()
        );
      const paragraphDraftHtml = paragraphDraftsRef.current.get(nodeIndex);
      const renderedEditableParagraphHtml =
        typeof paragraphDraftHtml === "string"
          ? paragraphDraftHtml
          : renderStaticHtml(renderedInteractiveParagraphContent);

      return (
        <div
          key={
            paragraphLineRange
              ? `epoch-${paragraphStructureEpoch}-node-${nodeIndex}-lines-${paragraphSegmentStartLine}-${paragraphSegmentEndLine}${
                  options?.syntheticKeySuffix
                    ? `-${options.syntheticKeySuffix}`
                    : ""
                }`
              : `epoch-${paragraphStructureEpoch}-node-${nodeIndex}${
                  options?.syntheticKeySuffix
                    ? `-${options.syntheticKeySuffix}`
                    : ""
                }`
          }
          data-docx-paragraph-host="true"
          data-docx-paragraph-kind="paragraph"
          data-docx-paragraph-node-index={nodeIndex}
          data-docx-paragraph-start-line={paragraphSegmentStartLine}
          data-docx-paragraph-end-line={paragraphSegmentEndLine}
          data-docx-paragraph-partial-line-range={
            hasPartialLineRange ? "true" : undefined
          }
          style={paragraphStyle}
          dangerouslySetInnerHTML={
            editable
              ? {
                  __html: renderedEditableParagraphHtml,
                }
              : undefined
          }
          ref={(element) => {
            if (shouldTrackParagraphElement) {
              if (element) {
                paragraphElementsRef.current.set(nodeIndex, element);
              } else {
                paragraphElementsRef.current.delete(nodeIndex);
              }
            }

            if (!element || !editable) {
              return;
            }

            if (
              paragraphSegmentHasPartialLineRange(paragraphLineRange) &&
              paragraphSegmentIdentityMatches(
                pendingParagraphSegmentFocusRef.current,
                nodeIndex,
                paragraphLineRange
              )
            ) {
              const pendingFocus = pendingParagraphSegmentFocusRef.current;
              pendingParagraphSegmentFocusRef.current = undefined;
              scheduleDomWrite(() => {
                if (!element.isConnected) {
                  return;
                }
                if (pendingFocus?.point) {
                  placeCaretInsideElement(element, pendingFocus.point);
                } else {
                  placeCaretInsideElement(element);
                }
              });
            }

            const draft = paragraphDraftsRef.current.get(nodeIndex);
            if (typeof draft === "string") {
              const draftHtml = draft;
              scheduleDomWrite(() => {
                if (!element.isConnected) {
                  return;
                }
                const latestDraft = paragraphDraftsRef.current.get(nodeIndex);
                if (latestDraft !== draftHtml) {
                  return;
                }

                if (element.innerHTML !== draftHtml) {
                  const activeElement = document.activeElement;
                  const shouldRestoreSelection =
                    activeElement === element ||
                    (activeElement instanceof Element &&
                      element.contains(activeElement));
                  const selectionOffsets = shouldRestoreSelection
                    ? selectionOffsetsWithinElement(element)
                    : undefined;
                  element.innerHTML = draftHtml;
                  if (shouldRestoreSelection) {
                    if (selectionOffsets) {
                      const textLength =
                        editableTextFromElement(element).length;
                      const safeStart = Math.max(
                        0,
                        Math.min(selectionOffsets.start, textLength)
                      );
                      const safeEnd = Math.max(
                        safeStart,
                        Math.min(selectionOffsets.end, textLength)
                      );
                      setSelectionWithinElementByTextOffsets(
                        element,
                        safeStart,
                        safeEnd
                      );
                    } else {
                      placeCaretInsideElement(element);
                    }
                  }
                }

                const latestParagraph = editor.model.nodes[nodeIndex];
                if (
                  latestParagraph &&
                  latestParagraph.type === "paragraph" &&
                  editableTextFromElement(element) ===
                    paragraphText(latestParagraph)
                ) {
                  paragraphDraftsRef.current.delete(nodeIndex);
                }
              });
            }
          }}
          onPointerDown={(event) => {
            if (eventTargetIsInteractiveControl(event.target) || !editable) {
              return;
            }

            if (hasPartialLineRange) {
              activateEditableParagraphSegment(nodeIndex, paragraphLineRange);
            }
            clearObjectSelectionForParagraphEntry(nodeIndex);
          }}
          onClick={(event) => {
            if (eventTargetIsInteractiveControl(event.target)) {
              return;
            }

            clearObjectSelectionForParagraphEntry(nodeIndex);
            if (!editable) {
              if (
                !isReadOnly &&
                hasPartialLineRange &&
                !hasImage &&
                !isManualPageBreakParagraph
              ) {
                activateEditableParagraphSegment(
                  nodeIndex,
                  paragraphLineRange,
                  {
                    x: event.clientX,
                    y: event.clientY,
                  }
                );
                return;
              }
              editor.selectParagraph(nodeIndex);
              return;
            }

            if (event.detail > 1) {
              return;
            }

            if (hasPartialLineRange) {
              activateEditableParagraphSegment(nodeIndex, paragraphLineRange);
            }
            const paragraphElement = event.currentTarget;
            schedulePointerSelectionReconcile(
              paragraphElement,
              {
                x: event.clientX,
                y: event.clientY,
              },
              flushActiveRangeFromSelection
            );
          }}
          onDoubleClick={(event) => {
            if (eventTargetIsInteractiveControl(event.target)) {
              return;
            }

            if (!editable) {
              return;
            }

            if (hasPartialLineRange) {
              activateEditableParagraphSegment(nodeIndex, paragraphLineRange);
            }
            const paragraphElement = event.currentTarget;
            schedulePointerSelectionReconcile(
              paragraphElement,
              {
                x: event.clientX,
                y: event.clientY,
              },
              flushActiveRangeFromSelection
            );
          }}
          contentEditable={editable}
          suppressContentEditableWarning
          onMouseUp={(event) => {
            if (!editable) {
              return;
            }

            // Synchronously capture the post-click/drag selection (before any
            // re-render can clobber it) so the selection invariant restores the
            // exact position the pointer landed on, not a stale one.
            const mouseUpOffsets = selectionOffsetsWithinElement(
              event.currentTarget
            );
            if (mouseUpOffsets) {
              pendingEditableCaretRef.current = {
                nodeIndex,
                start: mouseUpOffsets.start,
                end: mouseUpOffsets.end,
              };
            }

            if (selectionIsExpandedWithinElement(event.currentTarget)) {
              flushActiveRangeFromSelection();
              cancelPendingPointerSelectionReconcile();
              return;
            }

            if (hasPartialLineRange) {
              activateEditableParagraphSegment(nodeIndex, paragraphLineRange);
            }
            const paragraphElement = event.currentTarget;
            schedulePointerSelectionReconcile(
              paragraphElement,
              {
                x: event.clientX,
                y: event.clientY,
              },
              flushActiveRangeFromSelection
            );
          }}
          onKeyUp={(event) => {
            if (!editable || !shouldSyncActiveRangeOnKeyUp(event)) {
              return;
            }

            if (hasPartialLineRange) {
              activateEditableParagraphSegment(nodeIndex, paragraphLineRange);
            }
            updateActiveTextRangeFromElement(event.currentTarget, {
              kind: "paragraph",
              nodeIndex,
            });
          }}
          onInput={(event) => {
            if (!editable) {
              return;
            }

            cancelPendingPointerSelectionReconcile();
            editor.beginSelectionSession("keyboard", { settleAfterMs: 220 });
            if (hasPartialLineRange) {
              activateEditableParagraphSegment(nodeIndex, paragraphLineRange);
            }
            schedulePaginationMeasurementResume();
            paragraphDraftsRef.current.set(
              nodeIndex,
              event.currentTarget.innerHTML
            );
            // Capture the live caret offset now (post-insert, pre-render) so the
            // selection invariant can restore it after React rewrites innerHTML.
            const typedOffsets = selectionOffsetsWithinElement(
              event.currentTarget
            );
            pendingEditableCaretRef.current = typedOffsets
              ? {
                  nodeIndex,
                  start: typedOffsets.start,
                  end: typedOffsets.end,
                }
              : null;
          }}
          onCompositionStart={() => {
            if (!editable) {
              return;
            }
            cancelPendingPointerSelectionReconcile();
            editor.beginSelectionSession("composition");
          }}
          onCompositionEnd={() => {
            if (!editable) {
              return;
            }
            editor.beginSelectionSession("keyboard", { settleAfterMs: 260 });
          }}
          onPaste={(event) => {
            const pastedText = event.clipboardData.getData("text/plain");
            if (!pastedText) {
              return;
            }

            const replaced = replaceExpandedSelectionWithText(pastedText);
            if (!replaced) {
              return;
            }

            event.preventDefault();
            event.stopPropagation();
            paragraphDraftsRef.current.delete(nodeIndex);
          }}
          onKeyDown={(event) => {
            if (!editable) {
              return;
            }

            cancelPendingPointerSelectionReconcile();
            editor.beginSelectionSession("keyboard", { settleAfterMs: 220 });
            if (hasPartialLineRange) {
              activateEditableParagraphSegment(nodeIndex, paragraphLineRange);
            }
            if (handleDeleteAcrossTableCellSelection(event)) {
              return;
            }

            if (handleDeleteAcrossSelection(event)) {
              return;
            }

            const handledHistoryShortcut = handleEditorHistoryShortcut(
              event,
              () => {
                commitParagraphDraftFromElement(
                  nodeIndex,
                  node,
                  event.currentTarget
                );
              }
            );
            if (handledHistoryShortcut) {
              return;
            }

            const isPlainTextInsertKey =
              event.key.length === 1 &&
              !event.metaKey &&
              !event.ctrlKey &&
              !event.altKey;
            if (isPlainTextInsertKey) {
              const replaced = replaceExpandedSelectionWithText(event.key);
              if (replaced) {
                event.preventDefault();
                event.stopPropagation();
                paragraphDraftsRef.current.delete(nodeIndex);
                return;
              }
            }

            const isPlainEnterKey =
              event.key === "Enter" &&
              !event.metaKey &&
              !event.ctrlKey &&
              !event.altKey;
            if (isPlainEnterKey && event.shiftKey) {
              const replaced = replaceExpandedSelectionWithText("\n");
              if (replaced) {
                event.preventDefault();
                event.stopPropagation();
                paragraphDraftsRef.current.delete(nodeIndex);
                return;
              }
            }

            const currentText = editableTextFromElement(event.currentTarget);
            const currentLocation: ParagraphLocation = {
              kind: "paragraph",
              nodeIndex,
            };
            const selectionOffsets =
              selectionOffsetsWithinElement(event.currentTarget) ??
              (() => {
                const activeRange = editor.activeTextRange
                  ? normalizeTextRange(editor.activeTextRange)
                  : undefined;
                if (
                  !activeRange ||
                  !sameParagraphLocation(
                    activeRange.start.location,
                    currentLocation
                  ) ||
                  !sameParagraphLocation(
                    activeRange.end.location,
                    currentLocation
                  )
                ) {
                  return undefined;
                }
                const safeStart = Math.max(
                  0,
                  Math.min(
                    Math.round(activeRange.start.offset),
                    currentText.length
                  )
                );
                const safeEnd = Math.max(
                  safeStart,
                  Math.min(
                    Math.round(activeRange.end.offset),
                    currentText.length
                  )
                );
                return {
                  start: safeStart,
                  end: safeEnd,
                };
              })();
            const isListParagraph = paragraphIsList(node, currentText);
            const isCollapsedSelection =
              selectionOffsets !== undefined &&
              selectionOffsets.start === selectionOffsets.end;
            const collapsedSelectionOffset = selectionOffsets?.start ?? -1;
            const listStartBackspaceOffset = paragraphHasNumbering(node)
              ? 0
              : listPrefixLength(currentText);

            const isPlainBackspaceOrDelete =
              (event.key === "Backspace" || event.key === "Delete") &&
              !event.metaKey &&
              !event.ctrlKey &&
              !event.altKey &&
              !event.shiftKey;
            const isPlainVerticalArrowKey =
              (event.key === "ArrowUp" || event.key === "ArrowDown") &&
              !event.metaKey &&
              !event.ctrlKey &&
              !event.altKey &&
              !event.shiftKey;

            if (
              isPlainVerticalArrowKey &&
              isCollapsedSelection &&
              collapsedSelectionOffset >= 0
            ) {
              if (event.key === "ArrowUp" && collapsedSelectionOffset === 0) {
                let previousLocation: ParagraphLocation | undefined;
                for (
                  let scanNodeIndex = nodeIndex - 1;
                  scanNodeIndex >= 0;
                  scanNodeIndex -= 1
                ) {
                  const candidate = lastParagraphLocationInNode(
                    editor.model,
                    scanNodeIndex
                  );
                  if (candidate) {
                    previousLocation = candidate;
                    break;
                  }
                }

                const previousParagraph = previousLocation
                  ? getParagraphAtLocation(editor.model, previousLocation)
                      .paragraph
                  : undefined;
                if (previousLocation && previousParagraph) {
                  event.preventDefault();
                  event.stopPropagation();
                  paragraphDraftsRef.current.delete(nodeIndex);
                  const caretClientPoint =
                    collapsedCaretClientPointWithinElement(event.currentTarget);
                  if (
                    caretClientPoint &&
                    focusParagraphLocationAtClientPoint(
                      previousLocation,
                      caretClientPoint,
                      0
                    )
                  ) {
                    return;
                  }
                  focusParagraphLocationAtOffset(previousLocation, 0);
                  return;
                }
              }

              if (
                event.key === "ArrowDown" &&
                collapsedSelectionOffset === currentText.length
              ) {
                const nextLocation = nextParagraphLocation(
                  editor.model,
                  currentLocation
                );
                const nextParagraph = nextLocation
                  ? getParagraphAtLocation(editor.model, nextLocation).paragraph
                  : undefined;
                if (nextLocation && nextParagraph) {
                  event.preventDefault();
                  event.stopPropagation();
                  paragraphDraftsRef.current.delete(nodeIndex);
                  const caretClientPoint =
                    collapsedCaretClientPointWithinElement(event.currentTarget);
                  const fallbackOffset = paragraphText(nextParagraph).length;
                  if (
                    caretClientPoint &&
                    focusParagraphLocationAtClientPoint(
                      nextLocation,
                      caretClientPoint,
                      fallbackOffset
                    )
                  ) {
                    return;
                  }
                  focusParagraphLocationAtOffset(
                    nextLocation,
                    paragraphText(nextParagraph).length
                  );
                  return;
                }
              }
            }

            if (
              isPlainBackspaceOrDelete &&
              isCollapsedSelection &&
              collapsedSelectionOffset >= 0
            ) {
              if (event.key === "Backspace" && collapsedSelectionOffset === 0) {
                let previousLocation: ParagraphLocation | undefined;
                for (
                  let scanNodeIndex = nodeIndex - 1;
                  scanNodeIndex >= 0;
                  scanNodeIndex -= 1
                ) {
                  const candidate = lastParagraphLocationInNode(
                    editor.model,
                    scanNodeIndex
                  );
                  if (candidate) {
                    previousLocation = candidate;
                    break;
                  }
                }

                const previousParagraph = previousLocation
                  ? getParagraphAtLocation(editor.model, previousLocation)
                      .paragraph
                  : undefined;
                if (previousLocation && previousParagraph) {
                  event.preventDefault();
                  event.stopPropagation();
                  paragraphDraftsRef.current.delete(nodeIndex);
                  const collapsedRange = editor.deleteExpandedSelection({
                    start: {
                      location: cloneTextRangeLocation(previousLocation),
                      offset: paragraphText(previousParagraph).length,
                    },
                    end: {
                      location: cloneTextRangeLocation(currentLocation),
                      offset: 0,
                    },
                  });
                  if (collapsedRange) {
                    syncSelectionFromDocxRange(collapsedRange);
                  }
                  return;
                }
              }

              if (
                event.key === "Delete" &&
                collapsedSelectionOffset === currentText.length
              ) {
                const nextLocation = nextParagraphLocation(
                  editor.model,
                  currentLocation
                );
                const nextParagraph = nextLocation
                  ? getParagraphAtLocation(editor.model, nextLocation).paragraph
                  : undefined;
                if (nextLocation && nextParagraph) {
                  event.preventDefault();
                  event.stopPropagation();
                  paragraphDraftsRef.current.delete(nodeIndex);
                  const collapsedRange = editor.deleteExpandedSelection({
                    start: {
                      location: cloneTextRangeLocation(currentLocation),
                      offset: currentText.length,
                    },
                    end: {
                      location: cloneTextRangeLocation(nextLocation),
                      offset: 0,
                    },
                  });
                  if (collapsedRange) {
                    syncSelectionFromDocxRange(collapsedRange);
                  }
                  return;
                }
              }
            }

            if (
              event.key === "Backspace" &&
              !event.metaKey &&
              !event.ctrlKey &&
              !event.altKey &&
              !event.shiftKey &&
              isListParagraph &&
              isCollapsedSelection &&
              collapsedSelectionOffset >= 0 &&
              collapsedSelectionOffset <= listStartBackspaceOffset
            ) {
              const listDepthChanged = editor.adjustSelectedListDepth(
                -1,
                currentText
              );
              let handledBackspace = listDepthChanged;

              if (!handledBackspace) {
                const listType = paragraphListType(
                  node,
                  editor.model.metadata.numberingDefinitions
                );
                if (listType) {
                  editor.toggleList(listType);
                  handledBackspace = true;
                }
              }

              if (handledBackspace) {
                event.preventDefault();
                event.stopPropagation();
                paragraphDraftsRef.current.delete(nodeIndex);
                focusParagraphAtOffset(nodeIndex, 0);
                return;
              }
            }

            if (isPlainEnterKey && event.shiftKey) {
              event.preventDefault();
              event.stopPropagation();
              const start = Math.max(
                0,
                Math.min(
                  selectionOffsets?.start ?? currentText.length,
                  currentText.length
                )
              );
              const end = Math.max(
                start,
                Math.min(selectionOffsets?.end ?? start, currentText.length)
              );
              const nextText = `${currentText.slice(
                0,
                start
              )}\n${currentText.slice(end)}`;
              paragraphDraftsRef.current.delete(nodeIndex);
              editor.commitParagraphText(nodeIndex, nextText);
              focusParagraphAtOffset(nodeIndex, start + 1);
              return;
            }

            if (
              event.key === "Tab" &&
              !event.metaKey &&
              !event.ctrlKey &&
              !event.altKey &&
              isListParagraph
            ) {
              event.preventDefault();
              const listDepthChanged = editor.adjustSelectedListDepth(
                event.shiftKey ? -1 : 1,
                currentText
              );
              if (listDepthChanged) {
                paragraphDraftsRef.current.delete(nodeIndex);
                const targetOffset =
                  selectionOffsets?.end ?? currentText.length;
                focusParagraphAtOffset(nodeIndex, targetOffset);
              }
              return;
            }

            if (event.key === "Enter" && !event.shiftKey && isListParagraph) {
              event.preventDefault();
              event.stopPropagation();
              const start = selectionOffsets?.start ?? currentText.length;
              const end = selectionOffsets?.end ?? start;
              paragraphDraftsRef.current.clear();
              tableCellDraftsRef.current.clear();
              tableCellParagraphDraftsRef.current.clear();
              const insertedListItem = editor.insertListItemAfterSelection(
                currentText,
                start,
                end,
                {
                  kind: "paragraph",
                  nodeIndex,
                }
              );
              if (insertedListItem !== undefined) {
                focusParagraphAtOffset(
                  insertedListItem.paragraphIndex,
                  insertedListItem.caretOffset
                );
              }
              return;
            }

            if (isPlainEnterKey && !event.shiftKey && !isListParagraph) {
              event.preventDefault();
              event.stopPropagation();
              const start = Math.max(
                0,
                Math.min(
                  selectionOffsets?.start ?? currentText.length,
                  currentText.length
                )
              );
              const end = Math.max(
                start,
                Math.min(selectionOffsets?.end ?? start, currentText.length)
              );
              paragraphDraftsRef.current.clear();
              tableCellDraftsRef.current.clear();
              tableCellParagraphDraftsRef.current.clear();
              const splitParagraph = editor.splitParagraphAtSelection(
                currentText,
                start,
                end,
                {
                  kind: "paragraph",
                  nodeIndex,
                }
              );
              if (splitParagraph !== undefined) {
                focusParagraphAtOffset(
                  splitParagraph.paragraphIndex,
                  splitParagraph.caretOffset
                );
              }
              return;
            }
          }}
          onBlur={(event) => {
            if (!editable) {
              return;
            }

            if (!paragraphDraftsRef.current.has(nodeIndex)) {
              return;
            }

            commitParagraphDraftFromElement(
              nodeIndex,
              node,
              event.currentTarget
            );
          }}
        >
          {!editable ? renderedInteractiveParagraphContent : null}
        </div>
      );
    }

    const tableRowSliceOffsetPxForWrapper = tableRowSlice
      ? Math.max(0, Math.round(tableRowSlice.startOffsetPx))
      : 0;
    const tableRowSliceTopBleedPxForWrapper =
      tableRowSlice && tableRowSliceOffsetPxForWrapper > 0
        ? TABLE_ROW_SLICE_VISUAL_BLEED_PX
        : 0;
    const tableRowSliceBottomBleedPxForWrapper = tableRowSlice
      ? TABLE_ROW_SLICE_VISUAL_BLEED_PX
      : 0;

    return (
      <div
        key={
          tableRowRange
            ? `node-${nodeIndex}-rows-${tableRowRange.startRowIndex}-${
                tableRowRange.endRowIndex
              }${
                tableRowSlice
                  ? `-slice-${Math.max(
                      0,
                      Math.round(tableRowSlice.startOffsetPx)
                    )}-${Math.max(
                      MIN_PARAGRAPH_LINE_HEIGHT_PX,
                      Math.round(tableRowSlice.sliceHeightPx)
                    )}`
                  : ""
              }`
            : `node-${nodeIndex}`
        }
        ref={(element) => {
          const shouldTrackTableRef =
            !tableRowRange || tableRowRange.startRowIndex === 0;
          if (!shouldTrackTableRef) {
            return;
          }

          if (element) {
            tableElementsRef.current.set(nodeIndex, element);
          } else {
            tableElementsRef.current.delete(nodeIndex);
          }
        }}
        style={{
          ...tableWrapperStyle(
            node,
            twipsToSignedPixels(node.style?.indentTwips) ?? 0
          ),
          ...(tableRowSlice
            ? {
                height: Math.max(
                  MIN_PARAGRAPH_LINE_HEIGHT_PX,
                  Math.round(tableRowSlice.sliceHeightPx) +
                    tableRowSliceTopBleedPxForWrapper +
                    tableRowSliceBottomBleedPxForWrapper
                ),
                paddingTop: tableRowSliceTopBleedPxForWrapper,
                paddingBottom: tableRowSliceBottomBleedPxForWrapper,
                boxSizing: "border-box",
                overflow: "visible",
              }
            : undefined),
        }}
        onPointerEnter={() => {
          if (isReadOnly) {
            return;
          }
          setHoveredTableHandleTableIndex(nodeIndex);
        }}
        onPointerLeave={(event) => {
          if (isReadOnly) {
            return;
          }
          if (tableMoveDragRef.current?.tableIndex === nodeIndex) {
            return;
          }
          if (
            isPointWithinTableHandleHoverZone(
              nodeIndex,
              event.clientX,
              event.clientY
            )
          ) {
            return;
          }
          const nextTarget = event.relatedTarget;
          if (
            nextTarget instanceof Node &&
            event.currentTarget.contains(nextTarget)
          ) {
            return;
          }
          setHoveredTableHandleTableIndex((current) =>
            current === nodeIndex ? undefined : current
          );
        }}
      >
        {(() => {
          const tableIndentPx =
            twipsToSignedPixels(node.style?.indentTwips) ?? 0;
          const columnCount = tableColumnCount(node);
          const collapsedHorizontalBorderBleedPx =
            resolveCollapsedTableHorizontalOuterBleedPx(node, columnCount);
          const maxTableWidthPx = Math.max(
            120,
            nodeContentWidthPx -
              tableIndentPx -
              collapsedHorizontalBorderBleedPx
          );
          const tableWidthPx = twipsToPixels(node.style?.widthTwips);
          const hasStoredColumnWidths =
            (tableColumnWidths[nodeIndex]?.length ?? 0) === columnCount;
          const rawColumnWidthsPx = resolveTableColumnWidths(
            nodeIndex,
            node,
            tableWidthPx
          );
          const rawResolvedTableWidthPx = hasStoredColumnWidths
            ? rawColumnWidthsPx.reduce((sum, widthPx) => sum + widthPx, 0)
            : tableWidthPx ??
              rawColumnWidthsPx.reduce((sum, widthPx) => sum + widthPx, 0);
          const resolvedTableWidthPx = clampTableWidthPx(
            rawResolvedTableWidthPx,
            maxTableWidthPx
          );
          const tableBorderSpacingPx =
            resolveTableSeparateBorderSpacingPx(node);
          const tableColumnFitWidthPx = tableUsesSeparateBorderModel(node)
            ? Math.max(
                1,
                resolvedTableWidthPx -
                  tableBorderSpacingPx * Math.max(0, columnCount + 1)
              )
            : resolvedTableWidthPx;
          const { columnWidthsPx, effectiveColumnCount } =
            resolveFittedTableColumnWidths(
              node,
              rawColumnWidthsPx,
              tableColumnFitWidthPx
            );
          const rowHeightsPx = resolveTableRowHeights(nodeIndex, node);
          const tableCellMarginTwips = node.style?.cellMarginTwips;
          const tableBorders = node.style?.borders;
          const applyWordTableDefaults =
            tableUsesWordLikeParagraphDefaults(node);
          const tableRowStartIndex = Math.max(
            0,
            tableRowRange?.startRowIndex ?? 0
          );
          const tableRowEndIndex = Math.min(
            node.rows.length,
            tableRowRange?.endRowIndex ?? node.rows.length
          );
          const visibleRows = node.rows.slice(
            tableRowStartIndex,
            tableRowEndIndex
          );
          const hasTableRowSlice =
            Boolean(tableRowSlice) &&
            tableRowSlice!.rowIndex >= tableRowStartIndex &&
            tableRowSlice!.rowIndex < tableRowEndIndex;
          const tableRowSliceOffsetPx = hasTableRowSlice
            ? Math.max(0, Math.round(tableRowSlice!.startOffsetPx))
            : 0;
          const tableRowSliceTopBleedPx =
            hasTableRowSlice && tableRowSliceOffsetPx > 0
              ? TABLE_ROW_SLICE_VISUAL_BLEED_PX
              : 0;
          const isSplitTableSegment =
            tableRowStartIndex > 0 || tableRowEndIndex < node.rows.length;
          const isCornerResizing =
            !isSplitTableSegment && activeTableResize?.tableIndex === nodeIndex;
          const isColumnHandleActive =
            !isSplitTableSegment &&
            activeColumnResize?.tableIndex === nodeIndex;
          const isRowHandleActive =
            !isSplitTableSegment && activeRowResize?.tableIndex === nodeIndex;
          const isTableMoveDragging =
            tableMoveDragRef.current?.tableIndex === nodeIndex;
          const isTableContextMenuOpen =
            tableContextMenuState?.tableIndex === nodeIndex;
          const tableHasActiveCursor =
            (editor.selection.kind === "table-cell" &&
              editor.selection.tableIndex === nodeIndex) ||
            tableCellSelectionRange?.tableIndex === nodeIndex ||
            textRangeTouchesTable(editor.activeTextRange, nodeIndex);
          const showTableHandles =
            !isReadOnly &&
            !hasTableRowSlice &&
            (hoveredTableHandleTableIndex === nodeIndex ||
              tableHasActiveCursor ||
              isTableMoveDragging ||
              isColumnHandleActive ||
              isRowHandleActive ||
              isCornerResizing ||
              isTableContextMenuOpen);
          const tableColumnBoundaryOffsetsPx = columnWidthsPx.reduce(
            (offsets: number[], widthPx: number) => {
              const previous = offsets[offsets.length - 1] ?? 0;
              offsets.push(previous + widthPx);
              return offsets;
            },
            [] as number[]
          );
          const normalizedRowHeightsPx = rowHeightsPx.map((value) =>
            Number.isFinite(value) && (value as number) > 0
              ? Math.max(24, value as number)
              : 24
          );
          const measuredRowHeightsPx = tableMeasuredRowHeights[nodeIndex];
          const rowBoundarySourceHeightsPx =
            measuredRowHeightsPx &&
            measuredRowHeightsPx.length === node.rows.length
              ? measuredRowHeightsPx.map((value) =>
                  Number.isFinite(value) && (value as number) > 0
                    ? Math.max(24, value as number)
                    : 24
                )
              : normalizedRowHeightsPx;
          const tableRowBoundaryOffsetsPx = rowBoundarySourceHeightsPx.reduce(
            (offsets: number[], rowHeightPx: number) => {
              const previous = offsets[offsets.length - 1] ?? 0;
              offsets.push(previous + rowHeightPx);
              return offsets;
            },
            [] as number[]
          );
          const maxBoundaryColumnIndex = Math.max(0, effectiveColumnCount - 2);
          const maxBoundaryRowIndex = Math.max(0, node.rows.length - 2);
          const showTableMoveHandle =
            showTableHandles && tableRowStartIndex === 0;
          const tableMoveHandleToneStyle: React.CSSProperties =
            editor.documentTheme === "dark"
              ? {
                  backgroundColor: "#111827",
                  border: "1px solid #374151",
                  color: "#cbd5e1",
                  boxShadow: "0 1px 2px rgba(2, 6, 23, 0.6)",
                }
              : {
                  backgroundColor: "#ffffff",
                  border: "1px solid #d4d4d8",
                  color: "#4b5563",
                  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.14)",
                };

          return (
            <>
              {showTableHandles ? (
                <>
                  <span
                    contentEditable={false}
                    data-docx-pagination-ignore="true"
                    style={{
                      position: "absolute",
                      top: -TABLE_HANDLE_SAFEZONE_TOP_PX,
                      left: -TABLE_HANDLE_SAFEZONE_LEFT_PX,
                      width: TABLE_HANDLE_SAFEZONE_LEFT_PX,
                      height: TABLE_HANDLE_SAFEZONE_TOP_PX,
                      backgroundColor: "transparent",
                      zIndex: 4,
                    }}
                    onPointerEnter={() =>
                      setHoveredTableHandleTableIndex(nodeIndex)
                    }
                    onMouseEnter={() =>
                      setHoveredTableHandleTableIndex(nodeIndex)
                    }
                  />
                  <span
                    contentEditable={false}
                    data-docx-pagination-ignore="true"
                    style={{
                      position: "absolute",
                      right: -TABLE_HANDLE_SAFEZONE_RIGHT_PX,
                      bottom: -TABLE_HANDLE_SAFEZONE_BOTTOM_PX,
                      width:
                        TABLE_RESIZE_HANDLE_HIT_SIZE +
                        TABLE_HANDLE_SAFEZONE_RIGHT_PX,
                      height:
                        TABLE_RESIZE_HANDLE_HIT_SIZE +
                        TABLE_HANDLE_SAFEZONE_BOTTOM_PX,
                      backgroundColor: "transparent",
                      zIndex: 4,
                    }}
                    onPointerEnter={() =>
                      setHoveredTableHandleTableIndex(nodeIndex)
                    }
                    onMouseEnter={() =>
                      setHoveredTableHandleTableIndex(nodeIndex)
                    }
                  />
                </>
              ) : null}
              {showTableMoveHandle ? (
                <span
                  contentEditable={false}
                  data-docx-table-move-handle="true"
                  title="Select or drag to move table"
                  style={{
                    position: "absolute",
                    top: -TABLE_MOVE_HANDLE_HIT_SIZE - 4,
                    left: -TABLE_MOVE_HANDLE_HIT_SIZE - 4,
                    width: TABLE_MOVE_HANDLE_HIT_SIZE,
                    height: TABLE_MOVE_HANDLE_HIT_SIZE,
                    display: "grid",
                    placeItems: "center",
                    cursor: "move",
                    zIndex: 7,
                    touchAction: "none",
                    userSelect: "none",
                  }}
                  onPointerDown={(event) => {
                    beginTableMoveDrag(event, nodeIndex);
                  }}
                  onContextMenu={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    const context = resolveTableContextMenuLocation(
                      nodeIndex,
                      0,
                      0
                    );
                    selectWholeTable(nodeIndex);
                    openTableContextMenu(context, {
                      x: event.clientX,
                      y: event.clientY,
                    });
                  }}
                >
                  <span
                    style={{
                      ...TABLE_MOVE_HANDLE_STYLE,
                      ...tableMoveHandleToneStyle,
                    }}
                  >
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M12 2v20" />
                      <path d="m7 7 5-5 5 5" />
                      <path d="m7 17 5 5 5-5" />
                      <path d="M2 12h20" />
                      <path d="m7 7-5 5 5 5" />
                      <path d="m17 7 5 5-5 5" />
                    </svg>
                  </span>
                </span>
              ) : null}
              <table
                style={{
                  width:
                    resolvedTableWidthPx > 0
                      ? `${resolvedTableWidthPx}px`
                      : "100%",
                  ...tableElementBorderStyle(node, tableBorderSpacingPx),
                  tableLayout:
                    node.style?.layout === "autofit" ? "auto" : "fixed",
                }}
              >
                <colgroup>
                  {columnWidthsPx.map((widthPx, columnIndex) => (
                    <col
                      key={`table-col-${nodeIndex}-${columnIndex}`}
                      style={{ width: `${widthPx}px` }}
                    />
                  ))}
                </colgroup>
                <tbody>
                  {visibleRows.map((row, visibleRowIndex) => {
                    const rowIndex = tableRowStartIndex + visibleRowIndex;
                    const isSlicedRow =
                      hasTableRowSlice &&
                      tableRowSlice !== undefined &&
                      rowIndex === tableRowSlice.rowIndex;
                    const rowHeightPx = rowHeightsPx[rowIndex];
                    const hasCustomRowHeight =
                      tableRowHeights[nodeIndex]?.[rowIndex] !== undefined;
                    const hasResolvedRowHeight =
                      typeof rowHeightPx === "number" &&
                      Number.isFinite(rowHeightPx) &&
                      rowHeightPx > 0;
                    const slicedRowViewportHeightPx =
                      isSlicedRow && tableRowSlice
                        ? Math.max(
                            MIN_PARAGRAPH_LINE_HEIGHT_PX,
                            Math.round(tableRowSlice.sliceHeightPx)
                          )
                        : undefined;
                    const resolvedRowHeightStyle = slicedRowViewportHeightPx
                      ? {
                          height: `${slicedRowViewportHeightPx}px`,
                          maxHeight: `${slicedRowViewportHeightPx}px`,
                          overflow: "hidden" as const,
                        }
                      : hasResolvedRowHeight
                      ? resolveTableRowHeightCss(row, rowHeightPx)
                      : undefined;
                    let columnCursor = 0;

                    return (
                      <tr
                        key={`row-${nodeIndex}-${rowIndex}`}
                        data-docx-table-index={nodeIndex}
                        data-docx-row-index={rowIndex}
                        data-docx-row-sliced={isSlicedRow ? "true" : undefined}
                        style={resolvedRowHeightStyle}
                      >
                        {row.cells.map((cell, cellIndex) => {
                          const selectedByRange =
                            isCellWithinTableSelectionRange(
                              tableCellSelectionRange,
                              nodeIndex,
                              rowIndex,
                              cellIndex
                            );
                          const selectedByAnchor = isCellSelected(
                            editor.selection,
                            nodeIndex,
                            rowIndex,
                            cellIndex
                          );
                          const isMultiCellSelectionActive = Boolean(
                            tableCellSelectionRange &&
                              tableCellSelectionRange.tableIndex ===
                                nodeIndex &&
                              !isSingleTableCellSelectionRange(
                                tableCellSelectionRange
                              )
                          );
                          const cellParagraphs = tableCellParagraphs(
                            cell.nodes
                          );
                          const recursiveCellParagraphs =
                            tableCellParagraphsRecursively(cell.nodes);
                          const cellHasImage = tableCellHasImage(cell.nodes);
                          const editableCell =
                            selectedByAnchor &&
                            !isSlicedRow &&
                            !isMultiCellSelectionActive &&
                            !cellHasImage &&
                            !isReadOnly &&
                            cellParagraphs.length > 0;
                          const nestedParagraphIndexesByNode = new Map<
                            ParagraphNode,
                            number
                          >();
                          recursiveCellParagraphs.forEach(
                            (paragraph, paragraphIndex) => {
                              nestedParagraphIndexesByNode.set(
                                paragraph,
                                paragraphIndex
                              );
                            }
                          );
                          const tableCellEditScope:
                            | TableCellParagraphEditScope
                            | undefined =
                            recursiveCellParagraphs.length > 0 &&
                            !isReadOnly &&
                            !isSlicedRow &&
                            !isMultiCellSelectionActive &&
                            !cellHasImage
                              ? {
                                  isEditable: true,
                                  draftKeyPrefix: `${nodeIndex}:${rowIndex}:${cellIndex}`,
                                  paragraphIndexesByNode:
                                    nestedParagraphIndexesByNode,
                                  handlers: {
                                    registerElement:
                                      registerTableCellParagraphElement,
                                    readDraftHtml: readTableCellParagraphDraft,
                                    writeDraftHtml:
                                      writeTableCellParagraphDraft,
                                    clearDraftHtml:
                                      clearTableCellParagraphDraft,
                                    commitText: (paragraphIndex, text) => {
                                      commitTableCellParagraphDraft(
                                        nodeIndex,
                                        rowIndex,
                                        cellIndex,
                                        paragraphIndex,
                                        text
                                      );
                                    },
                                  },
                                }
                              : undefined;
                          const cellDraftKey = `${nodeIndex}:${rowIndex}:${cellIndex}`;
                          const cellElementKey = `cell-${nodeIndex}-${rowIndex}-${cellIndex}`;
                          const colSpanValue =
                            cell.style?.gridSpan && cell.style.gridSpan > 1
                              ? cell.style.gridSpan
                              : 1;
                          const rowSpanValue =
                            cell.style?.rowSpan && cell.style.rowSpan > 1
                              ? cell.style.rowSpan
                              : 1;
                          const colSpan =
                            colSpanValue > 1 ? colSpanValue : undefined;
                          const rowSpan =
                            rowSpanValue > 1 ? rowSpanValue : undefined;
                          // Word clips hRule="exact" row content at the
                          // declared height. A vertically merged cell occupies
                          // every row it spans, so its clip height is the sum
                          // of the spanned rows — and only applies when each
                          // spanned row's height is fixed.
                          const exactRowSpanRows = node.rows.slice(
                            rowIndex,
                            rowIndex + rowSpanValue
                          );
                          const exactRowSpanClipHeightPx =
                            exactRowSpanRows.length > 0 &&
                            exactRowSpanRows.every((spannedRow, rowOffset) => {
                              const spannedHeightPx =
                                rowHeightsPx[rowIndex + rowOffset];
                              return (
                                spannedRow.style?.heightRule === "exact" &&
                                Number.isFinite(spannedHeightPx) &&
                                (spannedHeightPx as number) > 0
                              );
                            })
                              ? exactRowSpanRows.reduce(
                                  (sum, _spannedRow, rowOffset) =>
                                    sum +
                                    Math.max(
                                      MIN_PARAGRAPH_LINE_HEIGHT_PX,
                                      Math.round(
                                        rowHeightsPx[
                                          rowIndex + rowOffset
                                        ] as number
                                      )
                                    ),
                                  0
                                )
                              : undefined;
                          const startColumnIndex = columnCursor;
                          const boundaryColumnIndex =
                            startColumnIndex + colSpanValue - 1;
                          columnCursor += colSpanValue;
                          if (cell.style?.vMergeContinuation) {
                            return null;
                          }
                          const cellWidthPx = twipsToPixels(
                            cell.style?.widthTwips
                          );
                          const spannedWidthPx = columnWidthsPx
                            .slice(
                              startColumnIndex,
                              startColumnIndex + colSpanValue
                            )
                            .reduce((sum, widthPx) => sum + widthPx, 0);

                          const cellBackgroundColor =
                            cell.style?.backgroundColor ??
                            row.style?.backgroundColor;
                          const hasCustomCellRowHeight =
                            hasCustomRowHeight || hasResolvedRowHeight;
                          const mergedCellMarginTwips = mergeTableSpacing(
                            tableCellMarginTwips,
                            cell.style?.marginTwips
                          );
                          const cellPaddingPx = resolveTableSpacingPaddingPx(
                            mergedCellMarginTwips
                          );
                          const cellRenderedWidthPx =
                            cellWidthPx ?? spannedWidthPx;
                          const cellContentWidthPx = Math.max(
                            1,
                            cellRenderedWidthPx -
                              cellPaddingPx.left -
                              cellPaddingPx.right
                          );
                          const slicedRowHeightPx = Math.max(
                            MIN_PARAGRAPH_LINE_HEIGHT_PX,
                            Math.round(
                              rowBoundarySourceHeightsPx[rowIndex] ??
                                rowHeightPx ??
                                MIN_PARAGRAPH_LINE_HEIGHT_PX
                            )
                          );
                          const slicedCellViewportHeightPx =
                            isSlicedRow && tableRowSlice
                              ? Math.max(
                                  MIN_PARAGRAPH_LINE_HEIGHT_PX,
                                  Math.round(tableRowSlice.sliceHeightPx)
                                )
                              : undefined;
                          const slicedParagraphPlans =
                            isSlicedRow &&
                            tableRowSlice &&
                            !editableCell &&
                            slicedCellViewportHeightPx
                              ? resolveTableCellParagraphSlicePlans({
                                  cell,
                                  rowHeightPx: slicedRowHeightPx,
                                  sliceStartPx: tableRowSliceOffsetPx,
                                  sliceHeightPx: slicedCellViewportHeightPx,
                                  cellContentWidthPx,
                                  tableCellMarginTwips,
                                  applyWordTableDefaults,
                                  docGridLinePitchPx: nodeDocGridLinePitchPx,
                                })
                              : undefined;
                          const renderedEditableCellContent = editableCell
                            ? (() => {
                                let paragraphCursor = 0;
                                return cell.nodes.map(
                                  (cellContent, contentIndex) => {
                                    if (cellContent.type === "paragraph") {
                                      const paragraphIndex = paragraphCursor;
                                      paragraphCursor += 1;
                                      return (
                                        <div
                                          key={`active-cell-p-${nodeIndex}-${rowIndex}-${cellIndex}-${contentIndex}`}
                                          data-docx-paragraph-host="true"
                                          data-docx-paragraph-kind="table-cell"
                                          data-docx-table-index={nodeIndex}
                                          data-docx-row-index={rowIndex}
                                          data-docx-cell-index={cellIndex}
                                          data-docx-paragraph-index={
                                            paragraphIndex
                                          }
                                          data-docx-table-paragraph-index={
                                            paragraphIndex
                                          }
                                          style={tableCellParagraphBlockStyle(
                                            cellContent,
                                            editor.model.metadata
                                              .numberingDefinitions,
                                            headingStyles,
                                            paragraphIndex,
                                            applyWordTableDefaults,
                                            nodeDocGridLinePitchPx
                                          )}
                                        >
                                          {renderInteractiveParagraphRuns(
                                            cellContent,
                                            `active-cell-${nodeIndex}-${rowIndex}-${cellIndex}-${contentIndex}`,
                                            {
                                              kind: "table-cell",
                                              tableIndex: nodeIndex,
                                              rowIndex,
                                              cellIndex,
                                              paragraphIndex,
                                            }
                                          )}
                                        </div>
                                      );
                                    }

                                    return (
                                      <div
                                        key={`active-cell-nested-${nodeIndex}-${rowIndex}-${cellIndex}-${contentIndex}`}
                                        contentEditable={false}
                                        suppressContentEditableWarning
                                      >
                                        {renderHeaderNode(
                                          cellContent,
                                          `active-cell-nested-table-${nodeIndex}-${rowIndex}-${cellIndex}-${contentIndex}`,
                                          documentContentTheme,
                                          editor.model.metadata
                                            .numberingDefinitions,
                                          headingStyles,
                                          spannedWidthPx > 0
                                            ? spannedWidthPx
                                            : undefined,
                                          scrollToBookmark,
                                          undefined,
                                          undefined,
                                          undefined,
                                          undefined,
                                          paragraphRunRenderOptions,
                                          undefined,
                                          undefined,
                                          undefined,
                                          undefined,
                                          embeddedTableResizeController
                                        )}
                                      </div>
                                    );
                                  }
                                );
                              })()
                            : undefined;
                          const tableCellDraftHtml =
                            tableCellDraftsRef.current.get(cellDraftKey);
                          const renderedEditableCellHtml = editableCell
                            ? typeof tableCellDraftHtml === "string"
                              ? tableCellDraftHtml
                              : renderStaticHtml(renderedEditableCellContent)
                            : undefined;
                          const renderStaticCellContent = () => {
                            let paragraphCursor = 0;
                            return cell.nodes.map(
                              (cellContent, contentIndex) => {
                                if (cellContent.type === "paragraph") {
                                  const paragraphIndex = paragraphCursor;
                                  paragraphCursor += 1;
                                  return (
                                    <div
                                      key={`body-cell-p-${nodeIndex}-${rowIndex}-${cellIndex}-${contentIndex}`}
                                      data-docx-paragraph-host="true"
                                      data-docx-paragraph-kind="table-cell"
                                      data-docx-table-index={nodeIndex}
                                      data-docx-row-index={rowIndex}
                                      data-docx-cell-index={cellIndex}
                                      data-docx-paragraph-index={paragraphIndex}
                                      data-docx-table-paragraph-index={
                                        paragraphIndex
                                      }
                                      style={tableCellParagraphBlockStyle(
                                        cellContent,
                                        editor.model.metadata
                                          .numberingDefinitions,
                                        headingStyles,
                                        paragraphIndex,
                                        applyWordTableDefaults,
                                        nodeDocGridLinePitchPx
                                      )}
                                    >
                                      {renderInteractiveParagraphRuns(
                                        cellContent,
                                        `body-cell-${nodeIndex}-${rowIndex}-${cellIndex}-${contentIndex}`,
                                        {
                                          kind: "table-cell",
                                          tableIndex: nodeIndex,
                                          rowIndex,
                                          cellIndex,
                                          paragraphIndex,
                                        }
                                      )}
                                    </div>
                                  );
                                }

                                return renderHeaderNode(
                                  cellContent,
                                  `body-cell-nested-table-${nodeIndex}-${rowIndex}-${cellIndex}-${contentIndex}`,
                                  documentContentTheme,
                                  editor.model.metadata.numberingDefinitions,
                                  headingStyles,
                                  spannedWidthPx > 0
                                    ? spannedWidthPx
                                    : undefined,
                                  scrollToBookmark,
                                  undefined,
                                  undefined,
                                  undefined,
                                  undefined,
                                  paragraphRunRenderOptions,
                                  undefined,
                                  undefined,
                                  tableCellEditScope,
                                  undefined,
                                  embeddedTableResizeController
                                );
                              }
                            );
                          };

                          return (
                            <td
                              key={`cell-${nodeIndex}-${rowIndex}-${cellIndex}`}
                              data-docx-table-cell="true"
                              data-docx-table-index={nodeIndex}
                              data-docx-row-index={rowIndex}
                              data-docx-row-sliced={
                                isSlicedRow ? "true" : undefined
                              }
                              data-docx-cell-index={cellIndex}
                              colSpan={colSpan}
                              rowSpan={rowSpan}
                              style={{
                                ...resolveTableCellBorderCss(
                                  tableBorders,
                                  cell.style?.borders,
                                  rowIndex,
                                  node.rows.length,
                                  startColumnIndex,
                                  boundaryColumnIndex,
                                  columnCount
                                ),
                                ...resolveTableCellDiagonalOverlayCss(
                                  tableBorders,
                                  cell.style?.borders
                                ),
                                ...tableSpacingPaddingStyle(
                                  mergedCellMarginTwips
                                ),
                                backgroundColor: cellBackgroundColor,
                                verticalAlign:
                                  cell.style?.verticalAlign ?? "top",
                                minWidth: cellWidthPx
                                  ? `${cellWidthPx}px`
                                  : spannedWidthPx > 0
                                  ? `${spannedWidthPx}px`
                                  : 0,
                                width:
                                  cellWidthPx && colSpanValue === 1
                                    ? `${cellWidthPx}px`
                                    : spannedWidthPx > 0
                                    ? `${spannedWidthPx}px`
                                    : undefined,
                                ...(hasCustomCellRowHeight &&
                                !isSlicedRow
                                  ? resolvedRowHeightStyle
                                  : undefined),
                                wordWrap: "break-word",
                                overflowWrap: "break-word",
                                wordBreak: "break-word",
                                position: "relative",
                              }}
                              onClick={(event) => {
                                if (isReadOnly) {
                                  return;
                                }

                                const clickTargetElement =
                                  event.target instanceof Element
                                    ? event.target
                                    : event.target instanceof Node
                                    ? event.target.parentElement
                                    : null;
                                const clickInsideEditableCell = Boolean(
                                  clickTargetElement?.closest(
                                    `[data-docx-cell-key='${cellElementKey}']`
                                  )
                                );
                                if (clickInsideEditableCell) {
                                  return;
                                }

                                if (
                                  eventTargetIsNestedTableParagraphEditor(
                                    event.target
                                  )
                                ) {
                                  return;
                                }

                                if (suppressNextTableCellClickRef.current) {
                                  suppressNextTableCellClickRef.current = false;
                                  event.preventDefault();
                                  event.stopPropagation();
                                  return;
                                }

                                clearTableCellSelection();
                                setSingleTableCellSelection(
                                  nodeIndex,
                                  rowIndex,
                                  cellIndex
                                );
                                editor.selectTableCell(
                                  nodeIndex,
                                  rowIndex,
                                  cellIndex
                                );

                                const clickPoint = {
                                  x: event.clientX,
                                  y: event.clientY,
                                };
                                const paragraphSelector =
                                  `[data-docx-paragraph-kind='table-cell']` +
                                  `[data-docx-table-index='${nodeIndex}']` +
                                  `[data-docx-row-index='${rowIndex}']` +
                                  `[data-docx-cell-index='${cellIndex}']` +
                                  `[data-docx-table-paragraph-index]`;
                                const paragraphElements = Array.from(
                                  event.currentTarget.querySelectorAll<HTMLElement>(
                                    paragraphSelector
                                  )
                                );
                                const clickedParagraphElement =
                                  paragraphElements.length > 0
                                    ? paragraphElements.reduce<HTMLElement>(
                                        (closest, candidate) => {
                                          const closestRect =
                                            closest.getBoundingClientRect();
                                          const candidateRect =
                                            candidate.getBoundingClientRect();
                                          const closestDistance = Math.abs(
                                            clickPoint.y -
                                              (closestRect.top +
                                                closestRect.height / 2)
                                          );
                                          const candidateDistance = Math.abs(
                                            clickPoint.y -
                                              (candidateRect.top +
                                                candidateRect.height / 2)
                                          );
                                          return candidateDistance <
                                            closestDistance
                                            ? candidate
                                            : closest;
                                        },
                                        paragraphElements[0]
                                      )
                                    : undefined;
                                const clickedParagraphIndexRaw =
                                  clickedParagraphElement?.getAttribute(
                                    "data-docx-table-paragraph-index"
                                  ) ??
                                  clickedParagraphElement?.getAttribute(
                                    "data-docx-paragraph-index"
                                  );
                                const clickedParagraphIndexParsed =
                                  Number.parseInt(
                                    clickedParagraphIndexRaw ?? "",
                                    10
                                  );
                                const clickedParagraphIndex = Number.isFinite(
                                  clickedParagraphIndexParsed
                                )
                                  ? Math.max(
                                      0,
                                      Math.round(clickedParagraphIndexParsed)
                                    )
                                  : undefined;

                                window.requestAnimationFrame(() => {
                                  const cellEditorElement =
                                    tableCellEditorElementsRef.current.get(
                                      cellDraftKey
                                    );
                                  if (
                                    !cellEditorElement ||
                                    !cellEditorElement.isConnected
                                  ) {
                                    return;
                                  }

                                  cellEditorElement.focus();
                                  const paragraphSelector =
                                    `[data-docx-paragraph-kind='table-cell']` +
                                    `[data-docx-table-index='${nodeIndex}']` +
                                    `[data-docx-row-index='${rowIndex}']` +
                                    `[data-docx-cell-index='${cellIndex}']` +
                                    `[data-docx-table-paragraph-index]`;
                                  const paragraphElements = Array.from(
                                    cellEditorElement.querySelectorAll<HTMLElement>(
                                      paragraphSelector
                                    )
                                  );
                                  const paragraphElementByIndex =
                                    Number.isFinite(clickedParagraphIndex)
                                      ? cellEditorElement.querySelector<HTMLElement>(
                                          `[data-docx-paragraph-kind='table-cell']` +
                                            `[data-docx-table-index='${nodeIndex}']` +
                                            `[data-docx-row-index='${rowIndex}']` +
                                            `[data-docx-cell-index='${cellIndex}']` +
                                            `[data-docx-table-paragraph-index='${clickedParagraphIndex}']`
                                        ) ?? undefined
                                      : undefined;
                                  const paragraphElementAtPoint =
                                    document
                                      .elementFromPoint(
                                        clickPoint.x,
                                        clickPoint.y
                                      )
                                      ?.closest<HTMLElement>(
                                        paragraphSelector
                                      ) ?? undefined;
                                  const paragraphElementByPoint =
                                    paragraphElements.length > 0
                                      ? paragraphElements.reduce<HTMLElement>(
                                          (closest, candidate) => {
                                            const closestRect =
                                              closest.getBoundingClientRect();
                                            const candidateRect =
                                              candidate.getBoundingClientRect();
                                            const closestDistance = Math.abs(
                                              clickPoint.y -
                                                (closestRect.top +
                                                  closestRect.height / 2)
                                            );
                                            const candidateDistance = Math.abs(
                                              clickPoint.y -
                                                (candidateRect.top +
                                                  candidateRect.height / 2)
                                            );
                                            return candidateDistance <
                                              closestDistance
                                              ? candidate
                                              : closest;
                                          },
                                          paragraphElements[0]
                                        )
                                      : undefined;
                                  const paragraphElement =
                                    paragraphElementByIndex ??
                                    paragraphElementAtPoint ??
                                    paragraphElementByPoint;
                                  placeCaretInsideElement(
                                    paragraphElement ?? cellEditorElement,
                                    clickPoint
                                  );
                                  updateActiveTextRangeFromTableCell(
                                    cellEditorElement,
                                    nodeIndex,
                                    rowIndex,
                                    cellIndex
                                  );
                                });
                              }}
                              onContextMenu={(event) => {
                                if (isReadOnly) {
                                  return;
                                }

                                if (
                                  eventTargetIsInteractiveControl(
                                    event.target
                                  ) ||
                                  eventTargetIsNestedTableParagraphEditor(
                                    event.target
                                  )
                                ) {
                                  return;
                                }

                                event.preventDefault();
                                event.stopPropagation();
                                clearTableCellSelection();
                                setSingleTableCellSelection(
                                  nodeIndex,
                                  rowIndex,
                                  cellIndex
                                );
                                editor.selectTableCell(
                                  nodeIndex,
                                  rowIndex,
                                  cellIndex
                                );
                                openTableContextMenu(
                                  {
                                    tableIndex: nodeIndex,
                                    rowIndex,
                                    cellIndex,
                                  },
                                  {
                                    x: event.clientX,
                                    y: event.clientY,
                                  }
                                );
                              }}
                              onPointerDown={(event) => {
                                if (isReadOnly) {
                                  return;
                                }

                                // Always clear stale click suppression at the start of a new pointer interaction.
                                suppressNextTableCellClickRef.current = false;

                                const pointerTargetElement =
                                  event.target instanceof Element
                                    ? event.target
                                    : event.target instanceof Node
                                    ? event.target.parentElement
                                    : null;
                                const pointerInsideEditableCell = Boolean(
                                  pointerTargetElement?.closest(
                                    `[data-docx-cell-key='${cellElementKey}']`
                                  )
                                );
                                if (pointerInsideEditableCell) {
                                  // Preserve native caret placement and text drag behavior.
                                  pendingTableCellFocusRef.current = undefined;
                                  return;
                                }

                                const targetIsNestedTableParagraphEditor =
                                  eventTargetIsNestedTableParagraphEditor(
                                    event.target
                                  );
                                if (targetIsNestedTableParagraphEditor) {
                                  // Let nested table paragraph editors own pointer behavior.
                                  return;
                                }

                                if (
                                  (event.button !== 0 &&
                                    event.button !== undefined) ||
                                  eventTargetIsInteractiveControl(event.target)
                                ) {
                                  return;
                                }

                                const startBoundary = resolveBoundaryFromPoint({
                                  x: event.clientX,
                                  y: event.clientY,
                                });

                                beginTableCellSelectionDrag(
                                  event.pointerId,
                                  {
                                    tableIndex: nodeIndex,
                                    rowIndex,
                                    cellIndex,
                                  },
                                  event.clientX,
                                  event.clientY,
                                  startBoundary
                                );

                                const pendingFocusBoundary =
                                  startBoundary?.location.kind ===
                                    "table-cell" &&
                                  startBoundary.location.tableIndex ===
                                    nodeIndex &&
                                  startBoundary.location.rowIndex ===
                                    rowIndex &&
                                  startBoundary.location.cellIndex === cellIndex
                                    ? {
                                        location: cloneTextRangeLocation(
                                          startBoundary.location
                                        ),
                                        offset: startBoundary.offset,
                                      }
                                    : undefined;
                                const paragraphSelector =
                                  `[data-docx-paragraph-kind='table-cell']` +
                                  `[data-docx-table-index='${nodeIndex}']` +
                                  `[data-docx-row-index='${rowIndex}']` +
                                  `[data-docx-cell-index='${cellIndex}']` +
                                  `[data-docx-table-paragraph-index]`;
                                const paragraphElements = Array.from(
                                  event.currentTarget.querySelectorAll<HTMLElement>(
                                    paragraphSelector
                                  )
                                );
                                const pointerParagraphElement =
                                  paragraphElements.length > 0
                                    ? paragraphElements.reduce<HTMLElement>(
                                        (closest, candidate) => {
                                          const closestRect =
                                            closest.getBoundingClientRect();
                                          const candidateRect =
                                            candidate.getBoundingClientRect();
                                          const closestDistance = Math.abs(
                                            event.clientY -
                                              (closestRect.top +
                                                closestRect.height / 2)
                                          );
                                          const candidateDistance = Math.abs(
                                            event.clientY -
                                              (candidateRect.top +
                                                candidateRect.height / 2)
                                          );
                                          return candidateDistance <
                                            closestDistance
                                            ? candidate
                                            : closest;
                                        },
                                        paragraphElements[0]
                                      )
                                    : undefined;
                                const pointerParagraphIndexRaw =
                                  pointerParagraphElement?.getAttribute(
                                    "data-docx-table-paragraph-index"
                                  ) ??
                                  pointerParagraphElement?.getAttribute(
                                    "data-docx-paragraph-index"
                                  );
                                const pointerParagraphIndexParsed =
                                  Number.parseInt(
                                    pointerParagraphIndexRaw ?? "",
                                    10
                                  );
                                const pendingFocusParagraphIndex =
                                  Number.isFinite(pointerParagraphIndexParsed)
                                    ? Math.max(
                                        0,
                                        Math.round(pointerParagraphIndexParsed)
                                      )
                                    : undefined;
                                pendingTableCellFocusRef.current = {
                                  cellElementKey,
                                  point: {
                                    x: event.clientX,
                                    y: event.clientY,
                                  },
                                  boundary: pendingFocusBoundary,
                                  paragraphIndex: pendingFocusParagraphIndex,
                                };
                              }}
                            >
                              {editableCell ? (
                                <div
                                  contentEditable
                                  suppressContentEditableWarning
                                  data-docx-cell-key={cellElementKey}
                                  style={{
                                    minHeight: 0,
                                    outline: "none",
                                    padding: 0,
                                    borderRadius: 0,
                                    display: "grid",
                                    // Must match the read-mode cell wrapper (gap 0):
                                    // a different gap shifts paragraphs between the
                                    // read->edit DOM swap and breaks point-based
                                    // caret placement from the original click.
                                    gap: 0,
                                    whiteSpace: "pre-wrap",
                                    wordWrap: "break-word",
                                    overflowWrap: "break-word",
                                    wordBreak: "break-word",
                                  }}
                                  dangerouslySetInnerHTML={
                                    renderedEditableCellHtml
                                      ? {
                                          __html: renderedEditableCellHtml,
                                        }
                                      : undefined
                                  }
                                  ref={(element) => {
                                    if (element) {
                                      tableCellEditorElementsRef.current.set(
                                        cellDraftKey,
                                        element
                                      );
                                    } else {
                                      tableCellEditorElementsRef.current.delete(
                                        cellDraftKey
                                      );
                                    }

                                    if (!element || !editableCell) {
                                      return;
                                    }

                                    const draft =
                                      tableCellDraftsRef.current.get(
                                        cellDraftKey
                                      );
                                    if (typeof draft === "string") {
                                      const draftHtml = draft;
                                      scheduleDomWrite(() => {
                                        if (!element.isConnected) {
                                          return;
                                        }

                                        const latestDraft =
                                          tableCellDraftsRef.current.get(
                                            cellDraftKey
                                          );
                                        if (latestDraft !== draftHtml) {
                                          return;
                                        }

                                        if (element.innerHTML !== draftHtml) {
                                          const activeElement =
                                            document.activeElement;
                                          const shouldRestoreSelection =
                                            activeElement === element ||
                                            (activeElement instanceof Element &&
                                              element.contains(activeElement));
                                          const selectionOffsets =
                                            shouldRestoreSelection
                                              ? selectionOffsetsWithinElement(
                                                  element
                                                )
                                              : undefined;
                                          element.innerHTML = draftHtml;
                                          if (shouldRestoreSelection) {
                                            if (selectionOffsets) {
                                              const textLength =
                                                editableTextFromTableCellElement(
                                                  element
                                                ).length;
                                              const safeStart = Math.max(
                                                0,
                                                Math.min(
                                                  selectionOffsets.start,
                                                  textLength
                                                )
                                              );
                                              const safeEnd = Math.max(
                                                safeStart,
                                                Math.min(
                                                  selectionOffsets.end,
                                                  textLength
                                                )
                                              );
                                              setSelectionWithinElementByTextOffsets(
                                                element,
                                                safeStart,
                                                safeEnd
                                              );
                                            } else {
                                              placeCaretInsideElement(element);
                                            }
                                          }
                                        }

                                        const latestTableNode =
                                          editor.model.nodes[nodeIndex];
                                        if (
                                          !latestTableNode ||
                                          latestTableNode.type !== "table"
                                        ) {
                                          return;
                                        }

                                        const latestCell =
                                          latestTableNode.rows[rowIndex]?.cells[
                                            cellIndex
                                          ];
                                        if (!latestCell) {
                                          return;
                                        }

                                        if (
                                          editableTextFromTableCellElement(
                                            element
                                          ) ===
                                          tableCellText(
                                            tableCellParagraphs(
                                              latestCell.nodes
                                            )
                                          )
                                        ) {
                                          tableCellDraftsRef.current.delete(
                                            cellDraftKey
                                          );
                                        }
                                      });
                                    }

                                    const pendingFocus =
                                      pendingTableCellFocusRef.current;
                                    if (
                                      pendingFocus?.cellElementKey ===
                                      cellElementKey
                                    ) {
                                      if (pendingFocus.point) {
                                        const rect =
                                          element.getBoundingClientRect();
                                        const pointInsideElement =
                                          pendingFocus.point.x >= rect.left &&
                                          pendingFocus.point.x <= rect.right &&
                                          pendingFocus.point.y >= rect.top &&
                                          pendingFocus.point.y <= rect.bottom;
                                        if (!pointInsideElement) {
                                          return;
                                        }
                                      }

                                      window.requestAnimationFrame(() => {
                                        const latestPendingFocus =
                                          pendingTableCellFocusRef.current;
                                        if (
                                          !latestPendingFocus ||
                                          latestPendingFocus.cellElementKey !==
                                            cellElementKey ||
                                          !element.isConnected
                                        ) {
                                          return;
                                        }

                                        element.focus();
                                        const boundary =
                                          latestPendingFocus.boundary;
                                        const boundaryInCurrentCell = Boolean(
                                          boundary &&
                                            boundary.location.kind ===
                                              "table-cell" &&
                                            boundary.location.tableIndex ===
                                              nodeIndex &&
                                            boundary.location.rowIndex ===
                                              rowIndex &&
                                            boundary.location.cellIndex ===
                                              cellIndex
                                        );
                                        const paragraphElementForFocus =
                                          Number.isFinite(
                                            latestPendingFocus.paragraphIndex
                                          )
                                            ? element.querySelector<HTMLElement>(
                                                `[data-docx-paragraph-kind='table-cell']` +
                                                  `[data-docx-table-index='${nodeIndex}']` +
                                                  `[data-docx-row-index='${rowIndex}']` +
                                                  `[data-docx-cell-index='${cellIndex}']` +
                                                  `[data-docx-table-paragraph-index='${latestPendingFocus.paragraphIndex}']`
                                              ) ?? undefined
                                            : undefined;
                                        const paragraphSelector =
                                          `[data-docx-paragraph-kind='table-cell']` +
                                          `[data-docx-table-index='${nodeIndex}']` +
                                          `[data-docx-row-index='${rowIndex}']` +
                                          `[data-docx-cell-index='${cellIndex}']` +
                                          `[data-docx-table-paragraph-index]`;
                                        const paragraphElements = Array.from(
                                          element.querySelectorAll<HTMLElement>(
                                            paragraphSelector
                                          )
                                        );
                                        const paragraphElementAtPoint =
                                          latestPendingFocus.point
                                            ? document
                                                .elementFromPoint(
                                                  latestPendingFocus.point.x,
                                                  latestPendingFocus.point.y
                                                )
                                                ?.closest<HTMLElement>(
                                                  paragraphSelector
                                                ) ?? undefined
                                            : undefined;
                                        const paragraphElementByPoint =
                                          latestPendingFocus.point &&
                                          paragraphElements.length > 0
                                            ? paragraphElements.reduce<HTMLElement>(
                                                (closest, candidate) => {
                                                  const closestRect =
                                                    closest.getBoundingClientRect();
                                                  const candidateRect =
                                                    candidate.getBoundingClientRect();
                                                  const closestDistance =
                                                    Math.abs(
                                                      latestPendingFocus.point!
                                                        .y -
                                                        (closestRect.top +
                                                          closestRect.height /
                                                            2)
                                                    );
                                                  const candidateDistance =
                                                    Math.abs(
                                                      latestPendingFocus.point!
                                                        .y -
                                                        (candidateRect.top +
                                                          candidateRect.height /
                                                            2)
                                                    );
                                                  return candidateDistance <
                                                    closestDistance
                                                    ? candidate
                                                    : closest;
                                                },
                                                paragraphElements[0]
                                              )
                                            : undefined;
                                        const paragraphElement =
                                          paragraphElementForFocus ??
                                          paragraphElementAtPoint ??
                                          paragraphElementByPoint;
                                        if (boundaryInCurrentCell && boundary) {
                                          setSelectionFromDocxBoundaries(
                                            boundary,
                                            boundary
                                          );
                                          // Re-renders can replace the cell's
                                          // innerHTML right after this restore,
                                          // silently destroying the selection.
                                          // Verify for a few frames and re-apply
                                          // while the caret is degenerate.
                                          const verifyBoundaryRestore = (
                                            attempt: number
                                          ): void => {
                                            window.requestAnimationFrame(() => {
                                              if (!element.isConnected) {
                                                return;
                                              }
                                              const selection =
                                                window.getSelection();
                                              const range =
                                                selection &&
                                                selection.rangeCount > 0
                                                  ? selection.getRangeAt(0)
                                                  : null;
                                              const degenerate =
                                                !range ||
                                                (selection?.isCollapsed ===
                                                  true &&
                                                  range.startContainer ===
                                                    element &&
                                                  range.startOffset === 0);
                                              if (degenerate) {
                                                setSelectionFromDocxBoundaries(
                                                  boundary,
                                                  boundary
                                                );
                                              }
                                              // Keep watching for the full
                                              // window: late re-renders can
                                              // still destroy the selection
                                              // after a healthy frame.
                                              if (attempt < 6) {
                                                verifyBoundaryRestore(
                                                  attempt + 1
                                                );
                                              }
                                            });
                                          };
                                          verifyBoundaryRestore(0);
                                        } else if (paragraphElement) {
                                          placeCaretInsideElement(
                                            paragraphElement,
                                            latestPendingFocus.point
                                          );
                                        } else if (latestPendingFocus.point) {
                                          placeCaretInsideElement(
                                            element,
                                            latestPendingFocus.point
                                          );
                                        } else {
                                          placeCaretInsideElement(element);
                                        }

                                        updateActiveTextRangeFromTableCell(
                                          element,
                                          nodeIndex,
                                          rowIndex,
                                          cellIndex
                                        );
                                        pendingTableCellFocusRef.current =
                                          undefined;
                                      });
                                    }
                                  }}
                                  onInput={(event) => {
                                    cancelPendingPointerSelectionReconcile();
                                    editor.beginSelectionSession("keyboard", {
                                      settleAfterMs: 220,
                                    });
                                    schedulePaginationMeasurementResume();
                                    tableCellDraftsRef.current.set(
                                      cellDraftKey,
                                      event.currentTarget.innerHTML
                                    );
                                    requestTableDraftLayoutRefresh();
                                  }}
                                  onCompositionStart={() => {
                                    cancelPendingPointerSelectionReconcile();
                                    editor.beginSelectionSession("composition");
                                  }}
                                  onCompositionEnd={() => {
                                    editor.beginSelectionSession("keyboard", {
                                      settleAfterMs: 260,
                                    });
                                  }}
                                  onClick={(event) => {
                                    if (
                                      eventTargetIsInteractiveControl(
                                        event.target
                                      )
                                    ) {
                                      return;
                                    }

                                    if (
                                      eventTargetIsNestedTableParagraphEditor(
                                        event.target
                                      )
                                    ) {
                                      return;
                                    }

                                    if (event.detail > 1) {
                                      return;
                                    }

                                    const cellElement = event.currentTarget;
                                    const paragraphSelector =
                                      `[data-docx-paragraph-kind='table-cell']` +
                                      `[data-docx-table-index='${nodeIndex}']` +
                                      `[data-docx-row-index='${rowIndex}']` +
                                      `[data-docx-cell-index='${cellIndex}']` +
                                      `[data-docx-table-paragraph-index]`;
                                    const clickTargetElement =
                                      event.target instanceof Element
                                        ? event.target
                                        : event.target instanceof Node
                                        ? event.target.parentElement
                                        : null;
                                    const clickedParagraph = clickTargetElement
                                      ? clickTargetElement.closest<HTMLElement>(
                                          paragraphSelector
                                        ) ?? undefined
                                      : undefined;
                                    const fallbackParagraph = (() => {
                                      if (clickedParagraph) {
                                        return clickedParagraph;
                                      }

                                      const paragraphs = Array.from(
                                        event.currentTarget.querySelectorAll<HTMLElement>(
                                          paragraphSelector
                                        )
                                      );
                                      if (paragraphs.length === 0) {
                                        return undefined;
                                      }

                                      return paragraphs.reduce<HTMLElement>(
                                        (closest, candidate) => {
                                          const closestRect =
                                            closest.getBoundingClientRect();
                                          const candidateRect =
                                            candidate.getBoundingClientRect();
                                          const closestDistance = Math.abs(
                                            event.clientY -
                                              (closestRect.top +
                                                closestRect.height / 2)
                                          );
                                          const candidateDistance = Math.abs(
                                            event.clientY -
                                              (candidateRect.top +
                                                candidateRect.height / 2)
                                          );
                                          return candidateDistance <
                                            closestDistance
                                            ? candidate
                                            : closest;
                                        },
                                        paragraphs[0]
                                      );
                                    })();
                                    if (fallbackParagraph) {
                                      schedulePointerSelectionReconcile(
                                        fallbackParagraph,
                                        {
                                          x: event.clientX,
                                          y: event.clientY,
                                        },
                                        () => {
                                          updateActiveTextRangeFromTableCell(
                                            cellElement,
                                            nodeIndex,
                                            rowIndex,
                                            cellIndex
                                          );
                                        }
                                      );
                                    }
                                  }}
                                  onDoubleClick={(event) => {
                                    const cellElement = event.currentTarget;
                                    const paragraphSelector =
                                      `[data-docx-paragraph-kind='table-cell']` +
                                      `[data-docx-table-index='${nodeIndex}']` +
                                      `[data-docx-row-index='${rowIndex}']` +
                                      `[data-docx-cell-index='${cellIndex}']` +
                                      `[data-docx-table-paragraph-index]`;
                                    const targetElement =
                                      event.target instanceof Element
                                        ? event.target
                                        : event.target instanceof Node
                                        ? event.target.parentElement
                                        : null;
                                    const paragraphElement =
                                      targetElement?.closest<HTMLElement>(
                                        paragraphSelector
                                      ) ?? undefined;
                                    if (!paragraphElement) {
                                      return;
                                    }

                                    schedulePointerSelectionReconcile(
                                      paragraphElement,
                                      {
                                        x: event.clientX,
                                        y: event.clientY,
                                      },
                                      () => {
                                        updateActiveTextRangeFromTableCell(
                                          cellElement,
                                          nodeIndex,
                                          rowIndex,
                                          cellIndex
                                        );
                                      }
                                    );
                                  }}
                                  onPaste={(event) => {
                                    const pastedText =
                                      event.clipboardData.getData("text/plain");
                                    if (!pastedText) {
                                      return;
                                    }

                                    const replaced =
                                      replaceExpandedSelectionWithText(
                                        pastedText
                                      );
                                    if (!replaced) {
                                      return;
                                    }

                                    event.preventDefault();
                                    event.stopPropagation();
                                    tableCellDraftsRef.current.delete(
                                      cellDraftKey
                                    );
                                  }}
                                  onMouseUp={(event) => {
                                    const cellElement = event.currentTarget;
                                    const paragraphSelector =
                                      `[data-docx-paragraph-kind='table-cell']` +
                                      `[data-docx-table-index='${nodeIndex}']` +
                                      `[data-docx-row-index='${rowIndex}']` +
                                      `[data-docx-cell-index='${cellIndex}']` +
                                      `[data-docx-table-paragraph-index]`;
                                    const paragraphElements = Array.from(
                                      event.currentTarget.querySelectorAll<HTMLElement>(
                                        paragraphSelector
                                      )
                                    );
                                    const fallbackParagraph =
                                      paragraphElements.length > 0
                                        ? paragraphElements.reduce<HTMLElement>(
                                            (closest, candidate) => {
                                              const closestRect =
                                                closest.getBoundingClientRect();
                                              const candidateRect =
                                                candidate.getBoundingClientRect();
                                              const closestDistance = Math.abs(
                                                event.clientY -
                                                  (closestRect.top +
                                                    closestRect.height / 2)
                                              );
                                              const candidateDistance =
                                                Math.abs(
                                                  event.clientY -
                                                    (candidateRect.top +
                                                      candidateRect.height / 2)
                                                );
                                              return candidateDistance <
                                                closestDistance
                                                ? candidate
                                                : closest;
                                            },
                                            paragraphElements[0]
                                          )
                                        : undefined;
                                    if (
                                      fallbackParagraph &&
                                      selectionIsExpandedWithinElement(
                                        fallbackParagraph
                                      )
                                    ) {
                                      updateActiveTextRangeFromTableCell(
                                        cellElement,
                                        nodeIndex,
                                        rowIndex,
                                        cellIndex
                                      );
                                      cancelPendingPointerSelectionReconcile();
                                      return;
                                    }
                                    if (fallbackParagraph) {
                                      schedulePointerSelectionReconcile(
                                        fallbackParagraph,
                                        {
                                          x: event.clientX,
                                          y: event.clientY,
                                        },
                                        () => {
                                          updateActiveTextRangeFromTableCell(
                                            cellElement,
                                            nodeIndex,
                                            rowIndex,
                                            cellIndex
                                          );
                                        }
                                      );
                                    }
                                  }}
                                  onKeyUp={(event) => {
                                    if (!shouldSyncActiveRangeOnKeyUp(event)) {
                                      return;
                                    }

                                    updateActiveTextRangeFromTableCell(
                                      event.currentTarget,
                                      nodeIndex,
                                      rowIndex,
                                      cellIndex
                                    );
                                  }}
                                  onKeyDown={(event) => {
                                    cancelPendingPointerSelectionReconcile();
                                    editor.beginSelectionSession("keyboard", {
                                      settleAfterMs: 220,
                                    });
                                    if (
                                      handleDeleteAcrossTableCellSelection(
                                        event
                                      )
                                    ) {
                                      return;
                                    }

                                    if (handleDeleteAcrossSelection(event)) {
                                      return;
                                    }

                                    const handledHistoryShortcut =
                                      handleEditorHistoryShortcut(event, () => {
                                        commitTableCellDraftFromElement(
                                          nodeIndex,
                                          rowIndex,
                                          cellIndex,
                                          cellParagraphs,
                                          cellDraftKey,
                                          event.currentTarget
                                        );
                                      });
                                    if (handledHistoryShortcut) {
                                      return;
                                    }

                                    const isPlainTextInsertKey =
                                      event.key.length === 1 &&
                                      !event.metaKey &&
                                      !event.ctrlKey &&
                                      !event.altKey;
                                    if (isPlainTextInsertKey) {
                                      const replaced =
                                        replaceExpandedSelectionWithText(
                                          event.key
                                        );
                                      if (replaced) {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        tableCellDraftsRef.current.delete(
                                          cellDraftKey
                                        );
                                        return;
                                      }
                                    }

                                    if (
                                      event.key === "Enter" &&
                                      !event.metaKey &&
                                      !event.ctrlKey &&
                                      !event.altKey
                                    ) {
                                      const replaced =
                                        replaceExpandedSelectionWithText("\n");
                                      if (replaced) {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        tableCellDraftsRef.current.delete(
                                          cellDraftKey
                                        );
                                        return;
                                      }
                                    }

                                    const paragraphContext =
                                      activeTableCellParagraphContext(
                                        event.currentTarget
                                      );
                                    const activeParagraphIndex =
                                      paragraphContext?.paragraphIndex ?? 0;
                                    const activeParagraph =
                                      cellParagraphs[activeParagraphIndex];
                                    if (
                                      !activeParagraph ||
                                      !paragraphContext?.paragraphElement
                                    ) {
                                      return;
                                    }

                                    const currentText = editableTextFromElement(
                                      paragraphContext.paragraphElement
                                    );
                                    const isListParagraph = paragraphIsList(
                                      activeParagraph,
                                      currentText
                                    );
                                    const selectionOffsets =
                                      selectionOffsetsWithinElement(
                                        paragraphContext.paragraphElement
                                      );
                                    const isCollapsedSelection =
                                      selectionOffsets !== undefined &&
                                      selectionOffsets.start ===
                                        selectionOffsets.end;
                                    const isPlainEnterKey =
                                      event.key === "Enter" &&
                                      !event.metaKey &&
                                      !event.ctrlKey &&
                                      !event.altKey;
                                    if (isPlainEnterKey && !isListParagraph) {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      const start = Math.max(
                                        0,
                                        Math.min(
                                          selectionOffsets?.start ??
                                            currentText.length,
                                          currentText.length
                                        )
                                      );
                                      const end = Math.max(
                                        start,
                                        Math.min(
                                          selectionOffsets?.end ?? start,
                                          currentText.length
                                        )
                                      );
                                      const nextText = `${currentText.slice(
                                        0,
                                        start
                                      )}\n${currentText.slice(end)}`;
                                      tableCellDraftsRef.current.delete(
                                        cellDraftKey
                                      );
                                      tableCellParagraphDraftsRef.current.delete(
                                        tableCellParagraphDraftKey(
                                          nodeIndex,
                                          rowIndex,
                                          cellIndex,
                                          activeParagraphIndex
                                        )
                                      );
                                      editor.commitTableCellParagraphTextRecursive(
                                        nodeIndex,
                                        rowIndex,
                                        cellIndex,
                                        activeParagraphIndex,
                                        nextText
                                      );
                                      focusTableCellParagraphAtOffset(
                                        cellDraftKey,
                                        activeParagraphIndex,
                                        start + 1
                                      );
                                      return;
                                    }
                                    if (
                                      event.key === "Backspace" &&
                                      !event.metaKey &&
                                      !event.ctrlKey &&
                                      !event.altKey &&
                                      !event.shiftKey &&
                                      isListParagraph &&
                                      isCollapsedSelection &&
                                      (selectionOffsets?.start ?? -1) === 0
                                    ) {
                                      const listDepthChanged =
                                        editor.adjustSelectedListDepth(
                                          -1,
                                          currentText
                                        );
                                      let handledBackspace = listDepthChanged;

                                      if (!handledBackspace) {
                                        const listType = paragraphListType(
                                          activeParagraph,
                                          editor.model.metadata
                                            .numberingDefinitions
                                        );
                                        if (listType) {
                                          editor.toggleList(listType);
                                          handledBackspace = true;
                                        }
                                      }

                                      if (handledBackspace) {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        tableCellDraftsRef.current.delete(
                                          cellDraftKey
                                        );
                                        focusTableCellParagraphAtOffset(
                                          cellDraftKey,
                                          activeParagraphIndex,
                                          0
                                        );
                                        return;
                                      }
                                    }
                                    if (
                                      event.key === "Tab" &&
                                      !event.metaKey &&
                                      !event.ctrlKey &&
                                      !event.altKey &&
                                      isListParagraph
                                    ) {
                                      event.preventDefault();
                                      const listDepthChanged =
                                        editor.adjustSelectedListDepth(
                                          event.shiftKey ? -1 : 1,
                                          currentText
                                        );
                                      if (listDepthChanged) {
                                        tableCellDraftsRef.current.delete(
                                          cellDraftKey
                                        );
                                        const targetOffset =
                                          selectionOffsets?.end ??
                                          currentText.length;
                                        focusTableCellParagraphAtOffset(
                                          cellDraftKey,
                                          activeParagraphIndex,
                                          targetOffset
                                        );
                                      }
                                    }
                                  }}
                                  onBlur={(event) => {
                                    if (
                                      !tableCellDraftsRef.current.has(
                                        cellDraftKey
                                      )
                                    ) {
                                      return;
                                    }

                                    commitTableCellDraftFromElement(
                                      nodeIndex,
                                      rowIndex,
                                      cellIndex,
                                      cellParagraphs,
                                      cellDraftKey,
                                      event.currentTarget
                                    );
                                  }}
                                />
                              ) : slicedParagraphPlans &&
                                slicedCellViewportHeightPx ? (
                                <div
                                  style={{
                                    position: "relative",
                                    height: slicedCellViewportHeightPx,
                                    overflow: "visible",
                                  }}
                                >
                                  {slicedParagraphPlans.map((plan) => {
                                    const contentIndex = cell.nodes.indexOf(
                                      plan.paragraph
                                    );
                                    return (
                                      <div
                                        key={`body-cell-slice-host-${nodeIndex}-${rowIndex}-${cellIndex}-${contentIndex}`}
                                        style={{
                                          position: "absolute",
                                          left: 0,
                                          right: 0,
                                          top: plan.topPx,
                                        }}
                                      >
                                        {renderSlicedTableCellParagraph({
                                          paragraph: plan.paragraph,
                                          paragraphIndex: plan.paragraphIndex,
                                          tableIndex: nodeIndex,
                                          rowIndex,
                                          cellIndex,
                                          contentIndex,
                                          cellContentWidthPx,
                                          applyWordTableDefaults,
                                          docGridLinePitchPx:
                                            nodeDocGridLinePitchPx,
                                          layoutEntry: plan.layoutEntry,
                                          paragraphLineRange:
                                            plan.paragraphLineRange,
                                        })}
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : isSlicedRow && slicedCellViewportHeightPx ? (
                                <div
                                  style={{
                                    height: slicedCellViewportHeightPx,
                                    overflow: "hidden",
                                    position: "relative",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "grid",
                                      gap: 0,
                                      transform: `translateY(-${tableRowSliceOffsetPx}px)`,
                                    }}
                                  >
                                    {renderStaticCellContent()}
                                  </div>
                                </div>
                              ) : (
                                <div
                                  style={{
                                    display: "grid",
                                    gap: 0,
                                    // Without the clip the row balloons to fit
                                    // content (height on a <tr>/<td> is only
                                    // ever a minimum).
                                    ...(exactRowSpanClipHeightPx !== undefined &&
                                    !isSlicedRow
                                      ? {
                                          maxHeight: `${exactRowSpanClipHeightPx}px`,
                                          overflow: "hidden",
                                        }
                                      : undefined),
                                  }}
                                >
                                  {renderStaticCellContent()}
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {showTableHandles && !isSplitTableSegment && columnCount > 1
                ? columnWidthsPx
                    .slice(0, maxBoundaryColumnIndex + 1)
                    .map((_, boundaryColumnIndex) => {
                      if (boundaryColumnIndex > maxBoundaryColumnIndex) {
                        return null;
                      }

                      const leftPx =
                        tableColumnBoundaryOffsetsPx[boundaryColumnIndex] ?? 0;
                      const isResizing =
                        isColumnHandleActive &&
                        activeColumnResize?.boundaryColumnIndex ===
                          boundaryColumnIndex;
                      const isHovered =
                        hoveredTableResizeHandle?.tableIndex === nodeIndex &&
                        hoveredTableResizeHandle?.kind === "column" &&
                        hoveredTableResizeHandle.boundaryIndex ===
                          boundaryColumnIndex;
                      const isHot = isResizing || isHovered;

                      return (
                        <span
                          key={`column-border-${nodeIndex}-${boundaryColumnIndex}`}
                          contentEditable={false}
                          style={{
                            position: "absolute",
                            top: 0,
                            bottom: 0,
                            left: `${
                              leftPx -
                              TABLE_RESIZE_HANDLE_HIT_SIZE / 2 +
                              TABLE_RESIZE_HANDLE_ALIGNMENT_OFFSET_PX
                            }px`,
                            width: TABLE_RESIZE_HANDLE_HIT_SIZE,
                            cursor: "col-resize",
                            zIndex: 5,
                            touchAction: "none",
                            userSelect: "none",
                            backgroundColor: "transparent",
                          }}
                          onMouseEnter={() =>
                            setHoveredTableResizeHandle({
                              tableIndex: nodeIndex,
                              kind: "column",
                              boundaryIndex: boundaryColumnIndex,
                            })
                          }
                          onPointerEnter={() =>
                            setHoveredTableResizeHandle({
                              tableIndex: nodeIndex,
                              kind: "column",
                              boundaryIndex: boundaryColumnIndex,
                            })
                          }
                          onMouseLeave={() => {
                            if (
                              hoveredTableResizeHandle?.tableIndex ===
                                nodeIndex &&
                              hoveredTableResizeHandle.kind === "column" &&
                              hoveredTableResizeHandle.boundaryIndex ===
                                boundaryColumnIndex
                            ) {
                              setHoveredTableResizeHandle(undefined);
                            }
                          }}
                          onPointerLeave={() => {
                            if (
                              hoveredTableResizeHandle?.tableIndex ===
                                nodeIndex &&
                              hoveredTableResizeHandle.kind === "column" &&
                              hoveredTableResizeHandle.boundaryIndex ===
                                boundaryColumnIndex
                            ) {
                              setHoveredTableResizeHandle(undefined);
                            }
                          }}
                          onContextMenu={(event) => {
                            if (isReadOnly) {
                              return;
                            }

                            event.preventDefault();
                            event.stopPropagation();
                            const context = resolveTableContextMenuLocation(
                              nodeIndex,
                              0,
                              boundaryColumnIndex
                            );
                            clearTableCellSelection();
                            setSingleTableCellSelection(
                              context.tableIndex,
                              context.rowIndex,
                              context.cellIndex
                            );
                            editor.selectTableCell(
                              context.tableIndex,
                              context.rowIndex,
                              context.cellIndex
                            );
                            openTableContextMenu(context, {
                              x: event.clientX,
                              y: event.clientY,
                            });
                          }}
                          onPointerDown={(event) => {
                            const tableElement =
                              event.currentTarget.parentElement?.querySelector(
                                "table"
                              );
                            const tableElementWidthPx = tableElement
                              ? tableElement.getBoundingClientRect().width
                              : 0;
                            beginTableColumnResize(
                              event,
                              nodeIndex,
                              boundaryColumnIndex,
                              tableElementWidthPx,
                              columnWidthsPx
                            );
                            setHoveredTableResizeHandle(undefined);
                          }}
                        >
                          <span
                            style={{
                              position: "absolute",
                              left: "50%",
                              top: 0,
                              transform: "translateX(-50%)",
                              width: TABLE_RESIZE_BORDER_SIZE,
                              height: "100%",
                              backgroundColor: isHot
                                ? "#3b82f6"
                                : "transparent",
                            }}
                          />
                        </span>
                      );
                    })
                : null}
              {showTableHandles && !isSplitTableSegment && node.rows.length > 1
                ? rowHeightsPx
                    .slice(0, maxBoundaryRowIndex + 1)
                    .map((_, boundaryRowIndex) => {
                      if (boundaryRowIndex > maxBoundaryRowIndex) {
                        return null;
                      }

                      const topPx =
                        tableRowBoundaryOffsetsPx[boundaryRowIndex] ?? 0;
                      const isResizing =
                        isRowHandleActive &&
                        activeRowResize?.boundaryRowIndex === boundaryRowIndex;
                      const isHovered =
                        hoveredTableResizeHandle?.tableIndex === nodeIndex &&
                        hoveredTableResizeHandle?.kind === "row" &&
                        hoveredTableResizeHandle.boundaryIndex ===
                          boundaryRowIndex;
                      const isHot = isResizing || isHovered;

                      return (
                        <span
                          key={`row-border-${nodeIndex}-${boundaryRowIndex}`}
                          contentEditable={false}
                          style={{
                            position: "absolute",
                            left: 0,
                            width: `${resolvedTableWidthPx}px`,
                            top: `${
                              topPx -
                              TABLE_RESIZE_HANDLE_HIT_SIZE / 2 +
                              TABLE_RESIZE_HANDLE_ALIGNMENT_OFFSET_PX
                            }px`,
                            height: TABLE_RESIZE_HANDLE_HIT_SIZE,
                            cursor: "row-resize",
                            zIndex: 5,
                            touchAction: "none",
                            userSelect: "none",
                            backgroundColor: "transparent",
                          }}
                          onMouseEnter={() =>
                            setHoveredTableResizeHandle({
                              tableIndex: nodeIndex,
                              kind: "row",
                              boundaryIndex: boundaryRowIndex,
                            })
                          }
                          onPointerEnter={() =>
                            setHoveredTableResizeHandle({
                              tableIndex: nodeIndex,
                              kind: "row",
                              boundaryIndex: boundaryRowIndex,
                            })
                          }
                          onMouseLeave={() => {
                            if (
                              hoveredTableResizeHandle?.tableIndex ===
                                nodeIndex &&
                              hoveredTableResizeHandle.kind === "row" &&
                              hoveredTableResizeHandle.boundaryIndex ===
                                boundaryRowIndex
                            ) {
                              setHoveredTableResizeHandle(undefined);
                            }
                          }}
                          onPointerLeave={() => {
                            if (
                              hoveredTableResizeHandle?.tableIndex ===
                                nodeIndex &&
                              hoveredTableResizeHandle.kind === "row" &&
                              hoveredTableResizeHandle.boundaryIndex ===
                                boundaryRowIndex
                            ) {
                              setHoveredTableResizeHandle(undefined);
                            }
                          }}
                          onContextMenu={(event) => {
                            if (isReadOnly) {
                              return;
                            }

                            event.preventDefault();
                            event.stopPropagation();
                            const context = resolveTableContextMenuLocation(
                              nodeIndex,
                              boundaryRowIndex,
                              0
                            );
                            clearTableCellSelection();
                            setSingleTableCellSelection(
                              context.tableIndex,
                              context.rowIndex,
                              context.cellIndex
                            );
                            editor.selectTableCell(
                              context.tableIndex,
                              context.rowIndex,
                              context.cellIndex
                            );
                            openTableContextMenu(context, {
                              x: event.clientX,
                              y: event.clientY,
                            });
                          }}
                          onPointerDown={(event) => {
                            const tableElement =
                              event.currentTarget.parentElement?.querySelector(
                                "table"
                              );
                            const measuredRows = tableElement
                              ? Array.from(
                                  tableElement.querySelectorAll("tbody > tr")
                                ).map((rowElement) =>
                                  normalizeMeasuredTableRowHeightPx(
                                    (
                                      rowElement as HTMLTableRowElement
                                    ).getBoundingClientRect().height
                                  )
                                )
                              : [];
                            const startingHeights =
                              measuredRows.length > 0
                                ? measuredRows
                                : rowBoundarySourceHeightsPx;
                            beginTableRowResize(
                              event,
                              nodeIndex,
                              boundaryRowIndex,
                              startingHeights
                            );
                            setHoveredTableResizeHandle(undefined);
                          }}
                        >
                          <span
                            style={{
                              position: "absolute",
                              top: "50%",
                              left: 0,
                              right: 0,
                              transform: "translateY(-50%)",
                              width: "100%",
                              height: TABLE_RESIZE_BORDER_SIZE,
                              backgroundColor: isHot
                                ? "#3b82f6"
                                : "transparent",
                            }}
                          />
                        </span>
                      );
                    })
                : null}
              {showTableHandles && !isSplitTableSegment ? (
                <span
                  contentEditable={false}
                  style={{
                    position: "absolute",
                    left: `${Math.max(
                      0,
                      resolvedTableWidthPx -
                        TABLE_RESIZE_HANDLE_HIT_SIZE / 2 +
                        TABLE_RESIZE_HANDLE_ALIGNMENT_OFFSET_PX
                    )}px`,
                    bottom: -8,
                    width: TABLE_RESIZE_HANDLE_HIT_SIZE,
                    height: TABLE_RESIZE_HANDLE_HIT_SIZE,
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    cursor: "nwse-resize",
                    opacity: isCornerResizing ? 1 : 1,
                    zIndex: 6,
                    touchAction: "none",
                  }}
                  onPointerDown={(event) => {
                    const tableElement =
                      event.currentTarget.parentElement?.querySelector("table");
                    const handleTableWidthPx = tableElement
                      ? tableElement.getBoundingClientRect().width
                      : 0;
                    const measuredRows = tableElement
                      ? Array.from(
                          tableElement.querySelectorAll("tbody > tr")
                        ).map((rowElement) =>
                          normalizeMeasuredTableRowHeightPx(
                            (
                              rowElement as HTMLTableRowElement
                            ).getBoundingClientRect().height
                          )
                        )
                      : [];
                    const currentHeights =
                      measuredRows.length > 0
                        ? measuredRows
                        : rowHeightsPx.map((rowHeightPx) => rowHeightPx ?? 24);

                    beginTableCornerResize(
                      event,
                      nodeIndex,
                      handleTableWidthPx,
                      columnWidthsPx,
                      currentHeights,
                      maxTableWidthPx
                    );
                  }}
                  onContextMenu={(event) => {
                    if (isReadOnly) {
                      return;
                    }

                    event.preventDefault();
                    event.stopPropagation();
                    const context = resolveTableContextMenuLocation(
                      nodeIndex,
                      0,
                      0
                    );
                    clearTableCellSelection();
                    setSingleTableCellSelection(
                      context.tableIndex,
                      context.rowIndex,
                      context.cellIndex
                    );
                    editor.selectTableCell(
                      context.tableIndex,
                      context.rowIndex,
                      context.cellIndex
                    );
                    openTableContextMenu(context, {
                      x: event.clientX,
                      y: event.clientY,
                    });
                  }}
                >
                  <span
                    style={{
                      ...TABLE_RESIZE_HANDLE_STYLE,
                      position: "absolute",
                      right: 0,
                      bottom: 0,
                    }}
                  />
                </span>
              ) : null}
            </>
          );
        })()}
      </div>
    );
  };

  const renderNodeSegments = React.useCallback(
    (
      nodeSegments: DocumentPageNodeSegment[],
      options?: {
        pageLayout?: DocumentLayoutMetrics;
        suppressLikelyFullPageCoverImageKeys?: Set<string>;
      }
    ): React.ReactNode[] => {
      const rendered: React.ReactNode[] = [];
      let pageFlowTopPx = 0;
      const resolvedPageLayout = options?.pageLayout ?? documentLayout;
      const foreignWrapExclusionsBySegmentIndex =
        precomputePageSegmentForeignWrapExclusions(
          nodeSegments,
          editor.model,
          documentContentWidthPx,
          editor.model.metadata.numberingDefinitions,
          docGridLinePitchPxByNodeIndex,
          pageContentWidthPxByNodeIndex,
          resolvedPageLayout,
          {
            floatingMovePreview,
            resizePreview,
          }
        );

      for (let offset = 0; offset < nodeSegments.length; offset += 1) {
        const letterheadGroup = paragraphLetterheadColumnGroupAtSegmentOffset(
          editor.model.nodes,
          nodeSegments,
          offset
        );
        if (letterheadGroup) {
          const renderLetterheadSegment = (
            segment: DocumentPageNodeSegment,
            side: LetterheadFloatSide,
            oppositeSide: LetterheadFloatSide,
            isFirstInColumn: boolean
          ): React.ReactNode => {
            const groupNode = editor.model.nodes[segment.nodeIndex];
            if (!groupNode) {
              return null;
            }

            const segmentKeySuffix = segment.tableRowRange
              ? `rows-${segment.tableRowRange.startRowIndex}-${
                  segment.tableRowRange.endRowIndex
                }${
                  segment.tableRowSlice
                    ? `-slice-${Math.max(
                        0,
                        Math.round(segment.tableRowSlice.startOffsetPx)
                      )}-${Math.max(
                        MIN_PARAGRAPH_LINE_HEIGHT_PX,
                        Math.round(segment.tableRowSlice.sliceHeightPx)
                      )}`
                    : ""
                }`
              : segment.paragraphLineRange
              ? `lines-${segment.paragraphLineRange.startLineIndex}-${segment.paragraphLineRange.endLineIndex}`
              : "all";

            return (
              <React.Fragment
                key={`letterhead-segment-${side}-${segment.nodeIndex}-${segmentKeySuffix}`}
              >
                {renderDocumentNode(
                  groupNode,
                  segment.nodeIndex,
                  segment.tableRowRange,
                  segment.tableRowSlice,
                  segment.paragraphLineRange,
                  {
                    letterheadFloatSide: side,
                    oppositeLetterheadFloatSide: oppositeSide,
                    suppressLetterheadColumnLayout: true,
                    isFirstInLetterheadColumn: isFirstInColumn,
                    pageLayout: options?.pageLayout,
                    suppressLikelyFullPageCoverImageKeys:
                      options?.suppressLikelyFullPageCoverImageKeys,
                  }
                )}
              </React.Fragment>
            );
          };

          const leftSegments = letterheadGroup.leftSegments;
          const rightSegments = letterheadGroup.rightSegments;
          const letterheadRowCount = Math.max(
            leftSegments.length,
            rightSegments.length
          );
          const estimateLetterheadSegmentHeightPx = (
            segment: DocumentPageNodeSegment
          ): number => {
            const groupNode = editor.model.nodes[segment.nodeIndex];
            if (!groupNode) {
              return 0;
            }

            return estimateRenderedPageSegmentHeightPx(
              groupNode,
              segment,
              editor.model,
              documentContentWidthPx,
              editor.model.metadata.numberingDefinitions,
              docGridLinePitchPxByNodeIndex.get(segment.nodeIndex)
            );
          };

          rendered.push(
            <div
              key={`letterhead-group-${letterheadGroup.startOffset}-${letterheadGroup.endOffset}`}
              style={letterheadColumnGroupContainerStyle()}
            >
              {Array.from({ length: letterheadRowCount }, (_, rowIndex) => {
                const leftSegment = leftSegments[rowIndex];
                const rightSegment = rightSegments[rowIndex];

                return (
                  <React.Fragment
                    key={`letterhead-row-${letterheadGroup.startOffset}-${rowIndex}`}
                  >
                    <div style={letterheadColumnStackStyle()}>
                      {leftSegment
                        ? renderLetterheadSegment(
                            leftSegment,
                            "left",
                            "right",
                            rowIndex === 0
                          )
                        : null}
                    </div>
                    <div style={letterheadColumnStackStyle()}>
                      {rightSegment
                        ? renderLetterheadSegment(
                            rightSegment,
                            "right",
                            "left",
                            rowIndex === 0
                          )
                        : null}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          );

          pageFlowTopPx += Array.from(
            { length: letterheadRowCount },
            (_, rowIndex) => {
              const leftHeightPx = leftSegments[rowIndex]
                ? estimateLetterheadSegmentHeightPx(leftSegments[rowIndex]!)
                : 0;
              const rightHeightPx = rightSegments[rowIndex]
                ? estimateLetterheadSegmentHeightPx(rightSegments[rowIndex]!)
                : 0;
              return Math.max(leftHeightPx, rightHeightPx);
            }
          ).reduce((sum, rowHeightPx) => sum + rowHeightPx, 0);

          offset = letterheadGroup.endOffset - 1;
          continue;
        }

        const segment = nodeSegments[offset];
        const nodeIndex = segment.nodeIndex;
        const node = editor.model.nodes[nodeIndex];
        if (!node) {
          continue;
        }

        if (node.type === "paragraph" && !segment.tableRowRange) {
          const segmentHasPartialLines = paragraphSegmentHasPartialLineRange(
            segment.paragraphLineRange
          );
          const dropCap = paragraphDropCap(node);
          const nextSegment = nodeSegments[offset + 1];
          const nextSegmentHasPartialLines =
            paragraphSegmentHasPartialLineRange(
              nextSegment?.paragraphLineRange
            );
          const nextNodeIndex = nextSegment?.nodeIndex;
          const nextNode =
            nextNodeIndex !== undefined
              ? editor.model.nodes[nextNodeIndex]
              : undefined;
          if (
            dropCap?.type === "drop" &&
            nextNode?.type === "paragraph" &&
            !nextSegment?.tableRowRange &&
            !segmentHasPartialLines &&
            !nextSegmentHasPartialLines
          ) {
            const preview =
              dropCapMovePreview?.nodeIndex === nodeIndex
                ? dropCapMovePreview
                : undefined;
            const resizePreview =
              dropCapResizePreview?.nodeIndex === nodeIndex
                ? dropCapResizePreview
                : undefined;
            const dropCapText = paragraphText(node).replace(/\s+/g, " ").trim();
            const dropCapStyle = firstRunStyle(node);
            const nextParagraphWidthPx = paragraphAvailableTextWidthPx(
              nextNode,
              documentContentWidthPx,
              editor.model.metadata.numberingDefinitions
            );
            const nextParagraphLineHeight = paragraphLineHeight(
              nextNode,
              nextNodeIndex !== undefined
                ? docGridLinePitchPxByNodeIndex.get(nextNodeIndex)
                : undefined
            );
            const lineHeightPx =
              typeof nextParagraphLineHeight === "number"
                ? Math.max(
                    MIN_PARAGRAPH_LINE_HEIGHT_PX,
                    Math.round(
                      nextParagraphLineHeight *
                        paragraphBaseFontSizePx(nextNode)
                    )
                  )
                : typeof nextParagraphLineHeight === "string" &&
                  nextParagraphLineHeight.endsWith("px")
                ? Math.max(
                    MIN_PARAGRAPH_LINE_HEIGHT_PX,
                    Math.round(Number.parseFloat(nextParagraphLineHeight))
                  )
                : estimateParagraphLineHeightPx(
                    nextNode,
                    nextNodeIndex !== undefined
                      ? docGridLinePitchPxByNodeIndex.get(nextNodeIndex)
                      : undefined
                  );
            const lineCount = Math.max(2, Math.round(dropCap.lines ?? 3));
            const dropCapFontSizePx = resolveDropCapFontSizePx(
              dropCapStyle,
              lineHeightPx,
              lineCount,
              resizePreview?.fontSizePx
            );
            const dropCapHeightPx = resolveDropCapVisualHeightPx(
              dropCapStyle,
              lineHeightPx,
              lineCount,
              dropCapFontSizePx
            );
            const dropCapTextStyle: TextRunNode["style"] = {
              ...(dropCapStyle ?? {}),
              fontSizePt: Math.max(1, (dropCapFontSizePx * 72) / 96),
            };
            const dropCapWidthPx = Math.max(
              lineHeightPx,
              Math.round(
                measureTextWidthPx(
                  dropCapText || "A",
                  dropCapTextStyle,
                  dropCapFontSizePx
                ) +
                  dropCapFontSizePx * 0.18
              )
            );
            const horizontalGapPx = Math.max(
              4,
              twipsToPixels(dropCap.horizontalSpaceTwips) ?? 6
            );
            const baseLeftPx = Math.max(0, twipsToPixels(dropCap.xTwips) ?? 0);
            const baseTopPx = Math.max(0, twipsToPixels(dropCap.yTwips) ?? 0);
            const movedLeftPx = clampNumber(
              baseLeftPx + Math.round(preview?.deltaX ?? 0),
              0,
              Math.max(0, nextParagraphWidthPx - dropCapWidthPx)
            );
            const movedTopPx = Math.max(
              0,
              baseTopPx + Math.round(preview?.deltaY ?? 0)
            );
            const floatRight =
              movedLeftPx + dropCapWidthPx / 2 > nextParagraphWidthPx / 2;
            const trailingOffsetPx = Math.max(
              0,
              nextParagraphWidthPx - dropCapWidthPx - movedLeftPx
            );
            const isSelectedDropCap = selectedDropCapNodeIndex === nodeIndex;
            const isHoveredDropCap = hoveredDropCapNodeIndex === nodeIndex;
            const showDropCapHandles =
              !isReadOnly &&
              (isSelectedDropCap ||
                isHoveredDropCap ||
                dropCapMovePreview?.nodeIndex === nodeIndex ||
                dropCapResizePreview?.nodeIndex === nodeIndex);

            const dropCapWrapSource =
              buildParagraphPretextLayoutSource(nextNode);
            const dropCapWrapExclusion = floatRight
              ? {
                  left: Math.max(0, movedLeftPx - horizontalGapPx),
                  right: Math.min(
                    nextParagraphWidthPx,
                    movedLeftPx + dropCapWidthPx
                  ),
                  top: movedTopPx,
                  bottom:
                    movedTopPx +
                    dropCapHeightPx +
                    Math.max(0, twipsToPixels(dropCap.verticalSpaceTwips) ?? 0),
                }
              : {
                  left: Math.max(0, movedLeftPx),
                  right: Math.min(
                    nextParagraphWidthPx,
                    movedLeftPx + dropCapWidthPx + horizontalGapPx
                  ),
                  top: movedTopPx,
                  bottom:
                    movedTopPx +
                    dropCapHeightPx +
                    Math.max(0, twipsToPixels(dropCap.verticalSpaceTwips) ?? 0),
                };
            const dropCapManualWrapLayout = dropCapWrapSource
              ? resolveParagraphPretextExclusionLayout(
                  nextNode,
                  dropCapWrapSource,
                  nextParagraphWidthPx,
                  lineHeightPx,
                  [dropCapWrapExclusion]
                )
              : undefined;
            const dropCapBlockHeightPx = Math.max(
              dropCapManualWrapLayout?.height ?? 0,
              dropCapWrapExclusion.bottom
            );

            rendered.push(
              <div
                key={`dropcap-${nodeIndex}-${nextNodeIndex}`}
                style={{
                  display: "flow-root",
                  position: "relative",
                }}
              >
                {!dropCapManualWrapLayout && dropCapText.length > 0 ? (
                  <span
                    data-docx-drop-cap="true"
                    data-docx-drop-cap-node-index={nodeIndex}
                    contentEditable={false}
                    style={{
                      float: floatRight ? "right" : "left",
                      marginTop: movedTopPx,
                      marginBottom: Math.max(
                        0,
                        twipsToPixels(dropCap.verticalSpaceTwips) ?? 0
                      ),
                      ...(floatRight
                        ? {
                            marginLeft: horizontalGapPx,
                            marginRight: trailingOffsetPx,
                          }
                        : {
                            marginLeft: movedLeftPx,
                            marginRight: horizontalGapPx,
                          }),
                      position: "relative",
                      zIndex: 2,
                      width: dropCapWidthPx,
                      minHeight: dropCapHeightPx,
                      display: "inline-flex",
                      alignItems: "stretch",
                      touchAction: "none",
                      userSelect: "none",
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                      activateDropCapEditor(nodeIndex);
                    }}
                    onPointerEnter={() => {
                      if (!isReadOnly) {
                        setHoveredDropCapNodeIndex(nodeIndex);
                      }
                    }}
                    onPointerLeave={() => {
                      setHoveredDropCapNodeIndex((current) =>
                        current === nodeIndex ? undefined : current
                      );
                    }}
                  >
                    <input
                      type="text"
                      value={dropCapText}
                      readOnly={isReadOnly}
                      spellCheck={false}
                      aria-label="Drop cap text"
                      onChange={(event) => {
                        if (isReadOnly) {
                          return;
                        }
                        editor.setParagraphDropCapText(
                          nodeIndex,
                          event.currentTarget.value
                        );
                      }}
                      onPointerDown={(event) => {
                        event.stopPropagation();
                        activateDropCapEditor(nodeIndex);
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                      onFocus={() => {
                        activateDropCapEditor(nodeIndex);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                        }
                      }}
                      style={{
                        ...runStyleToCss(
                          dropCapTextStyle,
                          documentContentTheme
                        ),
                        width: "100%",
                        minHeight: dropCapHeightPx,
                        border: isSelectedDropCap
                          ? "1px solid rgba(37, 99, 235, 0.8)"
                          : "1px solid transparent",
                        borderRadius: 4,
                        backgroundColor: "transparent",
                        outline: "none",
                        padding: 0,
                        margin: 0,
                        lineHeight: 1,
                        whiteSpace: "pre",
                        overflow: "visible",
                        boxSizing: "border-box",
                        boxShadow: isSelectedDropCap
                          ? "0 0 0 2px rgba(191, 219, 254, 0.65)"
                          : undefined,
                        color:
                          themedRunColor(
                            dropCapTextStyle.color,
                            documentContentTheme
                          ) ?? undefined,
                        fontFamily:
                          cssFontFamily(dropCapTextStyle.fontFamily) ??
                          undefined,
                      }}
                    />
                    {showDropCapHandles ? (
                      <span
                        contentEditable={false}
                        data-docx-table-move-handle="true"
                        title="Move drop cap"
                        style={{
                          position: "absolute",
                          top: -TABLE_MOVE_HANDLE_HIT_SIZE - 4,
                          left: -TABLE_MOVE_HANDLE_HIT_SIZE - 4,
                          width: TABLE_MOVE_HANDLE_HIT_SIZE,
                          height: TABLE_MOVE_HANDLE_HIT_SIZE,
                          display: "grid",
                          placeItems: "center",
                          cursor: "move",
                          zIndex: 7,
                          touchAction: "none",
                          userSelect: "none",
                        }}
                        onPointerDown={(event) =>
                          beginDropCapMove(event, nodeIndex, dropCap)
                        }
                      >
                        <span
                          style={{
                            ...TABLE_MOVE_HANDLE_STYLE,
                          }}
                        >
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M12 2v20" />
                            <path d="m7 7 5-5 5 5" />
                            <path d="m7 17 5 5 5-5" />
                            <path d="M2 12h20" />
                            <path d="m7 7-5 5 5 5" />
                            <path d="m17 7 5 5-5 5" />
                          </svg>
                        </span>
                      </span>
                    ) : null}
                    {showDropCapHandles ? (
                      <span
                        contentEditable={false}
                        data-image-resize-handle="true"
                        title="Resize drop cap"
                        style={{
                          position: "absolute",
                          right: -TABLE_HANDLE_SAFEZONE_RIGHT_PX,
                          bottom: -TABLE_HANDLE_SAFEZONE_BOTTOM_PX,
                          width:
                            TABLE_RESIZE_HANDLE_HIT_SIZE +
                            TABLE_HANDLE_SAFEZONE_RIGHT_PX,
                          height:
                            TABLE_RESIZE_HANDLE_HIT_SIZE +
                            TABLE_HANDLE_SAFEZONE_BOTTOM_PX,
                          display: "grid",
                          placeItems: "end",
                          zIndex: 7,
                          cursor: "nwse-resize",
                          touchAction: "none",
                          userSelect: "none",
                        }}
                        onPointerDown={(event) =>
                          beginDropCapResize(
                            event,
                            nodeIndex,
                            dropCapFontSizePx,
                            dropCapWidthPx,
                            dropCapHeightPx
                          )
                        }
                      >
                        <span
                          style={{
                            ...TABLE_RESIZE_HANDLE_STYLE,
                            pointerEvents: "auto",
                          }}
                        />
                      </span>
                    ) : null}
                  </span>
                ) : null}
                {dropCapManualWrapLayout && dropCapWrapSource
                  ? (() => {
                      const activeSession =
                        activeWrappedParagraphSession?.locationKey ===
                        `p:${nextNodeIndex}`
                          ? activeWrappedParagraphSession
                          : undefined;
                      const activeSource = activeSession
                        ? buildSyntheticPretextLayoutSource(
                            activeSession.text,
                            firstRunStyle(nextNode)
                          )
                        : dropCapWrapSource;
                      const activeLayout = activeSession
                        ? layoutParagraphPretextSource(
                            nextNode,
                            activeSource,
                            nextParagraphWidthPx,
                            lineHeightPx,
                            [dropCapWrapExclusion]
                          ) ?? dropCapManualWrapLayout
                        : dropCapManualWrapLayout;
                      return renderWrappedPretextParagraph({
                        location: {
                          kind: "paragraph",
                          nodeIndex: nextNodeIndex,
                        },
                        keyPrefix: `dropcap-wrap-${nodeIndex}-${nextNodeIndex}`,
                        source: activeSource,
                        layout: activeLayout,
                        lineHeightPx,
                        blockHeightPx: dropCapBlockHeightPx,
                        obstacleNodes:
                          dropCapText.length > 0 ? (
                            <span
                              data-docx-drop-cap="true"
                              data-docx-drop-cap-node-index={nodeIndex}
                              contentEditable={false}
                              style={{
                                position: "absolute",
                                left: movedLeftPx,
                                top: movedTopPx,
                                zIndex: 3,
                                width: dropCapWidthPx,
                                minHeight: dropCapHeightPx,
                                display: "inline-flex",
                                alignItems: "stretch",
                                touchAction: "none",
                                userSelect: "none",
                              }}
                              onClick={(event) => {
                                event.stopPropagation();
                                activateDropCapEditor(nodeIndex);
                              }}
                              onPointerEnter={() => {
                                if (!isReadOnly) {
                                  setHoveredDropCapNodeIndex(nodeIndex);
                                }
                              }}
                              onPointerLeave={() => {
                                setHoveredDropCapNodeIndex((current) =>
                                  current === nodeIndex ? undefined : current
                                );
                              }}
                            >
                              <input
                                type="text"
                                value={dropCapText}
                                readOnly={isReadOnly}
                                spellCheck={false}
                                aria-label="Drop cap text"
                                onChange={(event) => {
                                  if (!isReadOnly) {
                                    editor.setParagraphDropCapText(
                                      nodeIndex,
                                      event.currentTarget.value
                                    );
                                  }
                                }}
                                onPointerDown={(event) => {
                                  event.stopPropagation();
                                  activateDropCapEditor(nodeIndex);
                                }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                }}
                                onFocus={() => {
                                  activateDropCapEditor(nodeIndex);
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") {
                                    event.preventDefault();
                                  }
                                }}
                                style={{
                                  ...runStyleToCss(
                                    dropCapTextStyle,
                                    documentContentTheme
                                  ),
                                  width: "100%",
                                  minHeight: dropCapHeightPx,
                                  border: isSelectedDropCap
                                    ? "1px solid rgba(37, 99, 235, 0.8)"
                                    : "1px solid transparent",
                                  borderRadius: 4,
                                  backgroundColor: "transparent",
                                  outline: "none",
                                  padding: 0,
                                  margin: 0,
                                  lineHeight: 1,
                                  whiteSpace: "pre",
                                  overflow: "visible",
                                  boxSizing: "border-box",
                                  boxShadow: isSelectedDropCap
                                    ? "0 0 0 2px rgba(191, 219, 254, 0.65)"
                                    : undefined,
                                  color:
                                    themedRunColor(
                                      dropCapTextStyle.color,
                                      documentContentTheme
                                    ) ?? undefined,
                                  fontFamily:
                                    cssFontFamily(
                                      dropCapTextStyle.fontFamily
                                    ) ?? undefined,
                                }}
                              />
                              {showDropCapHandles ? (
                                <span
                                  contentEditable={false}
                                  data-docx-table-move-handle="true"
                                  title="Move drop cap"
                                  style={{
                                    position: "absolute",
                                    top: -TABLE_MOVE_HANDLE_HIT_SIZE - 4,
                                    left: -TABLE_MOVE_HANDLE_HIT_SIZE - 4,
                                    width: TABLE_MOVE_HANDLE_HIT_SIZE,
                                    height: TABLE_MOVE_HANDLE_HIT_SIZE,
                                    display: "grid",
                                    placeItems: "center",
                                    cursor: "move",
                                    zIndex: 7,
                                    touchAction: "none",
                                    userSelect: "none",
                                  }}
                                  onPointerDown={(event) =>
                                    beginDropCapMove(event, nodeIndex, dropCap)
                                  }
                                >
                                  <span style={{ ...TABLE_MOVE_HANDLE_STYLE }}>
                                    <svg
                                      width="11"
                                      height="11"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      aria-hidden="true"
                                    >
                                      <path d="M12 2v20" />
                                      <path d="m7 7 5-5 5 5" />
                                      <path d="m7 17 5 5-5 5" />
                                      <path d="M2 12h20" />
                                      <path d="m7 7-5 5 5 5" />
                                      <path d="m17 7 5 5-5 5" />
                                    </svg>
                                  </span>
                                </span>
                              ) : null}
                              {showDropCapHandles ? (
                                <span
                                  contentEditable={false}
                                  data-image-resize-handle="true"
                                  title="Resize drop cap"
                                  style={{
                                    position: "absolute",
                                    right: -TABLE_HANDLE_SAFEZONE_RIGHT_PX,
                                    bottom: -TABLE_HANDLE_SAFEZONE_BOTTOM_PX,
                                    width:
                                      TABLE_RESIZE_HANDLE_HIT_SIZE +
                                      TABLE_HANDLE_SAFEZONE_RIGHT_PX,
                                    height:
                                      TABLE_RESIZE_HANDLE_HIT_SIZE +
                                      TABLE_HANDLE_SAFEZONE_BOTTOM_PX,
                                    display: "grid",
                                    placeItems: "end",
                                    zIndex: 7,
                                    cursor: "nwse-resize",
                                    touchAction: "none",
                                    userSelect: "none",
                                  }}
                                  onPointerDown={(event) =>
                                    beginDropCapResize(
                                      event,
                                      nodeIndex,
                                      dropCapFontSizePx,
                                      dropCapWidthPx,
                                      dropCapHeightPx
                                    )
                                  }
                                >
                                  <span
                                    style={{
                                      ...TABLE_RESIZE_HANDLE_STYLE,
                                      pointerEvents: "auto",
                                    }}
                                  />
                                </span>
                              ) : null}
                            </span>
                          ) : undefined,
                      });
                    })()
                  : renderDocumentNode(
                      nextNode,
                      nextNodeIndex,
                      nextSegment?.tableRowRange,
                      nextSegment?.tableRowSlice,
                      nextSegment?.paragraphLineRange,
                      {
                        pageLayout: options?.pageLayout,
                        suppressLikelyFullPageCoverImageKeys:
                          options?.suppressLikelyFullPageCoverImageKeys,
                      }
                    )}
              </div>
            );
            offset += 1;
            continue;
          }
        }

        rendered.push(
          renderDocumentNode(
            node,
            nodeIndex,
            segment.tableRowRange,
            segment.tableRowSlice,
            segment.paragraphLineRange,
            {
              pageLayout: options?.pageLayout,
              pageFlowTopPx,
              pageFlowForeignExclusions:
                foreignWrapExclusionsBySegmentIndex[offset],
              suppressLikelyFullPageCoverImageKeys:
                options?.suppressLikelyFullPageCoverImageKeys,
            }
          )
        );
        pageFlowTopPx += estimateRenderedPageSegmentHeightPx(
          node,
          segment,
          editor.model,
          documentContentWidthPx,
          editor.model.metadata.numberingDefinitions,
          docGridLinePitchPxByNodeIndex.get(nodeIndex)
        );
      }

      return rendered;
    },
    [
      activeEditableParagraphSegment,
      beginDropCapMove,
      beginDropCapResize,
      clearObjectSelectionForParagraphEntry,
      docGridLinePitchPxByNodeIndex,
      documentContentWidthPx,
      documentLayout,
      dropCapMovePreview,
      dropCapResizePreview,
      editor,
      floatingMovePreview,
      focusDropCapWrappedParagraph,
      hoveredDropCapNodeIndex,
      isReadOnly,
      pageContentWidthPxByNodeIndex,
      renderDocumentNode,
      resizePreview,
      selectedDropCapNodeIndex,
    ]
  );

  const footnotes = editor.model.metadata.footnotes ?? [];
  const endnotes = editor.model.metadata.endnotes ?? [];
  const footnoteDisplayIndexById = React.useMemo(
    () => new Map(footnotes.map((note, index) => [note.id, index + 1])),
    [footnotes]
  );
  const endnoteDisplayIndexById = React.useMemo(
    () => new Map(endnotes.map((note, index) => [note.id, index + 1])),
    [endnotes]
  );
  const footnotesById = React.useMemo(
    () => new Map(footnotes.map((note) => [note.id, note])),
    [footnotes]
  );
  const endnotesById = React.useMemo(
    () => new Map(endnotes.map((note) => [note.id, note])),
    [endnotes]
  );
  const pageFootnotesByIndex = React.useMemo(() => {
    if (footnotes.length === 0) {
      return pageNodeSegmentsByPage.map(() => [] as DocumentNoteDefinition[]);
    }

    return pageNodeSegmentsByPage.map((nodeSegments) => {
      const referencedIds: number[] = [];
      const seen = new Set<number>();
      nodeSegments.forEach((segment) => {
        const node = editor.model.nodes[segment.nodeIndex];
        if (!node) {
          return;
        }

        nodeReferencedNoteIds(
          node,
          "footnote",
          segment.tableRowRange,
          segment.paragraphLineRange
        ).forEach((referenceId) => {
          if (seen.has(referenceId)) {
            return;
          }
          seen.add(referenceId);
          referencedIds.push(referenceId);
        });
      });

      return referencedIds
        .map((referenceId) => footnotesById.get(referenceId))
        .filter((note): note is NonNullable<typeof note> => Boolean(note));
    });
  }, [
    editor.model.nodes,
    footnotes.length,
    footnotesById,
    pageNodeSegmentsByPage,
  ]);
  const referencedEndnotes = React.useMemo(() => {
    if (endnotes.length === 0) {
      return endnotes;
    }

    const referencedIds: number[] = [];
    const seen = new Set<number>();
    editor.model.nodes.forEach((node) => {
      nodeReferencedNoteIds(node, "endnote").forEach((referenceId) => {
        if (seen.has(referenceId)) {
          return;
        }
        seen.add(referenceId);
        referencedIds.push(referenceId);
      });
    });

    const referenced = referencedIds
      .map((referenceId) => endnotesById.get(referenceId))
      .filter((note): note is NonNullable<typeof note> => Boolean(note));
    return referenced.length > 0 ? referenced : endnotes;
  }, [editor.model.nodes, endnotes, endnotesById]);
  const placedFootnoteIds = React.useMemo(() => {
    const ids = new Set<number>();
    pageFootnotesByIndex.forEach((notesForPage) => {
      notesForPage.forEach((note) => {
        ids.add(note.id);
      });
    });
    return ids;
  }, [pageFootnotesByIndex]);
  const remainingFootnotes = React.useMemo(
    () => footnotes.filter((note) => !placedFootnoteIds.has(note.id)),
    [footnotes, placedFootnoteIds]
  );
  const renderDocumentNote = React.useCallback(
    (
      note: (typeof footnotes)[number],
      noteKind: "footnote" | "endnote",
      keyPrefix: string,
      pageNumberValue: number,
      runRenderOptions: ParagraphRunRenderOptions
    ): React.ReactNode => {
      const noteNodes = note.nodes?.filter(
        (node): node is ParagraphNode => node.type === "paragraph"
      );
      if (!noteNodes || noteNodes.length === 0) {
        return note.text;
      }

      return noteNodes.map((paragraph, paragraphIndex) => (
        <span
          key={`${keyPrefix}-paragraph-${paragraphIndex}`}
          style={{
            display: "block",
            ...(paragraphIndex > 0 ? { marginTop: 4 } : undefined),
          }}
        >
          {paragraphIndex === 0 ? (
            <sup
              style={{
                marginRight: 4,
                verticalAlign: "super",
                fontSize: "0.75em",
                ...(noteKind === "endnote" ? { opacity: 0.75 } : undefined),
              }}
            >
              {noteMarkerLabel(
                { kind: noteKind, id: note.id },
                footnoteDisplayIndexById,
                endnoteDisplayIndexById
              ) ?? note.id}
              {noteKind === "endnote" ? "." : null}
            </sup>
          ) : null}
          {renderParagraphRuns(
            paragraph,
            `${keyPrefix}-runs`,
            documentContentTheme,
            undefined,
            scrollToBookmark,
            undefined,
            {
              footnote: footnoteDisplayIndexById,
              endnote: endnoteDisplayIndexById,
            },
            pageNumberValue,
            totalPagesForFieldResolution,
            runRenderOptions
          )}
        </span>
      ));
    },
    [
      documentContentTheme,
      endnoteDisplayIndexById,
      footnoteDisplayIndexById,
      renderParagraphRuns,
      scrollToBookmark,
      totalPagesForFieldResolution,
    ]
  );
  const headerFooterEditHandlers =
    React.useMemo<HeaderFooterParagraphEditHandlers>(
      () => ({
        registerElement: registerSectionParagraphElement,
        readDraftHtml: readSectionParagraphDraft,
        writeDraftHtml: writeSectionParagraphDraft,
        clearDraftHtml: clearSectionParagraphDraft,
        commitText: commitSectionParagraphDraft,
      }),
      [
        clearSectionParagraphDraft,
        commitSectionParagraphDraft,
        readSectionParagraphDraft,
        registerSectionParagraphElement,
        writeSectionParagraphDraft,
      ]
    );
  const activateHeaderFooterEdit = React.useCallback(
    (
      region: DocxSectionRegion,
      partName: string | undefined,
      pageIndex: number,
      options?: {
        draftKey?: string;
        point?: {
          x: number;
          y: number;
        };
      }
    ): void => {
      if (isReadOnly || !partName) {
        return;
      }

      if (options?.draftKey) {
        pendingSectionParagraphFocusRef.current = {
          draftKey: options.draftKey,
          point: options.point,
        };
      } else {
        pendingSectionParagraphFocusRef.current = undefined;
      }

      clearTableCellSelection();
      editor.setActiveTextRange(undefined);
      setActiveHeaderFooterEdit({
        region,
        partName,
        pageIndex,
      });
    },
    [clearTableCellSelection, editor, isReadOnly]
  );

  return (
    <div
      data-testid="docx-editor-viewer"
      ref={viewerRootRef}
      className={className}
      style={{
        position: "relative",
        display: "grid",
        gap: DOC_PAGE_BREAK_GAP,
        minHeight:
          hideDocumentUntilPaginationSettled && stableHiddenDocumentHeightPx > 0
            ? stableHiddenDocumentHeightPx
            : undefined,
        backgroundColor: pageGapBackgroundColor ?? "transparent",
        ...style,
      }}
      onDragOver={onCanvasDragOver}
      onDragLeave={onCanvasDragLeave}
      onDrop={(event) => void onCanvasDrop(event)}
      onPointerDownCapture={onViewerPointerDown}
      onPointerMoveCapture={onViewerPointerMove}
      onPointerLeave={onViewerPointerLeave}
      onMouseUp={flushActiveRangeFromSelection}
      onPointerUp={flushActiveRangeFromSelection}
      onKeyUp={onViewerKeyUp}
      onKeyDownCapture={onViewerKeyDown}
      onContextMenu={onViewerContextMenu}
    >
      {hideDocumentUntilPaginationSettled ? (
        <div
          data-docx-initial-layout-overlay="true"
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 120,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            paddingTop: 24,
            pointerEvents: "all",
          }}
        >
          {loadingState ?? (
            <div
              style={{
                padding: "8px 12px",
                borderRadius: 999,
                backgroundColor:
                  editor.documentTheme === "dark"
                    ? "rgba(2, 6, 23, 0.9)"
                    : "rgba(255, 255, 255, 0.94)",
                border: `1px solid ${
                  editor.documentTheme === "dark"
                    ? "rgba(148, 163, 184, 0.28)"
                    : "rgba(15, 23, 42, 0.12)"
                }`,
                boxShadow:
                  editor.documentTheme === "dark"
                    ? "0 8px 20px rgba(2, 6, 23, 0.4)"
                    : "0 8px 20px rgba(15, 23, 42, 0.08)",
                color: editor.documentTheme === "dark" ? "#e5e7eb" : "#111827",
                fontSize: 13,
                lineHeight: 1.2,
              }}
            >
              Loading...
            </div>
          )}
        </div>
      ) : null}
      {isDragOverCanvas ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 200,
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(59, 130, 246, 0.1)",
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          Drop .docx anywhere to replace current document
        </div>
      ) : null}
      {editor.importError ? (
        <div
          role="alert"
          data-docx-import-error="true"
          style={{
            position: "absolute",
            top: 24,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 80,
            width: "min(520px, calc(100% - 32px))",
            display: "grid",
            gap: 6,
            padding: "12px 14px",
            borderRadius: 8,
            border: `1px solid ${
              editor.documentTheme === "dark"
                ? "rgba(248, 113, 113, 0.42)"
                : "rgba(185, 28, 28, 0.26)"
            }`,
            backgroundColor:
              editor.documentTheme === "dark"
                ? "rgba(69, 10, 10, 0.96)"
                : "rgba(254, 242, 242, 0.98)",
            color: editor.documentTheme === "dark" ? "#fee2e2" : "#7f1d1d",
            boxShadow:
              editor.documentTheme === "dark"
                ? "0 12px 28px rgba(2, 6, 23, 0.44)"
                : "0 12px 28px rgba(127, 29, 29, 0.12)",
            fontSize: 13,
            lineHeight: 1.35,
          }}
        >
          <strong style={{ fontWeight: 600 }}>Failed to load DOCX</strong>
          <span style={{ overflowWrap: "anywhere" }}>
            {editor.importError.message}
          </span>
        </div>
      ) : null}
      {tableMoveDropPreview && !isReadOnly ? (
        <div
          style={{
            position: "absolute",
            top: tableMoveDropPreview.top,
            left: tableMoveDropPreview.left,
            width: tableMoveDropPreview.width,
            height: 2,
            borderRadius: 999,
            backgroundColor: "#2563eb",
            boxShadow: "0 0 0 1px rgba(37, 99, 235, 0.2)",
            zIndex: 16,
            pointerEvents: "none",
          }}
        />
      ) : null}

      {pageStackVirtualSpacers.before.heightPx > 0 ? (
        <div
          aria-hidden="true"
          data-docx-page-window-spacer="before"
          style={{
            height: pageStackVirtualSpacers.before.heightPx,
            width: pageStackVirtualSpacers.before.widthPx,
            margin: "0 auto",
            pointerEvents: "none",
            visibility: hideDocumentUntilPaginationSettled
              ? "hidden"
              : undefined,
          }}
        />
      ) : null}

      {visiblePageIndexes.map((pageIndex) => {
        const pageNodeSegments = pageNodeSegmentsByPage[pageIndex] ?? [];
        const pageInfo = pageSectionInfoByIndex[pageIndex];
        const pageLayout = pageInfo?.layout ?? documentLayout;
        const pageWrapperWidthPx = showTrackedChangeGutter
          ? pageLayout.pageWidthPx + TRACKED_CHANGE_GUTTER_WIDTH_PX
          : pageLayout.pageWidthPx;

        const pageContentWidthPx = Math.max(
          120,
          pageLayout.pageWidthPx -
            pageLayout.marginsPx.left -
            pageLayout.marginsPx.right
        );
        const pageSections = pageHeaderAndFooterNodes[pageIndex] ?? {
          headerPartName: undefined,
          footerPartName: undefined,
          headerNodes: [] as DocModel["nodes"],
          footerNodes: [] as DocModel["nodes"],
        };
        const pageHeaderFooterEditActive =
          !isReadOnly && activeHeaderFooterEdit?.pageIndex === pageIndex;
        const headerEditScope = pageSections.headerPartName
          ? {
              region: "header" as const,
              partName: pageSections.headerPartName,
              handlers:
                !isReadOnly && pageHeaderFooterEditActive
                  ? headerFooterEditHandlers
                  : undefined,
            }
          : undefined;
        const footerEditScope = pageSections.footerPartName
          ? {
              region: "footer" as const,
              partName: pageSections.footerPartName,
              handlers:
                !isReadOnly && pageHeaderFooterEditActive
                  ? headerFooterEditHandlers
                  : undefined,
            }
          : undefined;
        const sectionImageInteractionReadOnly =
          isReadOnly || !pageHeaderFooterEditActive;
        const headerImageInteraction = pageSections.headerPartName
          ? {
              isReadOnly: sectionImageInteractionReadOnly,
              selectedImageKey: selectedSectionImageKey,
              floatingMovePreview,
              onImagePointerDown: beginSectionFloatingImageMove,
              onImageClick: onSectionImageClick,
            }
          : undefined;
        const footerImageInteraction = pageSections.footerPartName
          ? {
              isReadOnly: sectionImageInteractionReadOnly,
              selectedImageKey: selectedSectionImageKey,
              floatingMovePreview,
              onImagePointerDown: beginSectionFloatingImageMove,
              onImageClick: onSectionImageClick,
            }
          : undefined;
        const headerNeedsPageWideLayout = sectionNodesNeedPageWideLayout(
          pageSections.headerNodes,
          pageLayout.pageWidthPx,
          pageContentWidthPx
        );
        const footerNeedsPageWideLayout = sectionNodesNeedPageWideLayout(
          pageSections.footerNodes,
          pageLayout.pageWidthPx,
          pageContentWidthPx
        );
        const footerNeedsFullPageOverlay =
          sectionNodesNeedFullPageFooterOverlay(pageSections.footerNodes);
        const inactiveFooterActivationZoneHeightPx = Math.max(
          48,
          Math.round(
            pageLayout.marginsPx.bottom + pageLayout.footerDistancePx + 24
          )
        );
        const headerOverlayTop = Math.max(0, pageLayout.headerDistancePx);
        const footerOverlayBottom = Math.max(0, pageLayout.footerDistancePx);
        const headerTopOffsetPx = headerOverlayTop - pageLayout.marginsPx.top;
        // The header region renders in-flow, so its own height already offsets
        // the body. Once the header has been measured, use that measured
        // clearance directly (it positions the body right after the header).
        // Only fall back to the estimated floor before measurement, to avoid
        // a first-paint overlap. Taking max(floor, measured) double-counted the
        // in-flow header height and left a large empty gap below tall headers.
        const measuredHeaderClearancePx = headerBodyClearanceByPage[pageIndex];
        const headerBodyClearancePx =
          measuredHeaderClearancePx !== undefined
            ? measuredHeaderClearancePx
            : headerBodyClearanceFloorByPage[pageIndex] ?? 0;
        const footerBottomOffsetPx = footerOverlayBottom;
        const headerHostPageOriginLeftPx = headerNeedsPageWideLayout
          ? 0
          : -pageLayout.marginsPx.left;
        const headerHostPageOriginTopPx = -headerOverlayTop;
        const headerHostMarginOriginLeftPx = headerNeedsPageWideLayout
          ? pageLayout.marginsPx.left
          : 0;
        const headerHostMarginOriginTopPx =
          pageLayout.marginsPx.top - headerOverlayTop;
        const footerHostPageOriginLeftPx = 0;
        const footerHostPageOriginTopPx = footerNeedsFullPageOverlay
          ? 0
          : pageLayout.pageHeightPx - footerBottomOffsetPx;
        const footerHostMarginOriginLeftPx = pageLayout.marginsPx.left;
        const footerHostMarginOriginTopPx = pageLayout.marginsPx.top;
        const pageNumber = pageInfo?.pageNumber ?? pageIndex + 1;
        const pageNumberFormat = parseSectionPageNumberFormat(
          pageInfo?.section.sectionPropertiesXml ??
            editor.model.metadata.sectionPropertiesXml
        );
        const pageHeaderFooterRunRenderOptions: ParagraphRunRenderOptions = {
          ...paragraphRunRenderOptions,
          resolveStyleRefFieldValue: (target: string) =>
            resolveStyleRefFieldValueForPage(pageIndex, target),
          ...(pageNumberFormat ? { pageNumberFormat } : undefined),
        };
        const isLastPage = pageIndex === pageCount - 1;
        const pageFootnotes = pageFootnotesByIndex[pageIndex] ?? [];
        const pageBottomFootnotes =
          isLastPage && remainingFootnotes.length > 0
            ? [...pageFootnotes, ...remainingFootnotes]
            : pageFootnotes;
        const pageBodyAvailableHeightPx =
          resolveRenderPageContentHeightPxForPageSegments({
            pageSegments: pageNodeSegments,
            pageIndex,
            defaultPageContentHeightPx: Math.max(
              120,
              pageLayout.pageHeightPx -
                pageLayout.marginsPx.top -
                pageLayout.marginsPx.bottom
            ),
            metricsBySection: paginationSectionMetrics,
            measuredPageContentHeightsPxByPageIndex:
              renderMeasuredPageContentHeightsPxByPageIndex,
            measuredPageContentIdentityKeysByPageIndex:
              measuredPageContentIdentityKeysByIndex,
            pageIdentityKey: pageNodeSegmentIdentityKeysByPage[pageIndex],
            useMeasuredPageContentHeights:
              useMeasuredPageContentHeightsForRender,
            pageContentHeightScale: renderPageContentHeightScale,
          });
        const headerFooterBodyDimmed = pageHeaderFooterEditActive;
        const documentPageBackgroundColor =
          editor.model.metadata.documentBackgroundColor;
        const resolvedPageSurfaceBackgroundColor =
          pageBackgroundColor ??
          documentPageBackgroundColor ??
          pageSurfaceBaseStyle.backgroundColor;
        const pageBorders = parseSectionPageBorders(
          pageInfo?.section.sectionPropertiesXml ??
            editor.model.metadata.sectionPropertiesXml
        );
        const pageBorderOverlayStyle: React.CSSProperties | undefined =
          pageBorders
            ? {
                position: "absolute",
                top: pageBorders.top?.offsetPx ?? 0,
                right: pageBorders.right?.offsetPx ?? 0,
                bottom: pageBorders.bottom?.offsetPx ?? 0,
                left: pageBorders.left?.offsetPx ?? 0,
                pointerEvents: "none",
                borderTop: pageBorders.top?.cssBorder,
                borderRight: pageBorders.right?.cssBorder,
                borderBottom: pageBorders.bottom?.cssBorder,
                borderLeft: pageBorders.left?.cssBorder,
                boxSizing: "border-box",
                zIndex: 0,
              }
            : undefined;
        const pageTrackedChanges =
          positionedTrackedChangesByPage[pageIndex] ?? [];
        const pageGutterContentHeightPx =
          showTrackedChangeGutter && pageTrackedChanges.length > 0
            ? pageTrackedChanges.reduce((maxBottomPx, entry) => {
                return Math.max(
                  maxBottomPx,
                  Math.ceil(
                    entry.top +
                      entry.heightPx +
                      TRACKED_CHANGE_GUTTER_CARD_GAP_PX +
                      8
                  )
                );
              }, pageLayout.pageHeightPx)
            : pageLayout.pageHeightPx;
        const pageVisualHeightPx = Math.max(
          pageLayout.pageHeightPx,
          pageGutterContentHeightPx
        );
        const pageSurfaceStyle: React.CSSProperties = {
          ...pageSurfaceBaseStyle,
          width: pageLayout.pageWidthPx,
          height: pageVisualHeightPx,
          minHeight: pageVisualHeightPx,
          backgroundColor: resolvedPageSurfaceBackgroundColor,
          position: "relative",
          ...pageMarginPaddingStyle(pageLayout.marginsPx),
        };
        const pageCoverBackgroundImages = (() => {
          const collected = new Map<
            string,
            { nodeIndex: number; childIndex: number; image: ImageRunNode }
          >();
          const pageContentHeightPx = Math.max(
            120,
            pageLayout.pageHeightPx -
              pageLayout.marginsPx.top -
              pageLayout.marginsPx.bottom
          );

          for (const segment of pageNodeSegments) {
            const segmentNode = editor.model.nodes[segment.nodeIndex];
            if (!segmentNode || segmentNode.type !== "paragraph") {
              continue;
            }

            const paragraphContentWidthPx =
              pageContentWidthPxByNodeIndex.get(segment.nodeIndex) ??
              documentContentWidthPx;
            segmentNode.children.forEach((child, childIndex) => {
              if (
                child.type !== "image" ||
                !shouldRenderAbsoluteFloatingImage(child) ||
                !likelyFullPageCoverImageRelativeToContentBox(
                  child,
                  paragraphContentWidthPx,
                  pageContentHeightPx
                )
              ) {
                return;
              }

              collected.set(
                fullPageCoverImageRenderKey(segment.nodeIndex, childIndex),
                {
                  nodeIndex: segment.nodeIndex,
                  childIndex,
                  image: child,
                }
              );
            });
          }

          return [...collected.entries()].map(([key, entry]) => ({
            key,
            ...entry,
          }));
        })();
        const suppressedLikelyFullPageCoverImageKeys = new Set(
          pageCoverBackgroundImages.map((entry) => entry.key)
        );

        return (
          <div
            key={`page-${pageIndex}`}
            data-docx-page-wrapper="true"
            data-docx-page-index={pageIndex}
            data-index={pageIndex}
            style={{
              position: "relative",
              width: pageWrapperWidthPx,
              minHeight: pageVisualHeightPx,
              margin: "0 auto",
              // Isolate per-page layout/style recalculation so scrolling and
              // edits on one page don't force whole-document layout passes.
              // (No paint containment: floats and gutter cards may overhang.)
              contain: "layout style",
              visibility: hideDocumentUntilPaginationSettled
                ? "hidden"
                : undefined,
            }}
          >
            <div
              data-docx-page-surface="true"
              ref={pageSurfaceRefForIndex(pageIndex)}
              style={{
                ...pageSurfaceStyle,
                margin: 0,
              }}
            >
              {pageCoverBackgroundImages.length > 0 ? (
                <div
                  data-docx-page-cover-layer="true"
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: pageLayout.pageWidthPx,
                    height: pageLayout.pageHeightPx,
                    overflow: "clip",
                    pointerEvents: "none",
                    zIndex: 0,
                    ...(documentContentFilter
                      ? { filter: documentContentFilter }
                      : undefined),
                  }}
                >
                  {pageCoverBackgroundImages.map(({ key, image }) => {
                    const renderableImageSrc =
                      resolveRenderableImageSource(image);
                    if (!renderableImageSrc) {
                      return null;
                    }

                    return (
                      <img
                        key={`page-cover-${pageIndex}-${key}`}
                        data-docx-page-cover-image="true"
                        src={renderableImageSrc}
                        alt=""
                        aria-hidden="true"
                        draggable={false}
                        style={{
                          position: "absolute",
                          inset: 0,
                          width: "100%",
                          height: "100%",
                          display: "block",
                          objectFit: "cover",
                          filter: appendCssFilters(
                            image.cssFilter,
                            documentContentFilter
                          ),
                          opacity: image.cssOpacity,
                          ...resolveImageRenderTransformStyle(image, {
                            frameWidthPx: pageLayout.pageWidthPx,
                            frameHeightPx: pageLayout.pageHeightPx,
                            fillFrame: true,
                          }),
                        }}
                      />
                    );
                  })}
                </div>
              ) : null}
              {pageBorderOverlayStyle ? (
                <div
                  data-docx-page-border-overlay="true"
                  style={{
                    ...pageBorderOverlayStyle,
                    ...(documentContentFilter
                      ? { filter: documentContentFilter }
                      : undefined),
                  }}
                />
              ) : null}
              {pageSections.headerNodes.length > 0 ? (
                <div
                  ref={(element) => {
                    if (element) {
                      pageHeaderElementsRef.current.set(pageIndex, element);
                    } else {
                      pageHeaderElementsRef.current.delete(pageIndex);
                    }
                  }}
                  style={{
                    display: "grid",
                    gap: 0,
                    width: headerNeedsPageWideLayout
                      ? pageLayout.pageWidthPx
                      : "100%",
                    maxWidth: headerNeedsPageWideLayout
                      ? pageLayout.pageWidthPx
                      : "100%",
                    boxSizing: "border-box",
                    marginTop: headerTopOffsetPx,
                    marginBottom: headerBodyClearancePx,
                    ...(headerNeedsPageWideLayout
                      ? {
                          position: "relative",
                          left: -pageLayout.marginsPx.left,
                        }
                      : undefined),
                    opacity:
                      pageHeaderFooterEditActive || isReadOnly
                        ? 1
                        : HEADER_FOOTER_INACTIVE_OPACITY,
                    transition: "opacity 120ms ease",
                    outline: "none",
                    boxShadow: "none",
                    zIndex: 1,
                    ...(documentContentFilter
                      ? { filter: documentContentFilter }
                      : undefined),
                  }}
                  contentEditable={pageHeaderFooterEditActive && !isReadOnly}
                  suppressContentEditableWarning
                  data-docx-header-footer-region="header"
                  onFocus={(event) => {
                    event.currentTarget.style.outline = "none";
                    event.currentTarget.style.boxShadow = "none";
                  }}
                  onInputCapture={(event) => {
                    if (
                      !pageHeaderFooterEditActive ||
                      !pageSections.headerPartName
                    ) {
                      return;
                    }
                    schedulePaginationMeasurementResume();
                    updateSectionParagraphDraftFromTarget(event.target);
                  }}
                  onBlurCapture={(event) => {
                    if (
                      !pageHeaderFooterEditActive ||
                      !pageSections.headerPartName
                    ) {
                      return;
                    }
                    const relatedTarget = event.relatedTarget as Node | null;
                    if (
                      relatedTarget &&
                      event.currentTarget.contains(relatedTarget)
                    ) {
                      return;
                    }
                    commitSectionRegionDraftsFromContainer(
                      event.currentTarget,
                      "header",
                      pageSections.headerPartName
                    );
                  }}
                  onDoubleClick={(event) => {
                    if (isReadOnly || !pageSections.headerPartName) {
                      return;
                    }
                    event.preventDefault();
                    event.stopPropagation();

                    const target = event.target;
                    const paragraphHost =
                      target instanceof Element
                        ? target.closest<HTMLElement>(
                            "[data-docx-section-paragraph-key]"
                          )
                        : null;
                    const draftKey = paragraphHost?.getAttribute(
                      "data-docx-section-paragraph-key"
                    );
                    activateHeaderFooterEdit(
                      "header",
                      pageSections.headerPartName,
                      pageIndex,
                      {
                        draftKey: draftKey ?? undefined,
                        point: {
                          x: event.clientX,
                          y: event.clientY,
                        },
                      }
                    );
                  }}
                >
                  {pageSections.headerNodes.map((node, index) =>
                    renderHeaderNode(
                      node,
                      `header-${pageIndex}-${index}`,
                      documentContentTheme,
                      editor.model.metadata.numberingDefinitions,
                      headingStyles,
                      headerNeedsPageWideLayout
                        ? pageLayout.pageWidthPx
                        : pageContentWidthPx,
                      scrollToBookmark,
                      {
                        left: headerHostPageOriginLeftPx,
                        top: headerHostPageOriginTopPx,
                        marginLeft: headerHostMarginOriginLeftPx,
                        marginTop: headerHostMarginOriginTopPx,
                        columnLeft: 0,
                        columnTop: 0,
                        pageWidth: pageLayout.pageWidthPx,
                      },
                      {
                        footnote: footnoteDisplayIndexById,
                        endnote: endnoteDisplayIndexById,
                      },
                      pageNumber,
                      totalPagesForFieldResolution,
                      {
                        ...pageHeaderFooterRunRenderOptions,
                        headerFooterRegion: "header",
                      },
                      headerEditScope,
                      headerImageInteraction,
                      undefined,
                      index
                    )
                  )}
                </div>
              ) : null}

              <div
                ref={(element) => {
                  if (element) {
                    pageBodyElementsRef.current.set(pageIndex, element);
                  } else {
                    pageBodyElementsRef.current.delete(pageIndex);
                  }
                }}
                style={{
                  isolation: "isolate",
                  opacity: headerFooterBodyDimmed ? 0.5 : 1,
                  transition: "opacity 120ms ease",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: pageBodyAvailableHeightPx,
                  overflow: "visible",
                  ...(documentContentFilter
                    ? { filter: documentContentFilter }
                    : undefined),
                }}
              >
                <div style={{ minHeight: 0 }}>
                  {(() => {
                    if (pageNodeSegments.length === 0) {
                      return null;
                    }

                    const sectionGroups: Array<{
                      sectionIndex: number;
                      segments: DocumentPageNodeSegment[];
                    }> = [];
                    let currentSectionIndex = 0;

                    pageNodeSegments.forEach((segment) => {
                      currentSectionIndex = resolveSectionIndexForNodeIndex(
                        documentSections,
                        segment.nodeIndex,
                        currentSectionIndex
                      );
                      const currentGroup =
                        sectionGroups[sectionGroups.length - 1];
                      if (
                        currentGroup &&
                        currentGroup.sectionIndex === currentSectionIndex
                      ) {
                        currentGroup.segments.push(segment);
                        return;
                      }

                      // A nextColumn section with identical column geometry
                      // continues the SAME column region (generators encode
                      // each column's content as its own section); merging
                      // lets the explicit column-advance place it correctly
                      // instead of re-balancing each fragment.
                      if (
                        currentGroup &&
                        parseSectionStartType(
                          documentSections[currentSectionIndex]
                            ?.sectionPropertiesXml
                        ) === "nextcolumn"
                      ) {
                        const previousColumns =
                          sectionColumnsBySectionIndex[
                            currentGroup.sectionIndex
                          ];
                        const nextColumns =
                          sectionColumnsBySectionIndex[currentSectionIndex];
                        const sameGeometry =
                          previousColumns &&
                          nextColumns &&
                          previousColumns.count === nextColumns.count &&
                          Math.abs(
                            previousColumns.gapPx - nextColumns.gapPx
                          ) <= 2 &&
                          JSON.stringify(previousColumns.widthsPx ?? null) ===
                            JSON.stringify(nextColumns.widthsPx ?? null);
                        if (sameGeometry) {
                          currentGroup.segments.push(segment);
                          return;
                        }
                      }

                      sectionGroups.push({
                        sectionIndex: currentSectionIndex,
                        segments: [segment],
                      });
                    });

                    return sectionGroups.map((group, groupIndex) => {
                      const sectionColumns =
                        sectionColumnsBySectionIndex[group.sectionIndex];
                      // A column group that shares the page with later groups
                      // sizes to its balanced content (Word's continuous
                      // column sections), not a full page of height.
                      const isLastGroupOnPage =
                        groupIndex === sectionGroups.length - 1;
                      const overlaySegments = group.segments.filter(
                        (segment) => {
                          const segmentNode =
                            editor.model.nodes[segment.nodeIndex];
                          return (
                            segmentNode?.type === "paragraph" &&
                            paragraphActsAsDecorativeBehindTextBackgroundOverlay(
                              segmentNode
                            )
                          );
                        }
                      );
                      const flowSegments =
                        overlaySegments.length === 0
                          ? group.segments
                          : group.segments.filter(
                              (segment) =>
                                !overlaySegments.some(
                                  (overlaySegment) =>
                                    overlaySegment.nodeIndex ===
                                      segment.nodeIndex &&
                                    overlaySegment.tableRowRange ===
                                      segment.tableRowRange &&
                                    overlaySegment.paragraphLineRange ===
                                      segment.paragraphLineRange
                                )
                            );
                      const segmentIdentityMatches = (
                        left: DocumentPageNodeSegment,
                        right: DocumentPageNodeSegment
                      ): boolean =>
                        left.nodeIndex === right.nodeIndex &&
                        left.tableRowRange?.startRowIndex ===
                          right.tableRowRange?.startRowIndex &&
                        left.tableRowRange?.endRowIndex ===
                          right.tableRowRange?.endRowIndex &&
                        left.tableRowSlice?.rowIndex ===
                          right.tableRowSlice?.rowIndex &&
                        Math.round(left.tableRowSlice?.startOffsetPx ?? 0) ===
                          Math.round(right.tableRowSlice?.startOffsetPx ?? 0) &&
                        Math.round(left.tableRowSlice?.sliceHeightPx ?? 0) ===
                          Math.round(right.tableRowSlice?.sliceHeightPx ?? 0) &&
                        left.paragraphLineRange?.startLineIndex ===
                          right.paragraphLineRange?.startLineIndex &&
                        left.paragraphLineRange?.endLineIndex ===
                          right.paragraphLineRange?.endLineIndex;
                      const segmentIdentityKey = (
                        segment: DocumentPageNodeSegment
                      ): string =>
                        `${segment.nodeIndex}::${
                          segment.tableRowRange
                            ? `rows-${segment.tableRowRange.startRowIndex}-${
                                segment.tableRowRange.endRowIndex
                              }${
                                segment.tableRowSlice
                                  ? `-slice-${Math.max(
                                      0,
                                      Math.round(
                                        segment.tableRowSlice.startOffsetPx
                                      )
                                    )}-${Math.max(
                                      MIN_PARAGRAPH_LINE_HEIGHT_PX,
                                      Math.round(
                                        segment.tableRowSlice.sliceHeightPx
                                      )
                                    )}`
                                  : ""
                              }`
                            : segment.paragraphLineRange
                            ? `lines-${segment.paragraphLineRange.startLineIndex}-${segment.paragraphLineRange.endLineIndex}`
                            : "all"
                        }`;
                      const requiresPlainFlowRender = flowSegments.some(
                        (segment) => {
                          const segmentNode =
                            editor.model.nodes[segment.nodeIndex];
                          if (
                            !segmentNode ||
                            segmentNode.type !== "paragraph"
                          ) {
                            return false;
                          }
                          return paragraphHasAbsoluteFloatingImage(segmentNode);
                        }
                      );
                      const explicitColumnWidthsPx =
                        sectionColumns &&
                        sectionColumns.widthsPx?.length === sectionColumns.count
                          ? sectionColumns.widthsPx
                          : undefined;
                      const flowSegmentsHaveExplicitColumnBreak =
                        flowSegments.some((segment) => {
                          if (segment.tableRowRange) {
                            return false;
                          }

                          const segmentNode =
                            editor.model.nodes[segment.nodeIndex];
                          return (
                            segmentNode?.type === "paragraph" &&
                            paragraphHasExplicitColumnBreak(segmentNode)
                          );
                        });
                      const canUseColumnLineSplitRender =
                        flowSegments.length > 0 &&
                        !flowSegmentsHaveExplicitColumnBreak;
                      const renderSegmentInColumn = (
                        columnSegment: DocumentPageNodeSegment,
                        columnContentWidthPx?: number
                      ): React.ReactNode => {
                        const columnNode =
                          editor.model.nodes[columnSegment.nodeIndex];
                        if (!columnNode) {
                          return null;
                        }

                        const explicitColumnBreakParagraphSegments =
                          columnNode.type === "paragraph" &&
                          !columnSegment.tableRowRange &&
                          !columnSegment.tableRowSlice &&
                          !columnSegment.paragraphLineRange &&
                          !explicitColumnWidthsPx &&
                          (sectionColumns?.count ?? 0) > 1
                            ? splitParagraphAtExplicitColumnBreaks(columnNode)
                            : undefined;
                        if (
                          explicitColumnBreakParagraphSegments &&
                          explicitColumnBreakParagraphSegments.length > 1
                        ) {
                          return (
                            <React.Fragment
                              key={`column-layout-node-${pageIndex}-${columnSegment.nodeIndex}-column-break-group`}
                            >
                              {explicitColumnBreakParagraphSegments.map(
                                (segmentParagraph, segmentIndex) => {
                                  const isLastSegment =
                                    segmentIndex >=
                                    explicitColumnBreakParagraphSegments.length -
                                      1;
                                  return (
                                    <div
                                      key={`column-layout-node-${pageIndex}-${columnSegment.nodeIndex}-column-break-${segmentIndex}`}
                                      style={
                                        segmentIndex > 0
                                          ? { breakBefore: "column" }
                                          : undefined
                                      }
                                    >
                                      {renderDocumentNode(
                                        segmentParagraph,
                                        columnSegment.nodeIndex,
                                        undefined,
                                        undefined,
                                        undefined,
                                        {
                                          pageLayout,
                                          contentWidthPxOverride:
                                            columnContentWidthPx,
                                          suppressLikelyFullPageCoverImageKeys:
                                            suppressedLikelyFullPageCoverImageKeys,
                                          forceReadOnly: true,
                                          suppressParagraphElementTracking:
                                            segmentIndex > 0,
                                          syntheticKeySuffix: `column-break-${segmentIndex}`,
                                          overrideBeforeSpacingPx:
                                            segmentIndex === 0 ? undefined : 0,
                                          overrideAfterSpacingPx: isLastSegment
                                            ? undefined
                                            : 0,
                                          normalizeParagraphHostFontSize: true,
                                        }
                                      )}
                                    </div>
                                  );
                                }
                              )}
                            </React.Fragment>
                          );
                        }

                        const renderedNode = renderDocumentNode(
                          columnNode,
                          columnSegment.nodeIndex,
                          columnSegment.tableRowRange,
                          columnSegment.tableRowSlice,
                          columnSegment.paragraphLineRange,
                          {
                            pageLayout,
                            contentWidthPxOverride: columnContentWidthPx,
                            suppressLikelyFullPageCoverImageKeys:
                              suppressedLikelyFullPageCoverImageKeys,
                            normalizeParagraphHostFontSize: true,
                          }
                        );
                        const segmentKeySuffix = columnSegment.tableRowRange
                          ? `rows-${
                              columnSegment.tableRowRange.startRowIndex
                            }-${columnSegment.tableRowRange.endRowIndex}${
                              columnSegment.tableRowSlice
                                ? `-slice-${Math.max(
                                    0,
                                    Math.round(
                                      columnSegment.tableRowSlice.startOffsetPx
                                    )
                                  )}-${Math.max(
                                    MIN_PARAGRAPH_LINE_HEIGHT_PX,
                                    Math.round(
                                      columnSegment.tableRowSlice.sliceHeightPx
                                    )
                                  )}`
                                : ""
                            }`
                          : columnSegment.paragraphLineRange
                          ? `lines-${columnSegment.paragraphLineRange.startLineIndex}-${columnSegment.paragraphLineRange.endLineIndex}`
                          : "all";
                        return (
                          <div
                            key={`column-layout-node-${pageIndex}-${columnSegment.nodeIndex}-${segmentKeySuffix}`}
                            style={
                              columnNode.type === "table"
                                ? { breakInside: "avoid" }
                                : undefined
                            }
                          >
                            {renderedNode}
                          </div>
                        );
                      };
                      if (
                        !sectionColumns ||
                        sectionColumns.count <= 1 ||
                        requiresPlainFlowRender
                      ) {
                        return (
                          <React.Fragment
                            key={`page-${pageIndex}-section-group-${groupIndex}`}
                          >
                            {renderNodeSegments(group.segments, {
                              pageLayout,
                              suppressLikelyFullPageCoverImageKeys:
                                suppressedLikelyFullPageCoverImageKeys,
                            })}
                          </React.Fragment>
                        );
                      }

                      return (
                        <div
                          key={`page-${pageIndex}-section-group-${groupIndex}`}
                          style={{ position: "relative" }}
                        >
                          {canUseColumnLineSplitRender
                            ? (() => {
                                const columnWidthsPx =
                                  explicitColumnWidthsPx &&
                                  explicitColumnWidthsPx.length ===
                                    sectionColumns.count
                                    ? explicitColumnWidthsPx
                                    : (() => {
                                        const inferredColumnWidthPx = Math.max(
                                          120,
                                          Math.round(
                                            (pageContentWidthPx -
                                              sectionColumns.gapPx *
                                                Math.max(
                                                  0,
                                                  sectionColumns.count - 1
                                                )) /
                                              Math.max(1, sectionColumns.count)
                                          )
                                        );
                                        return Array.from(
                                          { length: sectionColumns.count },
                                          () => inferredColumnWidthPx
                                        );
                                      })();
                                const columnSegments =
                                  buildRenderColumnSegmentsForPageSection(
                                    editor.model,
                                    flowSegments,
                                    columnWidthsPx,
                                    pageBodyAvailableHeightPx,
                                    editor.model.metadata.numberingDefinitions,
                                    docGridLinePitchPxByNodeIndex,
                                    measuredParagraphOuterHeightsPxByNodeIndex,
                                    isLastPage || !isLastGroupOnPage,
                                    new Set(
                                      (editor.model.metadata.sections ?? [])
                                        .filter(
                                          (candidate) =>
                                            parseSectionStartType(
                                              candidate.sectionPropertiesXml
                                            ) === "nextcolumn"
                                        )
                                        .map((candidate) =>
                                          Math.max(
                                            0,
                                            Math.round(candidate.startNodeIndex)
                                          )
                                        )
                                    )
                                  );
                                return (
                                  <>
                                    {overlaySegments.map((overlaySegment) => {
                                      const overlayNode =
                                        editor.model.nodes[
                                          overlaySegment.nodeIndex
                                        ];
                                      if (!overlayNode) {
                                        return null;
                                      }

                                      return (
                                        <React.Fragment
                                          key={`column-layout-overlay-${pageIndex}-${
                                            overlaySegment.nodeIndex
                                          }-${segmentIdentityKey(
                                            overlaySegment
                                          )}`}
                                        >
                                          {renderDocumentNode(
                                            overlayNode,
                                            overlaySegment.nodeIndex,
                                            overlaySegment.tableRowRange,
                                            overlaySegment.tableRowSlice,
                                            overlaySegment.paragraphLineRange,
                                            {
                                              pageLayout,
                                              suppressLikelyFullPageCoverImageKeys:
                                                suppressedLikelyFullPageCoverImageKeys,
                                            }
                                          )}
                                        </React.Fragment>
                                      );
                                    })}
                                    <div
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns: columnWidthsPx
                                          .map((widthPx) => `${widthPx}px`)
                                          .join(" "),
                                        columnGap: sectionColumns.gapPx,
                                        alignItems: "start",
                                        ...(isLastGroupOnPage
                                          ? {
                                              minHeight:
                                                pageBodyAvailableHeightPx,
                                            }
                                          : undefined),
                                        position: "relative",
                                        zIndex: 1,
                                      }}
                                    >
                                      {columnSegments.map(
                                        (segmentsForColumn, columnIndex) => (
                                          <div
                                            key={`column-layout-flow-${pageIndex}-${groupIndex}-${columnIndex}`}
                                            style={
                                              isLastGroupOnPage
                                                ? {
                                                    minHeight:
                                                      pageBodyAvailableHeightPx,
                                                  }
                                                : undefined
                                            }
                                          >
                                            {segmentsForColumn.map((segment) =>
                                              renderSegmentInColumn(
                                                segment,
                                                columnWidthsPx[columnIndex]
                                              )
                                            )}
                                          </div>
                                        )
                                      )}
                                    </div>
                                  </>
                                );
                              })()
                            : explicitColumnWidthsPx &&
                              explicitColumnWidthsPx.length === 2 &&
                              flowSegments.length > 0
                            ? (() => {
                                const [leftColumnWidthPx, rightColumnWidthPx] =
                                  explicitColumnWidthsPx;
                                const prefixHeightsPx = new Array(
                                  flowSegments.length + 1
                                ).fill(0);
                                const suffixHeightsPx = new Array(
                                  flowSegments.length + 1
                                ).fill(0);
                                for (
                                  let flowIndex = 0;
                                  flowIndex < flowSegments.length;
                                  flowIndex += 1
                                ) {
                                  const segment = flowSegments[flowIndex];
                                  const segmentNode =
                                    editor.model.nodes[segment.nodeIndex];
                                  prefixHeightsPx[flowIndex + 1] =
                                    prefixHeightsPx[flowIndex] +
                                    (segmentNode
                                      ? estimateRenderedPageSegmentHeightPx(
                                          segmentNode,
                                          segment,
                                          editor.model,
                                          leftColumnWidthPx,
                                          editor.model.metadata
                                            .numberingDefinitions,
                                          docGridLinePitchPxByNodeIndex.get(
                                            segment.nodeIndex
                                          )
                                        )
                                      : 0);
                                }
                                for (
                                  let flowIndex = flowSegments.length - 1;
                                  flowIndex >= 0;
                                  flowIndex -= 1
                                ) {
                                  const segment = flowSegments[flowIndex];
                                  const segmentNode =
                                    editor.model.nodes[segment.nodeIndex];
                                  suffixHeightsPx[flowIndex] =
                                    suffixHeightsPx[flowIndex + 1] +
                                    (segmentNode
                                      ? estimateRenderedPageSegmentHeightPx(
                                          segmentNode,
                                          segment,
                                          editor.model,
                                          rightColumnWidthPx,
                                          editor.model.metadata
                                            .numberingDefinitions,
                                          docGridLinePitchPxByNodeIndex.get(
                                            segment.nodeIndex
                                          )
                                        )
                                      : 0);
                                }
                                const explicitColumnBreakFlowIndex =
                                  flowSegments.findIndex((segment) => {
                                    if (segment.tableRowRange) {
                                      return false;
                                    }
                                    const segmentNode =
                                      editor.model.nodes[segment.nodeIndex];
                                    return (
                                      segmentNode?.type === "paragraph" &&
                                      paragraphHasExplicitColumnBreak(
                                        segmentNode
                                      )
                                    );
                                  });
                                let bestBreakIndex =
                                  explicitColumnBreakFlowIndex >= 0
                                    ? explicitColumnBreakFlowIndex + 1
                                    : flowSegments.length;
                                if (explicitColumnBreakFlowIndex < 0) {
                                  let bestBreakScore = Number.POSITIVE_INFINITY;
                                  for (
                                    let breakIndex = 0;
                                    breakIndex <= flowSegments.length;
                                    breakIndex += 1
                                  ) {
                                    const leftHeightPx =
                                      prefixHeightsPx[breakIndex];
                                    const rightHeightPx =
                                      suffixHeightsPx[breakIndex];
                                    const score = Math.max(
                                      leftHeightPx,
                                      rightHeightPx
                                    );
                                    if (score < bestBreakScore) {
                                      bestBreakScore = score;
                                      bestBreakIndex = breakIndex;
                                    }
                                  }
                                }
                                const leftSegments = flowSegments.slice(
                                  0,
                                  bestBreakIndex
                                );
                                const rightSegments =
                                  flowSegments.slice(bestBreakIndex);
                                const leftSegmentKeys = new Set(
                                  leftSegments.map(segmentIdentityKey)
                                );
                                const rightSegmentKeys = new Set(
                                  rightSegments.map(segmentIdentityKey)
                                );
                                const leftPrefixHeightBySegmentKey = new Map<
                                  string,
                                  number
                                >();
                                let runningLeftHeightPx = 0;
                                leftSegments.forEach((segment) => {
                                  leftPrefixHeightBySegmentKey.set(
                                    segmentIdentityKey(segment),
                                    runningLeftHeightPx
                                  );
                                  const segmentNode =
                                    editor.model.nodes[segment.nodeIndex];
                                  runningLeftHeightPx += segmentNode
                                    ? estimateRenderedPageSegmentHeightPx(
                                        segmentNode,
                                        segment,
                                        editor.model,
                                        leftColumnWidthPx,
                                        editor.model.metadata
                                          .numberingDefinitions,
                                        docGridLinePitchPxByNodeIndex.get(
                                          segment.nodeIndex
                                        )
                                      )
                                    : 0;
                                });
                                const rightPrefixHeightBySegmentKey = new Map<
                                  string,
                                  number
                                >();
                                let runningRightHeightPx = 0;
                                rightSegments.forEach((segment) => {
                                  rightPrefixHeightBySegmentKey.set(
                                    segmentIdentityKey(segment),
                                    runningRightHeightPx
                                  );
                                  const segmentNode =
                                    editor.model.nodes[segment.nodeIndex];
                                  runningRightHeightPx += segmentNode
                                    ? estimateRenderedPageSegmentHeightPx(
                                        segmentNode,
                                        segment,
                                        editor.model,
                                        rightColumnWidthPx,
                                        editor.model.metadata
                                          .numberingDefinitions,
                                        docGridLinePitchPxByNodeIndex.get(
                                          segment.nodeIndex
                                        )
                                      )
                                    : 0;
                                });
                                const overlayColumnBySegmentKey = new Map<
                                  string,
                                  "left" | "right"
                                >();
                                overlaySegments.forEach((overlaySegment) => {
                                  const overlayNode =
                                    editor.model.nodes[
                                      overlaySegment.nodeIndex
                                    ];
                                  if (
                                    !overlayNode ||
                                    overlayNode.type !== "paragraph"
                                  ) {
                                    return;
                                  }
                                  let precedingFlowCount = 0;
                                  for (const groupedSegment of group.segments) {
                                    if (
                                      segmentIdentityMatches(
                                        groupedSegment,
                                        overlaySegment
                                      )
                                    ) {
                                      break;
                                    }
                                    if (
                                      flowSegments.some((flowSegment) =>
                                        segmentIdentityMatches(
                                          flowSegment,
                                          groupedSegment
                                        )
                                      )
                                    ) {
                                      precedingFlowCount += 1;
                                    }
                                  }
                                  const primaryOverlayImage =
                                    overlayNode.children.find(
                                      (
                                        child: ParagraphNode["children"][number]
                                      ): child is ImageRunNode =>
                                        child.type === "image" &&
                                        Boolean(child.floating)
                                    );
                                  const targetColumn = (() => {
                                    if (
                                      primaryOverlayImage?.floating
                                        ?.horizontalRelativeTo === "column" &&
                                      Number.isFinite(
                                        primaryOverlayImage.widthPx
                                      )
                                    ) {
                                      const imageWidthPx =
                                        primaryOverlayImage.widthPx ?? 0;
                                      const imageOffsetPx =
                                        primaryOverlayImage.floating?.xPx ?? 0;
                                      const leftPlacedLeftPx = imageOffsetPx;
                                      const rightPlacedLeftPx =
                                        leftColumnWidthPx +
                                        sectionColumns.gapPx +
                                        imageOffsetPx;
                                      const leftPlacedCenterPx =
                                        leftPlacedLeftPx + imageWidthPx / 2;
                                      const rightPlacedCenterPx =
                                        rightPlacedLeftPx + imageWidthPx / 2;
                                      const leftColumnCenterPx =
                                        leftColumnWidthPx / 2;
                                      const rightColumnCenterPx =
                                        leftColumnWidthPx +
                                        sectionColumns.gapPx +
                                        rightColumnWidthPx / 2;
                                      const totalColumnWidthPx =
                                        leftColumnWidthPx +
                                        sectionColumns.gapPx +
                                        rightColumnWidthPx;
                                      const leftOverflowPenaltyPx =
                                        Math.max(0, -leftPlacedLeftPx) +
                                        Math.max(
                                          0,
                                          leftPlacedLeftPx +
                                            imageWidthPx -
                                            totalColumnWidthPx
                                        );
                                      const rightOverflowPenaltyPx =
                                        Math.max(0, -rightPlacedLeftPx) +
                                        Math.max(
                                          0,
                                          rightPlacedLeftPx +
                                            imageWidthPx -
                                            totalColumnWidthPx
                                        );
                                      const leftScore =
                                        Math.abs(
                                          leftPlacedCenterPx -
                                            leftColumnCenterPx
                                        ) +
                                        leftOverflowPenaltyPx * 4;
                                      const rightScore =
                                        Math.abs(
                                          rightPlacedCenterPx -
                                            rightColumnCenterPx
                                        ) +
                                        rightOverflowPenaltyPx * 4;
                                      if (leftScore !== rightScore) {
                                        return leftScore < rightScore
                                          ? "left"
                                          : "right";
                                      }
                                    }
                                    return precedingFlowCount >= bestBreakIndex
                                      ? "right"
                                      : "left";
                                  })();
                                  overlayColumnBySegmentKey.set(
                                    segmentIdentityKey(overlaySegment),
                                    targetColumn
                                  );
                                });
                                const leftRenderedSegments: DocumentPageNodeSegment[] =
                                  [];
                                const rightRenderedSegments: DocumentPageNodeSegment[] =
                                  [];
                                group.segments.forEach((groupedSegment) => {
                                  const groupedSegmentKey =
                                    segmentIdentityKey(groupedSegment);
                                  const overlayColumn =
                                    overlayColumnBySegmentKey.get(
                                      groupedSegmentKey
                                    );
                                  if (overlayColumn === "left") {
                                    leftRenderedSegments.push(groupedSegment);
                                    return;
                                  }
                                  if (overlayColumn === "right") {
                                    rightRenderedSegments.push(groupedSegment);
                                    return;
                                  }
                                  if (leftSegmentKeys.has(groupedSegmentKey)) {
                                    leftRenderedSegments.push(groupedSegment);
                                    return;
                                  }
                                  if (rightSegmentKeys.has(groupedSegmentKey)) {
                                    rightRenderedSegments.push(groupedSegment);
                                  }
                                });
                                return (
                                  <div
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: `${leftColumnWidthPx}px ${rightColumnWidthPx}px`,
                                      columnGap: sectionColumns.gapPx,
                                      alignItems: "stretch",
                                      position: "relative",
                                      zIndex: 1,
                                    }}
                                  >
                                    <div
                                      style={{
                                        height: "100%",
                                      }}
                                    >
                                      {leftRenderedSegments.map((segment) =>
                                        renderSegmentInColumn(
                                          segment,
                                          leftColumnWidthPx
                                        )
                                      )}
                                    </div>
                                    <div
                                      style={{
                                        height: "100%",
                                      }}
                                    >
                                      {rightRenderedSegments.map((segment) =>
                                        renderSegmentInColumn(
                                          segment,
                                          rightColumnWidthPx
                                        )
                                      )}
                                    </div>
                                  </div>
                                );
                              })()
                            : (() => {
                                const inferredColumnWidthPx = Math.max(
                                  120,
                                  Math.round(
                                    (pageContentWidthPx -
                                      sectionColumns.gapPx *
                                        Math.max(0, sectionColumns.count - 1)) /
                                      Math.max(1, sectionColumns.count)
                                  )
                                );
                                return (
                                  <div
                                    style={{
                                      columnCount: sectionColumns.count,
                                      columnGap: sectionColumns.gapPx,
                                      columnFill: "balance",
                                      position: "relative",
                                      zIndex: 1,
                                    }}
                                  >
                                    {overlaySegments.map((overlaySegment) => {
                                      const overlayNode =
                                        editor.model.nodes[
                                          overlaySegment.nodeIndex
                                        ];
                                      if (!overlayNode) {
                                        return null;
                                      }
                                      const segmentKeySuffix =
                                        overlaySegment.tableRowRange
                                          ? `rows-${
                                              overlaySegment.tableRowRange
                                                .startRowIndex
                                            }-${
                                              overlaySegment.tableRowRange
                                                .endRowIndex
                                            }${
                                              overlaySegment.tableRowSlice
                                                ? `-slice-${Math.max(
                                                    0,
                                                    Math.round(
                                                      overlaySegment
                                                        .tableRowSlice
                                                        .startOffsetPx
                                                    )
                                                  )}-${Math.max(
                                                    MIN_PARAGRAPH_LINE_HEIGHT_PX,
                                                    Math.round(
                                                      overlaySegment
                                                        .tableRowSlice
                                                        .sliceHeightPx
                                                    )
                                                  )}`
                                                : ""
                                            }`
                                          : overlaySegment.paragraphLineRange
                                          ? `lines-${overlaySegment.paragraphLineRange.startLineIndex}-${overlaySegment.paragraphLineRange.endLineIndex}`
                                          : "all";
                                      return (
                                        <React.Fragment
                                          key={`column-layout-overlay-${pageIndex}-${overlaySegment.nodeIndex}-${segmentKeySuffix}`}
                                        >
                                          {renderDocumentNode(
                                            overlayNode,
                                            overlaySegment.nodeIndex,
                                            overlaySegment.tableRowRange,
                                            overlaySegment.tableRowSlice,
                                            overlaySegment.paragraphLineRange,
                                            {
                                              pageLayout,
                                              suppressLikelyFullPageCoverImageKeys:
                                                suppressedLikelyFullPageCoverImageKeys,
                                            }
                                          )}
                                        </React.Fragment>
                                      );
                                    })}
                                    {flowSegments.map((segment) =>
                                      renderSegmentInColumn(
                                        segment,
                                        inferredColumnWidthPx
                                      )
                                    )}
                                  </div>
                                );
                              })()}
                        </div>
                      );
                    });
                  })()}
                </div>

                {isLastPage && referencedEndnotes.length > 0 ? (
                  <section
                    data-docx-endnotes-section="true"
                    style={{
                      marginTop: 8,
                      paddingTop: 8,
                      borderTop: `1px solid ${
                        documentContentTheme === "dark" ? "#4b5563" : "#9ca3af"
                      }`,
                      display: "grid",
                      gap: 6,
                      fontSize: 12,
                    }}
                  >
                    {referencedEndnotes.map((note, index) => (
                      <p
                        key={`last-page-endnote-${note.id}-${index}`}
                        id={`docx-endnote-${note.id}`}
                        style={{
                          margin: 0,
                          whiteSpace: "pre-wrap",
                          lineHeight: 1.35,
                        }}
                      >
                        {renderDocumentNote(
                          note,
                          "endnote",
                          `last-page-endnote-${note.id}-${index}`,
                          pageNumber,
                          pageHeaderFooterRunRenderOptions
                        )}
                      </p>
                    ))}
                  </section>
                ) : null}

                {pageBottomFootnotes.length > 0 ? (
                  <section
                    data-docx-footnotes-section="true"
                    style={{
                      marginTop: "auto",
                      paddingTop: 8,
                      borderTop: `1px solid ${
                        documentContentTheme === "dark" ? "#4b5563" : "#9ca3af"
                      }`,
                      display: "grid",
                      gap: 6,
                      fontSize: 12,
                    }}
                  >
                    {pageBottomFootnotes.map((note, index) => (
                      <p
                        key={`page-footnote-${pageIndex}-${note.id}-${index}`}
                        id={`docx-footnote-${note.id}`}
                        style={{
                          margin: 0,
                          whiteSpace: "pre-wrap",
                          lineHeight: 1.35,
                        }}
                      >
                        {renderDocumentNote(
                          note,
                          "footnote",
                          `page-footnote-${pageIndex}-${note.id}-${index}`,
                          pageNumber,
                          pageHeaderFooterRunRenderOptions
                        )}
                      </p>
                    ))}
                  </section>
                ) : null}

                {isLastPage ? (
                  <div
                    data-docx-pagination-ignore="true"
                    style={{
                      minHeight: 28,
                      marginTop: 10,
                      cursor: "text",
                    }}
                    onClick={() => {
                      const nextParagraphIndex = editor.appendParagraph("");
                      focusParagraphAtEnd(nextParagraphIndex);
                    }}
                  />
                ) : null}
              </div>

              {pageSections.footerNodes.length > 0 ? (
                <>
                  {footerNeedsFullPageOverlay &&
                  !pageHeaderFooterEditActive &&
                  !isReadOnly &&
                  pageSections.footerPartName ? (
                    <div
                      data-docx-footer-activation-zone="true"
                      style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: inactiveFooterActivationZoneHeightPx,
                        zIndex: 2,
                        pointerEvents: "auto",
                        cursor: "text",
                      }}
                      onDoubleClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        activateHeaderFooterEdit(
                          "footer",
                          pageSections.footerPartName,
                          pageIndex
                        );
                      }}
                    />
                  ) : null}
                  <div
                    ref={(element) => {
                      if (element) {
                        pageFooterElementsRef.current.set(pageIndex, element);
                      } else {
                        pageFooterElementsRef.current.delete(pageIndex);
                      }
                    }}
                    style={{
                      display: "grid",
                      gap: 8,
                      position: "absolute",
                      left: 0,
                      right: 0,
                      ...(footerNeedsFullPageOverlay
                        ? {
                            top: 0,
                            bottom: 0,
                            alignContent: "end",
                          }
                        : {
                            bottom: footerBottomOffsetPx,
                          }),
                      width: "100%",
                      maxWidth: "100%",
                      boxSizing: "border-box",
                      paddingLeft: footerNeedsPageWideLayout
                        ? 0
                        : pageLayout.marginsPx.left,
                      paddingRight: footerNeedsPageWideLayout
                        ? 0
                        : pageLayout.marginsPx.right,
                      paddingBottom: footerNeedsFullPageOverlay
                        ? footerBottomOffsetPx
                        : 0,
                      pointerEvents:
                        isReadOnly ||
                        pageHeaderFooterEditActive ||
                        !footerNeedsFullPageOverlay
                          ? "auto"
                          : "none",
                      opacity:
                        pageHeaderFooterEditActive || isReadOnly
                          ? 1
                          : HEADER_FOOTER_INACTIVE_OPACITY,
                      transition: "opacity 120ms ease",
                      outline: "none",
                      boxShadow: "none",
                      zIndex: 1,
                      ...(documentContentFilter
                        ? { filter: documentContentFilter }
                        : undefined),
                    }}
                    contentEditable={pageHeaderFooterEditActive && !isReadOnly}
                    suppressContentEditableWarning
                    data-docx-header-footer-region="footer"
                    onFocus={(event) => {
                      event.currentTarget.style.outline = "none";
                      event.currentTarget.style.boxShadow = "none";
                    }}
                    onInputCapture={(event) => {
                      if (
                        !pageHeaderFooterEditActive ||
                        !pageSections.footerPartName
                      ) {
                        return;
                      }
                      schedulePaginationMeasurementResume();
                      updateSectionParagraphDraftFromTarget(event.target);
                    }}
                    onBlurCapture={(event) => {
                      if (
                        !pageHeaderFooterEditActive ||
                        !pageSections.footerPartName
                      ) {
                        return;
                      }
                      const relatedTarget = event.relatedTarget as Node | null;
                      if (
                        relatedTarget &&
                        event.currentTarget.contains(relatedTarget)
                      ) {
                        return;
                      }
                      commitSectionRegionDraftsFromContainer(
                        event.currentTarget,
                        "footer",
                        pageSections.footerPartName
                      );
                    }}
                    onDoubleClick={(event) => {
                      if (isReadOnly || !pageSections.footerPartName) {
                        return;
                      }
                      event.preventDefault();
                      event.stopPropagation();

                      const target = event.target;
                      const paragraphHost =
                        target instanceof Element
                          ? target.closest<HTMLElement>(
                              "[data-docx-section-paragraph-key]"
                            )
                          : null;
                      const draftKey = paragraphHost?.getAttribute(
                        "data-docx-section-paragraph-key"
                      );
                      activateHeaderFooterEdit(
                        "footer",
                        pageSections.footerPartName,
                        pageIndex,
                        {
                          draftKey: draftKey ?? undefined,
                          point: {
                            x: event.clientX,
                            y: event.clientY,
                          },
                        }
                      );
                    }}
                  >
                    {pageSections.footerNodes.map((node, index) =>
                      renderHeaderNode(
                        node,
                        `footer-${pageIndex}-${index}`,
                        documentContentTheme,
                        editor.model.metadata.numberingDefinitions,
                        headingStyles,
                        footerNeedsPageWideLayout
                          ? pageLayout.pageWidthPx
                          : pageContentWidthPx,
                        scrollToBookmark,
                        {
                          left: footerHostPageOriginLeftPx,
                          top: footerHostPageOriginTopPx,
                          marginLeft: footerHostMarginOriginLeftPx,
                          marginTop: footerHostMarginOriginTopPx,
                          columnLeft: footerNeedsPageWideLayout
                            ? pageLayout.marginsPx.left
                            : 0,
                          columnTop: 0,
                          pageWidth: pageLayout.pageWidthPx,
                        },
                        {
                          footnote: footnoteDisplayIndexById,
                          endnote: endnoteDisplayIndexById,
                        },
                        pageNumber,
                        totalPagesForFieldResolution,
                        {
                          ...pageHeaderFooterRunRenderOptions,
                          headerFooterRegion: "footer",
                        },
                        footerEditScope,
                        footerImageInteraction,
                        undefined,
                        index
                      )
                    )}
                  </div>
                </>
              ) : null}
            </div>

            {showTrackedChangeGutter ? (
              <div
                style={{
                  position: "absolute",
                  left: pageLayout.pageWidthPx,
                  top: 0,
                  width: TRACKED_CHANGE_GUTTER_WIDTH_PX,
                  height: pageVisualHeightPx,
                  backgroundColor: resolvedPageSurfaceBackgroundColor,
                  pointerEvents: "none",
                  overflow: "visible",
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: 1,
                    backgroundColor:
                      editor.documentTheme === "dark" ? "#374151" : "#d1d5db",
                  }}
                />
                <svg
                  width={
                    pageLayout.pageWidthPx + TRACKED_CHANGE_GUTTER_WIDTH_PX
                  }
                  height={pageVisualHeightPx}
                  viewBox={`0 0 ${
                    pageLayout.pageWidthPx + TRACKED_CHANGE_GUTTER_WIDTH_PX
                  } ${pageVisualHeightPx}`}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: -pageLayout.pageWidthPx,
                    overflow: "visible",
                  }}
                >
                  {pageTrackedChanges.map((entry) => {
                    const trackedChange = entry.annotation.trackedChange;
                    const comment = entry.annotation.comment;
                    const accentColor = trackedChange
                      ? trackedChangeAccentColor(
                          trackedChange.kind,
                          editor.documentTheme
                        )
                      : commentAccentColor(
                          editor.documentTheme,
                          comment?.commentId
                        );
                    const anchorY = clampNumber(
                      Math.round(entry.anchorY),
                      8,
                      Math.max(8, pageLayout.pageHeightPx - 8)
                    );
                    const anchorX = clampNumber(
                      Math.round(entry.anchorX),
                      8,
                      Math.max(8, pageLayout.pageWidthPx - 8)
                    );
                    const cardAttachX =
                      pageLayout.pageWidthPx +
                      TRACKED_CHANGE_GUTTER_CARD_LEFT_PX;
                    const maxConnectorLaneOffset = Math.max(
                      TRACKED_CHANGE_GUTTER_BEND_OFFSET_PX,
                      TRACKED_CHANGE_GUTTER_CARD_LEFT_PX - 6
                    );
                    const connectorLaneOffset = Math.min(
                      TRACKED_CHANGE_GUTTER_BEND_OFFSET_PX +
                        Math.max(0, entry.connectorLane) *
                          TRACKED_CHANGE_GUTTER_CONNECTOR_LANE_GAP_PX,
                      maxConnectorLaneOffset
                    );
                    const gutterBendX =
                      pageLayout.pageWidthPx + connectorLaneOffset;
                    const cardCenterY = clampNumber(
                      Math.round(entry.top + entry.heightPx / 2),
                      8,
                      Math.max(8, pageVisualHeightPx - 8)
                    );
                    const connectorPath =
                      Math.abs(cardCenterY - anchorY) <= 2
                        ? `M ${anchorX} ${anchorY} H ${cardAttachX}`
                        : `M ${anchorX} ${anchorY} H ${gutterBendX} V ${cardCenterY} H ${cardAttachX}`;
                    const revisionBarX = clampNumber(
                      Math.round(pageLayout.marginsPx.left - 20),
                      8,
                      Math.max(8, pageLayout.pageWidthPx - 8)
                    );
                    return (
                      <g
                        key={`tracked-connector-${pageIndex}-${entry.annotation.id}`}
                      >
                        {trackedChange ? (
                          <line
                            x1={revisionBarX}
                            y1={Math.max(8, anchorY - 16)}
                            x2={revisionBarX}
                            y2={Math.min(
                              pageLayout.pageHeightPx - 8,
                              anchorY + 16
                            )}
                            stroke={
                              editor.documentTheme === "dark"
                                ? "#94a3b8"
                                : "#9ca3af"
                            }
                            strokeWidth={1.25}
                            strokeLinecap="square"
                          />
                        ) : null}
                        <path
                          d={connectorPath}
                          stroke={accentColor}
                          strokeWidth={1.15}
                          strokeOpacity={0.78}
                          strokeDasharray="2 3"
                          strokeLinejoin="round"
                          strokeLinecap="round"
                          fill="none"
                        />
                      </g>
                    );
                  })}
                </svg>
                {(!trackedChangesEnabled || !hasTrackedChanges) &&
                (!commentsEnabled || !hasComments) &&
                pageIndex === 0 ? (
                  <p
                    style={{
                      position: "absolute",
                      top: 8,
                      left: TRACKED_CHANGE_GUTTER_CARD_LEFT_PX,
                      right: TRACKED_CHANGE_GUTTER_CARD_RIGHT_PX,
                      margin: 0,
                      fontSize: 11,
                      lineHeight: 1.35,
                      color: "#94a3b8",
                    }}
                  >
                    {trackedChangesEnabled && commentsEnabled
                      ? "No edits or comments found"
                      : commentsEnabled
                      ? "No comments found"
                      : "No edits found"}
                  </p>
                ) : null}
                {pageTrackedChanges.map((entry) => {
                  const trackedChange = entry.annotation.trackedChange;
                  const comment = entry.annotation.comment;
                  const accentColor = trackedChange
                    ? trackedChangeAccentColor(
                        trackedChange.kind,
                        editor.documentTheme
                      )
                    : commentAccentColor(
                        editor.documentTheme,
                        comment?.commentId
                      );
                  const formattedDate = formatTrackedChangeDate(
                    trackedChange?.date ?? comment?.date
                  );
                  const kindLabel = trackedChange
                    ? trackedChangeKindLabel(trackedChange.kind)
                    : comment?.resolved
                    ? "Comment · Resolved"
                    : comment?.parentId !== undefined
                    ? "Reply"
                    : "Comment";
                  const snippet = trackedChange
                    ? normalizeTrackedChangeSnippet(trackedChange.text) ??
                      (trackedChange.kind === "format-change" ||
                      trackedChange.kind === "paragraph-format-change"
                        ? "Formatting"
                        : "Change")
                    : comment?.text || "Comment";
                  const cardWidthPx = Math.max(
                    140,
                    TRACKED_CHANGE_GUTTER_WIDTH_PX -
                      TRACKED_CHANGE_GUTTER_CARD_LEFT_PX -
                      TRACKED_CHANGE_GUTTER_CARD_RIGHT_PX
                  );
                  const cardContainerStyle: React.CSSProperties = {
                    position: "absolute",
                    top: entry.top,
                    left: TRACKED_CHANGE_GUTTER_CARD_LEFT_PX,
                    width: cardWidthPx,
                    minHeight: entry.heightPx,
                    pointerEvents: "none",
                  };
                  const cardStyle: React.CSSProperties = {
                    width: "100%",
                    minHeight: entry.heightPx,
                  };
                  const defaultCard = (
                    <div
                      style={{
                        ...cardStyle,
                        padding: "4px 6px",
                        boxSizing: "border-box",
                        borderLeft: `1.5px solid ${accentColor}`,
                        borderTop: `1px solid ${
                          editor.documentTheme === "dark"
                            ? "rgba(148, 163, 184, 0.18)"
                            : "rgba(15, 23, 42, 0.08)"
                        }`,
                        borderRight: `1px solid ${
                          editor.documentTheme === "dark"
                            ? "rgba(148, 163, 184, 0.18)"
                            : "rgba(15, 23, 42, 0.08)"
                        }`,
                        borderBottom: `1px solid ${
                          editor.documentTheme === "dark"
                            ? "rgba(148, 163, 184, 0.18)"
                            : "rgba(15, 23, 42, 0.08)"
                        }`,
                        backgroundColor:
                          editor.documentTheme === "dark"
                            ? "rgba(15, 23, 42, 0.92)"
                            : "rgba(255, 255, 255, 0.72)",
                        color:
                          editor.documentTheme === "dark"
                            ? "#f3f4f6"
                            : "#111827",
                        boxShadow:
                          editor.documentTheme === "dark"
                            ? "0 1px 2px rgba(2, 6, 23, 0.45)"
                            : "0 1px 1px rgba(15, 23, 42, 0.08)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "baseline",
                          justifyContent: "space-between",
                          gap: 8,
                        }}
                      >
                        <p
                          style={{
                            margin: 0,
                            fontSize: 10,
                            fontWeight: 700,
                            lineHeight: 1.2,
                          }}
                        >
                          {(trackedChange?.author ?? comment?.author)?.trim() ||
                            "Unknown author"}
                        </p>
                        {formattedDate ? (
                          <p
                            style={{
                              margin: 0,
                              fontSize: 9,
                              lineHeight: 1.15,
                              color:
                                editor.documentTheme === "dark"
                                  ? "#94a3b8"
                                  : "#6b7280",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formattedDate}
                          </p>
                        ) : null}
                      </div>
                      {comment?.anchorText ? (
                        <p
                          style={{
                            margin: "2px 0 0",
                            fontSize: 9,
                            lineHeight: 1.25,
                            fontStyle: "italic",
                            color:
                              editor.documentTheme === "dark"
                                ? "#94a3b8"
                                : "#6b7280",
                          }}
                        >
                          “{comment.anchorText}”
                        </p>
                      ) : null}
                      <p
                        style={{
                          margin: "2px 0 0",
                          fontSize: 10,
                          lineHeight: 1.25,
                        }}
                      >
                        <strong>{kindLabel}:</strong> {snippet}
                      </p>
                    </div>
                  );
                  const renderedCard = trackedChange
                    ? renderTrackedChangeCard
                      ? renderTrackedChangeCard({
                          change: trackedChange,
                          kindLabel,
                          snippet,
                          formattedDate,
                          accentColor,
                          documentTheme: editor.documentTheme,
                          pageIndex,
                          style: cardStyle,
                        })
                      : defaultCard
                    : comment && renderCommentCard
                    ? renderCommentCard({
                        comment,
                        snippet,
                        formattedDate,
                        accentColor,
                        documentTheme: editor.documentTheme,
                        pageIndex,
                        style: cardStyle,
                      })
                    : defaultCard;
                  return (
                    <div
                      key={`tracked-card-${pageIndex}-${entry.annotation.id}`}
                      data-docx-gutter-annotation={
                        trackedChange ? "tracked-change" : "comment"
                      }
                      data-docx-gutter-annotation-id={entry.annotation.id}
                      data-docx-gutter-anchor-x={Math.round(entry.anchorX)}
                      data-docx-gutter-anchor-y={Math.round(entry.anchorY)}
                      data-docx-gutter-connector-lane={entry.connectorLane}
                      ref={(element) => {
                        const elementKey = `${pageIndex}:${entry.annotation.id}`;
                        if (element) {
                          trackedChangeCardElementsRef.current.set(
                            elementKey,
                            element
                          );
                        } else {
                          trackedChangeCardElementsRef.current.delete(
                            elementKey
                          );
                        }
                      }}
                      style={cardContainerStyle}
                    >
                      {renderedCard}
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
      {pageStackVirtualSpacers.after.heightPx > 0 ? (
        <div
          aria-hidden="true"
          data-docx-page-window-spacer="after"
          style={{
            height: pageStackVirtualSpacers.after.heightPx,
            width: pageStackVirtualSpacers.after.widthPx,
            margin: "0 auto",
            pointerEvents: "none",
            visibility: hideDocumentUntilPaginationSettled
              ? "hidden"
              : undefined,
          }}
        />
      ) : null}
      {!isReadOnly
        ? (() => {
            const hasCustomContextMenuState = Boolean(contextMenuState);
            const hasTableContextMenuState = Boolean(tableContextMenuState);
            if (!hasCustomContextMenuState && !hasTableContextMenuState) {
              return null;
            }

            if (hasCustomContextMenuState || renderContextMenu) {
              const tableContext = tableContextMenuState
                ? ({
                    tableIndex: tableContextMenuState.tableIndex,
                    rowIndex: tableContextMenuState.rowIndex,
                    cellIndex: tableContextMenuState.cellIndex,
                  } satisfies DocxTableContextMenuContext)
                : undefined;
              const context = contextMenuState
                ? contextMenuState
                : ({
                    kind: "table",
                    tableContext,
                    location: tableContext
                      ? firstParagraphLocationInTable(
                          editor.model,
                          tableContext.tableIndex
                        )
                      : undefined,
                    activeTextRange: editor.activeTextRange,
                  } satisfies DocxContextMenuContext);
              const resolvedContext =
                context.kind === "image" && context.image
                  ? {
                      ...context,
                      image: {
                        ...context.image,
                        wrap: resolveDocxImageWrapState(context.image.floating),
                      },
                    }
                  : context;
              const menuState = contextMenuState ?? tableContextMenuState;
              if (!menuState) {
                return null;
              }

              const isImageContext = resolvedContext.kind === "image";
              const estimatedWidth = isImageContext ? 252 : 228;
              const estimatedHeight = isImageContext ? 460 : 260;
              const position = {
                x:
                  typeof window === "undefined"
                    ? menuState.clientX
                    : clampNumber(
                        menuState.clientX,
                        8,
                        Math.max(8, window.innerWidth - estimatedWidth)
                      ),
                y:
                  typeof window === "undefined"
                    ? menuState.clientY
                    : clampNumber(
                        menuState.clientY,
                        8,
                        Math.max(8, window.innerHeight - estimatedHeight)
                      ),
              };
              const actions = resolveContextMenuActions(resolvedContext);
              const menuProps: DocxContextMenuRenderProps = {
                context: resolvedContext,
                actions,
                runAction: (actionId) => {
                  runContextMenuAction(actionId, resolvedContext);
                },
                closeMenu: contextMenuState
                  ? closeContextMenu
                  : closeTableContextMenu,
                position,
                documentTheme: editor.documentTheme,
              };
              const menuTextColor =
                editor.documentTheme === "dark" ? "#f3f4f6" : "#111827";
              const menuMutedTextColor =
                editor.documentTheme === "dark" ? "#94a3b8" : "#6b7280";
              const defaultMenu = (
                <div
                  style={{
                    width: isImageContext ? 248 : 220,
                    display: "grid",
                    gap: 2,
                    padding: 6,
                    borderRadius: 8,
                    border: `1px solid ${
                      editor.documentTheme === "dark"
                        ? "rgba(148, 163, 184, 0.3)"
                        : "rgba(15, 23, 42, 0.14)"
                    }`,
                    backgroundColor:
                      editor.documentTheme === "dark"
                        ? "rgba(2, 6, 23, 0.98)"
                        : "rgba(255, 255, 255, 0.98)",
                    boxShadow:
                      editor.documentTheme === "dark"
                        ? "0 8px 20px rgba(2, 6, 23, 0.55)"
                        : "0 8px 20px rgba(15, 23, 42, 0.16)",
                  }}
                >
                  {actions.map((action, actionIndex) => {
                    const childActions = action.children ?? [];
                    const hasChildren = childActions.length > 0;
                    return (
                      <div
                        key={String(action.id)}
                        style={{ display: "grid", gap: 2 }}
                      >
                        {action.separatorBefore && actionIndex > 0 ? (
                          <div
                            style={{
                              height: 1,
                              margin: "4px 2px",
                              backgroundColor:
                                editor.documentTheme === "dark"
                                  ? "rgba(148, 163, 184, 0.24)"
                                  : "rgba(15, 23, 42, 0.08)",
                            }}
                          />
                        ) : null}
                        <button
                          type="button"
                          disabled={action.disabled || hasChildren}
                          onClick={() =>
                            runContextMenuAction(action.id, resolvedContext)
                          }
                          style={{
                            appearance: "none",
                            border: "none",
                            background: "transparent",
                            color: action.destructive
                              ? editor.documentTheme === "dark"
                                ? "#fda4af"
                                : "#b91c1c"
                              : menuTextColor,
                            opacity: action.disabled ? 0.5 : 1,
                            textAlign: "left",
                            fontSize: 13,
                            lineHeight: 1.25,
                            padding: "7px 10px",
                            borderRadius: 6,
                            cursor:
                              action.disabled || hasChildren
                                ? "default"
                                : "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 8,
                          }}
                        >
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                width: 12,
                                justifyContent: "center",
                              }}
                            >
                              {action.checked ? "✓" : ""}
                            </span>
                            <span>{action.label}</span>
                          </span>
                          {action.shortcut ? (
                            <span
                              style={{
                                fontSize: 11,
                                color: menuMutedTextColor,
                              }}
                            >
                              {action.shortcut}
                            </span>
                          ) : hasChildren ? (
                            <span
                              style={{
                                fontSize: 11,
                                color: menuMutedTextColor,
                              }}
                            >
                              Submenu
                            </span>
                          ) : null}
                        </button>
                        {hasChildren ? (
                          <div
                            style={{
                              marginLeft: 10,
                              display: "grid",
                              gap: 2,
                              paddingLeft: 8,
                              borderLeft: `1px solid ${
                                editor.documentTheme === "dark"
                                  ? "rgba(148, 163, 184, 0.24)"
                                  : "rgba(15, 23, 42, 0.12)"
                              }`,
                            }}
                          >
                            {childActions.map((childAction) => (
                              <button
                                key={String(childAction.id)}
                                type="button"
                                disabled={childAction.disabled}
                                onClick={() =>
                                  runContextMenuAction(
                                    childAction.id,
                                    resolvedContext
                                  )
                                }
                                style={{
                                  appearance: "none",
                                  border: "none",
                                  background: "transparent",
                                  color: childAction.destructive
                                    ? editor.documentTheme === "dark"
                                      ? "#fda4af"
                                      : "#b91c1c"
                                    : menuTextColor,
                                  opacity: childAction.disabled ? 0.5 : 1,
                                  textAlign: "left",
                                  fontSize: 13,
                                  lineHeight: 1.25,
                                  padding: "7px 10px",
                                  borderRadius: 6,
                                  cursor: childAction.disabled
                                    ? "default"
                                    : "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 8,
                                }}
                              >
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 8,
                                  }}
                                >
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      width: 12,
                                      justifyContent: "center",
                                    }}
                                  >
                                    {childAction.checked ? "✓" : ""}
                                  </span>
                                  <span>{childAction.label}</span>
                                </span>
                                {childAction.shortcut ? (
                                  <span
                                    style={{
                                      fontSize: 11,
                                      color: menuMutedTextColor,
                                    }}
                                  >
                                    {childAction.shortcut}
                                  </span>
                                ) : null}
                              </button>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              );
              const renderedMenu = renderContextMenu
                ? renderContextMenu(menuProps)
                : defaultMenu;
              return (
                <div
                  data-docx-context-menu="true"
                  style={{
                    position: "fixed",
                    left: position.x,
                    top: position.y,
                    zIndex: 2000,
                  }}
                  onContextMenu={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                >
                  {renderedMenu}
                </div>
              );
            }

            if (!tableContextMenuState) {
              return null;
            }

            const context = {
              tableIndex: tableContextMenuState.tableIndex,
              rowIndex: tableContextMenuState.rowIndex,
              cellIndex: tableContextMenuState.cellIndex,
            } satisfies DocxTableContextMenuContext;
            const position = {
              x:
                typeof window === "undefined"
                  ? tableContextMenuState.clientX
                  : clampNumber(
                      tableContextMenuState.clientX,
                      8,
                      Math.max(8, window.innerWidth - 228)
                    ),
              y:
                typeof window === "undefined"
                  ? tableContextMenuState.clientY
                  : clampNumber(
                      tableContextMenuState.clientY,
                      8,
                      Math.max(8, window.innerHeight - 260)
                    ),
            };
            const menuProps: DocxTableContextMenuRenderProps = {
              context,
              actions: tableContextMenuActions,
              runAction: (actionId) => {
                runTableContextMenuAction(actionId, context);
              },
              closeMenu: closeTableContextMenu,
              position,
              documentTheme: editor.documentTheme,
            };
            const defaultMenu = (
              <div
                style={{
                  width: 220,
                  display: "grid",
                  gap: 2,
                  padding: 6,
                  borderRadius: 8,
                  border: `1px solid ${
                    editor.documentTheme === "dark"
                      ? "rgba(148, 163, 184, 0.3)"
                      : "rgba(15, 23, 42, 0.14)"
                  }`,
                  backgroundColor:
                    editor.documentTheme === "dark"
                      ? "rgba(2, 6, 23, 0.98)"
                      : "rgba(255, 255, 255, 0.98)",
                  boxShadow:
                    editor.documentTheme === "dark"
                      ? "0 8px 20px rgba(2, 6, 23, 0.55)"
                      : "0 8px 20px rgba(15, 23, 42, 0.16)",
                }}
              >
                {tableContextMenuActions.map((action) => (
                  <button
                    key={action.id}
                    type="button"
                    onClick={() =>
                      runTableContextMenuAction(action.id, context)
                    }
                    style={{
                      appearance: "none",
                      border: "none",
                      background: "transparent",
                      color: action.destructive
                        ? editor.documentTheme === "dark"
                          ? "#fda4af"
                          : "#b91c1c"
                        : editor.documentTheme === "dark"
                        ? "#f3f4f6"
                        : "#111827",
                      textAlign: "left",
                      fontSize: 13,
                      lineHeight: 1.25,
                      padding: "7px 10px",
                      borderRadius: 6,
                      cursor: "pointer",
                    }}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            );
            const renderedMenu = renderTableContextMenu
              ? renderTableContextMenu(menuProps)
              : defaultMenu;
            return (
              <div
                data-docx-table-context-menu="true"
                style={{
                  position: "fixed",
                  left: position.x,
                  top: position.y,
                  zIndex: 2000,
                }}
                onContextMenu={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
              >
                {renderedMenu}
              </div>
            );
          })()
        : null}
    </div>
  );
}
